// DispatchDeck - HR Module
// Human Resources: Disciplinary forms, New Hire Checklists, Employee Reviews
// Founder account only (trippin-trucking)

import {
  db,
  storage,
  functions,
  httpsCallable,
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  arrayUnion,
  arrayRemove,
  limit,
  writeBatch
} from '../firebase-config.js?v=1868';

// TASK-4776 — firebase-config.js does not re-export serverTimestamp. Imported
// straight from the CDN, exactly as clock.js:35 already does, and from the
// IDENTICAL 10.8.0 URL firebase-config.js itself imports, so the browser serves
// ONE module instance. (Pitfall #1 is about cross-importing OUR modules with a
// `?v=` buster — a CDN SDK URL is not that.)
// Load-bearing, not cosmetic: firestore.rules requires the void stamp to equal
// `request.time`, so a device clock (`Timestamp.now()`) is REFUSED. A voided
// review's timestamp is evidence; it must come from the server.
import { serverTimestamp } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';
// fix-1999-followups — runTransaction (onboarding-slot Remove: conditional clear/restore), same URL, same instance.
import { runTransaction } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';
// TASK-9722 — item History pages through the ledger with a cursor (same CDN URL).
import { startAfter } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';
// TASK-9883 — the default-PPE seed asks the SERVER whether any type exists (the
// persistent cache can answer "empty" before the listener has synced). Same CDN URL.
import { getDocsFromServer } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';
// TASK-934 — Driver Classification / DQ-applicability matrix. hr.js is the ONE
// main-app importer (sharedFormat.js precedent); every other module reads
// window.DqMatrix at call time — never a second ?v= import (pitfall #1).
import '../dqMatrix.js?v=10172124'; // TASK-9850 CMV needs-answers list · TASK-9806 CMV checks · // TASK-1403 ND intrastate exemption · TASK-9429 mvrRecordIsOnFile
// TASK-1144 — shared person-detail section renderers (window.PersonSections).
// Same single-main-app-importer precedent as dqMatrix.js above: hr.js is
// statically imported by app.js, so window.PersonSections is defined before any
// handler runs; compliance.js reads window.PersonSections at call time (never a
// second ?v= import — pitfall #1).
// TASK-9145 — shared bulk §382.601(d) receipt-link sender (window.DaReceiptBulk).
// hr.js is the main app's single importer; compliance.js reads it at call time.
import '../daReceiptBulk.js?v=9145002';
import '../personSections.js?v=9999826'; // TASK-9677 merged SPH checklist · TASK-9662 SPH-modal date bounds · TASK-9438 CDLIS driver card + summary ·TASK-9299 shared DQ empty-group notes · TASK-9295 View MVR report · TASK-1320 break-in-service >30d: full query + work history · TASK-1403 intrastate N/A note · TASK-9429 MVR row needs the record
// TASK-4725 — carrier job openings + the ONE Copy Apply Link picker
// (window.JobOpenings). hr.js is the main app's single importer; app.js's HR
// settings code reads window.JobOpenings at call time. The partner portal imports
// the same file from compliance-partner.js (separate document) at the SAME ?v=.
import '../jobOpenings.js?v=1549450'; // TASK-9806
// TASK-1383 — shared guarded FileReader helpers (window.FileReadUtil). Same
// single-main-app-importer precedent as dqMatrix.js above: hr.js is statically
// imported by app.js, so window.FileReadUtil is defined before any handler
// runs; every other view reads window.FileReadUtil at call time (never a
// second ?v= import — pitfall #1).
import '../fileReadUtil.js?v=1513801';
// TASK-9444 slice 2 — render-time "Things to Verify" checks that depend on events
// after submit (window.ApplicationAuditLive). Same single-main-app-importer
// precedent: read at call time, never a second ?v= import (pitfall #1).
import '../applicationAuditLive.js?v=9444201';
// TASK-1155 — ONE source for the FCRA report-based-rejection test, shared with
// the server fence (_applyCandidateRejection). This file is GENERATED from
// functions/src/shared/reportBasedRejection.js by scripts/build-shared.cjs
// (hosting predeploy) — never edit it by hand. hr.js is the only importer.
import { isReportBasedRejectionReason as _sharedIsReportBasedRejectionReason } from '../shared/report-based-rejection.js?v=1512000';
// TASK-9402 — iPhone HEIC → JPEG (and photo → PDF for PDF-only slots) before
// upload; stored content type comes from the bytes, never a PDF fallback.
import { prepareUploadFile, preparePdfOnlyUpload, photoErrorMessage } from '../utils/uploadPrep.js?v=1';
// TASK-9721 — optional Job on the PPE issue screens, preselected from the open
// clock entry / the single assignment that day. The pure decision module is
// LAZY-loaded (only when an issue form asks for a job), so it costs the cold
// load nothing. jobboard.js loads the same file at the SAME buster.
let _ppeJobMod = null;
function _ppeJobModLoad() {
  if (_ppeJobMod) return Promise.resolve(_ppeJobMod);
  return __ddChunkWrap(import('../shared/ppe-issue-job.js?v=9721')).then(m => (_ppeJobMod = m));
}
// TASK-9531 — masked SSN / licence numbers with an eye toggle (window.SensitiveValue).
// hr.js is the main app's single importer; the partner portal imports the same file
// from compliance-partner.js at the SAME ?v=.
import '../sensitiveValue.js?v=9531001';
// TASK-9570 — window.SafetyRates: the ONE OSHA TRIR / DART math (GENERATED by
// scripts/build-shared.cjs from functions/src/shared/safetyRates.js). Side-effect
// import, read at CALL time; hr.js is its only importer.
import '../shared/safety-rates.js?v=9570001';
import '../utils/recordDate.js?v=9923'; // TASK-9923 citation fallback date = company calendar
// TASK-9581 — header count "of N": one count aggregation, same CDN URL/instance.
import { getCountFromServer } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';

// Infer content-type from file extension when browser didn't set file.type.
// Storage rules reject application/octet-stream, so we must guess a real MIME
// for files coming from iOS scanner apps, HEIC photos, etc.
const EXT_TO_MIME = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg', jpeg: 'image/jpeg',
  png: 'image/png', gif: 'image/gif', webp: 'image/webp',
  heic: 'image/heic', heif: 'image/heif',
  bmp: 'image/bmp', tiff: 'image/tiff', tif: 'image/tiff',
};
function inferContentType(file, fallback = 'application/pdf') {
  if (file?.type && file.type !== 'application/octet-stream') return file.type;
  const ext = (file?.name || '').split('.').pop()?.toLowerCase() || '';
  return EXT_TO_MIME[ext] || fallback;
}

// TASK-815 — SSN / secure-tax (peopleTax) READ gate. The `hr-directory` module
// is the grant (reverses the TASK-637/607 admin-only carve-out). owner/admin/
// founder + hr-directory holders (incl. view-only) may VIEW SSN/W-4/I-9. A bare
// `hr`-module member (NO hr-directory) still must NOT see SSN — and never trigger
// the peopleTax read. Mirrors window.hrCanViewSecureTax (app.js); local wrapper
// so hr.js doesn't hard-depend on load order. Defaults OPEN if the app.js helper
// is missing (owner/legacy path is the common case and stays unchanged).
function _hrCanSeeSecureTax() {
  return typeof window.hrCanViewSecureTax === 'function' ? window.hrCanViewSecureTax() : true;
}

// TASK-815 — SSN / secure-tax (peopleTax) WRITE gate. Mirrors the firestore.rules
// peopleTax create/update/delete arm: isTenantAdmin || (hasModule('hr-directory')
// && !isViewUploadOnly). A "view & upload only" hr-directory holder may SEE SSN
// (read above) but must NOT edit it — so the SSN input renders read-only and the
// saveSecureTax write is skipped, avoiding a raw "insufficient permissions" throw.
// hrCanManageModule() (app.js) already returns false for viewUploadOnly members
// and false for non-hr members; combine with the SEE gate so only an hr-directory
// holder who CAN edit gets a writable SSN field.
function _hrCanEditSecureTax() {
  if (!_hrCanSeeSecureTax()) return false;
  return typeof window.hrCanManageModule === 'function' ? window.hrCanManageModule() : true;
}

// TASK-1511 — local wrapper for app.js's hrEffectiveAdmin (firestore.rules
// isTenantAdmin mirror) so hr.js doesn't hard-depend on load order. Defaults OPEN
// if the helper is missing, matching _hrCanSeeSecureTax's convention: the
// owner/legacy single-login path is the common case and must not be broken by a
// missing helper. Use for surfaces whose WRITE is admin-gated at the rules level.
function _hrEffectiveAdminSafe() {
  return typeof window.hrEffectiveAdmin === 'function' ? window.hrEffectiveAdmin() : true;
}

// Admin/owner client gate for bulk-PII HR actions (payroll exports, deletes).
// The server enforces the real gate (requireTenantAdmin); this just hides the
// buttons for non-admins. Owner / legacy-admin (no member doc) → true.
function _hrIsOwnerOrAdmin() {
  const m = window.AppState?.memberAccess;
  if (!m) return true; // owner/legacy (no member doc) — full access
  if (['revoked', 'inactive', 'pending'].includes(m.status)) return false;
  return m.role === 'owner' || m.role === 'admin';
}

// TASK-1269 — roster-write gate, byte-identical to compliance.js _compCanWriteRoster.
// The person updateDoc that saveMedExam / saveMvrPull perform is gated by
// firestore.rules people:update = isDispatcherOrHigher || (hasModule('roster') &&
// !viewUploadOnly). A member who fails THIS gate is denied on that write BY DESIGN,
// but the credential blob (medExam/mvr) has ALREADY uploaded via the catch-all
// Storage write (canAccess) — and those paths are delete-immutable client-side
// (§391.51, storage.rules `allow delete: if false`, medExam locked in TASK-1268).
// Result: a silent, un-reapable orphan. Block the upload at the SOURCE for exactly
// the members the person-write would reject. NOT a capability regression: these
// members could never complete the save — it always errored after orphaning.
function _hrCanWriteRoster() {
  const m = window.AppState?.memberAccess;
  if (!m) return true; // owner/legacy (no member doc)
  if (['revoked', 'inactive', 'pending'].includes(m.status)) return false;
  if (m.role === 'owner' || m.role === 'admin' || m.role === 'dispatcher') return true;
  // TASK-1269 (COD Code Reviewer) — mirror the AUTHORITATIVE server gate
  // (requireRosterWriteAccess / firestore.rules people:update), which requires
  // hasModule('roster') && !viewUploadOnly. A view/upload-only roster member is
  // denied the person write downstream, so block the upload up front to avoid the
  // wasted round-trip + orphan-then-reap. (_compCanWriteRoster carries the same
  // benign omission; the reaper covers it either way — this is the tighter parity.)
  return Array.isArray(m.modules) && m.modules.includes('roster') && m.viewUploadOnly !== true;
}

// TASK-1511 (Option A — client-side gating; the rules widening is Option B, parked
// for Chris). "Approve to Roster" is ONE atomic writeBatch (confirmApproveToRoster,
// ~L22770) whose legs are gated by DIFFERENT rules than the HR view that renders
// the button — so a scoped HR member walked the entire hire form and then ate a raw
// "Missing or insufficient permissions" AFTER filling in name / role / hire date:
//   • people create — firestore.rules:1255 = isDispatcherOrHigher || hasModule(
//     'roster') || isComplianceManager. There is NO `hr` arm.
//   • peopleTax/{personId} — firestore.rules:1582 = isTenantAdmin || (hasModule(
//     'hr-directory') && !isViewUploadOnly). This leg is only in the batch when the
//     application carried an SSN (i.e. every §391.21 application), so it is
//     candidate-dependent — pass the candidate whenever you have it.
//   • candidates update (rules:4228) + the peopleBackgroundChecks hire-mirror create
//     (rules:1657, TASK-1388a) both deny a view-upload-only member, so that flag
//     kills the batch on its own.
// The batch is ATOMIC: one denial fails the whole hire, and the person/candidate
// docs are left untouched. Mirror all three legs here and tell the member WHICH
// module to ask their admin for. Same shape as TASK-637's
// hrCanManageComplianceBilling: the UI fails closed, rules stay authoritative.
function _hrApproveToRosterGate(candidate = null) {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return { ok: true, missing: [] };
  const m = st.memberAccess;
  if (!m) return { ok: true, missing: [] }; // owner / legacy single-login (no member doc)
  if (['revoked', 'inactive', 'pending'].includes(m.status)) {
    return { ok: false, missing: ['access'] };
  }
  const isAdmin = m.role === 'owner' || m.role === 'admin';
  const modules = Array.isArray(m.modules) ? m.modules : [];
  const missing = [];
  // Leg 1 — people create. dispatcher+ or the `roster` module.
  if (!isAdmin && m.role !== 'dispatcher' && !modules.includes('roster')) missing.push('roster');
  // Leg 2 — peopleTax (SSN + tax address). Fail CLOSED when we don't have the
  // candidate in hand: an application with an SSN is the common case, and a
  // hidden-but-allowed button is a smaller harm than a hire that dies mid-form.
  // TASK-1488 (COD Code Reviewer ❌ catch) — key on ssnMasked TOO: post-TASK-1199
  // candidate docs carry ONLY ssnMasked (live scan 2026-08-13: 25/25 SSN-bearing
  // candidates have ssnMasked, zero have ssn), so the old `!!candidate.ssn` leg
  // never fired for any real application. Without this, a bare-hr approver could
  // hire a vault-backed candidate and the server SSN copy (TASK-1570
  // approveCandidateToRoster since TASK-1576 — same admin|hr-directory gate) would deny, so
  // the SSN would silently never reach peopleTax. Requiring hr-directory here
  // keeps the client gate and that callable's re-derived gate in lockstep.
  // TASK-1564 — the tax address now ALSO lands in peopleTax at hire (previously
  // written onto the broadcast people doc). So the peopleTax batch leg fires for
  // ANY candidate carrying an SSN OR a structured tax address; the gate must
  // require hr-directory in the same set or a roster-only approver's hire dies on
  // the peopleTax write (TASK-428 strand). address is {street,city,state,zip}.
  const _addr = candidate && candidate.address;
  const _hasTaxAddr = !!(_addr && (_addr.street || _addr.city || _addr.state || _addr.zip));
  const ssnLeg = candidate ? (!!(candidate.ssnMasked || candidate.ssn) || _hasTaxAddr) : true;
  if (ssnLeg && !isAdmin && !modules.includes('hr-directory')) missing.push('hr-directory');
  // Leg 3 — view & upload only can't complete the candidate update or the BG mirror.
  if (!isAdmin && m.viewUploadOnly === true) missing.push('view-only');
  return { ok: missing.length === 0, missing };
}

// Boolean wrapper for call sites that only need yes/no.
function _hrCanApproveToRoster(candidate = null) {
  return _hrApproveToRosterGate(candidate).ok === true;
}

// The specific, actionable note that renders WHERE the button was. Names the module
// to ask for rather than saying "no permission" — a scoped external hiring manager
// has no way to guess that "add to roster" needs the Roster module.
function _hrApproveToRosterNote(gate) {
  const missing = gate?.missing || [];
  if (missing.includes('access')) {
    return 'Your access to this account isn\'t active, so this applicant can\'t be added to the roster. Ask your admin to restore your login.';
  }
  const asks = [];
  if (missing.includes('roster')) {
    asks.push('the <strong>Roster</strong> module — adding someone to the roster creates their employee record');
  }
  if (missing.includes('hr-directory')) {
    asks.push('the <strong>HR Directory</strong> module — this application includes tax details (SSN and/or address) stored in the admin-gated tax record');
  }
  if (missing.includes('view-only')) {
    asks.push('edit access — your login is <strong>view &amp; upload only</strong>');
  }
  if (!asks.length) return '';
  return `You can review and screen this applicant, but adding them to the roster needs ${asks.join('; and ')}. Ask your admin for ${asks.length > 1 ? 'those' : 'it'}, or have an admin complete the hire.`;
}

// TASK-1269 — best-effort Admin-SDK reap of a TRULY-orphaned credential upload blob,
// mirror of compliance.js _reapOrphanCredBlobCompliance. The medExam/mvr Storage
// paths are delete-immutable client-side (§391.51), so a blob whose referencing
// write never landed can only be removed by the server callable, which RE-VERIFIES
// the blob is unreferenced before deleting (a lost-response where the write actually
// landed is refused). Safe to call whenever we uploaded a blob and its reference did
// not land. Never throws — cleanup is best-effort.
async function _reapOrphanCredBlobHr(tenantId, path) {
  if (!tenantId || !path) return;
  try {
    const reapFn = httpsCallable(functions, 'reapOrphanedCredentialBlob');
    await reapFn({ tenantId, path });
  } catch (err) {
    console.warn('[HR] orphan-blob reap failed (non-fatal):', err?.message);
  }
}

// Owner-ONLY gate (stricter than _hrIsOwnerOrAdmin). Mirrors the isTenantOwner
// rule on destructive deletes of disciplinary/review records (firestore.rules
// hrReviews:2364, hrForms:2389). 2026-06-26 — these delete buttons were rendered
// to every HR viewer but the rules reject anyone but the owner, so an HR admin or
// hr-module staffer saw a button that always errored. Hide it to match the rule;
// deletion of legal HR history stays an owner break-glass action.
function _hrIsOwner() {
  const m = window.AppState?.memberAccess;
  if (!m) return true; // owner/legacy (no member doc)
  if (['revoked', 'inactive', 'pending'].includes(m.status)) return false;
  return m.role === 'owner';
}

// Check if a sub-module (e.g. 'hr-directory') is unlocked by the current PIN
function isSubModuleUnlocked(subModule) {
  // Demo, no PINs configured, or no tenant = full access
  if (window.AppState?.isDemo || window.AppState?.tenant === 'demo') return true;
  // TASK-319 multi-user member — modules array is absolute source of truth.
  // Test Chris (member, hr granted but NOT hr-directory) was seeing Employee
  // Directory section because this function returned true on the "no PINs
  // configured" branch below for member tenants. Member context must be
  // checked BEFORE the PIN-based fallback. 2026-05-22.
  const memberAccess = window.AppState?.memberAccess;
  if (memberAccess && memberAccess.status === 'active'
      && memberAccess.role !== 'owner'
      && Array.isArray(memberAccess.modules)
      && memberAccess.modules.length > 0) {
    return memberAccess.modules.includes(subModule);
  }
  // TASK-732 — owner / legacy-admin (no modules) members must NOT be PIN-gated for
  // sub-modules. checkModuleAccess (app.js) already bypasses for them, but this gate
  // didn't: an owner authed via members/{uid} (modules:null) on a tenant WITH
  // accessPins fell through to the PIN path → HR Employee Directory hidden (Split
  // Rock, TASK-732). Mirror the module-level owner/legacy-admin bypass here. Scoped
  // members (role !== owner, with a real modules array) are still governed by the
  // branch above, so a member explicitly denied hr-directory stays denied.
  if (memberAccess && !['revoked', 'inactive', 'pending'].includes(memberAccess.status)) {
    if (memberAccess.role === 'owner') return true;
    if (memberAccess.role === 'admin'
        && (!Array.isArray(memberAccess.modules) || memberAccess.modules.length === 0)) return true;
  }
  const accessPins = (window.accessPinMap?.() || window.AppState?.settings?.accessPins || {});
  if (Object.keys(accessPins).length === 0) return true;
  // Check via app.js isModuleUnlocked (works for sub-modules too)
  if (typeof window.isModuleUnlocked === 'function') return window.isModuleUnlocked(subModule);
  return true;
}

// ============================================
// ORG CHART POSITIONS
// ============================================
const ORG_CHART = {
  'Field Operations': [
    { id: 'field_manager', title: 'Field Manager', level: 1 },
    { id: 'division_manager', title: 'Division Manager', level: 2 },
    { id: 'supervisor_foreman', title: 'Supervisor / Foreman', level: 3 },
    { id: 'crew_lead_driver', title: 'Crew Lead / Driver', level: 4 },
    { id: 'advanced_operator', title: 'Advanced Operator', level: 5 },
    { id: 'operator', title: 'Operator', level: 6 },
    { id: 'entry_operator', title: 'Entry Operator', level: 7 },
    // TASK-1566 — swamper is a TITS-specific (daylighting) role, not universal.
    // `niche` keeps the id resolving (existing assignments + reviews unaffected)
    // but hides it from every tenant's dropdowns/filters UNLESS they have someone
    // assigned to it. Mirrors roster.js DEFAULT_CREW_TYPES dropping 'swamper' as
    // "too niche, users can add custom". Never delete these ids — TITS has people
    // and reviews on them; the id must always resolve to a title.
    { id: 'veteran_swamper', title: 'Veteran Swamper', level: 8, niche: true },
    { id: 'entry_swamper', title: 'Entry Swamper', level: 9, niche: true }
  ],
  'Dispatch & Sales': [
    { id: 'dispatch_manager', title: 'Dispatch Manager', level: 1 },
    { id: 'dispatcher', title: 'Dispatcher', level: 2 },
    { id: 'sales_account_manager', title: 'Sales / Account Manager', level: 2 }
  ],
  'Maintenance / Shop': [
    { id: 'shop_manager', title: 'Shop Manager', level: 1 },
    { id: 'mechanic', title: 'Mechanic', level: 2 },
    { id: 'shop_hand', title: 'Shop Hand', level: 3 }
  ],
  'Office / Admin': [
    { id: 'office_manager', title: 'Office Manager', level: 1 },
    { id: 'billing_ar', title: 'Billing / AR', level: 2 },
    { id: 'payroll_hr_admin', title: 'Payroll / HR Admin', level: 2 }
  ]
};

// Flatten for easy lookup
const ALL_POSITIONS = Object.entries(ORG_CHART).flatMap(([dept, positions]) =>
  positions.map(p => ({ ...p, department: dept }))
);

// Resolve a position id → display title. Checks built-in REVIEW_POSITIONS first,
// then custom positions stored on timeOffPolicy, then falls back to the raw id.
function getPositionTitle(positionId) {
  if (!positionId) return '';
  if (REVIEW_POSITIONS[positionId]?.title) return REVIEW_POSITIONS[positionId].title;
  const custom = (HRState?.timeOffPolicy?.customPositions || []).find(p => p.id === positionId);
  if (custom?.title) return custom.title;
  return positionId;
}

// ============================================================================
// TASK-1566 — window.HRPositions: the ONE resolver for the HR position taxonomy.
// Built-in ORG_CHART/REVIEW_POSITIONS PLUS tenant customPositions (stored at
// hrTimeOffPolicy/config.customPositions), MINUS niche built-ins the tenant has
// nobody assigned to. Every consumer (review-create dropdown, position filter,
// Edit-HR select, onPositionChange, submit/view/print) drives off this so a
// tenant-defined position is a first-class citizen everywhere.
//   • window.* — NEVER a cross-import with a ?v= buster (Pitfall #1).
//   • PURE title/KPI taxonomy — it derives NO DOT position type. Title ⟂ FMCSA
//     tier (TASK-1566 addendum): never map a title → cdl/non_cdl/non_driver here.
// ============================================================================

// Generic KPI template for a custom (tenant-authored) position. Gives it a real
// review path instead of the "Coming Soon — Contact HR" dead end. Weights = 100.
const DEFAULT_CUSTOM_KPIS = [
  { id: 'quality_of_work', name: 'Quality of Work', weight: 15, description: 'Overall work quality' },
  { id: 'safety_compliance', name: 'Safety Compliance', weight: 15, description: 'Violations / incidents count' },
  { id: 'attendance_punctuality', name: 'Attendance & Punctuality', weight: 15, description: '% on-time' },
  { id: 'productivity', name: 'Productivity', weight: 15, description: 'Output vs expectation' },
  { id: 'reliability', name: 'Reliability & Initiative', weight: 10, description: 'Follow-through without prompting' },
  { id: 'teamwork', name: 'Teamwork & Communication', weight: 10, description: 'Peer / supervisor feedback' },
  { id: 'job_knowledge', name: 'Job Knowledge', weight: 10, description: 'Competency for the role' },
  { id: 'policy_adherence', name: 'Policy Adherence', weight: 10, description: 'Incidents count' }
];

// Display labels for KPIs RETIRED from a built-in set (e.g. `swamper_training`
// pulled off `operator`). Historical reviews render from their STORED kpiScores;
// this only resolves the display LABEL for an id that's no longer in the live
// set, so an old review's row keeps its original name byte-for-byte. Append
// here whenever a built-in KPI id is removed — never delete an entry.
const RETIRED_KPI_LABELS = {
  swamper_training: 'Swamper Training Progress'
};

function _hrCustomPositions() {
  const list = HRState && HRState.timeOffPolicy && HRState.timeOffPolicy.customPositions;
  return Array.isArray(list) ? list : [];
}

// A niche built-in is surfaced only when the tenant actually uses it.
function _hrPositionInUse(id) {
  return !!(HRState && Array.isArray(HRState.people) && HRState.people.some(p => p && p.hrPosition === id));
}

// Resolve ANY position id → { id, title, department, builtIn, available, kpis }.
// Custom positions without their own kpis[] fall back to the generic template.
function _hrResolvePosition(id) {
  if (!id) return null;
  const custom = _hrCustomPositions().find(p => p && p.id === id);
  if (custom) {
    return {
      id,
      title: custom.title || id,
      department: 'Custom',
      builtIn: false,
      available: true,
      kpis: (Array.isArray(custom.kpis) && custom.kpis.length) ? custom.kpis : DEFAULT_CUSTOM_KPIS
    };
  }
  const rp = REVIEW_POSITIONS[id];
  if (rp) {
    const dept = (ALL_POSITIONS.find(p => p.id === id) || {}).department || '';
    return {
      id,
      title: rp.title || id,
      department: dept,
      builtIn: true,
      available: rp.available !== false,
      kpis: Array.isArray(rp.kpis) ? rp.kpis : DEFAULT_CUSTOM_KPIS
    };
  }
  return null;
}

// Ordered [{ department, positions:[{id,title,available,builtIn}] }] for grouped
// <select>s. Niche built-ins are hidden unless in use (de-TITS). Custom
// positions land in a trailing "Custom" group.
function _hrGroupedPositions() {
  const groups = [];
  for (const [dept, positions] of Object.entries(ORG_CHART)) {
    const visible = positions
      .filter(p => !p.niche || _hrPositionInUse(p.id))
      .map(p => ({ id: p.id, title: p.title, builtIn: true, available: (REVIEW_POSITIONS[p.id] ? REVIEW_POSITIONS[p.id].available !== false : true) }));
    if (visible.length) groups.push({ department: dept, positions: visible });
  }
  const customs = _hrCustomPositions()
    .filter(p => p && p.id)
    .map(p => ({ id: p.id, title: p.title || p.id, builtIn: false, available: true }));
  if (customs.length) groups.push({ department: 'Custom', positions: customs });
  return groups;
}

// Flat list of every VISIBLE position (built-in-in-use + custom), each carrying
// its department. Used by the position filter + directory.
function _hrFlatPositions() {
  return _hrGroupedPositions().flatMap(g => g.positions.map(p => Object.assign({ department: g.department }, p)));
}

// Label resolver for a stored review's KPI row. Prefer the label snapshotted on
// the row (new reviews), then the live position config, then the retired-label
// map, then a prettified id. This is what lets historical reviews render from
// STORED data even after the live built-in set changes.
function _hrKpiLabel(kpiId, storedName, posConfig) {
  if (storedName) return storedName;
  const live = posConfig && Array.isArray(posConfig.kpis) ? posConfig.kpis.find(k => k.id === kpiId) : null;
  if (live && live.name) return live.name;
  if (RETIRED_KPI_LABELS[kpiId]) return RETIRED_KPI_LABELS[kpiId];
  return String(kpiId || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

window.HRPositions = {
  get: _hrResolvePosition,
  grouped: _hrGroupedPositions,
  list: _hrFlatPositions,
  kpiLabel: _hrKpiLabel,
  DEFAULT_CUSTOM_KPIS,
  RETIRED_KPI_LABELS
};

// ============================================
// TIME-OFF CATEGORIES
// ============================================
const TIME_OFF_CATEGORIES = [
  { id: 'vacation', label: 'Vacation' },
  { id: 'sick', label: 'Sick Leave' },
  { id: 'personal', label: 'Personal Day' },
  { id: 'unpaid', label: 'Unpaid' },
  { id: 'bereavement', label: 'Bereavement' },
  { id: 'parental_leave', label: 'Parental Leave' },
  { id: 'jury_duty', label: 'Jury Duty' },
  { id: 'other', label: 'Other' }
];

// ============================================
// BEREAVEMENT TIERS
// ============================================
const BEREAVEMENT_TIERS = [
  { id: 'level1', label: 'Level 1', desc: 'Spouse, child, stepchild, parent, stepparent, grandchild' },
  { id: 'level2', label: 'Level 2', desc: 'Sibling, in-laws (MIL/FIL/SIL/BIL/DIL)' },
  { id: 'level3', label: 'Level 3', desc: 'Household member' },
  { id: 'level4', label: 'Level 4', desc: 'Grandparent, grandparent-in-law' },
  { id: 'level5', label: 'Level 5', desc: 'Loss of pregnancy' },
  { id: 'level6', label: 'Level 6', desc: 'Aunt, uncle, cousin, niece, nephew' },
  { id: 'level7', label: 'Level 7', desc: 'Distant relative, close friend' },
];

/** Get bereavement allowance — checks global policy first, then per-level for backward compat */
function getBereavementAllowance(allowances, level) {
  // Check global bereavement policy first
  const globalB = HRState.timeOffPolicy?.bereavement;
  if (typeof globalB === 'object' && globalB !== null) return globalB[level] || 0;
  // Fall back to per-level allowances (legacy)
  const val = allowances?.bereavement;
  if (typeof val === 'number') return val;
  if (typeof val === 'object' && val !== null) return val[level] || 0;
  return 0;
}

/** Sum total bereavement days from object (for summary display). Excludes travelDays. */
function sumBereavementDays(bereavementObj) {
  if (typeof bereavementObj === 'number') return bereavementObj;
  if (typeof bereavementObj !== 'object' || bereavementObj === null) return 0;
  return BEREAVEMENT_TIERS.reduce((sum, t) => sum + (bereavementObj[t.id] || 0), 0);
}

/** Sum all allowance days for a policy level, handling bereavement as object */
function sumAllowanceDays(allowances) {
  if (!allowances) return 0;
  let total = 0;
  for (const [key, val] of Object.entries(allowances)) {
    if (key === 'bereavement') {
      total += sumBereavementDays(val);
    } else if (typeof val === 'number') {
      total += val;
    }
  }
  return total;
}

// ============================================
// POSITION-SPECIFIC KPIs FOR REVIEWS
// Based on Org Chart Overview V3
// ============================================
const REVIEW_POSITIONS = {
  // ==================== FIELD OPERATIONS ====================
  'entry_swamper': {
    title: 'Entry Swamper',
    available: true,
    kpis: [
      { id: 'training_completion', name: 'Training Completion', weight: 3, description: 'Completion of required training' },
      { id: 'safety_compliance', name: 'Safety Compliance', weight: 12.5, description: 'Violations count' },
      { id: 'tool_organization', name: 'Tool Organization', weight: 12.5, description: 'Weekly check' },
      { id: 'cleanliness', name: 'Cleanliness', weight: 12.5, description: 'Weekly check' },
      { id: 'ticket_accuracy', name: 'Ticket Accuracy', weight: 15, description: '% complete + correct' },
      { id: 'job_readiness', name: 'Job Readiness', weight: 9, description: 'Startup delays count' },
      { id: 'teamwork', name: 'Teamwork', weight: 3, description: 'Operator feedback' },
      { id: 'attendance_punctuality', name: 'Attendance & Punctuality', weight: 10, description: '% on-time' },
      { id: 'procedure_adherence', name: 'Procedure Adherence', weight: 5, description: 'Mistakes requiring redo count' },
      { id: 'learning_progression', name: 'Learning Progression', weight: 5, description: 'Skills signed off count' }
    ]
  },
  'veteran_swamper': {
    title: 'Veteran Swamper',
    available: true,
    kpis: [
      { id: 'tool_inventory', name: 'Tool/Inventory Accuracy', weight: 10, description: 'Missing items / audit pass %' },
      { id: 'cleanliness', name: 'Cleanliness Score', weight: 10, description: 'Cab & body condition' },
      { id: 'ticket_accuracy', name: 'Ticket Accuracy', weight: 10, description: '% correct + complete' },
      { id: 'attendance_punctuality', name: 'Attendance & Punctuality', weight: 10, description: '% on-time' },
      { id: 'job_readiness', name: 'Job Readiness', weight: 14, description: 'Startup delays caused' },
      { id: 'task_completion', name: 'Task Completion Rate', weight: 8.75, description: '% complete' },
      { id: 'safety_compliance', name: 'Safety Compliance', weight: 7, description: 'Violations count' },
      { id: 'customer_professionalism', name: 'Customer Professionalism', weight: 5.25, description: 'Professionalism rating' },
      { id: 'communication_quality', name: 'Communication Quality', weight: 18.75, description: 'Radio/verbal effectiveness' },
      { id: 'new_operator_support', name: 'New Operator Support', weight: 6.25, description: 'Training effectiveness' }
    ]
  },
  'entry_operator': {
    title: 'Entry Operator',
    available: true,
    kpis: [
      { id: 'equipment_competency', name: 'Equipment Operation Competency', weight: 18.75, description: 'Competency rating' },
      { id: 'safety_compliance', name: 'Safety Compliance', weight: 8.75, description: 'Violations count' },
      { id: 'ticket_accuracy', name: 'Ticket Accuracy', weight: 9.33, description: '% correct + complete' },
      { id: 'tool_organization', name: 'Tool Organization Score', weight: 9.33, description: 'Weekly check' },
      { id: 'prepost_trip', name: 'Pre/Post Trip Completion Rate', weight: 9.33, description: '% complete' },
      { id: 'coachability', name: 'Coachability Score', weight: 8.75, description: 'Supervisor rating' },
      { id: 'task_completion', name: 'Task Completion Accuracy', weight: 8.75, description: '% accurate' },
      { id: 'attendance_punctuality', name: 'Attendance & Punctuality', weight: 12, description: '% on-time' },
      { id: 'customer_interaction', name: 'Customer Interaction Basics', weight: 6.25, description: 'Professionalism rating' },
      { id: 'policy_adherence', name: 'Policy Adherence', weight: 8.75, description: 'Incidents count' }
    ]
  },
  'operator': {
    title: 'Operator',
    available: true,
    // TASK-1566 — `swamper_training` (11.11) REMOVED: a company with no swampers
    // was scoring its operators on an impossible metric worth 1/9 of the review.
    // Swamper-mentoring is a TITS-specific concern; a tenant that wants it back
    // adds a custom position. Remaining 8 KPIs re-normalized 11.11/11.12 → 12.5
    // (sum 100). SAFE because view/print render historical reviews from STORED
    // kpiScores, not this live set — see viewReview/printReview + RETIRED_KPI_LABELS.
    kpis: [
      { id: 'job_completion', name: 'Job Completion Rate', weight: 12.5, description: '% complete' },
      { id: 'safety_compliance', name: 'Safety Compliance', weight: 12.5, description: 'Incidents/near misses count' },
      { id: 'dvir_completion', name: 'DVIR / Inspection Completion Rate', weight: 12.5, description: '% complete' },
      { id: 'ticket_accuracy', name: 'Ticket Accuracy', weight: 12.5, description: '% complete + correct' },
      { id: 'multi_truck_coordination', name: 'Multi-Truck Coordination Success', weight: 12.5, description: 'Jobs supported without delays' },
      { id: 'customer_feedback', name: 'Customer Feedback', weight: 12.5, description: 'Complaints count' },
      { id: 'equipment_care', name: 'Equipment Care Score', weight: 12.5, description: 'Damage/neglect incidents' },
      { id: 'attendance_punctuality', name: 'Attendance & Punctuality', weight: 12.5, description: '% on-time' }
    ]
  },
  'advanced_operator': {
    title: 'Advanced Operator',
    available: true,
    kpis: [
      { id: 'multi_division_coverage', name: 'Multi-Division Coverage / Attitude', weight: 10, description: 'Shifts/tasks covered / attitude' },
      { id: 'mentoring', name: 'Mentoring Activity', weight: 15, description: 'New hires signed off / skills checklists completed' },
      { id: 'safety_compliance', name: 'Safety Compliance', weight: 12.25, description: 'Violations # / audits passed %' },
      { id: 'attendance_punctuality', name: 'Attendance Efficiency / Punctuality', weight: 8.75, description: '% on-time' },
      { id: 'pm_reporting', name: 'Preventive Maintenance Reporting Accuracy', weight: 14, description: '% issues caught early' },
      { id: 'equipment_uptime', name: 'Equipment Uptime', weight: 12, description: '% uptime' },
      { id: 'job_quality', name: 'Job Quality', weight: 12, description: 'Rework/callbacks #' },
      { id: 'truck_preparedness', name: 'Overall Truck Preparedness', weight: 6, description: 'Cleanliness + functionality' },
      { id: 'trailer_condition', name: 'Pre-Trip Submission', weight: 6, description: 'Submission rate / compliance %' },
      { id: 'permit_readiness', name: 'Permit-Readiness Competency', weight: 4, description: 'Weights/dims documented correctly %' }
    ]
  },
  'crew_lead_driver': {
    title: 'Crew Lead / Driver',
    available: true,
    kpis: [
      { id: 'ontime_arrival', name: 'On-Time Arrival / Delivery Rate', weight: 10, description: '% on-time' },
      { id: 'dvir_compliance', name: 'DVIR / Inspection Compliance', weight: 10, description: '% compliant' },
      { id: 'preventable_incidents', name: 'Preventable Incidents', weight: 10, description: 'Count' },
      { id: 'attendance_punctuality', name: 'Attendance Efficiency / Punctuality', weight: 10, description: '% on-time' },
      { id: 'load_ticket_accuracy', name: 'Load/Ticket Accuracy', weight: 10, description: '% accurate' },
      { id: 'dispatch_communication', name: 'Dispatch Communication Responsiveness', weight: 10, description: 'Avg response time' },
      { id: 'customer_satisfaction', name: 'Customer Satisfaction', weight: 10, description: 'Complaints vs positive notes' },
      { id: 'equipment_cleanliness', name: 'Equipment Cleanliness Score', weight: 10, description: 'Weekly check' },
      { id: 'hos_compliance', name: 'HOS Compliance', weight: 10, description: 'Violations count' },
      { id: 'route_efficiency', name: 'Route Efficiency', weight: 10, description: 'Avoidable deadhead miles %' }
    ]
  },
  'supervisor_foreman': {
    title: 'Supervisor / Foreman',
    available: true,
    kpis: [
      { id: 'daily_communication', name: 'Daily Communication', weight: 7, description: 'Communication effectiveness' },
      { id: 'field_checks_customer', name: 'Field Checks + Customer Relationships', weight: 7, description: 'Site visits & relationship quality' },
      { id: 'shop_coordination', name: 'Shop Coordination / Yard', weight: 6, description: 'Coordination effectiveness' },
      { id: 'safety_compliance', name: 'Safety Compliance', weight: 8.75, description: 'Toolbox talks, PPE, JSA completion %' },
      { id: 'inspection_compliance', name: 'Inspection Compliance', weight: 7.5, description: 'Pre/post trips on time %' },
      { id: 'job_documentation', name: 'Job Documentation Accuracy', weight: 8.75, description: 'Tickets/photos/notes complete %' },
      { id: 'attendance_punctuality', name: 'Attendance & Punctuality', weight: 7.5, description: 'Crew on-time %' },
      { id: 'customer_feedback', name: 'Customer Feedback', weight: 3.75, description: 'Complaints vs compliments' },
      { id: 'survey_feedback', name: 'Survey Feedback', weight: 3.75, description: 'Survey scores' },
      { id: 'job_completion_accuracy', name: 'Daily Job Completion Accuracy', weight: 8, description: 'Scope done right, first pass %' },
      { id: 'crew_productivity', name: 'Crew Productivity', weight: 8, description: 'Revenue or output per shift' },
      { id: 'material_tool_loss', name: 'Material & Tool Loss/Damage', weight: 12, description: '$/month' },
      { id: 'cost_tracking_equipment', name: 'Cost Tracking (Equipment/Shop)', weight: 6, description: 'Equipment/shop costs tracked' },
      { id: 'cost_tracking_accuracy', name: 'Cost Tracking Accuracy', weight: 6, description: 'Costs coded correctly %' }
    ]
  },
  'division_manager': {
    title: 'Division Manager',
    available: true,
    kpis: [
      { id: 'division_profitability', name: 'Division Profitability vs Budget', weight: 11.11, description: 'Margin %' },
      { id: 'ontime_job_completion', name: 'On-Time Job Completion Rate', weight: 11.11, description: '% on-time' },
      { id: 'safety_rate', name: 'Safety Rate', weight: 11.11, description: 'TRIR/recordables' },
      { id: 'equipment_uptime', name: 'Equipment Uptime', weight: 11.11, description: '% uptime' },
      { id: 'permit_readiness', name: 'Permit Readiness', weight: 11.11, description: 'Permits submitted on time / % no-delay' },
      { id: 'customer_satisfaction', name: 'Customer Satisfaction', weight: 11.11, description: 'Score / repeat work rate' },
      { id: 'labor_cost_variance', name: 'Field Labor Cost Variance vs Estimate', weight: 11.11, description: '% variance' },
      { id: 'employee_retention', name: 'Employee Retention / Turnover', weight: 11.11, description: '% turnover' },
      { id: 'survey_feedback', name: 'Survey Feedback', weight: 11.12, description: 'Survey scores' }
    ]
  },
  'field_manager': {
    title: 'Field Manager',
    available: true,
    kpis: [
      { id: 'ontime_job_completion', name: 'On-Time Job Completion Rate', weight: 12.5, description: '% on-time' },
      { id: 'safety_incident', name: 'Safety Incident Reporting', weight: 12.5, description: 'Incidents reported' },
      { id: 'crew_utilization', name: 'Crew Utilization', weight: 12.5, description: '% utilized' },
      { id: 'equipment_uptime', name: 'Equipment Uptime', weight: 12.5, description: '% uptime' },
      { id: 'permit_readiness', name: 'Permit Readiness', weight: 12.5, description: 'Permits submitted on time / % no-delay' },
      { id: 'preventable_rework', name: 'Preventable Rework / Callbacks', weight: 12.5, description: 'Count' },
      { id: 'employee_retention', name: 'Employee Retention / Turnover', weight: 12.5, description: '% turnover' },
      { id: 'survey_feedback', name: 'Survey Feedback', weight: 12.5, description: 'Survey scores' }
    ]
  },

  // ==================== DISPATCH & SALES ====================
  'dispatch_manager': {
    title: 'Dispatch Manager',
    available: true,
    kpis: [
      { id: 'ontime_dispatch', name: 'On-Time Dispatch Rate', weight: 10, description: '% on-time' },
      { id: 'dispatch_error', name: 'Dispatch Error Rate', weight: 10, description: '# / % corrected' },
      { id: 'driver_utilization', name: 'Driver Utilization', weight: 10, description: '% utilized' },
      { id: 'customer_satisfaction', name: 'Customer Satisfaction', weight: 10, description: 'Complaints count' },
      { id: 'response_time', name: 'Response Time', weight: 10, description: 'Avg minutes to confirm' },
      { id: 'schedule_adherence', name: 'Schedule Adherence', weight: 10, description: '% of jobs executed as planned' },
      { id: 'ticket_completeness', name: 'Ticket Completeness/Accuracy', weight: 10, description: '% complete' },
      { id: 'communication_quality', name: 'Communication Quality', weight: 10, description: 'Crew rating' },
      { id: 'afterhours_coverage', name: 'After-Hours Coverage Performance', weight: 10, description: 'Missed calls/texts count' },
      { id: 'system_reliability', name: 'System Reliability', weight: 10, description: 'Downtime/incidents count' }
    ]
  },
  'dispatcher': {
    title: 'Dispatcher',
    available: true,
    kpis: [
      { id: 'ontime_dispatch', name: 'On-Time Dispatch Rate', weight: 10, description: '% on-time' },
      { id: 'communication_accuracy', name: 'Communication Accuracy', weight: 10, description: 'Wrong info incidents count' },
      { id: 'load_ticket_accuracy', name: 'Load/Ticket Documentation Accuracy', weight: 10, description: '% accurate' },
      { id: 'customer_communication', name: 'Customer Communication Quality', weight: 10, description: 'Rating/complaints count' },
      { id: 'driver_satisfaction', name: 'Driver Satisfaction', weight: 10, description: 'Rating' },
      { id: 'route_optimization', name: 'Route Optimization', weight: 10, description: 'Avoidable deadhead miles %' },
      { id: 'error_correction', name: 'Error Correction Speed', weight: 10, description: 'Avg minutes' },
      { id: 'response_time', name: 'Response Time', weight: 10, description: 'Avg minutes' },
      { id: 'training_participation', name: 'Training Participation', weight: 10, description: '% complete' },
      { id: 'shift_handoff', name: 'Shift Handoff Quality', weight: 10, description: 'Missed details count' }
    ]
  },
  'sales_account_manager': {
    title: 'Sales / Account Manager',
    available: true,
    kpis: [
      { id: 'revenue_growth', name: 'Revenue Growth', weight: 10, description: '% growth' },
      { id: 'customer_retention', name: 'Customer Retention Rate', weight: 10, description: '% retained' },
      { id: 'new_account_acquisition', name: 'New Account Acquisition', weight: 10, description: 'Count' },
      { id: 'quote_close_ratio', name: 'Quote-to-Close Ratio', weight: 10, description: '% closed' },
      { id: 'customer_satisfaction', name: 'Customer Satisfaction', weight: 10, description: 'Rating' },
      { id: 'margin_per_account', name: 'Margin per Account', weight: 10, description: '% margin' },
      { id: 'sales_pipeline', name: 'Sales Pipeline Accuracy', weight: 10, description: '% accurate' },
      { id: 'contract_renewal', name: 'Contract Renewal Rate', weight: 10, description: '% renewed' },
      { id: 'communication_ops', name: 'Communication with Operations', weight: 10, description: 'Rating' },
      { id: 'training_completion', name: 'Training Completion', weight: 10, description: '% complete' }
    ]
  },

  // ==================== MAINTENANCE / SHOP ====================
  'shop_manager': {
    title: 'Shop Manager',
    available: true,
    kpis: [
      { id: 'equipment_uptime', name: 'Equipment Uptime', weight: 10, description: '% uptime' },
      { id: 'pm_schedule', name: 'PM Schedule Adherence', weight: 10, description: '% on schedule' },
      { id: 'repair_turnaround', name: 'Repair Turnaround Time', weight: 10, description: 'Avg days' },
      { id: 'repeat_repair', name: 'Repeat Repair Rate', weight: 10, description: '% repeat' },
      { id: 'inventory_accuracy', name: 'Inventory Accuracy', weight: 10, description: 'Cycle count pass %' },
      { id: 'cost_per_repair', name: 'Cost per Repair', weight: 10, description: 'Avg $' },
      { id: 'safety_compliance', name: 'Safety Compliance', weight: 10, description: 'Incidents count' },
      { id: 'technician_productivity', name: 'Technician Productivity', weight: 10, description: 'Hours billed vs hours worked' },
      { id: 'shop_cleanliness', name: 'Shop Cleanliness Score', weight: 10, description: 'Weekly audit' },
      { id: 'vendor_performance', name: 'Vendor Performance', weight: 10, description: 'On-time parts %' }
    ]
  },
  'mechanic': {
    title: 'Mechanic',
    available: true,
    kpis: [
      { id: 'repair_turnaround', name: 'Repair Turnaround Time', weight: 10, description: 'Avg time' },
      { id: 'first_time_fix', name: 'First-Time Fix Rate', weight: 10, description: '% fixed first time' },
      { id: 'safety_compliance', name: 'Safety Compliance', weight: 10, description: 'Incidents count' },
      { id: 'documentation_accuracy', name: 'Documentation Accuracy', weight: 10, description: 'Work orders complete %' },
      { id: 'pm_completion', name: 'Preventive Maintenance Completion Rate', weight: 10, description: '% complete' },
      { id: 'comeback_rate', name: 'Comeback Rate', weight: 10, description: 'Repeat issue within 30 days count' },
      { id: 'tool_care', name: 'Tool Care / Loss', weight: 10, description: '$ lost' },
      { id: 'attendance_punctuality', name: 'Attendance & Punctuality', weight: 10, description: '% on-time' },
      { id: 'training_participation', name: 'Training Participation', weight: 10, description: '% complete' },
      { id: 'fleet_readiness', name: 'Fleet Readiness Impact', weight: 10, description: 'Down units reduced count' }
    ]
  },
  'shop_hand': {
    title: 'Shop Hand',
    available: true,
    kpis: [
      { id: 'shop_cleanliness', name: 'Shop Cleanliness Score', weight: 18.75, description: 'Weekly audit' },
      { id: 'equipment_prep', name: 'Equipment Prep Accuracy', weight: 6.25, description: 'Ready when needed %' },
      { id: 'tool_organization', name: 'Tool Organization Score', weight: 5, description: 'Weekly audit' },
      { id: 'safety_compliance', name: 'Safety Compliance', weight: 10, description: 'Violations count' },
      { id: 'communication_quality', name: 'Communication Quality', weight: 5, description: 'Mechanic rating' },
      { id: 'task_completion', name: 'Task Completion Rate', weight: 10, description: '% complete' },
      { id: 'attendance_punctuality', name: 'Attendance & Punctuality', weight: 5, description: '% on-time' },
      { id: 'material_handling', name: 'Material Handling Accuracy', weight: 5, description: 'Parts staged correctly %' },
      { id: 'inventory_tracking', name: 'Track Inventory', weight: 5, description: 'Inventory accuracy' },
      { id: 'training_participation', name: 'Training Participation', weight: 15, description: '% complete' },
      { id: 'support_efficiency', name: 'Support Efficiency', weight: 15, description: 'Avg turnaround on requests' }
    ]
  },

  // ==================== OFFICE / ADMIN ====================
  'office_manager': {
    title: 'Office Manager',
    available: true,
    kpis: [
      { id: 'task_sla', name: 'Office Task SLA', weight: 10, description: 'Requests closed on time %' },
      { id: 'document_accuracy', name: 'Document Accuracy', weight: 10, description: 'Errors count' },
      { id: 'policy_compliance', name: 'Policy Compliance', weight: 10, description: 'Audit pass %' },
      { id: 'vendor_management', name: 'Vendor Management', weight: 10, description: 'On-time payments % / disputes count' },
      { id: 'internal_satisfaction', name: 'Internal Customer Satisfaction', weight: 10, description: 'Staff rating' },
      { id: 'budget_adherence', name: 'Budget Adherence', weight: 10, description: 'Variance %' },
      { id: 'record_retention', name: 'Record Retention Accuracy', weight: 10, description: 'Files complete %' },
      { id: 'training_completion', name: 'Training Completion Rate', weight: 10, description: '% complete' },
      { id: 'payroll_billing_support', name: 'Payroll/Billing Support Turnaround', weight: 10, description: 'Avg days' },
      { id: 'communication_timeliness', name: 'Communication Timeliness', weight: 10, description: 'Response SLA' }
    ]
  },
  'billing_ar': {
    title: 'Billing / AR',
    available: true,
    kpis: [
      { id: 'dso', name: 'Days Sales Outstanding (DSO)', weight: 10, description: 'Days' },
      { id: 'invoice_accuracy', name: 'Invoice Accuracy', weight: 10, description: '% no rework' },
      { id: 'billing_timeliness', name: 'Billing Timeliness', weight: 10, description: 'Days from job to invoice' },
      { id: 'collection_rate', name: 'Collection Rate', weight: 10, description: '% collected' },
      { id: 'dispute_rate', name: 'Dispute Rate', weight: 10, description: '% invoices disputed' },
      { id: 'dispute_resolution', name: 'Dispute Resolution Time', weight: 10, description: 'Avg days' },
      { id: 'data_entry_accuracy', name: 'Data Entry Accuracy', weight: 10, description: 'Audit pass %' },
      { id: 'customer_communication', name: 'Customer Communication SLA', weight: 10, description: 'Response time' },
      { id: 'reporting_accuracy', name: 'Reporting Accuracy', weight: 10, description: 'Month-end errors count' },
      { id: 'write_offs', name: 'Write-offs / Bad Debt', weight: 10, description: '%' }
    ]
  },
  'payroll_hr_admin': {
    title: 'Payroll / HR Admin',
    available: true,
    kpis: [
      { id: 'payroll_accuracy', name: 'Payroll Accuracy', weight: 10, description: '% error-free' },
      { id: 'ontime_payroll', name: 'On-Time Payroll Processing', weight: 10, description: '% on-time' },
      { id: 'employee_file_completeness', name: 'Employee File Completeness', weight: 10, description: '% compliant' },
      { id: 'onboarding_time', name: 'Onboarding Completion Time', weight: 10, description: 'Avg days' },
      { id: 'training_completion', name: 'Training Completion Rate', weight: 10, description: '% complete' },
      { id: 'policy_acknowledgment', name: 'Policy Acknowledgment Compliance', weight: 10, description: '% acknowledged' },
      { id: 'turnover_tracking', name: 'Turnover Tracking Accuracy', weight: 10, description: 'Reports on time %' },
      { id: 'discipline_documentation', name: 'Incident/Discipline Documentation Completeness', weight: 10, description: '% complete' },
      { id: 'response_time', name: 'Response Time to Employee Requests', weight: 10, description: 'Avg time' },
      { id: 'confidentiality', name: 'Confidentiality / Access Compliance', weight: 10, description: 'Violations count' }
    ]
  }
};

// Universal rating rubric tied to disciplinary actions
const RATING_RUBRIC = [
  { score: 5, label: 'No issues' },
  { score: 4, label: '1 Verbal Warning' },
  { score: 3, label: '2 Verbal Warnings' },
  { score: 2, label: '3 Verbals or 1 Written' },
  { score: 1, label: 'PIP or Termination Review' }
];

// Compensation calculator tiers
const COMPENSATION_TIERS = [
  { min: 0, max: 59, raisePercent: 0, bonusPercent: 0 },
  { min: 60, max: 64, raisePercent: 1, bonusPercent: 1 },
  { min: 65, max: 69, raisePercent: 2, bonusPercent: 2 },
  { min: 70, max: 74, raisePercent: 3, bonusPercent: 3 },
  { min: 75, max: 79, raisePercent: 4, bonusPercent: 4 },
  { min: 80, max: 100, raisePercent: 5, bonusPercent: 5 }
];

// ============================================
// NEW HIRE CHECKLIST ITEMS
// ============================================
const CHECKLIST_ITEMS = [
  // Compliance / Paperwork
  { id: 'online_application', label: 'Online Application', category: 'Compliance' },
  // TASK-498 (Domain reviewer) — 49 CFR §382.701 requires a Drug & Alcohol
  // Clearinghouse pre-employment query BEFORE a CDL driver operates a CMV.
  // Added to the canonical checklist so the printed packet doesn't omit a
  // mandatory federal step. Existing hrChecklists docs default it to unchecked.
  { id: 'clearinghouse_query', label: 'FMCSA Clearinghouse Query (CDL)', category: 'Compliance' },
  { id: 'drug_test', label: 'Drug Test', category: 'Compliance' },
  { id: 'background_check', label: 'Background Check', category: 'Compliance' },
  { id: 'i9', label: 'I-9 Form', category: 'Compliance' },
  { id: 'w4', label: 'W-4 Form', category: 'Compliance' },
  { id: 'direct_deposit', label: 'Direct Deposit Setup', category: 'Compliance' },
  { id: 'cdl_verified', label: 'CDL Verified', category: 'Compliance' },
  { id: 'dot_physical', label: 'DOT Physical', category: 'Compliance' },
  // Training & Equipment
  { id: 'safety_training', label: 'Safety Training', category: 'Training' },
  { id: 'uniform', label: 'Uniform Issued', category: 'Training' },
  { id: 'equipment_assigned', label: 'Equipment Assigned', category: 'Training' },
  // App/System Setup
  { id: 'dispatchdeck_setup', label: 'DispatchDeck App Setup', category: 'Systems' },
  { id: 'compliance_portal', label: 'Compliance Portal Setup', category: 'Systems' },
  // TASK-515 — Benefits & Insurance. Kept as its OWN category (NOT Compliance):
  // putting insurance next to the 49 CFR §382 DOT items implies a federal gate
  // that doesn't exist (Domain reviewer). Existing hrChecklists docs default
  // these new keys to unchecked (render uses items?.[id] || {completed:false}).
  // Adding two items raises totalItems, so already-100% drivers drop a few % —
  // a "New" tag on the Benefits category header flags this so HR isn't confused.
  { id: 'health_insurance', label: 'Health Insurance', category: 'Benefits & Insurance' },
  { id: 'insurance_eligibility', label: 'Insurance Eligibility Verified', category: 'Benefits & Insurance' }
];

// TASK-515 — categories added after a checklist doc was first created get a
// "New" badge on their header so existing 100%-complete drivers (whose
// completion % drops when totalItems rises) aren't mistaken for regressed.
const CHECKLIST_NEW_CATEGORIES = new Set(['Benefits & Insurance']);

// Map of checklist-item ID → keyword/regex patterns. When an employee document
// is uploaded with a filename matching one of these patterns, the corresponding
// checklist item is auto-checked. Patterns are matched case-insensitively against
// the filename (sans extension). Add new patterns here when checklist items are
// document-bound.
const CHECKLIST_DOC_KEYWORDS = {
  i9: [/\bi[\s_-]?9\b/i],
  w4: [/\bw[\s_-]?4\b/i],
  direct_deposit: [/direct[\s_-]?deposit/i, /\bdd[\s_-]?form\b/i],
  cdl_verified: [/\bcdl\b/i, /commercial[\s_-]?driver/i],
  dot_physical: [/dot[\s_-]?physical/i, /med[\s_-]?card/i, /medical[\s_-]?card/i, /\bmcsa\b/i],
  background_check: [/background[\s_-]?check/i, /\bbgc\b/i],
  drug_test: [/drug[\s_-]?(test|screen)/i],
  online_application: [/\bapplication\b/i],
  safety_training: [/safety[\s_-]?(training|cert)/i, /\bosha\b/i],
};

function findChecklistItemForFilename(filename) {
  if (!filename) return null;
  const stem = filename.replace(/\.[a-z0-9]+$/i, '');
  for (const [itemId, patterns] of Object.entries(CHECKLIST_DOC_KEYWORDS)) {
    if (patterns.some(p => p.test(stem))) return itemId;
  }
  return null;
}

// Dedicated upload slots in the Compliance tab → Onboarding Documents section.
// Each slot binds a checklist item to a per-person field on the people doc.
// Slots that already have first-class sections (CDL, med card, drug tests,
// background check) are intentionally excluded — they live in their own UI.
const ONBOARDING_DOC_SLOTS = [
  { itemId: 'online_application', label: 'Online Application',  builtin: true, fieldUrl: 'onlineApplicationDocumentUrl', fieldStoragePath: 'onlineApplicationStoragePath', fieldUploadedAt: 'onlineApplicationUploadedAt' },
  { itemId: 'i9',                 label: 'I-9 Form',             builtin: true, fieldUrl: 'i9DocumentUrl',                fieldStoragePath: 'i9StoragePath',                fieldUploadedAt: 'i9UploadedAt' },
  { itemId: 'w4',                 label: 'W-4 Form',             builtin: true, fieldUrl: 'w4DocumentUrl',                fieldStoragePath: 'w4StoragePath',                fieldUploadedAt: 'w4UploadedAt' },
  { itemId: 'direct_deposit',     label: 'Direct Deposit Form',  builtin: true, fieldUrl: 'directDepositDocumentUrl',     fieldStoragePath: 'directDepositStoragePath',     fieldUploadedAt: 'directDepositUploadedAt' },
  { itemId: 'safety_training',    label: 'Safety Training Cert', builtin: true, fieldUrl: 'safetyTrainingDocumentUrl',    fieldStoragePath: 'safetyTrainingStoragePath',    fieldUploadedAt: 'safetyTrainingUploadedAt' },
];

// TASK-221 Slice 1 — Per-tenant hiring packet config. Reads from
// `tenants/{id}/settings.hiringPacket`; falls back to ONBOARDING_DOC_SLOTS
// hardcoded defaults so existing tenants (no setting yet) see no change.
// Settings UI lives in app.js (see HP_BUILTIN_ITEMS / HP_STARTER_LIBRARY).
//
// Custom items don't have hardcoded fieldUrl/fieldStoragePath/fieldUploadedAt
// names — their docs go in a single nested map `customPacketDocs.<itemId>`
// on the people doc (predictable schema, easy to migrate to sub-collection
// later per Slice 1.5+ COD #1). Resolution helper below returns the
// effective URL/path/uploadedAt regardless of which storage shape the item
// uses.
function getHiringPacketItems() {
  const stored = window.AppState?.settings?.hiringPacket;
  if (Array.isArray(stored) && stored.length) {
    return stored.filter(i => i && i.archived !== true);
  }
  return ONBOARDING_DOC_SLOTS;
}

// Resolve the effective doc URL / storagePath / uploadedAt for a packet item
// regardless of whether it's built-in (top-level fields) or custom (nested map).
function getHiringPacketDocFor(emp, item) {
  if (!item) return { url: null, storagePath: null, uploadedAt: null };
  if (item.fieldUrl) {
    return {
      url: emp[item.fieldUrl] || null,
      storagePath: emp[item.fieldStoragePath] || null,
      uploadedAt: emp[item.fieldUploadedAt] || null,
    };
  }
  // Custom item — read from customPacketDocs nested map
  const map = emp.customPacketDocs || {};
  const entry = map[item.itemId] || {};
  return {
    url: entry.url || null,
    storagePath: entry.storagePath || null,
    uploadedAt: entry.uploadedAt || null,
  };
}

// HR Resources — loaded from Firestore per tenant (tenants/{tenantId}/hrResources)
// Supports both links and uploaded documents. Users add their own via UI.
// Stored on window to survive duplicate module instances from SW cache
window._hrResources = window._hrResources || [];
let HR_RESOURCES = []; // local alias, kept in sync

// TASK-419 — "Free Compliance Library": a STATIC, hardcoded set of curated official
// .gov reference links, rendered as a distinct read-only section in HR Documents (NOT
// seeded into any tenant's hrResources — COD consensus: no DB writes, no idempotency
// problem, demo parity automatic, no rules change). Every .gov URL web-verified live
// 2026-06-02 (CLAUDE.md URL-discipline rule). Durable landing/topic pages, not deep PDFs.
const HR_COMPLIANCE_LIBRARY = [
  { category: 'Wages & Hours',       icon: 'payments',          title: 'Fair Labor Standards Act (FLSA)',        desc: 'Minimum wage, overtime, recordkeeping & child-labor rules.', url: 'https://www.dol.gov/agencies/whd/flsa' },
  { category: 'Wages & Hours',       icon: 'payments',          title: 'Family & Medical Leave Act (FMLA)',      desc: 'Job-protected leave: eligibility, coverage, employer guide.', url: 'https://www.dol.gov/agencies/whd/fmla' },
  { category: 'Required Postings',   icon: 'campaign',          title: 'Federal Workplace Posters',              desc: 'Which DOL posters you must display — free to download.',      url: 'https://www.dol.gov/general/topics/posters' },
  { category: 'Anti-Discrimination', icon: 'balance',           title: 'Prohibited Employment Practices (EEOC)', desc: 'What counts as illegal discrimination in hiring, pay, discipline.', url: 'https://www.eeoc.gov/prohibited-employment-policiespractices' },
  { category: 'Anti-Discrimination', icon: 'balance',           title: 'Workplace Harassment (EEOC)',            desc: 'Unlawful-harassment definition & employer responsibilities.',  url: 'https://www.eeoc.gov/harassment' },
  { category: 'DOT / FMCSA',         icon: 'local_shipping',    title: 'Hours of Service (FMCSA)',               desc: 'CDL driver on-duty/driving limits & required rest periods.',   url: 'https://www.fmcsa.dot.gov/regulations/hours-service/summary-hours-service-regulations' },
  { category: 'DOT / FMCSA',         icon: 'local_shipping',    title: 'Drug & Alcohol Clearinghouse (FMCSA)',   desc: 'CDL driver drug/alcohol violation database — queries & reporting.', url: 'https://clearinghouse.fmcsa.dot.gov/' },
  { category: 'Workplace Safety',    icon: 'health_and_safety', title: 'OSHA for Small Business',                desc: 'Free on-site consultation, safety-program steps, checklists.', url: 'https://www.osha.gov/smallbusiness' },
  { category: 'Workplace Safety',    icon: 'health_and_safety', title: 'OSHA Help for Employers',                desc: 'How to comply with OSHA standards & beyond.',                  url: 'https://www.osha.gov/employers' },
  { category: 'Payroll & Tax',       icon: 'receipt_long',      title: "Employer's Tax Guide (IRS Pub 15)",      desc: 'Withholding, depositing, reporting & paying employment taxes.', url: 'https://www.irs.gov/forms-pubs/about-publication-15' },
];

// Renders the static Free Compliance Library section. Read-only, distinct green accent
// (vs the blue tenant-doc cards) so "system reference" never blurs with "what I uploaded".
// Reuses the styled .hr-resource-card structure; links open in a new tab with rel=noopener
// noreferrer (Code Reviewer — tabnabbing guard). Static UI → renders in demo automatically.
function _hrComplianceLibraryHTML() {
  const cards = HR_COMPLIANCE_LIBRARY.map(l => `
      <a href="${_safeHttpUrl(l.url)}" target="_blank" rel="noopener noreferrer" class="hr-resource-card" style="position:relative;border-left:3px solid #10B981;" title="${escapeHtml(l.url)} — opens in a new tab">
        <div class="hr-resource-icon" style="background:#10B98115;color:#10B981;">
          <span class="material-icons">${escapeHtml(l.icon)}</span>
        </div>
        <div class="hr-resource-info" style="min-width:0;">
          <div class="hr-resource-name" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(l.title)}</div>
          <div class="hr-resource-desc" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(l.desc)}</div>
          <div class="hr-resource-category-inline" title="${escapeHtml(l.category)}">
            <span class="material-icons" style="font-size:11px;vertical-align:middle;margin-right:3px;">verified_user</span>${escapeHtml(l.category)}
          </div>
        </div>
        <span class="material-icons hr-resource-external">open_in_new</span>
      </a>`).join('');
  // Collapsible + collapsed by default (Chris 2026-06-03): the .gov reference
  // links are useful-but-secondary, so they shouldn't push the tenant's own
  // documents down the page. Header toggles; body hidden until clicked.
  return `
    <div class="hr-section hr-resources-section">
      <div class="hr-resources-header" role="button" tabindex="0" aria-expanded="false" aria-controls="hr-complib-body"
           onclick="var b=document.getElementById('hr-complib-body'),c=document.getElementById('hr-complib-chev');var o=b.style.display==='none';b.style.display=o?'block':'none';c.textContent=o?'expand_less':'expand_more';this.setAttribute('aria-expanded',o);"
           onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();this.click();}"
           style="cursor:pointer;user-select:none;display:flex;align-items:center;justify-content:space-between;gap:8px;">
        <h2 class="hr-section-title" style="margin:0;">
          <span class="material-icons" style="color:#10B981;">verified_user</span>
          Free Compliance Library
          <span style="font-size:0.68rem;font-weight:600;background:#10B98115;color:#059669;padding:2px 9px;border-radius:10px;margin-left:8px;vertical-align:middle;">Official .gov sources</span>
        </h2>
        <span class="material-icons" id="hr-complib-chev" style="color:var(--text-secondary,#64748b);" aria-hidden="true">expand_more</span>
      </div>
      <div id="hr-complib-body" style="display:none;">
        <p style="margin:8px 0 12px;font-size:0.85rem;color:var(--text-secondary,#64748b);">External federal reference links (DOL, EEOC, FMCSA, OSHA, IRS), provided for convenience. These are government resources — <strong>not</strong> your company's policies.</p>
        <div class="hr-resources-grid">${cards}</div>
      </div>
    </div>`;
}
let HR_EXTERNAL_LINKS = [];
let HR_DOC_CHAT_HISTORY = []; // Smart Assistant chat history
// TASK-9301 — mirrors HR_DOCS_MAX_QUESTION_CHARS in functions/src/index.js (tripwire-checked).
const HR_DOC_MAX_QUESTION_CHARS = 2000;

// ============================================
// HIRING / APPLICANT TRACKING CONSTANTS
// ============================================
const PIPELINE_STAGES = [
  { id: 'new', label: 'New', icon: 'fiber_new', color: '#3B82F6' },
  { id: 'reviewing', label: 'Reviewing', icon: 'visibility', color: '#8B5CF6' },
  { id: 'phone_screen', label: 'Phone Screen', icon: 'phone', color: '#06B6D4' },
  { id: 'interview', label: 'Interview', icon: 'groups', color: '#F59E0B' },
  { id: 'offer', label: 'Offer', icon: 'handshake', color: '#10B981' },
  { id: 'hired', label: 'Hired', icon: 'check_circle', color: '#059669' },
  { id: 'rejected', label: 'Rejected', icon: 'cancel', color: '#EF4444' },
  { id: 'withdrawn', label: 'Withdrawn', icon: 'undo', color: '#6B7280' },
  { id: 'potentials', label: 'Potentials', icon: 'star', color: '#D97706' }
];

// ── TASK-1360 — DISPLAY-ONLY stages (never members of PIPELINE_STAGES) ───────────────────────
// The same root gap the partner portal had (compliance-partner.js _stageForCandidate): call
// sites resolved a stage with `PIPELINE_STAGES.find(s => s.id === c.status) || PIPELINE_STAGES[0]`
// — and PIPELINE_STAGES[0] is 'New'. So an applicant who started the public application and
// never submitted it (`status:'draft'` — apply.html:5593) badged "New" here too, reading as a
// real application. Fix both files or the two surfaces disagree about the same candidate.
//
// 🔴 'applied' IS THE SUBMITTED STATUS and is NOT in PIPELINE_STAGES (apply.html writes
// 'applied' on submit; functions/src/index.js:29754 defaults to it). It maps to 'new' here,
// exactly as the partner portal does — WITHOUT that mapping every submitted application would
// badge "Incomplete / Other". These stages are display-only: they are never written to a doc and
// never offered in a status <select>.
const HR_STAGE_DRAFT = { id: 'draft', label: 'Application not submitted', icon: 'edit_note', color: '#9CA3AF', _display: true };
const HR_STAGE_OTHER = { id: '_other', label: 'Incomplete / Other', icon: 'help_outline', color: '#9CA3AF', _display: true };
function stageForCandidate(c) {
  const raw = String((c && c.status) || 'new').toLowerCase();
  const id = raw === 'applied' ? 'new' : raw;
  const found = PIPELINE_STAGES.find(s => s.id === id);
  if (found) return found;
  return id === 'draft' ? HR_STAGE_DRAFT : HR_STAGE_OTHER;
}

const REJECTION_REASONS = [
  'Not enough experience',
  'Failed background check',
  'Failed drug screen',
  'No CDL',
  'No show to interview',
  'Position filled',
  'Salary expectations too high',
  // TASK-1212 — oilfield-common logistics fallout (all NON-report reasons:
  // none of these match the shared report-based regex, so they take the
  // plain-decline + reportWasNotBasis stamp path, never the AA hard-route).
  'No transportation',
  'Needs housing / relocation',
  'Location / commute',
  'Not a good fit',
  'Candidate declined',
  'Other'
];

// TASK-1152 — a rejection is "report-based" (FCRA §615 adverse action applies)
// when the consumer report is a factor. These reasons + keywords HARD-ROUTE to
// the Adverse Action wizard — the user cannot decline them as "not based on the
// report" (Domain/Adversary COD: self-attest alone is a compliance bypass). Any
// OTHER reason on a report-bearing candidate is a plain decline, and we stamp
// `reportWasNotBasis` from that FACT (non-report reason chosen), not a checkbox.
// TASK-1155 — the list + regex moved to the shared module imported above (ONE
// source with the server fence). This wrapper keeps every existing call site.
function _isReportBasedRejectionReason(reason) {
  return _sharedIsReportBasedRejectionReason(reason);
}

const CANDIDATE_SOURCES = [
  { id: 'indeed', label: 'Indeed' },
  { id: 'referral', label: 'Referral' },
  { id: 'walkin', label: 'Walk-in' },
  { id: 'website', label: 'Website' },
  { id: 'craigslist', label: 'Craigslist' },
  { id: 'other', label: 'Other' }
];

const CDL_ENDORSEMENTS = [
  { id: 'H', label: 'H - Hazmat' },
  { id: 'T', label: 'T - Doubles/Triples' },
  { id: 'N', label: 'N - Tank' },
  { id: 'P', label: 'P - Passenger' },
  { id: 'S', label: 'S - School Bus' },
  { id: 'X', label: 'X - Hazmat + Tank' }
];

// ============================================
// SAFETY EQUIPMENT CONSTANTS
// ============================================
// TASK-9883 — fixed doc ids, so two tabs (or two people) seeding at once write
// the SAME nine docs instead of two sets. Never rename an id: a tenant's
// existing default docs are found by it.
const DEFAULT_SAFETY_EQUIPMENT_TYPES = [
  { id: 'default-hard-hat', name: 'Hard Hat', hasSize: false },
  { id: 'default-safety-glasses', name: 'Safety Glasses', hasSize: false },
  { id: 'default-gloves', name: 'Gloves', hasSize: true },
  { id: 'default-hi-vis-vest', name: 'Hi-Vis Vest', hasSize: true },
  { id: 'default-steel-toe-boots', name: 'Steel Toe Boots', hasSize: true },
  { id: 'default-ear-plugs', name: 'Ear Plugs', hasSize: false },
  { id: 'default-face-shield', name: 'Face Shield', hasSize: false },
  { id: 'default-respirator', name: 'Respirator', hasSize: true },
  { id: 'default-fall-protection-harness', name: 'Fall Protection Harness', hasSize: true }
];

const EQUIPMENT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];

// ============================================
// HR STATE
// ============================================
const HRState = {
  tenantId: null,
  people: [],
  supervisors: [],
  hrForms: [],
  // TASK-1140 Slice 1 — Incident & Accident Reports (capture). Live list +
  // subscribe flag + staged photo Files (uploaded on save; path-only storage).
  incidents: [],
  incidentsSubscribed: false,
  pendingIncidentPhotos: [],
  hrChecklists: [],
  hrReviews: [],
  timeOffRequests: [],
  // TASK-9535 — the all-employee balance table. TASK-9601 folded the old
  // by-employee filter / grouping fields (timeOffFilterPersonId,
  // timeOffGroupByPerson) into timeOffFilters below: ONE versioned, per-tenant
  // localStorage blob (the search text is never persisted).
  timeOffFilters: null,        // loaded lazily by _timeOffFilters()
  timeOffFiltersTenant: null,  // tenant the loaded blob belongs to
  timeOffSearch: '',           // live search box text — session only
  timeOffIndexCache: null,     // { src, index } — "Also off" day index, rebuilt per snapshot
  timeOffBalancesOpen: false,
  timeOffClockHoursById: null, // { personId: hoursThisYear } — only loaded when a level accrues per hours worked; reloaded on every Balances open
  timeOffClockHoursError: false, // last clock-hours load failed — table + print carry a banner, next open retries
  timeOffClockGen: 0, // bumped on every open / print / HR entry; a load from an older gen is discarded
  timeOffClockPromise: null, // the one in-flight clock-hours load (snapshots join it instead of re-querying)
  currentView: 'landing', // 'landing', 'disciplinary', 'checklist', 'checklist-view', 'reviews', 'safety'
  selectedEmployee: null,
  selectedChecklist: null,
  selectedReview: null,
  editingReviewId: null,
  // TASK-398: stale-async-write guard. editReview prefills the SHARED #modal-review
  // form via setTimeout, with a NESTED setTimeout that writes KPI scores BY INDEX
  // (#kpi-score-N) from a captured review. Without a guard, editReview(A) then
  // editReview(B) within ~250ms lets A's nested timer stamp A's index-keyed scores
  // onto B's freshly-rendered KPI rows (currentReviewKpis is module-shared) → saveReview
  // writes to editingReviewId(=B) → review B corrupted with A's scores/comp. Index-keyed
  // writes make this worse than JSA: no per-row record check. reviewFormGen is bumped
  // on every review-form open/close; both delayed writes capture it and abort if it moved.
  reviewFormGen: 0,
  _reviewPrefillTimers: [],
  // Hiring / Applicant Tracking
  candidates: [],
  // TASK-9293 — `view` replaces TASK-650's showArchived: the list shows ONE bucket at a time
  // (active | hired | closed | archived — see HR_CANDIDATE_VIEWS / candidateListView).
  candidateFilter: { status: null, search: '', view: 'active', sort: 'date' }, // TASK-1101 — sort: action|name|date. TASK-1215 — default flipped to 'date' (newest applied first, Chris 2026-07-16)
  selectedCandidate: null,
  editingCandidateId: null,
  pendingResumeFile: null,
  candidateSubscribed: false,
  // TASK-368 (2026-05-28): distinguish "loading" from "no candidates" — was a
  // first-paint lie where the empty state + stats pills at 0 rendered before
  // the first onSnapshot landed. Flips to true on first success OR error.
  candidatesLoaded: false,
  // Map of candidateId → { bgDecision, bgDecisionHistory[] } for the
  // candidate-list "last decision" pill (v1.15.491). Loaded lazily on first
  // Hiring sub-view open, then live-subscribed so flips reflect without reload.
  bgRecordsByCandidateId: {},
  _bgLoadedTid: null,            // TASK-9444 slice 2 — tenant whose bg records have LOADED (a missing record then means "none")
  bgRecordsSubscribed: false,
  // TASK-635 — recovery state for the candidates / peopleBackgroundChecks
  // onSnapshot listeners. After the PWA is backgrounded/idle, Firestore can
  // fire the snapshot ERROR callback (stale long-poll / auth-token-refresh
  // race / `unavailable`). Previously that path mislabeled EVERY error as
  // "no candidates yet", wiped the list to [] and never re-armed → the
  // Applicants list went permanently empty and re-entering the view didn't
  // help (guarded by candidateSubscribed which was never reset). Now: keep
  // last-known data, show a recoverable retry state, and re-subscribe with
  // capped backoff. These hold the live unsub + the pending retry timer so a
  // re-arm tears down the errored listener FIRST (no dup listeners — the
  // 2026-05-28 dup-listener incident) and debounces a double-tap race.
  candidatesError: false,        // true while a real listener error is unresolved (drives retry UI)
  bgRecordsError: false,
  _candUnsub: null,              // live candidates unsub (so a re-arm can kill the errored one)
  // TASK-9423 — applicant flags (tenants/{tid}/candidateFlags ledger, written by the partner).
  _candFlagsUnsub: null,
  _candFlagsTid: null,
  candidateFlagRows: [],
  candidateFlagMap: {},
  _candRetryTimer: null,         // pending setTimeout for candidates re-subscribe
  _candRetryAttempt: 0,          // backoff index into HR_LISTENER_BACKOFF_MS
  _candRearming: false,          // dedup guard against a double re-arm (visibility + error racing)
  _bgUnsub: null,
  _bgRetryTimer: null,
  _bgRetryAttempt: 0,
  _bgRearming: false,
  _hrVisibilityHooked: false,    // whether the visibility/focus re-arm listeners are installed
  // TASK-629 — single employment-status dropdown (Active / Inactive / Terminated / All),
  // matching the roster People list (TASK-626). showInactive/showTerminated are derived
  // from this by setDirectoryStatus() so the existing render + CSV-export read sites are
  // untouched. Default 'active' preserves the prior toggle default (both off → only active).
  directoryStatus: 'active',
  showInactive: false,
  showTerminated: false,
  // TASK-582 — compliance-tile filter on the employee directory.
  // 'cdl' | 'medCard' | 'clearinghouse' | 'annualReview' | null. Set by the
  // dashboard tiles, ANDs with search/position/toggles, cleared on ✕, on
  // manual search/position change, and on HR view re-enter (initHR).
  directoryComplianceFilter: null,
  timeOffPolicy: null,
  unsubscribers: [],
  // Employee Documents
  employeeDocuments: [],
  employeeDocsLoaded: null,
  uploadingDocument: false,
  // TASK-1381 — self-serve packetDocs cache (driver hiring-packet link uploads)
  packetDocs: [],
  packetDocsFor: null,
  // Safety Sign-In
  // TASK-4883 — EVERY sheet the tenant has ever had, not "the latest 30 days".
  // The comment that used to say 30 days was wrong and the query is right; see
  // the subscription in subscribeToHRData() for why that distinction is
  // load-bearing (Chris: keep the record available for at least 6 months).
  safetySignIns: [],
  safetySelectedDate: null, // Currently selected date for viewing
  // JSA Reports (loaded on-demand in Safety sub-view)
  jsaReports: [],
  jsaSubscribed: false,
  // TASK-9581 — the JSA list is a date-bounded, paged window, not the archive.
  jsaRangeKey: null,     // start|end ms of the window the live listener holds
  jsaLimit: 0,           // page size grows by JsaListWindow.PAGE_SIZE on "Load more"
  jsaLimitHit: false,    // the window returned a full page — there may be more
  jsaUpFailed: false,    // COD r6 — the after-today (upcoming) listener errored
  jsaPastFailed: false,  // COD r7 — the up-to-today listener errored
  jsaLoaded: false,      // first snapshot of the current window arrived
  jsaUnsub: null,        // current bounded listener
  jsaTenantId: null,
  jsaGen: 0,             // listener generation (stale-snapshot guard)
  jsaTotal: null,        // all-time count (one aggregation), null = unknown
  jsaSeen: new Map(),    // id → last snapshot of every JSA a window delivered (detail / print-one fallback)
  jsaOpenReport: null,   // the report in the open detail modal (its Print never loses it)
  // Safety Equipment
  safetyEquipmentTypes: [],
  safetyEquipmentRecords: [],
  // TASK-9720 — Roster items whose count the PPE stock ledger keeps
  // (assets where stockTracked == true). Read-only here; every change is a
  // callable. safetyInventory (HR purchase lots) is retired.
  stockAssets: [],
  stockAssetsLoaded: false,
  safetyEquipmentSubscribed: false,
  // TASK-1578 — `subscribed` means "listener armed" (flips synchronously);
  // `loaded` means "a snapshot has landed". The person-card Issued Equipment
  // section puts a money total under this list, so an empty list before the
  // first snapshot must read as "loading", and a REFUSED read must read as
  // "no access" — never as "this employee holds nothing."
  safetyEquipmentLoaded: false,
  safetyEquipmentDenied: false,
  safetyMode: 'user',
  _safetySearch: '', // TASK-840 — serial #/notes search query (user-mode Safety Equipment panel)
  // DVIR Accountability flags (real-time from dvirAccountability collection)
  dvirFlags: [],
  // JSA Accountability flags (real-time from jsaAccountability collection)
  jsaFlags: [],
  safetyManagerTab: 'people',
  // TASK-9452 — PPE Reports tab filters (preset/custom range, person, item, view).
  ppeReport: null,
  // Fire Ext Inspections
  fireExtInspections: [],
  fireExtSubscribed: false,
  // Drug tests (own subscription — no dependency on ComplianceModule)
  drugTests: [],
};

// ============================================
// INITIALIZE HR VIEW
// ============================================
export function initHR(container, tenantId) {
  console.log('[HR] Initializing for tenant:', tenantId);

  // Guard against null tenant
  if (!tenantId) {
    console.error('[HR] No tenant ID provided');
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">⚠️</div>
        <div class="empty-state-title">Account Setup Issue</div>
        <div class="empty-state-text">
          Unable to load HR module. Please try logging out and back in.
        </div>
        <button class="btn btn-primary" onclick="window.handleLogout()">
          <span class="material-icons">logout</span> Log Out
        </button>
      </div>
    `;
    return () => {};
  }

  // TASK-10142 (COD r4) — a company switch must not leave the previous company's
  // people in HRState until the new snapshot lands (the memo picker, among
  // others, reads it). Same company = keep them: backToHRLanding re-runs initHR.
  if (HRState.tenantId && HRState.tenantId !== tenantId) { HRState.people = []; HRState.supervisors = []; }
  HRState.tenantId = tenantId;
  _hrBindCdlisEntry(); // TASK-4549 — one-time delegated binding for the CDLIS entry controls
  HRState.currentView = 'landing';
  HRState.peopleLoaded = false;
  // TASK-9535 — the landing markup below is rebuilt with the Balances panel
  // closed; the state must agree or the first click closes an invisible panel
  // (COD round 2). Any clock-hours load still in flight is orphaned by the gen bump.
  HRState.timeOffBalancesOpen = false;
  HRState.timeOffClockHoursById = null;
  HRState.timeOffClockHoursError = false;
  HRState.timeOffClockGen = (HRState.timeOffClockGen || 0) + 1;
  HRState.timeOffClockPromise = null;
  // TASK-582 — never let a compliance tile filter silently persist across view entries.
  HRState.directoryComplianceFilter = null;
  // TASK-445: re-arm the Company Documents collapse-once seed on each view entry
  // (backToHRLanding routes through initHR, so this covers first-land + return).
  _companyDocsCollapseSeeded.value = false;
  // TASK-484 #2: re-arm the "docs not loaded yet" flag so the section shows a
  // compact header spinner + stays deterministically collapsed during the load
  // window instead of flashing open with a big grid spinner, then expand→collapse.
  _hrResourcesLoaded.value = false;

  // Compliance-only tenants: go straight to Hiring/Applicants (only unlocked HR feature)
  if (window.AppState?.tenantData?.complianceOnly) {
    HRState.currentView = 'hiring';
    container.innerHTML = getHRHTML();
    subscribeToData(tenantId);
    subscribeToCandidates(tenantId);
    const hrContent = document.getElementById('hr-content');
    if (hrContent) {
      hrContent.innerHTML = getHiringSubViewHTML();
      renderCandidatesList();
    }
    return () => {
      HRState.unsubscribers.forEach(unsub => unsub());
      HRState.unsubscribers = [];
      // TASK-635 — reset candidate/bg subscribe flags + pending retry timers +
      // visibility hook so re-init re-arms cleanly (the guard previously stayed
      // true → a dead listener was never re-subscribed on view re-entry).
      _teardownCandidateListeners();
      _teardownSafetyEquipmentListener(); // TASK-1578 — same class of dead-guard
    };
  }

  // Render the HR UI
  container.innerHTML = getHRHTML();

  // Subscribe to real-time data
  subscribeToData(tenantId);

  // TASK-346 reframe — toggle the "Add Employee Note" Action card visibility
  // based on signed-in role (admin/owner) or supervisor status
  // (AppState.hasDirectReports). Safe to call even if AppState isn't ready yet
  // — falls back to hidden, which is the default.
  applyAddEmployeeNoteGate();

  // TASK-1219 — fill the Actions section's upcoming-interviews strip (async,
  // best-effort; hidden for members who can't read candidates).
  _loadUpcomingInterviews().catch(() => {});

  // TASK-1482 — inbound §391.23 action queue (async, best-effort; hidden for
  // members who can't read sphInquiries).
  _loadInboundSphInquiries(tenantId).catch(() => {});

  // Return cleanup function
  return () => {
    HRState.unsubscribers.forEach(unsub => unsub());
    HRState.unsubscribers = [];
    // TASK-635 — reset candidate/bg subscribe flags + pending retry timers +
    // visibility/focus re-arm hook (logout calls this via AppState.viewCleanup,
    // so this is also where the visibility listeners are removed for a
    // signed-out session).
    _teardownCandidateListeners();
    _teardownSafetyEquipmentListener(); // TASK-1578 — same class of dead-guard
    ['disc-issue','disc-pip-expectations','review-notes']
      .forEach(id => window.SmartAssistant?.unregister(id));
  };
}

// ============================================
// HTML TEMPLATE
// ============================================
function getHRHTML() {
  return `
    <div class="hr-view">
      <!-- Header -->
      <div class="view-header">
        <div class="view-title">
          <h1>Human Resources</h1>
        </div>
        ${''/* TASK-1511 (folded in) — HR & Recruiting Settings saves via a direct
             updateDoc on tenants/{tid} (app.js:20410 → :20063), which
             firestore.rules:950 admin-gates. Rendering it for every HR viewer meant
             a scoped member could walk the whole settings modal and eat a raw save
             failure. Gate on the SAME effective-admin check the rule mirrors; the
             local `typeof` fallback keeps owner/legacy open if app.js's helper
             hasn't loaded yet (same convention as _hrCanSeeSecureTax above). */}
        ${_hrEffectiveAdminSafe() ? `
        <button class="btn btn-secondary btn-neon-settings" style="padding:8px 14px;font-size:0.85rem;display:inline-flex;align-items:center;gap:6px;" onclick="window.openHrRecruitingSettingsModal&&window.openHrRecruitingSettingsModal();window.HRModule&&window.HRModule.renderReviewScheduleEditor&&window.HRModule.renderReviewScheduleEditor()" title="HR &amp; Recruiting Settings">
          <span class="material-icons" style="font-size:18px;">settings</span> Settings
        </button>` : ''}
        ${''/* TASK-9449 — a managing compliance partner does not get the admin
             Settings modal (billing / access / every other setting stay the
             carrier's), but DOES get Application Questions: the modal saves
             through executePartnerAction('tenant.setApplicationQuestions'), which
             writes only settings.applicationQuestions and audits old -> new. */}
        ${(!_hrEffectiveAdminSafe() && _hrPartnerClientTenantId()) ? `
        <button class="btn btn-secondary" style="padding:8px 14px;font-size:0.85rem;display:inline-flex;align-items:center;gap:6px;" onclick="window.openApplicationQuestionsModal&&window.openApplicationQuestionsModal()" title="Application Questions">
          <span class="material-icons" style="font-size:18px;">quiz</span> Application Questions
        </button>` : ''}
      </div>

      <!-- Main Content -->
      <div class="hr-content" id="hr-content">
        ${getLandingHTML()}
      </div>
    </div>

    ${getDisciplinaryModalHTML()}
    ${getNewChecklistModalHTML()}
    ${getViewChecklistModalHTML()}
    ${getViewDisciplinaryModalHTML()}
    ${getReviewModalHTML()}
    ${getViewReviewModalHTML()}
    ${getTimeOffRequestModalHTML()}
  `;
}

function getLandingHTML() {
  return `
    <!-- TASK-447 — read-only note: BG/MVR alerts routed to compliance partner.
         Only shown when this carrier's compliance partner owns BG/MVR
         completion notices, so the fleet manager doesn't think notifications
         are broken (they won't get the completion email/SMS — partner does). -->
    <div id="hr-bg-routing-note" style="display:none;margin-bottom:var(--spacing-md);"></div>

    <!-- TASK-1482 — inbound §391.23 Safety Performance History action queue.
         Hidden until _loadInboundSphInquiries finds open inquiries (reads are
         admin | managing partner | hasModule('hr') per the existing
         sphInquiries rule; a denied read just leaves this hidden). -->
    <div id="hr-sph-inbound" style="display:none;margin-bottom:var(--spacing-lg);"></div>

    <!-- Compliance Dashboard Counters -->
    <div id="hr-compliance-dashboard" class="hr-compliance-dashboard" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-bottom:var(--spacing-lg);"></div>

    <!-- TASK-1512 — Hiring HOISTED out of the Actions section. It used to be one
         tile inside Actions, which TASK-782 made collapsed-by-default, so a
         first-time external user landed on #/hr and saw documents / library /
         time off / directory and never found hiring at all (John: "never actually
         explored the HR portal"). Only compliance-only tenants auto-landed on it
         (initHR ~L1150). This is a first-class row at the top of the landing —
         same shell (.hr-section) and the same opener as the old tile; the tile was
         REMOVED from the Actions grid rather than duplicated, so there is still
         exactly one way in. Discoverability change only: no gating change (the
         old tile was ungated too — candidates reads are admin | partner |
         hasModule('hr'), which every HR viewer here already has). -->
    <div class="hr-section" style="padding:0;overflow:hidden;margin-bottom:var(--spacing-lg);">
      <button type="button" onclick="window.HRModule.openHiringSubView()" title="Review applications, run screenings, and approve new hires"
              style="width:100%;display:flex;align-items:center;gap:var(--spacing-md);padding:var(--spacing-lg);background:none;border:none;cursor:pointer;text-align:left;font:inherit;color:inherit;">
        <span style="width:48px;height:48px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#3B82F6,#2563EB);color:#fff;">
          <span class="material-icons" style="font-size:26px;">people</span>
        </span>
        <span style="flex:1;min-width:0;">
          <span style="display:block;font-size:1.125rem;font-weight:700;color:var(--text-primary);">Hiring / Applicants</span>
          <span style="display:block;font-size:0.85rem;color:var(--text-secondary);margin-top:2px;">Review applications, run screenings, and approve new hires</span>
        </span>
        <span class="material-icons" style="color:var(--text-secondary);flex-shrink:0;">chevron_right</span>
      </button>
    </div>

    ${''/* TASK-10142 (Chris approved office pick 2026-11-02) — Driver Memos HOISTED
         out of the collapsed Actions section, same as Hiring (TASK-1512): one
         click from the HR landing instead of HR → expand Actions → Driver Memos.
         The Actions tile was REMOVED, not duplicated (one way in). Same opener and
         no gating change: openMemosSubView keeps its own compliance-only gate and
         the sub-view hides New Memo from non-senders (canSendMemos). */}
    <div class="hr-section" id="hr-memos-row" style="padding:0;overflow:hidden;margin-bottom:var(--spacing-lg);">
      <button type="button" onclick="window.HRModule.openMemosSubView()" title="Send a memo or PDF to your crew by email or text, and see who read it"
              style="width:100%;display:flex;align-items:center;gap:var(--spacing-md);padding:var(--spacing-lg);background:none;border:none;cursor:pointer;text-align:left;font:inherit;color:inherit;">
        <span style="width:48px;height:48px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#3B82F6,#2563EB);color:#fff;">
          <span class="material-icons" style="font-size:26px;">campaign</span>
        </span>
        <span style="flex:1;min-width:0;">
          <span style="display:block;font-size:1.125rem;font-weight:700;color:var(--text-primary);">Driver Memos</span>
          <span style="display:block;font-size:0.85rem;color:var(--text-secondary);margin-top:2px;">Send a memo or PDF by email or text, with read receipts</span>
        </span>
        <span class="material-icons" style="color:var(--text-secondary);flex-shrink:0;">chevron_right</span>
      </button>
    </div>

    <!-- Resources Section (collapsible: default open when ≤5 docs, collapsed when >5) -->
    <div class="hr-section hr-resources-section">
      <div class="hr-resources-header">
        <h2 class="hr-section-title hr-resources-title hr-section-toggle" onclick="window.HRModule.toggleCompanyDocsSection()" style="cursor:pointer;user-select:none;" title="Show/hide company documents">
          <span class="material-icons">library_books</span>
          <span class="hr-resources-title-text">Company Documents</span>
          <span class="hr-resources-count-chip" id="hr-resources-count-chip" style="display:none;font-size:0.72rem;font-weight:600;background:var(--bg-tertiary,#e5e7eb);color:var(--text-secondary,#475569);padding:2px 9px;border-radius:10px;margin-left:6px;vertical-align:middle;">0</span>
          <span class="material-icons" id="hr-resources-loading-spin" style="margin-left:6px;font-size:1rem;vertical-align:middle;color:var(--text-secondary);animation:spin 0.8s linear infinite;" title="Loading documents…">progress_activity</span>
          <!-- TASK-1379: no inline margin-left here — .hr-section-chevron's margin-left:auto
               pins the chevron to the right edge like the other four HR cards. -->
          <span class="material-icons hr-section-chevron" id="chevron-company-docs" style="transition:transform 0.3s ease;font-size:1.2rem;">expand_more</span>
        </h2>
        <div class="hr-resources-actions" onclick="event.stopPropagation();">
          <button class="hr-resources-action-btn hr-resources-add-btn" onclick="window.HRModule.showAddDocumentMenu(event)" title="Add a document or link">
            <span class="material-icons">add</span>
            <span class="hr-resources-action-label">Add</span>
          </button>
          <button class="hr-resources-action-btn ai-entry" onclick="window.HRModule.openDocAssistant()" title="Smart Search">
            <span class="material-icons">auto_awesome</span>
            <span class="hr-resources-action-label">Search</span>
          </button>
          <button class="hr-resources-action-btn" onclick="window.HRModule.sharePublicDocsPage()" title="Generate a public link to share selected documents">
            <span class="material-icons">share</span>
            <span class="hr-resources-action-label">Share</span>
          </button>
          <!-- TASK-483: master enable for the in-app driver "Doc Page" button. When ON,
               PIN drivers get a "Doc Page" tile in the Actions sheet that shows the SAME
               eye-marked (publiclyShared) docs — no public /?docs= link needed. -->
          <button id="hr-driver-docs-toggle" class="hr-resources-action-btn" onclick="window.HRModule.toggleDriverDocs()" title="Show shared documents to drivers inside the app (Actions → Doc Page)">
            <span class="material-icons">groups</span>
            <span class="hr-resources-action-label">Driver View</span>
          </button>
        </div>
      </div>
      <!-- TASK-484 #2: starts collapsed (display:none) so a slow load shows only the
           compact header spinner, never the expand→big-spinner→collapse flash. The
           first snapshot drives final state via _syncCompanyDocsCollapseUI. -->
      <div id="hr-resources-collapsible" class="hr-collapsible-content" style="display:none;">
        <div class="hr-resources-grid" id="hr-resources-grid"></div>
      </div>
    </div>

    <!-- TASK-419 Free Compliance Library (static .gov reference links) -->
    ${_hrComplianceLibraryHTML()}

    <!-- Actions Section (TASK-782: collapsible, collapsed by default so Directory/Time Off aren't pushed below the fold) -->
    <div class="hr-section hr-section-collapsible">
      <h2 class="hr-section-title hr-section-toggle" onclick="window.HRModule.toggleSection('actions')">
        <span class="material-icons">assignment</span>
        Actions
        <span class="material-icons hr-section-chevron" id="chevron-actions">expand_more</span>
      </h2>
      <div id="hr-actions" class="hr-collapsible-content" style="display:none;">
      <!-- TASK-1219 — upcoming interviews strip (filled async by _loadUpcomingInterviews) -->
      <div id="hr-upcoming-interviews" style="display:none;"></div>
      <div class="hr-actions-grid">
        ${(window.hrCanManageDisciplinary ? window.hrCanManageDisciplinary() : true) ? `
        <button class="hr-action-card" onclick="window.HRModule.openDisciplinarySubView()">
          <div class="hr-action-icon warning">
            <span class="material-icons">gavel</span>
          </div>
          <div class="hr-action-content">
            <div class="hr-action-title">Disciplinary Form</div>
            <div class="hr-action-desc">Verbal, Written, PIP, or Termination</div>
          </div>
        </button>
        ` : ''}

        <!-- TASK-346 reframe (2026-05-21) — Add Employee Note action card.
             Replaces the killed My Crew dedicated sub-view. Opens a modal
             that lets an admin (any roster person) or supervisor (their
             direct reports only) log a note against an employee. Same
             hrNotes subcollection as the HR detail Notes feed (Slice 3) —
             single source of truth. Visibility-gated to owner/admin OR
             AppState.hasDirectReports===true via inline style toggled by
             window.HRModule.applyAddEmployeeNoteGate() on landing render. -->
        <button class="hr-action-card" id="hr-action-add-employee-note" style="display:none;" onclick="window.HRModule.openAddEmployeeNoteModal()">
          <div class="hr-action-icon" style="background: linear-gradient(135deg, #06B6D4, #0891B2); color: white;">
            <span class="material-icons">note_add</span>
          </div>
          <div class="hr-action-content">
            <div class="hr-action-title">Add Employee Note</div>
            <div class="hr-action-desc">Log a note on a team member</div>
          </div>
        </button>

        ${''/* TASK-1512 — the "Hiring / Applicants" tile lived HERE and is now the
             first-class row at the top of the landing (see getLandingHTML above).
             Not duplicated: one entry point, and it is no longer behind a
             collapsed-by-default section. */}

        <button class="hr-action-card" onclick="window.HRModule.openSafetySubView()">
          <div class="hr-action-icon" style="background: linear-gradient(135deg, #10b981, #059669); color: white;">
            <span class="material-icons">health_and_safety</span>
          </div>
          <div class="hr-action-content">
            <div class="hr-action-title">Safety</div>
            <div class="hr-action-desc">Meetings, JSAs, required trainings</div>
          </div>
        </button>

        ${''/* TASK-10142 — the "Driver Memos" tile lived HERE and is now a
             first-class row at the top of the landing (see above). */}

        <button class="hr-action-card" onclick="window.HRModule.openSignPackets()">
          <div class="hr-action-icon info"><span class="material-icons">draw</span></div>
          <div class="hr-action-content">
            <div class="hr-action-title">Signature Packets</div>
            <div class="hr-action-desc">Many documents, many people, one link each</div>
            <span id="hr-sp-alert" class="badge" style="display:none;background:#FEF3C7;color:#92400E;"></span>
          </div>
        </button>
        <button class="hr-action-card" onclick="window.HRModule.openTrainingAssign()">
          <div class="hr-action-icon info"><span class="material-icons">school</span></div>
          <div class="hr-action-content">
            <div class="hr-action-title">Assign Training</div>
            <div class="hr-action-desc">Link + due date, see who's done</div>
          </div>
        </button>

        <button class="hr-action-card" onclick="window.HRModule.openChecklistsSubView()">
          <div class="hr-action-icon success">
            <span class="material-icons">checklist</span>
          </div>
          <div class="hr-action-content">
            <div class="hr-action-title">New Hire Checklist</div>
            <div class="hr-action-desc">Onboarding workflow</div>
          </div>
        </button>

        <button class="hr-action-card" onclick="window.HRModule.openReviewsSubView()">
          <div class="hr-action-icon info">
            <span class="material-icons">rate_review</span>
          </div>
          <div class="hr-action-content">
            <div class="hr-action-title">Employee Reviews</div>
            <div class="hr-action-desc">Performance evaluations</div>
          </div>
        </button>

        <button class="hr-action-card" onclick="window.HRModule.openTimeOffPolicyEditor()">
          <div class="hr-action-icon" style="background: linear-gradient(135deg, #f59e0b, #d97706); color: white;">
            <span class="material-icons">event_available</span>
          </div>
          <div class="hr-action-content">
            <div class="hr-action-title">Time Off Policy</div>
            <div class="hr-action-desc">Allowances by level</div>
          </div>
        </button>

        <!-- TASK-972 S4 — DOT auditor access links. Owner/admin only (mirrors
             the server's requireTenantAdmin gate on mint/list/revoke). -->
        ${_hrIsOwnerOrAdmin() ? `
        <button class="hr-action-card" onclick="window.HRModule.openDotAuditAccess()">
          <div class="hr-action-icon" style="background: linear-gradient(135deg, #0F172A, #334155); color: white;">
            <span class="material-icons">policy</span>
          </div>
          <div class="hr-action-content">
            <div class="hr-action-title">DOT Audit Access</div>
            <div class="hr-action-desc">Time-boxed read-only links for auditors</div>
          </div>
        </button>
        ` : ''}

        ${window.AppState?.settings?.trackOshaRecords === true ? `
        <button class="hr-action-card" onclick="window.showOshaRecordsLog?.()">
          <div class="hr-action-icon" style="background: linear-gradient(135deg, #ef4444, #b91c1c); color: white;">
            <span class="material-icons">folder_shared</span>
          </div>
          <div class="hr-action-content">
            <div class="hr-action-title">Records Access Log</div>
            <div class="hr-action-desc">OSHA 1910.1020 requests</div>
          </div>
        </button>
        <button class="hr-action-card" onclick="window.openOshaNotificationModal?.()">
          <div class="hr-action-icon" style="background: linear-gradient(135deg, #f59e0b, #d97706); color: white;">
            <span class="material-icons">campaign</span>
          </div>
          <div class="hr-action-content">
            <div class="hr-action-title">Annual Records-Access Notification</div>
            <div class="hr-action-desc" id="osha-notif-card-subtitle">1910.1020(g) reminders</div>
          </div>
        </button>
        <button class="hr-action-card" onclick="window.openOshaAnonymizedAnalysisModal?.()">
          <div class="hr-action-icon" style="background: linear-gradient(135deg, #6366f1, #4338ca); color: white;">
            <span class="material-icons">analytics</span>
          </div>
          <div class="hr-action-content">
            <div class="hr-action-title">Anonymized Analysis Export</div>
            <div class="hr-action-desc">1910.1020(e)(2)(iii)(C) designated-rep request</div>
          </div>
        </button>
        <button class="hr-action-card" onclick="window.openOshaCessationModal?.()">
          <div class="hr-action-icon" style="background: linear-gradient(135deg, #b91c1c, #7f1d1d); color: white;">
            <span class="material-icons">warning</span>
          </div>
          <div class="hr-action-content">
            <div class="hr-action-title">Export OSHA Records (Business Cessation)</div>
            <div class="hr-action-desc">1910.1020(h) successor transfer</div>
          </div>
        </button>
        ` : ''}

      </div>
      </div>
    </div>

    <!-- Time Off Section (Collapsible, closed by default) -->
    <div class="hr-section hr-section-collapsible">
      <h2 class="hr-section-title hr-section-toggle" onclick="window.HRModule.toggleSection('timeoff')">
        <span class="material-icons">event_busy</span>
        Time Off Requests
        <span class="hr-pending-badge" id="hr-timeoff-pending-count" style="display: none;">0</span>
        <span class="material-icons hr-section-chevron" id="chevron-timeoff">expand_more</span>
      </h2>
      <div id="hr-timeoff" class="hr-collapsible-content" style="display: none;">
        <div class="hr-timeoff-actions" style="margin-bottom: var(--spacing-md);display:flex;flex-wrap:wrap;gap:var(--spacing-sm);align-items:center;">
          <button class="btn btn-primary btn-sm" onclick="window.HRModule.openTimeOffRequest()">
            <span class="material-icons">add</span> Request Time Off
          </button>
          <button class="btn btn-sm" id="hr-timeoff-balances-btn" aria-pressed="false" onclick="window.HRModule.toggleTimeOffBalances()" style="background:transparent;border:1px solid var(--border-color);color:var(--text-primary);">
            <span class="material-icons">table_chart</span> <span class="tob-btn-label">Balances</span>
          </button>
        </div>
        ${_timeOffListStyles()}
        <!-- TASK-9601 — summary strip (each tile is a click-to-filter toggle) -->
        <div id="hr-timeoff-summary" class="hrto-tiles" aria-label="Time off summary"></div>
        <!-- TASK-9601 — filter bar (replaces TASK-9535's Employee select + Group checkbox; keeps both behaviours) -->
        <div id="hr-timeoff-filters" class="hrto-filters">
          <div class="hrto-frow">
            <label class="hrto-search" for="hr-timeoff-search"><span class="material-icons" aria-hidden="true">search</span>
              <input id="hr-timeoff-search" type="search" placeholder="Search name or reason" autocomplete="off" aria-label="Search name or reason" oninput="window.HRModule.setTimeOffSearch(this.value)">
            </label>
            <select id="hr-timeoff-person-filter" class="hrto-select" aria-label="Employee" onchange="window.HRModule.setTimeOffPersonFilter(this.value)">
              <option value="">All employees</option>
            </select>
            <button type="button" class="btn btn-sm hrto-morebtn" id="hr-timeoff-more-btn" aria-expanded="false" aria-controls="hr-timeoff-filters" onclick="window.HRModule.toggleTimeOffMoreFilters()">
              <span class="material-icons">tune</span> More filters
            </button>
          </div>
          <div class="hrto-frow">
            <span class="hrto-flabel">Status</span>
            <span id="hr-timeoff-status-chips" class="hrto-chips"></span>
          </div>
          <div class="hrto-frow hrto-adv">
            <span class="hrto-flabel">Type</span>
            <span id="hr-timeoff-type-chips" class="hrto-chips"></span>
          </div>
          <div class="hrto-frow hrto-adv">
            <span class="hrto-flabel">When</span>
            <select id="hr-timeoff-window" class="hrto-select" aria-label="Date window" onchange="window.HRModule.setTimeOffWindow(this.value)">${_timeOffWindowOptionsHtml()}</select>
            <span class="hrto-flabel">Group</span>
            <span class="hrto-seg" id="hr-timeoff-group" role="group" aria-label="Group requests by">
              <button type="button" data-v="none" aria-pressed="false" onclick="window.HRModule.setTimeOffGroup('none')">None</button><button type="button" data-v="employee" aria-pressed="true" onclick="window.HRModule.setTimeOffGroup('employee')">Employee</button><button type="button" data-v="month" aria-pressed="false" onclick="window.HRModule.setTimeOffGroup('month')">Month</button>
            </span>
            <span class="hrto-flabel">Sort</span>
            <select id="hr-timeoff-sort" class="hrto-select" aria-label="Sort" onchange="window.HRModule.setTimeOffSort(this.value)">${_timeOffSortOptionsHtml()}</select>
          </div>
          <div class="hrto-fsum"><span id="hr-timeoff-filter-count" aria-live="polite"></span><button type="button" class="hrto-linkbtn" id="hr-timeoff-clear-filters" hidden onclick="window.HRModule.clearTimeOffFilters()">Clear filters</button></div>
        </div>
        <div id="hr-timeoff-list" class="hr-list">
          <div class="empty-state-small">No time-off requests</div>
        </div>
        <!-- Below the list on purpose: opening Balances must never bury the pending queue -->
        <div id="hr-timeoff-balances" style="display:none;margin-top:var(--spacing-md);"></div>
      </div>
    </div>

    <!-- Employee Directory Section (Collapsible) — hidden if PIN lacks hr-directory -->
    <div class="hr-section hr-section-collapsible" id="hr-directory-section" ${!isSubModuleUnlocked('hr-directory') ? 'style="display:none;"' : ''}>
      <h2 class="hr-section-title hr-section-toggle" onclick="window.HRModule.toggleSection('employee-directory')">
        <span class="material-icons">people</span>
        Employee Directory
        <span id="hr-directory-compliance-badge" class="nav-alert-badge hidden" style="margin-left:8px;"></span>
        <span class="material-icons hr-section-chevron" id="chevron-employee-directory">expand_more</span>
      </h2>
      <div id="hr-employee-directory" class="hr-collapsible-content" style="display: none;">
        <div class="hr-directory-toolbar">
          <div class="hr-search-box">
            <span class="material-icons">search</span>
            <input type="search" id="hr-employee-search" placeholder="Search employees..." autocomplete="off" oninput="window.HRModule.filterEmployees()">
          </div>
          <select id="hr-position-filter" class="hr-filter-select" onchange="window.HRModule.filterEmployees()">
            <option value="">All Positions</option>
          </select>
          <select id="hr-status-filter" class="hr-filter-select" onchange="window.HRModule.setDirectoryStatus(this.value)" title="Filter by employment status. Inactive = still employed, temporarily not working. Use No Longer Employed (Terminated) when employment ends." aria-label="Filter by employment status. Inactive means still employed, temporarily not working. Use No Longer Employed when employment ends.">
            <option value="active" ${(HRState.directoryStatus || 'active') === 'active' ? 'selected' : ''}>Active</option>
            <option value="inactive" ${HRState.directoryStatus === 'inactive' ? 'selected' : ''}>Inactive</option>
            <option value="terminated" ${HRState.directoryStatus === 'terminated' ? 'selected' : ''}>Terminated</option>
            <option value="all" ${HRState.directoryStatus === 'all' ? 'selected' : ''}>All</option>
          </select>
          <div style="position:relative;">
            <button class="btn btn-secondary btn-sm" onclick="window.HRModule.toggleRosterExportMenu(event)" title="Export Roster" style="display:flex;align-items:center;gap:4px;padding:6px 12px;">
              <span class="material-icons" style="font-size:18px;">download</span>
              <span class="toggle-label">Export</span>
            </button>
            <div id="roster-export-menu" style="display:none;position:absolute;right:0;top:100%;margin-top:4px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-md);box-shadow:var(--shadow-md);z-index:100;min-width:200px;">
              <button onclick="window.HRModule.exportPayrollRoster()" style="display:flex;align-items:center;gap:8px;width:100%;padding:10px 14px;border:none;background:none;cursor:pointer;font-size:0.875rem;color:var(--text-primary);text-align:left;" onmouseenter="this.style.background='var(--bg-secondary)'" onmouseleave="this.style.background='none'">
                <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">table_chart</span> Payroll Roster (CSV)
              </button>
              <button onclick="window.HRModule.printPayrollRoster()" style="display:flex;align-items:center;gap:8px;width:100%;padding:10px 14px;border:none;background:none;cursor:pointer;font-size:0.875rem;color:var(--text-primary);text-align:left;" onmouseenter="this.style.background='var(--bg-secondary)'" onmouseleave="this.style.background='none'">
                <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">print</span> Print Roster (PDF)
              </button>
              ${_hrIsOwnerOrAdmin() ? `
              <button onclick="window.HRModule.exportQboPayrollCsv()" title="Export the active W-2 roster as a CSV (legal name, SSN, DOB, dates) to copy into QuickBooks" style="display:flex;align-items:center;gap:8px;width:100%;padding:10px 14px;border:none;background:none;cursor:pointer;font-size:0.875rem;color:var(--text-primary);text-align:left;" onmouseenter="this.style.background='var(--bg-secondary)'" onmouseleave="this.style.background='none'">
                <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">description</span> Roster for QuickBooks (CSV)
              </button>` : ''}
            </div>
          </div>
        </div>
        <!-- TASK-582 — active compliance-tile filter chip ("Showing N — Med Card expiring/expired ✕ Clear") -->
        <div id="hr-compliance-filter-chip" style="display:none;"></div>
        <div id="hr-employee-list" class="hr-employee-grid">
          <div class="empty-state-small">Loading employees...</div>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// MODAL HTML TEMPLATES
// ============================================
// Warning type configurations
const WARNING_TYPES = {
  verbal: {
    label: 'Verbal Warning',
    icon: 'record_voice_over',
    color: '#f59e0b', // warning/amber
    description: 'Informal discussion to address early concerns before they escalate.',
    acknowledgmentText: 'Employee acknowledges receipt of this verbal warning. Sign below or print to collect signature on paper.'
  },
  written: {
    label: 'Written Warning',
    icon: 'description',
    color: '#ef4444', // red
    description: 'Formal written notice that becomes part of the personnel file and may impact future raises or promotions.',
    acknowledgmentText: 'Employee acknowledges receipt of this written warning. This document will be placed in their personnel file. Sign below or print to collect signature on paper.'
  },
  pip: {
    label: 'Performance Improvement Plan (PIP)',
    icon: 'trending_up',
    color: '#8b5cf6', // purple
    description: 'Structured improvement plan with specific goals, weekly check-ins, and a defined timeline.',
    acknowledgmentText: 'Employee acknowledges receipt of this Performance Improvement Plan and commits to working toward improvement. Sign below or print to collect signature on paper.'
  },
  // TASK-999 — Termination / Separation: the natural end state of the discipline
  // ladder. This is the employee-facing SIGNED NOTICE. The FMCSA §391.23 Safety
  // Performance History (termination type / eligible-for-rehire / DOT-recordable /
  // drug-alcohol / SAP) is captured/edited on the Roster person card and is only
  // READ + surfaced here (see _hrRenderTerminationSphLink) — never written from
  // this form, so the two records stay linked without diverging.
  termination: {
    label: 'Termination / Separation',
    icon: 'logout',
    color: '#dc2626', // red-600
    description: 'Formal separation notice documenting the end of employment. The employee signature acknowledges receipt only, not agreement.',
    acknowledgmentText: 'Employee signature acknowledges receipt of this notice only, not agreement with its contents.'
  }
};

// PIP performance areas checklist
const PIP_PERFORMANCE_AREAS = [
  'Working safe and following all safety protocols',
  'Show up for work on time',
  'Work diligently throughout the day',
  'Maintain a good attitude and professional demeanor',
  'Demonstrate reliability and accountability',
  'Follow instructions and company policies',
  'Communicate effectively with supervisors and coworkers'
];

function getDisciplinaryModalHTML() {
  return `
    <div class="modal hidden" id="modal-disciplinary">
      <div class="modal-content" style="max-width: 650px;">
        <div class="modal-header">
          <h3 id="disc-modal-title"><span class="material-icons">gavel</span> Disciplinary Form</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('disciplinary')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-disciplinary" novalidate onsubmit="window.HRModule.submitDisciplinaryForm(event)">
          <div class="modal-form-scroll" style="max-height: 60vh; overflow-y: auto; padding: var(--spacing-lg);">

            <!-- Warning Type Selector -->
            <div class="form-group">
              <label>Warning Type *</label>
              <div class="warning-type-selector" style="display: flex; gap: var(--spacing-sm); flex-wrap: wrap;">
                <label class="warning-type-option selected" data-type="verbal" onclick="window.HRModule.selectWarningType('verbal')">
                  <input type="radio" name="disc-warning-type" value="verbal" checked style="display: none;">
                  <span class="material-icons" style="color: #f59e0b;">record_voice_over</span>
                  <span>Verbal Warning</span>
                </label>
                <label class="warning-type-option" data-type="written" onclick="window.HRModule.selectWarningType('written')">
                  <input type="radio" name="disc-warning-type" value="written" style="display: none;">
                  <span class="material-icons" style="color: #ef4444;">description</span>
                  <span>Written Warning</span>
                </label>
                <label class="warning-type-option" data-type="pip" onclick="window.HRModule.selectWarningType('pip')">
                  <input type="radio" name="disc-warning-type" value="pip" style="display: none;">
                  <span class="material-icons" style="color: #8b5cf6;">trending_up</span>
                  <span>PIP</span>
                </label>
                <label class="warning-type-option" data-type="termination" onclick="window.HRModule.selectWarningType('termination')">
                  <input type="radio" name="disc-warning-type" value="termination" style="display: none;">
                  <span class="material-icons" style="color: #dc2626;">logout</span>
                  <span>Termination</span>
                </label>
              </div>
              <p id="disc-type-description" style="font-size: 0.8rem; color: var(--text-secondary); margin-top: 8px;">
                Informal discussion to address early concerns before they escalate.
              </p>
            </div>

            <hr style="margin: var(--spacing-md) 0; border: none; border-top: 1px solid var(--border-light);">

            <div class="form-group">
              <label for="disc-supervisor">Supervisor *</label>
              <select id="disc-supervisor" required>
                <option value="">Select supervisor...</option>
              </select>
            </div>

            <div class="form-group">
              <label for="disc-employee">Employee *</label>
              <select id="disc-employee" required onchange="window.HRModule._onDiscEmployeeChange && window.HRModule._onDiscEmployeeChange()">
                <option value="">Select employee...</option>
              </select>
            </div>

            <div class="form-group">
              <label for="disc-date">Date *</label>
              <input type="date" id="disc-date" required>
            </div>

            <div class="form-group">
              <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
                <label for="disc-issue" id="disc-issue-label" style="margin:0;">Issue/Concern Discussed *</label>
                <button type="button" id="disc-term-prefill-btn" onclick="window.HRModule.prefillTerminationReason()" style="display:none;align-items:center;gap:4px;background:none;border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:3px 8px;font-size:0.7rem;cursor:pointer;color:var(--brand-blue);">
                  <span class="material-icons" style="font-size:14px;">history</span> Pull from disciplinary history
                </button>
              </div>
              <textarea id="disc-issue" rows="4" required placeholder="Describe the issue or concern..."></textarea>
            </div>

            <!-- PIP-specific fields (hidden by default) -->
            <div id="disc-pip-fields" style="display: none;">
              <div class="form-group">
                <label for="disc-pip-expectations">Performance Expectations *</label>
                <p style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 8px;">
                  Describe specific expectations the employee must meet during the PIP period:
                </p>
                <textarea id="disc-pip-expectations" rows="5" placeholder="List specific, measurable expectations...&#10;&#10;Example:&#10;- Arrive on time (within 5 minutes) for all scheduled shifts&#10;- Complete all assigned tasks with 95%+ accuracy&#10;- Follow all safety protocols without exception"></textarea>
              </div>

              <div class="form-group">
                <label for="disc-pip-duration">PIP Duration *</label>
                <select id="disc-pip-duration">
                  <option value="30">30 Days</option>
                  <option value="60">60 Days</option>
                  <option value="90" selected>90 Days</option>
                </select>
              </div>

              <div class="form-group">
                <label for="disc-pip-start">Weekly Check-ins Start Date *</label>
                <input type="date" id="disc-pip-start">
              </div>

              <div class="form-group">
                <label for="disc-employee-comments">Employee Comments (Optional)</label>
                <textarea id="disc-employee-comments" rows="3" placeholder="Employee's comments or response..."></textarea>
              </div>
            </div>

            <!-- TASK-999 Termination / Separation fields (hidden by default) -->
            <div id="disc-termination-fields" style="display: none;">
              <!-- TASK-999 (COD Adversary/Domain): this form is a signed NOTICE only.
                   It does NOT set terminated / revoke the driver's portal access.
                   Real termination (which fires the TASK-984 session revoke + DQ
                   archive + retention) happens on the Roster person card. Make that
                   unmistakable so no one signs this and assumes the driver is locked out. -->
              <div style="display:flex;gap:10px;align-items:flex-start;background:#FEF3C7;border:1px solid #F59E0B;border-radius:var(--radius-md);padding:12px 14px;margin-bottom:16px;">
                <span class="material-icons" style="color:#B45309;font-size:20px;flex-shrink:0;">warning_amber</span>
                <div style="font-size:0.85rem;color:#92400E;line-height:1.4;">
                  <strong>This is a signed record only.</strong> Completing it does <strong>not</strong> deactivate the employee or revoke their app / driver-portal access. To actually end their access, use <strong>Terminate</strong> on their Roster card.
                  <div style="margin-top:8px;">
                    <button type="button" onclick="document.getElementById('modal-disciplinary')?.classList.add('hidden'); window.navigateToView('roster');" style="display:inline-flex;align-items:center;gap:4px;background:#B45309;color:#fff;border:none;border-radius:var(--radius-sm);padding:6px 12px;font-size:0.78rem;font-weight:600;cursor:pointer;">
                      <span class="material-icons" style="font-size:16px;">arrow_forward</span> Go to Roster to terminate
                    </button>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label for="disc-term-separation-type">Separation Type *</label>
                <select id="disc-term-separation-type">
                  <option value="involuntary_cause">Involuntary — For Cause</option>
                  <option value="involuntary_layoff">Involuntary — Layoff / Position Eliminated</option>
                  <option value="voluntary_resignation">Voluntary — Resignation</option>
                </select>
              </div>

              <div class="form-group">
                <label for="disc-term-last-day">Last Day Worked *</label>
                <input type="date" id="disc-term-last-day">
              </div>

              <div class="form-group">
                <label>Company Property Returned</label>
                <!-- TASK-1578 Slice 4 — when the person has open items in the
                     equipment issue log, the LIVE list replaces the five generic
                     checkboxes: you check off what they actually hold, not a
                     category. The static set stays as the fallback for a tenant
                     that has never issued anything (or an HR member whose read of
                     safetyEquipment was refused) — SUPERSEDE, never both. Both
                     paths write the SAME terminationPropertyReturned field. -->
                <div id="disc-term-property-live" style="display:none;"></div>
                <div id="disc-term-property-static">
                  <p style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 8px;">
                    Check each item collected from the employee.
                  </p>
                  <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 8px;">
                    <label style="display:flex;align-items:center;gap:8px;font-weight:400;cursor:pointer;">
                      <input type="checkbox" id="disc-term-prop-keys" value="keys"> Keys
                    </label>
                    <label style="display:flex;align-items:center;gap:8px;font-weight:400;cursor:pointer;">
                      <input type="checkbox" id="disc-term-prop-fuelcard" value="fuelcard"> Fuel Card
                    </label>
                    <label style="display:flex;align-items:center;gap:8px;font-weight:400;cursor:pointer;">
                      <input type="checkbox" id="disc-term-prop-device" value="device"> Device / Phone
                    </label>
                    <label style="display:flex;align-items:center;gap:8px;font-weight:400;cursor:pointer;">
                      <input type="checkbox" id="disc-term-prop-ppe" value="ppe"> PPE
                    </label>
                    <label style="display:flex;align-items:center;gap:8px;font-weight:400;cursor:pointer;">
                      <input type="checkbox" id="disc-term-prop-other" value="other"> Other
                    </label>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label for="disc-term-rehire">Eligible for Rehire *</label>
                <select id="disc-term-rehire">
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
                <p style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">
                  This is the value stated on <em>this notice</em>. The official FMCSA §391.23 rehire-eligibility answer (used to respond to future Safety Performance History requests) lives on the Roster person card — shown read-only below for cross-check.
                </p>
              </div>

              <div class="form-group">
                <label for="disc-term-final-pay">Final Pay Handling Note</label>
                <input type="text" id="disc-term-final-pay" placeholder="e.g. Final check mailed 7/10, PTO paid out">
              </div>

              <!-- TASK-999 — LINK (read-only) to the FMCSA §391.23 Safety Performance
                   History captured on the Roster person card. Surfaced here so the
                   separator sees the DOT record without this form writing (and
                   diverging) a parallel copy. -->
              <div id="disc-term-sph-link" style="margin: var(--spacing-sm) 0 var(--spacing-md); font-size: 0.8rem;"></div>
            </div>

            <div class="form-group" id="disc-expectations-group">
              <label for="disc-expectations" id="disc-expectations-label">What Will Be Expected Going Forward *</label>
              <textarea id="disc-expectations" rows="4" required placeholder="Describe the expectations..."></textarea>
            </div>

            <div style="border-top:1px solid var(--border-light);margin-top:var(--spacing-md);padding-top:var(--spacing-md);">
              <div style="font-weight:700;font-size:0.85rem;color:var(--text-primary);margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
                <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">draw</span> Electronic Signatures
              </div>
              <p style="font-size:0.75rem;color:var(--text-secondary);margin-bottom:12px;">
                Both parties can sign below, or print the form for wet signatures.
              </p>

              <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-md);">
                <div>
                  <label style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);margin-bottom:4px;display:block;">Supervisor Signature</label>
                  <div style="position:relative;border:1px solid var(--border-color);border-radius:var(--radius-md);background:#fff;overflow:hidden;">
                    <canvas id="disc-supervisor-sig-canvas" width="270" height="100" style="width:100%;height:100px;cursor:crosshair;touch-action:none;display:block;"></canvas>
                    <button type="button" onclick="window.HRModule.clearSupervisorSig()" style="position:absolute;top:4px;right:4px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:2px 6px;font-size:0.65rem;cursor:pointer;color:var(--text-secondary);">Clear</button>
                  </div>
                </div>
                <div>
                  <label style="font-size:0.8rem;font-weight:600;color:var(--text-secondary);margin-bottom:4px;display:block;">Employee Signature</label>
                  <div style="position:relative;border:1px solid var(--border-color);border-radius:var(--radius-md);background:#fff;overflow:hidden;">
                    <canvas id="disc-signature-canvas" width="270" height="100" style="width:100%;height:100px;cursor:crosshair;touch-action:none;display:block;"></canvas>
                    <button type="button" onclick="window.HRModule.clearSignatureCanvas()" style="position:absolute;top:4px;right:4px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:2px 6px;font-size:0.65rem;cursor:pointer;color:var(--text-secondary);">Clear</button>
                  </div>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label for="disc-comments">Additional Comments (Optional)</label>
              <textarea id="disc-comments" rows="2" placeholder="Any additional comments..."></textarea>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" onclick="window.HRModule.closeModal('disciplinary')">Cancel</button>
            <button type="submit" class="btn btn-primary">
              <span class="material-icons">save</span> Submit Form
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
}

function getNewChecklistModalHTML() {
  return `
    <div class="modal hidden" id="modal-new-checklist">
      <div class="modal-content" style="max-width: 500px;">
        <div class="modal-header">
          <h3><span class="material-icons">person_add</span> Start New Hire Checklist</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('new-checklist')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-new-checklist" novalidate onsubmit="window.HRModule.createNewChecklist(event)">
          <div class="modal-body">
            <div class="form-group">
              <label for="checklist-employee">Select New Hire *</label>
              <select id="checklist-employee" required>
                <option value="">Select employee...</option>
              </select>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" onclick="window.HRModule.closeModal('new-checklist')">Cancel</button>
            <button type="submit" class="btn btn-primary">
              <span class="material-icons">add</span> Create Checklist
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
}

function getViewChecklistModalHTML() {
  return `
    <div class="modal hidden" id="modal-view-checklist">
      <div class="modal-content" style="max-width: 700px;">
        <div class="modal-header">
          <h3><span class="material-icons">checklist</span> <span id="checklist-title">New Hire Checklist</span></h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('view-checklist')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="checklist-content" style="max-height: 70vh; overflow-y: auto;">
          <!-- Populated dynamically -->
        </div>
        <div class="modal-footer" style="justify-content:space-between;">
          <!-- TASK-498 — Create Printable Packet. On success this slot is
               replaced with a tappable Download link (no window.open after the
               await → no iOS-Safari popup-blocker break). -->
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <div id="checklist-packet-slot">
              <button type="button" class="btn btn-primary" id="checklist-packet-btn" onclick="window.HRModule.createNewHirePacket()">
                <span class="material-icons" style="font-size:16px;">print</span> Create Printable Packet
              </button>
            </div>
            <!-- TASK-625 — Generate the §391.23(d)(e) PEI (Safety Performance
                 History) inquiry. One form per previous employer from the
                 application history, pre-filled with the applicant ASK half
                 (name, SSN last-4, prior employer + claimed dates) and the
                 prospective-employer block; the answer half (incl. §40.25 D&A
                 questions) is left blank for the previous employer. Requires a
                 signed §391.23 release on file. On success this slot is swapped
                 for a tappable Download link. -->
            <div id="checklist-pei-slot">
              <button type="button" class="btn btn-secondary" id="checklist-pei-btn" onclick="window.HRModule.createPeiForm()">
                <span class="material-icons" style="font-size:16px;">fact_check</span> Generate PEI Form
              </button>
            </div>
          </div>
          <button type="button" class="btn btn-secondary" onclick="window.HRModule.closeModal('view-checklist')">Close</button>
        </div>
      </div>
    </div>
  `;
}

function getViewDisciplinaryModalHTML() {
  return `
    <div class="modal hidden" id="modal-view-disciplinary">
      <div class="modal-content" style="max-width: 600px;">
        <div class="modal-header">
          <h3><span class="material-icons">gavel</span> Disciplinary Form</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('view-disciplinary')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="disciplinary-view-content">
          <!-- Populated dynamically -->
        </div>
        <div class="modal-footer" style="justify-content:space-between;">
          <button type="button" class="btn" style="background:var(--status-danger);color:#fff;" onclick="window.HRModule.deleteViewedDisciplinaryForm()">
            <span class="material-icons" style="font-size:16px;">delete</span> Delete
          </button>
          <div style="display:flex;gap:var(--spacing-sm);">
            <button type="button" class="btn btn-secondary" onclick="window.HRModule.closeModal('view-disciplinary')">Close</button>
            <button type="button" class="btn btn-secondary" onclick="window.HRModule.editDisciplinaryForm()">
              <span class="material-icons" style="font-size:16px;">edit</span> Edit
            </button>
            <button type="button" class="btn btn-secondary" onclick="window.HRModule.printDisciplinaryForm()">
              <span class="material-icons" style="font-size:16px;">print</span> Print
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function getReviewModalHTML() {
  return `
    <div class="modal hidden" id="modal-review">
      <div class="modal-content" style="max-width: 900px;">
        <div class="modal-header">
          <h3><span class="material-icons">rate_review</span> Employee Performance Review</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('review')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-review" novalidate onsubmit="window.HRModule.submitReview(event)">
          <div class="modal-form-scroll" style="max-height: 70vh; overflow-y: auto; padding: var(--spacing-lg);">
            <!-- Header Section -->
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: var(--spacing-md);">
              <div class="form-group">
                <label for="review-employee">Employee *</label>
                <select id="review-employee" required onchange="window.HRModule.onEmployeeSelect(this.value)">
                  <option value="">Select employee...</option>
                </select>
              </div>

              <div class="form-group">
                <label for="review-position">Position *</label>
                <select id="review-position" required onchange="window.HRModule.onPositionChange(this.value)">
                  <option value="">Select position...</option>
                  ${window.HRPositions.grouped().map(g => `
                    <optgroup label="${escapeHtml(g.department)}">
                      ${g.positions.map(p => {
                        const available = p.available ? '' : ' (Coming Soon)';
                        // HRPositions is a hardcoded catalog, so this was never
                        // exploitable -- escAttr for consistency.
                        return `<option value="${(window.escAttr || escapeHtml)(p.id)}">${escapeHtml(p.title)}${available}</option>`;
                      }).join('')}
                    </optgroup>
                  `).join('')}
                </select>
              </div>

              <div class="form-group">
                <label for="review-reviewer">Reviewer *</label>
                <select id="review-reviewer" required>
                  <option value="">Select reviewer...</option>
                </select>
              </div>

              <div class="form-group">
                <label for="review-period">Review Period *</label>
                <select id="review-period" required>
                  <option value="">Select period...</option>
                  <option value="30-day">30-Day Review</option>
                  <option value="60-day">60-Day Review</option>
                  <option value="90-day">90-Day Review</option>
                  <option value="6-month">6-Month Review</option>
                  <option value="annual">Annual Review</option>
                  <option value="performance-improvement">Performance Improvement</option>
                  <option value="promotion">Promotion Review</option>
                </select>
              </div>
            </div>

            <hr style="margin: var(--spacing-lg) 0; border: none; border-top: 1px solid var(--border-light);">

            <!-- KPI Table Section -->
            <div id="review-kpi-section">
              <div class="empty-state-small" style="padding: var(--spacing-xl); text-align: center; color: var(--text-secondary);">
                <span class="material-icons" style="font-size: 48px; margin-bottom: var(--spacing-sm);">assignment</span>
                <p>Select a position to load KPIs</p>
              </div>
            </div>

            <!-- Totals Section (hidden until position selected) -->
            <div id="review-totals-section" style="display: none;">
              <div class="review-totals-grid" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: var(--spacing-md); margin: var(--spacing-lg) 0; padding: var(--spacing-md); background: var(--surface-secondary); border-radius: var(--radius-md);">
                <div class="review-total-item" style="text-align: center;">
                  <div style="font-size: 0.75rem; color: var(--text-secondary); text-transform: uppercase;">Total Score</div>
                  <div id="review-total-score" style="font-size: 1.75rem; font-weight: 700; color: var(--text-primary);">0</div>
                  <div style="font-size: 0.75rem; color: var(--text-secondary);">/ 100</div>
                </div>
                <div class="review-total-item" style="text-align: center;">
                  <div style="font-size: 0.75rem; color: var(--text-secondary); text-transform: uppercase;">Overall Rating</div>
                  <div id="review-overall-rating" style="font-size: 1.75rem; font-weight: 700; color: var(--text-primary);">0.0</div>
                  <div style="font-size: 0.75rem; color: var(--text-secondary);">/ 5.0</div>
                </div>
                <div class="review-total-item" style="text-align: center;">
                  <div style="font-size: 0.75rem; color: var(--text-secondary); text-transform: uppercase;">Status</div>
                  <div id="review-pass-fail" style="font-size: 1.25rem; font-weight: 700; padding: 4px 12px; border-radius: var(--radius-sm); display: inline-block; margin-top: 4px;">--</div>
                </div>
              </div>

              <!-- Compensation Calculator -->
              <div class="compensation-calculator" style="background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%); border: 1px solid #0ea5e9; border-radius: var(--radius-md); padding: var(--spacing-md); margin-bottom: var(--spacing-lg);">
                <div style="margin: 0 0 var(--spacing-md) 0; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;">
                  <h4 style="margin: 0; color: #0369a1; display: flex; align-items: center; gap: 8px;">
                    <span class="material-icons">calculate</span>
                    Compensation Calculator
                  </h4>
                  <div style="display:flex;align-items:center;gap:6px;font-size:0.75rem;">
                    <span id="comp-mode-hourly-label" style="font-weight:600;color:#0369a1;">Hourly</span>
                    <label class="hr-comp-toggle" style="position:relative;display:inline-block;width:36px;height:20px;margin:0;cursor:pointer;">
                      <input type="checkbox" id="comp-mode-toggle" onchange="window.HRModule.calculateCompensation()"
                        style="opacity:0 !important;width:0 !important;height:0 !important;min-width:0 !important;min-height:0 !important;position:absolute !important;pointer-events:none;">
                      <span id="comp-mode-track" style="position:absolute;cursor:pointer;inset:0;background:#7dd3fc;border-radius:20px;transition:.2s;border:1px solid #38bdf8;"></span>
                      <span id="comp-mode-knob" style="position:absolute;left:2px;top:2px;width:16px;height:16px;background:#fff;border-radius:50%;transition:.2s;box-shadow:0 1px 3px rgba(0,0,0,.3);"></span>
                    </label>
                    <span id="comp-mode-salary-label" style="color:var(--text-secondary);">Salary</span>
                  </div>
                </div>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: var(--spacing-md);">
                  <div class="form-group" style="margin: 0;">
                    <label for="review-current-wage" id="comp-wage-label" style="font-size: 0.75rem;">Current Wage ($)</label>
                    <input type="text" id="review-current-wage" placeholder="18.50" oninput="window.HRModule.calculateCompensation()">
                  </div>
                  <div class="form-group" style="margin: 0;">
                    <label for="review-raise-percent" style="font-size: 0.75rem;">Raise %</label>
                    <div style="display: flex; align-items: center; gap: 4px;">
                      <input type="number" id="review-raise-percent" placeholder="0" min="0" max="100" step="0.5"
                             style="width: 70px;" oninput="window.HRModule.calculateCompensation()">
                      <span style="color: var(--text-secondary);">%</span>
                    </div>
                    <div id="comp-suggested-raise" style="font-size: 0.65rem; color: #0369a1; margin-top: 2px; cursor: pointer;" onclick="if(this.dataset.value){document.getElementById('review-raise-percent').value=this.dataset.value;window.HRModule.calculateCompensation()}" title="Click to apply"></div>
                  </div>
                  <div class="form-group" style="margin: 0;">
                    <label for="review-bonus-pool" style="font-size: 0.75rem;">Bonus Pool ($)</label>
                    <input type="text" id="review-bonus-pool" placeholder="500" oninput="window.HRModule.calculateCompensation()">
                    <div id="comp-suggested-bonus" style="font-size: 0.65rem; color: #0369a1; margin-top: 2px;"></div>
                  </div>
                </div>
                <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: var(--spacing-md); margin-top: var(--spacing-md); padding-top: var(--spacing-md); border-top: 1px solid #bae6fd;">
                  <div>
                    <div id="comp-result-label" style="font-size: 0.75rem; color: var(--text-secondary);">New Hourly Wage</div>
                    <div id="comp-new-wage" style="font-size: 1.25rem; font-weight: 700; color: #0369a1;">$0.00</div>
                    <div id="comp-suggested-wage" style="font-size: 0.7rem; color: #0369a1; margin-top: 2px;"></div>
                  </div>
                  <div>
                    <div style="font-size: 0.75rem; color: var(--text-secondary);">Bonus Earned</div>
                    <div id="comp-bonus" style="font-size: 1.25rem; font-weight: 700; color: #0369a1;">$0.00</div>
                  </div>
                </div>
              </div>
            </div>

            <hr style="margin: var(--spacing-lg) 0; border: none; border-top: 1px solid var(--border-light);">

            <!-- Notes Section -->
            <div class="form-group">
              <label for="review-notes">Supervisor Notes</label>
              <textarea id="review-notes" rows="4" placeholder="Overall comments, observations, and recommendations..."></textarea>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" onclick="window.HRModule.closeModal('review')">Cancel</button>
            <!-- TASK-4760 — this button is UNCHANGED in behavior: it is the draft
                 path and stays fully re-editable, exactly as it behaves today.
                 Relabelled "Save Draft" (COD UX Lead) because "Save Review" next
                 to "Submit & Lock" reads as if it did NOT submit the review —
                 the two labels have to contrast. "Submit &
                 Lock" is the new, opt-in finalize that stamps submitted/
                 submittedAt — the field firestore.rules' isPostSubmitRecord()
                 reads. Until this shipped, nothing in the app could ever produce
                 that state, so the hrReviews post-submit freeze in the rules file
                 was structurally present but could never engage. Locking is
                 deliberately opt-in (not the primary action) so that nobody doing
                 what they do today is newly refused. -->
            <button type="button" class="btn btn-secondary" id="btn-finalize-review" onclick="window.HRModule.finalizeReview()">
              <span class="material-icons">lock</span> Submit &amp; Lock
            </button>
            <button type="submit" class="btn btn-primary" id="btn-submit-review">
              <span class="material-icons">save</span> Save Draft
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
}

function getViewReviewModalHTML() {
  return `
    <div class="modal hidden" id="modal-view-review">
      <div class="modal-content" style="max-width: 700px;">
        <div class="modal-header">
          <h3><span class="material-icons">rate_review</span> Employee Review</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('view-review')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" id="review-view-content" style="max-height: 70vh; overflow-y: auto;">
          <!-- Populated dynamically -->
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" onclick="window.HRModule.closeModal('view-review')">Close</button>
          <button type="button" class="btn btn-secondary" onclick="window.HRModule.printReview()">
            <span class="material-icons">print</span> Print
          </button>
          <!-- TASK-4776 — hidden by default; viewReview() reveals it for an
               owner/admin on a locked, not-yet-voided review. A locked review
               can no longer be deleted, so this is the only correction route
               from here. -->
          <button type="button" class="btn btn-secondary hidden" id="btn-void-review" onclick="window.HRModule.voidReviewFromModal()">
            <span class="material-icons">block</span> Void
          </button>
          <button type="button" class="btn btn-primary" id="btn-edit-review" onclick="window.HRModule.editReview()">
            <span class="material-icons">edit</span> Edit
          </button>
        </div>
      </div>
    </div>
  `;
}

function getTimeOffRequestModalHTML() {
  return `
    <div class="modal hidden" id="modal-hr-timeoff-request">
      <div class="modal-content" style="max-width: 500px;">
        <div class="modal-header">
          <h3><span class="material-icons">event_busy</span> Request Time Off</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('hr-timeoff-request')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <form id="form-hr-timeoff" novalidate onsubmit="window.HRModule.submitTimeOffRequest(event)">
            <div class="form-group">
              <label for="hr-timeoff-person">Employee *</label>
              <select id="hr-timeoff-person" required>
                <option value="">Select employee...</option>
              </select>
            </div>
            <div class="form-row">
              <div class="form-group">
                <label for="hr-timeoff-start">Start Date *</label>
                <input type="date" id="hr-timeoff-start" required onchange="var _e=document.getElementById('hr-timeoff-end');if(_e&&this.value&&_e.value<this.value)_e.value=this.value;window.HRModule.updateTimeOffHours()">
              </div>
              <div class="form-group">
                <label for="hr-timeoff-end">End Date *</label>
                <input type="date" id="hr-timeoff-end" required onchange="window.HRModule.updateTimeOffHours()">
              </div>
            </div>
            <div class="form-group">
              <label for="hr-timeoff-hours">Hours *</label>
              <div style="display:flex;align-items:center;gap:var(--spacing-sm);">
                <input type="number" id="hr-timeoff-hours" class="form-control" min="1" max="2000" value="8" required style="max-width:100px;">
                <span id="hr-timeoff-hours-hint" style="font-size:0.75rem;color:var(--text-secondary);">1 day = 8 hrs</span>
              </div>
            </div>
            <div class="form-group">
              <label for="hr-timeoff-type">Type *</label>
              <select id="hr-timeoff-type" required onchange="document.getElementById('hr-bereavement-sub-group').style.display = this.value === 'bereavement' ? '' : 'none';">
                <option value="unpaid">Unpaid</option>
                <option value="vacation">Vacation</option>
                <option value="sick">Sick Leave</option>
                <option value="personal">Personal Day</option>
                <option value="bereavement">Bereavement</option>
                <option value="parental_leave">Parental Leave</option>
                <option value="jury_duty">Jury Duty</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div id="hr-bereavement-sub-group" style="display:none;">
              <div class="form-group">
                <label for="hr-bereavement-level">Relationship Level *</label>
                <select id="hr-bereavement-level" class="form-control" onchange="window.HRModule.autofillBereavementHours()">
                  <option value="">Select relationship...</option>
                  <option value="level1">Level 1 — Spouse, child, parent, grandchild</option>
                  <option value="level2">Level 2 — Sibling, in-laws</option>
                  <option value="level3">Level 3 — Household member</option>
                  <option value="level4">Level 4 — Grandparent, grandparent-in-law</option>
                  <option value="level5">Level 5 — Loss of pregnancy</option>
                  <option value="level6">Level 6 — Aunt, uncle, cousin, niece, nephew</option>
                  <option value="level7">Level 7 — Distant relative, close friend</option>
                </select>
              </div>
              <div class="form-group">
                <label style="display:flex;align-items:center;gap:var(--spacing-sm);cursor:pointer;">
                  <input type="checkbox" id="hr-bereavement-travel" onchange="window.HRModule.autofillBereavementHours()">
                  <span style="font-size:0.85rem;">Travel days (funeral 250+ miles away)</span>
                </label>
              </div>
            </div>
            <div class="form-group">
              <label for="hr-timeoff-reason">Reason/Notes</label>
              <textarea id="hr-timeoff-reason" rows="3" placeholder="Optional notes..."></textarea>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" onclick="window.HRModule.closeModal('hr-timeoff-request')">Cancel</button>
              <button type="submit" class="btn btn-primary">
                <span class="material-icons">send</span> Submit Request
              </button>
            </div>
          </form>

          <!-- My Time Off — upcoming + past history for the selected person
               (Chris 2026-07-13: users should see historic + future accepted
               time off wherever they request it; ported from timeoff.js).
               Sits BELOW the form per Chris's layout call. -->
          <div class="collapsible-section timeoff-history-section" id="hr-timeoff-history-section" style="margin-top:var(--spacing-md);">
            <div class="collapsible-header" onclick="window.HRModule.toggleTimeOffHistory()">
              <span class="material-icons collapsible-arrow">expand_more</span>
              <span class="collapsible-title">My Time Off</span>
            </div>
            <div class="collapsible-body" id="hr-timeoff-history-body" style="max-height:none;">
              <div style="text-align:center;padding:var(--spacing-sm);color:var(--text-secondary);font-size:0.85rem;">
                Loading...
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// DATA SUBSCRIPTION
// ============================================
function subscribeToData(tenantId) {
  // TASK-1481 — comp accessor: mirror the people subscription. The directory
  // re-renders when the comp snapshot lands so pay badges resolve comp-first
  // even when peopleComp arrives after the people snapshot.
  window.PersonComp?.subscribe?.(tenantId, {
    key: 'hr',
    onUpdate: () => { try { renderEmployeeDirectory(); } catch (e) { /* view may be mid-teardown */ } },
  });

  // TASK-4865 — bind the card-PIN accessor to this tenant. NOT a subscription:
  // it only clears any cached PIN so a tenant switch can never serve the
  // previous tenant's credential out of memory. Reads happen only on Reveal.
  window.PersonCard?.setTenant?.(tenantId);

  // TASK-9359 — DOB / licence number / state / expiry / CDL number moved off
  // the broadcast people doc into the peopleIdentity vault. HR reads them at
  // dozens of sites (profile, DQ packet, exports, compliance tab), so they are
  // OVERLAID onto HRState.people in place (vault-over-legacy; raw values kept
  // non-enumerable) on every people snapshot and again whenever the vault
  // snapshot lands. hr.js never spreads a person object back onto people, and
  // its writers route these fields through window.PersonIdentity.
  window.PersonIdentity?.subscribe?.(tenantId, {
    key: 'hr',
    onUpdate: () => {
      try {
        window.PersonIdentity.overlayList(HRState.people);
        renderEmployeeDirectory();
        _hrRefreshOpenIdentityViews();
      } catch (e) { /* view may be mid-teardown */ }
    },
  });

  // Subscribe to people (include inactive for exit interviews, reviews, etc.)
  const peopleQuery = query(
    collection(db, 'tenants', tenantId, 'people'),
    orderBy('name')
  );

  const unsubPeople = onSnapshot(peopleQuery, (snapshot) => {
    HRState.people = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    if (HRState.currentView === 'safety') { try { renderSafetyRatesPanel(); } catch (_) {} } // TASK-9570 — W-2/1099 needs people
    // TASK-9359 — resolve the identity kit from the peopleIdentity vault.
    try { window.PersonIdentity?.overlayList?.(HRState.people); } catch (_) { /* legacy values stay */ }

    // All active people can be supervisors — HR is PIN-gated so no role filter needed
    HRState.supervisors = HRState.people.filter(p => p.active !== false);

    updateDropdowns();
    updateComplianceBadges();
    renderComplianceDashboard();

    // Always render employee directory so data is ready when user expands the section
    renderEmployeeDirectory();
    // OSHA 1910.1020(g) — refresh the annual-notification card subtitle now
    // that people are loaded. Async refresh avoids the at-template-render
    // empty-state false positive.
    updateOshaNotificationBadge();
    // Mark data as loaded so toggleSection knows data is ready
    HRState.peopleLoaded = true;
    // TASK-10142 (COD r4/r5) — the memo picker renders once, when New Memo opens.
    // Opened before people arrived (first open, a company switch, an empty cache
    // snapshot) it said "No matching people" for good. Repaint it only while it
    // has NO rows: an empty list can't move under a finger (the TASK-10142
    // mis-send); a list with rows is never rebuilt here.
    { const _mm = document.getElementById('memo-modal'); if (_mm && !_mm.classList.contains('hidden') && HRState.people.length && !document.querySelector('#memo-picker-list input[data-pid]')) renderMemoPicker(); }
    // TASK-10142 item 2 (COD r2) — an open memo's "Remind (N)" count follows
    // the roster (someone terminated / given a phone while the memo is open).
    try { if (MemoState.drillMemoId) renderMemoDrillBody(MemoState.drillMemoId); } catch (_) { /* memo view not ready */ }

    // TASK-9535 — the by-employee filter options and the balance table both
    // read people; refresh them when people land or change.
    try { renderTimeOffList(); if (HRState.timeOffBalancesOpen) renderTimeOffBalancesTable(); } catch (e) { console.warn('[HR] time-off refresh after people snapshot failed', e); }

    // TASK-1067 — live-refresh the §382.601(d) Policy Receipt row in an open
    // employee-detail Compliance tab when the driver signs (server sets
    // drugAlcoholPolicyReceipt → this snapshot fires). No manual re-open needed.
    try { _refreshOpenDaReceiptSection(); } catch (_) {}

    // TASK-228 deep-link — open the requested employee detail modal once
    // people data lands. Drained on first successful match so a subsequent
    // onSnapshot re-fire doesn't re-open the modal.
    try {
      const focus = window.AppState?.pendingHrFocus;
      if (focus && focus.type === 'people' && focus.id) {
        const match = HRState.people.find(p => p.id === focus.id);
        if (match) {
          window.AppState.pendingHrFocus = null;
          setTimeout(() => openEmployeeDetail(focus.id), 0);
        }
      }
    } catch (e) {
      console.warn('[HR] pendingHrFocus(people) drain failed', e);
    }

    // TASK-9985 — the HR nav badge was tapped from another view: land on the
    // directory filtered to exactly the people it counted. Drained once.
    try {
      if (window.AppState?.pendingHrDirectoryFilter === 'redTags' && HRState.currentView === 'landing'
          && document.getElementById('hr-employee-directory')) {
        window.AppState.pendingHrDirectoryFilter = null;
        setTimeout(() => openRedTagDirectory(), 0);
      }
    } catch (e) {
      console.warn('[HR] pendingHrDirectoryFilter drain failed', e);
    }
  }, (error) => {
    console.error('[HR] Error loading people:', error);
    HRState.people = [];
    HRState.peopleLoaded = true;
  });
  HRState.unsubscribers.push(unsubPeople);

  // Subscribe to DVIR accountability flags (flagged + dismissed — dismissed stay in HR until deleted)
  if (window.AppState?.settings?.dvirAccountability) {
    const dvirFlagsQuery = query(
      collection(db, 'tenants', tenantId, 'dvirAccountability'),
      where('status', 'in', ['flagged', 'dismissed'])
    );
    const unsubDvirFlags = onSnapshot(dvirFlagsQuery, (snapshot) => {
      HRState.dvirFlags = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }, (error) => {
      console.error('[HR] Error loading DVIR flags:', error);
    });
    HRState.unsubscribers.push(unsubDvirFlags);
  }

  // Subscribe to JSA accountability flags (flagged + dismissed)
  if (window.AppState?.settings?.jsaAccountability) {
    const jsaFlagsQuery = query(
      collection(db, 'tenants', tenantId, 'jsaAccountability'),
      where('status', 'in', ['flagged', 'dismissed'])
    );
    const unsubJsaFlags = onSnapshot(jsaFlagsQuery, (snapshot) => {
      HRState.jsaFlags = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    }, (error) => {
      console.error('[HR] Error loading JSA flags:', error);
    });
    HRState.unsubscribers.push(unsubJsaFlags);
  }

  // Subscribe to drug tests (HR owns this data now)
  const drugTestsQuery = query(
    collection(db, 'tenants', tenantId, 'drugTests'),
    orderBy('createdAt', 'desc')
  );
  const unsubDrugTests = onSnapshot(drugTestsQuery, (snapshot) => {
    HRState.drugTests = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  }, (error) => {
    console.log('[HR] No drugTests collection yet, that is okay');
    HRState.drugTests = [];
  });
  HRState.unsubscribers.push(unsubDrugTests);

  // Subscribe to candidates early so dashboard tile has data
  if (!HRState.candidateSubscribed) {
    subscribeToCandidates(tenantId);
  }

  // Subscribe to HR forms
  const formsQuery = query(
    collection(db, 'tenants', tenantId, 'hrForms'),
    orderBy('createdAt', 'desc')
  );

  const unsubForms = onSnapshot(formsQuery, (snapshot) => {
    HRState.hrForms = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    renderFormsList();
  }, (error) => {
    console.log('[HR] No hrForms collection yet, that is okay');
    HRState.hrForms = [];
    renderFormsList();
  });
  HRState.unsubscribers.push(unsubForms);

  // Subscribe to HR checklists
  const checklistsQuery = query(
    collection(db, 'tenants', tenantId, 'hrChecklists'),
    orderBy('lastUpdated', 'desc')
  );

  const unsubChecklists = onSnapshot(checklistsQuery, (snapshot) => {
    HRState.hrChecklists = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    renderChecklistsList();
  }, (error) => {
    console.log('[HR] No hrChecklists collection yet, that is okay');
    HRState.hrChecklists = [];
    renderChecklistsList();
  });
  HRState.unsubscribers.push(unsubChecklists);

  // Subscribe to HR reviews
  const reviewsQuery = query(
    collection(db, 'tenants', tenantId, 'hrReviews'),
    orderBy('createdAt', 'desc')
  );

  const unsubReviews = onSnapshot(reviewsQuery, (snapshot) => {
    HRState.hrReviews = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    renderReviewsList();
  }, (error) => {
    console.log('[HR] No hrReviews collection yet, that is okay');
    HRState.hrReviews = [];
    renderReviewsList();
  });
  HRState.unsubscribers.push(unsubReviews);

  // Subscribe to time-off requests
  const timeOffQuery = query(
    collection(db, 'tenants', tenantId, 'timeOff'),
    orderBy('startDate', 'desc')
  );

  const unsubTimeOff = onSnapshot(timeOffQuery, (snapshot) => {
    HRState.timeOffRequests = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    renderTimeOffList();
    // Chris 2026-07-13 — live-refresh the My Time Off history while the
    // Request Time Off modal is open so a newly submitted request shows up
    // without re-opening (mirrors timeoff.js subscribeToData).
    if (!document.getElementById('modal-hr-timeoff-request')?.classList.contains('hidden')) {
      renderTimeOffHistory();
    }
    // TASK-9535 — an approval changes "used"; keep the open balance table honest.
    if (HRState.timeOffBalancesOpen) renderTimeOffBalancesTable();
    // TASK-9602 — directory cards carry days off / next off / pending, and an
    // open employee Time Off tab shows the history: repaint both. The
    // directory keeps its search / position / compliance filters.
    try { applyDirectoryFilters(); } catch (_) { /* view mid-teardown */ }
    const _openToTab = document.getElementById('hr-detail-tab-timeoff-balance');
    const _detailModal = document.getElementById('modal-employee-detail') || document.getElementById('employee-detail-modal');
    if (_openToTab && _openToTab.style.display !== 'none' && HRState.selectedEmployee?.id
        && !(_detailModal && _detailModal.classList.contains('hidden'))) {
      loadTimeOffBalance(HRState.selectedEmployee.id);
    }
  }, (error) => {
    console.log('[HR] No timeOff collection yet, that is okay');
    HRState.timeOffRequests = [];
    renderTimeOffList();
  });
  HRState.unsubscribers.push(unsubTimeOff);

  // Subscribe to safety sign-ins — UNBOUNDED ON PURPOSE.
  //
  // 🔴 TASK-4883 — this comment used to read "(last 30 days)". It was WRONG:
  // there is no where() clause here and there never was. Do NOT "fix the query
  // to match the comment" — a 30-day window would hide the older half of an
  // OSHA-adjacent attendance record from the office, and Chris's requirement
  // (2026-09-10) is the opposite: "we should keep the full document available
  // to the driver for at least 6 months."
  //
  // Retention is already satisfied and was re-verified against prod 2026-09-22:
  // nothing in functions/src ever deletes a safetySignIns doc, there is no TTL
  // fieldOverride on the collection in firestore.indexes.json, and the Storage
  // bucket (dispatch-deck-be88c.firebasestorage.app, which holds the meeting
  // PDFs) has lifecycle = null. trippin-trucking's oldest live sheet is
  // 2026-02-12 — 7.3 months old and still present.
  const safetyQuery = query(
    collection(db, 'tenants', tenantId, 'safetySignIns'),
    orderBy('date', 'desc')
  );

  const unsubSafety = onSnapshot(safetyQuery, (snapshot) => {
    HRState.safetySignIns = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    HRState.safetySignInsLoaded = true; // TASK-9149 — a real answer, not the [] initializer
    renderSafetySignInList();
    initSignInDateDefaults();
  }, (error) => {
    console.log('[HR] No safetySignIns collection yet, that is okay');
    HRState.safetySignIns = [];
    HRState.safetySignInsLoaded = false; // TASK-9149 (COD r2) — [] after an error proves nothing
    renderSafetySignInList();
    initSignInDateDefaults();
  });
  HRState.unsubscribers.push(unsubSafety);

  // Subscribe to time-off policy
  const policyRef = doc(db, 'tenants', tenantId, 'hrTimeOffPolicy', 'config');
  const unsubPolicy = onSnapshot(policyRef, (docSnap) => {
    HRState.timeOffPolicy = docSnap.exists() ? docSnap.data() : null;
    // TASK-9535 — balances depend on the policy; refresh what is showing.
    try { renderTimeOffList(); if (HRState.timeOffBalancesOpen) renderTimeOffBalancesTable(); } catch (e) { console.warn('[HR] time-off refresh after policy snapshot failed', e); }
  }, (error) => {
    console.log('[HR] No hrTimeOffPolicy collection yet, that is okay');
    HRState.timeOffPolicy = null;
  });
  HRState.unsubscribers.push(unsubPolicy);

  // Subscribe to HR resources (company docs/links)
  const resourcesRef = collection(db, 'tenants', tenantId, 'hrResources');
  const unsubResources = onSnapshot(resourcesRef, (snapshot) => {
    HR_RESOURCES = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    // Sort by createdAt client-side (avoids Firestore query issues)
    HR_RESOURCES.sort((a, b) => {
      const aT = a.createdAt?.toDate?.()?.getTime() || 0;
      const bT = b.createdAt?.toDate?.()?.getTime() || 0;
      return aT - bT;
    });
    window._hrResources = HR_RESOURCES; // Share across module instances
    console.log('[HR] Resources loaded:', HR_RESOURCES.length, 'docs:', HR_RESOURCES.filter(r => r.type === 'document').length);
    _hrResourcesLoaded.value = true; // TASK-484 #2: first snapshot resolved
    renderHrResources();
    // TASK-772 — keep the Safety Records list (a filtered view of the same
    // hrResources store) live when the Safety sub-view is open.
    try { renderSafetyRecordsList(); } catch (_) {}
    // TASK-9653 S1 — a Claude "open in DispatchDeck" link waiting for HR opens
    // now that the folder list it names is loaded (one-shot; see the helper).
    _drainHrOpenHint();
  }, (err) => {
    console.error('[HR] hrResources listener error:', err.code, err.message);
    HR_RESOURCES = [];
    window._hrResources = HR_RESOURCES;
    _hrResourcesLoaded.value = true; // TASK-484 #2: settle the load state even on error
    renderHrResources();
    _drainHrOpenHint(); // TASK-9653 S1 — no folders loaded = shown as not found, user picks
  });
  HRState.unsubscribers.push(unsubResources);

  // Register fields with Smart Assistant
  if (window.SmartAssistant) {
    const SA = window.SmartAssistant;
    SA.register('disc-issue', () => [], 'HR', { formatOnly: true });
    SA.register('disc-pip-expectations', () => [], 'HR', { formatOnly: true });
    SA.register('review-notes', () => [], 'HR', { formatOnly: true });
  }
}

// ============================================
// OSHA 1910.1020(g) — refresh the annual-notification card subtitle from
// HRState.people (the authoritative source, populated by onSnapshot).
// Called from subscribeToData on every people-snapshot tick AND exposed via
// HRModule so the modal in app.js can read live people without depending
// on the unpopulated `AppState.people`.
// ============================================
function updateOshaNotificationBadge() {
  const subtitleEl = document.getElementById('osha-notif-card-subtitle');
  if (!subtitleEl) return; // card not rendered (toggle off or different view)
  try {
    const helpers = window.OshaRecords;
    if (!helpers?.listPeopleNeedingNotification) {
      subtitleEl.textContent = '1910.1020(g) reminders';
      return;
    }
    const due = helpers.listPeopleNeedingNotification(HRState.people || []);
    subtitleEl.textContent = due.length
      ? `1910.1020(g) reminders · ${due.length} due`
      : '1910.1020(g) reminders · all current';
  } catch (err) {
    console.warn('[HR] OSHA notification badge refresh failed:', err);
    subtitleEl.textContent = '1910.1020(g) reminders';
  }
}

function getOshaPeopleForNotification() {
  // Read live HRState.people (loaded via onSnapshot in subscribeToData).
  // Used by app.js openOshaNotificationModal — see CODE REVIEWER note from
  // the COD review of 2026-05-11: AppState.people is never populated.
  return Array.isArray(HRState.people) ? HRState.people : [];
}

// ============================================
// RENDER FUNCTIONS
// ============================================
function updateDropdowns() {
  // TASK-4762 — the person id lands inside an ATTRIBUTE value, and these three
  // option-value sites interpolated it RAW: no escaper at all, not escapeHtml,
  // not escAttr. window.escAttr is the attribute-context helper and is what
  // app.js:2929 already uses for the identical `<option value="…">` shape.
  //
  // Honest severity: this is NOT a live exploit, on two counts. (1) `p.id` is a
  // Firestore document id — SDK-generated [A-Za-z0-9], it has never contained a
  // quote. (2) The often-repeated "escapeHtml is not attribute-safe" warning
  // does NOT apply in this file: hr.js's escapeHtml (:8759) delegates to
  // window.escapeHtml (sharedFormat.js), which escapes the FULL &<>"' set —
  // byte-identical to escAttr. The defect here is simply the absence of any
  // escaper, plus the fact that the correct one exists and this call site did
  // not reach for it — so the next person who copies this pattern for a
  // user-controlled field (a slug, an imported external id, a composite key)
  // inherits an unescaped attribute sink with no sign anything is wrong.
  const escA = window.escAttr || escapeHtml;

  // Update supervisor dropdowns
  const supervisorSelects = document.querySelectorAll('#disc-supervisor, #review-reviewer');
  supervisorSelects.forEach(select => {
    const currentValue = select.value;
    select.innerHTML = '<option value="">Select supervisor...</option>' +
      HRState.supervisors.map(p =>
        `<option value="${escA(p.id)}">${escapeHtml(p.name)}</option>`
      ).join('');
    if (currentValue) select.value = currentValue;
  });

  // Update employee dropdowns.
  // TASK-4532 — Disciplinary + New Hire Checklist list ACTIVE people only (TITS:
  // 34 ever-hired, 17 active — the picker was unusable). Filter on `active`, NOT
  // `status` (status is wrong on 15 of 17 live rows). #review-employee stays
  // UNFILTERED: exit interviews are written about people who just left. An
  // already-selected inactive id is re-appended so historical write-ups stay
  // editable instead of silently losing their subject.
  const employeeSelects = document.querySelectorAll('#disc-employee, #checklist-employee, #review-employee');
  employeeSelects.forEach(select => {
    const currentValue = select.value;
    const pool = select.id === 'review-employee'
      ? HRState.people
      : HRState.people.filter(p => p.active !== false);
    let opts = pool.map(p =>
      `<option value="${escA(p.id)}">${escapeHtml(p.name)}</option>`
    ).join('');
    if (currentValue && !pool.some(p => p.id === currentValue)) {
      const cur = HRState.people.find(p => p.id === currentValue);
      if (cur) opts += `<option value="${escA(cur.id)}">${escapeHtml(cur.name)} (inactive)</option>`;
    }
    select.innerHTML = '<option value="">Select employee...</option>' + opts;
    if (currentValue) select.value = currentValue;
  });
}

// ============================================
// HR RESOURCES (Company Docs & Links)
// ============================================

// Company Documents collapse state — per-tenant, persisted in localStorage.
// TASK-1379: the persisted toggle is READ at seed time in _syncCompanyDocsCollapseUI
// (user's saved choice wins; the count-based default only applies when the key is
// unset). _companyDocsCollapseSeeded gates the seed to once per view entry (reset
// in initHR) so live re-renders never override a mid-session toggle (TASK-445).
const _companyDocsCollapseSeeded = { value: false };
// TASK-484 #2: true once the first hrResources onSnapshot has resolved. Lets
// _syncCompanyDocsCollapseUI distinguish "still loading" (keep collapsed, show
// a header spinner) from "loaded + genuinely empty" (expand to show the CTA).
const _hrResourcesLoaded = { value: false };

function _companyDocsStorageKey() {
  const tid = HRState.tenantId || 'default';
  return `dd.hr.companyDocs.collapsed.${tid}`;
}

function _readCompanyDocsCollapsed() {
  try {
    const v = localStorage.getItem(_companyDocsStorageKey());
    if (v === '1') return true;
    if (v === '0') return false;
  } catch (_) {}
  return null; // unset
}

function _writeCompanyDocsCollapsed(collapsed) {
  try { localStorage.setItem(_companyDocsStorageKey(), collapsed ? '1' : '0'); } catch (_) {}
}

function _syncCompanyDocsCollapseUI(count) {
  const chip = document.getElementById('hr-resources-count-chip');
  const body = document.getElementById('hr-resources-collapsible');
  const chev = document.getElementById('chevron-company-docs');
  const spin = document.getElementById('hr-resources-loading-spin');
  if (!body || !chev) return;

  const loaded = _hrResourcesLoaded.value;

  // Compact header spinner only while the first snapshot is still in flight.
  if (spin) spin.style.display = loaded ? 'none' : 'inline-block';

  // Count chip — visible once loaded with at least one doc (shows expanded + collapsed).
  if (chip) {
    if (loaded && count > 0) {
      chip.textContent = count === 1 ? '1 doc' : `${count} docs`;
      chip.style.display = 'inline-block';
    } else {
      chip.style.display = 'none';
    }
  }

  // TASK-484 #2: during the load window (before the first snapshot resolves) keep
  // the section deterministically COLLAPSED. The old code took the count===0 else
  // branch while loading and forced the section open with a big grid spinner, then
  // collapsed it once docs arrived — an expand→collapse flash that read as
  // "uncollapsed by default" on a slow cell connection. The compact header spinner
  // is the loading affordance now.
  if (!loaded) {
    body.style.display = 'none';
    chev.textContent = 'expand_more';
    return;
  }

  // Seed the collapse state ONCE per view entry (flag reset in initHR, which
  // backToHRLanding routes through), NOT on every render. TASK-445: the old
  // unconditional collapse fired on every category-chip click and every live
  // hrResources onSnapshot, yanking the section shut mid-interaction.
  // TASK-1379 (mirrors _syncEmpNotesCollapseUI): the user's persisted toggle
  // wins; only when the localStorage key is unset do we fall back to the
  // count default — ≤5 docs expanded (fresh tenants), >5 collapsed (heavy
  // tenants). Previously the persisted value was written but never read, so
  // the section re-collapsed on every HR entry regardless of the user's choice.
  if (count > 0) {
    if (!_companyDocsCollapseSeeded.value) {
      const persisted = _readCompanyDocsCollapsed();
      const collapsed = persisted === null ? (count > 5) : persisted;
      body.style.display = collapsed ? 'none' : 'block';
      chev.textContent = collapsed ? 'expand_more' : 'expand_less';
      _companyDocsCollapseSeeded.value = true;
    }
    // Seed already consumed → do NOT touch body.style.display (preserve state).
  } else {
    // Loaded + genuinely empty → stay expanded so the "Add your first document"
    // CTA shows (persisted state deliberately ignored — a collapsed empty card
    // reads as a dead header).
    body.style.display = 'block';
    chev.textContent = 'expand_less';
  }
}

function toggleCompanyDocsSection() {
  const body = document.getElementById('hr-resources-collapsible');
  const chev = document.getElementById('chevron-company-docs');
  if (!body) return;
  const willCollapse = body.style.display !== 'none';
  body.style.display = willCollapse ? 'none' : 'block';
  if (chev) chev.textContent = willCollapse ? 'expand_more' : 'expand_less';
  _writeCompanyDocsCollapsed(willCollapse);
}

// Category filter persistence — per-tenant. 'all' or 'uncat' or a literal category string.
// Mirrors the localStorage pattern used for _companyDocsStorageKey above.
function _hrDocCategoryKey() {
  const tid = HRState?.tenantId || (window.AppState && window.AppState.tenant) || 'unknown';
  return `dd.hr.docs.category.${tid}`;
}
function _readHrDocCategory() {
  try { return localStorage.getItem(_hrDocCategoryKey()) || 'all'; } catch (_) { return 'all'; }
}
function _writeHrDocCategory(cat) {
  try { localStorage.setItem(_hrDocCategoryKey(), cat || 'all'); } catch (_) {}
}

function renderHrResources() {
  const container = document.getElementById('hr-resources-grid');
  if (!container) return;

  const resources = window._hrResources || HR_RESOURCES;

  // Update collapsible count chip + seed collapse state (once per view entry).
  // TASK-1379: the user's persisted toggle (localStorage, per tenant) wins; when
  // unset, default rule applies — ≤5 docs = expanded (useful on fresh tenants),
  // >5 = collapsed (cuts wall-of-noise on heavy tenants like TITS with 16+ docs).
  _syncCompanyDocsCollapseUI(resources?.length || 0);

  // TASK-483 — reflect the "Driver View" master-enable toggle state in the header.
  _syncDriverDocsButton();

  // Empty state — small, friendly, and leads directly to the Add button in the header
  if (!resources || resources.length === 0) {
    container.innerHTML = `
      <div class="hr-resources-empty">
        <div class="hr-resources-empty-icon">
          <span class="material-icons">folder_open</span>
        </div>
        <div class="hr-resources-empty-title">No documents yet</div>
        <div class="hr-resources-empty-desc">Upload policies, manuals, or add links to external resources your team should be able to find in one place.</div>
        <button class="btn btn-primary hr-resources-empty-btn" onclick="window.HRModule.showAddDocumentMenu(event)">
          <span class="material-icons">add</span>
          Add your first document
        </button>
      </div>
    `;
    // Hide chip strip if it exists from a previous render with docs.
    const strip = document.getElementById('hr-docs-category-strip');
    if (strip) strip.style.display = 'none';
    return;
  }

  // Derive category set from existing docs. Uncategorized is its own bucket.
  // Sort alphabetically; "Uncategorized" pinned to the end if present.
  const catCounts = new Map();
  resources.forEach(r => {
    const cat = (r.category || '').trim();
    const key = cat || '__uncat__';
    catCounts.set(key, (catCounts.get(key) || 0) + 1);
  });
  const sortedCats = [...catCounts.keys()]
    .filter(k => k !== '__uncat__')
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  if (catCounts.has('__uncat__')) sortedCats.push('__uncat__');

  const activeCat = _readHrDocCategory();
  // Coerce stale selection: if active is a literal category but no docs have it
  // any more, fall back to All.
  const validActive = activeCat === 'all'
    || activeCat === '__uncat__' && catCounts.has('__uncat__')
    || (activeCat !== '__uncat__' && activeCat !== 'all' && catCounts.has(activeCat));
  const finalActive = validActive ? activeCat : 'all';
  if (finalActive !== activeCat) _writeHrDocCategory(finalActive);

  // Render chip strip (insert/update sibling to the grid).
  _renderHrDocCategoryStrip(resources.length, sortedCats, catCounts, finalActive);

  // Filter resources by active category.
  const filtered = finalActive === 'all'
    ? resources
    : finalActive === '__uncat__'
      ? resources.filter(r => !(r.category || '').trim())
      : resources.filter(r => (r.category || '').trim() === finalActive);

  // Show empty-filter state if the filter yields nothing
  if (filtered.length === 0) {
    const label = finalActive === '__uncat__' ? 'Uncategorized' : escapeHtml(finalActive);
    container.innerHTML = `
      <div class="hr-resources-empty">
        <div class="hr-resources-empty-icon"><span class="material-icons">folder_open</span></div>
        <div class="hr-resources-empty-title">No documents in ${label}</div>
        <div class="hr-resources-empty-desc">Upload a document and it'll be tagged with this category automatically.</div>
        <button class="btn btn-primary hr-resources-empty-btn" onclick="window.HRModule.showAddDocumentMenu(event)">
          <span class="material-icons">add</span>
          Upload here
        </button>
      </div>
    `;
    return;
  }

  const cards = filtered.map(r => {
    const isDoc = r.type === 'document';
    const icon = r.generated ? 'auto_awesome' : isDoc ? (r.mimeType?.includes('pdf') ? 'picture_as_pdf' : 'description') : (r.icon || 'link');
    const iconColor = r.generated ? '#8B5CF6' : isDoc ? (r.mimeType?.includes('pdf') ? '#dc2626' : '#3B82F6') : '';
    const sizeStr = r.sizeBytes ? ` · ${(r.sizeBytes / 1024 / 1024).toFixed(1)} MB` : '';
    const genBadge = r.generated ? ' <span style="font-size:0.65rem;background:#8B5CF620;color:#7C3AED;padding:1px 6px;border-radius:8px;vertical-align:middle;">Smart Draft</span>' : '';
    const desc = isDoc ? `${escapeHtml(r.fileName || '')}${sizeStr}` : escapeHtml(r.desc || '');

    const isShared = r.publiclyShared === true;
    // 2026-05-19 — aiSearchEnabled toggle. Default ON: missing field = true so
    // existing docs are still picked up by Smart Search. Admin clicks the
    // brain icon to exclude a doc (sensitive legal letters, exec comp, etc).
    const aiOn = r.aiSearchEnabled !== false;
    const aiOnlyForDocs = isDoc; // Links are URL-only — AI can't read them today
    const catColor = r.category ? '#3B82F6' : '#94A3B8';
    // TASK-484 #3: all actions live in ONE in-flow flex cluster (.hr-resource-actions,
    // margin-left:auto, flex-shrink:0) instead of position:absolute icons + a
    // padding-right reservation on .hr-resource-info. The old layout did not account
    // for the in-flow download icon, so on a narrow single-column mobile grid all 5
    // icons piled into the same right region and spilled past the card edge.
    return `
      <a href="${_safeHttpUrl(r.url)}" target="_blank" rel="noopener noreferrer" class="hr-resource-card"
         draggable="true"
         ondragstart="window.HRModule._onDocDragStart(event, '${r.id}')"
         ondragend="window.HRModule._onDocDragEnd(event)">
        <div class="hr-resource-icon"${iconColor ? ` style="background:${iconColor}15;color:${iconColor}"` : ''}>
          <span class="material-icons">${icon}</span>
        </div>
        <div class="hr-resource-info" style="min-width:0;">
          <div style="display:flex;align-items:flex-start;gap:4px;">
            <div class="hr-resource-name" style="flex:1;min-width:0;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;word-break:break-word;">${escapeHtml(r.title)}${genBadge}</div>
            <button type="button" class="hr-resource-action-btn" style="flex-shrink:0;padding:0 2px;color:#94A3B8;" onclick="event.preventDefault();event.stopPropagation();window.HRModule.renameHrResource('${r.id}')" title="Rename"><span class="material-icons" style="font-size:14px;">edit</span></button>
          </div>
          <div class="hr-resource-desc" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${desc}</div>
          ${r.category ? `<div class="hr-resource-category-inline" title="${escapeHtml(r.category)}">
            <span class="material-icons" style="font-size:11px;vertical-align:middle;margin-right:3px;">folder</span>${escapeHtml(r.category)}
          </div>` : ''}
        </div>
        <div class="hr-resource-actions">
          ${aiOnlyForDocs ? `<button type="button" class="hr-resource-action-btn" onclick="event.preventDefault();event.stopPropagation();window.HRModule.toggleAiSearch('${r.id}',${!aiOn})"
            style="color:${aiOn ? '#8B5CF6' : '#94A3B8'};" title="${aiOn ? 'Smart Search reads this — click to hide from AI' : 'Hidden from Smart Search — click to include'}">
            <span class="material-icons">psychology</span>
          </button>` : ''}
          <button type="button" class="hr-resource-action-btn" onclick="event.preventDefault();event.stopPropagation();window.HRModule.setDocCategory('${r.id}')"
            style="color:${catColor};" title="${r.category ? 'Category: ' + escapeHtml(r.category) + ' — click to change' : 'Click to tag with a category'}">
            <span class="material-icons">folder${r.category ? '_open' : ''}</span>
          </button>
          <button type="button" class="hr-resource-action-btn" onclick="event.preventDefault();event.stopPropagation();window.HRModule.togglePublicShare('${escJsAttr(r.id)}',${!isShared})"
            style="color:${isShared ? '#10B981' : '#94A3B8'};" title="${isShared ? 'Shared publicly — click to hide' : 'Share on public docs page'}">
            <span class="material-icons">${isShared ? 'visibility' : 'visibility_off'}</span>
          </button>
          <span class="material-icons hr-resource-external" title="${isDoc ? 'Download' : 'Open link'}">${isDoc ? 'download' : 'open_in_new'}</span>
          <button type="button" class="hr-resource-action-btn" onclick="event.preventDefault();event.stopPropagation();window.HRModule.deleteHrResource('${escJsAttr(r.id)}'${isDoc ? ",'" + escJsAttr(r.storagePath || '') + "'" : ''})"
            title="Remove">
            <span class="material-icons">close</span>
          </button>
        </div>
      </a>
    `;
  }).join('');

  container.innerHTML = cards;
}

// Filter chip strip — derived from existing doc categories. Renders into a
// container that sits as a sibling above #hr-resources-grid inside the
// collapsible. The container is created on first render.
function _renderHrDocCategoryStrip(totalCount, sortedCats, catCounts, activeCat) {
  let strip = document.getElementById('hr-docs-category-strip');
  if (!strip) {
    const collapsible = document.getElementById('hr-resources-collapsible');
    const grid = document.getElementById('hr-resources-grid');
    if (!collapsible || !grid) return;
    strip = document.createElement('div');
    strip.id = 'hr-docs-category-strip';
    strip.className = 'hr-docs-category-strip';
    collapsible.insertBefore(strip, grid);
  }
  strip.style.display = '';
  // Show the strip whenever there's at least one user-assigned category,
  // OR more than 5 total docs (so admins discover the feature on busy tenants).
  const hasAnyCategorized = sortedCats.some(c => c !== '__uncat__');
  if (!hasAnyCategorized && totalCount <= 5) {
    strip.innerHTML = `<button type="button" class="hr-docs-category-chip-empty-hint" onclick="window.HRModule.promptCategorizeAny()">
      <span class="material-icons" style="font-size:13px;vertical-align:middle;">folder</span>
      <span style="margin-left:4px;">Organize into categories</span>
    </button>`;
    return;
  }
  // TASK-4838 — `id` is the tenant-authored CATEGORY STRING itself (see the chips
  // array below: chip(c, c, …)), not an opaque key. The old
  //   escapeHtml(id).replace(/'/g, "\\'")
  // was a NO-OP that read like a fix: escapeHtml runs first and has already turned
  // every apostrophe into `&#39;`, so the .replace finds nothing left to escape.
  // The HTML parser then decodes `&#39;` back to `'` BEFORE compiling the handler,
  // which is why a folder named "Driver's Files" silently killed its own chip.
  // escJsAttr escapes for the JS-string layer first, then the attribute layer.
  // data-cat is a plain attribute and escapeHtml (=escAttr here) is correct for it.
  const chip = (id, label, count, active) => `
    <button type="button" class="hr-docs-category-chip${active ? ' hr-docs-category-chip-active' : ''}"
      data-cat="${escapeHtml(id)}"
      onclick="window.HRModule.filterByCategory('${escJsAttr(id)}')"
      ondragover="window.HRModule._onChipDragOver(event)"
      ondragleave="window.HRModule._onChipDragLeave(event)"
      ondrop="window.HRModule._onChipDrop(event, '${escJsAttr(id)}')">
      ${escapeHtml(label)} <span class="hr-docs-category-chip-count">${count}</span>
    </button>`;
  const chips = [
    chip('all', 'All', totalCount, activeCat === 'all'),
    ...sortedCats.map(c => c === '__uncat__'
      ? chip('__uncat__', 'Uncategorized', catCounts.get('__uncat__') || 0, activeCat === '__uncat__')
      : chip(c, c, catCounts.get(c) || 0, activeCat === c))
  ];
  strip.innerHTML = chips.join('');
}

function filterByCategory(cat) {
  _writeHrDocCategory(cat || 'all');
  renderHrResources();
}

async function setDocCategory(resourceId) {
  const resources = window._hrResources || HR_RESOURCES;
  const r = resources.find(x => x.id === resourceId);
  if (!r) return;
  // Existing categories (excluding empty/uncategorized) — show as hint so the
  // user can match an existing one instead of creating "Policies" vs "policies".
  const existing = [...new Set(resources.map(x => (x.category || '').trim()).filter(Boolean))].sort();
  const hint = existing.length
    ? `Existing: ${existing.join(', ')}`
    : 'e.g. Policies, Forms, Training';
  const current = r.category || '';
  const next = window.prompt(`Category for "${r.title}"\n${hint}\n\nLeave blank for Uncategorized.`, current);
  if (next === null) return; // cancel
  const trimmed = (next || '').trim().slice(0, 32);
  if (trimmed === current) return;
  // Case-insensitive collision: match an existing category's exact casing if
  // user typed a different case ("policies" vs existing "Policies" → use existing).
  const canonical = existing.find(e => e.toLowerCase() === trimmed.toLowerCase()) || trimmed;
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'hrResources', resourceId), {
      category: canonical || null
    });
    window.showToast(canonical ? `Moved to "${canonical}"` : 'Moved to Uncategorized', 'success');
  } catch (err) {
    console.error('[HR] setDocCategory error:', err);
    window.showToast('Could not update category', 'error');
  }
}

// TASK-1325: sanitize a user-entered document title — drop control chars,
// zero-width + bidi-override codepoints (the title renders on the driver Doc
// Page + the public ?docs= page), trim, cap 120. '' if nothing usable remains.
function _sanitizeDocTitle(s) {
  const out = [];
  for (const ch of String(s == null ? '' : s)) {
    const c = ch.codePointAt(0);
    if (c < 0x20 || c === 0x7F) continue;               // control
    if (c >= 0x200B && c <= 0x200F) continue;           // zero-width
    if (c >= 0x202A && c <= 0x202E) continue;           // bidi embed/override
    if (c >= 0x2066 && c <= 0x2069) continue;           // bidi isolate
    if (c === 0xFEFF) continue;                         // BOM
    out.push(ch);
  }
  return out.join('').trim().slice(0, 120);
}

// TASK-1325: inline rename of an hrResources doc/link title (pencil next to the
// title). Mirrors setDocCategory's window.prompt affordance. title is in the
// TASK-1326 field-mask allowlist, so the plain updateDoc is permitted for
// hr-module members; a viewUploadOnly member's write denies at the rule (same
// as the sibling category/share buttons already behave).
async function renameHrResource(resourceId) {
  const resources = window._hrResources || HR_RESOURCES;
  const r = resources.find(x => x.id === resourceId);
  if (!r) return;
  const current = r.title || '';
  const next = window.prompt('Rename this document', current);
  if (next === null) return; // cancel
  const cleaned = _sanitizeDocTitle(next);
  if (!cleaned) { window.showToast('Name cannot be empty', 'error'); return; }
  if (cleaned === current) return;
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'hrResources', resourceId), { title: cleaned });
    // TASK-1495 — shared writer (window.logAudit), not a direct addDoc: it stamps
    // category/action/timestamp/user and strips undefined before the write, so the
    // row is guaranteed to carry the `timestamp` the "who changed X" viewer filters
    // on. `type` stays in the payload so old and new rows share a filter key.
    // Category 'hr' matches the sibling rename/doc actions in this file; the
    // compliance.js twin keeps 'compliance' (same split as CLEARINGHOUSE_QUERY_LOGGED).
    try {
      await window.logAudit?.('hr', 'company_doc_renamed', {
        type: 'company_doc_renamed', resourceId, oldTitle: current, newTitle: cleaned,
        editedBy: window.AppState?.user?.email || window.AppState?.user?.uid || 'unknown',
      });
    } catch (auditErr) { console.warn('[HR] rename audit failed (non-blocking):', auditErr); }
    window.showToast('Renamed', 'success');
  } catch (err) {
    console.error('[HR] renameHrResource error:', err);
    window.showToast('Could not rename', 'error');
  }
}

// Drag-and-drop: card → category chip. Custom MIME so we don't fight the
// anchor's default text/uri-list drag payload (browsers drag the URL by
// default for <a>; we add our own data without preventing the default to keep
// keyboard accessibility intact).
const _DD_DOC_MIME = 'application/x-dd-resource-id';

function _onDocDragStart(ev, resourceId) {
  try {
    ev.dataTransfer.setData(_DD_DOC_MIME, String(resourceId));
    // Plain-text fallback so dropping into a text field doesn't show the URL.
    ev.dataTransfer.setData('text/plain', '');
    ev.dataTransfer.effectAllowed = 'move';
    ev.currentTarget?.classList.add('hr-resource-card-dragging');
  } catch (_) {}
}
function _onDocDragEnd(ev) {
  try { ev.currentTarget?.classList.remove('hr-resource-card-dragging'); } catch (_) {}
  // Belt-and-suspenders: clear any chip drag-over states left behind on snappy releases.
  document.querySelectorAll('.hr-docs-category-chip-drag-over')
    .forEach(el => el.classList.remove('hr-docs-category-chip-drag-over'));
}
function _onChipDragOver(ev) {
  if (!ev.dataTransfer || !ev.dataTransfer.types?.includes(_DD_DOC_MIME)) return;
  ev.preventDefault();
  ev.dataTransfer.dropEffect = 'move';
  ev.currentTarget.classList.add('hr-docs-category-chip-drag-over');
}
function _onChipDragLeave(ev) {
  ev.currentTarget.classList.remove('hr-docs-category-chip-drag-over');
}
async function _onChipDrop(ev, chipId) {
  ev.preventDefault();
  ev.currentTarget.classList.remove('hr-docs-category-chip-drag-over');
  const resourceId = ev.dataTransfer.getData(_DD_DOC_MIME);
  if (!resourceId) return;
  // Map chip id → category value: 'all' means clear-or-keep, '__uncat__' = clear.
  let nextCategory;
  if (chipId === 'all') return; // dropping on All is a no-op
  if (chipId === '__uncat__') nextCategory = null;
  else nextCategory = chipId;
  await setDocCategoryDirect(resourceId, nextCategory);
}

// Direct category-set (no prompt) — used by drag-drop.
async function setDocCategoryDirect(resourceId, category) {
  if (!resourceId || !HRState.tenantId) return;
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'hrResources', resourceId), {
      category: category || null
    });
    window.showToast?.(category ? `Moved to "${category}"` : 'Moved to Uncategorized', 'success');
  } catch (err) {
    console.error('[HR] setDocCategoryDirect error:', err);
    window.showToast?.('Could not update category', 'error');
  }
}

// "Organize into categories" hint button on tenants with few/no categorized docs.
function promptCategorizeAny() {
  const resources = window._hrResources || HR_RESOURCES;
  if (!resources?.length) return;
  // Pick the first uncategorized doc as a starter.
  const first = resources.find(r => !(r.category || '').trim()) || resources[0];
  setDocCategory(first.id);
}

// Add-menu popover — lets the user pick "Upload Document" or "Add Link"
// without dedicating two full rows of real estate to permanent dashed cards.
function showAddDocumentMenu(ev) {
  ev?.preventDefault?.();
  ev?.stopPropagation?.();

  // Close any existing menu first
  document.getElementById('hr-add-doc-menu')?.remove();

  const anchor = ev?.currentTarget || ev?.target?.closest('button');
  const rect = anchor?.getBoundingClientRect();

  const menu = document.createElement('div');
  menu.id = 'hr-add-doc-menu';
  menu.className = 'hr-add-doc-menu';
  menu.innerHTML = `
    <button type="button" class="hr-add-doc-menu-item" onclick="window.HRModule.uploadCompanyDocument();document.getElementById('hr-add-doc-menu')?.remove();">
      <span class="material-icons" style="color:#3B82F6;">upload_file</span>
      <div>
        <div class="hr-add-doc-menu-title">Upload Document</div>
        <div class="hr-add-doc-menu-desc">PDF, Word, images</div>
      </div>
    </button>
    <button type="button" class="hr-add-doc-menu-item" onclick="window.HRModule.showAddResourceModal();document.getElementById('hr-add-doc-menu')?.remove();">
      <span class="material-icons" style="color:#06B6D4;">link</span>
      <div>
        <div class="hr-add-doc-menu-title">Add Link</div>
        <div class="hr-add-doc-menu-desc">Google Drive, website, etc.</div>
      </div>
    </button>
  `;

  document.body.appendChild(menu);

  // Position relative to the Add button; on narrow viewports pin it below
  // the button and let the CSS clamp it inside the viewport.
  if (rect) {
    const menuWidth = 240;
    const vw = window.innerWidth;
    let left = rect.left;
    if (left + menuWidth > vw - 8) left = Math.max(8, vw - menuWidth - 8);
    menu.style.top = `${rect.bottom + 6}px`;
    menu.style.left = `${left}px`;
  } else {
    menu.style.top = '80px';
    menu.style.left = '16px';
  }

  // Close on outside click
  setTimeout(() => {
    const onClose = (e) => {
      if (!menu.contains(e.target)) {
        menu.remove();
        document.removeEventListener('click', onClose);
      }
    };
    document.addEventListener('click', onClose);
  }, 0);
}

function showAddResourceModal() {
  const html = `
    <div class="modal-content modal-small" style="max-width:420px;padding:0;">
      <div class="crm-detail-header" style="padding:16px 20px;">
        <div class="crm-detail-header-info">
          <div class="crm-detail-header-icon" style="width:36px;height:36px;">
            <span class="material-icons" style="font-size:20px;">link</span>
          </div>
          <h3 style="margin:0;font-size:1rem;">Add Company Link</h3>
        </div>
        <button class="modal-close" style="color:#fff;" onclick="document.getElementById('hr-add-resource-modal').classList.add('hidden')">&times;</button>
      </div>
      <div style="padding:20px;display:flex;flex-direction:column;gap:12px;">
        <div class="form-group" style="margin:0;">
          <label>Title *</label>
          <input type="text" id="hr-res-title" class="form-control" placeholder="e.g. Safety Policy">
        </div>
        <div class="form-group" style="margin:0;">
          <label>URL *</label>
          <input type="url" id="hr-res-url" class="form-control" placeholder="https://...">
        </div>
        <div class="form-group" style="margin:0;">
          <label>Description</label>
          <input type="text" id="hr-res-desc" class="form-control" placeholder="Brief description (optional)">
        </div>
        <div class="form-group" style="margin:0;">
          <label>Icon</label>
          <select id="hr-res-icon" class="form-control">
            <option value="menu_book">Book</option>
            <option value="description">Document</option>
            <option value="auto_stories">Manual</option>
            <option value="gavel">Policy</option>
            <option value="health_and_safety">Safety</option>
            <option value="person_add">Application</option>
            <option value="assignment">Form</option>
            <option value="link">Link</option>
          </select>
        </div>
      </div>
      <div class="modal-footer" style="display:flex;gap:8px;justify-content:flex-end;padding:16px 20px;">
        <button class="btn btn-secondary" onclick="document.getElementById('hr-add-resource-modal').classList.add('hidden')">Cancel</button>
        <button class="btn btn-primary" id="hr-save-resource-btn" onclick="window.HRModule.saveHrResource()">Add Link</button>
      </div>
    </div>
  `;

  let modal = document.getElementById('hr-add-resource-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'hr-add-resource-modal';
    modal.className = 'modal hidden';
    modal.style.zIndex = '1060';
    modal.onclick = (e) => { if (e.target === modal) modal.classList.add('hidden'); };
    document.body.appendChild(modal);
  }
  modal.innerHTML = html;
  modal.classList.remove('hidden');
  setTimeout(() => document.getElementById('hr-res-title')?.focus(), 100);
}

async function saveHrResource() {
  const title = document.getElementById('hr-res-title')?.value.trim();
  const url = document.getElementById('hr-res-url')?.value.trim();
  const desc = document.getElementById('hr-res-desc')?.value.trim();
  const icon = document.getElementById('hr-res-icon')?.value || 'link';

  if (!title || !url) {
    window.showToast('Title and URL are required', 'error');
    return;
  }

  const btn = document.getElementById('hr-save-resource-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    // Inherit currently-active category filter (if any) so docs added during a
    // "Policies" view land in Policies. 'all' / 'uncat' = no inheritance.
    const activeFilter = _readHrDocCategory();
    const inheritCategory = (activeFilter && activeFilter !== 'all' && activeFilter !== '__uncat__') ? activeFilter : null;
    await addDoc(collection(db, 'tenants', HRState.tenantId, 'hrResources'), {
      title, url, desc, icon,
      ...(inheritCategory ? { category: inheritCategory } : {}),
      createdAt: Timestamp.now()
    });
    document.getElementById('hr-add-resource-modal')?.classList.add('hidden');
    window.showToast('Link added', 'success');
  } catch (err) {
    console.error('[HR] Error saving resource:', err);
    window.showToast('Error saving link', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = 'Add Link'; }
  }
}

async function deleteHrResource(resourceId, storagePath) {
  if (!confirm('Remove this item?')) return;
  try {
    // Delete from Storage if it's an uploaded document
    if (storagePath) {
      try { await deleteObject(ref(storage, storagePath)); } catch (e) { /* file may already be gone */ }
    }
    await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'hrResources', resourceId));
    window.showToast('Removed', 'success');
  } catch (err) {
    console.error('[HR] Error deleting resource:', err);
    window.showToast('Error removing item', 'error');
  }
}

// Toggle public sharing on a resource
async function togglePublicShare(resourceId, share) {
  try {
    // TASK-483 soft-warn (Chris ask): at 25 shared docs, nudge — NO hard block.
    // Same eye-marked (publiclyShared) set drives both the public /?docs= link
    // AND the in-app driver "Doc Page", so the count is meaningful in both.
    if (share) {
      const resources = window._hrResources || HR_RESOURCES;
      const sharedCount = resources.filter(r => r.publiclyShared === true).length;
      if (sharedCount >= 25) {
        window.showToast(`Heads up — ${sharedCount} documents are already shared with drivers. Consider trimming the list so the important ones stay easy to find.`, 'info');
      }
    }
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'hrResources', resourceId), {
      publiclyShared: share
    });
    window.showToast(share ? 'Shared publicly' : 'Hidden from public page', 'success');
  } catch (err) {
    console.error('[HR] Error toggling public share:', err);
    window.showToast('Error updating', 'error');
  }
}

// TASK-483 — "Driver View" master enable. Flips settings.driverDocs.enabled on the
// tenant doc (admin-only write, mirrors roster.js toggleDriverDirectory). When ON,
// PIN drivers see a "Doc Page" tile in the Actions sheet (dispatch.js). Settings
// writes are admin-only at the rule level (firestore.rules tenant allow update) —
// a driver session can read settings but cannot enable this.
function _syncDriverDocsButton() {
  const btn = document.getElementById('hr-driver-docs-toggle');
  if (!btn) return;
  const on = window.AppState?.settings?.driverDocs?.enabled === true;
  btn.classList.toggle('btn-muted-active', on);
  btn.title = on
    ? 'Drivers can see shared documents in the app (Actions → Doc Page) — click to turn off'
    : 'Show shared documents to drivers inside the app (Actions → Doc Page)';
}

async function toggleDriverDocs() {
  const next = !(window.AppState?.settings?.driverDocs?.enabled === true);
  const ok = await window._settingsPersist?.({ driverDocs: { enabled: next } });
  if (ok) {
    _syncDriverDocsButton();
    window.showToast?.(`Driver document access ${next ? 'enabled' : 'disabled'}`, 'success');
  }
}

// TASK-483 — in-app driver "Doc Page" panel. Invoked from the driver Actions tile
// (dispatch.js _renderDriverActionTiles). Reuses the public-docs viewer logic from
// app.js loadPublicDocsPage, but stays IN-APP (no public /?docs= token/link) —
// drivers are already PIN-authed. Shows ONLY publiclyShared===true docs (the same
// eye-marked set), client-filtered to type==='document'. Defense-in-depth: self-
// gates on the tenant toggle so a console call can't bypass the feature flag.
async function openDriverDocs() {
  if (window.AppState?.settings?.driverDocs?.enabled !== true) return;

  // Resolve tenant from the driver session (HRState is null in a PIN session —
  // HR module isn't initialized for drivers).
  const tenantId = window.AppState?.tenant || HRState.tenantId;
  if (!tenantId) return;

  const escHTML = (s) => s ? String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') : '';
  // Block javascript:/data: and malformed URLs before injecting into attrs.
  const safeUrl = (u) => {
    try {
      const parsed = new URL(u);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '#';
      return escHTML(parsed.toString());
    } catch { return '#'; }
  };
  // TASK-1230 — RAW variant for DOM *property* assignment (el.href = ...),
  // the hr.js twin of app.js's TASK-1223 fix (local const there too; can't be
  // cross-imported, Pitfall #1). safeUrl's & → &amp; escaping is right inside
  // attribute MARKUP (parser decodes it) but wrong on the .href property (no
  // decoding): the Storage `&token=` became `amp;token=`, downloads 403'd, and
  // the browser saved the 73-byte JSON denial named after the storage path.
  const safeUrlRaw = (u) => {
    try {
      const parsed = new URL(u);
      if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '#';
      return parsed.toString();
    } catch { return '#'; }
  };

  // Build the modal shell immediately (loading state) so a slow read doesn't feel broken.
  let modal = document.getElementById('hr-driver-docs-modal');
  if (modal) modal.remove();
  modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'hr-driver-docs-modal';
  modal.innerHTML = `
    <div class="modal-content modal-fullscreen" style="display:flex;flex-direction:column;max-width:760px;height:90vh;height:90dvh;padding:0;overflow:hidden;">
      <div class="modal-header" style="flex-shrink:0;padding:14px 18px;">
        <h3 style="margin:0;"><span class="material-icons" style="vertical-align:middle;margin-right:6px;">library_books</span>Documents</h3>
        <button class="btn-icon" onclick="document.getElementById('hr-driver-docs-modal')?.remove()" aria-label="Close">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="flex-shrink:0;padding:12px 18px;border-bottom:1px solid var(--border-light,#e2e8f0);background:var(--bg-secondary);display:flex;align-items:center;gap:10px;">
        <select id="driver-docs-selector" onchange="window.HRModule._driverDocsSelect(this.value)"
          style="flex:1;padding:8px 12px;border:1px solid var(--border-light,#e2e8f0);border-radius:var(--radius-sm,8px);font-size:0.875rem;background:var(--bg-elevated);color:var(--text-primary);min-width:0;">
          <option value="" disabled selected>Loading…</option>
        </select>
        <a id="driver-docs-download" href="#" target="_blank" rel="noopener noreferrer" style="display:none;padding:8px 14px;background:var(--brand-blue,#3B82F6);color:#fff;border-radius:var(--radius-sm,8px);text-decoration:none;font-size:0.8rem;font-weight:600;white-space:nowrap;flex-shrink:0;">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;margin-right:2px;">download</span> Download
        </a>
      </div>
      <div id="driver-docs-viewer" style="flex:1;overflow:auto;background:#f1f5f9;display:flex;align-items:center;justify-content:center;">
        <div style="text-align:center;padding:48px 20px;color:var(--text-secondary,#475569);">
          <span class="material-icons" style="font-size:32px;animation:spin 0.8s linear infinite;">progress_activity</span>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  try {
    // SECURITY: the driver-read rule (firestore.rules hrResources) only permits
    // publiclyShared==true docs for a driver session — a bare collection read is
    // denied at list-level, so the where() filter is REQUIRED, not just an
    // optimization. Tenant isolation is enforced by hasDriverSession binding
    // session.tenantId == tenantId.
    const snap = await getDocs(
      query(
        collection(db, 'tenants', tenantId, 'hrResources'),
        where('publiclyShared', '==', true)
      )
    );
    // TASK-1288 — include BOTH uploaded documents AND link-type resources (e.g.
    // a driver survey / Google Form). Links used to be filtered out here so the
    // in-app Doc Page only showed viewable files, but that silently dropped any
    // eye-shared LINK from the driver side even though it showed on the company /
    // public docs page (app.js:4574 never filtered by type). _driverDocsSelect
    // renders a tap-through "Open" tile for links; PDFs/images keep the inline
    // viewer. Read auth is unchanged — the publiclyShared rule already covers both.
    const resources = snap.docs
      .map(d => ({ id: d.id, ...d.data() }));
    resources.sort((a, b) => {
      const aT = a.createdAt?.toDate?.()?.getTime() || 0;
      const bT = b.createdAt?.toDate?.()?.getTime() || 0;
      return aT - bT;
    });
    window._driverDocsResources = resources;

    const selector = document.getElementById('driver-docs-selector');
    const viewer = document.getElementById('driver-docs-viewer');
    if (!selector || !viewer) return; // modal closed mid-load

    if (resources.length === 0) {
      selector.innerHTML = '<option value="" disabled selected>No documents</option>';
      viewer.innerHTML = `
        <div style="text-align:center;padding:48px 20px;color:var(--text-secondary,#475569);">
          <span class="material-icons" style="font-size:48px;color:var(--border-light,#cbd5e1);margin-bottom:8px;">folder_open</span>
          <p style="margin:0;">No documents have been shared with you yet.</p>
        </div>`;
      return;
    }

    selector.innerHTML = `<option value="" disabled selected>Select a document…</option>` +
      resources.map((r, i) => {
        const isLink = r.type !== 'document';
        const sizeStr = r.sizeBytes ? ` (${(r.sizeBytes / 1024 / 1024).toFixed(1)} MB)` : (isLink ? ' (link)' : '');
        return `<option value="${i}">${escHTML(r.title) + sizeStr}</option>`;
      }).join('');

    // Per-doc renderer — mirrors app.js _publicDocsSelect (PDF/image/other).
    window.HRModule._driverDocsSelect = function(idx) {
      const r = window._driverDocsResources?.[parseInt(idx)];
      if (!r) return;
      const vw = document.getElementById('driver-docs-viewer');
      const dl = document.getElementById('driver-docs-download');
      if (!vw) return;

      // TASK-1288 — link-type shared resource (survey / form URL): there's no
      // stored file to stream, so render a tap-through "Open" tile and hide the
      // Download button. safeUrl still gates the protocol (https/http only).
      if (r.type !== 'document') {
        if (dl) dl.style.display = 'none';
        vw.innerHTML = `<div style="text-align:center;padding:40px 20px;">
            <span class="material-icons" style="font-size:48px;color:#3B82F6;margin-bottom:12px;">open_in_new</span>
            <h3 style="margin:0 0 4px;color:var(--text-primary,#0F172A);">${escHTML(r.title)}</h3>
            <p style="margin:0 0 16px;color:var(--text-secondary,#475569);font-size:0.8rem;">${r.desc ? escHTML(r.desc) : 'Opens in a new tab.'}</p>
            <a href="${safeUrl(r.url)}" target="_blank" rel="noopener noreferrer" class="btn btn-primary" style="text-decoration:none;display:inline-flex;align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:18px;">open_in_new</span> Open</a></div>`;
        return;
      }

      // Download via the per-doc getDownloadURL token URL (carries &token=,
      // bypasses storage.rules) — NOT the public /?docs= link.
      if (dl) { dl.href = safeUrlRaw(r.url); dl.style.display = 'inline-flex'; }

      const isPdf = r.mimeType?.includes('pdf') || r.isPdf;
      const isImage = r.mimeType?.startsWith('image/');
      if (isPdf) {
        const isMobile = window.innerWidth < 768;
        vw.innerHTML = isMobile
          ? `<div style="width:100%;height:100%;display:flex;flex-direction:column;"><iframe src="https://docs.google.com/gview?url=${encodeURIComponent(r.url)}&embedded=true" style="flex:1;border:none;width:100%;" allowfullscreen></iframe></div>`
          : `<iframe src="${safeUrl(r.url)}" style="width:100%;height:100%;border:none;" allowfullscreen></iframe>`;
      } else if (isImage) {
        vw.innerHTML = `<div style="padding:20px;max-width:100%;max-height:100%;overflow:auto;display:flex;align-items:center;justify-content:center;">
            <img src="${safeUrl(r.url)}" alt="${escHTML(r.title)}" style="max-width:100%;max-height:80vh;border-radius:8px;box-shadow:0 2px 12px rgba(0,0,0,0.1);"></div>`;
      } else {
        const sizeStr = r.sizeBytes ? `${(r.sizeBytes / 1024 / 1024).toFixed(1)} MB` : '';
        vw.innerHTML = `<div style="text-align:center;padding:40px 20px;">
            <span class="material-icons" style="font-size:48px;color:#3B82F6;margin-bottom:12px;">description</span>
            <h3 style="margin:0 0 4px;color:var(--text-primary,#0F172A);">${escHTML(r.title)}</h3>
            <p style="margin:0 0 16px;color:var(--text-secondary,#475569);font-size:0.8rem;">${escHTML(r.fileName || '')}${sizeStr ? ' · ' + sizeStr : ''}</p>
            <a href="${safeUrl(r.url)}" target="_blank" rel="noopener noreferrer" class="btn btn-primary" style="text-decoration:none;display:inline-flex;align-items:center;gap:6px;">
              <span class="material-icons" style="font-size:18px;">download</span> Download</a></div>`;
      }
    };

    // Auto-open when there's exactly one doc.
    if (resources.length === 1) {
      selector.value = '0';
      window.HRModule._driverDocsSelect('0');
    } else {
      const viewerEl = document.getElementById('driver-docs-viewer');
      if (viewerEl) viewerEl.innerHTML = `
        <div style="text-align:center;padding:48px 20px;color:var(--text-secondary,#475569);">
          <span class="material-icons" style="font-size:40px;color:var(--brand-blue,#3B82F6);margin-bottom:12px;">library_books</span>
          <p style="margin:0;">Select a document above to view it.</p>
        </div>`;
    }
  } catch (err) {
    console.error('[HR] Error loading driver docs:', err);
    const viewer = document.getElementById('driver-docs-viewer');
    if (viewer) viewer.innerHTML = `
      <div style="text-align:center;padding:48px 20px;color:var(--text-secondary,#475569);">
        <span class="material-icons" style="font-size:40px;color:#dc2626;margin-bottom:8px;">error_outline</span>
        <p style="margin:0;">Couldn't load documents. Please try again.</p>
      </div>`;
  }
}

// ── TASK-1017 — Driver self-service "My Documents" ──────────────────────────
// Driver-PIN Actions tile: a driver views THEIR OWN credential docs (training
// certs, CDL/license, med card) per 49 CFR §391.51. DISTINCT from
// openDriverDocs above (company-wide publiclyShared docs) — this reuses only
// the modal-shell pattern, never its stored-URL rendering. Everything routes
// through the getDriverOwnDocBytes callable: the server binds tenantId from
// the auth claim + personId from driverSessions/{uid} and re-resolves the doc
// set on EVERY call, so both the listed rows and the tapped storagePath can
// only ever be the signed-in driver's own file (coworker/forged references are
// denied server-side). No getDownloadURL/bearer URLs anywhere — credential
// docs are path-only post-TASK-996; bytes arrive base64 and open as a Blob
// URL (mirrors viewComplianceDoc).
let _myDocsFetchInFlight = false; // double-tap / in-flight guard for byte fetches

// Expiry/date display helper: date-only strings (yyyy-mm-dd) must NOT go
// through `new Date()` (UTC-midnight off-by-one — lessons.md timestamps rule).
function _myDocsFmtDate(v) {
  if (!v) return '';
  const s = String(v);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}`;
  try {
    const d = new Date(typeof v === 'number' ? v : s);
    return isNaN(d.getTime()) ? '' : d.toLocaleDateString();
  } catch (_) { return ''; }
}

async function openDriverMyDocuments() {
  const escHTML = (s) => s ? String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') : '';

  let modal = document.getElementById('hr-driver-mydocs-modal');
  if (modal) modal.remove();
  modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'hr-driver-mydocs-modal';
  modal.innerHTML = `
    <div class="modal-content modal-fullscreen" style="display:flex;flex-direction:column;max-width:560px;height:90vh;height:90dvh;padding:0;overflow:hidden;">
      <div class="modal-header" style="flex-shrink:0;padding:14px 18px;">
        <h3 style="margin:0;"><span class="material-icons" style="vertical-align:middle;margin-right:6px;">folder_shared</span>My Documents</h3>
        <button class="btn-icon" onclick="document.getElementById('hr-driver-mydocs-modal')?.remove()" aria-label="Close">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="flex-shrink:0;padding:10px 18px;border-bottom:1px solid var(--border-light,#e2e8f0);background:var(--bg-secondary,#f8fafc);font-size:0.78rem;color:var(--text-secondary,#475569);">
        Your own documents and safety sign-ins — tap one to open it.
      </div>
      <div id="driver-mydocs-list" style="flex:1;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;padding:12px 14px;">
        <div style="text-align:center;padding:48px 20px;color:var(--text-secondary,#475569);">
          <span class="material-icons" style="font-size:32px;animation:spin 0.8s linear infinite;">progress_activity</span>
        </div>
      </div>
    </div>`;
  document.body.appendChild(modal);

  // TASK-4883 — the driver's own safety-meeting sign-ins load alongside; a
  // failure there never blocks the credential list (and vice versa).
  const safetyP = httpsCallable(functions, 'getDriverSafetySheets')({ mode: 'list' })
    .then((r) => ({ ok: true, sheets: (r.data && Array.isArray(r.data.sheets)) ? r.data.sheets : [] }))
    .catch((e) => ({ ok: false, error: e }));

  try {
    const fn = httpsCallable(functions, 'getDriverOwnDocBytes');
    const res = await fn({ mode: 'list' });
    const docs = (res.data && res.data.ok && Array.isArray(res.data.docs)) ? res.data.docs : [];
    window._driverMyDocs = docs;

    const list = document.getElementById('driver-mydocs-list');
    if (!list) return; // modal closed mid-load

    if (docs.length === 0) {
      list.innerHTML = `
        <div style="text-align:center;padding:48px 20px;color:var(--text-secondary,#475569);">
          <span class="material-icons" style="font-size:48px;color:var(--border-light,#cbd5e1);margin-bottom:8px;">folder_open</span>
          <p style="margin:0;">No documents on file yet.</p>
          <p style="margin:6px 0 0;font-size:0.78rem;">Your CDL, medical card, and training certificates will appear here once they're uploaded.</p>
        </div>`;
      _appendDriverSafetySheets(await safetyP);
      return;
    }

    const iconFor = (t) => t === 'training' ? 'workspace_premium' : (t === 'medCard' ? 'medical_services' : 'badge');
    list.innerHTML = docs.map((d, i) => {
      let expiryChip = '';
      if (d.expiry) {
        const dateStr = _myDocsFmtDate(d.expiry);
        // Compare on the date string's own day (local) — treat past-day as expired.
        const expDate = /^\d{4}-\d{2}-\d{2}/.test(String(d.expiry))
          ? new Date(String(d.expiry).slice(0, 10) + 'T23:59:59')
          : new Date(d.expiry);
        const expired = !isNaN(expDate.getTime()) && expDate.getTime() < Date.now();
        expiryChip = `<span style="display:inline-block;margin-top:4px;padding:2px 8px;border-radius:999px;font-size:0.7rem;font-weight:600;${expired ? 'background:#FEE2E2;color:#991B1B;' : 'background:#ECFDF5;color:#065F46;'}">${expired ? 'Expired' : 'Expires'} ${escHTML(dateStr)}</span>`;
      }
      return `
        <button type="button" data-mydoc-idx="${i}" onclick="window.HRModule._driverMyDocOpen(${i}, this)"
          style="display:flex;align-items:center;gap:12px;width:100%;text-align:left;background:var(--bg-elevated,#fff);border:1px solid var(--border-light,#e2e8f0);border-radius:var(--radius-md,10px);padding:14px;margin-bottom:10px;min-height:60px;cursor:pointer;">
          <span class="material-icons" style="font-size:26px;color:var(--brand-blue,#3B82F6);flex-shrink:0;">${iconFor(d.credType)}</span>
          <span style="flex:1;min-width:0;">
            <span style="display:block;font-weight:600;font-size:0.9rem;color:var(--text-primary,#0F172A);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHTML(d.label || 'Document')}</span>
            <span style="display:block;font-size:0.74rem;color:var(--text-secondary,#475569);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHTML(d.fileName || '')}</span>
            ${expiryChip}
          </span>
          <span class="material-icons mydoc-row-chevron" style="font-size:20px;color:var(--text-secondary,#94a3b8);flex-shrink:0;">open_in_new</span>
        </button>`;
    }).join('');
    _appendDriverSafetySheets(await safetyP);
  } catch (err) {
    console.error('[HR] Error loading My Documents:', err);
    const list = document.getElementById('driver-mydocs-list');
    if (list) list.innerHTML = `
      <div style="text-align:center;padding:48px 20px;color:var(--text-secondary,#475569);">
        <span class="material-icons" style="font-size:40px;color:#dc2626;margin-bottom:8px;">error_outline</span>
        <p style="margin:0;">Couldn't load your documents. Please try again.</p>
      </div>`;
    // COD Code Reviewer: a credential-list failure must not hide the sign-ins.
    _appendDriverSafetySheets(await safetyP);
  }
}

// Fetch + open one of the listed docs. The storagePath sent back is only a
// SELECTOR — the server re-resolves the bound driver's own doc set and denies
// anything outside it. NEVER falls back to any URL on error (no bearer URLs
// exist on this surface).
async function _driverMyDocOpen(idx, btn) {
  if (_myDocsFetchInFlight) return; // in-flight guard (double-tap)
  const d = (window._driverMyDocs || [])[parseInt(idx, 10)];
  if (!d || !d.storagePath) return;
  _myDocsFetchInFlight = true;
  const rowBtn = btn || document.querySelector(`#driver-mydocs-list [data-mydoc-idx="${idx}"]`);
  const chevron = rowBtn ? rowBtn.querySelector('.mydoc-row-chevron') : null;
  if (rowBtn) rowBtn.disabled = true;
  if (chevron) { chevron.textContent = 'progress_activity'; chevron.style.animation = 'spin 0.8s linear infinite'; }
  try {
    const fn = httpsCallable(functions, 'getDriverOwnDocBytes');
    const result = await fn({ storagePath: d.storagePath });
    const data = result.data || {};
    if (!(data.ok && data.inline && data.base64)) throw new Error('No document data returned');
    const bin = atob(data.base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: data.contentType || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    console.error('[HR] My Documents open failed:', e);
    window.showToast?.(e?.message || 'Could not open the document', 'error');
  } finally {
    _myDocsFetchInFlight = false;
    if (rowBtn) rowBtn.disabled = false;
    if (chevron) { chevron.textContent = 'open_in_new'; chevron.style.animation = ''; }
  }
}

// ── TASK-4883 — the driver's own safety-meeting sign-ins (12 months) ────────
// Rows come from getDriverSafetySheets (server binds tenant + person from the
// driver session and returns ONLY sheets this driver signed — no coworker
// names, no counts, no URLs). Opening one re-renders that day's sheet with the
// driver's own signature only and streams it as bytes → short-lived blob URL.
function _appendDriverSafetySheets(result) {
  const list = document.getElementById('driver-mydocs-list');
  if (!list || !result) return;
  const escHTML = (s) => s ? String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;') : '';
  const sheets = result.ok ? result.sheets : [];
  window._driverSafetySheets = sheets;
  // Nothing to say on a carrier that never used sign-in sheets.
  if (result.ok && sheets.length === 0 && window.AppState?.settings?.safetyMeetingSignInEnabled !== true) return;
  const section = document.createElement('div');
  section.id = 'driver-mydocs-safety';
  section.style.cssText = 'margin-top:18px;';
  const head = `<div style="font-weight:700;font-size:0.82rem;letter-spacing:0.02em;color:var(--text-primary,#0F172A);margin:0 2px 8px;display:flex;align-items:center;gap:6px;">
      <span class="material-icons" style="font-size:18px;color:#059669;">how_to_reg</span>Safety Meeting Sign-Ins</div>`;
  let body;
  if (!result.ok) {
    body = `<div style="font-size:0.85rem;color:var(--text-secondary,#475569);padding:4px 2px;">Couldn't load your safety sign-ins. Close and try again.</div>`;
  } else if (sheets.length === 0) {
    body = `<div style="font-size:0.85rem;color:var(--text-secondary,#475569);padding:4px 2px;">No safety meeting sign-ins in the last 12 months. Signed one on the sign-in link and don't see it? Ask the office.</div>`;
  } else {
    const fmtTime = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      return isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    };
    body = sheets.map((s, i) => {
      const t = fmtTime(s.signedAt);
      const sub = [s.meetingType, s.topics].filter(Boolean).join(' · ');
      return `
        <button type="button" data-safety-idx="${i}" onclick="window.HRModule._driverSafetySheetOpen(${i}, this)"
          style="display:flex;align-items:center;gap:12px;width:100%;text-align:left;background:var(--bg-elevated,#fff);border:1px solid var(--border-light,#e2e8f0);border-radius:var(--radius-md,10px);padding:14px;margin-bottom:10px;min-height:60px;cursor:pointer;">
          <span class="material-icons" style="font-size:26px;color:#059669;flex-shrink:0;">fact_check</span>
          <span style="flex:1;min-width:0;">
            <span style="display:block;font-weight:600;font-size:0.9rem;color:var(--text-primary,#0F172A);">${escHTML(_myDocsFmtDate(s.date))}${t ? ` <span style="font-weight:500;color:var(--text-secondary,#475569);">· signed ${escHTML(t)}</span>` : ''}</span>
            <span style="display:block;font-size:0.74rem;color:var(--text-secondary,#475569);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escHTML(sub || 'Safety meeting')}</span>
          </span>
          <span class="material-icons safety-row-chevron" style="font-size:20px;color:var(--text-secondary,#94a3b8);flex-shrink:0;">open_in_new</span>
        </button>`;
    }).join('') + `<div style="font-size:0.74rem;color:var(--text-secondary,#475569);padding:0 2px;">Missing a meeting you signed on the sign-in link? Ask the office.</div>`;
  }
  section.innerHTML = head + body;
  list.appendChild(section);
}

async function _driverSafetySheetOpen(idx, btn) {
  if (_myDocsFetchInFlight) return;
  const s = (window._driverSafetySheets || [])[parseInt(idx, 10)];
  if (!s || !s.date) return;
  _myDocsFetchInFlight = true;
  const chevron = btn ? btn.querySelector('.safety-row-chevron') : null;
  if (btn) btn.disabled = true;
  if (chevron) { chevron.textContent = 'progress_activity'; chevron.style.animation = 'spin 0.8s linear infinite'; }
  try {
    const res = await httpsCallable(functions, 'getDriverSafetySheets')({ mode: 'open', date: s.date });
    const data = res.data || {};
    if (!(data.ok && data.inline && data.base64)) throw new Error('No document data returned');
    const bin = atob(data.base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener'); // same open path as _driverMyDocOpen
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    console.error('[HR] safety sheet open failed:', e);
    window.showToast?.(e?.message || 'Could not open the sign-in sheet', 'error');
  } finally {
    _myDocsFetchInFlight = false;
    if (btn) btn.disabled = false;
    if (chevron) { chevron.textContent = 'open_in_new'; chevron.style.animation = ''; }
  }
}

// Toggle Smart Search visibility (Direct opt-out of the AI document picker).
// Default state for missing field is ON, so admin clicks to OFF first; the
// server-side selector honours aiSearchEnabled === false (not bare falsy).
async function toggleAiSearch(resourceId, enable) {
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'hrResources', resourceId), {
      aiSearchEnabled: enable,
    });
    window.showToast(enable ? 'Included in Smart Search' : 'Hidden from Smart Search', 'success');
  } catch (err) {
    console.error('[HR] Error toggling Smart Search:', err);
    window.showToast('Error updating', 'error');
  }
}

// Generate / copy public docs page link
async function sharePublicDocsPage() {
  const resources = window._hrResources || HR_RESOURCES;
  const sharedCount = resources.filter(r => r.publiclyShared === true).length;

  if (sharedCount === 0) {
    window.showToast('No documents marked as public yet — click the eye icon on docs you want to share', 'error');
    return;
  }

  try {
    // TASK-9888 — the link is minted / reused SERVER-side (manageCompanyDocsLink,
    // tenant from the auth claim, in a transaction so two clicks never mint two
    // tokens). A reset link is never handed back.
    const res = await httpsCallable(functions, 'manageCompanyDocsLink')({ action: 'get' });
    showShareDocsDialog(res.data, sharedCount);
  } catch (err) {
    console.error('[HR] Error generating public docs link:', err);
    window.showToast(err?.code === 'functions/permission-denied'
      ? 'Sharing company documents needs the HR module'
      : 'Error generating link', 'error');
  }
}

// TASK-9888 — "Reset link": revokes every company-docs link of the tenant and
// mints a new one. Anyone holding the old link sees "this link was reset".
async function resetPublicDocsLink(sharedCount) {
  const go = await window.showConfirm(
    'Reset the documents link?',
    'Anyone with the old link loses access — drivers, customers and vendors you sent it to included. You will get a new link to send to the people who still need it. Files someone already downloaded stay with them.',
    { confirmText: 'Reset link', danger: true }
  );
  if (!go) return;
  try {
    const res = await httpsCallable(functions, 'manageCompanyDocsLink')({ action: 'reset' });
    showShareDocsDialog(res.data, sharedCount);
    window.showToast('Link reset. The old link no longer works.', 'success');
  } catch (err) {
    console.error('[HR] Error resetting public docs link:', err);
    // The server never throws after the reset commits, but a dropped
    // connection can hide a reply — so don't promise "nothing changed".
    window.showToast('Could not confirm the reset. Open Share Page again to see which link is live.', 'error');
  }
}

function _shareDocsDate(ms) {
  if (typeof ms !== 'number' || !isFinite(ms)) return '';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function showShareDocsDialog(link, sharedCount) {
  const token = String((link && link.token) || '');
  const url = `${window.location.origin}/?docs=${encodeURIComponent(token)}`;
  const created = _shareDocsDate(link && link.createdAtMs);
  const expires = (link && typeof link.expiresAtMs === 'number') ? _shareDocsDate(link.expiresAtMs) : 'Never';
  let modal = document.getElementById('hr-share-docs-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'hr-share-docs-modal';
    modal.className = 'modal hidden';
    modal.style.zIndex = '1060';
    modal.onclick = (e) => { if (e.target === modal) modal.classList.add('hidden'); };
    document.body.appendChild(modal);
  }
  modal.innerHTML = `
    <div class="modal-content modal-small" style="max-width:480px;text-align:center;padding:var(--spacing-xl);">
      <div style="display:flex;flex-direction:column;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-md);">
        <span class="material-icons" style="font-size:48px;color:var(--brand-blue);">share</span>
        <h3 style="font-size:1.25rem;font-weight:600;margin:0;">Public Documents Link</h3>
      </div>
      <p style="color:var(--text-secondary);margin-bottom:var(--spacing-md);line-height:1.5;font-size:0.875rem;">
        ${sharedCount} document${sharedCount === 1 ? '' : 's'} shared. Anyone with this link can view them — no login required.
      </p>
      <div style="display:flex;gap:8px;margin-bottom:var(--spacing-sm);">
        <input type="text" readonly value="${escapeHtml(url)}" id="hr-share-docs-url"
          style="flex:1;padding:10px 12px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.8rem;background:var(--bg-secondary);color:var(--text-primary);min-width:0;"
          onclick="this.select()">
        <button class="btn btn-primary" onclick="navigator.clipboard.writeText(document.getElementById('hr-share-docs-url').value);this.innerHTML='<span class=\\'material-icons\\' style=\\'font-size:18px;\\'>check</span> Copied';setTimeout(()=>{this.innerHTML='<span class=\\'material-icons\\' style=\\'font-size:18px;\\'>content_copy</span> Copy'},2000)"
          style="display:flex;align-items:center;gap:4px;padding:10px 16px;white-space:nowrap;">
          <span class="material-icons" style="font-size:18px;">content_copy</span> Copy
        </button>
      </div>
      <p id="hr-share-docs-meta" style="color:var(--text-secondary);margin:0 0 var(--spacing-lg);font-size:0.8rem;">
        ${created ? `Created ${escapeHtml(created)} · ` : ''}Expires ${escapeHtml(expires)}
      </p>
      <div style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap;">
        <button class="btn btn-secondary" id="hr-share-docs-reset" onclick="window.HRModule.resetPublicDocsLink(${Number(sharedCount) || 0})" style="padding:8px 16px;display:flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:18px;">restart_alt</span> Reset link
        </button>
        <button class="btn btn-primary" onclick="document.getElementById('hr-share-docs-modal').classList.add('hidden')" style="padding:8px 24px;">Done</button>
      </div>
    </div>
  `;
  modal.classList.remove('hidden');
}

// Upload a company document (PDF, Word, image, etc.)
// TASK-261 Slice 1B (2026-05-28) — known company-document types surfaced as
// suggested categories in the upload UI. User can still pick "Other" to type
// a custom category or "Uncategorized" to leave it blank. Order matches the
// spec frequency-of-use order: business-essentials first, expiring docs next,
// COIs last. Reused in the dropdown markup below + the existing category-chip
// strip via the regular `category` string field on the resource doc.
const HR_COMPANY_DOC_SUGGESTED_TYPES = [
  'Business License',
  'Letter of Good Standing',
  'Operating Authority (MC/DOT)',
  'IFTA License',
  "Workers' Comp Certificate",
  'General Liability COI',
];

// Opens a small modal to capture the document category BEFORE the file picker
// triggers. User picks a type (or "Other" / "Uncategorized"), then the upload
// flow continues. Replaces the bare file-input flow with a guided one so
// admins land on the right category without having to guess + type-correct
// every upload (TASK-261 Slice 1B remainder).
function showUploadCompanyDocumentModal(opts) {
  // TASK-9653 S1 — opened from a Claude link: { fromLink: true, folderId }.
  // Any other argument (an onclick Event, nothing) is the normal open.
  const link = (opts && typeof opts === 'object' && opts.fromLink === true) ? opts : null;
  // Inherit active filter as default category — same UX as the old flow but
  // surfaced in the dropdown so the user can confirm or change.
  const activeFilter = _readHrDocCategory();
  const defaultCat = link ? '' : ((activeFilter && activeFilter !== 'all' && activeFilter !== '__uncat__')
    ? activeFilter
    : '');
  // Suggested types as one optgroup.
  // Hardcoded constants, so this was never exploitable -- matched to the
  // folder list below so the two option builders cannot drift apart again.
  const suggestedHtml = HR_COMPANY_DOC_SUGGESTED_TYPES.map(t =>
    `<option value="${(window.escAttr || escapeHtml)(t)}" ${defaultCat === t ? 'selected' : ''}>${escapeHtml(t)}</option>`
  ).join('');
  // "Your folders" — distinct existing categories on the live resource set
  // (same source the category chips use, hr.js:2307). Excludes blank/uncategorized
  // and any value already in the suggested list (avoid showing it twice).
  const resources = window._hrResources || HR_RESOURCES || [];
  const suggestedLower = HR_COMPANY_DOC_SUGGESTED_TYPES.map(t => t.toLowerCase());
  const existingFolders = [...new Set(resources.map(x => (x.category || '').trim()).filter(Boolean))]
    .filter(c => !suggestedLower.includes(c.toLowerCase()))
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  // TASK-4838 CORRECTION 2026-09-09 to the TASK-4762 follow-up that shipped here
  // this morning (8b029349). That comment claimed a category named  a" onfocus=…
  // broke out of this attribute because escapeHtml "round-trips through
  // div.textContent" and leaves the quote. THAT IS TRUE OF app.js's escapeHtml
  // AND OF NO ESCAPER THIS FILE CAN REACH. hr.js's escapeHtml (:9109) delegates
  // to window.escapeHtml (sharedFormat.js:32), the regex escaper covering the
  // full &<>"' set — byte-identical to escAttr, as the ORIGINAL TASK-4762 comment
  // at :2733 already said correctly. Verified in a real Chromium DOM against the
  // shipped sharedFormat.js, not read off the lesson file:
  // tests/task4838-handler-jsstring.spec.js, "escapeHtml == escAttr" case.
  //
  // So escA here is a naming/consistency improvement, NOT a closed hole — this
  // option value was never exploitable. Saying otherwise would leave the next
  // reader hunting a vulnerability that does not exist, which is the same failure
  // mode as the confidently-wrong comment 4762 was filed to fix.
  // (The REAL escaper defect in this file was in inline event handlers, where
  // neither escapeHtml nor escAttr protects anything — see escJsAttr, :9118.)
  const escA = window.escAttr || escapeHtml;
  const foldersHtml = existingFolders.map(c =>
    `<option value="${escA(c)}" ${defaultCat === c ? 'selected' : ''}>${escapeHtml(c)}</option>`
  ).join('');
  const foldersGroupHtml = existingFolders.length
    ? `<optgroup label="Your folders">${foldersHtml}</optgroup>`
    : '';
  const html = `
    <div class="modal-content modal-small" style="max-width:440px;padding:0;">
      <div class="crm-detail-header" style="padding:16px 20px;">
        <div class="crm-detail-header-info">
          <div class="crm-detail-header-icon" style="width:36px;height:36px;">
            <span class="material-icons" style="font-size:20px;">upload_file</span>
          </div>
          <h3 style="margin:0;font-size:1rem;">Upload Company Document</h3>
        </div>
        <button class="modal-close" style="color:#fff;" onclick="document.getElementById('hr-upload-doc-modal').classList.add('hidden')">&times;</button>
      </div>
      <div style="padding:20px;display:flex;flex-direction:column;gap:14px;">
        <div id="hr-upload-doc-target" role="status" aria-live="polite" style="display:none;"></div>
        <div class="form-group" style="margin:0;">
          <label for="hr-upload-doc-type" style="font-weight:600;">Document type</label>
          <div class="input-with-action">
            <select id="hr-upload-doc-type" class="form-control">
              <option value="">— Uncategorized —</option>
              <optgroup label="Suggested">${suggestedHtml}</optgroup>
              ${foldersGroupHtml}
            </select>
            <button type="button" class="btn-add-inline" onclick="window.HRModule._showUploadDocNewCategory()" title="Create new folder">
              <span class="material-icons">add</span>
            </button>
          </div>
          <div id="hr-upload-doc-new-cat" class="input-with-action" style="margin-top:8px;display:none;">
            <input type="text" id="hr-upload-doc-new-cat-input" class="form-control" placeholder="New folder name" maxlength="32" onkeydown="if(event.key==='Enter'){event.preventDefault();window.HRModule._addUploadDocNewCategory();}">
            <button type="button" class="btn-add-inline" onclick="window.HRModule._addUploadDocNewCategory()" title="Add folder" style="background:var(--brand-blue,#3B82F6);color:#fff;">
              <span class="material-icons">check</span>
            </button>
            <button type="button" class="btn-add-inline" onclick="window.HRModule._cancelUploadDocNewCategory()" title="Cancel">
              <span class="material-icons">close</span>
            </button>
          </div>
          <p style="font-size:0.75rem;color:var(--text-secondary);margin-top:6px;line-height:1.45;">Pick an existing folder or a suggested type. Tap <span class="material-icons" style="font-size:13px;vertical-align:middle;">add</span> to create a new folder. "Uncategorized" leaves the tag blank.</p>
        </div>
        <div class="form-group" style="margin:0;">
          <label for="hr-upload-doc-file" style="font-weight:600;">File</label>
          <input type="file" id="hr-upload-doc-file" class="form-control" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv" style="padding:6px;">
          <p style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">PDF, Word, Excel, image, or CSV. 25 MB max.</p>
        </div>
      </div>
      <div class="modal-footer" style="display:flex;gap:8px;justify-content:flex-end;padding:16px 20px;">
        <button class="btn btn-secondary" onclick="document.getElementById('hr-upload-doc-modal').classList.add('hidden')">Cancel</button>
        <button class="btn btn-primary" id="hr-upload-doc-submit" onclick="window.HRModule._submitUploadCompanyDocumentModal()">
          <span class="material-icons" style="font-size:18px;vertical-align:middle;">upload</span> Upload
        </button>
      </div>
    </div>
  `;
  let modal = document.getElementById('hr-upload-doc-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'hr-upload-doc-modal';
    modal.className = 'modal hidden';
    modal.style.zIndex = '1060';
    modal.onclick = (e) => { if (e.target === modal) modal.classList.add('hidden'); };
    document.body.appendChild(modal);
  }
  modal.innerHTML = html;
  _uploadDocFromLink.value = !!link;
  // TASK-9653 S1 — hrOpenLink.js applyUploadLinkTarget: folder from live data + read-back.
  if (link && typeof link.apply === 'function') link.apply(link, UPLOAD_PICK_FOLDER, window._hrResources || HR_RESOURCES || []);
  modal.classList.remove('hidden');
}

// TASK-9653 S1 — the "pick a folder" placeholder a link-opened modal starts on
// when its folder is missing or not found. Never a stored category: submit
// refuses it, so an unmatched link can't fall to Uncategorized unseen.
const UPLOAD_PICK_FOLDER = '__dd_pick_folder__';
// true while the open modal came from a Claude link (rides on the audit row).
const _uploadDocFromLink = { value: false };

// TASK-9653 S1 — drain a parked "open in DispatchDeck" hint for HR (router:
// app.js handleHashChange → AppState.pendingFocus). Runs on each hrResources
// snapshot; takeHint consumes it the first time (a stale or other-company hint
// comes back null), so a later snapshot never re-opens the modal. The rest —
// access re-check, folder preselect, read-back — is the LAZY hrOpenLink.js
// (perf budget appColdLoadGzip: hr.js is a static chunk).
function _drainHrOpenHint() {
  const OL = window.OpenLinks;
  if (!OL || window.AppState?.pendingFocus?.view !== 'hr') return;
  const hint = OL.takeHint('hr');
  if (!hint) return;
  const tenant = window.AppState?.tenant || null; // re-checked after the import (COD r3)
  __ddChunkWrap(import('./hrOpenLink.js?v=1'))
    .then((m) => m.openFromHint(hint, { open: showUploadCompanyDocumentModal, tenant }))
    .catch((e) => {
      // COD r5 — the hint is already consumed: say so, so the user can open Upload Document by hand.
      console.warn('[HR] open-link upload modal failed', e);
      window.showToast?.('Could not open the upload screen from the link. Use Company Documents → Add → Upload Document.', 'error');
    });
}

// (+) button — reveal the inline "New folder name" row and focus it.
function _showUploadDocNewCategory() {
  const row = document.getElementById('hr-upload-doc-new-cat');
  const input = document.getElementById('hr-upload-doc-new-cat-input');
  if (!row || !input) return;
  row.style.display = 'flex';
  input.value = '';
  setTimeout(() => input.focus(), 50);
}

// Cancel/✕ — hide the inline new-folder row without changing the selection.
function _cancelUploadDocNewCategory() {
  const row = document.getElementById('hr-upload-doc-new-cat');
  const input = document.getElementById('hr-upload-doc-new-cat-input');
  if (input) input.value = '';
  if (row) row.style.display = 'none';
}

// Add — validate the typed folder name, case-insensitively dedup against the
// existing options (mirrors promptAddHrPosition hr.js:7410), then either SELECT
// the matching existing option (anti-misspelling guard) or append + select a
// new one. Uses the DOM Option API — never innerHTML string-concat (XSS-safe +
// preserves existing option state).
function _addUploadDocNewCategory() {
  const sel = document.getElementById('hr-upload-doc-type');
  const input = document.getElementById('hr-upload-doc-new-cat-input');
  if (!sel || !input) return;
  const trimmed = (input.value || '').trim().slice(0, 32); // match setDocCategory cap (hr.js:2314)
  if (!trimmed) return; // empty → no-op
  // Case-insensitive match against every existing option value.
  const existing = Array.from(sel.options)
    .find(o => o.value && o.value.toLowerCase() === trimmed.toLowerCase());
  if (existing) {
    sel.value = existing.value; // snap to the existing folder, no dup
  } else {
    // Append into the "Your folders" optgroup if present, else onto the select.
    const opt = new Option(trimmed, trimmed); // textContent + value, both raw
    let group = Array.from(sel.querySelectorAll('optgroup'))
      .find(g => g.label === 'Your folders');
    if (!group) {
      group = document.createElement('optgroup');
      group.label = 'Your folders';
      sel.add(group);
    }
    group.appendChild(opt);
    sel.value = trimmed;
  }
  // TASK-9653 S1 — a programmatic value change fires no 'change'; the
  // link read-back listens for one.
  try { sel.dispatchEvent(new Event('change')); } catch (_) {}
  _cancelUploadDocNewCategory();
}

// Read the resolved category from the modal — now just the select value.
// Returns '' for Uncategorized so the addDoc payload omits the category field
// (matching the existing inheritCategory branch).
function _resolveUploadDocCategory() {
  const sel = document.getElementById('hr-upload-doc-type');
  if (!sel) return '';
  return sel.value || ''; // '' = Uncategorized
}

// Modal Upload button handler — pulls the chosen category + file out of the
// modal DOM, closes the modal, then runs the existing upload pipeline with
// the explicit category override.
async function _submitUploadCompanyDocumentModal() {
  const fileEl = document.getElementById('hr-upload-doc-file');
  const file = fileEl?.files?.[0];
  if (!file) {
    window.showToast?.('Pick a file first', 'error');
    return;
  }
  const category = _resolveUploadDocCategory();
  // TASK-9653 S1 — a link-opened modal whose folder was missing / not found
  // starts on a placeholder; uploading needs a real choice (Uncategorized
  // included, picked on purpose).
  if (category === UPLOAD_PICK_FOLDER) {
    window.showToast?.('Pick a folder first', 'error');
    return;
  }
  const fromLink = _uploadDocFromLink.value === true;
  document.getElementById('hr-upload-doc-modal')?.classList.add('hidden');
  await _uploadCompanyDocumentFile(file, category, undefined, { fromClaudeLink: fromLink });
}

// Extracted from the original uploadCompanyDocument — runs upload with an
// explicit category override. TASK-261 Slice 1B (2026-05-28): when called via
// the new category-picker modal, overrideCategory takes precedence over the
// active-filter inheritance. Pass overrideCategory='' to mean Uncategorized
// (category field omitted); pass null/undefined to fall back to the legacy
// "inherit active filter" behavior.
// TASK-772 — `extraMeta` (optional) lets a caller attach a few well-known
// metadata fields to the hrResources doc without forking the upload pipeline.
// Used by the Safety Records area to stamp safetyDocType (OSHA 300 / 300A / …)
// + safetyYear on the same company-document store the rest of HR uses. Returns
// true on success so the caller can refresh its view. Existing callers pass no
// third arg → behavior unchanged.
async function _uploadCompanyDocumentFile(file, overrideCategory, extraMeta, auditMeta) {
  if (!file) return false;

  // 25MB hard limit
  if (file.size > 25 * 1024 * 1024) {
    window.showToast('File too large (25MB max)', 'error');
    return false;
  }
  // 5MB soft warn — Smart Search auto-summarizes anything past ~80K tokens
  // (~500KB-1MB extracted text) so giant PDFs work but lose page-level
  // fidelity. Warn the admin so it's not a silent surprise.
  if (file.size > 5 * 1024 * 1024) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    const ok = confirm(`This file is ${mb} MB. Smart Search will auto-summarize it on first AI query (10–20 seconds), then cache the summary. Continue?`);
    if (!ok) return false;
  }

  window.showToast('Uploading...', 'info');

  try {
    const tenantId = HRState.tenantId;
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storagePath = `tenants/${tenantId}/hr/company-documents/${timestamp}_${safeName}`;
    const storageRef = ref(storage, storagePath);

    await uploadBytes(storageRef, file, { contentType: inferContentType(file) });
    const url = await getDownloadURL(storageRef);

    // Derive a title from filename (remove extension, replace underscores)
    const title = file.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ');

    // Category resolution: explicit override wins, then fall back to active
    // filter inheritance (legacy behavior).
    let category;
    if (typeof overrideCategory === 'string') {
      category = overrideCategory.trim();
    } else {
      const activeFilter = _readHrDocCategory();
      category = (activeFilter && activeFilter !== 'all' && activeFilter !== '__uncat__') ? activeFilter : '';
    }
    const _newResRef = await addDoc(collection(db, 'tenants', tenantId, 'hrResources'), {
      title,
      fileName: file.name,
      url,
      storagePath,
      type: 'document',
      mimeType: file.type,
      isPdf: file.type === 'application/pdf',
      sizeBytes: file.size,
      icon: file.type.includes('pdf') ? 'picture_as_pdf' : 'description',
      ...(category ? { category } : {}),
      // TASK-772 — only the whitelisted safety metadata keys, sanitized.
      ...(extraMeta && typeof extraMeta === 'object' ? _safeSafetyDocMeta(extraMeta) : {}),
      uploadedAt: Timestamp.now(),
      uploadedBy: window.AppState?.userName || 'Unknown',
      createdAt: Timestamp.now()
    });

    // TASK-9653 S1 item 6 — the upload itself gets an audit row (it had none).
    // Shared writer (window.logAudit, TASK-1495: it stamps the user, time and
    // action). No file name or title: they are free text and may name a
    // person; the resource id leads to them. fromClaudeLink is client-set — a
    // hint for support, not proof (COD r1 Adversary).
    window.logAudit?.('hr', 'company_doc_uploaded', {
      resourceId: _newResRef?.id || null, category: category || null,
      mimeType: file.type || null, sizeBytes: file.size, fromClaudeLink: !!auditMeta?.fromClaudeLink,
    });
    window.showToast('Document uploaded', 'success');
    // TASK-9742 — the new doc's id (truthy, so every `if (ok)` caller is
    // unchanged); the safety-meeting "Upload new" attaches it by id.
    return _newResRef?.id || true;
  } catch (err) {
    console.error('[HR] Upload error:', err);
    window.showToast('Upload failed', 'error');
    return false;
  }
}

// TASK-772 — whitelist + sanitize the safety metadata a caller may attach to a
// company document. Only safetyDocType (capped string) + safetyYear (4-digit
// number within a sane range) are accepted; everything else is dropped so a
// caller can't smuggle arbitrary fields into the hrResources doc.
function _safeSafetyDocMeta(meta) {
  const out = {};
  if (typeof meta.safetyDocType === 'string') {
    const t = meta.safetyDocType.trim().slice(0, 60);
    if (t) out.safetyDocType = t;
  }
  const yr = parseInt(meta.safetyYear, 10);
  if (Number.isInteger(yr) && yr >= 1970 && yr <= 2100) out.safetyYear = yr;
  return out;
}

// ============================================
// DOCUMENT SMART ASSISTANT (AI Q&A)
// ============================================
// TASK-9689 slice 3 — `opts.ask` opens the assistant AND sends that question
// (the "How is overtime figured with two rates?" link in Workforce → Reports →
// Pay Settings). Callers pass a fixed string, never user text; it still goes
// through sendDocQuestion's own length check and escapeHtml render.
let _hrDocAskGen = 0; // TASK-9689 COD r9 — which openDocAssistant({ask}) is current
function openDocAssistant(opts) {
  _hrDocAskGen++;
  // Use window._hrResources to survive duplicate module instances from SW cache
  const resources = window._hrResources || HR_RESOURCES;
  const docs = resources.filter(r => r.type === 'document');
  const links = resources.filter(r => r.type !== 'document');
  const aiEligibleCount = docs.filter(r => r.aiSearchEnabled !== false).length;
  const totalDocCount = docs.length;

  let modal = document.getElementById('hr-doc-assistant-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'hr-doc-assistant-modal';
    modal.className = 'modal hidden';
    modal.style.zIndex = '1060';
    modal.onclick = (e) => { if (e.target === modal) modal.classList.add('hidden'); };
    document.body.appendChild(modal);
  }

  const docList = docs.length > 0
    ? docs.map(d => `<span style="display:inline-flex;align-items:center;gap:4px;background:#3B82F610;color:#3B82F6;padding:3px 10px;border-radius:12px;font-size:0.8rem;"><span class="material-icons" style="font-size:14px;">description</span>${escapeHtml(d.title)}</span>`).join(' ')
    : '<span style="color:var(--text-secondary);font-size:0.85rem;">No documents uploaded yet. You can still ask general wage-and-hour questions; upload documents to ask about your own policies.</span>';

  modal.innerHTML = `
    <div class="modal-content" style="max-width:600px;height:80vh;height:80dvh;display:flex;flex-direction:column;padding:0;">
      <div class="crm-detail-header" style="padding:16px 20px;flex-shrink:0;">
        <div class="crm-detail-header-info">
          <div class="crm-detail-header-icon" style="width:36px;height:36px;">
            <span class="material-icons" style="font-size:20px;">auto_awesome</span>
          </div>
          <div>
            <h3 style="margin:0;font-size:1rem;">Smart Search</h3>
            <p style="margin:0;font-size:0.75rem;opacity:0.8;">Search your documents, or ask a general wage-and-hour question</p>
          </div>
        </div>
        <button class="modal-close" style="color:#fff;" onclick="document.getElementById('hr-doc-assistant-modal').classList.add('hidden')">&times;</button>
      </div>

      <div style="padding:12px 16px;border-bottom:1px solid var(--border-light);flex-shrink:0;max-height:72px;overflow-y:auto;">
        <div style="display:flex;flex-wrap:wrap;gap:6px;">${docList}</div>
      </div>

      <div id="hr-doc-chat" style="flex:1;overflow-y:auto;padding:16px;display:flex;flex-direction:column;gap:12px;">
        <div style="text-align:center;padding:20px;color:var(--text-secondary);font-size:0.9rem;">
          <span class="material-icons" style="font-size:40px;display:block;margin-bottom:8px;opacity:0.4;">auto_awesome</span>
          Search your documents or generate new ones.<br>
          <span style="font-size:0.8rem;">
            <b>Search:</b> "What does the safety manual say about PPE?"<br>
            <b>Rules:</b> "How is overtime figured with two pay rates?"<br>
            <b>Generate:</b> "Write a drug & alcohol policy" or "Create an attendance policy"
          </span>
        </div>
      </div>

      <div style="padding:12px 16px 4px;border-top:1px solid var(--border-light);flex-shrink:0;display:flex;gap:8px;">
        <input type="text" id="hr-doc-chat-input" data-maxlen="50000" class="form-control" placeholder="Search docs or 'Write a safety policy'..."
          style="flex:1;" onkeydown="if(event.key==='Enter')window.HRModule.sendDocQuestion()">
        <button id="hr-doc-chat-send" class="btn btn-primary" onclick="window.HRModule.sendDocQuestion()" style="padding:8px 16px;">
          <span class="material-icons" style="font-size:18px;">send</span>
        </button>
      </div>
      ${(() => {
        // TASK-9268 slice 4 — askHRDocuments records an owner's / active
        // member's questions. Same copy as the other recorded assistants: it
        // lives in app.js (window.aiTranscriptNoticeText), read via window,
        // never imported (Pitfall #1). Empty on demo, which records nothing.
        const t = typeof window.aiTranscriptNoticeText === 'function'
          ? window.aiTranscriptNoticeText()
          : 'Questions you ask here are saved for 90 days. Your account owner can review them.';
        return t ? `<div class="hr-doc-transcript-notice" role="note" style="padding:0 16px 4px;flex-shrink:0;font-size:0.72rem;line-height:1.4;color:var(--text-secondary);">${escapeHtml(t)}</div>` : '';
      })()}
      <div id="hr-doc-search-hint" style="padding:0 16px 10px;flex-shrink:0;font-size:0.72rem;color:var(--text-secondary);text-align:right;">
        ${totalDocCount === 0 ? '' : `Searching <b>${aiEligibleCount}</b> of <b>${totalDocCount}</b> doc${totalDocCount === 1 ? '' : 's'}${aiEligibleCount < totalDocCount ? ` · ${totalDocCount - aiEligibleCount} hidden from AI` : ''}`}
      </div>
    </div>
  `;

  HR_DOC_CHAT_HISTORY = [];
  modal.classList.remove('hidden');
  const ask = (opts && typeof opts.ask === 'string') ? opts.ask.trim().slice(0, 300) : '';
  if (ask) {
    const input = document.getElementById('hr-doc-chat-input');
    if (!input) return;
    input.value = ask;
    // COD r8 (Code Reviewer): opened from Workforce → Pay Settings before HR
    // ever loaded, the company documents are not in memory yet and the
    // question would go out with none (a handbook that disagrees with Pay
    // Settings would be missed). Read them once — the same collection and
    // shape the HR listener keeps — then send. A failed read still sends
    // (document-free), never blocks the question.
    if (_hrResourcesLoaded.value) { sendDocQuestion(); return; }
    const tid = window.AppState?.tenant || HRState.tenantId;
    // COD r9: lock the input + Send while the read runs (a manual Send would
    // go out document-free and the later send would find an empty box), and
    // send only if THIS opening is still the one on screen for the same tenant.
    const gen = ++_hrDocAskGen;
    const sendBtn = document.getElementById('hr-doc-chat-send');
    input.readOnly = true;
    if (sendBtn) sendBtn.disabled = true;
    const chat = document.getElementById('hr-doc-chat');
    if (chat) chat.innerHTML = '<div style="text-align:center;padding:20px;color:var(--text-secondary);font-size:0.9rem;"><span class="btn-spinner"></span> Loading your company documents…</div>';
    (async () => {
      try {
        if (tid && !_hrResourcesLoaded.value) {
          const snap = await getDocs(collection(db, 'tenants', tid, 'hrResources'));
          if (!_hrResourcesLoaded.value && (window.AppState?.tenant || HRState.tenantId) === tid) {
            HR_RESOURCES = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            window._hrResources = HR_RESOURCES;
          }
        }
      } catch (e) {
        console.warn('[HR] Smart Search: company documents could not be read before asking', e?.code || e?.message || e);
      }
      input.readOnly = false;
      if (sendBtn) sendBtn.disabled = false;
      const m = document.getElementById('hr-doc-assistant-modal');
      if (gen !== _hrDocAskGen || !m || m.classList.contains('hidden') || (window.AppState?.tenant || HRState.tenantId) !== tid) return;
      sendDocQuestion();
    })();
    return;
  }
  setTimeout(() => document.getElementById('hr-doc-chat-input')?.focus(), 100);
}

// Detect if user wants to generate a saveable document vs search/query
function isGenerationRequest(text) {
  const lower = text.toLowerCase();
  // Must match a verb + document-type noun to trigger generation
  const docNouns = /\b(policy|handbook|template|form|sop|procedure|checklist|agreement|contract|description|posting|plan|packet|guide|manual)\b/;
  const genVerbs = /\b(create|generate|draft|write|make|build)\b/;
  // Direct noun requests
  const directPatterns = [
    'new policy', 'new template', 'new form', 'new handbook',
    'new checklist', 'new sop', 'new procedure',
    'policy for', 'template for', 'form for',
  ];
  if (directPatterns.some(k => lower.includes(k))) return true;
  // Verb + noun combo (e.g. "write a drug policy", "create an attendance handbook")
  return genVerbs.test(lower) && docNouns.test(lower);
}

// Build people + company profile + HR module context for document generation
function buildPeopleContext() {
  const lines = [];

  // Company profile from tenant data (AR/AP settings)
  const td = window.AppState?.tenantData || {};
  const companyName = td.companyName || td.name || '';
  const companyPhone = td.companyPhone || td.phone || '';
  const companyEmail = td.companyEmail || td.email || '';
  const companyAddress = td.companyAddress || td.address || '';
  if (companyName) lines.push(`Company: ${companyName}`);
  if (companyPhone) lines.push(`Phone: ${companyPhone}`);
  if (companyEmail) lines.push(`Email: ${companyEmail}`);
  if (companyAddress) lines.push(`Address: ${companyAddress}`);

  // People summary (always included — just counts/roles, no PII)
  const people = HRState.people || [];
  if (people.length > 0) {
    const owners = people.filter(p => (p.crewType || p.role || p.roles?.[0] || '').toLowerCase().includes('owner'));
    const active = people.filter(p => p.active !== false);
    lines.push(`\nTotal employees: ${active.length}`);
    if (owners.length > 0) {
      lines.push(`Owner(s): ${owners.map(o => o.name).join(', ')}`);
    }
    const roleCounts = {};
    active.forEach(p => {
      const role = p.crewType || p.role || p.roles?.[0] || 'Unassigned';
      roleCounts[role] = (roleCounts[role] || 0) + 1;
    });
    const roleList = Object.entries(roleCounts).map(([r, c]) => `${r}: ${c}`).join(', ');
    if (roleList) lines.push(`Roles: ${roleList}`);
  }

  // Time-off policy (always — structural, not personal)
  const policy = HRState.timeOffPolicy;
  if (policy) {
    lines.push('\nTime-Off Policy:');
    if (policy.bereavement) lines.push(`- Bereavement: ${JSON.stringify(policy.bereavement)}`);
    // Include all policy fields
    Object.entries(policy).forEach(([key, val]) => {
      if (key !== 'bereavement' && typeof val !== 'object') {
        lines.push(`- ${key}: ${val}`);
      }
    });
  }

  // HR module features available
  lines.push('\nHR Module Features:');
  lines.push('- Disciplinary Forms (Verbal, Written, PIP, Termination)');
  lines.push('- New Hire Checklists (onboarding workflow)');
  lines.push('- Employee Reviews (performance evaluations with KPI scoring)');
  lines.push('- Time Off Management (requests, approvals, policies by level)');
  lines.push('- Hiring/Applicants (candidate tracking)');
  lines.push('- Safety (sign-in sheets, DVIR/JSA accountability)');
  lines.push('- Company Document Repository');

  // Employee-specific data — HR is PIN-gated so anyone with access already has this data
  // Disciplinary summary
  const forms = HRState.hrForms || [];
  if (forms.length > 0) {
    lines.push(`\nDisciplinary Actions (${forms.length} total):`);
    forms.slice(0, 20).forEach(f => {
      const date = f.formDate || (f.createdAt?.toDate?.()?.toISOString?.()?.split('T')[0]) || 'unknown';
      const _typeLabel = WARNING_TYPES[f.warningType]?.label || `${f.warningType || 'unknown'} warning`;
      lines.push(`- ${f.employeeName || 'Unknown'}: ${_typeLabel} (${date}) — ${(f.issue || '').substring(0, 80)}`);
    });
  }

  // Review summary
  const reviews = HRState.hrReviews || [];
  if (reviews.length > 0) {
    lines.push(`\nPerformance Reviews (${reviews.length} total):`);
    reviews.slice(0, 20).forEach(r => {
      // TASK-4776 — a voided review is still in the corpus (the row is kept on
      // purpose). Label it, or Smart Tools will quote a review somebody voided
      // for being on the wrong person as if it were current.
      lines.push(`- ${r.employeeName || 'Unknown'}: ${r.passFail || ''} (score: ${r.overallRating?.toFixed?.(1) || 'N/A'}) — ${r.reviewPeriod || ''}${_hrReviewIsVoided(r) ? ' [VOIDED — does not count]' : ''}`);
    });
  }

  // Checklist summary
  const checklists = HRState.hrChecklists || [];
  if (checklists.length > 0) {
    lines.push(`\nNew Hire Checklists (${checklists.length} total):`);
    checklists.slice(0, 20).forEach(c => {
      lines.push(`- ${c.employeeName || 'Unknown'}: ${c.completionPercent || 0}% complete`);
    });
  }

  // Time-off requests summary
  const requests = HRState.timeOffRequests || [];
  if (requests.length > 0) {
    const pending = requests.filter(r => r.status === 'pending');
    const approved = requests.filter(r => r.status === 'approved');
    lines.push(`\nTime-Off Requests: ${requests.length} total (${pending.length} pending, ${approved.length} approved)`);
  }

  // Employee list with key details
  if (people.length > 0) {
    lines.push(`\nEmployee Directory:`);
    people.filter(p => p.active !== false).slice(0, 30).forEach(p => {
      const role = p.crewType || p.role || p.roles?.[0] || '';
      // hireDate is a Firestore Timestamp for most hires (hr.js promote +
      // Edit Employee modal); only bulkImportRoster writes it as an ISO
      // string. Without coercion, ${p.hireDate} interpolates as
      // "Timestamp(seconds=..., nanoseconds=...)" and Gemini can't answer
      // "when was X hired".
      const _hireRaw = p.hireDate?.toDate?.() || p.hireDate;
      let hire = '';
      if (_hireRaw) {
        const _d = new Date(_hireRaw);
        if (!isNaN(_d.getTime())) hire = _d.toISOString().slice(0, 10);
      }
      lines.push(`- ${p.name}${role ? ` (${role})` : ''}${hire ? `, hired ${hire}` : ''}`);
    });
  }

  return lines.join('\n');
}

async function sendDocQuestion() {
  const input = document.getElementById('hr-doc-chat-input');
  const chatContainer = document.getElementById('hr-doc-chat');
  if (!input || !chatContainer) return;
  // TASK-9689 COD r10 — Enter on the input bypasses the disabled Send button;
  // while openDocAssistant({ask}) is still reading the company documents the
  // input is readOnly and nothing may send.
  if (input.readOnly) return;

  const question = input.value.trim();
  if (!question) return;
  if (question.length > HR_DOC_MAX_QUESTION_CHARS) {   // TASK-9301 — told plainly, nothing sent, text kept
    window.showToast?.(`Your message is ${Number(question.length).toLocaleString('en-US')} characters — this assistant takes up to ${Number(HR_DOC_MAX_QUESTION_CHARS).toLocaleString('en-US')}. Nothing was sent. Shorten it or split it into two messages.`, 'error', null, 12000);
    return;
  }
  input.value = '';

  // Use window._hrResources to survive duplicate module instances from SW cache
  const resources = window._hrResources || HR_RESOURCES;
  const docs = resources.filter(r => r.type === 'document');

  // Detect if this is a generation request
  const isGenerate = isGenerationRequest(question);

  // TASK-9689 slice 1 — search mode no longer needs a document: a general
  // wage-and-hour question is answered from the cited federal rules the
  // server prompt carries, and company facts are reported missing. (Was a
  // hard "Upload documents first" refusal.)

  // Rate limit: 30 questions per session
  if (HR_DOC_CHAT_HISTORY.length >= 60) {
    window.showToast('Session limit reached. Close and reopen to continue.', 'error');
    return;
  }

  // Clear welcome message on first question
  if (HR_DOC_CHAT_HISTORY.length === 0) {
    chatContainer.innerHTML = '';
  }

  // Show user message
  chatContainer.innerHTML += `
    <div style="align-self:flex-end;background:var(--brand-blue);color:#fff;padding:10px 14px;border-radius:16px 16px 4px 16px;max-width:80%;font-size:0.9rem;">
      ${escapeHtml(question)}
    </div>
  `;

  // Show thinking indicator
  const thinkingId = 'doc-thinking-' + Date.now();
  chatContainer.innerHTML += `
    <div id="${thinkingId}" style="align-self:flex-start;display:flex;align-items:center;gap:8px;padding:10px 14px;color:var(--text-secondary);font-size:0.85rem;">
      <span class="btn-spinner"></span> ${isGenerate ? 'Generating document...' : 'Reading documents...'}
    </div>
  `;
  chatContainer.scrollTop = chatContainer.scrollHeight;

  HR_DOC_CHAT_HISTORY.push({ role: 'user', content: question });

  try {
    const token = await window.AppState?.user?.getIdToken?.();
    const companyName = window.AppState?.tenantData?.name || '';

    const body = {
      question,
      tenantId: HRState.tenantId,
      documentIds: docs.map(d => d.id),
      history: HR_DOC_CHAT_HISTORY.slice(-10),
      companyName,
      peopleContext: buildPeopleContext()
    };

    if (isGenerate) {
      body.mode = 'generate';
    }

    const response = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/askHRDocuments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ data: body })
    });

    // TASK-449 S3 — guard the response BEFORE parsing. On a cold doc cache the
    // function can exceed the gateway timeout and return a 504 HTML page (not
    // JSON); blindly calling response.json() throws into the generic catch and
    // shows a scary red "Error." Detect non-OK / non-JSON and show a calm
    // "indexing" hint instead so users wait for the background warm rather than
    // spam-retry (each retry warms more docs, so it eventually works — bad UX).
    const _indexingMsg = 'Indexing your documents — the first search after an upload can take a moment, and it runs in the background. Try again shortly.';
    let result;
    if (!response.ok) {
      document.getElementById(thinkingId)?.remove();
      let errBody = null;
      try { errBody = await response.json(); } catch (_) { /* non-JSON (gateway timeout) */ }
      // TASK-514: Gemini quota exhaustion (503 + reason) — distinct from a cold-
      // cache gateway timeout. Show the friendly "AI temporarily unavailable"
      // line rather than the "still indexing your documents" hint.
      if (errBody?.reason === 'AI_UNAVAILABLE') {
        chatContainer.innerHTML += `<div style="align-self:flex-start;color:var(--text-secondary);padding:10px 14px;font-size:0.85rem;">AI is temporarily unavailable — try again shortly.</div>`;
        chatContainer.scrollTop = chatContainer.scrollHeight;
        return;
      }
      const isTimeout = response.status === 504 || response.status === 503 || response.status === 408;
      const msg = errBody?.error ? escapeHtml(errBody.error) : (isTimeout ? _indexingMsg : 'Error getting response. Try again.');
      chatContainer.innerHTML += `<div style="align-self:flex-start;color:${isTimeout ? 'var(--text-secondary)' : '#dc2626'};padding:10px 14px;font-size:0.85rem;">${msg}</div>`;
      chatContainer.scrollTop = chatContainer.scrollHeight;
      return;
    }
    try {
      result = await response.json();
    } catch (_) {
      // 2xx but body wasn't JSON (truncated/gateway) — treat as still-indexing.
      document.getElementById(thinkingId)?.remove();
      chatContainer.innerHTML += `<div style="align-self:flex-start;color:var(--text-secondary);padding:10px 14px;font-size:0.85rem;">${_indexingMsg}</div>`;
      chatContainer.scrollTop = chatContainer.scrollHeight;
      return;
    }
    const answer = result.data?.response || result.result?.answer || 'No response received.';
    const wasGenerated = result.data?.mode === 'generate' || isGenerate;
    const docsUsed = Number.isFinite(result.data?.docsUsed) ? result.data.docsUsed : null;
    const docsTotal = Number.isFinite(result.data?.docsTotal) ? result.data.docsTotal : null;

    // Remove thinking indicator
    document.getElementById(thinkingId)?.remove();

    if (wasGenerated) {
      // Show generated document with disclaimer + save button
      const saveId = 'save-gen-doc-' + Date.now();
      chatContainer.innerHTML += `
        <div style="align-self:flex-start;background:var(--bg-secondary);color:var(--text-primary);padding:0;border-radius:16px 16px 16px 4px;max-width:90%;font-size:0.9rem;line-height:1.5;overflow:hidden;">
          <div style="background:linear-gradient(135deg,#F59E0B20,#D9770610);padding:8px 14px;display:flex;align-items:center;gap:6px;border-bottom:1px solid #F59E0B30;font-size:0.8rem;color:#92400E;">
            <span class="material-icons" style="font-size:16px;">warning_amber</span>
            Smart Tools draft — review carefully before use. This is not legal advice.
          </div>
          <div style="padding:12px 14px;max-height:400px;overflow-y:auto;white-space:pre-wrap;font-size:0.85rem;font-family:inherit;">${escapeHtml(answer)}</div>
          <div style="padding:10px 14px;border-top:1px solid var(--border-light);display:flex;gap:8px;flex-wrap:wrap;">
            <button id="${saveId}" class="btn btn-primary" style="font-size:0.8rem;padding:6px 14px;display:inline-flex;align-items:center;gap:4px;" onclick="window.HRModule.saveGeneratedDoc(this)">
              <span class="material-icons" style="font-size:16px;">save</span> Save to Documents
            </button>
            <button class="btn btn-secondary" style="font-size:0.8rem;padding:6px 14px;display:inline-flex;align-items:center;gap:4px;" onclick="window.HRModule.copyGeneratedDoc(this)">
              <span class="material-icons" style="font-size:16px;">content_copy</span> Copy
            </button>
          </div>
        </div>
      `;
      // Stash the content on the save button for later retrieval
      const saveBtn = document.getElementById(saveId);
      if (saveBtn) saveBtn._generatedContent = answer;
      if (saveBtn) saveBtn._generatedPrompt = question;
    } else {
      // Normal search response. Footer surfaces "read 3 of 12 docs" so the
      // user understands which slice of their corpus the answer came from —
      // matches the "Searching N of M" hint above the input but reflects the
      // server-side budget result.
      const usedFooter = (docsUsed !== null && docsTotal !== null)
        ? `<div style="font-size:0.68rem;color:var(--text-secondary);margin-top:6px;">Read ${docsUsed} of ${docsTotal} doc${docsTotal === 1 ? '' : 's'}</div>`
        : '';
      chatContainer.innerHTML += `
        <div style="align-self:flex-start;background:var(--bg-secondary);color:var(--text-primary);padding:10px 14px;border-radius:16px 16px 16px 4px;max-width:85%;font-size:0.9rem;line-height:1.5;">
          ${escapeHtml(answer).replace(/\n/g, '<br>')}
          ${usedFooter}
        </div>
      `;
    }

    HR_DOC_CHAT_HISTORY.push({ role: 'assistant', content: answer });
  } catch (err) {
    console.error('[HR] Doc assistant error:', err);
    document.getElementById(thinkingId)?.remove();
    chatContainer.innerHTML += `
      <div style="align-self:flex-start;color:#dc2626;padding:10px 14px;font-size:0.85rem;">
        Error getting response. Try again.
      </div>
    `;
  }

  chatContainer.scrollTop = chatContainer.scrollHeight;
}

// Save AI-generated document to company documents repository
async function saveGeneratedDoc(btn) {
  if (!btn || !btn._generatedContent) return;
  const content = btn._generatedContent;
  const prompt = btn._generatedPrompt || 'Generated Document';

  // Derive title from the prompt
  let title = prompt.replace(/^(create|generate|draft|write|make|build)\s+(a|an|me\s+a|me\s+an)?\s*/i, '').trim();
  title = title.charAt(0).toUpperCase() + title.slice(1);
  if (title.length > 80) title = title.substring(0, 77) + '...';

  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Saving...';

  try {
    // Cap: max 25 generated docs per tenant to prevent storage abuse
    const resources = window._hrResources || HR_RESOURCES;
    const genCount = resources.filter(r => r.generated).length;
    if (genCount >= 25) {
      window.showToast('Generated document limit reached (25). Delete some to save more.', 'error');
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save to Documents';
      return;
    }

    const tenantId = HRState.tenantId;
    const timestamp = Date.now();
    const safeName = title.replace(/[^a-zA-Z0-9 ._-]/g, '').replace(/\s+/g, '_').substring(0, 60);
    const fileName = `${safeName}.txt`;
    const storagePath = `tenants/${tenantId}/hr/company-documents/${timestamp}_${fileName}`;
    const storageRef = ref(storage, storagePath);

    // Create text file blob
    const blob = new Blob([content], { type: 'text/plain' });
    await uploadBytes(storageRef, blob, { contentType: 'text/plain' });
    const url = await getDownloadURL(storageRef);

    // Save to hrResources collection
    await addDoc(collection(db, 'tenants', tenantId, 'hrResources'), {
      title,
      fileName,
      url,
      storagePath,
      type: 'document',
      mimeType: 'text/plain',
      isPdf: false,
      sizeBytes: blob.size,
      icon: 'auto_awesome',
      generated: true,
      generatedPrompt: prompt,
      uploadedAt: Timestamp.now(),
      uploadedBy: window.AppState?.userName || 'Smart Search',
      createdAt: Timestamp.now()
    });

    btn.innerHTML = '<span class="material-icons" style="font-size:16px;">check</span> Saved';
    btn.classList.remove('btn-primary');
    btn.style.background = '#059669';
    btn.style.color = '#fff';
    window.showToast('Document saved to Company Documents', 'success');
  } catch (err) {
    console.error('[HR] Save generated doc error:', err);
    btn.disabled = false;
    btn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save to Documents';
    window.showToast('Failed to save document', 'error');
  }
}

// Copy generated document content to clipboard
function copyGeneratedDoc(btn) {
  // Find the pre-wrap content div (sibling)
  const container = btn.closest('div[style*="overflow:hidden"]');
  const contentDiv = container?.querySelector('div[style*="pre-wrap"]');
  if (!contentDiv) return;

  navigator.clipboard.writeText(contentDiv.textContent).then(() => {
    const origHTML = btn.innerHTML;
    btn.innerHTML = '<span class="material-icons" style="font-size:16px;">check</span> Copied';
    setTimeout(() => { btn.innerHTML = origHTML; }, 2000);
  }).catch(() => {
    window.showToast('Copy failed', 'error');
  });
}

// TASK-1119 — DEFER, don't drop: several list renderers early-return while their
// detail/create modal is open (a full re-render under an open modal would yank
// the DOM out from under the user). But the snapshot that arrives DURING the
// modal (e.g. the latency-compensated local snapshot right after addDoc) was
// being silently dropped, and the server ack is metadata-only — so the list
// stayed stale until the next unrelated data change. Fix: the guard records
// which list owes a render; closeModal() flushes it the moment the modal hides.
const _pendingModalRenders = new Set();

function _flushPendingModalRender(key) {
  if (!_pendingModalRenders.has(key)) return;
  _pendingModalRenders.delete(key);
  const _renderers = {
    forms: renderFormsList,
    reviews: renderReviewsList,
    checklists: renderChecklistsList,
    safetySignIn: renderSafetySignInList,
    jsa: renderJSAReportsList,
  };
  try { _renderers[key](); } catch (e) { console.warn('[HR] deferred render flush failed:', key, e?.message); }
}

function renderFormsList() {
  const container = document.getElementById('hr-forms-list');
  if (!container) return;
  const modal = document.getElementById('modal-disciplinary');
  if (modal && !modal.classList.contains('hidden')) { _pendingModalRenders.add('forms'); return; } // TASK-1119

  if (HRState.hrForms.length === 0) {
    container.innerHTML = '<div class="empty-state-small">No disciplinary forms yet</div>';
    return;
  }

  container.innerHTML = HRState.hrForms.map(form => {
    const date = form.createdAt?.toDate?.() || new Date(form.createdAt);
    const warningType = form.warningType || 'verbal';
    const warningConfig = WARNING_TYPES[warningType] || WARNING_TYPES.verbal;

    return `
      <div class="hr-list-item" onclick="window.HRModule.viewDisciplinaryForm('${form.id}')">
        <div class="hr-list-item-icon" style="background: ${warningConfig.color}20; color: ${warningConfig.color};">
          <span class="material-icons">${warningConfig.icon}</span>
        </div>
        <div class="hr-list-item-content">
          <div class="hr-list-item-title">${escapeHtml(form.employeeName)}</div>
          <div class="hr-list-item-meta">
            <span style="color: ${warningConfig.color}; font-weight: 500;">${warningConfig.label}</span>
            &bull; ${formatDate(date)}
          </div>
        </div>
        <span class="material-icons hr-list-item-arrow">chevron_right</span>
      </div>
    `;
  }).join('');
}

function renderChecklistsList() {
  const container = document.getElementById('hr-checklists-list');
  if (!container) return;
  const newModal = document.getElementById('modal-new-checklist');
  const viewModal = document.getElementById('modal-view-checklist');
  if ((newModal && !newModal.classList.contains('hidden')) ||
      (viewModal && !viewModal.classList.contains('hidden'))) { _pendingModalRenders.add('checklists'); return; } // TASK-1119

  // Filter to show only incomplete checklists
  const activeChecklists = HRState.hrChecklists.filter(c => c.completionPercent < 100);

  if (activeChecklists.length === 0) {
    container.innerHTML = '<div class="empty-state-small">No active checklists</div>';
    return;
  }

  container.innerHTML = activeChecklists.map(checklist => {
    const date = checklist.lastUpdated?.toDate?.() || new Date(checklist.lastUpdated);
    const percent = checklist.completionPercent || 0;

    return `
      <div class="hr-list-item" onclick="window.HRModule.viewChecklist('${checklist.id}')">
        <div class="hr-list-item-icon success">
          <span class="material-icons">person_add</span>
        </div>
        <div class="hr-list-item-content">
          <div class="hr-list-item-title">${escapeHtml(checklist.employeeName)}</div>
          <div class="hr-list-item-meta">
            Last updated: ${formatDate(date)}
          </div>
          <div class="hr-progress-bar">
            <div class="hr-progress-fill" style="width: ${percent}%"></div>
          </div>
          <div class="hr-progress-text">${percent}% complete</div>
        </div>
        <span class="material-icons hr-list-item-arrow">chevron_right</span>
      </div>
    `;
  }).join('');
}

function renderReviewsList() {
  renderQuickLinks();
  const container = document.getElementById('hr-reviews-list');
  if (!container) return;
  const modal = document.getElementById('modal-view-review');
  if (modal && !modal.classList.contains('hidden')) { _pendingModalRenders.add('reviews'); return; } // TASK-1119

  // Filter to reviews from last 30 days
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const recentReviews = HRState.hrReviews.filter(r => {
    const date = r.createdAt?.toDate?.() || new Date(r.createdAt);
    return date >= thirtyDaysAgo;
  });

  if (recentReviews.length === 0) {
    container.innerHTML = '<div class="empty-state-small">No reviews in the last 30 days</div>';
    return;
  }

  container.innerHTML = recentReviews.map(review => {
    const date = review.createdAt?.toDate?.() || new Date(review.createdAt);
    const positionTitle = review.positionTitle || getPositionTitle(review.positionId); // TASK-1566 — custom positions resolve
    const isNewFormat = review.passFail != null;
    const score = review.totalScore || 0;
    const isPassing = review.passFail === 'PASS';

    // Score color
    const scoreColor = score >= 80 ? '#059669' : score >= 60 ? '#0369a1' : '#dc2626';
    const scoreBg = score >= 80 ? '#ecfdf5' : score >= 60 ? '#f0f9ff' : '#fef2f2';

    // Period label
    const periodLabel = formatReviewPeriod(review.reviewPeriod);

    return `
      <div class="hr-review-card" onclick="window.HRModule.viewReview('${review.id}')">
        <div class="hr-review-card-top">
          <div class="hr-review-card-name">${escapeHtml(review.employeeName)}</div>
          ${/* TASK-4760 — locked vs draft has to be visible from the list, or a
                user only finds out a review is final by clicking Edit and being
                refused. */ ''}
          ${_hrReviewIsLocked(review)
            ? `<span class="material-icons" title="Submitted — locked as a final record" style="font-size:15px;color:var(--text-secondary);">lock</span>`
            : ''}
          ${/* TASK-4776 — a voided review is KEPT on file, so it must read as
                void everywhere it appears. Hiding it would recreate the exact
                absence this task closed. */ ''}
          ${_hrReviewIsVoided(review)
            ? `<span class="hr-review-card-badge" title="${_hrReviewVoidTooltip(review)}" style="background:#f1f5f9;color:#475569;">VOID</span>`
            : ''}
          ${isNewFormat ? `<span class="hr-review-card-badge" style="background:${isPassing ? '#dcfce7' : '#fee2e2'};color:${isPassing ? '#166534' : '#991b1b'};">${escapeHtml(String(review.passFail || ''))}</span>` : ''}
        </div>
        <div class="hr-review-card-position">${escapeHtml(positionTitle || 'Unknown')} &middot; ${escapeHtml(periodLabel)}</div>
        <div class="hr-review-card-bottom">
          <div class="hr-review-card-score" style="background:${scoreBg};color:${scoreColor};">
            ${score.toFixed(0)}<span style="font-size:0.65rem;font-weight:400;">/100</span>
          </div>
          <div class="hr-review-card-date">${formatDate(date)}</div>
        </div>
      </div>
    `;
  }).join('');
}

function calculateAvgRating(review) {
  // Check if this is new format (with kpiScores and overallRating)
  if (review.overallRating != null) {
    return review.overallRating;
  }

  // Legacy format with generic ratings
  const ratings = [
    review.ratings?.quality,
    review.ratings?.attendance,
    review.ratings?.safety,
    review.ratings?.teamwork,
    review.ratings?.initiative
  ].filter(r => r != null).map(Number);

  if (ratings.length === 0) return 0;
  return ratings.reduce((a, b) => a + b, 0) / ratings.length;
}

function renderStars(rating) {
  const fullStars = Math.floor(rating);
  const hasHalf = rating - fullStars >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);

  return `
    ${'<span class="material-icons star-full">star</span>'.repeat(fullStars)}
    ${hasHalf ? '<span class="material-icons star-half">star_half</span>' : ''}
    ${'<span class="material-icons star-empty">star_border</span>'.repeat(emptyStars)}
  `;
}

// ============================================
// DISCIPLINARY FORM
// ============================================
function openDisciplinaryForm() {
  if (_hrGateComplianceOnly('disciplinary')) return; // TASK-1117 — exported+previously ungated (writes hrForms)
  document.getElementById('form-disciplinary')?.reset();
  updateDropdowns();
  HRState.editingDisciplinaryId = null;
  // TASK-1578 Slice 4 — the separation checkout answers are per-open, never
  // carried into the next person's form.
  HRState._termEquipAnswers = {};

  // Reset modal title
  const titleEl = document.getElementById('disc-modal-title');
  if (titleEl) titleEl.innerHTML = `<span class="material-icons">gavel</span> Disciplinary Form`;

  // Reset to Verbal Warning by default
  selectWarningType('verbal');

  // Set default dates to today
  const today = new Date().toISOString().split('T')[0];
  const dateEl = document.getElementById('disc-date');
  if (dateEl) dateEl.value = today;
  const pipStartEl = document.getElementById('disc-pip-start');
  if (pipStartEl) pipStartEl.value = today;
  // TASK-999 — default Last Day Worked to today for a termination
  const termLastDayEl = document.getElementById('disc-term-last-day');
  if (termLastDayEl) {
    termLastDayEl.value = today;
    // TASK-9677 (3) — picker bounds; the submit check (_hrT9677LastDayError) is the real gate.
    termLastDayEl.min = '2000-01-01';
    termLastDayEl.max = new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10);
  }

  _clearDiscFormDirty();
  document.getElementById('modal-disciplinary')?.classList.remove('hidden');

  // Track dirty state on any input
  const form = document.getElementById('form-disciplinary');
  if (form) {
    form.removeEventListener('input', _markDiscFormDirty);
    form.addEventListener('input', _markDiscFormDirty);
  }

  // Init signature canvases
  setTimeout(() => { initDiscSignatureCanvas(); initDiscSupervisorSigCanvas(); }, 100);
}

// --- Disciplinary Signature Canvas ---
let _discSigCtx = null;
let _discSigDrawing = false;
let _discSigHasData = false;
let _discFormDirty = false;
let _discSupSigCtx = null;
let _discSupSigDrawing = false;
let _discSupSigHasData = false;

function _discBeforeUnload(e) {
  if (_discFormDirty) { e.preventDefault(); e.returnValue = ''; }
}

function _markDiscFormDirty() {
  if (!_discFormDirty) {
    _discFormDirty = true;
    window.addEventListener('beforeunload', _discBeforeUnload);
  }
}

function _clearDiscFormDirty() {
  _discFormDirty = false;
  window.removeEventListener('beforeunload', _discBeforeUnload);
}

function initDiscSignatureCanvas() {
  const canvas = document.getElementById('disc-signature-canvas');
  if (!canvas) return;
  _discSigCtx = canvas.getContext('2d');
  _discSigHasData = false;

  // Set actual pixel resolution
  const rect = canvas.getBoundingClientRect();
  canvas.width = rect.width * 2;
  canvas.height = rect.height * 2;
  _discSigCtx.scale(2, 2);
  _discSigCtx.lineWidth = 2;
  _discSigCtx.lineCap = 'round';
  _discSigCtx.lineJoin = 'round';
  _discSigCtx.strokeStyle = '#1a1a1a';
  _discSigCtx.clearRect(0, 0, rect.width, rect.height);

  function getPos(e) {
    const r = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  }

  function startDraw(e) {
    e.preventDefault();
    _discSigDrawing = true;
    _markDiscFormDirty();
    const p = getPos(e);
    _discSigCtx.beginPath();
    _discSigCtx.moveTo(p.x, p.y);
  }

  function draw(e) {
    if (!_discSigDrawing) return;
    e.preventDefault();
    const p = getPos(e);
    _discSigCtx.lineTo(p.x, p.y);
    _discSigCtx.stroke();
    _discSigHasData = true;
  }

  function stopDraw() {
    _discSigDrawing = false;
  }

  // Remove old listeners by replacing canvas node
  const newCanvas = canvas.cloneNode(true);
  canvas.parentNode.replaceChild(newCanvas, canvas);
  _discSigCtx = newCanvas.getContext('2d');
  const r2 = newCanvas.getBoundingClientRect();
  newCanvas.width = r2.width * 2;
  newCanvas.height = r2.height * 2;
  _discSigCtx.scale(2, 2);
  _discSigCtx.lineWidth = 2;
  _discSigCtx.lineCap = 'round';
  _discSigCtx.lineJoin = 'round';
  _discSigCtx.strokeStyle = '#1a1a1a';

  newCanvas.addEventListener('mousedown', startDraw);
  newCanvas.addEventListener('mousemove', draw);
  newCanvas.addEventListener('mouseup', stopDraw);
  newCanvas.addEventListener('mouseleave', stopDraw);
  newCanvas.addEventListener('touchstart', startDraw, { passive: false });
  newCanvas.addEventListener('touchmove', draw, { passive: false });
  newCanvas.addEventListener('touchend', stopDraw);
}

function clearSignatureCanvas() {
  const canvas = document.getElementById('disc-signature-canvas');
  if (!canvas) return;
  _discSigCtx = canvas.getContext('2d');
  const rect = canvas.getBoundingClientRect();
  _discSigCtx.clearRect(0, 0, rect.width * 2, rect.height * 2);
  _discSigHasData = false;
}

function getDiscSignatureDataUrl() {
  const canvas = document.getElementById('disc-signature-canvas');
  if (!canvas || !_discSigHasData) return null;
  return canvas.toDataURL('image/png');
}

// --- Supervisor Signature Canvas ---
function initDiscSupervisorSigCanvas() {
  const canvas = document.getElementById('disc-supervisor-sig-canvas');
  if (!canvas) return;
  _discSupSigHasData = false;

  const newCanvas = canvas.cloneNode(true);
  canvas.parentNode.replaceChild(newCanvas, canvas);
  _discSupSigCtx = newCanvas.getContext('2d');
  const r = newCanvas.getBoundingClientRect();
  newCanvas.width = r.width * 2;
  newCanvas.height = r.height * 2;
  _discSupSigCtx.scale(2, 2);
  _discSupSigCtx.lineWidth = 2;
  _discSupSigCtx.lineCap = 'round';
  _discSupSigCtx.lineJoin = 'round';
  _discSupSigCtx.strokeStyle = '#1a1a1a';

  function getPos(e) {
    const rect = newCanvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  }
  function startDraw(e) { e.preventDefault(); _discSupSigDrawing = true; _markDiscFormDirty(); const p = getPos(e); _discSupSigCtx.beginPath(); _discSupSigCtx.moveTo(p.x, p.y); }
  function draw(e) { if (!_discSupSigDrawing) return; e.preventDefault(); const p = getPos(e); _discSupSigCtx.lineTo(p.x, p.y); _discSupSigCtx.stroke(); _discSupSigHasData = true; }
  function stopDraw() { _discSupSigDrawing = false; }

  newCanvas.addEventListener('mousedown', startDraw);
  newCanvas.addEventListener('mousemove', draw);
  newCanvas.addEventListener('mouseup', stopDraw);
  newCanvas.addEventListener('mouseleave', stopDraw);
  newCanvas.addEventListener('touchstart', startDraw, { passive: false });
  newCanvas.addEventListener('touchmove', draw, { passive: false });
  newCanvas.addEventListener('touchend', stopDraw);
}

function clearSupervisorSig() {
  const canvas = document.getElementById('disc-supervisor-sig-canvas');
  if (!canvas) return;
  _discSupSigCtx = canvas.getContext('2d');
  const rect = canvas.getBoundingClientRect();
  _discSupSigCtx.clearRect(0, 0, rect.width * 2, rect.height * 2);
  _discSupSigHasData = false;
}

function getSupervisorSigDataUrl() {
  const canvas = document.getElementById('disc-supervisor-sig-canvas');
  if (!canvas || !_discSupSigHasData) return null;
  return canvas.toDataURL('image/png');
}

function deleteViewedDisciplinaryForm() {
  if (!HRState.selectedDisciplinaryId) return;
  const form = HRState.hrForms.find(f => f.id === HRState.selectedDisciplinaryId);
  deleteDisciplinaryForm(HRState.selectedDisciplinaryId, form?.employeeId);
}

async function deleteDisciplinaryForm(formId, employeeId) {
  if (!confirm('Delete this disciplinary form? This cannot be undone.')) return;
  try {
    await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'hrForms', formId));
    window.showToast?.('Form deleted', 'success');
    closeModal('view-disciplinary');
    // Refresh employee detail if open
    if (employeeId) openEmployeeDetail(employeeId);
  } catch (error) {
    console.error('[HR] Error deleting form:', error);
    window.showToast?.('Error deleting form', 'error');
  }
}

function selectWarningType(type) {
  const config = WARNING_TYPES[type];
  if (!config) return;

  // Update radio button
  const radioBtn = document.querySelector(`input[name="disc-warning-type"][value="${type}"]`);
  if (radioBtn) radioBtn.checked = true;

  // Update visual selection
  document.querySelectorAll('.warning-type-option').forEach(opt => {
    opt.classList.toggle('selected', opt.dataset.type === type);
  });

  // Update modal title
  const titleEl = document.getElementById('disc-modal-title');
  if (titleEl) {
    titleEl.innerHTML = `<span class="material-icons">gavel</span> Disciplinary Form - ${config.label}`;
  }

  // Update description text
  const descEl = document.getElementById('disc-type-description');
  if (descEl) descEl.textContent = config.description;

  // Update acknowledgment text
  const ackEl = document.getElementById('disc-acknowledgment-text');
  if (ackEl) ackEl.textContent = config.acknowledgmentText;

  const isTermination = type === 'termination';

  // Show/hide PIP-specific fields
  const pipFields = document.getElementById('disc-pip-fields');
  if (pipFields) {
    pipFields.style.display = type === 'pip' ? 'block' : 'none';
  }

  // TASK-999 — Show/hide Termination-specific fields
  const termFields = document.getElementById('disc-termination-fields');
  if (termFields) {
    termFields.style.display = isTermination ? 'block' : 'none';
  }

  // TASK-999 — For a termination, the "Issue/Concern" textarea IS the reason /
  // summary (relabelled + a prefill-from-history helper). Reuses the existing
  // required textarea rather than duplicating a parallel reason field.
  const issueLabel = document.getElementById('disc-issue-label');
  if (issueLabel) {
    issueLabel.textContent = isTermination ? 'Reason for Separation *' : 'Issue/Concern Discussed *';
  }
  const prefillBtn = document.getElementById('disc-term-prefill-btn');
  if (prefillBtn) prefillBtn.style.display = isTermination ? 'inline-flex' : 'none';

  // TASK-999 — "What Will Be Expected Going Forward" doesn't apply to a
  // separation. Hide + drop `required` so validateRequiredFields (which skips
  // display:none fields, but belt-and-suspenders) never traps the user.
  const expectationsGroup = document.getElementById('disc-expectations-group');
  const expectationsField = document.getElementById('disc-expectations');
  if (expectationsGroup) expectationsGroup.style.display = isTermination ? 'none' : 'block';
  if (expectationsField) {
    if (isTermination) expectationsField.removeAttribute('required');
    else expectationsField.setAttribute('required', '');
  }

  // Update expectations label for PIP
  const expectationsLabel = document.getElementById('disc-expectations-label');
  if (expectationsLabel) {
    expectationsLabel.textContent = type === 'pip'
      ? 'Additional Improvement Goals *'
      : 'What Will Be Expected Going Forward *';
  }

  // TASK-999 — refresh the read-only FMCSA §391.23 SPH link for the selected employee
  if (isTermination) _hrRenderTerminationSphLink();
  // TASK-1578 Slice 4 — and the live property checkout (falls back to the five
  // static checkboxes when the log has nothing open for this person).
  if (isTermination) _hrRenderTerminationPropertyChecklist();
}

// TASK-999 — Fired when the employee dropdown changes on the disciplinary form.
// Keeps the read-only SPH link in sync when Termination is the active type.
function _onDiscEmployeeChange() {
  const type = document.querySelector('input[name="disc-warning-type"]:checked')?.value;
  if (type === 'termination') {
    _hrRenderTerminationSphLink();
    // TASK-1578 Slice 4 — a different employee means different records; keeping
    // the old Yes/No answers would silently attribute one person's checkout to
    // another. Drop them and rebuild.
    HRState._termEquipAnswers = {};
    _hrRenderTerminationPropertyChecklist();
  }
}

// TASK-999 — Render a READ-ONLY summary of the selected person's FMCSA §391.23
// Safety Performance History (captured on the Roster person card). This form
// NEVER writes safetyPerformanceHistory — it only references it so the signed
// separation notice and the DOT SPH record stay linked without diverging.
function _hrRenderTerminationSphLink() {
  const el = document.getElementById('disc-term-sph-link');
  if (!el) return;
  const empId = document.getElementById('disc-employee')?.value || '';
  const person = (HRState.people || []).find(p => p.id === empId);
  const sph = person && person.safetyPerformanceHistory;
  const wrap = (inner) => `<div style="border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-elevated);padding:10px 12px;">
      <div style="font-weight:600;display:flex;align-items:center;gap:6px;margin-bottom:6px;">
        <span class="material-icons" style="font-size:16px;color:var(--brand-blue);">verified_user</span>
        FMCSA §391.23 Safety Performance History
      </div>${inner}</div>`;
  if (!empId) { el.innerHTML = ''; return; }
  if (!sph || Object.keys(sph).length === 0) {
    el.innerHTML = wrap(`<div style="color:var(--text-secondary);">No DOT Safety Performance History on file for this person yet. It is captured on the <strong>Roster</strong> person card (Terminated section) — separate from this signed notice.</div>`);
    return;
  }
  const yn = (v) => v === 'yes' ? 'Yes' : v === 'no' ? 'No' : '—';
  const rows = [
    ['Voluntary separation', yn(sph.voluntary)],
    ['Eligible for rehire', yn(sph.eligibleForRehire)],
    ['Recordable accidents', yn(sph.hadAccidents)],
    ['Drug/alcohol violation', yn(sph.hadDrugAlcoholViolation)],
    ['Return-to-duty completed', yn(sph.returnToDutyCompleted)],
  ].filter(r => r[1] !== '—');
  const rowsHtml = rows.length
    ? rows.map(r => `<div style="display:flex;justify-content:space-between;gap:12px;padding:2px 0;"><span style="color:var(--text-secondary);">${r[0]}</span><span style="font-weight:500;">${r[1]}</span></div>`).join('')
    : '<div style="color:var(--text-secondary);">Record on file (no yes/no answers set).</div>';
  el.innerHTML = wrap(`${rowsHtml}<div style="margin-top:6px;font-size:0.72rem;color:var(--text-secondary);">This DOT record is read-only here — edit it on the Roster person card.</div>`);
}

// ============================================
// TASK-1578 Slice 4 — SEPARATION PROPERTY CHECKOUT
// ============================================
// The five static checkboxes ask "did you get the PPE back?" — a category, not a
// thing. When the equipment issue log has open items for this person, the LIVE
// list replaces them: every item they actually hold, one Yes/No each, and the
// replacement cost of whatever is answered No totalled underneath. That total is
// the *replacement cost of unreturned property*, NOT an amount authorized to be
// deducted from anyone's pay — the copy says so, on screen and on paper.
//
// Answers live in HRState (not the DOM) so a safetyEquipment snapshot landing
// mid-form can repaint the list without wiping what the separator already
// clicked.

// Open (not-yet-returned) items for whoever is selected in the disciplinary
// form's employee dropdown. `denied` is kept distinct from `empty` for the same
// reason the person card does: "no items" is a claim about an employee and a
// refused read must never be allowed to make it.
function _hrTerminationOpenEquipment() {
  const empId = document.getElementById('disc-employee')?.value || '';
  const emp = empId ? (HRState.people || []).find(p => p.id === empId) : null;
  if (!emp) return { empId, emp: null, items: [], denied: false };
  if (HRState.safetyEquipmentDenied === true) return { empId, emp, items: [], denied: true };
  const items = _hrEquipmentForPerson(emp)
    .filter(r => window.PersonSections.equipmentStatus(r) !== 'returned')
    .sort((a, b) => String(a.equipmentType || '').localeCompare(String(b.equipmentType || '')));
  return { empId, emp, items, denied: false };
}

// The items the separator answered "not returned" AND can still lawfully be
// charged for. Everything money-related runs through
// PersonSections.outstandingEquipmentValue — ONE total function in the product,
// so the person card and this sheet can never disagree.
//
// 🔴 Gear whose ownership has transferred to the employee is excluded even when
// answered "not returned", because it is not unreturned — it is THEIRS. TITS's
// agreement: FR shirts and pants become the employee's own property on their 90th
// day of employment and are never returned. Charging for that is billing someone
// for their own property. Chris, 2026-08-14: "we wont ask for money back after 90
// days." The predicate fails toward NOT charging when the hire date is unknown.
function _hrTermUnreturnedItems(items, emp) {
  const ans = HRState._termEquipAnswers || {};
  return (items || []).filter(r => ans[r.id] === 'n')
    .filter(r => !(emp && window.PersonSections.equipmentOwnershipTransferred(r, emp)));
}

// Items the employee now owns outright. Surfaced as their own group rather than
// silently dropped: an item vanishing off a checkout sheet with no explanation is
// how someone concludes the log is wrong and goes back to a paper list.
function _hrTermEmployeeOwnedItems(items, emp) {
  if (!emp) return [];
  return (items || []).filter(r => window.PersonSections.equipmentOwnershipTransferred(r, emp));
}

function _hrSetTermEquipAnswer(recordId, value) {
  if (!HRState._termEquipAnswers) HRState._termEquipAnswers = {};
  HRState._termEquipAnswers[recordId] = (value === 'y' || value === 'n') ? value : null;
  _hrUpdateTermEquipTotal();
}

function _hrUpdateTermEquipTotal() {
  const { items, emp } = _hrTerminationOpenEquipment();
  const ans = HRState._termEquipAnswers || {};
  const unreturned = _hrTermUnreturnedItems(items, emp);
  const owed = window.PersonSections.outstandingEquipmentValue(unreturned, emp);
  const unanswered = items.filter(r => ans[r.id] !== 'y' && ans[r.id] !== 'n').length;

  const totalEl = document.getElementById('disc-term-eq-total');
  if (totalEl) totalEl.textContent = owed > 0 ? '$' + owed.toFixed(2) : '$0.00';
  const cntEl = document.getElementById('disc-term-eq-count');
  if (cntEl) {
    cntEl.textContent = unreturned.length
      ? `${unreturned.length} item${unreturned.length === 1 ? '' : 's'} not returned`
      : 'All listed items returned';
  }
  const unansEl = document.getElementById('disc-term-eq-unanswered');
  if (unansEl) {
    unansEl.style.display = unanswered ? '' : 'none';
    unansEl.textContent = `${unanswered} item${unanswered === 1 ? '' : 's'} still unanswered — those are recorded as "not recorded", not as returned.`;
  }
}

// Render (or hide) the live checkout. Called when Termination is selected, when
// the employee dropdown changes, and when a safetyEquipment snapshot lands.
function _hrRenderTerminationPropertyChecklist() {
  const live = document.getElementById('disc-term-property-live');
  const stat = document.getElementById('disc-term-property-static');
  if (!live || !stat) return;
  const isTerm = document.querySelector('input[name="disc-warning-type"]:checked')?.value === 'termination';
  if (!isTerm) return;

  // The disciplinary modal can be opened without ever visiting a person card or
  // the Safety view, which are the only other places this listener starts.
  _hrEnsureSafetyEquipmentSubscription();

  const { items, denied, emp } = _hrTerminationOpenEquipment();

  // 🔴 WAIT for the ledger before choosing a mode. Falling back to static merely
  // because `items` is empty conflates "this person holds nothing" with "the
  // snapshot hasn't landed yet" — and in that ~200ms window the static checkboxes
  // are live, so anything ticked there is silently DISCARDED the moment the real
  // list arrives and replaces them. Losing a separation answer is losing evidence
  // about company property, so hold a neutral placeholder instead of guessing.
  // Flagged by COD on the integrated diff.
  if (!HRState.safetyEquipmentLoaded && !denied) {
    live.style.display = '';
    live.innerHTML = `<div style="padding:var(--spacing-sm);color:var(--text-secondary);font-size:0.85rem;">
        <span class="loading-spinner-small" style="margin-right:6px;"></span>Checking issued equipment…
      </div>`;
    stat.style.display = 'none';
    return;
  }

  // Fall back to the static checkboxes whenever the log genuinely can't answer the
  // question: nothing issued, or the read was refused. Never show an empty live
  // list — that reads as "they hold nothing", which we don't know.
  if (denied || !items.length) {
    live.style.display = 'none';
    live.innerHTML = '';
    stat.style.display = '';
    return;
  }

  const ans = HRState._termEquipAnswers || {};
  const escA = window.escAttr || escapeHtml;

  // 🔴 Split BEFORE rendering. Gear whose ownership has transferred is the
  // employee's property — it is not a return question, so it must not get a Y/N
  // control at all. Asking "returned? Y/N" about something they own invites the
  // wrong answer and then charges for it.
  const ownedByEmployee = _hrTermEmployeeOwnedItems(items, emp);
  const ownedIds = new Set(ownedByEmployee.map(r => r.id));
  const chargeable = items.filter(r => !ownedIds.has(r.id));

  // Shown, never silently dropped — an item disappearing off a checkout sheet is
  // how someone decides the log is wrong and goes back to a paper list.
  const ownedHtml = ownedByEmployee.length ? `
    <div style="margin-top:10px;padding:8px 10px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-sm);">
      <div style="font-size:0.72rem;font-weight:700;text-transform:uppercase;letter-spacing:0.3px;color:var(--text-secondary);margin-bottom:4px;">
        Employee keeps &mdash; not returned, nothing owed
      </div>
      ${ownedByEmployee.map(r => `
        <div style="font-size:0.82rem;color:var(--text-primary);">
          ${escapeHtml(String(r.equipmentType || 'Equipment'))}${r.size ? ' (' + escapeHtml(String(r.size)) + ')' : ''}
          <span style="color:var(--text-secondary);">&mdash; became the employee's property after ${escapeHtml(String(r.transfersToEmployeeAfterDays))} days</span>
        </div>`).join('')}
      ${(emp && !(emp.hireDate || emp.dateOfHire || emp.startDate)) ? `
        <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:5px;">
          No hire date on file, so the ownership window can't be confirmed &mdash; these are treated as the employee's and are not charged.
        </div>` : ''}
    </div>` : '';

  const rows = chargeable.map(r => {
    const qty = Math.max(1, parseInt(r.quantity, 10) || 1);
    const unit = Number(r.replacementValue);
    const hasVal = isFinite(unit) && unit > 0;
    const a = ans[r.id];
    const btn = (val, label, activeColor) => `
      <label style="display:inline-flex;align-items:center;gap:4px;font-weight:600;font-size:0.72rem;cursor:pointer;border:1px solid ${a === val ? activeColor : 'var(--border-light)'};background:${a === val ? activeColor : 'var(--bg-elevated)'};color:${a === val ? '#fff' : 'var(--text-primary)'};border-radius:999px;padding:3px 12px;">
        <input type="radio" name="disc-term-eq-${escA(r.id)}" value="${val}" ${a === val ? 'checked' : ''}
          onchange="window.HRModule._setTermEquipAnswer('${escJsAttr(r.id)}','${escJsAttr(val)}')" style="display:none;"> ${label}
      </label>`;
    return `
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:7px 2px;border-bottom:1px solid var(--border-light);">
        <div style="flex:1;min-width:170px;">
          <div style="font-weight:600;color:var(--text-primary);font-size:0.85rem;">
            ${escapeHtml(r.equipmentType || 'Equipment')}${r.size ? ' (' + escapeHtml(r.size) + ')' : ''}${qty > 1 ? ' &times;' + qty : ''}
          </div>
          <div style="font-size:0.7rem;color:var(--text-secondary);">
            ${r.serialNumber ? 'Serial ' + escapeHtml(String(r.serialNumber)) + ' &middot; ' : ''}Issued ${escapeHtml(_hrEquipDateLabel(r.dateReceived) || String(r.dateReceived || '—'))}
          </div>
        </div>
        <div style="font-size:0.78rem;color:var(--text-primary);white-space:nowrap;min-width:74px;text-align:right;">
          ${hasVal ? escapeHtml('$' + (unit * qty).toFixed(2)) : '<span style="color:var(--text-secondary);">no value</span>'}
        </div>
        <div style="display:inline-flex;gap:6px;">
          ${btn('y', 'Returned', '#15803d')}
          ${btn('n', 'Not returned', '#b91c1c')}
        </div>
      </div>`;
  }).join('');

  stat.style.display = 'none';
  live.style.display = '';
  live.innerHTML = `
    <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:6px;">
      From this employee's equipment issue log &mdash; mark each item as you collect it.
    </p>
    <div style="border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-primary);padding:4px 10px 8px;">
      ${rows}
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;padding-top:8px;">
        <div>
          <div style="font-size:0.78rem;font-weight:600;color:var(--text-primary);" id="disc-term-eq-count">&nbsp;</div>
          <div style="font-size:0.7rem;color:#b45309;display:none;" id="disc-term-eq-unanswered">&nbsp;</div>
        </div>
        <div style="text-align:right;">
          <div style="font-size:0.7rem;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.4px;">Replacement cost, not returned</div>
          <div style="font-size:1.05rem;font-weight:700;color:var(--text-primary);" id="disc-term-eq-total">$0.00</div>
        </div>
      </div>
      <div style="font-size:0.7rem;color:var(--text-primary);background:var(--bg-secondary);border-radius:8px;padding:8px 10px;margin-top:8px;line-height:1.45;">
        This is the company's cost to replace the items not handed back. <strong>It is not an amount authorized to be
        deducted from final pay.</strong> Normal wear and tear is never charged. Any deduction from wages needs its own
        written authorization, signed at separation, and is limited or prohibited outright in some states &mdash; it may
        never drop pay below minimum wage or come out of overtime premium.
      </div>
      <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:6px;">
        Saving this notice also updates the equipment log: items marked <em>Returned</em> are closed out, items marked
        <em>Not returned</em> are flagged unreturned. Items the employee now owns are left untouched.
      </div>
      <div style="margin-top:8px;">
        <button type="button" class="btn btn-sm btn-secondary" style="font-size:0.72rem;padding:3px 10px;display:inline-flex;align-items:center;gap:4px;"
          onclick="window.HRModule.printSeparationCheckoutInsert('${escJsAttr(emp.id)}')"
          title="${escA('Print the Separation Checkout attachment for your own separation form — blank Y/N if nothing is answered yet, filled in once it is')}">
          <span class="material-icons" style="font-size:14px;">print</span> Print checkout attachment
        </button>
        <span style="font-size:0.68rem;color:var(--text-secondary);margin-left:6px;">Attaches to your own separation form.</span>
      </div>
      ${ownedHtml}
    </div>`;
  _hrUpdateTermEquipTotal();
}

// Repaint the checkout when a safetyEquipment snapshot lands while the
// disciplinary modal is open. Answers live in HRState, so a repaint keeps them.
function _hrRefreshTerminationPropertyChecklist() {
  const modal = document.getElementById('modal-disciplinary');
  if (!modal || modal.classList.contains('hidden')) return;
  if (!document.getElementById('disc-term-property-live')) return;
  _hrRenderTerminationPropertyChecklist();
}

// The saved checkout, rendered back on the notice. Both readers below use the
// STORED `terminationEquipmentUnreturnedValue` rather than re-summing: the
// figure on the signed notice must be the figure that was on screen when it was
// signed, even if the ledger moved afterwards.
function _hrTermCheckoutRows(form) {
  const rows = form && form.terminationEquipmentCheckout;
  return Array.isArray(rows) && rows.length ? rows : null;
}
// Shared by BOTH saved-notice renderers (on-screen viewer and print), so the
// carve-out below cannot end up on one and not the other.
//
// 🔴 An owned row says so ON THE ROW. Its value and its returned-answer are both
// already null, so without this it reads as an ordinary checkout line that nobody
// filled in — which on a durable record is worse than wrong, it is ambiguous.
// Old notices saved before this shipped have no employeeOwns flag, so they render
// exactly as they always did.
function _hrTermCheckoutLabel(row) {
  const qty = Math.max(1, parseInt(row.quantity, 10) || 1);
  const base = `${row.item || 'Equipment'}${row.size ? ' (' + row.size + ')' : ''}${qty > 1 ? ' ×' + qty : ''}`;
  if (!row.employeeOwns) return base;
  const d = Number(row.transfersToEmployeeAfterDays);
  return `${base} — employee keeps${d > 0 ? ` (theirs after ${d} days)` : ''}, nothing owed`;
}
function _hrTermCheckoutLineValue(row) {
  const unit = Number(row.unitReplacementValue);
  if (!isFinite(unit) || unit <= 0) return '';
  const qty = Math.max(1, parseInt(row.quantity, 10) || 1);
  return '$' + (unit * qty).toFixed(2);
}

// On-screen (person card → notice viewer).
function _hrTermCheckoutViewHtml(form) {
  const rows = _hrTermCheckoutRows(form);
  if (!rows) return '';
  const owed = Number(form.terminationEquipmentUnreturnedValue);
  const body = rows.map(r => {
    const ansTxt = r.returned === true ? 'Returned' : r.returned === false ? 'Not returned' : 'Not recorded';
    const color = r.returned === true ? '#15803d' : r.returned === false ? '#b91c1c' : 'var(--text-secondary)';
    const val = _hrTermCheckoutLineValue(r);
    return `<div style="display:flex;gap:10px;align-items:center;flex-wrap:wrap;padding:4px 0;border-bottom:1px solid var(--border-light);">
      <span style="flex:1;min-width:150px;color:var(--text-primary);font-size:0.85rem;">${escapeHtml(_hrTermCheckoutLabel(r))}</span>
      <span style="font-size:0.8rem;color:var(--text-primary);min-width:70px;text-align:right;">${val ? escapeHtml(val) : '&mdash;'}</span>
      <span style="font-size:0.75rem;font-weight:600;color:${color};min-width:96px;text-align:right;">${escapeHtml(ansTxt)}</span>
    </div>`;
  }).join('');
  return `
    <div class="hr-form-field">
      <label>Company Property Checkout</label>
      <div style="border:1px solid var(--border-light);border-radius:var(--radius-md);padding:6px 10px;background:var(--bg-elevated);">
        ${body}
        <div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;padding-top:6px;">
          <span style="font-size:0.75rem;color:var(--text-secondary);">Replacement cost of items not returned</span>
          <span style="font-size:0.9rem;font-weight:700;color:var(--text-primary);">${escapeHtml(isFinite(owed) && owed > 0 ? '$' + owed.toFixed(2) : '$0.00')}</span>
        </div>
        <div style="font-size:0.7rem;color:var(--text-primary);margin-top:4px;line-height:1.4;">
          Replacement cost only &mdash; not an authorized wage deduction. Normal wear and tear is never charged.
        </div>
      </div>
    </div>`;
}

// Printed (the sheet that goes in the file with the signed notice).
function _hrTermCheckoutPrintHtml(form) {
  const rows = _hrTermCheckoutRows(form);
  if (!rows) return '';
  const _esc = (s) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const owed = Number(form.terminationEquipmentUnreturnedValue);
  const cell = 'border:1px solid #999;padding:4px 6px;font-size:11px;';
  const body = rows.map(r => `
    <tr>
      <td style="${cell}">${_esc(_hrTermCheckoutLabel(r))}</td>
      <td style="${cell}text-align:right;">${_esc(_hrTermCheckoutLineValue(r) || '—')}</td>
      <td style="${cell}text-align:center;font-weight:600;">${_esc(r.returned === true ? 'YES' : r.returned === false ? 'NO' : '—')}</td>
    </tr>`).join('');
  return `
    <div class="section">
      <div class="section-label">Company Property Checkout</div>
      <table style="width:100%;border-collapse:collapse;margin-top:6px;">
        <thead><tr>
          <th style="border:1px solid #999;padding:4px 6px;font-size:10px;text-transform:uppercase;background:#f0f0f0;text-align:left;">Item</th>
          <th style="border:1px solid #999;padding:4px 6px;font-size:10px;text-transform:uppercase;background:#f0f0f0;text-align:right;">Replacement cost</th>
          <th style="border:1px solid #999;padding:4px 6px;font-size:10px;text-transform:uppercase;background:#f0f0f0;text-align:center;">Returned?</th>
        </tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr>
          <td style="border:1px solid #999;padding:4px 6px;font-size:11px;text-align:right;font-weight:700;background:#f7f7f7;">Replacement cost of items not returned</td>
          <td style="border:1px solid #999;padding:4px 6px;font-size:11px;text-align:right;font-weight:700;background:#f7f7f7;" colspan="2">${_esc(isFinite(owed) && owed > 0 ? '$' + owed.toFixed(2) : '$0.00')}</td>
        </tr></tfoot>
      </table>
      <div style="font-size:10px;color:#444;margin-top:6px;line-height:1.45;border:1px solid #999;background:#f7f7f7;padding:8px 10px;">
        <strong>This figure is the company's replacement cost for property not handed back. It is not an authorized
        deduction from pay.</strong> Normal wear and tear is never charged. Any deduction from wages requires a separate
        written authorization signed at the time of the deduction, is limited or prohibited outright in some states,
        and may never bring pay below the applicable minimum wage for the workweek or come out of overtime premium pay
        (29 C.F.R. §§531.35, 531.37). Have counsel review before withholding any pay.
      </div>
    </div>`;
}

// TASK-999 — Prefill the Reason for Separation from the person's prior
// disciplinary forms (the natural end state of the ladder). Reference only —
// the admin edits the pulled text before submitting.
function prefillTerminationReason() {
  const empId = document.getElementById('disc-employee')?.value || '';
  if (!empId) { window.showToast?.('Select the employee first', 'info'); return; }
  const prior = (HRState.hrForms || [])
    .filter(f => f.employeeId === empId && f.warningType && f.warningType !== 'termination')
    .sort((a, b) => {
      const da = a.formDate || (a.createdAt?.toDate?.()?.toISOString?.().split('T')[0]) || '';
      const db = b.formDate || (b.createdAt?.toDate?.()?.toISOString?.().split('T')[0]) || '';
      return da < db ? -1 : da > db ? 1 : 0;
    });
  if (prior.length === 0) { window.showToast?.('No prior disciplinary forms on file for this employee', 'info'); return; }
  const summary = prior.map(f => {
    const label = WARNING_TYPES[f.warningType]?.label || f.warningType;
    const date = f.formDate || (f.createdAt?.toDate?.()?.toISOString?.().split('T')[0]) || 'unknown';
    return `• ${label} (${date}): ${(f.issue || '').trim()}`;
  }).join('\n');
  const issueEl = document.getElementById('disc-issue');
  if (issueEl) {
    const header = `Prior disciplinary history (${prior.length} form${prior.length === 1 ? '' : 's'} on file):\n`;
    issueEl.value = header + summary + '\n\n';
    issueEl.dispatchEvent(new Event('input', { bubbles: true }));
    issueEl.focus();
  }
}

// TASK-9677 (3) — last-day-worked plausibility for the disciplinary termination notice.
// 🔴 SAME RULES + messages as roster.js _t9662YmdOf / _t9662LastDayError (hr.js cannot
// import roster.js — Pitfall #1); tests/task9677-crown-followups.unit.cjs runs both
// copies over one fixture matrix. Empty = no error (the caller requires a value).
function _hrT9677YmdOf(v) {
  if (!v) return '';
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
    const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(v.trim());
    if (us) {
      const y = +us[3], mo = +us[1], d = +us[2];
      const dt = new Date(Date.UTC(y, mo - 1, d));
      if (y >= 1000 && dt.getUTCFullYear() === y && dt.getUTCMonth() === mo - 1 && dt.getUTCDate() === d) {
        return `${us[3]}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      }
    }
    return '';
  }
  const d = (v && typeof v.toDate === 'function') ? v.toDate() : (v instanceof Date ? v : null);
  return (d && !isNaN(d.getTime())) ? d.toISOString().slice(0, 10) : '';
}
function _hrT9677LastDayError(ymd, hireDate, now) {
  const v = String(ymd || '').trim();
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return 'Enter the last day worked as a full date.';
  const dt = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  if (+m[1] < 2000) return `The year on ${v} is ${m[1]} — check it (did you mean ${'20' + m[1].slice(2)}?).`;
  if (dt.getUTCFullYear() !== +m[1] || dt.getUTCMonth() !== +m[2] - 1 || dt.getUTCDate() !== +m[3]) return `${v} is not a real date.`;
  const t = now instanceof Date ? now : new Date();
  const max = new Date(t.getTime() + 90 * 86400000).toISOString().slice(0, 10);
  if (v > max) return `${v} is more than 90 days from today. Enter the actual last day worked.`;
  const hire = _hrT9677YmdOf(hireDate);
  if (hire && hire >= '2000-01-01' && v < hire) return `${v} is before the hire date (${hire}).`;
  return null;
}

async function submitDisciplinaryForm(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const supervisorId = document.getElementById('disc-supervisor').value;
  const employeeId = document.getElementById('disc-employee').value;
  const formDateStr = document.getElementById('disc-date').value;
  const issue = document.getElementById('disc-issue').value.trim();
  const expectations = document.getElementById('disc-expectations').value.trim();
  const signatureDataUrl = getDiscSignatureDataUrl();
  const supervisorSigDataUrl = getSupervisorSigDataUrl();
  const comments = document.getElementById('disc-comments').value.trim();

  // Get warning type
  const warningType = document.querySelector('input[name="disc-warning-type"]:checked')?.value || 'verbal';

  const supervisor = HRState.supervisors.find(p => p.id === supervisorId);
  const employee = HRState.people.find(p => p.id === employeeId);

  if (!supervisor || !employee) {
    window.showToast?.('Please select supervisor and employee', 'error');
    return;
  }

  // Use selected date (noon local time to avoid timezone shifting)
  const formDate = formDateStr ? new Date(formDateStr + 'T12:00:00') : new Date();
  const createdAt = Timestamp.fromDate(formDate);

  // M12 (2026-06-25): when EDITING an existing form, preserve a previously-captured
  // signature if the edit didn't re-capture one. The firestore.rules freeze on
  // hrForms.employeeSignature (immutable once set) would otherwise DENY a legit edit
  // that re-sends '' over a signed form. Keeps the signature non-repudiable + lets
  // admins edit other fields.
  const _editingForm = HRState.editingDisciplinaryId
    ? (HRState.hrForms.find(f => f.id === HRState.editingDisciplinaryId) || null)
    : null;
  const finalEmployeeSig = signatureDataUrl || _editingForm?.employeeSignature || '';
  const finalSupervisorSig = supervisorSigDataUrl || _editingForm?.supervisorSignature || '';

  const formData = {
    type: 'disciplinary',
    warningType,
    supervisorId,
    supervisorName: supervisor.name,
    employeeId,
    employeeName: employee.name,
    issue,
    expectations,
    employeeSignature: finalEmployeeSig,
    supervisorSignature: finalSupervisorSig,
    comments,
    createdAt,
    formDate: formDateStr || new Date().toISOString().split('T')[0],
    createdBy: window.AppState?.user?.email || 'unknown',
    // TASK-346 Slice 2 — UID stamp so ownership-scoped queries (My Crew
    // "show only forms I wrote") and future Firestore-rule writes (e.g.
    // "supervisor can write hrForms where their personId === target.reportsTo")
    // have a stable principal. Email is mutable; UID is not.
    createdByUid: window.AppState?.user?.uid || null,
  };

  // Add PIP-specific fields
  if (warningType === 'pip') {
    const performanceExpectations = document.getElementById('disc-pip-expectations')?.value.trim() || '';

    if (!performanceExpectations) {
      window.showToast?.('Please enter performance expectations', 'error');
      return;
    }

    formData.pipPerformanceExpectations = performanceExpectations;
    formData.pipDuration = parseInt(document.getElementById('disc-pip-duration')?.value || '90', 10);
    formData.pipStartDate = document.getElementById('disc-pip-start')?.value || null;
    formData.employeeComments = document.getElementById('disc-employee-comments')?.value.trim() || '';

    // Calculate end date
    if (formData.pipStartDate) {
      const startDate = new Date(formData.pipStartDate);
      startDate.setDate(startDate.getDate() + formData.pipDuration);
      formData.pipEndDate = startDate.toISOString().split('T')[0];
    }
  }

  // TASK-999 — Termination-specific fields. NOTE: this writes ONLY the signed-
  // notice fields (terminationEligibleForRehire is the employee-facing notice's
  // answer). It intentionally does NOT write person.safetyPerformanceHistory —
  // that FMCSA §391.23 record is owned by the Roster person card, surfaced here
  // read-only, so the two records stay linked without diverging.
  // TASK-1578 Slice 4 — the live checkout snapshot, hoisted so the post-save
  // ledger write-back below can see it. Null = the static checkboxes were used.
  let _termEquipCheckout = null;
  if (warningType === 'termination') {
    const separationType = document.getElementById('disc-term-separation-type')?.value || '';
    const lastDayWorked = document.getElementById('disc-term-last-day')?.value || '';
    const eligibleForRehire = document.getElementById('disc-term-rehire')?.value || '';
    const finalPayNote = document.getElementById('disc-term-final-pay')?.value.trim() || '';
    const propertyReturned = [];
    ['keys', 'fuelcard', 'device', 'ppe', 'other'].forEach(k => {
      if (document.getElementById('disc-term-prop-' + k)?.checked) propertyReturned.push(k);
    });

    if (!lastDayWorked) { window.showToast?.('Please enter the last day worked', 'error'); return; }
    // TASK-9677 (3) — same bound as the roster termination (roster.js _t9662LastDayError,
    // server _t9662LastDayError): a real date, year >= 2000, not before the hire date,
    // at most today+90. Only a NEW or CHANGED date is checked, so re-saving an old signed
    // notice (e.g. the employee was later rehired, moving hireDate past it) still works.
    if (!_editingForm || _editingForm.terminationLastDayWorked !== lastDayWorked) {
      const _ldErr = _hrT9677LastDayError(lastDayWorked, employee.hireDate);
      if (_ldErr) { window.showToast?.(_ldErr, 'error', 8000); return; }
    }
    if (!eligibleForRehire) { window.showToast?.('Please select rehire eligibility', 'error'); return; }

    formData.terminationSeparationType = separationType;
    formData.terminationLastDayWorked = lastDayWorked;
    formData.terminationEligibleForRehire = eligibleForRehire;
    formData.terminationFinalPayNote = finalPayNote;
    // A separation has no "going forward" expectations — don't persist a stale value.
    formData.expectations = '';

    // TASK-1578 Slice 4 — SUPERSEDE the five static checkboxes when the live
    // equipment checkout was the thing on screen. Both paths write the SAME
    // `terminationPropertyReturned` field so every existing reader (the view
    // section, the print sheet) keeps working untouched: the static path writes
    // category keys ('keys','ppe'…), the live path writes item labels, and both
    // render through `propLabels[p] || p`, which falls through for a label.
    //
    // Which UI the user actually SAW is the authority here — not a re-derivation
    // from state, which could have changed under them between render and submit.
    const liveEl = document.getElementById('disc-term-property-live');
    const usedLiveCheckout = !!liveEl && liveEl.style.display !== 'none' && liveEl.innerHTML.trim() !== '';
    if (usedLiveCheckout) {
      const { items, emp: _termEmp } = _hrTerminationOpenEquipment();
      const ans = HRState._termEquipAnswers || {};
      // 🔴 NO serial numbers in this snapshot. hrForms is a different collection
      // with a different audience; serials and fuel-card ids stay in the ledger.
      // `returned: null` = not recorded — deliberately distinct from `false`
      // (recorded as NOT handed back), which is the answer money hangs off.
      _termEquipCheckout = items.map(r => {
        const unit = Number(r.replacementValue);
        // 🔴 Mark gear the employee now OWNS. The live screen and the printed
        // attachment both carve these out, but the STORED notice is the durable
        // record — the thing someone reads back a year later — and without this flag
        // it listed transferred items as checkout rows carrying a replacement value.
        // The total was right; the document still implied money was owed on gear
        // that is legally theirs. Flagged by COD on the integrated diff.
        const employeeOwns = !!(_termEmp && window.PersonSections.equipmentOwnershipTransferred(r, _termEmp));
        return {
          recordId: r.id,
          item: String(r.equipmentType || 'Equipment'),
          size: String(r.size || ''),
          quantity: Math.max(1, parseInt(r.quantity, 10) || 1),
          category: String(r.category || ''),
          // Null on owned gear so no reader can revive a value we have decided is
          // not chargeable — the flag alone would leave the number sitting there.
          unitReplacementValue: employeeOwns ? null : ((isFinite(unit) && unit > 0) ? unit : null),
          employeeOwns,
          transfersToEmployeeAfterDays: employeeOwns ? (Number(r.transfersToEmployeeAfterDays) || null) : null,
          // An owned item is not a return question, so it has no return answer.
          returned: employeeOwns ? null : (ans[r.id] === 'y' ? true : ans[r.id] === 'n' ? false : null),
        };
      });
      formData.terminationEquipmentCheckout = _termEquipCheckout;
      // ONE total function in the product (PersonSections.outstandingEquipmentValue) —
      // replacement cost of what was answered "not returned". Never an amount owed
      // in wages; the on-screen and printed copy both say so.
      formData.terminationEquipmentUnreturnedValue = window.PersonSections
        .outstandingEquipmentValue(_hrTermUnreturnedItems(items, _termEmp), _termEmp);
      formData.terminationPropertyReturned = _termEquipCheckout
        .filter(c => c.returned === true)
        .map(c => `${c.item}${c.size ? ' (' + c.size + ')' : ''}${c.quantity > 1 ? ' ×' + c.quantity : ''}`);
    } else {
      formData.terminationPropertyReturned = propertyReturned;
    }
  }

  const warningLabel = WARNING_TYPES[warningType]?.label || 'Warning';
  const _wasEditing = !!HRState.editingDisciplinaryId;

  try {
    if (HRState.editingDisciplinaryId) {
      // Update existing form
      await updateDoc(doc(db, 'tenants', HRState.tenantId, 'hrForms', HRState.editingDisciplinaryId), formData);
      _clearDiscFormDirty();
      window.showToast?.(`${warningLabel} updated`, 'success');
      HRState.editingDisciplinaryId = null;
    } else {
      // Create new form
      await addDoc(collection(db, 'tenants', HRState.tenantId, 'hrForms'), formData);
      _clearDiscFormDirty();
      window.showToast?.(`${warningLabel} submitted`, 'success');
    }
    closeModal('disciplinary');
    // TASK-1578 Slice 4 — close the loop on the equipment ledger AFTER the notice
    // is safely saved. Without this the person card keeps saying "Issued" with a
    // dollar total forever, and the separator would have to do the same job twice.
    // Deliberately create-only: re-running it on every edit of an old notice would
    // re-stamp statuses long after the fact.
    // Its own try/catch on purpose: a throw in here must never fall into the
    // outer catch and tell the user "Error saving form" about a form that saved.
    if (_termEquipCheckout && !_wasEditing) {
      try {
        await _hrApplySeparationEquipmentStatuses(_termEquipCheckout, employee);
      } catch (eqErr) {
        console.error('[HR] separation checkout write-back failed:', eqErr);
        window.showToast?.("Notice saved. The equipment log wasn't updated — check the person's equipment list.", 'error');
      }
    }
  } catch (error) {
    console.error('[HR] Error saving disciplinary form:', error);
    window.showToast?.('Error saving form', 'error');
  }
}

// TASK-1578 Slice 4 — write the separation answers back onto the equipment ledger.
// Yes → 'returned' (which is what makes an item NOT chargeable), No → 'unreturned'.
// Never fails the notice: the signed record is the important artifact and it is
// already saved by the time this runs. Per-item try/catch so one refused write
// can't strand the rest, and an honest toast when any of them miss.
async function _hrApplySeparationEquipmentStatuses(checkout, employee) {
  if (!Array.isArray(checkout) || !checkout.length) return;
  // Client mirror of the safetyEquipment update rule. A view/upload-only HR
  // member can save the notice but cannot move the ledger — say so instead of
  // firing a row of permission denials.
  if (!_hrCanIssueEquipment()) {
    window.showToast?.('Notice saved. Equipment log not updated — your access level cannot change equipment records.', 'info');
    return;
  }
  const by = window.AppState?.userName || window.AppState?.user?.email || 'unknown';
  const now = new Date();
  let ok = 0, failed = 0;
  for (const row of checkout) {
    if (!row || !row.recordId || (row.returned !== true && row.returned !== false)) continue;
    const rec = (HRState.safetyEquipmentRecords || []).find(r => r.id === row.recordId);
    // Never regress a row someone else closed out while this form was open.
    if (rec && window.PersonSections.equipmentStatus(rec) === 'returned') continue;
    if (rec && rec.voidedAt) continue; // TASK-9719 — a voided row is frozen
    // 🔴 Gear the employee now OWNS must never be stamped 'unreturned'. That status
    // is what every downstream money surface reads as a debt, so writing it here
    // would recreate the charge the checkout just correctly declined to make —
    // permanently, in the ledger, after the form is gone.
    if (rec && employee && window.PersonSections.equipmentOwnershipTransferred(rec, employee)) continue;
    const patch = row.returned
      ? { status: 'returned', returnedAt: now, returnedBy: by, checkoutNotes: 'Collected at separation', returnedQty: Math.max(1, parseInt(rec && rec.quantity, 10) || 1), returnCondition: 'good' }
      : { status: 'unreturned', checkoutNotes: 'Not returned at separation' };
    try {
      await updateDoc(doc(db, 'tenants', HRState.tenantId, 'safetyEquipment', row.recordId), patch);
      ok++;
      window.logAudit?.('hr', row.returned ? 'EQUIPMENT_RETURNED' : 'EQUIPMENT_UNRETURNED', {
        personId: employee?.id || null,
        recordId: row.recordId,
        equipmentType: row.item || null,
        via: 'separation-checkout',
      });
    } catch (e) {
      failed++;
      console.error('[HR] separation checkout: could not update equipment record', row.recordId, e?.code || e?.message || e);
    }
  }
  if (failed) {
    window.showToast?.(`Notice saved. ${failed} equipment record${failed === 1 ? '' : 's'} could not be updated — check the person's equipment list.`, 'error');
  } else if (ok) {
    window.showToast?.(`Equipment log updated — ${ok} item${ok === 1 ? '' : 's'}`, 'success');
  }
  _hrRefreshIssuedEquipmentSection();
}

function viewDisciplinaryForm(id) {
  const form = HRState.hrForms.find(f => f.id === id);
  if (!form) return;
  HRState.selectedDisciplinaryId = id;

  const date = form.createdAt?.toDate?.() || new Date(form.createdAt);
  const warningType = form.warningType || 'verbal';
  const warningConfig = WARNING_TYPES[warningType] || WARNING_TYPES.verbal;

  // Build PIP-specific section if applicable
  let pipSection = '';
  if (warningType === 'pip') {
    // Support both new (pipPerformanceExpectations) and legacy (pipPerformanceAreas) formats
    const expectations = form.pipPerformanceExpectations ||
      (form.pipPerformanceAreas || []).join('\n• ');

    pipSection = `
      <div class="hr-form-field">
        <label>Performance Expectations</label>
        <div style="white-space: pre-wrap; color: var(--text-primary); background: var(--bg-elevated); padding: var(--spacing-md); border-radius: var(--radius-md); border: 1px solid var(--border-light);">
          ${escapeHtml(expectations) || 'None specified'}
        </div>
      </div>

      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: var(--spacing-md);">
        <div class="hr-form-field">
          <label>PIP Duration</label>
          <p>${form.pipDuration || 90} Days</p>
        </div>
        <div class="hr-form-field">
          <label>Start Date</label>
          <p>${form.pipStartDate || 'Not set'}</p>
        </div>
        <div class="hr-form-field">
          <label>End Date</label>
          <p>${form.pipEndDate || 'Not set'}</p>
        </div>
      </div>

      ${form.employeeComments ? `
        <div class="hr-form-field">
          <label>Employee Comments</label>
          <p>${escapeHtml(form.employeeComments)}</p>
        </div>
      ` : ''}
    `;
  }

  // TASK-999 — Termination-specific view section
  let termSection = '';
  if (warningType === 'termination') {
    const sepLabels = {
      involuntary_cause: 'Involuntary — For Cause',
      involuntary_layoff: 'Involuntary — Layoff / Position Eliminated',
      voluntary_resignation: 'Voluntary — Resignation'
    };
    const propLabels = { keys: 'Keys', fuelcard: 'Fuel Card', device: 'Device / Phone', ppe: 'PPE', other: 'Other' };
    const props = (form.terminationPropertyReturned || []).map(p => propLabels[p] || p).join(', ') || 'None recorded';
    const rehire = form.terminationEligibleForRehire === 'yes' ? 'Yes' : form.terminationEligibleForRehire === 'no' ? 'No' : 'Not set';
    termSection = `
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: var(--spacing-md);">
        <div class="hr-form-field">
          <label>Separation Type</label>
          <p>${escapeHtml(sepLabels[form.terminationSeparationType] || form.terminationSeparationType || 'Not set')}</p>
        </div>
        <div class="hr-form-field">
          <label>Last Day Worked</label>
          <p>${escapeHtml(form.terminationLastDayWorked || 'Not set')}</p>
        </div>
        <div class="hr-form-field">
          <label>Eligible for Rehire</label>
          <p>${rehire}</p>
        </div>
      </div>
      ${_hrTermCheckoutRows(form)
        // TASK-1578 Slice 4 — the item-level checkout SUPERSEDES the category
        // summary; showing both would present two answers to the same question.
        ? _hrTermCheckoutViewHtml(form)
        : `<div class="hr-form-field">
        <label>Company Property Returned</label>
        <p>${escapeHtml(props)}</p>
      </div>`}
      ${form.terminationFinalPayNote ? `
        <div class="hr-form-field">
          <label>Final Pay Handling</label>
          <p>${escapeHtml(form.terminationFinalPayNote)}</p>
        </div>
      ` : ''}
    `;
  }

  const content = document.getElementById('disciplinary-view-content');
  content.innerHTML = `
    <div class="hr-form-view">
      <div class="hr-form-view-header" style="border-left: 4px solid ${warningConfig.color}; padding-left: 12px;">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span class="material-icons" style="color: ${warningConfig.color};">${warningConfig.icon}</span>
          <h4 style="margin: 0;">${warningConfig.label}</h4>
        </div>
        <p class="hr-form-date">${formatDate(date)}</p>
      </div>

      <div class="hr-form-field">
        <label>Supervisor</label>
        <p>${escapeHtml(form.supervisorName)}</p>
      </div>

      <div class="hr-form-field">
        <label>Employee</label>
        <p>${escapeHtml(form.employeeName)}</p>
      </div>

      <div class="hr-form-field">
        <label>${warningType === 'termination' ? 'Reason for Separation' : 'Issue/Concern Discussed'}</label>
        <p>${escapeHtml(form.issue)}</p>
      </div>

      ${pipSection}
      ${termSection}

      ${warningType === 'termination' ? '' : `
      <div class="hr-form-field">
        <label>${warningType === 'pip' ? 'Additional Improvement Goals' : 'What Will Be Expected Going Forward'}</label>
        <p>${escapeHtml(form.expectations)}</p>
      </div>`}

      ${form.comments ? `
        <div class="hr-form-field">
          <label>Additional Comments</label>
          <p>${escapeHtml(form.comments)}</p>
        </div>
      ` : ''}

      <div style="border-top:2px solid var(--border-light);margin-top:var(--spacing-lg);padding-top:var(--spacing-md);">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-lg);">
          <div>
            <div style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Supervisor</div>
            ${form.supervisorSignature?.startsWith('data:image')
              ? `<div style="background:#fff;border-bottom:2px solid #1a1a1a;padding:4px 0;min-height:60px;display:flex;align-items:flex-end;">
                  <img src="${escapeHtml(form.supervisorSignature)}" style="max-width:100%;height:50px;display:block;" alt="Supervisor Signature">
                </div>
                <div style="font-size:0.75rem;color:var(--text-primary);margin-top:4px;font-weight:500;">${escapeHtml(form.supervisorName)}</div>
                <div style="font-size:0.65rem;color:var(--text-secondary);">Signed electronically${form.formDate ? ' • ' + escapeHtml(form.formDate) : ''}</div>`
              : `<div style="border-bottom:2px solid #ccc;min-height:60px;display:flex;align-items:flex-end;padding-bottom:4px;">
                  <span style="font-size:0.75rem;color:var(--text-secondary);font-style:italic;">Not signed</span>
                </div>
                <div style="font-size:0.75rem;color:var(--text-primary);margin-top:4px;">${escapeHtml(form.supervisorName)}</div>`}
          </div>
          <div>
            <div style="font-size:0.7rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;margin-bottom:6px;">Employee</div>
            ${form.employeeSignature?.startsWith('data:image')
              ? `<div style="background:#fff;border-bottom:2px solid #1a1a1a;padding:4px 0;min-height:60px;display:flex;align-items:flex-end;">
                  <img src="${escapeHtml(form.employeeSignature)}" style="max-width:100%;height:50px;display:block;" alt="Employee Signature">
                </div>
                <div style="font-size:0.75rem;color:var(--text-primary);margin-top:4px;font-weight:500;">${escapeHtml(form.employeeName)}</div>
                <div style="font-size:0.65rem;color:var(--text-secondary);">Signed electronically${form.formDate ? ' • ' + escapeHtml(form.formDate) : ''}</div>`
              : `<div style="border-bottom:2px solid #ccc;min-height:60px;display:flex;align-items:flex-end;padding-bottom:4px;">
                  <span style="font-size:0.75rem;color:var(--text-secondary);font-style:italic;">Not signed</span>
                </div>
                <div style="font-size:0.75rem;color:var(--text-primary);margin-top:4px;">${escapeHtml(form.employeeName)}</div>`}
          </div>
        </div>
      </div>

      <div style="text-align:center;margin-top:var(--spacing-md);padding-top:var(--spacing-sm);border-top:1px solid var(--border-light);">
        <span style="font-size:0.65rem;color:var(--text-secondary);">Filed by ${escapeHtml(form.createdBy)}${form.formDate ? ' on ' + escapeHtml(form.formDate) : ''}</span>
      </div>
    </div>
  `;

  let discModal = document.getElementById('modal-view-disciplinary');
  if (discModal) {
    // Move to document.body so it sits above the employee-detail-modal (which is also on body)
    if (discModal.parentElement !== document.body) {
      document.body.appendChild(discModal);
    }
    discModal.style.zIndex = '1010';
    discModal.classList.remove('hidden');
  }
}

function editDisciplinaryForm() {
  const form = HRState.hrForms.find(f => f.id === HRState.selectedDisciplinaryId);
  if (!form) return;

  // Close the view modal
  closeModal('view-disciplinary');

  // Open the create/edit modal
  document.getElementById('form-disciplinary')?.reset();
  updateDropdowns();

  // Store the editing ID so submit knows to update instead of create
  HRState.editingDisciplinaryId = form.id;

  // Set warning type
  selectWarningType(form.warningType || 'verbal');

  // Populate fields
  const supervisorEl = document.getElementById('disc-supervisor');
  if (supervisorEl) supervisorEl.value = form.supervisorId || '';

  const employeeEl = document.getElementById('disc-employee');
  if (employeeEl) employeeEl.value = form.employeeId || '';

  const dateEl = document.getElementById('disc-date');
  if (dateEl) dateEl.value = form.formDate || '';

  const issueEl = document.getElementById('disc-issue');
  if (issueEl) issueEl.value = form.issue || '';

  const expectationsEl = document.getElementById('disc-expectations');
  if (expectationsEl) expectationsEl.value = form.expectations || '';

  const commentsEl = document.getElementById('disc-comments');
  if (commentsEl) commentsEl.value = form.comments || '';

  // PIP fields
  if (form.warningType === 'pip') {
    const pipExpEl = document.getElementById('disc-pip-expectations');
    if (pipExpEl) pipExpEl.value = form.pipPerformanceExpectations || '';

    const pipDurEl = document.getElementById('disc-pip-duration');
    if (pipDurEl) pipDurEl.value = String(form.pipDuration || 90);

    const pipStartEl = document.getElementById('disc-pip-start');
    if (pipStartEl) pipStartEl.value = form.pipStartDate || '';

    const empCommentsEl = document.getElementById('disc-employee-comments');
    if (empCommentsEl) empCommentsEl.value = form.employeeComments || '';
  }

  // TASK-999 — Termination fields
  if (form.warningType === 'termination') {
    const sepEl = document.getElementById('disc-term-separation-type');
    if (sepEl) sepEl.value = form.terminationSeparationType || 'involuntary_cause';
    const lastDayEl = document.getElementById('disc-term-last-day');
    if (lastDayEl) lastDayEl.value = form.terminationLastDayWorked || '';
    const rehireEl = document.getElementById('disc-term-rehire');
    if (rehireEl) rehireEl.value = form.terminationEligibleForRehire || '';
    const finalPayEl = document.getElementById('disc-term-final-pay');
    if (finalPayEl) finalPayEl.value = form.terminationFinalPayNote || '';
    const propReturned = form.terminationPropertyReturned || [];
    ['keys', 'fuelcard', 'device', 'ppe', 'other'].forEach(k => {
      const cb = document.getElementById('disc-term-prop-' + k);
      if (cb) cb.checked = propReturned.includes(k);
    });
    // Refresh the read-only SPH link for the edited employee
    _hrRenderTerminationSphLink();
    // TASK-1578 Slice 4 — restore the saved Yes/No answers so an edit doesn't
    // open with every item blank (which would then re-save as "not recorded").
    // Items closed out since the notice was written are gone from the open list;
    // their stale keys are simply never read.
    HRState._termEquipAnswers = {};
    (form.terminationEquipmentCheckout || []).forEach(row => {
      if (!row || !row.recordId) return;
      if (row.returned === true) HRState._termEquipAnswers[row.recordId] = 'y';
      else if (row.returned === false) HRState._termEquipAnswers[row.recordId] = 'n';
    });
    _hrRenderTerminationPropertyChecklist();
  }

  // Update modal title to indicate editing
  const titleEl = document.getElementById('disc-modal-title');
  if (titleEl) titleEl.innerHTML = `<span class="material-icons">gavel</span> Edit Disciplinary Form`;

  _clearDiscFormDirty();
  const discEditModal = document.getElementById('modal-disciplinary');
  if (discEditModal) {
    // Move to document.body so it sits above the employee-detail-modal
    if (discEditModal.parentElement !== document.body) {
      document.body.appendChild(discEditModal);
    }
    discEditModal.style.zIndex = '1010';
    discEditModal.classList.remove('hidden');
  }

  const formEl = document.getElementById('form-disciplinary');
  if (formEl) {
    formEl.removeEventListener('input', _markDiscFormDirty);
    formEl.addEventListener('input', _markDiscFormDirty);
  }

  setTimeout(() => { initDiscSignatureCanvas(); initDiscSupervisorSigCanvas(); }, 100);
}

// ── TASK-4560 — a print window must not be snapshotted before its images decode ──
// The house pattern (document.write → close() → focus() → print()) hands Chrome the
// page for the print dialog BEFORE any <img> has decoded, so the FIRST print of a
// signed warning comes out with an empty signature box and the SECOND one — served
// from the in-memory image cache — comes out right.
//
// Confirmed from the two real PDFs Chris printed a minute apart (Ethan Young written
// warning): both files place the supervisor rule at content-stream y=441 while the
// date block's known-30px `.signature-line` sits at y=431. The print content space is
// 1:1 CSS pixels (net CTM .24 x 3.125 = 0.75 = 72/96), so that 10-unit delta is exactly
// the 40px data:image branch minus the 30px empty branch — i.e. the IMG branch was
// taken in BOTH prints, so the signature was in Firestore both times. Yet the first
// PDF contains ZERO image XObjects and the second contains one (562x200 + SMask).
// Laid out, never painted. It is a decode race, not a data problem.
//
// So wait on the REAL signal — decode()/load — never a magic delay. The timeout is a
// FALLBACK so a broken or slow image can never strand the print dialog; it is not the
// mechanism. Nothing here is tenant-specific: it waits on whatever <img> the document
// already contains, which for the equipment/property docs is the TENANT's own logo.
function _hrAwaitPrintImages(win, timeoutMs = 2000) {
  const settled = (async () => {
    const doc = win.document;
    const raf = win.requestAnimationFrame
      ? (cb) => win.requestAnimationFrame(cb)
      : (cb) => win.setTimeout(cb, 16);
    const decoded = (img) => (typeof img.decode === 'function'
      ? img.decode().catch(() => {})
      : Promise.resolve());

    await Promise.all(Array.from(doc.images || []).map((img) => {
      // `complete` alone only means the bytes arrived — decode() is what guarantees
      // the bitmap is ready to paint. A decode failure resolves; it must not block.
      if (img.complete) return img.naturalWidth > 0 ? decoded(img) : Promise.resolve();
      return new Promise((resolve) => {
        img.addEventListener('load', () => { decoded(img).then(resolve, resolve); }, { once: true });
        img.addEventListener('error', () => resolve(), { once: true });
      });
    }));

    try { await doc.fonts?.ready; } catch (_) {}

    // Two frames: one to flush layout with the decoded bitmaps in place, one to
    // guarantee that frame actually painted before the print dialog snapshots it.
    await new Promise((resolve) => raf(() => raf(resolve)));
  })();

  // Fallback timer on the OPENER, which is guaranteed alive even if the popup is closed.
  // Fallback timer on the OPENER, which is guaranteed alive even if the popup is closed.
  //
  // TASK-4560 (COD Code Reviewer + Independent, both flagged the same thing): the
  // fallback must not be SILENT. If it wins the race the user gets the ORIGINAL
  // bug back — an unpainted signature — and until now nothing anywhere said so.
  // A slow tenant-logo fetch is the realistic way that happens, and "it printed
  // blank again, sometimes" with no signal is exactly how this took two prints and
  // a PDF byte-dump to diagnose the first time. Name it in the console so the next
  // report is one grep, not one forensic session.
  let _timedOut = false;
  const fallback = new Promise((resolve) => window.setTimeout(() => { _timedOut = true; resolve(); }, timeoutMs));
  return Promise.race([settled.catch(() => {}), fallback]).then((r) => {
    if (_timedOut) {
      console.warn('[HR print] image decode did not finish within ' + timeoutMs + 'ms — printing anyway; an image may come out blank (TASK-4560)');
    }
    return r;
  });
}

// Snapshot-safe replacement for the trailing `focus(); print();` of the house pattern.
function _hrPrintWhenPainted(win) {
  return _hrAwaitPrintImages(win).then(() => {
    if (win.closed) return;
    try { win.focus(); win.print(); } catch (_) {}
  });
}

async function printDisciplinaryForm() {
  const viewedForm = HRState.hrForms.find(f => f.id === HRState.selectedDisciplinaryId);
  if (!viewedForm) return;

  const _esc = (s) => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  const date = viewedForm.createdAt?.toDate?.() || new Date(viewedForm.createdAt);
  const warningType = viewedForm.warningType || 'verbal';
  const warningConfig = WARNING_TYPES[warningType] || WARNING_TYPES.verbal;
  const companyName = _esc(window.AppState?.companyName || 'Company');

  // Build PIP section if applicable
  let pipHtml = '';
  if (warningType === 'pip') {
    const expectations = viewedForm.pipPerformanceExpectations ||
      (viewedForm.pipPerformanceAreas || []).join('\n• ');

    pipHtml = `
      <div class="section">
        <div class="section-label">Performance Expectations</div>
        <div class="section-content">${_esc(expectations || 'None specified').replace(/\n/g, '<br>')}</div>
      </div>
      <div class="info-row" style="margin-top:15px;">
        <div class="info-item"><span class="label">PIP Duration</span><span class="value">${viewedForm.pipDuration || 90} Days</span></div>
        <div class="info-item"><span class="label">Start Date</span><span class="value">${_esc(viewedForm.pipStartDate || 'Not set')}</span></div>
        <div class="info-item"><span class="label">End Date</span><span class="value">${_esc(viewedForm.pipEndDate || 'Not set')}</span></div>
      </div>
      ${viewedForm.employeeComments ? `
        <div class="section" style="margin-top:15px;">
          <div class="section-label">Employee Comments</div>
          <div class="section-content">${_esc(viewedForm.employeeComments).replace(/\n/g, '<br>')}</div>
        </div>
      ` : ''}
    `;
  }

  // TASK-999 — Termination print section
  let termHtml = '';
  if (warningType === 'termination') {
    const sepLabels = {
      involuntary_cause: 'Involuntary — For Cause',
      involuntary_layoff: 'Involuntary — Layoff / Position Eliminated',
      voluntary_resignation: 'Voluntary — Resignation'
    };
    const propLabels = { keys: 'Keys', fuelcard: 'Fuel Card', device: 'Device / Phone', ppe: 'PPE', other: 'Other' };
    const props = (viewedForm.terminationPropertyReturned || []).map(p => propLabels[p] || p).join(', ') || 'None recorded';
    const rehire = viewedForm.terminationEligibleForRehire === 'yes' ? 'Yes' : viewedForm.terminationEligibleForRehire === 'no' ? 'No' : 'Not set';
    termHtml = `
      <div class="info-row" style="margin-top:15px;">
        <div class="info-item"><span class="label">Separation Type</span><span class="value">${_esc(sepLabels[viewedForm.terminationSeparationType] || viewedForm.terminationSeparationType || 'Not set')}</span></div>
        <div class="info-item"><span class="label">Last Day Worked</span><span class="value">${_esc(viewedForm.terminationLastDayWorked || 'Not set')}</span></div>
        <div class="info-item"><span class="label">Eligible for Rehire</span><span class="value">${rehire}</span></div>
      </div>
      ${_hrTermCheckoutRows(viewedForm)
        // TASK-1578 Slice 4 — item-level checkout sheet supersedes the category line.
        ? _hrTermCheckoutPrintHtml(viewedForm)
        : `<div class="section">
        <div class="section-label">Company Property Returned</div>
        <div class="section-content">${_esc(props)}</div>
      </div>`}
      ${viewedForm.terminationFinalPayNote ? `
        <div class="section">
          <div class="section-label">Final Pay Handling</div>
          <div class="section-content">${_esc(viewedForm.terminationFinalPayNote).replace(/\n/g, '<br>')}</div>
        </div>
      ` : ''}
    `;
  }

  const printWindow = window.open('', '_blank');
  if (!printWindow) { window.showToast?.('Popup blocked. Allow popups for this site to print.', 'error'); return; }
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${warningConfig.label} - ${_esc(viewedForm.employeeName)}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 25px 35px; max-width: 750px; margin: 0 auto; color: #1a1a1a; font-size: 13px; }
        .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #333; padding-bottom: 10px; }
        .header h1 { margin: 0 0 3px 0; font-size: 18px; text-transform: uppercase; letter-spacing: 1px; }
        .header .company { font-size: 13px; color: #555; margin-bottom: 2px; }
        .header .date { font-size: 12px; color: #666; }
        .type-badge { display: inline-block; padding: 3px 12px; border-radius: 4px; font-weight: 600; font-size: 12px; margin-top: 5px;
          background: ${warningType === 'verbal' ? '#fef3c7' : warningType === 'written' ? '#fee2e2' : warningType === 'termination' ? '#fee2e2' : '#ede9fe'};
          color: ${warningType === 'verbal' ? '#92400e' : warningType === 'written' ? '#991b1b' : warningType === 'termination' ? '#991b1b' : '#5b21b6'}; }
        .info-row { display: flex; gap: 30px; margin: 12px 0; }
        .info-item { flex: 1; }
        .info-item .label { font-size: 10px; text-transform: uppercase; color: #666; letter-spacing: 0.5px; display: block; margin-bottom: 2px; }
        .info-item .value { font-size: 14px; font-weight: 500; }
        .section { margin: 14px 0; }
        .section-label { font-size: 11px; text-transform: uppercase; color: #666; letter-spacing: 0.5px; margin-bottom: 4px; font-weight: 600; border-bottom: 1px solid #e5e5e5; padding-bottom: 3px; }
        .section-content { font-size: 13px; line-height: 1.5; white-space: pre-wrap; padding: 4px 0; }
        .acknowledgment { margin: 18px 0; padding: 12px; background: #f9f9f9; border: 1px solid #e5e5e5; border-radius: 4px; }
        .acknowledgment .ack-label { font-size: 10px; text-transform: uppercase; color: #666; margin-bottom: 3px; }
        .acknowledgment .ack-text { font-size: 11px; color: #555; margin-bottom: 8px; line-height: 1.4; }
        .acknowledgment .ack-signature { font-size: 16px; font-style: italic; font-family: 'Georgia', serif; }
        .signature-section { margin-top: 30px; display: flex; gap: 30px; }
        .signature-block { flex: 1; text-align: center; }
        .signature-line { width: 100%; border-bottom: 1px solid #333; height: 30px; }
        .signature-name { font-size: 11px; color: #666; margin-top: 4px; }
        .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #e5e5e5; padding-top: 8px; }
        @media print { body { padding: 15px 25px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company">${companyName}</div>
        <h1>${warningConfig.label}</h1>
        <div class="date">${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
        <div class="type-badge">${warningConfig.label}</div>
      </div>

      <div class="info-row">
        <div class="info-item"><span class="label">Supervisor</span><span class="value">${_esc(viewedForm.supervisorName)}</span></div>
        <div class="info-item"><span class="label">Employee</span><span class="value">${_esc(viewedForm.employeeName)}</span></div>
      </div>

      <div class="section">
        <div class="section-label">${warningType === 'termination' ? 'Reason for Separation' : 'Issue / Concern Discussed'}</div>
        <div class="section-content">${_esc(viewedForm.issue || '').replace(/\n/g, '<br>')}</div>
      </div>

      ${pipHtml}
      ${termHtml}

      ${warningType === 'termination' ? '' : `
      <div class="section">
        <div class="section-label">${warningType === 'pip' ? 'Additional Improvement Goals' : 'What Will Be Expected Going Forward'}</div>
        <div class="section-content">${_esc(viewedForm.expectations || '').replace(/\n/g, '<br>')}</div>
      </div>`}

      ${viewedForm.comments ? `
        <div class="section">
          <div class="section-label">Additional Comments</div>
          <div class="section-content">${_esc(viewedForm.comments).replace(/\n/g, '<br>')}</div>
        </div>
      ` : ''}

      <div class="acknowledgment">
        <div class="ack-label">Employee Acknowledgment</div>
        <div class="ack-text">${warningConfig.acknowledgmentText}</div>
        ${viewedForm.employeeSignature?.startsWith('data:image')
          ? `<div style="margin-top:8px;"><img src="${_esc(viewedForm.employeeSignature)}" style="max-height:80px;" alt="Employee Signature"></div>`
          : `<div class="ack-signature">${_esc(viewedForm.employeeSignature)}</div>`}
      </div>

      <div class="signature-section">
        <div class="signature-block">
          ${viewedForm.supervisorSignature?.startsWith('data:image')
            ? `<div style="height:40px;display:flex;align-items:flex-end;justify-content:center;border-bottom:1px solid #333;"><img src="${_esc(viewedForm.supervisorSignature)}" style="max-height:36px;" alt="Supervisor Signature"></div>`
            : `<div class="signature-line"></div>`}
          <div class="signature-name">${_esc(viewedForm.supervisorName || 'Supervisor Signature')}</div>
        </div>
        <div class="signature-block">
          ${viewedForm.employeeSignature?.startsWith('data:image')
            ? `<div style="height:40px;display:flex;align-items:flex-end;justify-content:center;border-bottom:1px solid #333;"><img src="${_esc(viewedForm.employeeSignature)}" style="max-height:36px;" alt="Employee Signature"></div>`
            : `<div class="signature-line"></div>`}
          <div class="signature-name">${_esc(viewedForm.employeeName || 'Employee Signature')}</div>
        </div>
        <div class="signature-block">
          <div class="signature-line" style="display:flex;align-items:flex-end;justify-content:center;font-size:14px;">${_esc(viewedForm.formDate || '')}</div>
          <div class="signature-name">Date</div>
        </div>
      </div>

      <div class="footer">Created by ${_esc(viewedForm.supervisorName || 'Unknown')} on ${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} at ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}</div>
    </body>
    </html>
  `);
  printWindow.document.close();
  // TASK-4560 — the signature <img> is a canvas data: URL and still decodes async.
  // Do NOT put print() back on this line; it snapshots a blank signature box.
  await _hrPrintWhenPainted(printWindow);
}

// ============================================
// NEW HIRE CHECKLIST
// ============================================
function openNewChecklistModal() {
  document.getElementById('form-new-checklist')?.reset();
  updateDropdowns();
  document.getElementById('modal-new-checklist')?.classList.remove('hidden');
}

async function createNewChecklist(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;

  const employeeId = document.getElementById('checklist-employee').value;
  const employee = HRState.people.find(p => p.id === employeeId);

  if (!employee) {
    window.showToast?.('Please select an employee', 'error');
    return;
  }

  // Check if checklist already exists for this employee
  const existing = HRState.hrChecklists.find(c => c.employeeId === employeeId);
  if (existing) {
    window.showToast?.('A checklist already exists for this employee', 'error');
    return;
  }

  // Initialize all items as not completed
  const items = {};
  CHECKLIST_ITEMS.forEach(item => {
    items[item.id] = {
      completed: false,
      completedAt: null,
      completedBy: null,
      assignedTo: null,
      assignedToName: null
    };
  });

  const checklistData = {
    employeeId,
    employeeName: employee.name,
    items,
    completionPercent: 0,
    createdAt: Timestamp.now(),
    lastUpdated: Timestamp.now(),
    createdBy: window.AppState?.user?.email || 'unknown'
  };

  try {
    const docRef = await addDoc(collection(db, 'tenants', HRState.tenantId, 'hrChecklists'), checklistData);
    window.showToast?.('Checklist created', 'success');
    closeModal('new-checklist');

    // Open the newly created checklist
    setTimeout(() => viewChecklist(docRef.id), 300);
  } catch (error) {
    console.error('[HR] Error creating checklist:', error);
    window.showToast?.('Error creating checklist', 'error');
  }
}

// TASK-515 — surface the carrier's configured pre-employment drug-test
// collection site(s) as escaped sub-lines under the Drug Test checklist item.
// Source of truth is the tenant doc's partner-managed `compliance.drugTestSites`
// array (set in the Compliance Settings modal or by a partner). Read the live
// cached tenant doc (window.AppState.tenantData) — no extra Firestore read.
// Returns '' when no sites are configured so the item renders unchanged.
function renderDrugTestSitesSubLines() {
  const sites = window.AppState?.tenantData?.compliance?.drugTestSites;
  if (!Array.isArray(sites) || sites.length === 0) return '';
  const rows = sites
    .filter(s => s && (s.name || s.address))
    .map(s => {
      const name = escapeHtml(String(s.name || '').trim());
      const addr = escapeHtml(String(s.address || '').trim());
      const line = [name ? `<strong>${name}</strong>` : '', addr].filter(Boolean).join(' — ');
      if (!line) return '';
      return `<div style="display:flex;align-items:flex-start;gap:4px;">
        <span class="material-icons" style="font-size:13px;color:var(--text-secondary);margin-top:2px;">place</span>
        <span>${line}</span>
      </div>`;
    })
    .filter(Boolean)
    .join('');
  if (!rows) return '';
  return `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;line-height:1.4;">${rows}</div>`;
}

function viewChecklist(id) {
  const checklist = HRState.hrChecklists.find(c => c.id === id);
  if (!checklist) return;

  HRState.selectedChecklist = checklist;

  // TASK-498 (COD): reset the packet slot to the default button on every open —
  // createNewHirePacket() swaps this slot to a download link on success, and this
  // modal is reused (shown/hidden, not rebuilt), so without the reset a prior
  // checklist's stale packet link would persist when viewing a different employee.
  const _packetSlot = document.getElementById('checklist-packet-slot');
  if (_packetSlot) {
    _packetSlot.innerHTML = '<button type="button" class="btn btn-primary" id="checklist-packet-btn" onclick="window.HRModule.createNewHirePacket()"><span class="material-icons" style="font-size:16px;">print</span> Create Printable Packet</button>';
  }
  // TASK-625: reset the PEI slot the same way (same stale-link rationale).
  const _peiSlot = document.getElementById('checklist-pei-slot');
  if (_peiSlot) {
    _peiSlot.innerHTML = '<button type="button" class="btn btn-secondary" id="checklist-pei-btn" onclick="window.HRModule.createPeiForm()"><span class="material-icons" style="font-size:16px;">fact_check</span> Generate PEI Form</button>';
  }

  document.getElementById('checklist-title').textContent = `${checklist.employeeName} - New Hire Checklist`;

  const content = document.getElementById('checklist-content');

  // Group items by category
  const categories = {};
  CHECKLIST_ITEMS.forEach(item => {
    if (!categories[item.category]) {
      categories[item.category] = [];
    }
    categories[item.category].push(item);
  });

  const buildSupervisorOptions = (selectedId) => HRState.supervisors.map(p =>
    `<option value="${p.id}" ${p.id === selectedId ? 'selected' : ''}>${escapeHtml(p.name)}</option>`
  ).join('');

  const driverClockUrl = `${location.origin}/marketing/training-driver-clock.html`;

  content.innerHTML = `
    <div class="hr-checklist-view">
      <div class="hr-driver-training-panel" style="background:linear-gradient(135deg,rgba(59,130,246,0.08),rgba(6,182,212,0.08));border:1px solid var(--border-light);border-radius:var(--radius-lg);padding:14px 16px;margin-bottom:16px;display:flex;align-items:center;gap:14px;flex-wrap:wrap;">
        <div style="display:flex;align-items:center;gap:10px;flex:1;min-width:240px;">
          <span class="material-icons" style="color:var(--brand-blue);font-size:28px;">play_circle</span>
          <div>
            <div style="font-weight:700;color:var(--text-primary);font-size:0.9375rem;">Share with this driver</div>
            <div style="font-size:0.8125rem;color:var(--text-secondary);">Clocking In &amp; Out — 2 min training video</div>
          </div>
        </div>
        <div style="display:flex;gap:8px;">
          <a href="${driverClockUrl}" target="_blank" rel="noopener" class="btn btn-secondary" style="font-size:0.8125rem;padding:8px 12px;">
            <span class="material-icons" style="font-size:16px;">open_in_new</span> Preview
          </a>
          <button type="button" class="btn btn-primary" style="font-size:0.8125rem;padding:8px 12px;"
            onclick="navigator.clipboard.writeText('${driverClockUrl}');this.innerHTML='<span class=\\'material-icons\\' style=\\'font-size:16px;\\'>check</span> Copied';setTimeout(()=>{this.innerHTML='<span class=\\'material-icons\\' style=\\'font-size:16px;\\'>content_copy</span> Copy share link'},2000)">
            <span class="material-icons" style="font-size:16px;">content_copy</span> Copy share link
          </button>
        </div>
      </div>

      <div class="hr-checklist-progress">
        <div class="hr-progress-bar large">
          <div class="hr-progress-fill" style="width: ${checklist.completionPercent || 0}%"></div>
        </div>
        <div class="hr-progress-text">${checklist.completionPercent || 0}% complete</div>
      </div>

      ${Object.entries(categories).map(([category, items]) => `
        <div class="hr-checklist-category">
          <h4 class="hr-category-title">${escapeHtml(category)}${CHECKLIST_NEW_CATEGORIES.has(category) ? ` <span style="display:inline-flex;align-items:center;font-size:0.62rem;font-weight:700;letter-spacing:0.5px;text-transform:uppercase;color:#fff;background:var(--brand-blue,#3B82F6);padding:2px 7px;border-radius:999px;margin-left:6px;vertical-align:middle;" title="Newly added checklist items — drivers completed before this update may show a slightly lower percentage.">New</span>` : ''}</h4>
          <div class="hr-checklist-items">
            ${items.map(item => {
              const itemData = checklist.items?.[item.id] || { completed: false };
              const completedAt = itemData.completedAt?.toDate?.() || (itemData.completedAt ? new Date(itemData.completedAt) : null);

              return `
                <div class="hr-checklist-item ${itemData.completed ? 'completed' : ''}">
                  <label class="hr-checklist-item-left">
                    <input type="checkbox"
                      ${itemData.completed ? 'checked' : ''}
                      onchange="window.HRModule.toggleChecklistItem('${id}', '${item.id}', this.checked)">
                    <span class="hr-checklist-label">
                      ${item.link ? `<a href="${item.link}" target="_blank" onclick="event.stopPropagation()">${escapeHtml(item.label)}</a>` : escapeHtml(item.label)}
                      ${item.id === 'drug_test' ? renderDrugTestSitesSubLines() : ''}
                    </span>
                  </label>
                  <div class="hr-checklist-item-right">
                    <select class="hr-checklist-assign ${itemData.assignedTo ? 'assigned' : ''}"
                      onchange="window.HRModule.assignChecklistItem('${id}', '${item.id}', this.value)"
                      onclick="event.stopPropagation()">
                      <option value="">Assign to...</option>
                      ${buildSupervisorOptions(itemData.assignedTo)}
                    </select>
                    ${itemData.completed && completedAt ? `
                      <span class="hr-checklist-meta">
                        ${formatDate(completedAt)}
                      </span>
                    ` : ''}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `).join('')}
    </div>
  `;

  document.getElementById('modal-view-checklist')?.classList.remove('hidden');
}

async function toggleChecklistItem(checklistId, itemId, completed) {
  const checklist = HRState.hrChecklists.find(c => c.id === checklistId);
  if (!checklist) return;

  // Update the item (spread existing to preserve assignedTo/assignedToName)
  const items = { ...checklist.items };
  items[itemId] = {
    ...items[itemId],
    completed,
    completedAt: completed ? Timestamp.now() : null,
    completedBy: completed ? (window.AppState?.user?.email || 'unknown') : null
  };

  // Calculate completion percentage
  const totalItems = CHECKLIST_ITEMS.length;
  const completedItems = Object.values(items).filter(i => i.completed).length;
  const completionPercent = Math.round((completedItems / totalItems) * 100);

  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'hrChecklists', checklistId), {
      items,
      completionPercent,
      lastUpdated: Timestamp.now()
    });

    // Refresh the checklist view
    setTimeout(() => {
      const updated = HRState.hrChecklists.find(c => c.id === checklistId);
      if (updated) viewChecklist(checklistId);
    }, 100);
  } catch (error) {
    console.error('[HR] Error updating checklist:', error);
    window.showToast?.('Error updating checklist', 'error');
  }
}

async function assignChecklistItem(checklistId, itemId, personId) {
  const checklist = HRState.hrChecklists.find(c => c.id === checklistId);
  if (!checklist) return;

  const person = HRState.supervisors.find(p => p.id === personId);
  const assignedTo = personId || null;
  const assignedToName = person?.name || null;

  const items = { ...checklist.items };
  items[itemId] = {
    ...items[itemId],
    assignedTo,
    assignedToName
  };

  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'hrChecklists', checklistId), {
      items,
      lastUpdated: Timestamp.now()
    });

    // Refresh the checklist view
    setTimeout(() => {
      const updated = HRState.hrChecklists.find(c => c.id === checklistId);
      if (updated) viewChecklist(checklistId);
    }, 100);
  } catch (error) {
    console.error('[HR] Error assigning checklist item:', error);
    window.showToast?.('Error assigning task', 'error');
  }
}

// TASK-498 — Generate the printable New-Hire Packet PDF (cover-sheet checklist
// + the tenant's blank onboarding forms merged behind it). Server-side callable
// reads settings.hiringPacket + merges templates via the Admin SDK; we just
// invoke it and swap the button for a tappable Download link on success (no
// window.open after the await → avoids the iOS-Safari popup blocker).
async function createNewHirePacket() {
  const checklist = HRState.selectedChecklist;
  if (!checklist?.id) {
    window.showToast?.('Open a checklist first', 'error');
    return;
  }
  const slot = document.getElementById('checklist-packet-slot');
  const btn = document.getElementById('checklist-packet-btn');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Generating packet...';
  }
  try {
    const fn = httpsCallable(functions, 'generateNewHirePacketPdf');
    const result = await fn({ tenantId: HRState.tenantId, checklistId: checklist.id });
    const url = result?.data?.url;
    if (!url) throw new Error('No URL returned');

    if (slot) {
      // Replace the button with a tappable link — the user taps it, so the
      // browser treats the navigation as a user gesture (no popup block).
      slot.innerHTML = `
        <a href="${escapeHtml(url)}" target="_blank" rel="noopener" class="btn btn-primary" style="text-decoration:none;display:inline-flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:16px;">download</span> Download / Print Packet
        </a>`;
    }
    const skipped = result?.data?.skippedForms || 0;
    window.showToast?.(
      skipped > 0
        ? `Packet ready — ${skipped} form(s) listed as provided separately`
        : 'Packet ready — filed under Company Documents',
      'success'
    );
  } catch (error) {
    console.error('[HR] Error generating new-hire packet:', error);
    window.showToast?.(error?.message || 'Error generating packet', 'error');
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons" style="font-size:16px;">print</span> Create Printable Packet';
    }
  }
}

// TASK-625 — Generate the §391.23(d)(e) Safety Performance History Investigation
// (PEI) PDF for the checklist's employee. The callable produces ONE pre-filled
// form per previous employer (from the application history), merged into a single
// multi-page PDF; the answer half (incl. §40.25 D&A questions) is left blank for
// the previous employer. Generation is gated server-side on a signed §391.23
// release being on file — a missing release surfaces here as a failed-precondition
// error so the user is told to collect the signed release first. Mirror of
// createNewHirePacket() — invoke the callable, then swap the button for a tappable
// Download link (no window.open after the await → no iOS popup block).
async function createPeiForm() {
  const checklist = HRState.selectedChecklist;
  if (!checklist?.id) {
    window.showToast?.('Open a checklist first', 'error');
    return;
  }
  const personId = checklist.employeeId;
  if (!personId) {
    window.showToast?.('This checklist has no linked employee', 'error');
    return;
  }
  const slot = document.getElementById('checklist-pei-slot');
  const btn = document.getElementById('checklist-pei-btn');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Generating PEI...';
  }
  try {
    const fn = httpsCallable(functions, 'generatePeiPdf');
    const result = await fn({ tenantId: HRState.tenantId, personId });
    const url = result?.data?.url;
    if (!url) throw new Error('No URL returned');

    const formCount = result?.data?.formCount || 1;
    if (slot) {
      slot.innerHTML = `
        <a href="${escapeHtml(url)}" target="_blank" rel="noopener" class="btn btn-secondary" style="text-decoration:none;display:inline-flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:16px;">download</span> Download / Print PEI Form${formCount > 1 ? 's' : ''}
        </a>
        <div style="margin-top:6px;font-size:0.72rem;color:var(--text-secondary);display:flex;align-items:flex-start;gap:5px;">
          <span class="material-icons" style="font-size:14px;color:var(--warning,#d97706);">edit_note</span>
          <span><strong>The driver must sign each form</strong> before you mail it to the previous employer (§391.23(c)). The signature line is intentionally blank.</span>
        </div>`;
    }
    window.showToast?.(
      formCount > 1
        ? `${formCount} PEI forms ready — one per previous employer, answer section left blank`
        : 'PEI form ready — answer section left blank for the previous employer',
      'success'
    );
  } catch (error) {
    console.error('[HR] Error generating PEI form:', error);
    // A missing signed release returns failed-precondition; surface its message.
    window.showToast?.(error?.message || 'Error generating PEI form', 'error');
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons" style="font-size:16px;">fact_check</span> Generate PEI Form';
    }
  }
}

// TASK-853 — RESPOND to an inbound §391.23/§40.25 DOT-records request for the
// carrier's OWN driver. Generates a DRAFT "Employment / Safety-Performance
// Verification" (answer half pre-filled from the DD record) that the carrier
// reviews and SIGNS before release. A §40.25 confidential-release consent confirm
// is required up front (GUARDRAIL #5); the server writes the SPH_RESPONSE_GENERATED
// release-audit row and renders a DRAFT-watermarked PDF (GUARDRAIL #2).
// TASK-891 — opens the pre-generation modal (Slice 2 release upload + Slice 3
// §40.25 D&A toggle + Slice 1 optional e-signature). The actual callable runs in
// _submitSphResponse. The signature pad is the SHARED clock.js pad reused via
// window.ClockModule.* (Pitfall #1 — never cross-import clock.js with a ?v=).
let _sphResponseInFlight = false;
async function generateSphResponse(personId) {
  if (!personId) { window.showToast?.('No driver selected', 'error'); return; }
  const tenantId = HRState.tenantId;
  if (!tenantId) { window.showToast?.('No tenant context', 'error'); return; }
  if (_sphResponseInFlight) return;

  // Fetch the driver doc so we can tell whether a §40.25/§391.23 release is
  // ALREADY on file (don't force a re-upload every time — TASK-891 Slice 2).
  let person = {};
  try {
    const snap = await getDoc(doc(db, 'tenants', tenantId, 'people', personId));
    person = snap.exists() ? (snap.data() || {}) : {};
  } catch (_e) { person = {}; }

  const hasReleaseOnFile = !!person.sphConsentLatestPdfPath;
  const releasedDate = person.sphConsentReceivedDate ? escapeHtml(String(person.sphConsentReceivedDate)) : '';
  const driverName = escapeHtml(person.name || `${person.firstName || ''} ${person.lastName || ''}`.trim() || 'this driver');

  document.getElementById('sph-response-modal')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'sph-response-modal';
  overlay.className = 'modal';
  overlay.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:10000;';
  overlay.innerHTML = `
    <div class="modal-content" style="max-width:560px;max-height:92vh;overflow:auto;">
      <div class="modal-header">
        <h3><span class="material-icons">assignment_turned_in</span> Employment/Safety Verification</h3>
        <button class="btn-icon" onclick="document.getElementById('sph-response-modal').remove()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="display:flex;flex-direction:column;gap:16px;">
        <div style="font-size:0.85rem;color:var(--text-secondary);line-height:1.45;">Generate a pre-filled DRAFT to respond to an inbound §391.23/§40.25 records request for <strong>${driverName}</strong>. Review &amp; sign it before releasing it to the requester.</div>

        <!-- TASK-853 clarity — users kept opening this looking for the §382.601(d) D&A policy receipt -->
        <div style="font-size:0.78rem;color:#3730A3;background:#EEF2FF;border:1px solid #C7D2FE;border-radius:8px;padding:9px 11px;line-height:1.5;">
          <strong>Use this only when another carrier has asked you for this driver's records</strong> — normally because the driver applied there, and §391.23 makes them verify the last 3 years of employment and safety history. The release below is the one <em>that carrier</em> had the driver sign; it comes to you attached to their request. Nothing is sent anywhere — you get a PDF to review, sign, and return to them.
          <div style="margin-top:6px;">Looking for the driver's signed <strong>company Drug &amp; Alcohol policy receipt</strong> (§382.601(d))? That is a different form — it's the <strong>Policy Receipt</strong> section on this driver's Compliance tab, with a <strong>Send Link</strong> button that texts them a one-tap signature.</div>
        </div>

        <!-- Slice 2 — signed driver release upload -->
        <div style="border:1px solid var(--border-color,#E5E7EB);border-radius:8px;padding:12px;">
          <div style="font-weight:700;font-size:0.85rem;display:flex;align-items:center;gap:6px;margin-bottom:6px;"><span class="material-icons" style="font-size:18px;color:#0F766E;">verified_user</span> Signed driver release (49 CFR §40.25 / §391.23)</div>
          ${hasReleaseOnFile
            ? `<div style="font-size:0.8rem;color:#065F46;background:#ECFDF5;border:1px solid #6EE7B7;border-radius:6px;padding:8px 10px;">A signed release is on file${releasedDate ? ` (received ${releasedDate})` : ''}. Re-upload below only to replace it (optional).</div>`
            : `<div style="font-size:0.8rem;color:#92400E;background:#FFFBEB;border:1px solid #FCD34D;border-radius:6px;padding:8px 10px;">Required — upload the requesting employer's signed driver release before generating.</div>`}
          <input type="file" id="sph-consent-file" accept="application/pdf,image/png,image/jpeg,image/webp,image/heic" style="margin-top:8px;font-size:0.8rem;width:100%;">
        </div>

        <!-- Slice 3 — §40.25 D&A section toggle -->
        <div style="border:1px solid var(--border-color,#E5E7EB);border-radius:8px;padding:12px;">
          <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;">
            <input type="checkbox" id="sph-include-da" checked style="margin-top:3px;">
            <span style="font-size:0.85rem;line-height:1.45;"><strong>Include DOT drug &amp; alcohol history (49 CFR §40.25)</strong><br><span style="color:var(--text-secondary);font-size:0.8rem;">Include only if the driver's release authorizes the §40.25 drug &amp; alcohol inquiry. When unchecked, the form notes that §40.25 history is furnished separately upon a §40.25 release. (Do not omit on the basis that it is &ldquo;not required for FMCSA drivers&rdquo; — that is incorrect.)</span></span>
          </label>
        </div>

        <!-- Slice 1 — optional e-signature -->
        <div style="border:1px solid var(--border-color,#E5E7EB);border-radius:8px;padding:12px;">
          <div style="font-weight:700;font-size:0.85rem;display:flex;align-items:center;gap:6px;margin-bottom:4px;"><span class="material-icons" style="font-size:18px;color:var(--brand-blue,#3B82F6);">draw</span> Electronically sign (optional)</div>
          <div style="font-size:0.78rem;color:var(--text-secondary);margin-bottom:8px;">Review the pre-filled answers first. Sign here (with your printed name) and the PDF comes out final, ready to send. Leave it blank and you get a DRAFT with a blank line to sign by hand.</div>
          <div id="sig-wrap-sphCert" style="border:1px dashed #9CA3AF;border-radius:6px;background:#fff;">
            <canvas id="sig-canvas-sphCert" style="width:100%;height:120px;display:block;touch-action:none;"></canvas>
          </div>
          <button type="button" class="btn btn-secondary btn-sm" style="margin-top:6px;" onclick="window.ClockModule&&window.ClockModule.clearSignature('sphCert')">Clear</button>
          <div style="display:flex;gap:8px;margin-top:10px;flex-wrap:wrap;">
            <div style="flex:1;min-width:180px;"><label style="font-size:0.72rem;font-weight:700;color:var(--text-secondary);">Print name</label><input type="text" id="sph-signer-name" maxlength="120" placeholder="Authorized representative" style="width:100%;padding:6px 8px;font-size:0.85rem;"></div>
            <div style="flex:1;min-width:140px;"><label style="font-size:0.72rem;font-weight:700;color:var(--text-secondary);">Title</label><input type="text" id="sph-signer-title" maxlength="80" placeholder="e.g. Safety Manager" style="width:100%;padding:6px 8px;font-size:0.85rem;"></div>
          </div>
        </div>

        <div style="display:flex;flex-wrap:wrap;justify-content:flex-end;gap:8px;">
          <button class="btn btn-secondary" onclick="document.getElementById('sph-response-modal').remove()">Cancel</button>
          <button class="btn btn-primary" id="sph-generate-btn" onclick="window.HRModule._submitSphResponse('${escJsAttr(personId)}')"><span class="material-icons" style="font-size:18px;">picture_as_pdf</span> Generate PDF</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  // Init the shared signature pad once the canvas has layout dimensions.
  setTimeout(() => { try { window.ClockModule?.initSignaturePad?.('sphCert'); } catch (_e) {} }, 60);
}

async function _submitSphResponse(personId) {
  const tenantId = HRState.tenantId;
  if (!tenantId || !personId) { window.showToast?.('No tenant context', 'error'); return; }
  if (_sphResponseInFlight) return;
  const btn = document.getElementById('sph-generate-btn');

  const fileInput = document.getElementById('sph-consent-file');
  const file = fileInput && fileInput.files && fileInput.files[0];
  const includeDa = !!document.getElementById('sph-include-da')?.checked;

  // Optional e-signature (shared clock.js pad). 'N/A' is not meaningful here → treat as none.
  let signatureDataUrl = null;
  try {
    const sig = window.ClockModule?.getSignatureDataUrl?.('sphCert');
    if (sig && sig !== 'N/A' && sig.startsWith('data:image/png')) signatureDataUrl = sig;
  } catch (_e) {}
  const signerName = (document.getElementById('sph-signer-name')?.value || '').trim();
  const signerTitle = (document.getElementById('sph-signer-title')?.value || '').trim();
  if (signatureDataUrl && !signerName) { window.showToast?.('Enter the printed signer name to e-sign', 'error'); return; }

  _sphResponseInFlight = true;
  // Chris 2026-10-07: "there is no feedback while this report is building and it takes a while".
  // The button turns into a spinner with the current step, and a status line under it counts
  // the seconds, so a 20-40 s build never looks frozen. Both clear in `finally`.
  const idleHtml = btn ? btn.innerHTML : '';
  const t0 = Date.now();
  let step = file ? 'Uploading the signed release…' : 'Building the verification PDF…';
  let status = document.getElementById('sph-generate-status');
  if (!status && btn && btn.parentElement) {
    status = document.createElement('div');
    status.id = 'sph-generate-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.style.cssText = 'flex-basis:100%;text-align:right;font-size:0.8rem;color:var(--text-secondary);margin-top:6px;';
    btn.parentElement.appendChild(status);
  }
  const paint = () => {
    const secs = Math.round((Date.now() - t0) / 1000);
    if (btn) btn.innerHTML = `<span class="btn-spinner"></span> ${escapeHtml(step)}`;
    if (status) status.textContent = `${step} ${secs}s — this usually takes under a minute. Keep this window open.`;
  };
  if (btn) { btn.disabled = true; btn.style.opacity = '0.85'; }
  paint();
  const ticker = setInterval(paint, 1000);
  try {
    // Slice 2 — upload the signed release (if provided) to the person-scoped
    // sphConsent Storage path; pass the PATH (server validates + derives the URL).
    let consentDocPath = null;
    if (file) {
      const type = (file.type || '').toLowerCase();
      if (type === 'image/svg+xml') throw new Error('SVG files are not allowed.');
      // TASK-9402 — an iPhone HEIC used to land here as `file.type || 'application/pdf'`:
      // HEIC bytes under a PDF label. Convert first; store under the bytes' own type.
      let prepped;
      try {
        prepped = await prepareUploadFile(file, { fallbackType: 'application/pdf' });
      } catch (convErr) {
        throw new Error(photoErrorMessage(convErr));
      }
      if (prepped.file.size > 10 * 1024 * 1024) throw new Error('File is too big — max 10 MB.');
      const safeName = (prepped.file.name || file.name || 'release').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 128);
      consentDocPath = `tenants/${tenantId}/people/${personId}/sphConsent/${Date.now()}_${safeName}`;
      await uploadBytes(ref(storage, consentDocPath), prepped.file, { contentType: prepped.contentType });
      step = 'Building the verification PDF…';
      paint();
    }

    const fn = httpsCallable(functions, 'generateSphResponsePdf');
    const result = await fn({
      tenantId, personId,
      consentDocPath,
      includeDaSection: includeDa,
      signatureDataUrl,
      signerName, signerTitle,
    });
    const url = result?.data?.url;
    if (!url) throw new Error('No URL returned');
    // TASK-9655 — the server says whether the signature made it onto the PDF.
    const signed = result?.data?.signed === true;
    document.getElementById('sph-response-modal')?.remove();
    _showSphResponseResult(url, signed);
    window.showToast?.(signed ? 'Signed verification ready to send' : 'Verification DRAFT ready — review and sign before sending', 'success');
  } catch (error) {
    console.error('[HR] Error generating SPH response:', error);
    const msg = (error && error.message) || 'Error generating verification';
    window.showToast?.(msg.replace(/^CONSENT_REQUIRED:\s*/, ''), 'error');
  } finally {
    clearInterval(ticker);
    _sphResponseInFlight = false;
    if (btn) { btn.disabled = false; btn.style.opacity = '1'; btn.innerHTML = idleHtml; }
    document.getElementById('sph-generate-status')?.remove();
  }
}

// Lightweight result overlay with a tappable Download link (no window.open after
// the await — avoids the iOS-Safari popup block, same pattern as createPeiForm).
function _showSphResponseResult(url, signed = false) {
  document.getElementById('sph-response-result-modal')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'sph-response-result-modal';
  overlay.className = 'modal';
  overlay.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:10000;';
  overlay.innerHTML = `
    <div class="modal-content" style="max-width:480px;">
      <div class="modal-header">
        <h3><span class="material-icons">assignment_turned_in</span> Employment/Safety Verification</h3>
        <button class="btn-icon" onclick="document.getElementById('sph-response-result-modal').remove()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="display:flex;flex-direction:column;gap:12px;">
        ${signed
          ? `<div style="display:flex;align-items:flex-start;gap:8px;background:#ECFDF5;border:1px solid #6EE7B7;border-radius:8px;padding:10px 12px;">
          <span class="material-icons" style="font-size:18px;color:#047857;">task_alt</span>
          <span style="font-size:0.82rem;color:#065F46;line-height:1.4;"><strong>Signed and ready to send.</strong> Download it and send it to the requester. Nothing was sent automatically.</span>
        </div>`
          : `<div style="display:flex;align-items:flex-start;gap:8px;background:#FFFBEB;border:1px solid #F59E0B;border-radius:8px;padding:10px 12px;">
          <span class="material-icons" style="font-size:18px;color:#B45309;">edit_note</span>
          <span style="font-size:0.82rem;color:#92400E;line-height:1.4;"><strong>This is a DRAFT.</strong> Review every pre-filled answer against your records, then <strong>sign it</strong> before releasing it to the requester. It is watermarked DRAFT and was not sent anywhere.</span>
        </div>`}
        <a href="${escapeHtml(url)}" target="_blank" rel="noopener" class="btn btn-primary" style="text-decoration:none;display:inline-flex;align-items:center;justify-content:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;">download</span> Download / Print Verification${signed ? '' : ' (DRAFT)'}
        </a>
      </div>
    </div>`;
  document.body.appendChild(overlay);
}

// TASK-9938 (bundle headroom) — the deferred leg of every lazy-HR shim below (the four
// _loadHrXxx blocks). A failed chunk load has already told the user (the loader's
// toast) and is retried on the next tap, so it is marked handled here: onclick callers
// ignore the returned promise and must not leave an unhandled rejection. Any other
// error a moved function throws on that first call is logged. Awaiting callers still
// see the promise reject exactly as before.
function _hrLazyCall(load, name, args) {
  const p = load().then((m) => m[name](...args));
  p.catch((e) => { if (!(e && e._hrLazyLoadFailed)) console.error('[HR] ' + name + ' failed:', e); });
  return p;
}

// =============================================================================
// =============================================================================
// TASK-9938 (bundle headroom) — the performance review form, view / edit / print,
// delete / void and the review schedule editor live in the LAZY views/hrReviews.js
// (perf budget appColdLoadGzip). Bodies moved verbatim; each shim keeps the name
// window.HRModule calls, loads the module on first use and, once loaded, calls
// straight through synchronously. Deps are handed over, never imported (pitfall #1).
// =============================================================================
let _hrRevMod = null;
let _hrRevLoad = null;
function _hrRevDeps() {
  return {
    COMPENSATION_TIERS, HRState, HR_REVIEW_DELETE_LOCKED_MSG, HR_REVIEW_LOCKED_MSG, RATING_RUBRIC,
    Timestamp, _appendPayRateHistory, _hrComp, _hrIsOwnerOrAdmin, _hrReviewIsLocked,
    _hrReviewIsVoided, _hrReviewSchedule, addDoc, bumpReviewFormGen, calculateAvgRating,
    closeModal, collection, db, deleteDoc, doc, escapeHtml, formatDate, formatReviewPeriod,
    functions, getPositionTitle, httpsCallable, openEmployeeDetail, renderReviewsList, renderStars,
    serverTimestamp, updateDoc, updateDropdowns
  };
}
function _loadHrRev() {
  if (_hrRevMod) return Promise.resolve(_hrRevMod);
  if (!_hrRevLoad) {
    _hrRevLoad = __ddChunkWrap(import('./hrReviews.js?v=9999972')).then((m) => { m._bind(_hrRevDeps()); _hrRevMod = m; return m; }).catch((e) => {
      _hrRevLoad = null; // the next click tries again
      console.error('[HR] hrReviews load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _hrLazyLoadFailed: true });
    });
  }
  return _hrRevLoad;
}
function openReviewModal(...a) { return _hrRevMod ? _hrRevMod.openReviewModal(...a) : _hrLazyCall(_loadHrRev, 'openReviewModal', a); }
function onEmployeeSelect(...a) { return _hrRevMod ? _hrRevMod.onEmployeeSelect(...a) : _hrLazyCall(_loadHrRev, 'onEmployeeSelect', a); }
function onPositionChange(...a) { return _hrRevMod ? _hrRevMod.onPositionChange(...a) : _hrLazyCall(_loadHrRev, 'onPositionChange', a); }
function calculateScores(...a) { return _hrRevMod ? _hrRevMod.calculateScores(...a) : _hrLazyCall(_loadHrRev, 'calculateScores', a); }
function calculateCompensation(...a) { return _hrRevMod ? _hrRevMod.calculateCompensation(...a) : _hrLazyCall(_loadHrRev, 'calculateCompensation', a); }
// The form's onsubmit passes the event: stop the native submit NOW, before the
// module lands (submitReview calls preventDefault itself once it runs).
function submitReview(...a) {
  if (_hrRevMod) return _hrRevMod.submitReview(...a);
  if (a[0] && typeof a[0].preventDefault === 'function') a[0].preventDefault();
  return _hrLazyCall(_loadHrRev, 'submitReview', a);
}
function finalizeReview(...a) { return _hrRevMod ? _hrRevMod.finalizeReview(...a) : _hrLazyCall(_loadHrRev, 'finalizeReview', a); }
function viewReview(...a) { return _hrRevMod ? _hrRevMod.viewReview(...a) : _hrLazyCall(_loadHrRev, 'viewReview', a); }
function editReview(...a) { return _hrRevMod ? _hrRevMod.editReview(...a) : _hrLazyCall(_loadHrRev, 'editReview', a); }
function printReview(...a) { return _hrRevMod ? _hrRevMod.printReview(...a) : _hrLazyCall(_loadHrRev, 'printReview', a); }
function printCurrentReview(...a) { return _hrRevMod ? _hrRevMod.printCurrentReview(...a) : _hrLazyCall(_loadHrRev, 'printCurrentReview', a); }
function deleteReview(...a) { return _hrRevMod ? _hrRevMod.deleteReview(...a) : _hrLazyCall(_loadHrRev, 'deleteReview', a); }
function voidReview(...a) { return _hrRevMod ? _hrRevMod.voidReview(...a) : _hrLazyCall(_loadHrRev, 'voidReview', a); }
function voidReviewFromModal(...a) { return _hrRevMod ? _hrRevMod.voidReviewFromModal(...a) : _hrLazyCall(_loadHrRev, 'voidReviewFromModal', a); }
function savePersonReviewReminder(...a) { return _hrRevMod ? _hrRevMod.savePersonReviewReminder(...a) : _hrLazyCall(_loadHrRev, 'savePersonReviewReminder', a); }
function renderReviewScheduleEditor(...a) { return _hrRevMod ? _hrRevMod.renderReviewScheduleEditor(...a) : _hrLazyCall(_loadHrRev, 'renderReviewScheduleEditor', a); }
function addReviewScheduleRow(...a) { return _hrRevMod ? _hrRevMod.addReviewScheduleRow(...a) : _hrLazyCall(_loadHrRev, 'addReviewScheduleRow', a); }
function removeReviewScheduleRow(...a) { return _hrRevMod ? _hrRevMod.removeReviewScheduleRow(...a) : _hrLazyCall(_loadHrRev, 'removeReviewScheduleRow', a); }
function saveReviewSchedule(...a) { return _hrRevMod ? _hrRevMod.saveReviewSchedule(...a) : _hrLazyCall(_loadHrRev, 'saveReviewSchedule', a); }

// TASK-398: invalidate any in-flight review-form prefill (outer + nested setTimeout)
// and cancel pending timers. Call at the top of every review-form open AND on close.
function bumpReviewFormGen() {
  HRState.reviewFormGen++;
  if (HRState._reviewPrefillTimers.length) {
    HRState._reviewPrefillTimers.forEach(t => clearTimeout(t));
    HRState._reviewPrefillTimers = [];
  }
}

// TASK-4760 — the single source of truth for "is this review a final record?".
// Deliberately IDENTICAL to firestore.rules' isPostSubmitRecord()
// (`resource.data.get('submitted', false) == true || 'submittedAt' in
// resource.data`) and to the correctSubmittedRecord callable's own precondition
// (functions/src/index.js — `original.submitted === true || original.submittedAt`).
// If those three ever drift, the UI starts offering an action the rules refuse.
// COD Code Reviewer (2026-09-08): the rule tests KEY PRESENCE
// (`'submittedAt' in resource.data`), not truthiness. A doc carrying
// `submittedAt: null` is FROZEN server-side, and a `!!review.submittedAt`
// predicate would call it editable — the UI would offer an Edit button whose
// save can only be refused. Mirror the rule exactly: presence, not value.
function _hrReviewIsLocked(review) {
  if (!review) return false;
  if (review.submitted === true) return true;
  return Object.prototype.hasOwnProperty.call(review, 'submittedAt');
}

// TASK-4760 — the message every locked-review dead-end uses. One string so the
// user is told the same thing whether we refuse client-side or the rules do.
const HR_REVIEW_LOCKED_MSG = 'This review was submitted and locked — it is a final employment record and can no longer be edited here. Start a new review for this employee, or have an owner/admin file a correction.';

// TASK-4776 — "is this review voided?". Mirrors firestore.rules'
// `resource.data.get('voided', false) == true`. Unlike the LOCK predicate this
// one is a VALUE test, not a key-presence test, because the rule reads the
// value with a default — keep the two shaped differently on purpose.
function _hrReviewIsVoided(review) {
  return !!review && review.voided === true;
}

// TASK-4776 — what a user is told when they try to DELETE a locked review.
// A rules denial arrives as a bare `permission-denied`; without this the owner
// would get "Error deleting review" and no route forward (the TASK-428 /
// TASK-4760 failure mode). Names the cause AND the way out.
const HR_REVIEW_DELETE_LOCKED_MSG = 'This review was submitted and locked — it is a final employment record and can no longer be deleted. An owner or admin can VOID it instead: the review stays on file, marked void with who did it and why.';

// TASK-4776 — the void badge's tooltip. Returns ATTRIBUTE-escaped text (a void
// reason is free text a user typed and lands inside a title="…"), so callers
// interpolate it directly. escapeHtml in this file delegates to the shared
// quote-safe implementation, so it is a correct fallback for window.escAttr.
function _hrReviewVoidTooltip(review) {
  if (!review) return '';
  const who = review.voidedBy ? ' by ' + String(review.voidedBy) : '';
  const whenSrc = review.voidedAt?.toDate?.() || (review.voidedAt ? new Date(review.voidedAt) : null);
  const when = whenSrc && !Number.isNaN(whenSrc.getTime()) ? ' on ' + formatDate(whenSrc) : '';
  const why = review.voidReason ? ' — ' + String(review.voidReason) : '';
  const esc = window.escAttr || escapeHtml;
  return esc('Voided' + who + when + why);
}

// TASK-9456 - the tenant's review schedule (settings.reviewSchedule). Display + editor
// only: every date that is actually SCHEDULED is computed server-side from the same
// setting (functions/src/reviewSchedule.js), never from this copy.
function _hrReviewSchedule() {
  const raw = window.AppState?.settings?.reviewSchedule;
  const ok = Array.isArray(raw) && raw.length > 0 && raw.every(s => s && Number.isInteger(s.days) && s.days > 0 && s.label);
  return ok ? raw : [{ label: '30-day', days: 30 }];
}

function _hrFirstReviewHint() {
  const first = _hrReviewSchedule()[0];
  return `${first.label} review, ${first.days} days after hire`;
}

// "90-day review" for the step the person is on - but only when the scheduled date
// really is hire + that step's days (a hand-picked date reads plain "Review").
function _hrNextReviewLabel(emp, reviewDate) {
  const sched = _hrReviewSchedule();
  const idx = Number.isInteger(emp.reviewStepIndex) && emp.reviewStepIndex >= 0 && emp.reviewStepIndex < sched.length ? emp.reviewStepIndex : 0;
  const hire = emp.hireDate?.toDate?.() || (emp.hireDate ? new Date(emp.hireDate) : null);
  if (hire && !isNaN(hire.getTime()) && reviewDate) {
    const diff = (reviewDate.getTime() - hire.getTime()) / 86400000;
    if (Math.abs(diff - sched[idx].days) <= 1) return `${sched[idx].label} review`;
    if (sched[idx].repeat) return `${sched[idx].label} review`;
  }
  return 'Review';
}

function formatReviewPeriod(period) {
  const periods = {
    '30-day': '30-Day Review',
    '60-day': '60-Day Review',
    '90-day': '90-Day Review',
    '6-month': '6-Month Review',
    'annual': 'Annual Review',
    'performance-improvement': 'Performance Improvement',
    'promotion': 'Promotion Review'
  };
  return periods[period] || period;
}

// ============================================
// MODAL HELPERS
// ============================================
function closeModal(type) {
  // Try standard naming (modal-{type}) and alternate naming ({type}-modal)
  const modal = document.getElementById(`modal-${type}`) || document.getElementById(`${type}-modal`);
  modal?.classList.add('hidden');
  // TASK-1119 — flush any list render the modal-open guard deferred (see
  // _pendingModalRenders). Runs AFTER the hidden class lands so the guard passes.
  const _flushMap = {
    'disciplinary': ['forms'],
    'view-review': ['reviews'],
    'new-checklist': ['checklists'],
    'view-checklist': ['checklists'],
    'meeting-details': ['safetySignIn'],
    'hr-jsa-detail': ['jsa'],
  };
  for (const k of (_flushMap[type] || [])) _flushPendingModalRender(k);
  if (type === 'review') {
    // TASK-398: closing the review form invalidates any pending edit prefill.
    bumpReviewFormGen();
  }
  if (type === 'disciplinary') {
    _clearDiscFormDirty();
  }
  if (type === 'view-checklist') {
    HRState.selectedChecklist = null;
  }
  if (type === 'view-review') {
    HRState.selectedReview = null;
  }
  if (type === 'isnet-training') {
    // TASK-723 — clear any matrix-save context on cancel/close so a stale context
    // can't redirect a later non-matrix training save.
    _matrixSaveContext = null;
  }
}

// ============================================
// EMPLOYEE NOTES — TASK-346 Slice 3 + TASK-350 calendar-feel (2026-05-22)
// Structured feed (subcollection: tenants/{tid}/people/{pid}/hrNotes/{noteId}).
// Each doc: { body, createdAt, authorUid, authorName, observedAt, ... }.
// observedAt is the user-pickable date the observation actually happened;
// createdAt is the server clock at write time (immutable, audit anchor).
// Feed sorts by observedAt DESC (calendar feel); legacy docs without
// observedAt fall back to createdAt at render time.
// ============================================
// In-memory cache of notes per personId so the feed survives re-renders
// without re-fetching. Cleared on openEmployeeDetail of a different person.
const _hrNotesCache = new Map(); // personId -> [{ id, body, createdAt, observedAt, authorUid, authorName }]
// Set of currently-expanded inline edit cards keyed by `${personId}:${noteId}`.
// Survives feed re-render so the editor stays open after an optimistic update.
const _hrNotesEditing = new Set();

// Date helpers. YYYY-MM-DD anchored at noon local to avoid DST midnight ambiguity.
function _todayYmd() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}
function _ymdFromDate(d) {
  if (!d || isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}
function _minYmd() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 5);
  return _ymdFromDate(d);
}
function _maxYmd() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 5);
  return _ymdFromDate(d);
}
// Convert a YYYY-MM-DD picker value into a Firestore Timestamp anchored at
// noon local. Noon-anchor avoids DST midnight ambiguity and keeps the date
// display stable across timezones (a Friday note doesn't drift to Thursday
// in PT vs ET).
function _ymdToTimestamp(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const dt = new Date(ymd + 'T12:00:00');
  if (isNaN(dt.getTime())) return null;
  return Timestamp.fromDate(dt);
}
// Format a Timestamp-or-Date as "MMM D, YYYY" (e.g. "May 22, 2026").
function _fmtObservedAt(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : (ts instanceof Date ? ts : new Date(ts));
  if (!d || isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
// Pull observedAt-or-createdAt millis for sort + comparison.
function _noteSortMs(n) {
  if (n.observedAt?.toMillis) return n.observedAt.toMillis();
  if (n.createdAt?.toMillis) return n.createdAt.toMillis();
  return 0;
}

// TASK-464 — collapsible employee-notes feed. PER-PERSON localStorage key (NOT
// per-tenant like company docs — notes are N-person; a per-tenant key would
// collapse every employee's feed once you viewed one heavy profile). Seeded ONCE
// per modal open in loadEmployeeNotesFeed (NOT in _renderEmployeeNotesFeed, which
// re-runs on every add/edit/delete — re-seeding there would slam the feed shut
// mid-interaction, the TASK-445 company-docs bug). Composer + print stay visible;
// only #hr-employee-notes-feed collapses.
function _empNotesStorageKey(personId) {
  const tid = HRState.tenantId || 'default';
  return `dd.hr.empNotes.collapsed.${tid}.${personId}`;
}
function _readEmpNotesCollapsed(personId) {
  try {
    const v = localStorage.getItem(_empNotesStorageKey(personId));
    if (v === '1') return true;
    if (v === '0') return false;
  } catch (_) {}
  return null; // unset → fall back to count-based default
}
function _writeEmpNotesCollapsed(personId, collapsed) {
  try { localStorage.setItem(_empNotesStorageKey(personId), collapsed ? '1' : '0'); } catch (_) {}
}
// seed=true: apply collapse state (persisted choice wins, else default >5 collapsed).
// seed=false: update the count chip only, preserve current expand/collapse.
function _syncEmpNotesCollapseUI(count, personId, seed) {
  const chip = document.getElementById('hr-emp-notes-count-chip');
  const body = document.getElementById('hr-employee-notes-feed');
  const chev = document.getElementById('chevron-emp-notes');
  if (chip) {
    if (count > 0) { chip.textContent = String(count); chip.style.display = 'inline-block'; }
    else { chip.style.display = 'none'; }
  }
  if (!body || !chev) return;
  if (seed) {
    const persisted = _readEmpNotesCollapsed(personId);
    const collapsed = persisted === null ? (count > 5) : persisted;
    body.style.display = collapsed ? 'none' : 'flex';
    chev.textContent = collapsed ? 'expand_more' : 'expand_less';
  }
}
function toggleEmployeeNotesSection(personId) {
  const body = document.getElementById('hr-employee-notes-feed');
  const chev = document.getElementById('chevron-emp-notes');
  if (!body) return;
  const willCollapse = body.style.display !== 'none';
  body.style.display = willCollapse ? 'none' : 'flex';
  if (chev) chev.textContent = willCollapse ? 'expand_more' : 'expand_less';
  _writeEmpNotesCollapsed(personId, willCollapse);
}

async function loadEmployeeNotesFeed(employeeId) {
  const feedEl = document.getElementById('hr-employee-notes-feed');
  if (!feedEl) return;
  try {
    const notesRef = collection(db, 'tenants', HRState.tenantId, 'people', employeeId, 'hrNotes');
    // Fetch unsorted; sort client-side by observedAt DESC with createdAt
    // fallback. Doing the sort in JS keeps the migration tolerant (legacy
    // docs without observedAt aren't dropped — they fall back to createdAt)
    // and avoids needing a Firestore composite index.
    const snap = await getDocs(notesRef);
    const notes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    notes.sort((a, b) => _noteSortMs(b) - _noteSortMs(a));
    _hrNotesCache.set(employeeId, notes);
    _hrNotesEditing.clear();
    _renderEmployeeNotesFeed(employeeId);
    // Seed collapse state ONCE per modal open (after the feed is populated).
    _syncEmpNotesCollapseUI(notes.length, employeeId, true);
  } catch (err) {
    console.error('[HR] Load notes feed failed:', err);
    if (feedEl) feedEl.innerHTML = '<div class="empty-state-small" style="color:var(--text-secondary);font-size:0.85rem;">Could not load notes.</div>';
  }
}

function _renderEmployeeNotesFeed(employeeId) {
  const feedEl = document.getElementById('hr-employee-notes-feed');
  if (!feedEl) return;
  const notes = _hrNotesCache.get(employeeId) || [];
  // TASK-464 — keep the "Notes (N)" chip live on add/edit/delete WITHOUT
  // re-seeding the collapse (seed=false preserves the user's expand/collapse).
  _syncEmpNotesCollapseUI(notes.length, employeeId, false);
  if (notes.length === 0) {
    feedEl.innerHTML = '<div class="empty-state-small" style="color:var(--text-secondary);font-size:0.85rem;padding:var(--spacing-sm);">No notes yet. Add one above to start tracking observations + conversations.</div>';
    return;
  }
  // Re-sort defensively in case an optimistic insert pushed stale order.
  notes.sort((a, b) => _noteSortMs(b) - _noteSortMs(a));
  const currentUid = window.AppState?.user?.uid || null;
  // 2026-05-22 dogfood follow-up: owner/admin (and founder/VIP fallback) can
  // delete any note — server rule already allows it. Edit stays author-only
  // (mirrors callable updateHrNote, which enforces authorUid == auth.uid).
  const _role = window.AppState?.memberAccess?.role || null;
  const _isOwnerOrAdmin = _role === 'owner' || _role === 'admin';
  // 2026-05-22 QA sweep F4: only apply founder/VIP fallback when there is NO
  // memberAccess doc (legacy single-login owner). On founder/VIP tenants like
  // trippin-trucking, every staff member would otherwise show the cross-author
  // delete button + hit a permissions error (firestore.rules:798 requires
  // isTenantAdmin). Staff members on founder tenants must NOT see cross-author
  // delete; owner/admin still get it via _isOwnerOrAdmin above.
  // TASK-932 — was `_isOwnerOrAdmin || (founder/VIP tenant with no member doc)`:
  // a TENANT test, so a legacy single-login owner on any other tenant could not
  // moderate notes, and HR module-holders (whom the hrNotes delete rule allows:
  // author || isTenantAdmin || hasModule('hr') && !isViewUploadOnly) never saw
  // the button. hrCanManageModule() is exactly that rule's shape: effective admin
  // (owner/admin, or no member doc = legacy single-login owner) OR hr module.
  const _canDeleteAny = _isOwnerOrAdmin ||
    (typeof window.hrCanManageModule === 'function' && window.hrCanManageModule() === true);
  feedEl.innerHTML = notes.map(n => {
    const authorName = n.authorName || 'Unknown';
    const body = n.body || '';
    const isOwn = currentUid && n.authorUid === currentUid;
    const canDeleteThis = isOwn || _canDeleteAny;
    const observedTs = n.observedAt || n.createdAt || null;
    const observedDate = observedTs?.toDate ? observedTs.toDate() : null;
    const observedLabel = _fmtObservedAt(observedTs);
    // Backdate hint: if observedAt and createdAt differ by more than 1 day,
    // surface a muted "(logged MMM D)" so readers know it was recorded later.
    let backdateHint = '';
    if (n.observedAt && n.createdAt) {
      const oMs = n.observedAt?.toMillis?.() || 0;
      const cMs = n.createdAt?.toMillis?.() || 0;
      if (oMs && cMs && Math.abs(oMs - cMs) > 24 * 60 * 60 * 1000) {
        backdateHint = '<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:2px;">(logged ' + escapeHtml(_fmtObservedAt(n.createdAt)) + ')</div>';
      }
    }
    const editKey = employeeId + ':' + n.id;
    const isEditing = _hrNotesEditing.has(editKey);
    if (isEditing && isOwn) {
      const ymd = observedDate ? _ymdFromDate(observedDate) : _todayYmd();
      return ''
        + '<div class="hr-note-entry hr-note-editing" data-note-id="' + escapeHtml(n.id) + '" style="padding:var(--spacing-sm);background:var(--bg-secondary,#f8fafc);border:1px solid var(--brand-blue,#3B82F6);border-radius:var(--radius-md,6px);">'
        + '<textarea id="hr-note-edit-body-' + escapeHtml(n.id) + '" rows="3" maxlength="4000" style="width:100%;resize:vertical;font-size:0.9rem;padding:var(--spacing-sm);border:1px solid var(--border-light);border-radius:var(--radius-sm);font-family:inherit;background:var(--bg-primary);color:var(--text-primary);">' + escapeHtml(body) + '</textarea>'
        + '<div style="display:flex;align-items:center;justify-content:space-between;gap:var(--spacing-xs);margin-top:var(--spacing-xs);flex-wrap:wrap;">'
        +   '<label for="hr-note-edit-date-' + escapeHtml(n.id) + '" style="display:flex;align-items:center;gap:4px;font-size:0.78rem;color:var(--text-secondary);margin:0;">'
        +     '<span class="material-icons" style="font-size:14px;">event</span> Observed date'
        +     '<input type="date" class="control-auto" id="hr-note-edit-date-' + escapeHtml(n.id) + '" value="' + escapeHtml(ymd) + '" max="' + _maxYmd() + '" min="' + _minYmd() + '" style="font-size:0.78rem;padding:2px 6px;border:1px solid var(--border-light);border-radius:var(--radius-sm);background:var(--bg-primary);color:var(--text-primary);" />'
        +   '</label>'
        +   '<div style="display:flex;gap:6px;">'
        +     '<button class="btn btn-sm btn-secondary" onclick="window.HRModule.cancelEditEmployeeNote(\'' + escJsAttr(employeeId) + '\',\'' + escJsAttr(n.id) + '\')" style="padding:4px 10px;">Cancel</button>'
        +     '<button class="btn btn-sm btn-primary" id="btn-save-emp-note-edit-' + escapeHtml(n.id) + '" onclick="window.HRModule.saveEmployeeNoteEdit(\'' + escJsAttr(employeeId) + '\',\'' + escJsAttr(n.id) + '\')" style="padding:4px 12px;">'
        +       '<span class="material-icons" style="font-size:16px;">save</span> Save'
        +     '</button>'
        +   '</div>'
        + '</div>'
        + '</div>';
    }
    // Read-only placard card: body left + observed-date right + author/delete/edit row underneath.
    const editBtn = isOwn
      ? '<button class="btn btn-sm btn-text" onclick="window.HRModule.editEmployeeNote(\'' + escJsAttr(employeeId) + '\',\'' + escJsAttr(n.id) + '\')" title="Edit note" style="padding:2px 6px;color:var(--text-secondary);background:none;border:none;cursor:pointer;">'
        + '<span class="material-icons" style="font-size:16px;">edit</span>'
      + '</button>'
      : '';
    const delBtn = canDeleteThis
      ? '<button class="btn btn-sm btn-text" onclick="window.HRModule.deleteEmployeeNote(\'' + escJsAttr(employeeId) + '\',\'' + escJsAttr(n.id) + '\')" title="Delete note" style="padding:2px 6px;color:var(--text-secondary);background:none;border:none;cursor:pointer;">'
        + '<span class="material-icons" style="font-size:16px;">delete</span>'
      + '</button>'
      : '';
    return ''
      + '<div class="hr-note-entry" data-note-id="' + escapeHtml(n.id) + '" style="padding:var(--spacing-sm);background:var(--bg-secondary,#f8fafc);border:1px solid var(--border-light,#e5e7eb);border-radius:var(--radius-md,6px);">'
      +   '<div style="display:flex;align-items:flex-start;justify-content:space-between;gap:var(--spacing-sm);">'
      +     '<div style="white-space:pre-wrap;font-size:0.9rem;line-height:1.5;color:var(--text-primary);flex:1 1 auto;min-width:0;">' + escapeHtml(body) + '</div>'
      +     '<div style="font-size:0.78rem;color:var(--text-secondary);text-align:right;white-space:nowrap;flex:0 0 auto;">'
      +       escapeHtml(observedLabel)
      +       backdateHint
      +     '</div>'
      +   '</div>'
      +   _renderHrNoteAttachment(n)
      +   '<div style="display:flex;align-items:center;justify-content:space-between;gap:var(--spacing-xs);margin-top:6px;padding-top:6px;border-top:1px dashed var(--border-light);">'
      +     '<div style="font-size:0.75rem;color:var(--text-secondary);">'
      +       '<strong style="color:var(--text-primary);">' + escapeHtml(authorName) + '</strong>'
      +     '</div>'
      +     '<div style="display:flex;gap:2px;">' + editBtn + delBtn + '</div>'
      +   '</div>'
      + '</div>';
  }).join('');
}

async function addEmployeeNote(employeeId) {
  const composer = document.getElementById('hr-employee-note-composer');
  const dateInput = document.getElementById('hr-employee-note-observed-date');
  const btn = document.getElementById('btn-add-emp-note');
  if (!composer) return;
  const body = (composer.value || '').trim().slice(0, 4000);
  if (!body) { window.showToast?.('Type a note first', 'info'); composer.focus(); return; }
  const uid = window.AppState?.user?.uid || null;
  if (!uid) { window.showToast?.('Sign in to add notes', 'error'); return; }

  const observedYmd = dateInput?.value || _todayYmd();
  const observedAt = _ymdToTimestamp(observedYmd) || Timestamp.now();

  const authorName = (
    window.AppState?.user?.displayName ||
    window.AppState?.user?.email ||
    'Unknown'
  );

  // TASK-684 — optional image/PDF attachment. Upload to Storage FIRST; if
  // the upload fails we keep the note text (toast + abort) and NEVER clear
  // the composer.
  const attachInput = document.getElementById('hr-emp-note-attach-input');
  const attachFile = attachInput?.files?.[0] || null;
  let attachment = null;
  if (attachFile) {
    const v = _validateHrNoteAttachment(attachFile);
    if (!v.ok) { window.showToast?.(v.msg, 'error'); return; }
  }

  try {
    if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }
    if (attachFile) {
      try {
        attachment = await _uploadHrNoteAttachment(attachFile, employeeId);
      } catch (upErr) {
        console.error('[HR] Note attachment upload failed:', upErr);
        window.showToast?.('Attachment upload failed — note not saved, try again', 'error');
        return; // keep composer text intact
      }
    }
    const notesRef = collection(db, 'tenants', HRState.tenantId, 'people', employeeId, 'hrNotes');
    const noteDoc = {
      body,
      createdAt: Timestamp.now(),
      observedAt,
      authorUid: uid,
      authorName,
      ...(attachment ? { attachment } : {}),
    };
    const newRef = await addDoc(notesRef, noteDoc);

    // Optimistic local insert. Resort by observedAt DESC.
    const cached = _hrNotesCache.get(employeeId) || [];
    cached.push({ id: newRef.id, ...noteDoc });
    cached.sort((a, b) => _noteSortMs(b) - _noteSortMs(a));
    _hrNotesCache.set(employeeId, cached);
    _renderEmployeeNotesFeed(employeeId);

    composer.value = '';
    if (dateInput) dateInput.value = _todayYmd();
    _clearHrNoteAttach('detail');
    window.showToast?.('Saved', 'success');
    window.logAudit?.('hr', 'PERSON_HR_NOTE_ADD', { personId: employeeId, noteId: newRef.id, bodyLength: body.length, observedAtMs: observedAt.toMillis(), hasAttachment: !!attachment });
  } catch (err) {
    console.error('[HR] Add note failed:', err);
    window.showToast?.('Could not save note', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">add</span> Add note'; }
  }
}

function editEmployeeNote(employeeId, noteId) {
  if (!employeeId || !noteId) return;
  _hrNotesEditing.add(employeeId + ':' + noteId);
  _renderEmployeeNotesFeed(employeeId);
  // Focus the textarea after render.
  setTimeout(() => {
    const ta = document.getElementById('hr-note-edit-body-' + noteId);
    if (ta) {
      ta.focus();
      // Move cursor to end.
      const len = ta.value.length;
      ta.setSelectionRange(len, len);
    }
  }, 30);
}

function cancelEditEmployeeNote(employeeId, noteId) {
  if (!employeeId || !noteId) return;
  _hrNotesEditing.delete(employeeId + ':' + noteId);
  _renderEmployeeNotesFeed(employeeId);
}

async function saveEmployeeNoteEdit(employeeId, noteId) {
  if (!employeeId || !noteId) return;
  const bodyEl = document.getElementById('hr-note-edit-body-' + noteId);
  const dateEl = document.getElementById('hr-note-edit-date-' + noteId);
  const saveBtn = document.getElementById('btn-save-emp-note-edit-' + noteId);
  if (!bodyEl || !dateEl) return;
  const body = (bodyEl.value || '').trim().slice(0, 4000);
  if (!body) { window.showToast?.('Note body cannot be empty', 'info'); bodyEl.focus(); return; }
  const observedYmd = dateEl.value || _todayYmd();
  const observedTs = _ymdToTimestamp(observedYmd);
  if (!observedTs) { window.showToast?.('Pick a valid observed date', 'error'); dateEl.focus(); return; }
  try {
    if (saveBtn) { saveBtn.disabled = true; saveBtn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }
    const updateFn = httpsCallable(functions, 'updateHrNote');
    const observedAtMs = observedTs.toMillis();
    const resp = await updateFn({
      tenantId: HRState.tenantId,
      personId: employeeId,
      noteId,
      body,
      observedAtMs,
    });
    if (!resp?.data?.ok) {
      window.showToast?.('Could not save edit', 'error');
      return;
    }
    // Optimistic cache update — overwrite body + observedAt, preserve the rest.
    const cached = _hrNotesCache.get(employeeId) || [];
    const idx = cached.findIndex(n => n.id === noteId);
    if (idx >= 0) {
      cached[idx] = {
        ...cached[idx],
        body,
        observedAt: observedTs,
      };
      cached.sort((a, b) => _noteSortMs(b) - _noteSortMs(a));
      _hrNotesCache.set(employeeId, cached);
    }
    _hrNotesEditing.delete(employeeId + ':' + noteId);
    _renderEmployeeNotesFeed(employeeId);
    window.showToast?.('Saved', 'success');
  } catch (err) {
    console.error('[HR] Edit note failed:', err);
    const msg = err?.message || 'Could not save edit';
    window.showToast?.(msg, 'error');
  } finally {
    if (saveBtn) { saveBtn.disabled = false; saveBtn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save'; }
  }
}

async function deleteEmployeeNote(employeeId, noteId) {
  if (!employeeId || !noteId) return;
  // 2026-05-22 dogfood follow-up: when admin/owner deletes someone else's
  // note, show a richer confirm (author + observed date) to prevent fat-finger
  // destruction of another supervisor's observation.
  const cachedNotes = _hrNotesCache.get(employeeId) || [];
  const note = cachedNotes.find(n => n.id === noteId);
  const currentUid = window.AppState?.user?.uid || null;
  const isOwn = note && currentUid && note.authorUid === currentUid;
  let title = 'Delete this note?';
  let detail = 'This can\'t be undone.';
  if (note && !isOwn) {
    const authorName = note.authorName || 'Unknown';
    const observedLabel = _fmtObservedAt(note.observedAt || note.createdAt);
    title = 'Delete ' + authorName + '\'s note?';
    if (observedLabel) detail = 'Observed ' + observedLabel + '. This can\'t be undone.';
  }
  const confirmed = await (window.showConfirm?.(title, detail) ?? confirm(title + ' ' + detail));
  if (!confirmed) return;
  // TASK-684 — read the attachment storage path from cache BEFORE deleting
  // the doc so we can best-effort clean up the Storage object afterward.
  const attachPath = note?.attachment?.path || null;
  try {
    await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'people', employeeId, 'hrNotes', noteId));
    if (attachPath) {
      try { await deleteObject(ref(storage, attachPath)); } catch (e) { /* file may already be gone */ }
    }
    const cached = _hrNotesCache.get(employeeId) || [];
    _hrNotesCache.set(employeeId, cached.filter(n => n.id !== noteId));
    _hrNotesEditing.delete(employeeId + ':' + noteId);
    _renderEmployeeNotesFeed(employeeId);
    window.showToast?.('Note deleted', 'success');
    // Stamp adminOverride flag when admin/owner deletes someone else's note,
    // so the audit log carries the cross-author signal.
    window.logAudit?.('hr', 'PERSON_HR_NOTE_DELETE', {
      personId: employeeId,
      noteId,
      adminOverride: note && !isOwn ? true : false,
      noteAuthorUid: note?.authorUid || null,
    });
  } catch (err) {
    console.error('[HR] Delete note failed:', err);
    window.showToast?.('Could not delete', 'error');
  }
}

// Back-compat shim: old call sites referenced saveEmployeeNotes(). Now an
// alias that no-ops (the new composer auto-saves on Add Note click). Kept
// to avoid breakage if any pre-Slice-3 cached HTML calls it.
async function saveEmployeeNotes(employeeId) {
  return addEmployeeNote(employeeId);
}

function printEmployeeNotes(employeeId) {
  const emp = HRState.people.find(p => p.id === employeeId);
  if (!emp) return;
  const notes = _hrNotesCache.get(employeeId) || [];
  if (notes.length === 0) { window.showToast?.('No notes to print', 'info'); return; }

  const printWindow = window.open('', '_blank');
  if (!printWindow) { window.showToast?.('Popup blocked. Allow popups for this site to print.', 'error'); return; }
  // Sort by observedAt DESC for print so the printed sheet matches the
  // calendar-feel feed order (TASK-350).
  const sorted = notes.slice().sort((a, b) => _noteSortMs(b) - _noteSortMs(a));
  const entries = sorted.map(n => {
    const observedTs = n.observedAt || n.createdAt || null;
    const when = _fmtObservedAt(observedTs);
    // Backdate hint on print too -- HR may need to see when a note was
    // physically recorded vs when the observation happened.
    let loggedHint = '';
    if (n.observedAt && n.createdAt) {
      const oMs = n.observedAt?.toMillis?.() || 0;
      const cMs = n.createdAt?.toMillis?.() || 0;
      if (oMs && cMs && Math.abs(oMs - cMs) > 24 * 60 * 60 * 1000) {
        loggedHint = ' <span style="color:#999;font-size:11px;">(logged ' + escapeHtml(_fmtObservedAt(n.createdAt)) + ')</span>';
      }
    }
    return '<div class="entry"><div class="meta"><strong>' + escapeHtml(n.authorName || 'Unknown') + '</strong> &middot; ' + escapeHtml(when) + loggedHint + '</div><div class="body">' + escapeHtml(n.body || '') + '</div></div>';
  }).join('');

  printWindow.document.write(`<!DOCTYPE html><html><head><title>Notes - ${escapeHtml(emp.name)}</title>
    <style>body{font-family:Arial,sans-serif;padding:40px;max-width:700px;margin:0 auto;}
    h1{font-size:20px;margin-bottom:4px;} .subtitle{color:#666;font-size:14px;margin-bottom:24px;}
    .entry{margin-bottom:18px;padding-bottom:12px;border-bottom:1px solid #eee;}
    .entry:last-child{border-bottom:none;}
    .meta{font-size:12px;color:#666;margin-bottom:6px;}
    .body{white-space:pre-wrap;font-size:14px;line-height:1.6;}</style>
    </head><body><h1>${escapeHtml(emp.name)}</h1>
    <div class="subtitle">HR Notes &mdash; Printed ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</div>
    ${entries}</body></html>`);
  printWindow.document.close();
  printWindow.print();
}

// ============================================
// UTILITIES
// ============================================
// TASK-9941 F S3 — calculateDriverPinHR removed: the HR directory no longer
// prints the name + phone code.

// TASK-862 — delegates to shared window.escapeHtml (sharedFormat.js); escapes the
// identical set (&<>"', '->&#39;). Hoisted wrapper, window lookup deferred to call time.
function escapeHtml(str) {
  return window.escapeHtml(str);
}

// TASK-9234 — the HR directory avatar is the SECOND reader of a person's photo
// (roster.js:renderPeopleList is the first). It used to <img src> the persisted
// `profilePhotoUrl`, which is a Firebase download URL — a bearer credential that
// rides the `people` broadcast into every session. Both readers now go through
// the one resolver in roster.js, which resolves the storage PATH via the SDK at
// display time. Reached on window.* at call time, never a cross-?v= import
// (Pitfall #1). If the roster module were somehow absent we render initials —
// fail closed, never fall back to painting a bearer URL.
function _hrPersonAvatarInner(emp) {
  const initials = emp?.name?.charAt(0)?.toUpperCase() || '?';
  const build = window.RosterModule?.personAvatarInnerHtml;
  return typeof build === 'function'
    ? build(emp, 'hr-employee-avatar-photo', initials)
    : initials;
}

// TASK-4838 — delegating wrapper for the shared JS-STRING-IN-AN-ATTRIBUTE escaper
// (sharedFormat.js). Use it for `onclick="window.Mod.fn('${…}')"`, never escapeHtml:
// the HTML parser decodes `&#39;` back to an apostrophe BEFORE the handler is
// compiled as JavaScript, so escapeHtml/escAttr give that position no protection
// at all. Hoisted wrapper, window lookup deferred to call time (Pitfall #1).
function escJsAttr(str) {
  return window.escJsAttr(str);
}

// TASK-1460 — delegating wrapper for the shared person-name capitalizer
// (sharedFormat.js). window.* at CALL time, never a cross-?v= import
// (Pitfall #1). Falls back to the raw value if the helper is somehow
// absent, so a missing helper can never blank out a name on save.
function capPersonName(str) {
  return window.capitalizePersonName ? window.capitalizePersonName(str) : str;
}

// TASK-743 — render-time href guard (defense-in-depth). New MVR PDF URLs are now
// server-derived from a validated Storage path, but LEGACY person docs may still
// carry a previously-injected raw URL in mvrLatestPdfUrl / mvrPullHistory[].pdfUrl
// (the pre-fix footgun). escapeHtml alone does NOT neutralize a `javascript:` /
// `data:` scheme inside an href, so gate every rendered href through this: only
// http(s) URLs pass; anything else collapses to '#'. Returns an attribute-safe,
// escaped string.
function _safeHttpUrl(u) {
  try {
    const parsed = new URL(String(u));
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return '#';
    return escapeHtml(parsed.toString());
  } catch { return '#'; }
}

// ============================================================
// TASK-684 — attach an image or PDF to an HR employee note.
// Shared by BOTH note composers (HR-detail Notes feed + standalone
// "Add Note" modal). Adversary-hardened: we store ONLY the storage
// PATH on the note doc (never a client-supplied url) and derive the
// real Firebase download URL at RENDER time, so a forged
// attachment.url can never become a clickable href / XSS vector.
// SVG is explicitly excluded from the accept allowlist (image/svg+xml
// can carry <script> and would execute on the Storage domain when
// opened via a link).
// ============================================================

// Explicit allowlist — NOT image/* (which the shared isImage() matches
// against image/svg+xml). Used for both the <input accept> attribute
// and client-side file.type validation.
const HR_NOTE_ATTACH_ACCEPT = 'image/png,image/jpeg,image/heic,image/webp,application/pdf';
const HR_NOTE_ATTACH_TYPES = ['image/png', 'image/jpeg', 'image/heic', 'image/webp', 'application/pdf'];

// Validate a chosen file against the allowlist + 10MB storage-rule limit.
// Returns { ok:true } or { ok:false, msg }.
function _validateHrNoteAttachment(file) {
  if (!file) return { ok: false, msg: 'No file selected' };
  // 10MB — matches storage.rules:550 (request.resource.size < 10MB).
  if (file.size > 10 * 1024 * 1024) {
    return { ok: false, msg: 'File too large. Max 10MB.' };
  }
  const type = (file.type || '').toLowerCase();
  if (!HR_NOTE_ATTACH_TYPES.includes(type)) {
    // Explicitly reject SVG and anything else not on the allowlist.
    return { ok: false, msg: 'Only PNG, JPG, HEIC, WEBP, or PDF allowed.' };
  }
  return { ok: true };
}

// Upload the chosen file to Storage and return the immutable attachment
// object stored on the note doc. We persist ONLY the storage path (no url).
async function _uploadHrNoteAttachment(file, personId) {
  const tenantId = HRState.tenantId;
  const epoch = Date.now();
  // safeName carries the original extension (e.g. "BAT_photo.png"); the
  // existing HR-doc upload pattern (hr.js:7651) does the same — no extra
  // ext suffix needed.
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `hr/${tenantId}/${personId}/note_${epoch}_${safeName}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file, { contentType: inferContentType(file) });
  return {
    path,
    name: file.name,
    contentType: file.type || inferContentType(file),
    size: file.size,
    uploadedAt: Timestamp.now(),
  };
}

// Render an attachment chip/img inside a note card. Returns '' when the
// note has no attachment. ADVERSARY: derives the href/src from the storage
// PATH via getDownloadURL at render time (never from a stored url) and
// protocol-checks (https only) before using it. Images render as <img>
// ONLY (tap → lightbox); PDFs render as a file-chip link.
function _renderHrNoteAttachment(note) {
  const att = note && note.attachment;
  if (!att || !att.path) return '';
  // TASK-684 (COD Adversary): an hrNote attachment MUST live under THIS tenant's
  // own `hr/{tid}/` prefix — where note uploads land AND where isImage() blocks
  // SVG. Firestore rules do NOT constrain attachment.path, so a malicious member
  // could forge a path to a public, SVG-hostable location (e.g. partner-logos/,
  // which uses a raw image/* match, not isImage()) and have it rendered as a
  // clickable Storage link → SVG-with-script XSS. The render is the gate: reject
  // any path not under this tenant's hr/ prefix (render nothing).
  const _attTid = (typeof HRState !== 'undefined' && HRState.tenantId) || (window.AppState && window.AppState.tenantId) || '';
  if (!_attTid || !String(att.path).startsWith('hr/' + _attTid + '/')) return '';
  const ct = (att.contentType || '').toLowerCase();
  const isImg = ct.startsWith('image/') && ct !== 'image/svg+xml';
  const isPdf = ct === 'application/pdf';
  if (!isImg && !isPdf) return ''; // unknown/unsafe type — render nothing
  // Unique element id so we can fill src/href after the async URL resolves.
  const elId = 'hr-note-att-' + Math.random().toString(36).slice(2, 10);
  const safeName = escapeHtml(att.name || 'attachment');
  // Resolve the genuine Firebase download URL from the path, then wire it in.
  // protocol-check: only https URLs become an src/href.
  getDownloadURL(ref(storage, att.path)).then((url) => {
    if (typeof url !== 'string' || !/^https:\/\//i.test(url)) return;
    const el = document.getElementById(elId);
    if (!el) return;
    if (isImg) {
      el.src = url;
      el.style.display = 'block';
      el.onclick = () => _openHrNoteLightbox(url, att.name || '');
    } else {
      el.href = url;
    }
  }).catch(() => { /* token fetch failed — leave placeholder */ });
  if (isImg) {
    return ''
      + '<div style="margin-top:8px;">'
      +   '<img id="' + elId + '" alt="' + safeName + '" title="' + safeName + '" '
      +     'style="display:none;max-width:160px;max-height:160px;border-radius:6px;border:1px solid var(--border-light,#e5e7eb);cursor:zoom-in;object-fit:cover;" />'
      + '</div>';
  }
  // PDF — file chip link (href filled in after URL resolves; https-checked).
  return ''
    + '<div style="margin-top:8px;">'
    +   '<a id="' + elId + '" target="_blank" rel="noopener noreferrer" '
    +     'style="display:inline-flex;align-items:center;gap:6px;padding:6px 10px;background:var(--bg-primary,#fff);border:1px solid var(--border-light,#e5e7eb);border-radius:6px;font-size:0.8rem;color:var(--brand-blue,#3B82F6);text-decoration:none;">'
    +     '<span class="material-icons" style="font-size:16px;">picture_as_pdf</span>' + safeName
    +   '</a>'
    + '</div>';
}

// Full-size image lightbox for note image attachments.
function _openHrNoteLightbox(url, name) {
  if (typeof url !== 'string' || !/^https:\/\//i.test(url)) return;
  let lb = document.getElementById('hr-note-lightbox');
  if (!lb) {
    lb = document.createElement('div');
    lb.id = 'hr-note-lightbox';
    lb.style.cssText = 'position:fixed;inset:0;z-index:2000;background:rgba(0,0,0,0.85);display:flex;align-items:center;justify-content:center;padding:24px;cursor:zoom-out;';
    lb.onclick = () => lb.classList.add('hidden');
    document.body.appendChild(lb);
  }
  lb.innerHTML = '';
  const img = document.createElement('img');
  img.src = url;
  img.alt = name || '';
  img.style.cssText = 'max-width:95vw;max-height:95vh;border-radius:8px;box-shadow:0 8px 40px rgba(0,0,0,0.5);';
  lb.appendChild(img);
  lb.classList.remove('hidden');
}

// §40.327 picker warning — /hr/{tid}/ is readable by ANY tenant member,
// so a DOT D&A test record attached here would leak to all staff.
const HR_NOTE_ATTACH_DA_WARNING = "Don't attach drug/alcohol test records here — use the DOT Drug &amp; Alcohol folder.";

// Picker change handler — validates the chosen file against the allowlist
// and shows a chip (or clears the input on rejection). surface = 'detail'
// (HR-detail composer) or 'modal' (standalone Add Note modal).
function _onHrNoteAttachPick(surface) {
  const inputId = surface === 'modal' ? 'add-emp-note-attach-input' : 'hr-emp-note-attach-input';
  const chipId = surface === 'modal' ? 'add-emp-note-attach-chip' : 'hr-emp-note-attach-chip';
  const input = document.getElementById(inputId);
  const chip = document.getElementById(chipId);
  const file = input?.files?.[0];
  if (!chip) return;
  if (!file) {
    chip.style.display = 'none';
    chip.innerHTML = '';
    return;
  }
  const v = _validateHrNoteAttachment(file);
  if (!v.ok) {
    window.showToast?.(v.msg, 'error');
    if (input) input.value = '';
    chip.style.display = 'none';
    chip.innerHTML = '';
    return;
  }
  chip.style.display = 'flex';
  chip.innerHTML = ''
    + '<span class="material-icons" style="font-size:14px;">'
    +   (file.type === 'application/pdf' ? 'picture_as_pdf' : 'image')
    + '</span>'
    + '<span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:200px;">' + escapeHtml(file.name) + '</span>'
    + '<button type="button" onclick="window.HRModule._clearHrNoteAttach(\'' + surface + '\')" title="Remove attachment" style="background:none;border:none;cursor:pointer;color:var(--text-secondary);padding:0;display:inline-flex;">'
    +   '<span class="material-icons" style="font-size:14px;">close</span>'
    + '</button>';
}

function _clearHrNoteAttach(surface) {
  const inputId = surface === 'modal' ? 'add-emp-note-attach-input' : 'hr-emp-note-attach-input';
  const chipId = surface === 'modal' ? 'add-emp-note-attach-chip' : 'hr-emp-note-attach-chip';
  const input = document.getElementById(inputId);
  const chip = document.getElementById(chipId);
  if (input) input.value = '';
  if (chip) { chip.style.display = 'none'; chip.innerHTML = ''; }
}

function formatDate(date) {
  if (!date) return '-';
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

// ============================================
// EMPLOYEE DIRECTORY FUNCTIONS
// ============================================
function toggleSection(sectionId) {
  const content = document.getElementById(`hr-${sectionId}`);
  const chevron = document.getElementById(`chevron-${sectionId}`);
  if (!content) return;

  const isHidden = content.style.display === 'none';
  content.style.display = isHidden ? 'block' : 'none';
  if (chevron) {
    chevron.textContent = isHidden ? 'expand_less' : 'expand_more';
  }

  if (isHidden && sectionId === 'actions') _spCall('paintBadge');
  // Load employees when first expanded
  if (isHidden && sectionId === 'employee-directory') {
    renderEmployeeDirectory();
    populatePositionFilter();
    // If people haven't loaded yet, retry rendering when they arrive
    if (!HRState.peopleLoaded) {
      const retryInterval = setInterval(() => {
        if (HRState.peopleLoaded || (HRState.people && HRState.people.length > 0)) {
          clearInterval(retryInterval);
          renderEmployeeDirectory();
          populatePositionFilter();
        }
      }, 500);
      // Stop retrying after 10 seconds
      setTimeout(() => clearInterval(retryInterval), 10000);
    }
  }
}

function populatePositionFilter() {
  const select = document.getElementById('hr-position-filter');
  if (!select) return;

  // TASK-1566 — built-in-in-use + custom, via the single HRPositions resolver.
  const positions = window.HRPositions.list();

  select.innerHTML = '<option value="">All Positions</option>' +
    positions.map(p => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.title)}</option>`).join('');
}

// TASK-629 — single employment-status dropdown (Active / Inactive / Terminated / All),
// matching the roster People list (TASK-626). Derives the legacy showInactive/showTerminated
// booleans so the existing render + CSV-export filters (which read p.active / p.terminated)
// keep working unchanged. Bucket semantics mirror roster.js renderPeopleList:
//   active     → not terminated AND active !== false  (showInactive off, showTerminated off)
//   inactive   → not terminated AND active === false   (showInactive on,  showTerminated off)
//   terminated → terminated === true                   (showInactive off, showTerminated on)
//   all        → no status filter                      (both on)
function setDirectoryStatus(status) {
  HRState.directoryStatus = status || 'active';
  switch (HRState.directoryStatus) {
    case 'inactive':
      // Only people who are inactive-but-not-terminated. showInactive surfaces
      // active===false rows; renderEmployeeDirectory then narrows to that bucket.
      HRState.showInactive = true;
      HRState.showTerminated = false;
      break;
    case 'terminated':
      HRState.showInactive = false;
      HRState.showTerminated = true;
      break;
    case 'all':
      HRState.showInactive = true;
      HRState.showTerminated = true;
      break;
    case 'active':
    default:
      HRState.showInactive = false;
      HRState.showTerminated = false;
      break;
  }
  // TASK-582 — preserve an active compliance tile filter (it ANDs with the status filter).
  applyDirectoryFilters();
}

// User-driven search/position interaction (oninput/onchange in the toolbar).
// TASK-582 — manually touching search or position CLEARS the compliance tile
// filter so it never silently persists underneath a user-driven filter change.
function filterEmployees() {
  HRState.directoryComplianceFilter = null;
  applyDirectoryFilters();
}

// TASK-582 — re-render the directory with the current toolbar values WITHOUT
// touching the compliance tile filter (used by toggles + chip/tile plumbing).
function applyDirectoryFilters() {
  const searchTerm = (document.getElementById('hr-employee-search')?.value || '').toLowerCase();
  const positionFilter = document.getElementById('hr-position-filter')?.value || '';
  renderEmployeeDirectory(searchTerm, positionFilter);
}

function getEmployeeTimeOffSummary(emp) {
  const policy = HRState.timeOffPolicy;
  if (!policy?.levels?.length || !emp.hrTimeOffLevel || !emp.hrEmploymentType) return null;

  const level = policy.levels.find(l => l.id === emp.hrTimeOffLevel);
  if (!level) return null;

  const allowances = level[emp.hrEmploymentType] || {};
  const currentYear = new Date().getFullYear();

  const approvedRequests = (HRState.timeOffRequests || []).filter(r => {
    if (r.personId !== emp.id && r.employeeId !== emp.id && r.employeeName !== emp.name) return false;
    if (r.status !== 'approved') return false;
    const start = r.startDate?.toDate?.() || new Date(r.startDate);
    return start.getFullYear() === currentYear;
  });

  let totalAllowedHrs = 0;
  let totalUsedHrs = 0;

  const categories = TIME_OFF_CATEGORIES.filter(c => {
    if (c.id === 'unpaid' && emp.hrEmploymentType === 'salary') return false;
    return true;
  });

  categories.forEach(cat => {
    // Bereavement is per-event (resets each occurrence), not an annual pool.
    // Unpaid is also excluded from the pool — it doesn't draw from PTO.
    if (cat.id === 'bereavement') return;
    if (cat.id === 'unpaid') return;
    totalAllowedHrs += ((allowances[cat.id] || 0) * 8);
  });

  approvedRequests.forEach(r => {
    // Day-level bucketing: each day's effective type (from per-day overrides
    // or the request-level type) determines whether it counts toward the
    // paid pool. Excluded days don't count for any type.
    // TASK-341 — Paid company holidays (settings.companyHolidays) don't
    // burn a PTO day. Falls back to standard pool burn when no holiday
    // matches OR the holiday isn't paid for this person's scope.
    const days = enumerateRequestDays(r);
    const hpd = hoursPerDayForRequest(r);
    days.forEach(d => {
      if (d.isExcluded) return;
      if (!isPaidPoolType(d.type)) return;
      if (window.AppHelpers?.isPaidHolidayForPerson?.(d.iso, emp)) return;
      totalUsedHrs += hpd;
    });
  });

  // TASK-9839 — milestone grants landed this year add to the pool (same
  // shared calculator as the tab, so the placard and the tab agree on them).
  try {
    const _tob = computeEmployeeTimeOffBalances(emp);
    if (_tob.status === 'ok') _tob.rows.forEach(r => { if (isPaidPoolType(r.id)) totalAllowedHrs += (r.grantHours || 0); });
  } catch (_e) { /* placard only — never block the directory */ }

  if (totalAllowedHrs === 0) return null;

  return { totalAllowed: totalAllowedHrs, totalUsed: totalUsedHrs, totalRemaining: totalAllowedHrs - totalUsedHrs };
}

/**
 * Enumerate a request's days with the effective type per day, accounting for
 * per-day overrides (request.dayTypes) and excluded days (request.excludedDates).
 *
 * Returns: [{ iso, type, isExcluded }, ...]
 *
 * Optional override lets callers preview hypothetical changes — pass an object
 * keyed by iso for type overrides, or `excludedOverride` (Set<iso>) for inclusion.
 */
function enumerateRequestDays(req, opts = {}) {
  if (!req) return [];
  const start = req.startDate?.toDate ? req.startDate.toDate() : new Date(req.startDate);
  const end = req.endDate?.toDate ? req.endDate.toDate() : new Date(req.endDate);
  const allDays = enumerateDays(start, end);
  const baseType = req.type || 'unpaid';
  const savedDayTypes = req.dayTypes || {};
  const dayTypeOverride = opts.dayTypesOverride || null;
  const excludedOverride = opts.excludedOverride || null;
  const savedExcluded = new Set(req.excludedDates || []);

  return allDays.map(d => {
    const isExcluded = excludedOverride
      ? excludedOverride.has(d.iso)
      : savedExcluded.has(d.iso);
    let type = baseType;
    if (dayTypeOverride && Object.prototype.hasOwnProperty.call(dayTypeOverride, d.iso)) {
      type = dayTypeOverride[d.iso];
    } else if (Object.prototype.hasOwnProperty.call(savedDayTypes, d.iso)) {
      type = savedDayTypes[d.iso];
    }
    return { iso: d.iso, label: d.label, date: d.date, type, isExcluded };
  });
}

/** Hours each day represents for a given request (default 8, derived from req.hours / day count if set). */
function hoursPerDayForRequest(req) {
  if (!req) return 8;
  const start = req.startDate?.toDate ? req.startDate.toDate() : new Date(req.startDate);
  const end = req.endDate?.toDate ? req.endDate.toDate() : new Date(req.endDate);
  const allDays = enumerateDays(start, end);
  if (allDays.length > 0 && req.hours) return req.hours / allDays.length;
  return 8;
}

/** True when a type draws from the paid PTO pool (i.e. not unpaid, not bereavement). */
function isPaidPoolType(t) {
  return t && t !== 'unpaid' && t !== 'bereavement';
}

/**
 * Compute the PTO balance impact of approving / having approved this request,
 * accounting for per-day types. Returns null when the request has no paid-pool
 * days (e.g. all-unpaid) or when no policy is configured.
 */
function getRequestBalanceImpact(req, statusOverride) {
  if (!req) return null;

  const personId = req.personId;
  const emp = (HRState.people || []).find(p => p.id === personId)
    || (HRState.people || []).find(p => p.name === req.personName);
  if (!emp) return null;

  const summary = getEmployeeTimeOffSummary(emp);
  if (!summary) return null;

  // Day-level pool hours
  // TASK-341 — Paid company holidays (settings.companyHolidays) don't
  // burn a PTO day. Same rule as getEmployeeTimeOffSummary above.
  const days = enumerateRequestDays(req);
  const hpd = hoursPerDayForRequest(req);
  const poolHours = days.reduce((sum, d) => {
    if (d.isExcluded) return sum;
    if (!isPaidPoolType(d.type)) return sum;
    if (window.AppHelpers?.isPaidHolidayForPerson?.(d.iso, emp)) return sum;
    return sum + hpd;
  }, 0);
  if (poolHours === 0) return null;

  const status = statusOverride || req.status;
  const projected = status === 'approved'
    ? summary.totalRemaining
    : summary.totalRemaining - poolHours;

  return {
    allowed: summary.totalAllowed,
    used: summary.totalUsed,
    remaining: summary.totalRemaining,
    requestHours: Math.round(poolHours),
    projected: Math.round(projected),
    willOverdraw: projected < 0,
    isApproved: status === 'approved',
  };
}

function renderEmployeeDirectory(searchTerm = '', positionFilter = '') {
  const container = document.getElementById('hr-employee-list');
  if (!container) return;

  let employees = HRState.people || [];

  // TASK-629 — employment-status dropdown (matches roster.js renderPeopleList).
  //   active     → not terminated AND active !== false (legacy default)
  //   inactive   → not terminated AND active === false
  //   terminated → terminated === true
  //   all        → no status filter
  const status = HRState.directoryStatus || 'active';
  if (status === 'active') {
    employees = employees.filter(p => p.terminated !== true && p.active !== false);
  } else if (status === 'inactive') {
    employees = employees.filter(p => p.terminated !== true && p.active === false);
  } else if (status === 'terminated') {
    employees = employees.filter(p => p.terminated === true);
  }

  // Filter by search term
  if (searchTerm) {
    employees = employees.filter(p =>
      p.name?.toLowerCase().includes(searchTerm) ||
      p.hrPosition?.toLowerCase().includes(searchTerm) ||
      getPositionTitle(p.hrPosition)?.toLowerCase().includes(searchTerm)
    );
  }

  // Filter by position
  if (positionFilter) {
    employees = employees.filter(p => p.hrPosition === positionFilter);
  }

  // TASK-582 — compliance tile filter (ANDs with search/position/toggles above).
  // Uses the SAME predicate as the dashboard tile counts (getComplianceTileStatus)
  // so the tile number and this list can never disagree.
  const complianceKey = HRState.directoryComplianceFilter;
  if (complianceKey && COMPLIANCE_TILE_DEFS[complianceKey]) {
    const win = getComplianceWindow();
    employees = employees.filter(p => getComplianceTileStatus(p, complianceKey, win));
    // Soonest expiry first — expired dates sort earliest, so they land on top.
    // TASK-1391 — predicate tiles (unclassified) have no date field; keep the
    // directory's default order for them.
    const field = COMPLIANCE_TILE_DEFS[complianceKey].field;
    if (field) employees.sort((a, b) => String(a[field] || '').localeCompare(String(b[field] || '')));
  }
  renderComplianceFilterChip(employees.length);

  if (employees.length === 0) {
    container.innerHTML = '<div class="empty-state-small">No employees found</div>';
    return;
  }

  // TASK-9602 — one time-off gate + one "today" for every card in this render.
  const _toNow = new Date();
  const toCtx = { canView: _hrCanViewTimeOff(), year: _toNow.getFullYear(), todayDay: _hrTimeOffList.localDayNum(_toNow) };

  container.innerHTML = employees.map(rawEmp => {
    // TASK-1481 — the pay/insurance/deduction badges below read comp fields;
    // resolve comp-first (peopleComp) with legacy person-doc fallback.
    const emp = _hrComp(rawEmp);
    const posTitle = getPositionTitle(emp.hrPosition) || '';
    const posInfo = window.HRPositions.get(emp.hrPosition); // TASK-1566 — resolves custom (dept 'Custom') too
    const department = posInfo?.department || '';
    // TASK-4776 — a VOIDED review is kept on file but must not count as a
    // current one, or the void modal's promise ("it stops counting as a current
    // review") is a lie and the roster card still shows the score of a review
    // that was voided for being on the wrong person. The row itself stays
    // visible, marked VOID, in the employee-detail list.
    const _liveReviews = (HRState.hrReviews || []).filter(r => r.employeeId === emp.id && !_hrReviewIsVoided(r));
    const reviewCount = _liveReviews.length;
    const lastReview = _liveReviews
      .slice()
      .sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0))[0];
    const avgScore = lastReview?.totalScore;

    // Check level-up eligibility
    const levelUpInfo = getEmployeeLevelUpInfo(emp);

    // Time-off summary badge
    const toSummary = toCtx.canView ? getEmployeeTimeOffSummary(emp) : null;
    // TASK-9602 — days off this year / off today or next off / pending, shown
    // even with no paid pool (unpaid-only tenants). Same time-off gate.
    let toBadgeHtml = _timeOffDirectoryBadgesHtml(emp, toCtx);
    if (toSummary) {
      const pct = toSummary.totalRemaining / toSummary.totalAllowed;
      const badgeClass = pct > 0.5 ? 'good' : pct > 0.25 ? 'warning' : 'poor';
      toBadgeHtml += `<span class="hr-emp-badge ${badgeClass}" title="Time off: ${toSummary.totalUsed} hrs used / ${toSummary.totalAllowed} hrs total"><span class="material-icons" style="font-size:12px;vertical-align:middle;">event_busy</span> ${toSummary.totalUsed}/${toSummary.totalAllowed} hrs</span>`;
    }

    // Department badge class
    const deptClass = department.startsWith('Field') ? 'dept-field'
      : department.startsWith('Dispatch') ? 'dept-dispatch'
      : department.startsWith('Maintenance') ? 'dept-shop'
      : department.startsWith('Office') ? 'dept-office'
      : 'dept-default';

    // DVIR flag count (live flagged/dismissed records + persistent counter)
    const empDvirFlags = (HRState.dvirFlags || []).filter(f => f.driverId === emp.id);
    const dvirFlagCount = empDvirFlags.length || (emp.missedDvirCount || 0);

    // JSA flag count (live flagged/dismissed records + persistent counter)
    const empJsaFlags = (HRState.jsaFlags || []).filter(f => f.driverId === emp.id);
    const jsaFlagCount = empJsaFlags.length || (emp.missedJsaCount || 0);

    // Expired documents
    const expiredDocs = getExpiredDocs(emp);

    // TASK-9941 F S3 (Chris 10/18) — the HR directory no longer prints the
    // name + phone code for anyone. It is a name tag, not a password; a driver
    // signs in with their private PIN (Roster › Driver PINs sends setup codes).

    // Hire date — emp.hireDate is a Firestore Timestamp (written by hr.js
    // promote + Edit Employee modal). Phantom `hrHireDate` alias never
    // written; render was silently blank pre-2026-05-18.
    const _hireRaw = emp.hireDate?.toDate?.() || emp.hireDate;
    // TASK-541 — local-parse date-only strings (same UTC-midnight −1 day bug as
    // formatDateShort). _hireRaw is normally a Timestamp→Date, but a raw
    // YYYY-MM-DD string would slip to the prior month on a 1st-of-month hire.
    const _hireDateObj = (typeof _hireRaw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(_hireRaw))
      ? (() => { const [y, m, day] = _hireRaw.split('-').map(Number); return new Date(y, m - 1, day); })()
      : (_hireRaw ? new Date(_hireRaw) : null);
    const hireDate = _hireDateObj ? _hireDateObj.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';

    // Position fallback: hrPosition → crewType
    let displayPosition = posTitle;
    if (!displayPosition) {
      const ct = emp.crewType || emp.role || (emp.roles && emp.roles[0]) || '';
      if (ct) displayPosition = window.getTypeLabel ? window.getTypeLabel(ct, 'crewTypes') : ct; // TASK-9884 — (slug, kind); was reversed
    }

    return `
      <div class="hr-employee-card ${emp.active === false ? 'inactive' : ''} ${expiredDocs.some(d => d.severity === 'alert') ? 'compliance-alert' : ''}" onclick="window.HRModule.openEmployeeDetail('${emp.id}')">
        <div class="hr-employee-card-header">
          <div class="hr-employee-avatar">
            ${_hrPersonAvatarInner(emp)}
          </div>
          <div class="hr-employee-info">
            <div class="hr-employee-name">
              ${escapeHtml(emp.name || 'Unknown')}
              ${emp.active === false ? '<span class="hr-inactive-label">INACTIVE</span>' : ''}
              ${emp.terminated ? '<span class="hr-inactive-label" style="background:#fef2f2;color:#dc2626;border-color:#fecaca;">FORMER</span>' : ''}
              ${levelUpInfo ? '<span class="material-icons" style="color:var(--warning-amber);font-size:1rem;vertical-align:middle;margin-left:4px;" title="Level-up eligible">trending_up</span>' : ''}
            </div>
            ${displayPosition ? `<div class="hr-employee-position">${escapeHtml(displayPosition)}</div>` : '<div class="hr-employee-position" style="color:var(--text-secondary);font-style:italic;">No position assigned</div>'}
          </div>
        </div>
        <div class="hr-employee-card-body">
          <div class="hr-employee-card-badges">
            ${emp.flagged === true ? `<span class="hr-emp-badge poor" title="${escapeHtml(formatFlagTooltip(emp))}">🚩 Flagged</span>` : ''}
            ${department ? `<span class="hr-dept-badge ${deptClass}">${escapeHtml(department)}</span>` : ''}
            ${emp.hrEmploymentType === 'salary' && emp.hrAnnualSalary ? `<span class="hr-emp-badge"><span class="material-icons" style="font-size:12px;vertical-align:middle;">payments</span> $${Number(emp.hrAnnualSalary).toLocaleString()}/yr</span>` : emp.hrEmploymentType === 'salary' ? `<span class="hr-emp-badge"><span class="material-icons" style="font-size:12px;vertical-align:middle;">payments</span> Salary</span>` : emp.hrHourlyRate ? `<span class="hr-emp-badge"><span class="material-icons" style="font-size:12px;vertical-align:middle;">payments</span> $${parseFloat(emp.hrHourlyRate).toFixed(2)}/hr</span>` : ''}
            ${emp.hrBCBS ? `<span class="hr-emp-badge"><span class="material-icons" style="font-size:12px;vertical-align:middle;">health_and_safety</span> Health Ins $${parseFloat(emp.hrBCBS).toFixed(2)}/paycheck</span>` : ''}
            ${emp.hrDeductions ? `<span class="hr-emp-badge"><span class="material-icons" style="font-size:12px;vertical-align:middle;">receipt_long</span> Deductions</span>` : ''}
            ${toBadgeHtml}
            ${reviewCount > 0 ? `<span class="hr-emp-badge"><span class="material-icons" style="font-size:12px;vertical-align:middle;">rate_review</span> ${reviewCount} review${reviewCount !== 1 ? 's' : ''}</span>` : ''}
            ${avgScore ? `<span class="hr-emp-badge ${avgScore >= 80 ? 'good' : avgScore >= 60 ? 'warning' : 'poor'}">${avgScore.toFixed(0)}%</span>` : ''}
            ${dvirFlagCount > 0 ? `<span class="hr-emp-badge poor"><span class="material-icons" style="font-size:12px;vertical-align:middle;">warning</span> ${dvirFlagCount} Missed DVIR${dvirFlagCount !== 1 ? 's' : ''}</span>` : ''}
            ${jsaFlagCount > 0 ? `<span class="hr-emp-badge warning"><span class="material-icons" style="font-size:12px;vertical-align:middle;">health_and_safety</span> ${jsaFlagCount} Missed JSA${jsaFlagCount !== 1 ? 's' : ''}</span>` : ''}
            ${(emp.pendingSignatureCount || 0) > 0 ? `<span class="hr-emp-badge warning" title="${emp.pendingSignatureCount} document signature${emp.pendingSignatureCount === 1 ? '' : 's'} still awaiting this person — sent from the HR Document Signatures panel and not yet signed."><span class="material-icons" style="font-size:12px;vertical-align:middle;">draw</span> Awaiting signature${emp.pendingSignatureCount > 1 ? ` (${emp.pendingSignatureCount})` : ''}</span>` : ''}
            ${expiredDocs.map(d => d.severity === 'classify'
              ? `<span class="hr-emp-badge warning" style="cursor:pointer;" title="No DOT role classification set — until classified, the full CDL checklist applies on the DQ file. Tap to classify." onclick="event.stopPropagation();window.HRModule.openEmployeeClassification('${emp.id}')"><span class="material-icons" style="font-size:12px;vertical-align:middle;">assignment_ind</span> ${escapeHtml(d.label)}</span>`
              : `<span class="hr-emp-badge poor"><span class="material-icons" style="font-size:12px;vertical-align:middle;">error</span> ${escapeHtml(d.label)}</span>`).join('')}
          </div>
        </div>
        ${hireDate ? `
        <div class="hr-employee-card-footer">
          <span></span>
          <span>Hired ${hireDate}</span>
        </div>
        ` : ''}
      </div>
    `;
  }).join('');

  // TASK-9234 — resolve avatars from their storage path now that the cards are
  // in the DOM. Fire-and-forget; a photo that can't resolve keeps its initials.
  window.RosterModule?.hydratePersonPhotos?.(container);

  // Update compliance badges on nav
  updateComplianceBadges();
  renderComplianceDashboard();
}

// TASK-1123 — the Employee Details modal header, extracted from
// openEmployeeDetail so saveEmploymentFields can surgically re-render it
// after an Employment-tab save (Current Pay / card last4s / hired date went
// stale until a full page refresh). Includes the posTitle derivation
// (2026-05-21 fix — mirror the directory card fallback chain
// hrPosition → crewType → role → roles[0]; people promoted from candidates /
// imported via GAS / manually added with crewType set but no hrPosition
// rendered as "No position assigned" — caught on Malachi King. Standard role
// pattern per MEMORY.md: `p.crewType || p.role || p.roles?.[0]`.)
function buildEmployeeDetailHeaderHTML(rawEmp) {
  const emp = _hrComp(rawEmp); // TASK-1481 — Current Pay / Deductions lines read comp
  let posTitle = getPositionTitle(emp.hrPosition);
  if (!posTitle) {
    const ct = emp.crewType || emp.role || (emp.roles && emp.roles[0]) || '';
    if (ct) posTitle = window.getTypeLabel ? window.getTypeLabel(ct, 'crewTypes') : ct; // TASK-9884 — (slug, kind); was reversed
  }
  if (!posTitle) posTitle = 'No position assigned';

  return `
      <div class="hr-employee-detail-header">
        <div class="hr-employee-detail-avatar">${emp.name?.charAt(0)?.toUpperCase() || '?'}</div>
        <div class="hr-employee-detail-info">
          <h2>${escapeHtml(emp.name || 'Unknown')}${emp.active === false ? ' <span class="hr-inactive-label">INACTIVE</span>' : ''}${emp.flagged === true ? ` <span class="hr-emp-badge poor" style="vertical-align:middle;" title="${escapeHtml(formatFlagTooltip(emp))}">🚩 Flagged</span>` : ''}</h2>
          <div class="hr-employee-detail-position">${escapeHtml(posTitle)}</div>
          ${emp.phone ? `<div style="font-size:0.85rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">phone</span> ${escapeHtml(emp.phone)}</div>` : ''}
          ${emp.email ? `<div style="font-size:0.85rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">email</span> ${escapeHtml(emp.email)}</div>` : ''}
          ${emp.personalEmail ? `<div style="font-size:0.85rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">alternate_email</span> ${escapeHtml(emp.personalEmail)} <span style="font-size:0.75rem;">(personal)</span></div>` : ''}
          ${emp.address ? `<div style="font-size:0.85rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">home</span> ${escapeHtml(emp.address)}</div>` : ''}
          ${emp.hrHourlyRate ? `<div class="hr-employee-detail-pay">Current Pay: <strong>$${parseFloat(emp.hrHourlyRate).toFixed(2)}/hr</strong></div>` : ''}
          ${emp.hrDeductions ? `<div class="hr-employee-detail-pay" style="font-size:0.85rem;color:var(--text-secondary);">Deductions: ${escapeHtml(emp.hrDeductions)}</div>` : ''}
          ${/* TASK-4866 - the card's NAME, falling back to the generic literal
               this line has always printed when nobody has named it. */''}
          ${emp.hasCreditCard ? `<div style="font-size:0.85rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">credit_card</span> ${escapeHtml(_hrCardIsNamed(emp, 'credit') ? _hrCardLabel(emp, 'credit') : 'Credit Card')}: ****${escapeHtml(emp.creditCardLast4 || '????')}</div>` : ''}
          ${/* TASK-4865 — the PIN is NO LONGER PRINTED HERE. It used to render as
               `/ PIN: 1234` in the clear on a screen anyone in the office could
               read over a shoulder, off a field (`fuelCardPin`) that lived on the
               BROADCAST `people` doc every driver-PIN session can read. The last-4
               stays (not a credential, PCI-permitted to display); the PIN now lives
               in the admin-gated `peopleCard` vault and is fetched on demand by
               revealFuelCardPin. See public/src/personCard.js for the field split. */''}
          ${emp.hasFuelCard ? `<div style="font-size:0.85rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">local_gas_station</span> ${escapeHtml(_hrCardLabel(emp, 'fuel'))}: ****${escapeHtml(emp.fuelCardLast4 || '????')} <span id="hr-fuelpin-slot" data-person="${escapeHtml(String(emp.id || ''))}">&middot; PIN <button type="button" class="btn btn-sm btn-secondary" style="padding:0 8px;font-size:0.7rem;line-height:1.6;" onclick="event.stopPropagation();window.HRModule.revealFuelCardPin('${escJsAttr(String(emp.id || ''))}')" title="The fuel-card PIN is kept in the admin-gated card vault, not on the shared employee record. Showing it loads it just for you and records who looked in the activity log.">Show</button></span></div>` : ''}${_hrCardAdditional(emp).map(c => `<div style="font-size:0.85rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:2px;">${_hrCardIcon(c.type)}</span> ${escapeHtml(c.label)}: ****${escapeHtml(c.last4 || '????')}${_hrCardStatusNote(c)}</div>`).join('')}
          ${emp.hireDate ? `<div class="hr-employee-detail-hire">Hired: ${formatDate(emp.hireDate?.toDate?.() || emp.hireDate)}</div>` : ''}
        </div>
        <div class="hr-emp-detail-actions" style="display:flex;flex-direction:column;gap:6px;align-items:flex-end;">
          <button class="btn btn-secondary btn-sm" onclick="window.HRModule.editEmployeeHR('${emp.id}')">
            <span class="material-icons">edit</span> Edit
          </button>
          ${''/* TASK-9424 — ADD only; each flag clears from its own Clear… in the flag panel. */}
          <button class="btn btn-sm" onclick="window.HRModule.addDriverFlag('${escJsAttr(String(emp.id || ''))}')" style="background:transparent;border:1.5px solid #B45309;color:#B45309;font-weight:600;" title="Flag this person as needing attention / follow-up — a person can carry more than one flag">
            <span class="material-icons">outlined_flag</span> ${emp.flagged === true ? 'Add a flag' : 'Flag'}
          </button>
        </div>
      </div>${buildFlagReasonCalloutHTML(emp)}`;
}

// TASK-1207 / TASK-9424 — flag panel under the Employee Details header. A person
// can now carry SEVERAL flags, each cleared on its own with a REQUIRED reason, and
// the history (every flag and every clear, with why) stays. The REASON is
// need-to-know (§382.601/§40.351 class free text): getDriverFlags returns it ONLY
// to owner/admin (the same set the server-only driverFlags read rule admits);
// other hr/compliance holders see who/when + "contact your admin" and can still
// pick WHICH flag to clear. This shell renders synchronously; _hydrateHrFlagReason
// fills it. The container is always present so a cleared flag's history is still
// reachable after the last flag goes.
function buildFlagReasonCalloutHTML(emp) {
  if (emp?.flagged !== true) return '<div id="hr-flag-panel"></div>';
  const meta = formatFlagTooltip(emp).replace(/^Flagged for follow-up/, ''); // '' or ' · by X · on date'
  return `
      <div id="hr-flag-panel">
      <div id="hr-flag-reason-callout" style="background:#FEF2F2;border:1.5px solid #FECACA;border-left:5px solid #DC2626;border-radius:8px;padding:12px 14px;margin:10px 0 4px;">
        <div style="display:flex;align-items:center;gap:6px;font-weight:800;color:#B91C1C;font-size:0.9rem;">
          <span class="material-icons" style="font-size:18px;">flag</span> Flagged for follow-up${escapeHtml(meta)}
        </div>
        <div id="hr-flag-reason-text" style="margin-top:6px;font-size:0.82rem;color:#991B1B;">Loading flags…</div>
      </div>
      </div>`;
}

// Whether to draw Clear… — the host's call (Pitfall #8), mirroring setDriverFlag's
// WRITE gate (owner/admin, or an hr/compliance holder who is not view/upload-only).
// The server re-checks.
function _hrCanWriteFlags() {
  const m = window.AppState?.memberAccess;
  if (!m) return true; // owner/legacy (no member doc)
  if (['revoked', 'inactive', 'pending'].includes(m.status)) return false;
  if (m.role === 'owner' || m.role === 'admin') return true;
  const mods = Array.isArray(m.modules) ? m.modules : [];
  return (mods.includes('hr') || mods.includes('compliance')) && m.viewUploadOnly !== true;
}

// TASK-9424 — fill the panel from getDriverFlags. Free text is escaped by the
// shared renderer; status copy goes through textContent. A partner-owned flag
// (server-stamped `source`, TASK-9227) gets a note instead of a Clear button.
async function _hydrateHrFlagReason(emp) {
  const host = document.getElementById('hr-flag-panel');
  if (!host || !emp?.id) return;
  const PS = window.PersonSections;
  let res;
  try {
    const fn = httpsCallable(functions, 'getDriverFlags');
    res = (await fn({ tenantId: HRState.tenantId, personId: emp.id }))?.data || {};
  } catch (err) {
    const el = document.getElementById('hr-flag-reason-text');
    if (el) {
      el.textContent = String(err?.code || '').endsWith('permission-denied')
        ? 'Flag details are visible to HR / Compliance and the owner/admin — contact your admin.'
        : 'Could not load this person\'s flags — reopen to try again.';
    }
    return;
  }
  if (document.getElementById('hr-flag-panel') !== host) return; // detail re-rendered meanwhile
  const canWrite = _hrCanWriteFlags();
  const pid = String(emp.id);
  const items = PS.driverFlagPanelItems(res.flags, {
    noteFor: (f) => (f.source === 'partner' ? 'Set by your compliance partner — only they can clear it.'
      : (f.reason == null ? 'Only an owner/admin (who can read the reason) can clear it.' : '')),
  });
  host.innerHTML = PS.renderFlagPanel(items, {
    hiddenReasonText: 'Reason visible to the owner/admin only — contact your admin.',
    emptyReasonText: 'No reason on file — this flag was set before reasons were recorded.',
    renderAction: (f) => ((f.source === 'partner' || !canWrite || f.reasonHidden) ? '' : `<button class="btn btn-sm" onclick="window.HRModule.clearDriverFlag('${escJsAttr(pid)}','${escJsAttr(f.flagId || '')}')" style="background:#fff;border:1.5px solid #DC2626;color:#DC2626;font-weight:600;" title="Clear this flag — you will be asked why">Clear…</button>`),
  }) + PS.renderFlagHistory(PS.driverFlagHistoryItems(res.history), {
    hiddenReasonText: 'Reasons visible to the owner/admin only.',
  });
}

async function openEmployeeDetail(employeeId) {
  if (_hrGateComplianceOnly('directory')) return; // TASK-1117 — Employee Directory detail is post-hire mgmt (paywalled)
  const emp = HRState.people.find(p => p.id === employeeId);
  if (!emp) return;

  // TASK-973 — fire-and-forget "who opened this employee/driver file" log. NEVER
  // blocks the detail render; the server derives the actor from auth, verifies
  // tenant membership + that the person exists, and rate-limits re-opens
  // (source='carrier'). Swallow every error — logging is best-effort.
  try {
    const _viewTid = window.AppState?.tenant || HRState.tenantId;
    if (_viewTid) httpsCallable(functions, 'logDriverFileView')({ tenantId: _viewTid, personId: emp.id }).catch(() => {});
  } catch (_) {}

  // TASK-1578 — the Employment tab now renders this person's Issued Equipment,
  // and the safetyEquipment listeners only ever started from the Safety sub-view.
  // Bootstrap them here so the section is populated for someone who opens a
  // person card without ever visiting Safety (guarded + unsub-tracked inside).
  _hrEnsureSafetyEquipmentSubscription();

  HRState.selectedEmployee = emp;
  HRState.employeeDocuments = [];
  HRState.employeeDocsLoaded = null;
  HRState.packetDocs = [];       // TASK-1381 — refetch link uploads per open
  HRState.packetDocsFor = null;

  // TASK-1147 — do NOT block first paint on the two admin-only secure reads
  // (peopleTax + peopleBackgroundChecks). They are consumed ONLY by the Tax +
  // Compliance tabs, but the modal opens on the Reviews tab (in-memory data) —
  // awaiting them here made every employee-file open feel slow. They now load
  // lazily on first Tax/Compliance tab open (switchDetailTab →
  // _ensureSecureDetailTabs), the same lazy pattern as the timeoff-balance /
  // documents / activity tabs. When both are already memoized on this emp
  // object (modal re-open before a people-snapshot replaces it), the tabs
  // build synchronously exactly as before.
  const secureLoaded = emp.secureTax !== undefined && emp.bgCheck !== undefined;

  // Get employee's reviews
  const reviews = (HRState.hrReviews || [])
    .filter(r => r.employeeId === employeeId)
    .sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));

  // Get employee's disciplinary forms + uploaded docs count
  const forms = (HRState.hrForms || [])
    .filter(f => f.employeeId === employeeId)
    .sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));

  // Eagerly load uploaded documents (updates badge via updateDetailTabCounts)
  // Deferred to after modal HTML is in the DOM (below)

  // 2026-05-19 — Review-reminder card. Lets admin set/change/cancel the
  // 30-day reviewer post-hire. Approve-to-Roster is the only other write path
  // for thirtyDayReviewerId today, so without this admins are stuck if the
  // wrong reviewer was picked at approve time.
  const supervisorList = (HRState.supervisors || HRState.people || [])
    .filter(p => p.active !== false);
  const currentReviewerId = emp.thirtyDayReviewerId || '';
  const currentReviewerName = currentReviewerId
    ? (supervisorList.find(p => p.id === currentReviewerId)?.name || '(unknown — pick someone below)')
    : '';
  const currentReviewDate = emp.thirtyDayReviewDate?.toDate?.() || (emp.thirtyDayReviewDate ? new Date(emp.thirtyDayReviewDate) : null);
  const currentReviewDateISO = currentReviewDate ? currentReviewDate.toISOString().slice(0, 10) : '';
  const defaultReviewerOptions = supervisorList.map(p => {
    const roleLabel = _hrRoleLabel(p.role || p.crewType);
    return `<option value="${p.id}" ${p.id === currentReviewerId ? 'selected' : ''}>${escapeHtml(p.name)}${roleLabel ? ' (' + escapeHtml(roleLabel) + ')' : ''}</option>`;
  }).join('');
  const reviewReminderCard = `
    <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:8px;padding:12px 14px;margin-bottom:12px;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">notifications_active</span>
        <span style="font-size:0.88rem;font-weight:700;color:var(--text-primary);">${Number.isInteger(emp.reviewStepIndex) && emp.reviewStepIndex > 0 ? 'Next review reminder' : 'First review reminder'}</span>
      </div>
      <div style="font-size:0.72rem;color:var(--text-secondary);margin-bottom:10px;">
        ${currentReviewerId && currentReviewDate
          ? `Next: <b>${escapeHtml(_hrNextReviewLabel(emp, currentReviewDate))}</b>, <b>${currentReviewDate.toLocaleDateString()}</b>, <b>${escapeHtml(currentReviewerName)}</b> will get an SMS + email. Pick another reviewer or date to override.`
          : 'No reminder set. Pick a reviewer and date to schedule one.'}
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
        <div>
          <label style="font-size:0.7rem;color:var(--text-secondary);font-weight:600;">Reviewer</label>
          <select id="hr-emp-reviewer" class="form-control" style="font-size:0.85rem;">
            <option value="">— None (cancel reminder) —</option>
            ${defaultReviewerOptions}
          </select>
        </div>
        <div>
          <label style="font-size:0.7rem;color:var(--text-secondary);font-weight:600;">Reminder date</label>
          <input type="date" id="hr-emp-review-date" class="form-control" style="font-size:0.85rem;" value="${currentReviewDateISO}">
        </div>
      </div>
      <div style="margin-top:8px;display:flex;justify-content:flex-end;">
        <button id="hr-emp-save-reviewer" class="btn btn-sm btn-primary" onclick="window.HRModule.savePersonReviewReminder('${employeeId}')" style="font-size:0.78rem;padding:5px 12px;">
          <span class="material-icons" style="font-size:14px;">save</span> Save reminder
        </button>
      </div>
    </div>
  `;
  const reviewsHtml = reviewReminderCard + (reviews.length > 0
    ? reviews.map(r => {
        const date = r.createdAt?.toDate?.() || new Date(r.createdAt);
        const scoreClass = r.totalScore >= 80 ? 'good' : r.totalScore >= 60 ? 'warning' : 'poor';
        return `
          <div class="hr-detail-item" onclick="window.HRModule.viewReview('${r.id}')">
            <div class="hr-detail-item-icon ${scoreClass}">
              <span class="material-icons">rate_review</span>
            </div>
            <div class="hr-detail-item-info">
              <div class="hr-detail-item-title">${escapeHtml(getPositionTitle(r.positionId))}${_hrReviewIsLocked(r) ? ` <span class="material-icons" title="Submitted — locked as a final record" style="font-size:13px;vertical-align:-2px;color:var(--text-secondary);">lock</span>` : ''}${_hrReviewIsVoided(r) ? ` <span title="${_hrReviewVoidTooltip(r)}" style="font-size:0.62rem;font-weight:700;letter-spacing:0.04em;background:#f1f5f9;color:#475569;border-radius:4px;padding:1px 5px;vertical-align:1px;">VOID</span>` : ''}</div>
              <div class="hr-detail-item-meta">${formatDate(date)} • Score: ${r.totalScore?.toFixed(1) || 0}%${_hrReviewIsVoided(r) && r.voidReason ? ` • Voided: ${escapeHtml(String(r.voidReason))}` : ''}</div>
            </div>
            ${/* TASK-4776 — a DRAFT is deletable (owner only, unchanged). A LOCKED
                  review is not deletable by anyone any more, so the button becomes
                  Void (owner/admin, matching the rules gate). An already-voided
                  review offers neither — a void is one-way. Never a `disabled`
                  button: on a tablet a disabled button eats the tap and its
                  tooltip never fires (the TASK-4760 Edit-button finding). */ ''}
            ${_hrReviewIsVoided(r)
              ? ''
              : _hrReviewIsLocked(r)
                ? (_hrIsOwnerOrAdmin() ? `<button class="hr-detail-item-delete" onclick="event.stopPropagation(); window.HRModule.voidReview('${r.id}', '${employeeId}')" title="Void this submitted review (the record is kept)">
              <span class="material-icons">block</span>
            </button>` : '')
                : (_hrIsOwner() ? `<button class="hr-detail-item-delete" onclick="event.stopPropagation(); window.HRModule.deleteReview('${r.id}', '${employeeId}')" title="Delete this draft review">
              <span class="material-icons">delete_outline</span>
            </button>` : '')}
          </div>
        `;
      }).join('')
    : '<div class="empty-state-small">No reviews yet</div>');

  const formsHtml = forms.length > 0
    ? forms.map(f => {
        const date = f.createdAt?.toDate?.() || new Date(f.createdAt);
        const typeConfig = WARNING_TYPES[f.warningType];
        return `
          <div class="hr-detail-item" onclick="window.HRModule.viewDisciplinaryForm('${f.id}')">
            <div class="hr-detail-item-icon ${f.warningType}">
              <span class="material-icons">${typeConfig?.icon || 'description'}</span>
            </div>
            <div class="hr-detail-item-info">
              <div class="hr-detail-item-title">${escapeHtml(typeConfig?.label || f.warningType)}</div>
              <div class="hr-detail-item-meta">${formatDate(date)}</div>
            </div>
            ${_hrIsOwner() ? `<button class="hr-detail-item-delete" onclick="event.stopPropagation(); window.HRModule.deleteDisciplinaryForm('${f.id}', '${employeeId}')" title="Delete form">
              <span class="material-icons">delete_outline</span>
            </button>` : ''}
          </div>
        `;
      }).join('')
    : '<div class="empty-state-small">No forms on file</div>';

  const modalHtml = `
    <div class="modal-header">
      <h3><span class="material-icons">person</span> Employee Details</h3>
      <button class="modal-close" onclick="window.HRModule.closeModal('employee-detail')">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-body">
      ${buildEmployeeDetailHeaderHTML(emp)}

      <div class="hr-detail-tabs">
        <button class="hr-detail-tab active" onclick="window.HRModule.switchDetailTab('reviews')">
          <span class="material-icons">rate_review</span> Reviews (${reviews.length})
        </button>
        ${(window.hrCanManageDisciplinary ? window.hrCanManageDisciplinary() : true) ? `
        <button class="hr-detail-tab" id="hr-docs-tab-btn" onclick="window.HRModule.switchDetailTab('forms')">
          <span class="material-icons">gavel</span> Disciplinary (${forms.length})
        </button>
        ` : ''}
        <button class="hr-detail-tab" onclick="window.HRModule.switchDetailTab('timeoff-balance')">
          <span class="material-icons">event_available</span> Time Off
        </button>
        <button class="hr-detail-tab" onclick="window.HRModule.switchDetailTab('employment')">
          <span class="material-icons">work</span> Employment
        </button>
        <button class="hr-detail-tab" onclick="window.HRModule.switchDetailTab('compliance')">
          <span class="material-icons">shield</span> Compliance
        </button>
        <button class="hr-detail-tab" onclick="window.HRModule.switchDetailTab('signatures')">
          <span class="material-icons">draw</span> Signatures
        </button>
        <button class="hr-detail-tab" id="hr-documents-tab-btn" onclick="window.HRModule.switchDetailTab('documents')">
          <span class="material-icons">folder</span> Documents
        </button>
        ${_hrCanSeeSecureTax() ? `
        <button class="hr-detail-tab" onclick="window.HRModule.switchDetailTab('tax')">
          <span class="material-icons">receipt_long</span> Tax
        </button>
        ` : ''}
        <button class="hr-detail-tab" onclick="window.HRModule.switchDetailTab('activity')">
          <span class="material-icons">timeline</span> Activity
        </button>
      </div>

      <div id="hr-detail-tab-reviews" class="hr-detail-tab-content">
        ${reviewsHtml}
      </div>
      <div id="hr-detail-tab-forms" class="hr-detail-tab-content" style="display: none;">
        ${(window.hrCanManageDisciplinary ? window.hrCanManageDisciplinary() : true) ? formsHtml : ''}
      </div>
      <div id="hr-detail-tab-timeoff-balance" class="hr-detail-tab-content" style="display: none;">
        <div id="hr-timeoff-balance-content"><div class="empty-state-small">Loading...</div></div>
      </div>
      <div id="hr-detail-tab-employment" class="hr-detail-tab-content" style="display: none;">
        ${buildEmploymentTabHTML(emp)}
      </div>
      <div id="hr-detail-tab-compliance" class="hr-detail-tab-content" style="display: none;">
        ${secureLoaded ? buildComplianceTabHTML(emp) : _secureTabLoadingHTML()}
      </div>
      <div id="hr-detail-tab-signatures" class="hr-detail-tab-content" style="display: none;">
        ${buildSignaturesTabHTML(emp)}
      </div>
      <div id="hr-detail-tab-documents" class="hr-detail-tab-content" style="display: none;">
        ${buildDocumentsTabHTML(emp)}
      </div>
      <div id="hr-detail-tab-tax" class="hr-detail-tab-content" style="display: none;">
        ${_hrCanSeeSecureTax() ? (secureLoaded ? buildTaxTabHTML(emp) : _secureTabLoadingHTML()) : ''}
      </div>
      <div id="hr-detail-tab-activity" class="hr-detail-tab-content" style="display: none;">
        <div id="hr-activity-content"><div class="empty-state-small">Loading activity...</div></div>
      </div>

      <!-- TASK-346 Slice 3 — structured notes feed (hrNotes subcollection:
           {body, createdAt, authorUid, authorName, observedAt}). TASK-464
           2026-06-04 — RELOCATED below the tabs (a long feed used to push the
           Reviews/Disciplinary/Compliance tabs off-screen) + made the historical
           feed COLLAPSIBLE (clickable "Notes (N)" header; per-PERSON persisted
           state; default expanded ≤5 / collapsed >5). Composer + print stay
           ALWAYS visible — only the feed list collapses. -->
      <div class="hr-employee-notes" id="hr-employee-notes-section" data-person-id="${emp.id}" style="margin-top:var(--spacing-md);border-top:1px solid var(--border-light);padding-top:var(--spacing-md);">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-xs);">
          <button type="button" onclick="window.HRModule.toggleEmployeeNotesSection('${emp.id}')" title="Show or hide the notes history" style="display:flex;align-items:center;gap:6px;background:none;border:none;cursor:pointer;padding:0;font-weight:600;font-size:0.875rem;color:var(--text-primary);">
            <span class="material-icons" style="font-size:16px;">note</span> Notes
            <span id="hr-emp-notes-count-chip" style="display:none;background:var(--bg-secondary,#eef2f7);color:var(--text-secondary);font-size:0.72rem;font-weight:700;padding:1px 8px;border-radius:10px;">0</span>
            <span class="material-icons" id="chevron-emp-notes" style="font-size:18px;color:var(--text-secondary);">expand_less</span>
          </button>
          <button class="btn btn-sm btn-secondary" id="btn-print-emp-notes" onclick="window.HRModule.printEmployeeNotes('${emp.id}')" title="Print notes" style="padding:4px 8px;">
            <span class="material-icons" style="font-size:16px;">print</span>
          </button>
        </div>
        <div style="display:flex;flex-direction:column;gap:var(--spacing-xs);margin-bottom:var(--spacing-sm);">
          <textarea id="hr-employee-note-composer" rows="2" placeholder="Add a note... (year-end review reminder, conversation, observation)" style="width:100%;resize:vertical;font-size:0.875rem;padding:var(--spacing-sm);border:1px solid var(--border-light);border-radius:var(--radius-md);font-family:inherit;background:var(--bg-primary);color:var(--text-primary);" maxlength="4000"></textarea>
          <div style="display:flex;align-items:center;justify-content:space-between;gap:var(--spacing-xs);flex-wrap:wrap;">
            <label for="hr-employee-note-observed-date" style="display:flex;align-items:center;gap:4px;font-size:0.78rem;color:var(--text-secondary);margin:0;">
              <span class="material-icons" style="font-size:14px;">event</span> Observed date
              <input type="date" id="hr-employee-note-observed-date" class="control-auto" value="${_todayYmd()}" max="${_maxYmd()}" min="${_minYmd()}" style="font-size:0.78rem;padding:2px 6px;border:1px solid var(--border-light);border-radius:var(--radius-sm);background:var(--bg-primary);color:var(--text-primary);" />
            </label>
            <div style="display:flex;align-items:center;gap:6px;">
              <input type="file" id="hr-emp-note-attach-input" accept="${HR_NOTE_ATTACH_ACCEPT}" style="display:none;" onchange="window.HRModule._onHrNoteAttachPick('detail')" />
              <button type="button" class="btn btn-sm btn-secondary" id="btn-attach-emp-note" onclick="document.getElementById('hr-emp-note-attach-input').click()" title="Attach a photo or PDF" style="padding:4px 10px;">
                <span class="material-icons" style="font-size:16px;">attach_file</span>
              </button>
              <button class="btn btn-sm btn-primary" id="btn-add-emp-note" onclick="window.HRModule.addEmployeeNote('${emp.id}')" style="padding:4px 12px;">
                <span class="material-icons" style="font-size:16px;">add</span> Add note
              </button>
            </div>
          </div>
          <div id="hr-emp-note-attach-chip" style="display:none;align-items:center;gap:6px;font-size:0.75rem;color:var(--text-secondary);"></div>
          <div style="font-size:0.7rem;color:var(--text-secondary);display:flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:13px;color:#d97706;">warning</span>
            <span>${HR_NOTE_ATTACH_DA_WARNING}</span>
          </div>
        </div>
        <div id="hr-employee-notes-feed" style="display:flex;flex-direction:column;gap:var(--spacing-xs);">
          <div class="empty-state-small" style="color:var(--text-secondary);font-size:0.85rem;">Loading notes...</div>
        </div>
      </div>
    </div>
  `;

  let modal = document.getElementById('employee-detail-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'employee-detail-modal';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `<div class="modal-content modal-lg">${modalHtml}</div>`;
  modal.classList.remove('hidden');

  // TASK-1207 — fill the flag-reason callout (owner/admin only; fire-and-forget,
  // never blocks the modal).
  _hydrateHrFlagReason(emp);

  // Eagerly load uploaded documents so badge count is correct immediately
  loadEmployeeDocuments(employeeId);

  // TASK-346 Slice 3 — load the structured notes feed (was a single string
  // field). Async; UI shows "Loading notes..." until first render.
  loadEmployeeNotesFeed(employeeId);

  // TASK-1550 — the signed-in user's own reminders on this person, under the
  // notes feed (window.Reminders self-gates: person = roster | hr module).
  window.Reminders?.mountAppend(document.getElementById('hr-employee-notes-feed')?.parentElement, {
    type: 'person', id: employeeId, label: emp.name || '',
  });
}

function switchDetailTab(tabName) {
  document.querySelectorAll('.hr-detail-tab').forEach(t => t.classList.remove('active'));
  document.querySelectorAll('.hr-detail-tab-content').forEach(c => c.style.display = 'none');

  document.querySelector(`.hr-detail-tab[onclick*="${tabName}"]`)?.classList.add('active');
  document.getElementById(`hr-detail-tab-${tabName}`).style.display = 'block';

  // Lazy-load tab data
  const emp = HRState.selectedEmployee;
  if (emp) {
    if (tabName === 'forms') { /* disciplinary forms are already rendered */ }
    if (tabName === 'timeoff-balance') loadTimeOffBalance(emp.id);
    // TASK-1147 — Tax + Compliance hydrate the admin-only secure reads
    // (peopleTax + peopleBackgroundChecks) on first open instead of blocking
    // the modal's first paint. A fast tab-click lands on the tab's own loading
    // placeholder until the fetch resolves — the builders never run with
    // emp.secureTax / emp.bgCheck undefined.
    if (tabName === 'compliance') { _ensureSecureDetailTabs(emp); loadEmployeeCitations(emp.id); }
    if (tabName === 'tax') _ensureSecureDetailTabs(emp);
    if (tabName === 'signatures') loadPendingSignDocs(emp.id);
    if (tabName === 'documents') loadEmployeeDocuments(emp.id);
    if (tabName === 'activity') loadEmployeeActivity(emp.id);
  }
}

// ============================================
// TASK-1147 — LAZY SECURE-TAB HYDRATION (peopleTax + peopleBackgroundChecks)
// ============================================
// openEmployeeDetail used to await BOTH admin-only reads before painting the
// modal even though only the Tax + Compliance tabs consume them. Those tabs now
// render a loading placeholder and hydrate on first open. Known residual (pre-
// existing, noted in TASK-1147): the emp.secureTax/bgCheck memo lives on the
// people-array object, which every people-onSnapshot replaces — a snapshot
// while the modal is open discards the memo for the NEXT open (it re-fetches).

let _secureDetailHydration = null; // { empId } — dedupes a fast double tab-click

function _secureTabLoadingHTML() {
  return `<div data-secure-tab-loading="1" style="display:flex;align-items:center;justify-content:center;gap:10px;padding:32px 0;color:var(--text-secondary);font-size:0.85rem;">
    <div class="loading-spinner" style="width:20px;height:20px;"></div> Loading secure records...
  </div>`;
}

function _secureTabsPending() {
  const modal = document.getElementById('employee-detail-modal');
  return !!(modal && modal.querySelector('[data-secure-tab-loading]'));
}

function _renderSecureDetailTabs(emp) {
  const complianceEl = document.getElementById('hr-detail-tab-compliance');
  if (complianceEl) {
    complianceEl.innerHTML = buildComplianceTabHTML(emp);
    // The citations list lives INSIDE the compliance tab — a pre-hydration
    // loadEmployeeCitations call no-ops (its container didn't exist yet), so
    // re-run it against the freshly built tab when it's the visible one.
    if (complianceEl.style.display !== 'none') loadEmployeeCitations(emp.id);
  }
  const taxEl = document.getElementById('hr-detail-tab-tax');
  if (taxEl && _hrCanSeeSecureTax()) taxEl.innerHTML = buildTaxTabHTML(emp);
}

function _ensureSecureDetailTabs(emp) {
  if (!emp) return;
  if (emp.secureTax !== undefined && emp.bgCheck !== undefined) {
    // Already memoized (modal re-open, or another flow — e.g. the BG-report
    // viewer — loaded them). Only rebuild if the tabs still show the loading
    // placeholder; otherwise the real content was built at modal open.
    if (_secureTabsPending()) _renderSecureDetailTabs(emp);
    return;
  }
  if (_secureDetailHydration && _secureDetailHydration.empId === emp.id) return; // fetch in flight
  _secureDetailHydration = { empId: emp.id };
  Promise.all([ensureSecureTax(emp), ensureBgCheck(emp)])
    .then(() => {
      if (HRState.selectedEmployee?.id === emp.id) _renderSecureDetailTabs(emp);
    })
    .catch((err) => {
      // ensureSecureTax/ensureBgCheck swallow read errors internally (return
      // {}), so this is belt-and-suspenders — render with what we have rather
      // than leaving the spinner up forever.
      console.warn('[HR] Secure detail hydration failed:', err?.message || err);
      if (emp.secureTax === undefined) emp.secureTax = {};
      if (emp.bgCheck === undefined) emp.bgCheck = {};
      if (HRState.selectedEmployee?.id === emp.id) _renderSecureDetailTabs(emp);
    })
    .finally(() => {
      if (_secureDetailHydration && _secureDetailHydration.empId === emp.id) _secureDetailHydration = null;
    });
}

// ============================================
// EMPLOYEE DOCUMENT UPLOADS
// ============================================

function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

async function loadEmployeeDocuments(personId) {
  if (HRState.employeeDocsLoaded === personId) {
    renderEmployeeDocuments();
    return;
  }
  const container = document.getElementById('hr-employee-docs-list');
  if (container) container.innerHTML = '<div class="empty-state-small">Loading files...</div>';

  try {
    const tenantId = HRState.tenantId;
    const snap = await getDocs(
      query(
        collection(db, 'tenants', tenantId, 'people', personId, 'documents'),
        orderBy('uploadedAt', 'desc')
      )
    );
    HRState.employeeDocuments = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    HRState.employeeDocsLoaded = personId;
    renderEmployeeDocuments();
  } catch (err) {
    console.error('Failed to load employee documents:', err);
    if (container) container.innerHTML = '<div class="empty-state-small">Failed to load files</div>';
  }
}

function updateDetailTabCounts() {
  const formsCount = (HRState.hrForms || [])
    .filter(f => f.employeeId === HRState.employeeDocsLoaded).length;
  const docsCount = (HRState.employeeDocuments || []).length;
  const formsBtn = document.getElementById('hr-docs-tab-btn');
  if (formsBtn) {
    formsBtn.innerHTML = `<span class="material-icons">gavel</span> Disciplinary (${formsCount})`;
  }
  const docsBtn = document.getElementById('hr-documents-tab-btn');
  if (docsBtn) {
    docsBtn.innerHTML = `<span class="material-icons">folder</span> Documents (${docsCount})`;
  }
}

// TASK-970 — Fixed regulatory document taxonomy for the carrier driver-file
// browser (mirrors Crown's partner-side file browser). Every uploaded doc is
// grouped under a fixed DOT/OSHA category, in a stable regulatory order, with a
// citation + (where the category has a person-level regulatory date) a per-
// category expiry chip. Any doc whose `category` is missing / legacy / unknown
// falls back to Miscellaneous so nothing is EVER dropped. `expiryFields` names
// the people/{id} field(s) that date this category (first non-empty wins).
// Colors/short labels preserved from the prior CATEGORY_BADGE map.
const DQ_DOC_CATEGORIES = [
  { key: 'cdl',                  label: 'CDL / License',                  badgeLabel: 'CDL',           cite: '49 CFR 391.11',  icon: 'badge',            bg: '#DBEAFE', color: '#1E40AF', expiryFields: ['cdlExpiryDate', 'dlExpiryDate'] },
  { key: 'mec',                  label: "Medical Examiner's Certificate", badgeLabel: 'MEC',           cite: '49 CFR 391.41',  icon: 'medical_services', bg: '#FCE7F3', color: '#9D174D', expiryFields: ['medCardExpiryDate'] },
  { key: 'mvr',                  label: 'Motor Vehicle Record',           badgeLabel: 'MVR',           cite: '49 CFR 391.25',  icon: 'directions_car',   bg: '#FEF3C7', color: '#92400E', expiryFields: ['mvrNextDueDate'] },
  { key: 'mvr_annual_review',    label: 'Annual MVR Review',              badgeLabel: 'Annual MVR',    cite: '49 CFR 391.25',  icon: 'fact_check',       bg: '#FED7AA', color: '#9A3412', expiryFields: ['annualReviewNextDue'] },
  { key: 'employment_app',       label: 'Employment Application',         badgeLabel: 'Application',   cite: '49 CFR 391.21',  icon: 'assignment_ind',   bg: '#DCFCE7', color: '#166534', expiryFields: [] },
  { key: 'road_test',            label: 'Road Test / Certificate',        badgeLabel: 'Road Test',     cite: '49 CFR 391.31',  icon: 'drive_eta',        bg: '#CFFAFE', color: '#155E75', expiryFields: [] },
  { key: 'prev_employer_safety', label: 'Prev. Employer Safety History',  badgeLabel: 'Prev Employer', cite: '49 CFR 391.23',  icon: 'history',          bg: '#E0E7FF', color: '#3730A3', expiryFields: [] },
  { key: 'training',             label: 'Training Certificates',          badgeLabel: 'Training',      cite: 'Training',       icon: 'school',           bg: '#FAE8FF', color: '#86198F', expiryFields: [] },
  { key: 'drug_alcohol',         label: 'Drug / Alcohol Records',         badgeLabel: 'D&A',           cite: '49 CFR 382',     icon: 'science',          bg: '#FECACA', color: '#7F1D1D', expiryFields: [] },
  { key: 'clearinghouse',        label: 'Clearinghouse Query',            badgeLabel: 'Clearinghouse', cite: '49 CFR 382',     icon: 'verified_user',    bg: '#EDE9FE', color: '#5B21B6', expiryFields: [] },
  { key: 'on_duty_hours',        label: '7-Day On-Duty Statement',        badgeLabel: '7-Day HOS',     cite: '49 CFR 395.8(j)',icon: 'schedule',         bg: '#CCFBF1', color: '#115E59', expiryFields: [] },
  // TASK-1623 — the DRIVER's signed notification of conviction. 🔴 NOT the same
  // record as 'mvr_annual_review': that is the CARRIER's §391.25(c)(2) review
  // note, a different regulation with a different signer, and only the review
  // note is a §391.51(b)(5) DQ-file requirement. Without this row the filed
  // record resolved to '__misc__' and was therefore INVISIBLE on the compliance
  // surface (DQ_FILE_CATEGORY_KEYS excludes misc).
  //
  // Intended to be SERVER-AUTHORED: the only writer is submitConvictionNotice,
  // after a driver e-signs a tokenized link. It is deliberately absent from the
  // retag cycle (:10062), the upload picker allowlist (:10176), the compliance
  // picker, and the firestore.rules compliance-module create allowlist.
  // ⚠️ HONEST LIMIT (COD Adversary): that is a convention, not yet a boundary.
  // The `documents` create rule's isTenantAdmin / hasModule('hr') /
  // isComplianceManager legs (firestore.rules:2307) carry NO category fence at
  // all, so an admin or HR member can still client-write a doc claiming this
  // category with no signature behind it. That gap is PRE-EXISTING and applies
  // equally to every DQ category today — closing it means fencing those legs in
  // firestore.rules, which is dispatcher-owned. Do not read this comment as a
  // guarantee the category cannot be forged.
  { key: 'conviction_notice',    label: 'Conviction Notification',        badgeLabel: 'Conviction',    cite: '49 CFR 383.31',  icon: 'gavel',            bg: '#FFEDD5', color: '#9A3412', expiryFields: [] },
  { key: 'medical',              label: 'Medical Records',                badgeLabel: 'Medical',       cite: 'OSHA 1910.1020', icon: 'local_hospital',   bg: '#FEE2E2', color: '#991B1B', expiryFields: [] },
  { key: 'exposure',             label: 'Exposure Records',               badgeLabel: 'Exposure',      cite: 'OSHA 1910.1020', icon: 'coronavirus',      bg: '#FEF3C7', color: '#92400E', expiryFields: [] },
  // TASK-1458 — "Other" from the partner upload picker (Crown: "hopefully many
  // kinds"). A DQ-file document that fits no fixed key; the row carries the
  // uploader's own label in `categoryLabel`, rendered on the row below. Part of
  // DQ_FILE_CATEGORY_KEYS so the compliance surface shows it; NOT in the retag
  // cycle or the compliance picker (it only arrives via the server upload).
  { key: 'other',                label: 'Other DQ Documents',             badgeLabel: 'Other',         cite: 'Labeled by uploader', icon: 'note_add',     bg: '#E2E8F0', color: '#334155', expiryFields: [] },
  { key: 'general',              label: 'General',                        badgeLabel: 'General',       cite: '',               icon: 'description',      bg: '#F1F5F9', color: '#475569', expiryFields: [] },
  { key: '__misc__',             label: 'Miscellaneous',                  badgeLabel: 'Misc',          cite: 'Legacy / untyped', icon: 'folder_open',    bg: '#F1F5F9', color: '#64748B', expiryFields: [] },
];
const DQ_DOC_CATEGORY_KEYS = new Set(DQ_DOC_CATEGORIES.filter(c => c.key !== '__misc__').map(c => c.key));

// Driver-qualification-file categories only (FMCSA 391.x / 49 CFR 382). Used to
// scope the COMPLIANCE-side browser so HR onboarding/OSHA-medical docs (I-9/W-4/
// direct-deposit = category 'general'; 'medical'/'exposure'; and untyped/legacy
// '__misc__') are NOT surfaced to a compliance-module viewer who may not be an
// hr-directory/secure-tax user (COD Code-Reviewer PII-leak flag, TASK-970). The
// HR surface still shows every category incl. Miscellaneous (nothing dropped).
const DQ_FILE_CATEGORY_KEYS = new Set([
  'cdl', 'mec', 'mvr', 'mvr_annual_review', 'employment_app',
  'road_test', 'prev_employer_safety', 'training', 'drug_alcohol', 'clearinghouse', 'on_duty_hours',
  'conviction_notice', // TASK-1623 — driver-signed §383.31 notice; server-authored, see above
  'other',             // TASK-1458 — partner "Other: <label>" upload; server-authored, see above
]);

// Resolve a doc's stored category to a known taxonomy key, else Miscellaneous.
function _resolveDqDocCategory(cat) {
  const k = (cat || '').trim();
  return DQ_DOC_CATEGORY_KEYS.has(k) ? k : '__misc__';
}

// Attribute-safe escape (escapeHtml does NOT encode quotes — insufficient for an
// HTML attribute). Used for data-doc-url so a poisoned documents.url can't break
// out of the attribute.
function _dqEscAttr(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Only http(s) URLs are openable — blocks javascript:/data: and any non-URL
// value written directly to documents.url via the client SDK.
function _dqSafeHttpUrl(url) {
  const u = String(url == null ? '' : url).trim();
  return /^https?:\/\//i.test(u) ? u : '';
}

// TASK-970 — open a doc from its data attributes (re-validated). Replaces
// the old inline onclick="window.open('<url>')" which string-interpolated the
// stored url into a JS string — a single quote in a poisoned documents.url broke
// out and executed (COD Adversary 🔴). No URL ever reaches a JS string now.
// TASK-1029 — path-first: documents rows no longer persist a getDownloadURL
// bearer `url`; when the row carries a storagePath we open it through the
// HR-gated getComplianceDocBytes byte-stream (viewComplianceDoc). The
// data-doc-url leg survives ONLY for legacy rows the backfill couldn't resolve
// (url present, no extractable path) — viewComplianceDoc's rule applies here
// too: legacy url is never a fallback for a callable DENIAL, only for a row
// with no path at all.
function openDqDoc(el) {
  try {
    const path = (el && el.getAttribute ? el.getAttribute('data-doc-path') : '') || '';
    if (path) {
      const name = (el.getAttribute('data-doc-name') || '').trim();
      viewComplianceDoc(path, null, name || 'document');
      return;
    }
    const raw = el && el.getAttribute ? el.getAttribute('data-doc-url') : '';
    const safe = _dqSafeHttpUrl(raw);
    if (safe) window.open(safe, '_blank', 'noopener');
  } catch (_e) {}
}

// Category-level regulatory expiry chip (med card / CDL / MVR next-due / annual
// review). Reads the person-doc field, formats via formatDateShort, colors by
// expired / ≤30-day / valid. Empty when the category has no dated field or the
// person has no value.
function _dqCategoryExpiryChip(emp, cat) {
  if (!emp || !cat || !(cat.expiryFields || []).length) return '';
  let val = '';
  for (const f of cat.expiryFields) { if (emp[f]) { val = emp[f]; break; } }
  if (!val) return '';
  const iso = String(val).slice(0, 10);
  const today = new Date().toISOString().split('T')[0];
  const soonStr = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
  const expired = !!iso && iso < today;
  const soon = !expired && !!iso && iso <= soonStr;
  const bg = expired ? '#FEE2E2' : soon ? '#FEF3C7' : '#DCFCE7';
  const color = expired ? '#991B1B' : soon ? '#92400E' : '#166534';
  return `<span title="Category expiry" style="margin-left:auto;font-size:0.68rem;font-weight:700;color:${color};background:${bg};padding:1px 8px;border-radius:10px;white-space:nowrap;">${expired ? 'Expired' : 'Expires'} ${escapeHtml(formatDateShort(val) || iso)}</span>`;
}

// One file row. `interactive` (HR detail) adds re-tag + delete; read-only
// (compliance driver detail) shows a static badge + View-on-click only.
function _renderDqDocRow(d, opts) {
  opts = opts || {};
  const meta = DQ_DOC_CATEGORIES.find(c => c.key === _resolveDqDocCategory(d.category)) || DQ_DOC_CATEGORIES[DQ_DOC_CATEGORIES.length - 1];
  const date = d.uploadedAt?.toDate?.() ? d.uploadedAt.toDate() : (d.uploadedAt ? new Date(d.uploadedAt) : new Date());
  const dateStr = isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const sizeStr = formatFileSize(d.sizeBytes || 0);
  const isPdf = d.isPdf || d.mimeType === 'application/pdf';
  const icon = isPdf ? 'picture_as_pdf' : 'image';
  const iconBg = isPdf ? '#fef3c7' : '#dbeafe';
  const iconColor = isPdf ? '#92400e' : '#1d4ed8';
  // Per-file expiry — some uploaded docs carry their own expiry date.
  const fileExpiry = d.expiresAt || d.expiryDate || d.expiresDate || '';
  let expiryHtml = '';
  if (fileExpiry) {
    const iso = String(fileExpiry).slice(0, 10);
    const today = new Date().toISOString().split('T')[0];
    const expired = !!iso && iso < today;
    expiryHtml = `<span style="font-size:0.68rem;font-weight:600;color:${expired ? '#DC2626' : '#047857'};">${expired ? 'Expired ' : 'Expires '}${escapeHtml(formatDateShort(fileExpiry) || iso)}</span>`;
  }
  const badgeTitle = `${meta.label}${meta.cite ? ' (' + meta.cite + ')' : ''}`;
  // TASK-1458 — a doc filed through the partner/category upload carries the
  // picker's own label (e.g. "Road test certificate / equivalent", or
  // "Other: <what the uploader typed>") and who filed it. Escaped text only.
  const catLabelHtml = (typeof d.categoryLabel === 'string' && d.categoryLabel.trim())
    ? `<div style="font-size:0.72rem;font-weight:600;color:var(--text-primary);">${escapeHtml(d.categoryLabel.trim().slice(0, 100))}</div>`
    : '';
  const byHtml = (d.source === 'partner' && d.uploadedBy)
    ? ` &middot; by ${escapeHtml(String(d.uploadedBy).slice(0, 120))} (compliance partner)`
    : '';
  const badge = opts.interactive
    ? `<button onclick="event.stopPropagation(); window.HRModule.retagEmployeeDocument('${opts.personId}', '${d.id}')" title="${escapeHtml(badgeTitle)} — click to re-tag" style="background:${meta.bg};color:${meta.color};border:none;border-radius:10px;padding:2px 8px;font-size:0.7rem;font-weight:600;cursor:pointer;margin-right:6px;">${escapeHtml(meta.badgeLabel)}</button>`
    : `<span title="${escapeHtml(badgeTitle)}" style="background:${meta.bg};color:${meta.color};border-radius:10px;padding:2px 8px;font-size:0.7rem;font-weight:600;margin-right:6px;">${escapeHtml(meta.badgeLabel)}</span>`;
  // TASK-1277 — rename the DISPLAY name only (fileName). The stored object +
  // storagePath are never touched (renaming Storage would break the TASK-1029
  // path-first byte-stream). The clean name updates the on-screen Uploaded Files
  // list; the row still carries data-doc-name for the viewer (viewComplianceDoc
  // opens in a tab and doesn't apply a download filename today).
  const rename = opts.interactive
    ? `<button class="hr-detail-item-rename" onclick="event.stopPropagation(); window.HRModule.renameEmployeeDocument('${escJsAttr(opts.personId)}', '${escJsAttr(d.id)}')" title="Rename file"><span class="material-icons">edit</span></button>`
    : '';
  // SEC-FOLLOWUPS-2 — deleteHrPersonDocument has no partner arm (owner / hr /
  // compliance module only), so a managing-partner session gets no Delete button.
  const _partnerSess = typeof window !== 'undefined' && typeof window.isPartnerClientSession === 'function' && window.isPartnerClientSession();
  const del = (opts.interactive && !_partnerSess)
    ? `<button class="hr-detail-item-delete" onclick="event.stopPropagation(); window.HRModule.deleteEmployeeDocument('${escJsAttr(opts.personId)}', '${escJsAttr(d.id)}')" title="Delete file"><span class="material-icons">delete_outline</span></button>`
    : '';
  // XSS-safe open: scheme-validated URL in a data attribute + delegated handler.
  // Never interpolate the stored url into a JS string (COD Adversary 🔴).
  // TASK-1029 — path-first: prefer storagePath → the HR-gated byte-stream
  // callable (no bearer URL). data-doc-url is legacy-only (rows the backfill
  // couldn't resolve to a path).
  const safePath = (typeof d.storagePath === 'string' && d.storagePath.trim() &&
    !d.storagePath.includes('..')) ? d.storagePath.trim() : '';
  const safeUrl = _dqSafeHttpUrl(d.url);
  const openAttrs = safePath
    ? `data-doc-path="${_dqEscAttr(safePath)}" data-doc-name="${_dqEscAttr(d.fileName || '')}" onclick="window.HRModule.openDqDoc(this)" style="cursor:pointer;"`
    : (safeUrl
      ? `data-doc-url="${_dqEscAttr(safeUrl)}" onclick="window.HRModule.openDqDoc(this)" style="cursor:pointer;"`
      : `style="cursor:default;opacity:0.7;"`);
  return `
      <div class="hr-detail-item" ${openAttrs}>
        <div class="hr-detail-item-icon" style="background:${iconBg};color:${iconColor};">
          <span class="material-icons">${icon}</span>
        </div>
        <div class="hr-detail-item-info">
          <div class="hr-detail-item-title" style="color:var(--text-primary);">${escapeHtml(d.fileName || 'Document')}</div>
          ${catLabelHtml}
          <div class="hr-detail-item-meta" style="color:var(--text-secondary);">${dateStr}${sizeStr ? ' &middot; ' + sizeStr : ''}${byHtml}${expiryHtml ? ' &middot; ' : ''}${expiryHtml}</div>
        </div>
        ${badge}
        ${rename}
        ${del}
      </div>
    `;
}

// Group docs into fixed regulatory categories (Miscellaneous fallback), render
// each present category in the canonical order with a header (icon/label/cite/
// count + category expiry chip) and an anchor, plus a Jump-to-category dropdown.
// TASK-1100 — per-category collapse for the DQ/compliance document sections
// (shared by the HR Documents tab + the compliance driver detail). State persists
// per tenant + category in localStorage; default is EXPANDED (preserves prior
// behavior) so nothing hides unexpectedly. Keyed by category only (not idPrefix)
// so a preference is consistent across both surfaces.
function _dqDocCollapseKey(catKey) {
  return `dd.hr.dqDocs.collapsed.${HRState.tenantId || 'default'}.${catKey}`;
}
function _readDqDocCollapsed(catKey) {
  try { return localStorage.getItem(_dqDocCollapseKey(catKey)) === '1'; } catch (_) { return false; }
}
function _writeDqDocCollapsed(catKey, collapsed) {
  try { localStorage.setItem(_dqDocCollapseKey(catKey), collapsed ? '1' : '0'); } catch (_) {}
}
function toggleDqDocSection(catKey, prefix) {
  const body = document.getElementById(`dqdoc-body-${prefix}-${catKey}`);
  const chev = document.getElementById(`dqdoc-chevron-${prefix}-${catKey}`);
  if (!body) return;
  const willCollapse = body.style.display !== 'none';
  body.style.display = willCollapse ? 'none' : 'block';
  if (chev) chev.style.transform = willCollapse ? 'rotate(-90deg)' : 'rotate(0deg)';
  _writeDqDocCollapsed(catKey, willCollapse);
}
// Jump-to-category: scroll to the section AND expand it if the user had it collapsed
// (so the jump target is never an empty header).
function jumpToDqDocSection(catKey, prefix) {
  const anchor = document.getElementById(`dqdocs-${prefix}-${catKey}`);
  const body = document.getElementById(`dqdoc-body-${prefix}-${catKey}`);
  const chev = document.getElementById(`dqdoc-chevron-${prefix}-${catKey}`);
  if (body && body.style.display === 'none') {
    body.style.display = 'block';
    if (chev) chev.style.transform = 'rotate(0deg)';
    _writeDqDocCollapsed(catKey, false);
  }
  if (anchor) anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function _renderDqDocSections(docs, emp, opts) {
  opts = opts || {};
  const idPrefix = opts.idPrefix || 'hr';
  const allowedKeys = opts.allowedKeys || null; // when set, other categories are hidden
  const groups = new Map();
  for (const d of (docs || [])) {
    const k = _resolveDqDocCategory(d.category);
    if (allowedKeys && !allowedKeys.has(k)) continue; // scoped surface (compliance)
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(d);
  }
  const present = DQ_DOC_CATEGORIES.filter(c => groups.has(c.key));
  if (!present.length) {
    return `<div class="empty-state-small">${allowedKeys ? 'No driver-qualification documents on file.' : 'No files uploaded yet'}</div>`;
  }

  const jump = present.length > 1
    ? `<div style="margin-bottom:8px;">
        <select onchange="if(this.value){window.HRModule.jumpToDqDocSection(this.value,'${idPrefix}');this.selectedIndex=0;}" title="Jump to a document category" style="width:100%;padding:5px 8px;font-size:0.75rem;border:1px solid var(--border-light);border-radius:6px;background:#fff;color:var(--text-primary);">
          <option value="">Jump to category…</option>
          ${present.map(c => `<option value="${c.key}">${escapeHtml(c.label)} (${groups.get(c.key).length})</option>`).join('')}
        </select>
      </div>`
    : '';

  const sections = present.map(cat => {
    const items = groups.get(cat.key);
    const anchorId = `dqdocs-${idPrefix}-${cat.key}`;
    const expiryChip = _dqCategoryExpiryChip(emp, cat);
    const rows = items.map(d => _renderDqDocRow(d, { interactive: !!opts.interactive, personId: opts.personId })).join('');
    const collapsed = _readDqDocCollapsed(cat.key);
    // TASK-1100 — clickable header toggles the section; chevron rotates; state persists.
    return `
      <div id="${anchorId}" style="margin-bottom:10px;scroll-margin-top:8px;">
        <div onclick="window.HRModule.toggleDqDocSection('${cat.key}','${idPrefix}')" title="Collapse / expand"
             style="display:flex;align-items:center;gap:6px;margin-bottom:4px;flex-wrap:wrap;cursor:pointer;user-select:none;">
          <span class="material-icons" id="dqdoc-chevron-${idPrefix}-${cat.key}" style="font-size:18px;color:var(--text-muted);transition:transform .15s;transform:rotate(${collapsed ? '-90deg' : '0deg'});">expand_more</span>
          <span class="material-icons" style="font-size:16px;color:${cat.color};">${cat.icon}</span>
          <span style="font-size:0.8rem;font-weight:700;color:var(--text-primary);">${escapeHtml(cat.label)}</span>
          ${cat.cite ? `<span style="font-size:0.7rem;color:var(--text-secondary);">${escapeHtml(cat.cite)}</span>` : ''}
          <span style="font-size:0.68rem;color:var(--text-muted);">· ${items.length} file${items.length !== 1 ? 's' : ''}</span>
          ${expiryChip}
        </div>
        <div id="dqdoc-body-${idPrefix}-${cat.key}" style="display:${collapsed ? 'none' : 'block'};">
          ${rows}
        </div>
      </div>`;
  }).join('');

  return jump + sections;
}

function renderEmployeeDocuments() {
  const container = document.getElementById('hr-employee-docs-list');
  if (!container) return;

  updateDetailTabCounts();

  const docs = HRState.employeeDocuments || [];
  const emp = HRState.selectedEmployee
    || (HRState.people || []).find(p => p.id === HRState.employeeDocsLoaded)
    || null;
  container.innerHTML = _renderDqDocSections(docs, emp, {
    interactive: true,
    personId: HRState.employeeDocsLoaded,
    idPrefix: 'hr',
  });
}

// Read-only grouped browser for external surfaces (compliance driver detail).
// Same fixed taxonomy + Miscellaneous fallback + per-file expiry, no re-tag/
// delete. Exposed on window.HRModule so compliance.js can render it without a
// ?v= cross-import (Pitfall #1).
function renderDqDocBrowserHtml(docs, emp, opts) {
  opts = opts || {};
  return _renderDqDocSections(docs, emp, {
    interactive: false,
    idPrefix: 'cp',
    // Compliance passes dqOnly:true → scope to FMCSA/382 DQ-file categories only,
    // so HR onboarding/OSHA-medical/untyped docs aren't surfaced there.
    allowedKeys: opts.dqOnly ? DQ_FILE_CATEGORY_KEYS : null,
  });
}

async function retagEmployeeDocument(personId, docId) {
  const doc0 = (HRState.employeeDocuments || []).find(d => d.id === docId);
  if (!doc0) return;
  const current = doc0.category || 'general';
  // Cycle: DQF categories first (the common case for partners), then OSHA
  // retention buckets. Clicking the badge cycles forward through this list.
  const order = [
    'cdl', 'mec', 'mvr', 'mvr_annual_review', 'employment_app',
    'road_test', 'prev_employer_safety', 'training', 'drug_alcohol',
    'general', 'medical', 'exposure', 'clearinghouse', 'on_duty_hours',
    'other', // TASK-1458 (COD r2) — in the cycle so a partner "Other" row advances predictably and can come back
  ];
  const next = order[(order.indexOf(current) + 1) % order.length];
  try {
    const tenantId = HRState.tenantId;
    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId, 'documents', docId), {
      category: next,
      categoryUpdatedAt: Timestamp.now(),
      categoryUpdatedBy: window.AppState?.userName || 'Unknown',
    });
    HRState.employeeDocsLoaded = null;
    await loadEmployeeDocuments(personId);
    showToast(`Re-tagged as ${next}`, 'success');
  } catch (err) {
    console.error('Re-tag failed:', err);
    showToast('Re-tag failed. Try again.', 'error');
  }
}

// TASK-1277 (Chris dogfooding: "would be nice to rename these files") — rename an
// uploaded HR document's DISPLAY name. Metadata-only write to the documents record;
// the Storage object + storagePath are deliberately left alone (moving Storage would
// break the TASK-1029 path-first byte-stream + any legacy bearer URL). The clean name
// updates the on-screen Uploaded Files list (the display + re-tag/delete row).
async function renameEmployeeDocument(personId, docId) {
  const doc0 = (HRState.employeeDocuments || []).find(d => d.id === docId);
  if (!doc0) return;
  const currentName = doc0.fileName || 'Document';
  // Keep the extension so opens/downloads stay a valid file type — rename the base only.
  const dot = currentName.lastIndexOf('.');
  const ext = dot > 0 ? currentName.slice(dot) : '';
  const base = dot > 0 ? currentName.slice(0, dot) : currentName;
  const entered = window.prompt('Rename this document (display name only — the stored file is untouched):', base);
  if (entered === null) return; // cancelled
  // Strip characters illegal in a download filename; never allow a path separator.
  const cleanBase = entered.trim().replace(/[\\/:*?"<>|]/g, '').replace(/\s+/g, ' ').slice(0, 120);
  if (!cleanBase) { showToast('Name cannot be empty', 'error'); return; }
  const newName = cleanBase + ext;
  if (newName === currentName) return;
  try {
    const tenantId = HRState.tenantId;
    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId, 'documents', docId), {
      fileName: newName,
      nameUpdatedAt: Timestamp.now(),
      nameUpdatedBy: window.AppState?.userName || 'Unknown',
    });
    HRState.employeeDocsLoaded = null;
    await loadEmployeeDocuments(personId);
    showToast('Document renamed', 'success');
  } catch (err) {
    console.error('Rename failed:', err);
    showToast('Rename failed. Try again.', 'error');
  }
}

async function uploadEmployeeDocument(personId) {
  if (HRState.uploadingDocument) return;

  let input = document.getElementById('hr-emp-doc-file-input');
  if (!input) {
    input = document.createElement('input');
    input.type = 'file';
    input.id = 'hr-emp-doc-file-input';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
    document.body.appendChild(input);
  }
  input.value = '';

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    // Validate size
    if (file.size > 10 * 1024 * 1024) {
      showToast('File too large. Max 10MB.', 'error');
      return;
    }

    // Validate extension
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) {
      showToast('Only PDF, JPG, and PNG files allowed.', 'error');
      return;
    }

    HRState.uploadingDocument = true;
    const btn = document.getElementById('btn-upload-emp-doc');
    if (btn) {
      btn.disabled = true;
      btn.innerHTML = '<span class="btn-spinner"></span> Uploading...';
    }

    try {
      const tenantId = HRState.tenantId;
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `hr/${tenantId}/${personId}/${timestamp}_${safeName}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file, { contentType: inferContentType(file) });
      // TASK-1029 — PATH-ONLY: never mint/persist a getDownloadURL bearer `url`
      // on the documents row (it bypasses storage.rules for anyone who ever read
      // the row). Render goes through the HR-gated getComplianceDocBytes
      // byte-stream callable via storagePath.

      const userName = window.AppState?.userName || 'Unknown';
      // OSHA 1910.1020 category — medical/exposure protected from auto-purge.
      const categorySel = document.getElementById('hr-emp-doc-category');
      const ALLOWED_DOC_CATEGORIES = [
        // OSHA retention buckets
        'general', 'medical', 'exposure', 'clearinghouse',
        // DOT Driver Qualification File (FMCSA 391.x / 49 CFR 382 / 395)
        'cdl', 'mec', 'mvr', 'mvr_annual_review', 'employment_app',
        'road_test', 'prev_employer_safety', 'training', 'drug_alcohol', 'on_duty_hours',
      ];
      const category = ALLOWED_DOC_CATEGORIES.includes(categorySel?.value)
        ? categorySel.value : 'general';
      try {
        const docsBefore = (HRState.employeeDocuments || []).length;
        window.logAudit?.('hr', 'PERSON_DOCS_SAVE', {
          entityType: 'person',
          entityId: personId,
          source: 'hr-employee-doc-upload',
          before: { docCount: docsBefore },
          after: { docCount: docsBefore + 1 },
          redFlags: { docCountDrop: false, fileRefsRemoved: [], expiryChanged: false, category, fileName: file.name }
        });
      } catch {}
      await addDoc(
        collection(db, 'tenants', tenantId, 'people', personId, 'documents'),
        {
          fileName: file.name,
          storagePath, // TASK-1029 — path-only; view via getComplianceDocBytes
          mimeType: file.type,
          isPdf: file.type === 'application/pdf',
          sizeBytes: file.size,
          category,
          uploadedAt: Timestamp.now(),
          uploadedBy: userName
        }
      );

      // Invalidate cache and reload (updateDetailTabCounts called inside renderEmployeeDocuments)
      HRState.employeeDocsLoaded = null;
      await loadEmployeeDocuments(personId);
      showToast('File uploaded', 'success');

      // Auto-check matching New Hire Checklist item (if filename hints at a known doc).
      // Looks up the active (incomplete) checklist for this person; if the item isn't
      // already checked, marks it complete via the same path the checkbox uses.
      try {
        const matchedItemId = findChecklistItemForFilename(file.name);
        if (matchedItemId) {
          const personChecklists = (HRState.hrChecklists || []).filter(c => c.employeeId === personId);
          // Prefer an in-progress checklist; fall back to the most recent.
          const activeChecklist = personChecklists.find(c => (c.completionPercent || 0) < 100) || personChecklists[0];
          if (activeChecklist && !activeChecklist.items?.[matchedItemId]?.completed) {
            await toggleChecklistItem(activeChecklist.id, matchedItemId, true);
            const itemLabel = CHECKLIST_ITEMS.find(i => i.id === matchedItemId)?.label || matchedItemId;
            showToast(`Auto-checked "${itemLabel}" on the new hire checklist`, 'success');
          }
        }
      } catch (autoCheckErr) {
        console.warn('[HR] Auto-check from upload failed (non-fatal):', autoCheckErr);
      }
    } catch (err) {
      console.error('Upload failed:', err);
      showToast('Upload failed. Try again.', 'error');
    } finally {
      HRState.uploadingDocument = false;
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = '<span class="material-icons" style="font-size:14px;">upload_file</span> Upload';
      }
    }
  };

  setTimeout(() => input.click(), 50);
}

// SEC-FOLLOWUPS-2 — server-side delete. The old client path deleted only the
// documents row (and tried a Storage delete), leaving the credential pointer on
// the person doc and the newest-in-folder fallback still serving the "deleted"
// file to the partner portal, auditor link and DQ packet. deleteHrPersonDocument
// removes the row, clears the matching pointer, voids the version row and writes
// the audit row in one transaction, and KEEPS the file (§391.51 retention).
// Gate (server): owner via ownerId, or the hr / compliance module; tenant from
// the auth claim. Demo has no members doc to carry modules, so the demo tenant
// keeps the direct row delete (firestore.rules keeps a demo-only delete arm).
//
// fix-1999-followups — the ONE client path that removes a people/{pid}/documents
// row. Every caller (Documents-tab delete, slot extra-file delete, onboarding-slot
// Remove) goes through here; tests/hr-person-doc-delete-ratchet.unit.cjs fails if
// a direct client delete of that path reappears anywhere else.
async function _hrDeletePersonDocRow(tenantId, personId, docId) {
  if (tenantId === 'demo') {
    await deleteDoc(doc(db, 'tenants', tenantId, 'people', personId, 'documents', docId));
    return { ok: true, demo: true };
  }
  const res = await httpsCallable(functions, 'deleteHrPersonDocument')({ tenantId, personId, docId });
  return (res && res.data) || { ok: true };
}

// The server's refusal is the message: permission-denied = module gate / upload-only
// login / DQ-only login; failed-precondition = §391.51 retention lock (the only or
// original copy of a required DQ document, with what to do instead); not-found =
// already removed. Anything else (network, internal) gets the generic line.
function _hrDocDeleteErrMsg(err, fallback) {
  const readable = ['functions/permission-denied', 'functions/failed-precondition', 'functions/not-found',
    'functions/invalid-argument', 'functions/unauthenticated', 'functions/unavailable'];
  return (err && readable.includes(err.code) && err.message) ? err.message : (fallback || 'Delete failed. Try again.');
}

async function deleteEmployeeDocument(personId, docId) {
  if (!confirm('Delete this file? It is removed from this list and stops being shared with your compliance partner or auditors. The original stays in your records.')) return;

  const docData = (HRState.employeeDocuments || []).find(d => d.id === docId);
  if (!docData) return;

  try {
    await _hrDeletePersonDocRow(HRState.tenantId, personId, docId);

    // Invalidate cache and reload (updateDetailTabCounts called inside renderEmployeeDocuments)
    HRState.employeeDocsLoaded = null;
    await loadEmployeeDocuments(personId);
    showToast('File deleted', 'success');
  } catch (err) {
    console.error('Delete failed:', err);
    showToast(_hrDocDeleteErrMsg(err), 'error');
  }
}

// TASK-970 — carrier-side "Download Full DQ File (ZIP)". Bundles every uploaded
// DQ doc + a §391.51 manifest into a ZIP via the EXISTING generateDqFilePacket
// callable (server-gated by requireTenantAdminOrPartner — owner/admin member OR
// managing compliance partner). The full ZIP contains SSN/DOB/MVR/drug-test PII,
// so this button is OWNER/ADMIN-only client-side (_hrIsOwnerOrAdmin), matching
// the server gate — never module-based (hasModule). In-flight guard prevents a
// double-click double-generate. Mirrors compliance-partner.js generateDqFileFromPartner.
let _hrDqFileInFlight = false;
async function downloadFullDqFile(personId) {
  if (!_hrIsOwnerOrAdmin()) { showToast('Owner or admin access required.', 'error'); return; }
  const tenantId = HRState.tenantId || window.AppState?.tenant;
  if (!tenantId || !personId) { showToast('Open a driver first.', 'error'); return; }
  if (_hrDqFileInFlight) return;
  _hrDqFileInFlight = true;

  const emp = (HRState.people || []).find(p => p.id === personId) || HRState.selectedEmployee || {};
  let modal = document.getElementById('hr-dqfile-modal');
  if (!modal) { modal = document.createElement('div'); modal.id = 'hr-dqfile-modal'; document.body.appendChild(modal); }
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:10050;display:flex;align-items:center;justify-content:center;padding:16px;';
  const close = () => { if (modal && modal.parentNode) modal.parentNode.removeChild(modal); _hrDqFileInFlight = false; };
  modal.innerHTML = `
    <div style="background:#fff;border-radius:var(--radius-lg,12px);max-width:460px;width:100%;max-height:90dvh;overflow-y:auto;box-shadow:0 10px 40px rgba(0,0,0,0.25);">
      <div style="padding:14px 16px;border-bottom:1px solid var(--border-light);display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="color:var(--brand-blue);">folder_zip</span>
        <h3 style="margin:0;font-size:1.05rem;font-weight:700;flex:1;color:var(--text-primary);">Full DQ File</h3>
        <button class="btn-icon" id="hr-dqfile-x" title="Close" style="padding:2px;"><span class="material-icons">close</span></button>
      </div>
      <div id="hr-dqfile-body" style="padding:16px;color:var(--text-secondary);font-size:0.85rem;line-height:1.5;">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;">hourglass_empty</span> Assembling ${escapeHtml(emp.name || "this driver")}'s complete §391.51 file — this can take up to a minute…
      </div>
    </div>`;
  const body = () => document.getElementById('hr-dqfile-body');
  document.getElementById('hr-dqfile-x')?.addEventListener('click', close);

  try {
    const res = await httpsCallable(functions, 'generateDqFilePacket')({ tenantId, personId });
    const out = (res && res.data) || {};
    const dlUrl = _dqSafeHttpUrl(out.downloadUrl); // scheme-validate (same guard as doc URLs)
    const scoreLine = (out.checklistScore != null)
      ? `<div style="color:#047857;margin-top:6px;">Part 391 + 382 completeness: ${out.checklistScore}%${out.checklistMissing ? ` — ${out.checklistMissing} item${out.checklistMissing === 1 ? '' : 's'} missing` : ' — complete'}</div>` : '';
    if (body()) body().innerHTML = `
      <div style="font-weight:700;color:#065F46;margin-bottom:6px;">DQ file ready</div>
      <div style="color:#047857;margin-bottom:10px;">${out.docCount || 0} document${out.docCount === 1 ? '' : 's'} bundled, ${Math.round((out.sizeBytes || 0) / 1024)} KB</div>
      ${scoreLine}
      ${dlUrl ? `<a href="${_dqEscAttr(dlUrl)}" target="_blank" rel="noopener" class="btn btn-primary" style="margin-top:10px;display:inline-flex;align-items:center;gap:6px;"><span class="material-icons" style="font-size:18px;">download</span> Download ZIP</a>` : `<div style="color:var(--danger,#DC2626);margin-top:6px;">Download link unavailable — please retry.</div>`}
      <div style="color:var(--text-secondary);margin-top:8px;font-size:0.75rem;">Link valid for 1 hour. Includes SSN/DOB/MVR/drug-test records — handle per your retention policy.</div>
      <div style="margin-top:14px;text-align:right;"><button class="btn btn-secondary btn-sm" id="hr-dqfile-done">Close</button></div>`;
    document.getElementById('hr-dqfile-done')?.addEventListener('click', close);
    if (dlUrl) { try { window.open(dlUrl, '_blank', 'noopener'); } catch (_e) {} }
  } catch (err) {
    if (body()) body().innerHTML = `<div style="color:var(--danger,#DC2626);">Error: ${escapeHtml((err && err.message) || 'unknown')}</div>
      <div style="margin-top:14px;text-align:right;"><button class="btn btn-secondary btn-sm" id="hr-dqfile-done">Close</button></div>`;
    document.getElementById('hr-dqfile-done')?.addEventListener('click', close);
  } finally {
    _hrDqFileInFlight = false;
  }
}

// TASK-9394 — FLSA Status: ONE list of choices + labels, shared by both
// selects (Edit Employee modal + Tax tab), the roster CSV / print roster and
// the employee detail. `mca_exempt` = Motor Carrier Act (FLSA §13(b)(1)):
// exempt from OVERTIME only — hours are still tracked/exported, minimum wage
// still applies. The OT gate itself is clock.js isPayrollExempt.
const FLSA_STATUS_OPTIONS = [
  { value: 'non_exempt', label: 'Hourly + OT Eligible (most workers)' },
  { value: 'ot_exempt', label: 'Hourly — No OT (manager/supervisor)' },
  { value: 'mca_exempt', label: 'Hourly — No OT (Motor Carrier Act exempt)' },
  { value: 'exempt', label: 'Salaried — No Hour Tracking (executive/professional)' },
];
// Legacy rows carry only hrOvertimeExempt (no flsaStatus) — they read as ot_exempt.
function resolveFlsaStatus(emp) {
  return (emp && emp.flsaStatus) || (emp && emp.hrOvertimeExempt ? 'ot_exempt' : 'non_exempt');
}
function flsaStatusOptionsHtml(emp) {
  const cur = resolveFlsaStatus(emp);
  return FLSA_STATUS_OPTIONS.map(o =>
    `<option value="${o.value}" ${cur === o.value ? 'selected' : ''}>${o.label}</option>`).join('');
}
// Short label for the roster CSV / print roster / employee detail.
// hrOvertimeExempt===true wins over a conflicting flsaStatus 'non_exempt'
// (payroll's isPayrollExempt treats that row as exempt, and the pre-9394 CSV /
// print labelled it "OT Exempt") — the label must match what payroll does.
function flsaStatusShortLabel(emp, nonExemptText = 'Non-Exempt') {
  const s = resolveFlsaStatus(emp);
  if (s === 'exempt') return 'Exempt';
  if (s === 'mca_exempt') return 'OT Exempt (MCA)';
  if (s === 'ot_exempt') return 'OT Exempt';
  if (emp && emp.hrOvertimeExempt === true) return 'OT Exempt';
  return nonExemptText;
}
function flsaStatusHelpHtml() {
  return '<strong>Hourly + OT Eligible</strong> tracks regular and overtime hours and pays time-and-a-half over 40h/week. '
    + '<strong>Hourly — No OT (manager/supervisor)</strong> tracks hours but never accrues overtime (rare — usually a manager whose duties qualify under the FLSA executive/admin exemption). '
    + '<strong>Hourly — No OT (Motor Carrier Act exempt)</strong> is for drivers, and the mechanics/loaders whose work affects the safety of trucks over 10,000 lb GVWR in interstate commerce (FLSA §13(b)(1)); the small-vehicle exception can put someone back on OT. Hours are still tracked and exported and minimum wage still applies — only the OT premium is off. Whether it applies depends on duties and the vehicles worked on; DispatchDeck records your classification, it doesn\'t decide it. '
    + '<strong>Salaried</strong> means hour totals don\'t drive pay calculations at all. '
    + 'Picking any "No OT" option means this employee will never appear in payroll OT calculations.';
}

function editEmployeeHR(employeeId) {
  // TASK-1481 — the modal's FLSA select pre-fills from flsaStatus /
  // hrOvertimeExempt (comp fields); resolve comp-first. Display-only here —
  // saveEmployeeHR never persists these (see its comment).
  const emp = _hrComp(HRState.people.find(p => p.id === employeeId));
  if (!emp) return;

  // TASK-1566 — one resolver drives the assign control. Niche built-ins (swamper)
  // are hidden unless the tenant already uses them; custom positions appear in a
  // "Custom" group. The person's CURRENT position is always kept selectable (even
  // if hidden/legacy/removed) so saving never silently blanks an assignment.
  const posGroups = window.HRPositions.grouped();
  const knownIds = new Set(posGroups.flatMap(g => g.positions.map(p => p.id)));
  const groupsHtml = posGroups.map(g =>
    `<optgroup label="${escapeHtml(g.department)}">` +
    g.positions.map(p => `<option value="${escapeHtml(p.id)}" ${emp.hrPosition === p.id ? 'selected' : ''}>${escapeHtml(p.title)}</option>`).join('') +
    `</optgroup>`
  ).join('');
  const currentFallback = (emp.hrPosition && !knownIds.has(emp.hrPosition))
    ? `<optgroup label="Current"><option value="${escapeHtml(emp.hrPosition)}" selected>${escapeHtml(getPositionTitle(emp.hrPosition))}</option></optgroup>`
    : '';
  const posOptions = groupsHtml + currentFallback;

  const policyLevels = (HRState.timeOffPolicy?.levels || []).sort((a, b) => (a.yearThreshold || 0) - (b.yearThreshold || 0));
  const levelOptions = policyLevels.map(l =>
    `<option value="${l.id}" ${emp.hrTimeOffLevel === l.id ? 'selected' : ''}>${escapeHtml(l.name)} (${l.yearThreshold || 0}+ yrs)</option>`
  ).join('');

  const modalHtml = `
    <div class="modal-header">
      <h3><span class="material-icons">edit</span> Edit HR Info</h3>
      <button class="modal-close" onclick="window.HRModule.closeModal('edit-employee-hr')">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-body">
      <div class="form-group">
        <label for="edit-hr-name">Employee Name</label>
        <input type="text" id="edit-hr-name" class="form-control" value="${escapeHtml(emp.name || '')}" placeholder="Full Name" required>
      </div>
      <div class="form-group">
        <label for="edit-hr-position">Position</label>
        <div style="display:flex;gap:6px;align-items:stretch;">
          <select id="edit-hr-position" class="form-control" style="flex:1;">
            <option value="">Select position...</option>
            ${posOptions}
          </select>
          <button type="button" class="btn btn-secondary" onclick="window.HRModule.promptAddHrPosition()" title="Add custom position" style="padding:6px 10px;">
            <span class="material-icons" style="font-size:18px;">add</span>
          </button>
        </div>
      </div>
      ${/* TASK-934 — Driver Classification tier (canonical DQ gate), right after
           the Position/Role field because it's definitional. Saved through the
           setDriverClassification callable, never a direct doc write (rules
           deny it). TASK-1087: 'Non-Driver' is always selectable — the server
           flags for review when operational drive-signals exist. */
        window.DqMatrix.classificationControlHtml({
          person: emp,
          name: 'edit-hr-position-type',
          attributionLabel: (window.AppState?.tenantData?.compliancePartner || '').trim() || null,
        })}
      <div class="form-group">
        <label for="edit-hr-address">Address</label>
        <input type="text" id="edit-hr-address" class="form-control" placeholder="123 Main St, City, ST 12345" value="${escapeHtml(emp.address || '')}">
      </div>
      <div class="form-group">
        <label for="edit-hr-personal-email">Personal Email</label>
        <input type="email" id="edit-hr-personal-email" class="form-control" placeholder="personal@gmail.com" value="${escapeHtml(emp.personalEmail || '')}">
      </div>

      <div style="padding:12px;margin-top:var(--spacing-sm);background:var(--bg-secondary);border-radius:var(--radius-sm);font-size:0.82rem;color:var(--text-secondary);text-align:center;">
        <span class="material-icons" style="font-size:14px;vertical-align:middle;">info</span>
        Employment, Compliance, Tax, and Documents are editable in their own tabs.
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" onclick="window.HRModule.closeModal('edit-employee-hr')">Cancel</button>
      <button class="btn btn-primary" onclick="window.HRModule.saveEmployeeHR('${employeeId}')">
        <span class="material-icons">save</span> Save
      </button>
    </div>
  `;
  /* REMOVED SECTIONS: Compliance, Tax, Documents — now editable in their own tabs

            <div class="password-input-wrapper">
              <input type="password" id="edit-hr-ssn" class="form-control" placeholder="123-45-6789" maxlength="11" value="${escapeHtml(emp.ssn || '')}">
              <button type="button" class="password-toggle" onclick="const i=document.getElementById('edit-hr-ssn');i.type=i.type==='password'?'text':'password';this.querySelector('.material-icons').textContent=i.type==='password'?'visibility':'visibility_off';">
                <span class="material-icons">visibility</span>
              </button>
            </div>
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-dob">Date of Birth</label>
            <input type="date" id="edit-hr-dob" class="form-control" value="${emp.dob || ''}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-employee-id">Employee ID</label>
            <input type="text" id="edit-hr-employee-id" class="form-control" placeholder="EMP-001" value="${escapeHtml(emp.employeeId || '')}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-isnet-nickname">ISNetworld #</label>
            <input type="text" id="edit-hr-isnet-nickname" class="form-control" placeholder="e.g. 07445259" value="${escapeHtml(emp.isnetNickname || '')}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-isnet-emp-type">Employee Type</label>
            <select id="edit-hr-isnet-emp-type" class="form-control">
              <option value="">Select...</option>
              <option value="FMCSA" ${emp.isnetEmployeeType === 'FMCSA' ? 'selected' : ''}>FMCSA</option>
              <option value="PHMSA" ${emp.isnetEmployeeType === 'PHMSA' ? 'selected' : ''}>PHMSA</option>
              <option value="FMCSA|PHMSA" ${emp.isnetEmployeeType === 'FMCSA|PHMSA' ? 'selected' : ''}>FMCSA | PHMSA</option>
            </select>
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-go-to-field">Go To Field</label>
            <select id="edit-hr-go-to-field" class="form-control">
              <option value="">Select...</option>
              <option value="YES" ${emp.goToField === 'YES' ? 'selected' : ''}>YES</option>
              <option value="NO" ${emp.goToField === 'NO' ? 'selected' : ''}>NO</option>
            </select>
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-dl-state">DL State</label>
            <input type="text" id="edit-hr-dl-state" class="form-control" placeholder="ND" maxlength="2" style="text-transform:uppercase;" value="${escapeHtml(emp.dlState || '')}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-dl-number">DL Number</label>
            <input type="text" id="edit-hr-dl-number" class="form-control" placeholder="D123456789" value="${escapeHtml(emp.dlNumber || '')}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-dl-expiry">DL Expiry Date</label>
            <input type="date" id="edit-hr-dl-expiry" class="form-control" value="${emp.dlExpiryDate || ''}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-cdl-class">CDL Class</label>
            <select id="edit-hr-cdl-class" class="form-control">
              <option value="">Select...</option>
              <option value="A" ${emp.cdlClass === 'A' ? 'selected' : ''}>A</option>
              <option value="B" ${emp.cdlClass === 'B' ? 'selected' : ''}>B</option>
              <option value="C" ${emp.cdlClass === 'C' ? 'selected' : ''}>C</option>
            </select>
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-cdl-endorsements">Endorsements</label>
            <input type="text" id="edit-hr-cdl-endorsements" class="form-control" placeholder="H, N, T, X" value="${escapeHtml(emp.cdlEndorsements || '')}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-cdl-issue">CDL Issue Date</label>
            <input type="date" id="edit-hr-cdl-issue" class="form-control" value="${emp.cdlIssueDate || ''}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-cdl-expiry">CDL Expiry Date</label>
            <input type="date" id="edit-hr-cdl-expiry" class="form-control" value="${emp.cdlExpiryDate || ''}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-med-card-number">Med Card Provider</label>
            <input type="text" id="edit-hr-med-card-number" class="form-control" placeholder="Doctor or clinic name" value="${escapeHtml((window.DqMatrix?.medDetail ? window.DqMatrix.medDetail(emp, HRState.tenantId) : emp).medCardNumber || '')}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-med-card-expiry">Med Card Expiry</label>
            <input type="date" id="edit-hr-med-card-expiry" class="form-control" value="${emp.medCardExpiryDate || ''}">
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-safety-verified">Safety Training Verified</label>
            <select id="edit-hr-safety-verified" class="form-control">
              <option value="">Select...</option>
              <option value="YES" ${emp.safetyTrainingVerified === 'YES' ? 'selected' : ''}>YES</option>
              <option value="NO" ${emp.safetyTrainingVerified === 'NO' ? 'selected' : ''}>NO</option>
            </select>
          </div>
          <div class="form-group" style="margin-bottom:0;">
            <label for="edit-hr-supervisor">Supervisor Role</label>
            <select id="edit-hr-supervisor" class="form-control">
              <option value="">Select...</option>
              <option value="YES" ${emp.supervisorRole === 'YES' ? 'selected' : ''}>YES</option>
              <option value="NO" ${emp.supervisorRole === 'NO' ? 'selected' : ''}>NO</option>
            </select>
          </div>
        </div>
        <div class="form-group" style="margin-top:var(--spacing-sm);">
          <label for="edit-hr-spec-cert">Specialized Certification</label>
          <input type="text" id="edit-hr-spec-cert" class="form-control" placeholder="e.g. HAZWOPER 40-Hour" value="${escapeHtml(emp.specializedCertification || '')}">
        </div>
        <div class="form-group">
          <label for="edit-hr-suspicion-training">Reasonable Suspicion Training</label>
          <select id="edit-hr-suspicion-training" class="form-control">
            <option value="">Select...</option>
            <option value="DOT" ${emp.reasonableSuspicionTraining === 'DOT' ? 'selected' : ''}>DOT</option>
            <option value="NON-DOT" ${emp.reasonableSuspicionTraining === 'NON-DOT' ? 'selected' : ''}>NON-DOT</option>
            <option value="NEEDS TRAINING" ${emp.reasonableSuspicionTraining === 'NEEDS TRAINING' ? 'selected' : ''}>NEEDS TRAINING</option>
            <option value="NO" ${emp.reasonableSuspicionTraining === 'NO' ? 'selected' : ''}>NO</option>
          </select>
        </div>
        </div>
      </details>

      <!-- Tax & Classification — collapsible -->
      <details class="hr-edit-section">
        <summary class="hr-edit-section-toggle">
          <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">receipt_long</span> Tax & Classification
          <span class="material-icons hr-edit-chevron">expand_more</span>
        </summary>
        <div class="hr-edit-section-body">
          <div class="form-group">
            <label for="edit-hr-tax-classification">Classification</label>
            <select id="edit-hr-tax-classification" class="form-control" onchange="const v=this.value;const biz=document.getElementById('edit-hr-tax-biz-fields');if(biz)biz.style.display=(v==='contractor'||v==='owner_operator')?'':'none';">
              <option value="">Select...</option>
              <option value="employee" ${emp.taxClassification === 'employee' ? 'selected' : ''}>Employee (W-2)</option>
              <option value="contractor" ${emp.taxClassification === 'contractor' ? 'selected' : ''}>Contractor (1099)</option>
              <option value="owner_operator" ${emp.taxClassification === 'owner_operator' ? 'selected' : ''}>Owner-Operator (1099)</option>
            </select>
          </div>
          <div class="form-group">
            <label for="edit-hr-flsa-status">FLSA Status</label>
            <select id="edit-hr-flsa-status" class="form-control" data-prev-value="${resolveFlsaStatus(emp)}" onchange="window.HRModule && window.HRModule.warnFlsaChange && window.HRModule.warnFlsaChange(this);">
              ${flsaStatusOptionsHtml(emp)}
            </select>
            <p style="margin:4px 0 0;font-size:0.75rem;color:var(--text-secondary);line-height:1.4;">${flsaStatusHelpHtml()}</p>
          </div>
          <div class="form-group">
            <label for="edit-hr-legal-name">Legal Name</label>
            <input type="text" id="edit-hr-legal-name" class="form-control" placeholder="Full legal name" value="${escapeHtml(emp.legalName || '')}">
          </div>
          <div id="edit-hr-tax-biz-fields" style="${emp.taxClassification === 'contractor' || emp.taxClassification === 'owner_operator' ? '' : 'display:none;'}">
            <div class="form-group">
              <label for="edit-hr-business-name">Business Name</label>
              <input type="text" id="edit-hr-business-name" class="form-control" placeholder="DBA or LLC name" value="${escapeHtml(emp.businessName || '')}">
            </div>
            <div class="form-group">
              <label for="edit-hr-business-type">Business Type</label>
              <select id="edit-hr-business-type" class="form-control">
                <option value="">Select...</option>
                <option value="sole_prop" ${emp.businessType === 'sole_prop' ? 'selected' : ''}>Sole Proprietor</option>
                <option value="llc" ${emp.businessType === 'llc' ? 'selected' : ''}>LLC</option>
                <option value="s_corp" ${emp.businessType === 's_corp' ? 'selected' : ''}>S-Corp</option>
                <option value="c_corp" ${emp.businessType === 'c_corp' ? 'selected' : ''}>C-Corp</option>
                <option value="partnership" ${emp.businessType === 'partnership' ? 'selected' : ''}>Partnership</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label>TIN Type</label>
            <div style="display:flex;gap:16px;margin-top:4px;">
              <label style="display:flex;align-items:center;gap:4px;cursor:pointer;font-size:0.85rem;">
                <input type="radio" name="edit-hr-tin-type" value="ssn" ${emp.tinType !== 'ein' ? 'checked' : ''} onchange="document.getElementById('edit-hr-ein-group').style.display='none';"> SSN
              </label>
              <label style="display:flex;align-items:center;gap:4px;cursor:pointer;font-size:0.85rem;">
                <input type="radio" name="edit-hr-tin-type" value="ein" ${emp.tinType === 'ein' ? 'checked' : ''} onchange="document.getElementById('edit-hr-ein-group').style.display='';"> EIN
              </label>
            </div>
          </div>
          <div class="form-group" id="edit-hr-ein-group" style="${emp.tinType === 'ein' ? '' : 'display:none;'}">
            <label for="edit-hr-ein">EIN</label>
            <div class="password-input-wrapper">
              <input type="password" id="edit-hr-ein" class="form-control" placeholder="12-3456789" maxlength="10" value="${escapeHtml(emp.ein || '')}">
              <button type="button" class="password-toggle" onclick="const i=document.getElementById('edit-hr-ein');i.type=i.type==='password'?'text':'password';this.querySelector('.material-icons').textContent=i.type==='password'?'visibility':'visibility_off';">
                <span class="material-icons">visibility</span>
              </button>
            </div>
          </div>
          <div style="font-weight:600;font-size:0.85rem;margin:var(--spacing-sm) 0 var(--spacing-xs);color:var(--text-secondary);">Mailing Address</div>
          <div class="form-group">
            <input type="text" id="edit-hr-tax-street" class="form-control" placeholder="Street address" value="${escapeHtml(emp.taxStreet || '')}">
          </div>
          <div style="display:grid;grid-template-columns:2fr 1fr 1fr;gap:8px;">
            <div class="form-group" style="margin:0;">
              <input type="text" id="edit-hr-tax-city" class="form-control" placeholder="City" value="${escapeHtml(emp.taxCity || '')}">
            </div>
            <div class="form-group" style="margin:0;">
              <input type="text" id="edit-hr-tax-state" class="form-control" placeholder="State" maxlength="2" style="text-transform:uppercase;" value="${escapeHtml(emp.taxState || '')}">
            </div>
            <div class="form-group" style="margin:0;">
              <input type="text" id="edit-hr-tax-zip" class="form-control" placeholder="ZIP" maxlength="10" value="${escapeHtml(emp.taxZip || '')}">
            </div>
          </div>
          <div class="form-group" style="margin-top:var(--spacing-sm);">
            <label style="display:flex;align-items:center;gap:6px;cursor:pointer;">
              <input type="checkbox" id="edit-hr-w9-collected" ${emp.w9Collected ? 'checked' : ''}>
              W-9 Collected
            </label>
            ${emp.w9Collected && emp.w9CollectedAt ? `<p style="margin:4px 0 0;font-size:0.75rem;color:var(--text-secondary);">Collected: ${(emp.w9CollectedAt.toDate ? emp.w9CollectedAt.toDate() : new Date(emp.w9CollectedAt)).toLocaleDateString('en-US')}</p>` : ''}
          </div>
        </div>
      </details>

      <!-- Documents — collapsible -->
      <details class="hr-edit-section">
        <summary class="hr-edit-section-toggle">
          <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">folder_open</span> Documents
          <span class="material-icons hr-edit-chevron">expand_more</span>
        </summary>
        <div class="hr-edit-section-body">
        <div id="hr-emp-documents-list" style="display:flex;flex-direction:column;gap:8px;margin-bottom:var(--spacing-md);">
          ${_renderComplianceDocRows(employeeId, emp.complianceDocs)}
        </div>
        <div id="hr-add-doc-form" style="display:flex;flex-direction:column;gap:8px;padding:12px;border:2px dashed var(--border-light);border-radius:var(--radius-sm);">
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;">
            <div class="form-group" style="margin:0;">
              <label>Document Name</label>
              <input type="text" id="hr-doc-name" class="form-control" placeholder="e.g. TWIC Card">
            </div>
            <div class="form-group" style="margin:0;">
              <label>Expiration Date</label>
              <input type="date" id="hr-doc-expiry" class="form-control">
            </div>
          </div>
          <div class="form-group" style="margin:0;">
            <label>Attach File (optional)</label>
            <input type="file" id="hr-doc-file" class="form-control" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif">
          </div>
  END OF REMOVED SECTIONS */

  let modal = document.getElementById('edit-employee-hr-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'edit-employee-hr-modal';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `<div class="modal-content" style="max-width: 520px;">${modalHtml}</div>`;
  modal.classList.remove('hidden');

  // Attach address autocomplete to HR address fields
  const hrAddr = document.getElementById('edit-hr-address');
  if (hrAddr && window.attachAddressAutocomplete) window.attachAddressAutocomplete(hrAddr);
  const taxStreet = document.getElementById('edit-hr-tax-street');
  if (taxStreet && window.attachAddressAutocomplete) window.attachAddressAutocomplete(taxStreet);
}

// TASK-1391 — tap target for the amber "Needs DOT Role" directory badge: open
// the Edit HR Info modal scrolled/highlighted to the Driver Classification
// control so classifying takes one tap instead of a hunt through the modal.
function openEmployeeClassification(employeeId) {
  editEmployeeHR(employeeId);
  const grp = document.getElementById('edit-hr-position-type-group');
  if (!grp) return;
  grp.scrollIntoView({ block: 'center' });
  grp.style.transition = 'box-shadow 0.3s';
  grp.style.borderRadius = '8px';
  grp.style.boxShadow = '0 0 0 3px rgba(245,158,11,0.55)';
  setTimeout(() => { grp.style.boxShadow = ''; }, 1800);
}

async function promptAddHrPosition() {
  const existingCustom = HRState.timeOffPolicy?.customPositions || [];

  // Abuse guardrail — 100 custom positions is more than any real company needs
  if (existingCustom.length >= 100) {
    window.showToast?.('Custom position limit reached (100). Contact support if you need more.', 'error');
    return;
  }

  const title = prompt('New position title:');
  if (!title || !title.trim()) return;
  const trimmed = title.trim().slice(0, 60); // cap title length

  // Check duplicates against built-in and existing custom
  const builtInTitles = Object.values(REVIEW_POSITIONS)
    .filter(cfg => cfg.available)
    .map(cfg => cfg.title.toLowerCase());
  const customTitles = existingCustom.map(p => p.title.toLowerCase());
  if (builtInTitles.includes(trimmed.toLowerCase()) || customTitles.includes(trimmed.toLowerCase())) {
    window.showToast?.('That position already exists', 'error');
    return;
  }

  const newPos = {
    id: `custom_${Date.now()}`,
    title: trimmed
  };

  try {
    const policyRef = doc(db, 'tenants', HRState.tenantId, 'hrTimeOffPolicy', 'config');
    await setDoc(policyRef, {
      customPositions: arrayUnion(newPos)
    }, { merge: true });

    // Append the new option to the currently-open select and select it
    const select = document.getElementById('edit-hr-position');
    if (select) {
      let group = select.querySelector('optgroup[label="Custom"]');
      if (!group) {
        group = document.createElement('optgroup');
        group.label = 'Custom';
        select.appendChild(group);
      }
      const opt = document.createElement('option');
      opt.value = newPos.id;
      opt.textContent = newPos.title;
      group.appendChild(opt);
      select.value = newPos.id;
    }

    window.showToast?.(`Added "${trimmed}"`, 'success');
  } catch (err) {
    console.error('[HR] Error adding custom position:', err);
    window.showToast?.('Error adding position', 'error');
  }
}

async function saveEmployeeHR(employeeId) {
  // The Edit Employee modal renders ONLY identity fields (name, position,
  // address, personalEmail). Compliance / Tax / Documents were intentionally
  // moved to dedicated tabs (saveComplianceFields, saveTaxFields,
  // saveEmploymentFields) and removed from this modal — see the
  // "REMOVED SECTIONS" /* */ block below where the old HTML lived.
  // Don't try to read flsaStatus or other tax fields here; those DOM IDs
  // never exist in this modal.
  const newName = document.getElementById('edit-hr-name')?.value?.trim() || '';
  if (!newName) {
    window.showToast?.('Name is required', 'error');
    return;
  }
  const position = document.getElementById('edit-hr-position')?.value || '';
  const address = document.getElementById('edit-hr-address')?.value?.trim() || '';
  const personalEmail = document.getElementById('edit-hr-personal-email')?.value?.trim() || '';

  try {
    const oldEmp = HRState.people.find(p => p.id === employeeId);
    const oldName = oldEmp?.name || '';

    const updateData = {
      name: newName,
      hrPosition: position || null,
      address: address,
      personalEmail: personalEmail,
    };

    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'people', employeeId), updateData);

    // TASK-934 — Driver Classification saves through the setDriverClassification
    // callable ONLY (positionType is server-owned; firestore.rules denies a
    // direct doc write — including the updateDoc above, which never carries it).
    // Fire only on an actual change so an untouched modal stays a no-op.
    try {
      const pickedPt = window.DqMatrix.readClassificationControl('edit-hr-position-type');
      const currentPt = window.DqMatrix.explicitPositionType(oldEmp);
      // TASK-9806 — "Drives, no CMV" asks the placarded-hazmat / passenger checks first;
      // a yes switches to the tier it requires, cancel leaves the classification alone.
      const _pick9806 = (pickedPt && pickedPt !== currentPt)
        ? await window.DqMatrix.confirmClassificationPick(pickedPt, { name: newName })
        : null;
      if (pickedPt && pickedPt !== currentPt && !_pick9806) window.showToast?.('Classification not changed', 'info');
      if (_pick9806 && _pick9806.positionType !== currentPt) {
        const setPt = httpsCallable(functions, 'setDriverClassification');
        const ptRes = await setPt({ tenantId: HRState.tenantId, personId: employeeId, positionType: _pick9806.positionType, cmvChecks: _pick9806.cmvChecks });
        updateData.positionType = _pick9806.positionType;
        updateData.positionTypeSetBy = 'carrier';
        // TASK-1353 (COD UX) — surface trainings that newly apply under the new tier.
        const _nt = ptRes?.data?.newlyApplicableTrainings;
        if (Array.isArray(_nt) && _nt.length > 0) {
          const _lbls = _nt.map(t => t && t.label).filter(Boolean).join(', ');
          window.showToast?.(`Role change: ${_nt.length} required training${_nt.length > 1 ? 's' : ''} now appl${_nt.length > 1 ? 'y' : 'ies'}${_lbls ? ' — ' + _lbls : ''}`, 'warning');
        }
      }
    } catch (ptErr) {
      console.error('[HR] Driver classification save failed:', ptErr);
      window.showToast?.(ptErr?.message || 'Could not save driver classification', 'error');
    }

    // Cascade name change to active clock entries + ticket lines (same as Roster)
    if (oldName && newName !== oldName) {
      try {
        const activeSnap = await getDocs(query(
          collection(db, 'tenants', HRState.tenantId, 'clockEntries'),
          where('personId', '==', employeeId),
          where('status', '==', 'active')
        ));
        if (!activeSnap.empty) {
          const batch = writeBatch(db);
          for (const entryDoc of activeSnap.docs) {
            batch.update(entryDoc.ref, { personName: newName });
            const tLineId = entryDoc.data().ticketLineId;
            const ticketId = entryDoc.data().ticketId;
            if (tLineId && ticketId) {
              batch.update(doc(db, 'tenants', HRState.tenantId, 'tickets', ticketId, 'ticketLines', tLineId), { personName: newName });
            }
          }
          await batch.commit();
          console.log(`[HR] Cascaded name change to ${activeSnap.size} active clock entry(s)`);
        }
      } catch (err) {
        console.warn('[HR] Failed to cascade name to clock entries:', err);
      }
    }

    // Update local state
    const emp = HRState.people.find(p => p.id === employeeId);
    if (emp) Object.assign(emp, updateData);

    window.showToast?.('Employee HR info updated', 'success');
  } catch (error) {
    console.error('[HR] Error saving employee HR info:', error);
    window.showToast?.('Error saving employee info', 'error');
    return;
  }

  // Refresh UI outside try/catch so render errors don't show misleading "Error saving" toast
  try {
    closeModal('edit-employee-hr');
    openEmployeeDetail(employeeId);
    renderEmployeeDirectory();
  } catch (renderErr) {
    console.warn('[HR] Error refreshing after save (data was saved successfully):', renderErr);
  }
}

// TASK-980 — flag chip tooltip. 🔒 The reason is never on the person doc; the
// chip shows ONLY who + when. TASK-1207: the reason lives in the server-only
// driverFlags doc — owner/admin see it in the detail callout
// (buildFlagReasonCalloutHTML/_hydrateHrFlagReason); everyone else, Activity.
function formatFlagTooltip(emp) {
  const by = String(emp?.flaggedByEmail || '').trim();
  let when = '';
  try {
    const d = emp?.flaggedAt?.toDate?.() || (emp?.flaggedAt ? new Date(emp.flaggedAt) : null);
    if (d && !isNaN(d.getTime())) when = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (_) {}
  return 'Flagged for follow-up' + (by ? ' · by ' + by : '') + (when ? ' · on ' + when : '');
}

// TASK-980 / TASK-9424 — ADD a follow-up flag, or CLEAR one named flag. Routes
// through the dual-gated setDriverFlag callable ONLY — the flag cluster is
// server-owned (firestore.rules denies a direct doc write) and a driver session
// can never reach the callable, so no one can set/clear their own flag. A person
// can carry several flags; a clear names its flag and MUST say why (the server
// refuses a reasonless clear and keeps the reason in the history + auditLog).
async function addDriverFlag(employeeId) {
  const tenantId = HRState.tenantId;
  if (!tenantId || !employeeId) return;
  const emp = HRState.people.find(p => p.id === employeeId);
  const name = emp?.name || 'this person';
  const reason = await window.PersonSections.openFlagReasonModal({
    title: `Flag ${name} for follow-up`,
    message: emp?.flagged === true
      ? 'This person already has a flag. This adds ANOTHER one — the existing flag stays as it is.'
      : 'Why does this person need attention? Owners and admins see the reason; everyone with HR or Compliance access sees that they are flagged.',
    placeholder: 'e.g. Med card expires 10/15 — renewal not scheduled',
    okText: 'Flag',
    icon: 'outlined_flag',
  });
  if (reason === null) return; // cancelled
  try {
    const fn = httpsCallable(functions, 'setDriverFlag');
    await fn({ tenantId, personId: employeeId, flagged: true, reason });
    if (emp) emp.flagged = true; // the live people listener backfills who/when
    window.showToast?.('Flagged for follow-up', 'success');
  } catch (err) {
    console.error('[HR] flag employee failed:', err);
    window.showToast?.(err?.message || 'Could not add the flag', 'error');
    return;
  }
  _hrRefreshAfterFlag(employeeId);
}

async function clearDriverFlag(employeeId, flagId) {
  const tenantId = HRState.tenantId;
  if (!tenantId || !employeeId) return;
  const emp = HRState.people.find(p => p.id === employeeId);
  const name = emp?.name || 'this person';
  const reason = await window.PersonSections.openFlagReasonModal({
    title: `Clear a flag on ${name}`,
    message: 'Why is this flag being cleared? Your reason is kept with your name and the date in the flag history.\n\n'
      + 'If your compliance partner set this flag, only they can clear it — ask them to.',
    placeholder: 'e.g. Med card renewed 9/28 — copy on file',
    okText: 'Clear flag',
    minLength: 3,
    icon: 'flag_circle',
  });
  if (reason === null) return; // cancelled
  try {
    const fn = httpsCallable(functions, 'setDriverFlag');
    const res = await fn({ tenantId, personId: employeeId, flagged: false, flagId: flagId || undefined, reason });
    if (emp && res?.data && res.data.flagged === false) {
      delete emp.flagged; delete emp.flaggedByUid;
      delete emp.flaggedByEmail; delete emp.flaggedAt;
    }
    window.showToast?.('Flag cleared — the reason is in the flag history', 'success');
  } catch (err) {
    console.error('[HR] clear flag failed:', err);
    // TASK-9227 — the partner-origin refusal is policy, not a fault: say what to do.
    const _code = String(err?.code || '');
    if (_code.endsWith('permission-denied') && /partner/i.test(String(err?.message || ''))) {
      window.showToast?.(`${err.message} Ask them to clear it — your attempt has been recorded so they can see it.`, 'warning', null, 12000);
    } else {
      window.showToast?.(err?.message || 'Could not clear the flag', 'error');
    }
    return;
  }
  _hrRefreshAfterFlag(employeeId);
}

function _hrRefreshAfterFlag(employeeId) {
  try {
    openEmployeeDetail(employeeId);
    renderEmployeeDirectory();
  } catch (renderErr) {
    console.warn('[HR] Error refreshing after flag change (write succeeded):', renderErr);
  }
}

// ============================================
// TIME-OFF POLICY EDITOR
// ============================================
function openTimeOffPolicyEditor() {
  if (_hrGateComplianceOnly('timeoff')) return; // 2026-05-13 compliance-only gate (Time Off)
  const policy = HRState.timeOffPolicy;
  const levels = policy?.levels || [];
  const sorted = [...levels].sort((a, b) => (a.yearThreshold || 0) - (b.yearThreshold || 0));

  const levelsHtml = sorted.length > 0 ? sorted.map(level => {
    const salaryTotal = sumAllowanceDays(level.salary);
    const hourlyTotal = sumAllowanceDays(level.hourly);
    return `
      <div class="hr-detail-item" onclick="window.HRModule.editPolicyLevel('${level.id}')" style="cursor:pointer;">
        <div class="hr-detail-item-icon" style="background:linear-gradient(135deg, #f59e0b, #d97706);color:white;">
          <span class="material-icons">calendar_month</span>
        </div>
        <div class="hr-detail-item-info">
          <div class="hr-detail-item-title">${escapeHtml(level.name)}</div>
          <div class="hr-detail-item-meta">${level.yearThreshold || 0}+ years &middot; Salary: ${salaryTotal}d &middot; Hourly: ${hourlyTotal}d${_policyGrantCountLabel(level)}</div>
        </div>
        <button class="hr-detail-item-delete" onclick="event.stopPropagation(); window.HRModule.deletePolicyLevel('${level.id}')" title="Delete level">
          <span class="material-icons">delete_outline</span>
        </button>
      </div>
    `;
  }).join('') : '<div class="empty-state-small">No levels defined yet. Add your first level.</div>';

  // Build bereavement section for main policy modal
  const policyBereavement = policy?.bereavement
    || (sorted[0]?.salary?.bereavement) || {};
  const bObj = (typeof policyBereavement === 'object' && policyBereavement !== null) ? policyBereavement : {};
  const bereavementTierRows = BEREAVEMENT_TIERS.map(t => {
    const val = bObj[t.id] || 0;
    return `<div class="bereavement-tier-row">
      <div style="flex:1;">
        <div style="font-weight:500;font-size:0.8rem;">${t.label}</div>
        <div style="font-size:0.7rem;color:var(--text-secondary);">${t.desc}</div>
      </div>
      <input type="number" id="policy-global-bereavement-${t.id}" class="form-control" min="0" value="${val}" style="width:60px;">
    </div>`;
  }).join('');

  const bereavementHtml = `
    <div class="bereavement-tiers-section collapsed" style="margin-top:var(--spacing-lg);border-top:1px solid var(--border-light, #e2e8f0);padding-top:var(--spacing-md);">
      <div class="bereavement-tiers-header" onclick="this.parentElement.classList.toggle('collapsed')" style="cursor:pointer;display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:1.1rem;color:#e11d48;">favorite</span>
        <div style="flex:1;font-weight:700;font-size:0.9rem;">Bereavement Policy</div>
        <span class="material-icons bereavement-expand-icon" style="font-size:1.2rem;color:var(--text-secondary);transition:transform 0.2s;">expand_more</span>
      </div>
      <div class="bereavement-tiers-body">
        <div style="display:flex;align-items:center;gap:var(--spacing-sm);margin-top:var(--spacing-sm);margin-bottom:var(--spacing-sm);">
          <p style="font-size:0.8rem;color:var(--text-secondary);margin:0;flex:1;">Applies to all employees regardless of tenure.</p>
          <select id="policy-global-bereavement-unit" class="form-control" style="width:auto;font-size:0.8rem;padding:4px 8px;">
            <option value="days" ${(policy?.bereavementUnit || 'days') === 'days' ? 'selected' : ''}>Days per event</option>
            <option value="hours" ${policy?.bereavementUnit === 'hours' ? 'selected' : ''}>Hours per event</option>
          </select>
        </div>
        ${bereavementTierRows}
        <div style="border-top:1px solid var(--border-light, #e2e8f0);margin-top:8px;padding-top:8px;">
          <div class="bereavement-tier-row">
            <div style="flex:1;">
              <div style="font-weight:500;font-size:0.8rem;">Travel Days</div>
              <div style="font-size:0.7rem;color:var(--text-secondary);">Extra days for funerals 250+ miles away</div>
            </div>
            <input type="number" id="policy-global-bereavement-travelDays" class="form-control" min="0" value="${bObj.travelDays || 0}" style="width:60px;">
          </div>
        </div>
      </div>
    </div>
  `;

  // TASK-341 — Company Holidays editor (collapsed by default; same disclosure
  // pattern as Bereavement Policy above). Per COD Item 2B, ships BLANK: no US
  // federal seed. Admins add what their fleet actually observes.
  const holidays = Array.isArray(window.AppState?.settings?.companyHolidays)
    ? [...window.AppState.settings.companyHolidays]
    : [];
  holidays.sort((a, b) => String(a?.date || '').localeCompare(String(b?.date || '')));
  const holidayRowsHtml = holidays.length > 0
    ? holidays.map(h => {
        const scopeLabel = h?.observedFor === 'salary_only' ? 'Salary only'
          : h?.observedFor === 'driver_only' ? 'Drivers only'
          : 'Everyone';
        const paidLabel = h?.paid === false ? 'Unpaid' : 'Paid';
        const mult = parseFloat(h?.hoursMultiplier);
        const multLabel = Number.isFinite(mult) && mult !== 1 ? `${mult}× pay` : 'Regular pay';
        return `<div class="hr-detail-item" onclick="window.HRModule.editCompanyHoliday('${escJsAttr(h?.id || '')}')" style="cursor:pointer;">
          <div class="hr-detail-item-icon" style="background:linear-gradient(135deg,#06b6d4,#0891b2);color:white;">
            <span class="material-icons">celebration</span>
          </div>
          <div class="hr-detail-item-info">
            <div class="hr-detail-item-title">${escapeHtml(h?.label || '(no label)')}</div>
            <div class="hr-detail-item-meta">${escapeHtml(h?.date || '')} &middot; ${paidLabel} &middot; ${multLabel} &middot; ${scopeLabel}</div>
          </div>
          <button class="hr-detail-item-delete" onclick="event.stopPropagation(); window.HRModule.deleteCompanyHoliday('${escJsAttr(h?.id || '')}')" title="Delete holiday">
            <span class="material-icons">delete_outline</span>
          </button>
        </div>`;
      }).join('')
    : '<div class="empty-state-small">No company holidays yet. Add the dates your fleet observes — they won\'t burn PTO and can carry a premium-pay multiplier on the Clock.</div>';

  const companyHolidaysHtml = `
    <div class="bereavement-tiers-section collapsed" style="margin-top:var(--spacing-lg);border-top:1px solid var(--border-light, #e2e8f0);padding-top:var(--spacing-md);">
      <div class="bereavement-tiers-header" onclick="this.parentElement.classList.toggle('collapsed')" style="cursor:pointer;display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:1.1rem;color:#06b6d4;">celebration</span>
        <div style="flex:1;font-weight:700;font-size:0.9rem;">Company Holidays</div>
        <span class="material-icons bereavement-expand-icon" style="font-size:1.2rem;color:var(--text-secondary);transition:transform 0.2s;">expand_more</span>
      </div>
      <div class="bereavement-tiers-body">
        <p style="font-size:0.8rem;color:var(--text-secondary);margin:var(--spacing-sm) 0;">
          Paid holidays don't burn a day from PTO balances. Premium-pay multipliers apply to clock entries whose <strong>shift starts</strong> on that date — overnight shifts that span a holiday boundary use the start-date's rate for v1.
        </p>
        <div id="company-holidays-list">${holidayRowsHtml}</div>
        <div style="display:flex;justify-content:flex-end;margin-top:var(--spacing-sm);">
          <button class="btn btn-primary btn-sm" onclick="window.HRModule.addCompanyHoliday()">
            <span class="material-icons">add</span> Add Holiday
          </button>
        </div>
      </div>
    </div>
  `;

  const modalHtml = `
    <div class="modal-header">
      <h3><span class="material-icons">event_available</span> Time Off Policy</h3>
      <button class="modal-close" onclick="window.HRModule.closeModal('timeoff-policy')">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-body">
      <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:var(--spacing-md);">Define time-off allowance levels based on years of service. Assign levels to employees in their HR profile.</p>
      ${levelsHtml}
      ${bereavementHtml}
      ${companyHolidaysHtml}
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" onclick="window.HRModule.closeModal('timeoff-policy')">Close</button>
      <button class="btn btn-primary" onclick="window.HRModule.saveBereavementPolicy()" style="margin-right:auto;">
        <span class="material-icons">save</span> Save Bereavement
      </button>
      <button class="btn btn-primary" onclick="window.HRModule.addPolicyLevel()">
        <span class="material-icons">add</span> Add Level
      </button>
    </div>
  `;

  let modal = document.getElementById('timeoff-policy-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'timeoff-policy-modal';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `<div class="modal-content modal-lg">${modalHtml}</div>`;
  modal.classList.remove('hidden');
}

function addPolicyLevel() {
  editPolicyLevel(null);
}

function editPolicyLevel(levelId) {
  const policy = HRState.timeOffPolicy;
  const level = levelId ? (policy?.levels || []).find(l => l.id === levelId) : null;

  // Flat categories exclude bereavement (handled separately) and unpaid (salary only)
  const salaryCategories = TIME_OFF_CATEGORIES.filter(c => c.id !== 'unpaid' && c.id !== 'bereavement');
  const hourlyCategories = TIME_OFF_CATEGORIES.filter(c => c.id !== 'bereavement');

  // Accrual-eligible categories (exclude bereavement per-event and unpaid)
  const accrualCategories = TIME_OFF_CATEGORIES.filter(c => c.id !== 'bereavement' && c.id !== 'unpaid');

  function buildFlatInputs(cats, prefix, levelData, accrualData) {
    return cats.map(c => {
      const ac = accrualData?.[c.id] || {};
      const method = ac.method || 'annual';
      const isAccrual = c.id !== 'unpaid' && c.id !== 'bereavement';
      return `<div class="form-group" style="flex:1;min-width:100px;">
        <label style="font-size:0.75rem;">${c.label}</label>
        <input type="number" id="policy-${prefix}-${c.id}" class="form-control" min="0" value="${levelData?.[c.id] || 0}"
          ${method !== 'annual' && isAccrual ? 'title="Annual equivalent (read-only when using accrual)" style="opacity:0.5;" readonly' : ''}>
      </div>`;
    }).join('');
  }

  function buildAccrualSection(prefix, accrualData) {
    return accrualCategories.map(c => {
      const ac = accrualData?.[c.id] || {};
      const method = ac.method || 'annual';
      return `<div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);">
        <span style="font-size:0.8rem;font-weight:500;min-width:90px;">${c.label}</span>
        <select id="accrual-${prefix}-${c.id}-method" class="form-control" style="font-size:0.75rem;padding:4px;width:auto;" onchange="window.HRModule.onAccrualMethodChange('${prefix}','${c.id}')">
          <option value="annual" ${method === 'annual' ? 'selected' : ''}>Annual (flat)</option>
          <option value="per_pay_period" ${method === 'per_pay_period' ? 'selected' : ''}>Per Pay Period</option>
          <option value="per_hours_worked" ${method === 'per_hours_worked' ? 'selected' : ''}>Per Hours Worked</option>
        </select>
        <div id="accrual-${prefix}-${c.id}-fields" style="display:${method === 'annual' ? 'none' : 'flex'};align-items:center;gap:6px;flex-wrap:wrap;">
          <label style="font-size:0.7rem;margin:0;">Earn</label>
          <input type="number" id="accrual-${prefix}-${c.id}-rate" class="form-control" min="0" step="0.01" value="${ac.rate || ''}" style="width:60px;font-size:0.75rem;padding:4px;" placeholder="hrs">
          <span style="font-size:0.7rem;color:var(--text-secondary);">hrs</span>
          <div id="accrual-${prefix}-${c.id}-base-wrap" style="display:${method === 'per_hours_worked' ? 'flex' : 'none'};align-items:center;gap:4px;">
            <span style="font-size:0.7rem;">per</span>
            <input type="number" id="accrual-${prefix}-${c.id}-base" class="form-control" min="1" value="${ac.rateBase || 40}" style="width:56px;font-size:0.75rem;padding:4px;">
            <span style="font-size:0.7rem;color:var(--text-secondary);">hrs worked</span>
          </div>
          <div style="display:flex;align-items:center;gap:4px;margin-left:4px;">
            <label style="font-size:0.7rem;margin:0;">Cap</label>
            <input type="number" id="accrual-${prefix}-${c.id}-cap" class="form-control" min="0" value="${ac.cap || ''}" style="width:56px;font-size:0.75rem;padding:4px;" placeholder="∞">
          </div>
          <div style="display:flex;align-items:center;gap:4px;">
            <label style="font-size:0.7rem;margin:0;">Rollover</label>
            <input type="number" id="accrual-${prefix}-${c.id}-rollover" class="form-control" min="0" value="${ac.rolloverMax != null ? ac.rolloverMax : ''}" style="width:56px;font-size:0.75rem;padding:4px;" placeholder="∞">
          </div>
        </div>
      </div>`;
    }).join('');
  }

  const salaryInputs = buildFlatInputs(salaryCategories, 'salary', level?.salary, level?.salary?._accrual);
  const hourlyInputs = buildFlatInputs(hourlyCategories, 'hourly', level?.hourly, level?.hourly?._accrual);
  const salaryAccrual = buildAccrualSection('salary', level?.salary?._accrual);
  const hourlyAccrual = buildAccrualSection('hourly', level?.hourly?._accrual);

  const modalHtml = `
    <div class="modal-header">
      <h3><span class="material-icons">edit</span> ${level ? 'Edit' : 'Add'} Policy Level</h3>
      <button class="modal-close" onclick="window.HRModule.closeModal('edit-policy-level')">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-body">
      <div style="display:flex;gap:var(--spacing-md);margin-bottom:var(--spacing-md);">
        <div class="form-group" style="flex:2;">
          <label for="policy-level-name">Level Name *</label>
          <input type="text" id="policy-level-name" class="form-control" placeholder="e.g., Year 1-2" value="${escapeHtml(level?.name || '')}">
        </div>
        <div class="form-group" style="flex:1;">
          <label for="policy-level-threshold">Years of Service</label>
          <input type="number" id="policy-level-threshold" class="form-control" min="0" value="${level?.yearThreshold || 0}">
        </div>
      </div>

      <div style="margin-bottom:var(--spacing-md);">
        <div style="font-weight:600;font-size:0.85rem;margin-bottom:var(--spacing-sm);">Salary Employees (days/year &middot; 8 hrs each)</div>
        <div style="display:flex;flex-wrap:wrap;gap:var(--spacing-sm);">
          ${salaryInputs}
        </div>
      </div>

      <div style="margin-bottom:var(--spacing-md);">
        <div style="font-weight:600;font-size:0.85rem;margin-bottom:var(--spacing-sm);">Hourly Employees (days/year &middot; 8 hrs each)</div>
        <div style="display:flex;flex-wrap:wrap;gap:var(--spacing-sm);">
          ${hourlyInputs}
        </div>
      </div>

      <div class="collapsible-section" style="border:1px solid var(--border-light);border-radius:var(--radius-md);padding:0;">
        <div class="collapsible-header" onclick="this.parentElement.classList.toggle('open')" style="padding:var(--spacing-sm) var(--spacing-md);cursor:pointer;display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="font-size:1rem;color:var(--brand-blue);">schedule</span>
          <span style="font-weight:600;font-size:0.85rem;flex:1;">Accrual Settings</span>
          <span class="material-icons collapsible-arrow" style="font-size:1.2rem;color:var(--text-secondary);">expand_more</span>
        </div>
        <div class="collapsible-body" style="padding:0 var(--spacing-md) var(--spacing-md);">
          <p style="font-size:0.75rem;color:var(--text-secondary);margin-bottom:var(--spacing-sm);">Configure how PTO accrues. "Annual" gives the full amount on Jan 1. Other methods earn hours over time.</p>
          <div style="margin-bottom:var(--spacing-md);">
            <div style="font-weight:500;font-size:0.8rem;margin-bottom:4px;color:var(--text-secondary);">Salary</div>
            ${salaryAccrual}
          </div>
          <div>
            <div style="font-weight:500;font-size:0.8rem;margin-bottom:4px;color:var(--text-secondary);">Hourly</div>
            ${hourlyAccrual}
          </div>
        </div>
      </div>

      ${_policyGrantsSectionHtml(level)}
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" onclick="window.HRModule.closeModal('edit-policy-level')">Cancel</button>
      <button class="btn btn-primary" onclick="window.HRModule.savePolicyLevel('${levelId || ''}')">
        <span class="material-icons">save</span> Save Level
      </button>
    </div>
  `;

  let modal = document.getElementById('edit-policy-level-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'edit-policy-level-modal';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `<div class="modal-content modal-lg">${modalHtml}</div>`;
  modal.classList.remove('hidden');
}

function onAccrualMethodChange(prefix, catId) {
  const method = document.getElementById(`accrual-${prefix}-${catId}-method`)?.value || 'annual';
  const fields = document.getElementById(`accrual-${prefix}-${catId}-fields`);
  const baseWrap = document.getElementById(`accrual-${prefix}-${catId}-base-wrap`);
  const daysInput = document.getElementById(`policy-${prefix}-${catId}`);
  if (fields) fields.style.display = method === 'annual' ? 'none' : 'flex';
  if (baseWrap) baseWrap.style.display = method === 'per_hours_worked' ? 'flex' : 'none';
  if (daysInput) {
    daysInput.readOnly = method !== 'annual';
    daysInput.style.opacity = method !== 'annual' ? '0.5' : '1';
  }
}

function readAccrualConfig(prefix) {
  const accrualCategories = TIME_OFF_CATEGORIES.filter(c => c.id !== 'bereavement' && c.id !== 'unpaid');
  const accrual = {};
  let hasAny = false;
  accrualCategories.forEach(c => {
    const method = document.getElementById(`accrual-${prefix}-${c.id}-method`)?.value || 'annual';
    if (method === 'annual') return;
    hasAny = true;
    const entry = { method };
    entry.rate = parseFloat(document.getElementById(`accrual-${prefix}-${c.id}-rate`)?.value) || 0;
    if (method === 'per_hours_worked') {
      entry.rateBase = parseInt(document.getElementById(`accrual-${prefix}-${c.id}-base`)?.value) || 40;
    }
    const cap = document.getElementById(`accrual-${prefix}-${c.id}-cap`)?.value;
    if (cap !== '' && cap != null) entry.cap = parseFloat(cap);
    const rollover = document.getElementById(`accrual-${prefix}-${c.id}-rollover`)?.value;
    if (rollover !== '' && rollover != null) entry.rolloverMax = parseFloat(rollover);
    accrual[c.id] = entry;
  });
  return hasAny ? accrual : null;
}

// ============================================
// TASK-9839 — milestone grants in the plan editor.
// A level may carry `grants: [{ id, kind: 'once'|'yearly', after, unit:
// 'days'|'months', days, category }]`. Validation + math live in the shared
// calculator (window.TimeOffBalance); this is only the form.
// ============================================
function _policyGrantCountLabel(level) {
  const n = (window.TimeOffBalance?.normalizeGrantRules(level?.grants) || []).length;
  return n ? ` &middot; ${n} milestone grant${n === 1 ? '' : 's'}` : '';
}

function _policyGrantAuditText(g) {
  return window.TimeOffBalance?.ruleSummary(g) || JSON.stringify(g);
}

function _policyGrantRowHtml(rule) {
  const r = rule || { kind: 'once', after: 90, unit: 'days', days: 3, category: 'vacation' };
  const TOB = window.TimeOffBalance;
  const cats = (TOB?.GRANT_CATEGORIES || ['vacation']).map(c =>
    `<option value="${c}" ${r.category === c ? 'selected' : ''}>${escapeHtml(TOB?.categoryLabel(c) || c)}</option>`).join('');
  const rid = (typeof r.id === 'string' && /^[A-Za-z0-9_-]{1,40}$/.test(r.id)) ? r.id : ('g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6));
  const sel = 'border:1px solid var(--border-light);border-radius:6px;padding:6px;font-size:0.8rem;background:var(--bg-primary);color:var(--text-primary);min-height:36px;';
  return `<div class="policy-grant-row" data-grant-id="${rid}" style="display:flex;flex-wrap:wrap;align-items:center;gap:6px;padding:8px 0;border-bottom:1px solid var(--border-light);">
    <select class="pg-kind control-auto" style="${sel}" aria-label="When it lands">
      <option value="once" ${r.kind !== 'yearly' ? 'selected' : ''}>Once, at</option>
      <option value="yearly" ${r.kind === 'yearly' ? 'selected' : ''}>Every Jan 1, once past</option>
    </select>
    <input type="number" class="pg-after form-control" min="0" step="1" value="${Number(r.after) || 0}" style="width:72px;min-height:36px;font-size:0.8rem;padding:6px;" aria-label="How long after hire">
    <select class="pg-unit control-auto" style="${sel}" aria-label="Days or months">
      <option value="days" ${r.unit !== 'months' ? 'selected' : ''}>days</option>
      <option value="months" ${r.unit === 'months' ? 'selected' : ''}>months</option>
    </select>
    <span style="font-size:0.8rem;color:var(--text-secondary);">after hire &rarr; +</span>
    <input type="number" class="pg-days form-control" min="0.5" max="365" step="0.5" value="${Number(r.days) || ''}" style="width:64px;min-height:36px;font-size:0.8rem;padding:6px;" aria-label="Days granted">
    <span style="font-size:0.8rem;color:var(--text-secondary);">days of</span>
    <select class="pg-category control-auto" style="${sel}" aria-label="Which balance">${cats}</select>
    <button type="button" class="btn btn-sm" title="Remove this milestone" aria-label="Remove this milestone" onclick="window.HRModule.removePolicyGrantRow(this)" style="min-height:36px;min-width:36px;background:transparent;border:1px solid var(--border-light);color:var(--text-secondary);">
      <span class="material-icons" style="font-size:18px;">close</span>
    </button>
  </div>`;
}

function _policyGrantsSectionHtml(level) {
  const rules = window.TimeOffBalance?.normalizeGrantRules(level?.grants) || [];
  return `<div style="border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm) var(--spacing-md);margin-top:var(--spacing-md);">
    <div style="display:flex;align-items:center;gap:8px;">
      <span class="material-icons" style="font-size:1rem;color:var(--brand-blue);">flag</span>
      <span style="font-weight:600;font-size:0.85rem;flex:1;">Milestone grants</span>
    </div>
    <p style="font-size:0.75rem;color:var(--text-secondary);margin:4px 0 var(--spacing-sm);">
      Days that land on their own, counted from each person's hire date &mdash; e.g. 3 days at 90 days, 3 more at 180 days, 7 every Jan 1 once past 90 days.
      They add to the days above and reset with the calendar year like everything else.
    </p>
    <div id="policy-grants-list" oninput="window.HRModule.previewPolicyGrants()" onchange="window.HRModule.previewPolicyGrants()">
      ${rules.map(_policyGrantRowHtml).join('')}
    </div>
    <button type="button" class="btn btn-sm btn-secondary" style="margin-top:var(--spacing-sm);min-height:36px;" onclick="window.HRModule.addPolicyGrantRow()">
      <span class="material-icons" style="font-size:16px;">add</span> Add a milestone
    </button>
    <div id="policy-grants-preview" style="font-size:0.75rem;color:var(--text-secondary);margin-top:var(--spacing-sm);">${_policyGrantsPreviewText(rules)}</div>
  </div>`;
}

function addPolicyGrantRow() {
  const list = document.getElementById('policy-grants-list');
  if (!list) return;
  if (list.querySelectorAll('.policy-grant-row').length >= (window.TimeOffBalance?.MAX_GRANT_RULES || 20)) {
    window.showToast?.('That is the most milestones one level can hold', 'error');
    return;
  }
  list.insertAdjacentHTML('beforeend', _policyGrantRowHtml(null));
  previewPolicyGrants();
}

function removePolicyGrantRow(btn) {
  btn?.closest?.('.policy-grant-row')?.remove();
  previewPolicyGrants();
}

// Reads the grant rows. A row the shared validator would drop is an ERROR here
// (the admin typed something), so nothing is silently thrown away on save.
function readPolicyGrantRows() {
  const TOB = window.TimeOffBalance;
  const rows = Array.from(document.querySelectorAll('#policy-grants-list .policy-grant-row'));
  const rules = [];
  for (let i = 0; i < rows.length; i++) {
    const el = rows[i];
    const afterRaw = el.querySelector('.pg-after')?.value;
    const daysRaw = el.querySelector('.pg-days')?.value;
    const raw = {
      id: el.getAttribute('data-grant-id') || ('g' + i),
      kind: el.querySelector('.pg-kind')?.value,
      after: afterRaw === '' || afterRaw == null ? NaN : Number(afterRaw),
      unit: el.querySelector('.pg-unit')?.value,
      days: daysRaw === '' || daysRaw == null ? NaN : Number(daysRaw),
      category: el.querySelector('.pg-category')?.value,
    };
    const ok = TOB ? TOB.normalizeGrantRules([raw]) : [];
    if (ok.length !== 1) {
      return { rules: [], error: `Milestone ${i + 1}: enter a whole number of days or months after hire and the days to grant (more than 0, up to 365).` };
    }
    rules.push(ok[0]);
  }
  return { rules, error: null };
}

function _policyGrantsPreviewText(rules) {
  const TOB = window.TimeOffBalance;
  if (!TOB || !rules.length) return '';
  const today = TOB.isoInZone(new Date(), _hrTenantTimeZone());
  const g = TOB.grantsForPerson(today, rules, TOB.addDays(today, 800));
  const fmt = (iso) => new Date(iso + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const items = g.landed.slice(0, 6).map(x => `+${x.days} ${escapeHtml(x.categoryLabel)} on ${fmt(x.on)}`);
  return items.length ? `Someone hired today would get: ${items.join(' &middot; ')}` : '';
}

function previewPolicyGrants() {
  const el = document.getElementById('policy-grants-preview');
  if (!el) return;
  const read = readPolicyGrantRows();
  el.innerHTML = read.error ? '' : _policyGrantsPreviewText(read.rules);
}

async function savePolicyLevel(levelId) {
  const name = document.getElementById('policy-level-name')?.value?.trim();
  if (!name) { window.showToast?.('Level name is required', 'error'); return; }

  const yearThreshold = parseInt(document.getElementById('policy-level-threshold')?.value) || 0;

  const salaryCategories = TIME_OFF_CATEGORIES.filter(c => c.id !== 'unpaid' && c.id !== 'bereavement');
  const salary = {};
  salaryCategories.forEach(c => {
    salary[c.id] = parseInt(document.getElementById(`policy-salary-${c.id}`)?.value) || 0;
  });
  const salaryAccrual = readAccrualConfig('salary');
  if (salaryAccrual) salary._accrual = salaryAccrual;

  const hourlyCategories = TIME_OFF_CATEGORIES.filter(c => c.id !== 'bereavement');
  const hourly = {};
  hourlyCategories.forEach(c => {
    hourly[c.id] = parseInt(document.getElementById(`policy-hourly-${c.id}`)?.value) || 0;
  });
  const hourlyAccrual = readAccrualConfig('hourly');
  if (hourlyAccrual) hourly._accrual = hourlyAccrual;

  // TASK-9839 — milestone grants. A half-filled row is refused, never guessed.
  const grantRead = readPolicyGrantRows();
  if (grantRead.error) { window.showToast?.(grantRead.error, 'error'); return; }

  const id = levelId || ('level-' + Date.now());
  const levelData = { id, name, yearThreshold, salary, hourly };
  if (grantRead.rules.length) levelData.grants = grantRead.rules;
  const _prevLevel = levelId ? (HRState.timeOffPolicy?.levels || []).find(l => l.id === levelId) : null;
  const _prevGrants = (_prevLevel && Array.isArray(_prevLevel.grants)) ? _prevLevel.grants : [];

  try {
    const policy = HRState.timeOffPolicy || { levels: [] };
    let levels = [...(policy.levels || [])];

    if (levelId) {
      const idx = levels.findIndex(l => l.id === levelId);
      if (idx >= 0) levels[idx] = levelData;
      else levels.push(levelData);
    } else {
      levels.push(levelData);
    }

    const saveData = { levels, updatedAt: Timestamp.now() };
    // Preserve global bereavement + unit if they exist
    const existingPolicy = HRState.timeOffPolicy || {};
    if (existingPolicy.bereavement) saveData.bereavement = existingPolicy.bereavement;
    if (existingPolicy.bereavementUnit) saveData.bereavementUnit = existingPolicy.bereavementUnit;
    // TASK-9839 — this whole-doc setDoc silently dropped every other key on
    // the config doc, including customPositions (the HR position list).
    if (Array.isArray(existingPolicy.customPositions)) saveData.customPositions = existingPolicy.customPositions;

    await setDoc(doc(db, 'tenants', HRState.tenantId, 'hrTimeOffPolicy', 'config'), saveData);

    // Money-governing: a grant change moves balances, so log old + new.
    if (JSON.stringify(_prevGrants) !== JSON.stringify(levelData.grants || [])) {
      await window.logAudit?.('hr', 'TIME_OFF_PLAN_GRANTS_CHANGED', {
        levelId: id, levelName: name,
        oldGrants: _prevGrants.map(_policyGrantAuditText),
        newGrants: (levelData.grants || []).map(_policyGrantAuditText),
      });
    }

    window.showToast?.('Policy level saved', 'success');
    closeModal('edit-policy-level');
    openTimeOffPolicyEditor(); // Refresh list
  } catch (error) {
    console.error('[HR] Error saving policy level:', error);
    window.showToast?.('Error saving policy level', 'error');
  }
}

async function saveBereavementPolicy() {
  const obj = {};
  BEREAVEMENT_TIERS.forEach(t => {
    obj[t.id] = parseInt(document.getElementById(`policy-global-bereavement-${t.id}`)?.value) || 0;
  });
  obj.travelDays = parseInt(document.getElementById('policy-global-bereavement-travelDays')?.value) || 0;
  const bereavementUnit = document.getElementById('policy-global-bereavement-unit')?.value || 'days';

  try {
    const policy = HRState.timeOffPolicy || { levels: [] };
    await setDoc(doc(db, 'tenants', HRState.tenantId, 'hrTimeOffPolicy', 'config'), {
      ...policy,
      bereavement: obj,
      bereavementUnit,
      updatedAt: Timestamp.now()
    });

    window.showToast?.('Bereavement policy saved', 'success');
    HRState.timeOffPolicy = { ...policy, bereavement: obj, bereavementUnit };
  } catch (error) {
    console.error('[HR] Error saving bereavement policy:', error);
    window.showToast?.('Error saving bereavement policy', 'error');
  }
}

async function deletePolicyLevel(levelId) {
  if (!confirm('Delete this policy level?')) return;

  try {
    const policy = HRState.timeOffPolicy || { levels: [] };
    const levels = (policy.levels || []).filter(l => l.id !== levelId);

    const saveData = { levels, updatedAt: Timestamp.now() };
    if (policy.bereavement) saveData.bereavement = policy.bereavement;
    await setDoc(doc(db, 'tenants', HRState.tenantId, 'hrTimeOffPolicy', 'config'), saveData);

    window.showToast?.('Policy level deleted', 'success');
    openTimeOffPolicyEditor(); // Refresh list
  } catch (error) {
    console.error('[HR] Error deleting policy level:', error);
    window.showToast?.('Error deleting policy level', 'error');
  }
}

// ============================================
// TASK-341 — Company Holidays CRUD
// Persists to tenants/{tid}.settings.companyHolidays[] via window._settingsPersist.
// Schema (locked): { id, date: 'YYYY-MM-DD', label, paid: bool,
//   hoursMultiplier: number, observedFor: 'all'|'salary_only'|'driver_only' }
// ============================================

function addCompanyHoliday() {
  editCompanyHoliday(null);
}

function editCompanyHoliday(holidayId) {
  const list = Array.isArray(window.AppState?.settings?.companyHolidays)
    ? window.AppState.settings.companyHolidays
    : [];
  const h = holidayId ? list.find(x => x?.id === holidayId) : null;

  const labelVal = h?.label || '';
  const dateVal = h?.date || '';
  const paidVal = h?.paid !== false; // default true
  const multVal = Number.isFinite(parseFloat(h?.hoursMultiplier)) ? parseFloat(h.hoursMultiplier) : 1;
  const scopeVal = h?.observedFor || 'all';

  const modalHtml = `
    <div class="modal-header">
      <h3><span class="material-icons">celebration</span> ${h ? 'Edit' : 'Add'} Company Holiday</h3>
      <button class="modal-close" onclick="window.HRModule.closeModal('edit-company-holiday')">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-body">
      <div class="form-group">
        <label for="company-holiday-label">Holiday Name *</label>
        <input type="text" id="company-holiday-label" class="form-control" placeholder="e.g., Memorial Day" value="${escapeHtml(labelVal)}">
      </div>
      <div class="form-group">
        <label for="company-holiday-date">Date *</label>
        <input type="date" id="company-holiday-date" class="form-control" value="${escapeHtml(dateVal)}">
        <small style="color:var(--text-secondary);font-size:0.75rem;">v1 stores literal dates. If a holiday falls on a weekend and you observe it on a different day, add the observed date instead.</small>
      </div>
      <div class="form-group">
        <label for="company-holiday-paid">PTO treatment</label>
        <select id="company-holiday-paid" class="form-control">
          <option value="paid" ${paidVal ? 'selected' : ''}>Paid — don't burn a PTO day</option>
          <option value="unpaid" ${!paidVal ? 'selected' : ''}>Unpaid — PTO burns normally</option>
        </select>
      </div>
      <div class="form-group">
        <label for="company-holiday-multiplier">Premium-pay multiplier (clock hours)</label>
        <input type="number" id="company-holiday-multiplier" class="form-control" min="0" step="0.1" value="${multVal}">
        <small style="color:var(--text-secondary);font-size:0.75rem;">1 = regular pay. 1.5 or 2 = time-and-a-half / double-time. Applies to the shift's <strong>start date</strong> only.</small>
      </div>
      <div class="form-group">
        <label for="company-holiday-scope">Observed by</label>
        <select id="company-holiday-scope" class="form-control">
          <option value="all" ${scopeVal === 'all' ? 'selected' : ''}>Everyone</option>
          <option value="salary_only" ${scopeVal === 'salary_only' ? 'selected' : ''}>Salaried employees only</option>
          <option value="driver_only" ${scopeVal === 'driver_only' ? 'selected' : ''}>Drivers only</option>
        </select>
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" onclick="window.HRModule.closeModal('edit-company-holiday')">Cancel</button>
      <button class="btn btn-primary" onclick="window.HRModule.saveCompanyHoliday('${escJsAttr(holidayId || '')}')">
        <span class="material-icons">save</span> Save Holiday
      </button>
    </div>
  `;

  let modal = document.getElementById('edit-company-holiday-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'edit-company-holiday-modal';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `<div class="modal-content">${modalHtml}</div>`;
  modal.classList.remove('hidden');
}

async function saveCompanyHoliday(holidayId) {
  // Double-click guard (Edge Case Hunter #1): disable the trigger button up
  // front so the user can't fire two concurrent _settingsPersist writes that
  // race against each other and clobber the array.
  const triggerBtn = document.querySelector(`#edit-company-holiday-modal .btn-primary`);
  if (triggerBtn?.disabled) return;
  if (triggerBtn) triggerBtn.disabled = true;

  const label = document.getElementById('company-holiday-label')?.value?.trim();
  const date = document.getElementById('company-holiday-date')?.value?.trim();
  const paid = (document.getElementById('company-holiday-paid')?.value || 'paid') === 'paid';
  const multRaw = parseFloat(document.getElementById('company-holiday-multiplier')?.value);
  const hoursMultiplier = Number.isFinite(multRaw) && multRaw > 0 ? multRaw : 1;
  const observedFor = document.getElementById('company-holiday-scope')?.value || 'all';

  const releaseBtn = () => { if (triggerBtn) triggerBtn.disabled = false; };

  if (!label) { window.showToast?.('Holiday name is required', 'error'); releaseBtn(); return; }
  // YYYY-MM-DD shape check — <input type="date"> on supported browsers
  // always returns this format, but a misbehaving polyfill could slip something
  // else through.
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    window.showToast?.('A valid date is required', 'error');
    releaseBtn();
    return;
  }

  const existing = Array.isArray(window.AppState?.settings?.companyHolidays)
    ? [...window.AppState.settings.companyHolidays]
    : [];

  const isEdit = !!holidayId && existing.some(x => x?.id === holidayId);

  // Duplicate-date guard (Code Reviewer ⚠️). getHolidayForDate returns the
  // FIRST match — two rows with the same date make payroll + PTO calcs
  // order-dependent (which row "wins" depends on insertion order). Reject the
  // collision up front; admins can edit the existing row if they want a
  // different rule. Allow same-date through ONLY when we're editing the row
  // that already owns that date.
  const collision = existing.find(x => x?.date === date && x?.id !== holidayId);
  if (collision) {
    window.showToast?.(`A holiday already exists on ${date} — edit "${collision.label}" instead.`, 'error');
    releaseBtn();
    return;
  }

  const finalId = isEdit ? holidayId : ('hol_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8));
  const row = { id: finalId, date, label, paid, hoursMultiplier, observedFor };

  let next;
  if (isEdit) {
    next = existing.map(x => x?.id === holidayId ? row : x);
  } else {
    next = [...existing, row];
  }

  const ok = await window._settingsPersist?.({ companyHolidays: next });
  if (!ok) { releaseBtn(); return; } // _settingsPersist already toasted the error

  try {
    window.logAudit?.('hr', isEdit ? 'COMPANY_HOLIDAY_EDIT' : 'COMPANY_HOLIDAY_ADD', {
      holidayId: finalId,
      label,
      date,
      paid,
      hoursMultiplier,
      observedFor,
    });
  } catch (_) { /* non-fatal */ }

  window.showToast?.(isEdit ? 'Holiday updated' : 'Holiday added', 'success');
  closeModal('edit-company-holiday');
  openTimeOffPolicyEditor(); // refresh list
}

async function deleteCompanyHoliday(holidayId) {
  if (!holidayId) return;
  if (!confirm('Delete this company holiday?')) return;

  const existing = Array.isArray(window.AppState?.settings?.companyHolidays)
    ? window.AppState.settings.companyHolidays
    : [];
  const removed = existing.find(x => x?.id === holidayId) || null;
  const next = existing.filter(x => x?.id !== holidayId);

  const ok = await window._settingsPersist?.({ companyHolidays: next });
  if (!ok) return;

  try {
    window.logAudit?.('hr', 'COMPANY_HOLIDAY_DELETE', {
      holidayId,
      label: removed?.label || null,
      date: removed?.date || null,
    });
  } catch (_) { /* non-fatal */ }

  window.showToast?.('Holiday deleted', 'success');
  openTimeOffPolicyEditor(); // refresh list
}

// ============================================
// DVIR COMPLIANCE FUNCTIONS
// ============================================
async function resetAllDvirData(employeeId, employeeName) {
  if (!confirm(`WARNING: This will permanently delete ALL DVIR accountability records for ${employeeName} and reset their missed DVIR count to zero.\n\nThis cannot be undone. Continue?`)) return;

  try {
    // Delete all dvirAccountability records for this employee
    const flagsSnap = await getDocs(query(
      collection(db, 'tenants', HRState.tenantId, 'dvirAccountability'),
      where('driverId', '==', employeeId)
    ));

    let deleteCount = 0;
    for (const fdoc of flagsSnap.docs) {
      await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'dvirAccountability', fdoc.id));
      deleteCount++;
    }

    // Reset missed DVIR counter on person doc
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'people', employeeId), {
      missedDvirCount: 0,
      lastMissedDvirDate: null,
    });

    window.showToast?.(`Deleted ${deleteCount} DVIR record${deleteCount !== 1 ? 's' : ''} for ${employeeName}`, 'success');

    // Refresh the employee detail modal
    const emp = HRState.people.find(p => p.id === employeeId);
    if (emp) showEmployeeDetail(emp);
  } catch (error) {
    console.error('[HR] Error resetting DVIR data:', error);
    window.showToast?.('Error resetting DVIR data', 'error');
  }
}

async function resetMissedDvirCount(personId) {
  if (!confirm('Reset this employee\'s missed DVIR count to zero?')) return;
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'people', personId), {
      missedDvirCount: 0,
      lastMissedDvirDate: null
    });
    // Update local state
    const person = HRState.people.find(p => p.id === personId);
    if (person) { person.missedDvirCount = 0; person.lastMissedDvirDate = null; }
    window.showToast?.('Missed DVIR counter reset', 'success');
    openEmployeeDetail(personId); // Refresh detail view
  } catch (err) {
    console.error('[HR] Error resetting missed DVIR count:', err);
    window.showToast?.('Error resetting counter', 'error');
  }
}

async function deleteDvirFlag(flagId, employeeId) {
  if (!confirm('Permanently delete this DVIR flag record?')) return;
  try {
    await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'dvirAccountability', flagId));
    window.showToast?.('DVIR flag deleted', 'success');
    if (employeeId) openEmployeeDetail(employeeId);
  } catch (err) {
    console.error('[HR] Error deleting DVIR flag:', err);
    window.showToast?.('Error deleting flag', 'error');
  }
}

async function deleteJsaFlag(flagId, employeeId) {
  if (!confirm('Permanently delete this JSA flag record?')) return;
  try {
    await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'jsaAccountability', flagId));
    window.showToast?.('JSA flag deleted', 'success');
    if (employeeId) openEmployeeDetail(employeeId);
  } catch (err) {
    console.error('[HR] Error deleting JSA flag:', err);
    window.showToast?.('Error deleting flag', 'error');
  }
}

async function resetMissedJsaCount(personId) {
  if (!confirm('Reset this employee\'s missed JSA count to zero?')) return;
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'people', personId), {
      missedJsaCount: 0,
      lastMissedJsaDate: null
    });
    const person = HRState.people.find(p => p.id === personId);
    if (person) { person.missedJsaCount = 0; person.lastMissedJsaDate = null; }
    window.showToast?.('Missed JSA counter reset', 'success');
    openEmployeeDetail(personId);
  } catch (err) {
    console.error('[HR] Error resetting missed JSA count:', err);
    window.showToast?.('Error resetting counter', 'error');
  }
}

// TASK-1003 — single source of truth for an employee's missed DVIR/JSA events.
// Both the DVIR & JSA Accountability card headline count AND the drill-in list
// call this, so the list length is GUARANTEED to equal the number the manager
// clicked (same predicate as the Safety Roster card badge, hr.js ~L7739).
// type: 'dvir' | 'jsa'. Records are the live flagged/dismissed accountability
// docs subscribed into HRState (status in ['flagged','dismissed']).
function getEmployeeAccountabilityFlags(emp, type) {
  if (!emp) return [];
  const src = type === 'jsa' ? (HRState.jsaFlags || []) : (HRState.dvirFlags || []);
  return src.filter(f => f.driverId === emp.id);
}

// TASK-1003 — dated drill-in behind a missed-DVIR / missed-JSA count. Read-only:
// lists the actual missed events (date, driver, vehicle/job, flagged vs
// dismissed) newest-first. Reuses getEmployeeAccountabilityFlags so the row
// count matches the headline exactly. type: 'dvir' | 'jsa'. The card only ever
// renders for the open employee, so the subject is HRState.selectedEmployee —
// no user-controlled id is interpolated into the click handler (COD Adversary).
// Optional empId retained for future callers (legacy dual lookup, id or name).
function openMissedEventsDrill(type, empId) {
  const emp = empId
    ? HRState.people.find(p => p.id === empId || p.name === empId)
    : HRState.selectedEmployee;
  if (!emp) return;
  const isJsa = type === 'jsa';
  const label = isJsa ? 'JSA' : 'DVIR';
  const flags = getEmployeeAccountabilityFlags(emp, type)
    .slice()
    // date is a YYYY-MM-DD string (lexical sort == chronological); createdAt is
    // the tiebreak for same-day records. Newest first.
    .sort((a, b) => {
      const byDate = String(b.date || '').localeCompare(String(a.date || ''));
      if (byDate !== 0) return byDate;
      const at = a.createdAt?.toDate?.()?.getTime?.() || 0;
      const bt = b.createdAt?.toDate?.()?.getTime?.() || 0;
      return bt - at;
    });

  const rowsHtml = flags.length ? flags.map(f => {
    // formatDateShort echoes its raw input back on an unparseable date, so escape
    // it before it lands in HTML text (COD Code Reviewer TASK-1003).
    const dateTxt = escapeHtml(String(formatDateShort(f.date) || '—'));
    const statusRaw = (f.status || '').toLowerCase();
    const isDismissed = statusRaw === 'dismissed';
    const statusLabel = isDismissed ? 'Dismissed' : 'Flagged';
    const statusColor = isDismissed ? 'var(--text-secondary)' : '#DC2626';
    const vehicleTxt = !isJsa && f.truckUnit ? `Vehicle ${escapeHtml(String(f.truckUnit))}` : '';
    const jobTxt = f.jobTitle ? escapeHtml(String(f.jobTitle)) : '';
    const meta = [vehicleTxt, jobTxt].filter(Boolean).join(' · ');
    return `
      <div style="display:flex;align-items:center;gap:12px;padding:10px 14px;border-bottom:1px solid var(--border-light);">
        <span class="material-icons" style="font-size:18px;color:${statusColor};flex-shrink:0;">${isJsa ? 'health_and_safety' : 'warning'}</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:700;font-size:0.85rem;color:var(--text-primary);">${dateTxt}</div>
          <div style="font-size:0.78rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;">${escapeHtml(emp.name || f.driverName || 'Driver')}${meta ? ' · ' + meta : ''}</div>
        </div>
        <span style="font-size:0.72rem;font-weight:700;color:${statusColor};flex-shrink:0;">${statusLabel}</span>
      </div>`;
  }).join('') : `
    <div style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:36px 16px;color:var(--text-secondary);text-align:center;">
      <span class="material-icons" style="font-size:32px;color:#059669;">check_circle</span>
      <div style="font-size:0.9rem;">No missed ${label}s in this window</div>
    </div>`;

  const modalHtml = `
    <div class="modal-header">
      <h3 style="display:flex;align-items:center;gap:8px;margin:0;font-size:1rem;">
        <span class="material-icons" style="color:#DC2626;">checklist</span>
        Missed ${label}s — ${escapeHtml(emp.name || 'Driver')}
      </h3>
      <button class="btn-icon" onclick="window.HRModule.closeModal('missed-events-drill')" aria-label="Close" title="Close" style="padding:4px;">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-body" style="overflow-y:auto;padding:0;">
      <div style="padding:10px 14px;font-size:0.78rem;color:var(--text-secondary);background:var(--bg-elevated);border-bottom:1px solid var(--border-light);">
        ${flags.length} missed ${label}${flags.length !== 1 ? 's' : ''}${flags.length ? ', newest first' : ''}
      </div>
      ${rowsHtml}
    </div>`;

  let modal = document.getElementById('missed-events-drill-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'missed-events-drill-modal';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  // .modal-content mobile CSS (≤768px) already forces width:100vw + max-height
  // 90dvh; cap desktop height so a long list scrolls inside the modal-body.
  modal.innerHTML = `<div class="modal-content modal-lg" style="max-height:85vh;max-height:85dvh;">${modalHtml}</div>`;
  modal.classList.remove('hidden');
}

// ============================================
// TIME-OFF BALANCE FUNCTIONS
// ============================================
function getEmployeeLevelUpInfo(emp) {
  const policy = HRState.timeOffPolicy;
  if (!policy?.levels?.length || !emp.hireDate || !emp.hrTimeOffLevel) return null;

  const hireDate = emp.hireDate?.toDate?.() || new Date(emp.hireDate);
  const now = new Date();
  if (isNaN(hireDate.getTime()) || hireDate > now) return null;
  const yearsOfService = (now - hireDate) / (365.25 * 24 * 60 * 60 * 1000);

  const sorted = [...policy.levels].sort((a, b) => (a.yearThreshold || 0) - (b.yearThreshold || 0));
  const currentIdx = sorted.findIndex(l => l.id === emp.hrTimeOffLevel);
  if (currentIdx < 0 || currentIdx >= sorted.length - 1) return null;

  const nextLevel = sorted[currentIdx + 1];
  if (!nextLevel?.id) return null;
  if (emp.hrTimeOffLevelSuggestionDismissed === nextLevel.id) return null;
  if (yearsOfService >= (nextLevel.yearThreshold || 0)) {
    return { nextLevelId: nextLevel.id, nextLevelName: nextLevel.name, years: Math.floor(yearsOfService) };
  }
  return null;
}

/** Calculate accrued hours based on method, for current year up to today.
 *  TASK-9535 — `hoursWorked` is optional: the per-employee tab still reads
 *  HRState.employeeClockHours; the all-employee balance table passes each
 *  person's own clock hours so one shared HRState slot can't bleed across rows. */
function calculateAccruedHours(accrualEntry, year, hoursWorked) {
  // TASK-9839 — the math lives in the shared calculator (window.TimeOffBalance).
  const hrsWorked = (typeof hoursWorked === 'number' && !isNaN(hoursWorked)) ? hoursWorked : (HRState.employeeClockHours || 0);
  return window.TimeOffBalance.accruedHours(accrualEntry, year, {
    payPeriodType: window.AppState?.settings?.payPeriodType || 'biweekly',
    hoursWorked: hrsWorked,
  });
}

async function updateTimeOffLevel(employeeId, levelId) {
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'people', employeeId), {
      hrTimeOffLevel: levelId || null
    });
    const emp = HRState.people.find(p => p.id === employeeId);
    if (emp) emp.hrTimeOffLevel = levelId || null;
    window.showToast?.('Time-off level updated', 'success');
    loadTimeOffBalance(employeeId);
    renderEmployeeDirectory();
    // TASK-9535 — the all-employee table and the per-employee headers show
    // the same balance; refresh them whichever screen changed the level.
    if (HRState.timeOffBalancesOpen) renderTimeOffBalancesTable();
    renderTimeOffList();
  } catch (err) {
    console.error('[HR] Error updating time-off level:', err);
    window.showToast?.('Error updating level', 'error');
  }
}

async function dismissTimeOffLevelSuggestion(employeeId, nextLevelId) {
  if (!employeeId || !nextLevelId) return;
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'people', employeeId), {
      hrTimeOffLevelSuggestionDismissed: nextLevelId
    });
    const emp = HRState.people.find(p => p.id === employeeId);
    if (emp) emp.hrTimeOffLevelSuggestionDismissed = nextLevelId;
    window.showToast?.('Suggestion dismissed', 'success');
    loadTimeOffBalance(employeeId);
    renderEmployeeDirectory();
  } catch (err) {
    console.error('[HR] Error dismissing time-off level suggestion:', err);
    window.showToast?.('Error dismissing suggestion', 'error');
  }
}

// ============================================
// TASK-9535 — ONE balance math, two readers.
//
// computeEmployeeTimeOffBalances() is the per-category earned / used /
// remaining math that used to live inline in loadTimeOffBalance() (the
// employee detail "Time Off Balance" tab). It was pulled out so the
// all-employee balance table (Lacey: "calculate each person's PTO, sick,
// whatever else time has left") reads the SAME function and the two screens
// can never disagree. Pure: every input is a parameter with an HRState
// default, so the node tripwire runs it with no DOM and no Firestore.
//
// Returns:
//   { status: 'ok', level, empType, year, categories, rows: [{ id, label,
//     earned, used, remaining, methodLabel }], used, approvedRequests }
//   or { status: 'no_policy' | 'no_employment_type' | 'no_level' |
//        'level_missing' } when the tab would show an explainer instead.
// ============================================
function computeEmployeeTimeOffBalances(emp, opts = {}) {
  const policy = Object.prototype.hasOwnProperty.call(opts, 'policy') ? opts.policy : HRState.timeOffPolicy;
  const requests = Object.prototype.hasOwnProperty.call(opts, 'requests') ? (opts.requests || []) : (HRState.timeOffRequests || []);
  // Two-arg callers keep reading HRState.employeeClockHours (TASK-9535).
  const clockHours = (typeof opts.clockHours === 'number') ? opts.clockHours : (HRState.employeeClockHours || 0);
  // TASK-9839 — the math is the shared calculator (window.TimeOffBalance,
  // generated from functions/src/shared/timeOffBalance.js) so the driver modal
  // and the approval text show THIS number. Milestone grants come back in
  // `grants`. Tenant time zone decides which day a hire anniversary lands on.
  const TOB = window.TimeOffBalance;
  if (!TOB || typeof TOB.computeBalances !== 'function') return { status: 'unavailable' };
  return TOB.computeBalances(emp, policy, requests, {
    year: opts.year,
    tz: _hrTenantTimeZone(),
    payPeriodType: window.AppState?.settings?.payPeriodType || 'biweekly',
    clockHours,
  });
}

// TASK-9839 — the tenant's zone (settings.timezone, else the tenant doc's
// timezone, else Central — the same fallback chain the server uses).
function _hrTenantTimeZone() {
  return window.AppState?.settings?.timezone || window.AppState?.tenantData?.timezone || 'America/Chicago';
}

// TASK-9535 — the all-employee balance table. Pure aggregation over
// computeEmployeeTimeOffBalances(): one row per ACTIVE person (not inactive,
// not terminated — same predicate as the directory's "active" filter), sorted
// by name. A person without a level / employment type keeps a row with a
// `status` so the table can say "No level set — assign" instead of silently
// dropping them (TITS: 17 of 19 had no level when this was filed).
//
// Columns = every pool category any row earns or uses, always including
// vacation / sick / personal so the table reads the same every time.
function buildTimeOffBalanceTable(opts = {}) {
  const people = Object.prototype.hasOwnProperty.call(opts, 'people') ? (opts.people || []) : (HRState.people || []);
  const policy = Object.prototype.hasOwnProperty.call(opts, 'policy') ? opts.policy : HRState.timeOffPolicy;
  const requests = Object.prototype.hasOwnProperty.call(opts, 'requests') ? (opts.requests || []) : (HRState.timeOffRequests || []);
  const year = opts.year || new Date().getFullYear();
  const clockHoursById = opts.clockHoursById || {};

  const active = people
    .filter(p => p && p.active !== false && p.terminated !== true)
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const rows = active.map(p => {
    // ALWAYS a number here: a person with no completed shifts this year has 0
    // hours, never "fall back to HRState.employeeClockHours" (which is whichever
    // employee's tab was opened last — COD Code Reviewer 2026-10-02).
    const r = computeEmployeeTimeOffBalances(p, {
      policy, requests, year,
      clockHours: Number(clockHoursById[p.id]) || 0,
    });
    const cells = {};
    let total = null;
    if (r.status === 'ok') {
      total = { earned: 0, used: 0, remaining: 0 };
      r.rows.forEach(c => {
        cells[c.id] = { earned: c.earned, used: c.used, remaining: c.remaining, methodLabel: c.methodLabel };
        total.earned += c.earned; total.used += c.used; total.remaining += c.remaining;
      });
    }
    return {
      id: p.id,
      name: p.name || '',
      status: r.status,
      levelId: p.hrTimeOffLevel || null,
      levelName: r.status === 'ok' ? (r.level.name || '') : '',
      empType: p.hrEmploymentType || null,
      cells,
      total,
    };
  });

  const always = ['vacation', 'sick', 'personal'];
  const categories = TIME_OFF_CATEGORIES.filter(c => {
    if (c.id === 'bereavement') return false;
    if (always.includes(c.id)) return true;
    return rows.some(row => {
      const cell = row.cells[c.id];
      return cell && (cell.earned > 0 || cell.used > 0);
    });
  });

  return { year, categories, rows, policyConfigured: !!(policy?.levels?.length) };
}

async function loadTimeOffBalance(employeeId) {
  const host = document.getElementById('hr-timeoff-balance-content');
  if (!host) return;
  // TASK-9602 — balances keep the top slot; the year summary / strip / history
  // render into their own slot BELOW, so every early return in the balance
  // code (no policy, no level, no employment type) still leaves the history.
  host.innerHTML = '<div id="hr-timeoff-balance-part"></div><div id="hr-timeoff-history-part"></div>';
  renderEmployeeTimeOffHistory(employeeId);
  const container = document.getElementById('hr-timeoff-balance-part');
  if (!container) return;

  const emp = HRState.people.find(p => p.id === employeeId);
  if (!emp) { container.innerHTML = '<div class="empty-state-small">Employee not found</div>'; return; }

  const policy = HRState.timeOffPolicy;
  if (!policy?.levels?.length) {
    container.innerHTML = '<div class="empty-state-small"><span class="material-icons">info</span> No time-off policy configured. Create one from the HR landing page.</div>';
    return;
  }

  // Time-Off Level selector (always shown at top of tab)
  const policyLevels = (policy.levels || []).sort((a, b) => (a.yearThreshold || 0) - (b.yearThreshold || 0));
  const levelSelectHtml = `
    <div style="display:flex;align-items:center;gap:var(--spacing-sm);margin-bottom:var(--spacing-md);padding:10px;background:var(--bg-secondary);border-radius:var(--radius-md);border:1px solid var(--border-light);">
      <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">tune</span>
      <label style="font-size:0.82rem;font-weight:600;white-space:nowrap;">Time-Off Level:</label>
      <select id="hr-timeoff-level-select" style="flex:1;border:1px solid var(--border-light);border-radius:6px;padding:6px 10px;font-size:0.82rem;background:var(--bg-primary);color:var(--text-primary);"
        onchange="window.HRModule.updateTimeOffLevel('${employeeId}', this.value)">
        <option value="">No time-off plan</option>
        ${policyLevels.map(l => `<option value="${l.id}" ${emp.hrTimeOffLevel === l.id ? 'selected' : ''}>${escapeHtml(l.name)} (${l.yearThreshold || 0}+ yrs)</option>`).join('')}
      </select>
    </div>`;

  // TASK-9838 — "No time-off plan" is an explicit choice: no level means no
  // balance anywhere and the person can only request Unpaid time off.
  if (!emp.hrTimeOffLevel || !emp.hrEmploymentType) {
    container.innerHTML = levelSelectHtml + '<div class="empty-state-small"><span class="material-icons">info</span> ' +
      (!emp.hrEmploymentType ? 'Set Employment Type in the Employment tab first.' : 'No time-off plan — this person can only request Unpaid time off. Pick a Time-Off Level above to put them on the plan.') + '</div>';
    return;
  }

  const level = policy.levels.find(l => l.id === emp.hrTimeOffLevel);
  if (!level) {
    container.innerHTML = '<div class="empty-state-small">Assigned level not found in policy</div>';
    return;
  }

  const empType = emp.hrEmploymentType; // 'salary' or 'hourly'
  const allowances = level[empType] || {};

  // Load clock hours for per-hours-worked accrual (if any category uses it)
  const accrualConfig = allowances._accrual || {};
  const needsClockHours = Object.values(accrualConfig).some(a => a.method === 'per_hours_worked');
  HRState.employeeClockHours = 0;
  if (needsClockHours) {
    try {
      const currentYear = new Date().getFullYear();
      const yearStart = new Date(currentYear, 0, 1);
      const clockSnap = await getDocs(query(
        collection(db, 'tenants', HRState.tenantId, 'clockEntries'),
        where('personId', '==', employeeId),
        where('status', '==', 'completed')
      ));
      let totalHrs = 0;
      clockSnap.docs.forEach(d => {
        const data = d.data();
        const clockDate = data.clockIn?.toDate?.() || new Date(data.clockIn);
        if (clockDate >= yearStart) totalHrs += (data.totalHours || data.hours || 0);
      });
      HRState.employeeClockHours = totalHrs;
    } catch (err) {
      console.warn('[HR] Could not load clock hours for accrual:', err);
    }
  }

  // TASK-9535 — the per-category earned / used / remaining math lives in
  // computeEmployeeTimeOffBalances() (shared with the all-employee table).
  // Clock hours for per-hours-worked accrual were loaded into
  // HRState.employeeClockHours just above; pass them explicitly.
  const currentYear = new Date().getFullYear();
  const bal = computeEmployeeTimeOffBalances(emp, { year: currentYear, clockHours: HRState.employeeClockHours || 0 });
  if (bal.status !== 'ok') {
    // Every non-ok status was already handled by the guards above; this is a
    // belt-and-braces refusal so a future guard change can't render NaN rows.
    container.innerHTML = levelSelectHtml + '<div class="empty-state-small">Balance unavailable</div>';
    return;
  }
  const approvedRequests = bal.approvedRequests;

  // Check global bereavement first, then per-level (legacy)
  const globalBereavement = policy?.bereavement;
  const bereavementSource = (typeof globalBereavement === 'object' && globalBereavement !== null)
    ? globalBereavement
    : allowances.bereavement;
  const isTieredBereavement = typeof bereavementSource === 'object' && bereavementSource !== null;

  // Level-up check
  const levelUpInfo = getEmployeeLevelUpInfo(emp);

  let html = levelSelectHtml;

  if (levelUpInfo) {
    html += `<div style="background:rgba(255,193,7,0.1);border:1px solid var(--warning-amber);border-radius:var(--radius-md);padding:var(--spacing-sm) var(--spacing-md);margin-bottom:var(--spacing-md);display:flex;align-items:center;gap:var(--spacing-sm);flex-wrap:wrap;">
      <span class="material-icons" style="color:var(--warning-amber);">trending_up</span>
      <span style="font-size:0.85rem;flex:1;min-width:180px;"><strong>Level-up eligible:</strong> ${levelUpInfo.years} years qualifies for ${escapeHtml(levelUpInfo.nextLevelName)}</span>
      <div style="display:flex;gap:8px;">
        <button class="btn btn-sm btn-primary" style="min-height:36px;padding:6px 14px;font-size:0.82rem;" onclick="window.HRModule.updateTimeOffLevel('${employeeId}', '${levelUpInfo.nextLevelId}')">Apply ${escapeHtml(levelUpInfo.nextLevelName)}</button>
        <button class="btn btn-sm" style="min-height:36px;padding:6px 14px;font-size:0.82rem;background:transparent;border:1px solid var(--border-light);color:var(--text-secondary);" onclick="window.HRModule.dismissTimeOffLevelSuggestion('${employeeId}', '${levelUpInfo.nextLevelId}')">Dismiss</button>
      </div>
    </div>`;
  }

  html += `<div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:var(--spacing-sm);">
    <strong>${escapeHtml(level.name)}</strong> &middot; ${empType === 'salary' ? 'Salary' : 'Hourly'} &middot; ${currentYear}
  </div>`;

  html += `<table style="width:100%;border-collapse:collapse;font-size:0.85rem;">
    <thead>
      <tr style="border-bottom:2px solid var(--border-light);text-align:left;">
        <th style="padding:6px 8px;">Category</th>
        <th style="padding:6px 8px;text-align:center;">Earned</th>
        <th style="padding:6px 8px;text-align:center;">Used</th>
        <th style="padding:6px 8px;text-align:center;">Available</th>
      </tr>
    </thead>
    <tbody>`;

  bal.rows.forEach(row => {
    const { earned: earnedHrs, used: usedHrs, remaining, methodLabel } = row;
    const color = remaining < 0 ? 'var(--danger-red)' : remaining === 0 ? 'var(--warning-amber)' : 'var(--text-primary)';

    html += `<tr style="border-bottom:1px solid var(--border-light);">
      <td style="padding:6px 8px;">${row.label}${methodLabel ? `<span style="font-size:0.7rem;color:var(--text-secondary);">${methodLabel}</span>` : ''}</td>
      <td style="padding:6px 8px;text-align:center;">${Math.round(earnedHrs * 100) / 100} hrs</td>
      <td style="padding:6px 8px;text-align:center;">${usedHrs} hrs</td>
      <td style="padding:6px 8px;text-align:center;font-weight:600;color:${color};">${Math.round(remaining * 100) / 100} hrs</td>
    </tr>`;
  });

  html += '</tbody></table>';

  // TASK-9839 — where the milestone hours came from, with the day each landed.
  html += _renderTimeOffGrantHistory(bal);

  // Bereavement per-event reference section
  if (isTieredBereavement) {
    // Count bereavement events this year
    const bereavementEvents = approvedRequests.filter(r => r.type === 'bereavement');

    html += `<div style="margin-top:var(--spacing-md);border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-secondary);padding:var(--spacing-sm) var(--spacing-md);">
      <div style="font-size:0.85rem;font-weight:600;margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:4px;">
        <span class="material-icons" style="font-size:1rem;">favorite</span> Bereavement Policy
        <span style="font-weight:400;color:var(--text-secondary);font-size:0.75rem;margin-left:auto;">resets per event</span>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:0.8rem;">
        <thead>
          <tr style="border-bottom:1px solid var(--border-light);text-align:left;">
            <th style="padding:4px 8px;">Relationship</th>
            <th style="padding:4px 8px;text-align:center;">${(policy?.bereavementUnit === 'hours') ? 'Hrs/Event' : 'Days/Event'}</th>
          </tr>
        </thead>
        <tbody>`;

    const bUnit = policy?.bereavementUnit === 'hours' ? 'hr' : 'day';
    BEREAVEMENT_TIERS.forEach(t => {
      const val = bereavementSource[t.id] || 0;
      if (val > 0) {
        html += `<tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:4px 8px;color:var(--text-secondary);">${t.label}: ${t.desc}</td>
          <td style="padding:4px 8px;text-align:center;font-weight:600;">${val} ${bUnit}${val !== 1 ? 's' : ''}</td>
        </tr>`;
      }
    });

    if (bereavementSource.travelDays) {
      const tv = bereavementSource.travelDays;
      html += `<tr style="border-bottom:1px solid var(--border-light);">
        <td style="padding:4px 8px;color:var(--text-secondary);">+ Travel (funeral 250+ miles away)</td>
        <td style="padding:4px 8px;text-align:center;font-weight:600;">${tv} ${bUnit}${tv !== 1 ? 's' : ''}</td>
      </tr>`;
    }

    html += '</tbody></table>';

    if (bereavementEvents.length > 0) {
      html += `<div style="margin-top:var(--spacing-sm);font-size:0.75rem;color:var(--text-secondary);">
        ${bereavementEvents.length} bereavement event${bereavementEvents.length !== 1 ? 's' : ''} approved this year
      </div>`;
    }

    html += '</div>';
  }

  // TASK-341 follow-up: removed `buildPaidHolidaysHtml()` salaried-employee
  // fallthrough that rendered a hardcoded `PAID_HOLIDAYS_2026` federal-holiday
  // hint table. TASK-341 deliberately chose BLANK as the default for
  // `settings.companyHolidays` (per Architect + Skeptic risk reasoning —
  // pre-populating premiums guarantees payroll errors when admins forget to
  // prune). Showing the federal list as a "hint" contradicted that decision.
  // Real configured holidays live at `settings.companyHolidays` and surface
  // through their own HR Settings UI + the canonical
  // `window.AppHelpers.getCompanyHolidays()` reader.

  container.innerHTML = html;
}

// TASK-9839 — milestone grant history on the employee Time Off tab. Every
// grant this person has received (any year) with the date it landed and why,
// plus what is coming next, so a balance is explainable line by line. Grants
// are derived from the hire date + the plan; nothing here writes anything.
function _renderTimeOffGrantHistory(bal) {
  const g = bal && bal.grants;
  if (!g || !g.rules || !g.rules.length) return '';
  const fmt = (iso) => new Date(iso + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const box = 'margin-top:var(--spacing-md);border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-secondary);padding:var(--spacing-sm) var(--spacing-md);';
  const head = `<div style="font-size:0.85rem;font-weight:600;margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:4px;">
      <span class="material-icons" style="font-size:1rem;">flag</span> Milestone grants
      <span style="font-weight:400;color:var(--text-secondary);font-size:0.75rem;margin-left:auto;">from the hire date${bal.hireIso ? ' (' + fmt(bal.hireIso) + ')' : ''}</span>
    </div>`;
  if (!bal.hireIso) {
    return `<div style="${box}">${head}<div style="font-size:0.8rem;color:var(--text-primary);">This plan has milestone grants, but this person has no hire date. Add one in the Employment tab and the grants apply on their own.</div></div>`;
  }
  const row = (x, status) => `<tr style="border-bottom:1px solid var(--border-light);">
      <td style="padding:4px 8px;white-space:nowrap;">${fmt(x.on)}</td>
      <td style="padding:4px 8px;font-weight:600;">+${x.days} day${x.days === 1 ? '' : 's'} ${escapeHtml(x.categoryLabel)}</td>
      <td style="padding:4px 8px;color:var(--text-secondary);">${escapeHtml(x.reason)}</td>
      <td style="padding:4px 8px;color:var(--text-secondary);white-space:nowrap;">${status}</td>
    </tr>`;
  const landed = g.landed.slice().reverse().map(x => row(x, x.year === bal.year ? `Counted in ${bal.year}` : `${x.year} (reset)`)).join('');
  const next = g.upcoming.slice(0, 3).map(x => row(x, 'Coming up')).join('');
  if (!landed && !next) return '';
  return `<div style="${box}">${head}
    <table style="width:100%;border-collapse:collapse;font-size:0.8rem;"><tbody>${next}${landed}</tbody></table>
    <div style="margin-top:4px;font-size:0.72rem;color:var(--text-secondary);">Each grant counts in the calendar year it lands, like the rest of the plan.</div>
  </div>`;
}

// ---- TASK-9601: Time Off list core (PURE — vm-tested by tests/hr-timeoff-list-9601.unit.cjs) ----
//
// Everything the list needs to decide WHAT to show lives here, with no DOM, no
// HRState and no Firestore, so the notice / overlap / filter rules are unit
// tested instead of eyeballed. The day math is NOT copied in: callers pass
// enumerateRequestDays / hoursPerDayForRequest (TASK-9601 spec item 6), so
// excludedDates + dayTypes are respected exactly as the balance math does.
//
// Date model: startDate / endDate are calendar days stored at noon (HR writes
// T12:00Z, the driver portal local noon), and every placard already formats
// them in UTC — so a request's days are UTC date parts. createdAt is a real
// instant; its calendar day is the viewer's LOCAL date. Notice = whole calendar
// days between the two, from date parts, never ms / 86400000 (DST).
function _hrTimeOffListCoreFactory() {
  const DAY_MS = 86400000;
  const hasOwn = (o, k) => Object.prototype.hasOwnProperty.call(o, k);

  // CLOSED whitelists — a raw `type` / `source` / `status` is never put into a
  // label or a class name (stored values are free text from three writers).
  const TYPE_LABELS = {
    unpaid: 'Unpaid', vacation: 'Vacation', sick: 'Sick', personal: 'Personal',
    bereavement: 'Bereavement', parental_leave: 'Parental Leave', jury_duty: 'Jury Duty',
    days_on: 'Days On', other: 'Other',
  };
  const TYPE_COLORS = {
    unpaid: '#64748B', vacation: '#06B6D4', sick: '#E11D48', personal: '#8B5CF6',
    bereavement: '#334155', parental_leave: '#DB2777', jury_duty: '#0284C7',
    days_on: '#10B981', other: '#F59E0B',
  };
  // Protected leave never gets a notice flag (COD Domain lane): asking why a
  // sick day wasn't requested a week ahead is the retaliation pattern.
  const PROTECTED_TYPES = ['sick', 'bereavement', 'parental_leave', 'jury_duty'];
  const VIA_LABELS = { driver_pin: 'Driver PIN', self: 'Self-service', office: 'Office', link: 'Public link' };
  const VIA_ICONS = { driver_pin: 'smartphone', self: 'person', office: 'desk', link: 'link' };
  const STATUS_CHIPS = [
    { k: 'review', l: 'Needs review' },
    { k: 'upcoming', l: 'Upcoming' },
    { k: 'taken', l: 'Taken' },
    { k: 'denied', l: 'Denied' },
    { k: 'cancelled', l: 'Cancelled' },
  ];
  const WINDOWS = [
    { k: 'default', l: 'Current & recent' },
    { k: 'today', l: 'Off today' },
    { k: 'next7', l: 'Next 7 days' },
    { k: 'next30', l: 'Next 30 days' },
    { k: 'month', l: 'This month' },
    { k: 'year', l: 'This year' },
    { k: 'all', l: 'All time' },
  ];
  const GROUPS = ['none', 'employee', 'month'];
  const SORTS = [
    { k: 'start', l: 'Time-off date' },
    { k: 'submitted', l: 'Submitted (newest)' },
    { k: 'notice', l: 'Notice (shortest)' },
  ];
  const TILES = ['review', 'today', 'next7', 'short'];
  // TASK-10121: 2 — the default Group became 'employee'. A v1 blob is MIGRATED,
  // not dropped (see sanitizeStored): every other saved filter survives.
  const STORAGE_VERSION = 2;
  const SHORT_NOTICE_DAYS = 7;
  const HOURS_PER_DAY_MAX = 12;
  const RECENT_REVIEW_DAYS = 30;
  // A request spanning more than this is a data error, not a vacation; it is
  // never enumerated day-by-day for the overlap index (it would cost O(span)
  // on every snapshot) and simply never appears in an "Also off" line.
  const MAX_INDEX_SPAN_DAYS = 366;

  const isKey = (list, k) => list.some(x => (typeof x === 'string' ? x : x.k) === k);

  function toDate(v) {
    if (v === null || v === undefined || v === '') return null;
    let d = null;
    if (v && typeof v.toDate === 'function') d = v.toDate();
    else if (v instanceof Date) d = v;
    else if (v && typeof v === 'object' && typeof v.seconds === 'number') d = new Date(v.seconds * 1000);
    else d = new Date(v);
    return d && !isNaN(d.getTime()) ? d : null;
  }
  const utcDayNum = d => Math.round(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) / DAY_MS);
  const localDayNum = d => Math.round(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY_MS);
  function isoDayNum(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
    return m ? Math.round(Date.UTC(+m[1], +m[2] - 1, +m[3]) / DAY_MS) : null;
  }
  const dayNumToDate = n => new Date(n * DAY_MS); // UTC midnight — format with timeZone:'UTC'

  function startDay(req) { const d = toDate(req && req.startDate); return d ? utcDayNum(d) : null; }
  function endDay(req) {
    const s = startDay(req);
    const d = toDate(req && req.endDate);
    if (!d) return s;
    const e = utcDayNum(d);
    return (s !== null && e < s) ? s : e;
  }
  function submittedDay(req) { const d = toDate(req && req.createdAt); return d ? localDayNum(d) : null; }
  /** Whole calendar days from submission to the first day off; null when the
   *  row has no createdAt (5 legacy TITS rows) — those are excluded from every
   *  notice flag, sort and count rather than guessed. Negative = logged after. */
  function noticeDays(req) {
    const s = startDay(req), c = submittedDay(req);
    return (s === null || c === null) ? null : s - c;
  }
  function normType(t) {
    const k = (t === null || t === undefined || t === '') ? 'unpaid' : String(t);
    return hasOwn(TYPE_LABELS, k) ? k : 'other';
  }
  const typeLabel = t => TYPE_LABELS[normType(t)];
  const typeColor = t => TYPE_COLORS[normType(t)];
  const isProtectedType = t => PROTECTED_TYPES.indexOf(normType(t)) !== -1;

  /** "via" — a closed mapping of what the writers actually stamp; unknown → null (omit). */
  function viaKey(req) {
    if (!req) return null;
    const src = req.source, by = req.requestedBy;
    if (src === 'driver_pin') return 'driver_pin';
    if (src === 'spa_personal_action') return 'self';
    if (by === 'public-link') return 'link';
    if (typeof by === 'string' && (by === 'HR' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(by))) return 'office';
    return null;
  }

  function statusChip(req, todayDay) {
    const st = req && req.status;
    if (st === 'pending') return 'review';
    if (st === 'approved') { const e = endDay(req); return (e !== null && e >= todayDay) ? 'upcoming' : 'taken'; }
    if (st === 'denied') return 'denied';
    if (st === 'cancelled' || st === 'withdrawn') return 'cancelled';
    return null;
  }

  /** Informational flags (never block anything). */
  function flags(req, hoursPerDay) {
    const out = { shortNotice: false, loggedAfter: false, loggedAfterDays: 0, highHours: false };
    const st = req && req.status;
    const n = noticeDays(req);
    // days_on is extra work, not an absence — notice does not apply.
    if (n !== null && !isProtectedType(req && req.type) && normType(req && req.type) !== 'days_on' && st !== 'cancelled' && st !== 'withdrawn') {
      if (n < 0) { out.loggedAfter = true; out.loggedAfterDays = -n; }
      else if (n < SHORT_NOTICE_DAYS) out.shortNotice = true;
    }
    if (typeof hoursPerDay === 'number' && hoursPerDay > HOURS_PER_DAY_MAX) out.highHours = true;
    return out;
  }

  function personName(req) { return String((req && (req.personName || req.employeeName)) || 'Unknown'); }
  function personKey(req) {
    if (!req) return '';
    return req.personId || req.employeeId || ('name:' + personName(req).trim().toLowerCase());
  }
  function samePerson(a, b) {
    if (personKey(a) === personKey(b)) return true;
    const na = personName(a).trim().toLowerCase(), nb = personName(b).trim().toLowerCase();
    return !!na && na !== 'unknown' && na === nb;
  }
  function shortName(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'Unknown';
    if (parts.length === 1) return parts[0];
    return parts[0] + ' ' + parts[parts.length - 1].charAt(0).toUpperCase() + '.';
  }

  /** Days a request actually has someone OFF, via the caller's
   *  enumerateRequestDays: excluded days and `days_on` days (per-day dayTypes
   *  included) are not off. null when the span is implausible. */
  function activeDays(req, enumerate) {
    const s = startDay(req), e = endDay(req);
    if (s === null || e === null || e - s > MAX_INDEX_SPAN_DAYS) return null;
    const out = [];
    (enumerate(req) || []).forEach(d => {
      if (d && !d.isExcluded && normType(d.type) !== 'days_on') { const n = isoDayNum(d.iso); if (n !== null) out.push(n); }
    });
    return out;
  }

  /** ONE pass over the snapshot: day → requests that are off that day
   *  (approved + pending only; denied / cancelled are nobody's absence). */
  function buildDayIndex(requests, enumerate) {
    const byDay = new Map();
    const active = new Map();
    (requests || []).forEach(r => {
      if (!r || (r.status !== 'approved' && r.status !== 'pending')) return;
      const days = activeDays(r, enumerate);
      if (!days) return;
      active.set(r, new Set(days));
      days.forEach(n => {
        if (!byDay.has(n)) byDay.set(n, []);
        byDay.get(n).push(r);
      });
    });
    return { byDay, active };
  }

  /** Other people's approved / pending requests sharing a day off with `req`.
   *  Cached on the index (one per snapshot), so a keystroke never rescans. */
  function alsoOff(req, index, enumerate) {
    if (!index.alsoCache) index.alsoCache = new Map();
    if (index.alsoCache.has(req)) return index.alsoCache.get(req);
    const days = index.active.get(req) || new Set(activeDays(req, enumerate) || []);
    const seen = new Set();
    const out = [];
    days.forEach(n => {
      (index.byDay.get(n) || []).forEach(o => {
        if (o === req || seen.has(o) || samePerson(o, req)) return;
        seen.add(o);
        out.push(o);
      });
    });
    out.sort((a, b) => (startDay(a) - startDay(b)) || personName(a).localeCompare(personName(b)));
    index.alsoCache.set(req, out);
    return out;
  }

  /** TODAY'S default list, unchanged: every pending request, plus approved /
   *  denied whose time off is current or upcoming OR that was reviewed in the
   *  last 30 days (reviewedAt, falling back to updatedAt). */
  function isCurrentOrRecent(req, todayDay) {
    if (!req) return false;
    if (req.status === 'pending') return true;
    if (req.status !== 'approved' && req.status !== 'denied') return false;
    const e = endDay(req);
    if (e !== null && e >= todayDay) return true;
    const reviewed = toDate(req.reviewedAt) || toDate(req.updatedAt);
    return !!reviewed && localDayNum(reviewed) >= todayDay - RECENT_REVIEW_DAYS;
  }
  /** TASK-9535's one-employee default, unchanged: any status that starts this
   *  calendar year or is still upcoming / in progress, plus anything pending. */
  function isPersonDefault(req, todayDay, year) {
    if (!req) return false;
    if (req.status === 'pending') return true;
    const s = startDay(req), e = endDay(req);
    if (s !== null && dayNumToDate(s).getUTCFullYear() === year) return true;
    return e !== null && e >= todayDay;
  }

  function overlapsRange(req, from, to) {
    const s = startDay(req), e = endDay(req);
    return s !== null && e !== null && s <= to && e >= from;
  }
  function windowRange(win, todayDay) {
    const t = dayNumToDate(todayDay);
    const y = t.getUTCFullYear(), m = t.getUTCMonth();
    switch (win) {
      case 'today': return [todayDay, todayDay];
      case 'next7': return [todayDay, todayDay + 6];
      case 'next30': return [todayDay, todayDay + 29];
      case 'month': return [Math.round(Date.UTC(y, m, 1) / DAY_MS), Math.round(Date.UTC(y, m + 1, 0) / DAY_MS)];
      case 'year': return [Math.round(Date.UTC(y, 0, 1) / DAY_MS), Math.round(Date.UTC(y, 11, 31) / DAY_MS)];
      default: return null;
    }
  }
  /** Pending is NEVER hidden by the date window — only a status chip can drop it. */
  function inWindow(req, win, ctx) {
    if (!req) return false;
    if (req.status === 'pending') return true;
    if (win === 'all') return true;
    if (win === 'default' || !isKey(WINDOWS, win)) {
      return ctx.emp ? isPersonDefault(req, ctx.todayDay, ctx.year) : isCurrentOrRecent(req, ctx.todayDay);
    }
    const r = windowRange(win, ctx.todayDay);
    return overlapsRange(req, r[0], r[1]);
  }

  function matchesSearch(req, q) {
    const needle = String(q || '').trim().toLowerCase();
    if (!needle) return true;
    return personName(req).toLowerCase().indexOf(needle) !== -1
      || String((req && req.reason) || '').toLowerCase().indexOf(needle) !== -1;
  }

  /** Summary-strip predicates. Approved (+ pending where labelled) only;
   *  denied / cancelled never count, and `days_on` is not "off". */
  function tilePredicate(tile, ctx, index) {
    const offIn = (from, to) => r => {
      if (!r || r.status !== 'approved' || normType(r.type) === 'days_on') return false;
      const set = index.active.get(r);
      if (!set) return false;
      for (let n = from; n <= to; n++) if (set.has(n)) return true;
      return false;
    };
    switch (tile) {
      case 'review': return r => !!r && r.status === 'pending';
      case 'today': return offIn(ctx.todayDay, ctx.todayDay);
      case 'next7': return offIn(ctx.todayDay, ctx.todayDay + 6);
      case 'short': return r => {
        if (!r || r.status !== 'pending') return false;
        const f = flags(r);
        return f.shortNotice || f.loggedAfter;
      };
      default: return null;
    }
  }

  function uniquePeople(rows) {
    const seen = new Set(); const out = [];
    rows.forEach(r => { const k = personKey(r); if (!seen.has(k)) { seen.add(k); out.push(r); } });
    return out;
  }
  function summarize(requests, ctx, index) {
    const all = requests || [];
    const pick = k => all.filter(tilePredicate(k, ctx, index));
    const review = pick('review'), today = pick('today'), next7 = pick('next7'), short = pick('short');
    const names = rows => uniquePeople(rows).map(r => shortName(personName(r)));
    return [
      { k: 'review', icon: 'pending_actions', label: 'Needs review', value: review.length, names: names(review), empty: 'All caught up', warn: review.length > 0 },
      { k: 'today', icon: 'today', label: 'Off today', value: uniquePeople(today).length, names: names(today), empty: 'Full crew', warn: false },
      { k: 'next7', icon: 'date_range', label: 'Off next 7 days', value: uniquePeople(next7).length, names: names(next7), empty: 'Nobody', warn: false },
      { k: 'short', icon: 'bolt', label: 'Short notice', value: short.length, names: names(short), empty: 'None pending', warn: short.length > 0 },
    ];
  }

  function defaultFilters() {
    // TASK-10121 (Lacey, TITS): grouped by employee by default — name order,
    // pending first inside each block. The Group control still switches back.
    return { emp: '', status: [], types: [], win: 'default', grp: 'employee', sort: 'start', tile: '' };
  }
  /** What a tile click does to the filters (a second click on the same tile resets). */
  function applyTile(F, tile) {
    const next = Object.assign({}, F, { status: [], types: [], win: 'default', sort: 'start', tile: '' });
    if (!isKey(TILES, tile) || F.tile === tile) return next;
    next.tile = tile;
    if (tile === 'review') next.status = ['review'];
    if (tile === 'today') { next.win = 'today'; next.status = ['upcoming']; }
    if (tile === 'next7') { next.win = 'next7'; next.status = ['upcoming']; }
    if (tile === 'short') { next.status = ['review']; next.sort = 'notice'; }
    return next;
  }

  /** base = every filter except status + type (so chip counts stay honest);
   *  rows = base narrowed by the status / type chips. */
  function applyFilters(requests, F, ctx, index) {
    const tileFn = F.tile ? tilePredicate(F.tile, ctx, index) : null;
    const base = (requests || []).filter(r => r
      && inWindow(r, F.win, ctx)
      && (!F.emp || ctx.isForEmp(r))
      && matchesSearch(r, ctx.q)
      && (!tileFn || tileFn(r)));
    const rows = base.filter(r => (F.status.length === 0 || F.status.indexOf(statusChip(r, ctx.todayDay)) !== -1)
      && (F.types.length === 0 || F.types.indexOf(normType(r.type)) !== -1));
    return { base, rows };
  }
  function chipCounts(base, todayDay) {
    const status = {}; const types = {};
    STATUS_CHIPS.forEach(c => { status[c.k] = 0; });
    (base || []).forEach(r => {
      const s = statusChip(r, todayDay);
      if (s) status[s] += 1;
      const t = normType(r.type);
      types[t] = (types[t] || 0) + 1;
    });
    return { status, types };
  }
  /** Type chips: only types present in the data, in whitelist order. */
  function presentTypes(requests) {
    const seen = new Set((requests || []).map(r => normType(r && r.type)));
    return Object.keys(TYPE_LABELS).filter(k => seen.has(k));
  }
  function activeFilterCount(F, q) {
    return (String(q || '').trim() ? 1 : 0) + (F.emp ? 1 : 0) + F.status.length + F.types.length
      + (F.win !== 'default' ? 1 : 0) + (F.tile === 'short' ? 1 : 0);
  }

  function sortRows(rows, sort) {
    const created = r => { const d = toDate(r.createdAt); return d ? d.getTime() : null; };
    const byStart = (a, b) => ((startDay(a) || 0) - (startDay(b) || 0)) || personName(a).localeCompare(personName(b));
    const nullsLast = (va, vb) => (va === null && vb === null) ? 0 : va === null ? 1 : vb === null ? -1 : null;
    const out = rows.slice();
    if (sort === 'submitted') {
      out.sort((a, b) => { const va = created(a), vb = created(b); const nl = nullsLast(va, vb); return nl !== null ? (nl || byStart(a, b)) : ((vb - va) || byStart(a, b)); });
    } else if (sort === 'notice') {
      out.sort((a, b) => { const va = noticeDays(a), vb = noticeDays(b); const nl = nullsLast(va, vb); return nl !== null ? (nl || byStart(a, b)) : ((va - vb) || byStart(a, b)); });
    } else {
      out.sort(byStart);
    }
    return out;
  }

  /** Stored blob → filters, or null to drop it (unknown version / not an object).
   *  Every field is re-validated against the whitelists; the search text is
   *  never stored, so it is never restored.
   *  TASK-10121 — a v1 blob is migrated: v1 saved EVERY field on ANY filter
   *  change, so its grp:'none' was usually just the old default riding along,
   *  not a choice — it is read as the new default ('employee'). A v1 'month'
   *  can only have come from a click, so it is kept. Everything else in the
   *  blob (employee, window, chips, sort) is kept too. */
  function sanitizeStored(raw) {
    let o = raw;
    if (typeof raw === 'string') { try { o = JSON.parse(raw); } catch (_) { return null; } }
    if (!o || typeof o !== 'object' || (o.v !== STORAGE_VERSION && o.v !== 1)) return null;
    const F = defaultFilters();
    if (typeof o.emp === 'string' && o.emp.length <= 200) F.emp = o.emp;
    if (Array.isArray(o.status)) F.status = o.status.filter((k, i, a) => isKey(STATUS_CHIPS, k) && a.indexOf(k) === i);
    if (Array.isArray(o.types)) F.types = o.types.filter((k, i, a) => typeof k === 'string' && hasOwn(TYPE_LABELS, k) && a.indexOf(k) === i);
    if (isKey(WINDOWS, o.win)) F.win = o.win;
    if (isKey(GROUPS, o.grp) && !(o.v === 1 && o.grp === 'none')) F.grp = o.grp;
    if (isKey(SORTS, o.sort)) F.sort = o.sort;
    if (isKey(TILES, o.tile)) F.tile = o.tile;
    return F;
  }
  function serialize(F) {
    return JSON.stringify({ v: STORAGE_VERSION, emp: F.emp, status: F.status, types: F.types, win: F.win, grp: F.grp, sort: F.sort, tile: F.tile });
  }

  return {
    TYPE_LABELS, STATUS_CHIPS, WINDOWS, GROUPS, SORTS, TILES, VIA_LABELS, VIA_ICONS, STORAGE_VERSION,
    toDate, localDayNum, isoDayNum, dayNumToDate, startDay, endDay, submittedDay, noticeDays,
    normType, typeLabel, typeColor, isProtectedType, viaKey, statusChip, flags,
    personName, personKey, samePerson, shortName, activeDays, buildDayIndex, alsoOff,
    isCurrentOrRecent, isPersonDefault, inWindow, matchesSearch, tilePredicate, summarize,
    defaultFilters, applyTile, applyFilters, chipCounts, presentTypes, activeFilterCount,
    sortRows, sanitizeStored, serialize,
  };
}
const _hrTimeOffList = _hrTimeOffListCoreFactory();

// ---- TASK-9602: one employee's time off (PURE — vm-tested by tests/hr-timeoff-person-9602.unit.cjs) ----
//
// Builds on the TASK-9601 core, never beside it: "off" is core.activeDays
// (excluded days and `days_on` days are NOT off), status buckets are
// core.statusChip, labels are core's closed whitelists, and the per-request
// Days / Hrs are the exact placard formula (non-excluded days from
// enumerateRequestDays × hoursPerDayForRequest). Callers pass both helpers in,
// so this factory holds no copy of the day math and no DOM / HRState.
function _hrTimeOffPersonCoreFactory(core) {
  const DAY_MS = 86400000;
  const STATUS_LABELS = {
    pending: 'Pending', approved: 'Approved', denied: 'Denied', cancelled: 'Cancelled',
    withdrawn: 'Withdrawn', expired: 'Expired',
  };
  const statusKey = s => (Object.prototype.hasOwnProperty.call(STATUS_LABELS, s) ? s : 'other');
  const statusLabel = s => STATUS_LABELS[statusKey(s)] || 'Unknown';

  function yearRange(year) {
    return [Math.round(Date.UTC(year, 0, 1) / DAY_MS), Math.round(Date.UTC(year, 11, 31) / DAY_MS)];
  }
  function touchesYear(req, year) {
    const s = core.startDay(req), e = core.endDay(req);
    if (s === null || e === null) return false;
    const [ys, ye] = yearRange(year);
    return s <= ye && e >= ys;
  }

  /** Same numbers as the 9601 placard: non-excluded days (a `days_on` day in
   *  the request still counts toward the REQUEST's days) and those days × the
   *  request's hours per day, to 2 dp. */
  function requestCounts(req, enumerate, hoursPerDay) {
    const days = (enumerate(req) || []).filter(d => d && !d.isExcluded).length;
    return { days, hours: Math.round(days * hoursPerDay(req) * 100) / 100 };
  }

  /** Every non-excluded day of an approved request inside [from, to], split
   *  into off-days (by type) and days-on. A span the 9601 index refuses as
   *  implausible (core.activeDays → null) is skipped here too, so the two
   *  surfaces never disagree. */
  function eachCountedDay(req, enumerate, from, to, fn) {
    if (core.activeDays(req, enumerate) === null) return;
    (enumerate(req) || []).forEach(d => {
      if (!d || d.isExcluded) return;
      const n = core.isoDayNum(d.iso);
      if (n === null || n < from || n > to) return;
      fn(n, core.normType(d.type));
    });
  }

  /** Approved days OFF in `year` — cross-year requests split by actual day.
   *  Calendar days, not request-days: two approved requests covering the same
   *  date count it ONCE (COD Code Reviewer). A date that is off in any
   *  approved request is off; it is a day on only when no request has it off. */
  function daysOffInYear(requests, year, enumerate) {
    const [ys, ye] = yearRange(year);
    const off = new Map(); // dayNum → type (first approved request wins)
    const on = new Set();
    (requests || []).forEach(r => {
      if (!r || r.status !== 'approved') return;
      eachCountedDay(r, enumerate, ys, ye, (n, t) => {
        if (t === 'days_on') { on.add(n); return; }
        if (!off.has(n)) off.set(n, t);
      });
    });
    const byType = {};
    off.forEach(t => { byType[t] = (byType[t] || 0) + 1; });
    let daysOn = 0;
    on.forEach(n => { if (!off.has(n)) daysOn += 1; });
    return { daysOff: off.size, daysOn, byType };
  }

  /** First day >= todayDay that someone is OFF (core.activeDays — never a
   *  days_on or excluded day). Approved only unless includePending. */
  function nextOff(requests, todayDay, enumerate, includePending) {
    let best = null;
    (requests || []).forEach(r => {
      if (!r) return;
      if (r.status !== 'approved' && !(includePending && r.status === 'pending')) return;
      const days = core.activeDays(r, enumerate);
      if (!days) return;
      days.forEach(n => {
        if (n < todayDay) return;
        if (!best || n < best.day || (n === best.day && best.req.status === 'pending' && r.status === 'approved')) best = { day: n, req: r };
      });
    });
    return best;
  }

  function median(nums) {
    if (!nums.length) return null;
    const a = nums.slice().sort((x, y) => x - y);
    const mid = Math.floor(a.length / 2);
    return a.length % 2 ? a[mid] : Math.round((a[mid - 1] + a[mid]) / 2);
  }

  /** Year summary for one person's requests (already filtered to that person). */
  function yearStats(requests, opts) {
    const { year, todayDay, enumerate } = opts;
    const inYear = (requests || []).filter(r => r && touchesYear(r, year));
    const off = daysOffInYear(requests, year, enumerate);
    let approved = 0, pending = 0, deniedCancelled = 0;
    inYear.forEach(r => {
      if (r.status === 'approved') approved += 1;
      else if (r.status === 'pending') pending += 1;
      else if (r.status === 'denied' || r.status === 'cancelled' || r.status === 'withdrawn') deniedCancelled += 1;
    });
    // Notice applies exactly where the 9601 flags apply: not protected leave,
    // not days_on, not cancelled, and only with a createdAt.
    const noticeRows = inYear.filter(r => core.noticeDays(r) !== null && !core.isProtectedType(r.type)
      && core.normType(r.type) !== 'days_on' && r.status !== 'cancelled' && r.status !== 'withdrawn');
    const shortCount = noticeRows.filter(r => { const f = core.flags(r); return f.shortNotice || f.loggedAfter; }).length;
    return {
      year,
      daysOff: off.daysOff,
      daysOn: off.daysOn,
      byType: off.byType,
      requests: inYear.length,
      approved,
      pending,
      deniedCancelled,
      medianNotice: median(noticeRows.map(core.noticeDays)),
      noticeCount: noticeRows.length,
      shortCount,
      next: nextOff(requests, todayDay, enumerate, true),
    };
  }

  /** Directory card line. daysOff is yearStats(...).daysOff, by construction. */
  function directorySummary(requests, opts) {
    const { year, todayDay, enumerate } = opts;
    const next = nextOff(requests, todayDay, enumerate, false);
    return {
      year,
      daysOff: daysOffInYear(requests, year, enumerate).daysOff,
      offToday: !!next && next.day === todayDay,
      nextOffDay: next ? next.day : null,
      pending: (requests || []).filter(r => r && r.status === 'pending').length,
    };
  }

  /** dayNum → { type, pending } for the 12-month strip. Approved + pending,
   *  excluded days blank; an approved day wins over a pending one. days_on
   *  days are drawn in their own colour (they are on the calendar, not off). */
  function yearCells(requests, year, enumerate) {
    const [ys, ye] = yearRange(year);
    const cells = new Map();
    (requests || []).forEach(r => {
      if (!r || (r.status !== 'approved' && r.status !== 'pending')) return;
      const pend = r.status === 'pending';
      eachCountedDay(r, enumerate, ys, ye, (n, t) => {
        const cur = cells.get(n);
        if (cur && !cur.pending && pend) return;
        cells.set(n, { type: t, pending: pend });
      });
    });
    return cells;
  }

  /** Years worth offering in the selector: current year plus any year a request touches. */
  function yearsFor(requests, currentYear) {
    const ys = new Set([currentYear]);
    (requests || []).forEach(r => {
      const s = core.startDay(r), e = core.endDay(r);
      if (s === null || e === null || e - s > 366) return;
      for (let y = core.dayNumToDate(s).getUTCFullYear(); y <= core.dayNumToDate(e).getUTCFullYear(); y++) ys.add(y);
    });
    return [...ys].sort((a, b) => b - a);
  }

  /** Full history, ALL statuses, newest first (start date, then submitted). */
  function historyRows(requests, enumerate, hoursPerDay) {
    const created = r => { const d = core.toDate(r.createdAt); return d ? d.getTime() : 0; };
    return (requests || []).filter(Boolean).slice()
      .sort((a, b) => ((core.startDay(b) || 0) - (core.startDay(a) || 0)) || (created(b) - created(a)))
      .map(r => {
        const c = requestCounts(r, enumerate, hoursPerDay);
        return {
          req: r,
          start: core.startDay(r),
          end: core.endDay(r),
          days: c.days,
          hours: c.hours,
          type: core.normType(r.type),
          submitted: core.toDate(r.createdAt),
          notice: core.noticeDays(r),
          via: core.viaKey(r),
          status: statusKey(r.status),
          reviewer: String(r.reviewedByName || r.reviewedBy || ''),
          reviewedAt: core.toDate(r.reviewedAt),
          reason: typeof r.reason === 'string' ? r.reason.trim() : '',
        };
      });
  }

  const isoOfDay = n => (n === null || n === undefined) ? '' : core.dayNumToDate(n).toISOString().slice(0, 10);
  const isoLocal = d => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';
  function noticeText(n) {
    if (n === null || n === undefined) return '';
    if (n === 0) return 'same day';
    if (n > 0) return `${n} day${n === 1 ? '' : 's'}`;
    return `${-n} day${n === -1 ? '' : 's'} after`;
  }

  const CSV_HEADER = ['Employee', 'Submitted', 'Via', 'Start', 'End', 'Days', 'Hours', 'Type', 'Notice', 'Status', 'Reviewed by', 'Reviewed on', 'Reason'];
  /** Rows → CSV text. EVERY cell goes through `cell` (the caller passes
   *  csvSafeCell — the formula-injection guard), header included. */
  function toCsv(name, rows, baseCell) {
    // csvSafeCell checks only the FIRST character; a spreadsheet can still
    // evaluate " =HYPERLINK(...)" or "\u00a0@SUM". Prefix those too
    // (COD Adversary) before the shared guard runs.
    const cell = v => {
      const s = (v === null || v === undefined) ? '' : String(v);
      return baseCell(/^[\s\u0000-\u001f\u00a0\ufeff]+[=+\-@]/.test(s) ? "'" + s : s);
    };
    const lines = [CSV_HEADER.map(cell).join(',')];
    rows.forEach(r => {
      lines.push([
        name,
        isoLocal(r.submitted),
        r.via ? core.VIA_LABELS[r.via] : '',
        isoOfDay(r.start),
        isoOfDay(r.end),
        String(r.days),
        String(r.hours),
        core.typeLabel(r.type),
        noticeText(r.notice),
        statusLabel(r.status),
        r.reviewer,
        isoLocal(r.reviewedAt),
        r.reason,
      ].map(cell).join(','));
    });
    return lines.join('\r\n');
  }

  return {
    STATUS_LABELS, CSV_HEADER, statusKey, statusLabel, yearRange, touchesYear, requestCounts,
    daysOffInYear, nextOff, median, yearStats, directorySummary, yearCells, yearsFor,
    historyRows, noticeText, toCsv,
  };
}
const _hrTimeOffPerson = _hrTimeOffPersonCoreFactory(_hrTimeOffList);

// ---- TASK-9601: filter state (one versioned, per-tenant localStorage blob) ----

function _timeOffFiltersStorageKey() {
  return `dd.hr.timeOffFilters.${HRState.tenantId || 'default'}`;
}

/** The live filter object, loaded once per tenant. Storage can throw (private
 *  mode, blocked site data) — the list always renders with defaults then. */
function _timeOffFilters() {
  const tid = HRState.tenantId || 'default';
  if (HRState.timeOffFilters && HRState.timeOffFiltersTenant === tid) return HRState.timeOffFilters;
  let F = null;
  try {
    const raw = localStorage.getItem(_timeOffFiltersStorageKey());
    if (raw) {
      F = _hrTimeOffList.sanitizeStored(raw);
      if (!F) localStorage.removeItem(_timeOffFiltersStorageKey()); // stale version / junk — drop it
    }
  } catch (_) { F = null; }
  HRState.timeOffFilters = F || _hrTimeOffList.defaultFilters();
  HRState.timeOffFiltersTenant = tid;
  return HRState.timeOffFilters;
}

function _saveTimeOffFilters() {
  try { localStorage.setItem(_timeOffFiltersStorageKey(), _hrTimeOffList.serialize(_timeOffFilters())); } catch (_) {}
}

function _setTimeOffFilters(patch) {
  HRState.timeOffFilters = Object.assign({}, _timeOffFilters(), patch);
  _saveTimeOffFilters();
  renderTimeOffList();
}

function _timeOffListStyles() {
  return `<style>
    #hr-timeoff .hrto-tiles{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin-bottom:var(--spacing-sm);}
    #hr-timeoff .hrto-tile{box-sizing:border-box;text-align:left;cursor:pointer;border:1px solid var(--border-light);border-radius:var(--radius-md);padding:10px 12px;display:flex;flex-direction:column;gap:2px;background:var(--bg-primary);color:var(--text-primary);min-width:0;font:inherit;}
    #hr-timeoff .hrto-tile:hover{border-color:var(--border-medium);}
    #hr-timeoff .hrto-tile[aria-pressed="true"]{border-color:var(--brand-blue);box-shadow:inset 0 0 0 1px var(--brand-blue);}
    #hr-timeoff .hrto-tile .k{font-size:0.7rem;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:var(--text-secondary);display:flex;align-items:center;gap:4px;}
    #hr-timeoff .hrto-tile .k .material-icons{font-size:15px;}
    #hr-timeoff .hrto-tile .v{font-size:1.5rem;font-weight:700;font-variant-numeric:tabular-nums;}
    #hr-timeoff .hrto-tile.warn .v{color:#B45309;}
    #hr-timeoff .hrto-tile .s{font-size:0.75rem;color:var(--text-secondary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    #hr-timeoff .hrto-filters{display:flex;flex-direction:column;gap:10px;border:1px solid var(--border-light);border-radius:var(--radius-md);padding:12px;background:var(--bg-secondary);margin-bottom:var(--spacing-md);}
    #hr-timeoff .hrto-frow{display:flex;flex-wrap:wrap;gap:8px;align-items:center;}
    #hr-timeoff .hrto-chips{display:flex;flex-wrap:wrap;gap:6px;}
    #hr-timeoff .hrto-search{flex:1 1 220px;display:flex;align-items:center;gap:6px;background:var(--bg-primary);border:1px solid var(--border-medium);border-radius:6px;padding:0 10px;min-height:40px;min-width:0;}
    #hr-timeoff .hrto-search .material-icons{font-size:18px;color:var(--text-secondary);}
    #hr-timeoff .hrto-search input{border:0;background:transparent;box-shadow:none;color:var(--text-primary);font-size:16px;flex:1;min-width:0;outline:none;padding:0;margin:0;height:38px;}
    #hr-timeoff .hrto-select{min-height:40px;border:1px solid var(--border-medium);border-radius:6px;padding:6px 10px;font-size:0.85rem;background:var(--bg-primary);color:var(--text-primary);max-width:100%;}
    #hr-timeoff #hr-timeoff-person-filter{min-width:160px;max-width:320px;}
    #hr-timeoff .hrto-flabel{font-size:0.7rem;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:var(--text-secondary);}
    #hr-timeoff .hrto-chip{border:1px solid var(--border-medium);background:var(--bg-primary);color:var(--text-primary);border-radius:999px;padding:5px 11px;font-size:0.8rem;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;gap:6px;min-height:32px;}
    #hr-timeoff .hrto-chip .c{font-variant-numeric:tabular-nums;color:var(--text-secondary);}
    #hr-timeoff .hrto-chip[aria-pressed="true"]{background:#DBEAFE;border-color:var(--brand-blue);color:#1E40AF;}
    #hr-timeoff .hrto-chip[aria-pressed="true"] .c{color:#1E40AF;}
    #hr-timeoff .hrto-dot{width:8px;height:8px;border-radius:50%;display:inline-block;flex:none;}
    #hr-timeoff .hrto-seg{display:inline-flex;border:1px solid var(--border-medium);border-radius:6px;overflow:hidden;background:var(--bg-primary);}
    #hr-timeoff .hrto-seg button{border:0;background:transparent;color:var(--text-secondary);font-size:0.8rem;font-weight:600;padding:0 11px;min-height:38px;cursor:pointer;}
    #hr-timeoff .hrto-seg button+button{border-left:1px solid var(--border-medium);}
    #hr-timeoff .hrto-seg button[aria-pressed="true"]{background:#DBEAFE;color:#1E40AF;}
    #hr-timeoff .hrto-seg button:disabled{opacity:0.45;cursor:not-allowed;}
    #hr-timeoff .hrto-fsum{font-size:0.8rem;color:var(--text-secondary);display:flex;gap:10px;align-items:center;flex-wrap:wrap;}
    #hr-timeoff .hrto-linkbtn{border:0;background:none;color:var(--brand-blue-dark);font-size:0.8rem;font-weight:600;cursor:pointer;padding:4px;}
    #hr-timeoff .hrto-morebtn{display:none;}
    #hr-timeoff .hrto-glabel{font-weight:600;font-size:0.8rem;color:var(--text-secondary);margin:var(--spacing-sm) 0 var(--spacing-xs);text-transform:uppercase;letter-spacing:0.5px;}
    #hr-timeoff .hrto-flag{display:inline-block;border-radius:6px;padding:1px 7px;font-weight:700;font-size:0.68rem;margin-left:6px;vertical-align:middle;}
    #hr-timeoff .hrto-flag.warn{background:#FFFBEB;color:#92400E;border:1px solid #FCD34D;}
    #hr-timeoff .hrto-flag.bad{background:#FEF2F2;color:#991B1B;border:1px solid #FCA5A5;}
    #hr-timeoff .hrto-reason{font-size:0.82rem;color:var(--text-primary);margin-top:6px;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;overflow-wrap:anywhere;cursor:zoom-in;}
    #hr-timeoff .hrto-reason.open{display:block;-webkit-line-clamp:unset;cursor:zoom-out;}
    #hr-timeoff .hrto-foot{display:flex;flex-wrap:wrap;gap:4px 14px;font-size:0.74rem;color:var(--text-secondary);border-top:1px dashed var(--border-light);padding-top:6px;margin-top:8px;}
    #hr-timeoff .hrto-foot > span{display:inline-flex;align-items:center;gap:4px;}
    #hr-timeoff .hrto-foot .material-icons{font-size:14px;}
    #hr-timeoff .hrto-foot .hrto-overlap{color:var(--text-primary);}
    #hr-timeoff .hrto-empty{padding:24px;text-align:center;color:var(--text-secondary);border:1px dashed var(--border-medium);border-radius:var(--radius-md);}
    @media (max-width:720px){
      #hr-timeoff .hrto-tiles{grid-template-columns:repeat(2,minmax(0,1fr));}
      #hr-timeoff .hrto-morebtn{display:inline-flex;align-items:center;gap:4px;min-height:40px;}
      #hr-timeoff .hrto-filters .hrto-adv{display:none;}
      #hr-timeoff .hrto-filters.open .hrto-adv{display:flex;}
      #hr-timeoff #hr-timeoff-person-filter{max-width:none;flex:1 1 160px;}
    }
  </style>`;
}

function _timeOffWindowOptionsHtml() {
  return _hrTimeOffList.WINDOWS.map(w => `<option value="${w.k}">${escapeHtml(w.l)}</option>`).join('');
}
function _timeOffSortOptionsHtml() {
  return _hrTimeOffList.SORTS.map(s => `<option value="${s.k}">${escapeHtml(s.l)}</option>`).join('');
}

/** Memoized per snapshot: the overlap index only changes when the array does
 *  (onSnapshot replaces it), never per keystroke. */
function _timeOffDayIndex() {
  const src = HRState.timeOffRequests || [];
  const c = HRState.timeOffIndexCache;
  if (c && c.src === src) return c.index;
  const index = _hrTimeOffList.buildDayIndex(src, enumerateRequestDays);
  HRState.timeOffIndexCache = { src, index };
  return index;
}

function _timeOffUtcLabel(dayNum, opts) {
  return _hrTimeOffList.dayNumToDate(dayNum).toLocaleDateString('en-US', Object.assign({ month: 'short', day: 'numeric', timeZone: 'UTC' }, opts || {}));
}
/** "Oct 21", "Oct 21–30", "Oct 30–Nov 2" */
function _timeOffRangeLabel(req) {
  const s = _hrTimeOffList.startDay(req), e = _hrTimeOffList.endDay(req);
  if (s === null) return '';
  if (e === null || e === s) return _timeOffUtcLabel(s);
  const sd = _hrTimeOffList.dayNumToDate(s), ed = _hrTimeOffList.dayNumToDate(e);
  const sameMonth = sd.getUTCFullYear() === ed.getUTCFullYear() && sd.getUTCMonth() === ed.getUTCMonth();
  return `${_timeOffUtcLabel(s)}–${sameMonth ? ed.getUTCDate() : _timeOffUtcLabel(e)}`;
}

function _timeOffListContext() {
  const F = _timeOffFilters();
  const now = new Date();
  const todayDay = _hrTimeOffList.localDayNum(now);
  const person = F.emp ? ((HRState.people || []).find(p => p.id === F.emp) || null) : null;
  return {
    todayDay,
    year: now.getFullYear(),
    emp: F.emp,
    person,
    q: HRState.timeOffSearch || '',
    isForEmp: r => _timeOffRequestIsFor(r, F.emp, person),
  };
}

// ============================================
// TIME OFF FUNCTIONS
// ============================================
function renderTimeOffList() {
  const container = document.getElementById('hr-timeoff-list');
  const badge = document.getElementById('hr-timeoff-pending-count');
  if (!container) return;

  const all = HRState.timeOffRequests || [];
  const pendingCount = all.filter(r => r.status === 'pending').length;
  if (badge) {
    if (pendingCount > 0) {
      badge.textContent = pendingCount;
      badge.style.display = 'inline-flex';
    } else {
      badge.style.display = 'none';
    }
  }

  // TASK-9535 — keep the employee filter's options current (people + requests
  // both arrive by snapshot, in either order) without losing the selection.
  _syncTimeOffPersonFilterOptions();
  const F = _timeOffFilters();
  const ctx = _timeOffListContext();
  const index = _timeOffDayIndex();
  const core = _hrTimeOffList;

  _renderTimeOffSummary(core.summarize(all, ctx, index), F);
  const { base, rows } = core.applyFilters(all, F, ctx, index);
  _renderTimeOffFilterBar(F, base, rows.length, all);

  if (all.length === 0) {
    container.innerHTML = '<div class="empty-state-small">No time-off requests</div>';
    return;
  }

  const sorted = core.sortRows(rows, F.sort);
  const cardCtx = { todayDay: ctx.todayDay, index };
  const card = req => renderTimeOffPlacard(req, req.status === 'pending' ? 'pending' : req.status, cardCtx);
  const label = text => `<div class="hr-timeoff-group-label hrto-glabel">${text}</div>`;
  const defaultView = F.win === 'default' && F.status.length === 0 && F.types.length === 0 && !F.tile && !ctx.q.trim();

  let html = '';
  if (F.emp) html += _renderTimeOffPersonHeader(ctx.person || { id: F.emp, name: '' }, rows.length);

  if (rows.length === 0) {
    if (F.emp && defaultView) {
      html += `<div class="empty-state-small">No time-off requests for ${escapeHtml(ctx.person?.name || 'this employee')} in ${ctx.year}</div>`;
    } else {
      html += `<div class="hrto-empty">No requests match these filters. <button type="button" class="hrto-linkbtn" onclick="window.HRModule.clearTimeOffFilters()">Clear filters</button></div>`;
    }
    container.innerHTML = html;
    return;
  }

  // TASK-10121 — the employee grouping is window.TimeOffGroup (timeOffGroup.js,
  // shared with Clock -> Time Off; loaded once by clock.js). If it somehow is
  // not loaded, the flat list renders rather than nothing.
  const TG = window.TimeOffGroup;
  const grp = (F.grp === 'employee' && (F.emp || !TG)) ? 'none' : F.grp;
  if (grp === 'employee') {
    // One block per employee (name order), pending first inside each block.
    TG.byEmployee(sorted).forEach((g, idx) => {
      const person = (HRState.people || []).find(p => p.id === g.personId) || (HRState.people || []).find(p => p.name === g.name) || null;
      if (idx > 0) html += '<div style="margin-top: var(--spacing-md);"></div>';
      html += _renderTimeOffPersonHeader(person || { id: g.personId, name: g.name }, g.items.length);
      g.items.forEach(r => { html += card(r); });
    });
  } else if (grp === 'month') {
    const months = new Map();
    sorted.forEach(req => {
      const s = core.startDay(req);
      const key = s === null ? 'Undated' : core.dayNumToDate(s).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
      if (!months.has(key)) months.set(key, []);
      months.get(key).push(req);
    });
    months.forEach((list, key) => {
      html += label(`${escapeHtml(key)} &middot; ${list.length}`);
      list.forEach(r => { html += card(r); });
    });
  } else {
    const pend = sorted.filter(r => r.status === 'pending');
    let rest = sorted.filter(r => r.status !== 'pending');
    // One employee, default window + date sort: TASK-9535 listed their history
    // newest first — keep that (COD Code Reviewer).
    if (F.emp && F.win === 'default' && F.sort === 'start') rest = rest.slice().reverse();
    if (pend.length) {
      html += label(`Needs review &middot; ${pend.length}`);
      pend.forEach(r => { html += card(r); });
    }
    if (rest.length) {
      if (pend.length) html += '<div style="margin-top: var(--spacing-md);"></div>';
      const restLabel = F.win !== 'default'
        ? 'Approved, denied &amp; other'
        : (F.emp ? `${ctx.year} &amp; upcoming &middot; Approved, denied &amp; other` : 'Approved &amp; Recent');
      html += label(restLabel);
      rest.forEach(r => { html += card(r); });
    }
  }

  container.innerHTML = html;
}

/** Summary strip — counts come from the whole snapshot, never the filters. */
function _renderTimeOffSummary(tiles, F) {
  const el = document.getElementById('hr-timeoff-summary');
  if (!el) return;
  el.innerHTML = tiles.map(t => {
    const sub = t.names.length ? t.names.join(', ') : t.empty;
    return `<button type="button" class="hrto-tile${t.warn ? ' warn' : ''}" data-tile="${t.k}" aria-pressed="${F.tile === t.k ? 'true' : 'false'}" onclick="window.HRModule.toggleTimeOffTile('${escJsAttrLocal(t.k)}')" title="${escapeHtml(sub)}">
      <span class="k"><span class="material-icons" aria-hidden="true">${t.icon}</span>${escapeHtml(t.label)}</span>
      <span class="v">${t.value}</span>
      <span class="s">${escapeHtml(sub)}</span>
    </button>`;
  }).join('');
}

function _renderTimeOffFilterBar(F, base, shown, all) {
  const core = _hrTimeOffList;
  const counts = core.chipCounts(base, _timeOffListContext().todayDay);
  const statusEl = document.getElementById('hr-timeoff-status-chips');
  if (statusEl) {
    statusEl.innerHTML = core.STATUS_CHIPS.map(c => `<button type="button" class="hrto-chip" data-status="${c.k}" aria-pressed="${F.status.indexOf(c.k) !== -1 ? 'true' : 'false'}" onclick="window.HRModule.toggleTimeOffStatus('${escJsAttrLocal(c.k)}')">${escapeHtml(c.l)} <span class="c">${counts.status[c.k] || 0}</span></button>`).join('');
  }
  const typeEl = document.getElementById('hr-timeoff-type-chips');
  if (typeEl) {
    const types = core.presentTypes(all);
    typeEl.innerHTML = types.length
      ? types.map(t => `<button type="button" class="hrto-chip" data-type="${t}" aria-pressed="${F.types.indexOf(t) !== -1 ? 'true' : 'false'}" onclick="window.HRModule.toggleTimeOffType('${escJsAttrLocal(t)}')"><span class="hrto-dot" style="background:${core.typeColor(t)};"></span>${escapeHtml(core.typeLabel(t))} <span class="c">${counts.types[t] || 0}</span></button>`).join('')
      : '<span style="font-size:0.8rem;color:var(--text-secondary);">No requests yet</span>';
  }
  const win = document.getElementById('hr-timeoff-window');
  if (win && win.value !== F.win) win.value = F.win;
  const sort = document.getElementById('hr-timeoff-sort');
  if (sort && sort.value !== F.sort) sort.value = F.sort;
  document.querySelectorAll('#hr-timeoff-group button[data-v]').forEach(b => {
    b.setAttribute('aria-pressed', b.getAttribute('data-v') === F.grp ? 'true' : 'false');
    // One employee picked → grouping by employee is meaningless (TASK-9535 disabled it too).
    if (b.getAttribute('data-v') === 'employee') b.disabled = !!F.emp;
  });
  const search = document.getElementById('hr-timeoff-search');
  if (search && document.activeElement !== search && search.value !== (HRState.timeOffSearch || '')) search.value = HRState.timeOffSearch || '';
  const n = core.activeFilterCount(F, HRState.timeOffSearch);
  const countEl = document.getElementById('hr-timeoff-filter-count');
  if (countEl) {
    countEl.textContent = `${shown} request${shown === 1 ? '' : 's'}`
      + (n ? ` · ${n} filter${n === 1 ? '' : 's'} on` : ' · pending plus current & recently reviewed');
  }
  const clear = document.getElementById('hr-timeoff-clear-filters');
  if (clear) clear.hidden = !n;
}

// ---- TASK-9535 helpers: requests by employee ------------------------------

/** True when `req` belongs to person `personId` — id match first, name fallback
 *  for legacy rows that stored only a name (same fallback the balance math uses). */
function _timeOffRequestIsFor(req, personId, person) {
  if (!req || !personId) return false;
  if (req.personId === personId || req.employeeId === personId) return true;
  if (req.personId || req.employeeId) return false;
  const nm = person?.name;
  return !!nm && (req.personName === nm || req.employeeName === nm);
}

/** Employee header used by the grouped view and the one-employee view: name,
 *  request count, and the pool balance (same math as the detail tab) when known.
 *  TASK-9601 — it deep-links to that employee's Time Off tab, and only for a
 *  viewer who can open the Employee Directory (`hr-directory` module) — the
 *  detail modal shows far more than time off. */
function _renderTimeOffPersonHeader(person, count) {
  const name = escapeHtml(person?.name || 'Unknown');
  let balance = '';
  // A per-hours-worked policy needs this person's own clock hours; without the
  // loaded map we show nothing rather than a number computed from someone
  // else's HRState.employeeClockHours.
  const needsClock = _timeOffPolicyNeedsClockHours(HRState.timeOffPolicy);
  const clockMap = HRState.timeOffClockHoursById;
  if (person?.id && (HRState.people || []).some(p => p.id === person.id) && (!needsClock || clockMap)) {
    const bal = computeEmployeeTimeOffBalances(person, needsClock ? { clockHours: Number(clockMap[person.id]) || 0 } : {});
    if (bal.status === 'ok') {
      const remaining = bal.rows.reduce((s, r) => s + r.remaining, 0);
      const color = remaining < 0 ? '#dc2626' : remaining === 0 ? '#d97706' : 'var(--text-secondary)';
      balance = `<span style="font-size:0.75rem;font-weight:600;color:${color};margin-left:auto;white-space:nowrap;">${Math.round(remaining * 100) / 100} hrs left</span>`;
    } else if (bal.status === 'no_level' || bal.status === 'no_employment_type') {
      balance = `<span style="font-size:0.72rem;color:#d97706;margin-left:auto;white-space:nowrap;">No level set</span>`;
    }
  }
  const linkable = person?.id && (HRState.people || []).some(p => p.id === person.id) && isSubModuleUnlocked('hr-directory');
  const open = linkable
    ? `role="button" tabindex="0" onclick="window.HRModule.openTimeOffForEmployee('${escJsAttrLocal(person.id)}')" onkeydown="if(event.key==='Enter'||event.key===' '){event.preventDefault();window.HRModule.openTimeOffForEmployee('${escJsAttrLocal(person.id)}')}" title="Open this employee's Time Off tab" style="cursor:pointer;"`
    : '';
  const linkHint = linkable
    ? `<span style="font-size:0.75rem;font-weight:600;color:var(--brand-blue-dark);white-space:nowrap;${balance ? '' : 'margin-left:auto;'}">Time Off tab &rarr;</span>`
    : '';
  return `<div class="hr-timeoff-person-header" data-person-id="${escapeHtml(person?.id || '')}" style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;padding:6px 10px;margin-bottom:var(--spacing-xs);background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:var(--radius-md);">
    <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">person</span>
    <span ${open}><strong style="font-size:0.88rem;">${name}</strong>
    <span style="font-size:0.75rem;color:var(--text-secondary);margin-left:6px;">${count} request${count !== 1 ? 's' : ''}</span></span>
    ${balance}
    ${linkHint}
  </div>`;
}

/** Deep link: employee detail → Time Off tab. openEmployeeDetail carries its
 *  own gates (compliance-only bounce, person must exist). */
async function openTimeOffForEmployee(personId) {
  if (!personId || !isSubModuleUnlocked('hr-directory')) return;
  await openEmployeeDetail(personId);
  if (HRState.selectedEmployee?.id === personId && document.getElementById('hr-detail-tab-timeoff-balance')) {
    switchDetailTab('timeoff-balance');
  }
}

// Attribute-safe id for an inline onclick (ids are Firestore doc ids, but a
// JS string inside an attribute needs its own escaper — lesson_escapehtml_is_not_attribute_safe).
function escJsAttrLocal(s) {
  if (typeof window.escJsAttr === 'function') return window.escJsAttr(s);
  // Fallback mirrors sharedFormat.js: JS-escape first, then HTML-attribute
  // escape INCLUDING `&`, or an id carrying `&#39;` is decoded by the HTML
  // parser into a quote before the JS runs (COD Adversary round 2).
  const js = String(s ?? '').replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\r/g, '\\r').replace(/\n/g, '\\n');
  return js.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Rebuild the employee filter's options: active people first, then inactive
 *  people who still have a request on file. Keeps the current selection. */
function _syncTimeOffPersonFilterOptions() {
  const select = document.getElementById('hr-timeoff-person-filter');
  if (!select) return;
  const F = _timeOffFilters();
  const people = HRState.people || [];
  const withRequests = new Set();
  const legacyNames = new Set(); // rows with no id at all — matched by name
  (HRState.timeOffRequests || []).forEach(r => {
    const id = r.personId || r.employeeId;
    if (id) withRequests.add(id);
    else if (r.personName || r.employeeName) legacyNames.add(r.personName || r.employeeName);
  });
  const opts = people
    .filter(p => (p.active !== false && p.terminated !== true) || withRequests.has(p.id) || legacyNames.has(p.name))
    .slice()
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  // TASK-9601 — the pick is now persisted, so only drop it once people have
  // actually loaded (an empty roster mid-load would otherwise wipe it).
  if (F.emp && people.length > 0 && !opts.some(p => p.id === F.emp)) {
    HRState.timeOffFilters = Object.assign({}, F, { emp: '' });
    _saveTimeOffFilters();
  }
  const sig = opts.map(p => p.id + '|' + (p.name || '') + '|' + (p.active === false || p.terminated === true ? 'i' : 'a')).join(';');
  if (select.dataset.sig !== sig) {
    select.innerHTML = '<option value="">All employees</option>' + opts.map(p => {
      const former = (p.active === false || p.terminated === true) ? ' (inactive)' : '';
      return `<option value="${escapeHtml(p.id)}">${escapeHtml(p.name || '')}${former}</option>`;
    }).join('');
    select.dataset.sig = sig;
  }
  select.value = _timeOffFilters().emp || '';
}

function setTimeOffPersonFilter(personId) {
  _setTimeOffFilters({ emp: String(personId || '') });
}

/** Kept for callers of the TASK-9535 checkbox API — maps onto the Group control. */
function setTimeOffGroupByPerson(on) {
  _setTimeOffFilters({ grp: on ? 'employee' : 'none' });
}

function setTimeOffGroup(g) {
  if (_hrTimeOffList.GROUPS.indexOf(g) === -1) return;
  _setTimeOffFilters({ grp: g });
}

function setTimeOffSearch(q) {
  HRState.timeOffSearch = String(q || '').slice(0, 200); // session only — never persisted
  renderTimeOffList();
}

function toggleTimeOffStatus(k) {
  if (!_hrTimeOffList.STATUS_CHIPS.some(c => c.k === k)) return;
  const F = _timeOffFilters();
  const status = F.status.indexOf(k) !== -1 ? F.status.filter(x => x !== k) : F.status.concat([k]);
  _setTimeOffFilters({ status, tile: '' });
}

function toggleTimeOffType(t) {
  if (!Object.prototype.hasOwnProperty.call(_hrTimeOffList.TYPE_LABELS, t)) return;
  const F = _timeOffFilters();
  const types = F.types.indexOf(t) !== -1 ? F.types.filter(x => x !== t) : F.types.concat([t]);
  _setTimeOffFilters({ types });
}

function setTimeOffWindow(w) {
  if (!_hrTimeOffList.WINDOWS.some(x => x.k === w)) return;
  _setTimeOffFilters({ win: w, tile: '' });
}

function setTimeOffSort(s) {
  if (!_hrTimeOffList.SORTS.some(x => x.k === s)) return;
  _setTimeOffFilters({ sort: s });
}

function toggleTimeOffTile(k) {
  HRState.timeOffFilters = _hrTimeOffList.applyTile(_timeOffFilters(), k);
  _saveTimeOffFilters();
  renderTimeOffList();
}

function clearTimeOffFilters() {
  const F = _timeOffFilters();
  // Group is a view choice, not a filter — it survives Clear (as in the mockup).
  HRState.timeOffFilters = Object.assign(_hrTimeOffList.defaultFilters(), { grp: F.grp });
  HRState.timeOffSearch = '';
  const search = document.getElementById('hr-timeoff-search');
  if (search) search.value = '';
  _saveTimeOffFilters();
  renderTimeOffList();
}

function toggleTimeOffMoreFilters() {
  const el = document.getElementById('hr-timeoff-filters');
  if (!el) return;
  const open = el.classList.toggle('open');
  document.getElementById('hr-timeoff-more-btn')?.setAttribute('aria-expanded', open ? 'true' : 'false');
}

// ---- TASK-9535: everyone's balances (allowed / used / remaining by category) ----

function toggleTimeOffBalances() {
  HRState.timeOffBalancesOpen = !HRState.timeOffBalancesOpen;
  const panel = document.getElementById('hr-timeoff-balances');
  if (panel) panel.style.display = HRState.timeOffBalancesOpen ? 'block' : 'none';
  const btn = document.getElementById('hr-timeoff-balances-btn');
  if (btn) {
    btn.setAttribute('aria-pressed', HRState.timeOffBalancesOpen ? 'true' : 'false');
    btn.style.background = HRState.timeOffBalancesOpen ? 'var(--brand-blue)' : 'transparent';
    btn.style.color = HRState.timeOffBalancesOpen ? '#fff' : 'var(--text-primary)';
    btn.style.borderColor = HRState.timeOffBalancesOpen ? 'var(--brand-blue)' : 'var(--border-color)';
    const lbl = btn.querySelector('.tob-btn-label');
    if (lbl) lbl.textContent = HRState.timeOffBalancesOpen ? 'Hide balances' : 'Balances';
  }
  if (HRState.timeOffBalancesOpen) {
    // Fresh clock hours on every open — shifts completed since the last look
    // must count (the cache lives only while the panel is open). Bumping the
    // generation orphans any load started before this open.
    HRState.timeOffClockHoursById = null;
    HRState.timeOffClockHoursError = false;
    HRState.timeOffClockGen = (HRState.timeOffClockGen || 0) + 1;
    HRState.timeOffClockPromise = null;
    renderTimeOffBalancesTable();
    // The panel sits BELOW the request list so opening it never buries the
    // pending queue (COD UX round 2); bring it into view instead.
    try { panel?.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (_) {}
  }
}

/** Load (or join the in-flight load of) this year's clock hours for the
 *  current generation. Resolves to the map, or null when the load failed or
 *  was orphaned by a newer open / tenant switch. One query at a time: the
 *  people / policy / timeOff snapshots can all ask while the panel is open. */
async function _ensureTimeOffClockHours() {
  if (HRState.timeOffClockHoursById) return HRState.timeOffClockHoursById;
  if (HRState.timeOffClockHoursError) return null;
  const gen = HRState.timeOffClockGen || 0;
  if (!HRState.timeOffClockPromise) {
    HRState.timeOffClockPromise = (async () => {
      try {
        const map = await _loadTimeOffClockHoursById();
        if (gen !== (HRState.timeOffClockGen || 0)) return null; // superseded
        HRState.timeOffClockHoursById = map;
        HRState.timeOffClockHoursError = false;
        return map;
      } catch (err) {
        if (gen !== (HRState.timeOffClockGen || 0)) return null;
        // NOT cached as {} — the next open retries. Meanwhile the table says so
        // in a banner (which prints too), not just a toast that scrolls away.
        console.warn('[HR] Could not load clock hours for the balance table:', err);
        HRState.timeOffClockHoursById = null;
        HRState.timeOffClockHoursError = true;
        return null;
      } finally {
        if (gen === (HRState.timeOffClockGen || 0)) HRState.timeOffClockPromise = null;
      }
    })();
  }
  return HRState.timeOffClockPromise;
}

/** Does any level, for any employment type, accrue a category per hours worked?
 *  Only then does the table need this year's clock hours per person. */
function _timeOffPolicyNeedsClockHours(policy) {
  return !!(policy?.levels || []).some(l => ['salary', 'hourly'].some(t => {
    const ac = l?.[t]?._accrual || {};
    return Object.values(ac).some(a => a && a.method === 'per_hours_worked');
  }));
}

/** This year's completed clock hours per person — one query (status + clockIn
 *  composite index exists), bucketed client-side. Same sum the detail tab makes
 *  per person (totalHours || hours, completed, clockIn >= Jan 1). */
async function _loadTimeOffClockHoursById() {
  const year = new Date().getFullYear();
  const yearStart = new Date(year, 0, 1);
  const byId = {};
  // orderBy matches the existing composite index (status ASC, clockIn DESC)
  // exactly — firestore.indexes.json — so this can never hit a missing-index error.
  const snap = await getDocs(query(
    collection(db, 'tenants', HRState.tenantId, 'clockEntries'),
    where('status', '==', 'completed'),
    where('clockIn', '>=', Timestamp.fromDate(yearStart)),
    orderBy('clockIn', 'desc')
  ));
  snap.docs.forEach(d => {
    const data = d.data();
    if (!data.personId) return;
    byId[data.personId] = (byId[data.personId] || 0) + (data.totalHours || data.hours || 0);
  });
  return byId;
}

const _fmtHrs = (n) => `${Math.round((Number(n) || 0) * 100) / 100}`;

async function renderTimeOffBalancesTable() {
  const panel = document.getElementById('hr-timeoff-balances');
  if (!panel || !HRState.timeOffBalancesOpen) return;

  const policy = HRState.timeOffPolicy;
  if (!policy?.levels?.length) {
    panel.innerHTML = `<div class="empty-state-small"><span class="material-icons">info</span> No time-off policy configured — set one up under Time-Off Policy, then balances show here.</div>`;
    return;
  }

  let clockHoursById = {};
  let warning = '';
  if (_timeOffPolicyNeedsClockHours(policy)) {
    if (!HRState.timeOffClockHoursById && !HRState.timeOffClockHoursError) {
      const gen = HRState.timeOffClockGen || 0;
      panel.innerHTML = '<div class="empty-state-small">Loading clock hours…</div>';
      await _ensureTimeOffClockHours();
      // A close, a reopen or a tenant switch while loading: that render owns the panel now.
      if (!HRState.timeOffBalancesOpen || gen !== (HRState.timeOffClockGen || 0)) return;
    }
    clockHoursById = HRState.timeOffClockHoursById || {};
    if (HRState.timeOffClockHoursError) {
      warning = 'Clock hours could not be loaded — categories that accrue per hours worked show 0 earned. Close and reopen Balances to retry.';
    }
  }

  const table = buildTimeOffBalanceTable({ clockHoursById });
  panel.innerHTML = _renderTimeOffBalancesHtml(table, { interactive: true, warning });
}

/** Shared markup for the in-app panel and the print view. `interactive`
 *  adds the assign-level control and the print button; `warning` is a
 *  banner shown in both (a printed sheet must carry the same caveat). */
function _renderTimeOffBalancesHtml(table, { interactive, warning } = {}) {
  const policyLevels = [...(HRState.timeOffPolicy?.levels || [])].sort((a, b) => (a.yearThreshold || 0) - (b.yearThreshold || 0));
  const cats = table.categories;
  const cellColor = (rem) => rem < 0 ? '#dc2626' : rem === 0 ? '#d97706' : 'inherit';

  const head = `<tr>
      <th class="tob-name">Employee</th>
      <th class="tob-level">Level</th>
      ${cats.map(c => `<th class="tob-cat">${escapeHtml(c.label)}<span class="tob-sub">allowed / used / left</span></th>`).join('')}
      <th class="tob-cat tob-total">Total<span class="tob-sub">allowed / used / left</span></th>
    </tr>`;

  const rows = table.rows.map(row => {
    if (row.status !== 'ok') {
      let why, fix = '';
      if (row.status === 'no_employment_type') {
        why = 'No employment type';
        if (interactive) fix = `<button class="btn btn-sm tob-fix" onclick="window.HRModule.openEmployeeDetail('${escJsAttrLocal(row.id)}')">Set in Employment tab</button>`;
      } else if (row.status === 'level_missing') {
        why = 'Assigned level no longer in policy';
        if (interactive) fix = _renderAssignLevelSelect(row.id, policyLevels);
      } else {
        why = 'No level set';
        if (interactive) fix = _renderAssignLevelSelect(row.id, policyLevels);
      }
      return `<tr class="tob-row tob-row-missing" data-person-id="${escapeHtml(row.id)}" data-status="${escapeHtml(row.status)}">
        <td class="tob-name" data-label="Employee"><strong>${escapeHtml(row.name)}</strong></td>
        <td class="tob-level tob-missing" data-label="Level" colspan="${cats.length + 2}">
          <span class="tob-why"><span class="material-icons" style="font-size:15px;vertical-align:-3px;">warning</span> ${why}${interactive ? ' — assign' : ''}</span> ${fix}
        </td>
      </tr>`;
    }
    // One wrapper span per cell so the stacked (390px) layout keeps "allowed / used / left" together.
    const cell = (c) => c
      ? `<span class="tob-cell"><span class="tob-n">${_fmtHrs(c.earned)}</span><span class="tob-sep">/</span><span class="tob-n">${_fmtHrs(c.used)}</span><span class="tob-sep">/</span><span class="tob-n tob-left" style="color:${cellColor(c.remaining)}">${_fmtHrs(c.remaining)}</span></span>`
      : '<span class="tob-cell tob-na">&mdash;</span>';
    return `<tr class="tob-row" data-person-id="${escapeHtml(row.id)}" data-status="ok">
      <td class="tob-name" data-label="Employee"><strong>${escapeHtml(row.name)}</strong></td>
      <td class="tob-level" data-label="Level"><span class="tob-cell">${escapeHtml(row.levelName)}<span class="tob-sub">${row.empType === 'salary' ? 'Salary' : 'Hourly'}</span></span></td>
      ${cats.map(c => `<td class="tob-cat" data-label="${escapeHtml(c.label)}" data-cat="${escapeHtml(c.id)}">${cell(row.cells[c.id])}</td>`).join('')}
      <td class="tob-cat tob-total" data-label="Total" data-cat="total">${cell(row.total)}</td>
    </tr>`;
  }).join('');

  const missing = table.rows.filter(r => r.status !== 'ok').length;
  const summary = `${table.rows.length} active employee${table.rows.length !== 1 ? 's' : ''}${missing ? ` &middot; <span style="color:#d97706;font-weight:600;">${missing} without a level</span>` : ''}`;

  const toolbar = interactive ? `
    <div class="tob-toolbar">
      <div>
        <div style="font-weight:700;font-size:0.95rem;">Time-off balances &middot; ${table.year}</div>
        <div style="font-size:0.78rem;color:var(--text-secondary);">${summary} &middot; hours, approved requests only &middot; same math as each employee's Time Off Balance tab</div>
      </div>
      <button class="btn btn-sm" onclick="window.HRModule.printTimeOffBalances()" style="background:transparent;border:1px solid var(--border-color);color:var(--text-primary);min-height:40px;">
        <span class="material-icons">print</span> Print
      </button>
    </div>` : '';

  const style = interactive ? `<style>
    #hr-timeoff-balances .tob-toolbar{display:flex;flex-wrap:wrap;gap:var(--spacing-sm);align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);}
    #hr-timeoff-balances .tob-wrap{overflow-x:auto;border:1px solid var(--border-light);border-radius:var(--radius-md);background:var(--bg-primary);}
    #hr-timeoff-balances table.tob{width:100%;border-collapse:collapse;font-size:0.82rem;}
    #hr-timeoff-balances table.tob th{position:sticky;top:0;background:var(--bg-secondary);text-align:left;padding:8px;border-bottom:2px solid var(--border-light);font-size:0.75rem;text-transform:uppercase;letter-spacing:0.4px;white-space:nowrap;}
    #hr-timeoff-balances table.tob td{padding:8px;border-bottom:1px solid var(--border-light);vertical-align:top;white-space:nowrap;}
    #hr-timeoff-balances .tob-sub{display:block;font-size:0.68rem;font-weight:400;text-transform:none;letter-spacing:0;color:var(--text-secondary);}
    #hr-timeoff-balances .tob-sep{color:var(--text-secondary);margin:0 3px;}
    #hr-timeoff-balances .tob-left{font-weight:700;}
    #hr-timeoff-balances .tob-na{color:var(--text-secondary);}
    #hr-timeoff-balances .tob-why{color:#b45309;font-weight:600;}
    #hr-timeoff-balances .tob-fix{min-height:36px;padding:4px 10px;font-size:0.78rem;border:1px solid var(--border-color);background:var(--bg-primary);color:var(--text-primary);border-radius:6px;margin-left:6px;}
    #hr-timeoff-balances .tob-total{background:rgba(14,116,144,0.06);}
    @media (max-width:600px){
      #hr-timeoff-balances table.tob thead{display:none;}
      #hr-timeoff-balances table.tob,#hr-timeoff-balances table.tob tbody,#hr-timeoff-balances table.tob tr,#hr-timeoff-balances table.tob td{display:block;width:100%;box-sizing:border-box;white-space:normal;}
      #hr-timeoff-balances table.tob tr{padding:8px 10px;border-bottom:1px solid var(--border-light);}
      #hr-timeoff-balances table.tob td{padding:3px 0;border:none;display:flex;justify-content:space-between;gap:8px;}
      #hr-timeoff-balances table.tob td.tob-name{display:block;font-size:0.95rem;padding-bottom:4px;}
      #hr-timeoff-balances table.tob td::before{content:attr(data-label);color:var(--text-secondary);font-size:0.75rem;flex:0 0 auto;}
      #hr-timeoff-balances table.tob td .tob-cell{text-align:right;}
      #hr-timeoff-balances table.tob td.tob-name::before{content:none;}
      #hr-timeoff-balances table.tob td.tob-missing{display:block;}
      #hr-timeoff-balances table.tob td.tob-missing::before{content:none;}
      #hr-timeoff-balances .tob-fix{margin:6px 0 0;display:block;width:100%;}
      #hr-timeoff-balances .tob-total{background:none;font-weight:600;}
    }
  </style>` : '';

  const banner = warning
    ? `<div class="tob-warning" role="alert" style="display:flex;gap:8px;align-items:flex-start;padding:8px 10px;margin-bottom:8px;border:1px solid #f59e0b;background:#fffbeb;color:#92400e;border-radius:6px;font-size:0.8rem;"><span class="material-icons" style="font-size:16px;">warning</span><span>${escapeHtml(warning)}</span></div>`
    : '';

  return `${style}${toolbar}${banner}<div class="tob-wrap"><table class="tob" id="hr-timeoff-balances-table"><thead>${head}</thead><tbody>${rows || `<tr><td colspan="${cats.length + 3}" style="text-align:center;color:var(--text-secondary);padding:14px;">No active employees</td></tr>`}</tbody></table></div>`;
}

function _renderAssignLevelSelect(personId, policyLevels) {
  return `<select class="tob-fix" aria-label="Assign time-off level" onchange="if(this.value)window.HRModule.assignTimeOffLevelFromTable('${escJsAttrLocal(personId)}', this.value)">
    <option value="">Assign level…</option>
    ${policyLevels.map(l => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.name)} (${l.yearThreshold || 0}+ yrs)</option>`).join('')}
  </select>`;
}

/** One-click fix from the table: same write as the detail tab's level picker. */
async function assignTimeOffLevelFromTable(personId, levelId) {
  if (!personId || !levelId) return;
  await updateTimeOffLevel(personId, levelId);
  renderTimeOffBalancesTable();
  renderTimeOffList();
}

/** Print-friendly view — the house printWindow pattern (popup-block toast,
 *  document.write, print when painted). HTML, not CSV, on purpose. */
async function printTimeOffBalances() {
  const policy = HRState.timeOffPolicy;
  if (!policy?.levels?.length) { window.showToast?.('No time-off policy configured', 'error'); return; }
  const needsClock = _timeOffPolicyNeedsClockHours(policy);
  if (needsClock) {
    // A printed sheet must not carry hours that went stale while the panel sat
    // open — refresh (or join the in-flight load) before printing.
    HRState.timeOffClockHoursById = null;
    HRState.timeOffClockHoursError = false;
    HRState.timeOffClockGen = (HRState.timeOffClockGen || 0) + 1;
    HRState.timeOffClockPromise = null;
    await _ensureTimeOffClockHours();
    if (HRState.timeOffBalancesOpen) renderTimeOffBalancesTable();
  }
  const clockHoursById = HRState.timeOffClockHoursById || {};
  const table = buildTimeOffBalanceTable({ clockHoursById });
  const warning = (needsClock && (HRState.timeOffClockHoursError || !HRState.timeOffClockHoursById))
    ? 'Clock hours were not loaded when this was printed — categories that accrue per hours worked show 0 earned.'
    : '';
  const body = _renderTimeOffBalancesHtml(table, { interactive: false, warning });
  const companyName = escapeHtml(window.AppState?.companyName || window.AppState?.tenantData?.name || 'Company');
  const generated = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

  const printWindow = window.open('', '_blank');
  if (!printWindow) { window.showToast?.('Popup blocked. Allow popups for this site to print.', 'error'); return; }
  printWindow.document.write(`<!DOCTYPE html>
    <html><head><title>Time-Off Balances ${table.year} - ${companyName}</title>
    <style>
      body{font-family:Arial,sans-serif;padding:20px 28px;color:#1a1a1a;font-size:12px;}
      .header{border-bottom:2px solid #333;padding-bottom:8px;margin-bottom:12px;display:flex;justify-content:space-between;align-items:flex-end;flex-wrap:wrap;gap:8px;}
      .header h1{margin:0;font-size:17px;text-transform:uppercase;letter-spacing:1px;}
      .header .meta{font-size:11px;color:#555;}
      table.tob{width:100%;border-collapse:collapse;}
      table.tob th{text-align:left;padding:6px;border-bottom:2px solid #333;font-size:10px;text-transform:uppercase;letter-spacing:.4px;}
      table.tob td{padding:6px;border-bottom:1px solid #ddd;vertical-align:top;}
      .tob-sub{display:block;font-size:9px;font-weight:400;text-transform:none;letter-spacing:0;color:#666;}
      .tob-sep{color:#888;margin:0 3px;}
      .tob-left{font-weight:700;}
      .tob-why{color:#b45309;font-weight:600;}
      .tob-na{color:#888;}
      .tob-total{background:#f3f6f8;}
      .footer{margin-top:14px;font-size:10px;color:#777;border-top:1px solid #ddd;padding-top:6px;}
      @media print{body{padding:10px 14px;} @page{size:landscape;margin:12mm;}}
    </style></head>
    <body>
      <div class="header">
        <div><h1>Time-Off Balances &middot; ${table.year}</h1><div class="meta">${companyName}</div></div>
        <div class="meta">Hours &middot; approved requests only &middot; printed ${escapeHtml(generated)}</div>
      </div>
      ${body}
      <div class="footer">Allowed / used / left per category. Same balance math as each employee's Time Off Balance tab in DispatchDeck. Bereavement is per event and not shown.</div>
    </body></html>`);
  printWindow.document.close();
  await _hrPrintWhenPainted(printWindow);
}

// TASK-1129 — reviewer attribution short form, mirroring billing.js
// _formatReadyByShort ("John Smith" → "J. Smith"). reviewedBy may be a bare
// email (legacy rows) — show its prefix. Local helper, NOT a cross-import
// (cache-buster pitfall #1); billing.js is untouched.
function _formatReviewerShort(name) {
  if (!name) return '';
  let s = String(name).trim();
  if (s.includes('@')) s = s.split('@')[0];
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0];
  return `${parts[0].charAt(0).toUpperCase()}. ${parts[parts.length - 1]}`;
}

/** Render one time-off entry as a clickable placard with a day strip.
 *  TASK-9601 — `ctx` ({ todayDay, index } from renderTimeOffList) adds the
 *  submitted / notice / via footer, the "Also off" coverage line and the
 *  informational flags. HR surface only — the driver portal never calls this. */
function renderTimeOffPlacard(req, status, ctx) {
  const core = _hrTimeOffList;
  const todayDay = ctx?.todayDay ?? core.localDayNum(new Date());
  const name = req.personName || 'Unknown';
  const start = req.startDate?.toDate ? req.startDate.toDate() : new Date(req.startDate);
  const end = req.endDate?.toDate ? req.endDate.toDate() : new Date(req.endDate);
  const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  const dateRange = startStr === endStr ? startStr : `${startStr} - ${endStr}`;
  // Closed whitelist — the raw stored type never reaches the label.
  const typeLabel = core.typeLabel(req.type);

  const days = enumerateDays(start, end);
  const excluded = new Set(req.excludedDates || []);
  // Day / hour counts through the shared helpers (excludedDates + dayTypes
  // respected — no new copy of the math).
  const reqDays = enumerateRequestDays(req);
  const activeCount = reqDays.filter(d => !d.isExcluded).length;
  const hrsPerDay = hoursPerDayForRequest(req);
  const effectiveHours = Math.round(activeCount * hrsPerDay * 100) / 100;
  const hrsLabel = ` &middot; ${activeCount} day${activeCount !== 1 ? 's' : ''} &middot; ${effectiveHours} hrs`;
  const isMultiDay = days.length > 1;

  // Informational flags (never block anything).
  const fl = core.flags(req, hrsPerDay);
  let flagHtml = '';
  if (fl.loggedAfter) flagHtml += `<span class="hrto-flag bad" title="Entered ${fl.loggedAfterDays} day${fl.loggedAfterDays !== 1 ? 's' : ''} after the time off started">Logged after</span>`;
  else if (fl.shortNotice) flagHtml += `<span class="hrto-flag warn" title="Submitted less than 7 days before the first day off">Short notice</span>`;
  if (fl.highHours) {
    const totalHrs = Math.round((Number(req.hours) || 0) * 100) / 100;
    flagHtml += `<span class="hrto-flag warn" title="Hours look high for the days requested">${totalHrs} hrs on ${days.length} day${days.length !== 1 ? 's' : ''}?</span>`;
  }

  // Footer: submitted + notice · via · Also off.
  const created = core.toDate(req.createdAt);
  const notice = core.noticeDays(req);
  let submittedText = 'Submitted &mdash;';
  if (created) {
    const when = created.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const nText = notice === null ? '' : notice > 0
      ? ` &middot; ${notice} day${notice !== 1 ? 's' : ''}&rsquo; notice`
      : notice === 0 ? ' &middot; same day' : ` &middot; ${-notice} day${notice !== -1 ? 's' : ''} after`;
    submittedText = `Submitted ${escapeHtml(when)}${nText}`;
  }
  const footParts = [`<span title="${created ? escapeHtml(created.toLocaleString()) : 'No submission date on file'}"><span class="material-icons" aria-hidden="true">schedule_send</span>${submittedText}</span>`];
  const via = core.viaKey(req);
  if (via) footParts.push(`<span><span class="material-icons" aria-hidden="true">${core.VIA_ICONS[via]}</span>via ${escapeHtml(core.VIA_LABELS[via])}</span>`);
  const endD = core.endDay(req);
  const showCoverage = ctx?.index && core.normType(req.type) !== 'days_on'
    && (status === 'pending' || (status === 'approved' && endD !== null && endD >= todayDay));
  if (showCoverage) {
    const others = core.alsoOff(req, ctx.index, enumerateRequestDays);
    if (others.length) {
      const list = others.map(o => `${escapeHtml(core.shortName(core.personName(o)))} (${escapeHtml(_timeOffRangeLabel(o))}${o.status === 'pending' ? ', pending' : ''})`).join(', ');
      footParts.push(`<span class="hrto-overlap"><span class="material-icons" aria-hidden="true">groups</span>Also off: ${list}</span>`);
    } else {
      footParts.push(`<span><span class="material-icons" aria-hidden="true">groups</span>Nobody else off</span>`);
    }
  }
  const footHtml = ctx ? `<div class="hrto-foot">${footParts.join('')}</div>` : '';

  // Full reason, 2-line clamp; tap to expand (stopPropagation — the card
  // click opens openDayPicker).
  const reasonText = typeof req.reason === 'string' ? req.reason.trim() : '';
  const reasonHtml = reasonText
    ? `<div class="hrto-reason" title="${escapeHtml(reasonText)}" onclick="event.stopPropagation();this.classList.toggle('open')">&ldquo;${escapeHtml(reasonText)}&rdquo;</div>`
    : '';

  // Color by status
  let bg = 'var(--bg-primary)', border = 'var(--border-color)', accent = '#94A3B8', label = '';
  if (status === 'pending') { bg = 'rgba(234, 179, 8, 0.08)'; border = 'rgba(234, 179, 8, 0.3)'; accent = '#eab308'; }
  else if (status === 'approved') { accent = '#16a34a'; label = 'Approved'; }
  else if (status === 'denied') { accent = '#dc2626'; label = 'Denied'; }
  // TASK-1350 — every status gets an explicit label so a placard can never
  // render unlabelled again and be mistaken for an approval.
  // The catch-all label is a CLOSED map, never the raw status string — `label`
  // is interpolated into innerHTML below (COD Code Reviewer catch).
  else if (status === 'cancelled') { accent = '#94A3B8'; label = 'Cancelled'; }
  else if (status === 'withdrawn') { accent = '#94A3B8'; label = 'Withdrawn'; }
  else if (status === 'expired') { accent = '#94A3B8'; label = 'Expired'; }
  else if (status) { accent = '#94A3B8'; label = 'Not approved'; }

  // Day strip — a chip per day, struck through if excluded
  const dayChips = days.map(d => {
    const isExcluded = excluded.has(d.iso);
    const isPast = d.date < new Date(new Date().setHours(0, 0, 0, 0));
    const chipBg = isExcluded ? 'transparent' : (isPast ? 'var(--bg-secondary)' : accent + '22');
    const chipColor = isExcluded ? 'var(--text-secondary)' : 'var(--text-primary)';
    const chipBorder = isExcluded ? '1px dashed var(--border-color)' : `1px solid ${accent}55`;
    const deco = isExcluded ? 'text-decoration: line-through;' : '';
    return `<span style="display:inline-flex;align-items:center;justify-content:center;min-width:32px;height:24px;padding:0 6px;font-size:0.7rem;font-weight:600;background:${chipBg};color:${chipColor};border:${chipBorder};border-radius:6px;${deco}">${d.label}</span>`;
  }).join('');

  const reqId = req.id;
  const safeName = escapeHtml(name);
  // TASK-9601 — every inline-onclick string arg goes through escJsAttr (an HTML
  // escaper does not make a JS string inside an attribute safe).
  const jsId = escJsAttrLocal(reqId);
  const clickHandler = `onclick="window.HRModule.openDayPicker('${jsId}')"`;
  const cursor = 'cursor: pointer;';

  let actions = '';
  if (status === 'pending') {
    actions = `
      <button class="btn btn-sm" style="background:#16a34a;color:#fff;border:none;padding:4px 10px;border-radius:var(--radius-sm);font-size:0.75rem;cursor:pointer;" onclick="event.stopPropagation();window.HRModule.approveTimeOff('${jsId}')">
        <span class="material-icons" style="font-size:14px;vertical-align:middle;">check</span> Approve
      </button>
      <button class="btn btn-sm" style="background:#dc2626;color:#fff;border:none;padding:4px 10px;border-radius:var(--radius-sm);font-size:0.75rem;cursor:pointer;" onclick="event.stopPropagation();window.HRModule.denyTimeOff('${jsId}')">
        <span class="material-icons" style="font-size:14px;vertical-align:middle;">close</span> Deny
      </button>
    `;
  } else {
    // TASK-1129 — "who approved/denied" attribution (ticket readyByName
    // parity, billing.js:6193 pattern). reviewedByName is stamped by
    // adminManageTimeOff going forward; legacy rows fall back to the
    // reviewedBy email; rows with neither render exactly as before.
    const reviewer = req.reviewedByName || req.reviewedBy || '';
    const reviewedAtD = req.reviewedAt?.toDate?.() || null;
    const reviewedShort = reviewedAtD ? reviewedAtD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
    const attributionFull = reviewer
      ? `${label} by ${reviewer}${reviewedAtD ? ' · ' + reviewedAtD.toLocaleString() : ''}`
      : '';
    const attributionLine = reviewer
      ? `<span title="${escapeHtml(attributionFull)}" style="font-size:0.65rem;color:var(--text-secondary);max-width:120px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;display:inline-block;">by ${escapeHtml(_formatReviewerShort(reviewer))}${reviewedShort ? ` &middot; ${escapeHtml(reviewedShort)}` : ''}</span>`
      : '';
    actions = `<div style="display:flex;flex-direction:column;align-items:flex-end;gap:2px;">
      <span style="font-size:0.7rem;font-weight:700;color:${accent};text-transform:uppercase;">${label}</span>
      ${attributionLine}
    </div>`;
  }
  // TASK-4720 — someone else's PENDING request: approve / deny only (same test as
  // _hrPendingLocked, inlined so this renderer stays self-contained for its tests).
  const _selfPid = (typeof window !== 'undefined' && window.AppState?.memberAccess?.personId) || null;
  const pendingLocked = status === 'pending' && !(_selfPid && req.personId === _selfPid);
  if (!pendingLocked) actions += `
    <button class="btn btn-sm" style="background:transparent;color:var(--text-secondary);border:1px solid var(--border-color);padding:4px 8px;border-radius:var(--radius-sm);font-size:0.75rem;cursor:pointer;" onclick="event.stopPropagation();window.HRModule.deleteTimeOff('${jsId}', '${escJsAttrLocal(name)}')" title="Delete entire request" aria-label="Delete request">
      <span class="material-icons" style="font-size:14px;vertical-align:middle;">delete</span>
    </button>
  `;

  const editHint = pendingLocked
    ? '<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:6px;">Pending: only the requester can change it</div>'
    : isMultiDay
    ? '<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:6px;">Click to edit type or days</div>'
    : '<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:6px;">Click to edit type</div>';

  // PTO balance preview — only for paid types, never for denied (denied = no impact)
  // or a legacy self-cancelled row (TASK-4720: withdrawn requests count for nothing).
  let balanceLine = '';
  if (status !== 'denied' && status !== 'cancelled') {
    const impact = getRequestBalanceImpact(req, status);
    if (impact) {
      const overdrawColor = '#dc2626';
      const okColor = impact.projected === 0 ? '#d97706' : '#16a34a';
      const color = impact.willOverdraw ? overdrawColor : okColor;
      const text = status === 'pending'
        ? `PTO: ${impact.remaining} hrs left &middot; approve = ${impact.projected} hrs ${impact.willOverdraw ? '(overdrawn)' : ''}`
        : `PTO: ${impact.projected} hrs remaining ${impact.willOverdraw ? '(overdrawn)' : ''}`;
      balanceLine = `<div style="font-size:0.72rem;font-weight:600;color:${color};margin-top:6px;display:flex;align-items:center;gap:4px;">
        <span class="material-icons" style="font-size:13px;">${impact.willOverdraw ? 'warning' : 'event_available'}</span>
        ${text}
      </div>`;
    }
  }

  // TASK-1136 — a denied placard shows the admin's deny reason (reviewNote),
  // escaped (free text). Legacy denied rows without a reason render as before.
  const denyReasonLine = (status === 'denied' && req.reviewNote)
    ? `<div style="font-size:0.75rem;color:#dc2626;margin-top:6px;display:flex;align-items:flex-start;gap:4px;">
        <span class="material-icons" style="font-size:13px;margin-top:1px;">info</span>
        <span>Reason: ${escapeHtml(req.reviewNote)}</span>
      </div>`
    : '';

  // Closed class list — a raw stored status never becomes a class name.
  const statusClass = ['pending', 'approved', 'denied', 'cancelled', 'withdrawn', 'expired'].includes(status) ? status : 'other';
  return `
    <div class="hr-timeoff-placard ${statusClass}" data-id="${escapeHtml(reqId)}" ${clickHandler} style="padding:var(--spacing-sm) var(--spacing-md);margin-bottom:var(--spacing-sm);background:${bg};border:1px solid ${border};border-left:4px solid ${accent};border-radius:var(--radius-md);${cursor}">
      <div style="display:flex;align-items:flex-start;gap:var(--spacing-sm);">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;font-size:0.9rem;">${safeName}${flagHtml}</div>
          <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">${dateRange}${hrsLabel} &middot; <span class="hrto-dot" style="background:${core.typeColor(req.type)};margin-right:3px;"></span>${escapeHtml(typeLabel)}</div>
          ${reasonHtml}
          <div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:8px;">${dayChips}</div>
          ${denyReasonLine}
          ${balanceLine}
          ${editHint}
          ${footHtml}
        </div>
        <div style="display:flex;align-items:center;gap:var(--spacing-xs);flex-shrink:0;">${actions}</div>
      </div>
    </div>
  `;
}

/** Enumerate days between start and end (inclusive). Returns [{iso, label, date}]. */
function enumerateDays(start, end) {
  const days = [];
  const cur = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const stop = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()));
  while (cur <= stop) {
    const iso = cur.toISOString().slice(0, 10);
    const label = cur.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    days.push({ iso, label, date: new Date(cur) });
    cur.setUTCDate(cur.getUTCDate() + 1);
  }
  return days;
}

/** Open the Edit Time Off modal — handles type editing (request-level + per-day) and day inclusion. */
// TASK-4720 (Chris 2026-10-22) — "only the requester; office approves or
// rejects". A PENDING request is not editable or deletable from HR unless it is
// the viewer's own (adminManageTimeOff refuses the same server-side).
function _hrPendingLocked(req) {
  if (!req || String(req.status || '').toLowerCase() !== 'pending') return false;
  const self = window.AppState?.memberAccess?.personId || null;
  return !(self && req.personId === self);
}
const _HR_PENDING_LOCKED_MSG = 'Only the person who asked for this time off can change or withdraw it while it is pending. You can approve or reject it.';

function openDayPicker(requestId) {
  const req = HRState.timeOffRequests.find(r => r.id === requestId);
  if (!req) return;
  if (_hrPendingLocked(req)) { window.showToast?.(_HR_PENDING_LOCKED_MSG, 'info'); return; }
  const start = req.startDate?.toDate ? req.startDate.toDate() : new Date(req.startDate);
  const end = req.endDate?.toDate ? req.endDate.toDate() : new Date(req.endDate);
  const days = enumerateDays(start, end);
  const isMultiDay = days.length > 1;

  const excluded = new Set(req.excludedDates || []);
  const savedDayTypes = req.dayTypes || {};
  const name = req.personName || 'Unknown';
  const currentType = req.type || 'unpaid';
  const hasMixedTypes = Object.keys(savedDayTypes).length > 0;

  // Find this person to determine eligible types (hourly vs salary)
  const person = (HRState.people || []).find(p => p.id === req.personId);
  const empType = person?.hrEmploymentType || 'hourly';
  const eligibleTypes = TIME_OFF_CATEGORIES.filter(c => {
    if (c.id === 'unpaid' && empType === 'salary') return false;
    return true;
  });
  // Always include the current type as an option, even if it would normally be hidden
  // (e.g. legacy salary employee with an existing unpaid request — don't silently reclassify on save).
  if (!eligibleTypes.find(c => c.id === currentType)) {
    const cur = TIME_OFF_CATEGORIES.find(c => c.id === currentType);
    if (cur) eligibleTypes.unshift(cur);
  }

  const typeOptionsHtml = eligibleTypes.map(c => `<option value="${c.id}">${escapeHtml(c.label)}</option>`).join('');

  // Build modal
  const existing = document.getElementById('modal-day-picker');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'modal-day-picker';
  modal.className = 'modal active';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:520px;">
      <div class="modal-header">
        <h2><span class="material-icons">edit_calendar</span> Edit Time Off &mdash; ${escapeHtml(name)}</h2>
        <button class="modal-close" onclick="window.HRModule.closeDayPicker()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body">
        <div class="form-group" id="day-picker-type-section" style="margin-bottom:var(--spacing-md);">
          <label for="day-picker-type" style="font-size:0.85rem;font-weight:600;color:var(--text-primary);margin-bottom:6px;display:block;">Type (whole request)</label>
          <select id="day-picker-type" class="form-control" data-original="${escapeHtml(currentType)}" data-request-id="${escapeHtml(requestId)}" onchange="window.HRModule.onDayPickerBaseTypeChange()">
            ${eligibleTypes.map(c => `<option value="${c.id}" ${c.id === currentType ? 'selected' : ''}>${escapeHtml(c.label)}</option>`).join('')}
          </select>
          <p style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">
            Reclassify the entire request. Unpaid &amp; Bereavement don't deduct from the paid PTO pool.
          </p>
        </div>
        ${isMultiDay ? `
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:var(--spacing-md);">
            <input type="checkbox" id="day-picker-perday-toggle" ${hasMixedTypes ? 'checked' : ''} onchange="window.HRModule.toggleDayPickerPerDay()">
            <label for="day-picker-perday-toggle" style="font-size:0.85rem;cursor:pointer;margin:0;">Edit individual days (mixed types)</label>
          </div>
        ` : ''}
        <div id="day-picker-balance" style="margin-bottom:var(--spacing-md);"></div>
        ${isMultiDay ? `
          <hr style="border:none;border-top:1px solid var(--border-light);margin:var(--spacing-md) 0;">
          <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:var(--spacing-md);">
            <strong>Active days</strong> &mdash; uncheck to remove a day from this request entirely.
          </p>
          <div id="day-picker-grid" style="display:flex;flex-direction:column;gap:6px;max-height:40vh;overflow-y:auto;">
            ${days.map(d => {
              const isExcluded = excluded.has(d.iso);
              const dayType = savedDayTypes[d.iso] || currentType;
              const dow = d.date.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
              const perDayHidden = hasMixedTypes ? '' : 'display:none;';
              return `
                <div data-iso="${d.iso}" style="display:flex;align-items:center;gap:10px;padding:8px 12px;background:var(--bg-secondary);border:1px solid var(--border-color);border-radius:var(--radius-sm);">
                  <input type="checkbox" class="day-picker-include" data-iso="${d.iso}" ${isExcluded ? '' : 'checked'} onchange="window.HRModule.refreshDayPickerBalance()" style="width:18px;height:18px;cursor:pointer;flex-shrink:0;">
                  <div style="flex:1;min-width:0;">
                    <div style="font-weight:600;font-size:0.9rem;">${dow}, ${d.label}</div>
                    <div style="font-size:0.7rem;color:var(--text-secondary);">${d.iso}</div>
                  </div>
                  <select class="day-picker-day-type control-auto" data-iso="${d.iso}" onchange="window.HRModule.refreshDayPickerBalance()" style="${perDayHidden}padding:4px 6px;border:1px solid var(--border-color);border-radius:var(--radius-sm);font-size:0.8rem;">
                    ${eligibleTypes.map(c => `<option value="${c.id}" ${c.id === dayType ? 'selected' : ''}>${escapeHtml(c.label)}</option>`).join('')}
                  </select>
                </div>
              `;
            }).join('')}
          </div>
        ` : `<div id="day-picker-grid" style="display:none;"></div>`}
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" onclick="window.HRModule.closeDayPicker()">Cancel</button>
        <button class="btn btn-primary" onclick="window.HRModule.saveDayPicker('${requestId}')">
          <span class="material-icons">save</span> Save
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  refreshDayPickerBalance();
}

/**
 * Show/hide per-day Type dropdowns when the "Edit individual days" toggle flips.
 * Also reset all per-day dropdowns to the current base type if turning OFF
 * (so saving doesn't accidentally persist stale per-day selections).
 */
function toggleDayPickerPerDay() {
  const toggle = document.getElementById('day-picker-perday-toggle');
  const baseSelect = document.getElementById('day-picker-type');
  const dayTypeSelects = document.querySelectorAll('.day-picker-day-type');
  if (!toggle) return;
  const showPerDay = toggle.checked;
  dayTypeSelects.forEach(sel => {
    sel.style.display = showPerDay ? '' : 'none';
    if (!showPerDay && baseSelect) sel.value = baseSelect.value;
  });
  refreshDayPickerBalance();
}

/**
 * When the request-level type changes, sync any per-day dropdowns that aren't
 * currently overriding (i.e. when per-day mode is OFF, propagate to all rows).
 */
function onDayPickerBaseTypeChange() {
  const toggle = document.getElementById('day-picker-perday-toggle');
  const baseSelect = document.getElementById('day-picker-type');
  if (baseSelect && (!toggle || !toggle.checked)) {
    document.querySelectorAll('.day-picker-day-type').forEach(sel => { sel.value = baseSelect.value; });
  }
  refreshDayPickerBalance();
}

/**
 * Read the live modal state — checkbox inclusions + per-day type dropdowns —
 * and build override maps suitable for `enumerateRequestDays`.
 */
function readDayPickerLiveState() {
  const baseSelect = document.getElementById('day-picker-type');
  const baseType = baseSelect?.value || null;
  const includeBoxes = document.querySelectorAll('.day-picker-include');
  const dayTypeSelects = document.querySelectorAll('.day-picker-day-type');
  const excludedOverride = new Set();
  includeBoxes.forEach(cb => { if (!cb.checked) excludedOverride.add(cb.dataset.iso); });
  const dayTypesOverride = {};
  dayTypeSelects.forEach(sel => { dayTypesOverride[sel.dataset.iso] = sel.value; });
  return { baseType, excludedOverride, dayTypesOverride };
}

/**
 * Recompute and re-render the balance preview block. Reads live state from
 * the modal so checkbox toggles and per-day dropdowns immediately reflect.
 *
 * Day-level math: for each non-excluded day, look at its effective type. Sum
 * paid-pool hours. Projected remaining = current pool remaining adjusted by
 * the delta between saved-state pool hours and live-state pool hours.
 */
function refreshDayPickerBalance() {
  const select = document.getElementById('day-picker-type');
  const target = document.getElementById('day-picker-balance');
  if (!select || !target) return;
  const requestId = select.dataset.requestId;
  const req = HRState.timeOffRequests.find(r => r.id === requestId);
  if (!req) { target.innerHTML = ''; return; }

  const emp = (HRState.people || []).find(p => p.id === req.personId)
    || (HRState.people || []).find(p => p.name === req.personName);
  const summary = emp ? getEmployeeTimeOffSummary(emp) : null;
  if (!summary) {
    target.innerHTML = `
      <div style="padding:8px 12px;background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.8rem;color:var(--text-secondary);display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:16px;">info</span>
        No PTO policy configured for this employee.
      </div>`;
    return;
  }

  const live = readDayPickerLiveState();
  const hpd = hoursPerDayForRequest(req);

  // Saved-state pool hours (what's currently counted in summary.totalUsed)
  // TASK-341 — mirror getEmployeeTimeOffSummary: paid company holidays don't
  // burn a PTO day, so they must drop out of the preview projection too. If
  // the preview math diverges from the calculator, the balance number jumps
  // when the day-picker closes — confused admins reach for the ticket form.
  const savedDays = enumerateRequestDays(req);
  const savedPoolHours = savedDays.reduce((sum, d) => {
    if (d.isExcluded) return sum;
    if (!isPaidPoolType(d.type)) return sum;
    if (window.AppHelpers?.isPaidHolidayForPerson?.(d.iso, emp)) return sum;
    return sum + hpd;
  }, 0);

  // Live-state pool hours (what would be counted if save fires now)
  const liveDays = enumerateRequestDays(req, {
    excludedOverride: live.excludedOverride,
    dayTypesOverride: live.dayTypesOverride,
  });
  const livePoolHours = liveDays.reduce((sum, d) => {
    if (d.isExcluded) return sum;
    if (!isPaidPoolType(d.type)) return sum;
    if (window.AppHelpers?.isPaidHolidayForPerson?.(d.iso, emp)) return sum;
    return sum + hpd;
  }, 0);

  // For approved: delta is live - saved. For pending/denied: nothing is in the
  // pool yet, so the projection is what would happen on approval.
  let projected;
  if (req.status === 'approved') {
    projected = summary.totalRemaining - (livePoolHours - savedPoolHours);
  } else {
    projected = summary.totalRemaining - livePoolHours;
  }

  const liveActiveDays = liveDays.filter(d => !d.isExcluded);
  const totalRequestHours = Math.round(liveActiveDays.length * hpd);
  const livePoolRounded = Math.round(livePoolHours);
  const liveNonPoolHours = Math.round(totalRequestHours - livePoolRounded);
  const projectedRounded = Math.round(projected);
  const willOverdraw = projectedRounded < 0;

  // Build per-type breakdown for the live state ("3d Vacation, 2d Unpaid")
  const byType = {};
  liveDays.forEach(d => { if (!d.isExcluded) byType[d.type] = (byType[d.type] || 0) + 1; });
  const typeLabel = {
    vacation: 'Vacation', sick: 'Sick', personal: 'Personal', unpaid: 'Unpaid',
    bereavement: 'Bereavement', parental_leave: 'Parental Leave', jury_duty: 'Jury Duty', other: 'Other'
  };
  const breakdown = Object.entries(byType)
    .map(([t, n]) => `${n}d ${escapeHtml(typeLabel[t] || t)}`)
    .join(' &middot; ');

  // No paid-pool days — render info card, but still show the breakdown
  if (livePoolRounded === 0) {
    const restored = req.status === 'approved' ? Math.round(savedPoolHours) : 0;
    target.innerHTML = `
      <div style="padding:8px 12px;background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.8rem;color:var(--text-secondary);display:flex;align-items:start;gap:6px;">
        <span class="material-icons" style="font-size:16px;margin-top:1px;">info</span>
        <div>
          <div>No paid-pool days in this request${breakdown ? ` (${breakdown})` : ''}.</div>
          <div>Current balance: <strong>${summary.totalRemaining} hrs remaining</strong>${restored > 0 ? ` &middot; would restore <strong>+${restored} hrs</strong> to pool on save` : ''}.</div>
        </div>
      </div>`;
    return;
  }

  const bg = willOverdraw ? 'rgba(220,38,38,0.08)' : (projectedRounded === 0 ? 'rgba(217,119,6,0.08)' : 'rgba(22,163,74,0.08)');
  const border = willOverdraw ? 'rgba(220,38,38,0.3)' : (projectedRounded === 0 ? 'rgba(217,119,6,0.3)' : 'rgba(22,163,74,0.3)');
  const color = willOverdraw ? '#dc2626' : (projectedRounded === 0 ? '#d97706' : '#16a34a');
  const icon = willOverdraw ? 'warning' : 'event_available';

  let label;
  if (req.status === 'approved') {
    const delta = Math.round(livePoolHours - savedPoolHours);
    if (delta === 0) label = `Type change has no balance impact &mdash; <strong>${projectedRounded} hrs remaining</strong>`;
    else if (delta > 0) label = `On save: deduct <strong>${delta}</strong> more &mdash; <strong>${projectedRounded} hrs remaining</strong> ${willOverdraw ? '(overdrawn)' : ''}`;
    else label = `On save: restore <strong>${-delta}</strong> hrs &mdash; <strong>${projectedRounded} hrs remaining</strong>`;
  } else {
    label = `Approving deducts <strong>${livePoolRounded} hrs</strong> &mdash; <strong>${projectedRounded} hrs remaining</strong> ${willOverdraw ? '(overdrawn)' : ''}`;
  }

  target.innerHTML = `
    <div style="padding:8px 12px;background:${bg};border:1px solid ${border};border-radius:var(--radius-sm);font-size:0.8rem;color:${color};display:flex;align-items:start;gap:6px;">
      <span class="material-icons" style="font-size:16px;margin-top:1px;">${icon}</span>
      <div>
        <div><strong>${summary.totalUsed} / ${summary.totalAllowed} hrs used</strong> &middot; ${summary.totalRemaining} hrs remaining</div>
        <div>${totalRequestHours}-hr request${breakdown ? ` (${breakdown})` : ''} &mdash; ${label}</div>
      </div>
    </div>`;
}

function closeDayPicker() {
  const modal = document.getElementById('modal-day-picker');
  if (modal) modal.remove();
}

async function saveDayPicker(requestId) {
  if (_hrGateComplianceOnly('timeoff')) return; // TASK-1117 (2nd-COD) — callable-backed privileged edit
  const typeSelect = document.getElementById('day-picker-type');
  if (!typeSelect) return;
  const req = HRState.timeOffRequests.find(r => r.id === requestId);
  if (!req) return;

  const live = readDayPickerLiveState();
  const baseType = live.baseType;
  const originalType = typeSelect.dataset.original;

  // Inclusion: gather excluded ISOs from live state
  const includeBoxes = document.querySelectorAll('.day-picker-include');
  const excludedDates = Array.from(includeBoxes)
    .filter(cb => !cb.checked)
    .map(cb => cb.dataset.iso);
  const beforeExcluded = new Set(req.excludedDates || []);
  const afterExcluded = new Set(excludedDates);
  const exclusionsDiffer = beforeExcluded.size !== afterExcluded.size ||
    [...afterExcluded].some(d => !beforeExcluded.has(d)) ||
    [...beforeExcluded].some(d => !afterExcluded.has(d));

  // If user excluded every day, treat as a delete.
  if (includeBoxes.length > 0 && excludedDates.length === includeBoxes.length) {
    if (!confirm('You unchecked every day. Delete this request entirely?')) return;
    try {
      const fn = httpsCallable(functions, 'adminManageTimeOff');
      const result = await fn({ requestId, action: 'delete' });
      window.showToast?.(result?.data?.notified ? 'Request deleted — employee notified' : 'Request deleted', 'success');
      closeDayPicker();
    } catch (error) {
      console.error('[HR] Error deleting time off:', error);
      window.showToast?.('Error deleting request', 'error');
    }
    return;
  }

  // Build a normalized dayTypes map from live state. Only include entries
  // that differ from baseType. If all days agree with baseType, dayTypes is
  // empty (and the CF will clear the field on the doc).
  const dayTypes = {};
  Object.entries(live.dayTypesOverride || {}).forEach(([iso, t]) => {
    if (afterExcluded.has(iso)) return; // excluded days don't carry a type
    if (t && t !== baseType) dayTypes[iso] = t;
  });

  // Has the dayTypes map changed vs saved state?
  const savedDayTypes = req.dayTypes || {};
  const dayTypesDiffer = (() => {
    const a = Object.keys(dayTypes), b = Object.keys(savedDayTypes);
    if (a.length !== b.length) return true;
    for (const k of a) { if (savedDayTypes[k] !== dayTypes[k]) return true; }
    return false;
  })();

  // Combined updateType/updateDayTypes — type or per-day-types changed
  const typeChanged = baseType && baseType !== originalType;
  const anyTypeChange = typeChanged || dayTypesDiffer;

  if (!anyTypeChange && !exclusionsDiffer) {
    closeDayPicker();
    return;
  }

  try {
    const fn = httpsCallable(functions, 'adminManageTimeOff');
    let notified = false;
    if (anyTypeChange) {
      const r = await fn({ requestId, action: 'updateDayTypes', baseType, dayTypes });
      notified = notified || !!r?.data?.notified;
    }
    if (exclusionsDiffer) {
      const r = await fn({ requestId, action: 'updateDays', excludedDates });
      notified = notified || !!r?.data?.notified;
    }
    const what = anyTypeChange && exclusionsDiffer ? 'Type & days updated'
      : anyTypeChange ? 'Type updated'
      : 'Days updated';
    window.showToast?.(notified ? `${what} — employee notified` : what, 'success');
    closeDayPicker();
  } catch (error) {
    console.error('[HR] Error updating time off:', error);
    window.showToast?.('Error updating request', 'error');
  }
}

// TASK-1136 — approve/deny now flow through the adminManageTimeOff callable
// (role-gated + self-approval-blocked + server audit + trigger-carried decision
// SMS with the deny reason). The old raw updateDoc paths — and their client
// logAudit calls, which read the TYPO'd `HRState.timeoffRequests` key (real key
// is `timeOffRequests`, so audit `before` was always null) — are DELETED: the
// server's _writeAudit row is authoritative and a client duplicate would
// pollute the tech-support audit trail.
//
// DEMO PARITY: anonymous demo users have no users/{uid} doc, so the callable
// can't resolve their tenant ("No tenant for user"). firestore.rules leave the
// demo tenant open — keep the pre-1136 raw write for demo ONLY.
async function _decideTimeOffViaCallable(requestId, action, reason) {
  const tenant = HRState.tenantId || window.AppState?.tenant || 'demo';
  if (tenant === 'demo') {
    const data = {
      status: action === 'approve' ? 'approved' : 'denied',
      reviewedAt: Timestamp.now(),
      reviewedBy: window.AppState?.user?.email || 'Manager',
      // TASK-1129 — demo parity for the attribution stamp (billing
      // _resolveReadyByName pattern, condensed; server stamps it for real tenants).
      reviewedByName: (window.AppState?.memberAccess?.displayName || window.AppState?.user?.displayName
        || (window.AppState?.user?.email || '').split('@')[0] || null),
      updatedAt: Timestamp.now()
    };
    if (action === 'deny') data.reviewNote = reason || '';
    await updateDoc(doc(db, 'tenants', tenant, 'timeOff', requestId), data);
    return;
  }
  const fn = httpsCallable(functions, 'adminManageTimeOff');
  await fn({ requestId, action, reason: reason || '' });
}

async function approveTimeOff(requestId) {
  // TASK-1117 — time-off ADMIN decision goes through the adminManageTimeOff
  // callable (Admin SDK, bypasses rules), so this client gate is a real belt.
  if (_hrGateComplianceOnly('timeoff')) return;
  try {
    await _decideTimeOffViaCallable(requestId, 'approve', '');
    window.showToast?.('Time off approved', 'success');
  } catch (error) {
    console.error('[HR] Error approving time off:', error);
    window.showToast?.(error?.message || 'Error approving request', 'error');
  }
}

// TASK-1136 (Lacey) — deny opens a small reason modal instead of denying
// blind. Reason is OPTIONAL, texted to the requester via the decision SMS and
// shown on the HR placard + driver "Your Requests" panel. Placeholder nudges
// schedule-related wording (Domain COD: a stored+texted free-text reason is
// discoverable — keep medical/personal detail out of it).
function denyTimeOff(requestId) {
  if (_hrGateComplianceOnly('timeoff')) return; // TASK-1117 — callable-backed admin decision (see approveTimeOff)
  const req = HRState.timeOffRequests.find(r => r.id === requestId);
  const personName = req?.personName || 'this employee';

  const existing = document.getElementById('modal-deny-timeoff');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'modal-deny-timeoff';
  modal.className = 'modal active';
  modal.dataset.requestId = requestId; // id-only handler pattern — never user text in onclick args
  modal.innerHTML = `
    <div class="modal-content" style="max-width:440px;">
      <div class="modal-header">
        <h2><span class="material-icons" style="color:#dc2626;">event_busy</span> Deny Time Off</h2>
        <button class="modal-close" onclick="window.HRModule.closeDenyTimeOffModal()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body">
        <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:var(--spacing-sm);">
          Denying the request for <strong>${escapeHtml(personName)}</strong>. The reason below is optional — it's texted to them with the decision and shown on their request.
        </p>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px;">
          <button type="button" class="btn btn-sm btn-secondary" style="font-size:0.75rem;" data-reason="Schedule is full that week" onclick="window.HRModule.applyDenyReasonChip(this)">Schedule full</button>
          <button type="button" class="btn btn-sm btn-secondary" style="font-size:0.75rem;" data-reason="We're short-staffed on those dates" onclick="window.HRModule.applyDenyReasonChip(this)">Short-staffed</button>
          <button type="button" class="btn btn-sm btn-secondary" style="font-size:0.75rem;" data-reason="Please pick different dates and resubmit" onclick="window.HRModule.applyDenyReasonChip(this)">Pick new dates</button>
        </div>
        <textarea id="deny-timeoff-reason" class="form-control" rows="3" maxlength="300"
          placeholder="Optional reason — keep it schedule-related (no medical or personal detail)"></textarea>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" onclick="window.HRModule.closeDenyTimeOffModal()">Cancel</button>
        <button class="btn" id="btn-confirm-deny-timeoff" style="background:#dc2626;color:#fff;" onclick="window.HRModule.confirmDenyTimeOff()">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">close</span> Deny Request
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  // UX Lead COD suggestion: auto-focus the textarea on open.
  setTimeout(() => document.getElementById('deny-timeoff-reason')?.focus(), 50);
}

function applyDenyReasonChip(btn) {
  const ta = document.getElementById('deny-timeoff-reason');
  if (ta && btn?.dataset?.reason) { ta.value = btn.dataset.reason; ta.focus(); }
}

function closeDenyTimeOffModal() {
  document.getElementById('modal-deny-timeoff')?.remove();
}

async function confirmDenyTimeOff() {
  const modal = document.getElementById('modal-deny-timeoff');
  const requestId = modal?.dataset?.requestId;
  if (!requestId) return;
  const reason = (document.getElementById('deny-timeoff-reason')?.value || '').trim().slice(0, 300);
  const btn = document.getElementById('btn-confirm-deny-timeoff');
  if (btn) btn.disabled = true; // debounce — no double deny
  try {
    await _decideTimeOffViaCallable(requestId, 'deny', reason);
    window.showToast?.('Time off denied — employee notified', 'success');
    closeDenyTimeOffModal();
  } catch (error) {
    console.error('[HR] Error denying time off:', error);
    window.showToast?.(error?.message || 'Error denying request', 'error');
    if (btn) btn.disabled = false;
  }
}

async function deleteTimeOff(requestId, personName) {
  if (_hrGateComplianceOnly('timeoff')) return; // TASK-1117 (2nd-COD) — callable-backed privileged delete
  if (!confirm(`Delete time-off request for ${personName}? The employee will be notified.`)) return;
  try {
    const fn = httpsCallable(functions, 'adminManageTimeOff');
    const result = await fn({ requestId, action: 'delete' });
    window.showToast?.(result?.data?.notified ? 'Request deleted — employee notified' : 'Request deleted', 'success');
  } catch (error) {
    console.error('[HR] Error deleting time off:', error);
    window.showToast?.(error?.message || 'Error deleting request', 'error');
  }
}

function updateTimeOffHours() {
  const startVal = document.getElementById('hr-timeoff-start')?.value;
  const endVal = document.getElementById('hr-timeoff-end')?.value;
  const hoursInput = document.getElementById('hr-timeoff-hours');
  const hint = document.getElementById('hr-timeoff-hours-hint');
  if (!startVal || !endVal || !hoursInput) return;
  const start = new Date(startVal + 'T00:00:00');
  const end = new Date(endVal + 'T00:00:00');
  if (end < start) return;
  const days = Math.max(1, Math.round((end - start) / (24 * 60 * 60 * 1000)) + 1);
  hoursInput.value = days * 8;
  if (hint) hint.textContent = `${days} day${days !== 1 ? 's' : ''} = ${days * 8} hrs`;
}

/** Auto-fill hours when bereavement tier or travel checkbox changes */
function autofillBereavementHours() {
  const level = document.getElementById('hr-bereavement-level')?.value;
  const travel = document.getElementById('hr-bereavement-travel')?.checked;
  const hoursInput = document.getElementById('hr-timeoff-hours');
  const hint = document.getElementById('hr-timeoff-hours-hint');
  if (!level || !hoursInput) return;

  // Look up policy for the selected employee
  const personId = document.getElementById('hr-timeoff-person')?.value;
  const person = personId ? HRState.people.find(p => p.id === personId) : null;
  const policy = HRState.timeOffPolicy;
  if (!policy?.levels?.length || !person?.hrTimeOffLevel || !person?.hrEmploymentType) return;

  const policyLevel = policy.levels.find(l => l.id === person.hrTimeOffLevel);
  if (!policyLevel) return;

  // Check global bereavement first, then per-level (legacy)
  const globalB = policy?.bereavement;
  const allowances = policyLevel[person.hrEmploymentType] || {};
  const bObj = (typeof globalB === 'object' && globalB !== null) ? globalB
    : (typeof allowances.bereavement === 'object' && allowances.bereavement !== null) ? allowances.bereavement : null;
  if (!bObj) return;

  let days = bObj[level] || 0;
  if (travel && bObj.travelDays) days += bObj.travelDays;

  hoursInput.value = days * 8;
  if (hint) hint.textContent = `${days} day${days !== 1 ? 's' : ''} = ${days * 8} hrs (per policy)`;
}

function openTimeOffRequest() {
  if (_hrGateComplianceOnly('timeoff')) return; // TASK-1117 — HR admin create-for-anyone surface (paywalled)
  // Build a simple modal for creating a time-off request inline
  const modal = document.getElementById('modal-hr-timeoff-request');
  if (modal) {
    // Update person dropdown
    const select = document.getElementById('hr-timeoff-person');
    if (select) {
      // TASK-4532 — active people only (a time-off request for someone who left
      // is meaningless); same `active !== false` predicate as the pickers above.
      select.innerHTML = '<option value="">Select employee...</option>' +
        HRState.people.filter(p => p.active !== false)
          .map(p => `<option value="${p.id}">${escapeHtml(p.name)}</option>`).join('');
      // Chris 2026-07-13 — re-render the My Time Off history when a different
      // person is picked (idempotent — replaces handler on every open).
      select.onchange = () => renderTimeOffHistory();
    }
    // Default dates to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    // Local calendar day — toISOString() is UTC and pre-filled the day AFTER
    // tomorrow for anyone opening this after ~7pm Central.
    const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;
    const startInput = document.getElementById('hr-timeoff-start');
    const endInput = document.getElementById('hr-timeoff-end');
    if (startInput) startInput.value = tomorrowStr;
    if (endInput) endInput.value = tomorrowStr;
    const hoursInput = document.getElementById('hr-timeoff-hours');
    if (hoursInput) hoursInput.value = 8;
    const hint = document.getElementById('hr-timeoff-hours-hint');
    if (hint) hint.textContent = '1 day = 8 hrs';
    const typeSelect = document.getElementById('hr-timeoff-type');
    if (typeSelect) typeSelect.value = 'unpaid';
    const reasonInput = document.getElementById('hr-timeoff-reason');
    if (reasonInput) reasonInput.value = '';
    // Reset bereavement sub-group
    const bGroup = document.getElementById('hr-bereavement-sub-group');
    if (bGroup) bGroup.style.display = 'none';
    const bLevel = document.getElementById('hr-bereavement-level');
    if (bLevel) bLevel.value = '';
    const bTravel = document.getElementById('hr-bereavement-travel');
    if (bTravel) bTravel.checked = false;

    // My Time Off history (Chris 2026-07-13, ported from timeoff.js): default
    // the section open on desktop, collapsed on mobile, then render for the
    // currently selected person (empty selection shows a pick-someone prompt).
    const historySection = document.getElementById('hr-timeoff-history-section');
    if (historySection) {
      const isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
      historySection.classList.toggle('open', !isMobile);
    }
    renderTimeOffHistory();

    modal.classList.remove('hidden');
  }
}

// ============================================
// MY TIME OFF — upcoming + past history under the request form
// (Chris 2026-07-13 — ported from timeoff.js toggleHistory/renderHistory so
// users see historic + future accepted time off wherever they request it)
// ============================================
function toggleTimeOffHistory() {
  document.getElementById('hr-timeoff-history-section')?.classList.toggle('open');
}

function renderTimeOffHistory() {
  const body = document.getElementById('hr-timeoff-history-body');
  if (!body) return;

  // HR is an admin surface — the person is whoever is picked in the form's
  // dropdown (same resolution submitTimeOffRequest uses). No self fallback.
  const personId = document.getElementById('hr-timeoff-person')?.value || '';

  if (!personId) {
    body.innerHTML = `
      <div style="text-align:center;padding:var(--spacing-sm);color:var(--text-secondary);font-size:0.85rem;">
        Select an employee to see their time off.
      </div>
    `;
    return;
  }

  // TASK-9602 — the request modal's history is the SAME renderer as the
  // employee Time Off tab (one history UI, not three): every status, newest
  // first, no 15-row cap, Days / Hrs from the 9601 placard formula.
  if (!_hrCanViewTimeOff()) { body.innerHTML = ''; return; }
  const person = (HRState.people || []).find(p => p.id === personId) || null;
  const mine = _timeOffRequestsForPerson(personId, person);
  body.innerHTML = _timeOffHistoryStyles() + `<div class="hrtoh">${_timeOffHistoryTableHtml(_hrTimeOffPerson.historyRows(mine, enumerateRequestDays, hoursPerDayForRequest), { compact: true })}</div>`;
}

// ---- TASK-9602: employee Time Off tab — year summary, 12-month strip, history, CSV ----

/** The module that gates time-off data today. Time off is shown inside the HR
 *  view, which app.js admits on the `hr` module (checkModuleAccess('hr')), and
 *  nowhere in HR is it narrower than that. Every new time-off surface in this
 *  file (directory card line, employee tab history, modal history, CSV) asks
 *  this ONE function, so tightening it later (TASK-9603) tightens them all.
 *  Fails closed when app.js has not exposed the gate. */
function _hrCanViewTimeOff() {
  try {
    if (typeof window.checkModuleAccess !== 'function') return false;
    return window.checkModuleAccess('hr', true) === true;
  } catch (_) { return false; }
}

/** One person's requests — the SAME matcher as the 9601 employee filter
 *  (_timeOffRequestIsFor: id first, name only for legacy id-less rows), so a
 *  person's numbers here match the list filtered to that person. */
function _timeOffRequestsForPerson(personId, person) {
  return (HRState.timeOffRequests || []).filter(r => _timeOffRequestIsFor(r, personId, person));
}

function _timeOffHistoryStyles() {
  return `<style>
    .hrtoh{display:flex;flex-direction:column;gap:var(--spacing-sm);}
    .hrtoh .hrtoh-head{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
    .hrtoh .hrtoh-head h4{margin:0;font-size:0.92rem;}
    .hrtoh .hrtoh-head .sp{flex:1;}
    .hrtoh .hrtoh-sub{font-size:0.75rem;color:var(--text-secondary);}
    .hrtoh .hrtoh-select{min-height:36px;border:1px solid var(--border-medium);border-radius:6px;padding:4px 8px;font-size:0.85rem;background:var(--bg-primary);color:var(--text-primary);}
    .hrtoh .hrtoh-btn{min-height:36px;display:inline-flex;align-items:center;gap:4px;border:1px solid var(--border-medium);background:var(--bg-primary);color:var(--text-primary);border-radius:6px;padding:4px 10px;font-size:0.8rem;font-weight:600;cursor:pointer;}
    .hrtoh .hrtoh-btn .material-icons{font-size:16px;}
    .hrtoh .hrtoh-stats{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:8px;}
    .hrtoh .hrtoh-stat{border:1px solid var(--border-light);border-radius:var(--radius-md);padding:8px 10px;min-width:0;background:var(--bg-primary);}
    .hrtoh .hrtoh-stat .k{font-size:0.68rem;font-weight:700;letter-spacing:0.05em;text-transform:uppercase;color:var(--text-secondary);}
    .hrtoh .hrtoh-stat .v{font-size:1.25rem;font-weight:700;font-variant-numeric:tabular-nums;color:var(--text-primary);}
    .hrtoh .hrtoh-stat .s{font-size:0.72rem;color:var(--text-secondary);overflow-wrap:anywhere;}
    .hrtoh .hrtoh-yrwrap{overflow-x:auto;}
    .hrtoh .hrtoh-yr{display:grid;grid-template-columns:34px repeat(31,minmax(10px,1fr));gap:2px;min-width:480px;font-size:0.66rem;color:var(--text-secondary);}
    .hrtoh .hrtoh-yr .m{display:flex;align-items:center;}
    .hrtoh .hrtoh-yr .d{aspect-ratio:1;border-radius:3px;background:var(--bg-secondary);}
    .hrtoh .hrtoh-yr .d.x{background:transparent;}
    .hrtoh .hrtoh-yr .d.today{outline:1.5px solid var(--text-primary);outline-offset:-1px;}
    .hrtoh .hrtoh-legend{display:flex;flex-wrap:wrap;gap:10px;font-size:0.74rem;color:var(--text-secondary);}
    .hrtoh .hrtoh-legend span{display:inline-flex;align-items:center;gap:5px;}
    .hrtoh .hrtoh-dot{width:9px;height:9px;border-radius:50%;display:inline-block;flex:none;}
    .hrtoh .hrtoh-tblw{overflow-x:auto;border:1px solid var(--border-light);border-radius:var(--radius-md);}
    .hrtoh table.hrtoh-tbl{border-collapse:collapse;width:100%;font-size:0.8rem;min-width:640px;}
    .hrtoh table.hrtoh-tbl th{text-align:left;font-size:0.68rem;letter-spacing:0.05em;text-transform:uppercase;color:var(--text-secondary);background:var(--bg-secondary);padding:7px 8px;font-weight:700;white-space:nowrap;}
    .hrtoh table.hrtoh-tbl td{padding:7px 8px;border-top:1px solid var(--border-light);vertical-align:top;font-variant-numeric:tabular-nums;color:var(--text-primary);}
    .hrtoh table.hrtoh-tbl td.n{text-align:right;}
    .hrtoh table.hrtoh-tbl td .sub{font-size:0.7rem;color:var(--text-secondary);}
    .hrtoh table.hrtoh-tbl td.reason{max-width:240px;overflow-wrap:anywhere;}
    .hrtoh .hrtoh-st{font-size:0.66rem;font-weight:800;letter-spacing:0.05em;text-transform:uppercase;padding:2px 7px;border-radius:6px;white-space:nowrap;background:#F3F4F6;color:#374151;}
    .hrtoh .hrtoh-st.approved{background:#ECFDF5;color:#065F46;}
    .hrtoh .hrtoh-st.denied{background:#FEF2F2;color:#991B1B;}
    .hrtoh .hrtoh-st.pending{background:#FFFBEB;color:#92400E;}
    .hrtoh .hrtoh-flag{display:inline-block;border-radius:6px;padding:0 6px;font-weight:700;font-size:0.68rem;}
    .hrtoh .hrtoh-flag.warn{background:#FFFBEB;color:#92400E;border:1px solid #FCD34D;}
    .hrtoh .hrtoh-flag.bad{background:#FEF2F2;color:#991B1B;border:1px solid #FCA5A5;}
    .hrtoh .hrtoh-empty{padding:14px;text-align:center;color:var(--text-secondary);font-size:0.85rem;border:1px dashed var(--border-medium);border-radius:var(--radius-md);}
    @media (max-width:720px){ .hrtoh .hrtoh-stats{grid-template-columns:repeat(2,minmax(0,1fr));} }
  </style>`;
}

function _timeOffHistoryLocalShort(d) {
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
}

/** The ONE history table (employee Time Off tab + Request modal). Every
 *  free-text cell is escapeHtml'd; type / status / via are closed whitelists. */
function _timeOffHistoryTableHtml(rows, opts) {
  const core = _hrTimeOffList;
  if (!rows.length) return '<div class="hrtoh-empty">No time-off requests on file.</div>';
  const body = rows.map(r => {
    const s = r.start, e = r.end;
    let when = s === null ? '&mdash;' : escapeHtml(_timeOffUtcLabel(s));
    if (s !== null && e !== null && e !== s) when += `&ndash;${escapeHtml(_timeOffUtcLabel(e))}`;
    const yr = s === null ? '' : ` ${core.dayNumToDate(s).getUTCFullYear()}`;
    const sub = r.submitted ? escapeHtml(_timeOffHistoryLocalShort(r.submitted)) : '&mdash;';
    const via = r.via ? `<div class="sub">${escapeHtml(core.VIA_LABELS[r.via])}</div>` : '';
    const fl = core.flags(r.req);
    let notice = escapeHtml(_hrTimeOffPerson.noticeText(r.notice)) || '&mdash;';
    if (fl.loggedAfter) notice = `<span class="hrtoh-flag bad">${notice}</span>`;
    else if (fl.shortNotice) notice = `<span class="hrtoh-flag warn">${notice}</span>`;
    const reviewed = r.reviewer
      ? `${escapeHtml(_formatReviewerShort(r.reviewer))}${r.reviewedAt ? `<div class="sub">${escapeHtml(_timeOffHistoryLocalShort(r.reviewedAt))}</div>` : ''}`
      : (r.reviewedAt ? escapeHtml(_timeOffHistoryLocalShort(r.reviewedAt)) : '&mdash;');
    return `<tr>
      <td>${sub}${via}</td>
      <td style="white-space:nowrap;">${when}${yr}</td>
      <td class="n">${r.days}</td>
      <td class="n">${r.hours}</td>
      <td style="white-space:nowrap;"><span class="hrtoh-dot" style="background:${core.typeColor(r.type)};"></span> ${escapeHtml(core.typeLabel(r.type))}</td>
      <td style="white-space:nowrap;">${notice}</td>
      <td><span class="hrtoh-st ${r.status}">${escapeHtml(_hrTimeOffPerson.statusLabel(r.status))}</span></td>
      <td>${reviewed}</td>
      <td class="reason">${escapeHtml(r.reason)}</td>
    </tr>`;
  }).join('');
  const minW = opts && opts.compact ? 'style="min-width:600px;"' : '';
  return `<div class="hrtoh-tblw"><table class="hrtoh-tbl" ${minW}><thead><tr><th>Submitted</th><th>Time off</th><th style="text-align:right;">Days</th><th style="text-align:right;">Hrs</th><th>Type</th><th>Notice</th><th>Status</th><th>Reviewed</th><th>Reason</th></tr></thead><tbody>${body}</tbody></table></div>`;
}

/** Year the employee tab shows — remembered per person while the modal is open. */
function _timeOffHistoryYear(personId) {
  const sel = HRState.timeOffHistSel;
  return (sel && sel.personId === personId && Number.isInteger(sel.year)) ? sel.year : new Date().getFullYear();
}

function setTimeOffHistoryYear(personId, year) {
  const y = parseInt(year, 10);
  if (!personId || !Number.isInteger(y)) return;
  HRState.timeOffHistSel = { personId, year: y };
  renderEmployeeTimeOffHistory(personId);
}

/** Employee detail → Time Off tab, the part under the balances. Renders even
 *  when the balance table bails out (no policy / no level), which is most of
 *  TITS. */
function renderEmployeeTimeOffHistory(employeeId) {
  const host = document.getElementById('hr-timeoff-history-part');
  if (!host) return;
  if (!_hrCanViewTimeOff()) { host.innerHTML = ''; return; }
  const emp = (HRState.people || []).find(p => p.id === employeeId);
  if (!emp) { host.innerHTML = ''; return; }
  const core = _hrTimeOffList;
  const P = _hrTimeOffPerson;
  const mine = _timeOffRequestsForPerson(emp.id, emp);
  const now = new Date();
  const todayDay = core.localDayNum(now);
  const year = _timeOffHistoryYear(emp.id);
  const st = P.yearStats(mine, { year, todayDay, enumerate: enumerateRequestDays });
  const jsId = escJsAttrLocal(emp.id);

  // Stats
  const byType = Object.keys(core.TYPE_LABELS).filter(t => st.byType[t]).map(t => `${st.byType[t]} ${core.typeLabel(t).toLowerCase()}`);
  const offSub = (byType.length ? byType.join(' · ') : 'none') + (st.daysOn ? ` · plus ${st.daysOn} day${st.daysOn === 1 ? '' : 's'} on` : '');
  const next = st.next;
  const stats = [
    { k: 'Days off', v: String(st.daysOff), s: offSub, t: 'Approved days off in ' + year + '. Excluded days and Days On are not counted. Not a PTO balance.' },
    { k: 'Requests', v: String(st.requests), s: `${st.approved} approved · ${st.pending} pending` },
    { k: 'Denied / cancelled', v: String(st.deniedCancelled), s: `in ${year}` },
    { k: 'Typical notice', v: st.medianNotice === null ? '—' : _hrTimeOffPerson.noticeText(st.medianNotice), s: `${st.shortCount} short or after the fact`, t: 'Median days from submission to the first day off. Sick, bereavement, parental leave, jury duty, Days On and requests with no submission date are left out.' },
    { k: 'Next off', v: next ? (next.day === todayDay ? 'Today' : _timeOffUtcLabel(next.day)) : '—', s: next ? (next.req.status === 'pending' ? 'pending review' : core.typeLabel(next.req.type)) : 'nothing booked' },
  ].map(x => `<div class="hrtoh-stat"${x.t ? ` title="${escapeHtml(x.t)}"` : ''}><div class="k">${escapeHtml(x.k)}</div><div class="v">${escapeHtml(x.v)}</div><div class="s">${escapeHtml(x.s)}</div></div>`).join('');

  // 12-month strip — one cell per day, native Date (UTC day numbers, the same
  // calendar model the 9601 list uses).
  const cells = P.yearCells(mine, year, enumerateRequestDays);
  let grid = '';
  for (let m = 0; m < 12; m++) {
    const dim = new Date(Date.UTC(year, m + 1, 0)).getUTCDate();
    grid += `<div class="m">${new Date(Date.UTC(year, m, 1)).toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })}</div>`;
    for (let d = 1; d <= 31; d++) {
      if (d > dim) { grid += '<div class="d x"></div>'; continue; }
      const n = Math.round(Date.UTC(year, m, d) / 86400000);
      const hit = cells.get(n);
      const color = hit ? core.typeColor(hit.type) : '';
      const style = hit ? (hit.pending ? `background:repeating-linear-gradient(45deg,${color} 0 3px,transparent 3px 6px);` : `background:${color};`) : '';
      const label = _timeOffUtcLabel(n, { year: 'numeric' }) + (hit ? ` · ${core.typeLabel(hit.type)}${hit.pending ? ' (pending)' : ''}` : '');
      grid += `<div class="d${n === todayDay ? ' today' : ''}" style="${style}" title="${escapeHtml(label)}"></div>`;
    }
  }
  const presentTypes = Object.keys(core.TYPE_LABELS).filter(t => [...cells.values()].some(c => c.type === t));
  const legend = presentTypes.map(t => `<span><span class="hrtoh-dot" style="background:${core.typeColor(t)};"></span>${escapeHtml(core.typeLabel(t))}</span>`).join('')
    + '<span><span class="hrtoh-dot" style="background:repeating-linear-gradient(45deg,var(--text-secondary) 0 2px,transparent 2px 4px);"></span>Pending</span>'
    + '<span><span class="hrtoh-dot" style="outline:1.5px solid var(--text-primary);"></span>Today</span>';

  const years = P.yearsFor(mine, now.getFullYear());
  const yearSel = `<select class="hrtoh-select" aria-label="Year" onchange="window.HRModule.setTimeOffHistoryYear('${jsId}', this.value)">${years.map(y => `<option value="${y}"${y === year ? ' selected' : ''}>${y}</option>`).join('')}</select>`;
  const rows = P.historyRows(mine, enumerateRequestDays, hoursPerDayForRequest);

  host.innerHTML = `${_timeOffHistoryStyles()}<div class="hrtoh" style="margin-top:var(--spacing-md);">
    <div class="hrtoh-head"><h4>${year} at a glance</h4><span class="sp"></span>${yearSel}</div>
    <div class="hrtoh-stats">${stats}</div>
    <div class="hrtoh-yrwrap"><div class="hrtoh-yr" aria-label="Time off calendar ${year}">${grid}</div></div>
    <div class="hrtoh-legend">${legend}</div>
    <div class="hrtoh-head" style="margin-top:var(--spacing-sm);"><h4>Request history</h4><span class="hrtoh-sub">Every request on file, newest first. Pending, denied and cancelled included.</span><span class="sp"></span>
      ${rows.length ? `<button type="button" class="hrtoh-btn" onclick="window.HRModule.downloadTimeOffHistoryCsv('${jsId}')"><span class="material-icons" aria-hidden="true">download</span>Download CSV</button>` : ''}
    </div>
    ${_timeOffHistoryTableHtml(rows)}
  </div>`;
}

/** CSV of the history table (all of it — what the table shows). Every cell,
 *  header included, through csvSafeCell (leading = + - @ tab CR neutralised). */
function downloadTimeOffHistoryCsv(personId) {
  if (!personId || !_hrCanViewTimeOff()) return;
  const emp = (HRState.people || []).find(p => p.id === personId);
  if (!emp) return;
  const rows = _hrTimeOffPerson.historyRows(_timeOffRequestsForPerson(emp.id, emp), enumerateRequestDays, hoursPerDayForRequest);
  if (!rows.length) { window.showToast?.('No time-off requests to export', 'error'); return; }
  const csv = _hrTimeOffPerson.toCsv(String(emp.name || ''), rows, csvSafeCell);
  const safeName = String(emp.name || 'employee').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'employee';
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `time-off-${safeName}-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 0);
}

/** Directory card: days off this year / off today or next off / pending.
 *  Returns '' when the viewer fails the time-off gate. */
function _timeOffDirectoryBadgesHtml(emp, ctx) {
  if (!ctx.canView) return '';
  // Memoized per snapshot + day (COD Code Reviewer / Skeptic): a directory
  // search keystroke re-renders every card but never re-scans the requests.
  const src = HRState.timeOffRequests || [];
  let cache = HRState.timeOffDirCache;
  if (!cache || cache.src !== src || cache.todayDay !== ctx.todayDay || cache.year !== ctx.year) {
    cache = HRState.timeOffDirCache = { src, todayDay: ctx.todayDay, year: ctx.year, byEmp: new Map() };
  }
  const key = String(emp.id || '') + '|' + String(emp.name || '');
  let s = cache.byEmp.get(key);
  if (!s) {
    const mine = _timeOffRequestsForPerson(emp.id, emp);
    s = _hrTimeOffPerson.directorySummary(mine, { year: ctx.year, todayDay: ctx.todayDay, enumerate: enumerateRequestDays });
    cache.byEmp.set(key, s);
  }
  const icon = n => `<span class="material-icons" style="font-size:12px;vertical-align:middle;">${n}</span>`;
  let html = `<span class="hr-emp-badge" title="Approved days off in ${s.year}. Not a PTO balance.">${icon('event_busy')} ${s.daysOff} day${s.daysOff === 1 ? '' : 's'} off &middot; ${s.year}</span>`;
  if (s.offToday) html += `<span class="hr-emp-badge warning">${icon('today')} Off today</span>`;
  else if (s.nextOffDay !== null) html += `<span class="hr-emp-badge">${icon('flight_takeoff')} Next off ${escapeHtml(_timeOffUtcLabel(s.nextOffDay))}</span>`;
  if (s.pending) html += `<span class="hr-emp-badge warning">${icon('pending_actions')} ${s.pending} pending</span>`;
  return html;
}

async function submitTimeOffRequest(e) {
  if (e) e.preventDefault();
  if (e && !window.validateRequiredFields(e.target)) return;
  const personId = document.getElementById('hr-timeoff-person')?.value;
  const startDate = document.getElementById('hr-timeoff-start')?.value;
  const endDate = document.getElementById('hr-timeoff-end')?.value;
  const hours = parseInt(document.getElementById('hr-timeoff-hours')?.value) || 8;
  const type = document.getElementById('hr-timeoff-type')?.value || 'unpaid';
  const reason = document.getElementById('hr-timeoff-reason')?.value?.trim() || '';

  if (!personId || !startDate || !endDate) {
    window.showToast?.('Please fill in all required fields', 'error');
    return;
  }

  // TITS 2026-09-23 — Oct 1 → Sep 24 was stored from this form and texted to
  // every manager. 'YYYY-MM-DD' strings compare correctly as strings.
  if (endDate < startDate) {
    window.showToast?.('End date cannot be before start date', 'error');
    return;
  }

  if (hours < 1) {
    window.showToast?.('Hours must be at least 1', 'error');
    return;
  }

  const person = HRState.people.find(p => p.id === personId);
  if (!person) {
    window.showToast?.('Employee not found', 'error');
    return;
  }

  // TASK-4720 — saveTimeOffRequest is the only write path. HR files on the
  // employee's behalf (still pending, still overlap-checked server-side:
  // "<Name> already has time off requested for <dates>. ...").
  const payload = {
    tenantId: HRState.tenantId,
    action: 'create',
    personId,
    startDate,
    endDate,
    hours,
    type,
    reason,
    source: 'hr',
  };

  if (type === 'bereavement') {
    const bereavementLevel = document.getElementById('hr-bereavement-level')?.value || '';
    const bereavementTravel = document.getElementById('hr-bereavement-travel')?.checked || false;
    if (bereavementLevel) payload.bereavementLevel = bereavementLevel;
    if (bereavementTravel) payload.bereavementTravel = true;
  }

  try {
    await httpsCallable(functions, 'saveTimeOffRequest')(payload);
    window.showToast?.('Time off request created', 'success');
    closeModal('hr-timeoff-request');
  } catch (error) {
    console.error('[HR] Error creating time off request:', error);
    window.showToast?.(error?.message || 'Error creating request', 'error');
  }
}

// ============================================
// SAFETY SIGN-IN FUNCTIONS
// ============================================

function getTodayDateString() {
  // TASK-342 fix 2026-05-21 — local-day YYYY-MM-DD. Prior version used
  // `now.toISOString().split('T')[0]` which returns UTC date. After 7pm CT
  // (midnight UTC) the filter defaulted to TOMORROW's date and showed no
  // sign-ins. Same bug class as the payroll TASK-338 off-by-one. Use local
  // date parts here; server side uses `Intl.DateTimeFormat('America/Chicago')`
  // in submitSafetySignIn so the two stay aligned for CT-based carriers.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function initSignInDateDefaults() {
  const startInput = document.getElementById('hr-signin-date-start');
  const endInput = document.getElementById('hr-signin-date-end');
  if (startInput && !startInput.value) startInput.value = getTodayDateString();
  if (endInput && !endInput.value) endInput.value = getTodayDateString();
}

// TASK-9385 round 3 — browser port of functions/src/services/safetySignInPdfDecision.js
// safetyRosterKey (FNV-1a, two 32-bit lanes over a key-sorted JSON of the signer
// ids + meetingDetails). The server stamps pdfRosterKey with the same hash; if
// they match, the linked PDF shows exactly the current roster. Pinned equal to
// the server by tests/task9385-safety-pdf-decision.unit.cjs — change BOTH or neither.
function _srkStable(v) {
  if (v === undefined) return 'null';
  if (v === null || typeof v !== 'object') return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(_srkStable).join(',')}]`;
  if (typeof v.toMillis === 'function') return JSON.stringify(v.toMillis());
  if (v instanceof Date) return JSON.stringify(v.getTime());
  return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${_srkStable(v[k])}`).join(',')}}`;
}
function _srkFnv(str, seed) {
  let h = seed >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}
function safetyRosterKeyClient(data) {
  const sigs = data && Array.isArray(data.signatures) ? data.signatures : [];
  const ids = sigs.map((s, i) => (s && (s.id || s.signaturePath || `${s.name || ''}#${i}`)) || `#${i}`);
  const md = (data && data.meetingDetails && typeof data.meetingDetails === 'object') ? data.meetingDetails : {};
  // TASK-9742 — attached company documents are part of the PDF; the key covers
  // their ids ONLY when non-empty, byte-for-byte with safetySignInPdfDecision.js.
  const docs = (data && Array.isArray(data.meetingDocuments) ? data.meetingDocuments : [])
    .map((d) => (d && typeof d.id === 'string') ? d.id : '').filter(Boolean);
  const p = _srkStable(docs.length ? { ids, details: md, docs } : { ids, details: md });
  return _srkFnv(p, 0x811c9dc5).toString(16).padStart(8, '0') + _srkFnv(p, 0x01000193 ^ 0x9e3779b9).toString(16).padStart(8, '0');
}

// TASK-4882 — browser port of functions/src/services/safetyOutline.js
// safetyOutlineMissing. The SERVER refuses a crew sign-in for any day whose
// sheet lacks a meeting type + topics; this tells the office the same thing
// before the crew finds out. Pinned equal to the server by
// tests/task4882-safety-outline-gate.unit.cjs — change BOTH or neither.
const SAFETY_MEETING_TYPES_CLIENT = ['daily', 'weekly', 'toolbox', 'incident', 'training'];
function safetyOutlineMissingClient(meetingDetails) {
  const md = (meetingDetails && typeof meetingDetails === 'object' && !Array.isArray(meetingDetails)) ? meetingDetails : {};
  const missing = [];
  const types = (md.types && typeof md.types === 'object' && !Array.isArray(md.types)) ? md.types : {};
  if (!SAFETY_MEETING_TYPES_CLIENT.some((k) => types[k] === true)) missing.push('meeting type');
  if (typeof md.topics !== 'string' || md.topics.trim().length === 0) missing.push('topics');
  return missing;
}

function renderSafetySignInList() {
  const container = document.getElementById('hr-safety-signin-list');
  const badge = document.getElementById('hr-safety-today-count');
  if (!container) return;
  const modal = document.getElementById('modal-meeting-details');
  if (modal && !modal.classList.contains('hidden')) { _pendingModalRenders.add('safetySignIn'); return; } // TASK-1119

  const today = getTodayDateString();
  const startVal = document.getElementById('hr-signin-date-start')?.value;
  const endVal = document.getElementById('hr-signin-date-end')?.value;

  // Update today's badge
  if (badge) {
    const todaySheet = HRState.safetySignIns.find(s => s.date === today);
    const todayCount = todaySheet?.signatures?.length || 0;
    if (todayCount > 0) {
      badge.textContent = todayCount;
      badge.style.display = 'inline-flex';
    } else {
      badge.style.display = 'none';
    }
  }

  // Filter sign-in sheets by date range
  let matchingSheets = HRState.safetySignIns;
  if (startVal) {
    matchingSheets = matchingSheets.filter(s => s.date >= startVal);
  }
  if (endVal) {
    matchingSheets = matchingSheets.filter(s => s.date <= endVal);
  }
  // Sort by date descending
  matchingSheets = matchingSheets.sort((a, b) => (b.date || '').localeCompare(a.date || ''));

  // Collect all signatures across matching dates
  const allEntries = [];
  matchingSheets.forEach(sheet => {
    (sheet.signatures || []).forEach(sig => {
      allEntries.push({ ...sig, sheetDate: sheet.date, pdfUrl: sheet.pdfUrl });
    });
  });

  if (allEntries.length === 0) {
    const noFilter = !startVal && !endVal;
    // TASK-4882 — the empty state tells the office whether crew CAN sign in
    // today. A sheet with no meeting type + topics refuses every phone, so
    // "share the link" would be wrong advice until the outline is in.
    // COD Independent Reviewer: initSignInDateDefaults fills BOTH range inputs
    // with today before the first render, so `noFilter` is false on a normal
    // Safety open — the CTA keys off "does the range include today" instead.
    const rangeIncludesToday = (!startVal || startVal <= today) && (!endVal || endVal >= today);
    const todaySheet = HRState.safetySignIns.find(s => s.date === today);
    const todayMissing = safetyOutlineMissingClient(todaySheet?.meetingDetails);
    const todayReady = !!todaySheet && todayMissing.length === 0;
    container.innerHTML = `
      <div class="empty-state-small">
        <span class="material-icons" style="font-size: 24px; margin-bottom: 8px;">${rangeIncludesToday && !todayReady ? 'edit_note' : 'health_and_safety'}</span>
        <div>No sign-ins ${noFilter ? 'yet today' : 'for selected dates'}</div>
        ${rangeIncludesToday ? (todayReady
          ? '<div style="font-size: 0.75rem; color: var(--text-tertiary); margin-top: 4px;">Today\'s sheet is ready — share the link with your crew to sign in</div>'
          : `<div style="font-size: 0.8rem; color: var(--text-primary); margin-top: 6px;">Crew can't sign in today until you add the <strong>${escapeHtml(todayMissing.join(' + '))}</strong>.</div>
             <button class="btn btn-primary btn-sm" style="margin-top:8px;" onclick="window.HRModule.openMeetingDetailsModal('${escJsAttr(today)}')">
               <span class="material-icons" style="font-size:16px;">edit_note</span> Set up today's sheet
             </button>`) : ''}
      </div>
    `;
    return;
  }

  let html = '';
  const multiDate = matchingSheets.length > 1;

  matchingSheets.forEach(sheet => {
    const sigs = sheet.signatures || [];
    if (sigs.length === 0) return;

    const dateObj = new Date(sheet.date + 'T12:00:00');
    const dateLabel = dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    const isToday = sheet.date === today;

    // TASK-342 Slice D — inline "Add meeting details" button on rows that
    // have signatures but no meetingDetails. Handles the orphan-signatures
    // backfill case Chris flagged.
    // TASK-344 — auto-generated PDF state machine, TASK-9385 round 3: a PDF is
    // only presented as THE record when its stamped pdfRosterKey equals the
    // sheet's CURRENT roster key (same hash the server stamps). Otherwise it is
    // the previous version, never "PDF".
    //   no details                          → "Add details" button
    //   details, no current PDF, no error   → "Updating PDF…" (+ "Previous PDF" link if one exists)
    //   details + error                     → "PDF failed" (+ "Previous PDF" link if one exists)
    //   current PDF                         → "PDF" download
    //   legacy PDF (filed before roster keys) → primary "PDF" link + a small hint
    //     that Generate PDF refreshes it (round 4: the office's filed record stays
    //     one click away; we just can't PROVE it lists everyone)
    const hasMeetingDetails = !!(sheet.meetingDetails && Object.keys(sheet.meetingDetails).length > 0);
    // TASK-4882 — a sheet with signatures but no outline is a legacy row
    // (pre-gate) or one whose outline was cleared after signing. Say so.
    const outlineMissing = safetyOutlineMissingClient(sheet.meetingDetails);
    const hasPdfError = !!sheet.pdfGenerationError;
    const hasPdf = !!sheet.pdfUrl;
    const legacyPdf = hasPdf && !sheet.pdfRosterKey;
    const pdfIsCurrent = hasPdf && !!sheet.pdfRosterKey && sheet.pdfRosterKey === safetyRosterKeyClient(sheet);
    const pdfIsPrevious = hasPdf && !pdfIsCurrent && !legacyPdf;
    const isGenerating = hasMeetingDetails && !pdfIsCurrent && !hasPdfError && !legacyPdf;
    html += `
      <div style="margin-bottom: var(--spacing-md);">
        <div class="safety-signin-header" style="display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--spacing-sm); padding-bottom: var(--spacing-xs); border-bottom: 1px solid var(--border-light);gap:var(--spacing-sm);flex-wrap:wrap;">
          <span style="font-weight: 600; font-size: 0.9rem;">${multiDate ? `${dateLabel}${isToday ? ' (Today)' : ''} — ` : ''}${sigs.length} Attendee${sigs.length !== 1 ? 's' : ''}</span>
          <div style="display:flex;gap:6px;align-items:center;">
            ${!hasMeetingDetails ? `
              <button class="btn btn-ghost btn-sm" onclick="window.HRModule.openMeetingDetailsModal('${escJsAttr(sheet.date)}')" style="font-size:0.75rem;color:var(--brand-blue);" title="No meeting details on file for this date — add them now">
                <span class="material-icons" style="font-size:14px;">edit_note</span> Add details
              </button>
            ` : outlineMissing.length ? `
              <button class="btn btn-ghost btn-sm" onclick="window.HRModule.openMeetingDetailsModal('${escJsAttr(sheet.date)}')" style="font-size:0.75rem;color:#d97706;" title="Outline incomplete — missing ${escapeHtml(outlineMissing.join(' + '))}. ${isToday ? 'Crew can\'t sign in until it is added.' : ''}">
                <span class="material-icons" style="font-size:14px;">report_problem</span> Add ${escapeHtml(outlineMissing.join(' + '))}
              </button>
            ` : `
              <button class="btn btn-ghost btn-sm" onclick="window.HRModule.openMeetingDetailsModal('${escJsAttr(sheet.date)}')" style="font-size:0.75rem;color:var(--text-secondary);" title="Edit meeting details (will regenerate PDF)">
                <span class="material-icons" style="font-size:14px;">edit</span> Edit details
              </button>
            `}
            ${isGenerating ? `
              <span class="btn btn-ghost btn-sm" style="font-size:0.75rem;color:var(--text-secondary);cursor:default;" title="The PDF is being rebuilt with everyone signed in so far">
                <span class="btn-spinner" style="display:inline-block;width:12px;height:12px;border:2px solid var(--border-light);border-top-color:var(--brand-blue);border-radius:50%;animation:spin 0.8s linear infinite;vertical-align:middle;"></span>
                Updating PDF…
              </span>
            ` : ''}
            ${hasPdfError ? `
              <span class="btn btn-ghost btn-sm" style="font-size:0.75rem;color:var(--brand-red, #dc2626);cursor:default;" title="${escapeHtml(sheet.pdfGenerationError)}">
                <span class="material-icons" style="font-size:14px;">error_outline</span> PDF failed — use Generate PDF to retry
              </span>
            ` : ''}
            ${pdfIsCurrent ? `
              <a href="${escapeHtml(sheet.pdfUrl)}" target="_blank" rel="noopener noreferrer" class="btn btn-ghost btn-sm" style="font-size: 0.75rem;" title="Includes everyone signed in">
                <span class="material-icons" style="font-size: 14px;">download</span> PDF
              </a>
            ` : ''}
            ${pdfIsPrevious ? `
              <a href="${escapeHtml(sheet.pdfUrl)}" target="_blank" rel="noopener noreferrer" class="btn btn-ghost btn-sm" style="font-size: 0.75rem;color:var(--text-secondary);" title="An earlier version — it does not include the latest sign-ins or details.">
                <span class="material-icons" style="font-size: 14px;">history</span> Previous PDF
              </a>
            ` : ''}
            ${legacyPdf ? `
              <a href="${escapeHtml(sheet.pdfUrl)}" target="_blank" rel="noopener noreferrer" class="btn btn-ghost btn-sm" style="font-size: 0.75rem;">
                <span class="material-icons" style="font-size: 14px;">download</span> PDF
              </a>
              <span style="font-size:0.72rem;color:var(--text-secondary);" title="This PDF was filed before the app tracked which roster it shows. Pick this date and use Generate PDF to rebuild it with everyone signed in.">Filed earlier — Generate PDF to refresh</span>
            ` : ''}
          </div>
        </div>
        <div class="safety-signin-grid" style="display: grid; gap: var(--spacing-xs);">
    `;

    sigs.forEach((sig, i) => {
      const signedAt = sig.signedAt?.toDate?.() || new Date(sig.signedAt);
      const timeStr = signedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
      html += `
        <div class="safety-signin-row" style="display: flex; align-items: center; gap: var(--spacing-sm); padding: var(--spacing-xs) var(--spacing-sm); background: var(--bg-secondary); border-radius: var(--radius-sm);">
          <span style="font-size: 0.75rem; color: var(--text-tertiary); min-width: 20px;">${i + 1}.</span>
          <span style="flex: 1; font-weight: 500;">${escapeHtml(sig.name)}</span>
          <span style="font-size: 0.75rem; color: var(--text-secondary);">${timeStr}</span>
        </div>
      `;
    });

    html += '</div></div>';
  });

  container.innerHTML = html;
}

function loadSafetySignInsForDate() {
  renderSafetySignInList();
}

// TASK-342 Slice D — flip settings.safetyMeetingSignInEnabled. Mirror change
// to local AppState so the driver portal navicon visibility updates on next
// re-render without a page reload. Server gate enforces the same flag on
// every submitSafetySignIn write, so flipping OFF immediately stops accepting
// sign-ins (both driver-portal and forever URL paths).
async function toggleSafetyMeetingEnabled(enabled) {
  const tenantId = HRState.tenantId;
  if (!tenantId) { window.showToast?.('Not signed in', 'error'); return; }
  try {
    // TASK-637 — tenant-doc settings write routed through the hr-gated callable
    // so hr-module staff can toggle it (direct tenant-doc write is admin-only).
    const ok = await window._persistHrSettings?.({ safetyMeetingSignInEnabled: !!enabled });
    if (ok === false) throw new Error('save failed');
    window.showToast?.(enabled ? 'Safety meeting sign-in enabled' : 'Safety meeting sign-in disabled', 'success');
  } catch (err) {
    console.error('[HR] toggleSafetyMeetingEnabled failed:', err);
    window.showToast?.('Could not update setting', 'error');
    // Revert the checkbox to its actual state
    const cb = document.getElementById('hr-safety-meeting-enabled-toggle');
    if (cb) cb.checked = window.AppState?.settings?.safetyMeetingSignInEnabled === true;
  }
}

// TASK-437 Slice 1 — flip settings.safetyEquipmentDriverEnabled. Mirror to
// local AppState so the driver portal's PPE tab visibility updates on next
// re-render without a page reload (same pattern as toggleSafetyMeetingEnabled).
// The firestore rule (Slice 5) independently gates driver writes, so flipping
// OFF here only hides the tab; a stale client can't write because the rule
// requires an own-personId binding regardless of this flag.
async function toggleSafetyEquipmentDriverEnabled(enabled) {
  const tenantId = HRState.tenantId;
  if (!tenantId) { window.showToast?.('Not signed in', 'error'); return; }
  try {
    // TASK-637 — routed through the hr-gated callable (see toggleSafetyMeetingEnabled).
    const ok = await window._persistHrSettings?.({ safetyEquipmentDriverEnabled: !!enabled });
    if (ok === false) throw new Error('save failed');
    window.showToast?.(enabled ? 'PPE sign-off enabled in driver app' : 'PPE sign-off disabled in driver app', 'success');
  } catch (err) {
    console.error('[HR] toggleSafetyEquipmentDriverEnabled failed:', err);
    window.showToast?.('Could not update setting', 'error');
    const cb = document.getElementById('hr-safety-ppe-driver-toggle');
    if (cb) cb.checked = window.AppState?.settings?.safetyEquipmentDriverEnabled === true;
  }
}

// TASK-4879 — THE CROSS-TENANT BUG WAS HERE.
//
// This handed EVERY carrier the literal URL `${origin}/safety.html`, and that
// page hardcodes Trippin Trucking. Any other carrier who put that link in front
// of their crew would have collected THEIR signatures into TITS's sign-in sheet
// — a compliance record, with a named person's signature image attached to the
// wrong company. It never happened only because 1 of 93 tenants has the feature
// switched on, and because nothing in the UI actually called this function (the
// button below is new; it was exported to window and orphaned).
//
// Now it mints — or reuses — an OPAQUE per-tenant `safety_signin` token on the
// existing accessTokens pattern (same shape as sharePublicDocsPage above and the
// driver `?access=` links) and emits `/safety.html?safety=<token>`. The tenant
// travels in the token, never as a guessable slug, so a carrier's link cannot be
// pointed at another carrier by editing the URL.
//
// Reuse-before-mint matters: minting a fresh token on every click would leave a
// trail of live never-expiring credentials in accessTokens and mean a link
// printed last month and one copied today disagree about which is current.
async function copySafetyLink() {
  const tenantId = HRState.tenantId;
  if (!tenantId) { window.showToast?.('Not signed in', 'error'); return; }

  let link;
  try {
    const existingSnap = await getDocs(
      query(collection(db, 'accessTokens'),
        where('tenantId', '==', tenantId),
        where('type', '==', 'safety_signin')
      )
    );

    // Skip a revoked OR expired token — otherwise "Copy link" would keep handing
    // out a credential the server will refuse, and the crew would meet a 403 at
    // the meeting with no idea why. We mint with expiresAt:null (never expires),
    // so the expiry arm is defensive: it covers a token an admin later gave a
    // TTL, and matches the server's own rule (_accessTokenExpired) that an
    // absent expiry means "never" and an UNPARSEABLE one means expired.
    const live = existingSnap.docs.find(d => {
      const t = d.data() || {};
      if (t.revoked === true) return false;
      const raw = t.expiresAt;
      if (raw === null || raw === undefined) return true;
      const exp = raw?.toDate?.() || new Date(raw);
      return !isNaN(exp.getTime()) && exp > new Date();
    });

    let token;
    if (live) {
      token = live.id;
    } else {
      token = window.mintAccessTokenId('safety');
      await setDoc(doc(db, 'accessTokens', token), {
        type: 'safety_signin',
        tenantId,
        createdAt: Timestamp.now(),
        // Never expires, matching company_docs and the driver links. A safety
        // meeting link lives on a crew linktree or a yard whiteboard for years;
        // a TTL here would silently kill it between meetings.
        expiresAt: null
      });
    }
    link = `${window.location.origin}/safety.html?safety=${token}`;
  } catch (err) {
    console.error('[HR] copySafetyLink: could not mint/read the sign-in token:', err);
    // DELIBERATELY no fallback to the bare `/safety.html`. That bare URL belongs
    // to ONE carrier; handing it to this tenant on an error path is precisely
    // the cross-tenant misfile this change exists to remove. Fail visibly.
    window.showToast?.('Could not build the sign-in link — try again', 'error');
    return;
  }

  // TASK-4882 — the link is a forever credential (linktree / yard whiteboard),
  // so copying it is not "sending today's sheet". But if today's outline is
  // not in, every phone that opens it today is refused — say that now.
  const _todaySheet = HRState.safetySignIns.find(s => s.date === getTodayDateString());
  const _todayMissing = safetyOutlineMissingClient(_todaySheet?.meetingDetails);
  const copiedMsg = _todayMissing.length
    ? `Link copied — but crew can't sign in today until you add the ${_todayMissing.join(' + ')} in Sign-In Sheet Details.`
    : 'Safety sign-in link copied!';
  const copiedKind = _todayMissing.length ? 'warning' : 'success';

  navigator.clipboard.writeText(link).then(() => {
    window.showToast?.(copiedMsg, copiedKind);
  }).catch(() => {
    // Fallback for older browsers
    const input = document.createElement('input');
    input.value = link;
    document.body.appendChild(input);
    input.select();
    document.execCommand('copy');
    document.body.removeChild(input);
    window.showToast?.(copiedMsg, copiedKind);
  });
}

async function generateSafetyPdf() {
  // TASK-9385 — rebuilds the PDF for the date in the Sign-In Sheet picker (any
  // day, not only today), so a filed roster can always be re-filed from the
  // current signatures. Falls back to today when the picker is empty.
  const today = getTodayDateString();
  const date = document.getElementById('hr-safety-sheet-date')?.value || today;
  const sheet = HRState.safetySignIns.find(s => s.date === date);
  const dayLabel = date === today ? 'today' : new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  if (!sheet || !sheet.signatures || sheet.signatures.length === 0) {
    window.showToast?.(`No signatures to generate a PDF for ${dayLabel}`, 'error');
    return;
  }

  // Show loading state
  const btn = event?.target?.closest?.('button');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Generating...';
  }

  try {
    // Use pre-imported functions from firebase-config.js
    const generatePdf = httpsCallable(functions, 'generateSafetySignInPdf');

    const result = await generatePdf({
      tenantId: HRState.tenantId,
      date
    });

    if (result.data?.url) {
      window.open(result.data.url, '_blank');
      window.showToast?.('PDF generated and opened!', 'success');
    } else {
      throw new Error('No URL returned');
    }
  } catch (error) {
    console.error('[HR] Error generating safety PDF:', error);
    // The callable's failed-precondition / aborted messages are written for the
    // user ("needs meeting details…", "people are still signing in…").
    const code = String(error?.code || '');
    const friendly = /failed-precondition|aborted/.test(code) && error?.message ? error.message : 'Error generating PDF';
    window.showToast?.(friendly, 'error');
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons">picture_as_pdf</span> Generate PDF';
    }
  }
}

// ============================================
// SAFETY MEETING DETAILS MODAL
// ============================================

// TASK-4882 — read the Sign-In Sheet Details form into the exact meetingDetails
// shape firestore.rules safetyMeetingDetailsOk admits (5 keys). One reader for
// the live outline hint AND saveMeetingDetails, so they can never disagree
// about what is on the form.
function _readMeetingDetailsForm() {
  return {
    time: document.getElementById('meeting-time')?.value || '',
    location: document.getElementById('meeting-location')?.value?.trim() || '',
    facilitator: document.getElementById('meeting-facilitator')?.value?.trim() || '',
    types: {
      daily: document.getElementById('meeting-type-daily')?.checked || false,
      weekly: document.getElementById('meeting-type-weekly')?.checked || false,
      toolbox: document.getElementById('meeting-type-toolbox')?.checked || false,
      incident: document.getElementById('meeting-type-incident')?.checked || false,
      training: document.getElementById('meeting-type-training')?.checked || false
    },
    topics: document.getElementById('meeting-topics')?.value?.trim() || ''
  };
}

// TASK-4882 — the inline line under the form: what the crew's phone will say
// is missing, updated as the office types. Green when the sheet is ready.
function _renderMeetingOutlineHint() {
  const el = document.getElementById('meeting-outline-hint');
  if (!el) return;
  const missing = safetyOutlineMissingClient(_readMeetingDetailsForm());
  if (missing.length === 0) {
    el.innerHTML = `<span class="material-icons" style="font-size:16px;vertical-align:-3px;color:#059669;">check_circle</span> Outline complete — crew can sign in on this date.`;
    el.style.color = 'var(--text-primary)';
  } else {
    el.innerHTML = `<span class="material-icons" style="font-size:16px;vertical-align:-3px;color:#d97706;">report_problem</span> Crew <strong>can't sign in</strong> on this date until you add: <strong>${escapeHtml(missing.join(' + '))}</strong>.`;
    el.style.color = 'var(--text-primary)';
  }
}

// `prefill` (TASK-9742 hook, not built here): a meetingDetails-shaped object
// — e.g. a saved template — laid over the sheet's existing details before the
// form renders. Nothing calls it yet; it is the seam a template feature fills.
function openMeetingDetailsModal(explicitDate, prefill) {
  // TASK-1117 — safety sign-in ADMIN edits are paywalled for complianceOnly.
  // Inline check (NOT _hrGateComplianceOnly): this surface is also embedded in
  // the Compliance view's Safety tab via initSafetyStandalone, where the
  // helper's bounce-to-hiring would stomp the compliance view. Driver sign-ins
  // (submitSafetySignIn callable) are unaffected.
  if (window.AppState?.tenantData?.complianceOnly) {
    window.showComplianceUpgradeOverlay?.('hr-safety');
    return;
  }
  // TASK-342 Slice D — accept explicit date arg so the date picker + inline
  // "Add details" buttons can target any day. Falls through to the prior
  // behavior (date-range start or today) when no arg passed.
  // TASK-9385 — ANY date, past or future: no clamp here, none in
  // saveMeetingDetails. A future date is how the office prepares tomorrow's
  // sign-in roster today; the drivers' signatures land on the same
  // safetySignIns/{date} doc tomorrow (submitSafetySignIn txn.update keeps
  // meetingDetails intact).
  const today = getTodayDateString();
  const selectedDate = (typeof explicitDate === 'string' && explicitDate)
    ? explicitDate
    : (document.getElementById('hr-signin-date-start')?.value || today);
  const sheet = HRState.safetySignIns.find(s => s.date === selectedDate);

  // Get existing meeting details or defaults (+ TASK-4882 prefill seam)
  const details = Object.assign({}, sheet?.meetingDetails || {},
    (prefill && typeof prefill === 'object' && !Array.isArray(prefill)) ? prefill : {});

  // Format date for display
  const dateObj = new Date(selectedDate + 'T12:00:00');
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });
  // Header date, e.g. "Fri, Sep 25" — the user preparing tomorrow's sheet must
  // see WHICH day they are editing without reading the body.
  const headerDate = dateObj.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric'
  });
  const isFuture = selectedDate > today;
  const isPast = selectedDate < today;
  // TASK-9385 (COD Domain) — no backdated attendance records: a PAST date can
  // only get details on a sheet that already exists (people signed that day).
  // TASK-9149 — and the modal must not OFFER one either. It used to render the
  // full form + Save here; Save then refused with a toast (the rules create arm
  // refuses a backdated sheet too). A button that can only fail is the bug 9149
  // filed. On a past day with no sheet: no form, no Save — say why, and hand
  // over today's sheet instead. saveMeetingDetails keeps its own refusal as the
  // belt. COD: the form is only hidden once the sheet list has actually
  // LOADED (a success snapshot). Before that — or after the subscription
  // errored and left [] — "no sheet in the list" proves nothing, so the form
  // stays and saveMeetingDetails' getDoc decides.
  const pastNoSheet = isPast && !sheet;
  const hideSheetForm = pastNoSheet && HRState.safetySignInsLoaded === true;
  const whenHint = isFuture
    ? 'Preparing ahead — drivers will sign this sheet on that day from the driver app or the sign-in link.'
    : (hideSheetForm
      ? 'Nobody signed in on this day, so there is no sheet to add details to. You can prepare today or any future date.'
      : (isPast ? 'Past meeting — signatures already on file for that day stay attached.' : ''));

  // TASK-9742 — the documents attached to this sheet, edited in the modal and
  // saved by saveMeetingDetails through setSafetyMeetingDocuments.
  const sheetDocs = (Array.isArray(sheet?.meetingDocuments) ? sheet.meetingDocuments : [])
    .filter((d) => d && typeof d.id === 'string')
    .map((d) => ({ id: d.id, title: d.title || 'Document', fileName: d.fileName || '', mimeType: d.mimeType || '' }));
  // COD Domain/Adversary — once crew have signed, the document list is what
  // they were shown; it is frozen (server refuses the change too).
  const docsLocked = Array.isArray(sheet?.signatures) && sheet.signatures.length > 0;
  HRState.meetingModal = { date: selectedDate, docs: sheetDocs, originalDocIds: sheetDocs.map((d) => d.id), docsLocked };

  // Create modal HTML
  const modalHtml = `
    <div class="modal" id="modal-meeting-details">
      <div class="modal-content modal-fullscreen" style="max-width: 500px;">
        <div class="modal-header">
          <h2><span class="material-icons">edit_note</span> Sign-In Sheet — ${headerDate}</h2>
          <button class="modal-close" onclick="window.HRModule.closeMeetingDetailsModal()">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <div style="background: var(--bg-secondary); padding: var(--spacing-sm) var(--spacing-md); border-radius: var(--radius-sm); margin-bottom: var(--spacing-md); text-align: center;">
            <strong>${formattedDate}</strong>
            ${whenHint ? `<div style="font-size:0.78rem;color:var(--text-secondary);margin-top:4px;">${whenHint}</div>` : ''}
          </div>
          ${hideSheetForm ? '' : `
          <!-- TASK-9742 — start from a saved template (fills the outline in one step) -->
          <div class="form-group">
            <label for="meeting-template-select">Start from a template</label>
            <div style="display:flex;gap:8px;align-items:center;">
              <select id="meeting-template-select" class="form-control" style="flex:1;min-width:0;" onchange="window.HRModule.applySafetyMeetingTemplate(this.value)">
                <option value="">Loading templates…</option>
              </select>
              <button type="button" class="btn btn-ghost btn-sm" title="Rename or delete templates" aria-label="Manage templates" onclick="window.HRModule.openSafetyTemplateManager()">
                <span class="material-icons" style="font-size:18px;">settings</span>
              </button>
            </div>
          </div>

          <div class="form-group">
            <label for="meeting-time">Time</label>
            <input type="time" id="meeting-time" class="form-control" maxlength="20" value="${escapeHtml(details.time || '')}">
          </div>

          <div class="form-group">
            <label for="meeting-location">Location</label>
            <input type="text" id="meeting-location" class="form-control" maxlength="500" placeholder="e.g., Shop, Job Site" value="${escapeHtml(details.location || '')}">
          </div>

          <div class="form-group">
            <label for="meeting-facilitator">Facilitator</label>
            <input type="text" id="meeting-facilitator" class="form-control" maxlength="500" placeholder="e.g., John Smith" value="${escapeHtml(details.facilitator || '')}">
          </div>

          <div class="form-group">
            <label>Meeting Type</label>
            <div style="display: flex; flex-wrap: wrap; gap: var(--spacing-sm); margin-top: var(--spacing-xs);">
              <label class="checkbox-label" style="display: flex; align-items: center; gap: 4px; font-weight: normal; font-size: 0.875rem;">
                <input type="checkbox" id="meeting-type-daily" ${details.types?.daily ? 'checked' : ''}> Daily Tailgate
              </label>
              <label class="checkbox-label" style="display: flex; align-items: center; gap: 4px; font-weight: normal; font-size: 0.875rem;">
                <input type="checkbox" id="meeting-type-weekly" ${details.types?.weekly ? 'checked' : ''}> Weekly Safety
              </label>
              <label class="checkbox-label" style="display: flex; align-items: center; gap: 4px; font-weight: normal; font-size: 0.875rem;">
                <input type="checkbox" id="meeting-type-toolbox" ${details.types?.toolbox ? 'checked' : ''}> Toolbox Talk
              </label>
              <label class="checkbox-label" style="display: flex; align-items: center; gap: 4px; font-weight: normal; font-size: 0.875rem;">
                <input type="checkbox" id="meeting-type-incident" ${details.types?.incident ? 'checked' : ''}> Incident Review
              </label>
              <label class="checkbox-label" style="display: flex; align-items: center; gap: 4px; font-weight: normal; font-size: 0.875rem;">
                <input type="checkbox" id="meeting-type-training" ${details.types?.training ? 'checked' : ''}> Training
              </label>
            </div>
          </div>

          <div class="form-group">
            <label for="meeting-topics">Topics Covered</label>
            <textarea id="meeting-topics" class="form-control" rows="4" maxlength="5000" placeholder="• Topic 1&#10;• Topic 2&#10;• Topic 3">${escapeHtml(details.topics || '')}</textarea>
          </div>

          <!-- TASK-9742 — the company's own documents (HR Company Documents). -->
          <div class="form-group">
            <label>Documents for crew to review</label>
            <!-- TASK-9742 S2 UX follow-up: what lands in the signed PDF (pdf.js appends PDF / PNG / JPEG only). -->
            <div id="meeting-docs-pdf-hint" style="font-size:0.8rem;color:var(--text-secondary);margin:-2px 0 6px;">PDFs and PNG or JPG images are copied into the signed sign-in PDF when they fit its size limits. Word, Excel and other files, and any file that is too large or password-protected, are listed by title only.</div>
            <div id="meeting-docs-list"></div>
            <div id="meeting-docs-actions" style="display:flex;gap:8px;flex-wrap:wrap;margin-top:6px;">
              <button type="button" class="btn btn-secondary btn-sm" onclick="window.HRModule.openMeetingDocPicker()">
                <span class="material-icons" style="font-size:16px;">attach_file</span> From Company Documents
              </button>
              <label class="btn btn-secondary btn-sm" style="cursor:pointer;margin:0;">
                <span class="material-icons" style="font-size:16px;">upload_file</span> Upload new
                <input type="file" id="meeting-doc-upload" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.heic,application/pdf,image/*" style="display:none;" onchange="window.HRModule.uploadMeetingDoc(this)">
              </label>
            </div>
          </div>
          <!-- TASK-4882 — the outline gate, office side. Mirrors the server:
               no meeting type + topics = crew phones are refused for this date. -->
          <div id="meeting-outline-hint" style="font-size:0.82rem;line-height:1.45;padding:8px 10px;background:var(--bg-secondary);border-radius:var(--radius-sm);"></div>

          <!-- TASK-9742 — Save as template: inline name row, shown on demand. -->
          <div style="margin-top:var(--spacing-sm);">
            <button type="button" class="btn btn-ghost btn-sm" onclick="window.HRModule.toggleSaveAsTemplate(true)" style="color:var(--brand-blue);">
              <span class="material-icons" style="font-size:16px;">bookmark_add</span> Save as template
            </button>
            <div id="meeting-save-template-row" style="display:none;margin-top:6px;">
              <div style="display:flex;gap:8px;flex-wrap:wrap;">
                <input type="text" id="meeting-template-name" class="form-control" maxlength="80" placeholder="Template name, e.g. Monday tailgate" style="flex:1;min-width:180px;">
                <button type="button" class="btn btn-primary btn-sm" id="btn-meeting-template-save" onclick="window.HRModule.saveMeetingAsTemplate()">Save template</button>
                <button type="button" class="btn btn-secondary btn-sm" onclick="window.HRModule.toggleSaveAsTemplate(false)">Cancel</button>
              </div>
              <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">Saves the meeting type, topics, time, location, facilitator and documents shown above.</div>
            </div>
          </div>
          `}
        </div>
        <div class="modal-footer">
          ${hideSheetForm ? `
          <button class="btn btn-secondary" onclick="window.HRModule.closeMeetingDetailsModal()">Close</button>
          <button class="btn btn-primary" onclick="window.HRModule.openMeetingDetailsModal('${escJsAttr(today)}')">
            <span class="material-icons">edit_note</span> Open today's sheet
          </button>
          ` : `
          <button class="btn btn-secondary" onclick="window.HRModule.closeMeetingDetailsModal()">Cancel</button>
          <button class="btn btn-primary" onclick="window.HRModule.saveMeetingDetails('${escJsAttr(selectedDate)}')">
            <span class="material-icons">save</span> Save Details
          </button>
          `}
        </div>
      </div>
    </div>
  `;

  // Remove existing modal if any
  const existing = document.getElementById('modal-meeting-details');
  if (existing) existing.remove();

  // Add modal to body
  document.body.insertAdjacentHTML('beforeend', modalHtml);
  document.getElementById('modal-meeting-details').classList.remove('hidden');

  // TASK-4882 — live outline hint: re-render on every keystroke / tick.
  // `typeof` guard: tests/task9149-past-no-sheet-no-save.unit.cjs evaluates
  // this ONE function in an isolated vm context where sibling helpers do not
  // exist; in the real module the helper is always defined.
  if (!hideSheetForm && typeof _renderMeetingOutlineHint === 'function') {
    const modalEl = document.getElementById('modal-meeting-details');
    modalEl.addEventListener('input', _renderMeetingOutlineHint);
    modalEl.addEventListener('change', _renderMeetingOutlineHint);
    _renderMeetingOutlineHint();
  }
  // TASK-9742 — template picker + attached documents (same isolated-vm guard).
  if (!hideSheetForm && typeof _initMeetingTemplateUi === 'function') _initMeetingTemplateUi();
}

function closeMeetingDetailsModal() {
  const modal = document.getElementById('modal-meeting-details');
  if (modal) modal.remove();
  // TASK-9742 — the document picker / template manager belong to this modal.
  document.getElementById('modal-meeting-doc-picker')?.remove();
  document.getElementById('modal-safety-templates')?.remove();
  HRState.meetingModal = null;
  _flushPendingModalRender('safetySignIn'); // TASK-1119 — this modal bypasses closeModal()
}

// ============================================
// TASK-9742 — SAFETY MEETING TEMPLATES + ATTACHED COMPANY DOCUMENTS
// ============================================
// Wolverine demo 10/8 (Doug: "so I can't upload a document"). Office side:
//   • "Start from a template" on the Sign-In Sheet form fills type + topics
//     (+ time / location / facilitator) + documents in one step — which is the
//     TASK-4882 outline, so a sheet started from a template is ready for crew.
//   • "Save as template" captures the form + its documents under a name.
//   • Manage (gear) renames / deletes templates.
//   • Documents ARE HR Company Documents (hrResources + Storage
//     tenants/{tid}/hr/company-documents/) — attach from the library or upload
//     a new one into it. No new storage path.
// Templates and a sheet's document list are written ONLY by the
// safetyMeetingTemplates / setSafetyMeetingDocuments callables (server mirror
// of rules canEditSafetySheet); no client Firestore write here.
const SAFETY_MEETING_MAX_DOCS = 10; // == services/safetyMeetingTemplates.js MAX_DOCS_PER_MEETING

async function _callSafetyTemplates(payload) {
  const fn = httpsCallable(functions, 'safetyMeetingTemplates');
  const res = await fn({ tenantId: HRState.tenantId, ...payload });
  return res?.data || {};
}

function _safetyCallableMessage(err, fallback) {
  // HttpsError messages from our own callables are written for the office;
  // "internal" / network errors are not.
  const code = String(err?.code || "");
  const m = String(err?.message || "");
  if (!m || m.length > 240 || /internal|unavailable|deadline/.test(code) || m === "INTERNAL") return fallback;
  return m;
}

// TASK-9742 (COD Code Reviewer) — window._hrResources starts as [] before any
// snapshot, so "is an array" never meant "loaded". The listener's flag does.
function _companyDocsReady() {
  return _hrResourcesLoaded.value === true && Array.isArray(window._hrResources);
}

/** Company documents a meeting may attach: this tenant's HR company-documents files. */
function _attachableCompanyDocs() {
  const prefix = `tenants/${HRState.tenantId}/hr/company-documents/`;
  const list = Array.isArray(window._hrResources) ? window._hrResources : [];
  return list.filter((r) => r && r.type === 'document' && typeof r.storagePath === 'string' && r.storagePath.startsWith(prefix));
}

function _docSnapFromResource(r) {
  return { id: r.id, title: r.title || r.fileName || 'Document', fileName: r.fileName || '', mimeType: r.mimeType || '' };
}

function _docIcon(mimeType) {
  const m = String(mimeType || '');
  if (m.includes('pdf')) return 'picture_as_pdf';
  if (m.startsWith('image/')) return 'image';
  return 'description';
}

// Called once after the Sign-In Sheet modal renders its form.
function _initMeetingTemplateUi() {
  _renderMeetingDocsList();
  _renderMeetingTemplateSelect();
  _loadSafetyTemplates(false).then(_renderMeetingTemplateSelect).catch((err) => {
    console.warn('[HR] safety templates load failed', err);
    const sel = document.getElementById('meeting-template-select');
    if (sel) sel.innerHTML = '<option value="">Templates unavailable — try again later</option>';
  });
}

async function _loadSafetyTemplates(force) {
  if (!force && HRState.safetyTemplatesTenant === HRState.tenantId && Array.isArray(HRState.safetyTemplates)) {
    return HRState.safetyTemplates;
  }
  const data = await _callSafetyTemplates({ action: 'list' });
  HRState.safetyTemplates = Array.isArray(data.templates) ? data.templates : [];
  HRState.safetyTemplatesTenant = HRState.tenantId;
  return HRState.safetyTemplates;
}

function _renderMeetingTemplateSelect() {
  const sel = document.getElementById('meeting-template-select');
  if (!sel) return;
  const loaded = HRState.safetyTemplatesTenant === HRState.tenantId && Array.isArray(HRState.safetyTemplates);
  if (!loaded) { sel.innerHTML = '<option value="">Loading templates…</option>'; return; }
  const list = HRState.safetyTemplates;
  sel.innerHTML = list.length
    ? `<option value="">Choose a template…</option>${list.map((t) => `<option value="${escapeHtml(t.id)}">${escapeHtml(t.name)}</option>`).join('')}`
    : '<option value="">No templates yet — fill this sheet in, then "Save as template"</option>';
  sel.disabled = list.length === 0;
}

function _renderMeetingDocsList() {
  const el = document.getElementById('meeting-docs-list');
  if (!el) return;
  const docs = HRState.meetingModal?.docs || [];
  const locked = HRState.meetingModal?.docsLocked === true;
  const lockNote = locked ? '<div style="font-size:0.78rem;color:var(--text-secondary);margin-bottom:6px;">Crew have signed this sheet, so its documents are locked — they are what was shown before signing.</div>' : '';
  document.getElementById('meeting-docs-actions')?.style.setProperty('display', locked ? 'none' : 'flex');
  if (!docs.length) {
    el.innerHTML = lockNote + (locked ? '' : '<div style="font-size:0.8rem;color:var(--text-secondary);">None attached. Crew will see attached documents before they sign.</div>');
    return;
  }
  const lib = Array.isArray(window._hrResources) ? window._hrResources : [];
  el.innerHTML = docs.map((d) => {
    const live = lib.find((r) => r.id === d.id);
    const href = live && typeof live.url === 'string' && live.url.startsWith('https://') ? live.url : '';
    return `
      <div style="display:flex;align-items:center;gap:8px;padding:6px 8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);margin-bottom:6px;">
        <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">${_docIcon(d.mimeType)}</span>
        <span style="flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--text-primary);font-size:0.875rem;">
          ${href ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" style="color:var(--text-primary);">${escapeHtml(d.title)}</a>` : escapeHtml(d.title)}
          ${live || !_companyDocsReady() ? '' : '<span style="color:#d97706;font-size:0.75rem;"> — no longer in Company Documents</span>'}
        </span>
        ${locked ? '' : `<button type="button" class="btn btn-ghost btn-sm" title="Remove from this sheet" aria-label="Remove ${escapeHtml(d.title)}"
          onclick="window.HRModule.removeMeetingDoc('${escJsAttr(d.id)}')"><span class="material-icons" style="font-size:16px;">close</span></button>`}
      </div>`;
  }).join('');
  if (lockNote) el.insertAdjacentHTML('afterbegin', lockNote);
}

function removeMeetingDoc(id) {
  if (!HRState.meetingModal || HRState.meetingModal.docsLocked) return;
  HRState.meetingModal.docs = HRState.meetingModal.docs.filter((d) => d.id !== id);
  _renderMeetingDocsList();
}

// Fill the form from a template. Type + topics are replaced (that is what a
// template IS); time / location / facilitator only when the template has one,
// so a template never blanks a time the office already typed for this day.
async function applySafetyMeetingTemplate(templateId) {
  const sel = document.getElementById('meeting-template-select');
  if (!templateId) return;
  const t = (HRState.safetyTemplates || []).find((x) => x.id === templateId);
  if (sel) sel.value = '';
  if (!t || !HRState.meetingModal) return;
  const current = _readMeetingDetailsForm();
  const md = t.meetingDetails || {};
  if (current.topics && current.topics !== (md.topics || '')) {
    const go = await window.showConfirm(
      'Replace this sheet\'s outline?',
      `"${t.name}" will replace the meeting type, topics and attached documents on this sheet. Nothing is saved until you press Save.`,
      { confirmText: 'Use template', cancelText: 'Keep mine' }
    );
    if (!go) return;
  }
  if (!document.getElementById('modal-meeting-details')) return; // closed while confirming
  const setVal = (id, v) => { const el = document.getElementById(id); if (el && typeof v === 'string' && v) el.value = v; };
  setVal('meeting-time', md.time);
  setVal('meeting-location', md.location);
  setVal('meeting-facilitator', md.facilitator);
  const topicsEl = document.getElementById('meeting-topics');
  if (topicsEl) topicsEl.value = md.topics || '';
  ['daily', 'weekly', 'toolbox', 'incident', 'training'].forEach((k) => {
    const el = document.getElementById(`meeting-type-${k}`);
    if (el) el.checked = md.types?.[k] === true;
  });
  // Documents: keep only those still in the library (when it has loaded).
  // A signed sheet's documents are frozen (what the crew saw before signing).
  const libLoaded = _companyDocsReady();
  const attachable = new Set(_attachableCompanyDocs().map((r) => r.id));
  const tDocs = Array.isArray(t.documents) ? t.documents : [];
  const kept = libLoaded ? tDocs.filter((d) => attachable.has(d.id)) : tDocs;
  if (!HRState.meetingModal.docsLocked) {
    HRState.meetingModal.docs = kept.map((d) => ({ id: d.id, title: d.title || 'Document', fileName: d.fileName || '', mimeType: d.mimeType || '' }));
    _renderMeetingDocsList();
  }
  if (typeof _renderMeetingOutlineHint === 'function') _renderMeetingOutlineHint();
  const dropped = tDocs.length - kept.length;
  if (HRState.meetingModal.docsLocked && tDocs.length) {
    window.showToast?.('Template applied — crew already signed this sheet, so its documents stay as they were. Review, then Save.', 'warning');
    return;
  }
  window.showToast?.(dropped
    ? `Template applied — ${dropped} document${dropped === 1 ? ' is' : 's are'} no longer in Company Documents and ${dropped === 1 ? 'was' : 'were'} left off. Review, then Save.`
    : 'Template applied — review, then Save.', dropped ? 'warning' : 'success');
}

// ---- Save as template (inline name row in the modal footer area) ----
function toggleSaveAsTemplate(show) {
  const row = document.getElementById('meeting-save-template-row');
  if (!row) return;
  row.style.display = show ? '' : 'none';
  if (show) document.getElementById('meeting-template-name')?.focus();
}

async function saveMeetingAsTemplate() {
  const nameEl = document.getElementById('meeting-template-name');
  const btn = document.getElementById('btn-meeting-template-save');
  const name = (nameEl?.value || '').replace(/\s+/g, ' ').trim();
  if (!name) { window.showToast?.('Give the template a name', 'error'); nameEl?.focus(); return; }
  if (name.length > 80) { window.showToast?.('Template names are 80 characters or less', 'error'); return; }
  const meetingDetails = _readMeetingDetailsForm();
  const missing = safetyOutlineMissingClient(meetingDetails);
  if (missing.length) {
    window.showToast?.(`Add the ${missing.join(' + ')} first — a template has to be ready for crew to sign.`, 'error');
    return;
  }
  let templateId;
  try { await _loadSafetyTemplates(false); } catch (_) { /* save still works; duplicate check skipped */ }
  const same = (HRState.safetyTemplates || []).find((t) => t.name.toLowerCase() === name.toLowerCase());
  if (same) {
    const go = await window.showConfirm('Replace template?', `A template named "${same.name}" already exists. Replace it with this sheet?`, { confirmText: 'Replace', cancelText: 'Cancel' });
    if (!go) return;
    templateId = same.id;
  }
  if (btn) { btn.disabled = true; btn.textContent = 'Saving…'; }
  try {
    const data = await _callSafetyTemplates({
      action: 'save', templateId, name, meetingDetails,
      documentIds: (HRState.meetingModal?.docs || []).map((d) => d.id),
    });
    if (data.template) {
      const list = (HRState.safetyTemplates || []).filter((t) => t.id !== data.template.id);
      list.push(data.template);
      list.sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
      HRState.safetyTemplates = list;
      HRState.safetyTemplatesTenant = HRState.tenantId;
    }
    _renderMeetingTemplateSelect();
    toggleSaveAsTemplate(false);
    if (nameEl) nameEl.value = '';
    window.showToast?.(`Template "${name}" saved`, 'success');
  } catch (err) {
    console.error('[HR] save safety template failed', err);
    window.showToast?.(_safetyCallableMessage(err, 'Could not save the template'), 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.textContent = 'Save template'; }
  }
}

// ---- Document picker (HR Company Documents) ----
function openMeetingDocPicker() {
  if (!HRState.meetingModal || HRState.meetingModal.docsLocked) return;
  if (!_companyDocsReady()) {
    window.showToast?.('Company Documents are still loading — try again in a moment', 'info');
    return;
  }
  const attached = new Set(HRState.meetingModal.docs.map((d) => d.id));
  const docs = _attachableCompanyDocs().slice().sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
  const rows = docs.length ? docs.map((r) => `
      <label class="meeting-doc-pick" data-title="${escapeHtml(String(r.title || r.fileName || '').toLowerCase())}"
        style="display:flex;align-items:center;gap:10px;padding:8px;border-bottom:1px solid var(--border-light);cursor:pointer;color:var(--text-primary);">
        <input type="checkbox" value="${escapeHtml(r.id)}" ${attached.has(r.id) ? 'checked' : ''}>
        <span class="material-icons" style="font-size:18px;color:var(--text-secondary);">${_docIcon(r.mimeType)}</span>
        <span style="flex:1;min-width:0;">${escapeHtml(r.title || r.fileName || 'Document')}${r.category ? `<span style="display:block;font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(r.category)}</span>` : ''}</span>
      </label>`).join('')
    : '<div style="padding:16px;text-align:center;color:var(--text-secondary);font-size:0.875rem;">No company documents yet. Use "Upload new" on the sheet to add one.</div>';
  const html = `
    <div class="modal" id="modal-meeting-doc-picker" style="z-index:10010;">
      <div class="modal-content modal-fullscreen" style="max-width:480px;display:flex;flex-direction:column;max-height:85vh;max-height:85dvh;">
        <div class="modal-header">
          <h2><span class="material-icons">attach_file</span> Attach company documents</h2>
          <button class="modal-close" onclick="window.HRModule.closeMeetingDocPicker()"><span class="material-icons">close</span></button>
        </div>
        <div class="modal-body" style="flex:1;overflow-y:auto;padding-top:8px;">
          <input type="search" class="form-control" placeholder="Search documents" oninput="window.HRModule.filterMeetingDocPicker(this.value)" style="margin-bottom:8px;">
          <div id="meeting-doc-pick-list">${rows}</div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.HRModule.closeMeetingDocPicker()">Cancel</button>
          <button class="btn btn-primary" onclick="window.HRModule.applyMeetingDocPicker()"><span class="material-icons">check</span> Attach</button>
        </div>
      </div>
    </div>`;
  document.getElementById('modal-meeting-doc-picker')?.remove();
  document.body.insertAdjacentHTML('beforeend', html);
  document.getElementById('modal-meeting-doc-picker').classList.remove('hidden');
}

function filterMeetingDocPicker(q) {
  const needle = String(q || '').trim().toLowerCase();
  document.querySelectorAll('#meeting-doc-pick-list .meeting-doc-pick').forEach((el) => {
    el.style.display = !needle || (el.dataset.title || '').includes(needle) ? '' : 'none';
  });
}

function closeMeetingDocPicker() {
  document.getElementById('modal-meeting-doc-picker')?.remove();
}

function applyMeetingDocPicker() {
  if (!HRState.meetingModal) { closeMeetingDocPicker(); return; }
  const checked = Array.from(document.querySelectorAll('#meeting-doc-pick-list input[type="checkbox"]:checked')).map((el) => el.value);
  if (checked.length > SAFETY_MEETING_MAX_DOCS) {
    window.showToast?.(`Attach up to ${SAFETY_MEETING_MAX_DOCS} documents to one sheet`, 'error');
    return;
  }
  const byId = new Map(_attachableCompanyDocs().map((r) => [r.id, r]));
  // Keep the existing order for docs that stay; append new picks.
  const keep = HRState.meetingModal.docs.filter((d) => checked.includes(d.id));
  const added = checked.filter((id) => !keep.some((d) => d.id === id) && byId.has(id)).map((id) => _docSnapFromResource(byId.get(id)));
  HRState.meetingModal.docs = keep.concat(added);
  closeMeetingDocPicker();
  _renderMeetingDocsList();
}

async function uploadMeetingDoc(input) {
  const file = input?.files?.[0];
  if (input) input.value = '';
  if (!file || !HRState.meetingModal || HRState.meetingModal.docsLocked) return;
  if (HRState.meetingModal.docs.length >= SAFETY_MEETING_MAX_DOCS) {
    window.showToast?.(`Attach up to ${SAFETY_MEETING_MAX_DOCS} documents to one sheet`, 'error');
    return;
  }
  const date = HRState.meetingModal.date;
  // Same pipeline as HR → Company Documents → Upload (25MB cap, MIME-checked by
  // storage.rules), filed under "Safety Meetings" so it is findable later.
  const newId = await _uploadCompanyDocumentFile(file, 'Safety Meetings');
  if (typeof newId !== 'string') return; // the uploader already toasted the failure
  if (!HRState.meetingModal || HRState.meetingModal.date !== date) return; // modal moved on
  HRState.meetingModal.docs.push({
    id: newId,
    title: file.name.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' '),
    fileName: file.name,
    mimeType: file.type || '',
  });
  _renderMeetingDocsList();
}

// Save the sheet's document list (after saveMeetingDetails wrote the details).
// Returns true when nothing needed saving or the save landed.
// `pending` is captured by saveMeetingDetails BEFORE its first await (COD Code
// Reviewer): closing the modal mid-save clears HRState.meetingModal, and that
// must never read as "nothing to attach".
function _captureMeetingDocs(date) {
  const mm = HRState.meetingModal;
  if (!mm || mm.date !== date || mm.docsLocked) return null;
  const ids = mm.docs.map((d) => d.id);
  if (JSON.stringify(ids) === JSON.stringify(mm.originalDocIds)) return null;
  return { date, ids, tenantId: HRState.tenantId };
}
async function _syncMeetingDocs(pending) {
  if (!pending) return true;
  const fn = httpsCallable(functions, 'setSafetyMeetingDocuments');
  await fn({ tenantId: pending.tenantId, date: pending.date, documentIds: pending.ids });
  const mm = HRState.meetingModal;
  if (mm && mm.date === pending.date) mm.originalDocIds = pending.ids;
  return true;
}

// ---- Manage templates (rename / delete) ----
async function openSafetyTemplateManager() {
  const html = `
    <div class="modal" id="modal-safety-templates" style="z-index:10010;">
      <div class="modal-content modal-fullscreen" style="max-width:480px;display:flex;flex-direction:column;max-height:85vh;max-height:85dvh;">
        <div class="modal-header">
          <h2><span class="material-icons">bookmarks</span> Safety meeting templates</h2>
          <button class="modal-close" onclick="window.HRModule.closeSafetyTemplateManager()"><span class="material-icons">close</span></button>
        </div>
        <div class="modal-body" style="flex:1;overflow-y:auto;" id="safety-templates-body">
          <div style="padding:16px;text-align:center;color:var(--text-secondary);">Loading…</div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.HRModule.closeSafetyTemplateManager()">Done</button>
        </div>
      </div>
    </div>`;
  document.getElementById('modal-safety-templates')?.remove();
  document.body.insertAdjacentHTML('beforeend', html);
  document.getElementById('modal-safety-templates').classList.remove('hidden');
  try {
    await _loadSafetyTemplates(true);
  } catch (err) {
    const body = document.getElementById('safety-templates-body');
    if (body) body.innerHTML = `<div style="padding:16px;text-align:center;color:var(--text-primary);">${escapeHtml(_safetyCallableMessage(err, 'Could not load templates.'))}</div>`;
    return;
  }
  _renderSafetyTemplateManager();
  _renderMeetingTemplateSelect();
}

function closeSafetyTemplateManager() {
  document.getElementById('modal-safety-templates')?.remove();
}

const _SAFETY_TYPE_LABELS = { daily: 'Daily Tailgate', weekly: 'Weekly Safety', toolbox: 'Toolbox Talk', incident: 'Incident Review', training: 'Training' };

function _renderSafetyTemplateManager(editingId) {
  const body = document.getElementById('safety-templates-body');
  if (!body) return;
  const list = HRState.safetyTemplates || [];
  if (!list.length) {
    body.innerHTML = '<div style="padding:16px;text-align:center;color:var(--text-secondary);font-size:0.875rem;">No templates yet. Fill in a sign-in sheet, then use "Save as template".</div>';
    return;
  }
  body.innerHTML = list.map((t) => {
    const types = Object.keys(_SAFETY_TYPE_LABELS).filter((k) => t.meetingDetails?.types?.[k]).map((k) => _SAFETY_TYPE_LABELS[k]).join(', ');
    const nDocs = Array.isArray(t.documents) ? t.documents.length : 0;
    const meta = [types, nDocs ? `${nDocs} document${nDocs === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · ');
    if (editingId === t.id) {
      return `
        <div style="display:flex;gap:8px;align-items:center;padding:8px 0;border-bottom:1px solid var(--border-light);">
          <input type="text" id="safety-template-rename-input" class="form-control" maxlength="80" value="${escapeHtml(t.name)}" style="flex:1;">
          <button class="btn btn-primary btn-sm" onclick="window.HRModule.renameSafetyTemplate('${escJsAttr(t.id)}')">Save</button>
          <button class="btn btn-secondary btn-sm" onclick="window.HRModule.startRenameSafetyTemplate(null)">Cancel</button>
        </div>`;
    }
    return `
      <div style="display:flex;gap:8px;align-items:center;padding:8px 0;border-bottom:1px solid var(--border-light);">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(t.name)}</div>
          ${meta ? `<div style="font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(meta)}</div>` : ''}
        </div>
        <button class="btn btn-ghost btn-sm" title="Rename" aria-label="Rename ${escapeHtml(t.name)}" onclick="window.HRModule.startRenameSafetyTemplate('${escJsAttr(t.id)}')"><span class="material-icons" style="font-size:18px;">edit</span></button>
        <button class="btn btn-ghost btn-sm" title="Delete" aria-label="Delete ${escapeHtml(t.name)}" onclick="window.HRModule.deleteSafetyTemplate('${escJsAttr(t.id)}')" style="color:#dc2626;"><span class="material-icons" style="font-size:18px;">delete</span></button>
      </div>`;
  }).join('');
  if (editingId) document.getElementById('safety-template-rename-input')?.focus();
}

function startRenameSafetyTemplate(id) {
  _renderSafetyTemplateManager(id || undefined);
}

async function renameSafetyTemplate(id) {
  const name = (document.getElementById('safety-template-rename-input')?.value || '').replace(/\s+/g, ' ').trim();
  if (!name || name.length > 80) { window.showToast?.('Template names are 1–80 characters', 'error'); return; }
  try {
    await _callSafetyTemplates({ action: 'rename', templateId: id, name });
    const t = (HRState.safetyTemplates || []).find((x) => x.id === id);
    if (t) t.name = name;
    (HRState.safetyTemplates || []).sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
    _renderSafetyTemplateManager();
    _renderMeetingTemplateSelect();
    window.showToast?.('Template renamed', 'success');
  } catch (err) {
    console.error('[HR] rename safety template failed', err);
    window.showToast?.(_safetyCallableMessage(err, 'Could not rename the template'), 'error');
  }
}

async function deleteSafetyTemplate(id) {
  const t = (HRState.safetyTemplates || []).find((x) => x.id === id);
  if (!t) return;
  const go = await window.showConfirm('Delete template?', `"${t.name}" will be removed. Sheets already started from it keep their details and documents.`, { confirmText: 'Delete', cancelText: 'Cancel' });
  if (!go) return;
  try {
    await _callSafetyTemplates({ action: 'delete', templateId: id });
    HRState.safetyTemplates = (HRState.safetyTemplates || []).filter((x) => x.id !== id);
    _renderSafetyTemplateManager();
    _renderMeetingTemplateSelect();
    window.showToast?.('Template deleted', 'success');
  } catch (err) {
    console.error('[HR] delete safety template failed', err);
    window.showToast?.(_safetyCallableMessage(err, 'Could not delete the template'), 'error');
  }
}

async function saveMeetingDetails(date) {
  // TASK-1117 — belt behind the opener gate above; the safetySignIns update
  // rule now also denies complianceOnly (this avoids a raw error toast).
  if (window.AppState?.tenantData?.complianceOnly) {
    window.showComplianceUpgradeOverlay?.('hr-safety');
    return;
  }
  const meetingDetails = _readMeetingDetailsForm(); // TASK-4882 — one reader, shared with the hint
  // TASK-4882 — saving an INCOMPLETE outline is allowed (time/location ahead
  // of the day is normal prep); the gate is on the crew's sign-in, not on
  // this save. But the office must hear it in the same breath as "saved".
  const outlineMissing = safetyOutlineMissingClient(meetingDetails);
  const pendingDocs = _captureMeetingDocs(date); // TASK-9742 — before any await

  try {
    const docRef = doc(db, 'tenants', HRState.tenantId, 'safetySignIns', date);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // Update existing document
      await updateDoc(docRef, {
        meetingDetails,
        updatedAt: new Date()
      });
    } else {
      // TASK-9385 (COD Domain) — no backdated meeting records. A new sheet can
      // only be opened for today or a future date (firestore.rules enforces the
      // same, with ~1 day of timezone slack); say so plainly instead of letting
      // the rules refusal land on the generic error toast.
      if (date < getTodayDateString()) {
        window.showToast?.('Nobody signed in on that day, so there is no sheet to add details to. Pick today or a future date to prepare a sheet.', 'error');
        return;
      }
      // Create new document with meeting details. createdAt/updatedAt are
      // SERVER time — the rules require request.time, never a client clock.
      await setDoc(docRef, {
        date,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        signatures: [],
        meetingDetails,
        pdfUrl: null,
        pdfGeneratedAt: null
      });
    }

    // TASK-9742 — the attached company documents go through the server
    // (meetingDocuments is outside the client rules allowlist). The details
    // above are already saved; if this half fails, say exactly that and keep
    // the modal open so Save retries only what is left.
    try {
      await _syncMeetingDocs(pendingDocs);
    } catch (docErr) {
      console.error('[HR] Error saving meeting documents:', docErr);
      window.showToast?.(`Details saved, but the documents didn't attach: ${_safetyCallableMessage(docErr, 'please press Save again.')}`, 'error');
      return;
    }

    if (outlineMissing.length) {
      window.showToast?.(`Saved — but crew can't sign in on this date until you add: ${outlineMissing.join(' + ')}.`, 'warning');
    } else {
      window.showToast?.('Meeting details saved — crew can sign in.', 'success');
    }
    closeMeetingDetailsModal();

    // TASK-9385 — the post-save `{ pdfUrl: null, pdfGeneratedAt: null }` clear
    // that used to sit here is GONE. It predates the TASK-344 trigger, which
    // regenerates the PDF on any meetingDetails change by itself; the clear
    // raced that trigger (clear lands AFTER the fresh pdfUrl writeback → sheet
    // stuck on "PDF generating…" with no PDF). The old link stays clickable
    // for the few seconds until the regenerated one replaces it. The rules arm
    // still permits the clear write for any older client build.

  } catch (error) {
    console.error('[HR] Error saving meeting details:', error);
    window.showToast?.('Error saving meeting details', 'error');
  }
}

// ============================================
// SAFETY SUB-VIEW (Equipment + Meetings)
// ============================================

function openSafetySubView() {
  if (_hrGateComplianceOnly('safety')) return; // 2026-05-13 compliance-only gate
  HRState.currentView = 'safety';
  if (!HRState.safetyEquipmentSubscribed) {
    subscribeToSafetyEquipment(HRState.tenantId);
  }
  subscribeToJSAReports(HRState.tenantId); // TASK-9581 — self-guards per tenant
  if (!HRState.incidentsSubscribed) {
    subscribeToIncidents(HRState.tenantId); // TASK-1140
  }
  const container = document.getElementById('hr-content') || document.querySelector('.hr-view');
  if (!container) return;
  container.innerHTML = getSafetySubViewHTML(false);
  window._stRenderList?.(); // TASK-787 — populate inline Required Trainings editor
  initSignInDateDefaults();
  renderSafetySignInList();
  renderSafetyEquipmentView();
  renderJSAReportsList();
  renderIncidentsList(); // TASK-1140
  renderSafetyRatesPanel(); // TASK-9570
  renderSafetyRecordsList(); // TASK-772
  if (!HRState.fireExtSubscribed) {
    subscribeToFireExtInspections(HRState.tenantId);
  }
  renderFireExtInspectionsList();
}

// Standalone safety init for Compliance module (renders into any container)
export function initSafetyStandalone(container, tenantId) {
  if (!container || !tenantId) return;
  // Ensure HRState has tenant and people data for safety functions
  if (!HRState.tenantId) HRState.tenantId = tenantId;
  HRState.currentView = 'safety';

  // Start safetySignIns subscription if HR hasn't been visited
  if (!HRState.safetySignInsSubscribed) {
    HRState.safetySignInsSubscribed = true;
    const safetyQuery = query(
      collection(db, 'tenants', tenantId, 'safetySignIns'),
      orderBy('date', 'desc')
    );
    const unsubSafety = onSnapshot(safetyQuery, (snapshot) => {
      HRState.safetySignIns = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      HRState.safetySignInsLoaded = true; // TASK-9149
      renderSafetySignInList();
      initSignInDateDefaults();
    }, () => {
      HRState.safetySignIns = [];
      HRState.safetySignInsLoaded = false; // TASK-9149 (COD r2)
      renderSafetySignInList();
      initSignInDateDefaults();
    });
    HRState.unsubscribers.push(unsubSafety);
  }
  if (!HRState.safetyEquipmentSubscribed) {
    subscribeToSafetyEquipment(tenantId);
  }
  subscribeToJSAReports(tenantId); // TASK-9581 — self-guards per tenant
  if (!HRState.incidentsSubscribed) {
    subscribeToIncidents(tenantId); // TASK-1140
  }
  if (!HRState.fireExtSubscribed) {
    subscribeToFireExtInspections(tenantId);
  }
  // Subscribe to people if not already (needed for equipment assignment)
  if (!HRState.people || HRState.people.length === 0) {
    const unsubPeople = onSnapshot(
      query(collection(db, 'tenants', tenantId, 'people'), orderBy('name')),
      (snap) => {
        HRState.people = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        HRState.peopleLoaded = true;
        if (HRState.currentView === 'safety') { try { renderSafetyRatesPanel(); } catch (_) {} } // TASK-9570
      },
      (error) => {
        console.error('[HR Safety] Error loading people:', error);
      }
    );
    HRState.unsubscribers.push(unsubPeople);
  }

  // TASK-772 — the standalone (Compliance-embedded) safety surface doesn't run
  // initHR, so hrResources may not be subscribed yet. Subscribe defensively so
  // the Safety Records list populates + stays live here too. Track the tenant
  // this subscription belongs to so a tenant switch re-subscribes instead of
  // showing/writing another tenant's docs (Code Reviewer flag). Always trust the
  // passed tenantId for the Safety Records reads here, never a stale global.
  if (HRState.safetyRecordsResourcesTenant !== tenantId) {
    HRState.safetyRecordsResourcesTenant = tenantId;
    // Drop any stale cross-tenant docs before the first snapshot lands.
    if (!window._hrResources || HRState.tenantId !== tenantId) { window._hrResources = []; HR_RESOURCES = []; }
    const unsubSafetyDocs = onSnapshot(
      collection(db, 'tenants', tenantId, 'hrResources'),
      (snap) => {
        // Ignore a late snapshot from a tenant we've since navigated away from.
        if (HRState.safetyRecordsResourcesTenant !== tenantId) return;
        HR_RESOURCES = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        HR_RESOURCES.sort((a, b) => {
          const aT = a.createdAt?.toDate?.()?.getTime() || 0;
          const bT = b.createdAt?.toDate?.()?.getTime() || 0;
          return aT - bT;
        });
        window._hrResources = HR_RESOURCES;
        _hrResourcesLoaded.value = true; // TASK-9742 — the meeting doc picker keys off this
        try { renderSafetyRecordsList(); } catch (_) {}
      },
      (err) => { console.error('[HR Safety] hrResources listener error:', err?.code, err?.message); }
    );
    HRState.unsubscribers.push(unsubSafetyDocs);
  }

  container.innerHTML = getSafetySubViewHTML(true);
  window._stRenderList?.(); // TASK-787 — populate inline Required Trainings editor
  initSignInDateDefaults();
  renderSafetySignInList();
  renderSafetyEquipmentView();
  renderJSAReportsList();
  renderIncidentsList(); // TASK-1140
  renderSafetyRatesPanel(); // TASK-9570
  renderSafetyRecordsList(); // TASK-772
  renderFireExtInspectionsList();
}

function getSafetySubViewHTML(standalone) {
  return `
    <div class="hiring-subview">
      ${standalone ? '' : `<button class="hiring-back-btn" onclick="window.HRModule.backToHRLanding()">
        <span class="material-icons">arrow_back</span> Back to HR
      </button>`}
      <div class="view-header" style="margin-bottom:0;">
        <div class="view-title"><h1>Safety</h1></div>
      </div>

      <!-- Section D (moved to TOP per TASK-723): Safety Roster (required trainings) + ISNetworld Compliance -->
      <div class="hr-section" style="margin-top: var(--spacing-md);">
        <h2 class="hr-section-title">
          <span class="material-icons">verified_user</span>
          Safety Roster
        </h2>
        <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-md);margin-bottom:var(--spacing-md);">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:var(--spacing-sm);">
            <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">info</span>
            <span style="font-size:0.85rem;color:var(--text-primary);">Set your certifications (required trainings), then open the matrix to see who has each one (with expiry). Click any "Missing" or expired cell to upload that cert. Record each employee's certs via HR → Employee → Compliance → Training &amp; Certifications.</span>
          </div>
          ${(() => {
            // TASK-9911 M2 — ONE door. The required-trainings editor (TASK-787
            // inline here) now lives in the shared "Skills & certifications"
            // screen (roster.js showSkillsAndCerts), which Roster → Skills opens
            // too. Same app.js editor, same ids, same updateSafetyTrainings
            // callable and TASK-667 partner lock — it just renders once, there.
            // This card keeps the count + the partner note + the matrix.
            const canEdit = _canCarrierEditTrainings();
            const td = window.AppState?.tenantData || {};
            const partnerName = (typeof td.compliancePartner === 'string' && td.compliancePartner.trim())
              ? td.compliancePartner.trim() : 'your compliance partner';
            const note = canEdit ? '' : `
              <div style="font-size:0.78rem;color:var(--text-secondary);margin-bottom:var(--spacing-sm);display:flex;align-items:center;gap:6px;">
                <span class="material-icons" style="font-size:16px;color:#10B981;">lock</span>
                Managed by <strong style="color:var(--text-primary);">${escapeHtml(partnerName)}</strong> — they control your required trainings.
              </div>`;
            return `${note}
              <div style="display:flex;align-items:center;gap:8px;margin-bottom:var(--spacing-sm);">
                <span style="font-weight:600;font-size:0.9rem;color:var(--text-primary);">Required trainings</span>
                <span id="safety-cert-door-count" style="font-size:0.78rem;color:var(--text-secondary);font-weight:400;">${_safetyCertDoorCountText()}</span>
              </div>
              <p style="font-size:0.78rem;color:var(--text-secondary);margin:0 0 var(--spacing-sm);line-height:1.5;">
                Your certifications (OSHA 10/30, H2S, Fit Test, First Aid/CPR…) and your crew skills (no card) are kept on one screen. Each employee's expiration is read from the issue/expiry date on their uploaded cert.
              </p>
              <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:var(--spacing-md);">
                <button type="button" class="btn btn-secondary" id="btn-hr-skills-certs" onclick="window.HRModule.openSkillsAndCerts()">
                  <span class="material-icons">workspace_premium</span> Skills &amp; certifications
                </button>
                <button class="btn btn-primary" onclick="window.HRModule.printSafetyRosterMatrix()">
                  <span class="material-icons">grid_on</span> Safety Roster Matrix
                </button>
              </div>`;
          })()}
        </div>

        <!-- ISNetworld Compliance (existing roster export — name unchanged) -->
        <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-md);margin-bottom:var(--spacing-md);">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:var(--spacing-sm);">
            <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">info</span>
            <span style="font-size:0.85rem;color:var(--text-primary);">Generate a printable roster of all active employees with ISNet profile data and training records. Fill in employee data via HR → Employee → Edit → ISNet/Compliance section.</span>
          </div>
          <button class="btn btn-primary" onclick="window.HRModule.printISNetRoster()">
            <span class="material-icons">print</span> Print ISNet Roster
          </button>
        </div>
      </div>

      <!-- TASK-772 — Company-level Safety Records (OSHA 300 / 300A + written
           safety programs). These are COMPANY/establishment records, not
           per-person. Stored in the existing HR Company Documents repo
           (hrResources, category "Safety Records") so Smart Search + sharing
           work; surfaced here with OSHA-specific framing + 5-year retention
           note. Reuses the same upload/storage pipeline as Company Documents. -->
      <div class="hr-section" style="margin-top: var(--spacing-md);">
        <h2 class="hr-section-title">
          <span class="material-icons">folder_special</span>
          Safety Records (OSHA 300 / 300A)
        </h2>
        <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-md);margin-bottom:var(--spacing-md);">
          <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:var(--spacing-sm);">
            <span class="material-icons" style="font-size:20px;color:var(--brand-blue);flex-shrink:0;">info</span>
            <span style="font-size:0.85rem;color:var(--text-primary);line-height:1.5;">Store your company-level injury &amp; illness records and written safety programs here: the <strong>OSHA 300 Log</strong>, the <strong>OSHA 300A Annual Summary</strong> (post Feb&nbsp;1&ndash;Apr&nbsp;30), your written safety program / IIPP, SDS index, and training-matrix exports. OSHA requires you to keep the 300 and 300A on file for <strong>5 years</strong>. These are saved to your Company Documents (searchable via HR&nbsp;&rarr;&nbsp;Smart Search).</span>
          </div>
          <button class="btn btn-primary" onclick="window.HRModule.openSafetyRecordUploadModal()">
            <span class="material-icons">upload_file</span> Add Safety Record
          </button>
        </div>
        <div id="hr-safety-records-list" class="hr-list">
          <div class="empty-state-small">Loading safety records…</div>
        </div>
      </div>

      <!-- TASK-1140 Slice 1 — Incident & Accident Reports (capture). ONE record
           per incident; the §390.15 accident register + OSHA 300/301 are
           generated VIEWS over these in later slices (no double entry). Read is
           gated tighter than the rest of Safety in firestore.rules — injury
           narratives / medical-adjacent photos must never reach driver-PIN
           sessions. -->
      <div class="hr-section" style="margin-top: var(--spacing-md);">
        <h2 class="hr-section-title">
          <span class="material-icons">report</span>
          Incident &amp; Accident Reports
        </h2>
        <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-md);margin-bottom:var(--spacing-md);">
          <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:var(--spacing-sm);">
            <span class="material-icons" style="font-size:20px;color:var(--brand-blue);flex-shrink:0;">info</span>
            <span style="font-size:0.85rem;color:var(--text-primary);line-height:1.5;">Log vehicle accidents, injuries/illnesses, near-misses &amp; property damage in one place. Each record feeds your <strong>FMCSA §390.15 accident register</strong> and <strong>OSHA 300</strong> log — but only when <em>you</em> attest it meets those tests (two separate legal standards; you decide, not the form).</span>
          </div>
          <div style="display:flex;gap:var(--spacing-sm);flex-wrap:wrap;">
            <button class="btn btn-primary" onclick="window.HRModule.openIncidentModal()">
              <span class="material-icons">add</span> Report an Incident
            </button>
            <!-- TASK-1140 Slice 2 — the §390.15 register is a generated VIEW over
                 these same incident docs (dotAccidentRegister === true). No second
                 store, no denormalized register collection. -->
            <button class="btn btn-secondary" data-trailguide="accident-register-btn" onclick="window.HRModule.openAccidentRegister()">
              <span class="material-icons">table_view</span> §390.15 Accident Register
            </button>
          </div>
        </div>
        <div id="hr-incidents-list" class="hr-list">
          <div class="empty-state-small">Loading incidents…</div>
        </div>
      </div>

      <!-- TASK-9570 — OSHA injury rates (TRIR / DART) computed from the incidents
           above (human-attested oshaRecordable) and completed clock hours. Same
           read gate as the incidents list. -->
      <div class="hr-section" style="margin-top: var(--spacing-md);">
        <h2 class="hr-section-title">
          <span class="material-icons">query_stats</span>
          Injury Rates (TRIR / DART)
        </h2>
        <div id="hr-safety-rates" class="hr-list">
          <div class="empty-state-small">Loading injury rates…</div>
        </div>
      </div>

      <!-- Section A: Safety Meeting Sign-In -->
      <div class="hr-section" style="margin-top: var(--spacing-md);">
        <h2 class="hr-section-title">
          <span class="material-icons">groups</span>
          Safety Meeting Sign-In
          <span class="hr-pending-badge" id="hr-safety-today-count" style="display: none;">0</span>
        </h2>
        <!-- TASK-342 Slice D — Enable toggle + backfill date picker.
             Toggle controls settings.safetyMeetingSignInEnabled (the new
             server-side gate). When OFF, the in-app driver-portal navicon
             is hidden and the public forever URL stops accepting writes.
             Date picker lets admin retroactively open ANY day's sheet —
             handles the "orphan signatures without meeting details" case
             flagged by Chris (caught when forever URL collected sigs on
             days where no meeting wrapper existed). -->
        <!-- TASK-342 / 2026-05-21 — restructured. Enable toggle gets its own
             prominent row with the real .switch-slider rendering (was rendering
             as plain text before because .checkbox-switch requires a
             .switch-slider sibling). Actions row is its own line. Backfill
             picker on a third row with the label sized so the input doesn't
             collapse on mobile. -->
        <div style="display:flex;align-items:center;gap:12px;padding:10px 14px;background:#fff;border:1px solid var(--border-light);border-radius:8px;margin-bottom:var(--spacing-md);">
          <label class="checkbox-switch" style="flex:1;justify-content:flex-start;cursor:pointer;">
            <input type="checkbox" id="hr-safety-meeting-enabled-toggle"
              ${window.AppState?.settings?.safetyMeetingSignInEnabled === true ? 'checked' : ''}
              onchange="window.HRModule.toggleSafetyMeetingEnabled(this.checked)">
            <span class="switch-slider"></span>
            <span style="font-size:0.9rem;font-weight:600;color:var(--text-primary);">Enable in driver app</span>
          </label>
        </div>
        <!-- TASK-437 Slice 1 — PPE driver sign-off toggle. Mirrors the safety
             meeting toggle pattern (flips settings.safetyEquipmentDriverEnabled
             via setDoc merge + writes back to AppState.settings). When ON, the
             driver-PIN portal shows a 4th "PPE" tab where the employee can sign
             off they received gear (attestation) + self-log items they grabbed.
             Default OFF. -->
        <div style="display:flex;align-items:center;gap:12px;padding:10px 14px;background:#fff;border:1px solid var(--border-light);border-radius:8px;margin-bottom:var(--spacing-md);">
          <label class="checkbox-switch" style="flex:1;justify-content:flex-start;cursor:pointer;">
            <input type="checkbox" id="hr-safety-ppe-driver-toggle"
              ${window.AppState?.settings?.safetyEquipmentDriverEnabled === true ? 'checked' : ''}
              onchange="window.HRModule.toggleSafetyEquipmentDriverEnabled(this.checked)">
            <span class="switch-slider"></span>
            <span style="display:flex;flex-direction:column;">
              <span style="font-size:0.9rem;font-weight:600;color:var(--text-primary);">PPE sign-off in driver app</span>
              <span style="font-size:0.72rem;color:var(--text-secondary);font-weight:400;">Drivers sign off they received gear &amp; can self-log PPE</span>
            </span>
          </label>
        </div>
        <div class="hr-safety-actions" style="margin-bottom: var(--spacing-sm); display: flex; gap: var(--spacing-sm); flex-wrap: wrap; align-items: center;">
          <!-- TASK-9385 (Chris 2026-09-24): "allow user to make it for any date, so
               user can prepare a day ahead of time for safety sign in rosters."
               ONE control replaces the old today-only button + the dashed
               past-date-only picker box: pick ANY date (default today, no min/max)
               and open the details modal for it. openMeetingDetailsModal(dateStr)
               already took an explicit date; the future-date save was what was
               dead (rules refused the create -- see firestore.rules safetySignIns). -->
          <div style="display:inline-flex;align-items:center;gap:6px;flex-wrap:wrap;">
            <button class="btn btn-secondary btn-sm" onclick="window.HRModule.openMeetingDetailsModal(document.getElementById('hr-safety-sheet-date')?.value)"
              title="Add or edit the meeting details for the selected date — pick tomorrow to prepare the sheet ahead of time">
              <span class="material-icons">edit_note</span> Sign-In Sheet Details
            </button>
            <input type="date" id="hr-safety-sheet-date" class="form-control" value="${getTodayDateString()}"
              aria-label="Sign-in sheet date" title="Any date — today, a past meeting, or tomorrow's to prepare ahead"
              style="width:auto;padding:4px 8px;font-size:0.85rem;">
          </div>
          <button class="btn btn-secondary btn-sm" onclick="window.HRModule.generateSafetyPdf()" title="Build the PDF for the date picked beside Sign-In Sheet Details, from everyone signed in so far">
            <span class="material-icons">picture_as_pdf</span> Generate PDF
          </button>
          <!-- TASK-4879 — copySafetyLink existed and was exported to
               window.HRModule, but NOTHING called it, so no carrier could
               actually obtain their own sign-in link from the app. Wiring it is
               what makes the tenant-scoped link reachable; without it, opening
               this feature to a second carrier gives them a feature they have no
               way to hand to their crew. -->
          <button class="btn btn-secondary btn-sm" onclick="window.HRModule.copySafetyLink()">
            <span class="material-icons">link</span> Copy Sign-In Link
          </button>
        </div>
        <div style="display:flex;gap:var(--spacing-sm);flex-wrap:wrap;align-items:center;margin-bottom:var(--spacing-md);">
          <label style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;">Date range:</label>
          <input type="date" id="hr-signin-date-start" class="form-control" style="width:auto;padding:4px 8px;font-size:0.85rem;" onchange="window.HRModule.filterSignInsByRange()">
          <span style="color:var(--text-secondary);font-size:0.8rem;">to</span>
          <input type="date" id="hr-signin-date-end" class="form-control" style="width:auto;padding:4px 8px;font-size:0.85rem;" onchange="window.HRModule.filterSignInsByRange()">
          <button class="btn btn-sm btn-secondary" onclick="window.HRModule.clearSignInDateRange()">Clear</button>
        </div>
        <div id="hr-safety-signin-list" class="hr-list">
          <div class="empty-state-small">No sign-ins yet today</div>
        </div>
      </div>

      <!-- Section B: JSA Reports — TASK-9581: collapsed by default, last-day window, filters.
           Mounted by the lazy utils/jsaListWindow.js (renderJSAReportsList). -->
      <div id="hr-jsa-mount"></div>

      <!-- Section C: Safety Equipment -->
      <div class="hr-section" style="margin-top: var(--spacing-lg);">
        <h2 class="hr-section-title">
          <span class="material-icons">shield</span>
          Safety Equipment (PPE)
        </h2>

        <!-- Mode Tabs -->
        <div style="display: flex; gap: var(--spacing-xs); margin-bottom: var(--spacing-md);">
          <button id="safety-mode-user" class="btn btn-sm" style="background: var(--action-primary); color: #fff;" onclick="window.HRModule.switchSafetyMode('user')">
            <span class="material-icons" style="font-size: 16px;">add_circle</span> Log Equipment
          </button>
          <button id="safety-mode-manager" class="btn btn-sm btn-secondary" onclick="window.HRModule.switchSafetyMode('manager')">
            <span class="material-icons" style="font-size: 16px;">manage_accounts</span> Manage
          </button>
        </div>

        <div id="safety-equipment-content"></div>
      </div>

      <!-- Section E: Fire Extinguisher Inspections -->
      <div class="hr-section" style="margin-top: var(--spacing-lg);">
        <h2 class="hr-section-title">
          <span class="material-icons">local_fire_department</span>
          Fire Extinguisher Inspections
        </h2>
        <p style="font-size:0.8rem;color:var(--text-secondary);margin:0 0 var(--spacing-md);">
          Monthly shop extinguisher checks (OSHA 29 CFR 1910.157), plus eyewash, first-aid and spill-kit inspections, are recorded in Maintenance → Inspections.
        </p>
        <div id="hr-fire-ext-list" class="hr-list">
          <div class="empty-state-small">Loading inspections...</div>
        </div>
      </div>
    </div>
  `;
}

// ============================================================
// TASK-772 — Company-level Safety Records (OSHA 300 / 300A + safety programs)
// ============================================================
// These live in the existing HR Company Documents store (hrResources) under the
// fixed category "Safety Records" so the rest of HR (Smart Search, sharing,
// retention) sees them too — this is a FILTERED VIEW + an OSHA-framed uploader,
// NOT a separate collection or storage path.
const HR_SAFETY_RECORD_CATEGORY = 'Safety Records';
const HR_SAFETY_RECORD_TYPES = [
  'OSHA 300 Log',
  'OSHA 300A Annual Summary',
  'Written Safety Program / IIPP',
  'SDS Index',
  'Training Matrix Export',
  'Other Safety Record',
];

// Render the Safety Records list — the hrResources docs in the Safety Records
// category, grouped/sorted newest-year first. Mirrors the company-docs card
// look; reuses deleteHrResource for removal.
function renderSafetyRecordsList() {
  const container = document.getElementById('hr-safety-records-list');
  if (!container) return; // Safety sub-view not open
  const resources = window._hrResources || HR_RESOURCES || [];
  const records = resources.filter(r =>
    (r.category || '').trim().toLowerCase() === HR_SAFETY_RECORD_CATEGORY.toLowerCase());

  if (records.length === 0) {
    container.innerHTML = `<div class="empty-state-small">No safety records yet. Add your OSHA 300 / 300A or written safety program above. <span style="color:var(--text-secondary);">OSHA requires the 300 &amp; 300A to be kept on file for 5 years.</span></div>`;
    return;
  }

  // Sort: by safetyYear desc (records with a year first), then newest upload.
  const sorted = [...records].sort((a, b) => {
    const ay = Number.isInteger(a.safetyYear) ? a.safetyYear : -1;
    const by = Number.isInteger(b.safetyYear) ? b.safetyYear : -1;
    if (ay !== by) return by - ay;
    const aT = a.createdAt?.toDate?.()?.getTime() || 0;
    const bT = b.createdAt?.toDate?.()?.getTime() || 0;
    return bT - aT;
  });

  container.innerHTML = sorted.map(r => {
    const url = r.url || '';
    const title = escapeHtml(r.title || r.fileName || 'Document');
    const docType = r.safetyDocType ? escapeHtml(String(r.safetyDocType)) : '';
    const year = Number.isInteger(r.safetyYear) ? String(r.safetyYear) : '';
    const sizeStr = r.sizeBytes ? `${(r.sizeBytes / 1024 / 1024).toFixed(1)} MB` : '';
    const isPdf = r.isPdf || (r.mimeType || '').includes('pdf');
    const icon = isPdf ? 'picture_as_pdf' : (r.icon || 'description');
    const metaBits = [docType, year, sizeStr].filter(Boolean).map(escapeHtml).join(' · ');
    return `
      <div class="hr-list-item" style="display:flex;align-items:center;gap:12px;padding:10px 12px;border:1px solid var(--border-light);border-radius:8px;margin-bottom:8px;background:#fff;">
        <span class="material-icons" style="font-size:24px;color:${isPdf ? '#DC2626' : 'var(--brand-blue)'};flex-shrink:0;">${icon}</span>
        <div style="flex:1;min-width:0;">
          <div style="font-size:0.9rem;font-weight:600;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${title}</div>
          ${metaBits ? `<div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">${metaBits}</div>` : ''}
        </div>
        <div style="display:flex;gap:4px;flex-shrink:0;">
          ${url ? `<a class="btn-icon" href="${_safeHttpUrl(url)}" target="_blank" rel="noopener" title="Open / download"><span class="material-icons">open_in_new</span></a>` : ''}
          <button class="btn-icon" style="color:var(--status-danger);" title="Delete safety record"
            onclick="window.HRModule.deleteHrResource('${escJsAttr(r.id)}','${escJsAttr(r.storagePath || '')}')">
            <span class="material-icons">delete</span>
          </button>
        </div>
      </div>`;
  }).join('');
}

// Upload modal for a company-level safety record. Captures the doc type (300 /
// 300A / safety program / …) + the record year, then runs the SAME company-doc
// upload pipeline (_uploadCompanyDocumentFile) with the fixed Safety Records
// category + the safety metadata. No new storage path, no new collection.
function openSafetyRecordUploadModal() {
  const thisYear = new Date().getFullYear();
  const typeOptions = HR_SAFETY_RECORD_TYPES
    .map(t => `<option value="${escapeHtml(t)}">${escapeHtml(t)}</option>`).join('');
  const yearOptions = ['<option value="">— No year —</option>']
    .concat(Array.from({ length: 12 }, (_, i) => thisYear - i)
      .map(y => `<option value="${y}" ${y === thisYear ? 'selected' : ''}>${y}</option>`)).join('');
  let modal = document.getElementById('hr-safety-record-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'hr-safety-record-modal';
    modal.className = 'modal hidden';
    modal.style.zIndex = '1060';
    modal.onclick = (e) => { if (e.target === modal) modal.classList.add('hidden'); };
    document.body.appendChild(modal);
  }
  modal.innerHTML = `
    <div class="modal-content modal-small" style="max-width:460px;padding:0;">
      <div class="crm-detail-header" style="padding:16px 20px;">
        <div class="crm-detail-header-info">
          <div class="crm-detail-header-icon" style="width:36px;height:36px;">
            <span class="material-icons" style="font-size:20px;">folder_special</span>
          </div>
          <h3 style="margin:0;font-size:1rem;">Add Safety Record</h3>
        </div>
        <button class="modal-close" style="color:#fff;" onclick="document.getElementById('hr-safety-record-modal').classList.add('hidden')">&times;</button>
      </div>
      <div style="padding:20px;display:flex;flex-direction:column;gap:14px;">
        <div class="form-group" style="margin:0;">
          <label for="hr-safety-record-type" style="font-weight:600;">Record type</label>
          <select id="hr-safety-record-type" class="form-control">${typeOptions}</select>
        </div>
        <div class="form-group" style="margin:0;">
          <label for="hr-safety-record-year" style="font-weight:600;">Year</label>
          <select id="hr-safety-record-year" class="form-control">${yearOptions}</select>
          <p style="font-size:0.74rem;color:var(--text-secondary);margin-top:5px;line-height:1.4;">For OSHA 300 / 300A, pick the calendar year the log covers. Keep both on file for 5 years.</p>
        </div>
        <div class="form-group" style="margin:0;">
          <label for="hr-safety-record-file" style="font-weight:600;">File</label>
          <input type="file" id="hr-safety-record-file" class="form-control" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv" style="padding:6px;">
          <p style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">PDF, Word, Excel, image, or CSV. 25 MB max.</p>
        </div>
      </div>
      <div class="modal-footer" style="display:flex;gap:8px;justify-content:flex-end;padding:16px 20px;">
        <button class="btn btn-secondary" onclick="document.getElementById('hr-safety-record-modal').classList.add('hidden')">Cancel</button>
        <button class="btn btn-primary" id="hr-safety-record-submit" onclick="window.HRModule._submitSafetyRecordUpload()">
          <span class="material-icons" style="font-size:18px;vertical-align:middle;">upload</span> Upload
        </button>
      </div>
    </div>`;
  modal.classList.remove('hidden');
}

// Pull the chosen type/year/file from the modal, run the company-doc upload with
// the Safety Records category + safety metadata, then refresh the list.
async function _submitSafetyRecordUpload() {
  const fileEl = document.getElementById('hr-safety-record-file');
  const file = fileEl?.files?.[0];
  if (!file) { window.showToast?.('Pick a file first', 'error'); return; }
  const docType = document.getElementById('hr-safety-record-type')?.value || '';
  const yearRaw = document.getElementById('hr-safety-record-year')?.value || '';
  const submitBtn = document.getElementById('hr-safety-record-submit');
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;animation:spin 0.8s linear infinite;">progress_activity</span> Uploading…';
  }
  const meta = {};
  if (docType) meta.safetyDocType = docType;
  if (yearRaw) meta.safetyYear = yearRaw; // sanitized in _safeSafetyDocMeta
  const ok = await _uploadCompanyDocumentFile(file, HR_SAFETY_RECORD_CATEGORY, meta);
  if (ok) {
    document.getElementById('hr-safety-record-modal')?.classList.add('hidden');
    renderSafetyRecordsList();
  } else if (submitBtn) {
    submitBtn.disabled = false;
    submitBtn.innerHTML = '<span class="material-icons" style="font-size:18px;vertical-align:middle;">upload</span> Upload';
  }
}

// =========================================================================
// TASK-1140 Slice 1 — Incident & Accident Reports (CAPTURE)
// =========================================================================
// One incident record is the single source of truth; later slices generate the
// §390.15 accident register + OSHA 300/301 as generated VIEWS over these docs
// (never a separate store). Slice 1 = collection + rules + capture form + list +
// photos. Signatures, register/OSHA export, and partner visibility are later
// slices — `signatures`, `linkedDvirIds`, `linkedWorkOrderIds`, and `status` are
// reserved here so the data model doesn't paint those slices into a corner.
//
// SECURITY: firestore.rules `incidents` gates read to admin / dispatcher /
// hr-module / compliance-module (NOT canReadTenant — a driver-PIN session must
// never read coworkers' injury narratives). Photos persist PATH-ONLY (no bearer
// download URL — TASK-1029) under the existing storage rule
// hr/{tid}/{employeeId}/{fileName} using a single-token bucket `incident_{id}`,
// so no storage.rules change is needed. Render resolves the URL from the path,
// scoped to hr/{tid}/, https-only, image (non-SVG) only.
const HR_INCIDENT_TYPES = [
  { value: 'vehicle-accident', label: 'Vehicle Accident', icon: 'car_crash' },
  { value: 'injury', label: 'Injury / Illness', icon: 'personal_injury' },
  { value: 'near-miss', label: 'Near Miss', icon: 'warning' },
  { value: 'property-damage', label: 'Property Damage', icon: 'broken_image' },
];
const HR_INCIDENT_MAX_PHOTOS = 10;

function _incidentTypeMeta(type) {
  return HR_INCIDENT_TYPES.find(t => t.value === type)
    || { value: type || '', label: type || 'Incident', icon: 'report' };
}

// TASK-1140 Slice 2 — COD-suggested nudge. Heuristic ONLY flags for human
// review: a vehicle-accident whose narrative mentions tow / fatality / injury
// language but is NOT attested for the §390.15 register probably deserves a
// second look. This NEVER sets dotAccidentRegister — recordability is a human
// attestation, hard rule from the original COD.
function _incidentLooksRegisterRelevant(r) {
  if (!r || r.dotAccidentRegister === true) return false;
  if (r.type !== 'vehicle-accident') return false;
  const text = String(r.narrative || '');
  return /\btow(?:ed|ing|-?away)?\b|\bfatal(?:ity|ities)?\b|\b(?:died|death)\b|\binjur(?:y|ies|ed)\b|\bambulance\b|\bhospital(?:ized)?\b|\bmedical (?:treatment|attention)\b/i.test(text);
}

// Resolve the best available accident date for an incident doc (same waterfall
// the list rows use): explicit dateTime Timestamp → the raw datetime-local
// string → createdAt. Returns a Date or null.
function _incidentDate(r) {
  const d = r.dateTime?.toDate?.() || (r.dateTimeLocal ? new Date(r.dateTimeLocal) : (r.createdAt?.toDate?.() || null));
  return (d && !isNaN(d)) ? d : null;
}

// The register PROJECTION — incidents where the human attested §390.15
// membership, newest first. This is the ONLY membership test; there is no
// register collection.
function _registerIncidents() {
  return (HRState.incidents || [])
    .filter(r => r.dotAccidentRegister === true)
    .sort((a, b) => (_incidentDate(b)?.getTime() || 0) - (_incidentDate(a)?.getTime() || 0));
}

function subscribeToIncidents(tenantId) {
  if (!tenantId || HRState.incidentsSubscribed) return;
  HRState.incidentsSubscribed = true;
  const q = query(collection(db, 'tenants', tenantId, 'incidents'), orderBy('createdAt', 'desc'));
  const unsub = onSnapshot(q, (snap) => {
    HRState.incidents = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    HRState.incidentsLoaded = true;      // TASK-9570 — the rate panel shows no number before this
    HRState.incidentsReadFailed = false;
    if (HRState.currentView === 'safety') { renderIncidentsList(); renderSafetyRatesPanel(); }
  }, (err) => {
    // Never log narrative/PII — code only.
    console.error('[HR] Incidents subscribe error:', err?.code || 'error');
    HRState.incidents = [];
    HRState.incidentsReadFailed = true;  // TASK-9570 — a refused read must never print "0 cases"
    if (HRState.currentView === 'safety') { renderIncidentsList(); renderSafetyRatesPanel(); }
  });
  HRState.unsubscribers.push(unsub);
}

function renderIncidentsList() {
  const container = document.getElementById('hr-incidents-list');
  if (!container) return;
  const records = HRState.incidents || [];
  if (records.length === 0) {
    container.innerHTML = `
      <div class="empty-state-small">
        <span class="material-icons" style="font-size:24px;margin-bottom:8px;">report</span>
        <div>No incidents reported yet</div>
        <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">Log vehicle accidents, injuries, near-misses &amp; property damage here.</div>
      </div>`;
    return;
  }
  const tagStyle = 'font-size:0.62rem;font-weight:700;padding:2px 6px;border-radius:10px;white-space:nowrap;';
  container.innerHTML = `
    <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:var(--spacing-sm);">${records.length} incident${records.length !== 1 ? 's' : ''}</div>
    ${records.map(r => {
      const meta = _incidentTypeMeta(r.type);
      const d = r.dateTime?.toDate?.() || (r.dateTimeLocal ? new Date(r.dateTimeLocal) : (r.createdAt?.toDate?.() || null));
      const dateStr = (d && !isNaN(d)) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
      const ppl = Array.isArray(r.people) ? r.people.length : 0;
      const veh = Array.isArray(r.vehicles) ? r.vehicles.length : 0;
      const photos = Array.isArray(r.photos) ? r.photos.length : 0;
      const tags = [];
      if (r.dotAccidentRegister) tags.push(`<span style="${tagStyle}background:#FEF3C7;color:#92400E;">§390.15</span>`);
      if (r.oshaRecordable) tags.push(`<span style="${tagStyle}background:#DBEAFE;color:#1E40AF;">OSHA</span>`);
      const sub = [dateStr,
        ppl ? `${ppl} ${ppl === 1 ? 'person' : 'people'}` : '',
        veh ? `${veh} veh` : '',
        photos ? `${photos} photo${photos !== 1 ? 's' : ''}` : ''].filter(Boolean).join(' · ');
      // TASK-1140 S2 nudge — subtle, non-blocking, informational only. Never
      // auto-sets the attestation (human decision, original-COD hard rule).
      const nudge = _incidentLooksRegisterRelevant(r)
        ? `<div style="font-size:0.7rem;color:#92400E;display:flex;align-items:center;gap:4px;margin-top:2px;">
             <span class="material-icons" style="font-size:13px;">lightbulb</span>
             Looks register-relevant — review the §390.15 attestation
           </div>`
        : '';
      return `
        <div style="display:flex;align-items:center;gap:var(--spacing-sm);padding:var(--spacing-sm);background:var(--bg-secondary);border-radius:var(--radius-sm);margin-bottom:4px;cursor:pointer;" onclick="window.HRModule.viewIncidentDetail('${r.id}')">
          <span class="material-icons" style="font-size:20px;color:var(--action-warning);">${meta.icon}</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;font-size:0.9rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(meta.label)}${r.location ? ' · ' + escapeHtml(r.location) : ''}</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(sub)}</div>
            ${nudge}
          </div>
          ${tags.join(' ')}
          <span class="material-icons" style="color:var(--text-muted);font-size:18px;">chevron_right</span>
        </div>`;
    }).join('')}`;
}

function openIncidentModal() {
  // Reset any previously-staged photos.
  (HRState.pendingIncidentPhotos || []).forEach(p => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl); });
  HRState.pendingIncidentPhotos = [];

  const people = (HRState.people || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const trucks = window.AppState?.trucks || [];
  const nowLocal = (() => {
    const n = new Date();
    n.setMinutes(n.getMinutes() - n.getTimezoneOffset());
    return n.toISOString().slice(0, 16);
  })();
  const typeOpts = HR_INCIDENT_TYPES.map(t => `<option value="${escapeHtml(t.value)}">${escapeHtml(t.label)}</option>`).join('');
  const peopleOpts = people.map(p => `<option value="${escapeHtml(p.id)}">${escapeHtml(p.name || p.id)}</option>`).join('');
  const vehicleOpts = trucks.map(t => {
    const unit = window.getUnitDisplay ? window.getUnitDisplay(t) : (t.unit || t.name || t.id);
    return `<option value="${escapeHtml(t.id)}">${escapeHtml(unit)}</option>`;
  }).join('');

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-hr-incident';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:600px;max-height:90vh;max-height:90dvh;overflow-y:auto;">
      <div class="modal-header" style="background:linear-gradient(135deg,#D97706,#B45309);color:#fff;">
        <h3 style="margin:0;display:flex;align-items:center;gap:8px;">
          <span class="material-icons">report</span> Report an Incident
        </h3>
        <button class="btn-icon" onclick="document.getElementById('modal-hr-incident')?.remove()" style="color:#fff;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px;">
          <div class="form-group" style="margin:0;">
            <label class="form-label">Type *</label>
            <select id="hr-incident-type" class="form-control">${typeOpts}</select>
          </div>
          <div class="form-group" style="margin:0;">
            <label class="form-label">Date &amp; Time *</label>
            <input type="datetime-local" id="hr-incident-datetime" class="form-control" value="${nowLocal}" required>
          </div>
        </div>

        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">Location</label>
          <input type="text" id="hr-incident-location" class="form-control" placeholder="Address, mile marker, or facility">
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px;">
          <div class="form-group" style="margin:0;">
            <label class="form-label">People involved</label>
            <select id="hr-incident-people" class="form-control" multiple size="4">${peopleOpts}</select>
            <div style="font-size:0.68rem;color:var(--text-muted);margin-top:2px;">Ctrl/Cmd-click for multiple</div>
          </div>
          <div class="form-group" style="margin:0;">
            <label class="form-label">Vehicles involved</label>
            <select id="hr-incident-vehicles" class="form-control" multiple size="4">${vehicleOpts}</select>
            <div style="font-size:0.68rem;color:var(--text-muted);margin-top:2px;">Ctrl/Cmd-click for multiple</div>
          </div>
        </div>

        <div class="form-group" style="margin-bottom:12px;">
          <label class="form-label">What happened</label>
          <textarea id="hr-incident-narrative" class="form-control" rows="4" placeholder="Describe the incident: sequence of events, conditions, injuries, damage."></textarea>
        </div>

        <!-- Recordability = HUMAN attestation. §390.15 and OSHA 300 are DIFFERENT
             legal tests; the person filing decides, the form never auto-classifies. -->
        <div style="margin-bottom:12px;padding:12px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;">
          <div style="font-weight:700;font-size:0.8rem;color:#92400E;margin-bottom:8px;display:flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:16px;">verified_user</span> Regulatory attestations
          </div>
          <label style="display:flex;gap:8px;align-items:flex-start;cursor:pointer;margin-bottom:10px;font-size:0.8rem;color:var(--text-primary);line-height:1.4;">
            <input type="checkbox" id="hr-incident-dot" style="margin-top:3px;flex-shrink:0;" onchange="var f=document.getElementById('hr-incident-register-fields');if(f)f.style.display=this.checked?'block':'none';">
            <span><strong>FMCSA §390.15 accident register.</strong> This qualifies as a DOT-recordable accident: a fatality, a vehicle towed from the scene due to disabling damage, OR bodily injury requiring immediate medical treatment away from the scene.</span>
          </label>
          <!-- §390.15(b)(1) register fields — the register VIEW (Slice 2) can only
               show what capture stores; these are the legally-required columns.
               Facts, not classification: the checkbox above stays the human
               attestation, these are the numbers/flags the register prints. -->
          <div id="hr-incident-register-fields" style="display:none;margin:0 0 10px 24px;padding:10px;background:#fff;border:1px solid #FDE68A;border-radius:6px;">
            <div style="display:grid;grid-template-columns:2fr 1fr;gap:10px;margin-bottom:10px;">
              <div class="form-group" style="margin:0;">
                <label class="form-label" style="font-size:0.72rem;">City</label>
                <input type="text" id="hr-incident-reg-city" class="form-control" placeholder="Williston">
              </div>
              <div class="form-group" style="margin:0;">
                <label class="form-label" style="font-size:0.72rem;">State</label>
                <input type="text" id="hr-incident-reg-state" class="form-control" maxlength="2" placeholder="ND" style="text-transform:uppercase;">
              </div>
            </div>
            <div class="form-group" style="margin:0 0 10px;">
              <label class="form-label" style="font-size:0.72rem;">Driver</label>
              <select id="hr-incident-reg-driver" class="form-control">
                <option value="">— Select driver —</option>${peopleOpts}
              </select>
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:10px;">
              <div class="form-group" style="margin:0;">
                <label class="form-label" style="font-size:0.72rem;">Fatalities</label>
                <input type="number" id="hr-incident-reg-fatalities" class="form-control" min="0" step="1" value="0" inputmode="numeric">
              </div>
              <div class="form-group" style="margin:0;">
                <label class="form-label" style="font-size:0.72rem;" title="§390.15(b)(1): count only injuries requiring immediate medical treatment away from the scene">Injuries (treated away from scene)</label>
                <input type="number" id="hr-incident-reg-injuries" class="form-control" min="0" step="1" value="0" inputmode="numeric">
              </div>
            </div>
            <label style="display:flex;gap:8px;align-items:center;cursor:pointer;margin-bottom:8px;font-size:0.78rem;color:var(--text-primary);min-height:24px;">
              <input type="checkbox" id="hr-incident-reg-tow" style="flex-shrink:0;">
              <span>Vehicle towed from the scene (tow-away)</span>
            </label>
            <label style="display:flex;gap:8px;align-items:center;cursor:pointer;font-size:0.78rem;color:var(--text-primary);min-height:24px;">
              <input type="checkbox" id="hr-incident-reg-hazmat" style="flex-shrink:0;">
              <span>Hazardous materials released</span>
            </label>
          </div>
          <label style="display:flex;gap:8px;align-items:flex-start;cursor:pointer;font-size:0.8rem;color:var(--text-primary);line-height:1.4;">
            <input type="checkbox" id="hr-incident-osha" style="margin-top:3px;flex-shrink:0;" onchange="var f=document.getElementById('hr-incident-osha-fields');if(f)f.style.display=this.checked?'block':'none';">
            <span><strong>OSHA 300 recordable.</strong> This is OSHA-recordable: days away from work, restricted duty / job transfer, loss of consciousness, OR medical treatment beyond first aid.</span>
          </label>
          <!-- TASK-9570 — the Form 300 outcome column (feeds DART) and how many
               employees were hurt (each is its own 300 line). Left blank = the
               injury-rate panel flags the incident instead of guessing. -->
          <div id="hr-incident-osha-fields" style="display:none;margin:8px 0 0 24px;padding:10px;background:#fff;border:1px solid #BFDBFE;border-radius:6px;">
            <div class="form-group" style="margin:0 0 10px;">
              <label class="form-label" for="hr-incident-osha-outcome" style="font-size:0.72rem;">Most serious outcome (OSHA 300 column)</label>
              <select id="hr-incident-osha-outcome" class="form-control">
                <option value="">— Not decided yet —</option>
                ${(window.SafetyRates?.OUTCOMES || []).map(o => `<option value="${o}">${escapeHtml(window.SafetyRates.OUTCOME_LABELS[o] || o)}</option>`).join('')}
              </select>
            </div>
            <div class="form-group" style="margin:0;">
              <label class="form-label" for="hr-incident-osha-cases" style="font-size:0.72rem;">Employees hurt (each is a separate OSHA 300 case)</label>
              <input type="number" id="hr-incident-osha-cases" class="form-control" min="1" max="100" step="1" placeholder="1" inputmode="numeric">
            </div>
          </div>
        </div>

        <!-- Photos — path-only storage (no bearer URL), reuses the HR attachment primitive. -->
        <div class="form-group" style="margin-bottom:16px;">
          <label class="form-label">Photos</label>
          <div id="hr-incident-photo-grid" style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:8px;"></div>
          <input type="file" id="hr-incident-photo-input" accept="image/*" multiple capture="environment" style="display:none;" onchange="window.HRModule.handleIncidentPhotoCapture(event)">
          <button type="button" class="btn btn-secondary btn-sm" onclick="document.getElementById('hr-incident-photo-input')?.click()">
            <span class="material-icons" style="font-size:16px;">add_a_photo</span> Add Photos
          </button>
          <div style="font-size:0.68rem;color:var(--text-muted);margin-top:4px;">Up to ${HR_INCIDENT_MAX_PHOTOS} images, 10 MB each.</div>
        </div>

        <div style="display:flex;gap:8px;">
          <button id="hr-incident-save-btn" class="btn btn-primary" style="flex:1;" onclick="window.HRModule.saveIncident()">
            <span class="material-icons" style="font-size:16px;">save</span> Save Incident
          </button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(modal);
  renderIncidentPhotoGrid();
}

function handleIncidentPhotoCapture(event) {
  const files = Array.from(event.target.files || []);
  event.target.value = '';
  for (const file of files) {
    if ((HRState.pendingIncidentPhotos || []).length >= HR_INCIDENT_MAX_PHOTOS) {
      window.showToast?.(`Maximum ${HR_INCIDENT_MAX_PHOTOS} photos`, 'error');
      break;
    }
    const ct = (file.type || inferContentType(file, '')).toLowerCase();
    if (!ct.startsWith('image/') || ct === 'image/svg+xml') {
      window.showToast?.('Images only (no SVG)', 'error');
      continue;
    }
    if (file.size > 10 * 1024 * 1024) {
      window.showToast?.('Each photo must be under 10 MB', 'error');
      continue;
    }
    const id = `p_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    HRState.pendingIncidentPhotos.push({ id, file, previewUrl: URL.createObjectURL(file) });
  }
  renderIncidentPhotoGrid();
}

function removeIncidentPhoto(id) {
  const idx = (HRState.pendingIncidentPhotos || []).findIndex(p => p.id === id);
  if (idx >= 0) {
    const p = HRState.pendingIncidentPhotos[idx];
    if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
    HRState.pendingIncidentPhotos.splice(idx, 1);
    renderIncidentPhotoGrid();
  }
}

function renderIncidentPhotoGrid() {
  const grid = document.getElementById('hr-incident-photo-grid');
  if (!grid) return;
  grid.innerHTML = (HRState.pendingIncidentPhotos || []).map(p => `
    <div style="position:relative;width:64px;height:64px;border-radius:6px;overflow:hidden;border:1px solid var(--border-light);">
      <img src="${p.previewUrl}" alt="" style="width:100%;height:100%;object-fit:cover;">
      <button type="button" onclick="window.HRModule.removeIncidentPhoto('${p.id}')" style="position:absolute;top:2px;right:2px;background:rgba(0,0,0,0.6);color:#fff;border:none;border-radius:50%;width:20px;height:20px;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;">
        <span class="material-icons" style="font-size:14px;">close</span>
      </button>
    </div>`).join('');
}

// Upload staged photos PATH-ONLY (no persisted download URL — TASK-1029) under
// the existing HR storage rule hr/{tid}/{employeeId}/{fileName}. The single-token
// bucket `incident_{id}` keeps the path 3-segment so no storage.rules change is
// needed. Best-effort: a failed photo doesn't fail the incident save.
async function _uploadIncidentPhotos(tenantId, incidentId) {
  const pending = HRState.pendingIncidentPhotos || [];
  const results = [];
  for (const p of pending) {
    try {
      const safeName = (p.file?.name || 'photo.jpg').replace(/[^a-zA-Z0-9._-]/g, '_');
      const ct = inferContentType(p.file, 'image/jpeg');
      const path = `hr/${tenantId}/incident_${incidentId}/${p.id}_${safeName}`;
      await uploadBytes(ref(storage, path), p.file, { contentType: ct });
      results.push({
        path,
        name: p.file?.name || safeName,
        contentType: p.file?.type || ct,
        size: p.file?.size || 0,
        uploadedAt: Timestamp.now(),
      });
    } catch (e) {
      console.warn('[HR] Incident photo upload failed:', e?.code || 'error');
    }
  }
  return results;
}

async function saveIncident() {
  const btn = document.getElementById('hr-incident-save-btn');
  const type = document.getElementById('hr-incident-type')?.value || '';
  const dtLocal = document.getElementById('hr-incident-datetime')?.value || '';
  if (!type) { window.showToast?.('Pick an incident type', 'error'); return; }
  if (!dtLocal) { window.showToast?.('Date & time is required', 'error'); return; }
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving…'; }

  const tenant = HRState.tenantId;
  const selVals = (id) => Array.from(document.getElementById(id)?.selectedOptions || []).map(o => o.value);
  const trucks = window.AppState?.trucks || [];
  const people = selVals('hr-incident-people').map(pid => {
    const p = (HRState.people || []).find(x => x.id === pid);
    return { id: pid, name: p?.name || '' };
  });
  const vehicles = selVals('hr-incident-vehicles').map(vid => {
    const t = trucks.find(x => x.id === vid);
    const unit = t ? (window.getUnitDisplay ? window.getUnitDisplay(t) : (t.unit || '')) : '';
    return { id: vid, unit };
  });

  let dtTs = null;
  const parsed = new Date(dtLocal);
  if (!isNaN(parsed)) dtTs = Timestamp.fromDate(parsed);

  // §390.15(b)(1) register fields — first-class so the Slice-2 register view
  // has every legally-required column. Persisted regardless of attestation
  // (they're facts); the attestation checkbox governs register MEMBERSHIP.
  const _regAttested = document.getElementById('hr-incident-dot')?.checked || false;
  const regDriverId = document.getElementById('hr-incident-reg-driver')?.value || '';
  const regDriver = regDriverId ? (HRState.people || []).find(x => x.id === regDriverId) : null;
  const intVal = (id) => {
    const n = parseInt(document.getElementById(id)?.value, 10);
    return (Number.isFinite(n) && n >= 0) ? n : 0;
  };

  const _oshaAttested = document.getElementById('hr-incident-osha')?.checked || false;
  const _oshaOutcomeRaw = document.getElementById('hr-incident-osha-outcome')?.value || '';
  const _oshaOutcome = (window.SafetyRates?.OUTCOMES || []).includes(_oshaOutcomeRaw) ? _oshaOutcomeRaw : '';
  const _oshaCasesStr = String(document.getElementById('hr-incident-osha-cases')?.value ?? '').trim();
  const _oshaCasesRaw = _oshaCasesStr === '' ? NaN : Number(_oshaCasesStr); // 2.9 is not a case count
  const _oshaCases = (Number.isInteger(_oshaCasesRaw) && _oshaCasesRaw >= 1 && _oshaCasesRaw <= 100) ? _oshaCasesRaw : null;

  const incidentData = {
    type,
    dateTime: dtTs,
    dateTimeLocal: dtLocal,
    location: document.getElementById('hr-incident-location')?.value?.trim() || '',
    people,
    vehicles,
    narrative: document.getElementById('hr-incident-narrative')?.value?.trim() || '',
    // Recordability = explicit HUMAN attestation; NEVER derived from form logic.
    dotAccidentRegister: _regAttested,
    oshaRecordable: _oshaAttested,
    // TASK-9570 — Form 300 outcome + case count, stored only with the OSHA
    // attestation (same rule as the register fields below). Blank = undecided;
    // the injury-rate panel flags it rather than guessing.
    oshaOutcome: _oshaAttested ? _oshaOutcome : '',
    oshaCaseCount: _oshaAttested ? _oshaCases : null,
    // §390.15(b)(1) register columns (first-class — see comment above).
    // 2nd-COD catch: persist them ONLY when the register attestation is checked.
    // Check-fill-then-UNCHECK previously still stored driver/facts — silent PII
    // retention on a record the human explicitly said is not register-recordable.
    ..._regAttested ? {
      locationCity: document.getElementById('hr-incident-reg-city')?.value?.trim() || '',
      locationState: (document.getElementById('hr-incident-reg-state')?.value?.trim() || '').toUpperCase(),
      driverId: regDriverId,
      driverName: regDriver?.name || '',
      fatalitiesCount: intVal('hr-incident-reg-fatalities'),
      injuriesCount: intVal('hr-incident-reg-injuries'),
      towAway: document.getElementById('hr-incident-reg-tow')?.checked || false,
      hazmatRelease: document.getElementById('hr-incident-reg-hazmat')?.checked || false,
    } : {
      locationCity: '', locationState: '', driverId: null, driverName: '',
      fatalitiesCount: 0, injuriesCount: 0, towAway: false, hazmatRelease: false,
    },
    photos: [],
    signatures: [],          // reserved — signature capture is a later slice
    linkedDvirIds: [],       // reserved (additive) — link DVIR/work-order later
    linkedWorkOrderIds: [],
    status: 'open',          // additive for later workflow
    createdAt: Timestamp.now(),
    createdBy: window.AppState?.user?.email || 'unknown',
    createdByUid: window.AppState?.user?.uid || '',
    updatedAt: Timestamp.now(),
    updatedBy: window.AppState?.user?.email || 'unknown',
  };

  try {
    const newRef = await addDoc(collection(db, 'tenants', tenant, 'incidents'), incidentData);
    if ((HRState.pendingIncidentPhotos || []).length) {
      const photos = await _uploadIncidentPhotos(tenant, newRef.id);
      if (photos.length) {
        await updateDoc(doc(db, 'tenants', tenant, 'incidents', newRef.id), { photos, updatedAt: Timestamp.now() });
      }
    }
    (HRState.pendingIncidentPhotos || []).forEach(p => { if (p.previewUrl) URL.revokeObjectURL(p.previewUrl); });
    HRState.pendingIncidentPhotos = [];
    document.getElementById('modal-hr-incident')?.remove();
    window.showToast?.('Incident reported', 'success');
    renderIncidentsList();
  } catch (e) {
    // Never log the narrative — code only.
    console.error('[HR] Save incident failed:', e?.code || 'error');
    window.showToast?.('Could not save incident', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save Incident'; }
  }
}

function viewIncidentDetail(id) {
  const r = (HRState.incidents || []).find(x => x.id === id);
  if (!r) return;
  const tid = HRState.tenantId || '';
  const meta = _incidentTypeMeta(r.type);
  const d = r.dateTime?.toDate?.() || (r.dateTimeLocal ? new Date(r.dateTimeLocal) : null);
  const dateStr = (d && !isNaN(d))
    ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '—';
  const peopleStr = (r.people || []).map(p => escapeHtml(p.name || p.id)).join(', ') || '—';
  const vehStr = (r.vehicles || []).map(v => escapeHtml(v.unit || v.id)).join(', ') || '—';
  const row = (label, val) => `
    <div style="display:flex;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);">
      <div style="width:120px;flex-shrink:0;font-size:0.78rem;color:var(--text-secondary);font-weight:600;">${label}</div>
      <div style="flex:1;font-size:0.85rem;color:var(--text-primary);">${val}</div>
    </div>`;
  const badge = (txt, bg, fg) => `<span style="font-size:0.68rem;font-weight:700;padding:2px 8px;border-radius:10px;background:${bg};color:${fg};">${txt}</span>`;
  const recTags = [
    r.dotAccidentRegister ? badge('§390.15 accident register', '#FEF3C7', '#92400E') : '',
    r.oshaRecordable ? badge('OSHA 300 recordable', '#DBEAFE', '#1E40AF') : '',
  ].filter(Boolean).join(' ') || '<span style="font-size:0.78rem;color:var(--text-muted);">Not attested as recordable</span>';
  // Photo placeholders — resolved async from PATH via getDownloadURL (never a
  // stored URL); path-scoped to hr/{tid}/, image (non-SVG) only.
  const photoHtml = (r.photos || []).map(ph => {
    if (!ph || !ph.path || !String(ph.path).startsWith('hr/' + tid + '/')) return '';
    const ct = (ph.contentType || '').toLowerCase();
    if (!ct.startsWith('image/') || ct === 'image/svg+xml') return '';
    const elId = 'inc-photo-' + Math.random().toString(36).slice(2, 10);
    return `<img id="${elId}" data-inc-path="${escapeHtml(ph.path)}" alt="${escapeHtml(ph.name || 'photo')}" style="width:84px;height:84px;object-fit:cover;border-radius:6px;border:1px solid var(--border-light);cursor:pointer;display:none;">`;
  }).join('');

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-hr-incident-detail';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;max-height:90vh;max-height:90dvh;overflow-y:auto;">
      <div class="modal-header" style="background:linear-gradient(135deg,#D97706,#B45309);color:#fff;">
        <h3 style="margin:0;display:flex;align-items:center;gap:8px;">
          <span class="material-icons">${meta.icon}</span> ${escapeHtml(meta.label)}
        </h3>
        <button class="btn-icon" onclick="document.getElementById('modal-hr-incident-detail')?.remove()" style="color:#fff;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <div style="margin-bottom:12px;">${recTags}</div>
        ${row('When', escapeHtml(dateStr))}
        ${row('Location', r.location ? escapeHtml(r.location) : '—')}
        ${row('People', peopleStr)}
        ${row('Vehicles', vehStr)}
        ${row('Status', escapeHtml(r.status || 'open'))}
        ${r.oshaRecordable ? `
        ${row('OSHA outcome', r.oshaOutcome && window.SafetyRates?.OUTCOME_LABELS?.[r.oshaOutcome] ? escapeHtml(window.SafetyRates.OUTCOME_LABELS[r.oshaOutcome]) : 'Not decided — set it under Injury Rates')}
        ${row('OSHA cases', Number.isInteger(r.oshaCaseCount) && r.oshaCaseCount >= 1 ? escapeHtml(String(r.oshaCaseCount)) : 'Not entered (counted as 1)')}` : ''}
        ${r.dotAccidentRegister ? `
        ${row('City / State', escapeHtml([r.locationCity, r.locationState].filter(Boolean).join(', ') || '—'))}
        ${row('Driver', r.driverName ? escapeHtml(r.driverName) : '—')}
        ${row('Fatalities', escapeHtml(String(r.fatalitiesCount ?? 0)))}
        ${row('Injuries', escapeHtml(String(r.injuriesCount ?? 0)))}
        ${row('Tow-away', r.towAway ? 'Yes' : 'No')}
        ${row('Hazmat release', r.hazmatRelease ? 'Yes' : 'No')}` : ''}
        <div style="margin-top:12px;">
          <div style="font-size:0.78rem;color:var(--text-secondary);font-weight:600;margin-bottom:4px;">What happened</div>
          <div style="font-size:0.85rem;color:var(--text-primary);white-space:pre-wrap;line-height:1.5;">${r.narrative ? escapeHtml(r.narrative) : '<span style="color:var(--text-muted);">No narrative recorded.</span>'}</div>
        </div>
        ${photoHtml ? `<div style="margin-top:12px;">
          <div style="font-size:0.78rem;color:var(--text-secondary);font-weight:600;margin-bottom:6px;">Photos</div>
          <div style="display:flex;flex-wrap:wrap;gap:8px;">${photoHtml}</div>
        </div>` : ''}
        <div style="margin-top:14px;font-size:0.7rem;color:var(--text-muted);">
          Filed by ${escapeHtml(r.createdBy || 'unknown')}${r.createdAt?.toDate ? ' on ' + r.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
        </div>
      </div>
    </div>`;
  document.body.appendChild(modal);

  // Resolve photo URLs from their storage paths (path-only persistence).
  document.querySelectorAll('#modal-hr-incident-detail img[data-inc-path]').forEach(el => {
    const path = el.getAttribute('data-inc-path');
    if (!path || !path.startsWith('hr/' + tid + '/')) return;
    getDownloadURL(ref(storage, path)).then(url => {
      if (typeof url !== 'string' || !/^https:\/\//i.test(url)) return;
      el.src = url;
      el.style.display = 'block';
      el.onclick = () => window.open(url, '_blank', 'noopener');
    }).catch(() => {});
  });
}

// ============================================
// TASK-9570 — INJURY RATES (TRIR / DART)
// ============================================
// The math lives ONCE in functions/src/shared/safetyRates.js (window.SafetyRates,
// generated mirror). This panel only gathers inputs and renders:
//   cases  = incidents the human attested `oshaRecordable` (TASK-1140), dated in
//            the period, counted by `oshaCaseCount` (default 1) and classified by
//            `oshaOutcome` (Form 300 column G/H/I/J);
//   hours  = COMPLETED clockEntries in the period (totalHours || hours — the same
//            sum the Time Off balances use) + hours worked OFF the clock, which
//            the owner types in (not saved; a settings doc would need a rules
//            change for non-admin HR users).
// Every missing input comes back from SafetyRates.build() as a flag, and the
// rate is labeled "not final" until each blocking flag is answered.
// Never logs narratives or names — the print carries dates, outcomes and counts.

function _srState() {
  // Scoped to the tenant (COD r2): a tenant switch must never carry one company's
  // off-clock hours or cached clock data into another's.
  if (HRState.safetyRates && HRState.safetyRates.tenantId !== (HRState.tenantId || '')) HRState.safetyRates = null;
  if (!HRState.safetyRates) {
    HRState.safetyRates = { tenantId: HRState.tenantId || '', periodKey: 'year:' + new Date().getFullYear(), extraHoursByPeriod: {}, include1099: false, clock: {} };
  }
  return HRState.safetyRates;
}

// End of today (local midnight tonight) so a rolling-12 period is stable all day.
function _srRollingEndMs() {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth(), n.getDate() + 1).getTime();
}

function _srPeriodFromKey(key) {
  const SR = window.SafetyRates;
  if (!SR) return null;
  if (key === 'rolling12') return SR.period({ kind: 'rolling12', nowMs: _srRollingEndMs() });
  const m = /^year:(\d{4})$/.exec(String(key || ''));
  return m ? SR.period({ kind: 'year', year: Number(m[1]) }) : null;
}

// Off-the-clock hours are typed PER PERIOD (COD r1: one value silently carried
// into another year's denominator). '' / missing = not answered.
function _srExtraFor(st) {
  const v = (st.extraHoursByPeriod || {})[st.periodKey];
  return (v === undefined || v === '') ? null : v;
}

function _srFmtHours(n) {
  return (Math.round((Number(n) || 0) * 10) / 10).toLocaleString('en-US', { maximumFractionDigits: 1 });
}

function _srFmtRate(r) {
  return (r === null || r === undefined) ? '—' : r.toFixed(2);
}

function _srDateStr(ms) {
  return Number.isFinite(ms) ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

function _srIncidentInputs() {
  return (HRState.incidents || []).map(r => ({
    id: r.id,
    type: r.type,
    oshaRecordable: r.oshaRecordable === true,
    oshaOutcome: r.oshaOutcome || '',
    oshaCaseCount: r.oshaCaseCount,
    peopleCount: Array.isArray(r.people) ? r.people.length : 0,
    dateMs: _srIncidentDateMs(r),
  }));
}

// The incident's OWN date (dateTime, else the typed datetime-local) — never
// createdAt (COD r1): an undated old injury must be flagged, not counted this year.
function _srIncidentDateMs(r) {
  const d = r?.dateTime?.toDate?.() || (r?.dateTimeLocal ? new Date(r.dateTimeLocal) : null);
  return (d && !isNaN(d)) ? d.getTime() : NaN;
}

// W-2 / 1099 through the ONE classification rule (window.WorkerClass).
function _srPersonClass() {
  const out = {};
  (HRState.people || []).forEach(p => {
    if (!p || !p.id) return;
    const r = window.WorkerClass?.resolve ? window.WorkerClass.resolve(p) : null;
    out[p.id] = r?.isW2 ? 'employee' : (r?.is1099 ? '1099' : 'unclassified');
  });
  return out;
}

async function _loadSafetyRateClock(tenantId, period, cacheKey) {
  const st = _srState();
  st.clock[cacheKey] = { status: 'loading' };
  try {
    // (status ASC, clockIn DESC) composite index already exists — same query
    // shape as _loadTimeOffClockHoursById, bounded on both ends.
    const snap = await getDocs(query(
      collection(db, 'tenants', tenantId, 'clockEntries'),
      where('status', '==', 'completed'),
      where('clockIn', '>=', Timestamp.fromMillis(period.startMs)),
      where('clockIn', '<', Timestamp.fromMillis(period.endMs)),
      orderBy('clockIn', 'desc')
    ));
    const entries = snap.docs.map(d => {
      const x = d.data() || {};
      return {
        personId: x.personId || '',
        clockInMs: x.clockIn?.toMillis ? x.clockIn.toMillis() : NaN,
        hours: Number(x.totalHours || x.hours || 0),
        status: x.status,
      };
    });
    st.clock[cacheKey] = { status: 'ok', entries };
  } catch (e) {
    console.error('[HR] Injury-rate clock read failed:', e?.code || 'error');
    st.clock[cacheKey] = { status: 'error' };
  }
  if (HRState.currentView === 'safety') renderSafetyRatesPanel();
}

function _srComputeCurrent() {
  const SR = window.SafetyRates;
  const st = _srState();
  const tid = HRState.tenantId || '';
  const period = _srPeriodFromKey(st.periodKey);
  if (!SR || !period || !tid) return { state: 'unavailable' };
  if (HRState.incidentsReadFailed) return { state: 'incidents-error', period };
  if (!HRState.incidentsLoaded) return { state: 'loading', period };
  const cacheKey = tid + '|' + st.periodKey + '|' + period.startMs + '|' + period.endMs;
  const clock = st.clock[cacheKey];
  if (!clock) { _loadSafetyRateClock(tid, period, cacheKey); return { state: 'loading', period }; }
  if (clock.status === 'loading') return { state: 'loading', period };
  if (clock.status !== 'ok') return { state: 'clock-error', period, cacheKey };
  const result = SR.build({
    period,
    incidents: _srIncidentInputs(),
    clockEntries: clock.entries,
    personClass: _srPersonClass(),
    include1099: st.include1099 === true,
    extraHours: _srExtraFor(st),
  });
  return { state: result.ok ? 'ok' : 'unavailable', period, result };
}

function _srFlagText(f, res) {
  const n = (f.ids && f.ids.length) || f.count || 0;
  const s = n === 1 ? '' : 's';
  switch (f.code) {
    case 'hours-clock-only': return 'Hours come from the time clock only. OSHA counts hours worked by <strong>all</strong> employees, including salaried, office and part-time staff. Enter hours worked off the clock above (enter 0 if everyone clocks in). Until you do, the rate shown is higher than your real rate.';
    case 'no-hours': return 'No hours worked in this period, so no rate can be calculated.';
    case 'outcome-missing': return `${n} recordable case${s} ${n === 1 ? 'has' : 'have'} no OSHA outcome. Pick one below. DART is only a minimum until then.`;
    case 'case-count-unconfirmed': return `${n} recordable incident${s} list${n === 1 ? 's' : ''} more than one person. Each hurt employee is a separate OSHA case. Enter how many were hurt below.`;
    case 'recordable-undated': return `${n} recordable incident${s} ${n === 1 ? 'has' : 'have'} no date, so ${n === 1 ? 'it is' : 'they are'} not counted in any period.`;
    case 'workers-unclassified': return `${n} ${n === 1 ? 'person' : 'people'} with clock hours ${n === 1 ? 'has' : 'have'} no W-2 / 1099 setting on their HR profile. Their ${_srFmtHours(f.hours)} hours are counted as employee hours for now. Set it on each profile so 1099 hours are left out correctly.`;
    case 'multi-case-one-outcome': return `${n} incident${s} count${n === 1 ? 's' : ''} more than one case under one outcome. If the people hurt had different outcomes, log each person as a separate incident.`;
    case 'long-shifts': return `${n} clock entr${n === 1 ? 'y is' : 'ies are'} longer than 24 hours (likely a missed clock-out). ${n === 1 ? 'It is' : 'They are'} counted as recorded. Fix ${n === 1 ? 'it' : 'them'} in Workforce if wrong.`;
    case 'not-attested': return `${n} injury or vehicle-accident incident${s} in this period ${n === 1 ? 'is' : 'are'} not marked OSHA recordable. If any ${n === 1 ? 'is' : 'are'}, tick Recordable below.`;
    default: return escapeHtml(f.code);
  }
}

function renderSafetyRatesPanel() {
  const el = document.getElementById('hr-safety-rates');
  if (!el) return;
  const SR = window.SafetyRates;
  if (!SR) {
    el.innerHTML = '<div class="empty-state-small">The injury-rate calculator did not load. Refresh the page.</div>';
    return;
  }
  const st = _srState();
  const yr = new Date().getFullYear();
  const periodOpts = [0, 1, 2, 3, 4].map(i => `year:${yr - i}`).concat(['rolling12']);
  const periodLabel = (k) => k === 'rolling12' ? 'Last 12 months' : 'Calendar year ' + k.slice(5);
  const controls = `
    <div style="display:flex;flex-wrap:wrap;gap:12px;align-items:flex-end;margin-bottom:var(--spacing-sm);">
      <div class="form-group" style="margin:0;min-width:170px;">
        <label class="form-label" for="hr-sr-period" style="font-size:0.72rem;">Period</label>
        <select id="hr-sr-period" class="form-control" onchange="window.HRModule.setSafetyRateOption('period', this.value)">
          ${periodOpts.map(k => `<option value="${k}" ${k === st.periodKey ? 'selected' : ''}>${periodLabel(k)}</option>`).join('')}
        </select>
      </div>
      <div class="form-group" style="margin:0;min-width:200px;">
        <label class="form-label" for="hr-sr-extra" style="font-size:0.72rem;">Hours worked off the time clock</label>
        <input type="number" id="hr-sr-extra" class="form-control" min="0" step="1" inputmode="decimal" placeholder="Not entered" value="${escapeHtml(String(_srExtraFor(st) ?? ''))}" onchange="window.HRModule.setSafetyRateOption('extra', this.value)">
        <div style="font-size:0.68rem;color:var(--text-secondary);margin-top:3px;">Salaried / office hours. Not saved: re-enter after a page refresh.</div>
      </div>
      <label style="display:flex;gap:8px;align-items:center;cursor:pointer;font-size:0.8rem;color:var(--text-primary);min-height:36px;">
        <input type="checkbox" id="hr-sr-1099" ${st.include1099 ? 'checked' : ''} onchange="window.HRModule.setSafetyRateOption('include1099', this.checked)">
        <span>Count 1099 / owner-operator hours <span style="color:var(--text-secondary);">(only if you supervise them day to day)</span></span>
      </label>
    </div>`;

  const cur = _srComputeCurrent();
  if (cur.state !== 'ok') {
    const msg = {
      'loading': 'Loading clock hours and incidents…',
      'incidents-error': 'Incidents could not be loaded, so no rate is shown.',
      'clock-error': 'Clock hours could not be loaded, so no rate is shown. <a href="#" onclick="window.HRModule.retrySafetyRates();return false;">Retry</a>',
      'unavailable': 'Injury rates are not available right now.',
    }[cur.state] || 'Injury rates are not available right now.';
    el.innerHTML = controls + `<div class="empty-state-small">${msg}</div>`;
    return;
  }
  const res = cur.result;
  const blocking = res.flags.filter(f => f.blocking);
  const info = res.flags.filter(f => !f.blocking);
  const statusPill = res.complete
    ? '<span style="font-size:0.7rem;font-weight:700;padding:3px 10px;border-radius:10px;background:#DCFCE7;color:#166534;">Inputs answered — check before you submit</span>'
    : `<span style="font-size:0.7rem;font-weight:700;padding:3px 10px;border-radius:10px;background:#FEF3C7;color:#92400E;">Not final: ${blocking.length} item${blocking.length === 1 ? '' : 's'} to answer</span>`;
  const H = res.cases.byOutcome['days-away'];
  const I = res.cases.byOutcome['restricted'];
  const hrs = _srFmtHours(res.hours.total);
  const tile = (label, val, sub) => `
    <div style="flex:1;min-width:150px;background:var(--bg-secondary);border-radius:var(--radius-md);padding:12px;">
      <div style="font-size:0.72rem;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;">${label}</div>
      <div style="font-size:1.8rem;font-weight:800;color:var(--text-primary);line-height:1.2;">${val}</div>
      <div style="font-size:0.72rem;color:var(--text-secondary);">${sub}</div>
    </div>`;
  const dartVal = (res.dartIsFloor && res.dartRounded !== null ? 'at least ' : '') + _srFmtRate(res.dartRounded);
  const notFinal = res.complete ? '' : ' · not final';

  // Incidents to show: every recordable in the period + injury / vehicle accidents
  // in the period not (yet) marked recordable, newest first.
  const incs = (HRState.incidents || []).filter(r => {
    const ms = _srIncidentDateMs(r);
    const inP = Number.isFinite(ms) && ms >= res.period.startMs && ms < res.period.endMs;
    return (r.oshaRecordable === true && (inP || !Number.isFinite(ms))) || (inP && (r.type === 'injury' || r.type === 'vehicle-accident'));
  }).sort((a, b) => (_srIncidentDateMs(b) || 0) - (_srIncidentDateMs(a) || 0));
  const outcomeOpts = (sel) => `<option value="">— Not decided —</option>` + SR.OUTCOMES.map(o => `<option value="${o}" ${o === sel ? 'selected' : ''}>${escapeHtml(SR.OUTCOME_LABELS[o])}</option>`).join('');
  const rows = incs.map(r => {
    const id = escapeHtml(r.id);
    const rec = r.oshaRecordable === true;
    const cc = SR.caseCount(r);
    const ppl = Array.isArray(r.people) ? r.people.length : 0;
    return `
      <tr style="border-bottom:1px solid var(--border-light);">
        <td style="padding:6px 8px;white-space:nowrap;"><a href="#" data-inc-id="${id}" onclick="window.HRModule.viewIncidentDetail(this.dataset.incId);return false;">${escapeHtml(_srDateStr(_srIncidentDateMs(r)))}</a></td>
        <td style="padding:6px 8px;">${escapeHtml(_incidentTypeMeta(r.type).label)}${ppl > 1 ? ` <span style="color:var(--text-secondary);font-size:0.72rem;">(${ppl} people listed)</span>` : ''}</td>
        <td style="padding:6px 8px;text-align:center;"><input type="checkbox" aria-label="OSHA recordable" data-inc-id="${id}" ${rec ? 'checked' : ''} onchange="window.HRModule.setIncidentOsha(this.dataset.incId, 'recordable', this.checked)"></td>
        <td style="padding:6px 8px;"><select class="form-control" style="min-width:190px;" aria-label="OSHA outcome" data-inc-id="${id}" ${rec ? '' : 'disabled'} onchange="window.HRModule.setIncidentOsha(this.dataset.incId, 'outcome', this.value)">${outcomeOpts(rec ? (r.oshaOutcome || '') : '')}</select></td>
        <td style="padding:6px 8px;"><input type="number" class="form-control" style="width:80px;" min="1" max="100" step="1" aria-label="Employees hurt" data-inc-id="${id}" ${rec ? '' : 'disabled'} value="${rec && cc.explicit ? cc.count : ''}" placeholder="1" onchange="window.HRModule.setIncidentOsha(this.dataset.incId, 'cases', this.value)"></td>
      </tr>`;
  }).join('');

  el.innerHTML = controls + `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:8px;">
      <div style="font-size:0.85rem;font-weight:600;color:var(--text-primary);">${escapeHtml(res.period.label)}</div>
      <div style="display:flex;gap:8px;align-items:center;">${statusPill}
        <button class="btn btn-secondary btn-sm" onclick="window.HRModule.retrySafetyRates()" title="Re-read clock hours"><span class="material-icons" style="font-size:16px;">refresh</span> Refresh</button>
        <button class="btn btn-secondary btn-sm" onclick="window.HRModule.printSafetyRates()"><span class="material-icons" style="font-size:16px;">print</span> Print</button>
      </div>
    </div>
    <div style="display:flex;gap:10px;flex-wrap:wrap;margin-bottom:10px;">
      ${tile('TRIR', _srFmtRate(res.trirRounded), 'Total recordable case rate' + notFinal)}
      ${tile('DART', dartVal, 'Days away, restricted or transferred' + notFinal)}
    </div>
    <div style="font-size:0.8rem;color:var(--text-primary);line-height:1.6;background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-sm);padding:10px;margin-bottom:10px;">
      <div><strong>TRIR</strong> = ${res.cases.total} recordable case${res.cases.total === 1 ? '' : 's'} × 200,000 ÷ ${hrs} hours = <strong>${_srFmtRate(res.trirRounded)}</strong></div>
      <div><strong>DART</strong> = (${H} days-away + ${I} restricted/transfer) × 200,000 ÷ ${hrs} hours = <strong>${dartVal}</strong></div>
      <div style="color:var(--text-secondary);margin-top:4px;">Hours (clock totals as recorded): ${_srFmtHours(res.hours.employee)} W-2 clock · ${_srFmtHours(res.hours.unclassified)} unclassified clock · ${_srFmtHours(res.hours.contractor)} 1099 clock (${res.hours.include1099 ? 'counted' : 'not counted'}) · ${res.hours.extraAnswered ? _srFmtHours(res.hours.extra) + ' off the clock' : 'off-the-clock hours not entered'}. 200,000 = 100 full-time workers × 40 hours × 50 weeks (OSHA). Deaths count in TRIR, not DART.</div>
    </div>
    ${blocking.length || info.length ? `<ul style="margin:0 0 10px;padding-left:18px;font-size:0.8rem;line-height:1.5;color:var(--text-primary);">
      ${blocking.map(f => `<li style="color:#92400E;">${_srFlagText(f, res)}</li>`).join('')}
      ${info.map(f => `<li>${_srFlagText(f, res)}</li>`).join('')}
    </ul>` : ''}
    ${rows ? `<div style="overflow-x:auto;"><table style="width:100%;border-collapse:collapse;font-size:0.8rem;">
      <thead><tr style="text-align:left;color:var(--text-secondary);font-size:0.72rem;">
        <th style="padding:6px 8px;">Date</th><th style="padding:6px 8px;">Type</th><th style="padding:6px 8px;text-align:center;">Recordable</th><th style="padding:6px 8px;">OSHA outcome</th><th style="padding:6px 8px;">Employees hurt</th>
      </tr></thead><tbody>${rows}</tbody></table></div>`
      : '<div style="font-size:0.8rem;color:var(--text-secondary);">No injury or vehicle-accident incidents in this period.</div>'}
    <div style="font-size:0.7rem;color:var(--text-muted);margin-top:8px;">Only incidents you mark OSHA recordable count. DispatchDeck never decides recordability for you.</div>`;
}

function setSafetyRateOption(name, value) {
  const st = _srState();
  if (name === 'period') st.periodKey = String(value || '');
  else if (name === 'extra') {
    const v = String(value ?? '').trim();
    const n = Number(v);
    const val = (v === '' || !Number.isFinite(n) || n < 0) ? '' : n;
    st.extraHoursByPeriod = st.extraHoursByPeriod || {};
    st.extraHoursByPeriod[st.periodKey] = val;
    if (v !== '' && val === '') window.showToast?.('Enter 0 or more hours', 'error');
  } else if (name === 'include1099') st.include1099 = value === true;
  renderSafetyRatesPanel();
}

function retrySafetyRates() {
  _srState().clock = {};
  renderSafetyRatesPanel();
}

// Inline edit of the OSHA fields on an existing incident. The incidents update
// rule already admits exactly who may edit an incident (admin / dispatcher /
// hr or compliance module, not view-only) — a refusal lands as a toast.
async function setIncidentOsha(id, field, value) {
  const tid = HRState.tenantId;
  const r = (HRState.incidents || []).find(x => x.id === id);
  if (!tid || !r) return;
  const update = { updatedAt: Timestamp.now(), updatedBy: window.AppState?.user?.email || 'unknown' };
  if (field === 'recordable') {
    // A legal attestation: confirm it (COD r1). Unticking keeps the outcome and
    // count on the record (non-recordables are never counted) so nothing is lost.
    const on = value === true;
    const go = await window.showConfirm?.(
      on ? 'Mark this incident OSHA recordable?' : 'Remove OSHA recordable?',
      on ? 'It goes on your OSHA 300 log and counts in your injury rates. Recordable means days away from work, restricted duty or job transfer, loss of consciousness, or medical treatment beyond first aid.'
         : 'It comes off your OSHA 300 count and your injury rates.',
      { confirmText: on ? 'Mark recordable' : 'Remove', danger: !on }
    );
    if (!go) { renderSafetyRatesPanel(); return; }
    update.oshaRecordable = on;
  } else if (field === 'outcome') {
    if (r.oshaRecordable !== true) return;
    update.oshaOutcome = (window.SafetyRates?.OUTCOMES || []).includes(value) ? value : '';
  } else if (field === 'cases') {
    if (r.oshaRecordable !== true) return;
    const n = String(value ?? '').trim() === '' ? NaN : Number(value); // integers only, never 2.9 -> 2
    if (String(value ?? '').trim() !== '' && !(Number.isInteger(n) && n >= 1 && n <= 100)) {
      window.showToast?.('Enter a number from 1 to 100', 'error');
      renderSafetyRatesPanel();
      return;
    }
    update.oshaCaseCount = Number.isInteger(n) && n >= 1 && n <= 100 ? n : null;
  } else return;
  try {
    await updateDoc(doc(db, 'tenants', tid, 'incidents', id), update);
    window.showToast?.('Incident updated', 'success');
  } catch (e) {
    console.error('[HR] Incident OSHA update failed:', e?.code || 'error');
    window.showToast?.(e?.code === 'permission-denied' ? 'You do not have permission to edit incidents' : 'Could not update the incident', 'error');
    renderSafetyRatesPanel();
  }
}

// Printable summary for prequalification forms (ISN / Avetta / shipper
// questionnaires). Dates, outcomes and counts only — no names, no narratives.
function printSafetyRates() {
  const cur = _srComputeCurrent();
  if (cur.state !== 'ok') { window.showToast?.('Injury rates are not ready yet', 'error'); return; }
  const res = cur.result;
  const SR = window.SafetyRates;
  const company = escapeHtml(window.AppState?.companyName || window.AppState?.tenantData?.name || '');
  const H = res.cases.byOutcome['days-away'];
  const I = res.cases.byOutcome['restricted'];
  const hrs = _srFmtHours(res.hours.total);
  const dartVal = (res.dartIsFloor && res.dartRounded !== null ? 'at least ' : '') + _srFmtRate(res.dartRounded);
  const flags = res.flags.map(f => `<li${f.blocking ? ' style="color:#92400E;"' : ''}>${_srFlagText(f, res)}</li>`).join('');
  const caseRows = res.cases.recordables
    .slice().sort((a, b) => (a.dateMs || 0) - (b.dateMs || 0))
    .map(c => `<tr><td>${escapeHtml(_srDateStr(c.dateMs))}</td><td>${escapeHtml(c.outcome ? SR.OUTCOME_LABELS[c.outcome] : 'Not decided')}</td><td>${c.cases}${c.caseCountExplicit ? '' : ' (not entered, counted as 1)'}</td></tr>`).join('');
  const w = window.open('', '_blank');
  if (!w) { window.showToast?.('Popup blocked. Allow popups for this site to print.', 'error'); return; }
  w.document.write(`<!DOCTYPE html><html><head><title>Injury Rates ${escapeHtml(res.period.label)}</title>
    <style>body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;padding:24px;color:#0F172A;}h1{font-size:20px;margin:0 0 4px;}
    .muted{color:#475569;font-size:12px;}table{border-collapse:collapse;width:100%;margin-top:8px;font-size:13px;}td,th{border:1px solid #CBD5E1;padding:6px 8px;text-align:left;}
    .big{font-size:28px;font-weight:800;}.status{display:inline-block;padding:3px 10px;border-radius:10px;font-weight:700;font-size:12px;${res.complete ? 'background:#DCFCE7;color:#166534;' : 'background:#FEF3C7;color:#92400E;'}}
    @media print { body { padding: 0; } }</style></head><body>
    <h1>OSHA Injury Rates${company ? ' — ' + company : ''}</h1>
    <div class="muted">${escapeHtml(res.period.label)} · printed ${escapeHtml(new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }))}</div>
    <p><span class="status">${res.complete ? 'Inputs answered — check before you submit' : 'NOT FINAL — inputs missing (see below)'}</span></p>
    <table><tr><th>TRIR</th><th>DART</th><th>Recordable cases</th><th>DART cases</th><th>Hours worked</th></tr>
    <tr><td class="big">${_srFmtRate(res.trirRounded)}</td><td class="big">${dartVal}</td><td>${res.cases.total}</td><td>${H + I}</td><td>${hrs}</td></tr></table>
    <p style="font-size:13px;">TRIR = ${res.cases.total} × 200,000 ÷ ${hrs} = ${_srFmtRate(res.trirRounded)}<br>
    DART = (${H} days away + ${I} restricted/transfer) × 200,000 ÷ ${hrs} = ${dartVal}<br>
    <span class="muted">Hours: ${_srFmtHours(res.hours.employee)} W-2 clock · ${_srFmtHours(res.hours.unclassified)} unclassified clock · ${_srFmtHours(res.hours.contractor)} 1099 clock (${res.hours.include1099 ? 'counted' : 'not counted'}) · ${res.hours.extraAnswered ? _srFmtHours(res.hours.extra) + ' off the clock' : 'off-the-clock hours not entered'}.</span></p>
    ${flags ? `<h3 style="font-size:14px;">Open items</h3><ul style="font-size:13px;">${flags}</ul>` : ''}
    <h3 style="font-size:14px;">Recordable cases</h3>
    ${caseRows ? `<table><tr><th>Date</th><th>OSHA outcome</th><th>Cases</th></tr>${caseRows}</table>` : '<p class="muted">None in this period.</p>'}
    <p class="muted">Formula per OSHA Recordkeeping Forms (Form 300A instructions): cases × 200,000 ÷ hours worked by all employees. 200,000 = 100 full-time workers × 40 hours × 50 weeks.</p>
    </body></html>`);
  w.document.close();
  w.focus();
  w.print();
}

// ============================================
// TASK-1140 Slice 2 — §390.15 ACCIDENT REGISTER (generated view)
// ============================================
// The register is a FILTERED PROJECTION of tenants/{tid}/incidents where
// dotAccidentRegister === true (the human attestation from Slice 1). There is
// no register collection, no denormalized register docs, and no new rules —
// the same incidents read-gate (admin / dispatcher / hr / compliance module)
// covers it. Columns per §390.15(b)(1): date, city/state, driver name,
// fatalities, injuries treated away from the scene, hazmat release. Tow-away
// is shown too because it's part of the §390.5 "accident" definition carriers
// use to decide recordability. Print (what gets handed to a DOT auditor) copies
// the house printWindow pattern; CSV uses the formula-injection-safe csvSafeCell.

function _registerRowDateStr(r) {
  const d = _incidentDate(r);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
}

function _registerCityState(r) {
  return [r.locationCity, r.locationState].filter(Boolean).join(', ') || r.location || '';
}

// Human-readable covered range for the header ("Jan 3, 2024 – Jun 12, 2026").
// §390.15(b) retention: each accident stays on the register for 3 years after
// the accident date — we show everything logged and let the carrier see the
// span at a glance.
function _registerDateRange(records) {
  const dates = records.map(_incidentDate).filter(Boolean).sort((a, b) => a - b);
  if (!dates.length) return '';
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const first = fmt(dates[0]);
  const last = fmt(dates[dates.length - 1]);
  return first === last ? first : `${first} – ${last}`;
}

function openAccidentRegister() {
  const records = _registerIncidents();
  const range = _registerDateRange(records);

  const emptyHtml = `
    <div style="text-align:center;padding:28px 16px;">
      <span class="material-icons" style="font-size:32px;color:var(--text-muted);">table_view</span>
      <div style="font-weight:600;font-size:0.95rem;color:var(--text-primary);margin:8px 0 6px;">Nothing on your accident register yet</div>
      <div style="font-size:0.82rem;color:var(--text-secondary);line-height:1.55;max-width:440px;margin:0 auto;">
        The §390.15 accident register is the list DOT expects every carrier to keep of
        crashes that involved a <strong>fatality</strong>, an <strong>injury treated away
        from the scene</strong>, or a <strong>vehicle towed from the scene</strong>.
        When you report an incident and check the §390.15 attestation, it shows up here
        automatically — nothing to enter twice. Keep each accident on the register for
        <strong>3 years</strong> after the accident date; this is one of the first things
        an auditor asks for.
      </div>
    </div>`;

  const rowsHtml = records.map(r => `
    <tr style="cursor:pointer;" onclick="window.HRModule.viewIncidentDetail('${r.id}')">
      <td style="white-space:nowrap;">${escapeHtml(_registerRowDateStr(r))}</td>
      <td>${escapeHtml(_registerCityState(r) || '—')}</td>
      <td>${escapeHtml(r.driverName || '—')}</td>
      <td style="text-align:center;">${escapeHtml(String(r.fatalitiesCount ?? 0))}</td>
      <td style="text-align:center;">${escapeHtml(String(r.injuriesCount ?? 0))}</td>
      <td style="text-align:center;">${r.towAway ? 'Yes' : 'No'}</td>
      <td style="text-align:center;">${r.hazmatRelease ? 'Yes' : 'No'}</td>
    </tr>`).join('');

  const tableHtml = `
    <div style="overflow-x:auto;-webkit-overflow-scrolling:touch;">
      <table style="width:100%;border-collapse:collapse;min-width:560px;">
        <thead>
          <tr>
            <th style="text-align:left;padding:6px 8px;font-size:0.7rem;text-transform:uppercase;color:var(--text-secondary);border-bottom:2px solid var(--border-light);">Date</th>
            <th style="text-align:left;padding:6px 8px;font-size:0.7rem;text-transform:uppercase;color:var(--text-secondary);border-bottom:2px solid var(--border-light);">City / State</th>
            <th style="text-align:left;padding:6px 8px;font-size:0.7rem;text-transform:uppercase;color:var(--text-secondary);border-bottom:2px solid var(--border-light);">Driver</th>
            <th style="text-align:center;padding:6px 8px;font-size:0.7rem;text-transform:uppercase;color:var(--text-secondary);border-bottom:2px solid var(--border-light);">Fatalities</th>
            <th style="text-align:center;padding:6px 8px;font-size:0.7rem;text-transform:uppercase;color:var(--text-secondary);border-bottom:2px solid var(--border-light);" title="Injuries treated away from the scene">Injuries</th>
            <th style="text-align:center;padding:6px 8px;font-size:0.7rem;text-transform:uppercase;color:var(--text-secondary);border-bottom:2px solid var(--border-light);">Tow-away</th>
            <th style="text-align:center;padding:6px 8px;font-size:0.7rem;text-transform:uppercase;color:var(--text-secondary);border-bottom:2px solid var(--border-light);">Hazmat</th>
          </tr>
        </thead>
        <tbody style="font-size:0.85rem;color:var(--text-primary);">
          ${rowsHtml}
        </tbody>
      </table>
    </div>
    <style>
      #modal-hr-accident-register tbody td { padding:7px 8px; border-bottom:1px solid var(--border-light); }
      #modal-hr-accident-register tbody tr:hover { background: var(--bg-secondary); }
    </style>`;

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-hr-accident-register';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:760px;max-height:90vh;max-height:90dvh;overflow-y:auto;">
      <div class="modal-header" style="background:linear-gradient(135deg,#D97706,#B45309);color:#fff;">
        <h3 style="margin:0;display:flex;align-items:center;gap:8px;">
          <span class="material-icons">table_view</span> §390.15 Accident Register
        </h3>
        <button class="btn-icon" onclick="document.getElementById('modal-hr-accident-register')?.remove()" style="color:#fff;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;">
        <div style="display:flex;align-items:center;gap:var(--spacing-sm);flex-wrap:wrap;margin-bottom:12px;">
          <div style="flex:1;min-width:180px;font-size:0.8rem;color:var(--text-secondary);">
            ${records.length} accident${records.length !== 1 ? 's' : ''} on the register${range ? ` · ${escapeHtml(range)}` : ''}<br>
            <span style="font-size:0.72rem;color:var(--text-muted);">Keep each accident listed for 3 years after the accident date (49 CFR §390.15).</span>
          </div>
          <button class="btn btn-secondary btn-sm" onclick="window.HRModule.printAccidentRegister()" ${records.length ? '' : 'disabled'}>
            <span class="material-icons">print</span> Print
          </button>
          <button class="btn btn-secondary btn-sm" onclick="window.HRModule.exportAccidentRegisterCsv()" ${records.length ? '' : 'disabled'}>
            <span class="material-icons">download</span> CSV
          </button>
        </div>
        ${records.length ? tableHtml : emptyHtml}
      </div>
    </div>`;
  document.body.appendChild(modal);
}

// Print output — this is the sheet a carrier physically hands a DOT auditor.
// Copies the house printWindow pattern (printEmployeeReview / warning print):
// window.open('', '_blank') + popup-block toast + document.write + print().
function printAccidentRegister() {
  const records = _registerIncidents();
  if (!records.length) { window.showToast?.('No accidents on the register to print', 'error'); return; }
  const companyName = window.AppState?.companyName || window.AppState?.tenantData?.name || 'Company';
  const range = _registerDateRange(records);
  const now = new Date();

  const rowsHtml = records.map(r => `
    <tr>
      <td style="white-space:nowrap;">${escapeHtml(_registerRowDateStr(r))}</td>
      <td>${escapeHtml(_registerCityState(r) || '—')}</td>
      <td>${escapeHtml(r.driverName || '—')}</td>
      <td style="text-align:center;">${escapeHtml(String(r.fatalitiesCount ?? 0))}</td>
      <td style="text-align:center;">${escapeHtml(String(r.injuriesCount ?? 0))}</td>
      <td style="text-align:center;">${r.towAway ? 'Yes' : 'No'}</td>
      <td style="text-align:center;">${r.hazmatRelease ? 'Yes' : 'No'}</td>
    </tr>`).join('');

  const printWindow = window.open('', '_blank');
  if (!printWindow) { window.showToast?.('Popup blocked. Allow popups for this site to print.', 'error'); return; }
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Accident Register (49 CFR 390.15) - ${escapeHtml(companyName)}</title>
      <style>
        @page { margin: 0.4in 0.5in; size: letter landscape; }
        body { font-family: Arial, sans-serif; padding: 0; margin: 0 auto; color: #1a1a1a; font-size: 11px; }
        .header { text-align: center; margin-bottom: 12px; border-bottom: 2px solid #333; padding-bottom: 8px; }
        .header h1 { margin: 0 0 3px 0; font-size: 17px; text-transform: uppercase; letter-spacing: 1px; }
        .header .company { font-size: 13px; color: #555; margin-bottom: 2px; }
        .header .meta { font-size: 10px; color: #666; }
        table { width: 100%; border-collapse: collapse; margin: 8px 0; }
        th, td { padding: 5px 7px; text-align: left; border: 1px solid #999; font-size: 10px; }
        th { background: #f0f0f0; font-weight: 700; font-size: 9px; text-transform: uppercase; }
        .footer { margin-top: 14px; font-size: 9px; color: #666; line-height: 1.4; border-top: 1px solid #ccc; padding-top: 6px; }
        @media print { body { padding: 0; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="company">${escapeHtml(companyName)}</div>
        <h1>Accident Register — 49 CFR §390.15</h1>
        <div class="meta">
          ${records.length} accident${records.length !== 1 ? 's' : ''}${range ? ' · Covering ' + escapeHtml(range) : ''}
          · Generated ${now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Date of Accident</th>
            <th>City / Town &amp; State</th>
            <th>Driver Name</th>
            <th style="text-align:center;">Fatalities</th>
            <th style="text-align:center;">Injuries (treated away from scene)</th>
            <th style="text-align:center;">Tow-away</th>
            <th style="text-align:center;">Hazmat Released</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div class="footer">
        Per 49 CFR §390.15(b), each accident remains on this register for 3 years after the accident date.
        "Hazmat Released" excludes fuel spilled from the fuel tanks of the vehicles involved.
        Copies of required accident reports are retained with the underlying incident records in DispatchDeck.
      </div>
    </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}

// CSV export — every cell through csvSafeCell (the TASK-453/TASK-843
// formula-injection guard: leading = + - @ tab CR gets a quote prefix, then
// normal CSV quoting). Never a naive join — narrative-adjacent free text
// (city, driver name) is user-controlled and lands in Excel.
function exportAccidentRegisterCsv() {
  const records = _registerIncidents();
  if (!records.length) { window.showToast?.('No accidents on the register to export', 'error'); return; }

  const header = ['Date of Accident', 'City/Town', 'State', 'Driver Name', 'Fatalities', 'Injuries (treated away from scene)', 'Tow-away', 'Hazmat Released'];
  const rows = [header.map(csvSafeCell).join(',')];
  records.forEach(r => {
    const d = _incidentDate(r);
    rows.push([
      csvSafeCell(d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : ''),
      csvSafeCell(r.locationCity || r.location || ''),
      csvSafeCell(r.locationState || ''),
      csvSafeCell(r.driverName || ''),
      csvSafeCell(String(r.fatalitiesCount ?? 0)),
      csvSafeCell(String(r.injuriesCount ?? 0)),
      csvSafeCell(r.towAway ? 'Yes' : 'No'),
      csvSafeCell(r.hazmatRelease ? 'Yes' : 'No'),
    ].join(','));
  });

  const dateStr = new Date().toISOString().split('T')[0];
  const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `accident-register-390-15-${dateStr}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
  window.showToast?.('Accident register exported', 'success');
}

// --- JSA Reports for Safety sub-view ---
// TASK-9581 — Compliance → Safety (and HR → Safety) used to subscribe to EVERY
// JSA the tenant ever filed and filter client-side. Now the listener holds only
// the selected date window (default: the last day = today + yesterday), paged
// (JsaListWindow.PAGE_SIZE, "Load more" grows the page), and the section loads
// collapsed (header = toggle, count still shown). Markup, presets, customer /
// crew / job filters, rendering and Print live in the LAZY utils/jsaListWindow.js
// (shared with the JSA page archive); this file owns only the Firestore read.
// `date` is a Timestamp on every writer (jsa.js save paths, demoSeeder), so a
// single-field range + orderBy on `date` needs no composite index.
let _hrJsaLoadP = null;
let _hrJsaCtl = null;
function _hrJsaLoad() {
  if (!_hrJsaLoadP) {
    _hrJsaLoadP = __ddChunkWrap(import('../utils/jsaListWindow.js?v=9581002')).then(() => {
      _hrJsaCtl = window.JsaListWindow.createList({
        p: 'hr-jsa', mountId: 'hr-jsa-mount', openKey: 'dd.jsaReports.open.safety',
        sectionStyle: 'margin-top: var(--spacing-lg);',
        h: {
          toggle: 'window.HRModule.toggleJSAReports()', range: 'window.HRModule.renderJSAReportsList()',
          filter: 'window.HRModule.renderJSAReportsList()', clear: 'window.HRModule.clearJSAFilters()',
          print: 'window.HRModule.printJSAReports()', more: 'window.HRModule.loadMoreJSAReports()',
          detail: 'window.HRModule.viewJSADetail',
        },
        reports: () => HRState.jsaReports,
        loaded: () => !!HRState.jsaLoaded,
        total: () => HRState.jsaTotal,
        limitHit: () => !!HRState.jsaLimitHit,
        upcomingFailed: () => !!HRState.jsaUpFailed, // COD r6 — no "+ upcoming" claim on a failed read
        pastFailed: () => !!HRState.jsaPastFailed,   // COD r7 — say it, never an empty "no reports"
        onRange: (range, key) => { if (key !== HRState.jsaRangeKey) { HRState.jsaLimit = 0; _hrJsaListen(range, key); } },
        // COD r1 — template sheets keep crew in signIns (signInCount), like jsa.js.
        rowMeta: (r) => ({ crew: r.formMode === 'template' ? (Number(r.signInCount) || 0) : (r.crewAcknowledgments?.length || 0), hazards: r.hazards?.length || 0 }),
      });
      return _hrJsaCtl;
    }).catch((e) => { _hrJsaLoadP = null; console.error('[HR] JSA list module failed to load:', e?.message); throw e; });
  }
  return _hrJsaLoadP;
}

// (Re)attach the bounded listener for `range`. Drops the previous one first.
function _hrJsaListen(range, key) {
  const tenantId = HRState.jsaTenantId;
  if (!tenantId || !HRState.jsaSubscribed) return;
  if (typeof HRState.jsaUnsub === 'function') { try { HRState.jsaUnsub(); } catch (_) {} }
  HRState.jsaUnsub = null;
  const pageSize = HRState.jsaLimit || window.JsaListWindow.PAGE_SIZE;
  HRState.jsaLimit = pageSize;
  HRState.jsaRangeKey = key;
  // COD r1 — a generation token, not the range key: Load more (same range) and a
  // re-subscribe after teardown reuse the key, so only `gen` tells a stale
  // listener's late snapshot apart.
  const gen = (HRState.jsaGen = (HRState.jsaGen || 0) + 1);
  HRState.jsaReports = [];
  HRState.jsaLoaded = false;
  HRState.jsaLimitHit = false;
  HRState.jsaUpFailed = false;
  HRState.jsaPastFailed = false;
  const col = collection(db, 'tenants', tenantId, 'jsaReports');
  // COD r5 — the look-back presets also hold the upcoming week's pre-starts.
  // One newest-first page would let 100 pre-starts push today's and
  // yesterday's sheets behind Load more, so the window is read as TWO bounded
  // listeners: up to the end of today (newest first) and after it (soonest
  // first), each a page. The list (JsaListWindow.orderTodayFirst) paints today,
  // then earlier days, then upcoming — so today's work is on the first page.
  // COD r7 — All dates / an open or future-ending Custom split too (no end bound
  // on the upcoming half then).
  const split = (range.split && (!range.end || range.split < range.end)) ? range.split : null;
  // COD r8 — a Custom range that STARTS after today has no up-to-today half;
  // its upcoming half is lower-bounded by the range start (not end of today),
  // so nearer pre-starts can't fill the page with rows the range then drops.
  const futureOnly = !!(split && range.start && range.start > split);
  const pastEnd = split || range.end;
  const pastCons = [];
  if (range.start) pastCons.push(where('date', '>=', Timestamp.fromDate(range.start)));
  if (pastEnd) pastCons.push(where('date', '<=', Timestamp.fromDate(pastEnd)));
  const parts = { past: { docs: [], hit: false, ready: futureOnly, failed: false }, up: { docs: [], hit: false, ready: !split, failed: false } };
  const publish = () => {
    if (!parts.past.ready || !parts.up.ready) return;
    HRState.jsaLimitHit = parts.past.hit || parts.up.hit;
    HRState.jsaUpFailed = !!split && parts.up.failed;
    HRState.jsaPastFailed = parts.past.failed;
    // Today + earlier first, then upcoming soonest-first (the list orders them
    // the same way — orderTodayFirst — this just keeps the state in that order).
    const docs = [...parts.past.docs, ...parts.up.docs];
    // COD r1 — a row tapped while a re-listen is in flight (jsaReports is
    // emptied until the new window's first snapshot) still opens.
    docs.forEach(r => { HRState.jsaSeen.delete(r.id); HRState.jsaSeen.set(r.id, r); });
    // Bounded: drop the longest-unseen ids (Map keeps insertion order), never all at once.
    for (const k of HRState.jsaSeen.keys()) { if (HRState.jsaSeen.size <= 2000) break; HRState.jsaSeen.delete(k); }
    HRState.jsaReports = docs;
    HRState.jsaLoaded = true;
    if (HRState.currentView === 'safety') renderJSAReportsList();
  };
  const listen = (q, part, label) => onSnapshot(q, (snapshot) => {
    if (HRState.jsaGen !== gen || HRState.jsaTenantId !== tenantId) return; // a newer listener replaced this one
    part.docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    part.hit = snapshot.docs.length >= pageSize;
    part.ready = true;
    part.failed = false;
    publish();
  }, (err) => {
    if (HRState.jsaGen !== gen || HRState.jsaTenantId !== tenantId) return;
    console.error(`[HR] JSA reports ${label} listener error:`, err?.code, err?.message);
    part.docs = []; part.hit = false; part.ready = true; part.failed = true;
    publish();
  });
  const unsubs = futureOnly ? [] : [listen(query(col, ...pastCons, orderBy('date', 'desc'), limit(pageSize)), parts.past, 'window')];
  if (split) {
    const upCons = [futureOnly ? where('date', '>=', Timestamp.fromDate(range.start)) : where('date', '>', Timestamp.fromDate(split))];
    if (range.end) upCons.push(where('date', '<=', Timestamp.fromDate(range.end)));
    unsubs.push(listen(query(col, ...upCons, orderBy('date', 'asc'), limit(pageSize)), parts.up, 'upcoming'));
  }
  HRState.jsaUnsub = () => unsubs.forEach(u => { try { u(); } catch (_) {} });
}

function _hrJsaTeardown() {
  if (typeof HRState.jsaUnsub === 'function') { try { HRState.jsaUnsub(); } catch (_) {} }
  HRState.jsaUnsub = null;
  HRState.jsaRangeKey = null;
  HRState.jsaGen = (HRState.jsaGen || 0) + 1; // orphan any in-flight snapshot
  HRState.jsaSeen.clear();
  HRState.jsaOpenReport = null;
  HRState.jsaReports = [];
  HRState.jsaLoaded = false;
  // Re-arm on the next visit (the old guard stayed true after teardown, so a
  // torn-down listener was never re-subscribed — same class as TASK-635).
  HRState.jsaSubscribed = false;
}

function subscribeToJSAReports(tenantId) {
  if (!tenantId) return;
  if (HRState.jsaSubscribed && HRState.jsaTenantId === tenantId) return;
  // COD r2 — a different tenant while still subscribed (Compliance → Safety
  // never runs HR's unsubscribers): drop the old company's listener + caches.
  if (HRState.jsaSubscribed) _hrJsaTeardown();
  HRState.jsaSubscribed = true;
  HRState.jsaTenantId = tenantId;
  // The first window comes from the controls on first render (default: last day).
  // One teardown entry for whichever bounded listener is current.
  if (!HRState.unsubscribers.includes(_hrJsaTeardown)) HRState.unsubscribers.push(_hrJsaTeardown);
  // "of N" — one count aggregation (billed ~1 read per 1,000 docs), never the
  // documents. Best effort: on failure the summary just omits the total.
  HRState.jsaTotal = null;
  // COD r4 — a per-request token: a late answer from an earlier visit (same
  // tenant, torn down + re-subscribed) never overwrites the current one.
  const countTok = (HRState.jsaCountTok = (HRState.jsaCountTok || 0) + 1);
  getCountFromServer(collection(db, 'tenants', tenantId, 'jsaReports'))
    .then(snap => {
      if (HRState.jsaTenantId !== tenantId || HRState.jsaCountTok !== countTok) return;
      HRState.jsaTotal = snap.data().count;
      if (HRState.currentView === 'safety') renderJSAReportsList();
    })
    .catch(() => { if (HRState.jsaTenantId === tenantId && HRState.jsaCountTok === countTok) HRState.jsaTotal = null; });
}

function renderJSAReportsList() {
  if (!document.getElementById('hr-jsa-mount')) return;
  const modal = document.getElementById('modal-hr-jsa-detail');
  if (modal && !modal.classList.contains('hidden')) { _pendingModalRenders.add('jsa'); return; } // TASK-1119
  _hrJsaLoad().then(ctl => ctl.render()).catch(() => {
    const m = document.getElementById('hr-jsa-mount');
    if (m && !m.firstChild) m.innerHTML = '<div class="empty-state-small">JSA Reports could not load. Refresh to try again.</div>';
  });
}

function toggleJSAReports() { _hrJsaLoad().then(ctl => ctl.toggle()).catch(() => {}); }
function clearJSAFilters() { _hrJsaLoad().then(ctl => ctl.clear()).catch(() => {}); }
// Sync when loaded (it always is once the section shows) so window.open stays in the tap.
function printJSAReports() { if (_hrJsaCtl) _hrJsaCtl.print(); else _hrJsaLoad().then(ctl => ctl.print()).catch(() => {}); }
function loadMoreJSAReports() {
  _hrJsaLoad().then(ctl => {
    if (!ctl.more() || !HRState.jsaTenantId) return;
    // Past the loaded rows: a bigger page of the same window.
    HRState.jsaLimit = (HRState.jsaLimit || window.JsaListWindow.PAGE_SIZE) + window.JsaListWindow.PAGE_SIZE;
    const c = window.JsaListWindow.readControls('hr-jsa');
    _hrJsaListen(c.range, window.JsaListWindow.rangeKey(c.range));
    ctl.render();
  }).catch(() => {});
}

// --- Fire Extinguisher Inspections for Safety sub-view ---
// TASK-4884 — RETIRED reader. `safetyInspections` was written only by the retired
// Shop Duties submitFireExtInspection callable; the live census (2026-10-06) found
// 0 docs on every tenant and nothing writes it now, so its rules arm was removed
// (default deny) and this no longer subscribes. Shop extinguishers, eyewash,
// first-aid and spill kits live in Maintenance → Inspections (window.FacilityInspections).
function subscribeToFireExtInspections(tenantId) {
  if (!tenantId || HRState.fireExtSubscribed) return;
  HRState.fireExtSubscribed = true;
  HRState.fireExtInspections = [];
}

function renderFireExtInspectionsList() {
  const container = document.getElementById('hr-fire-ext-list');
  if (!container) return;
  container.innerHTML = `
    <div class="empty-state-small">
      <span class="material-icons" style="font-size:24px;margin-bottom:8px;">local_fire_department</span>
      <div>Shop equipment inspections moved</div>
      <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">Record fire extinguisher, eyewash, first-aid and spill-kit inspections in Maintenance → Inspections. Vehicle extinguishers are on the DVIR.</div>
    </div>`;
}

// TASK-9581 — the Safety list holds only the current window, and a re-listen
// empties it until the first snapshot: fall back to the open detail, then to
// the seen-by-id cache. ALL of them (live list included) are trusted only when
// the signed-in tenant is the one the listener loaded, and it FAILS CLOSED with
// no tenant (COD r2/r3: Compliance and logout never run HR's unsubscribers, so
// after a tenant switch or a same-tab logout these still hold the previous
// company's sheets; HRState.tenantId is not a fallback — it is that stale id).
function _hrJsaFind(jsaId) {
  const cur = window.AppState?.tenant;
  if (!cur || !HRState.jsaTenantId || cur !== HRState.jsaTenantId) return null;
  const live = HRState.jsaReports.find(r => r.id === jsaId);
  if (live) return live;
  if (HRState.jsaOpenReport && HRState.jsaOpenReport.id === jsaId) return HRState.jsaOpenReport;
  return HRState.jsaSeen.get(jsaId) || null;
}
// A JSA's day is company-calendar noon (TASK-9923): show THAT day, as the list does.
function _hrJsaDayLabel(date, opts) {
  if (!date || Number.isNaN(date.getTime())) return '';
  const rd = window.RecordDate;
  const ymd = rd?.ymd ? rd.ymd(date.getTime(), rd.zone()) : '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || '');
  if (!m) return date.toLocaleDateString('en-US', opts);
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString('en-US', Object.assign({}, opts, { timeZone: 'UTC' }));
}

function viewJSADetail(jsaId) {
  const report = _hrJsaFind(jsaId); // TASK-9581
  if (!report) return;
  HRState.jsaOpenReport = report;

  const date = report.date?.toDate?.() || new Date(report.date);

  // Build a simple detail view in an alert-style overlay
  const hazardLabels = (report.hazards || []).map(id => {
    const map = { gravity:'Gravity', motion:'Motion', mechanical:'Mechanical', electrical:'Electrical', pressure:'Pressure', temperature:'Temperature', biological:'Biological', radiation:'Radiation', people:'People', location:'Location', chemical:'Chemical', sound:'Sound', other:'Other' };
    return map[id] || id;
  });
  const ppeLabels = (report.ppeEquipment || []).map(id => {
    const map = { hard_hat:'Hard Hat', steel_toed_boots:'Steel Toed Boots', glasses:'Safety Glasses', gas_monitor:'Gas Monitor', grinding_shield:'Grinding Shield', welding_leathers:'Welding Leathers', full_face_shield:'Full Face Shield', chemical_gloves:'Chemical Gloves', gloves:'Gloves', dust_mask:'Dust Mask', body_harness:'Body Harness', goggles:'Goggles', ear_plugs:'Ear Plugs', h2s_monitor:'H2S Monitor', kevlar_gloves:'Kevlar Gloves', other:'Other' };
    return map[id] || id;
  });

  const content = `
    <div style="max-height:70vh;overflow-y:auto;padding:var(--spacing-md);">
      <h3 style="margin:0 0 var(--spacing-md) 0;">${escapeHtml(report.customerName || 'JSA Report')}</h3>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;font-size:0.85rem;margin-bottom:var(--spacing-md);">
        <div><strong>Date:</strong> ${_hrJsaDayLabel(date, { month: 'short', day: 'numeric', year: 'numeric' })}</div>
        <div><strong>Supervisor:</strong> ${escapeHtml(report.supervisorName || 'N/A')}</div>
        <div><strong>Job:</strong> ${escapeHtml(report.jobTitle || 'N/A')}</div>
        <div><strong>Hours:</strong> ${escapeHtml(String(report.totalHours || 'N/A'))}</div>
        <div><strong>Permit:</strong> ${report.permitRequired ? 'Yes' : 'No'}</div>
        <div><strong>811:</strong> ${report.oneCallCompleted ? 'Yes' : 'No'}${report.oneCallNumber ? ` (#${escapeHtml(report.oneCallNumber)})` : ''}</div>
      </div>
      ${hazardLabels.length > 0 ? `<div style="margin-bottom:var(--spacing-sm);"><strong>Hazards:</strong> ${hazardLabels.map(h => escapeHtml(String(h))).join(', ')}</div>` : ''}
      ${ppeLabels.length > 0 ? `<div style="margin-bottom:var(--spacing-sm);"><strong>PPE:</strong> ${ppeLabels.map(x => escapeHtml(String(x))).join(', ')}</div>` : ''}
      ${(report.tasks || []).length > 0 ? `
        <div style="margin-bottom:var(--spacing-sm);">
          <strong>Tasks:</strong>
          <table style="width:100%;border-collapse:collapse;font-size:0.8rem;margin-top:4px;">
            <tr style="border-bottom:1px solid var(--border-light);"><th style="text-align:left;padding:4px;">Task</th><th style="text-align:left;padding:4px;">Hazards</th><th style="text-align:left;padding:4px;">Controls</th></tr>
            ${report.tasks.map(t => `<tr style="border-bottom:1px solid var(--border-light);"><td style="padding:4px;">${escapeHtml(t.task||'')}</td><td style="padding:4px;">${escapeHtml(t.potentialHazards||'')}</td><td style="padding:4px;">${escapeHtml(t.controlMeasures||'')}</td></tr>`).join('')}
          </table>
        </div>` : ''}
      ${report.workLayoutPlan ? `<div style="margin-bottom:var(--spacing-sm);"><strong>Work Layout:</strong> ${escapeHtml(report.workLayoutPlan)}</div>` : ''}
      ${report.musterPoint ? `<div style="margin-bottom:var(--spacing-sm);"><strong>Muster Point:</strong> ${escapeHtml(report.musterPoint)}</div>` : ''}
      <div style="margin-top:var(--spacing-md);">
        <strong>Crew (${(report.crewAcknowledgments || []).length}):</strong>
        ${(report.crewAcknowledgments || []).map(ack => `
          <div style="display:flex;align-items:center;gap:8px;padding:4px 0;">
            <span class="material-icons" style="font-size:16px;color:${ack.signature ? 'var(--action-success)' : 'var(--text-muted)'};">${ack.signature ? 'check_circle' : 'radio_button_unchecked'}</span>
            <span style="font-size:0.85rem;">${escapeHtml(ack.name)}</span>
          </div>
        `).join('')}
      </div>
    </div>
    <div style="display:flex;gap:var(--spacing-sm);justify-content:flex-end;padding:var(--spacing-sm) var(--spacing-md);border-top:1px solid var(--border-light);">
      <button class="btn btn-secondary btn-sm" onclick="window.HRModule.printSingleJSA('${escJsAttr(report.id)}')">
        <span class="material-icons" style="font-size:16px;">print</span> Print
      </button>
      <button class="btn btn-secondary btn-sm" onclick="window.HRModule.closeHrJsaDetailModal()">Close</button>
    </div>`;

  // Use a generic modal overlay
  let modal = document.getElementById('modal-hr-jsa-detail');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-hr-jsa-detail';
    modal.className = 'modal';
    modal.innerHTML = '<div class="modal-content" style="max-width:600px;"></div>';
    modal.addEventListener('click', (e) => { if (e.target === modal) closeHrJsaDetailModal(); }); // TASK-1119
    document.body.appendChild(modal);
  }
  modal.querySelector('.modal-content').innerHTML = content;
  modal.classList.remove('hidden');
}

// TASK-1119 — dedicated close for the JSA detail modal (it bypasses closeModal),
// so the deferred list render flushes on close like every other gated modal.
function closeHrJsaDetailModal() {
  document.getElementById('modal-hr-jsa-detail')?.classList.add('hidden');
  _flushPendingModalRender('jsa');
}

function printSingleJSA(jsaId) {
  const report = _hrJsaFind(jsaId); // TASK-9581
  if (!report) return;

  const date = report.date?.toDate?.() || new Date(report.date);
  const w = window.open('', '_blank');
  if (!w) { window.showToast?.('Popup blocked. Allow popups for this site to print.', 'error'); return; }
  w.document.write(`<html><head><title>JSA - ${escapeHtml(report.customerName || 'Report')}</title>
    <style>body{font-family:Arial,sans-serif;padding:20px;font-size:12px;}h2{margin:0 0 10px;}table{width:100%;border-collapse:collapse;margin:8px 0;}th,td{border:1px solid #ccc;padding:4px 6px;text-align:left;}th{background:#f0f0f0;}.crew{margin:4px 0;}</style></head><body>
    <h2>Job Safety Analysis</h2>
    <p><strong>Customer:</strong> ${escapeHtml(report.customerName||'N/A')} | <strong>Date:</strong> ${escapeHtml(_hrJsaDayLabel(date))} | <strong>Supervisor:</strong> ${escapeHtml(report.supervisorName||'N/A')}</p>
    <p><strong>Job:</strong> ${escapeHtml(report.jobTitle||'N/A')} | <strong>Hours:</strong> ${escapeHtml(String(report.totalHours||'N/A'))} | <strong>Permit:</strong> ${report.permitRequired?'Yes':'No'}</p>
    ${(report.hazards||[]).length?`<p><strong>Hazards:</strong> ${report.hazards.map(h => escapeHtml(String(h))).join(', ')}</p>`:''}
    ${(report.ppeEquipment||[]).length?`<p><strong>PPE:</strong> ${report.ppeEquipment.map(x => escapeHtml(String(x))).join(', ')}</p>`:''}
    ${(report.tasks||[]).length?`<table><tr><th>Task</th><th>Hazards</th><th>Controls</th></tr>${report.tasks.map(t=>`<tr><td>${escapeHtml(t.task||'')}</td><td>${escapeHtml(t.potentialHazards||'')}</td><td>${escapeHtml(t.controlMeasures||'')}</td></tr>`).join('')}</table>`:''}
    ${report.workLayoutPlan?`<p><strong>Work Layout:</strong> ${escapeHtml(report.workLayoutPlan)}</p>`:''}
    ${report.musterPoint?`<p><strong>Muster Point:</strong> ${escapeHtml(report.musterPoint)}</p>`:''}
    <p><strong>Crew (${(report.crewAcknowledgments||[]).length}):</strong></p>
    ${(report.crewAcknowledgments||[]).map(a=>`<div class="crew">${a.signature?'[Signed]':'[ ]'} ${escapeHtml(a.name)}</div>`).join('')}
    <script>window.print();</script></body></html>`);
  w.document.close();
}

// --- Sign-In date range filter ---
function filterSignInsByRange() {
  renderSafetySignInList();
}

function clearSignInDateRange() {
  const s = document.getElementById('hr-signin-date-start');
  const e = document.getElementById('hr-signin-date-end');
  if (s) s.value = '';
  if (e) e.value = '';
  renderSafetySignInList();
}

// TASK-1578 — reset the safetyEquipment subscribe/loaded flags whenever the
// shared HRState.unsubscribers array is flushed. Every HR teardown runs the
// unsubs and empties the array, but only the candidate listeners ever reset
// their guard (TASK-635); `safetyEquipmentSubscribed` stayed true forever, so
// after HR → Safety → back to landing the listener was dead AND re-subscribe was
// blocked. That was invisible while only the Safety view read these records
// (last-known values still painted); the person-card section would have shown a
// silently frozen list with a money total on it. Call this everywhere the
// unsubscribers are flushed.
function _teardownSafetyEquipmentListener() {
  HRState.safetyEquipmentSubscribed = false;
  HRState.safetyEquipmentLoaded = false;
  HRState.safetyEquipmentDenied = false;
  HRState.safetyEquipmentReadFailed = false; // TASK-9452
}

// Seed the default PPE catalogue into a tenant that has NO PPE types at all.
// TASK-9883 — this used to trust the CLIENT's list: Issue Equipment opened
// before the types listener delivered saw [] and wrote a second set of nine.
// Idempotence now lives at the data, not in a client flag:
//   1. ask the SERVER (not the persistent cache) whether ANY type doc exists —
//      a tenant with types under old random ids is left alone;
//   2. write the nine under fixed ids in one transaction that only creates the
//      ids that are missing — two tabs racing past step 1 converge on the same
//      nine docs (the loser's transaction retries and finds them).
// The session flag only stops a re-check on every snapshot (cleared on an
// error so a reconnect retries). Resolves true when it wrote anything.
// Offline / denied → writes nothing (never seed blind).
async function _seedDefaultEquipmentTypes(tenantId) {
  if (!tenantId || HRState._safetyTypesSeeded) return false;
  HRState._safetyTypesSeeded = true;
  try {
    const typesCol = collection(db, 'tenants', tenantId, 'safetyEquipmentTypes');
    const existing = await getDocsFromServer(query(typesCol, limit(1)));
    if (!existing.empty) return false;
    const wrote = await runTransaction(db, async (tx) => {
      const refs = DEFAULT_SAFETY_EQUIPMENT_TYPES.map(t => doc(typesCol, t.id));
      const snaps = [];
      for (const r of refs) snaps.push(await tx.get(r)); // all reads before any write
      let n = 0;
      snaps.forEach((snap, i) => {
        if (snap.exists()) return;
        const t = DEFAULT_SAFETY_EQUIPMENT_TYPES[i];
        tx.set(refs[i], { name: t.name, hasSize: t.hasSize, active: true, createdAt: new Date() });
        n++;
      });
      return n;
    });
    if (wrote) console.log(`[HR] Seeded ${wrote} default safety equipment types`);
    return wrote > 0;
  } catch (e) {
    // TASK-9883 COD — offline / transient failure wrote nothing: let the next
    // snapshot or Issue open retry instead of switching seeding off until reload.
    HRState._safetyTypesSeeded = false;
    console.error('[HR] Error seeding equipment types:', e);
    return false;
  }
}

// --- Subscription ---
function subscribeToSafetyEquipment(tenantId) {
  if (!tenantId) return;

  // Subscribe to equipment types
  const typesQuery = query(
    collection(db, 'tenants', tenantId, 'safetyEquipmentTypes'),
    orderBy('name', 'asc')
  );
  const unsubTypes = onSnapshot(typesQuery, async (snapshot) => {
    HRState.safetyEquipmentTypes = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    // Auto-seed defaults if collection is empty.
    // TASK-1578 — scoped to the SAFETY view. This listener now also starts from
    // openEmployeeDetail (person-card Issued Equipment), and merely LOOKING at a
    // person card must not write 9 PPE-type docs into a tenant that has never
    // used Safety. The Add Item modal seeds on demand instead (an explicit
    // "issue equipment" act), via the same _safetyTypesSeeded-guarded helper.
    // TASK-9883 — a cache-only empty snapshot is not "this tenant has none";
    // the helper asks the server before writing anything.
    if (HRState.currentView === 'safety' && HRState.safetyEquipmentTypes.length === 0 && !HRState._safetyTypesSeeded) {
      const seeded = await _seedDefaultEquipmentTypes(tenantId);
      if (seeded) return; // snapshot will re-fire with seeded data
    }
    if (HRState.currentView === 'safety') renderSafetyEquipmentView();
    // TASK-9883 — Issue Equipment can open before the types land; repaint its
    // tiles from the fresh list (no-op when the modal isn't open).
    const _presetGrid = document.getElementById('issue-equip-presets');
    if (_presetGrid) _presetGrid.innerHTML = _issueEquipPresetTilesHTML();
  }, (error) => {
    console.error('[HR] Error subscribing to safetyEquipmentTypes:', error);
  });
  HRState.unsubscribers.push(unsubTypes);

  // Subscribe to equipment records (issued to people)
  const equipQuery = query(
    collection(db, 'tenants', tenantId, 'safetyEquipment'),
    orderBy('createdAt', 'desc')
  );
  const unsubEquip = onSnapshot(equipQuery, (snapshot) => {
    HRState.safetyEquipmentRecords = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    // TASK-1578 — `subscribed` flips synchronously below, so it can't tell
    // "listener armed" from "data arrived". This one does (TASK-368 first-paint
    // lie: an empty state rendered before the first snapshot reads as "nothing
    // issued" when the truth is "not loaded yet").
    HRState.safetyEquipmentLoaded = true;
    HRState.safetyEquipmentDenied = false; // a real read landed — clear any prior denial
    HRState.safetyEquipmentReadFailed = false; // TASK-9452
    if (HRState.currentView === 'safety') renderSafetyEquipmentView();
    // Repaint the person-card section if one is open. No-ops when the container
    // isn't in the DOM, so this costs nothing outside the person card.
    _hrRefreshIssuedEquipmentSection();
    // TASK-1578 Slice 4 — same for the separation checkout, which is usually the
    // surface that ARMED this listener (the disciplinary modal can be opened
    // without ever touching a person card or the Safety view).
    _hrRefreshTerminationPropertyChecklist();
    _refreshIssueEquipSerialPicker(); // TASK-9728 — "Out with …" on the serial tiles
  }, (error) => {
    console.log('[HR] No safetyEquipment collection yet, that is okay');
    HRState.safetyEquipmentRecords = [];
    HRState.safetyEquipmentLoaded = true;
    // TASK-1578 — an empty list under an "outstanding replacement value" total is
    // an authoritative claim about an employee. A REFUSED read must not be able
    // to make it, so keep the two apart.
    HRState.safetyEquipmentDenied = error?.code === 'permission-denied';
    // TASK-9452 — ANY failed read (not only a denial) leaves an empty list that
    // the PPE Reports tab must never print as "0 issued" (COD round 2).
    HRState.safetyEquipmentReadFailed = true;
    if (HRState.currentView === 'safety' && HRState.safetyManagerTab === 'reports') renderSafetyEquipmentView();
    _hrRefreshIssuedEquipmentSection();
    // A refused read falls the checkout back to the five static checkboxes
    // rather than showing an empty live list (which would read as "holds nothing").
    _hrRefreshTerminationPropertyChecklist();
    _refreshIssueEquipSerialPicker(); // TASK-9728
  });
  HRState.unsubscribers.push(unsubEquip);

  // TASK-9720 — stock counts. The Roster items the PPE ledger keeps (one per
  // tracked type). Replaces the retired safetyInventory listener (0 rows on
  // every tenant). Equality-only query → served by the automatic index.
  const stockQuery = query(collection(db, 'tenants', tenantId, 'assets'), where('stockTracked', '==', true));
  const unsubStock = onSnapshot(stockQuery, (snapshot) => {
    HRState.stockAssets = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    HRState.stockAssetsLoaded = true;
    if (HRState.currentView === 'safety') renderSafetyEquipmentView();
  }, (error) => {
    console.warn('[HR] stock-tracked items could not be read:', error?.code || error?.message);
    HRState.stockAssets = [];
    HRState.stockAssetsLoaded = true;
  });
  HRState.unsubscribers.push(unsubStock);

  // TASK-9728 (S6) — serialized Safety Gear (gas monitors): the individual
  // Roster units Issue Equipment can pick by serial. Equality-only query on one
  // field → the automatic index; quantity items are filtered out here.
  const gearQuery = query(collection(db, 'tenants', tenantId, 'assets'), where('category', '==', 'safety_gear'));
  const unsubGear = onSnapshot(gearQuery, (snapshot) => {
    HRState.serialGearAssets = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(a => a.trackingMode === 'individual');
    _refreshIssueEquipSerialPicker();
  }, (error) => {
    console.warn('[HR] serialized safety gear could not be read:', error?.code || error?.message);
    HRState.serialGearAssets = [];
    _refreshIssueEquipSerialPicker();
  });
  HRState.unsubscribers.push(unsubGear);

  HRState.safetyEquipmentSubscribed = true;
}

// --- Mode/Tab Switching ---
function switchSafetyMode(mode) {
  HRState.safetyMode = mode;
  HRState._safetySearch = ''; // TASK-840 — reset serial search on mode switch
  const userBtn = document.getElementById('safety-mode-user');
  const mgrBtn = document.getElementById('safety-mode-manager');
  if (userBtn && mgrBtn) {
    if (mode === 'user') {
      userBtn.style.background = 'var(--action-primary)';
      userBtn.style.color = '#fff';
      userBtn.classList.remove('btn-secondary');
      mgrBtn.style.background = '';
      mgrBtn.style.color = '';
      mgrBtn.classList.add('btn-secondary');
    } else {
      mgrBtn.style.background = 'var(--action-primary)';
      mgrBtn.style.color = '#fff';
      mgrBtn.classList.remove('btn-secondary');
      userBtn.style.background = '';
      userBtn.style.color = '';
      userBtn.classList.add('btn-secondary');
    }
  }
  renderSafetyEquipmentView();
}

function switchSafetyManagerTab(tab) {
  HRState.safetyManagerTab = tab;
  document.querySelectorAll('.safety-mgr-tab').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.tab === tab);
    if (btn.dataset.tab === tab) {
      btn.style.background = 'var(--brand-blue)';
      btn.style.color = '#fff';
    } else {
      btn.style.background = 'var(--bg-secondary)';
      btn.style.color = 'var(--text-primary)';
    }
  });
  renderSafetyEquipmentView();
}

// --- Render Functions ---
function renderSafetyEquipmentView() {
  const container = document.getElementById('safety-equipment-content');
  if (!container) return;

  // TASK-1612 — the Log-Equipment form is a DRAFT the user is mid-way through, and
  // both innerHTML writes below throw away every <select>/<input> in it. So ANY
  // re-render (equipment-type change, one of the three live safetyEquipment/type/
  // inventory snapshots, or a Manage-tab round trip) silently reset "Employee" back
  // to "Select employee...". Capture BEFORE the mode branch, not inside the user
  // arm: a switch to manager mode also destroys the form, and skipping the capture
  // there left _safetyDraft holding the empty snapshot taken when the form was
  // first built — so returning to the tab restored blanks. (Caught by the
  // manager->user leg of tests/_task1612-ppe-employee-select.spec.js; the
  // "form not on screen" case is already handled inside _captureSafetyDraft.)
  _captureSafetyDraft();

  if (HRState.safetyMode === 'user') {
    container.innerHTML = getUserModeHTML() + getSafetySearchHTML();
    _restoreSafetyDraft();
    // TASK-840 — a live safetyEquipment/type/inventory snapshot can fire while the
    // user is mid-type and rebuild this panel (incl. the search <input>), stealing
    // focus. The query already survives (value is restored from _safetySearch); also
    // restore focus + caret-to-end so an in-flight snapshot doesn't interrupt typing.
    if (HRState._safetySearch) {
      const inp = document.getElementById('safety-serial-search');
      if (inp) {
        const v = inp.value;
        inp.focus();
        try { inp.setSelectionRange(v.length, v.length); } catch (_) {}
      }
    }
  } else {
    container.innerHTML = getManagerModeHTML();
  }
}

// TASK-1612 — draft preservation for the Log Equipment Issued form.
// `_safetyDraft` is a plain snapshot of the five user-entered fields. It is NOT a
// second source of truth: submitSafetyEquipment() still reads the live DOM, and
// the draft is dropped on a successful log so the form comes back clean.
// Set for exactly one render to mean "this render is a deliberate form RESET —
// do not carry the old values forward." A flag beats clearing each input by hand
// in submitSafetyEquipment(): a field added to the form later is covered
// automatically instead of silently reappearing post-submit (COD, Codex lane).
let _suppressSafetyDraftCapture = false;

function _captureSafetyDraft() {
  if (_suppressSafetyDraftCapture) { HRState._safetyDraft = null; return; }
  const person = document.getElementById('safety-person-select');
  if (!person) return; // user-mode form isn't rendered — nothing to preserve
  HRState._safetyDraft = {
    personId: person.value || '',
    typeId: document.getElementById('safety-type-select')?.value || '',
    size: document.getElementById('safety-size-select')?.value || '',
    date: document.getElementById('safety-date')?.value || '',
    qty: document.getElementById('safety-qty')?.value || '',
    notes: document.getElementById('safety-notes')?.value || ''
  };
}

function _restoreSafetyDraft() {
  const d = HRState._safetyDraft;
  if (!d) return;
  // Assign only when the option/element still exists — a person deactivated or a
  // type deleted by the snapshot that triggered this render must not leave the
  // select holding a stale value that submit would then write.
  const set = (id, val) => {
    if (!val) return;
    const el = document.getElementById(id);
    if (!el) return;
    if (el.tagName === 'SELECT' && !Array.from(el.options).some(o => o.value === val)) return;
    el.value = val;
  };
  set('safety-person-select', d.personId);
  set('safety-type-select', d.typeId);
  set('safety-size-select', d.size);
  set('safety-date', d.date);
  set('safety-qty', d.qty);
  set('safety-notes', d.notes);
}

// TASK-840 — PPE / gas-monitor serial-number search (user-mode).
// Serials live in the free-text `notes` field of safetyEquipment records (and may
// also land in acknowledgment.notes via driver sign-off). Pure client-side filter
// over the already-live-cached HRState.safetyEquipmentRecords — no new read/index/rule.
function getSafetySearchHTML() {
  const q = HRState._safetySearch || '';
  return `
    <div style="margin-top: var(--spacing-md);">
      <div class="form-group" style="margin-bottom: var(--spacing-sm);">
        <input type="text" id="safety-serial-search" class="form-control"
          placeholder="Search serial # / notes / person..."
          value="${escapeHtml(q)}"
          oninput="window.HRModule.onSafetySearchInput(this.value)"
          style="font-size: 0.85rem;">
      </div>
      <div id="safety-search-results">${getSafetySearchResultsHTML()}</div>
    </div>
  `;
}

// Update only the results container on each keystroke so the input keeps focus
// (full renderSafetyEquipmentView() would rebuild the <input> and steal focus).
function onSafetySearchInput(value) {
  HRState._safetySearch = value || '';
  const el = document.getElementById('safety-search-results');
  if (el) el.innerHTML = getSafetySearchResultsHTML();
}

function getSafetySearchResultsHTML() {
  const q = (HRState._safetySearch || '').trim().toLowerCase();
  // Empty query → default "Recent Log" (latest 10).
  if (!q) return getRecentEquipmentLogHTML();

  const matches = (HRState.safetyEquipmentRecords || []).filter(rec => {
    if (rec.voidedAt) return false; // TASK-9719 — a voided entry was never handed out
    // TASK-1578 — `serialNumber` is now a real field. Without it here, a serial
    // typed into the structured field would be invisible to the search built to
    // find serials (they used to live only in free-text `notes`).
    // 🔴 The SERVER twin `searchSafetyEquipmentSerial` (functions/src/index.js)
    // searches the same four legacy fields and needs the same addition.
    const fields = [rec.notes, rec.personName, rec.equipmentType, rec.serialNumber, rec.acknowledgment && rec.acknowledgment.notes];
    return fields.some(f => f != null && String(f).toLowerCase().includes(q));
  });

  if (matches.length === 0) {
    return `
      <div style="padding: var(--spacing-md); text-align:center; color: var(--text-tertiary); font-size: 0.85rem;">
        No equipment records match "${escapeHtml(HRState._safetySearch.trim())}".
      </div>`;
  }

  let html = `
    <div>
      <h3 style="margin: 0 0 var(--spacing-sm) 0; font-size: 0.95rem;">${matches.length} match${matches.length !== 1 ? 'es' : ''}</h3>
      <div style="display: grid; gap: var(--spacing-xs);">
  `;

  // Show ALL matches (not capped at 10) so an old serial isn't hidden past the window.
  matches.forEach(rec => {
    const dateStr = rec.dateReceived || '';
    const note = rec.notes ? String(rec.notes) : '';
    const ackNote = (rec.acknowledgment && rec.acknowledgment.notes) ? String(rec.acknowledgment.notes) : '';
    html += `
      <div onclick="window.HRModule.viewPpeDetail('${rec.id}')" title="View full record" style="display: flex; align-items: flex-start; gap: var(--spacing-sm); padding: var(--spacing-xs) var(--spacing-sm); background: var(--bg-secondary); border-radius: var(--radius-sm); font-size: 0.85rem; cursor: pointer;">
        <span class="material-icons" style="font-size: 16px; color: var(--text-tertiary); margin-top: 2px;">shield</span>
        <div style="flex:1;min-width:0;">
          <div><strong>${escapeHtml(rec.personName || '')}</strong> — ${escapeHtml(rec.equipmentType || '')}${rec.size ? ' (' + escapeHtml(rec.size) + ')' : ''} x${rec.quantity || 1}</div>
          ${note ? `<div style="color: var(--text-secondary); font-size: 0.78rem; margin-top: 2px; word-break: break-word;">${escapeHtml(note)}</div>` : ''}
          ${ackNote ? `<div style="color: var(--text-secondary); font-size: 0.78rem; margin-top: 2px; word-break: break-word;"><em>Sign-off:</em> ${escapeHtml(ackNote)}</div>` : ''}
        </div>
        <span style="color: var(--text-secondary); font-size: 0.75rem; white-space: nowrap; margin-top: 2px;">${escapeHtml(dateStr)}</span>
      </div>
    `;
  });

  html += '</div></div>';
  return html;
}

function getUserModeHTML() {
  const people = (HRState.people || []).filter(p => p.active !== false);
  const types = HRState.safetyEquipmentTypes.filter(t => t.active !== false);
  const selectedType = types.find(t => t.id === HRState._selectedEquipTypeId);

  return `
    <div style="background: var(--bg-secondary); border-radius: var(--radius-md); padding: var(--spacing-md); border: 1px solid var(--border-light);">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
        <h3 style="margin:0;font-size:0.95rem;">Log Equipment Issued</h3>
        <button class="btn btn-sm btn-secondary" onclick="window.HRModule.openManageEquipmentTypes()" title="Manage equipment types" style="font-size:0.75rem;">
          <span class="material-icons" style="font-size:14px;">settings</span> Types
        </button>
      </div>

      <div class="form-group">
        <label for="safety-person-select">Employee</label>
        <select id="safety-person-select" class="form-control" onchange="window.HRModule.refreshSafetyJobContext()">
          <option value="">Select employee...</option>
          ${/* escapeHtml does NOT escape quotes — data-name is an attribute, so it needs escAttr (TASK-1612, COD Codex lane). */''}
          ${people.map(p => `<option value="${window.escAttr(p.id)}" data-name="${window.escAttr(p.name)}">${escapeHtml(p.name)}</option>`).join('')}
        </select>
      </div>

      <div class="form-group">
        <label>Equipment Type</label>
        <div style="display:flex;gap:var(--spacing-xs);align-items:center;">
          <select id="safety-type-select" class="form-control" style="flex:1;" onchange="window.HRModule.onEquipmentTypeSelect()">
            <option value="">Select type...</option>
            ${types.map(t => `<option value="${t.id}" data-has-size="${t.hasSize}">${escapeHtml(t.name)}</option>`).join('')}
          </select>
          <button class="btn btn-sm btn-secondary" onclick="window.HRModule.promptAddEquipmentType()" title="Add new type" style="padding:6px 8px;">
            <span class="material-icons" style="font-size:18px;">add</span>
          </button>
        </div>
        <div id="safety-size-wrap">${getSafetySizeHTML(selectedType)}</div>
      </div>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: var(--spacing-sm);">
        <div class="form-group">
          <label for="safety-date">Date</label>
          <input type="date" id="safety-date" class="form-control" value="${_hrLocalToday()}" onchange="window.HRModule.refreshSafetyJobContext()">
        </div>
        <div class="form-group">
          <label for="safety-qty">Qty</label>
          <input type="number" id="safety-qty" class="form-control" value="1" min="1">
        </div>
      </div>

      ${_safetyJobGroupHTML()}

      <div class="form-group">
        <label for="safety-notes">Notes (optional)</label>
        <input type="text" id="safety-notes" class="form-control" placeholder="e.g., Size exchange, replacement">
      </div>

      <button class="btn btn-primary" onclick="window.HRModule.submitSafetyEquipment()" style="width: 100%;">
        <span class="material-icons">check</span> Log Equipment
      </button>
    </div>
  `;
}

// TASK-1612 — the size dropdown is the ONLY part of the form that depends on the
// selected equipment type, so it lives in a permanent #safety-size-wrap that
// onEquipmentTypeSelect() swaps on its own. That keeps a type change off the
// full-panel re-render path, which is what was resetting the Employee select.
function getSafetySizeHTML(selectedType) {
  if (!selectedType?.hasSize) return '';
  return `
    <div style="margin-top:var(--spacing-xs);">
      <label for="safety-size-select" style="font-size:0.8rem;">Size</label>
      <select id="safety-size-select" class="form-control" style="width:120px;">
        ${EQUIPMENT_SIZES.map(s => `<option value="${s}">${s}</option>`).join('')}
      </select>
    </div>`;
}

function getRecentEquipmentLogHTML() {
  const recent = HRState.safetyEquipmentRecords.slice(0, 10);
  if (recent.length === 0) return '';

  let html = `
    <div style="margin-top: var(--spacing-md);">
      <h3 style="margin: 0 0 var(--spacing-sm) 0; font-size: 0.95rem;">Recent Log</h3>
      <div style="display: grid; gap: var(--spacing-xs);">
  `;

  recent.forEach(rec => {
    const dateStr = rec.dateReceived || '';
    // TASK-881 — whole row is click-to-detail so admin notes (rec.notes) are reachable.
    // stopPropagation on the delete button so deleting doesn't also open the modal.
    const hasNote = !!(rec.notes && String(rec.notes).trim());
    // TASK-9719 — a voided entry stays in the log, crossed out; it never counts.
    const voided = !!rec.voidedAt;
    html += `
      <div onclick="window.HRModule.viewPpeDetail('${rec.id}')" title="View full record" style="display: flex; align-items: center; gap: var(--spacing-sm); padding: var(--spacing-xs) var(--spacing-sm); background: var(--bg-secondary); border-radius: var(--radius-sm); font-size: 0.85rem; cursor: pointer;${voided ? 'opacity:0.72;' : ''}">
        <span class="material-icons" style="font-size: 16px; color: var(--text-tertiary);">${voided ? 'block' : 'shield'}</span>
        <span style="flex:1;${voided ? 'text-decoration:line-through;' : ''}"><strong>${escapeHtml(rec.personName || '')}</strong> — ${escapeHtml(rec.equipmentType || '')}${rec.size ? ' (' + escapeHtml(rec.size) + ')' : ''} x${escapeHtml(String(rec.quantity || 1))}${hasNote ? ' <span class="material-icons" style="font-size:13px;color:var(--text-tertiary);vertical-align:middle;" title="Has a note">sticky_note_2</span>' : ''}</span>
        ${voided ? '<span style="font-size:0.68rem;font-weight:700;color:#b45309;background:#fef3c7;border:1px solid #fde68a;border-radius:999px;padding:1px 7px;">Voided</span>' : ''}
        <span style="color: var(--text-secondary); font-size: 0.75rem;">${escapeHtml(dateStr)}</span>
        ${!voided && _hrCanVoidEquipment() ? `<button type="button" class="btn-icon" onclick="event.stopPropagation(); window.HRModule.voidSafetyEquipmentRecord('${rec.id}')" title="Void this entry (it stays on file)" aria-label="Void this entry" style="min-width:40px;min-height:40px;color:var(--text-secondary);">
          <span class="material-icons" style="font-size:18px;">block</span>
        </button>` : ''}
      </div>
    `;
  });

  html += '</div></div>';
  return html;
}

function getManagerModeHTML() {
  const tab = HRState.safetyManagerTab;
  return `
    <div style="display: flex; flex-wrap: wrap; gap: var(--spacing-xs); margin-bottom: var(--spacing-md);">
      <button class="btn btn-sm safety-mgr-tab" data-tab="people" style="background:${tab === 'people' ? 'var(--brand-blue)' : 'var(--bg-secondary)'};color:${tab === 'people' ? '#fff' : 'var(--text-primary)'};" onclick="window.HRModule.switchSafetyManagerTab('people')">By Person</button>
      <button class="btn btn-sm safety-mgr-tab" data-tab="inventory" style="background:${tab === 'inventory' ? 'var(--brand-blue)' : 'var(--bg-secondary)'};color:${tab === 'inventory' ? '#fff' : 'var(--text-primary)'};" onclick="window.HRModule.switchSafetyManagerTab('inventory')">Inventory</button>
      <button class="btn btn-sm safety-mgr-tab" data-tab="reports" style="background:${tab === 'reports' ? 'var(--brand-blue)' : 'var(--bg-secondary)'};color:${tab === 'reports' ? '#fff' : 'var(--text-primary)'};" onclick="window.HRModule.switchSafetyManagerTab('reports')">Reports</button>
    </div>
    <div id="safety-manager-content">${getManagerTabContent(tab)}</div>
  `;
}

function getManagerTabContent(tab) {
  if (tab === 'people') return getByPersonHTML();
  // TASK-9720 — the old Costs tab read only the retired safetyInventory lots
  // (0 rows anywhere). Supply cost comes back as an owner-only switch (TASK-9724).
  if (tab === 'inventory' || tab === 'costs') return getInventoryHTML();
  if (tab === 'reports') return getPpeReportsHTML(); // TASK-9452
  return '';
}

// TASK-1578 — human label for the optional loan-ledger status. Absent === legacy
// 'issued'. Delegates to the shared predicate so the Safety tab and the person
// card can never disagree about what a row's status IS.
const _PPE_STATUS_LABELS = { issued: 'Issued', returned: 'Returned', unreturned: 'Unreturned', 'damaged-intentional': 'Damaged' };
function _ppeStatusLabel(r) {
  return _PPE_STATUS_LABELS[window.PersonSections.equipmentStatus(r)] || 'Issued';
}

// TASK-437 Slice 4 — render the "Sign-off" cell for a safetyEquipment row.
// Driver-attested rows (admin-issued ack OR driver self-log) show a signed chip
// (green/good, amber/issue) that opens the signature viewer. Admin-issued rows
// with no ack show a muted "Pending" label (non-blocking).
function _renderPpeSignoffCell(r) {
  const ack = r.acknowledgment;
  if (ack) {
    const cond = ack.condition || 'good';
    const good = cond === 'good' && ack.fits !== false;
    const label = cond === 'damaged' ? 'Damaged' : cond === 'wrong_size' ? 'Wrong size' : (ack.fits === false ? 'Fit issue' : 'Signed');
    const color = good ? '#16a34a' : '#b45309';
    const bg = good ? '#dcfce7' : '#fef3c7';
    const border = good ? '#bbf7d0' : '#fde68a';
    // TASK-697 — surface the optional note. The title="" tooltip is a desktop bonus
    // (escapeHtml escapes " so an anonymous-driver note can't break out of the attr —
    // stored-XSS guard), but tooltips are invisible on touch (COD UX ⚠️), so ALSO show
    // a small note glyph in the cell when a note exists → prompts a click into the modal
    // (which renders the full note, escaped).
    const hasNote = !!(ack.notes && String(ack.notes).trim());
    const titleAttr = hasNote ? `View signature — Note: ${escapeHtml(ack.notes)}` : 'View signature';
    return `<button onclick="window.HRModule.viewPpeSignoff('${r.id}')" title="${titleAttr}" style="cursor:pointer;display:inline-flex;align-items:center;gap:3px;font-size:0.7rem;font-weight:600;color:${color};background:${bg};border:1px solid ${border};border-radius:999px;padding:1px 7px;">
      <span class="material-icons" style="font-size:13px;">${good ? 'check_circle' : 'report_problem'}</span> ${label}${hasNote ? ' <span class="material-icons" style="font-size:13px;opacity:0.85;" title="Has a note / serial #">sticky_note_2</span>' : ''}
    </button>`;
  }
  if (r.source === 'driver') {
    // Driver self-log always carries an ack; this branch is a safety net.
    return '<span style="color:var(--text-tertiary);font-size:0.72rem;">self-logged</span>';
  }
  return '<span style="color:var(--text-tertiary);font-size:0.72rem;">Pending</span>';
}

// Signature + condition viewer for an attested PPE row.
function viewPpeSignoff(recordId) {
  const r = (HRState.safetyEquipmentRecords || []).find(x => x.id === recordId);
  if (!r || !r.acknowledgment) { window.showToast?.('No sign-off on file', 'info'); return; }
  const ack = r.acknowledgment;
  const at = ack.at?.toDate?.() || (ack.at ? new Date(ack.at) : null);
  const atStr = at ? at.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  const condLabel = ack.condition === 'damaged' ? 'Damaged' : ack.condition === 'wrong_size' ? 'Wrong size' : 'Good';
  const overlay = document.createElement('div');
  overlay.id = 'ppe-signoff-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:10000;display:flex;align-items:center;justify-content:center;';
  overlay.innerHTML = `
    <div style="background:var(--bg-primary);border-radius:var(--radius-lg);padding:var(--spacing-lg);max-width:420px;width:90%;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
        <h3 style="margin:0;font-size:1rem;">PPE Sign-Off</h3>
        <button class="btn-icon" onclick="document.getElementById('ppe-signoff-overlay')?.remove()" style="font-size:20px;"><span class="material-icons">close</span></button>
      </div>
      <div style="font-size:0.85rem;color:var(--text-primary);margin-bottom:8px;">
        <strong>${escapeHtml(r.equipmentType || 'Equipment')}</strong>${r.size ? ' (' + escapeHtml(r.size) + ')' : ''} ×${r.quantity || 1}
      </div>
      <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:4px;">Signed by ${escapeHtml(ack.name || r.personName || '')}${atStr ? ' · ' + escapeHtml(atStr) : ''}</div>
      <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;">Condition: <strong>${condLabel}</strong> · Fits: <strong>${ack.fits === false ? 'No' : 'Yes'}</strong>${r.source === 'driver' ? ' · <em>self-logged</em>' : ''}</div>
      ${ack.notes ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;background:var(--bg-secondary);border-radius:8px;padding:8px 10px;"><strong>Notes / Serial #:</strong> ${escapeHtml(ack.notes)}</div>` : ''}
      ${ack.signature ? `<img src="${escapeHtml(ack.signature)}" alt="signature" style="width:100%;border:1px solid var(--border-light);border-radius:8px;background:#fff;">` : '<div style="color:var(--text-tertiary);font-size:0.8rem;">No signature image.</div>'}
    </div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
}

// TASK-881 — full PPE record detail viewer. Opens from a Recent-Log / search-result
// row click so the admin note (rec.notes) is visible — it was never surfaced in the
// list. Unlike viewPpeSignoff (which bails when there's no acknowledgment), this
// handles EVERY record: shows person/equipment/size/qty/date/source/notes always,
// and the sign-off section only when an ack exists. Matches the viewPpeSignoff
// centered-overlay pattern (a small modal, not fullscreen); max-height + dvh so a
// long note scrolls inside the viewport on mobile (Pitfall #2). All free-text fields
// are escapeHtml'd (notes / personName / acknowledgment.notes are XSS surfaces).
function viewPpeDetail(recordId, recOverride) {
  // TASK-9722 — the stock History passes the record it read (it may be opened
  // from Roster > Assets, where the HR list is not loaded).
  const r = (recOverride && recOverride.id === recordId) ? recOverride : (HRState.safetyEquipmentRecords || []).find(x => x.id === recordId);
  if (!r) { window.showToast?.('Record not found', 'info'); return; }
  const ack = r.acknowledgment;
  const at = ack ? (ack.at?.toDate?.() || (ack.at ? new Date(ack.at) : null)) : null;
  const atStr = at ? at.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  const condLabel = ack ? (ack.condition === 'damaged' ? 'Damaged' : ack.condition === 'wrong_size' ? 'Wrong size' : 'Good') : '';
  const sourceLabel = r.source === 'driver' ? 'Driver self-logged' : 'Admin-issued';
  const overlay = document.createElement('div');
  overlay.id = 'ppe-detail-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:10000;display:flex;align-items:center;justify-content:center;padding:var(--spacing-md);';
  overlay.innerHTML = `
    <div style="background:var(--bg-primary);border-radius:var(--radius-lg);padding:var(--spacing-lg);max-width:460px;width:90%;max-height:90vh;max-height:90dvh;overflow-y:auto;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
        <h3 style="margin:0;font-size:1rem;">Equipment Record</h3>
        <button class="btn-icon" onclick="document.getElementById('ppe-detail-overlay')?.remove()" style="font-size:20px;"><span class="material-icons">close</span></button>
      </div>
      ${r.voidedAt ? `<div style="font-size:0.82rem;color:#92400e;background:#fef3c7;border:1px solid #fde68a;border-radius:8px;padding:8px 10px;margin-bottom:10px;"><span class="material-icons" style="font-size:15px;vertical-align:-3px;">block</span> <strong>Voided</strong>${r.voidedByName ? ' by ' + escapeHtml(String(r.voidedByName)) : ''}${r.voidedAt ? ' &middot; ' + escapeHtml(_hrEquipDateLabel(r.voidedAt)) : ''}. Kept on file; not counted anywhere.${r.voidReason ? '<br>Reason: ' + escapeHtml(String(r.voidReason)) : ''}</div>` : ''}
      <div style="font-size:0.9rem;color:var(--text-primary);margin-bottom:6px;"><strong>${escapeHtml(r.personName || 'Unknown')}</strong></div>
      <div style="font-size:0.85rem;color:var(--text-primary);margin-bottom:8px;${r.voidedAt ? 'text-decoration:line-through;' : ''}">
        <strong>${escapeHtml(r.equipmentType || 'Equipment')}</strong>${r.size ? ' (' + escapeHtml(r.size) + ')' : ''} ×${escapeHtml(String(r.quantity || 1))}
      </div>
      <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:4px;">Date received: <strong>${escapeHtml(r.dateReceived || '—')}</strong></div>
      <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;">Source: <strong>${escapeHtml(sourceLabel)}</strong></div>
      <!-- TASK-1578 — the optional loan-ledger fields. Every one is absent on a
           legacy record, in which case its row simply doesn't render. -->
      ${r.serialNumber ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:4px;word-break:break-all;">Serial / ID #: <strong>${escapeHtml(String(r.serialNumber))}</strong></div>` : ''}
      ${Number(r.replacementValue) > 0 ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:4px;">Replacement value: <strong>$${Number(r.replacementValue).toFixed(2)}</strong> each &mdash; replacement cost only; normal wear and tear is never charged.</div>` : ''}
      ${r.status ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;">Status: <strong>${escapeHtml(_ppeStatusLabel(r))}</strong>${r.status === 'returned' && r.returnedAt ? ' &middot; ' + escapeHtml(_hrEquipDateLabel(r.returnedAt)) : ''}${r.status === 'returned' && r.returnedBy ? ' by ' + escapeHtml(String(r.returnedBy)) : ''}${r.status === 'returned' && r.returnedQty != null ? ' &middot; ' + escapeHtml(String(r.returnedQty)) + ' back' + (r.returnCondition === 'damaged' ? ' (damaged)' : '') : ''}</div>` : ''}
      ${r.notes ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;background:var(--bg-secondary);border-radius:8px;padding:8px 10px;word-break:break-word;"><strong>Notes:</strong> ${escapeHtml(r.notes)}</div>` : '<div style="font-size:0.8rem;color:var(--text-tertiary);margin-bottom:10px;">No admin notes.</div>'}
      <div style="border-top:1px solid var(--border-light);margin-top:10px;padding-top:10px;">
        ${ack ? `
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:4px;">Signed by <strong>${escapeHtml(ack.name || r.personName || '')}</strong>${atStr ? ' · ' + escapeHtml(atStr) : ''}</div>
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;">Condition: <strong>${escapeHtml(condLabel)}</strong> · Fits: <strong>${ack.fits === false ? 'No' : 'Yes'}</strong></div>
          ${ack.notes ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:10px;background:var(--bg-secondary);border-radius:8px;padding:8px 10px;word-break:break-word;"><strong>Sign-off note / Serial #:</strong> ${escapeHtml(ack.notes)}</div>` : ''}
          ${ack.signature ? `<img src="${escapeHtml(ack.signature)}" alt="signature" style="width:100%;border:1px solid var(--border-light);border-radius:8px;background:#fff;">` : ''}
        ` : '<div style="font-size:0.8rem;color:var(--text-tertiary);">Awaiting driver sign-off.</div>'}
      </div>
      ${r.returnSignature && /^data:image\/png;base64,/.test(String(r.returnSignature)) ? `
        <div style="border-top:1px solid var(--border-light);margin-top:10px;padding-top:10px;">
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:6px;">Signed for the return${r.returnSignedAt ? ' · ' + escapeHtml(_hrEquipDateLabel(r.returnSignedAt)) : ''}</div>
          <img src="${escapeHtml(r.returnSignature)}" alt="return signature" style="width:100%;border:1px solid var(--border-light);border-radius:8px;background:#fff;">
        </div>` : ''}
      ${!r.voidedAt && _hrCanVoidEquipment() ? `
        <div style="border-top:1px solid var(--border-light);margin-top:12px;padding-top:12px;display:flex;justify-content:flex-end;">
          <button type="button" class="btn btn-secondary" style="min-height:48px;" onclick="window.HRModule.voidSafetyEquipmentRecord('${escJsAttr(r.id)}')"><span class="material-icons" style="font-size:18px;">block</span> Void entry</button>
        </div>` : ''}
    </div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
}

// =============================================================================
// TASK-9452 — PPE Reports (HR → Safety → Safety Equipment → Manage → Reports).
// =============================================================================
// Every number on this tab comes from window.PpeReport.build — the GENERATED
// browser copy of functions/src/shared/ppeReport.js, the SAME function the
// Claude connector (get_ppe_report) and Ask DispatchDeck (getPpeReport) run
// server-side, so the screen and the bots cannot disagree. Nothing is counted
// in this file. Data = HRState.safetyEquipmentRecords (already live-subscribed,
// unbounded); no new read, no rules change.
//
// 🔴 A refused read zeroes the cache (safetyEquipmentDenied), and before the
// first snapshot the list is empty — so the tab, Print and CSV all refuse to
// show a number in either state. A 0 printed on paper reads as fact.
//
// Drill-downs pass a ROW INDEX into the last-built report, never a name or a
// type: those are free text a driver typed, and an onclick attribute is a JS
// string no HTML escaper protects.
const _PPE_RPT_PRESET_LABELS = { last_7_days: 'Last 7 days', this_month: 'This month', last_month: 'Last month', all: 'All dates', custom: 'Custom' };
const _PPE_RPT_VIEWS = { item: 'By Item', employee: 'By Employee', lines: 'Line Detail' };

function _ppeRptState() {
  if (!HRState.ppeReport) HRState.ppeReport = { preset: 'last_7_days', start: '', end: '', personKey: '', typeKey: '', view: 'item' };
  return HRState.ppeReport;
}

// The COMPANY's today, in the tenant's zone — the same resolution order the
// server uses for the bots (_resolveCarrierAssistantTenant: tenant.timezone →
// settings.timezone → Central), so "Last 7 days" on this screen and from Ask
// DispatchDeck / Claude is the same seven days even near midnight or for a
// user travelling out of zone (COD Code Reviewer 2026-10-08).
function _ppeRptTodayKey() {
  const A = window.AppState || {};
  const td = A.tenantData || {};
  const tz = td.timezone || (td.settings && td.settings.timezone) || (A.settings && A.settings.timezone) || 'America/Chicago';
  const fmt = (zone) => {
    const p = {};
    for (const x of new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date())) p[x.type] = x.value;
    return `${p.year}-${p.month}-${p.day}`;
  };
  try { return fmt(tz); } catch (_) { return fmt('America/Chicago'); }
}

/** { start, end, label } for the current filters, or { error }. */
function _ppeRptRange() {
  const st = _ppeRptState();
  const P = window.PpeReport;
  if (st.preset === 'custom') {
    const start = st.start || null;
    const end = st.end || null;
    if (start && !P.isDayKey(start)) return { error: 'Pick a real start date.' };
    if (end && !P.isDayKey(end)) return { error: 'Pick a real end date.' };
    if (start && end && start > end) return { error: 'The start date is after the end date.' };
    return { start, end, label: P.rangeLabel(start, end) };
  }
  return P.presetRange(st.preset, _ppeRptTodayKey());
}

/** The report for the current filters (or { error } / { blocked }). Cached on HRState for drills. */
function _ppeRptBuild() {
  const P = window.PpeReport;
  if (!P || typeof P.build !== 'function') return { blocked: 'The PPE report did not load. Reload the page.' };
  if (HRState.safetyEquipmentDenied === true) return { blocked: "This login can't read PPE records, so there is no report to show." };
  if (HRState.safetyEquipmentReadFailed === true) return { blocked: "PPE records didn't load, so there is no report to show. Reload the page and try again." };
  if (!HRState.safetyEquipmentLoaded) return { blocked: 'Loading PPE records…', loading: true };
  const range = _ppeRptRange();
  if (range.error) return { error: range.error };
  const st = _ppeRptState();
  const rep = P.build(HRState.safetyEquipmentRecords || [], {
    start: range.start, end: range.end,
    personKey: st.personKey || null,
    types: st.typeKey ? [st.typeKey] : null,
  });
  if (!rep.error) HRState._ppeLastReport = rep;
  return rep;
}

/** Dropdown choices: every person and item type on file, regardless of the range. */
function _ppeRptChoices() {
  const P = window.PpeReport;
  const all = P.build(HRState.safetyEquipmentRecords || [], {});
  const people = all.byEmployee.map(p => ({ key: p.personKey, label: p.personName }));
  const items = all.byItem.map(i => ({ key: i.key, label: i.type }));
  return { people, items };
}

function _ppeRptFilterText(rep) {
  const st = _ppeRptState();
  const bits = [];
  if (st.personKey) {
    const p = (rep.byEmployee || [])[0];
    bits.push('Employee: ' + (p ? p.personName : 'none in range'));
  }
  if (st.typeKey) {
    const it = (rep.byItem || [])[0];
    bits.push('Item: ' + (it ? it.type : st.typeKey));
  }
  return bits.join(' · ');
}

function _ppeRptSizesText(sizes) {
  const list = sizes || [];
  if (!list.some(s => s.size)) return '—'; // an item with no sizes at all
  return list.map(s => `${s.size || 'no size'} ${s.issued}`).join(' · ');
}

function getPpeReportsHTML() {
  const st = _ppeRptState();
  const rep = _ppeRptBuild();
  const btn = (active) => `min-height:48px;padding:0 14px;border-radius:999px;font-weight:600;font-size:0.85rem;border:1px solid ${active ? 'var(--brand-blue)' : 'var(--border-color, #cbd5e1)'};background:${active ? 'var(--brand-blue)' : 'var(--bg-primary)'};color:${active ? '#fff' : 'var(--text-primary)'};cursor:pointer;`;
  const presets = ['last_7_days', 'this_month', 'last_month', 'all', 'custom'].map(p =>
    `<button type="button" class="ppe-rpt-preset" data-preset="${p}" style="${btn(st.preset === p)}" onclick="window.HRModule.setPpeReportPreset('${p}')">${_PPE_RPT_PRESET_LABELS[p]}</button>`).join('');

  if (rep.blocked) {
    return `<div class="ppe-rpt" style="display:grid;gap:var(--spacing-sm);">
      <div style="display:flex;flex-wrap:wrap;gap:8px;">${presets}</div>
      <div class="empty-state-small" style="color:var(--text-primary);"><span class="material-icons" style="font-size:24px;margin-bottom:8px;">${rep.loading ? 'hourglass_empty' : 'lock'}</span><div>${escapeHtml(rep.blocked)}</div></div>
    </div>`;
  }

  const ch = _ppeRptChoices();
  const sel = 'min-height:48px;padding:0 10px;border-radius:var(--radius-sm);border:1px solid var(--border-color, #cbd5e1);background:var(--bg-primary);color:var(--text-primary);font-size:0.9rem;width:100%;';
  const personOpts = ['<option value="">All employees</option>'].concat(ch.people.map(p => `<option value="${escapeHtml(p.key)}"${p.key === st.personKey ? ' selected' : ''}>${escapeHtml(p.label)}</option>`)).join('');
  const itemOpts = ['<option value="">All items</option>'].concat(ch.items.map(i => `<option value="${escapeHtml(i.key)}"${i.key === st.typeKey ? ' selected' : ''}>${escapeHtml(i.label)}</option>`)).join('');
  const custom = st.preset === 'custom' ? `
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:8px;">
        <label style="font-size:0.8rem;color:var(--text-primary);font-weight:600;">From<input type="date" id="ppe-rpt-start" value="${escapeHtml(st.start || '')}" style="${sel}" onchange="window.HRModule.setPpeReportDates()"></label>
        <label style="font-size:0.8rem;color:var(--text-primary);font-weight:600;">Through<input type="date" id="ppe-rpt-end" value="${escapeHtml(st.end || '')}" style="${sel}" onchange="window.HRModule.setPpeReportDates()"></label>
      </div>` : '';

  const filters = `
    <div style="display:grid;gap:8px;background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
      <div style="display:flex;flex-wrap:wrap;gap:8px;">${presets}</div>
      ${custom}
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:8px;">
        <label style="font-size:0.8rem;color:var(--text-primary);font-weight:600;">Employee<select style="${sel}" onchange="window.HRModule.setPpeReportFilter('person', this.value)">${personOpts}</select></label>
        <label style="font-size:0.8rem;color:var(--text-primary);font-weight:600;">Item<select style="${sel}" onchange="window.HRModule.setPpeReportFilter('item', this.value)">${itemOpts}</select></label>
      </div>
    </div>`;

  if (rep.error) {
    return `<div class="ppe-rpt" style="display:grid;gap:var(--spacing-sm);">${filters}<div style="color:var(--status-danger, #b91c1c);font-weight:600;">${escapeHtml(rep.error)}</div></div>`;
  }

  const t = rep.totals;
  const tile = (label, n) => `<div style="min-width:0;background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:10px 12px;"><div style="font-size:0.72rem;font-weight:700;letter-spacing:0.4px;text-transform:uppercase;color:var(--text-secondary);">${label}</div><div style="font-size:1.4rem;font-weight:800;color:var(--text-primary);">${n}</div></div>`;
  const filterText = _ppeRptFilterText(rep);
  const head = `
    <div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;">
      <div style="flex:1;min-width:200px;">
        <div style="font-weight:700;font-size:1rem;color:var(--text-primary);">PPE issued · ${escapeHtml(rep.range.label)}</div>
        <div style="font-size:0.78rem;color:var(--text-secondary);">By date received${filterText ? ' · ' + escapeHtml(filterText) : ''}</div>
      </div>
    </div>
    <div style="display:flex;flex-wrap:wrap;gap:8px;">
      ${(st.personKey || st.typeKey) ? `<button type="button" style="${btn(false)}" onclick="window.HRModule.clearPpeReportFilters()"><span class="material-icons" style="font-size:18px;vertical-align:middle;">filter_alt_off</span> Clear filters</button>` : ''}
      <button type="button" style="${btn(false)}" onclick="window.HRModule.printPpeReport()"><span class="material-icons" style="font-size:18px;vertical-align:middle;">print</span> Print</button>
      <button type="button" style="${btn(false)}" onclick="window.HRModule.exportPpeReportCsv()"><span class="material-icons" style="font-size:18px;vertical-align:middle;">download</span> CSV</button>
    </div>
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(78px,1fr));gap:8px;">${tile('Issued', t.issued)}${tile('Returned', t.returned)}${tile('Outstanding', t.outstanding)}${tile('People', t.people)}</div>`;

  const views = Object.entries(_PPE_RPT_VIEWS).map(([v, label]) =>
    `<button type="button" class="ppe-rpt-view" data-view="${v}" style="${btn(st.view === v)}" onclick="window.HRModule.setPpeReportView('${v}')">${label}</button>`).join('');

  let body;
  if (!t.rows) {
    body = `<div class="empty-state-small" style="color:var(--text-primary);"><span class="material-icons" style="font-size:24px;margin-bottom:8px;">shield</span><div>No PPE went out in this range${filterText ? ' for these filters' : ''}.</div></div>`;
  } else if (st.view === 'employee') body = _ppeRptEmployeeTable(rep);
  else if (st.view === 'lines') body = _ppeRptLinesTable(rep);
  else body = _ppeRptItemTable(rep);

  const notes = rep.notes.map(n => `<li>${escapeHtml(n)}</li>`).join('');
  return `<div class="ppe-rpt" style="display:grid;gap:var(--spacing-sm);">
    ${filters}
    ${head}
    <div style="display:flex;flex-wrap:wrap;gap:8px;">${views}</div>
    ${body}
    <ul style="margin:0;padding-left:18px;font-size:0.75rem;color:var(--text-secondary);">${notes}</ul>
  </div>`;
}

const _PPE_RPT_TH = 'padding:8px 6px;text-align:left;font-size:0.75rem;color:var(--text-primary);border-bottom:1px solid var(--border-light);white-space:nowrap;';
const _PPE_RPT_TD = 'padding:8px 6px;font-size:0.85rem;color:var(--text-primary);border-bottom:1px solid var(--border-light);vertical-align:top;';
function _ppeRptTableWrap(head, rows) {
  return `<div style="overflow-x:auto;background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:var(--radius-md);">
    <table style="width:100%;border-collapse:collapse;"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table></div>`;
}
function _ppeRptDrillBtn(kind, i, label) {
  return `<button type="button" onclick="window.HRModule.drillPpeReport('${kind}', ${i})" style="min-height:44px;padding:0 10px;border-radius:var(--radius-sm);border:1px solid var(--border-color, #cbd5e1);background:var(--bg-primary);color:var(--text-primary);font-weight:600;font-size:0.8rem;cursor:pointer;white-space:nowrap;">${label}</button>`;
}

function _ppeRptItemTable(rep) {
  const head = ['Item', 'Issued', 'Returned', 'Out', 'Sizes'].map(h => `<th style="${_PPE_RPT_TH}">${h}</th>`).join('');
  const rows = rep.byItem.map((it, i) => `<tr>
    <td style="${_PPE_RPT_TD}font-weight:600;"><div>${escapeHtml(it.type)}</div><div style="margin-top:6px;">${_ppeRptDrillBtn('item', i, 'See entries')}</div></td>
    <td style="${_PPE_RPT_TD}font-weight:700;">${it.issued}</td>
    <td style="${_PPE_RPT_TD}">${it.returned}</td>
    <td style="${_PPE_RPT_TD}">${it.outstanding}</td>
    <td style="${_PPE_RPT_TD}color:var(--text-secondary);">${escapeHtml(_ppeRptSizesText(it.sizes))}</td>
  </tr>`).join('');
  return _ppeRptTableWrap(head, rows);
}

function _ppeRptEmployeeTable(rep) {
  const head = ['Employee', 'Issued', 'Returned', 'Out', 'What they got'].map(h => `<th style="${_PPE_RPT_TH}">${h}</th>`).join('');
  const rows = rep.byEmployee.map((p, i) => `<tr>
    <td style="${_PPE_RPT_TD}font-weight:600;"><div>${escapeHtml(p.personName)}</div><div style="margin-top:6px;">${_ppeRptDrillBtn('person', i, 'See entries')}</div></td>
    <td style="${_PPE_RPT_TD}font-weight:700;">${p.issued}</td>
    <td style="${_PPE_RPT_TD}">${p.returned}</td>
    <td style="${_PPE_RPT_TD}">${p.outstanding}</td>
    <td style="${_PPE_RPT_TD}color:var(--text-secondary);">${p.items.map(x => escapeHtml(`${x.type}${x.size ? ' (' + x.size + ')' : ''} ×${x.issued}`)).join('<br>')}</td>
  </tr>`).join('');
  return _ppeRptTableWrap(head, rows);
}

function _ppeRptLinesTable(rep) {
  const byId = new Map((HRState.safetyEquipmentRecords || []).map(r => [r.id, r]));
  const head = ['Date', 'Employee', 'Item', 'Size', 'Qty', 'Serial #', 'Source', 'Status', 'Sign-off'].map(h => `<th style="${_PPE_RPT_TH}">${h}</th>`).join('');
  const rows = rep.lines.map(l => {
    const rec = byId.get(l.id);
    return `<tr style="${l.returned ? 'opacity:0.75;' : ''}">
    <td style="${_PPE_RPT_TD}white-space:nowrap;">${escapeHtml(l.date || '—')}</td>
    <td style="${_PPE_RPT_TD}">${escapeHtml(l.personName || 'Unknown')}</td>
    <td style="${_PPE_RPT_TD}">${escapeHtml(l.type)}${l.flags.length ? ' <span title="Quantity was not a positive number; counted as 1" style="font-size:0.7rem;font-weight:700;color:#92400e;background:#fef3c7;border-radius:999px;padding:1px 6px;">check qty</span>' : ''}</td>
    <td style="${_PPE_RPT_TD}">${escapeHtml(l.size || '—')}</td>
    <td style="${_PPE_RPT_TD}font-weight:700;">${l.quantity}</td>
    <td style="${_PPE_RPT_TD}word-break:break-all;">${escapeHtml(l.serialNumber || '—')}</td>
    <td style="${_PPE_RPT_TD}">${l.source === 'driver' ? 'Driver logged' : 'Admin issued'}</td>
    <td style="${_PPE_RPT_TD}">${escapeHtml(_PPE_STATUS_LABELS[l.status] || 'Issued')}</td>
    <td style="${_PPE_RPT_TD}">${rec ? _renderPpeSignoffCell(rec) : ''}</td>
  </tr>`;
  }).join('');
  return _ppeRptTableWrap(head, rows);
}

function _ppeRptRerender() {
  const el = document.getElementById('safety-manager-content');
  if (el && HRState.safetyManagerTab === 'reports') el.innerHTML = getPpeReportsHTML();
  else renderSafetyEquipmentView();
}

function setPpeReportPreset(preset) {
  if (!_PPE_RPT_PRESET_LABELS[preset]) return;
  const st = _ppeRptState();
  if (preset === 'custom' && st.preset !== 'custom') {
    // Start the custom range from whatever was on screen, so it never opens blank.
    const r = window.PpeReport ? window.PpeReport.presetRange(st.preset, _ppeRptTodayKey()) : {};
    st.start = r.start || _ppeRptTodayKey();
    st.end = r.end || _ppeRptTodayKey();
  }
  st.preset = preset;
  _ppeRptRerender();
}

function setPpeReportDates() {
  const st = _ppeRptState();
  st.start = (document.getElementById('ppe-rpt-start') || {}).value || '';
  st.end = (document.getElementById('ppe-rpt-end') || {}).value || '';
  st.preset = 'custom';
  _ppeRptRerender();
}

function setPpeReportFilter(field, value) {
  const st = _ppeRptState();
  const v = String(value || '');
  if (field === 'person') st.personKey = v;
  else if (field === 'item') st.typeKey = v;
  else return;
  _ppeRptRerender();
}

function setPpeReportView(view) {
  if (!_PPE_RPT_VIEWS[view]) return;
  _ppeRptState().view = view;
  _ppeRptRerender();
}

function clearPpeReportFilters() {
  const st = _ppeRptState();
  st.personKey = '';
  st.typeKey = '';
  _ppeRptRerender();
}

// Drill from a By Item / By Employee row to its entries. `i` indexes the last
// report built (what is on screen), so no typed text rides in the onclick.
function drillPpeReport(kind, i) {
  const rep = HRState._ppeLastReport;
  const st = _ppeRptState();
  if (!rep) return;
  if (kind === 'item' && rep.byItem[i]) st.typeKey = rep.byItem[i].key;
  else if (kind === 'person' && rep.byEmployee[i]) st.personKey = rep.byEmployee[i].personKey;
  else return;
  st.view = 'lines';
  _ppeRptRerender();
}

function printPpeReport() {
  const rep = _ppeRptBuild();
  if (rep.blocked || rep.error) { window.showToast?.(rep.blocked || rep.error, 'error'); return; }
  const filterText = _ppeRptFilterText(rep);
  const meta = [
    `<strong>${escapeHtml(rep.range.label)}</strong> · by date received`,
    filterText ? escapeHtml(filterText) : 'All employees · All items',
    `Printed ${escapeHtml(new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }))}`,
  ].join('<br>');
  const t = rep.totals;
  const th = (h) => `<th>${h}</th>`;
  const itemRows = rep.byItem.map(it => `<tr><td>${escapeHtml(it.type)}</td><td>${it.issued}</td><td>${it.returned}</td><td>${it.outstanding}</td><td>${escapeHtml(_ppeRptSizesText(it.sizes))}</td></tr>`).join('');
  const empRows = rep.byEmployee.map(p => `<tr><td>${escapeHtml(p.personName)}</td><td>${p.issued}</td><td>${p.returned}</td><td>${p.outstanding}</td><td>${p.items.map(x => escapeHtml(`${x.type}${x.size ? ' (' + x.size + ')' : ''} ×${x.issued}`)).join('<br>')}</td></tr>`).join('');
  const lineRows = rep.lines.map(l => `<tr><td>${escapeHtml(l.date || '—')}</td><td>${escapeHtml(l.personName || 'Unknown')}</td><td>${escapeHtml(l.type)}</td><td>${escapeHtml(l.size || '—')}</td><td>${l.quantity}</td><td>${escapeHtml(l.serialNumber || '—')}</td><td>${l.source === 'driver' ? 'Driver logged' : 'Admin issued'}</td><td>${escapeHtml(_PPE_STATUS_LABELS[l.status] || 'Issued')}</td><td>${l.signedOff ? 'Signed' : '—'}</td></tr>`).join('');
  const body = `${_hrEquipPrintHeaderHtml('PPE Issued Report', meta)}
    <p><strong>Issued ${t.issued}</strong> · Returned ${t.returned} · Outstanding ${t.outstanding} · ${t.people} ${t.people === 1 ? 'person' : 'people'} · ${t.rows} ${t.rows === 1 ? 'entry' : 'entries'}</p>
    <h2 style="font-size:13px;margin:14px 0 4px;">By item</h2>
    <table><thead><tr>${['Item', 'Issued', 'Returned', 'Outstanding', 'Sizes'].map(th).join('')}</tr></thead><tbody>${itemRows || '<tr><td colspan="5">Nothing went out in this range.</td></tr>'}</tbody></table>
    <h2 style="font-size:13px;margin:14px 0 4px;">By employee</h2>
    <table><thead><tr>${['Employee', 'Issued', 'Returned', 'Outstanding', 'What they got'].map(th).join('')}</tr></thead><tbody>${empRows || '<tr><td colspan="5">Nothing went out in this range.</td></tr>'}</tbody></table>
    <h2 class="page-break" style="font-size:13px;margin:14px 0 4px;">Line detail</h2>
    <table><thead><tr>${['Date', 'Employee', 'Item', 'Size', 'Qty', 'Serial #', 'Source', 'Status', 'Sign-off'].map(th).join('')}</tr></thead><tbody>${lineRows || '<tr><td colspan="9">No entries.</td></tr>'}</tbody></table>
    <div class="footer">${rep.notes.map(n => escapeHtml(n)).join('<br>')}</div>`;
  _hrEquipOpenPrint(`PPE Issued Report - ${rep.range.label}`, body);
}

function exportPpeReportCsv() {
  const rep = _ppeRptBuild();
  if (rep.blocked || rep.error) { window.showToast?.(rep.blocked || rep.error, 'error'); return; }
  if (!rep.lines.length) { window.showToast?.('No PPE entries in this range to export', 'error'); return; }
  const filterText = _ppeRptFilterText(rep);
  const rows = [
    ['PPE issued', rep.range.label, 'by date received', filterText || 'All employees, all items'],
    [],
    ['Date received', 'Employee', 'Item', 'Size', 'Quantity', 'Serial #', 'Source', 'Status', 'Returned', 'Signed off'],
  ].concat(rep.lines.map(l => [l.date || '', l.personName || '', l.type, l.size || '', l.quantity, l.serialNumber || '', l.source === 'driver' ? 'Driver logged' : 'Admin issued', _PPE_STATUS_LABELS[l.status] || 'Issued', l.returned ? 'Yes' : 'No', l.signedOff ? 'Yes' : 'No']))
    .concat([[], ['Totals', '', '', '', rep.totals.issued, '', '', '', rep.totals.returned, ''], ['Outstanding', '', '', '', rep.totals.outstanding]])
    .concat(rep.notes.map(n => [n]));
  const csv = rows.map(r => r.map(csvSafeCell).join(',')).join('\r\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  const tag = (rep.range.start || 'all') + (rep.range.end ? '_' + rep.range.end : '');
  link.download = `ppe-issued-${tag}.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 0);
}

function getByPersonHTML() {
  // Group equipment records by personId
  const byPerson = {};
  HRState.safetyEquipmentRecords.forEach(rec => {
    const key = rec.personId || 'unknown';
    if (!byPerson[key]) byPerson[key] = { name: rec.personName || 'Unknown', records: [], live: 0 };
    byPerson[key].records.push(rec);
    if (!rec.voidedAt) byPerson[key].live++; // TASK-9719 — voided rows show, never count
  });

  const people = Object.entries(byPerson).sort((a, b) => a[1].name.localeCompare(b[1].name));

  if (people.length === 0) {
    return `<div class="empty-state-small"><span class="material-icons" style="font-size:24px;margin-bottom:8px;">shield</span><div>No equipment logged yet</div></div>`;
  }

  let html = '<div style="display: grid; gap: var(--spacing-sm);">';
  people.forEach(([personId, data]) => {
    // TASK-437 Slice 4 — count admin-issued items still waiting for a driver
    // sign-off (non-blocking badge). Pending = NOT driver-self-logged AND no ack.
    // TASK-1578 — a RETURNED item must drop out of the "awaiting sign-off" count.
    // Asking someone to sign for gear they already handed back is noise, and the
    // badge is what drives the chase.
    // 🔴 The DRIVER-side twin of this predicate is jobboard.js ("Waiting for your
    // sign-off"); the two must move together or the driver still sees the item.
    const pendingCount = data.records.filter(r => !r.voidedAt && r.source !== 'driver' && !r.acknowledgment && r.acknowledged !== true && window.PersonSections.equipmentStatus(r) !== 'returned').length;
    const pendingBadge = pendingCount > 0
      ? `<span title="${pendingCount} item${pendingCount !== 1 ? 's' : ''} awaiting driver sign-off" style="display:inline-flex;align-items:center;gap:3px;font-size:0.68rem;font-weight:600;color:#b45309;background:#fef3c7;border:1px solid #fde68a;border-radius:999px;padding:1px 7px;">
           <span class="material-icons" style="font-size:13px;">pending_actions</span> ${pendingCount} pending sign-off
         </span>`
      : '';
    html += `
      <details class="safety-person-accordion" style="background: var(--bg-secondary); border-radius: var(--radius-sm); border: 1px solid var(--border-light);">
        <summary style="padding: var(--spacing-sm) var(--spacing-md); cursor: pointer; font-weight: 600; display: flex; align-items: center; gap: var(--spacing-sm); flex-wrap: wrap;">
          <span class="material-icons" style="font-size: 18px;">person</span>
          ${escapeHtml(data.name)}
          ${pendingBadge}
          <span style="margin-left: auto; font-size: 0.75rem; color: var(--text-secondary);">${data.live} item${data.live !== 1 ? 's' : ''}</span>
        </summary>
        <div style="padding: 0 var(--spacing-md) var(--spacing-sm);">
          <table style="width: 100%; font-size: 0.8rem; border-collapse: collapse;">
            <thead><tr style="border-bottom: 1px solid var(--border-light); text-align: left;">
              <th style="padding: 4px 6px;">Type</th>
              <th style="padding: 4px 6px;">Size</th>
              <th style="padding: 4px 6px;">Qty</th>
              <th style="padding: 4px 6px;">Date</th>
              <th style="padding: 4px 6px;">Sign-off</th>
              <th style="padding: 4px 6px;">Notes</th>
              <th style="padding: 4px 2px;"></th>
            </tr></thead>
            <tbody>
              ${data.records.map(r => r.voidedAt ? `<tr style="border-bottom: 1px solid var(--border-light);opacity:0.72;" title="Voided — kept on file, not counted">
                <td style="padding: 4px 6px;text-decoration:line-through;">${escapeHtml(r.equipmentType || '')} <span style="font-size:0.66rem;font-weight:700;color:#b45309;background:#fef3c7;border-radius:999px;padding:1px 6px;text-decoration:none;display:inline-block;">Voided</span></td>
                <td style="padding: 4px 6px;text-decoration:line-through;">${r.size ? escapeHtml(r.size) : '—'}</td>
                <td style="padding: 4px 6px;text-decoration:line-through;">${escapeHtml(String(r.quantity || 1))}</td>
                <td style="padding: 4px 6px;">${escapeHtml(r.dateReceived || '')}</td>
                <td style="padding: 4px 6px;">${_renderPpeSignoffCell(r)}</td>
                <td style="padding: 4px 6px; color: var(--text-secondary);">${escapeHtml(r.voidReason ? 'Voided: ' + r.voidReason : 'Voided')}</td>
                <td style="padding: 4px 2px;"></td>
              </tr>` : `<tr style="border-bottom: 1px solid var(--border-light);${window.PersonSections.equipmentStatus(r) === 'returned' ? 'opacity:0.62;' : ''}">
                <td style="padding: 4px 6px;">${escapeHtml(r.equipmentType || '')}${window.PersonSections.equipmentStatus(r) !== 'issued' ? ` <span style="font-size:0.66rem;font-weight:600;color:var(--text-secondary);background:var(--bg-elevated);border-radius:999px;padding:1px 6px;">${escapeHtml(_ppeStatusLabel(r))}</span>` : ''}</td>
                <td style="padding: 4px 6px;">${r.size ? escapeHtml(r.size) : '—'}</td>
                <td style="padding: 4px 6px;">${escapeHtml(String(r.quantity || 1))}</td>
                <td style="padding: 4px 6px;">${escapeHtml(r.dateReceived || '')}</td>
                <td style="padding: 4px 6px;">${_renderPpeSignoffCell(r)}</td>
                <td style="padding: 4px 6px; color: var(--text-secondary);">${escapeHtml(r.notes || '')}</td>
                <td style="padding: 4px 2px;">${_hrCanVoidEquipment() ? `<button type="button" class="btn-icon" onclick="window.HRModule.voidSafetyEquipmentRecord('${r.id}')" title="Void this entry (it stays on file)" aria-label="Void this entry" style="min-width:36px;min-height:36px;color:var(--text-secondary);"><span class="material-icons" style="font-size:16px;">block</span></button>` : ''}</td>
              </tr>`).join('')}
            </tbody>
          </table>
        </div>
      </details>
    `;
  });
  html += '</div>';
  return html;
}

// --- Equipment Type Toggle ---
function onEquipmentTypeSelect() {
  const sel = document.getElementById('safety-type-select');
  HRState._selectedEquipTypeId = sel?.value || '';
  // TASK-1612 — show/hide the size dropdown ONLY. The old renderSafetyEquipmentView()
  // here rebuilt the whole panel and reset the Employee select (plus date/qty/notes)
  // every time the user picked an equipment type.
  const wrap = document.getElementById('safety-size-wrap');
  if (wrap) {
    const selectedType = HRState.safetyEquipmentTypes.find(t => t.id === HRState._selectedEquipTypeId);
    wrap.innerHTML = getSafetySizeHTML(selectedType);
  }
}

// TASK-686 — in-app modal replacing the old native prompt()/confirm() pair.
// The old flow trapped users: confirm() Cancel meant "no sizes" AND read as
// "abort," yet still wrote the doc (hasSize:false) — there was no way to answer
// "no, no sizes" and no real abort. This modal (reusing the openManageEquipmentTypes
// overlay pattern) captures the name + an explicit Yes/No "has sizes" choice
// (default No), and Cancel truly aborts (no doc written). hasSize is still
// persisted so the size dropdown in onEquipmentTypeSelect/renderSafetyEquipmentView
// keeps working.
function promptAddEquipmentType() {
  HRState._addEquipTypeHasSize = false;
  const overlay = document.createElement('div');
  overlay.id = 'add-equip-type-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:10001;display:flex;align-items:center;justify-content:center;';
  overlay.innerHTML = `
    <div style="background:var(--bg-primary);border-radius:var(--radius-lg);padding:var(--spacing-lg);max-width:420px;width:90%;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
        <h3 style="margin:0;font-size:1rem;">Add Equipment Type</h3>
        <button class="btn-icon" onclick="document.getElementById('add-equip-type-overlay')?.remove()" style="font-size:20px;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <label style="display:block;font-size:0.8rem;color:var(--text-secondary);margin-bottom:4px;">Equipment type name</label>
      <input id="add-equip-type-name" class="form-control" type="text" placeholder="e.g. Hard Hat, Gloves" style="width:100%;margin-bottom:var(--spacing-md);" autocomplete="off">
      <label style="display:block;font-size:0.8rem;color:var(--text-secondary);margin-bottom:6px;">Does this type have sizes? (XS, S, M, L, XL, etc.)</label>
      <div style="display:flex;gap:var(--spacing-sm);margin-bottom:var(--spacing-lg);">
        <button type="button" id="add-equip-size-no" class="btn btn-sm" onclick="window.HRModule.setAddEquipTypeHasSize(false)" style="flex:1;background:var(--brand-blue);color:#fff;">No</button>
        <button type="button" id="add-equip-size-yes" class="btn btn-sm" onclick="window.HRModule.setAddEquipTypeHasSize(true)" style="flex:1;background:var(--bg-secondary);color:var(--text-primary);">Yes</button>
      </div>
      <div style="display:flex;gap:var(--spacing-sm);">
        <button type="button" class="btn btn-sm btn-secondary" onclick="document.getElementById('add-equip-type-overlay')?.remove()" style="flex:1;">Cancel</button>
        <button type="button" id="add-equip-type-submit" class="btn btn-sm btn-primary" onclick="window.HRModule.submitAddEquipmentType()" style="flex:1;">Add</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  // Cancel on backdrop click — no doc written.
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  const input = document.getElementById('add-equip-type-name');
  input?.focus();
  // Enter submits.
  input?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submitAddEquipmentType(); } });
}

// Toggle the explicit Yes/No "has sizes" control. Default No (set on open).
function setAddEquipTypeHasSize(val) {
  HRState._addEquipTypeHasSize = !!val;
  const noBtn = document.getElementById('add-equip-size-no');
  const yesBtn = document.getElementById('add-equip-size-yes');
  if (noBtn) { noBtn.style.background = val ? 'var(--bg-secondary)' : 'var(--brand-blue)'; noBtn.style.color = val ? 'var(--text-primary)' : '#fff'; }
  if (yesBtn) { yesBtn.style.background = val ? 'var(--brand-blue)' : 'var(--bg-secondary)'; yesBtn.style.color = val ? '#fff' : 'var(--text-primary)'; }
}

function submitAddEquipmentType() {
  const input = document.getElementById('add-equip-type-name');
  const submitBtn = document.getElementById('add-equip-type-submit');
  const name = input?.value || '';
  if (!name.trim()) {
    window.showToast?.('Enter a name', 'error');
    input?.focus();
    return;
  }
  const trimmed = name.trim();
  // Duplicate guard (kept from old flow).
  const exists = HRState.safetyEquipmentTypes.some(t => t.name.toLowerCase() === trimmed.toLowerCase());
  if (exists) {
    window.showToast?.('That equipment type already exists', 'error');
    return;
  }
  // Double-submit guard — a fast double-click could otherwise create dupes.
  if (submitBtn) {
    if (submitBtn.disabled) return;
    submitBtn.disabled = true;
    submitBtn.textContent = 'Adding…';
  }
  const hasSize = !!HRState._addEquipTypeHasSize;
  addDoc(collection(db, 'tenants', HRState.tenantId, 'safetyEquipmentTypes'), {
    name: trimmed,
    hasSize,
    active: true,
    createdAt: new Date()
  }).then(() => {
    document.getElementById('add-equip-type-overlay')?.remove();
    window.showToast?.(`Added "${trimmed}"`, 'success');
  }).catch(err => {
    console.error('[HR] Error adding equipment type:', err);
    window.showToast?.('Error adding type', 'error');
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Add'; }
  });
}

function openManageEquipmentTypes() {
  const types = HRState.safetyEquipmentTypes || [];
  const overlay = document.createElement('div');
  overlay.id = 'safety-types-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;';
  overlay.innerHTML = `
    <div style="background:var(--bg-primary);border-radius:var(--radius-lg);padding:var(--spacing-lg);max-width:420px;width:90%;max-height:70vh;overflow-y:auto;">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
        <h3 style="margin:0;font-size:1rem;">Manage Equipment Types</h3>
        <button class="btn-icon" onclick="document.getElementById('safety-types-overlay')?.remove()" style="font-size:20px;">
          <span class="material-icons">close</span>
        </button>
      </div>
      ${types.length === 0 ? '<p style="color:var(--text-secondary);font-size:0.85rem;">No equipment types yet.</p>' : ''}
      <div style="display:grid;gap:var(--spacing-xs);">
        ${types.map(t => `
          <div style="display:flex;align-items:center;gap:var(--spacing-sm);padding:var(--spacing-xs) var(--spacing-sm);background:var(--bg-secondary);border-radius:var(--radius-sm);">
            <span class="material-icons" style="font-size:18px;color:var(--text-tertiary);">shield</span>
            <span style="flex:1;font-size:0.875rem;">${escapeHtml(t.name)}${t.hasSize ? ' <span style="color:var(--text-secondary);font-size:0.75rem;">(sized)</span>' : ''}${(() => { const a = _hrStockAssetForType(t); if (a) return `<br><span style="font-size:0.72rem;color:var(--text-secondary);">Counting stock: ${_hrStockQtyText(a.quantity)} on hand</span>`; const n = _hrSerialAssetsForType(t).length; /* TASK-9728 */ if (n) return `<br><span style="font-size:0.72rem;color:var(--text-secondary);">Tracked one by one: ${n} unit${n === 1 ? '' : 's'}. Pick the serial when you issue it.</span>`; return t.linkedAssetId ? '<br><span style="font-size:0.72rem;color:var(--text-secondary);">Linked to Roster &rsaquo; Assets (Safety Gear)</span>' : ''; })()}</span>
            ${/* TASK-9720 — one tap to start (or stop) counting this item's stock. */''}
            ${_hrCanIssueEquipment() ? (_hrStockAssetForType(t)
              ? `<button type="button" class="btn btn-sm btn-secondary" style="min-height:40px;font-size:0.75rem;" onclick="window.HRModule.stopTrackingStock('${escJsAttr(t.id)}')">Stop counting</button>`
              : _hrSerialAssetsForType(t).length ? '' : `<button type="button" class="btn btn-sm btn-secondary" style="min-height:40px;font-size:0.75rem;" onclick="document.getElementById('safety-types-overlay')?.remove();window.HRModule.openTrackStockSheet('${escJsAttr(t.id)}')"><span class="material-icons" style="font-size:14px;">inventory_2</span> Track stock</button>`) : ''}
            ${/* TASK-4838 — t.name is a tenant-typed PPE/equipment type name in a JS string
                 literal. escapeHtml's &#39; is decoded back to an apostrophe before the
                 handler compiles, so "Driver's Gloves" made this delete button inert. */''}
            <button class="btn-icon" onclick="window.HRModule.deleteEquipmentType('${t.id}','${escJsAttr(t.name)}')" title="Delete" style="color:var(--text-tertiary);">
              <span class="material-icons" style="font-size:18px;">delete_outline</span>
            </button>
          </div>
        `).join('')}
      </div>
      <button class="btn btn-sm btn-secondary" onclick="window.HRModule.promptAddEquipmentType();document.getElementById('safety-types-overlay')?.remove();" style="margin-top:var(--spacing-md);width:100%;">
        <span class="material-icons" style="font-size:16px;">add</span> Add New Type
      </button>
    </div>
  `;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
}

async function deleteEquipmentType(typeId, typeName) {
  // Check if anyone has received this equipment type
  const records = HRState.safetyEquipmentRecords.filter(r => {
    const typeObj = HRState.safetyEquipmentTypes.find(t => t.id === typeId);
    return typeObj && !r.voidedAt && r.equipmentType === typeObj.name;
  });
  let msg = `Delete "${typeName}"?`;
  if (records.length > 0) {
    msg = `⚠️ "${typeName}" has been issued ${records.length} time${records.length !== 1 ? 's' : ''}. Those records will remain, but this type will no longer appear in the dropdown.\n\nDelete anyway?`;
  }
  if (!confirm(msg)) return;
  try {
    await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'safetyEquipmentTypes', typeId));
    window.showToast?.(`Deleted "${typeName}"`, 'success');
    document.getElementById('safety-types-overlay')?.remove();
  } catch (err) {
    console.error('[HR] Error deleting equipment type:', err);
    window.showToast?.('Error deleting type', 'error');
  }
}

// ============================================
// TASK-9721 — optional Job on the issue screens (PPE inventory S1b)
// ============================================
// The decision table lives in shared/ppe-issue-job.js. Here: when to ask it,
// and where the answer is painted. Rules that must hold:
//   • NEVER blocks a log. A pending, failed or ambiguous lookup = "No job".
//   • A context is keyed to person + date. The submit only uses it when the key
//     still matches what is on screen, so a quick person change can never stamp
//     the previous person's job.
//   • The issuer's own pick (incl. an explicit "No job") wins over the preselect
//     until the person or date changes.

// The PPE issue forms used to default the date with toISOString() (UTC), which
// after ~7 PM Central read as TOMORROW (and stamped tomorrow's date on tonight's
// issue). They now default to the LOCAL day. Any other date is another day,
// where the clock does not count (it says where they are NOW).
function _hrLocalToday() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}
function _ppeJobDayFor(dateStr) {
  const local = _hrLocalToday();
  const isToday = !dateStr || dateStr === local;
  return { isToday, day: isToday ? local : dateStr };
}

let _ppeJobGen = 0;
async function _ppeLoadJobCtxFor(stateKey, personId, dateStr, onDone) {
  const person = (HRState.people || []).find(p => p.id === personId);
  // Keyed to tenant + person + date; `gen` makes a late answer from an older
  // request (modal reopened, tenant switched) unable to land (COD r2).
  const key = `${HRState.tenantId}|${personId}|${dateStr}`;
  const cur = HRState[stateKey];
  if (cur && cur.key === key && (cur.ctx || cur.loading)) { onDone(); return; }
  _ppeJobGen += 1;
  const gen = _ppeJobGen;
  HRState[stateKey] = { key, gen, ctx: null, loading: !!personId, choice: undefined };
  onDone();
  if (!personId) return;
  const { isToday, day } = _ppeJobDayFor(dateStr);
  const M = await _ppeJobModLoad().catch(() => null);
  const ctx = M ? await M.loadPpeIssueJobContext({
    fs: { collection, query, where, getDocs, getDoc, doc, Timestamp },
    db, tenantId: HRState.tenantId, personId, personName: person?.name || '',
    dateStr: day, isToday, withOthers: true,
  }) : { jobId: null, reason: 'none', candidates: [], others: [] };
  const now = HRState[stateKey];
  if (!now || now.key !== key || now.gen !== gen) return; // the person, date, tenant or form moved on
  now.ctx = ctx; now.loading = false;
  onDone();
}

// Paint the picker + hint from HRState[stateKey] into existing elements.
function _ppePaintJobPicker(stateKey, selectId, hintId) {
  const sel = document.getElementById(selectId);
  const hint = document.getElementById(hintId);
  const st = HRState[stateKey];
  if (!sel) return;
  const ctx = st && st.ctx;
  const chosen = st && st.choice !== undefined ? st.choice : (ctx && ctx.jobId) || '';
  sel.innerHTML = _ppeJobOptions(ctx, chosen);
  sel.value = chosen;
  if (sel.value !== chosen) sel.value = '';
  if (hint) hint.textContent = _ppeJobHintText(st);
}

function _ppeJobHintText(st) {
  const ctx = st && st.ctx;
  if (st && st.loading) return 'Looking up their job…';
  if (ctx && !ctx.candidates.length && !ctx.others.length) return 'No jobs scheduled that day.';
  return _ppeJobMod && ctx ? _ppeJobMod.ppeIssueJobHint(ctx) : '';
}

function _ppeJobOptions(ctx, chosen) {
  return _ppeJobMod && ctx ? _ppeJobMod.ppeIssueJobOptionsHtml(ctx, chosen, escapeHtml) : '<option value="">No job</option>';
}

function _ppeJobFieldsFrom(stateKey, personId, dateStr, selectId) {
  const st = HRState[stateKey];
  if (!st || st.key !== `${HRState.tenantId}|${personId}|${dateStr}` || !st.ctx || !_ppeJobMod) return {};
  return _ppeJobMod.ppeIssueJobFields(st.ctx, document.getElementById(selectId)?.value || '');
}

function _safetyJobGroupHTML() {
  const st = HRState._safetyJobCtx;
  const ctx = st && st.ctx;
  const chosen = st && st.choice !== undefined ? st.choice : (ctx && ctx.jobId) || '';
  return `
      <div class="form-group" id="safety-job-group">
        <label for="safety-job-select">Job (optional)</label>
        <select id="safety-job-select" class="form-control" onchange="window.HRModule.onSafetyJobChange()">
          ${_ppeJobOptions(ctx, chosen)}
        </select>
        <div id="safety-job-hint" style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">${escapeHtml(_ppeJobHintText(st))}</div>
      </div>`;
}

function refreshSafetyJobContext() {
  const personId = document.getElementById('safety-person-select')?.value || '';
  const dateStr = document.getElementById('safety-date')?.value || '';
  return _ppeLoadJobCtxFor('_safetyJobCtx', personId, dateStr,
    () => _ppePaintJobPicker('_safetyJobCtx', 'safety-job-select', 'safety-job-hint'));
}

function onSafetyJobChange() {
  const st = HRState._safetyJobCtx;
  if (st) st.choice = document.getElementById('safety-job-select')?.value || '';
}

function refreshIssueEquipJobContext() {
  const personId = HRState._issueEquipPersonId || '';
  const dateStr = document.getElementById('issue-equip-date')?.value || '';
  return _ppeLoadJobCtxFor('_issueEquipJobCtx', personId, dateStr,
    () => _ppePaintJobPicker('_issueEquipJobCtx', 'issue-equip-job', 'issue-equip-job-hint'));
}

function onIssueEquipJobChange() {
  const st = HRState._issueEquipJobCtx;
  if (st) st.choice = document.getElementById('issue-equip-job')?.value || '';
}

// --- CRUD Functions ---
async function submitSafetyEquipment() {
  const personSelect = document.getElementById('safety-person-select');
  const personId = personSelect?.value;
  const personName = personSelect?.selectedOptions[0]?.dataset?.name || '';
  const dateReceived = document.getElementById('safety-date')?.value;
  const quantity = parseInt(document.getElementById('safety-qty')?.value) || 1;
  const notes = document.getElementById('safety-notes')?.value?.trim() || '';
  const typeSelect = document.getElementById('safety-type-select');
  const typeId = typeSelect?.value;

  if (!personId) {
    window.showToast?.('Please select an employee', 'error');
    return;
  }

  if (!typeId) {
    window.showToast?.('Please select an equipment type', 'error');
    return;
  }

  const selectedType = HRState.safetyEquipmentTypes.find(t => t.id === typeId);
  if (!selectedType) {
    window.showToast?.('Invalid equipment type', 'error');
    return;
  }

  const sizeSelect = document.getElementById('safety-size-select');
  const size = (selectedType.hasSize && sizeSelect) ? sizeSelect.value : '';

  // TASK-9721 — optional; {} when no job is picked or the lookup never landed.
  const jobFields = _ppeJobFieldsFrom('_safetyJobCtx', personId, dateReceived || '', 'safety-job-select');

  try {
    await addDoc(collection(db, 'tenants', HRState.tenantId, 'safetyEquipment'), {
      personId,
      personName,
      equipmentType: selectedType.name,
      quantity,
      size,
      notes,
      ...jobFields,
      dateReceived: dateReceived || _hrLocalToday(),
      // TASK-437 — discriminator: admin-issued rows. Driver attestation (Slice 4)
      // sets acknowledgment + acknowledged:true on UPDATE; this row starts unacked
      // so it surfaces in the driver's "waiting for sign-off" list + the admin
      // "pending sign-off" badge. (Optional sign-off — non-blocking.)
      source: 'admin',
      acknowledged: false,
      createdAt: new Date()
    });

    // TASK-9720 — no client stock math here any more. If this item's stock is
    // tracked, the server trigger (onPpeIssueStockWrite) lowers the count from
    // this very create — the same as for Issue Equipment and a driver self-log.

    window.showToast?.(`${escapeHtml(selectedType.name)} logged for ${escapeHtml(personName)}`, 'success');

    // Reset form. TASK-1612 — nulling _safetyDraft alone is not enough:
    // renderSafetyEquipmentView() re-captures from the still-populated DOM on
    // entry, so the just-logged entry would come straight back. Suppress the
    // capture for this one render instead. try/finally so a render throw can't
    // leave the flag stuck on and kill draft preservation for the whole session.
    HRState._safetyDraft = null;
    HRState._selectedEquipTypeId = '';
    HRState._safetyJobCtx = null; // TASK-9721 — the form comes back with no person, so no job
    _suppressSafetyDraftCapture = true;
    try {
      renderSafetyEquipmentView();
    } finally {
      _suppressSafetyDraftCapture = false;
    }
  } catch (error) {
    console.error('[HR] Error logging safety equipment:', error);
    window.showToast?.('Error logging equipment', 'error');
  }
}

// ============================================
// TASK-1578 Slice 1 — PERSON-CARD "ISSUED EQUIPMENT"
// ============================================
// The Safety sub-view answers "what did we hand out this month?"; this answers
// "what is THIS person holding right now, and what does it cost to replace?" —
// the question you actually have open on the last day of employment.
//
// SAME COLLECTION, NO NEW ONE (COD 2026-08-13). `tenants/{tid}/safetyEquipment`
// has been an issue/ack ledger since TASK-437. Slice 1 adds four OPTIONAL fields
// to it — `category`, `serialNumber`, `replacementValue`, `status` (+
// `returnedAt`/`returnedBy`) — and every legacy doc that carries none of them
// keeps rendering as a plain issued row (absent status === 'issued'). NO rules
// delta is needed: create/update already read
// `isTenantAdmin || (hasModule('hr') && !isViewUploadOnly)` with no key
// allowlist, and the driver-ack arm's `hasOnly(['acknowledgment','acknowledged'])`
// mask is untouched — a driver session still cannot write a serial, a value or a
// status.
//
// 🔴 These rows now carry serial numbers and fuel-card identifiers. Never
// console.log a record; everything that reaches an HTML ATTRIBUTE goes through
// window.escAttr (escapeHtml skips quotes).

// Client mirror of the safetyEquipment create/update rule
// (isTenantAdmin || hasModule('hr') && !isViewUploadOnly) — hrCanManageModule()
// is that exact predicate. Fails CLOSED so a view/upload-only HR member sees the
// table read-only instead of tapping into a raw permission denial.
function _hrCanIssueEquipment() {
  return typeof window.hrCanManageModule === 'function' ? window.hrCanManageModule() : true;
}

// The person card can be opened without ever visiting the Safety sub-view, which
// is the only place the safetyEquipment listeners used to start. Bootstrap them
// on first person-card open; the unsub lands in HRState.unsubscribers like every
// other HR listener, so initHR's teardown still owns it (no leak, no dup — the
// subscribed flag guards re-entry).
function _hrEnsureSafetyEquipmentSubscription() {
  if (HRState.safetyEquipmentSubscribed) return;
  if (!HRState.tenantId) return;
  subscribeToSafetyEquipment(HRState.tenantId);
}

// This person's rows. TASK-437 admin-issued and driver self-logged docs both
// carry personId; older GAS-era rows can carry only the name string, so match on
// either (Pitfall #5 dual lookup).
function _hrEquipmentForPerson(emp) {
  if (!emp) return [];
  const id = emp.id;
  const name = String(emp.name || '').trim().toLowerCase();
  return (HRState.safetyEquipmentRecords || []).filter(r => {
    // TASK-9719 — a voided entry was never handed out: it is not property the
    // person holds, owes for, or signs a receipt for.
    if (r.voidedAt) return false;
    if (r.personId) return r.personId === id;
    return !!name && String(r.personName || '').trim().toLowerCase() === name;
  });
}

// Firestore timestamps are never rendered raw — normalise to the HR formatter.
function _hrEquipDateLabel(value) {
  if (!value) return '';
  if (typeof value === 'string') return formatDateShort(value);
  const d = value?.toDate?.() || (value instanceof Date ? value : null);
  return d ? formatDateShort(d.toISOString().slice(0, 10)) : '';
}

// Host wrapper around window.PersonSections.renderIssuedEquipment. The renderer
// RENDERS; this decides who may act (Pitfall #8). data-person-id lets a live
// snapshot refuse to paint one person's serials into another person's card.
function _hrIssuedEquipmentSectionHTML(emp) {
  if (!emp) return '';
  const canManage = _hrCanIssueEquipment();
  const inner = window.PersonSections.renderIssuedEquipment(_hrEquipmentForPerson(emp), {
    canManage,
    // 🔴 The person IS the ownership rule's second argument. Without it the
    // card's "outstanding replacement value" charges the employee for gear that
    // has become their own property, while the printed attachment correctly
    // doesn't — one money question, two answers. (COD 2026-08-14.)
    person: emp,
    // `subscribed` flips synchronously when the listener is armed, so it can't
    // tell "armed" from "data arrived" — safetyEquipmentLoaded can.
    loading: !HRState.safetyEquipmentLoaded,
    denied: HRState.safetyEquipmentDenied === true,
    addOnClick: canManage ? `window.HRModule.openIssueEquipmentModal('${emp.id}')` : null,
    returnOnClick: (r) => `window.HRModule.markEquipmentReturned('${r.id}')`,
    detailOnClick: (r) => `window.HRModule.viewPpeDetail('${r.id}')`,
    formatDate: _hrEquipDateLabel,
    // TASK-1578 Slice 2 — printables. Read-only actions over rows already on
    // screen, so they are NOT canManage-gated (see the renderer's contract note).
    // Firestore doc ids are [A-Za-z0-9_-] so the single-quoted JS literal below
    // is safe; every free-text value is escaped where it is RENDERED, never here.
    issuedInsertOnClick: `window.HRModule.printEquipmentIssuedInsert('${emp.id}')`,
    checkoutInsertOnClick: `window.HRModule.printSeparationCheckoutInsert('${emp.id}')`,
    receiptOnClick: `window.HRModule.printEquipmentIssueReceipt('${emp.id}')`,
    agreementOnClick: `window.HRModule.printCompanyPropertyAgreement('${emp.id}')`,
    rowReceiptOnClick: (r) => `window.HRModule.printEquipmentIssueReceipt('${emp.id}','${r.id}')`,
  });
  return `<div id="hr-person-issued-equipment" data-person-id="${window.escAttr ? window.escAttr(emp.id) : escapeHtml(emp.id)}">${inner}</div>`;
}

// Repaint just the section when a safetyEquipment snapshot lands while the
// person card is open. Scoped to the container so nothing else in the modal is
// rebuilt (no focus theft in the notes composer above it).
function _hrRefreshIssuedEquipmentSection() {
  const host = document.getElementById('hr-person-issued-equipment');
  if (!host) return;
  // The employee-detail modal is CREATED ONCE and reused — closeModal only adds
  // .hidden, so this container survives a close. Don't repaint a closed card.
  if (document.getElementById('employee-detail-modal')?.classList.contains('hidden')) return;
  const personId = host.getAttribute('data-person-id');
  // Re-resolve from HRState.people: a people-snapshot replaces the object
  // HRState.selectedEmployee points at, and the DOM is the only place the
  // currently-open person id is authoritative.
  const emp = (HRState.people || []).find(p => p.id === personId) || HRState.selectedEmployee;
  if (!emp || emp.id !== personId) return;
  host.outerHTML = _hrIssuedEquipmentSectionHTML(emp);
}

// --- Add Item -------------------------------------------------------------
// Tap-first by design (COD UX lane): the preset grid is big buttons off the
// tenant's own safetyEquipmentTypes, so a shop manager issuing gear at the
// counter never types an item name.
function openIssueEquipmentModal(empId) {
  const emp = HRState.people.find(p => p.id === empId);
  if (!emp) return;
  if (!_hrCanIssueEquipment()) {
    window.showToast?.('You do not have permission to issue equipment — ask an owner or admin.', 'error');
    return;
  }
  _hrEnsureSafetyEquipmentSubscription();
  // Seed the default PPE catalogue HERE rather than on every person-card open —
  // issuing equipment is the explicit act that justifies the write. Fire and
  // forget: the types listener repaints the preset grid when it lands.
  // TASK-9883 — an empty CLIENT list often just means "not loaded yet"; the
  // helper checks the server and only ever fills a tenant that has no types.
  if ((HRState.safetyEquipmentTypes || []).length === 0 && !HRState._safetyTypesSeeded) {
    _seedDefaultEquipmentTypes(HRState.tenantId);
  }

  document.getElementById('modal-issue-equipment')?.remove();
  HRState._issueEquipTypeId = '';
  HRState._issueEquipAssetId = ''; // TASK-9728 — the picked serial unit, if any
  HRState._issueEquipPersonId = empId;
  HRState._issueEquipCategoryTouched = false;
  HRState._issueEquipJobCtx = null; // TASK-9721

  const today = _hrLocalToday(); // TASK-9721 — local day, not UTC
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-issue-equipment';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:560px;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
      <div class="modal-header" style="background:linear-gradient(135deg, #2563eb, #1d4ed8);color:#fff;flex-shrink:0;">
        <h3 style="color:#fff;display:flex;align-items:center;gap:8px;">
          <span class="material-icons">inventory_2</span> Issue Equipment
        </h3>
        <button class="btn-icon" onclick="window.HRModule.closeModal('issue-equipment')" style="color:#fff;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:20px;overflow-y:auto;flex:1;min-height:0;">
        <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:14px;">
          Log company property issued to <strong>${escapeHtml(emp.name || '')}</strong>. It stays on their record until it is marked returned.
        </div>

        <div class="form-group">
          <label style="font-size:0.8rem;font-weight:600;">Item *</label>
          <div id="issue-equip-presets" style="display:grid;grid-template-columns:repeat(auto-fill,minmax(132px,1fr));gap:8px;margin-top:6px;">
            ${_issueEquipPresetTilesHTML()}
          </div>
          <div id="issue-equip-other-wrap" style="display:none;margin-top:8px;">
            <input type="text" id="issue-equip-other-name" class="form-control" maxlength="80" placeholder="Item name (e.g. Yard gate key, iPad, Fuel card)">
          </div>
        </div>

        <div id="issue-equip-size-wrap" style="display:none;" class="form-group">
          <label style="font-size:0.8rem;font-weight:600;">Size</label>
          <select id="issue-equip-size" class="form-control" style="max-width:140px;">
            ${EQUIPMENT_SIZES.map(s => `<option value="${s}">${s}</option>`).join('')}
          </select>
        </div>

        ${/* TASK-9728 — serialized Safety Gear: pick WHICH unit (filled by _refreshIssueEquipSerialPicker). */''}
        <div id="issue-equip-serial-pick-wrap" class="form-group" style="display:none;"></div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Category</label>
            <select id="issue-equip-category" class="form-control" onchange="window.HRModule._markIssueEquipCategoryTouched()">
              <option value="ppe">PPE</option>
              <option value="electronics">Electronics</option>
              <option value="keys">Keys</option>
              <option value="fuelcard">Fuel Card</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Quantity</label>
            <input type="number" id="issue-equip-qty" class="form-control" value="1" min="1" step="1">
          </div>
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Serial / ID #</label>
            <input type="text" id="issue-equip-serial" class="form-control" maxlength="80" placeholder="Optional">
          </div>
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Replacement value ($ each)</label>
            <input type="number" id="issue-equip-value" class="form-control" min="0" step="0.01" placeholder="0.00">
          </div>
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Date issued</label>
            <input type="date" id="issue-equip-date" class="form-control" value="${today}" onchange="window.HRModule.refreshIssueEquipJobContext()">
          </div>
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Notes</label>
            <input type="text" id="issue-equip-notes" class="form-control" maxlength="200" placeholder="Optional">
          </div>
        </div>

        <div class="form-group" id="issue-equip-job-group">
          <label for="issue-equip-job" style="font-size:0.8rem;font-weight:600;">Job (optional)</label>
          <select id="issue-equip-job" class="form-control" onchange="window.HRModule.onIssueEquipJobChange()">
            <option value="">No job</option>
          </select>
          <div id="issue-equip-job-hint" style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;"></div>
        </div>

        <div style="background:var(--bg-secondary);border-radius:8px;padding:10px 12px;margin-top:8px;font-size:0.75rem;color:var(--text-primary);line-height:1.45;">
          <strong>Replacement value is replacement cost only</strong> &mdash; never a markup. Normal wear and tear is never charged back to an employee. Employer-paid PPE is required under OSHA 1910.132(h).
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);display:flex;gap:var(--spacing-sm);justify-content:flex-end;flex-shrink:0;">
        <button class="btn btn-secondary" onclick="window.HRModule.closeModal('issue-equipment')">Cancel</button>
        <button class="btn btn-primary" id="btn-issue-equipment" onclick="window.HRModule.submitIssueEquipment()">
          <span class="material-icons">check</span> Issue Item
        </button>
      </div>
    </div>`;
  document.body.appendChild(modal);
  refreshIssueEquipJobContext(); // TASK-9721 — fills the Job picker; never awaited
}

// Preset tiles + a trailing "Other" tile so non-PPE property (keys, fuel cards,
// tablets) can be logged without polluting the PPE type catalogue.
function _issueEquipPresetTilesHTML() {
  const escA = window.escAttr || escapeHtml;
  const types = (HRState.safetyEquipmentTypes || []).filter(t => t.active !== false);
  const selected = HRState._issueEquipTypeId || '';
  const tile = (id, label, isSel) => `
    <button type="button" class="issue-equip-tile" data-type-id="${escA(id)}"
      onclick="window.HRModule.selectIssueEquipPreset('${escJsAttr(id)}')"
      style="display:flex;align-items:center;justify-content:center;text-align:center;gap:4px;min-height:52px;padding:8px 10px;font-size:0.8rem;font-weight:600;line-height:1.2;cursor:pointer;border-radius:10px;border:1px solid ${isSel ? 'var(--brand-blue)' : 'var(--border-light)'};background:${isSel ? 'var(--brand-blue)' : 'var(--bg-secondary)'};color:${isSel ? '#fff' : 'var(--text-primary)'};">
      ${escapeHtml(label)}
    </button>`;
  return types.map(t => tile(t.id, t.name || 'Item', selected === t.id)).join('')
    + tile('__other__', '+ Other', selected === '__other__');
}

// Once the issuer picks a category by hand, the tile-driven nudge stops
// overwriting it.
function _markIssueEquipCategoryTouched() {
  HRState._issueEquipCategoryTouched = true;
}

function selectIssueEquipPreset(typeId) {
  HRState._issueEquipTypeId = typeId || '';
  const grid = document.getElementById('issue-equip-presets');
  if (grid) grid.innerHTML = _issueEquipPresetTilesHTML();

  const otherWrap = document.getElementById('issue-equip-other-wrap');
  const sizeWrap = document.getElementById('issue-equip-size-wrap');
  const isOther = typeId === '__other__';
  if (otherWrap) otherWrap.style.display = isOther ? 'block' : 'none';
  if (isOther) document.getElementById('issue-equip-other-name')?.focus();

  const type = (HRState.safetyEquipmentTypes || []).find(t => t.id === typeId);
  if (sizeWrap) sizeWrap.style.display = (type && type.hasSize) ? 'block' : 'none';

  // TASK-9728 — a new item clears any picked serial, then lists this item's units.
  pickIssueEquipSerial('');

  // Nudge the category to match the tile so the common case needs no thought.
  const catEl = document.getElementById('issue-equip-category');
  if (catEl && !HRState._issueEquipCategoryTouched) catEl.value = isOther ? 'other' : 'ppe';
}

// =============================================================================
// TASK-9728 (S6) — SERIALIZED PPE: pick WHICH gas monitor on Issue Equipment.
// =============================================================================
// Gas monitors and other serial-numbered gear are individual Roster assets in
// Safety Gear (the Roster → PPE link already makes the item type). When the
// picked item has units, the issuer taps the serial; the issue row is stamped
// `assetId` + the unit's serial, quantity 1. Who has the unit now and its
// history are read from those rows (window.PpeReport.serialCustody — the same
// function the server's getSerialPpeCustody runs for the Roster card). Never
// blocks: a unit already out can be issued again (the tile says who has it),
// and a serial that is not listed can still be typed by hand.
function _hrSerialAssetsForType(type) {
  if (!type || !window.PpeReport?.serialAssetsForType) return [];
  return window.PpeReport.serialAssetsForType(HRState.serialGearAssets || [], type);
}

function _hrSerialCustodyLine(assetId) {
  // An empty list is "not loaded" or "refused", never "nobody has it".
  if (!HRState.safetyEquipmentLoaded || HRState.safetyEquipmentReadFailed || !window.PpeReport?.serialCustody) return { text: '', state: '' };
  const c = window.PpeReport.serialCustody(HRState.safetyEquipmentRecords || [], assetId);
  if (c.state === 'out') {
    const h = c.holders[0];
    const since = h.dateReceived ? ' since ' + formatDateShort(h.dateReceived) : '';
    const more = c.holders.length > 1 ? ` (+${c.holders.length - 1} more)` : '';
    return { text: `Out with ${h.personName || 'someone'}${since}${more}`, state: 'out' };
  }
  if (c.state === 'not_returned') return { text: `Not returned by ${(c.last && c.last.personName) || 'last holder'}`, state: 'not_returned' };
  if (c.lastReturnDamaged) return { text: 'Came back damaged. Check it before use', state: 'damaged' };
  // Never issued here (or only before S6): no claim it is on the shelf.
  return c.everIssued ? { text: 'On the shelf', state: 'in' } : { text: 'No sign-out on record', state: 'in' };
}

function _issueEquipSerialPickerHTML() {
  const typeId = HRState._issueEquipTypeId || '';
  if (!typeId || typeId === '__other__') return '';
  const type = (HRState.safetyEquipmentTypes || []).find(t => t.id === typeId);
  const units = _hrSerialAssetsForType(type);
  if (!units.length) return '';
  const sel = HRState._issueEquipAssetId || '';
  const tiles = units.map(a => {
    const isSel = sel === a.id;
    const serial = window.PpeReport.serialLabel(a) || 'No serial';
    const cust = _hrSerialCustodyLine(a.id);
    const custColor = isSel ? '#fff' : (cust.state === 'in' ? '#15803d' : (cust.state ? '#b45309' : 'var(--text-secondary)'));
    return `
    <button type="button" class="issue-equip-serial-tile" data-asset-id="${(window.escAttr || escapeHtml)(a.id)}" aria-pressed="${isSel ? 'true' : 'false'}"
      onclick="window.HRModule.pickIssueEquipSerial('${escJsAttr(a.id)}')"
      style="display:flex;flex-direction:column;align-items:flex-start;justify-content:center;text-align:left;gap:2px;min-height:56px;padding:8px 10px;cursor:pointer;border-radius:10px;border:1px solid ${isSel ? 'var(--brand-blue)' : 'var(--border-light)'};background:${isSel ? 'var(--brand-blue)' : 'var(--bg-secondary)'};color:${isSel ? '#fff' : 'var(--text-primary)'};min-width:0;">
      <span style="font-size:0.85rem;font-weight:700;word-break:break-all;">${escapeHtml(serial)}</span>
      ${cust.text ? `<span style="font-size:0.72rem;font-weight:600;color:${custColor};">${escapeHtml(cust.text)}</span>` : ''}
    </button>`;
  }).join('');
  return `
    <label style="font-size:0.8rem;font-weight:600;">Which one? <span style="font-weight:400;color:var(--text-secondary);">(tap the serial)</span></label>
    <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(150px,1fr));gap:8px;margin-top:6px;">${tiles}</div>
    <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">${sel ? 'Tap it again to un-pick.' : 'Not on the list? Type the serial below.'}</div>`;
}

// Repaint the serial tiles if Issue Equipment is open (types, units or issue
// rows changed). A picked unit that is no longer pickable is let go.
function _refreshIssueEquipSerialPicker() {
  const wrap = document.getElementById('issue-equip-serial-pick-wrap');
  if (!wrap) return;
  const sel = HRState._issueEquipAssetId || '';
  if (sel) {
    const type = (HRState.safetyEquipmentTypes || []).find(t => t.id === HRState._issueEquipTypeId);
    if (!_hrSerialAssetsForType(type).some(a => a.id === sel)) { pickIssueEquipSerial(''); return; }
  }
  const html = _issueEquipSerialPickerHTML();
  wrap.innerHTML = html;
  wrap.style.display = html ? 'block' : 'none';
}

// Pick (or, tapping the picked one again / passing '', un-pick) a serial unit.
// A picked unit fixes quantity at 1 and the serial at the asset's own.
function pickIssueEquipSerial(assetId) {
  const type = (HRState.safetyEquipmentTypes || []).find(t => t.id === HRState._issueEquipTypeId);
  const already = HRState._issueEquipAssetId || '';
  const unit = (assetId && assetId !== already) ? (_hrSerialAssetsForType(type).find(a => a.id === assetId) || null) : null;
  HRState._issueEquipAssetId = unit ? unit.id : '';
  const serialEl = document.getElementById('issue-equip-serial');
  const qtyEl = document.getElementById('issue-equip-qty');
  if (unit) {
    if (serialEl) { serialEl.value = window.PpeReport.serialLabel(unit); serialEl.readOnly = true; }
    if (qtyEl) { qtyEl.value = '1'; qtyEl.readOnly = true; }
  } else {
    if (serialEl && serialEl.readOnly) { serialEl.value = ''; serialEl.readOnly = false; }
    if (qtyEl) qtyEl.readOnly = false;
  }
  const wrap = document.getElementById('issue-equip-serial-pick-wrap');
  if (wrap) {
    const html = _issueEquipSerialPickerHTML();
    wrap.innerHTML = html;
    wrap.style.display = html ? 'block' : 'none';
  }
}

async function submitIssueEquipment() {
  const empId = HRState._issueEquipPersonId;
  const emp = HRState.people.find(p => p.id === empId);
  if (!emp) { window.showToast?.('Employee not found', 'error'); return; }
  if (!_hrCanIssueEquipment()) { window.showToast?.('You do not have permission to issue equipment', 'error'); return; }

  const typeId = HRState._issueEquipTypeId || '';
  if (!typeId) { window.showToast?.('Pick an item', 'error'); return; }

  const type = (HRState.safetyEquipmentTypes || []).find(t => t.id === typeId);
  const otherName = document.getElementById('issue-equip-other-name')?.value?.trim() || '';
  const equipmentType = typeId === '__other__' ? otherName : (type?.name || '');
  if (!equipmentType) { window.showToast?.('Enter the item name', 'error'); return; }

  // TASK-9728 — a picked serial unit is ONE unit, and its serial is the asset's
  // own (re-resolved here; a unit retired since the tap is simply not linked).
  // A serial TYPED by hand that is exactly one unit's serial links that unit too
  // (COD r1: otherwise the unit reads as unissued while a crew has it).
  const _units = typeId !== '__other__' ? _hrSerialAssetsForType(type) : [];
  const _typed = String(document.getElementById('issue-equip-serial')?.value || '').trim().toLowerCase();
  const _typedHits = (!HRState._issueEquipAssetId && _typed) ? _units.filter(a => window.PpeReport.serialLabel(a).toLowerCase() === _typed) : [];
  const serialAsset = HRState._issueEquipAssetId
    ? (_units.find(a => a.id === HRState._issueEquipAssetId) || null)
    : (_typedHits.length === 1 ? _typedHits[0] : null);
  const quantity = serialAsset ? 1 : Math.max(1, parseInt(document.getElementById('issue-equip-qty')?.value, 10) || 1);
  const size = (type?.hasSize) ? (document.getElementById('issue-equip-size')?.value || '') : '';
  const category = document.getElementById('issue-equip-category')?.value || 'other';
  const serialNumber = (serialAsset && window.PpeReport.serialLabel(serialAsset)) || document.getElementById('issue-equip-serial')?.value?.trim() || '';
  const notes = document.getElementById('issue-equip-notes')?.value?.trim() || '';
  const dateReceived = document.getElementById('issue-equip-date')?.value || _hrLocalToday();

  // Replacement value: optional, but a NEGATIVE or non-numeric entry is a typo,
  // not a decision — refuse it rather than storing a number that later drives an
  // "amount owed" figure at separation.
  const rawValue = document.getElementById('issue-equip-value')?.value;
  let replacementValue = null;
  if (rawValue != null && String(rawValue).trim() !== '') {
    replacementValue = Number(rawValue);
    if (!isFinite(replacementValue) || replacementValue < 0) {
      window.showToast?.('Replacement value must be 0 or more', 'error');
      return;
    }
  }

  const btn = document.getElementById('btn-issue-equipment');
  if (btn) { if (btn.disabled) return; btn.disabled = true; btn.textContent = 'Issuing…'; }

  try {
    // Same base shape submitSafetyEquipment writes (source/acknowledged/createdAt
    // drive the driver sign-off flow and the createdAt orderBy on the listener) —
    // the new fields ride ALONGSIDE it, never instead of it. Omit an absent
    // replacementValue entirely rather than writing undefined (Firestore throws,
    // and a swallowed throw is how audit rows silently vanished before).
    const payload = {
      personId: emp.id,
      personName: emp.name || '',
      equipmentType,
      quantity,
      size,
      notes,
      dateReceived,
      category,
      status: 'issued',
      source: 'admin',
      acknowledged: false,
      createdAt: new Date(),
    };
    if (serialNumber) payload.serialNumber = serialNumber;
    if (replacementValue != null) payload.replacementValue = replacementValue;
    if (serialAsset) payload.assetId = serialAsset.id; // TASK-9728 — which Roster unit
    // TASK-9721 — optional job; never blocks (absent = no job).
    Object.assign(payload, _ppeJobFieldsFrom('_issueEquipJobCtx', emp.id, document.getElementById('issue-equip-date')?.value || '', 'issue-equip-job'));

    const ref = await addDoc(collection(db, 'tenants', HRState.tenantId, 'safetyEquipment'), payload);

    // TASK-9720 — stock (if tracked for this type) is lowered by the server
    // trigger from this create; nothing to do here, and nothing ever blocks.

    // Audit the issue. IDs and flags only — never the serial, never the person's
    // name (auditLog is exempt from PII masking by design, so keep it clean).
    window.logAudit?.('hr', 'EQUIPMENT_ISSUED', {
      personId: emp.id,
      recordId: ref.id,
      equipmentType,
      category,
      quantity,
      hasSerial: !!serialNumber,
      assetId: serialAsset ? serialAsset.id : null, // TASK-9728
      replacementValue: replacementValue != null ? replacementValue : null,
    });

    document.getElementById('modal-issue-equipment')?.remove();
    window.showToast?.(`${equipmentType} issued to ${emp.name || 'employee'}`, 'success');
    // The snapshot repaints the section, but paint immediately so the row is
    // there before the round-trip lands.
    _hrRefreshIssuedEquipmentSection();
  } catch (error) {
    console.error('[HR] Error issuing equipment:', error);
    window.showToast?.('Error issuing equipment', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">check</span> Issue Item'; }
  }
}

// --- Mark Returned --------------------------------------------------------
// Returning gear is the normal, expected outcome — 'returned' is what makes an
// item NOT chargeable, so this is the one-tap path and nothing about condition
// is asked here (damage assessment is a separate, deliberate act).
// TASK-9719 — a Return records HOW MANY came back (returnedQty, default all)
// and their condition. A good return goes back on the shelf when the item's
// stock is tracked (the server trigger reads these two fields); a damaged one
// is recorded but not restocked. The worker's signature is not required.
function markEquipmentReturned(recordId) {
  if (!_hrCanIssueEquipment()) { window.showToast?.('You do not have permission to update equipment', 'error'); return; }
  const rec = (HRState.safetyEquipmentRecords || []).find(r => r.id === recordId);
  if (!rec) { window.showToast?.('Record not found', 'error'); return; }
  if (rec.voidedAt) { window.showToast?.('That entry is voided', 'info'); return; }
  const qty = Math.max(1, parseInt(rec.quantity, 10) || 1);
  const label = rec.equipmentType || 'this item';
  _hrTrackStockOverlay('ppe-return-overlay', `
    <div style="text-align:center;">
      <span class="material-icons" style="font-size:34px;color:#15803d;">assignment_return</span>
      <h3 style="margin:6px 0 4px;font-size:1.05rem;color:var(--text-primary);">Mark returned?</h3>
      <div style="font-size:0.86rem;color:var(--text-primary);margin-bottom:10px;">${escapeHtml(label)}${rec.size ? ' (' + escapeHtml(rec.size) + ')' : ''} &middot; ${escapeHtml(rec.personName || 'this employee')}</div>
    </div>
    ${qty > 1 ? `
      <label for="ppe-return-qty" style="display:block;font-size:0.88rem;font-weight:700;color:var(--text-primary);margin-bottom:4px;">How many came back? (of ${qty})</label>
      <input type="number" id="ppe-return-qty" class="form-control" inputmode="numeric" min="1" max="${qty}" step="1" value="${qty}" style="font-size:1.3rem;min-height:52px;">` : `<input type="hidden" id="ppe-return-qty" value="1">`}
    <div style="font-size:0.88rem;font-weight:700;color:var(--text-primary);margin:12px 0 6px;">Condition</div>
    <div style="display:flex;gap:8px;flex-wrap:wrap;">
      <label style="flex:1;min-width:130px;display:flex;align-items:center;gap:8px;border:1px solid var(--border-light);border-radius:10px;padding:10px 12px;min-height:48px;cursor:pointer;"><input type="radio" name="ppe-return-cond" value="good" checked style="width:20px;height:20px;"> Good, back on the shelf</label>
      <label style="flex:1;min-width:130px;display:flex;align-items:center;gap:8px;border:1px solid var(--border-light);border-radius:10px;padding:10px 12px;min-height:48px;cursor:pointer;"><input type="radio" name="ppe-return-cond" value="damaged" style="width:20px;height:20px;"> Damaged / thrown out</label>
    </div>
    <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:10px;">Normal wear and tear is never charged.</div>
    ${/* TASK-9722 — DECISION 6: the worker MAY sign for a hand-back; it is never required. */''}
    <div style="display:flex;justify-content:space-between;align-items:center;margin:12px 0 4px;gap:8px;">
      <label for="ppe-return-sig" style="font-size:0.88rem;font-weight:700;color:var(--text-primary);">Worker signature (optional)</label>
      <button type="button" class="btn btn-secondary btn-sm" style="min-height:44px;" onclick="window.HRModule._clearReturnSignature()">Clear</button>
    </div>
    <canvas id="ppe-return-sig" aria-label="Signature pad (optional)" style="display:block;width:100%;height:120px;border:1px solid var(--border-medium, #94a3b8);border-radius:8px;background:#fff;touch-action:none;cursor:crosshair;"></canvas>
    <div id="ppe-return-error" role="alert" style="min-height:18px;font-size:0.8rem;color:var(--status-danger);margin-top:6px;"></div>
    <div style="display:flex;gap:16px;justify-content:center;margin-top:8px;flex-wrap:wrap;">
      <button type="button" class="btn btn-secondary" style="min-height:48px;min-width:110px;" onclick="document.getElementById('ppe-return-overlay')?.remove()">Cancel</button>
      <button type="button" id="btn-ppe-return" class="btn btn-primary" style="min-height:48px;min-width:150px;" onclick="window.HRModule.submitEquipmentReturn('${escJsAttr(recordId)}')"><span class="material-icons" style="font-size:18px;">check</span> Mark returned</button>
    </div>`);
  HRState._returnSigPad = _hrMiniSignaturePad('ppe-return-sig');
}

function _clearReturnSignature() {
  HRState._returnSigPad?.clear();
}

async function submitEquipmentReturn(recordId) {
  const rec = (HRState.safetyEquipmentRecords || []).find(r => r.id === recordId);
  if (!rec) { window.showToast?.('Record not found', 'error'); return; }
  const qty = Math.max(1, parseInt(rec.quantity, 10) || 1);
  const errEl = document.getElementById('ppe-return-error');
  const raw = String(document.getElementById('ppe-return-qty')?.value || '').trim();
  const back = /^\d+$/.test(raw) ? parseInt(raw, 10) : NaN;
  if (!isFinite(back) || back < 1 || back > qty) {
    if (errEl) errEl.textContent = `Enter a number from 1 to ${qty}.`;
    return;
  }
  const condition = document.querySelector('input[name="ppe-return-cond"]:checked')?.value === 'damaged' ? 'damaged' : 'good';
  const btn = document.getElementById('btn-ppe-return');
  if (btn) { if (btn.disabled) return; btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving…'; }
  // TASK-9722 — optional worker signature for the hand-back (never required).
  const pad = HRState._returnSigPad;
  const sig = pad && pad.hasData() ? pad.toDataURL() : '';
  const upd = {
    status: 'returned',
    returnedAt: new Date(),
    returnedBy: window.AppState?.userName || window.AppState?.user?.email || 'unknown',
    returnedQty: back,
    returnCondition: condition,
  };
  if (sig && sig.length < 300000) { upd.returnSignature = sig; upd.returnSignedAt = new Date(); }
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'safetyEquipment', recordId), upd);
    HRState._returnSigPad = null;
    window.logAudit?.('hr', 'EQUIPMENT_RETURNED', {
      personId: rec.personId || null,
      recordId,
      equipmentType: rec.equipmentType || null,
      previousStatus: window.PersonSections.equipmentStatus(rec),
      returnedQty: back,
      returnCondition: condition,
      workerSigned: !!upd.returnSignature,
    });
    document.getElementById('ppe-return-overlay')?.remove();
    window.showToast?.(back < qty ? `Marked ${back} of ${qty} returned` : 'Marked returned', 'success');
    _hrRefreshIssuedEquipmentSection();
  } catch (error) {
    console.error('[HR] Error marking equipment returned:', error);
    if (errEl) errEl.textContent = 'Could not save just now. Try again.';
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:18px;">check</span> Mark returned'; }
  }
}

// ============================================
// TASK-1578 Slice 2 — PRINTABLES
// ============================================
// FOUR documents, ONE data source: the person's OPEN safetyEquipment rows. What
// the employee signs IS the live list — there is no second, hand-typed copy of
// the inventory to drift from the ledger.
//
// 🔴 THE DOCUMENT RELATIONSHIP — read this before adding a fifth.
//
// Carriers that have been around a while already own lawyer-shaped separation
// paperwork, and the good ones name their system of record inside it ("the record
// of issued items is the DispatchDeck equipment issue log"; "print the employee's
// issued-items list from DispatchDeck and reconcile each line"). Those forms have
// a blank table waiting for our data. So DispatchDeck's job is to FILL IN the
// tenant's paperwork, not to compete with it. The two INSERTS below exist for
// exactly that:
//
//   • Issued Equipment — Attachment   (_hrEquipIssuedInsertHtml → printEquipmentIssuedInsert)
//       item / qty / replacement value + a total. Drops straight into the
//       issued-items table of any company property agreement.
//   • Separation Checkout — Attachment (printSeparationCheckoutInsert)
//       item / returned Y-N / amount owed + a subtotal of unreturned property.
//       Drops straight into the checkout table of any separation form.
//
// They are labelled by WHAT THEY CONTAIN, never by another company's part
// numbering ("Part C" is one carrier's lettering; the next carrier numbers it
// differently and the label would then be a lie).
//
// The two supporting documents:
//   • Equipment Issue Receipt — the day-of custody record. Independent of anyone's
//     agreement; every tenant needs it.
//   • Company Property Agreement (GENERIC) — DEMOTED, deliberately kept. A tenant
//     with no paperwork of its own still needs something to sign, and deleting it
//     would leave that tenant worse off than before Slice 2. It is no longer a
//     peer of the inserts: it is the FALLBACK HOST for the same attachment. Its
//     page 2 IS `_hrEquipIssuedInsertHtml` — the identical builder, not a second
//     table — so the two documents can never disagree about what was issued, and
//     the UI says "use only if your company doesn't already have its own."
//
// 🔴 OWNERSHIP TRANSFER runs through the ONE shipped rule,
// `window.PersonSections.equipmentOwnershipTransferred(record, person)`. Gear that
// has become the employee's property after N days of employment is never
// "unreturned" and is never charged — on screen OR on paper. Do not add a second
// ownership rule here, and do not let a transferred item reach a money column.
//
// 💰 ONE money total in the product — `PersonSections.outstandingEquipmentValue`.
// Every figure on every page below comes out of it. Never sum a column by hand.
//
// 🔴 MULTI-TENANT. Every printable carries the TENANT's name and logo, resolved
// at print time from the tenant doc. Nothing here hardcodes a carrier name.
//
// ⚖️ The agreement copy is deliberately STATE-NEUTRAL and is NOT a wage-deduction
// authorization (COD Domain lane 2026-08-13: wage deductions for unreturned
// property are restricted or prohibited in several states even WITH a signed
// release, and any deduction below minimum wage or out of overtime premium
// violates the FLSA regardless of state). DispatchDeck renders records; it must
// not hand a tenant boilerplate that is illegal where they operate.

// Tenant identity for a printed document. https-only on the logo — the same
// guard the portal header uses (TASK-1422): the field is tenant-writable, and a
// non-https value in an <img src> is a mixed-content/exfil vector.
function _hrEquipPrintBrand() {
  const td = window.AppState?.tenantData || {};
  const name = window.AppState?.companyName || td.companyName || td.name || 'Company';
  const rawLogo = td.companyLogoUrl || td.logoUrl || '';
  const logo = /^https:\/\//i.test(String(rawLogo || '')) ? String(rawLogo) : '';
  return { name, logo };
}

// TASK-9512 — the Safety Roster Matrix (panel title + print) name. Reuses
// _hrEquipPrintBrand (AppState.tenantData); when no name is known it returns ''
// so the title reads "Safety Roster Matrix", never "Company — Safety Roster Matrix".
function _hrSafetyMatrixCompanyName() {
  const td = window.AppState?.tenantData || {};
  const known = window.AppState?.companyName || td.companyName || td.name || '';
  return known ? String(_hrEquipPrintBrand().name || '').trim() : '';
}

function _hrEquipPrintCss() {
  return `
    @page { margin: 0.5in 0.55in; size: letter; }
    body { font-family: Arial, sans-serif; padding: 20px 28px; max-width: 780px; margin: 0 auto; color: #1a1a1a; font-size: 12.5px; line-height: 1.45; }
    .header { text-align: center; margin-bottom: 14px; border-bottom: 2px solid #333; padding-bottom: 10px; }
    .header img.logo { max-height: 56px; max-width: 220px; object-fit: contain; margin-bottom: 6px; }
    .header .company { font-size: 14px; font-weight: 700; color: #333; margin-bottom: 2px; }
    .header h1 { margin: 4px 0 3px; font-size: 17px; text-transform: uppercase; letter-spacing: 1px; }
    .header .meta { font-size: 11px; color: #666; }
    .who { display: flex; gap: 26px; flex-wrap: wrap; margin: 12px 0; }
    .who .item { flex: 1; min-width: 150px; }
    .who .label { font-size: 9.5px; text-transform: uppercase; color: #666; letter-spacing: 0.5px; display: block; }
    .who .value { font-size: 13px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; margin: 10px 0; }
    th, td { padding: 5px 7px; text-align: left; border: 1px solid #999; font-size: 10.5px; vertical-align: top; }
    th { background: #f0f0f0; font-weight: 700; font-size: 9.5px; text-transform: uppercase; }
    td.num, th.num { text-align: right; white-space: nowrap; }
    tfoot td { font-weight: 700; background: #f7f7f7; }
    .terms { margin: 14px 0 6px; }
    .terms h2 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 6px; border-bottom: 1px solid #ddd; padding-bottom: 3px; }
    .terms ol { margin: 0; padding-left: 18px; }
    .terms li { margin-bottom: 7px; font-size: 11.5px; }
    .callout { border: 1px solid #999; background: #f7f7f7; padding: 9px 11px; margin: 10px 0; font-size: 11px; }
    /* Insert (attachment) chrome — see the ATTACHMENT INSERTS block below. */
    .sub { font-size: 9.5px; color: #555; margin-top: 2px; line-height: 1.35; }
    .page-break { page-break-before: always; }
    .attach-note { border: 1px solid #333; background: #f4f4f4; padding: 8px 10px; margin: 6px 0 10px; font-size: 11px; line-height: 1.45; }
    .attach-note .tag { text-transform: uppercase; letter-spacing: 0.5px; font-size: 10.5px; font-weight: 700; }
    .fill { display: inline-block; border-bottom: 1px solid #333; min-width: 92px; height: 11px; }
    td.fillcell { height: 22px; }
    .keeps { border: 1px solid #999; padding: 8px 10px; margin: 10px 0; font-size: 11px; }
    .keeps h3 { margin: 0 0 5px; font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; }
    .keeps ul { margin: 0; padding-left: 16px; }
    .keeps li { margin-bottom: 3px; }
    .initials { margin-top: 20px; display: flex; gap: 30px; font-size: 10.5px; page-break-inside: avoid; }
    .initials div { flex: 1; }
    .yn { text-align: center; letter-spacing: 2px; font-weight: 700; white-space: nowrap; }
    .sig-row { margin-top: 26px; display: flex; gap: 34px; page-break-inside: avoid; }
    .sig-block { flex: 1; }
    .sig-line { border-bottom: 1px solid #333; height: 34px; }
    .sig-name { font-size: 10px; color: #555; margin-top: 4px; }
    .esig { font-family: Georgia, serif; font-style: italic; font-size: 15px; padding-bottom: 2px; }
    .footer { margin-top: 20px; font-size: 9.5px; color: #666; border-top: 1px solid #ddd; padding-top: 7px; line-height: 1.4; }
    @media print { body { padding: 0; } }
  `;
}

function _hrEquipPrintHeaderHtml(heading, metaHtml) {
  const b = _hrEquipPrintBrand();
  const escA = window.escAttr || escapeHtml;
  return `
    <div class="header">
      ${b.logo ? `<img class="logo" src="${escA(b.logo)}" alt="">` : ''}
      <div class="company">${escapeHtml(b.name)}</div>
      <h1>${escapeHtml(heading)}</h1>
      ${metaHtml ? `<div class="meta">${metaHtml}</div>` : ''}
    </div>`;
}

// The house printWindow pattern (printAccidentRegister / printDisciplinaryForm):
// window.open('', '_blank') FIRST (never after an await — mobile Safari blocks
// that silently), popup-block toast, document.write, print().
function _hrEquipOpenPrint(docTitle, bodyHtml) {
  const w = window.open('', '_blank');
  if (!w) { window.showToast?.('Popup blocked. Allow popups for this site to print.', 'error'); return false; }
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(docTitle)}</title><style>${_hrEquipPrintCss()}</style></head><body>${bodyHtml}</body></html>`);
  w.document.close();
  // TASK-4560 — the header carries the TENANT's logo over https (a real network
  // fetch, so this race is wider than the disciplinary form's data: URL). Printing
  // synchronously here drops the tenant's branding off the first copy.
  // No caller reads the return value, so returning a Promise is safe.
  _hrPrintWhenPainted(w);
  return true;
}

const _HR_EQUIP_CAT_PRINT = { ppe: 'PPE', electronics: 'Electronics', keys: 'Keys', fuelcard: 'Fuel Card', other: 'Other' };

function _hrEquipMoneyStr(n) {
  const v = Number(n);
  if (!isFinite(v) || v <= 0) return '';
  return '$' + v.toFixed(2);
}

// 🔴 The ONE ownership rule, asked the ONE way. Every printable below routes
// "is this still company property?" through the shipped predicate rather than
// re-deriving it from `transfersToEmployeeAfterDays`. It fails toward NOT
// charging (unknown hire date ⇒ treated as the employee's).
function _hrEquipTransferred(rec, person) {
  return !!(person && window.PersonSections.equipmentOwnershipTransferred(rec, person));
}

// "DESCRIPTION / SIZE / SERIAL #" in one cell — the shape the issued-items table
// of a signed property agreement expects, so a line transcribes 1:1.
function _hrEquipItemCellHtml(r, extraSub) {
  const bits = [];
  if (r.size) bits.push('Size ' + escapeHtml(String(r.size)));
  if (r.serialNumber) bits.push('Serial ' + escapeHtml(String(r.serialNumber)));
  const cat = _HR_EQUIP_CAT_PRINT[String(r.category || '').trim()];
  if (cat) bits.push(escapeHtml(cat));
  const issued = _hrEquipDateLabel(r.dateReceived);
  if (issued) bits.push('Issued ' + escapeHtml(issued));
  const sub = bits.join(' &middot; ');
  return `<strong>${escapeHtml(r.equipmentType || 'Equipment')}</strong>`
    + (sub ? `<div class="sub">${sub}</div>` : '')
    + (extraSub ? `<div class="sub">${extraSub}</div>` : '');
}

// The item table used by the Issue Receipt. `withAck` adds the sign-off column.
// `person` is what lets a transferred item drop out of the money columns — pass
// it or the receipt will price gear the employee already owns.
function _hrEquipPrintTable(records, opts) {
  opts = opts || {};
  const person = opts.person || null;
  const rows = records.map(r => {
    const qty = Math.max(1, parseInt(r.quantity, 10) || 1);
    const unit = Number(r.replacementValue);
    const transferred = _hrEquipTransferred(r, person);
    const hasVal = isFinite(unit) && unit > 0 && !transferred;
    const ackTxt = r.acknowledgment
      ? `Signed${r.acknowledgment.at ? ' ' + _hrEquipDateLabel(r.acknowledgment.at) : ''}`
      : (r.source === 'driver' ? 'Self-logged' : 'Not signed');
    return `
      <tr>
        <td>${escapeHtml(r.equipmentType || 'Equipment')}${r.size ? ' (' + escapeHtml(r.size) + ')' : ''}${transferred ? '<div class="sub">Employee&rsquo;s own property &mdash; not returned, not charged.</div>' : ''}</td>
        <td>${escapeHtml(_HR_EQUIP_CAT_PRINT[String(r.category || '').trim()] || '—')}</td>
        <td class="num">${qty}</td>
        <td>${r.serialNumber ? escapeHtml(String(r.serialNumber)) : '—'}</td>
        <td>${escapeHtml(_hrEquipDateLabel(r.dateReceived) || String(r.dateReceived || '—'))}</td>
        <td class="num">${hasVal ? escapeHtml(_hrEquipMoneyStr(unit)) : '—'}</td>
        <td class="num">${hasVal ? escapeHtml(_hrEquipMoneyStr(unit * qty)) : '—'}</td>
        ${opts.withAck ? `<td>${escapeHtml(ackTxt)}</td>` : ''}
      </tr>`;
  }).join('');

  // 🔴 ONE total in the product — the same predicate the person card and the
  // separation checkout use. A second summation is a second answer.
  const total = window.PersonSections.outstandingEquipmentValue(records, person);

  return `
    <table>
      <thead>
        <tr>
          <th>Item</th>
          <th>Category</th>
          <th class="num">Qty</th>
          <th>Serial / ID #</th>
          <th>Date issued</th>
          <th class="num">Replacement cost ea.</th>
          <th class="num">Line total</th>
          ${opts.withAck ? '<th>Sign-off</th>' : ''}
        </tr>
      </thead>
      <tbody>${rows}</tbody>
      ${total > 0 ? `<tfoot><tr>
        <td colspan="${opts.withAck ? 7 : 6}" style="text-align:right;">Total replacement cost of items listed</td>
        <td class="num" ${opts.withAck ? 'colspan="1"' : ''}>${escapeHtml(_hrEquipMoneyStr(total))}</td>
      </tr></tfoot>` : ''}
    </table>
    ${total > 0 ? `<div style="font-size:10px;color:#666;margin-top:-4px;">Replacement cost is what it costs the company to replace the item. It is not a price, a rental charge, or a markup, and it is not an amount owed.</div>` : ''}`;
}

// Signature blocks. If an item was already e-signed by the employee in the
// driver portal, say so rather than pretending the paper is the only record.
function _hrEquipSigBlockHtml(employeeName, employeeLine, companyLine) {
  return `
    <div class="sig-row">
      <div class="sig-block">
        <div class="sig-line"></div>
        <div class="sig-name">${escapeHtml(employeeLine || 'Employee signature')} &mdash; ${escapeHtml(employeeName || '')}</div>
      </div>
      <div class="sig-block">
        <div class="sig-line"></div>
        <div class="sig-name">Date</div>
      </div>
    </div>
    <div class="sig-row">
      <div class="sig-block">
        <div class="sig-line"></div>
        <div class="sig-name">${escapeHtml(companyLine || 'Company representative')}</div>
      </div>
      <div class="sig-block">
        <div class="sig-line"></div>
        <div class="sig-name">Date</div>
      </div>
    </div>`;
}

// ==========================================================================
// ATTACHMENT INSERTS — the pages that go INTO the tenant's own signed forms
// ==========================================================================
// Neutral labelling is a hard requirement here. One carrier calls the issued-
// items table "Part C"; the next one letters it differently or doesn't letter it
// at all. So the heading names the CONTENT ("Issued Equipment — Attachment") and
// the note tells the reader what to do with the page. Nothing on these pages
// hardcodes a carrier, a form revision, or a section number.

// The "attach me here" banner. `where` is the plain-English destination.
function _hrEquipAttachNoteHtml(where, employeeName) {
  return `
    <div class="attach-note">
      <span class="tag">Attachment</span> &mdash; attach this page to ${escapeHtml(where)} for
      ${escapeHtml(employeeName || 'this employee')}, or transcribe the lines below into the form&rsquo;s table.
      Both copies must match; where they differ, the equipment issue log is the record.
    </div>`;
}

// Initials rather than full signatures: an attachment is part of a packet that
// already carries a signature block. Two documents each demanding a full
// signature is how a separator ends up signing the wrong one.
function _hrEquipInitialsRowHtml() {
  return `
    <div class="initials">
      <div>Employee initials <span class="fill"></span></div>
      <div>Company representative initials <span class="fill"></span></div>
      <div>Date <span class="fill"></span></div>
    </div>`;
}

// --- Issued Equipment insert ---------------------------------------------
// Column shape: ISSUED ITEM (description / size / serial #) | QTY | REPLACEMENT
// VALUE, closed by "Total Replacement Value of Issued Items". That is the table
// a company property agreement leaves blank for exactly this data.
//
// An EMPTY list is a valid, useful print — a hire-day attachment that says NONE
// is what the agreement's own footnote asks for ("if nothing is issued at hire,
// write NONE"). Do not gate this builder on having rows.
function _hrEquipIssuedInsertHtml(emp, records, opts) {
  opts = opts || {};
  const rows = Array.isArray(records) ? records : [];
  const asOf = opts.asOfLabel || '';

  // 🔴 ONE total. Transferred gear is excluded by the shipped predicate, and the
  // rows below show "—" in the money column for exactly those items, so the
  // printed column always adds up to the printed total.
  const total = window.PersonSections.outstandingEquipmentValue(rows, emp);

  const body = rows.length
    ? rows.map(r => {
        const qty = Math.max(1, parseInt(r.quantity, 10) || 1);
        const unit = Number(r.replacementValue);
        const transferred = _hrEquipTransferred(r, emp);
        const hasVal = isFinite(unit) && unit > 0;
        const days = String(r.transfersToEmployeeAfterDays || '');
        const extra = transferred
          ? `Becomes the employee&rsquo;s own property after ${escapeHtml(days)} days of employment &mdash; not returned, nothing owed.`
          : '';
        const money = transferred
          ? '&mdash;'
          : (hasVal
              ? escapeHtml(_hrEquipMoneyStr(unit * qty)) + (qty > 1 ? `<div class="sub">${escapeHtml(_hrEquipMoneyStr(unit))} ea.</div>` : '')
              : '&mdash;');
        return `
          <tr>
            <td>${_hrEquipItemCellHtml(r, extra)}</td>
            <td class="num">${qty}</td>
            <td class="num">${money}</td>
          </tr>`;
      }).join('')
    : `<tr><td colspan="3" style="text-align:center;padding:16px 6px;font-weight:700;letter-spacing:0.5px;">
         NONE &mdash; no company property issued to this employee${asOf ? ' as of ' + escapeHtml(asOf) : ''}
       </td></tr>`;

  return `
    <table>
      <thead>
        <tr>
          <th>Issued item (description / size / serial #)</th>
          <th class="num">Qty</th>
          <th class="num">Replacement value</th>
        </tr>
      </thead>
      <tbody>${body}</tbody>
      <tfoot><tr>
        <td colspan="2" style="text-align:right;">Total replacement value of issued items</td>
        <td class="num">${escapeHtml(total > 0 ? _hrEquipMoneyStr(total) : '$0.00')}</td>
      </tr></tfoot>
    </table>
    <div class="sub">
      Replacement value is what it costs the company to replace the item. It is not a price, a rental charge, or a
      markup, and <strong>it is not an amount owed</strong>. Normal wear and tear is never charged. Personal property
      the employee bought &mdash; safety-toe boots, for example &mdash; is not company property and is not listed here.
    </div>`;
}

// --- (a) Issue Receipt ----------------------------------------------------
// Batch by default (every item the person still holds); pass a recordId for a
// single-item receipt off the row's print affordance.
function printEquipmentIssueReceipt(personId, recordId) {
  const emp = (HRState.people || []).find(p => p.id === personId) || (HRState.selectedEmployee?.id === personId ? HRState.selectedEmployee : null);
  if (!emp) { window.showToast?.('Employee not found', 'error'); return; }
  if (HRState.safetyEquipmentDenied === true) {
    window.showToast?.("Equipment records aren't visible with your access level", 'error');
    return;
  }
  let records = _hrEquipmentForPerson(emp).filter(r => window.PersonSections.equipmentStatus(r) !== 'returned');
  if (recordId) records = records.filter(r => r.id === recordId);
  if (!records.length) { window.showToast?.('No outstanding items to print a receipt for', 'info'); return; }

  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const brand = _hrEquipPrintBrand();

  const body = `
    ${_hrEquipPrintHeaderHtml('Equipment Issue Receipt', `Printed ${escapeHtml(dateStr)}`)}
    <div class="who">
      <div class="item"><span class="label">Issued to</span><span class="value">${escapeHtml(emp.name || '')}</span></div>
      ${emp.employeeId ? `<div class="item"><span class="label">Employee ID</span><span class="value">${escapeHtml(String(emp.employeeId))}</span></div>` : ''}
      <div class="item"><span class="label">Items on this receipt</span><span class="value">${records.length}</span></div>
    </div>
    ${_hrEquipPrintTable(records, { withAck: true, person: emp })}
    <div class="callout">
      I acknowledge that I received the item(s) listed above and that they are the property of
      ${escapeHtml(brand.name)}, issued to me on loan for use in my job. I will return them on or before my last
      day of work, or sooner on request. <strong>Normal wear and tear is not chargeable to me.</strong>
    </div>
    ${_hrEquipSigBlockHtml(emp.name, 'Employee signature', 'Issued by')}
    <div class="footer">
      Kept as part of this employee's equipment issue log. Employer-paid personal protective equipment stays
      employer-paid (29 C.F.R. §1910.132(h)); this receipt records custody, not a charge.
    </div>`;

  _hrEquipOpenPrint(`Equipment Issue Receipt - ${emp.name || 'Employee'}`, body);
}

// Shared resolver for every printable. Keeps "who is this and can I read their
// gear?" in one place — a printable that resolves the person differently is a
// printable that can put one employee's serials on another's paper.
function _hrEquipPrintContext(personId) {
  const emp = (HRState.people || []).find(p => p.id === personId) || (HRState.selectedEmployee?.id === personId ? HRState.selectedEmployee : null);
  if (!emp) { window.showToast?.('Employee not found', 'error'); return null; }
  if (HRState.safetyEquipmentDenied === true) {
    window.showToast?.("Equipment records aren't visible with your access level", 'error');
    return null;
  }
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const records = _hrEquipmentForPerson(emp)
    .filter(r => window.PersonSections.equipmentStatus(r) !== 'returned')
    .sort((a, b) => String(a.equipmentType || '').localeCompare(String(b.equipmentType || '')));
  return { emp, records, dateStr };
}

// Employee / date strip shared by both inserts.
function _hrEquipWhoStripHtml(emp, dateStr, dateLabel) {
  // 🔴 Resolve the position to a HUMAN title, don't print the raw slug. Caught by
  // actually rendering this against live data: it printed "cdl_driver" on a page an
  // employee signs. Mirrors the resolution the rest of HR already uses
  // (hrPosition → getPositionTitle, else crewType → getTypeLabel), so this page
  // cannot drift from how the same person is labelled everywhere else.
  const rawPos = emp.hrPosition || emp.position || '';
  const rawCrew = emp.crewType || emp.role || (Array.isArray(emp.roles) ? emp.roles[0] : '') || '';
  let role = '';
  if (rawPos) {
    role = (typeof getPositionTitle === 'function' ? getPositionTitle(rawPos) : '') || rawPos;
  } else if (rawCrew) {
    // 🔴 ARGS ARE (slug, kind). I shipped these swapped an hour ago by copying the
    // call at :9092, which is itself swapped. Reversed, getTypeList('cdl_driver')
    // finds nothing and the fallback title-cases the FIRST argument — so this
    // printed the literal "CrewTypes" as a person's job title on a document an
    // employee signs. My E2E missed it because the person I tested has an
    // hrPosition and took the other branch entirely.
    // hr.js:17584 carries a comment about someone making this exact swap in
    // 2026-05-22. Third time; :9092 and :9160 are still wrong and are filed.
    role = (window.getTypeLabel ? window.getTypeLabel(rawCrew, 'crewTypes') : '') || rawCrew;
  }
  // Last resort: a slug that resolved to nothing still beats printing an underscore.
  if (role && /_/.test(role) && role === (rawPos || rawCrew)) {
    role = role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  }
  return `
    <div class="who">
      <div class="item"><span class="label">Employee</span><span class="value">${escapeHtml(emp.name || '')}</span></div>
      ${emp.employeeId ? `<div class="item"><span class="label">Employee ID</span><span class="value">${escapeHtml(String(emp.employeeId))}</span></div>` : ''}
      ${role ? `<div class="item"><span class="label">Position</span><span class="value">${escapeHtml(String(role))}</span></div>` : ''}
      <div class="item"><span class="label">${escapeHtml(dateLabel || 'Date')}</span><span class="value">${escapeHtml(dateStr)}</span></div>
    </div>`;
}

// --- (b) Issued Equipment insert ------------------------------------------
// The page that fills in the issued-items table of the tenant's OWN company
// property agreement. Prints with zero items on purpose (a "NONE" attachment is
// what a hire-day agreement wants in that box).
function printEquipmentIssuedInsert(personId) {
  const ctx = _hrEquipPrintContext(personId);
  if (!ctx) return;
  const { emp, records, dateStr } = ctx;

  const body = `
    ${_hrEquipPrintHeaderHtml('Issued Equipment — Attachment', `As of ${escapeHtml(dateStr)}`)}
    ${_hrEquipAttachNoteHtml('the signed company property agreement', emp.name)}
    ${_hrEquipWhoStripHtml(emp, dateStr, 'Issued items as of')}
    ${_hrEquipIssuedInsertHtml(emp, records, { asOfLabel: dateStr })}
    ${_hrEquipInitialsRowHtml()}
    <div class="footer">
      Generated from the DispatchDeck equipment issue log so the list attached here matches the record on file.
      Employer-paid personal protective equipment stays employer-paid (29 C.F.R. &sect;1910.132(h)); this page records
      custody, not a charge, and is <strong>not</strong> an authorization to deduct anything from pay.
    </div>`;

  _hrEquipOpenPrint(`Issued Equipment Attachment - ${emp.name || 'Employee'}`, body);
}

// --- (c) Separation Checkout insert ---------------------------------------
// The page that fills in the checkout table of the tenant's OWN separation form:
// ISSUED ITEM | RETURNED (Y / N) | AMOUNT OWED, closed by a subtotal of
// unreturned property.
//
// 🔴 Transferred gear NEVER appears in that table and NEVER touches the subtotal.
// It gets its own "employee keeps — nothing owed" block, shown rather than
// silently dropped: an item vanishing off a checkout sheet is how someone decides
// the log is wrong and goes back to a paper list.
//
// Two modes. If the separation notice for THIS employee is open with Yes/No
// answers recorded, the page prints FILLED (and the subtotal comes out of
// PersonSections.outstandingEquipmentValue, the one total function). Otherwise it
// prints as a WORKSHEET — Y / N to circle, blank amount cells — for the walk
// around the yard.
function printSeparationCheckoutInsert(personId) {
  const ctx = _hrEquipPrintContext(personId);
  if (!ctx) return;
  const { emp, records, dateStr } = ctx;
  if (!records.length) {
    window.showToast?.('Nothing issued to this person — print the Issued list attachment instead', 'info');
    return;
  }

  // Answers only apply when the open separation notice is for THIS person.
  // Without that guard a stale dropdown would print one employee's answers onto
  // another employee's checkout.
  const liveEmpId = document.getElementById('disc-employee')?.value || '';
  const ans = (liveEmpId && liveEmpId === personId) ? (HRState._termEquipAnswers || {}) : {};
  const answered = records.some(r => ans[r.id] === 'y' || ans[r.id] === 'n');

  const keeps = records.filter(r => _hrEquipTransferred(r, emp));
  const keepIds = new Set(keeps.map(r => r.id));
  const chargeable = records.filter(r => !keepIds.has(r.id));

  // 🔴 ONE total function. The subtotal is the replacement value of the items
  // answered "not returned" — nothing else is summed anywhere on this page.
  const notReturned = chargeable.filter(r => ans[r.id] === 'n');
  const subtotal = window.PersonSections.outstandingEquipmentValue(notReturned, emp);

  const rowsHtml = chargeable.map(r => {
    const qty = Math.max(1, parseInt(r.quantity, 10) || 1);
    const unit = Number(r.replacementValue);
    const hasVal = isFinite(unit) && unit > 0;
    const lineVal = hasVal ? _hrEquipMoneyStr(unit * qty) : '';
    const a = ans[r.id];
    const valueSub = hasVal
      ? `Replacement value ${escapeHtml(lineVal)}${qty > 1 ? ' (' + escapeHtml(String(qty)) + ' &times; ' + escapeHtml(_hrEquipMoneyStr(unit)) + ')' : ''}`
      : 'No replacement value on file';
    const ynCell = a === 'y' ? 'YES' : a === 'n' ? 'NO' : (answered ? '&mdash;' : 'Y &nbsp;/&nbsp; N');
    const owedCell = a === 'y'
      ? '$0.00'
      : a === 'n'
        ? (hasVal ? escapeHtml(lineVal) : '&mdash;')
        : (answered ? '<span class="sub">not recorded</span>' : '<span class="fill"></span>');
    return `
      <tr>
        <td>${_hrEquipItemCellHtml(r, valueSub)}</td>
        <td class="yn">${ynCell}</td>
        <td class="num fillcell">${owedCell}</td>
      </tr>`;
  }).join('');

  const keepsHtml = keeps.length ? `
    <div class="keeps">
      <h3>Employee keeps &mdash; not returned, nothing owed</h3>
      <ul>
        ${keeps.map(r => `<li>${escapeHtml(r.equipmentType || 'Equipment')}${r.size ? ' (' + escapeHtml(String(r.size)) + ')' : ''}
          &mdash; became the employee&rsquo;s own property after ${escapeHtml(String(r.transfersToEmployeeAfterDays || ''))} days of employment.</li>`).join('')}
      </ul>
      <div class="sub">
        These items are the employee&rsquo;s personal property. They are <strong>not</strong> collected at separation,
        <strong>not</strong> listed as unreturned, and are <strong>not</strong> included in the subtotal above.
        ${(!(emp.hireDate || emp.dateOfHire || emp.startDate))
          ? 'No hire date is on file, so the ownership window cannot be confirmed &mdash; they are treated as the employee&rsquo;s and are not charged.'
          : ''}
      </div>
    </div>` : '';

  const body = `
    ${_hrEquipPrintHeaderHtml('Separation Checkout — Attachment', `As of ${escapeHtml(dateStr)}`)}
    ${_hrEquipAttachNoteHtml('the separation / exit checkout form', emp.name)}
    ${_hrEquipWhoStripHtml(emp, dateStr, 'Checkout date')}
    ${answered ? '' : `<div class="sub" style="margin-bottom:4px;">Circle <strong>Y</strong> or <strong>N</strong> as each item is collected, then write the amount owed for anything not returned and total the column.</div>`}
    <table>
      <thead>
        <tr>
          <th>Issued item (description / size / serial #)</th>
          <th style="text-align:center;">Returned (Y / N)</th>
          <th class="num">Amount owed</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
      <tfoot><tr>
        <td colspan="2" style="text-align:right;">Subtotal &mdash; unreturned property</td>
        <td class="num fillcell">${answered ? escapeHtml(subtotal > 0 ? _hrEquipMoneyStr(subtotal) : '$0.00') : '<span class="fill"></span>'}</td>
      </tr></tfoot>
    </table>
    ${keepsHtml}
    <div class="callout">
      <strong>Normal wear and tear is never charged.</strong> An item handed back in ordinary working condition is
      returned at no cost to the employee. The subtotal above is the company&rsquo;s replacement cost for property that
      was not handed back &mdash; <strong>it is not an amount authorized to be deducted from final pay</strong>. Any
      deduction from wages needs its own written authorization signed at separation, is restricted or prohibited
      outright in a number of states, and may never drop pay below minimum wage or come out of overtime premium
      (29 C.F.R. &sect;&sect;531.35, 531.37).
    </div>
    ${_hrEquipInitialsRowHtml()}
    <div class="footer">
      Generated from the DispatchDeck equipment issue log so this checkout matches the record on file. Personal
      property the employee bought &mdash; safety-toe boots, for example &mdash; is not company property, is not
      collected, and is not listed here. This page is provided for record-keeping and is <strong>not legal
      advice</strong>.
    </div>`;

  _hrEquipOpenPrint(`Separation Checkout Attachment - ${emp.name || 'Employee'}`, body);
}

// --- (d) Company Property Agreement — GENERIC FALLBACK ---------------------
// DEMOTED, not deleted. A tenant that already has its own lawyer-reviewed
// agreement should print the ISSUED EQUIPMENT ATTACHMENT above and staple it to
// that. A tenant with no paperwork at all still needs something to sign, and
// leaving them with nothing would be a regression. So this stays as the fallback
// host — and its issued-items page is `_hrEquipIssuedInsertHtml`, the SAME
// builder the attachment uses, so the two can never disagree.
//
// ⚖️ READ THE COPY BEFORE CHANGING IT. The clause numbering below is load-bearing:
// 4 (wear and tear not chargeable), 6 (this is NOT a deduction authorization) and
// 7 (federal floor + state variance) are the three that keep this template lawful
// to hand to a tenant in ANY state. Do not "tighten" them into an authorization.
// Clause 3 carries the ownership-transfer carve-out so the agreement cannot
// contradict what the checkout attachment does.
function printCompanyPropertyAgreement(personId) {
  const ctx = _hrEquipPrintContext(personId);
  if (!ctx) return;
  const { emp, records, dateStr } = ctx;
  const co = escapeHtml(_hrEquipPrintBrand().name);

  const body = `
    ${_hrEquipPrintHeaderHtml('Company Property Agreement', `Printed ${escapeHtml(dateStr)}`)}
    ${_hrEquipWhoStripHtml(emp, dateStr, 'Agreement date')}

    <div class="attach-note">
      <strong>Use only if your company has no property agreement of its own.</strong>
      If it does, print the <em>Issued Equipment &mdash; Attachment</em> instead and attach it to that form. The
      issued-items page attached here is the same attachment, generated from the same log.
    </div>

    <div class="terms">
      <h2>Terms</h2>
      <ol>
        <li><strong>Property on loan.</strong> The items listed on the attached <em>Issued Equipment</em> page belong to ${co}. They are issued to me on loan so I can do my job and remain company property at all times. Being issued an item does not transfer ownership of it to me, except as stated in paragraph 3.</li>
        <li><strong>Care and reporting.</strong> I will use the items for company business, keep them in working condition, and tell my supervisor promptly if an item is lost, stolen, or damaged.</li>
        <li><strong>Return, and items that become mine.</strong> I will return every item on the attached page on or before my last day of employment, or sooner if the company asks for it back. Where the attached page states that an item becomes my own property after a stated number of days of employment, that item is mine once that period has passed: it is not returned and nothing is owed for it.</li>
        <li><strong>Normal wear and tear is not chargeable.</strong> An item returned in normal working condition &mdash; including ordinary wear from doing the job &mdash; is returned at no cost to me. I will not be charged for an item that wears out, breaks in normal use, or is damaged in an accident that was not my willful misconduct.</li>
        <li><strong>Items not returned.</strong> If an item I am required to return is not returned, the company may ask me to reimburse its replacement value &mdash; the amount shown on the attached page &mdash; and may pursue any other remedy available to it under applicable law.</li>
        <li><strong>This agreement is not authorization to deduct from my pay.</strong> Signing it does not permit the company to withhold or deduct anything from my wages. Any deduction from wages requires a separate written authorization, signed at the time the deduction is made, and only where that deduction is permitted by the law of the state where I work.</li>
        <li><strong>Limits that always apply.</strong> Federal law does not permit a deduction for the employer's benefit that brings pay below the applicable minimum wage for the workweek, or that comes out of overtime premium pay (29 C.F.R. §§531.35, 531.37). State wage-deduction laws vary widely, and a number of states restrict or prohibit deductions for lost, damaged, or unreturned property even where the employee has signed an authorization. Where state law gives more protection than federal law, state law controls.</li>
        <li><strong>Required safety equipment stays employer-paid.</strong> Personal protective equipment that OSHA requires the employer to provide at no cost to the employee (29 C.F.R. §1910.132(h)) remains employer-paid. Nothing here shifts that cost to me. This agreement is about returning company property, not about paying for required PPE. Equipment I bought myself &mdash; safety-toe boots, for example &mdash; is my own property, is not listed on the attached page, and is not returned.</li>
      </ol>
    </div>

    <div class="callout">
      I have read this agreement, I have received the items listed on the attached <em>Issued Equipment</em> page, and
      I understand that I am responsible for returning them. I understand that <strong>normal wear and tear is never
      charged to me</strong>, and that this agreement does <strong>not</strong> authorize any deduction from my pay.
    </div>

    ${_hrEquipSigBlockHtml(emp.name, 'Employee signature', 'Company representative')}

    <div class="footer">
      This template is provided for record-keeping and is <strong>not legal advice</strong>. Wage-deduction and
      property-recovery rules vary by state &mdash; have counsel review before relying on this document as the
      basis for withholding any pay.
    </div>

    <div class="page-break">
      ${_hrEquipPrintHeaderHtml('Issued Equipment — Attachment', `Attachment to the Company Property Agreement dated ${escapeHtml(dateStr)}`)}
      ${_hrEquipWhoStripHtml(emp, dateStr, 'Issued items as of')}
      ${_hrEquipIssuedInsertHtml(emp, records, { asOfLabel: dateStr })}
      ${_hrEquipInitialsRowHtml()}
      <div class="footer">
        Generated from the DispatchDeck equipment issue log so the list signed here matches the record on file.
      </div>
    </div>`;

  _hrEquipOpenPrint(`Company Property Agreement - ${emp.name || 'Employee'}`, body);
}

// =============================================================================
// TASK-9719 — VOID, never delete, a PPE issue row (OSHA record). Chris
// DECISION 6 (2026-10-08): a mistaken issue (wrong person / qty / item, never
// handed out) is VOIDED with a required reason; the row stays on file, struck
// through, and drops out of every count (Reports, the person card, printables,
// the stock count). Fixing a quantity = void + re-issue, so the original
// signature stays honest. The void is SERVER-written (voidPpeIssue callable):
// firestore.rules refuses any client write of `voidedAt` and freezes a voided
// row, so nobody can quietly un-void it.
// =============================================================================
function voidSafetyEquipmentRecord(id) {
  if (!_hrCanVoidEquipment()) { window.showToast?.('Only someone with HR access can void an entry.', 'error'); return; }
  const rec = (HRState.safetyEquipmentRecords || []).find(r => r.id === id);
  if (!rec) { window.showToast?.('Record not found', 'error'); return; }
  if (rec.voidedAt) { window.showToast?.('That entry is already voided', 'info'); return; }
  document.getElementById('ppe-void-overlay')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'ppe-void-overlay';
  overlay.className = 'ppe-sheet-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:10001;display:flex;align-items:center;justify-content:center;padding:12px;';
  overlay.innerHTML = `
    <div role="dialog" aria-modal="true" aria-labelledby="ppe-void-title" style="background:var(--bg-primary);border-radius:var(--radius-lg);padding:var(--spacing-lg);max-width:440px;width:100%;max-height:90vh;max-height:90dvh;overflow-y:auto;text-align:center;">
      <span class="material-icons" style="font-size:36px;color:#b45309;">block</span>
      <h3 id="ppe-void-title" style="margin:6px 0 4px;font-size:1.05rem;color:var(--text-primary);">Void this entry?</h3>
      <div style="font-size:0.88rem;color:var(--text-primary);margin-bottom:6px;">
        <strong>${escapeHtml(rec.equipmentType || 'Equipment')}</strong>${rec.size ? ' (' + escapeHtml(rec.size) + ')' : ''} ×${escapeHtml(String(rec.quantity || 1))} &middot; ${escapeHtml(rec.personName || 'Unknown')} &middot; ${escapeHtml(rec.dateReceived || '')}
      </div>
      <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:12px;text-align:left;line-height:1.45;">
        Use this when the gear was never handed out (wrong person, item or quantity). The entry stays on file, crossed out, and stops counting. If they really got it, issue it again with the right details.
      </div>
      <label for="ppe-void-reason" style="display:block;text-align:left;font-size:0.82rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Reason (required)</label>
      <textarea id="ppe-void-reason" class="form-control" rows="2" maxlength="300" placeholder="e.g. Logged to the wrong person" oninput="document.getElementById('ppe-void-error').textContent=''" style="width:100%;font-size:16px;font-family:inherit;"></textarea>
      <div id="ppe-void-error" role="alert" style="min-height:18px;font-size:0.8rem;color:var(--status-danger);text-align:left;margin-top:4px;"></div>
      <div style="display:flex;gap:16px;justify-content:center;margin-top:8px;flex-wrap:wrap;">
        <button type="button" class="btn btn-secondary" style="min-height:48px;min-width:120px;" onclick="document.getElementById('ppe-void-overlay')?.remove()">Cancel</button>
        <button type="button" id="btn-ppe-void" class="btn btn-primary" style="min-height:48px;min-width:140px;" onclick="window.HRModule.submitVoidSafetyEquipment('${escJsAttr(id)}')">
          <span class="material-icons" style="font-size:18px;">block</span> Void entry
        </button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  setTimeout(() => document.getElementById('ppe-void-reason')?.focus(), 50);
}

async function submitVoidSafetyEquipment(id) {
  const reason = (document.getElementById('ppe-void-reason')?.value || '').trim();
  const errEl = document.getElementById('ppe-void-error');
  if (reason.length < 3) {
    if (errEl) errEl.textContent = 'Say why this entry is being voided.';
    document.getElementById('ppe-void-reason')?.focus();
    return;
  }
  const btn = document.getElementById('btn-ppe-void');
  if (btn) { if (btn.disabled) return; btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Voiding…'; }
  try {
    await httpsCallable(functions, 'voidPpeIssue')({ recordId: id, reason });
    document.getElementById('ppe-void-overlay')?.remove();
    document.getElementById('ppe-detail-overlay')?.remove();
    // TASK-9722 — an open stock History re-reads (the issue is now struck through).
    const _h = HRState._supplyHist;
    if (_h && document.getElementById('ppe-history-overlay')) setTimeout(() => openSupplyHistory(_h.assetId, _h.opts), 1500);
    window.showToast?.('Entry voided — it stays on file and no longer counts', 'success');
  } catch (err) {
    console.error('[HR] voidPpeIssue failed:', err?.code, err?.message);
    if (errEl) errEl.textContent = err?.message || 'Could not void just now. Try again.';
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:18px;">block</span> Void entry'; }
  }
}

// Void is HR-only (the callable gates on the hr module, owner via ownerId).
// Client mirror only — hides a button that would be refused anyway.
function _hrCanVoidEquipment() {
  return _hrCanIssueEquipment();
}

// =============================================================================
// TASK-9720 — STOCK COUNTS ("Track stock"). HR → Safety Equipment → Manage →
// Inventory. A PPE type with Track stock on is linked to ONE Roster consumable
// (category Safety Gear); its count is SERVER-maintained — every signed issue
// (admin, Issue Equipment, a driver's PIN self-log) lowers it via the
// onPpeIssueStockWrite trigger, a good Return or a Void puts it back. Nothing
// here ever blocks or slows a sign-out: the issue path is unchanged and the
// count may go below zero. All writes are callables (setPpeStockTracking,
// recordSupplyStock); the client only reads `assets where stockTracked`.
// =============================================================================
function _hrStockAssetForType(type) {
  if (!type) return null;
  const key = String(type.name || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const list = HRState.stockAssets || [];
  return list.find(a => a.ppeTypeId === type.id) || list.find(a => a.ppeTypeKey && a.ppeTypeKey === key) || null;
}

function _hrStockReqId() {
  if (!HRState._stockReqId) HRState._stockReqId = 'h' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  return HRState._stockReqId;
}

function _hrStockQtyText(n) {
  const v = Number(n) || 0;
  return v < 0 ? `−${Math.abs(v)}` : String(v);
}

function getInventoryHTML() {
  const types = (HRState.safetyEquipmentTypes || []).filter(t => t.active !== false);
  const canManage = _hrCanIssueEquipment();
  if (!HRState.stockAssetsLoaded) {
    return `<div class="empty-state-small"><span class="material-icons" style="font-size:24px;margin-bottom:8px;">hourglass_empty</span><div>Loading stock&hellip;</div></div>`;
  }
  const tracked = types.map(t => ({ type: t, asset: _hrStockAssetForType(t) })).filter(x => x.asset);
  const untracked = types.filter(t => !_hrStockAssetForType(t));

  if (!tracked.length) {
    return `
      <div class="ppe-stock-firstrun" style="background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-md);">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
          <span class="material-icons" style="font-size:22px;color:var(--brand-blue);">inventory_2</span>
          <h3 style="margin:0;font-size:1rem;color:var(--text-primary);">Track glove stock</h3>
        </div>
        <ol style="margin:0 0 12px 20px;padding:0;font-size:0.88rem;color:var(--text-primary);line-height:1.6;">
          <li>Turn on <strong>Track stock</strong> for an item (gloves, glasses, ear plugs&hellip;).</li>
          <li>Enter how many you have on the shelf right now.</li>
          <li>Keep signing gear out as usual. Every sign-out lowers the count, including ones drivers log themselves.</li>
        </ol>
        <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:12px;line-height:1.45;">Nobody is ever stopped from taking PPE. If the count goes below zero it just shows red, so you know to count the shelf.</div>
        ${canManage
          ? `<button type="button" class="btn btn-primary" style="width:100%;min-height:48px;" onclick="window.HRModule.openTrackStockPicker()"><span class="material-icons">add_task</span> Turn on Track stock</button>`
          : `<div style="font-size:0.8rem;color:var(--text-secondary);">Ask someone with HR or Roster access to turn it on.</div>`}
      </div>`;
  }

  // TASK-9722 — the ledger rows behind each card (mismatch strip + banner).
  _hrEnsureSupplyRows(tracked.map(x => x.asset));
  const banner = _hrSupplyBannerHTML(tracked.map(x => x.asset));

  const cards = tracked.map(({ type, asset }) => {
    const q = Number(asset.quantity) || 0;
    const neg = q < 0;
    const uomWord = _hrSupplyUomWord(asset);
    const since = asset.ledgerOpenedDay ? `Counting since ${formatDateShort(asset.ledgerOpenedDay)}` : 'Counting';
    const jsId = escJsAttr(asset.id);
    return `
      <div class="ppe-stock-card" data-type-id="${window.escAttr(type.id)}" data-asset-id="${window.escAttr(asset.id)}" style="background:var(--bg-secondary);border:1px solid ${neg ? '#fecaca' : 'var(--border-light)'};border-radius:var(--radius-md);padding:12px;">
        <div style="display:flex;align-items:flex-start;gap:10px;">
          <div style="flex:1;min-width:0;">
            <div style="font-weight:700;font-size:0.95rem;color:var(--text-primary);">${escapeHtml(type.name)}</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">${since}</div>
          </div>
          <div style="text-align:right;">
            <div class="ppe-stock-qty" style="font-size:1.6rem;font-weight:800;line-height:1;color:${neg ? '#b91c1c' : 'var(--text-primary)'};">${_hrStockQtyText(q)}</div>
            <div style="font-size:0.72rem;color:var(--text-secondary);">${uomWord ? escapeHtml(uomWord) + ' ' : ''}on hand</div>
          </div>
        </div>
        ${_hrSupplyStripHTML(asset)}
        ${neg ? `<div style="margin-top:8px;font-size:0.78rem;color:#991b1b;background:#fee2e2;border-radius:8px;padding:6px 8px;"><span class="material-icons" style="font-size:15px;vertical-align:-3px;">warning</span> Below zero: more went out than was counted in. Count the shelf with Count now.</div>` : ''}
        <div class="ppe-stock-actions" style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:10px;">
          ${canManage ? `
            <button type="button" class="btn btn-secondary ppe-stock-count-btn" style="min-height:48px;height:48px;" onclick="window.HRModule.openSupplyCountSheet('${jsId}')"><span class="material-icons" style="font-size:18px;">fact_check</span> Count now</button>
            <button type="button" class="btn btn-secondary" style="min-height:48px;height:48px;" onclick="window.HRModule.openStockMoveSheet('${jsId}','receive')"><span class="material-icons" style="font-size:18px;">add_circle</span> Receive</button>
            <button type="button" class="btn btn-secondary" style="min-height:48px;height:48px;" onclick="window.HRModule.openStockMoveSheet('${jsId}','adjust')"><span class="material-icons" style="font-size:18px;">tune</span> Adjust</button>` : ''}
          <button type="button" class="btn btn-secondary ppe-stock-history-btn" style="min-height:48px;height:48px;${canManage ? '' : 'grid-column:1 / -1;'}" onclick="window.HRModule.openSupplyHistory('${jsId}')"><span class="material-icons" style="font-size:18px;">history</span> History</button>
        </div>
      </div>`;
  }).join('');

  return `
    ${banner}
    <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-bottom:var(--spacing-sm);flex-wrap:wrap;">
      <span style="font-weight:700;font-size:0.95rem;color:var(--text-primary);">Stock on hand</span>
      ${canManage && untracked.length ? `<button type="button" class="btn btn-secondary btn-sm" style="min-height:44px;" onclick="window.HRModule.openTrackStockPicker()"><span class="material-icons" style="font-size:16px;">add</span> Track another item</button>` : ''}
    </div>
    <div style="display:grid;gap:10px;">${cards}</div>
    <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:10px;line-height:1.45;">
      Each signed sign-out lowers the count; a Return in good condition or a Void puts it back. Sign-outs from before tracking started are not subtracted. Count now sets the number to what is on the shelf and keeps the difference on record.
      ${untracked.length ? `<br>Not tracked: ${untracked.map(t => escapeHtml(t.name)).join(', ')}.` : ''}
    </div>`;
}

function _hrTrackStockOverlay(id, inner) {
  document.getElementById(id)?.remove();
  const overlay = document.createElement('div');
  overlay.id = id;
  overlay.className = 'ppe-sheet-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:10001;display:flex;align-items:center;justify-content:center;padding:12px;';
  overlay.innerHTML = `<div role="dialog" aria-modal="true" style="background:var(--bg-primary);border-radius:var(--radius-lg);padding:var(--spacing-lg);max-width:460px;width:100%;max-height:90vh;max-height:90dvh;overflow-y:auto;">${inner}</div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  return overlay;
}

// Pick which item type to start counting (first-run "Turn on Track stock").
function openTrackStockPicker() {
  if (!_hrCanIssueEquipment()) { window.showToast?.('You do not have permission to change stock settings', 'error'); return; }
  const untracked = (HRState.safetyEquipmentTypes || []).filter(t => t.active !== false && !_hrStockAssetForType(t));
  if (!untracked.length) { window.showToast?.('Every item type is already tracked', 'info'); return; }
  _hrTrackStockOverlay('ppe-track-picker-overlay', `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-sm);">
      <h3 style="margin:0;font-size:1rem;color:var(--text-primary);">Which item do you want to count?</h3>
      <button type="button" class="btn-icon" aria-label="Close" style="min-width:44px;min-height:44px;" onclick="document.getElementById('ppe-track-picker-overlay')?.remove()"><span class="material-icons">close</span></button>
    </div>
    <div style="display:grid;gap:8px;">
      ${untracked.map(t => `<button type="button" class="btn btn-secondary ppe-track-pick" style="min-height:48px;justify-content:flex-start;text-align:left;" onclick="document.getElementById('ppe-track-picker-overlay')?.remove();window.HRModule.openTrackStockSheet('${escJsAttr(t.id)}')"><span class="material-icons" style="font-size:18px;">shield</span> ${escapeHtml(t.name)}</button>`).join('')}
    </div>`);
}

// The Track stock sheet for one type: link an existing Roster item (same name)
// or make a new one in Safety Gear, then the opening count.
async function openTrackStockSheet(typeId) {
  if (!_hrCanIssueEquipment()) { window.showToast?.('You do not have permission to change stock settings', 'error'); return; }
  const type = (HRState.safetyEquipmentTypes || []).find(t => t.id === typeId);
  if (!type) { window.showToast?.('Item type not found', 'error'); return; }
  if (_hrStockAssetForType(type)) { window.showToast?.(`${type.name} is already tracked`, 'info'); return; }
  // Same-named Roster quantity items → offer "use this one" (ONE item, never a twin).
  let matches = [];
  try {
    const snap = await getDocs(collection(db, 'tenants', HRState.tenantId, 'assets'));
    const key = String(type.name || '').replace(/\s+/g, ' ').trim().toLowerCase();
    matches = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(a => a.active !== false
      && (a.trackingMode || 'quantity') === 'quantity' && a.quantityType !== 'rental' && a.isRental !== true
      && a.stockTracked !== true && String(a.name || '').replace(/\s+/g, ' ').trim().toLowerCase() === key);
  } catch (e) {
    console.warn('[HR] could not read Roster items for the link check:', e?.message || e);
  }
  HRState._trackStockSheet = { typeId, matches };
  HRState._stockReqId = null;
  const linkOpts = matches.map((a, i) => `
    <label class="ppe-track-opt" style="display:flex;gap:10px;align-items:center;padding:10px 12px;border:1px solid var(--border-light);border-radius:10px;min-height:48px;cursor:pointer;">
      <input type="radio" name="ppe-track-mode" value="link:${window.escAttr(a.id)}" ${i === 0 ? 'checked' : ''} style="width:20px;height:20px;">
      <span style="font-size:0.88rem;color:var(--text-primary);">Use the Roster item <strong>${escapeHtml(a.name || '')}</strong>${a.yardName ? ' (' + escapeHtml(a.yardName) + ')' : ''} &mdash; shows ${escapeHtml(String(Number(a.quantity) || 0))} now</span>
    </label>`).join('');
  _hrTrackStockOverlay('ppe-track-overlay', `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-sm);">
      <h3 style="margin:0;font-size:1rem;color:var(--text-primary);">Track stock: ${escapeHtml(type.name)}</h3>
      <button type="button" class="btn-icon" aria-label="Close" style="min-width:44px;min-height:44px;" onclick="document.getElementById('ppe-track-overlay')?.remove()"><span class="material-icons">close</span></button>
    </div>
    <div style="display:grid;gap:8px;margin-bottom:12px;">
      ${linkOpts}
      <label class="ppe-track-opt" style="display:flex;gap:10px;align-items:center;padding:10px 12px;border:1px solid var(--border-light);border-radius:10px;min-height:48px;cursor:pointer;">
        <input type="radio" name="ppe-track-mode" value="create" ${matches.length ? '' : 'checked'} style="width:20px;height:20px;">
        <span style="font-size:0.88rem;color:var(--text-primary);">Add it to Roster &rsaquo; Assets as a new Safety Gear item</span>
      </label>
    </div>
    <label for="ppe-track-count" style="display:block;font-size:0.88rem;font-weight:700;color:var(--text-primary);margin-bottom:4px;">How many do you have right now?</label>
    <input type="number" id="ppe-track-count" class="form-control" inputmode="numeric" min="0" step="1" placeholder="Count the shelf" oninput="document.getElementById('ppe-track-error').textContent=''" style="font-size:1.4rem;min-height:52px;">
    <label for="ppe-track-uom" style="display:block;font-size:0.8rem;color:var(--text-secondary);margin:10px 0 4px;">Counted in</label>
    <select id="ppe-track-uom" class="form-control" style="min-height:44px;">
      <option value="">Each</option><option value="pair">Pairs</option><option value="box">Boxes</option>
    </select>
    <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:10px;line-height:1.45;">From now on every signed sign-out lowers this count. Sign-outs before today are not subtracted.</div>
    <div id="ppe-track-error" role="alert" style="min-height:18px;font-size:0.8rem;color:var(--status-danger);margin-top:6px;"></div>
    <div style="display:flex;gap:16px;justify-content:flex-end;margin-top:8px;flex-wrap:wrap;">
      <button type="button" class="btn btn-secondary" style="min-height:48px;min-width:110px;" onclick="document.getElementById('ppe-track-overlay')?.remove()">Cancel</button>
      <button type="button" id="btn-ppe-track" class="btn btn-primary" style="min-height:48px;min-width:150px;" onclick="window.HRModule.submitTrackStock()"><span class="material-icons" style="font-size:18px;">check</span> Start tracking</button>
    </div>`);
  setTimeout(() => document.getElementById('ppe-track-count')?.focus(), 50);
}

async function submitTrackStock() {
  const st = HRState._trackStockSheet
  if (!st) return;
  const errEl = document.getElementById('ppe-track-error');
  const raw = (document.getElementById('ppe-track-count')?.value || '').trim();
  const count = /^\d+$/.test(raw) ? parseInt(raw, 10) : NaN;
  if (!isFinite(count)) {
    if (errEl) errEl.textContent = 'Enter how many you have now (0 or more).';
    document.getElementById('ppe-track-count')?.focus();
    return;
  }
  const pick = document.querySelector('input[name="ppe-track-mode"]:checked')?.value || 'create';
  const payload = { typeId: st.typeId, openingCount: count, uom: document.getElementById('ppe-track-uom')?.value || '' };
  if (pick.startsWith('link:')) { payload.mode = 'link'; payload.assetId = pick.slice(5); } else { payload.mode = 'create'; }
  const btn = document.getElementById('btn-ppe-track');
  if (btn) { if (btn.disabled) return; btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving…'; }
  try {
    const res = await httpsCallable(functions, 'setPpeStockTracking')(payload);
    document.getElementById('ppe-track-overlay')?.remove();
    document.getElementById('safety-types-overlay')?.remove();
    HRState._trackStockSheet = null;
    window.showToast?.(`Tracking ${res?.data?.typeName || 'stock'}: ${count} on hand`, 'success');
    HRState.safetyManagerTab = 'inventory';
    if (HRState.currentView === 'safety') renderSafetyEquipmentView();
  } catch (err) {
    console.error('[HR] setPpeStockTracking failed:', err?.code, err?.message);
    const reason = err?.details?.reason;
    if (reason === 'name_exists') {
      // A same-named Roster item exists the browser didn't see — reopen with the link offered.
      document.getElementById('ppe-track-overlay')?.remove();
      await openTrackStockSheet(st.typeId);
      const el = document.getElementById('ppe-track-error');
      if (el) el.textContent = err.message;
      return;
    }
    if (errEl) errEl.textContent = err?.message || 'Could not save just now. Try again.';
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:18px;">check</span> Start tracking'; }
  }
}

async function stopTrackingStock(typeId) {
  const type = (HRState.safetyEquipmentTypes || []).find(t => t.id === typeId);
  if (!type) return;
  const ok = typeof window.showConfirm === 'function'
    ? await window.showConfirm('Stop counting stock?', `${type.name} keeps its last count in Roster, but sign-outs stop lowering it. You can turn it back on with a new count.`, { confirmText: 'Stop counting', cancelText: 'Keep counting' })
    : confirm(`Stop counting ${type.name}?`);
  if (!ok) return;
  try {
    await httpsCallable(functions, 'setPpeStockTracking')({ typeId, mode: 'off' });
    document.getElementById('safety-types-overlay')?.remove();
    window.showToast?.(`Stopped counting ${type.name}`, 'success');
  } catch (err) {
    console.error('[HR] stop tracking failed:', err?.code, err?.message);
    window.showToast?.(err?.message || 'Could not save just now. Try again.', 'error');
  }
}

// Receive (stock in) / Adjust (signed correction with a reason) from HR.
function openStockMoveSheet(assetId, kind) {
  if (!_hrCanIssueEquipment()) { window.showToast?.('You do not have permission to change stock', 'error'); return; }
  const asset = (HRState.stockAssets || []).find(a => a.id === assetId);
  if (!asset) { window.showToast?.('Item not found', 'error'); return; }
  HRState._stockMove = { assetId, kind };
  HRState._stockReqId = null;
  const isAdj = kind === 'adjust';
  _hrTrackStockOverlay('ppe-stockmove-overlay', `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-sm);">
      <h3 style="margin:0;font-size:1rem;color:var(--text-primary);">${isAdj ? 'Adjust' : 'Receive'}: ${escapeHtml(asset.name || '')}</h3>
      <button type="button" class="btn-icon" aria-label="Close" style="min-width:44px;min-height:44px;" onclick="document.getElementById('ppe-stockmove-overlay')?.remove()"><span class="material-icons">close</span></button>
    </div>
    <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:10px;">On hand now: <strong style="color:var(--text-primary);">${_hrStockQtyText(asset.quantity)}</strong></div>
    <label for="ppe-move-qty" style="display:block;font-size:0.88rem;font-weight:700;color:var(--text-primary);margin-bottom:4px;">${isAdj ? 'Add (+) or take off (−)' : 'How many came in?'}</label>
    <input type="number" id="ppe-move-qty" class="form-control" inputmode="${isAdj ? 'text' : 'numeric'}" step="1" ${isAdj ? '' : 'min="1"'} placeholder="${isAdj ? 'e.g. -4' : 'e.g. 120'}" oninput="window.HRModule._stockMovePreview()" style="font-size:1.4rem;min-height:52px;">
    <div id="ppe-move-preview" aria-live="polite" style="min-height:20px;font-size:0.85rem;color:var(--text-primary);margin-top:6px;"></div>
    ${isAdj ? `
      <label for="ppe-move-reason" style="display:block;font-size:0.88rem;font-weight:700;color:var(--text-primary);margin:10px 0 4px;">Why (required)</label>
      <select id="ppe-move-reason" class="form-control" style="min-height:44px;">
        <option value="">Pick a reason…</option>
        <option>Shelf count was different</option>
        <option>Damaged / thrown out</option>
        <option>Found more</option>
        <option>Entered wrong earlier</option>
        <option>Other</option>
      </select>` : ''}
    <label for="ppe-move-note" style="display:block;font-size:0.8rem;color:var(--text-secondary);margin:10px 0 4px;">Note (optional)</label>
    <input type="text" id="ppe-move-note" class="form-control" maxlength="120" placeholder="${isAdj ? 'e.g. counted bin 4' : 'e.g. PO 1182'}" style="min-height:44px;">
    <div id="ppe-move-error" role="alert" style="min-height:18px;font-size:0.8rem;color:var(--status-danger);margin-top:6px;"></div>
    <div style="display:flex;gap:16px;justify-content:flex-end;margin-top:8px;flex-wrap:wrap;">
      <button type="button" class="btn btn-secondary" style="min-height:48px;min-width:110px;" onclick="document.getElementById('ppe-stockmove-overlay')?.remove()">Cancel</button>
      <button type="button" id="btn-ppe-move" class="btn btn-primary" style="min-height:48px;min-width:130px;" onclick="window.HRModule.submitStockMove()"><span class="material-icons" style="font-size:18px;">check</span> Save</button>
    </div>`);
  setTimeout(() => document.getElementById('ppe-move-qty')?.focus(), 50);
}

async function submitStockMove() {
  const mv = HRState._stockMove;
  if (!mv) return;
  const errEl = document.getElementById('ppe-move-error');
  const raw = (document.getElementById('ppe-move-qty')?.value || '').trim();
  const qty = /^[-+]?\d+$/.test(raw) ? parseInt(raw, 10) : NaN;
  if (!isFinite(qty) || qty === 0 || (mv.kind === 'receive' && qty < 1)) {
    if (errEl) errEl.textContent = mv.kind === 'receive' ? 'Enter how many came in (1 or more).' : 'Enter a whole number to add or take off, not 0.';
    return;
  }
  const why = mv.kind === 'adjust' ? (document.getElementById('ppe-move-reason')?.value || '') : '';
  if (mv.kind === 'adjust' && !why) { if (errEl) errEl.textContent = 'Pick a reason.'; return; }
  const note = (document.getElementById('ppe-move-note')?.value || '').trim();
  const reason = [why, note].filter(Boolean).join(' — ');
  const btn = document.getElementById('btn-ppe-move');
  if (btn) { if (btn.disabled) return; btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving…'; }
  try {
    const res = await httpsCallable(functions, 'recordSupplyStock')({ assetId: mv.assetId, kind: mv.kind, qty, reason, requestId: _hrStockReqId() });
    HRState._stockReqId = null;
    HRState._stockMove = null;
    document.getElementById('ppe-stockmove-overlay')?.remove();
    const bal = res?.data?.balance;
    const was = res?.data?.previous;
    window.showToast?.(`Saved${typeof bal === 'number' ? ` — on hand ${_hrStockQtyText(bal)}${typeof was === 'number' ? ` (was ${_hrStockQtyText(was)})` : ''}` : ''}`, 'success');
    _hrSupplyAfterWrite(mv.assetId); // TASK-9722 — strip + History re-read
  } catch (err) {
    console.error('[HR] recordSupplyStock failed:', err?.code, err?.message);
    if (errEl) errEl.textContent = err?.message || 'Could not save just now. Try again.';
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:18px;">check</span> Save'; }
  }
}

// =============================================================================
// TASK-9722 — PPE inventory S2: Count now, mismatch strip, item History,
// alerts banner. Every number comes from the SERVER ledger (`supply_*` rows in
// assetTransactions, written only by functions/src/services/supplyLedger.js)
// read through window.PpeReport (the GENERATED copy of
// functions/src/shared/ppeReport.js — the daily alert digest runs the same
// functions server-side, so the banner and the email cannot disagree).
// Writes go through the recordSupplyStock callable (count / adjust / receive);
// nothing here writes a count from the browser, and nothing here is on the
// sign-out path — a worker taking gear is never asked, slowed or refused.
// The History view, Count now and the strip are also opened from Roster >
// Assets (roster.js) — the HOST passes what the viewer may do (canManage);
// the server decides again on every write.
// =============================================================================
const SUPPLY_HISTORY_PAGE = 25;
const SUPPLY_DAY_MS = 24 * 60 * 60 * 1000;

function _hrSupplyTenant() {
  return HRState.tenantId || window.AppState?.tenant || '';
}

function _hrSupplyUomWord(asset) {
  const u = String((asset && asset.uom) || '').toLowerCase();
  return { pair: 'pairs', box: 'boxes' }[u] || (asset && asset.uom ? String(asset.uom) : '');
}

function _hrSupplyMs(v) {
  return window.PpeReport?.rowMs ? window.PpeReport.rowMs(v) : null;
}

function _hrSupplySigned(n) {
  const v = Number(n) || 0;
  return v > 0 ? `+${v}` : _hrStockQtyText(v);
}

function _hrSupplyWhen(v) {
  const ms = _hrSupplyMs(v);
  if (ms == null) return '';
  const opts = { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' };
  const tz = window.AppState?.settings?.timezone;
  try {
    return new Date(ms).toLocaleString('en-US', tz ? { ...opts, timeZone: tz } : opts);
  } catch (_) {
    return new Date(ms).toLocaleString('en-US', opts);
  }
}

/** Every ledger row for one item (equality filter only — no composite index). */
async function _hrFetchAllSupplyRows(assetId) {
  const tid = _hrSupplyTenant();
  const snap = await getDocs(query(collection(db, 'tenants', tid, 'assetTransactions'), where('assetId', '==', assetId)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// The rows behind the Inventory cards: one read per item, re-read only when
// the item's count or last move changes (the stockTracked listener sees that).
function _hrSupplyRowsKey(a) {
  return [_hrSupplyMs(a.lastSupplyMoveAt), _hrSupplyMs(a.ledgerOpenedAt), Number(a.quantity) || 0].join('|');
}

function _hrEnsureSupplyRows(assets) {
  const cache = HRState.supplyRows || (HRState.supplyRows = {});
  (assets || []).forEach((a) => {
    if (!a || !a.id) return;
    const key = _hrSupplyRowsKey(a);
    const c = cache[a.id];
    if (c && (c.key === key || c.loading)) return;
    cache[a.id] = { key, loading: true, rows: c ? c.rows : null };
    _hrFetchAllSupplyRows(a.id)
      .then((rows) => { cache[a.id] = { key, loading: false, rows }; })
      .catch((e) => {
        console.warn('[HR] stock history could not be read:', e?.code || e?.message);
        cache[a.id] = { key, loading: false, rows: c ? c.rows : null, error: e?.code || 'error' };
      })
      .finally(() => _hrRefreshInventoryTab());
  });
}

function _hrRefreshInventoryTab() {
  if (HRState.currentView !== 'safety' || HRState.safetyMode !== 'manager') return;
  const tab = HRState.safetyManagerTab;
  if (tab !== 'inventory' && tab !== 'costs') return;
  const el = document.getElementById('safety-manager-content');
  if (el) el.innerHTML = getInventoryHTML();
}

/** The mismatch strip: Started · Received · Taken · Returned · Counted · On hand. */
function _hrSupplyStripHTML(asset, rowsOverride) {
  const c = rowsOverride ? { rows: rowsOverride } : (HRState.supplyRows || {})[asset.id];
  const wrap = (inner) => `<div class="ppe-stock-strip" data-asset-id="${window.escAttr(asset.id)}" style="margin-top:10px;">${inner}</div>`;
  if (!c || (!c.rows && c.loading)) return wrap('<div style="font-size:0.78rem;color:var(--text-secondary);">Loading history&hellip;</div>');
  if (!c.rows) return wrap('<div style="font-size:0.78rem;color:var(--text-secondary);">History could not be loaded just now.</div>');
  if (!window.PpeReport?.ledgerTotals) return '';
  const t = window.PpeReport.ledgerTotals(c.rows);
  if (!t.openId) return '';
  const q = Number(asset.quantity) || 0;
  const stat = (label, val, cls, color) => `
    <div class="ppe-strip-stat ${cls}" style="min-width:0;background:var(--bg-primary);border:1px solid var(--border-light);border-radius:8px;padding:6px 8px;">
      <div style="font-size:0.68rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.02em;">${label}</div>
      <div class="ppe-strip-val" style="font-size:1.05rem;font-weight:800;color:${color || 'var(--text-primary)'};">${val}</div>
    </div>`;
  const corr = t.counted + t.adjusted;
  const lc = t.lastCount;
  return wrap(`
    <div class="ppe-strip-grid" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(88px,1fr));gap:6px;">
      ${stat('Started', _hrStockQtyText(t.opening), 'is-opening')}
      ${stat('Received', _hrStockQtyText(t.received), 'is-received')}
      ${stat('Taken', _hrStockQtyText(t.taken), 'is-taken')}
      ${stat('Returned', _hrStockQtyText(t.returned), 'is-returned')}
      ${stat('Counted', corr === 0 ? '0' : _hrSupplySigned(corr), 'is-counted', corr < 0 ? '#b91c1c' : null)}
      ${stat('On hand', _hrStockQtyText(q), 'is-onhand', q < 0 ? '#b91c1c' : null)}
    </div>
    <div style="font-size:0.74rem;color:var(--text-secondary);margin-top:6px;line-height:1.4;">
      ${t.voided ? `${t.voided} voided sign-out${t.voided === 1 ? '' : 's'} not counted. ` : ''}${lc ? `Last shelf count ${escapeHtml(_hrSupplyWhen(lc.at))}: system said ${escapeHtml(_hrStockQtyText(lc.expected))}, shelf had ${escapeHtml(_hrStockQtyText(lc.counted))}${lc.delta ? ` (${escapeHtml(_hrSupplySigned(lc.delta))})` : ' (matched)'}.` : 'Not counted on the shelf yet.'}
    </div>`);
}

/** The in-app alert banner (TASK-9275 styling): below zero, below par, shelf count off today. */
function _hrSupplyBannerHTML(assets) {
  const PR = window.PpeReport;
  if (!PR?.alertFlags) return '';
  const since = Date.now() - SUPPLY_DAY_MS;
  const neg = []; const par = []; const off = [];
  (assets || []).forEach((a) => {
    if (!a || a.active === false) return;
    const rows = ((HRState.supplyRows || {})[a.id] || {}).rows || [];
    const f = PR.alertFlags(a, { countRows: rows, sinceMs: since });
    const name = escapeHtml(a.name || 'Item');
    if (f.negative) neg.push(`<strong>${name}</strong> (${escapeHtml(_hrStockQtyText(a.quantity))})`);
    if (f.belowPar) par.push(`<strong>${name}</strong> (${escapeHtml(String(Number(a.quantity) || 0))} of ${escapeHtml(String(a.parLevel))})`);
    if (f.countVariance) off.push(`<strong>${name}</strong> (${escapeHtml(_hrSupplySigned(f.countVariance.net))})`);
  });
  const box = (cls, border, bg, color, text, icon, title, list, tail) => `
    <div class="${cls}" role="status" style="margin:0 0 var(--spacing-sm);padding:10px 12px;border:1px solid ${border};border-left:4px solid ${color};border-radius:8px;background:${bg};color:${text};font-size:0.82rem;">
      <div style="display:flex;align-items:center;gap:6px;font-weight:700;"><span class="material-icons" style="font-size:18px;">${icon}</span> ${title}</div>
      <div style="margin-top:4px;">${list.join(', ')} &mdash; ${tail}</div>
    </div>`;
  let html = '';
  if (neg.length) html += box('ppe-stock-banner ppe-stock-banner--negative', '#FECACA', '#FEF2F2', '#DC2626', '#991B1B', 'inventory_2', `${neg.length} item${neg.length === 1 ? '' : 's'} below zero`, neg, 'more went out than was counted in. Count the shelf, then save it with Count now.');
  if (par.length) html += box('ppe-stock-banner ppe-stock-banner--par', '#FCD34D', '#FFFBEB', '#D97706', '#92400E', 'production_quantity_limits', `${par.length} item${par.length === 1 ? '' : 's'} below the reorder level`, par, 'time to order more.');
  if (off.length) html += box('ppe-stock-banner ppe-stock-banner--count', '#FCD34D', '#FFFBEB', '#D97706', '#92400E', 'rule', 'Shelf count did not match today', off, 'the difference is on record in History.');
  return html;
}

async function _hrGetStockAsset(assetId) {
  const local = (HRState.stockAssets || []).find(a => a.id === assetId);
  if (local) return local;
  const tid = _hrSupplyTenant();
  if (!tid || !assetId) return null;
  try {
    const s = await getDoc(doc(db, 'tenants', tid, 'assets', assetId));
    return s.exists() ? { id: s.id, ...s.data() } : null;
  } catch (e) {
    console.warn('[HR] item read failed:', e?.code || e?.message);
    return null;
  }
}

// After a write lands, drop the cached rows so the strip and History re-read.
function _hrSupplyAfterWrite(assetId) {
  if (HRState.supplyRows) delete HRState.supplyRows[assetId];
  _hrRefreshInventoryTab();
  const h = HRState._supplyHist;
  if (h && h.assetId === assetId && document.getElementById('ppe-history-overlay')) openSupplyHistory(assetId, h.opts);
}

// ---- Count now --------------------------------------------------------------
const _SUPPLY_COUNT_REASONS = ['Regular count', 'Spot check', 'Count looked wrong', 'Restocked the shelf', 'Other'];

async function openSupplyCountSheet(assetId, opts) {
  const o = opts || {};
  const canManage = typeof o.canManage === 'boolean' ? o.canManage : _hrCanIssueEquipment();
  if (!canManage) { window.showToast?.('Ask someone with HR or Roster access to count stock.', 'error'); return; }
  const asset = await _hrGetStockAsset(assetId);
  if (!asset) { window.showToast?.('Item not found', 'error'); return; }
  if (asset.stockTracked !== true) { window.showToast?.('Stock is not being counted for that item.', 'info'); return; }
  HRState._supplyCount = { assetId, asset, opts: o };
  HRState._stockReqId = null;
  const uom = _hrSupplyUomWord(asset);
  _hrTrackStockOverlay('ppe-count-overlay', `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-sm);">
      <h3 style="margin:0;font-size:1rem;color:var(--text-primary);">Count now: ${escapeHtml(asset.name || '')}</h3>
      <button type="button" class="btn-icon" aria-label="Close" style="min-width:48px;min-height:48px;" onclick="document.getElementById('ppe-count-overlay')?.remove()"><span class="material-icons">close</span></button>
    </div>
    <div style="font-size:0.88rem;color:var(--text-primary);margin-bottom:10px;">The system says <strong id="ppe-count-expected">${escapeHtml(_hrStockQtyText(asset.quantity))}</strong>${uom ? ' ' + escapeHtml(uom) : ''}.</div>
    <label for="ppe-count-qty" style="display:block;font-size:0.9rem;font-weight:700;color:var(--text-primary);margin-bottom:4px;">How many are on the shelf right now?</label>
    <input type="number" id="ppe-count-qty" class="form-control" inputmode="numeric" min="0" step="1" placeholder="Count the shelf" oninput="window.HRModule._supplyCountPreview()" style="font-size:1.5rem;min-height:56px;">
    <div id="ppe-count-preview" aria-live="polite" style="min-height:22px;font-size:0.85rem;color:var(--text-primary);margin-top:6px;"></div>
    <label for="ppe-count-reason" style="display:block;font-size:0.9rem;font-weight:700;color:var(--text-primary);margin:10px 0 4px;">Why are you counting? (required)</label>
    <select id="ppe-count-reason" class="form-control" style="min-height:48px;height:48px;" onchange="document.getElementById('ppe-count-error').textContent=''">
      <option value="">Pick one&hellip;</option>
      ${_SUPPLY_COUNT_REASONS.map(r => `<option>${escapeHtml(r)}</option>`).join('')}
    </select>
    <label for="ppe-count-note" style="display:block;font-size:0.8rem;color:var(--text-secondary);margin:10px 0 4px;">Note (optional)</label>
    <input type="text" id="ppe-count-note" class="form-control" maxlength="120" placeholder="e.g. bin 4 behind the shop" style="min-height:48px;height:48px;">
    <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:10px;line-height:1.45;">The count becomes what you type. The old number, the new number and the difference are kept in History.</div>
    <div id="ppe-count-error" role="alert" style="min-height:18px;font-size:0.82rem;color:var(--status-danger);margin-top:6px;"></div>
    <div style="display:flex;gap:16px;justify-content:space-between;margin-top:8px;flex-wrap:wrap;">
      <button type="button" class="btn btn-secondary" style="min-height:48px;height:48px;min-width:110px;" onclick="document.getElementById('ppe-count-overlay')?.remove()">Cancel</button>
      <button type="button" id="btn-ppe-count" class="btn btn-primary" style="min-height:48px;height:48px;min-width:150px;" onclick="window.HRModule.submitSupplyCount()"><span class="material-icons" style="font-size:18px;">fact_check</span> Save count</button>
    </div>`);
  setTimeout(() => document.getElementById('ppe-count-qty')?.focus(), 50);
}

function _supplyCountPreview() {
  const st = HRState._supplyCount;
  const out = document.getElementById('ppe-count-preview');
  const errEl = document.getElementById('ppe-count-error');
  if (errEl) errEl.textContent = '';
  if (!st || !out) return;
  const raw = String(document.getElementById('ppe-count-qty')?.value || '').trim();
  if (!/^\d+$/.test(raw)) { out.textContent = ''; return; }
  const counted = parseInt(raw, 10);
  const prev = Number(st.asset.quantity) || 0;
  const d = counted - prev;
  out.innerHTML = d === 0
    ? '<span class="material-icons" style="font-size:16px;vertical-align:-3px;color:#15803d;">check_circle</span> Matches the system. Saving records that the shelf was counted.'
    : `Count goes from <strong>${escapeHtml(_hrStockQtyText(prev))}</strong> to <strong>${escapeHtml(_hrStockQtyText(counted))}</strong> (<strong style="color:${d < 0 ? '#b91c1c' : '#15803d'};">${escapeHtml(_hrSupplySigned(d))}</strong>).`;
}

async function submitSupplyCount() {
  const st = HRState._supplyCount;
  if (!st) return;
  const errEl = document.getElementById('ppe-count-error');
  const raw = String(document.getElementById('ppe-count-qty')?.value || '').trim();
  if (!/^\d+$/.test(raw)) {
    if (errEl) errEl.textContent = 'Enter how many are on the shelf (0 or more, whole number).';
    document.getElementById('ppe-count-qty')?.focus();
    return;
  }
  const why = document.getElementById('ppe-count-reason')?.value || '';
  if (!why) { if (errEl) errEl.textContent = 'Pick why you are counting.'; document.getElementById('ppe-count-reason')?.focus(); return; }
  const note = (document.getElementById('ppe-count-note')?.value || '').trim();
  const reason = [why, note].filter(Boolean).join(' — ');
  const countedQty = parseInt(raw, 10);
  const btn = document.getElementById('btn-ppe-count');
  if (btn) { if (btn.disabled) return; btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving…'; }
  try {
    const res = await httpsCallable(functions, 'recordSupplyStock')({ assetId: st.assetId, kind: 'count', countedQty, seenQty: Number(st.asset.quantity) || 0, reason, requestId: _hrStockReqId() });
    HRState._stockReqId = null;
    HRState._supplyCount = null;
    document.getElementById('ppe-count-overlay')?.remove();
    const r = res?.data || {};
    const d = Number(r.qtyDelta) || 0;
    window.showToast?.(d === 0
      ? `Counted: ${_hrStockQtyText(r.balance)} on hand — matches`
      : `Counted: ${_hrStockQtyText(r.balance)} on hand (was ${_hrStockQtyText(r.previous)}, ${_hrSupplySigned(d)})`, 'success');
    // The count changed while the sheet was open (a sign-out or return landed).
    if (typeof r.sinceSeen === 'number' && r.sinceSeen !== 0) {
      window.showToast?.(`Heads up: the count moved by ${_hrSupplySigned(r.sinceSeen)} while you were counting. Your shelf count was saved as typed.`, 'info');
    }
    if (typeof st.opts.onDone === 'function') { try { st.opts.onDone(r); } catch (_) { /* host refresh only */ } }
    _hrSupplyAfterWrite(st.assetId);
  } catch (err) {
    console.error('[HR] count failed:', err?.code, err?.message);
    if (errEl) errEl.textContent = err?.message || 'Could not save just now. Try again.';
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:18px;">fact_check</span> Save count'; }
  }
}

// ---- Adjust / Receive preview (old → new) -------------------------------------
function _hrStockMovePreview() {
  const mv = HRState._stockMove;
  const out = document.getElementById('ppe-move-preview');
  const errEl = document.getElementById('ppe-move-error');
  if (errEl) errEl.textContent = '';
  if (!mv || !out) return;
  const asset = (HRState.stockAssets || []).find(a => a.id === mv.assetId);
  const raw = String(document.getElementById('ppe-move-qty')?.value || '').trim();
  if (!asset || !/^[-+]?\d+$/.test(raw)) { out.textContent = ''; return; }
  const qty = parseInt(raw, 10);
  if (!qty || (mv.kind === 'receive' && qty < 1)) { out.textContent = ''; return; }
  const prev = Number(asset.quantity) || 0;
  out.innerHTML = `On hand goes from <strong>${escapeHtml(_hrStockQtyText(prev))}</strong> to <strong>${escapeHtml(_hrStockQtyText(prev + qty))}</strong>.`;
}

// ---- Item History -------------------------------------------------------------
// Paged newest-first with a cursor (index assetTransactions assetId ASC +
// createdAt DESC, firestore.indexes.json). Until that index is built the query
// fails `failed-precondition`; the view then reads the item's rows once and
// pages them in memory — same order, same pages.
const _SUPPLY_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'out', label: 'Sign-outs', types: ['supply_issue', 'supply_use', 'dispatch', 'ticket'] }, // TASK-9730 supply_use = job / ticket use
  { key: 'in', label: 'Received & returned', types: ['supply_receive', 'receipt', 'supply_return'] },
  { key: 'count', label: 'Counts & adjustments', types: ['supply_count', 'supply_adjust', 'adjustment', 'supply_open', 'supply_close'] },
  { key: 'void', label: 'Voids', types: ['supply_void'] },
];

async function openSupplyHistory(assetId, opts) {
  const o = opts || {};
  const asset = await _hrGetStockAsset(assetId);
  if (!asset) { window.showToast?.('Item not found', 'error'); return; }
  const prevH = HRState._supplyHist;
  HRState._supplyHist = {
    assetId, asset, opts: o, filter: (prevH && prevH.assetId === assetId && prevH.filter) || 'all',
    rows: [], cursor: null, hasMore: false, mode: 'indexed', all: null, loading: false, error: '',
    issues: {}, issuesHidden: false, totalsRows: null, names: {},
    cost: null, rcpt: {}, // TASK-9724 S2 — cost access + receipt prices (cost logins only)
  };
  document.getElementById('ppe-history-overlay')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'ppe-history-overlay';
  overlay.className = 'ppe-sheet-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:10000;display:flex;align-items:center;justify-content:center;padding:12px;';
  overlay.innerHTML = `<div role="dialog" aria-modal="true" aria-labelledby="ppe-history-title" class="ppe-history-sheet" style="background:var(--bg-primary);border-radius:var(--radius-lg);padding:var(--spacing-md);max-width:640px;width:100%;max-height:92vh;max-height:92dvh;overflow-y:auto;"><div id="ppe-history-body"></div></div>`;
  document.body.appendChild(overlay);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  _hrRenderSupplyHistory();
  // The strip totals the whole current tracking period; the list pages.
  const mine = HRState._supplyHist;
  _hrFetchAllSupplyRows(assetId).then((rows) => {
    if (HRState._supplyHist === mine) { mine.totalsRows = rows; _hrRenderSupplyHistory(); }
  }).catch(() => {
    if (HRState._supplyHist === mine) { mine.totalsRows = []; _hrRenderSupplyHistory(); }
  });
  await _hrSupplyHistLoadMore();
}

async function _hrSupplyHistLoadMore() {
  const h = HRState._supplyHist;
  if (!h || h.loading) return;
  h.loading = true; h.error = '';
  _hrRenderSupplyHistory();
  const assetId = h.assetId;
  const added = [];
  try {
    const col = collection(db, 'tenants', _hrSupplyTenant(), 'assetTransactions');
    if (h.mode === 'indexed') {
      try {
        const parts = [where('assetId', '==', assetId), orderBy('createdAt', 'desc')];
        if (h.cursor) parts.push(startAfter(h.cursor));
        parts.push(limit(SUPPLY_HISTORY_PAGE + 1));
        const snap = await getDocs(query(col, ...parts));
        const page = snap.docs.slice(0, SUPPLY_HISTORY_PAGE);
        h.hasMore = snap.docs.length > SUPPLY_HISTORY_PAGE;
        if (page.length) h.cursor = page[page.length - 1];
        page.forEach(d => added.push({ id: d.id, ...d.data() }));
      } catch (e) {
        if (e?.code !== 'failed-precondition') throw e;
        h.mode = 'all';
      }
    }
    if (h.mode === 'all') {
      if (!h.all) h.all = window.PpeReport.sortRows(await _hrFetchAllSupplyRows(assetId));
      const pg = window.PpeReport.historyPage(h.all, h.rows.length, SUPPLY_HISTORY_PAGE);
      h.hasMore = pg.hasMore;
      pg.rows.forEach(r => added.push(r));
    }
    if (HRState._supplyHist !== h) return;
    h.rows = h.rows.concat(added);
    await Promise.all([_hrSupplyHistJoinIssues(h, added), _hrSupplyHistJoinNames(h, added), _hrSupplyHistJoinReceipts(h, added)]);
  } catch (e) {
    console.error('[HR] history read failed:', e?.code, e?.message);
    h.error = e?.code === 'permission-denied' ? 'You do not have access to this history.' : 'Could not load history just now. Try again.';
  }
  h.loading = false;
  if (HRState._supplyHist === h) _hrRenderSupplyHistory();
}

// TASK-9724 S2 — does a received delivery carry a price? Only a login that may
// see cost (owner / BI / Billing: window.RosterModule.supplyCostState) asks,
// and only while the owner's switch is on; anyone else never reads the cost
// home and the row shows exactly as before. The receipt's cost doc id is
// derived from the ledger row id (supply_receive_<x> -> receipt_<x>).
async function _hrSupplyHistJoinReceipts(h, rows) {
  if (h.cost === null) {
    try { h.cost = (await window.RosterModule?.supplyCostState?.()) || { see: false }; } catch (_) { h.cost = { see: false }; }
  }
  if (!h.cost || !h.cost.see || !h.cost.on) return;
  const ids = [...new Set((rows || []).filter(r => r.type === 'supply_receive' && typeof r.id === 'string' && r.id.startsWith('supply_receive_')).map(r => r.id))]
    .filter(id => !(id in h.rcpt));
  if (!ids.length) return;
  const tid = _hrSupplyTenant();
  await Promise.all(ids.map(async (id) => {
    h.rcpt[id] = null;
    try {
      const s = await getDoc(doc(db, 'tenants', tid, 'assetCosts', 'receipt_' + id.slice('supply_receive_'.length)));
      const d = s.exists() ? (s.data() || {}) : null;
      h.rcpt[id] = d && typeof d.unitCost === 'number' && d.unitCost > 0 ? { unitCost: d.unitCost, pricedLater: !!d.pricedLater } : null;
    } catch (_) { h.rcpt[id] = undefined; } // unreadable = say nothing
  }));
}

// TASK-9724 S2 — "Add price" on an unpriced receipt row: an inline box, then
// priceSupplyReceipt (owner / Billing, switch on; audited). Sign-outs already
// booked keep their cost — the server re-values only what is on the shelf.
function priceSupplyReceiptRow(rowId, action) {
  const h = HRState._supplyHist;
  const li = document.querySelector(`#ppe-history-overlay li[data-row-id="${window.CSS && CSS.escape ? CSS.escape(rowId) : rowId}"]`);
  if (!h || !li) return;
  const box = li.querySelector('.ppe-history-price');
  if (!box) return;
  if (action === 'cancel') { _hrRenderSupplyHistory(); return; }
  if (action !== 'save') {
    box.innerHTML = `<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
      <input type="text" class="form-control ppe-history-price-in" inputmode="decimal" placeholder="$ each" aria-label="Price paid per unit" style="min-height:48px;height:48px;max-width:120px;">
      <button type="button" class="btn btn-primary btn-sm" style="min-height:48px;height:48px;" onclick="window.HRModule.priceSupplyReceiptRow('${escJsAttr(rowId)}','save')">Save price</button>
      <button type="button" class="btn btn-secondary btn-sm" style="min-height:48px;height:48px;" onclick="window.HRModule.priceSupplyReceiptRow('${escJsAttr(rowId)}','cancel')">Cancel</button>
      <div class="ppe-history-price-err" role="alert" style="flex-basis:100%;font-size:0.8rem;color:var(--status-danger);"></div>
    </div>`;
    box.querySelector('input')?.focus();
    return;
  }
  const inp = box.querySelector('.ppe-history-price-in');
  const err = box.querySelector('.ppe-history-price-err');
  const raw = String(inp?.value || '').trim().replace(/^\$/, '');
  if (!/^\d+(\.\d{1,4})?$/.test(raw) || !(parseFloat(raw) > 0)) { if (err) err.textContent = 'Enter the price paid per unit, more than $0 (for example 2.49).'; return; }
  const btns = box.querySelectorAll('button');
  btns.forEach(b => { b.disabled = true; });
  httpsCallable(functions, 'priceSupplyReceipt')({ ledgerRowId: rowId, unitCost: parseFloat(raw) }).then((res) => {
    const d = res?.data || {};
    if (HRState._supplyHist === h) h.rcpt[rowId] = { unitCost: parseFloat(raw), pricedLater: true };
    const avg = d.cost && typeof d.cost.avgUnitCost === 'number' ? d.cost.avgUnitCost : null;
    window.showToast?.(d.changed === false ? 'That receipt already had this price.' : (avg == null ? 'Price saved.' : `Price saved. Average unit cost is now $${avg.toFixed(2)}.`), 'success');
    if (HRState._supplyHist === h) _hrRenderSupplyHistory();
  }).catch((e) => {
    console.error('[HR] priceSupplyReceipt failed:', e?.code, e?.message);
    if (err) err.textContent = e?.message || 'Could not save the price just now. Try again.';
    btns.forEach(b => { b.disabled = false; });
  });
}

/** The signed sign-out behind an issue / return / void row — HR access only. */
async function _hrSupplyHistJoinIssues(h, rows) {
  if (h.issuesHidden) return;
  const ids = [...new Set((rows || []).filter(r => r.sourceType === 'safetyEquipment' && r.sourceId).map(r => String(r.sourceId)))]
    .filter(id => !(id in h.issues));
  if (!ids.length) return;
  const known = new Map((HRState.safetyEquipmentRecords || []).map(r => [r.id, r]));
  const tid = _hrSupplyTenant();
  const need = [];
  ids.forEach(id => { if (known.has(id)) h.issues[id] = known.get(id); else need.push(id); });
  for (let i = 0; i < need.length; i += 10) {
    const chunk = need.slice(i, i + 10);
    const res = await Promise.all(chunk.map(id => getDoc(doc(db, 'tenants', tid, 'safetyEquipment', id))
      .then(s => (s.exists() ? { id: s.id, ...s.data() } : null))
      .catch(e => (e?.code === 'permission-denied' ? 'denied' : null))));
    if (res.includes('denied')) { h.issuesHidden = true; return; }
    chunk.forEach((id, k) => { h.issues[id] = res[k]; });
  }
}

function _hrSupplyRowWho(r, h) {
  const rec = r.sourceType === 'safetyEquipment' ? h.issues[String(r.sourceId)] : null;
  if (r.type === 'supply_issue' || r.type === 'supply_return' || r.type === 'supply_void') {
    const person = rec ? (rec.personName || 'Unknown') : (h.issuesHidden ? 'Open HR to see who' : '');
    const dir = r.type === 'supply_issue' ? 'To' : (r.type === 'supply_return' ? 'Back from' : 'Was for');
    let by = '';
    if (r.type === 'supply_issue') by = r.issueSource === 'driver' ? 'logged and signed on their phone' : 'issued by the office';
    else if (r.type === 'supply_void' && rec && rec.voidedByName) by = `voided by ${rec.voidedByName}`;
    else if (r.type === 'supply_return' && rec && rec.returnedBy) by = `by ${rec.returnedBy}`;
    return { who: person ? `${dir} ${person}` : '', by };
  }
  // The ledger keeps only the staff uid (no names on a driver-readable
  // collection — supplyLedger.js invariant 6); _hrSupplyHistJoinNames turns it
  // into a name from members (member-readable only).
  const uid = r.recordedByUid || (r.server ? '' : r.issuedBy);
  const name = (uid && h.names && h.names[uid]) || (r.server ? 'Office' : (r.issuedBy ? 'Office (older entry)' : ''));
  return { who: '', by: name ? `by ${name}` : '' };
}

function _hrRenderSupplyHistory() {
  const h = HRState._supplyHist;
  const body = document.getElementById('ppe-history-body');
  if (!h || !body) return;
  const PR = window.PpeReport;
  const asset = h.asset;
  const filt = _SUPPLY_FILTERS.find(f => f.key === h.filter) || _SUPPLY_FILTERS[0];
  const voidedSrc = new Set();
  h.rows.forEach(r => { if (r.type === 'supply_void' && r.sourceId) voidedSrc.add(String(r.sourceId)); });
  Object.keys(h.issues).forEach(id => { if (h.issues[id] && h.issues[id].voidedAt) voidedSrc.add(id); });
  const shown = filt.types ? h.rows.filter(r => filt.types.includes(r.type)) : h.rows;
  const items = shown.map((r) => {
    const d = PR.rowDelta(r);
    const label = PR.rowLabel(r);
    const isVoidedIssue = r.type === 'supply_issue' && voidedSrc.has(String(r.sourceId || ''));
    const w = _hrSupplyRowWho(r, h);
    const rec = r.sourceType === 'safetyEquipment' ? h.issues[String(r.sourceId)] : null;
    const bal = r.balanceAfter != null ? r.balanceAfter : r.newQty;
    const moved = r.type === 'supply_count' && r.seenQty != null && Number(r.seenQty) !== Number(r.expectedQty);
    const extra = r.type === 'supply_count' ? `System said ${_hrStockQtyText(r.expectedQty)}, shelf had ${_hrStockQtyText(r.countedQty)}${moved ? ` (screen showed ${_hrStockQtyText(r.seenQty)} when counting started)` : ''}` : '';
    return `
      <li class="ppe-history-row" data-type="${window.escAttr(r.type || '')}" data-row-id="${window.escAttr(String(r.id || ''))}" style="list-style:none;border:1px solid var(--border-light);border-radius:10px;padding:10px 12px;background:var(--bg-secondary);">
        <div style="display:flex;justify-content:space-between;gap:8px;align-items:flex-start;">
          <div style="min-width:0;flex:1;">
            <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);"><span style="${isVoidedIssue ? 'text-decoration:line-through;' : ''}">${escapeHtml(label)}${r.size ? ' (' + escapeHtml(String(r.size)) + ')' : ''}</span>${isVoidedIssue ? ' <span class="ppe-history-voided" style="font-size:0.68rem;font-weight:700;color:#92400e;background:#fef3c7;border:1px solid #fde68a;border-radius:999px;padding:1px 7px;">Voided</span>' : ''}</div>
            <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:2px;">${escapeHtml(_hrSupplyWhen(r.createdAt) || (r.dateReceived || ''))}</div>
          </div>
          <div style="text-align:right;">
            <div class="ppe-history-delta" style="font-weight:800;font-size:1.05rem;color:${d < 0 ? '#b91c1c' : (d > 0 ? '#15803d' : 'var(--text-primary)')};">${escapeHtml(d === 0 ? '0' : _hrSupplySigned(d))}</div>
            ${bal != null ? `<div style="font-size:0.72rem;color:var(--text-secondary);">left: ${escapeHtml(_hrStockQtyText(bal))}</div>` : ''}
          </div>
        </div>
        ${w.who || w.by ? `<div class="ppe-history-who" style="font-size:0.8rem;color:var(--text-primary);margin-top:4px;">${escapeHtml([w.who, w.by].filter(Boolean).join(' · '))}</div>` : ''}
        ${extra ? `<div style="font-size:0.8rem;color:var(--text-primary);margin-top:2px;">${escapeHtml(extra)}</div>` : ''}
        ${r.reason && r.type !== 'supply_issue' ? `<div class="ppe-history-why" style="font-size:0.8rem;color:var(--text-secondary);margin-top:2px;word-break:break-word;">Why: ${escapeHtml(String(r.reason))}</div>` : ''}
        ${rec ? `<div style="margin-top:8px;"><button type="button" class="btn btn-secondary btn-sm ppe-history-open-issue" style="min-height:48px;height:48px;" onclick="window.HRModule.openSupplyIssueRecord('${escJsAttr(String(r.sourceId))}')"><span class="material-icons" style="font-size:16px;">draw</span> View signed sign-out</button></div>` : ''}
        ${_hrSupplyReceiptPriceHtml(r, h)}
      </li>`;
  }).join('');
  const strip = h.totalsRows ? _hrSupplyStripHTML(asset, h.totalsRows) : '<div style="font-size:0.78rem;color:var(--text-secondary);margin-top:8px;">Loading totals&hellip;</div>';
  body.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;">
      <h3 id="ppe-history-title" style="margin:0;font-size:1.05rem;color:var(--text-primary);">History: ${escapeHtml(asset.name || '')}</h3>
      <button type="button" class="btn-icon" aria-label="Close" style="min-width:48px;min-height:48px;" onclick="document.getElementById('ppe-history-overlay')?.remove()"><span class="material-icons">close</span></button>
    </div>
    ${strip}
    <div role="group" aria-label="Show" style="display:flex;gap:6px;flex-wrap:wrap;margin:12px 0 8px;">
      ${_SUPPLY_FILTERS.map(f => `<button type="button" aria-pressed="${f.key === filt.key}" class="btn btn-sm ppe-history-filter" data-filter="${f.key}" style="min-height:48px;height:48px;${f.key === filt.key ? 'background:var(--brand-blue);color:#fff;' : 'background:var(--bg-secondary);color:var(--text-primary);'}" onclick="window.HRModule.setSupplyHistoryFilter('${f.key}')">${escapeHtml(f.label)}</button>`).join('')}
    </div>
    ${h.issuesHidden ? '<div style="font-size:0.78rem;color:var(--text-secondary);margin-bottom:8px;">Who took each item shows for people with HR access.</div>' : ''}
    <ul class="ppe-history-list" style="display:grid;gap:8px;margin:0;padding:0;">${items || (h.loading ? '' : `<li style="list-style:none;font-size:0.85rem;color:var(--text-secondary);padding:12px 0;">${h.rows.length ? 'Nothing of this kind in what is loaded.' : 'No history yet.'}</li>`)}</ul>
    ${h.error ? `<div role="alert" style="font-size:0.82rem;color:var(--status-danger);margin-top:8px;">${escapeHtml(h.error)}</div>` : ''}
    <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:12px;">
      ${h.loading ? '<div style="font-size:0.85rem;color:var(--text-secondary);padding:12px 0;"><span class="btn-spinner"></span> Loading&hellip;</div>' : (h.hasMore ? '<button type="button" class="btn btn-secondary ppe-history-more" style="min-height:48px;height:48px;flex:1 1 160px;" onclick="window.HRModule.loadMoreSupplyHistory()"><span class="material-icons" style="font-size:18px;">expand_more</span> Show older</button>' : '')}
      <button type="button" class="btn btn-secondary" style="min-height:48px;height:48px;flex:1 1 120px;" onclick="window.HRModule.printSupplyHistory()"><span class="material-icons" style="font-size:18px;">print</span> Print</button>
      <button type="button" class="btn btn-secondary ppe-history-csv" style="min-height:48px;height:48px;flex:1 1 120px;" onclick="window.HRModule.exportSupplyHistoryCsv()"><span class="material-icons" style="font-size:18px;">download</span> CSV</button>
    </div>`;
}

// TASK-9724 S2 — the price line under a received-delivery row. Nothing at all
// unless this login may see cost AND the switch is on (both from the cost
// home); "Add price" only for a login that may edit cost (owner / Billing).
function _hrSupplyReceiptPriceHtml(r, h) {
  if (!r || r.type !== 'supply_receive' || !h.cost || !h.cost.see || !h.cost.on || !(r.id in h.rcpt)) return '';
  const p = h.rcpt[r.id];
  if (p === undefined) return '';
  if (p) return `<div class="ppe-history-price" style="font-size:0.8rem;color:var(--text-primary);margin-top:6px;">Priced $${escapeHtml(p.unitCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }))} each${p.pricedLater ? ' (added later)' : ''}</div>`;
  if (!h.cost.edit) return '<div class="ppe-history-price" style="font-size:0.8rem;color:var(--text-secondary);margin-top:6px;">No price on this receipt.</div>';
  return `<div class="ppe-history-price" style="margin-top:8px;"><button type="button" class="btn btn-secondary btn-sm ppe-history-add-price" style="min-height:48px;height:48px;" onclick="window.HRModule.priceSupplyReceiptRow('${escJsAttr(String(r.id))}')"><span class="material-icons" style="font-size:16px;">sell</span> Add price</button></div>`;
}

function setSupplyHistoryFilter(key) {
  const h = HRState._supplyHist;
  if (!h) return;
  h.filter = _SUPPLY_FILTERS.some(f => f.key === key) ? key : 'all';
  _hrRenderSupplyHistory();
}

function loadMoreSupplyHistory() { return _hrSupplyHistLoadMore(); }

function openSupplyIssueRecord(recordId) {
  const h = HRState._supplyHist;
  const rec = h ? h.issues[recordId] : null;
  if (!rec) { window.showToast?.('That sign-out is not available.', 'info'); return; }
  viewPpeDetail(recordId, rec);
}

/** Every row of the item, newest first, with the sign-out records joined — for Print / CSV. */
async function _hrSupplyHistAllRows() {
  const h = HRState._supplyHist;
  if (!h) return null;
  const rows = h.all || window.PpeReport.sortRows(await _hrFetchAllSupplyRows(h.assetId));
  await Promise.all([_hrSupplyHistJoinIssues(h, rows), _hrSupplyHistJoinNames(h, rows)]);
  return rows;
}

/** Who recorded an office row: members/{uid} name (member-readable), else Owner / Office. */
async function _hrSupplyHistJoinNames(h, rows) {
  const tid = _hrSupplyTenant();
  const me = window.AppState?.user;
  const td = window.AppState?.tenantData || {};
  const uids = [...new Set((rows || []).map(r => r.recordedByUid || (r.server ? '' : r.issuedBy))
    .filter(u => typeof u === 'string' && u && u !== 'unknown' && !u.includes('/') && u.length <= 128))]
    .filter(u => !(u in h.names));
  await Promise.all(uids.map(async (uid) => {
    h.names[uid] = '';
    if (me && uid === me.uid && (window.AppState?.userName || me.displayName || me.email)) {
      h.names[uid] = String(window.AppState?.userName || me.displayName || me.email);
      return;
    }
    try {
      const s = await getDoc(doc(db, 'tenants', tid, 'members', uid));
      const m = s.exists() ? (s.data() || {}) : null;
      if (m) h.names[uid] = String(m.name || m.displayName || m.email || '');
    } catch (_) { /* a driver/PIN session cannot read members — falls back below */ }
    if (!h.names[uid] && td.ownerId && uid === td.ownerId) h.names[uid] = String(td.ownerName || 'Owner');
  }));
}

function _hrSupplyExportLine(r, h) {
  const PR = window.PpeReport;
  const w = _hrSupplyRowWho(r, h);
  const bal = r.balanceAfter != null ? r.balanceAfter : r.newQty;
  const rec = r.sourceType === 'safetyEquipment' ? h.issues[String(r.sourceId)] : null;
  let why = r.type === 'supply_issue' ? '' : String(r.reason || '');
  if (r.type === 'supply_count') why = `${why}${why ? ' — ' : ''}system ${r.expectedQty}, shelf ${r.countedQty}`;
  return {
    when: _hrSupplyWhen(r.createdAt) || (r.dateReceived || ''),
    what: PR.rowLabel(r),
    change: PR.rowDelta(r),
    left: bal != null && isFinite(Number(bal)) ? Number(bal) : '',
    who: w.who,
    by: w.by.replace(/^by /, ''),
    reason: why,
    size: String(r.size || ''),
    voided: rec && rec.voidedAt ? 'Yes' : '',
    recordId: r.sourceType === 'safetyEquipment' ? String(r.sourceId || '') : '',
  };
}

// CSV: numbers go out as numbers (a −4 is data, not a formula); every TEXT cell
// goes through csvSafeCell, which defuses = + - @ and a leading tab/CR.
function _hrSupplyCsvCell(v) {
  if (typeof v === 'number' && isFinite(v)) return String(v);
  return csvSafeCell(v);
}

function _hrSupplyCsvText(h, rows) {
  const lines = rows.map(r => _hrSupplyExportLine(r, h));
  const table = [
    ['Stock history', h.asset.name || '', `On hand ${Number(h.asset.quantity) || 0}`],
    [],
    ['When', 'What', 'Change', 'Left after', 'To / from', 'Recorded by', 'Why', 'Size', 'Voided', 'Sign-out record'],
  ].concat(lines.map(l => [l.when, l.what, l.change, l.left, l.who, l.by, l.reason, l.size, l.voided, l.recordId]));
  return table.map(r => r.map(_hrSupplyCsvCell).join(',')).join('\r\n');
}

async function exportSupplyHistoryCsv() {
  const h = HRState._supplyHist;
  if (!h) return;
  let rows;
  try { rows = await _hrSupplyHistAllRows(); } catch (e) { window.showToast?.('Could not load history to export.', 'error'); return; }
  if (!rows || !rows.length) { window.showToast?.('No history to export yet', 'info'); return; }
  const csv = _hrSupplyCsvText(h, rows);
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  const slug = String(h.asset.name || 'item').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40) || 'item';
  link.download = `stock-history-${slug}.csv`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 0);
}

async function printSupplyHistory() {
  const h = HRState._supplyHist;
  if (!h) return;
  let rows;
  try { rows = await _hrSupplyHistAllRows(); } catch (e) { window.showToast?.('Could not load history to print.', 'error'); return; }
  const t = window.PpeReport.ledgerTotals(rows || []);
  const lines = (rows || []).map(r => _hrSupplyExportLine(r, h));
  const meta = [
    `<strong>${escapeHtml(h.asset.name || 'Item')}</strong> · on hand ${escapeHtml(_hrStockQtyText(h.asset.quantity))}`,
    t.openId ? `Started ${escapeHtml(_hrStockQtyText(t.opening))} · Received ${escapeHtml(_hrStockQtyText(t.received))} · Taken ${escapeHtml(_hrStockQtyText(t.taken))} · Returned ${escapeHtml(_hrStockQtyText(t.returned))} · Counted ${escapeHtml(_hrSupplySigned(t.counted + t.adjusted))}` : '',
    `Printed ${escapeHtml(new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }))}`,
  ].filter(Boolean).join('<br>');
  const th = (x) => `<th>${x}</th>`;
  const body = `${_hrEquipPrintHeaderHtml('Stock History', meta)}
    <table><thead><tr>${['When', 'What', 'Change', 'Left', 'To / from', 'Recorded by', 'Why', 'Size'].map(th).join('')}</tr></thead><tbody>
    ${lines.map(l => `<tr><td>${escapeHtml(l.when)}</td><td>${escapeHtml(l.what)}${l.voided ? ' (voided)' : ''}</td><td>${escapeHtml(l.change === 0 ? '0' : _hrSupplySigned(l.change))}</td><td>${escapeHtml(l.left === '' ? '' : _hrStockQtyText(l.left))}</td><td>${escapeHtml(l.who)}</td><td>${escapeHtml(l.by)}</td><td>${escapeHtml(l.reason)}</td><td>${escapeHtml(l.size)}</td></tr>`).join('') || '<tr><td colspan="8">No history yet.</td></tr>'}
    </tbody></table>`;
  _hrEquipOpenPrint(`Stock History - ${h.asset.name || 'Item'}`, body);
}

/** Roster > Assets: the strip inside the item's edit form (host passes the element). */
async function renderSupplyStripInto(el, assetId) {
  if (!el) return;
  el.innerHTML = '<div style="font-size:0.78rem;color:var(--text-secondary);">Loading history&hellip;</div>';
  try {
    const asset = await _hrGetStockAsset(assetId);
    if (!asset) { el.innerHTML = ''; return; }
    const rows = await _hrFetchAllSupplyRows(assetId);
    el.innerHTML = _hrSupplyStripHTML(asset, rows);
  } catch (e) {
    el.innerHTML = '<div style="font-size:0.78rem;color:var(--text-secondary);">History could not be loaded just now.</div>';
  }
}

// ---- Optional worker signature on a Return (DECISION 6: optional, never required)
function _hrMiniSignaturePad(canvasId) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return null;
  const state = { has: false, drawing: false };
  const rect = canvas.getBoundingClientRect();
  canvas.width = Math.max(1, Math.round(rect.width * 2));
  canvas.height = Math.max(1, Math.round(rect.height * 2));
  const ctx = canvas.getContext('2d');
  ctx.scale(2, 2);
  ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.strokeStyle = '#111827';
  const pos = (e) => { const r = canvas.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
  canvas.addEventListener('pointerdown', (e) => {
    e.preventDefault(); state.drawing = true;
    try { canvas.setPointerCapture(e.pointerId); } catch (_) { /* older browsers */ }
    const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y);
  });
  canvas.addEventListener('pointermove', (e) => {
    if (!state.drawing) return;
    e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); state.has = true;
  });
  const stop = () => { state.drawing = false; };
  canvas.addEventListener('pointerup', stop);
  canvas.addEventListener('pointercancel', stop);
  canvas.addEventListener('pointerleave', stop);
  return {
    hasData: () => state.has,
    clear: () => { ctx.save(); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.restore(); state.has = false; },
    toDataURL: () => canvas.toDataURL('image/png'),
  };
}

// ============================================
// HIRING / APPLICANT TRACKING
// ============================================

/**
 * Compliance-only tenants are gated to Hiring/Applicants ONLY. Sub-views are
 * blocked at navigation time so a deep-link / stale UI / programmatic call
 * can't bypass the landing-page short-circuit at L743. Returns true if access
 * should be denied (caller bails). Shows the existing upgrade overlay so the
 * sidebar's locked-module tease keeps a consistent UX.
 *
 * Tier classification (matches sidebar `setupComplianceOnlySidebar`):
 *   - hiring:       UNLOCKED   (always allowed)
 *   - safety, files: VISIBLE-LOCKED (relevant compliance work, tease upgrade)
 *   - other:         HIDDEN     (operational, not applicable to compliance-only)
 *
 * TASK-1117 (Chris 2026-07-13) — the paid boundary is now HIRING + ONBOARDING/DQ
 * free; POST-HIRE employee management paywalled. Gate extended to: Employee
 * Directory ('directory' — openEmployeeDetail / openComplianceDirectory), memos
 * ('memos' — sub-view, composer, and submit since createMemoAndSend bypasses
 * rules), time-off admin ('timeoff' — openTimeOffRequest / approveTimeOff /
 * denyTimeOff, callable-backed), and openDisciplinaryForm. openDotAuditAccessModal
 * stays deliberately UNGATED — DQ/audit access is compliance, not post-hire mgmt.
 * firestore.rules carries the matching server-side write gates (the real belt).
 */
function _hrGateComplianceOnly(subView) {
  if (!window.AppState?.tenantData?.complianceOnly) return false; // paying tenant — no gate
  if (subView === 'hiring') return false;                          // always unlocked
  // Re-route compliance-only users back to the only unlocked sub-view, with an
  // upgrade nudge. The sidebar's `roster`/`dispatch`/etc. lock pattern uses
  // `showComplianceUpgradeOverlay` (defined in app.js) — reuse if available.
  try {
    if (typeof window.showComplianceUpgradeOverlay === 'function') {
      window.showComplianceUpgradeOverlay(`hr-${subView}`);
    } else if (typeof window.showToast === 'function') {
      window.showToast('Upgrade to access this HR section', 'info');
    }
  } catch (_) { /* non-fatal — bounce to hiring regardless */ }
  // Bounce back to the only unlocked HR sub-view so the user lands somewhere
  // useful instead of an empty container.
  try { openHiringSubView(); } catch (_) { /* swallow — defensive */ }
  return true;
}

// --- Navigation ---
function openHiringSubView() {
  HRState.currentView = 'hiring';
  if (!HRState.candidateSubscribed) {
    subscribeToCandidates(HRState.tenantId);
  }
  const container = document.getElementById('hr-content') || document.querySelector('.hr-view');
  if (!container) return;
  container.innerHTML = getHiringSubViewHTML();
  renderCandidatesList();
}

function backToHRLanding() {
  // Compliance-only tenants: stay on hiring (only unlocked HR feature)
  if (window.AppState?.tenantData?.complianceOnly) {
    openHiringSubView();
    return;
  }
  HRState.currentView = 'landing';
  // TASK-1215 — reset to the FULL default shape (sort:'date'). The old bare
  // {status,search} reset silently dropped sort/showArchived, which made the
  // sort fall back inconsistently after a landing round-trip.
  HRState.candidateFilter = { status: null, search: '', view: 'active', sort: 'date' };
  HRState.safetyMode = 'user';
  HRState.safetyManagerTab = 'people';
  HRState._safetySearch = ''; // TASK-840 — reset serial search on view switch
  // Clean up existing listeners before re-init to prevent duplicates
  HRState.unsubscribers.forEach(unsub => unsub());
  HRState.unsubscribers = [];
  // TASK-635 — reset candidate/bg subscribe flags + pending retry timers so the
  // re-init below re-arms the candidates listener (the guard at openHiringSubView
  // / subscribeToData previously stayed true after this teardown → dead listener
  // never re-subscribed).
  _teardownCandidateListeners();
  _teardownSafetyEquipmentListener(); // TASK-1578 — same class of dead-guard
  const container = document.getElementById('main-content');
  if (container) {
    container.innerHTML = '';
    initHR(container, HRState.tenantId);
  }
}

// --- Subscription ---
// TASK-635 — capped backoff for re-arming a listener that hit a real
// onSnapshot error (stale long-poll after the PWA is backgrounded, auth-token
// refresh race, transient `unavailable`). Last entry repeats for all later
// attempts.
const HR_LISTENER_BACKOFF_MS = [2000, 5000, 10000];

// TASK-635 — named visibility/focus handlers (mirrors app.js
// _onVisibilityChangeVersion/_onFocusVersion). When the app returns to the
// foreground after being backgrounded, re-arm any candidates/bg listener that
// died with an error. Named (not inline) so they can be removed on cleanup /
// logout — a dangling listener firing for a signed-out session is exactly the
// leak the app.js pattern guards against.
function _hrOnVisibilityRearm() {
  if (document.visibilityState === 'visible') _hrRearmDeadListeners();
}
function _hrOnFocusRearm() {
  _hrRearmDeadListeners();
}
function _hrRearmDeadListeners() {
  if (!HRState.tenantId) return;
  if (HRState.candidatesError) _armCandidatesListener(HRState.tenantId);
  if (HRState.bgRecordsError) _armBgRecordsListener(HRState.tenantId);
}
function _installHrVisibilityHook() {
  if (HRState._hrVisibilityHooked) return;
  document.addEventListener('visibilitychange', _hrOnVisibilityRearm);
  window.addEventListener('focus', _hrOnFocusRearm);
  HRState._hrVisibilityHooked = true;
}
function _removeHrVisibilityHook() {
  document.removeEventListener('visibilitychange', _hrOnVisibilityRearm);
  window.removeEventListener('focus', _hrOnFocusRearm);
  HRState._hrVisibilityHooked = false;
}

// Tear down BOTH listeners' live unsubs + pending retry timers and reset the
// re-arm flags. Called from the view cleanup (and logout via viewCleanup).
function _teardownCandidateListeners() {
  _teardownCandidateFlagsListener(); // TASK-9423
  try { HRState._candUnsub?.(); } catch (_) {}
  try { HRState._bgUnsub?.(); } catch (_) {}
  if (HRState._candRetryTimer) { clearTimeout(HRState._candRetryTimer); HRState._candRetryTimer = null; }
  if (HRState._bgRetryTimer) { clearTimeout(HRState._bgRetryTimer); HRState._bgRetryTimer = null; }
  HRState._candUnsub = null;
  HRState._bgUnsub = null;
  HRState.candidateSubscribed = false;
  HRState.bgRecordsSubscribed = false;
  HRState._candRearming = false;
  HRState._bgRearming = false;
  HRState._candRetryAttempt = 0;
  HRState._bgRetryAttempt = 0;
  // TASK-635 (COD) — clear error flags AND tenant-scoped cached data so a
  // logout/tenant-switch followed by a failed first re-subscribe can't flash
  // the PREVIOUS tenant's applicants/BG pills (the new error path intentionally
  // renders last-known rows, so stale caches must not survive teardown).
  // candidatesLoaded resets to false so the next mount shows the loading
  // skeleton, not a stale empty/error state.
  HRState.candidatesError = false;
  HRState.bgRecordsError = false;
  HRState.candidates = [];
  HRState.bgRecordsByCandidateId = {};
  HRState._bgLoadedTid = null; // TASK-9444 slice 2
  HRState.candidatesLoaded = false;
  _removeHrVisibilityHook();
}

function subscribeToCandidates(tenantId) {
  if (!tenantId) return;
  HRState.tenantId = tenantId;
  _installHrVisibilityHook();
  _armCandidatesListener(tenantId);
  _armBgRecordsListener(tenantId);
  _armCandidateFlagsListener(tenantId); // TASK-9423
}

// ============================================================================
// TASK-9423 — APPLICANT FLAGS ON THE CARRIER HIRING SCREEN.
//
// Kolsen (Crown), 2026-09-28: "flags that are marked on compliance Portal are also
// shown on Dispatch Deck!! A flag for compliance reason should be known to all
// users!" Driver flags already reached the carrier; APPLICANT flags (the
// append-only `tenants/{tid}/candidateFlags` ledger Crown writes, TASK-1626/4752)
// had ZERO references here — so a carrier could approve-to-roster an applicant
// Crown had flagged "MVR has violations not disclosed" and never see it.
//
// READ GATE = the candidateFlags rule (admin | managing partner | hr module) —
// exactly who can open this screen. The reason text shows to every one of them:
// unlike a hired driver's flag (whose person doc is broadcast to driver PINs, so
// its reason is owner/admin only), an applicant flag lives in an HR-gated
// collection and names an application problem ("Missing medical card"), which is
// what the HR person hiring needs to act on. Kolsen's ask is "known to all users".
//
// Same reducer + taxonomy + panel as the partner portal (window.PersonSections),
// so the two can never disagree about whether an applicant is flagged.
// A listener error fails CLOSED to "no flags shown" and never breaks the list;
// it is logged, and the approve-to-roster check below re-reads on its own.
// ============================================================================
function _teardownCandidateFlagsListener() {
  try { HRState._candFlagsUnsub?.(); } catch (_) {}
  HRState._candFlagsUnsub = null;
  HRState._candFlagsTid = null;
  HRState.candidateFlagRows = [];
  HRState.candidateFlagMap = {};
}

function _armCandidateFlagsListener(tenantId) {
  if (!tenantId) return;
  _teardownCandidateFlagsListener();
  HRState._candFlagsTid = tenantId;
  try {
    const unsub = onSnapshot(collection(db, 'tenants', tenantId, 'candidateFlags'), (snap) => {
      if (HRState._candFlagsTid !== tenantId) return; // tenant switched mid-flight
      HRState.candidateFlagRows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      HRState.candidateFlagMap = window.PersonSections.reduceCandidateFlagRows(HRState.candidateFlagRows);
      if (HRState.currentView === 'hiring') renderCandidatesList();
      _hrRepaintOpenCandidateFlags();
    }, (err) => {
      console.warn('[HR] candidateFlags listener error:', err?.code || 'unknown');
    });
    HRState._candFlagsUnsub = unsub;
    HRState.unsubscribers.push(unsub);
  } catch (e) {
    console.warn('[HR] candidateFlags subscribe threw:', e?.code || 'unknown');
  }
}

// Active flags on one applicant, taxonomy order. Tenant-guarded.
function _hrCandidateFlags(candidateId) {
  if (!candidateId || !HRState._candFlagsTid || HRState._candFlagsTid !== HRState.tenantId) return [];
  return window.PersonSections.activeCandidateFlags((HRState.candidateFlagMap || {})[candidateId]);
}

function _hrCandidateFlagChip(candidateId) {
  const flags = _hrCandidateFlags(candidateId);
  if (!flags.length) return '';
  const PS = window.PersonSections;
  const title = flags.map(f => PS.candidateFlagTypeLabel(f.flagType) + (f.reason ? ' — ' + f.reason : '')).join('\n');
  return `<span class="candidate-status-badge" style="margin-left:6px;background:#FEE2E2;color:#991B1B;font-weight:700;" title="${escapeHtml(title)}">🚩 ${flags.length === 1 ? 'Flagged' : flags.length + ' flags'}</span>`;
}

// The panel + history for the application detail. Read-only here: Crown sets and
// clears applicant flags from their portal (TASK-9227: a partner's finding is not
// the carrier's to erase).
function _hrCandidateFlagSectionHTML(candidateId) {
  const PS = window.PersonSections;
  const flags = _hrCandidateFlags(candidateId);
  const hist = (HRState._candFlagsTid === HRState.tenantId)
    ? PS.candidateFlagHistory(HRState.candidateFlagRows || [], candidateId) : [];
  if (!flags.length && !hist.length) return '';
  return PS.renderFlagPanel(flags.map(f => ({
    key: f.flagType,
    label: PS.candidateFlagTypeLabel(f.flagType),
    reason: f.reason || '',
    by: String(f.setByEmail || f.setByName || '').trim(),
    at: f.setAt || null,
  })), {
    title: flags.length === 1 ? 'Flagged by your compliance partner' : `${flags.length} flags from your compliance partner`,
    footnote: 'Your compliance partner flagged this application for follow-up. Resolve it with them before hiring — the flag does not change the applicant’s stage. If a flag drawn from a background report (e.g. the MVR) becomes your reason to decline, use Reject… so the FCRA adverse-action steps run.',
  }) + PS.renderFlagHistory(hist, { title: 'Flag history' });
}

function _hrRepaintOpenCandidateFlags() {
  _hrRepaintOpenCandidateAudit(); // TASK-9444 slice 2 — "flag still open after proof" reads the same ledger
  const slot = document.getElementById('cand-flag-slot');
  if (!slot) return;
  const cid = slot.getAttribute('data-cand-id');
  if (cid) slot.innerHTML = _hrCandidateFlagSectionHTML(cid);
}

// Hire gate. Returns true to proceed. Reads the LIVE ledger (not only the
// listener cache) so a flag set seconds ago — or a listener that errored — still
// stops the hire for a confirm that NAMES every flag. A read failure is "did not
// run", not "no flags": the confirm says so rather than waving the hire through.
async function _hrConfirmHireOverFlags(candidate) {
  const cid = candidate?.id;
  if (!cid) return true;
  const PS = window.PersonSections;
  let flags = _hrCandidateFlags(cid);
  let readFailed = false;
  try {
    const snap = await getDocs(query(collection(db, 'tenants', HRState.tenantId, 'candidateFlags'), where('candidateId', '==', cid)));
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    flags = PS.activeCandidateFlags(PS.reduceCandidateFlagRows(rows)[cid]);
  } catch (e) {
    readFailed = true;
    console.warn('[HR] hire-time flag check failed:', e?.code || 'unknown');
  }
  if (!flags.length && !readFailed) return true;
  if (typeof window.showConfirm !== 'function') return false;
  const name = candidate.name || 'This applicant';
  const lines = flags.map(f => '• ' + PS.candidateFlagTypeLabel(f.flagType) + (f.reason ? ' — ' + f.reason : '')).join('\n');
  const msg = flags.length
    ? `${name} is flagged by your compliance partner:\n\n${lines}\n\nHiring puts them on your roster with this unresolved. Resolve it with your compliance partner first, or hire anyway if it has been handled.`
    : `Could not check whether ${name} has open compliance flags (the check did not run). Hire anyway only if you know the application is clear.`;
  const ok = await window.showConfirm(flags.length ? 'Applicant is flagged' : 'Flag check did not run', msg, {
    confirmText: 'Hire anyway', cancelText: 'Go back', danger: true, safeDefault: true, icon: 'flag',
  });
  if (ok && flags.length) {
    try {
      window.logAudit?.('hr', 'CANDIDATE_HIRED_OVER_FLAGS', {
        candidateId: cid, flagTypes: flags.map(f => f.flagType),
      });
    } catch (_) {}
  }
  return !!ok;
}

// Idempotent: tears down any existing (errored) candidates listener first so a
// re-arm never stacks duplicate listeners (2026-05-28 dup-listener incident).
function _armCandidatesListener(tenantId) {
  if (!tenantId) return;
  // Dedup: a visibility re-arm and an error re-arm can race. Guard so only one
  // re-subscribe is in flight at a time.
  if (HRState._candRearming) return;
  HRState._candRearming = true;

  // Cancel any pending retry timer + tear down the old (possibly errored) unsub
  // BEFORE creating the new one.
  if (HRState._candRetryTimer) { clearTimeout(HRState._candRetryTimer); HRState._candRetryTimer = null; }
  try { HRState._candUnsub?.(); } catch (_) {}
  HRState._candUnsub = null;

  // TASK-635 (COD) — wrap setup in try/catch so a synchronous throw (e.g. query
  // construction) can't strand _candRearming=true and deadlock all future
  // retries. On throw, schedule a backoff retry just like the async error path.
  try {
  const candidatesQuery = query(
    collection(db, 'tenants', tenantId, 'candidates'),
    orderBy('createdAt', 'desc')
  );
  const unsubCandidates = onSnapshot(candidatesQuery, (snapshot) => {
    // Successful snapshot (including a genuinely empty collection) — clear
    // error/backoff state and render real data. An empty collection arrives
    // here as snapshot.empty, NOT via the error callback.
    HRState.candidates = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    HRState.candidatesLoaded = true;
    HRState.candidatesError = false;
    HRState._candRetryAttempt = 0;
    if (HRState.currentView === 'hiring') renderCandidatesList();
    renderComplianceDashboard();
    _hrRepaintOpenCandidateAudit(); // TASK-9444 slice 2 (COD r1) — a report attached while the card is open

    // TASK-228 deep-link — applicant-embed path keys `peopleBackgroundChecks`
    // by candidateId, so the BG-complete email links to
    // `#/hr/candidates/{candidateId}`. Drain pendingHrFocus once the
    // candidate is loaded; switch to hiring sub-view so the edit modal
    // makes sense in context.
    try {
      const focus = window.AppState?.pendingHrFocus;
      if (focus && focus.type === 'candidates' && focus.id) {
        const match = HRState.candidates.find(c => c.id === focus.id);
        if (match) {
          window.AppState.pendingHrFocus = null;
          setTimeout(() => {
            if (HRState.currentView !== 'hiring' && typeof openHiringSubView === 'function') {
              openHiringSubView();
            }
            openEditCandidateModal(focus.id);
          }, 0);
        }
      }
    } catch (e) {
      console.warn('[HR] pendingHrFocus(candidates) drain failed', e);
    }
  }, (error) => {
    // TASK-635 — REAL listener error (Firestore does NOT error on an empty
    // collection). Do NOT wipe HRState.candidates → keep last-known data so the
    // list doesn't flash a false "no applicants". Flag the error so the UI can
    // show a recoverable "couldn't load — retrying" state, then re-subscribe
    // with capped backoff. Log error.code ONLY (the message embeds the
    // tenant-scoped collection path).
    console.warn('[HR] candidates listener error, retrying:', error?.code || 'unknown');
    HRState.candidatesError = true;
    HRState.candidatesLoaded = true; // we have *something* to show (last-known or retry banner)
    try { HRState._candUnsub?.(); } catch (_) {}
    HRState._candUnsub = null;
    HRState.candidateSubscribed = false;
    if (HRState.currentView === 'hiring') renderCandidatesList();

    const delay = HR_LISTENER_BACKOFF_MS[Math.min(HRState._candRetryAttempt, HR_LISTENER_BACKOFF_MS.length - 1)];
    HRState._candRetryAttempt++;
    if (HRState._candRetryTimer) clearTimeout(HRState._candRetryTimer);
    HRState._candRetryTimer = setTimeout(() => {
      HRState._candRetryTimer = null;
      _armCandidatesListener(tenantId);
    }, delay);
  });
  HRState._candUnsub = unsubCandidates;
  HRState.unsubscribers.push(unsubCandidates);
  HRState.candidateSubscribed = true;
  } catch (e) {
    console.warn('[HR] candidates subscribe threw, retrying:', e?.code || 'unknown');
    HRState.candidatesError = true;
    HRState.candidatesLoaded = true;
    HRState.candidateSubscribed = false;
    if (HRState.currentView === 'hiring') renderCandidatesList();
    const delay = HR_LISTENER_BACKOFF_MS[Math.min(HRState._candRetryAttempt, HR_LISTENER_BACKOFF_MS.length - 1)];
    HRState._candRetryAttempt++;
    if (HRState._candRetryTimer) clearTimeout(HRState._candRetryTimer);
    HRState._candRetryTimer = setTimeout(() => {
      HRState._candRetryTimer = null;
      _armCandidatesListener(tenantId);
    }, delay);
  } finally {
    // Always release the dedup guard so a thrown setup can't block retries.
    HRState._candRearming = false;
  }
}

// v1.15.491 — also subscribe to peopleBackgroundChecks so the list-card
// "last decision" pill reflects state without per-row fetches. Same admin
// gate as candidates (Firestore rules already enforce). TASK-635 — same
// error/re-arm treatment as the candidates listener (same latent defect:
// a real error after backgrounding left the pill data permanently stale).
function _armBgRecordsListener(tenantId) {
  if (!tenantId) return;
  if (HRState._bgRearming) return;
  HRState._bgRearming = true;

  if (HRState._bgRetryTimer) { clearTimeout(HRState._bgRetryTimer); HRState._bgRetryTimer = null; }
  try { HRState._bgUnsub?.(); } catch (_) {}
  HRState._bgUnsub = null;

  try {
    const bgQuery = collection(db, 'tenants', tenantId, 'peopleBackgroundChecks');
    const unsubBg = onSnapshot(bgQuery, (snap) => {
      const map = {};
      snap.docs.forEach(d => {
        const data = d.data() || {};
        // 2026-05-19 — keep ALL records (was: only those with decision
        // history). Direct-invite flow (admin clicks "Send to Checkr") writes
        // to peopleBackgroundChecks but never dual-writes the chip fields
        // back to the candidate doc, so the card-side chip needs the BG
        // record itself for status/reportId. Bug surfaced for Justin Estes
        // who showed no chip while screenings were running.
        map[d.id] = {
          bgDecision: data.bgDecision || null,
          bgDecisionHistory: Array.isArray(data.bgDecisionHistory) ? data.bgDecisionHistory : [],
          latestStatus: data.latestStatus || null,
          latestAssessment: data.latestAssessment || null,
          latestReportId: data.latestReportId || null,
          latestPackage: data.latestPackage || null,
          latestInvitationId: data.latestInvitationId || null,
          invitationStatus: data.byPackage?.[data.latestPackage]?.invitationStatus || null,
        };
      });
      // Success — keep last-known on subsequent errors, clear error/backoff.
      HRState.bgRecordsByCandidateId = map;
      HRState._bgLoadedTid = tenantId; // TASK-9444 slice 2
      HRState.bgRecordsError = false;
      HRState._bgRetryAttempt = 0;
      if (HRState.currentView === 'hiring') renderCandidatesList();
      _hrRepaintOpenCandidateAudit(); // TASK-9444 slice 2
    }, (err) => {
      // Real error — do NOT wipe the existing pill map (keep last-known), flag,
      // re-arm with backoff. Log code only.
      console.warn('[HR] bg records listener error, retrying:', err?.code || 'unknown');
      HRState.bgRecordsError = true;
      try { HRState._bgUnsub?.(); } catch (_) {}
      HRState._bgUnsub = null;
      HRState.bgRecordsSubscribed = false;

      const delay = HR_LISTENER_BACKOFF_MS[Math.min(HRState._bgRetryAttempt, HR_LISTENER_BACKOFF_MS.length - 1)];
      HRState._bgRetryAttempt++;
      if (HRState._bgRetryTimer) clearTimeout(HRState._bgRetryTimer);
      HRState._bgRetryTimer = setTimeout(() => {
        HRState._bgRetryTimer = null;
        _armBgRecordsListener(tenantId);
      }, delay);
    });
    HRState._bgUnsub = unsubBg;
    HRState.unsubscribers.push(unsubBg);
    HRState.bgRecordsSubscribed = true;
  } catch (e) {
    // TASK-635 — bg pill data is optional (non-fatal); flag for re-arm on next
    // foreground/retry but don't schedule an aggressive backoff loop. Log code.
    console.warn('[HR] bg records subscribe threw (non-fatal):', e?.code || 'unknown');
    HRState.bgRecordsError = true;
    HRState.bgRecordsSubscribed = false;
  } finally {
    // Always release the dedup guard so a thrown setup can't block retries.
    HRState._bgRearming = false;
  }
}

// --- Templates ---
function getHiringSubViewHTML() {
  const isComplianceOnly = window.AppState?.tenantData?.complianceOnly;
  return `
    <div class="hiring-subview">
      ${isComplianceOnly ? '' : `<button class="hiring-back-btn" onclick="window.HRModule.backToHRLanding()">
        <span class="material-icons">arrow_back</span> Back to HR
      </button>`}
      <div class="view-header" style="margin-bottom:0;">
        <div class="view-title"><h1>Hiring / Applicants</h1></div>
      </div>
      <div id="hiring-stats-bar" class="hiring-stats-bar"></div>
      ${/* TASK-1605 — no screening package ⇒ say so here, where applicants land, with the link. */''}
      <div id="hiring-package-stop">${hrScreeningPackageStopHtml({ context: 'hiring' })}</div>
      <div class="hiring-toolbar">
        <div class="hiring-search-box">
          <span class="material-icons">search</span>
          <input type="search" id="hiring-search" placeholder="Search candidates..." autocomplete="off" oninput="window.HRModule.searchCandidates(this.value)">
        </div>
        <select id="hiring-status-filter" class="hr-filter-select" onchange="window.HRModule.filterCandidatesByStatus(this.value)">
          <option value="">All Statuses</option>
          ${PIPELINE_STAGES.map(s => `<option value="${s.id}">${s.label}</option>`).join('')}
          <!-- TASK-1360 — display-only buckets. Kept in this list so selecting the matching
               stats pill leaves the dropdown showing the right thing instead of "All Statuses". -->
          ${[HR_STAGE_DRAFT, HR_STAGE_OTHER].map(s => `<option value="${s.id}">${s.label}</option>`).join('')}
        </select>
        <button class="btn btn-secondary" onclick="window.HRModule.copyApplyLink()" title="Copy public application link">
          <span class="material-icons">link</span> Apply Link
        </button>
        <button class="btn btn-primary" onclick="window.HRModule.openAddCandidateModal()">
          <span class="material-icons">person_add</span> Add Candidate
        </button>
      </div>
      <div id="hiring-candidates-list"></div>
    </div>
  `;
}

// --- Disciplinary Sub-View ---
function openDisciplinarySubView() {
  if (_hrGateComplianceOnly('disciplinary')) return; // 2026-05-13 compliance-only gate
  HRState.currentView = 'disciplinary';
  const container = document.getElementById('hr-content') || document.querySelector('.hr-view');
  if (!container) return;
  container.innerHTML = getDisciplinarySubViewHTML();
  renderFormsList();
}

// ============================================
// TASK-346 reframe (2026-05-21) — Add Employee Note modal
//
// Replaces the killed My Crew dedicated sub-view (originally Slice 4). Chris
// judged the dedicated sub-view + sidebar entry overkill for founder/admin-
// flat shops like TITS — admins are already in HR Directory with full access.
// The notes-on-employees affordance still has value (especially for future
// Kayla/Crown-managed carriers with non-admin supervisors), but as a button
// in the HR module Actions section, not as a whole module.
//
// EXPORT CONTRACT (for Worker R TASK-347 driver-portal Actions sheet "Team
// note" tile):
//   window.HRModule.openAddEmployeeNoteModal(opts = {})
//     opts.preselectedPersonId  optional — pre-selects in dropdown and
//                               disables picker. Useful for per-row affordances.
//   Returns no value (fire-and-forget modal).
//
// Visibility gate (applied on HR landing render):
//   - Admin or owner: always sees the Action card.
//   - Has direct reports (AppState.hasDirectReports===true): sees the card.
//   - Driver-PIN / public access / regular crew with no reports: hidden.
//
// Picker scope:
//   - Admin/owner: full active roster (excluding the signed-in person).
//   - Non-admin supervisor: only their direct reports
//     (people where reportsTo === memberAccess.personId).
//
// Write path: reuses Slice 3 hrNotes subcollection shape — same doc shape
// addEmployeeNote() produces. No notification fanout by design.
// ============================================

// Module-scope guard to prevent double-submit on rapid double-click of Save.
let _addEmployeeNoteSubmitInFlight = false;

function applyAddEmployeeNoteGate() {
  const btn = document.getElementById('hr-action-add-employee-note');
  if (!btn) return;
  // Driver-PIN / public-access sessions never see HR Actions at all, but
  // belt-and-suspenders the gate so the button stays hidden if any code path
  // ever rendered the HR landing for those modes.
  if (window.AppState?.driverModePin ||
      window.AppState?.driverMode ||
      window.AppState?.isPublicAccess) {
    btn.style.display = 'none';
    return;
  }
  const role = window.AppState?.memberAccess?.role || null;
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';
  const hasReports = window.AppState?.hasDirectReports === true;
  // TASK-932 — was `|| isFounderOrVip` (a TENANT test: every staff member on
  // the founder/VIP tenants got the admin entry). Now module-based for everyone:
  // effective admin (incl. no-member-doc legacy owner) OR hr module-holder.
  const hrManager = typeof window.hrCanManageModule === 'function' && window.hrCanManageModule() === true;
  btn.style.display = (isOwnerOrAdmin || hasReports || hrManager) ? '' : 'none';
}

function _resolveAddEmployeeNoteCandidates() {
  // Returns the list of people the signed-in user is allowed to write a note
  // against, plus a flag for whether the caller is admin-equivalent. Admins
  // get full active roster (minus themselves); supervisors get only their
  // direct reports. List is sorted by name. Inactive/terminated are excluded.
  const role = window.AppState?.memberAccess?.role || null;
  const isOwnerOrAdmin = role === 'owner' || role === 'admin';
  // TASK-932 — same swap as applyAddEmployeeNoteGate: module, not tenant.
  const adminLike = isOwnerOrAdmin ||
    (typeof window.hrCanManageModule === 'function' && window.hrCanManageModule() === true);
  const myPersonId = window.AppState?.memberAccess?.personId || null;
  const all = (HRState.people || []).filter(p => {
    if (!p || !p.id) return false;
    if (p.active === false || p.terminated === true) return false;
    return true;
  });
  let candidates;
  if (adminLike) {
    candidates = all.filter(p => p.id !== myPersonId);
  } else {
    // Supervisor: only direct reports. If memberAccess.personId is missing
    // they get an empty list (the modal shows a "linked roster" empty state).
    candidates = myPersonId
      ? all.filter(p => p.reportsTo === myPersonId)
      : [];
  }
  candidates.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  return { candidates, adminLike };
}

function openAddEmployeeNoteModal(opts) {
  opts = opts || {};
  _addEmployeeNoteSubmitInFlight = false;
  const { candidates, adminLike } = _resolveAddEmployeeNoteCandidates();

  // No-candidates empty state — surface a clear message rather than rendering
  // an empty dropdown.
  if (candidates.length === 0) {
    const msg = adminLike
      ? 'No active employees on your roster yet. Add a person in Roster first.'
      : "No direct reports assigned to you yet. Ask an admin to set your team in their profile (HR → open the employee → Compliance → 'Reports to').";
    if (window.showToast) window.showToast(msg, 'info'); else alert(msg);
    return;
  }

  // Validate preselectedPersonId: must be in the candidates list (i.e. the
  // user is actually allowed to write against this person). Silently fall
  // through to picker-mode if invalid — defense against driver-portal
  // call sites passing stale IDs.
  let preselectedId = opts.preselectedPersonId || null;
  let lockPicker = false;
  if (preselectedId) {
    const found = candidates.find(p => p.id === preselectedId);
    if (found) {
      lockPicker = true;
    } else {
      preselectedId = null;
    }
  }

  const optionsHtml = candidates.map(p => {
    // getTypeLabel signature is (slug, kind) — Worker S swapped args; fixed 2026-05-22.
    const role = window.getTypeLabel
      ? window.getTypeLabel(p.crewType || p.role || '', 'crewTypes')
      : (p.crewType || p.role || '');
    const label = role ? `${p.name || '(unnamed)'} — ${role}` : (p.name || '(unnamed)');
    const selected = (p.id === preselectedId) ? ' selected' : '';
    return `<option value="${escapeHtml(p.id)}"${selected}>${escapeHtml(label)}</option>`;
  }).join('');

  // NOTE: every HTML comment inside this template literal uses single quotes
  // (never backticks) for identifier references — two prod-breaking parser
  // failures TODAY were caused by backticks inside HTML comments inside JS
  // template literals. Do not change this.
  const placeholderEmpty = '<option value="" disabled selected>Pick an employee…</option>';
  const html = `
    <div class="modal-content modal-small" style="max-width:480px;padding:0;">
      <div class="crm-detail-header" style="padding:16px 20px;">
        <div class="crm-detail-header-info">
          <div class="crm-detail-header-icon" style="width:36px;height:36px;">
            <span class="material-icons" style="font-size:20px;">note_add</span>
          </div>
          <h3 style="margin:0;font-size:1rem;">Add Employee Note</h3>
        </div>
        <button class="modal-close" style="color:#fff;" onclick="document.getElementById('add-employee-note-modal').classList.add('hidden')">&times;</button>
      </div>
      <div style="padding:20px;display:flex;flex-direction:column;gap:12px;flex:1 1 auto;min-height:0;overflow-y:auto;">
        <div class="form-group" style="margin:0;">
          <label for="add-emp-note-person">Employee *</label>
          <select id="add-emp-note-person" class="form-control" ${lockPicker ? 'disabled' : ''}
            onchange="window.HRModule._onAddNoteEmployeeChange(this.value)">
            ${preselectedId ? '' : placeholderEmpty}
            ${optionsHtml}
          </select>
          ${lockPicker ? '<div style="font-size:0.78rem;color:var(--text-secondary);margin-top:4px;">Logging a note on the selected employee.</div>' : ''}
        </div>
        <div class="form-group" style="margin:0;">
          <label for="add-emp-note-body">Note *</label>
          <textarea id="add-emp-note-body" class="form-control" rows="4" maxlength="2000"
            placeholder="Year-end review note, discipline lookback, attaboy, late-arrival pattern, etc."
            style="resize:vertical;"></textarea>
          <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">Notes are visible to HR and the employee's supervisor.</div>
        </div>
        <div class="form-group" style="margin:0;">
          <label for="add-emp-note-observed-date" style="display:flex;align-items:center;gap:6px;font-size:0.85rem;">
            <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">event</span> Observed date
          </label>
          <input type="date" id="add-emp-note-observed-date" class="form-control" value="${_todayYmd()}" max="${_maxYmd()}" min="${_minYmd()}" />
          <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:4px;">When did this happen? Defaults to today; backdate if you are logging an earlier observation.</div>
        </div>
        <div class="form-group" style="margin:0;">
          <label style="display:flex;align-items:center;gap:6px;font-size:0.85rem;">
            <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">attach_file</span> Attachment (optional)
          </label>
          <input type="file" id="add-emp-note-attach-input" accept="${HR_NOTE_ATTACH_ACCEPT}" onchange="window.HRModule._onHrNoteAttachPick('modal')" style="font-size:0.82rem;" />
          <div id="add-emp-note-attach-chip" style="display:none;align-items:center;gap:6px;font-size:0.75rem;color:var(--text-secondary);margin-top:4px;"></div>
          <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:4px;display:flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:13px;color:#d97706;">warning</span>
            <span>${HR_NOTE_ATTACH_DA_WARNING}</span>
          </div>
        </div>
        <!-- TASK-351 polish 2026-05-22 / TASK-542 2026-06-10 — recent-notes
             feed showing the picked employee's notes from ALL authors (matches
             the always-present detail feed; gives the supervisor full recent
             context). Lazy-fetched on picker change; cached per personId to
             dedup rapid switches. -->
        <div id="add-emp-note-own-feed-wrap" style="margin:0;display:none;">
          <div style="font-size:0.78rem;font-weight:600;color:var(--text-secondary);margin-bottom:6px;display:flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:14px;">history</span>
            Recent notes for this employee
          </div>
          <div id="add-emp-note-own-feed" style="display:flex;flex-direction:column;gap:6px;max-height:240px;overflow-y:auto;"></div>
        </div>
      </div>
      <div class="modal-footer" style="display:flex;gap:8px;justify-content:flex-end;padding:16px 20px;">
        <button class="btn btn-secondary" onclick="document.getElementById('add-employee-note-modal').classList.add('hidden')">Cancel</button>
        <button class="btn btn-primary" id="add-emp-note-save-btn" onclick="window.HRModule.submitAddEmployeeNote()">
          <span class="material-icons" style="font-size:16px;">save</span> Save
        </button>
      </div>
    </div>
  `;

  let modal = document.getElementById('add-employee-note-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'add-employee-note-modal';
    modal.className = 'modal hidden';
    modal.style.zIndex = '1060';
    modal.onclick = (e) => { if (e.target === modal) modal.classList.add('hidden'); };
    document.body.appendChild(modal);
  }
  modal.innerHTML = html;
  modal.classList.remove('hidden');
  setTimeout(() => {
    // Focus the textarea if picker is locked, otherwise the picker.
    const el = lockPicker
      ? document.getElementById('add-emp-note-body')
      : document.getElementById('add-emp-note-person');
    el && el.focus();
    // TASK-351 polish 2026-05-22 — auto-load own-notes feed if a person is
    // already preselected when the modal opens.
    if (preselectedId) _onAddNoteEmployeeChange(preselectedId);
  }, 100);
}

// TASK-351 polish 2026-05-22 — recent-notes feed support for Add Employee Note modal.
// TASK-542 2026-06-10 — now caches the employee's recent notes from ALL authors
// (was current-user-only). Cache: personId -> array of
// {id, body, observedAt, createdAt, authorName}. Cleared when modal closes / page reloads.
const _addNoteOwnFeedCache = new Map();
// In-flight markers to dedup concurrent fetches for the same personId during
// rapid picker switches.
const _addNoteOwnFeedInFlight = new Set();

function _onAddNoteEmployeeChange(personId) {
  const wrap = document.getElementById('add-emp-note-own-feed-wrap');
  const feedEl = document.getElementById('add-emp-note-own-feed');
  if (!wrap || !feedEl) return;
  if (!personId) {
    wrap.style.display = 'none';
    feedEl.innerHTML = '';
    return;
  }
  wrap.style.display = '';
  // Cache hit — render immediately.
  if (_addNoteOwnFeedCache.has(personId)) {
    _renderOwnNotesFeed(personId);
    return;
  }
  // Avoid double-fetch during rapid picker switches.
  if (_addNoteOwnFeedInFlight.has(personId)) return;
  feedEl.innerHTML = '<div style="font-size:0.78rem;color:var(--text-secondary);padding:6px 0;">Loading your recent notes…</div>';
  _fetchOwnNotesForPicker(personId).catch(err => {
    console.warn('[HR] own-notes feed load failed', err);
    feedEl.innerHTML = '<div style="font-size:0.78rem;color:var(--text-secondary);padding:6px 0;">Could not load your notes.</div>';
  });
}

async function _fetchOwnNotesForPicker(personId) {
  const uid = window.AppState?.user?.uid || null;
  if (!uid || !personId || !HRState.tenantId) {
    _addNoteOwnFeedCache.set(personId, []);
    _renderOwnNotesFeed(personId);
    return;
  }
  _addNoteOwnFeedInFlight.add(personId);
  try {
    // TASK-542 — show the employee's recent notes from ALL authors (was
    // where(authorUid==uid), own-only). The always-present detail feed
    // (_loadEmployeeNotesFeed) already renders all-author notes for the same
    // person; this preview now matches it so the supervisor sees the full
    // recent context, not just their own. Read/display change only — same
    // tenant-scoped subcollection, no rule/write change. Sort client-side to
    // avoid a composite Firestore index (same pattern as the detail feed).
    const notesRef = collection(db, 'tenants', HRState.tenantId, 'people', personId, 'hrNotes');
    const snap = await getDocs(notesRef);
    let notes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    notes.sort((a, b) => _noteSortMs(b) - _noteSortMs(a));
    // Keep most recent 10 (Chris explicit spec — memory aid for the supervisor).
    notes = notes.slice(0, 10);
    _addNoteOwnFeedCache.set(personId, notes);
    _renderOwnNotesFeed(personId);
  } finally {
    _addNoteOwnFeedInFlight.delete(personId);
  }
}

function _renderOwnNotesFeed(personId) {
  const feedEl = document.getElementById('add-emp-note-own-feed');
  if (!feedEl) return;
  const notes = _addNoteOwnFeedCache.get(personId) || [];
  if (notes.length === 0) {
    feedEl.innerHTML = '<div style="font-size:0.78rem;color:var(--text-secondary);padding:6px 0;">No prior notes on this employee yet.</div>';
    return;
  }
  feedEl.innerHTML = notes.map(n => {
    const body = n.body || '';
    const observedTs = n.observedAt || n.createdAt || null;
    const observedLabel = _fmtObservedAt(observedTs);
    // TASK-542 — feed now spans all authors, so surface who wrote each note.
    const author = n.authorName || '';
    const metaLabel = author ? observedLabel + ' · ' + author : observedLabel;
    return ''
      + '<div style="padding:8px 10px;background:var(--bg-secondary,#f8fafc);border:1px solid var(--border-light,#e5e7eb);border-radius:6px;">'
      +   '<div style="font-size:0.7rem;color:var(--text-secondary);margin-bottom:3px;">' + escapeHtml(metaLabel) + '</div>'
      +   '<div style="font-size:0.82rem;line-height:1.4;color:var(--text-primary);white-space:pre-wrap;">' + escapeHtml(body) + '</div>'
      +   _renderHrNoteAttachment(n)
      + '</div>';
  }).join('');
}

async function submitAddEmployeeNote() {
  if (_addEmployeeNoteSubmitInFlight) return; // double-click guard
  const personSel = document.getElementById('add-emp-note-person');
  const bodyEl = document.getElementById('add-emp-note-body');
  const btn = document.getElementById('add-emp-note-save-btn');
  if (!personSel || !bodyEl) return;

  const personId = personSel.value || '';
  const body = (bodyEl.value || '').trim().slice(0, 2000);

  if (!personId) {
    window.showToast?.('Pick an employee first', 'info');
    personSel.focus();
    return;
  }
  if (!body) {
    window.showToast?.('Type a note first', 'info');
    bodyEl.focus();
    return;
  }

  // Server-side authorization scope re-validation: even though the picker is
  // already filtered, an admin role bump or stale DOM could let a non-admin
  // submit a personId outside their direct reports. Re-resolve candidates
  // and reject if the personId is not in the allowed set.
  const { candidates, adminLike } = _resolveAddEmployeeNoteCandidates();
  const allowed = candidates.some(p => p.id === personId);
  if (!allowed) {
    window.showToast?.('You can only add notes for your direct reports', 'error');
    return;
  }

  const uid = window.AppState?.user?.uid || null;
  if (!uid) {
    window.showToast?.('Sign in to add notes', 'error');
    return;
  }

  const authorName = (
    window.AppState?.user?.displayName ||
    window.AppState?.user?.email ||
    'Unknown'
  );

  _addEmployeeNoteSubmitInFlight = true;
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Saving...';
  }

  // TASK-350: write observedAt as a noon-anchored Timestamp matching the date
  // picker (if present in the modal) -- defaults to today when missing so the
  // legacy modal surface stays calendar-consistent with the HR detail composer.
  const observedYmd = (document.getElementById('add-emp-note-observed-date')?.value) || _todayYmd();
  const observedAt = _ymdToTimestamp(observedYmd) || Timestamp.now();

  // TASK-684 — optional image/PDF attachment. Validate up front, upload to
  // Storage FIRST; keep the modal open + text intact if the upload fails.
  const attachInput = document.getElementById('add-emp-note-attach-input');
  const attachFile = attachInput?.files?.[0] || null;
  let attachment = null;
  if (attachFile) {
    const v = _validateHrNoteAttachment(attachFile);
    if (!v.ok) {
      window.showToast?.(v.msg, 'error');
      _addEmployeeNoteSubmitInFlight = false;
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save'; }
      return;
    }
  }

  try {
    if (attachFile) {
      try {
        attachment = await _uploadHrNoteAttachment(attachFile, personId);
      } catch (upErr) {
        console.error('[HR] Note attachment upload failed:', upErr);
        window.showToast?.('Attachment upload failed — note not saved, try again', 'error');
        return; // finally re-enables the button; modal + text stay
      }
    }
    const notesRef = collection(db, 'tenants', HRState.tenantId, 'people', personId, 'hrNotes');
    const noteDoc = {
      body,
      createdAt: Timestamp.now(),
      observedAt,
      authorUid: uid,
      authorName,
      // supervisorOnlyAtCreate captures intent at write time so future view
      // gates can distinguish supervisor-created notes from admin-created
      // notes if Chris ever wants role-aware visibility. Today's reads don't
      // filter on this — both flows surface the same feed on HR detail.
      supervisorOnlyAtCreate: !adminLike,
      ...(attachment ? { attachment } : {}),
    };
    const newRef = await addDoc(notesRef, noteDoc);

    // Keep the in-memory hrNotes cache in sync so the HR detail Notes feed
    // (Slice 3) shows the new note immediately if the user opens that person
    // next. The cache lives in this module — _hrNotesCache map at top.
    try {
      const cached = (typeof _hrNotesCache !== 'undefined' && _hrNotesCache.get(personId)) || [];
      cached.unshift({ id: newRef.id, ...noteDoc });
      if (typeof _hrNotesCache !== 'undefined') _hrNotesCache.set(personId, cached);
    } catch (_) { /* cache is best-effort */ }

    // TASK-351 polish 2026-05-22 — also prepend to the own-notes-only modal
    // cache so if Chris reopens the modal he sees his new entry immediately.
    try {
      const ownCached = _addNoteOwnFeedCache.get(personId) || [];
      ownCached.unshift({ id: newRef.id, ...noteDoc });
      // Keep cache trimmed to 10 to match the initial fetch shape.
      _addNoteOwnFeedCache.set(personId, ownCached.slice(0, 10));
    } catch (_) { /* cache best-effort */ }

    document.getElementById('add-employee-note-modal')?.classList.add('hidden');
    window.showToast?.('Saved', 'success');
    window.logAudit?.('hr', 'PERSON_HR_NOTE_ADD', {
      personId,
      noteId: newRef.id,
      bodyLength: body.length,
      observedAtMs: observedAt.toMillis(),
      via: 'hr-actions-add-note-modal',
      hasAttachment: !!attachment,
    });
  } catch (err) {
    console.error('[HR] submitAddEmployeeNote failed:', err);
    window.showToast?.('Could not save note', 'error');
  } finally {
    _addEmployeeNoteSubmitInFlight = false;
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons" style="font-size:16px;">save</span> Save';
    }
  }
}

function getDisciplinarySubViewHTML() {
  return `
    <div class="hiring-subview">
      <button class="hiring-back-btn" onclick="window.HRModule.backToHRLanding()">
        <span class="material-icons">arrow_back</span> Back to HR
      </button>
      <div class="view-header" style="margin-bottom:0;">
        <div class="view-title"><h1>Disciplinary Forms</h1></div>
      </div>
      <div style="margin: var(--spacing-md) 0;">
        <button class="btn btn-primary" onclick="window.HRModule.openDisciplinaryForm()">
          <span class="material-icons">add</span> New Disciplinary Form
        </button>
      </div>
      <div id="hr-forms-list" class="hr-list">
        <div class="empty-state-small">No disciplinary forms yet</div>
      </div>
    </div>
  `;
}

// --- Checklists Sub-View ---
function openChecklistsSubView() {
  // TASK-1117 (2nd-COD) — the 2026-05-13 compliance-only gate REMOVED here:
  // new-hire checklists ARE the onboarding surface (the printable onboarding
  // packet is generated from an hrChecklists doc), and Chris's paid boundary
  // keeps hiring + onboarding FREE for compliance-only carriers. The rules
  // deliberately keep hrChecklists writable for them; the client now agrees.
  HRState.currentView = 'checklists';
  const container = document.getElementById('hr-content') || document.querySelector('.hr-view');
  if (!container) return;
  container.innerHTML = getChecklistsSubViewHTML();
  renderChecklistsList();
}

function getChecklistsSubViewHTML() {
  return `
    <div class="hiring-subview">
      <button class="hiring-back-btn" onclick="window.HRModule.backToHRLanding()">
        <span class="material-icons">arrow_back</span> Back to HR
      </button>
      <div class="view-header" style="margin-bottom:0;">
        <div class="view-title"><h1>New Hire Checklists</h1></div>
      </div>
      <div style="margin: var(--spacing-md) 0;">
        <button class="btn btn-primary" onclick="window.HRModule.openNewChecklistModal()">
          <span class="material-icons">add</span> New Checklist
        </button>
      </div>
      <div id="hr-checklists-list" class="hr-list">
        <div class="empty-state-small">No active checklists</div>
      </div>
    </div>
  `;
}

// --- Reviews Sub-View ---
function openReviewsSubView() {
  if (_hrGateComplianceOnly('reviews')) return; // 2026-05-13 compliance-only gate (Performance Reviews)
  HRState.currentView = 'reviews';
  const container = document.getElementById('hr-content') || document.querySelector('.hr-view');
  if (!container) return;
  container.innerHTML = getReviewsSubViewHTML();
  renderReviewsList();
}

function getReviewsSubViewHTML() {
  return `
    <div class="hiring-subview">
      <button class="hiring-back-btn" onclick="window.HRModule.backToHRLanding()">
        <span class="material-icons">arrow_back</span> Back to HR
      </button>
      <div class="view-header" style="margin-bottom:0;">
        <div class="view-title"><h1>Employee Reviews</h1></div>
      </div>
      <div style="margin: var(--spacing-lg) 0;">
        <button class="btn btn-primary" onclick="window.HRModule.openReviewModal()">
          <span class="material-icons">add</span> New Review
        </button>
      </div>

      <div style="margin-bottom: var(--spacing-xl);">
        <h3 style="margin:0 0 var(--spacing-sm) 0;display:flex;align-items:center;gap:var(--spacing-xs);">
          <span class="material-icons" style="font-size:20px;">link</span> Quick Links
        </h3>
        <p style="margin:0 0 var(--spacing-md) 0;color:var(--text-secondary);font-size:0.9rem;">Add survey forms, reference docs, or any links your team needs.</p>
        <div id="hr-quick-links-list" style="display:flex;flex-direction:column;gap:var(--spacing-sm);"></div>
        <button class="btn btn-secondary btn-sm" style="margin-top:var(--spacing-sm);" onclick="window.HRModule.showAddLinkForm()">
          <span class="material-icons" style="font-size:16px;">add</span> Add Link
        </button>
        <div id="hr-add-link-form" class="hidden" style="margin-top:var(--spacing-md);background:var(--bg-secondary);border-radius:var(--radius-md);padding:var(--spacing-md);display:flex;flex-wrap:wrap;gap:var(--spacing-sm);align-items:flex-end;">
          <div style="flex:1;min-width:140px;">
            <label style="font-size:0.75rem;color:var(--text-secondary);display:block;margin-bottom:2px;">Name</label>
            <input type="text" id="hr-link-name" class="form-control" placeholder="e.g. Driver Survey" maxlength="60" style="font-size:0.9rem;">
          </div>
          <div style="flex:2;min-width:200px;">
            <label style="font-size:0.75rem;color:var(--text-secondary);display:block;margin-bottom:2px;">URL</label>
            <input type="url" id="hr-link-url" class="form-control" placeholder="https://..." style="font-size:0.9rem;">
          </div>
          <div style="display:flex;gap:var(--spacing-xs);">
            <button class="btn btn-primary btn-sm" onclick="window.HRModule.saveLink()">Save</button>
            <button class="btn btn-secondary btn-sm" onclick="document.getElementById('hr-add-link-form').classList.add('hidden')">Cancel</button>
          </div>
        </div>
      </div>

      <h3 style="margin:0 0 var(--spacing-md) 0;">Recent Reviews</h3>
      <div id="hr-reviews-list" class="hr-review-cards-grid">
        <div class="empty-state-small">No employee reviews yet</div>
      </div>
    </div>
  `;
}

function copySurveyLink(url, btn) {
  navigator.clipboard.writeText(url).then(() => {
    const orig = btn.innerHTML;
    btn.innerHTML = '<span class="material-icons" style="font-size:16px;">check</span> Copied!';
    setTimeout(() => { btn.innerHTML = orig; }, 2000);
    showToast('Link copied', 'success');
  }).catch(() => {
    showToast('Could not copy link', 'error');
  });
}

function renderQuickLinks() {
  const container = document.getElementById('hr-quick-links-list');
  if (!container) return;
  const links = window.AppState?.settings?.hrLinks || [];
  if (links.length === 0) {
    container.innerHTML = '<div style="color:var(--text-secondary);font-size:0.85rem;padding:var(--spacing-sm) 0;">No links added yet.</div>';
    return;
  }
  container.innerHTML = links.map((link, i) => `
    <div data-url="${escapeHtml(link.url)}" style="display:flex;align-items:center;gap:var(--spacing-sm);background:var(--bg-secondary);border-radius:var(--radius-md);padding:var(--spacing-sm) var(--spacing-md);">
      <span style="flex:1;font-weight:500;">${escapeHtml(link.name)}</span>
      <a href="${_safeHttpUrl(link.url)}" target="_blank" class="btn btn-secondary btn-sm" style="white-space:nowrap;">
        <span class="material-icons" style="font-size:16px;">open_in_new</span> Open
      </a>
      <button class="btn btn-primary btn-sm" style="white-space:nowrap;" onclick="window.HRModule.copySurveyLink(this.closest('[data-url]').dataset.url, this)">
        <span class="material-icons" style="font-size:16px;">content_copy</span> Copy Link
      </button>
      <button class="btn-icon" style="color:var(--text-muted);padding:4px;" onclick="window.HRModule.removeLink(${i})" title="Remove">
        <span class="material-icons" style="font-size:18px;">close</span>
      </button>
    </div>
  `).join('');
}

function showAddLinkForm() {
  const form = document.getElementById('hr-add-link-form');
  if (form) {
    form.classList.remove('hidden');
    document.getElementById('hr-link-name').value = '';
    document.getElementById('hr-link-url').value = '';
    document.getElementById('hr-link-name').focus();
  }
}

async function saveLink() {
  const name = document.getElementById('hr-link-name')?.value.trim();
  const url = document.getElementById('hr-link-url')?.value.trim();
  if (!name || !url) { showToast('Name and URL are required', 'error'); return; }
  if (!url.startsWith('http://') && !url.startsWith('https://')) { showToast('URL must start with http:// or https://', 'error'); return; }

  const links = [...(window.AppState?.settings?.hrLinks || []), { name, url }];
  try {
    const tenantRef = doc(db, 'tenants', HRState.tenantId);
    await setDoc(tenantRef, { settings: { hrLinks: links } }, { merge: true });
    if (window.AppState?.settings) window.AppState.settings.hrLinks = links;
    document.getElementById('hr-add-link-form')?.classList.add('hidden');
    renderQuickLinks();
    showToast('Link added', 'success');
  } catch (err) {
    console.error('[HR] Save link error:', err);
    showToast('Error saving link', 'error');
  }
}

async function removeLink(index) {
  const links = [...(window.AppState?.settings?.hrLinks || [])];
  if (index < 0 || index >= links.length) return;
  const removed = links.splice(index, 1)[0];
  if (!confirm(`Remove "${removed.name}"?`)) return;
  try {
    const tenantRef = doc(db, 'tenants', HRState.tenantId);
    await setDoc(tenantRef, { settings: { hrLinks: links } }, { merge: true });
    if (window.AppState?.settings) window.AppState.settings.hrLinks = links;
    renderQuickLinks();
    showToast('Link removed', 'success');
  } catch (err) {
    console.error('[HR] Remove link error:', err);
    showToast('Error removing link', 'error');
  }
}


// ============================================================
// TASK-4611 — the panel now trusts NOTHING stored on the candidate.
//
// TASK-4551 shipped this reading `documents.*.url` off the candidate doc, with a
// render-layer check that only auto-loaded a URL matching this tenant+candidate
// on Google's storage host. That contained the blast radius but left the panel
// deciding PRESENCE from an applicant-controlled field: saveDriverApplication is
// a PUBLIC callable that writes applicationData verbatim, so a planted url read
// as "document on file" and a real upload whose pointer write was lost read as
// missing.
//
// Now the server answers both questions by LISTING the storage prefix
// (listApplicationDocs / getApplicationDocBytes, HR-gated) — the posture
// applicationDqDocs.js already uses for the same objects. The client sends a
// docKey, never a path or a url, so there is nothing to forge or traverse.
// ============================================================
const _APP_DOC_LABELS = {
  driverLicense: { label: "Driver's License", icon: 'badge' },
  // TASK-9303 — apply.html now asks for the back (restrictions / endorsements).
  driverLicenseBack: { label: "Driver's License (back)", icon: 'badge' },
  medicalCard: { label: 'Medical Card', icon: 'medical_services' },
  forfeitureDocs: { label: 'Forfeiture Docs', icon: 'gavel' },
};
// Only these two are expected of every driver applicant; a missing forfeiture
// doc is the normal case and a permanent amber row would train reviewers to
// ignore the whole panel.
const _APP_DOC_REQUIRED = ['driverLicense', 'medicalCard'];
// TASK-9448 — true only when the SERVER recorded that this application's job does
// not need a DOT medical card (saveDriverApplication stamps `medCardRequired` at
// submit). Absent (every application before TASK-9448, or a draft) = unknown, so
// the old "expected" treatment stands.
function _candMedCardNotRequired(c) {
  return !!c && c.medCardRequired === false;
}
// TASK-9476 (Chris 2026-09-30) — the med card is off by default for non-CDL
// applicants; tell the person who can CHANGE that where it lives. Only when this
// application was not asked for a card, and only for a session that can write
// tenants/{id}.settings (firestore.rules isTenantAdmin — the same gate as the HR
// Settings button). Applicants never see this: apply.html is a separate page.
// Driver types only: a Non-Driver job never asks, and no setting changes that.
// Copy never claims "by default" — an opening's own yes/no can be the reason —
// and says the change reaches NEW applications (this one keeps its stamp).
function _medCardSettingHintHtml(c, canEdit) {
  if (canEdit !== true || !_candMedCardNotRequired(c)) return '';
  const lead = c.positionType === 'non-cdl-driver'
    ? 'Non-CDL drivers still need one if they drive a vehicle of 10,001 lb or more across state lines.'
    : c.positionType === 'cdl' ? 'CDL drivers need one under 49 CFR 391.41.' : '';
  if (!lead) return '';
  return `<div data-med-setting-hint style="margin-top:6px;font-size:0.75rem;color:var(--text-secondary);line-height:1.45;">
      ${lead} Change it for new applications in HR Settings &rarr; Job Openings.
      <button type="button" class="cand-btn cand-btn-secondary" onclick="window.openJobOpeningsModal&&window.openJobOpeningsModal()" style="font-size:0.75rem;padding:6px 12px;margin:4px 0 0 4px;display:inline-block;">Open Job Openings</button>
    </div>`;
}

function _appDocIsImage(contentType) {
  return /^image\//i.test(String(contentType || '')) && !/svg/i.test(String(contentType || ''));
}

// Synchronous shell. The real state arrives from the server — until then say so,
// rather than rendering "Not provided", which would be a claim we have not
// checked yet and is the exact failure this task exists to remove.
function _candidateAppDocsBodyHtml(c) {
  const cid = (c && c.id) ? String(c.id) : '';
  if (!cid) return `<div style="font-size:0.8rem;color:var(--text-secondary);">Save the candidate first — documents attach to a saved application.</div>`;
  return `<div data-app-docs-for="${window.escAttr ? window.escAttr(cid) : escapeHtml(cid)}">
    <div style="font-size:0.8rem;color:var(--text-secondary);display:flex;align-items:center;gap:6px;">
      <span class="material-icons" style="font-size:15px;">hourglass_top</span> Checking what the applicant uploaded…
    </div>
  </div>`;
}

function _candidateAppDocsHtml(c) {
  return `
    <hr style="margin: var(--spacing-md) 0; border: none; border-top: 1px solid var(--border-light);">
    <div class="form-group">
      <label>Application Documents</label>
      ${_candidateAppDocsBodyHtml(c)}
    </div>`;
}

function _appDocRowHtml(docKey, found, candidateId, missingNote) {
  const meta = _APP_DOC_LABELS[docKey] || { label: docKey, icon: 'description' };
  if (!found) {
    return `
      <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:var(--bg-page);border-radius:var(--radius-sm);">
        <span class="material-icons" style="color:var(--text-muted);font-size:20px;">${meta.icon}</span>
        <span style="flex:1;font-size:0.85rem;color:var(--text-secondary);">${escapeHtml(meta.label)}</span>
        <span style="font-size:0.75rem;font-weight:600;color:#B45309;background:#FEF3C7;border:1px solid #FCD34D;border-radius:999px;padding:2px 8px;">${escapeHtml(missingNote || 'Not provided')}</span>
      </div>`;
  }
  const kb = found.sizeBytes ? ` · ${Math.max(1, Math.round(found.sizeBytes / 1024))} KB` : '';
  const isImg = _appDocIsImage(found.contentType);
  return `
    <div style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:var(--bg-page);border-radius:var(--radius-sm);"
         data-app-doc-row="${escapeHtml(docKey)}">
      <span class="material-icons app-doc-thumb-slot" style="color:var(--action-primary);font-size:20px;">${meta.icon}</span>
      <div style="flex:1;min-width:0;">
        <div style="font-size:0.85rem;color:var(--text-primary);font-weight:600;">${escapeHtml(meta.label)}</div>
        <div style="font-size:0.72rem;color:var(--text-secondary);word-break:break-all;">${escapeHtml(found.leaf || 'On file')}${escapeHtml(kb)}</div>
      </div>
      <button type="button" class="cand-btn cand-btn-secondary"
              data-app-doc-view="${escapeHtml(docKey)}"
              data-app-doc-cand="${escapeHtml(candidateId)}"
              style="font-size:0.75rem;padding:4px 10px;white-space:nowrap;">
        <span class="material-icons" style="font-size:14px;vertical-align:middle;">open_in_new</span> View
      </button>
    </div>`;
}

// TASK-9303 — the applicant's logged "I will provide the back later" choice.
// licenseBackDeferredAt is written by the PUBLIC apply callable, so it is
// display-only and parsed, never echoed raw: anything that is not a date reads
// as a plain skip.
function _licenseBackDeferredNote(cand) {
  const raw = cand && typeof cand.licenseBackDeferredAt === 'string' ? cand.licenseBackDeferredAt : '';
  if (!raw) return '';
  const t = Date.parse(raw);
  return Number.isNaN(t)
    ? 'Applicant will provide later'
    : `Applicant will provide later (said so ${new Date(t).toLocaleDateString('en-US')})`;
}

// Fetch the real state and repaint. Called after the modal is in the DOM.
async function _loadCandidateAppDocs(candidateId, cand) {
  const host = document.querySelector(`[data-app-docs-for="${CSS.escape(String(candidateId))}"]`);
  if (!host) return;
  let docs = [];
  try {
    const fn = httpsCallable(functions, 'listApplicationDocs');
    const res = await fn({ tenantId: HRState.tenantId, candidateId });
    docs = (res && res.data && Array.isArray(res.data.docs)) ? res.data.docs : [];
  } catch (err) {
    console.warn('[HR] listApplicationDocs failed:', err?.message);
    // Say we could not check. Rendering "Not provided" here would assert an
    // absence off a call that failed — the false-negative twin of the bug this
    // panel was built to fix.
    host.innerHTML = `<div style="font-size:0.8rem;color:#92400E;background:#FEF3C7;border:1px solid #FCD34D;border-radius:var(--radius-sm);padding:8px 10px;">
      <span class="material-icons" style="font-size:14px;vertical-align:middle;color:#B45309;">warning_amber</span>
      Could not check the applicant's uploads just now. This does not mean there are none — reopen the candidate to try again.
    </div>`;
    return;
  }
  const byKey = {};
  docs.forEach(d => { if (d && d.docKey) byKey[d.docKey] = d; });

  // TASK-9303 — the back sits beside the front. Deliberately NOT in
  // _APP_DOC_REQUIRED / the banner: every application filed before the back was
  // asked for lacks one, and a permanent amber banner trains reviewers to skip
  // the panel. The row itself still says Not provided, or that the applicant
  // chose to hand it over later.
  const _cand = cand || (Array.isArray(HRState.candidates) ? HRState.candidates.find(x => x && x.id === candidateId) : null);
  // apply.html asks for the back on CDL positions only; a non-CDL or office applicant
  // was never asked, so their row would claim a gap that is not one. Unclassified =
  // 'cdl' (the full set). A back on file always shows.
  const _backAsked = (window.DqMatrix?.normalizePositionType?.(_cand && _cand.positionType) || 'cdl') === 'cdl';
  const keys = ['driverLicense', ...((_backAsked || byKey.driverLicenseBack) ? ['driverLicenseBack'] : []), ..._APP_DOC_REQUIRED.filter(k => k !== 'driverLicense')];
  if (byKey.forfeitureDocs) keys.push('forfeitureDocs');
  const _medNotReq = _candMedCardNotRequired(_cand); // TASK-9448
  const rows = keys.map(k => _appDocRowHtml(k, byKey[k], candidateId,
    k === 'driverLicenseBack' ? _licenseBackDeferredNote(_cand)
      : (k === 'medicalCard' && _medNotReq) ? 'Med card: not required for this job' : '')).join('');
  const missing = _APP_DOC_REQUIRED.filter(k => !byKey[k] && !(k === 'medicalCard' && _medNotReq));
  const banner = missing.length
    ? `<div style="margin-top:8px;font-size:0.75rem;color:#92400E;background:#FEF3C7;border:1px solid #FCD34D;border-radius:var(--radius-sm);padding:8px 10px;line-height:1.45;">
         <span class="material-icons" style="font-size:14px;vertical-align:middle;color:#B45309;">warning_amber</span>
         The applicant did not upload ${missing.map(k => k === 'driverLicense' ? 'a license' : 'a medical card').join(' or ')}. Ask for it before the DQ file is built — §391.51 wants the card itself, not just the expiry you typed.
       </div>`
    : '';
  const medHint = _medCardSettingHintHtml(_cand, _hrEffectiveAdminSafe() === true); // TASK-9476
  host.innerHTML = `<div style="display:flex;flex-direction:column;gap:6px;">${rows}</div>${medHint}${banner}`;

  // Inline thumbnails for the images, fetched through the same gated callable.
  // Deliberately sequential and best-effort: a thumbnail is worth one extra
  // round trip, never a broken panel.
  for (const d of docs) {
    if (!_appDocIsImage(d.contentType) || (d.sizeBytes || 0) > 7 * 1024 * 1024) continue;
    try {
      const fn = httpsCallable(functions, 'getApplicationDocBytes');
      const res = await fn({ tenantId: HRState.tenantId, candidateId, docKey: d.docKey });
      const b64 = res?.data?.base64;
      if (!b64) continue;
      const row = host.querySelector(`[data-app-doc-row="${CSS.escape(d.docKey)}"] .app-doc-thumb-slot`);
      if (!row) continue;
      const img = document.createElement('img');
      img.src = `data:${res.data.contentType || 'image/jpeg'};base64,${b64}`;
      img.alt = (_APP_DOC_LABELS[d.docKey] || {}).label || d.docKey;
      img.style.cssText = 'width:56px;height:40px;object-fit:cover;border-radius:4px;border:1px solid var(--border-light);display:block;background:var(--bg-page);flex-shrink:0;';
      row.replaceWith(img);
    } catch (err) {
      console.warn('[HR] app doc thumbnail failed:', d.docKey, err?.message);
    }
  }
}

// One delegated listener for View. The bytes come back base64 from the gated
// callable and are handed to the browser as a blob — no URL from the candidate
// doc is ever navigated to.
let _appDocViewDelegated = false;
function _installAppDocViewDelegate() {
  if (_appDocViewDelegated) return;
  _appDocViewDelegated = true;
  document.addEventListener('click', async (ev) => {
    const btn = ev.target && ev.target.closest ? ev.target.closest('[data-app-doc-view]') : null;
    if (!btn) return;
    ev.preventDefault();
    const docKey = btn.getAttribute('data-app-doc-view');
    const candidateId = btn.getAttribute('data-app-doc-cand');
    const prev = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = 'Opening…';
    let url = null;
    try {
      const fn = httpsCallable(functions, 'getApplicationDocBytes');
      const res = await fn({ tenantId: HRState.tenantId, candidateId, docKey });
      const d = res?.data || {};
      if (!d.base64) throw new Error('That document came back empty.');
      const bin = atob(d.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: d.contentType || 'application/octet-stream' });
      url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) window.showToast?.('Your browser blocked the popup. Allow popups for this site to view documents.', 'warning');
      setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) {} }, 60000);
    } catch (err) {
      console.warn('[HR] application doc view failed:', err?.message);
      if (url) { try { URL.revokeObjectURL(url); } catch (_) {} }
      window.showToast?.(err?.message || 'Could not open that document.', 'error');
    } finally {
      btn.disabled = false;
      btn.innerHTML = prev;
    }
  }, true);
}

function getCandidateModalHTML(candidate) {
  const isEdit = !!candidate;
  const c = candidate || {};
  // TASK-431 — prefill from flat OR nested license so editing an apply.html
  // candidate doesn't blank out their CDL class / endorsements on save.
  const editLic = resolveCandidateLicense(c);
  const endorsements = editLic.endorsements;
  const editCdlClass = (editLic.cdlClass || '').toUpperCase();
  return `
    <div class="modal" id="modal-candidate-form">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3>${isEdit ? 'Edit' : 'Add'} Candidate</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('candidate-form')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-candidate" novalidate onsubmit="window.HRModule.saveCandidateForm(event)">
          <div class="modal-form-scroll" style="max-height: 65vh; overflow-y: auto; padding: var(--spacing-lg);">

            ${isEdit ? '<div id="candidate-bg-decision-slot"></div>' : ''}

            <div class="form-row" style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-md);">
              <div class="form-group">
                <label>Name *</label>
                <input type="text" id="candidate-name" value="${escapeHtml(c.name || '')}" required>
              </div>
              <div class="form-group">
                <label>Position</label>
                <input type="text" id="candidate-position" value="${escapeHtml(c.position || '')}" placeholder="e.g. CDL Driver, Operator" list="candidate-position-openings" autocomplete="off">
                ${''/* TASK-4725 Slice D — suggest the carrier's active job-opening titles so one job
                       stops getting typed four ways. Stays free text: a suggestion, not a picker. */}
                <datalist id="candidate-position-openings">${(window.JobOpenings ? window.JobOpenings.active(window.AppState?.settings?.jobOpenings) : [])
                  .map(o => `<option value="${escapeHtml(o.title)}"></option>`).join('')}</datalist>
              </div>
            </div>

            <div class="form-row" style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-md);margin-top:var(--spacing-md);">
              <div class="form-group">
                <label>Phone</label>
                <input type="tel" id="candidate-phone" value="${escapeHtml(c.phone || '')}" placeholder="(555) 123-4567">
              </div>
              <div class="form-group">
                <label>Email</label>
                <input type="email" id="candidate-email" value="${escapeHtml(c.email || '')}" placeholder="name@email.com"${isEdit && _hrInviteOutstanding(c) ? ' disabled' : ''}>
                ${isEdit && _hrInviteOutstanding(c) ? `
                <div id="candidate-email-lock" style="font-size:0.8rem;color:var(--text-secondary);margin-top:6px;">
                  An application link is out to this address, so it can't be changed.
                  <button type="button" class="btn btn-secondary btn-sm" data-cancel-app-link="${escapeHtml(c.id)}" onclick="window.HRModule.cancelApplicationLink('${c.id}')" style="margin-left:4px;">Cancel invite to change</button>
                </div>` : ''}
              </div>
            </div>
            ${!isEdit ? `
            <!-- TASK-9530 — opt-in: the candidate finishes and signs the application themselves. -->
            <label class="radio-label" style="font-size:0.85rem;display:flex;align-items:flex-start;gap:8px;margin:4px 0 0;">
              <input type="checkbox" id="candidate-send-app-link" style="margin-top:3px;">
              <span>Email them a link to finish and sign their application (needs an email). You will see the address before it sends.</span>
            </label>` : ''}

            <hr style="margin: var(--spacing-md) 0; border: none; border-top: 1px solid var(--border-light);">

            <div class="form-row" style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:var(--spacing-md);">
              <div class="form-group">
                <!-- TASK-1218 — label truth: a non-A/B/C class (Class D etc. from the
                     apply form's non-CDL positions) is labeled "License Class (non-CDL)"
                     and keeps its stored value as a selectable option so editing a
                     Class-D candidate doesn't silently clobber the class to "none". -->
                <label>${editCdlClass && !/^[ABC]$/.test(editCdlClass) ? 'License Class (non-CDL)' : 'CDL Class'}</label>
                <select id="candidate-cdl">
                  <option value="none" ${!editCdlClass ? 'selected' : ''}>None</option>
                  <option value="A" ${editCdlClass === 'A' ? 'selected' : ''}>Class A</option>
                  <option value="B" ${editCdlClass === 'B' ? 'selected' : ''}>Class B</option>
                  <option value="C" ${editCdlClass === 'C' ? 'selected' : ''}>Class C</option>
                  ${editCdlClass && !/^[ABC]$/.test(editCdlClass) ? `<option value="${escapeHtml(editCdlClass)}" selected>Class ${escapeHtml(editCdlClass)} (non-CDL)</option>` : ''}
                </select>
              </div>
              <div class="form-group">
                <label>Experience</label>
                <input type="number" id="candidate-experience" value="${c.yearsExperience ?? ''}" min="0" max="50" placeholder="Years">
              </div>
              <div class="form-group">
                <label>Source</label>
                <select id="candidate-source">
                  ${CANDIDATE_SOURCES.map(s => `<option value="${s.id}" ${c.source === s.id ? 'selected' : ''}>${s.label}</option>`).join('')}
                </select>
              </div>
            </div>

            <div class="form-group" style="margin-top:var(--spacing-md);">
              <label>CDL Endorsements</label>
              <div style="display:grid;grid-template-columns:repeat(3, 1fr);gap:6px 12px;margin-top:4px;">
                ${CDL_ENDORSEMENTS.map(e => `
                  <label style="display:flex;align-items:center;gap:6px;font-size:0.85rem;cursor:pointer;white-space:nowrap;">
                    <input type="checkbox" class="candidate-endorsement" value="${e.id}" ${endorsements.includes(e.id) ? 'checked' : ''}>
                    ${e.label}
                  </label>
                `).join('')}
              </div>
            </div>

            <div class="form-group" style="margin-top:var(--spacing-md);">
              <label>Applied Date</label>
              <input type="date" id="candidate-applied-date" value="${c.appliedDate ? formatDateForInput(c.appliedDate) : new Date().toISOString().split('T')[0]}">
            </div>

            ${_candidateAppDocsHtml(c)}

            <hr style="margin: var(--spacing-md) 0; border: none; border-top: 1px solid var(--border-light);">

            <div class="form-group">
              <label>Resume</label>
              ${c.resumeUrl || c.resumeText ? `
                <div class="resume-preview" style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:var(--bg-page);border-radius:var(--radius-sm);margin-bottom:var(--spacing-sm);">
                  <span class="material-icons" style="color:var(--action-primary);">description</span>
                  ${c.resumeUrl ? `<a href="${_safeHttpUrl(c.resumeUrl)}" target="_blank" style="flex:1;font-size:0.85rem;">${escapeHtml(c.resumeFileName || 'View Resume')}</a>` : `<span style="flex:1;font-size:0.85rem;color:var(--text-secondary);">Text resume on file</span>`}
                  <button type="button" class="btn-icon" onclick="window.HRModule.removeResume('${c.id || ''}')" title="Remove resume">
                    <span class="material-icons" style="color:var(--status-danger);font-size:18px;">close</span>
                  </button>
                </div>
              ` : ''}
              <div class="resume-tabs" style="display:flex;gap:0;border:1px solid var(--border-light);border-radius:var(--radius-sm);overflow:hidden;margin-bottom:var(--spacing-sm);">
                <button type="button" class="resume-tab active" onclick="window.HRModule.switchResumeTab('file')" data-tab="file" style="flex:1;padding:6px 8px;font-size:0.75rem;border:none;cursor:pointer;background:var(--action-primary);color:#fff;display:flex;align-items:center;justify-content:center;gap:4px;">
                  <span class="material-icons" style="font-size:14px;">upload_file</span> Upload File
                </button>
                <button type="button" class="resume-tab" onclick="window.HRModule.switchResumeTab('text')" data-tab="text" style="flex:1;padding:6px 8px;font-size:0.75rem;border:none;border-left:1px solid var(--border-light);cursor:pointer;background:var(--bg-elevated);color:var(--text-secondary);display:flex;align-items:center;justify-content:center;gap:4px;">
                  <span class="material-icons" style="font-size:14px;">content_paste</span> Paste Text
                </button>
                <button type="button" class="resume-tab" onclick="window.HRModule.switchResumeTab('url')" data-tab="url" style="flex:1;padding:6px 8px;font-size:0.75rem;border:none;border-left:1px solid var(--border-light);cursor:pointer;background:var(--bg-elevated);color:var(--text-secondary);display:flex;align-items:center;justify-content:center;gap:4px;">
                  <span class="material-icons" style="font-size:14px;">link</span> Enter URL
                </button>
              </div>
              <div id="resume-tab-file" class="resume-tab-content">
                <input type="file" id="candidate-resume-file" accept=".pdf,.doc,.docx,.rtf,.jpg,.jpeg,.png" style="display:none;" onchange="window.HRModule.handleResumeFileSelect(event)">
                <div onclick="document.getElementById('candidate-resume-file').click()" style="border:2px dashed var(--border-light);border-radius:var(--radius-sm);padding:var(--spacing-lg);text-align:center;cursor:pointer;transition:border-color 0.2s;" onmouseover="this.style.borderColor='var(--action-primary)'" onmouseout="this.style.borderColor='var(--border-light)'">
                  <span class="material-icons" style="font-size:32px;color:var(--text-muted);display:block;margin-bottom:4px;">cloud_upload</span>
                  <div id="resume-file-name" style="font-size:0.85rem;color:var(--text-secondary);">Choose file or drag & drop</div>
                  <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:4px;">PDF, DOC, DOCX, RTF (max 10MB)</div>
                </div>
              </div>
              <div id="resume-tab-text" class="resume-tab-content" style="display:none;">
                <textarea id="candidate-resume-text" rows="5" placeholder="Paste resume text here..." style="width:100%;font-size:0.8rem;">${escapeHtml(c.resumeText || '')}</textarea>
              </div>
              <div id="resume-tab-url" class="resume-tab-content" style="display:none;">
                <input type="url" id="candidate-resume-url" value="${escapeHtml((!c.resumeStoragePath && c.resumeUrl) ? c.resumeUrl : '')}" placeholder="https://example.com/resume.pdf" style="width:100%;">
                <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:4px;">Link to resume hosted on Indeed, LinkedIn, Google Drive, etc.</div>
              </div>
            </div>

            <hr style="margin: var(--spacing-md) 0; border: none; border-top: 1px solid var(--border-light);">

            <div class="form-row" style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-md);">
              <div class="form-group">
                <label>Status</label>
                <select id="candidate-status" onchange="window.HRModule.toggleRejectionReason()">
                  ${PIPELINE_STAGES.map(s => `<option value="${s.id}" ${(c.status || 'new') === s.id ? 'selected' : ''}>${s.label}</option>`).join('')}
                </select>
              </div>
              <div class="form-group" id="rejection-reason-group" style="display:${c.status === 'rejected' ? 'block' : 'none'};">
                <label>Rejection Reason</label>
                <select id="candidate-rejection-reason" onchange="window.HRModule.syncCandidateFormDecisionCta()">
                  <option value="">Select reason...</option>
                  ${REJECTION_REASONS.map(r => `<option value="${r}" ${c.rejectionReason === r ? 'selected' : ''}>${r}</option>`).join('')}
                </select>
              </div>
            </div>

            <div class="form-group" style="margin-top:var(--spacing-md);">
              <label>Notes</label>
              <textarea id="candidate-notes" rows="3" placeholder="Interview notes, references, etc.">${escapeHtml(c.notes || '')}</textarea>
            </div>

            <!-- TASK-4550 — 🔴 §391.21. On a SUBMITTED application, Name / Phone /
                 Email are signed content. This form used to overwrite them with a
                 silent updateDoc, which breaks the certification's attestation.
                 They now route through correctApplicationFields (before/after +
                 reason + who + when, original frozen) and the reason is required.
                 Only rendered for a submitted application — everything else is
                 still a plain edit. -->
            ${(isEdit && c.applicationComplete === true) ? `
            <div class="form-group" id="candidate-correction-group" style="margin-top:var(--spacing-md);background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-md);padding:10px 12px;">
              <label style="color:#92400E;">Reason for correcting name / phone / email</label>
              <input type="text" id="candidate-correction-reason" maxlength="500" placeholder="e.g. Applicant transposed two digits in their phone number — confirmed by call.">
              <div style="font-size:0.72rem;color:#92400E;margin-top:4px;line-height:1.45;">
                This applicant already signed and submitted their application. Changing one of those three fields records a permanent correction with your name; the original submission is kept as signed. Leave this blank if you are not changing them.
                <strong>If the substance is wrong, close this and use “Send Back…” instead.</strong>
              </div>
            </div>` : ''}

          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" onclick="window.HRModule.closeModal('candidate-form')">Cancel</button>
            <button type="submit" class="btn btn-primary" id="candidate-form-save-btn">
              <span class="material-icons">save</span> ${isEdit ? 'Save Changes' : 'Add Candidate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  `;
}

// ============================================================================
// TASK-1366 — ONE card shell for the candidate-detail modal.
//
// The modal had grown to 13 sections, each inventing its own header, border,
// padding and label/value layout ("this looks like a nightmare" — Chris,
// dogfooding a real applicant 2026-08-05). These helpers are the single
// presentational vocabulary every section now renders through. Styling lives
// in styles.css under `.cand-*`, deliberately mirroring the compliance-portal
// card language (.cp-dd-card / .cp-set-card / .cp-set-field) which is the
// named internal style reference.
//
// PRESENTATION ONLY. No helper here reads access, changes a status, or writes
// anything — every onclick target in the sections below is unchanged.
//
// NOTE: no call site may pass an inline grid-template-columns / width. The
// hardcoded `1fr 1fr` is exactly what clipped the right column on a 430px
// phone (TASK-1328). `.cand-grid` owns the column count at every width.
// ============================================================================

// Section card. `actions` is trusted HTML the caller already escaped.
function _candCard(opts) {
  const o = opts || {};
  const icon = o.icon || 'chevron_right';
  const note = o.note ? ` <span class="cand-card-note">${o.note}</span>` : '';
  const actions = o.actions ? `<div class="cand-card-actions">${o.actions}</div>` : '';
  const sub = o.sub ? `<div class="cand-card-sub">${o.sub}</div>` : '';
  const foot = o.foot ? `<div class="cand-card-foot">${o.foot}</div>` : '';
  const bodyCls = o.flush ? 'cand-card-body cand-card-body--flush' : 'cand-card-body';
  const body = o.body ? `<div class="${bodyCls}">${o.body}</div>` : '';
  const attrs = o.attrs ? ' ' + o.attrs : '';
  return `
    <div class="cand-sec"${attrs}>
      <div class="cand-card">
        <div class="cand-card-h">
          <div class="cand-card-title"><span class="material-icons">${icon}</span><span>${o.title || ''}${note}</span></div>
          ${actions}
        </div>
        ${sub}
        ${body}
        ${o.raw || ''}
        ${foot}
      </div>
    </div>`;
}

// Sub-block header inside a card (keeps related content in ONE card instead of
// spawning a sibling card about the same subject).
function _candSubhead(icon, title, note) {
  return `<div class="cand-subhead">${icon ? `<span class="material-icons">${icon}</span>` : ''}<span>${title}</span>${note ? `<span class="cand-subhead-note">${note}</span>` : ''}</div>`;
}

// Real empty state. Never render a bare em-dash as content again.
function _candEmpty(text) {
  return `<span class="cand-empty">${escapeHtml(text || 'Not provided')}</span>`;
}

// Label/value pair. `value` is trusted HTML; pass an already-escaped string.
// When it's blank we substitute the caller's empty-state copy.
function _candField(label, value, opts) {
  const o = opts || {};
  const v = (value == null || value === '' || value === '—') ? _candEmpty(o.empty) : value;
  return `<div class="cand-field${o.wide ? ' cand-field--wide' : ''}">
      <div class="cand-field-label">${escapeHtml(label)}</div>
      <div class="cand-field-value">${v}</div>
    </div>`;
}

// ============================================================================
// TASK-1488 — §391.21(b)(2) SSN reveal-on-click (Crown 8/11 call).
// The application must CONTAIN the SSN (federal requirement), but the screen
// shows the mask by default; the FULL value lives in candidatesTax
// (admin/hr-directory read-gated in firestore.rules, written only by the
// server at final submit) and is fetched per-reveal. Every reveal — eye click
// OR print — writes a permanent SSN_REVEALED auditLog row ('hr' module is
// TTL-exempt server-side). Revealed state is render-local only: nothing is
// persisted, and re-opening the modal starts masked again.
// ============================================================================
function _candSsnValueHtml(c) {
  const masked = c.ssnMasked || c.ssn || '';
  // TASK-9577 — an applicant living outside the US declared no SSN (server
  // honours that only for a CA / MX home address; nothing is stored in its place).
  if (!masked) return c.noUsSsn === true ? '<span data-no-us-ssn>No U.S. SSN (applicant declared)</span>' : '';
  const _escA = window.escAttr || escapeHtml;
  // Eye renders ONLY for viewers who can actually read the vault
  // (admin / hr-directory — same gate as peopleTax). A bare hr-module member
  // sees the mask with no reveal affordance (TASK-815 posture).
  const eye = _hrCanSeeSecureTax()
    ? `<button type="button" class="btn-icon" data-no-print onclick="window.HRModule.toggleCandidateSsnReveal('${escJsAttr(c.id)}', this)" title="Reveal full SSN (recorded in the audit log)" aria-label="Reveal full SSN" style="padding:0;width:24px;height:24px;min-width:24px;vertical-align:middle;margin-left:6px;display:inline-flex;align-items:center;justify-content:center;"><span class="material-icons" style="font-size:16px;">visibility</span></button>`
    : '';
  return `<span data-ssn-candidate="${_escA(c.id)}" data-ssn-masked="${_escA(masked)}">${escapeHtml(masked)}</span>${eye}`;
}

// Fetch the full SSN via the SERVER-AUTHORED revealCandidateSsn callable (TASK-1565).
// Returns the full SSN string, or null when no vault doc exists (applications
// submitted before TASK-1488 only ever stored the last-4 mask).
//
// FAIL-CLOSED ON THE AUDIT (2nd-integration-COD Code Reviewer ❌): the SSN is
// returned ONLY if the SSN_REVEALED audit row lands. The whole compliance value
// of reveal-on-click is the "who saw the SSN, when" trail; returning the value
// after a swallowed audit failure permits an UNAUDITED disclosure. So a failed
// audit THROWS — the caller shows an error and no SSN is revealed.
//
// TASK-1565 moved the vault read AND the audit write to the server so uid/
// revealedBy are the VERIFIED caller (not client-controlled) and the row cannot
// be forged or omitted — the carrier side now matches the partner side's
// server-authored disclosure trail. A transient audit failure is rare and
// non-destructive (§391.21 is satisfied by the vault + print regardless) — the
// user just clicks again. Not the TASK-428 lockout class: it blocks a single
// reveal action, never a workflow, and clears on retry.
async function _fetchCandidateSsnFull(candidateId, source) {
  // TASK-1565 — the reveal is now SERVER-AUTHORED via the revealCandidateSsn
  // callable. It re-derives the candidatesTax read gate (admin | hr-directory),
  // reads the vault SSN with the Admin SDK, and writes the SSN_REVEALED audit row
  // server-side (uid = verified caller, revealedBy = caller token email) —
  // FAIL-CLOSED (it throws before returning the ssn if the audit row can't land).
  // The client no longer reads candidatesTax directly nor writes the audit row,
  // so the "who saw the SSN" row can't be forged or omitted by the caller.
  // Contract preserved: returns the full SSN string, or null when the server holds
  // no full SSN for it (TASK-9428: vault, then the hired driver's hash-matching
  // peopleTax; failed-precondition → the toggle's "ask the driver" toast). Any
  // other error (permission-denied / unavailable) propagates → caller fails closed.
  try {
    const fn = httpsCallable(functions, 'revealCandidateSsn');
    const res = await fn({ tenantId: HRState.tenantId, candidateId, source });
    return (res && res.data && res.data.ssn) || null;
  } catch (err) {
    if (err?.code === 'functions/failed-precondition' || err?.code === 'failed-precondition') {
      return null; // predates secure storage — same info-toast path as before
    }
    throw err; // permission-denied / unavailable → caller does NOT reveal (fail-closed)
  }
}

// Eye-icon click handler on the candidate application modal. Toggle OFF
// (re-mask) is free; every toggle ON is a fresh vault read + audit row.
async function toggleCandidateSsnReveal(candidateId, btn) {
  const span = document.querySelector(`[data-ssn-candidate="${(window.CSS && CSS.escape) ? CSS.escape(candidateId) : candidateId}"]`);
  if (!span) return;
  const icon = btn?.querySelector('.material-icons');
  if (span.dataset.revealed === '1') {
    // Re-mask (display only — nothing was persisted).
    span.textContent = span.dataset.ssnMasked || '';
    delete span.dataset.revealed;
    if (icon) icon.textContent = 'visibility';
    if (btn) { btn.title = 'Reveal full SSN (recorded in the audit log)'; btn.setAttribute('aria-label', 'Reveal full SSN'); }
    return;
  }
  if (btn) btn.disabled = true;
  try {
    const full = await _fetchCandidateSsnFull(candidateId, 'carrier_view');
    if (!full) {
      // TASK-9428 — plain words, not an error: the server already tried the vault
      // and the hired driver's secure tax record; nothing held means ask the driver.
      // TASK-9455 — a vault EDITOR (owner / hr-directory, not view-only) can then
      // type it in; everyone else keeps the plain "ask the driver" toast.
      if (_hrCanEnterApplicationSsn()) {
        openEnterCandidateSsnModal(candidateId, span.dataset.ssnMasked || '');
      } else {
        // TASK-9455 (Chris 2026-10-27) — a managing compliance partner sees the
        // Enter SSN button GREYED with "Carrier office only", not hidden. Display
        // only: the button is disabled and has no handler; the server refuses a
        // partner anyway (enterApplicationSsn has no partner arm).
        if ((window.AppState || {}).partnerClientContext) _hrShowSsnEntryOfficeOnly(span);
        window.showToast?.('This application was submitted before full SSNs were stored; ask the driver.', 'info');
      }
      return;
    }
    // textContent (never innerHTML) — the value can't become an HTML/attribute sink.
    span.textContent = full;
    span.dataset.revealed = '1';
    if (icon) icon.textContent = 'visibility_off';
    if (btn) { btn.title = 'Hide SSN'; btn.setAttribute('aria-label', 'Hide SSN'); }
  } catch (err) {
    console.error('[HR] SSN reveal failed:', err);
    const denied = /permission/i.test(err?.code || err?.message || '');
    window.showToast?.(denied
      ? 'You need the HR Directory module (or admin) to reveal the full SSN.'
      : 'Could not load the SSN — try again.', 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

// TASK-9455 — ENTER a missing full SSN on an application, after asking the
// driver. Offered only when the reveal found nothing AND the viewer can write
// the vault (_hrCanEditSecureTax). The server (enterApplicationSsn) re-checks
// access, requires the entry to match the application's last-4 (and its hash
// when held), writes ONLY the SSN vault (+ the hired driver's empty secure tax
// record), never overwrites a different SSN, and logs SSN_ENTERED. The value
// is read from masked inputs (text + -webkit-text-security, not type=password,
// so the browser never offers to save it as a password), sent once, never rendered.
// Client mirror of the server gate (enterApplicationSsn / hrVaultEditorDecision):
// the OWNER via tenants.ownerId, an active hr-directory member who is not
// view-only / read-only. OFFICE ONLY (Chris 2026-10-20): never the managing
// partner in the carrier's app. Never a role, never the demo. The UI may hide
// more than the server, never offer more.
function _hrCanEnterApplicationSsn() {
  const st = window.AppState || {};
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  if (st.isDemo || st.tenant === 'demo' || HRState.tenantId === 'demo') return false;
  if (st.partnerClientContext) return false;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true || ma.viewUploadOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('hr-directory');
}

// TASK-9455 (Chris 2026-10-27) — greyed, disabled Enter SSN + "Carrier office
// only" after the SSN span, once. Built with textContent; no click handler.
function _hrShowSsnEntryOfficeOnly(span) {
  if (!span || !span.parentNode) return;
  const host = span.parentNode;
  if (host.querySelector('[data-ssn-entry-office-only]')) return;
  const wrap = document.createElement('span');
  wrap.setAttribute('data-ssn-entry-office-only', '1');
  wrap.setAttribute('data-no-print', '');
  wrap.style.cssText = 'display:inline-flex;align-items:center;gap:6px;margin-left:8px;vertical-align:middle;';
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'btn btn-secondary';
  b.disabled = true;
  b.setAttribute('aria-disabled', 'true');
  b.title = 'Carrier office only';
  b.textContent = 'Enter SSN';
  b.style.cssText = 'opacity:0.5;cursor:not-allowed;padding:2px 8px;font-size:0.78rem;';
  const note = document.createElement('span');
  note.textContent = 'Carrier office only';
  note.style.cssText = 'font-size:0.78rem;color:var(--text-secondary);';
  wrap.append(b, note);
  host.appendChild(wrap);
}

let _ssnEntryInFlight = false;
function openEnterCandidateSsnModal(candidateId, maskedSsn) {
  if (!candidateId) return;
  const last4 = (/(\d{4})$/.exec(String(maskedSsn || '')) || [])[1] || '';
  document.getElementById('ssn-entry-modal')?.remove();
  const overlay = document.createElement('div');
  overlay.id = 'ssn-entry-modal';
  overlay.className = 'modal';
  overlay.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:10050;';
  overlay.innerHTML = `
    <div class="modal-content" role="dialog" aria-modal="true" aria-labelledby="ssn-entry-title" style="max-width:440px;width:calc(100% - 32px);">
      <div class="modal-header">
        <h3 id="ssn-entry-title"><span class="material-icons">pin</span> Enter SSN</h3>
        <button type="button" class="btn-icon" data-ssn-entry-close aria-label="Close"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="display:flex;flex-direction:column;gap:12px;">
        <div style="font-size:0.9rem;color:var(--text-primary);line-height:1.45;">No full SSN is on file for this application. Ask the driver, then enter it here.${last4 ? ` It must end in <strong>${escapeHtml(last4)}</strong>, the last 4 on the application.` : ''}</div>
        <label style="font-size:0.8rem;font-weight:700;color:var(--text-secondary);">SSN
          <input type="text" id="ssn-entry-value" inputmode="numeric" autocomplete="off" data-lpignore="true" data-1p-ignore spellcheck="false" maxlength="11" placeholder="123-45-6789" style="width:100%;padding:10px;font-size:1rem;margin-top:4px;-webkit-text-security:disc;">
        </label>
        <label style="font-size:0.8rem;font-weight:700;color:var(--text-secondary);">Type it again
          <input type="text" id="ssn-entry-confirm" inputmode="numeric" autocomplete="off" data-lpignore="true" data-1p-ignore spellcheck="false" maxlength="11" style="width:100%;padding:10px;font-size:1rem;margin-top:4px;-webkit-text-security:disc;">
        </label>
        <label style="display:flex;align-items:center;gap:8px;font-size:0.85rem;color:var(--text-primary);min-height:44px;cursor:pointer;"><input type="checkbox" id="ssn-entry-show" style="width:20px;height:20px;"> Show digits</label>
        <div id="ssn-entry-error" role="alert" style="display:none;font-size:0.85rem;color:#B91C1C;background:#FEF2F2;border:1px solid #FECACA;border-radius:6px;padding:8px 10px;"></div>
        <div style="font-size:0.78rem;color:var(--text-secondary);">Saved to the secure SSN record only, and recorded in the audit log. An SSN already on file is never changed.</div>
        <div style="display:flex;flex-wrap:wrap;justify-content:flex-end;gap:8px;">
          <button type="button" class="btn btn-secondary" data-ssn-entry-close>Cancel</button>
          <button type="button" class="btn btn-primary" id="ssn-entry-save"><span class="material-icons" style="font-size:18px;">save</span> Save SSN</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const close = () => overlay.remove();
  overlay.querySelectorAll('[data-ssn-entry-close]').forEach((b) => b.addEventListener('click', close));
  const showBox = overlay.querySelector('#ssn-entry-show');
  showBox.addEventListener('change', () => {
    for (const id of ['#ssn-entry-value', '#ssn-entry-confirm']) {
      overlay.querySelector(id).style.webkitTextSecurity = showBox.checked ? 'none' : 'disc';
    }
  });
  const errBox = overlay.querySelector('#ssn-entry-error');
  const showErr = (msg) => { errBox.textContent = msg; errBox.style.display = ''; };
  const saveBtn = overlay.querySelector('#ssn-entry-save');
  saveBtn.addEventListener('click', async () => {
    if (_ssnEntryInFlight) return;
    const a = String(overlay.querySelector('#ssn-entry-value').value || '').replace(/\D/g, '');
    const b = String(overlay.querySelector('#ssn-entry-confirm').value || '').replace(/\D/g, '');
    errBox.style.display = 'none';
    if (a.length !== 9) { showErr('Enter all 9 digits.'); return; }
    if (a !== b) { showErr('The two entries do not match.'); return; }
    if (last4 && a.slice(5) !== last4) { showErr(`That SSN does not end in ${last4}, the last 4 on the application. Check it with the driver.`); return; }
    _ssnEntryInFlight = true;
    saveBtn.disabled = true;
    try {
      const fn = httpsCallable(functions, 'enterApplicationSsn');
      const res = await fn({ tenantId: HRState.tenantId, candidateId, ssn: a });
      close();
      const st = res && res.data && res.data.status;
      window.showToast?.(st === 'already_on_file'
        ? 'That SSN was already on file.'
        : (res.data.verified === false
          ? 'SSN saved. Only the last 4 could be checked on this application, so double-check it with the driver. Click the eye to view it.'
          : 'SSN saved. Click the eye to view it.'), 'success');
    } catch (err) {
      console.error('[HR] enterApplicationSsn failed:', err?.code || err);
      showErr(err?.message && !/^internal$/i.test(err.message) ? err.message : 'Could not save the SSN. Try again.');
    } finally {
      _ssnEntryInFlight = false;
      saveBtn.disabled = false;
    }
  });
  setTimeout(() => { try { overlay.querySelector('#ssn-entry-value').focus(); } catch (_e) {} }, 30);
}

// TASK-219 Slice 3 — render carrier-defined custom-question answers
// captured on apply.html. Snapshot model means we render exactly what
// applicant SAW + answered, even if the tenant has since edited or
// archived the question. Both prompt and answer escaped (XSS — Code
// Reviewer 2026-05-06).
function buildCandidateCustomAnswersSection(c) {
  const answers = Array.isArray(c.customAnswers) ? c.customAnswers : [];
  if (answers.length === 0) return '';
  const fmt = a => {
    if (a.type === 'yesno') {
      const v = String(a.answer || '').toLowerCase();
      if (v === 'yes') return '<span style="color:#059669;font-weight:600;">Yes</span>';
      if (v === 'no') return '<span style="color:#DC2626;font-weight:600;">No</span>';
      return _candEmpty('Not answered');
    }
    const s = String(a.answer || '').trim();
    if (!s) return _candEmpty('Not answered');
    return escapeHtml(s).replace(/\n/g, '<br>');
  };
  const rows = answers.map(a => `
    <div class="cand-row">
      <div class="cand-row-label">${escapeHtml(a.prompt || a.qid || 'Question')}</div>
      <div class="cand-row-value">${fmt(a)}</div>
    </div>`).join('');
  return _candCard({
    icon: 'live_help',
    title: 'Application Questions',
    note: `(${answers.length})`,
    flush: true,
    body: rows,
  });
}

// TASK-995 — AI pre-screening interview transcript on the candidate detail panel.
// Info-only: this is the full chat the applicant VOLUNTARILY completed post-submit.
// It made NO hiring decision/score/recommendation; a human reviews. Never used for
// an FCRA adverse action. Derived answers (gap-fill) already appear in the
// Application Questions section above tagged as AI-derived.
// TASK-1341 — how long an 'in_progress' screening may sit with no new activity
// before the panel reads it as unfinished rather than live. The chat is a handful
// of turns and takes minutes, so 24h is deliberately generous.
const SCREENING_STALE_MS = 24 * 60 * 60 * 1000;

// Tolerant timestamp → epoch ms. Screening timestamps are written as ISO strings
// server-side, but never trust a raw Firestore value: accept Timestamp/Date/epoch
// too, and return null (not NaN, not 1970) when nothing is parseable.
function _screeningTimeMs(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'object') {
    if (typeof v.toDate === 'function') {
      try { const t = v.toDate().getTime(); return Number.isFinite(t) ? t : null; } catch (_) { return null; }
    }
    if (v instanceof Date) { const t = v.getTime(); return Number.isFinite(t) ? t : null; }
    // Client SDK Timestamp -> {seconds}; a JSON-serialized Admin SDK Timestamp
    // (what an admin-side export / callable payload yields) -> {_seconds}.
    if (typeof v.seconds === 'number') return v.seconds * 1000;
    if (typeof v._seconds === 'number') return v._seconds * 1000;
    return null;
  }
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? t : null;
}

// Newest usable activity timestamp: the last transcript entry that carries one,
// else the recorded start. null when nothing is parseable — in that case we do
// NOT derive anything and fall back to the stored status verbatim.
function _screeningLastActivityMs(st, transcript) {
  for (let i = transcript.length - 1; i >= 0; i--) {
    const ms = _screeningTimeMs(transcript[i] && transcript[i].at);
    if (ms != null) return ms;
  }
  return _screeningTimeMs(st.startedAt);
}

// Coarse relative age ("5m ago", "2d ago"). Display only.
function _screeningAgo(ms) {
  if (ms == null) return null;
  const diff = Date.now() - ms;
  if (diff < 60000) return 'just now'; // includes clock skew (negative diff)
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const mos = Math.floor(days / 30);
  if (mos < 12) return `${mos}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
}

function buildCandidateScreeningTranscriptSection(c) {
  const st = c.aiScreening || {};
  const transcript = Array.isArray(c.aiScreeningTranscript) ? c.aiScreeningTranscript : [];
  const status = st.status || null;
  // Nothing to show if the applicant never engaged and no status was recorded.
  if (transcript.length === 0 && !status) return '';

  // TASK-1341 — the stored status is only ever as good as the client event that
  // wrote it. The 'abandoned' marker is a callable fired from apply.html's
  // 'pagehide' handler, and browsers routinely cancel in-flight requests during
  // page teardown — so an interview the applicant walked away from sits at
  // 'in_progress' forever and a recruiter reads it as "still going, don't
  // interrupt." Derive the DISPLAY at render time from the last real activity.
  // Deliberately NEVER persist the inference: the copy below calls this a hiring
  // audit record, and writing a guessed 'abandoned' into it would falsify it (and
  // hand a human reviewer a fabricated negative signal). Pure function — this path
  // does not mutate aiScreening.status under any circumstance.
  const lastActivityMs = _screeningLastActivityMs(st, transcript);
  const derivedUnfinished = status === 'in_progress'
    && lastActivityMs != null
    && (Date.now() - lastActivityMs) >= SCREENING_STALE_MS;
  const ago = _screeningAgo(lastActivityMs);
  // Always show relative last-activity on the unfinished states — self-explaining,
  // so a recruiter never has to trust (or second-guess) the threshold.
  const agoSuffix = ago
    ? `<span style="color:var(--text-secondary);font-weight:400;white-space:nowrap;">&middot; ${derivedUnfinished || status === 'abandoned' ? 'last active' : 'active'} ${escapeHtml(ago)}</span>`
    : '';
  const statusLabel = derivedUnfinished
    ? `<span style="color:var(--text-secondary);">Left unfinished</span>${agoSuffix}`
    : ({
        completed: '<span style="color:#059669;font-weight:600;">Completed</span>',
        in_progress: `<span style="color:#D97706;font-weight:600;">In progress</span>${agoSuffix}`,
        declined: '<span style="color:var(--text-secondary);">Declined by applicant</span>',
        abandoned: `<span style="color:var(--text-secondary);">Left unfinished</span>${agoSuffix}`,
      }[status] || '<span style="color:var(--text-secondary);">—</span>');

  const rows = transcript.map(t => {
    const isUser = t.role === 'user';
    return `
      <div class="cand-row">
        <div class="cand-row-label" style="color:${isUser ? 'var(--brand-blue)' : '#7C3AED'};">${isUser ? 'Applicant' : 'Screening assistant (AI)'}</div>
        <div class="cand-row-value">${escapeHtml(String(t.text || '')).replace(/\n/g, '<br>')}</div>
      </div>`;
  }).join('');

  const body = transcript.length > 0
    ? `<div style="max-height:360px;overflow-y:auto;">${rows}</div>`
    : `<div class="cand-row"><div class="cand-row-value">${_candEmpty('No chat transcript recorded.')}</div></div>`;

  return _candCard({
    icon: 'forum',
    title: 'AI Pre-Screening Interview',
    // The screening status is the point of this header — keep it inline with
    // the title rather than as a competing chip elsewhere.
    note: `&middot; ${statusLabel}`,
    sub: 'Voluntary applicant chat, information only. AI made no hiring decision, score, or recommendation &mdash; a person reviews every application. Protected-class content is automatically filtered from this chat transcript and any AI-filled answers (shown as <em>[removed]</em>). Not used for any FCRA adverse action; retained as a hiring audit record.',
    flush: true,
    body,
  });
}

// TASK-235 (Brian Q 2026-05-11): surface the 10-yr employment history
// captured by apply.html (FMCSR 391.21) on the candidate detail modal.
// TASK-477 — stable, Firestore-safe key for a prior-employer entry. MUST match
// _empVerificationKey in functions/src/index.js byte-for-byte so a verification
// recorded server-side re-matches the right employer on render (name+dateFrom,
// not array index — survives an applicant re-submit that shifts indices).
function _empVerificationKey(name, dateFrom) {
  const n = String(name || '').toLowerCase().trim().replace(/\s+/g, ' ').replace(/[^a-z0-9]/g, '_').slice(0, 80);
  const d = String(dateFrom || '').replace(/[^0-9]/g, '').slice(0, 8);
  return `${n}__${d}`;
}
// Resolve the employer display name the same way everywhere (key + render).
function _empName(e) { return (e && (e.name || e.company || e.companyName || e.employerName)) || ''; }
// Days between two YYYY-MM-DD strings, or null if either is missing/unparseable.
function _empDayDiff(a, b) {
  if (!a || !b) return null;
  const da = new Date(String(a) + 'T00:00:00'), dbb = new Date(String(b) + 'T00:00:00');
  if (isNaN(da) || isNaN(dbb)) return null;
  return Math.abs((da - dbb) / 86400000);
}

// TASK-477 — per-employer verification block: the deterministic discrepancy
// badges (advisory only — never blocks Approve-to-Roster) + the carrier capture
// mini-form. `v` is c.employerVerifications[vkey] (or undefined). All carrier-
// controlled strings escaped (stored-XSS — COD Code).
function _buildEmployerVerificationBlock(candidateId, e, v, empIdx, opts = {}) {
  const name = _empName(e);
  const vkey = _empVerificationKey(name, e.dateFrom);
  const fid = `empver_${vkey}`.replace(/[^a-zA-Z0-9_]/g, '_');
  // TASK-1199 — per-employer §391.23 request-form actions on the candidate
  // modal (opts.requestForms is set ONLY by the candidate-modal call site —
  // the DQ-packet call site feeds PERSON docs whose id isn't a candidateId).
  const requestActions = (opts.requestForms && empIdx >= 0) ? `
    <div style="margin-top:6px;display:flex;gap:6px;flex-wrap:wrap;" data-no-print>
      <button class="btn btn-sm" onclick="window.HRModule.printPeiRequestForm('${escJsAttr(candidateId)}',${Number(empIdx)})" style="background:transparent;border:1px solid var(--brand-blue);color:var(--brand-blue);font-size:0.72rem;padding:3px 10px;display:inline-flex;align-items:center;gap:4px;" title="Pre-filled §391.23 previous-employer request form (prints even without an e-signature — collect a wet signature)"><span class="material-icons" style="font-size:13px;">print</span> Print request form</button>
      <button class="btn btn-sm" onclick="window.HRModule.emailPeiRequestForm('${escJsAttr(candidateId)}',${Number(empIdx)})" style="background:transparent;border:1px solid var(--brand-blue);color:var(--brand-blue);font-size:0.72rem;padding:3px 10px;display:inline-flex;align-items:center;gap:4px;" title="Email the signed request form to this previous employer — marks the §391.23 inquiry as contacted"><span class="material-icons" style="font-size:13px;">forward_to_inbox</span> Email to previous employer</button>
    </div>` : '';
  let flags = '';
  if (v) {
    // Date discrepancy: either endpoint off by > 31 days vs the applicant's claim.
    const fromDiff = _empDayDiff(e.dateFrom, v.reportedDateFrom);
    const toDiff = _empDayDiff(e.dateTo, v.reportedDateTo);
    const dateMismatch = (fromDiff !== null && fromDiff > 31) || (toDiff !== null && toDiff > 31);
    const fmt = (s) => { if (!s) return '—'; try { return new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); } catch { return s; } };
    if (dateMismatch) {
      flags += `<div style="margin-top:6px;padding:6px 9px;border-radius:6px;background:#FFFBEB;border:1px solid #FDE68A;color:#92400E;font-size:0.74rem;font-weight:600;display:flex;align-items:flex-start;gap:6px;">
        <span class="material-icons" style="font-size:14px;margin-top:1px;">event_busy</span>
        <span>Date discrepancy — Applicant: ${escapeHtml(fmt(e.dateFrom))}–${escapeHtml(fmt(e.dateTo))} · Employer reported: ${escapeHtml(fmt(v.reportedDateFrom))}–${escapeHtml(fmt(v.reportedDateTo))}</span></div>`;
    }
    if (v.daViolationReported === 'yes') {
      flags += `<div style="margin-top:6px;padding:6px 9px;border-radius:6px;background:#FEF2F2;border:1px solid #FECACA;color:#991B1B;font-size:0.74rem;font-weight:700;display:flex;align-items:flex-start;gap:6px;">
        <span class="material-icons" style="font-size:14px;margin-top:1px;">report</span>
        <span>Previous employer reported a drug/alcohol violation (§391.23)</span></div>`;
    }
    const vBy = v.verifiedBy ? ` by ${escapeHtml(String(v.verifiedBy))}` : '';
    let vWhen = '';
    try { const t = v.verifiedAt?.toDate ? v.verifiedAt.toDate() : (v.verifiedAt?._seconds ? new Date(v.verifiedAt._seconds * 1000) : null); if (t) vWhen = t.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch (_) {}
    flags += `<div style="margin-top:6px;font-size:0.72rem;color:#047857;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:13px;">verified</span>Verified${vWhen ? ' ' + escapeHtml(vWhen) : ''}${vBy} · D&A reported: ${escapeHtml(v.daViolationReported || 'unknown')}</div>`;
  }
  const da = v?.daViolationReported || 'unknown';
  const form = `
    <details style="margin-top:6px;">
      <summary style="font-size:0.74rem;color:var(--brand-blue);cursor:pointer;font-weight:600;list-style:none;">${v ? 'Edit' : 'Record'} employer verification (§391.23)</summary>
      <div style="margin-top:6px;padding:8px 10px;border:1px solid var(--border-light);border-radius:6px;background:var(--bg-page);">
        <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:6px;">
          <label style="font-size:0.72rem;color:var(--text-secondary);">Employer-reported From<br><input type="date" id="${fid}_from" value="${escapeHtml(v?.reportedDateFrom || '')}" style="padding:4px 6px;border:1px solid var(--border-light);border-radius:4px;font-size:0.78rem;"></label>
          <label style="font-size:0.72rem;color:var(--text-secondary);">To<br><input type="date" id="${fid}_to" value="${escapeHtml(v?.reportedDateTo || '')}" style="padding:4px 6px;border:1px solid var(--border-light);border-radius:4px;font-size:0.78rem;"></label>
        </div>
        <div style="font-size:0.72rem;color:var(--text-secondary);margin-bottom:6px;">Reported a drug/alcohol violation?
          <label style="margin-left:6px;"><input type="radio" name="${fid}_da" value="yes" ${da === 'yes' ? 'checked' : ''}> Yes</label>
          <label style="margin-left:6px;"><input type="radio" name="${fid}_da" value="no" ${da === 'no' ? 'checked' : ''}> No</label>
          <label style="margin-left:6px;"><input type="radio" name="${fid}_da" value="unknown" ${da === 'unknown' ? 'checked' : ''}> Unknown</label>
        </div>
        <button class="btn btn-sm btn-primary" style="font-size:0.74rem;padding:4px 10px;" onclick="window.HRModule.recordPriorEmployerVerification('${escJsAttr(candidateId)}',${Number(empIdx)},'${fid}')">Save verification</button>
      </div>
    </details>`;
  return flags + requestActions + form;
}

// ============================================================================
// TASK-1099 — §391.23 Prior Employer Inquiry (PEI) contact-status tracking.
//
// TASK-477 captured WHAT an employer reported; this layer tracks WHERE each
// inquiry stands (not_contacted → contacted → responded / no_response, plus a
// reversible not_applicable with a required reason). A legacy TASK-477 record
// with no explicit contactStatus means the employer already responded.
// Status vocabulary: red = not contacted, amber = contacted/pending or
// documented no-response, green = completed, blue info = N/A (+ Restore).
// ============================================================================
const PEI_STATES = {
  not_contacted: { label: 'Not contacted', short: 'Not contacted', color: '#DC2626', bg: '#FEF2F2', border: '#FECACA', icon: 'error_outline' },
  contacted: { label: 'Contacted — awaiting response', short: 'Contacted', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', icon: 'schedule' },
  responded: { label: 'Investigation complete', short: 'Completed', color: '#047857', bg: '#ECFDF5', border: '#A7F3D0', icon: 'check_circle' },
  no_response: { label: 'No response — good-faith attempt documented', short: 'No response', color: '#92400E', bg: '#FFFBEB', border: '#FDE68A', icon: 'unsubscribe' },
  not_applicable: { label: 'Not applicable', short: 'N/A', color: '#1E40AF', bg: '#EFF6FF', border: '#BFDBFE', icon: 'info' },
};

function _peiStatusKey(v) {
  if (!v) return 'not_contacted';
  if (v.contactStatus && PEI_STATES[v.contactStatus]) return v.contactStatus;
  return 'responded'; // legacy TASK-477 verification with no explicit status
}

// ===========================================================================
// TASK-4751 — §391.21 LIST vs §391.23 INVESTIGATION. Two different lists.
//
// COLLECT (application record, all of it stays on file):
//   §391.21(b)(10)  "A list of the names and addresses of the applicant's
//                    employers during the 3 years preceding the date the
//                    application is submitted" — ALL employers, driver or not.
//   §391.21(b)(11)  "For those drivers applying to operate a commercial motor
//                    vehicle as defined by part 383 ... employers during the
//                    7-year period preceding the 3 years contained in
//                    paragraph (b)(10) ... for which the applicant was an
//                    operator of a commercial motor vehicle" — years 4-10,
//                    CMV employers only, CDL applicants only.
//
// INVESTIGATE (a strictly SMALLER set):
//   §391.23(a)(2)   "safety performance history with Department of
//                    Transportation regulated employers during the preceding
//                    three years."
//   §391.23(d)      "all previous employers ... that employed the driver to
//                    operate a CMV within the previous three years."
//   §391.23(e)      "all previous DOT regulated employers that employed the
//                    driver within the previous three years FROM THE DATE OF
//                    THE EMPLOYMENT APPLICATION."
//   §391.21(d)      names ONLY the (b)(10) 3-year list as the one whose
//                    "previous employers will be contacted" for §391.23(d)/(e).
//
// Conflating them fails an audit in BOTH directions: under-collecting the
// 10-year list, or over-promising that everything listed was investigated.
// This predicate answers ONLY "is this employer inside the §391.23 three-year
// window". It NEVER asserts that an employer was investigated, and it NEVER
// writes a disposition — a `not_applicable` is a compliance status with a human
// author and deriving one silently would forge the record.
// Source read 2026-09-08: govinfo CFR-2024-title49-vol5 sec391-21 / sec391-23.
//
// 🔁 SYNC-GUARD: the applicant-side twin is public/apply.html
// `_peiWindowScope` / `_peiApplicationDateMs` — keep the two IDENTICAL.
// tests/task4751-employment-history-scope.unit.cjs asserts they match.
// ===========================================================================
const PEI_WINDOW_YEARS = 3;

// Firestore Timestamp | Date | ISO string | "YYYY-MM" → epoch ms, else null.
function _peiToMs(v) {
  if (!v) return null;
  try {
    if (typeof v === 'object') {
      if (typeof v.toDate === 'function') { const d = v.toDate(); return isNaN(d) ? null : d.getTime(); }
      if (typeof v._seconds === 'number') return v._seconds * 1000;
      if (typeof v.seconds === 'number') return v.seconds * 1000;
      if (v instanceof Date) return isNaN(v) ? null : v.getTime();
      return null;
    }
    const s = String(v).trim();
    if (!s) return null;
    // Month-precision ("YYYY-MM") and date-only ("YYYY-MM-DD") pin to LOCAL
    // midnight — a bare parse is UTC and slides a day (see _fmtCandDateShort).
    const str = /^\d{4}-\d{2}$/.test(s) ? s + '-01T00:00:00'
      : /^\d{4}-\d{2}-\d{2}$/.test(s) ? s + 'T00:00:00' : s;
    const d = new Date(str);
    return isNaN(d) ? null : d.getTime();
  } catch (_) { return null; }
}

// §391.23(e) anchors the window on "the date of the employment application".
// Live shape (measured 2026-09-08, 89 tenants): on CANDIDATE docs `appliedDate`
// is on 63/63 that carry employers, `submittedAt` on 29/63, `createdAt` on
// 63/63. On PERSON docs (the DQ-packet subject) there is no appliedDate or
// submittedAt at all — only `createdAt` and `hireDate`, both of which fall
// AFTER the application. Take the EARLIEST anchor available: a later anchor
// slides the window forward and would drop an employer that was inside it when
// the applicant signed, which is the under-investigation failure. An anchor a
// few days early costs one extra inquiry and violates nothing.
function _peiApplicationDateMs(c) {
  // Tier 1 — the real submit stamp. This is literally what §391.23(e) names.
  const submitted = [_peiToMs(c && c.appliedDate), _peiToMs(c && c.submittedAt)].filter(v => v !== null);
  if (submitted.length) return Math.min.apply(null, submitted);
  // Tier 2 — person-doc fallback. Both of these post-date the application, so
  // take the EARLIEST: a later anchor slides the window forward and would drop
  // an employer that was inside it when the applicant signed, which is the
  // under-investigation failure.
  const derived = [_peiToMs(c && c.createdAt), _peiToMs(c && c.hireDate)].filter(v => v !== null);
  if (derived.length) return Math.min.apply(null, derived);
  return Date.now();
}

// Start of the §391.23 window: CALENDAR three years back from the application
// date, floored to the start of that day.
// 🔴 Both of those matter. `appMs - 3 * 365.25 days` lands 18 hours PAST the
// calendar anniversary, and the application date carries a time-of-day while an
// employer's `dateTo` is date-only (local midnight) — so an employer who left
// exactly three years before the application read as OUTSIDE the window and
// silently lost its §391.23 inquiry. Under-inclusion is the audit failure this
// task exists to prevent, so the boundary rounds EARLIER, never later.
function _peiWindowStartMs(c) {
  const d = new Date(_peiApplicationDateMs(c));
  const dom = d.getDate();
  d.setFullYear(d.getFullYear() - PEI_WINDOW_YEARS);
  // Feb 29 minus 3 years overflows to Mar 1. Step back to the last day of the
  // previous month so the boundary moves earlier (more inclusive), not later.
  if (d.getDate() !== dom) d.setDate(0);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// → { inWindow, reason }. reason ∈ current | ended_in_window | before_window | undated
//
// 🔴 FAIL-SAFE, deliberately: an employer with no usable end date counts as IN
// scope. Dropping an employer from the §391.23 obligation because a date field
// is blank is the under-investigation audit failure; one extra inquiry is not a
// violation. 51 of 154 live employer rows carry no `currentlyEmployed` flag at
// all and 2 carry an empty `dateTo`, so this branch is real, not theoretical.
// An employment END date that is only month-precise ("YYYY-MM") means "sometime
// that month". _peiToMs resolves it to the 1st, which would push an employment
// that actually ran to the 30th out of the window — under-inclusion again. Round
// a month-only END to the LAST instant of that month. (Live census 2026-09-08:
// every one of the 179 stored `dateTo` values is full YYYY-MM-DD, so this is
// forward cover for a form change, not a live repair — but _peiToMs already
// accepts the shape, so leaving it unhandled is a trap, not a non-case.)
function _peiEmploymentEndMs(v) {
  const ms = _peiToMs(v);
  if (ms === null) return null;
  if (typeof v === 'string' && /^\d{4}-\d{2}$/.test(v.trim())) {
    const d = new Date(ms);
    d.setMonth(d.getMonth() + 1);
    d.setDate(0);
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  }
  return ms;
}

function _peiWindowScope(c, e) {
  if (!e) return { inWindow: false, reason: 'before_window' };
  if (e.currentlyEmployed === true) return { inWindow: true, reason: 'current' };
  const startMs = _peiWindowStartMs(c);
  const toMs = _peiEmploymentEndMs(e.dateTo);
  if (toMs !== null) return toMs >= startMs ? { inWindow: true, reason: 'ended_in_window' } : { inWindow: false, reason: 'before_window' };
  return { inWindow: true, reason: 'undated' };
}

// Split the §391.21 list into the §391.23 obligation and the record-only tail.
// Both halves keep their original array order/reference so index-addressed
// actions (printPeiRequestForm / emailPeiRequestForm) stay correct.
function _peiPartitionEmployers(c) {
  const employers = Array.isArray(c && c.employers) ? c.employers.filter(e => e && _empName(e)) : [];
  const inWindow = [], outOfWindow = [];
  for (const e of employers) (_peiWindowScope(c, e).inWindow ? inWindow : outOfWindow).push(e);
  const byDateDesc = (a, b) => String(b.dateFrom || '').localeCompare(String(a.dateFrom || ''));
  return { all: employers, inWindow: inWindow.sort(byDateDesc), outOfWindow: outOfWindow.sort(byDateDesc) };
}

// "N of M contacted" — TASK-4751: M is now the §391.23 THREE-YEAR set, not the
// whole §391.21 list. `total` still means "employers listed on the application"
// so no caller silently changes meaning; `applicable` (the denominator every
// chip renders) is the obligation. Out-of-window employers a carrier chose to
// contact anyway are reported separately in `outOfWindowRecorded` — never
// hidden, just not counted as outstanding.
function _peiSummary(c) {
  const { all, inWindow, outOfWindow } = _peiPartitionEmployers(c);
  const vmap = (c && c.employerVerifications) || {};
  const keyOf = (e) => _peiStatusKey(vmap[_empVerificationKey(_empName(e), e.dateFrom)]);
  let contacted = 0, done = 0, na = 0;
  for (const e of inWindow) {
    const k = keyOf(e);
    if (k === 'not_applicable') { na++; continue; }
    if (k !== 'not_contacted') contacted++;
    if (k === 'responded') done++;
  }
  // An out-of-window employer a carrier ACTUALLY contacted is reported so the
  // work is never hidden. A `not_applicable` is counted separately: on the one
  // live candidate that carries any §391.23 record at all (measured 2026-09-08),
  // all five records are hand-typed N/As on employers 3-22 years back — the
  // manual escape a carrier was forced into precisely because the panel demanded
  // an inquiry it never owed. Calling those five "inquiries recorded" would
  // restate the defect as an accomplishment.
  let outOfWindowRecorded = 0, outOfWindowNa = 0;
  for (const e of outOfWindow) {
    const k = keyOf(e);
    if (k === 'not_applicable') outOfWindowNa++;
    else if (k !== 'not_contacted') outOfWindowRecorded++;
  }
  return {
    total: all.length,
    listed: all.length,
    inWindow: inWindow.length,
    outOfWindow: outOfWindow.length,
    outOfWindowRecorded,
    outOfWindowNa,
    applicable: inWindow.length - na,
    contacted, done, na,
  };
}

function _peiChipHtml(v, opts) {
  const st = PEI_STATES[_peiStatusKey(v)];
  const small = opts && opts.small;
  return `<span style="display:inline-flex;align-items:center;gap:3px;background:${st.bg};color:${st.color};border:1px solid ${st.border};padding:${small ? '1px 6px' : '2px 8px'};border-radius:999px;font-size:${small ? '0.65rem' : '0.72rem'};font-weight:600;white-space:nowrap;" title="§391.23 investigation status: ${escapeHtml(st.label)}">
    <span class="material-icons" style="font-size:${small ? '12px' : '13px'};">${st.icon}</span>${escapeHtml(st.short)}</span>`;
}

// TASK-4751 — the per-row chip on the Employment History list. An employer
// outside the §391.23 three-year window must NOT wear a red "Not contacted"
// badge: that is the app asserting an unmet federal obligation that does not
// exist, and it is what made a correctly-filed 10-year history read as a pile
// of failures. It gets a neutral §391.21-record marker instead. An employer
// inside the window keeps the real status chip, red included. An out-of-window
// employer the carrier DID contact keeps its real chip too — work already done
// is never hidden.
function _peiRowScopeChipHtml(c, e) {
  const v = (c.employerVerifications || {})[_empVerificationKey(_empName(e), e.dateFrom)];
  if (_peiWindowScope(c, e).inWindow || _peiStatusKey(v) !== 'not_contacted') {
    return _peiChipHtml(v, { small: true });
  }
  return `<span style="display:inline-flex;align-items:center;gap:3px;background:#F1F5F9;color:#475569;border:1px solid #CBD5E1;padding:1px 6px;border-radius:999px;font-size:0.65rem;font-weight:600;white-space:nowrap;" title="Listed under 49 CFR §391.21(b)(11) as part of the 10-year employment history. Outside the §391.23(d)/(e) three-year investigation window — no prior-employer inquiry is required.">
    <span class="material-icons" style="font-size:12px;">history_edu</span>§391.21 record</span>`;
}

// `opts.plain` (TASK-1366) drops the pill chrome and returns just the coloured
// count — used when the summary lives INSIDE a button, where a bordered pill
// nested in a bordered button reads as two competing controls.
function _peiSummaryChipHtml(c, opts) {
  const s = _peiSummary(c);
  if (!s.total) return '';
  // TASK-4751 — every employer on the application sits outside the §391.23
  // three-year window. That is NOT "complete": §391.23(c)(4) requires the
  // carrier to file documentation that no investigation was possible. Amber and
  // said out loud, never a silent green or a "0 of 0".
  if (s.inWindow === 0) {
    const text = `No employer in the §391.23 3-yr window`;
    if (opts && opts.plain) return `<span style="color:#B45309;font-weight:700;">${text}</span>`;
    return `<span style="display:inline-flex;align-items:center;gap:4px;background:#FFFBEB;color:#B45309;border:1px solid #B4530944;padding:2px 9px;border-radius:999px;font-size:0.72rem;font-weight:700;white-space:nowrap;" title="All ${s.listed} listed employer(s) ended more than 3 years before the application date. §391.23(c)(4) requires documentation that no investigation was possible — that record is not created automatically.">
      ${text}</span>`;
  }
  const allDone = s.applicable > 0 && s.done === s.applicable;
  const color = allDone ? '#047857' : (s.contacted === 0 ? '#DC2626' : '#B45309');
  const bg = allDone ? '#ECFDF5' : (s.contacted === 0 ? '#FEF2F2' : '#FFFBEB');
  const text = `${s.contacted} of ${s.applicable} contacted${s.na ? ` <span style="font-weight:500;">&middot; ${s.na} N/A</span>` : ''}`;
  if (opts && opts.plain) return `<span style="color:${color};font-weight:700;">${text}</span>`;
  return `<span style="display:inline-flex;align-items:center;gap:4px;background:${bg};color:${color};border:1px solid ${color}44;padding:2px 9px;border-radius:999px;font-size:0.72rem;font-weight:700;white-space:nowrap;" title="§391.23(d)/(e) prior-employer inquiries made (contacted, responded, or documented no-response) out of the ${s.inWindow} employer(s) inside the 3-year window${s.na ? ` · ${s.na} marked N/A` : ''}${s.outOfWindow ? ` · ${s.outOfWindow} more listed under §391.21 outside the window (no inquiry required)` : ''}">
    ${text}</span>`;
}

// Previously the apply form stored `employers[]` + `employmentGaps[]` on
// the candidate doc but no carrier-side UI rendered it — dispatchers
// reviewing applicants had to take the carrier's word for the work
// history. This section makes it visible. TASK-477 adds per-employer
// verification capture + advisory discrepancy badges.
// TASK-1099 — `opts.pei` (candidate-modal call site ONLY) adds the §391.23
// per-employer status chips + the "N of M contacted" / Investigation header.
// It must stay OFF for the DQ-packet call site, which feeds a PERSON doc into
// this builder: person docs never carry employerVerifications (they live on
// the candidate doc), so the chips would all read "Not contacted" and the
// Investigation button would be keyed by a personId instead of a candidateId.
function buildCandidateEmploymentHistorySection(c, opts = {}) {
  const showPei = opts.pei === true;
  const employers = Array.isArray(c.employers) ? c.employers.filter(e => e && (e.name || e.company || e.companyName || e.employerName)) : [];
  const gaps = Array.isArray(c.employmentGaps) ? c.employmentGaps : [];
  if (employers.length === 0 && gaps.length === 0) return '';

  // Sort employers desc by dateFrom (most recent first).
  const sorted = [...employers].sort((a, b) => String(b.dateFrom || '').localeCompare(String(a.dateFrom || '')));

  const fmtDate = (s) => {
    if (!s) return '—';
    try { return new Date(s + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); }
    catch { return s; }
  };

  const rows = sorted.map(e => {
    // TASK-4550 — the ORIGINAL array index, not the display order. `sorted` is a
    // re-sorted copy, so a correction addressed by the display position would
    // rewrite the wrong employer. This is the same index the §391.23 verification
    // block below already resolves; both must stay on the stored array.
    const _origIdx = Array.isArray(c.employers) ? c.employers.indexOf(e) : -1;
    const company = e.name || e.company || e.companyName || e.employerName || '(no name)';
    const position = e.position || '';
    const cityState = [e.city, e.state].filter(Boolean).join(', ');
    const fromTo = `${fmtDate(e.dateFrom)} → ${e.dateTo ? fmtDate(e.dateTo) : 'present'}`;
    const phone = e.phone ? `<a href="tel:${escapeHtml(e.phone.replace(/[^\d+]/g, ''))}" style="color:var(--brand-blue);">${escapeHtml(e.phone)}</a>` : '';
    // TASK-478 — SAFER reference-verification deep link (public FMCSA data, no
    // FCRA/PII). Catches faked references (a friend's number as "prior employer")
    // by letting the reviewer compare the official SAFER phone to the typed one.
    const _saferUsdot = String(e.usdot || '').replace(/\D/g, '');
    const _safer = _saferUsdot
      ? `<div style="margin-top:4px;"><a href="https://safer.fmcsa.dot.gov/query.asp?query_type=queryCarrierSnapshot&query_param=USDOT&query_string=${encodeURIComponent(_saferUsdot)}" target="_blank" rel="noopener" style="font-size:0.74rem;color:#0E7490;font-weight:600;text-decoration:none;display:inline-flex;align-items:center;gap:3px;"><span class="material-icons" style="font-size:13px;">verified</span> Verify on SAFER (USDOT ${escapeHtml(_saferUsdot)}) — compare the official phone</a></div>`
      : (e.dotRegulated === 'yes' || e.dotRegulated === true)
        ? `<div style="margin-top:4px;font-size:0.72rem;color:var(--text-secondary);">DOT employer, no USDOT given — <a href="https://safer.fmcsa.dot.gov/CompanySnapshot.aspx" target="_blank" rel="noopener" style="color:#0E7490;font-weight:600;text-decoration:none;">search SAFER by name</a> to verify the phone.</div>`
        : '';
    // TASK-1203 Slice A — an explicit "No" must be distinguishable from an unanswered
    // question (§391.21(b)(10)(iv)-(v) audit reads): yes → colored badge (unchanged),
    // no → gray "No" badge, absent → muted "unanswered" hint.
    const dotBadge = (e.dotRegulated === 'yes' || e.dotRegulated === true)
      ? '<span title="DOT-regulated employer per FMCSR 391.21(b)(11)" style="display:inline-flex;align-items:center;background:#DBEAFE;color:#1E40AF;padding:1px 6px;border-radius:6px;font-size:0.65rem;font-weight:600;margin-left:4px;">DOT</span>'
      : (e.dotRegulated === 'no' || e.dotRegulated === false)
        ? '<span title="Applicant answered No — not a DOT-regulated employer (FMCSR 391.21(b)(11))" style="display:inline-flex;align-items:center;background:#F3F4F6;color:#4B5563;padding:1px 6px;border-radius:6px;font-size:0.65rem;font-weight:600;margin-left:4px;">DOT: No</span>'
        : '<span title="Applicant did not answer the DOT-regulated question (FMCSR 391.21(b)(11))" style="display:inline-flex;align-items:center;background:transparent;color:#9CA3AF;border:1px dashed #D1D5DB;padding:0 6px;border-radius:6px;font-size:0.65rem;font-weight:500;font-style:italic;margin-left:4px;">DOT: unanswered</span>';
    const safetyBadge = (e.safetySensitive === 'yes' || e.safetySensitive === true)
      ? '<span title="Safety-sensitive position per 49 CFR 40.305" style="display:inline-flex;align-items:center;background:#FEF3C7;color:#92400E;padding:1px 6px;border-radius:6px;font-size:0.65rem;font-weight:600;margin-left:4px;">Safety-Sensitive</span>'
      : (e.safetySensitive === 'no' || e.safetySensitive === false)
        ? '<span title="Applicant answered No — not a safety-sensitive position (49 CFR 40.305)" style="display:inline-flex;align-items:center;background:#F3F4F6;color:#4B5563;padding:1px 6px;border-radius:6px;font-size:0.65rem;font-weight:600;margin-left:4px;">Safety-Sensitive: No</span>'
        : '<span title="Applicant did not answer the safety-sensitive question (49 CFR 40.305)" style="display:inline-flex;align-items:center;background:transparent;color:#9CA3AF;border:1px dashed #D1D5DB;padding:0 6px;border-radius:6px;font-size:0.65rem;font-weight:500;font-style:italic;margin-left:4px;">Safety-Sensitive: unanswered</span>';
    // TASK-1366 — each employer collapses to a scannable summary row. With 6
    // employers the old flat layout put 18 buttons on screen at once; the three
    // §391.23 actions now live inside the row the reviewer actually opened.
    // <details> is native — no new JS, no new handler, and the print path
    // force-opens every one (printCandidateApplication + the @media print rule).
    const summaryMeta = [position, cityState].filter(Boolean).map(s => escapeHtml(s)).join(' &middot; ');
    return `
      <details class="cand-emp"${showPei ? '' : ' open'} data-employment-entry>
        <summary>
          <span class="material-icons cand-emp-chev" aria-hidden="true">expand_more</span>
          <span class="cand-emp-main">
            <span class="cand-emp-name">${escapeHtml(company)}${dotBadge}${safetyBadge}${showPei ? _peiRowScopeChipHtml(c, e) : ''}</span>
            <span class="cand-emp-meta">${summaryMeta || _candEmpty('No position or location given')}</span>
          </span>
          <span class="cand-emp-dates">${escapeHtml(fromTo)}</span>
        </summary>
        <div class="cand-emp-detail">
          ${(showPei && _origIdx >= 0) ? `<div style="margin-bottom:8px;" data-no-print>
            <button type="button" class="btn btn-primary" onclick="window.HRModule.openPeiInvestigation('${escJsAttr(c.id)}', ${_origIdx})" style="min-height:44px;font-size:15px;font-weight:600;padding:0 16px;display:inline-flex;align-items:center;gap:6px;border-radius:8px;" title="Open the §391.23 investigation at this employer — email, print, or log a call">
              <span class="material-icons" style="font-size:18px;">travel_explore</span> Open this employer's investigation
            </button>
          </div>` : ''}
          <div class="cand-grid" style="margin-bottom:8px;">
            ${_candField('Position', position ? escapeHtml(position) : '', { empty: 'Not given' })}
            ${/* TASK-9426 — the printed application shows the whole §391.21(b)(10) address
                 (street · city, state ZIP · non-US country), not just city/state. */''}
            ${_candField('Address', (() => { const _c = (e.country && !/^(US|USA)$/i.test(String(e.country))) ? String(e.country) : ''; const _a = [e.address, [e.city, e.state, e.zip].filter(Boolean).join(', '), _c].filter(Boolean).join(' · '); return _a ? escapeHtml(_a) : ''; })(), { empty: 'Not given' })}
            ${_candField('Phone', phone, { empty: 'No phone on file' })}
            ${_candField('Reason for leaving', e.reasonForLeaving ? escapeHtml(e.reasonForLeaving) : '', { empty: 'Not given' })}
          </div>
          ${_safer}
          ${(showPei && _origIdx >= 0 && c.applicationComplete === true) ? `
            <!-- TASK-4550 — the "misspelled street" half. Scoped to the candidate
                 surface (showPei) — the post-hire person view renders the same
                 section from a copy on the person doc, which this callable does
                 not address. -->
            <div style="margin-top:8px;" data-no-print>
              <button type="button" class="cand-btn cand-btn-secondary" onclick="window.HRModule.openCorrectEmployerModal('${escJsAttr(c.id)}', ${_origIdx})" title="Fix an obvious transcription error here (recorded with your name and a reason). If the substance is wrong, use Send Back instead.">
                <span class="material-icons">edit_note</span> Correct a typo…
              </button>
            </div>` : ''}
          ${(_origIdx >= 0) ? _CORRECTABLE_EMPLOYER_FIELDS.map(f => _hrCorrectionFootnote(c, `employers[${_origIdx}].${f.key}`)).join('') : ''}
          ${_buildEmployerVerificationBlock(c.id, e, (c.employerVerifications || {})[_empVerificationKey(_empName(e), e.dateFrom)], (Array.isArray(c.employers) ? c.employers.indexOf(e) : -1), { requestForms: showPei })}
        </div>
      </details>`;
  }).join('');

  const gapRows = gaps.length === 0 ? '' : `
    ${_candSubhead('warning', `Employment Gaps (${gaps.length})`)}
    <div style="background:#FFFBEB;">
      ${gaps.map(g => `
        <div class="cand-row" style="border-bottom-color:#FDE68A;">
          <div style="font-size:0.8rem;color:#7C2D12;">${escapeHtml(fmtDate(g.from))} → ${escapeHtml(fmtDate(g.to))} <span style="color:#92400E;font-variant-numeric:tabular-nums;">(${g.days || '?'} days)</span></div>
          ${g.explanation ? `<div style="font-size:0.78rem;color:#78350F;margin-top:2px;"><em>${escapeHtml(g.explanation)}</em></div>` : '<div style="font-size:0.78rem;color:#92400E;font-style:italic;margin-top:2px;">No explanation provided</div>'}
        </div>`).join('')}
    </div>`;

  // TASK-1099 — header carries the "N of M contacted" §391.23 rollup + the
  // entry point to the full Safety Performance History Investigation pop-out.
  // TASK-1366 — the counter used to be a dead label sitting above 18 buttons.
  // It's now the section's ONE bulk affordance: clicking it opens the same
  // §391.23 Investigation pop-out the button does (identical handler, identical
  // argument — this is a relabel, not a new action), where contact status is
  // driven for every employer at once.
  // TASK-10270 — the header counter stays (status at a glance) and a big
  // full-width primary "Open investigation" leads the card body.
  const peiHeaderBits = (showPei && sorted.length > 0) ? `
    <button type="button" class="cand-btn cand-btn-secondary" onclick="window.HRModule.openPeiInvestigation('${escJsAttr(c.id)}')" title="Open the §391.23 Safety Performance History Investigation — contact, record and print every prior-employer inquiry in one place">
      <span class="material-icons">travel_explore</span> ${_peiSummaryChipHtml(c, { plain: true })} &middot; Investigation
    </button>` : '';

  // TASK-4751 — the note used to read "10 yr CDL / 3 yr non-CDL", which is the
  // COLLECTION rule (§391.21(b)(10)+(b)(11)) and says nothing about how many of
  // those employers carry a §391.23 investigation duty. Name both counts.
  const _scope = showPei ? _peiSummary(c) : null;
  const peiOpenBtn = (showPei && sorted.length > 0) ? `
    <div style="padding:10px 12px;" data-no-print>
      <button type="button" class="btn btn-primary" onclick="window.HRModule.openPeiInvestigation('${escJsAttr(c.id)}')" style="width:100%;min-height:48px;font-size:16px;font-weight:700;display:flex;align-items:center;justify-content:center;gap:8px;border-radius:8px;" title="Email, print or log a call to each previous employer">
        <span class="material-icons" style="font-size:20px;">travel_explore</span> Open investigation
      </button>
    </div>` : '';
  const note = _scope
    ? `(${sorted.length}) listed &mdash; §391.21 &middot; ${_scope.inWindow} require a §391.23 inquiry (last 3 yr)`
    : `(${sorted.length}) &mdash; per FMCSR 391.21, 10 yr CDL / 3 yr non-CDL`;

  return _candCard({
    icon: 'work_history',
    title: 'Employment History',
    note,
    actions: peiHeaderBits,
    sub: showPei && sorted.length > 0
      ? (_scope.inWindow === 0
        ? 'No listed employer falls inside the §391.23 three-year window — open the Investigation for what §391.23(c)(4) requires you to document.'
        : 'Tap an employer to see the full entry and record the §391.23 previous-employer inquiry. Employers marked §391.21 record sit outside the 3-year window and need no inquiry.')
      : '',
    flush: true,
    body: sorted.length > 0 ? peiOpenBtn + rows : '',
    raw: gapRows,
  });
}

// TASK-235 v2 (Brian Q 2026-05-11 cont'd): surface the rest of apply.html's
// captured data on the carrier-side candidate detail modal. Five additive
// data-gated sections rendering ONLY when the candidate has that data:
//   • Driver License (full DL — state, class, number, endorsements, restrictions)
//   • Driving Experience (per-equipment-type from/to/miles)
//   • Driving Record (accidents + violations + forfeitures, last 3 yr)
//   • Credentials & Authority (med card, TWIC, passport, DOT modes drug-tested)
//   • Residence History (current + previous addresses, FCRA-required)
// All read-only. No edits, no schema changes.

const APPLY_EXP_TYPES = [
  { key: 'straightTruck', label: 'Straight Truck' },
  { key: 'truckTractor', label: 'Truck-Tractor' },
  { key: 'semiTrailers', label: 'Semi-Trailers' },
  { key: 'doublesTriples', label: 'Doubles/Triples' },
  { key: 'flatbed', label: 'Flatbed' },
  { key: 'bus', label: 'Bus' },
  { key: 'other', label: 'Other' },
];

function _fmtCandDateShort(s) {
  if (!s) return '—';
  // TASK-1502 — experience dates are now month-precision ("YYYY-MM"). A bare
  // "YYYY-MM" parses as UTC midnight, so toLocaleDateString in any US timezone
  // showed the PREVIOUS month. Pin both shapes to local midnight.
  try {
    const str = typeof s === 'string' && s.length === 7 ? s + '-01T00:00:00'
      : s + (typeof s === 'string' && s.length === 10 ? 'T00:00:00' : '');
    return new Date(str).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  catch { return String(s); }
}

// TASK-431 — Unified candidate license/CDL resolver. Candidates added MANUALLY
// (getCandidateModalHTML form) store FLAT fields: `cdlClass` ('A'/'B'/'C'/'none')
// + `cdlEndorsements` (string[] of codes). Candidates from apply.html (online
// application + Checkr embed) store a NESTED object instead:
//   license = { class, number, state, endorsements[], expires, issued, restrictions[] }
// Every CDL/DL display site + the candidate→employee conversion read ONLY the
// flat fields, so apply.html candidates showed CDL "None" AND — critically —
// were hired with NO CDL/endorsements and NO cdlExpiryDate mirror (the empty
// isCdl gate skipped it), silently dropping their CDL + killing CDL-expiry
// alerts. This resolver prefers flat fields, falls back to nested `license.*`,
// and coerces endorsements into a clean string[] of codes regardless of source
// shape (array of strings, array of {code}/{type}, a single 'N' string, etc.).
// CDL convention matches _dqBuildEmploymentHistoryGapWarning:
// class A/B/C = CDL; D/O/none/empty = not.
function _coerceEndorsementCodes(raw) {
  if (raw == null) return [];
  const arr = Array.isArray(raw) ? raw : (typeof raw === 'string' ? raw.split(/[,\s]+/) : [raw]);
  const out = [];
  for (const item of arr) {
    let code = '';
    if (item == null) continue;
    if (typeof item === 'string') code = item;
    else if (typeof item === 'object') code = item.code || item.type || item.endorsement || item.dmv_description || item.description || '';
    else code = String(item);
    code = String(code).trim().toUpperCase();
    if (code && code !== 'NONE' && !out.includes(code)) out.push(code);
  }
  return out;
}

// TASK-1218 — true iff the license class value is an actual CDL class (A/B/C).
// Anything else (D, E, "Regular", …) is a normal operator license — the apply
// form legitimately offers "Class D / Regular" for non-CDL positions, and
// printing it under a "CDL" label muddied screening.
function _isCdlClassValue(cls) {
  return /^[ABC]$/.test(String(cls || '').trim().toUpperCase());
}

// TASK-1218 bonus — small warning chip when the applied-for position is a CDL
// role but the license class on file is non-A/B/C (real screening signal).
// positionType 'cdl' is the canonical intake classification; fall back to the
// free-text position for manually-added candidates.
function _noCdlOnFileChip(c) {
  const lic = resolveCandidateLicense(c);
  if (_isCdlClassValue(lic.cdlClass)) return '';
  const isCdlRole = (c.positionType === 'cdl') || (!c.positionType && /\bcdl\b/i.test(c.position || ''));
  if (!isCdlRole) return '';
  return ` <span style="display:inline-flex;align-items:center;gap:3px;padding:1px 6px;font-size:0.68rem;background:#FEF3C7;color:#92400E;border-radius:4px;font-weight:600;vertical-align:middle;" title="This candidate applied for a CDL position, but the license class on file is not a CDL class (A/B/C).">
    <span class="material-icons" style="font-size:12px;">warning</span>No CDL on file
  </span>`;
}

function resolveCandidateLicense(c) {
  c = c || {};
  const lic = c.license || {};
  // Prefer the manually-entered flat cdlClass; fall back to nested license.class.
  let cdlClass = c.cdlClass != null && c.cdlClass !== '' ? c.cdlClass : (lic.class || '');
  cdlClass = String(cdlClass || '').trim();
  const cls = cdlClass.toUpperCase();
  const isCdl = /^[ABC]$/.test(cls) || c.cdlLicensed === true;
  // Endorsements: flat cdlEndorsements wins; else nested license.endorsements.
  const endorsements = _coerceEndorsementCodes(
    (c.cdlEndorsements != null && !(Array.isArray(c.cdlEndorsements) && c.cdlEndorsements.length === 0))
      ? c.cdlEndorsements
      : lic.endorsements
  );
  // DL identity: flat cdlNumber/licenseNumber, else nested license.number.
  // TASK-9359 — a PERSON record no longer carries license.number / state (moved
  // to the peopleIdentity vault, overlaid as dlNumber / dlState), so those are
  // the last fallback. Appended LAST so a candidate's resolution is unchanged.
  const dlNumber = c.cdlNumber || c.licenseNumber || lic.number || c.dlNumber || '';
  const dlState = String(c.cdlState || c.licenseState || lic.state || c.dlState || '').toUpperCase();
  const expires = c.cdlExpiryDate || c.dlExpiryDate || lic.expires || lic.expiry || lic.expiryDate || '';
  return {
    cdlClass: cdlClass && cdlClass.toLowerCase() !== 'none' ? cdlClass : '',
    endorsements,
    isCdl,
    dlNumber,
    dlState,
    expires,
  };
}

// TASK-4625 — `cdlLicensed` for a NEW person created by approve-to-roster.
// Pure (vm-tested in tests/task4625-hire-cdl-licensed.unit.cjs).
//
// THE BUG: this used to derive ONLY from the role NAME (/cdl|driver/i). A
// Class A applicant hired under "operator" or "laborer" — Michael Goodman and
// Ivan Vega Lerma, both 2026 pipeline hires — got cdlClass:'A' copied onto the
// person AND cdlLicensed:false in the same write. And because roster savePerson
// is explicit-value-wins on cdlLicensed (TASK-670), that false then survived
// every later crew-type edit, so the record never self-healed.
//
// Precedence (each rung only applies when the one above is silent):
//   1. An explicit candidate.cdlLicensed boolean wins (TASK-670).
//   2. An EXPLICIT non-CDL classification on the approve modal → false
//      (TASK-1312: the same gate that already suppresses the cdlClass copy).
//   3. A Class A or B license on file → true. A/B are commercial in every
//      state. Class C is deliberately NOT here: in many states (CA, ...) a
//      Class C is the ordinary passenger license, so it stays a role-name call.
//   4. Fallback: the old role-name heuristic.
function _deriveHireCdlLicensed(candidate, role, explicitNonCdlHire) {
  const c = candidate || {};
  if (c.cdlLicensed !== undefined) return c.cdlLicensed === true;
  if (explicitNonCdlHire) return false;
  const cls = String(resolveCandidateLicense(c).cdlClass || '').trim().toUpperCase();
  if (cls === 'A' || cls === 'B') return true;
  return /cdl|driver/i.test(String(role || ''));
}

function buildCandidateLicenseSection(c) {
  const l = c.license || {};
  if (!l.number && !l.state && !l.class) return '';
  const endorsements = Array.isArray(l.endorsements) && l.endorsements.length ? l.endorsements.join(', ') : 'None';
  const restrictions = Array.isArray(l.restrictions) && l.restrictions.length ? l.restrictions.join(', ') : 'None';
  // TASK-9250 — the §391.23(a)(1) answer, as signed. Nothing on an application
  // from before the question existed (no answer is not the same as "none").
  const _ps = Array.isArray(l.priorStates) ? l.priorStates.filter((s) => typeof s === 'string') : [];
  const priorStatesField = (l.otherStatesHeld === true || l.otherStatesHeld === false)
    ? _candField('Other states, last 3 years', escapeHtml(l.otherStatesHeld === false ? 'None' : (_ps.length ? _ps.join(', ') : 'Yes, none listed')), { wide: true })
    : '';
  const stateClass = (l.state || l.class)
    ? `${escapeHtml((l.state || '') + (l.state ? _candCountrySuffix(l.country) : ''))}${l.state && l.class ? ' &middot; ' : ''}${l.class ? 'Class ' + escapeHtml(l.class) : ''}`
    : '';
  return _candCard({
    icon: 'badge',
    title: 'Driver License',
    body: `<div class="cand-grid">
        ${_candField('State / Class', stateClass, { empty: 'Not on file' })}
        ${_candField('Number', l.number ? `<span style="font-variant-numeric:tabular-nums;">${escapeHtml(l.number)}</span>` : '', { empty: 'Not on file' })}
        ${_candField('Issued', escapeHtml(formatDateShort(l.issued) || ''), { empty: 'Not on file' })}
        ${_candField('Expires', escapeHtml(formatDateShort(l.expires) || ''), { empty: 'Not on file' })}
        ${_candField('Endorsements', escapeHtml(endorsements))}
        ${_candField('Restrictions', escapeHtml(restrictions))}
        ${priorStatesField}
      </div>`,
  });
}

// TASK-9699 (Crown 10/7) — LICENSE PHOTO CHECK: the Smart Tools read of the
// applicant's licence photo (class / endorsements / restrictions), REVIEWER-ONLY
// and ADVISORY. `licenseRead` is written by verifyApplicantCredentialDoc (Admin
// SDK) onto the HR-gated candidate doc; firestore.rules makes it server-only
// and the applicant resume projection allowlists it out, so the applicant never
// sees a mismatch (drop-off). It changes nothing: not the stage, not
// license.class, not the hire gate — the existing hard rule
// (licenseClassProblem) is still the only thing that blocks a submit. The
// applicant's class is pinned at read time (typedClass); if license.class has
// changed since, the card says "re-check" instead of asserting a stale verdict.
// Every string rendered here passed the server-side sanitiser, and is escaped
// again here — the photo is untrusted input.
function buildCandidateLicenseReadSection(c) {
  const r = c && c.licenseRead;
  if (!r || typeof r !== 'object') return '';
  const typedNow = String((c.license || {}).class || '').trim().toUpperCase();
  const classRead = r.classRead ? String(r.classRead).toUpperCase() : '';
  const stale = !!(r.typedClass && typedNow && String(r.typedClass).toUpperCase() !== typedNow);
  let tone = 'muted';
  let headline;
  if (r.status === 'wrong_document') {
    headline = "The uploaded file did not read as a driver's license.";
  } else if (r.status !== 'read' || !classRead) {
    headline = 'The license class could not be read off the photo.';
  } else if (stale) {
    headline = `Photo reads Class ${classRead}. The application's class was changed after this read — re-check it against the photo.`;
  } else if (r.match === 'mismatch') {
    tone = 'warn';
    headline = `Photo reads Class ${classRead} — the application says Class ${typedNow}.`;
  } else if (r.match === 'match') {
    tone = 'ok';
    headline = `Photo reads Class ${classRead}, same as the application.`;
  } else {
    headline = `Photo reads Class ${classRead} (low confidence — confirm by eye).`;
  }
  const endorsements = Array.isArray(r.endorsements) && r.endorsements.length ? r.endorsements.join(', ') : 'None read';
  const restrictions = Array.isArray(r.restrictions) && r.restrictions.length ? r.restrictions.join(', ') : 'None read';
  const colors = tone === 'warn'
    ? 'background:#FFFBEB;border-color:#F59E0B;color:#92400E;'
    : tone === 'ok'
      ? 'background:#ECFDF5;border-color:#10B981;color:#065F46;'
      : 'background:#F8FAFC;border-color:#CBD5E1;color:#334155;';
  return _candCard({
    icon: 'auto_awesome',
    title: 'License Photo Check',
    note: 'Smart Tools · advisory',
    body: `<div style="border:1px solid;border-radius:8px;padding:10px 12px;font-size:0.88rem;font-weight:600;${colors}">${escapeHtml(headline)}</div>
      ${r.positionProblem ? `<div style="margin-top:8px;font-size:0.85rem;color:#92400E;"><span class="material-icons" style="font-size:15px;vertical-align:-2px;">warning</span> ${escapeHtml(String(r.positionProblem))}</div>` : ''}
      <div class="cand-grid" style="margin-top:10px;">
        ${_candField('Endorsements read', escapeHtml(endorsements))}
        ${_candField('Restrictions read', escapeHtml(restrictions))}
        ${_candField('Read on', escapeHtml(formatDateShort(r.readAt) || ''), { empty: '—' })}
      </div>`,
    foot: 'Read off the uploaded license photo by Smart Tools, for your review only. It does not change the application, the applicant never sees it, and it can misread — the card in your hand wins.',
  });
}

// TASK-9699 — list-row chip, only for a live class mismatch (the detail card
// carries everything else). Never shown once the applicant changes their class.
function _hrLicenseReadChip(c) {
  const r = c && c.licenseRead;
  if (!r || r.status !== 'read' || r.match !== 'mismatch') return '';
  const typedNow = String((c.license || {}).class || '').trim().toUpperCase();
  if (!r.typedClass || !typedNow || String(r.typedClass).toUpperCase() !== typedNow) return '';
  const read = escapeHtml(String(r.classRead || '').toUpperCase());
  // COD UX — say the next step, not a question mark (tooltips do not exist on a yard tablet).
  return `<span class="candidate-status-badge" style="margin-left:6px;background:#FEF3C7;color:#92400E;font-weight:700;" title="License photo reads Class ${read}; the application says Class ${escapeHtml(typedNow)}. Advisory — open the application to review.">Check DL (Class ${read})</span>`;
}

function buildCandidateDrivingExperienceSection(c) {
  const exp = c.experience || {};
  const entries = APPLY_EXP_TYPES.filter(t => exp[t.key]?.has === true);
  if (entries.length === 0) return '';
  const rows = entries.map(t => {
    const e = exp[t.key] || {};
    const fromTo = `${_fmtCandDateShort(e.from)} → ${e.to ? _fmtCandDateShort(e.to) : 'present'}`;
    const miles = e.miles != null && e.miles !== '' ? `${Number(e.miles).toLocaleString()} mi` : '—';
    return `
      <div class="cand-row" style="display:flex;justify-content:space-between;align-items:baseline;gap:10px;flex-wrap:wrap;">
        <div style="font-weight:600;font-size:0.86rem;color:var(--text-primary);flex:1 1 130px;min-width:0;">${escapeHtml(t.label)}</div>
        <div style="font-size:0.8rem;color:var(--text-secondary);font-variant-numeric:tabular-nums;flex:1 1 160px;">${escapeHtml(fromTo)}</div>
        <div style="font-size:0.8rem;color:var(--text-secondary);text-align:right;font-variant-numeric:tabular-nums;flex:0 0 auto;">${miles === '—' ? _candEmpty('No mileage') : escapeHtml(miles)}</div>
      </div>`;
  }).join('');
  return _candCard({
    icon: 'local_shipping',
    title: 'Driving Experience',
    note: `(${entries.length} equipment ${entries.length === 1 ? 'type' : 'types'})`,
    flush: true,
    body: _candSubhead('', 'Equipment &middot; Period &middot; Miles') + rows,
  });
}

// TASK-1366 — this is no longer a top-level card. It renders as the
// "Self-reported by the applicant" SUB-BLOCK inside the single Background &
// Driving Record card (buildCandidateBackgroundSection), because a reviewer
// could not tell which of the three separate background-ish cards was
// authoritative. Returns '' when the applicant captured nothing.
function buildCandidateDrivingRecordSection(c) {
  const accidents = Array.isArray(c.accidents) ? c.accidents : [];
  const violations = Array.isArray(c.violations) ? c.violations : [];
  const forfeitures = c.forfeitures || {};
  const hasForfeit = forfeitures.deniedLicense || forfeitures.suspendedRevoked;
  const isClean = !c.hasAccidents && !c.hasViolations && !hasForfeit && accidents.length === 0 && violations.length === 0;
  // Skip the section entirely if no application data captured (i.e. legacy/manual candidate).
  if (c.hasAccidents == null && c.hasViolations == null && !c.forfeitures) return '';

  const accidentRows = accidents.map(a => `
    <div style="padding:8px 12px;border-bottom:1px solid #FDE68A;font-size:0.82rem;">
      <div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;">
        <strong style="color:#92400E;">${escapeHtml(formatDateShort(a.date) || '—')}</strong>
        <span style="color:#78350F;">${escapeHtml(a.location || '')}</span>
      </div>
      <div style="color:#78350F;font-size:0.78rem;margin-top:2px;">
        ${a.injuries ? `${a.injuries} injuries · ` : ''}${a.fatalities ? `${a.fatalities} fatalities · ` : ''}${a.hazmatSpill === 'yes' ? '⚠ Hazmat spill' : 'No hazmat spill'}
      </div>
    </div>`).join('');

  const violationRows = violations.map(v => `
    <div style="padding:8px 12px;border-bottom:1px solid #FECACA;font-size:0.82rem;">
      <div style="display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;">
        <strong style="color:#991B1B;">${escapeHtml(formatDateShort(v.date) || '—')}</strong>
        <span style="color:#7F1D1D;">${escapeHtml(v.location || '')}</span>
      </div>
      <div style="color:#7F1D1D;font-size:0.78rem;margin-top:2px;">
        ${escapeHtml(v.offense || 'Offense not specified')}${v.vehicleType ? ` · ${escapeHtml(v.vehicleType)}` : ''}
      </div>
    </div>`).join('');

  const forfeitureRow = hasForfeit ? `
    <div style="padding:10px 12px;background:#FEF2F2;border:1px solid #FECACA;border-radius:var(--radius-md);font-size:0.82rem;color:#7F1D1D;margin-top:8px;">
      <strong>License history flag:</strong>
      ${forfeitures.deniedLicense ? 'Has been denied a license/permit/privilege. ' : ''}
      ${forfeitures.suspendedRevoked ? 'Has had a license suspended or revoked. ' : ''}
      ${forfeitures.circumstances ? `<div style="margin-top:4px;font-style:italic;">${escapeHtml(forfeitures.circumstances)}</div>` : ''}
    </div>` : '';

  // TASK-581 — this whole section is the applicant's OWN answers from apply.html,
  // NOT an MVR result. It renders directly below the Screenings sub-block (where
  // the real Checkr MVR row lives), so without explicit labeling it reads
  // like an MVR came back with hits — caught live on a real candidate while the
  // MVR row said NOT STARTED. Title + caption make the source unmistakable; the
  // "Self-reported ≠ verified" nudge only shows while the MVR genuinely hasn't
  // been started (and only for driver position types, where the MVR card exists).
  const _pt = c.positionType || 'cdl';
  const _isDriverPos = _pt === 'cdl' || _pt === 'non-cdl-driver';
  const mvrNotStarted = _isDriverPos && isReportNotYetRun(getReportStatus(c, 'mvr')); // TASK-9300 (3): pending OR not_started

  // Sub-block header + the "self-reported ≠ verified" caption. The copy is the
  // TASK-581 wording verbatim — the only change is that it now sits INSIDE the
  // Background & Driving Record card instead of competing with it.
  const head = _candSubhead(
    isClean ? 'edit_note' : 'report_problem',
    'Self-Reported by the Applicant',
    mvrNotStarted
      ? 'Not an MVR &mdash; run one to verify. <strong style="color:#92400E;">Self-reported &ne; verified.</strong>'
      : 'From their application &mdash; not an MVR.'
  );

  if (isClean) {
    return head + `
      <div class="cand-row" style="font-size:0.84rem;color:var(--text-secondary);">
        Applicant reported no accidents, violations, or forfeitures.
      </div>`;
  }

  return head + `
    <div style="padding:10px 12px;">
      ${accidents.length > 0 ? `
        <div style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-md);overflow:hidden;margin-bottom:8px;">
          <div style="padding:6px 12px;background:#FEF3C7;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.05em;color:#92400E;font-weight:700;">Accidents (${accidents.length})</div>
          ${accidentRows}
        </div>` : ''}
      ${violations.length > 0 ? `
        <div style="background:#FEF2F2;border:1px solid #FECACA;border-radius:var(--radius-md);overflow:hidden;margin-bottom:8px;">
          <div style="padding:6px 12px;background:#FEE2E2;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.05em;color:#991B1B;font-weight:700;">Traffic Violations (${violations.length})</div>
          ${violationRows}
        </div>` : ''}
      ${forfeitureRow}
    </div>`;
}

function buildCandidateCredentialsSection(c) {
  const dotModes = Array.isArray(c.dotModes) ? c.dotModes : [];
  // TASK-334 Slice 1 follow-up — Code Reviewer flag (2026-05-26): the canonical
  // field on the people (roster) doc is `medCardExpiryDate` (see promote logic
  // at hr.js:12883-12888 + saveMedExam at hr.js:14420); apply.html writes the
  // legacy `medCardExpiry`. When the DQ packet feeds a person from the people
  // collection into this builder, the med card row used to silently drop. Read
  // BOTH so the builder works in both contexts (candidate doc + roster doc).
  //
  // 🔴 TASK-4802 — the ORDER is load-bearing, do not flip it back. This builder
  // is reached with a PERSON doc via getDqPacketHTML(emp), and a person can
  // carry BOTH names with DIFFERENT dates (measured 2026-09-08: 18 of 379
  // drivers carry both, 14 disagree, and on 9 the two disagree about whether
  // the card is expired TODAY). Reading the legacy name first made the printed
  // §391.51 Driver Qualification File the ONE surface that contradicted the DQ
  // classifier, the compliance view, the partner portal, the dispatch board and
  // the daily expiry cron — all of which read `medCardExpiryDate`. In 3 of
  // those 9 the packet printed a VALID card for a driver every other screen had
  // flagged as lapsed. Canonical first, legacy as the fallback:
  //   • candidate doc — 0 of 101 carry `medCardExpiryDate`, so it falls through
  //     to the bare field and the applicant view is unchanged;
  //   • person doc  — 0 of 379 carry the bare field ALONE, so the fallback
  //     never has to answer for a driver and the canonical value always wins.
  // Nothing that rendered before renders blank now. Pinned by
  // tests/medCardExpiryFieldParity.test.cjs.
  const medCardExpiryValue = c.medCardExpiryDate || c.medCardExpiry || '';
  const hasMed = !!medCardExpiryValue;
  // TASK-9448 — the server stamps `medCardRequired` at submit from the job opening
  // (or the carrier's default for the application type). false = this job never
  // asked for a DOT medical card, so say that instead of leaving a blank.
  const medNotRequired = !hasMed && !!c && c.medCardRequired === false; // inline: harnesses extract this builder alone
  const hasTwic = c.twicCard != null;
  const hasPassport = c.passport != null;
  if (!hasMed && !medNotRequired && !hasTwic && !hasPassport && dotModes.length === 0) return '';
  const yn = (v) => v === true || v === 'yes' ? '<span style="color:#059669;font-weight:600;">Yes</span>' : '<span style="color:var(--text-secondary);">No</span>';
  return _candCard({
    icon: 'verified_user',
    title: 'Credentials &amp; Authority',
    body: `<div class="cand-grid">
        ${hasMed ? _candField('Medical Card Expires', escapeHtml(formatDateShort(medCardExpiryValue) || ''), { empty: 'No expiry on file' }) : ''}
        ${medNotRequired ? _candField('Med card', 'Not required for this job') : ''}
        ${hasTwic ? _candField('TWIC Card', yn(c.twicCard)) : ''}
        ${hasPassport ? _candField('Passport', yn(c.passport)) : ''}
        ${dotModes.length > 0 ? _candField('Drug-tested under (DOT modes)', escapeHtml(dotModes.join(', ')), { wide: true }) : ''}
      </div>`,
  });
}

// TASK-9577 — " (Canada)" / " (Mexico)" for a non-US address or licence; '' for
// US / blank. Plain text — callers escape it with the rest of the line.
function _candCountrySuffix(country) {
  const code = String(country || '').trim().toUpperCase();
  if (!code || code === 'US' || code === 'USA') return '';
  return ` (${({ CA: 'Canada', MX: 'Mexico' })[code] || code})`;
}

function buildCandidateResidenceSection(c) {
  const current = c.address || null;
  const prior = Array.isArray(c.previousResidences) ? c.previousResidences : [];
  const hasCurrent = current && (current.street || current.city);
  if (!hasCurrent && prior.length === 0) return '';
  const fmtAddr = (a) => {
    const line1 = a.street || '';
    // TASK-9577 — name a non-US country so "Calgary, AB" does not read as a US address.
    const line2 = [a.city, a.state, a.zip].filter(Boolean).join(', ') + _candCountrySuffix(a.country);
    return line2 ? `${line1}${line1 ? ' · ' : ''}${line2}` : line1;
  };
  const addrRow = (label, a) => `
    <div class="cand-row" data-residence-entry>
      <div class="cand-row-label">${label}</div>
      <div class="cand-row-value">${fmtAddr(a) ? escapeHtml(fmtAddr(a)) : _candEmpty('Address incomplete')}</div>
    </div>`;
  return _candCard({
    icon: 'home',
    title: 'Residence History',
    note: `(${(hasCurrent ? 1 : 0) + prior.length})`,
    flush: true,
    body: (hasCurrent ? addrRow('Current', current) : '') + prior.map((r, i) => addrRow(`Prior #${i + 1}`, r)).join(''),
  });
}

function buildCandidateCheckrCard(c) {
  const bg = c._bgCheck || {};
  // FALLBACK to candidate-doc fields written by apply.html Embed flow when
  // peopleBackgroundChecks doesn't have an entry yet (one_stage submissions
  // write to candidate doc, not peopleBackgroundChecks). Without this Steve's
  // pending Checkr report shows "No background check on file" even though
  // the report is running. Bug caught 2026-05-08.
  const candidateReportStatus = c.checkrReportStatus || c.bgCheckStatus || null;
  const candidateReportId = c.checkrReportId || c.bgCheckReportId || null;
  const candidateCheckrCandidateId = c.checkrCandidateId || c.embedCheckrCandidateId || null;
  const candidatePackageSlug = c.checkrPackageSlug || c.embedPackageSlug || null;
  const status = bg.latestStatus
    || bg.latestInvitationStatus
    || candidateReportStatus
    || (candidateCheckrCandidateId ? 'created' : null);
  const invitedAt = bg.invitedAt?.toDate ? bg.invitedAt.toDate() : (bg.invitedAt ? new Date(bg.invitedAt) : null);
  const completedAt = bg.completedAt?.toDate
    ? bg.completedAt.toDate()
    : (bg.completedAt ? new Date(bg.completedAt) : (c.checkrCompletedAt?.toDate?.() || null));
  // Surface the apply-time package slug to bg so the UI label is correct.
  if (!bg.latestPackage && candidatePackageSlug) bg.latestPackage = candidatePackageSlug;
  const STATE = {
    clear: { label: 'Clear', color: '#059669', bg: '#D1FAE5', icon: 'check_circle' },
    eligible: { label: 'Eligible', color: '#059669', bg: '#D1FAE5', icon: 'check_circle' },
    consider: { label: 'Consider', color: '#D97706', bg: '#FFFBEB', icon: 'warning' },
    escalated: { label: 'Escalated', color: '#D97706', bg: '#FFFBEB', icon: 'warning' },
    suspended: { label: 'Suspended', color: '#DC2626', bg: '#FEE2E2', icon: 'pause_circle' },
    // TASK-559 — Checkr paused the report waiting on the candidate to confirm
    // ID/license/abstract (or report.suspended/paused/deferred). Read-only.
    needs_candidate_action: { label: '⏸ Action needed — candidate ID/identity confirmation', color: '#D97706', bg: '#FFFBEB', icon: 'pause_circle' },
    upgrade_failed: { label: 'Add-on screening failed to attach', color: '#DC2626', bg: '#FEE2E2', icon: 'error_outline' },
    canceled: { label: 'Canceled', color: '#6B7280', bg: '#F1F5F9', icon: 'cancel' },
    invited: { label: 'Invitation sent — waiting on candidate', color: '#2563EB', bg: '#DBEAFE', icon: 'mail' },
    created: { label: 'Invitation sent — waiting on candidate', color: '#2563EB', bg: '#DBEAFE', icon: 'mail' },
    completed: { label: 'Form completed — report processing', color: '#2563EB', bg: '#DBEAFE', icon: 'hourglass_top' },
    expired: { label: 'Invitation expired', color: '#DC2626', bg: '#FEE2E2', icon: 'timer_off' },
    // TASK-4584 — the invitation is gone at Checkr and NO report was ever
    // ordered. Distinct from `expired`/`canceled` (which read as report
    // outcomes) and from `invited` (which claims the driver still has a live
    // link). Copy says the actionable thing: send a new one.
    invitation_expired: { label: 'Invitation expired — send a new one', color: '#DC2626', bg: '#FEE2E2', icon: 'timer_off' },
    invitation_deleted: { label: 'Invitation canceled — send a new one', color: '#DC2626', bg: '#FEE2E2', icon: 'link_off' },
    // v1.15.374 — Checkr report-level statuses that land on candidate doc
    // when apply.html one_stage fires the Embed report.
    pending: { label: 'Background check running — waiting on Checkr', color: '#D97706', bg: '#FFFBEB', icon: 'hourglass_top' },
    complete: { label: 'Background check complete', color: '#059669', bg: '#D1FAE5', icon: 'check_circle' },
  };
  // v1.15.375 — Show typical turnaround when status is 'pending'.
  // Helps carriers know what to expect without escalating to support.
  // Times are typical, not guaranteed — Checkr surfaces actuals via webhook.
  const TYPICAL_TURNAROUNDS = `
    <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:6px;line-height:1.5;">
      <strong>Typical timing:</strong> MVR 1-6 hr · SSN trace, watchlist, sex offender minutes · National criminal minutes · <strong>County criminal 3-5 business days</strong> (slowest piece — gates the full report)
    </div>`;
  let s = STATE[status] || null;
  // TASK-559 — refine the generic needs_candidate_action label by the specific
  // Checkr stall reason (id/license/abstract/suspended/paused/deferred).
  if (status === 'needs_candidate_action' && s) {
    s = { ...s, label: _bgNeedsActionLabel(bg.needsActionKind) };
  }
  const hasCheck = !!bg.candidateId;
  // TASK-553 — no phantom fallback ('dot_driver_full' 404s at Checkr). A
  // twoStagePending candidate always has a CONSENTED slug on file (the embed
  // step set bgCheckPackageSlug), and the server (_runBackgroundCheckCore)
  // re-reads that consented slug and ignores a mismatched caller slug anyway.
  // Empty fallback + a safe label so we never render/submit an invented slug.
  // TASK-4557 — HOISTED above twoStagePending: the two-stage gate is now
  // computed FROM the consented slug rather than from "any bg candidateId".
  // TASK-10033 — display the package the SERVER will bill: an office pick, else the
  // server-recorded consent, else (legacy doc) the carrier default when the old field
  // names it. bgCheckPackageSlug alone is never trusted — the server refuses a Run whose
  // slug disagrees with its own, and '' renders the "Pick the screening package" state.
  const _t10033Def = (window.AppState?.tenantData || {}).compliancePackageDefault || '';
  const pendingSlug = (c.screeningPackageChoice && c.screeningPackageChoice.slug)
    || c.consentedPackageSlug
    || (_t10033Def && (c.bgCheckPackageSlug === _t10033Def || c.embedPackageSlug === _t10033Def) ? _t10033Def : '')
    || '';
  // ── TASK-4557 CONSENTED-SLUG GATE ─────────────────────────────────────────
  // 🔴 TASK-4557 — has a report actually FIRED for the package the applicant
  // CONSENTED to? The old gate was `!hasCheck` (`hasCheck = !!bg.candidateId`),
  // which any invitation for ANY package flips true — so a $9.50 MVR-only order
  // permanently hid the one-click run for the criminal+MVR package the applicant
  // had already signed for, with nothing left in the carrier UI able to act on
  // it. Confirmed live on two TITS applicants 2026-08-20. Now the gate asks the
  // question that actually matters: is the CONSENTED package still unrun?
  const _t4557ByPkg = (bg && bg.byPackage) || {};
  const _t4557ConsentedRow = (pendingSlug && _t4557ByPkg[pendingSlug]) || null;
  const _t4557ConsentedStatus = String((_t4557ConsentedRow && _t4557ConsentedRow.status) || '').toLowerCase();
  // 'invited'/'created' mean an invitation exists but NO report has fired —
  // that is still "ready to run". Anything else (pending/complete/consider/…)
  // or a reportId means the consented package has actually been ordered.
  // TASK-4584 — 'invitation_deleted'/'invitation_expired' (written by
  // checkrWebhook when Checkr kills the invitation) are the SAME answer to the
  // question this gate asks: no report ever fired, so the consented package is
  // still unrun and the Run button must stay. They are listed here rather than
  // left to the `!== 'invited'` test precisely because the naive reading —
  // "not invited, therefore ordered" — is the TASK-4557 dead-end all over
  // again. What CHANGES for those two is everywhere else: they are not "live
  // invitation" states, so the invite link and the awaiting-signature copy stop.
  const _t4557UnrunStatuses = ['invited', 'created', 'invitation_deleted', 'invitation_expired'];
  const _t4557ConsentedReportFired = !!(_t4557ConsentedRow && (
    _t4557ConsentedRow.reportId ||
    (_t4557ConsentedStatus && _t4557UnrunStatuses.indexOf(_t4557ConsentedStatus) === -1)
  ));
  // Pre-byPackage tenants: fall back to the legacy latest* pointer, and ONLY
  // when it names the consented package (a latestReportId for some OTHER
  // package is exactly the case this task exists to stop hiding the button).
  const _t4557LegacyConsentedFired = !_t4557ConsentedRow
    && !!bg.latestReportId
    && !!pendingSlug
    && String(bg.latestPackage || '') === String(pendingSlug);
  const _t4557ConsentedRun = _t4557ConsentedReportFired || _t4557LegacyConsentedFired;
  // TASK-219 Slice 2 — two-stage flow: applicant submitted + completed
  // the FCRA Embed at apply time, but the carrier hasn't triggered the
  // paid Checkr report yet. Replace the empty-state with a highlighted
  // "Ready to run" prompt + carrier-side Run button per UX Lead 2026-05-06.
  // (`bgCheckPending` is cleared by _runBackgroundCheckCore the moment the
  // consented run fires, so `_t4557ConsentedRun` is belt-and-braces for docs
  // where the flag was left set.)
  const twoStagePending = c.bgCheckPending === true && !!c.checkrCandidateId && !_t4557ConsentedRun;
  // ── END TASK-4557 CONSENTED-SLUG GATE ─────────────────────────────────────
  // TASK-307 — use central pretty-name helper instead of inline map.
  const slugLabel = pendingSlug ? _prettyPackageSlug(pendingSlug) : 'the selected';
  // TASK-10033 — who picked it (display mirror; the server's record is authoritative) + a
  // way to change it before Run. Order-gate holders only, same as the Run button's gate.
  const _t10033By = (c.screeningPackageChoice && c.screeningPackageChoice.slug === pendingSlug && c.screeningPackageChoice.byEmail)
    ? `<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:3px;">Package picked by ${escapeHtml(String(c.screeningPackageChoice.byEmail))}</div>` : '';
  const _t10033ChangeLink = _t10033By + (hrCanOrderBackgroundCheck()
    ? `<button type="button" class="cand-btn cand-btn-ghost" style="margin-top:4px;padding:2px 0;font-size:0.72rem;" onclick="window.HRModule.openHrScreeningPackagePicker('${escJsAttr(c.id)}')">Change package</button>`
    : '');
  // TASK-505 — partner-managed carriers can't self-bill. The two-stage button
  // becomes "Request from your DOT manager"; once requested (bgCheckRequestedAt
  // set, no report yet) we show a "Requested" confirmation in place of the run
  // button. Server gate is authoritative.
  const _partnerHandles = hrBgHandledByPartner();
  const _bgRequested = !!c.bgCheckRequestedAt && !c.checkrReportId;

  // BG Decision UI Phase 1+2 (2026-05-12): "View Background Check" button on
  // candidate side. Surfaces whenever a reportId is on the BG doc (mirrors the
  // employee-side gate at hr.js:15691 — bg.latestReportId presence is enough
  // to permit the modal, which fetches live state from Checkr).
  // bg here comes from peopleBackgroundChecks/{candidateId}; the candidate-doc
  // field `checkrReportId` is a separate Embed-flow fallback used as the
  // viewer key for one_stage applications.
  const viewableReportId = bg.latestReportId || candidateReportId || null;
  // TASK-652 (F) — suppress the duplicate "New Check" when a completed OR
  // in-flight report already exists for this candidate, and route review/
  // consider to adjudication. Re-requesting when a Checkr candidate already
  // exists is what triggers the 400 "ssn already used" collision this task
  // fixes, so don't offer it. `status` is the resolved bg/candidate status
  // (see top of this fn); existing-candidate link is read via either field the
  // source-of-truth stamp writes (checkrCandidateId || embedCheckrCandidateId).
  const _candStatusLc = String(status || '').toLowerCase();
  const _candBgInFlight = ['invited', 'created', 'completed', 'pending', 'needs_candidate_action'].includes(_candStatusLc);
  const _candBgNeedsReview = ['consider', 'review', 'escalated'].includes(_candStatusLc);
  const _candHasExistingCandidate = !!(hasCheck || candidateCheckrCandidateId);
  // 🔴 TASK-4557 — the second half of the intersection bug. TASK-652(F)'s
  // anti-duplicate guard is correct on its own, but combined with an in-flight
  // invitation for a DIFFERENT package it suppressed BOTH order buttons while
  // the consented package sat unrun forever. When the applicant's apply-time
  // consent is still unconsumed (`twoStagePending`), the ready-to-run panel
  // below owns the primary action and this guard must not also blank the card.
  const _candSuppressNewCheck = _candHasExistingCandidate && (!!viewableReportId || _candBgInFlight) && !twoStagePending;

  // TASK-1366 — action hierarchy. Exactly ONE filled button in this card: the
  // full-package run. Everything else (view report, MVR-only, "already have
  // one", "set status manually") renders as an outline or ghost so a reviewer
  // never has to ask which one to click. Labels now say what each ACTUALLY
  // runs — "Run Check" vs "Run via Checkr ($)" gave no clue that the first
  // fires the account's default package and the second orders an MVR only.
  // Handlers/arguments are byte-for-byte unchanged.
  //
  // TASK-1486 (John, 8/11 call) — the single header button never said WHICH
  // record was being ordered, and the MVR-only order was buried in the
  // Screenings sub-block further down the card. The header now carries the two
  // orders a carrier actually places, named exactly: "Run MVR" (the driving
  // record on its own — intent='mvr' preselects the pure-MVR package) and
  // "Run Background Check" (the account's default package). Same opener, same
  // modal, same callable (checkrInitiateBackgroundCheck mints a FRESH hosted
  // invitation, so the package is NOT bound to a prior consent) — only the
  // pre-aim differs. "Run MVR" stays the SECONDARY (outline) action: the one
  // filled button on this card is still the full background check.
  // 💸 Money guard + parity with the Screenings MVR row: BOTH buttons ask the same
  // question via _hrCandMvrOrderOfferable (terminal-row guard, Checkr-owns-the-row
  // guard, Checkr-live + DL-state requirements). Without that shared predicate the
  // header would offer an MVR order in three cases the row deliberately blocks.
  const _offerHeaderMvr = _hrCandMvrOrderOfferable(c);
  const _hdrMvrTitle = _partnerHandles
    ? `Request ${hrPartnerLabel()} run the driving record (MVR) on its own${hrBgPaidByPartner() ? ' — no charge to you' : ' — billed to your card when it completes'}.`
    : 'Orders the driving record (MVR) on its own, instead of the full background package.';
  const headerActions = `
          ${viewableReportId ? `
            <button type="button" class="cand-btn cand-btn-secondary" onclick="window.HRModule.openBgReportViewer('${escJsAttr(c.id)}','${escJsAttr(viewableReportId)}')" title="Open the completed report (background check and/or MVR)">
              <span class="material-icons">description</span> View report
            </button>` : ''}
          ${twoStagePending ? '' /* 2026-05-13 — hide the always-visible "+ Run Check" when the
             contextual "Applicant submitted — ready to run" blue panel is showing the primary
             Run Background Check CTA. Two buttons that both ran a BG check from the same header
             confused users (Chris feedback). The blue panel carries pre-selected package + FCRA
             consent context; the top "+ Run Check" reappears once that pending check fires or
             is cleared. */
            : (_candSuppressNewCheck
              ? (_candBgNeedsReview
                ? `<button type="button" class="cand-btn cand-btn-warn" onclick="window.HRModule.openBgReportViewer('${escJsAttr(c.id)}','${escJsAttr(viewableReportId || '')}')">
                    <span class="material-icons">flag</span> Action required: review report
                  </button>`
                : '' /* in-flight / clean: a check is already on file — no duplicate re-request */)
              : `
            ${_offerHeaderMvr ? `
            <button type="button" class="cand-btn cand-btn-secondary" onclick="window.HRModule.openBackgroundCheckModal('${escJsAttr(c.id)}', 'candidate', 'mvr')" title="${window.escAttr ? window.escAttr(_hdrMvrTitle) : escapeHtml(_hdrMvrTitle)}">
              <span class="material-icons">directions_car</span> Run MVR
            </button>` : ''}
            <button type="button" class="cand-btn cand-btn-primary" onclick="window.HRModule.openBackgroundCheckModal('${escJsAttr(c.id)}', 'candidate')" title="${hasCheck ? 'Runs another background check using your account&#39;s default package. Pick the package in the next step.' : 'Runs your account&#39;s default background package. Pick the package in the next step.'}">
              <span class="material-icons">${hasCheck ? 'refresh' : 'play_arrow'}</span> Run Background Check
            </button>`)}`;

  const statusPanel = `
      ${(c.ssnCollisionAtApply === true && !_candHasExistingCandidate) ? `
        <div style="background:#FFFBEB;border:1px solid #FCD34D;border-radius:var(--radius-md);padding:10px 12px;margin-bottom:8px;display:flex;align-items:flex-start;gap:8px;">
          <span class="material-icons" style="font-size:18px;color:#D97706;flex-shrink:0;margin-top:1px;">history</span>
          <div style="font-size:0.8rem;color:#92400E;line-height:1.45;">
            <strong>Previous application detected — send a fresh background check invite.</strong>
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:3px;">This applicant applied before, so the online consent form couldn't run at apply time. No check is in progress — the invite email captures fresh FCRA consent and relinks cleanly.</div>
            <button type="button" class="cand-btn cand-btn-warn" style="margin-top:8px;" onclick="window.HRModule.openBackgroundCheckModal('${escJsAttr(c.id)}', 'candidate')">
              <span class="material-icons">forward_to_inbox</span> Send background check invite
            </button>
          </div>
        </div>` : ''}
      <div style="background:${twoStagePending ? '#EFF6FF' : (s?.bg || 'var(--bg-page)')};border:1px solid ${twoStagePending ? '#93C5FD' : (s?.color ? s.color + '22' : 'var(--border-light)')};border-radius:var(--radius-md);padding:10px 12px;">
        ${twoStagePending ? (
          _bgRequested ? `
          <div style="display:flex;align-items:flex-start;gap:8px;">
            <span class="material-icons" style="font-size:18px;color:#059669;flex-shrink:0;margin-top:1px;">forward_to_inbox</span>
            <div style="font-size:0.8rem;color:#065F46;line-height:1.45;flex:1;min-width:0;">
              <strong>Requested — ${escapeHtml(hrPartnerLabel())} will run this.</strong>
              <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:3px;">Your DOT manager handles background checks for this account. They've been notified and it's on their worklist.</div>
              ${''/* TASK-1386 — cancel affordance. Without it an open request has NO exit
                 (the only clearing path is the report actually firing), so a bgHandledBy
                 flip or a change of mind stranded this banner forever. Owner/admin only —
                 mirrors the server gate (cancelCarrierBgRequest = requireTenantAdmin). */}
              ${_hrIsOwnerOrAdmin() ? `
              <button type="button" class="cand-btn cand-btn-secondary" style="margin-top:8px;" onclick="window.HRModule.cancelHrBgRequest('${escJsAttr(c.id)}')" title="Withdraw this request — it will be removed from your DOT manager's worklist">
                <span class="material-icons" style="font-size:16px;">close</span> Cancel request
              </button>` : ''}
            </div>
          </div>` : (
          !pendingSlug ? `
          <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
            <div style="flex:1;min-width:0;">
              <div style="display:flex;align-items:center;gap:6px;font-size:0.85rem;font-weight:700;color:#1E40AF;">
                <span class="material-icons" style="font-size:18px;">pending_actions</span> Applicant submitted — pick a package
              </div>
              <div style="font-size:0.75rem;color:#1E3A8A;margin-top:4px;line-height:1.4;">
                Pick the screening package for this applicant. Nothing is ordered until you click Run.
              </div>
            </div>
            <button type="button" id="hr-run-bg-${escapeHtml(c.id)}" class="cand-btn cand-btn-primary" onclick="window.HRModule.openHrScreeningPackagePicker('${escJsAttr(c.id)}')">
              <span class="material-icons">fact_check</span> Pick package
            </button>
          </div>` :
          _partnerHandles ? `
          <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
            <div style="flex:1;min-width:0;">
              <div style="display:flex;align-items:center;gap:6px;font-size:0.85rem;font-weight:700;color:#1E40AF;">
                <span class="material-icons" style="font-size:18px;">pending_actions</span> Applicant submitted — ready to run
              </div>
              <div style="font-size:0.75rem;color:#1E3A8A;margin-top:4px;line-height:1.4;">
                ${escapeHtml(hrPartnerLabel())} runs background checks for your account. Send them the request to run <strong>${escapeHtml(slugLabel)}</strong>.
                ${_t10033ChangeLink}
              </div>
            </div>
            <button type="button" id="hr-run-bg-${escapeHtml(c.id)}" class="cand-btn cand-btn-warn" onclick="window.HRModule.runHrTwoStageBgCheck('${escJsAttr(c.id)}','${escJsAttr(pendingSlug)}')">
              <span class="material-icons">forward_to_inbox</span> Request from your DOT manager
            </button>
          </div>` : `
          <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;">
            <div style="flex:1;min-width:0;">
              <div style="display:flex;align-items:center;gap:6px;font-size:0.85rem;font-weight:700;color:#1E40AF;">
                <span class="material-icons" style="font-size:18px;">pending_actions</span> Applicant submitted — ready to run
              </div>
              <div style="font-size:0.75rem;color:#1E3A8A;margin-top:4px;line-height:1.4;">
                FCRA consent was captured at apply time. Click below to run <strong>${escapeHtml(slugLabel)}</strong>. You'll be billed for the package only after the report fires.
                ${_t10033ChangeLink}
              </div>
            </div>
            <button type="button" id="hr-run-bg-${escapeHtml(c.id)}" class="cand-btn cand-btn-primary" onclick="window.HRModule.runHrTwoStageBgCheck('${escJsAttr(c.id)}','${escJsAttr(pendingSlug)}')">
              <span class="material-icons">play_arrow</span> Run background check
            </button>
          </div>`)) : (s ? `
          <div style="display:flex;align-items:center;gap:6px;font-size:0.82rem;font-weight:600;color:${s.color};">
            <span class="material-icons" style="font-size:18px;">${s.icon}</span> ${s.label}
          </div>
          ${status === 'pending' ? TYPICAL_TURNAROUNDS : ''}` : `
          <div style="font-size:0.82rem;color:var(--text-secondary);text-align:center;padding:6px;">
            No background check on file. Use <strong>Run Background Check</strong> above to send the candidate a Checkr-hosted consent form${_offerHeaderMvr ? ', or <strong>Run MVR</strong> for the driving record on its own' : ''}.
          </div>`)}
        ${''/* Package label removed 2026-05-08 — internal slug, not useful to carriers. */}
        ${invitedAt ? `<div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">Invited ${invitedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>` : ''}
        ${completedAt ? `<div style="font-size:0.72rem;color:var(--text-secondary);">Completed ${completedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</div>` : ''}
        ${bg.invitationUrl && (status === 'created' || status === 'invited') ? `
          <a href="${escapeHtml(bg.invitationUrl)}" target="_blank" rel="noopener" style="font-size:0.75rem;color:var(--brand-blue);display:inline-flex;align-items:center;gap:4px;margin-top:6px;">
            <span class="material-icons" style="font-size:14px;">open_in_new</span> Open candidate form (preview)
          </a>` : ''}
      </div>`;

  // ── TASK-1366: ONE card for everything background/driving ──────────────
  // Was THREE sibling cards, each styled differently, all about the same
  // subject ("Background Check" / "Background Reports" / "Driving Record —
  // Self-Reported"), so a carrier could not tell which was authoritative.
  // They are now: the status panel, a "Screenings" sub-block, and a
  // "Self-Reported by the Applicant" sub-block — inside one shell, in
  // authority order (verified first, applicant's own answers last).
  const screenings = _buildCandidateScreeningsBlock(c);
  const selfReported = buildCandidateDrivingRecordSection(c);
  return _candCard({
    icon: 'fact_check',
    title: 'Background &amp; Driving Record',
    actions: headerActions,
    body: statusPanel,
    raw: screenings + selfReported,
  });
}

// TASK-1366 — the per-screening checklist (MVR / PSP / CDLIS / Clearinghouse),
// lifted verbatim out of getViewCandidateModalHTML so it can render as a
// sub-block of the single Background & Driving Record card instead of as its
// own competing "Background Reports" card. Role gating, the §382.701/PSP gap
// note and every buildReportCard argument are unchanged.
function _buildCandidateScreeningsBlock(c) {
  // TASK-9698 — read the tier ONCE through DqMatrix: it normalizes every stored spelling
  // (prod 2026-10-13: candidates `cdl` / `non-cdl-driver` / `non-driver` / missing, people
  // `cdl` / `non_cdl_driver` / `non_driver` / missing; any case, `-` or `_`) and resolves an
  // unclassified applicant to 'cdl' (full checklist) exactly as `|| 'cdl'` did. Stored data is
  // never rewritten here.
  const pt = window.DqMatrix.getApplicableDqItems(c).positionType;
  const isDriver = pt !== 'non_driver'; // TASK-9696 — cdl, non_cdl_driver, drives_no_cmv: has a licence, MVR card applies
  const isCdl = pt === 'cdl';
  // TASK-9698 — the other-state list (CDLIS card) is for every CMV driver: CDL and Non-CDL Driver
  // (§391.23(a)(1) covers any license). Never drives-no-CMV. A non-CDL driver's card is labelled
  // for what it is — there is no CDLIS record for them.
  // COD fold: the same DqMatrix gate as every other host (both spellings, explicit tier wins).
  const isCmvDriver = window.DqMatrix.hasDqFile(pt);
  if (!isDriver) return '';
  // TASK-1270 — the Background Reports cards are gated on ROLE, not on what
  // the background package actually RUNS. With an MVR-only default package a
  // CDL driver's MVR goes green while the FMCSA-mandatory items sit NOT
  // STARTED — a carrier could reasonably read "green MVR" as fully screened.
  // Clearinghouse (§382.701) is never Checkr-covered (FMCSA-direct); PSP
  // (§391.23) is only in the full DOT packages. Surface an explicit note
  // while either mandatory item is still NOT STARTED (clears once handled).
  let _cdlGapNote = '';
  if (isCdl) {
    // Regulatory precision (COD Domain/Code-Reviewer): §382.701(a) is the
    // hard, package-uncovered Clearinghouse requirement. PSP is FMCSA
    // crash/inspection data — a pre-hire screening aid, NOT the §391.23
    // previous-employer investigation and not itself federally mandated;
    // frame it accurately so this legal-adjacent copy doesn't overstate.
    const _bits = [];
    if (isReportNotYetRun(getReportStatus(c, 'clearinghouse'))) { // TASK-9300 (3): a missing row is not_started, still a gap
      // TASK-9299 (Kolsen "De-Clutter", 9/21) — same facts, plain short words: the
      // query is owed BEFORE the driver operates and is run at FMCSA, not in a package.
      _bits.push('the <strong>Clearinghouse pre-employment query</strong> (§382.701) — due before the driver operates; run it at FMCSA');
    }
    if (isReportNotYetRun(getReportStatus(c, 'psp'))) {
      _bits.push('a <strong>PSP report</strong> — a screening aid, not a substitute for the §391.23 prior-employer checks');
    }
    if (_bits.length) {
      _cdlGapNote = `
        <div style="margin:0 12px 12px;background:#FEF3C7;border:1.5px solid #F59E0B;border-radius:var(--radius-md);padding:10px 12px;font-size:0.8rem;color:#92400E;line-height:1.45;">
          <span class="material-icons" style="font-size:16px;vertical-align:-3px;margin-right:4px;">gpp_maybe</span>
          <strong>Still open:</strong> ${_bits.join('; and ')}.
          An MVR-only package does <strong>not</strong> cover ${_bits.length > 1 ? 'these' : 'this'}.
        </div>`;
    }
  }
  // TASK-649 (#6B) — external state/federal "Run" redirects removed for MVR +
  // PSP (they bounced the user to a portal where they couldn't actually pull
  // the report). MVR keeps its IN-PLATFORM affordances inside buildReportCard;
  // record PSP via its status control. Clearinghouse keeps its FMCSA redirect
  // — there is no in-platform query path for it.
  // TASK-1366 — the 2-up grid is a class now (`bg-report-grid`); the old inline
  // grid-template-columns is the exact shape that clipped a column at 430px.
  return _candSubhead('policy', 'Screenings', isCdl ? 'MVR &middot; PSP &middot; CDLIS &middot; Clearinghouse' : (isCmvDriver ? 'MVR &middot; Prior-state licenses' : 'MVR')) + `
    <div class="bg-report-grid">
      ${buildReportCard(c, 'mvr', 'Motor Vehicle Record (MVR)', 'directions_car', '')}
      ${isCdl ? buildReportCard(c, 'psp', 'Pre-Employment Screening (PSP)', 'security', '') : ''}
      ${isCmvDriver ? buildReportCard(c, 'cdlis', isCdl ? 'CDLIS Report' : 'Prior-State Licenses (3 yrs)', 'fact_check', '') : ''}
      ${isCdl ? buildReportCard(c, 'clearinghouse', 'Drug & Alcohol Clearinghouse', 'vaccines', 'https://clearinghouse.fmcsa.dot.gov/') : ''}
    </div>
    ${_cdlGapNote}`;
}

// TASK-1203 Slice C — 391.21(b)(12) certification + applicant signature on the
// carrier view/print (the modal IS the print — printCandidateApplication clones
// it). The carrier legitimately holds the candidate doc, so the stored Storage
// token URL renders directly here (unlike the PARTNER surface, which gets the
// bytes only via the audited application.getSignature action — never the URL).
// Broken/missing image → "signature on file / no signature" fallback text.
function buildCandidateCertSignatureSection(c) {
  const sig = (c.signatures && c.signatures.application) || {};
  const hasSigUrl = !!(sig.url && String(sig.url).trim());
  const sigDate = sig.date || c.applicationSignatureDate || '';
  const certAccepted = c.certificationAccepted === true;
  const certAt = c.certificationAcceptedAt || '';
  const submittedAt = c.submittedAt || '';
  // Nothing recorded at all (legacy paper candidates) → skip the section.
  if (!hasSigUrl && !certAccepted && !sigDate) return '';
  const sigImgHtml = hasSigUrl
    ? `<img src="${_safeHttpUrl(sig.url)}" alt="Applicant signature" style="max-height:64px;max-width:280px;display:block;" onerror="this.style.display='none';this.nextElementSibling.style.display='block';">
       <div style="display:none;font-size:0.8rem;color:var(--text-secondary);font-style:italic;">Signature on file — image unavailable</div>`
    : `<div style="font-size:0.8rem;color:var(--text-secondary);font-style:italic;">No signature image on file</div>`;
  return _candCard({
    icon: 'draw',
    title: 'Certification &amp; Signature',
    note: '49 CFR §391.21(b)(12)',
    actions: certAccepted
      ? `<span style="display:inline-flex;align-items:center;gap:4px;color:#059669;font-size:0.72rem;font-weight:700;"><span class="material-icons" style="font-size:15px;">check_circle</span>Certified${certAt ? ' ' + escapeHtml(formatCandidateDate(certAt)) : ''}</span>`
      : `<span style="display:inline-flex;align-items:center;gap:4px;color:#B45309;font-size:0.72rem;font-weight:700;"><span class="material-icons" style="font-size:15px;">error_outline</span>Not recorded</span>`,
    body: `
        <div style="font-size:0.82rem;color:var(--text-primary);font-style:italic;margin-bottom:14px;line-height:1.5;">
          &ldquo;This certifies that this application was completed by me, and that all entries on it and information in it are true and complete to the best of my knowledge.&rdquo;
        </div>
        <div style="display:flex;gap:32px;align-items:flex-end;flex-wrap:wrap;">
          <div>
            ${sigImgHtml}
            <div style="border-top:1px solid var(--text-secondary);margin-top:4px;padding-top:2px;font-size:0.72rem;color:var(--text-secondary);min-width:220px;">(Applicant's signature)</div>
          </div>
          <div>
            <div style="font-size:0.9rem;color:var(--text-primary);">${sigDate ? escapeHtml(formatCandidateDate(sigDate)) : _candEmpty('No date signed')}</div>
            <div style="border-top:1px solid var(--text-secondary);margin-top:4px;padding-top:2px;font-size:0.72rem;color:var(--text-secondary);min-width:110px;">(Date)</div>
          </div>
        </div>`,
    foot: submittedAt ? `Submitted ${escapeHtml(formatCandidateDate(submittedAt))} (server-stamped)` : '',
  });
}

// ============================================
// TASK-1213 — Candidate "Notes & Calls" feed
// ============================================
// Timestamped call/screening notes on the APPLICANT, mirroring the employee
// hrNotes pattern (subcollection + module cache + feed renderer). Subcollection:
// tenants/{tid}/candidates/{id}/notes, doc shape
//   { body, type: 'note'|'status'|'interview', createdAt, authorUid, authorName,
//     fromStatus?, toStatus? }
// Every write also stamps lastContactDate on the candidate doc (brings the
// dead "Last Contact" field to life). Legacy flat c.notes renders as the
// feed's OLDEST entry labeled "Imported note" — never migrated/deleted, so the
// Edit-candidate form keeps working. firestore.rules gate mirrors the PARENT
// candidates surface (admin | managing partner | hr module) — deliberately
// NOT the wider hrNotes isTenantMember gate.
const _candNotesCache = new Map(); // candidateId -> [{ id, body, type, createdAt, authorUid, authorName, ... }]
let _candNoteSubmitInFlight = false;

// ============================================
// TASK-1219 — Interview scheduling (applicant card + quick-status inline)
// ============================================
// Single next-interview slot v1: interviewAt / interviewerPersonId /
// interviewerName / interviewNote / interviewReminderId live on the candidate
// doc, written SERVER-side by the scheduleCandidateInterview callable (the
// scheduledReminders SMS collection is admin-SDK-only by design). Google/
// Outlook calendar sync is explicitly FUTURE — the callable emits one event
// object internally as the seam.

function _candInterviewMs(c) {
  return c?.interviewAt?.toMillis?.()
    ?? (c?.interviewAt?.seconds ? c.interviewAt.seconds * 1000 : 0);
}

// Upcoming = from now through one hour after the scheduled start (so a
// just-started interview doesn't vanish mid-meeting).
function _candInterviewUpcoming(c) {
  const ms = _candInterviewMs(c);
  return !!(ms && ms > Date.now() - 3600e3);
}

// "Fri 4:30 PM" inside the next 6 days, "Jul 17, 4:30 PM" beyond.
function _fmtInterviewShort(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  const withinWeek = ms - Date.now() < 6 * 86400e3 && ms > Date.now() - 86400e3;
  return withinWeek
    ? d.toLocaleString('en-US', { weekday: 'short', hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function _fmtInterviewLong(ms) {
  if (!ms) return '';
  return new Date(ms).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Timestamp/Date → datetime-local input value ("YYYY-MM-DDTHH:MM", local time).
function _toDatetimeLocalValue(ts) {
  const ms = ts?.toMillis?.() ?? (ts?.seconds ? ts.seconds * 1000 : (typeof ts === 'number' ? ts : 0));
  if (!ms) return '';
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Interviewer picker: active roster people (they carry the phone the SMS
// reminder needs), defaulting to the signed-in user's linked person ("for
// themselves") with an admin-ish fallback. Same source the 30-day-review
// reviewer picker uses (openApproveToRosterModal).
function _defaultInterviewerPersonId() {
  const people = (HRState.supervisors?.length ? HRState.supervisors : HRState.people) || [];
  const selfPid = window.AppState?.memberAccess?.personId;
  if (selfPid && people.some(p => p.id === selfPid)) return selfPid;
  const email = (window.AppState?.user?.email || '').toLowerCase();
  const byEmail = email && people.find(p => (p.email || '').toLowerCase() === email);
  if (byEmail) return byEmail.id;
  const adminish = people.find(p => p.active !== false && /owner|admin|foreman|supervisor|dispatch/i.test(p.crewType || p.role || p.roles?.[0] || ''));
  return adminish?.id || '';
}

function _interviewerOptionsHtml(selectedId) {
  const people = ((HRState.supervisors?.length ? HRState.supervisors : HRState.people) || [])
    .filter(p => p.active !== false);
  const selected = selectedId || _defaultInterviewerPersonId();
  if (people.length === 0) return '<option value="">Me (default)</option>';
  return people.map(p => {
    const roleLabel = typeof _hrRoleLabel === 'function' ? _hrRoleLabel(p.crewType || p.role || p.roles?.[0]) : '';
    return `<option value="${escapeHtml(p.id)}" ${p.id === selected ? 'selected' : ''}>${escapeHtml(p.name || '(unnamed)')}${roleLabel ? ' (' + escapeHtml(roleLabel) + ')' : ''}</option>`;
  }).join('');
}

// Detail-card section: scheduled → prominent callout with Reschedule/Cancel;
// not scheduled + still in pipeline → a "Schedule interview" affordance.
function buildCandidateInterviewSection(c) {
  const TERMINAL = ['hired', 'rejected', 'withdrawn'];
  if (_candInterviewUpcoming(c)) {
    const ms = _candInterviewMs(c);
    const withWho = c.interviewerName ? 'With ' + escapeHtml(c.interviewerName) : '';
    const note = c.interviewNote ? (withWho ? ' &middot; ' : '') + escapeHtml(c.interviewNote) : '';
    return _candCard({
      icon: 'event',
      title: 'Upcoming Interview',
      attrs: 'data-no-print',
      actions: `
        <button type="button" class="cand-btn cand-btn-secondary" onclick="window.HRModule.openScheduleInterviewModal('${c.id}')">
          <span class="material-icons">edit_calendar</span> Reschedule
        </button>
        <button type="button" class="cand-btn cand-btn-ghost" onclick="window.HRModule.cancelCandidateInterview('${c.id}')">Cancel</button>`,
      body: `
        <div style="font-weight:700;color:var(--text-primary);font-size:0.95rem;">${escapeHtml(_fmtInterviewLong(ms))}</div>
        <div style="font-size:0.82rem;color:var(--text-secondary);margin-top:2px;">${withWho || note ? withWho + note : _candEmpty('No interviewer or note recorded')}</div>`,
    });
  }
  if (TERMINAL.includes(c.status)) return '';
  return `
    <div class="cand-sec" data-no-print>
      <button type="button" class="cand-btn cand-btn-secondary" style="border-style:dashed;" onclick="window.HRModule.openScheduleInterviewModal('${c.id}')" title="Pick a date/time and interviewer — they get an SMS reminder an hour before">
        <span class="material-icons">event</span> Schedule interview
      </button>
    </div>`;
}

function openScheduleInterviewModal(id) {
  const c = HRState.candidates.find(x => x.id === id);
  if (!c) return;
  document.getElementById('modal-schedule-interview')?.remove();
  document.body.insertAdjacentHTML('beforeend', `
    <div class="modal" id="modal-schedule-interview" style="z-index:1060;">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Schedule Interview — ${escapeHtml(c.name || '')}</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('schedule-interview')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>When</label>
            <input type="datetime-local" id="schedule-interview-at" value="${_toDatetimeLocalValue(c.interviewAt)}" min="${_toDatetimeLocalValue(Timestamp.now())}" style="width:100%;">
          </div>
          <div class="form-group">
            <label>Interviewer</label>
            <select id="schedule-interview-interviewer" style="width:100%;">
              ${_interviewerOptionsHtml(c.interviewerPersonId)}
            </select>
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">The interviewer gets an SMS reminder about an hour before.</div>
          </div>
          <div class="form-group">
            <label>Note (optional)</label>
            <textarea id="schedule-interview-note" rows="2" maxlength="500" placeholder="e.g. Meet at the shop office">${escapeHtml(c.interviewNote || '')}</textarea>
          </div>
          <input type="hidden" id="schedule-interview-candidate-id" value="${c.id}">
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.HRModule.closeModal('schedule-interview')">Cancel</button>
          <button class="btn btn-primary" id="schedule-interview-save-btn" onclick="window.HRModule.saveScheduleInterview()">
            <span class="material-icons">event_available</span> Schedule
          </button>
        </div>
      </div>
    </div>`);
  document.getElementById('modal-schedule-interview').classList.remove('hidden');
}

async function saveScheduleInterview() {
  const id = document.getElementById('schedule-interview-candidate-id')?.value;
  const atLocal = document.getElementById('schedule-interview-at')?.value || '';
  const personId = document.getElementById('schedule-interview-interviewer')?.value || '';
  const note = (document.getElementById('schedule-interview-note')?.value || '').trim().slice(0, 500);
  if (!id) return;
  if (!atLocal) { window.showToast?.('Pick a date and time', 'info'); return; }
  const btn = document.getElementById('schedule-interview-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Scheduling…'; }
  try {
    await _scheduleInterviewViaCallable(id, atLocal, personId, note);
    closeModal('schedule-interview');
    document.getElementById('modal-schedule-interview')?.remove();
    // Refresh the detail card if it's open underneath so the callout appears.
    if (document.getElementById('modal-view-candidate')) viewCandidate(id);
    if (HRState.currentView === 'hiring') renderCandidatesList();
  } catch (err) {
    console.error('[HR] saveScheduleInterview failed:', err);
    const _fnMsg = String(err?.code || '').startsWith('functions/') ? (err?.message || '') : '';
    window.showToast?.(_fnMsg || 'Could not schedule the interview', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">event_available</span> Schedule'; }
  }
}

// Shared schedule path (standalone modal + quick-status inline). Converts the
// datetime-local value (local time) to epoch ms, calls the server callable
// (which owns the candidate write + the scheduledReminders SMS doc), then
// writes the TASK-1213 feed entry and toasts the reminder outcome.
async function _scheduleInterviewViaCallable(candidateId, atLocalValue, interviewerPersonId, note) {
  const ms = Date.parse(atLocalValue);
  if (isNaN(ms)) { window.showToast?.('Invalid date/time', 'error'); throw new Error('invalid datetime'); }
  const fn = httpsCallable(functions, 'scheduleCandidateInterview');
  const res = await fn({
    tenantId: HRState.tenantId,
    candidateId,
    action: 'schedule',
    interviewAtMs: ms,
    interviewerPersonId: interviewerPersonId || _defaultInterviewerPersonId() || null,
    note: note || '',
  });
  const interviewerName = res?.data?.interviewerName || '';
  // Optimistic local update so the chip/callout render without waiting on the snapshot.
  const cand = HRState.candidates.find(x => x.id === candidateId);
  if (cand) {
    cand.interviewAt = Timestamp.fromMillis(ms);
    cand.interviewerPersonId = interviewerPersonId || cand.interviewerPersonId || null;
    cand.interviewerName = interviewerName || cand.interviewerName || '';
    cand.interviewNote = note || '';
  }
  // TASK-1213 feed entry — "Interview scheduled Fri Jul 17, 4:30 PM — Chris".
  _writeCandidateFeedDoc(candidateId, {
    body: `Interview scheduled ${_fmtInterviewLong(ms)}${interviewerName ? ' — ' + interviewerName : ''}${note ? ' — "' + note + '"' : ''}`,
    type: 'interview',
  }).then(() => _renderCandidateNotesFeed(candidateId))
    .catch(e => console.warn('[HR] interview feed entry failed:', e?.message));
  if (res?.data?.reminderScheduled) {
    window.showToast?.('Interview scheduled — SMS reminder set', 'success');
  } else if (res?.data?.noPhone) {
    window.showToast?.('Interview scheduled — interviewer has no phone on file, so no SMS reminder', 'info');
  } else {
    window.showToast?.('Interview scheduled', 'success');
  }
  return res;
}

async function cancelCandidateInterview(id) {
  const c = HRState.candidates.find(x => x.id === id);
  const ok = window.showConfirm
    ? await window.showConfirm('Cancel this interview?', 'The scheduled interview and its SMS reminder are removed. The applicant keeps their current status.', { confirmText: 'Cancel interview', cancelText: 'Keep it' })
    : confirm('Cancel this interview?');
  if (!ok) return;
  try {
    const fn = httpsCallable(functions, 'scheduleCandidateInterview');
    await fn({ tenantId: HRState.tenantId, candidateId: id, action: 'cancel' });
    const prevWhen = _fmtInterviewLong(_candInterviewMs(c));
    if (c) { c.interviewAt = null; c.interviewerPersonId = null; c.interviewerName = ''; c.interviewNote = ''; }
    _writeCandidateFeedDoc(id, { body: `Interview cancelled${prevWhen ? ' (was ' + prevWhen + ')' : ''}`, type: 'interview' })
      .then(() => _renderCandidateNotesFeed(id))
      .catch(() => {});
    window.showToast?.('Interview cancelled', 'success');
    if (document.getElementById('modal-view-candidate')) viewCandidate(id);
    if (HRState.currentView === 'hiring') renderCandidatesList();
  } catch (err) {
    console.error('[HR] cancelCandidateInterview failed:', err);
    window.showToast?.('Could not cancel the interview', 'error');
  }
}

// HR landing "Actions" — upcoming interviews strip. Cheap single-field range
// query (no composite index needed); permission errors (member without HR)
// leave the strip hidden.
async function _loadUpcomingInterviews() {
  const el = document.getElementById('hr-upcoming-interviews');
  if (!el || !HRState.tenantId) return;
  try {
    const q = query(
      collection(db, 'tenants', HRState.tenantId, 'candidates'),
      where('interviewAt', '>=', Timestamp.now()),
      orderBy('interviewAt'),
      limit(6)
    );
    const snap = await getDocs(q);
    const rows = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(c => !isArchivedCandidate(c));
    if (rows.length === 0) { el.style.display = 'none'; el.innerHTML = ''; return; }
    el.style.display = 'block';
    el.innerHTML = `
      <div style="margin-bottom:var(--spacing-md);padding:10px 14px;background:var(--bg-card);border:1px solid var(--border-light);border-radius:var(--radius-md);">
        <div style="font-size:0.8rem;font-weight:700;color:var(--text-secondary);display:flex;align-items:center;gap:6px;margin-bottom:6px;">
          <span class="material-icons" style="font-size:16px;color:#2563EB;">event</span> Upcoming interviews
        </div>
        ${rows.map(c => `
          <div style="display:flex;align-items:center;gap:8px;padding:4px 0;font-size:0.85rem;cursor:pointer;" onclick="window.HRModule.openHiringSubView();setTimeout(()=>window.HRModule.viewCandidate('${c.id}'),150);">
            <span style="font-weight:600;color:#1E3A8A;white-space:nowrap;">${escapeHtml(_fmtInterviewShort(_candInterviewMs(c)))}</span>
            <span style="flex:1;color:var(--text-primary);">${escapeHtml(c.name || '(unnamed)')}</span>
            ${c.interviewerName ? `<span style="color:var(--text-secondary);font-size:0.78rem;">${escapeHtml(c.interviewerName)}</span>` : ''}
          </div>`).join('')}
      </div>`;
  } catch (e) {
    // Member without HR/admin can't read candidates — leave the strip hidden.
    el.style.display = 'none';
  }
}

function _candNoteMs(n) {
  return n?.createdAt?.toMillis?.()
    ?? (n?.createdAt?.seconds ? n.createdAt.seconds * 1000 : 0);
}

// Compact "when" label: relative inside 24h, short date+time beyond.
function _candNoteWhen(ts) {
  const ms = ts?.toMillis?.() ?? (ts?.seconds ? ts.seconds * 1000 : 0);
  if (!ms) return '';
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function _candNoteAuthorName() {
  return window.AppState?.user?.displayName || window.AppState?.user?.email || 'Unknown';
}

// The prominent on-card section: quick-add composer + newest-first feed.
// data-no-print — internal screening notes must NOT ride onto the printed
// §391.21 employment application (printCandidateApplication clones the modal).
function buildCandidateNotesSection(c) {
  return _candCard({
    icon: 'forum',
    title: 'Notes &amp; Calls',
    attrs: 'data-no-print',
    actions: '<span id="candidate-notes-count-chip" style="display:none;font-size:0.7rem;font-weight:700;background:var(--bg-card,#fff);border:1px solid var(--border-medium);color:var(--text-secondary);padding:2px 9px;border-radius:999px;"></span>',
    body: `
        <div style="display:flex;gap:8px;align-items:flex-start;margin-bottom:10px;flex-wrap:wrap;">
          <textarea id="candidate-note-input" rows="2" maxlength="2000" placeholder="Log a call or note — e.g. Called, left voicemail. Wants night shift." style="flex:1 1 220px;resize:vertical;font-size:0.88rem;padding:8px 10px;border:1px solid var(--border-medium);border-radius:var(--radius-md);font-family:inherit;background:var(--bg-card,#fff);color:var(--text-primary);"></textarea>
          <button type="button" class="cand-btn cand-btn-primary" id="candidate-note-save-btn" onclick="window.HRModule.addCandidateNote('${c.id}')" style="align-self:flex-end;">
            <span class="material-icons">add_comment</span> Log note
          </button>
        </div>
        <div id="candidate-notes-feed" style="display:flex;flex-direction:column;gap:8px;">
          <div style="font-size:0.8rem;color:var(--text-secondary);padding:4px 0;">Loading notes…</div>
        </div>`,
  });
}

async function loadCandidateNotesFeed(candidateId) {
  const feedEl = document.getElementById('candidate-notes-feed');
  if (!feedEl || !HRState.tenantId) return;
  try {
    const notesRef = collection(db, 'tenants', HRState.tenantId, 'candidates', candidateId, 'notes');
    const snap = await getDocs(notesRef);
    const notes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    notes.sort((a, b) => _candNoteMs(b) - _candNoteMs(a));
    _candNotesCache.set(candidateId, notes);
    _renderCandidateNotesFeed(candidateId);
  } catch (err) {
    console.error('[HR] Load candidate notes failed:', err);
    feedEl.innerHTML = '<div style="font-size:0.8rem;color:var(--text-secondary);">Could not load notes.</div>';
  }
}

function _renderCandidateNotesFeed(candidateId) {
  const feedEl = document.getElementById('candidate-notes-feed');
  if (!feedEl) return;
  const notes = [...(_candNotesCache.get(candidateId) || [])];
  notes.sort((a, b) => _candNoteMs(b) - _candNoteMs(a));
  const c = HRState.candidates.find(x => x.id === candidateId);
  const currentUid = window.AppState?.user?.uid || null;
  const _role = window.AppState?.memberAccess?.role || null;
  const _isOwnerOrAdmin = _role === 'owner' || _role === 'admin' || !window.AppState?.memberAccess;
  const rows = notes.map(n => {
    const isStatus = n.type === 'status' || n.type === 'interview';
    const icon = n.type === 'interview' ? 'event' : (isStatus ? 'swap_horiz' : '');
    const meta = `${_candNoteWhen(n.createdAt)}${n.authorName ? ' · ' + escapeHtml(n.authorName) : ''}`;
    // Status/interview entries: single-line system style. Free-text: placard.
    if (isStatus) {
      return `
        <div style="display:flex;align-items:flex-start;gap:6px;padding:5px 8px;font-size:0.8rem;color:var(--text-secondary);border-left:3px solid var(--border-medium,#cbd5e1);">
          <span class="material-icons" style="font-size:15px;margin-top:1px;">${icon}</span>
          <div style="flex:1;min-width:0;">
            <span style="color:var(--text-primary);">${escapeHtml(n.body || '')}</span>
            <span style="margin-left:6px;font-size:0.72rem;">${meta}</span>
          </div>
        </div>`;
    }
    const canDelete = (currentUid && n.authorUid === currentUid) || _isOwnerOrAdmin;
    return `
      <div style="padding:8px 10px;background:var(--bg-card,#fff);border:1px solid var(--border-light,#e5e7eb);border-radius:6px;">
        <div style="display:flex;align-items:center;gap:6px;font-size:0.7rem;color:var(--text-secondary);margin-bottom:3px;">
          <span style="flex:1;">${meta}</span>
          ${canDelete ? `<button class="btn-icon" onclick="window.HRModule.deleteCandidateNote('${escJsAttr(candidateId)}','${escJsAttr(n.id)}')" title="Delete note" style="padding:0 2px;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;">close</span></button>` : ''}
        </div>
        <div style="font-size:0.85rem;line-height:1.4;color:var(--text-primary);white-space:pre-wrap;">${escapeHtml(n.body || '')}</div>
      </div>`;
  });
  // Legacy flat notes → oldest entry, labeled "Imported note" (never migrated).
  if (c?.notes) {
    rows.push(`
      <div style="padding:8px 10px;background:var(--bg-card,#fff);border:1px dashed var(--border-medium,#cbd5e1);border-radius:6px;opacity:0.85;">
        <div style="font-size:0.7rem;color:var(--text-secondary);margin-bottom:3px;">Imported note</div>
        <div style="font-size:0.85rem;line-height:1.4;color:var(--text-primary);white-space:pre-wrap;">${escapeHtml(c.notes)}</div>
      </div>`);
  }
  const chip = document.getElementById('candidate-notes-count-chip');
  if (chip) {
    const n = notes.length + (c?.notes ? 1 : 0);
    chip.style.display = n ? 'inline-block' : 'none';
    chip.textContent = n;
  }
  feedEl.innerHTML = rows.length
    ? rows.join('')
    : '<div style="font-size:0.8rem;color:var(--text-secondary);padding:4px 0;">No notes yet — log the first call above. Status changes will show up here too.</div>';
}

async function addCandidateNote(candidateId) {
  if (_candNoteSubmitInFlight) return; // double-click guard
  const input = document.getElementById('candidate-note-input');
  const btn = document.getElementById('candidate-note-save-btn');
  const body = (input?.value || '').trim().slice(0, 2000);
  if (!body) { window.showToast?.('Type a note first', 'info'); input?.focus(); return; }
  const uid = window.AppState?.user?.uid || null;
  if (!uid) { window.showToast?.('Sign in to add notes', 'error'); return; }
  _candNoteSubmitInFlight = true;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span>'; }
  try {
    await _writeCandidateFeedDoc(candidateId, { body, type: 'note' });
    if (input) input.value = '';
    _renderCandidateNotesFeed(candidateId);
    window.logAudit?.('hr', 'CANDIDATE_NOTE_ADD', {
      entityType: 'candidate', entityId: candidateId, candidateId, bodyLength: body.length,
    });
  } catch (err) {
    console.error('[HR] addCandidateNote failed:', err);
    window.showToast?.('Could not save note', 'error');
  } finally {
    _candNoteSubmitInFlight = false;
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">add_comment</span> Log note'; }
  }
}

async function deleteCandidateNote(candidateId, noteId) {
  try {
    await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'candidates', candidateId, 'notes', noteId));
    const cached = _candNotesCache.get(candidateId) || [];
    _candNotesCache.set(candidateId, cached.filter(n => n.id !== noteId));
    _renderCandidateNotesFeed(candidateId);
  } catch (err) {
    console.error('[HR] deleteCandidateNote failed:', err);
    window.showToast?.('Could not delete note', 'error');
  }
}

// Shared write path — writes the feed doc AND stamps lastContactDate +
// lastActivityAt on the candidate (a logged touch IS contact/activity).
// Returns the new note id. Callers own their error handling.
async function _writeCandidateFeedDoc(candidateId, fields) {
  const noteDoc = {
    type: 'note',
    ...fields,
    createdAt: Timestamp.now(),
    authorUid: window.AppState?.user?.uid || null,
    authorName: _candNoteAuthorName(),
  };
  const newRef = await addDoc(collection(db, 'tenants', HRState.tenantId, 'candidates', candidateId, 'notes'), noteDoc);
  const cached = _candNotesCache.get(candidateId) || [];
  cached.unshift({ id: newRef.id, ...noteDoc });
  _candNotesCache.set(candidateId, cached);
  // Stamp lastContactDate — best-effort; the note itself already landed.
  // TASK-9419 — never touch the PARENT of an archived applicant (read-only until Restored).
  if (isArchivedCandidate(HRState.candidates.find(x => x.id === candidateId))) return newRef.id;
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'candidates', candidateId), {
      lastContactDate: Timestamp.now(),
      lastActivityAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    const cand = HRState.candidates.find(x => x.id === candidateId);
    if (cand) cand.lastContactDate = Timestamp.now();
  } catch (e) {
    console.warn('[HR] lastContactDate stamp failed (note saved):', e?.message);
  }
  return newRef.id;
}

// ============================================================================
// TASK-4791 (part C) — APPLICANT ACTIVITY LOG (carrier host).
// Kolsen: "who has reviewed the application, who ordered the reports … what has
// already been done". The server (getApplicantActivity) aggregates only rows
// whose actor cannot be forged and gates the read itself (hr module / admin, or
// a managing partner working inside this app); the SAME renderer paints the
// partner portal (window.PersonSections.renderApplicantActivity — Pitfall #8).
// data-no-print: internal review trail, never part of the printed §391.21 form.
// ============================================================================
function buildCandidateActivitySection(c) {
  const cid = escapeHtml(String((c && c.id) || ''));
  return _candCard({
    icon: 'history',
    title: 'Activity',
    attrs: 'data-no-print',
    sub: 'Who opened this application and what has been done on it.',
    body: `<div id="candidate-activity-body" data-cand-id="${cid}">${window.PersonSections.renderApplicantActivity(null)}</div>`,
  });
}

let _candActivitySeq = 0;
async function loadCandidateActivity(candidateId, opts = {}) {
  const el = document.getElementById('candidate-activity-body');
  if (!el || !candidateId || !HRState.tenantId) return;
  const seq = ++_candActivitySeq;
  const tenantAtCall = HRState.tenantId;
  let state;
  try {
    const fn = httpsCallable(functions, 'getApplicantActivity');
    const res = await fn({ tenantId: tenantAtCall, candidateId, markViewed: opts.markViewed === true });
    state = (res && res.data && res.data.ok) ? res.data : { error: true };
  } catch (err) {
    console.warn('[HR] applicant activity load failed:', err?.code || err?.message || 'unknown');
    state = { error: true };
  }
  // A newer open (or a tenant switch) owns the slot now — paint nothing.
  if (seq !== _candActivitySeq || HRState.tenantId !== tenantAtCall) return;
  const live = document.getElementById('candidate-activity-body');
  if (!live || live.getAttribute('data-cand-id') !== String(candidateId)) return;
  live.innerHTML = window.PersonSections.renderApplicantActivity(state, {
    fmtWhen: (ms) => new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }),
  });
}

// TASK-1213 CORE — every status change writes a feed entry, e.g.
//   Status: New → Phone Screen — "sounded great, wants night shift"
// Rejection reason + the TASK-1212 potentials note ride in `detail`.
// Best-effort by design: a feed hiccup must never block/undo the status write.
async function _logCandidateStatusFeedEntry(candidateId, fromStatus, toStatus, opts = {}) {
  try {
    const label = (s) => (PIPELINE_STAGES.find(x => x.id === s)?.label) || s || '—';
    let body = `Status: ${label(fromStatus)} → ${label(toStatus)}`;
    if (opts.detail) body += ` — ${opts.detail}`;
    if (opts.note) body += ` — "${opts.note}"`;
    if (opts.undo) body += ' (undo)';
    await _writeCandidateFeedDoc(candidateId, {
      body, type: 'status',
      fromStatus: fromStatus || null, toStatus: toStatus || null,
      note: opts.note || null,
    });
    _renderCandidateNotesFeed(candidateId);
  } catch (e) {
    console.warn('[HR] status feed entry failed (status change already saved):', e?.message);
  }
}

// ============================================================================
// TASK-4550 — SEND AN APPLICATION BACK / CORRECT ONE WITHOUT A ROUND TRIP
// ----------------------------------------------------------------------------
// Kolsen + TITS, same day, twice: *"Add an option to send the application back
// to the candidate for an alteration. Example, driver puts 'Workin' as the
// employer name for 15 years… We need the option to add a NOTE."*
//
// Two halves, and the split is the customer's own line: a 15-year "Workin" needs
// the DRIVER (Send back), a misspelled street does not (Correct).
//
// 🔴 §391.21 — the application's certification attests to what the APPLICANT
// submitted, so neither half may silently rewrite signed content. Both routes go
// through server callables that (a) freeze the original submission once, before
// the first divergence, (b) record who/what/when/why, and (c) in the send-back
// case require the applicant's OWN new signature before the amendment counts.
// Nothing here writes the candidate doc directly — the direct-write path is
// exactly the attestation hole this task exists to close.
// ============================================================================

/** Corrections recorded against one field path, oldest first. */
function _hrCorrectionsFor(c, path) {
  const all = Array.isArray(c && c.applicationCorrections) ? c.applicationCorrections : [];
  return all.filter(r => r && r.path === path);
}

/**
 * The "Corrected by staff" footnote. Same shape, weight and degradation rules as
 * the `attestationReason` line on a background-report card (hr.js ~:22915):
 * fixed sentence, visually subordinate, who and when degrade independently,
 * empty input renders NOTHING (a "not corrected" placeholder on every field
 * would bury the handful that were).
 */
function _hrCorrectionFootnote(c, path) {
  const rows = _hrCorrectionsFor(c, path);
  if (!rows.length) return '';
  return rows.map((r) => {
    const when = _hrFmtAttestDate(r.at);
    const before = String(r.before || '');
    return `<div style="font-size:0.68rem;color:var(--text-secondary);line-height:1.4;border-top:1px dashed #F59E0B55;padding-top:5px;margin-top:5px;">`
      + `Corrected by staff${before ? ` from &ldquo;${escapeHtml(before)}&rdquo;` : ''}: ${escapeHtml(String(r.reason || ''))}`
      + `${r.byName ? ` &mdash; ${escapeHtml(String(r.byName))}` : ''}${when ? `, ${escapeHtml(when)}` : ''}</div>`;
  }).join('');
}

/**
 * Application-integrity card: is this application out with the applicant right
 * now, what were they asked to fix, what came back, and what did staff change by
 * hand. Rendered ONLY when there is something to say — a card that is always
 * present and usually empty trains people to skip it.
 */
function buildCandidateApplicationIntegritySection(c) {
  const retStatus = c.applicationReturnStatus || '';
  const ret = c.applicationReturn || {};
  const amendments = Array.isArray(c.applicationAmendments) ? c.applicationAmendments : [];
  const corrections = Array.isArray(c.applicationCorrections) ? c.applicationCorrections : [];
  if (!retStatus && !amendments.length && !corrections.length && !c.applicationOriginal) return '';

  let banner = '';
  if (retStatus === 'returned') {
    const when = _hrFmtAttestDate(ret.requestedAt);
    // The delivery line is the honest one: emailed:false means nobody told them.
    // A send-back that silently never arrives is worse than no send-back, so the
    // failure is stated in the card, not buried in a toast that already closed.
    const delivery = ret.emailed
      ? `<div style="font-size:0.75rem;color:#166534;margin-top:6px;">Emailed to ${escapeHtml(String(ret.emailedTo || ''))}.</div>`
      : `<div style="font-size:0.78rem;color:#991B1B;background:#FEF2F2;border:1px solid #FECACA;border-radius:6px;padding:8px 10px;margin-top:8px;"><strong>The applicant was not notified.</strong> ${escapeHtml(String(ret.emailError || 'No email address on file.'))} Their application is open — call them and ask them to go to your application page, tap <em>Continue Your Application</em> and enter their SSN and date of birth.</div>`;
    banner = `
      <div style="background:#FFF7ED;border:1px solid #FDBA74;border-left:4px solid #F59E0B;border-radius:8px;padding:12px 14px;">
        <div style="font-size:0.72rem;letter-spacing:.05em;text-transform:uppercase;color:#92400E;font-weight:700;">Sent back to the applicant &mdash; waiting on them</div>
        <div style="color:var(--text-primary);font-size:0.9rem;line-height:1.5;white-space:pre-wrap;margin-top:6px;">${escapeHtml(String(ret.note || ''))}</div>
        ${Array.isArray(ret.fields) && ret.fields.length ? `<div style="font-size:0.75rem;color:#7C2D12;margin-top:6px;">Sections flagged: <strong>${escapeHtml(ret.fields.join(', '))}</strong></div>` : ''}
        <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:6px;">Requested by ${escapeHtml(String(ret.requestedByName || 'staff'))}${when ? `, ${escapeHtml(when)}` : ''}${ret.round ? ` &middot; round ${escapeHtml(String(ret.round))}` : ''}</div>
        ${delivery}
      </div>`;
  } else if (retStatus === 'resubmitted') {
    const when = _hrFmtAttestDate(c.applicationResubmittedAt);
    banner = `
      <div style="background:#ECFDF5;border:1px solid #6EE7B7;border-left:4px solid #10B981;border-radius:8px;padding:12px 14px;">
        <div style="font-size:0.72rem;letter-spacing:.05em;text-transform:uppercase;color:#065F46;font-weight:700;">Corrections received</div>
        <div style="font-size:0.82rem;color:var(--text-primary);margin-top:4px;">The applicant re-submitted${when ? ` on ${escapeHtml(when)}` : ''} and signed the application again.</div>
      </div>`;
  }

  const amendRows = amendments.length ? `
    ${_candSubhead('history', `Amendment history (${amendments.length})`)}
    <div>
      ${amendments.map((a) => {
        const rq = _hrFmtAttestDate(a.returnedAt);
        const rs = _hrFmtAttestDate(a.resubmittedAt);
        return `<div class="cand-row">
          <div style="font-size:0.8rem;color:var(--text-primary);">Round ${escapeHtml(String(a.round || 1))}: ${escapeHtml(String(a.note || ''))}</div>
          <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">Sent back by ${escapeHtml(String(a.returnedBy || 'staff'))}${rq ? `, ${escapeHtml(rq)}` : ''} &middot; re-signed and returned${rs ? ` ${escapeHtml(rs)}` : ''}</div>
        </div>`;
      }).join('')}
    </div>` : '';

  const corrRows = corrections.length ? `
    ${_candSubhead('edit_note', `Staff corrections (${corrections.length})`)}
    <div>
      ${corrections.map((r) => {
        const when = _hrFmtAttestDate(r.at);
        return `<div class="cand-row">
          <div style="font-size:0.8rem;color:var(--text-primary);"><strong>${escapeHtml(String(r.label || r.path || ''))}</strong>: &ldquo;${escapeHtml(String(r.before || ''))}&rdquo; &rarr; &ldquo;${escapeHtml(String(r.after || ''))}&rdquo;</div>
          <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">${escapeHtml(String(r.reason || ''))}${r.byName ? ` &mdash; ${escapeHtml(String(r.byName))}` : ''}${when ? `, ${escapeHtml(when)}` : ''}</div>
        </div>`;
      }).join('')}
    </div>` : '';

  const originalNote = c.applicationOriginal ? `
    <div style="font-size:0.72rem;color:var(--text-secondary);padding:8px 0 0;">
      The application exactly as the applicant signed it is preserved on this record and is what the DQ file falls back to. Nothing above overwrote it.
    </div>` : '';

  return _candCard({
    icon: 'fact_check',
    title: 'Application Integrity',
    note: '&mdash; §391.21 record of every change since the applicant signed',
    body: `${banner}${amendRows}${corrRows}${originalNote}`,
  });
}

// ── TASK-9444 — THINGS TO VERIFY (automatic application audit) ─────────────
//
// `applicationAudit` is computed SERVER-SIDE at submit by saveDriverApplication
// (functions/src/services/applicationAudit.js) — deterministic rules, no AI.
// `riskFlags` are the address-verification warnings apply.html records; they
// were saved on every application and rendered nowhere until this card.
// Both live on the candidate doc, which only admins, the managing partner and
// HR-module holders can read, so this card needs no gate of its own.
// ADVISORY ONLY: nothing here blocks hiring, changes a stage or rejects anyone.
// It is a list of facts to check, never a score or a recommendation.
const _HR_AUDIT_SUPPRESSED_CODES = ['fast_completion'];
function _hrApplicationAuditItems(c) {
  const a = c && c.applicationAudit;
  // TASK-9444 — `fast_completion` is switched off server-side (it under-measured:
  // createdAt is stamped after step 1); audits stored before that still carry it,
  // so it is hidden here too. Mirrors SUPPRESSED_CODES in applicationAudit.js.
  const items = (a && Array.isArray(a.items))
    ? a.items.filter(i => i && typeof i.message === 'string' && i.message.trim() && !_HR_AUDIT_SUPPRESSED_CODES.includes(i.code))
    : [];
  const addr = (c && Array.isArray(c.riskFlags)) ? c.riskFlags.filter(f => typeof f === 'string' && f.trim()).slice(0, 10).map(f => f.slice(0, 300)) : [];
  return {
    items, addr, live: _hrApplicationAuditLiveItems(c),
    computedAt: a && a.computedAt ? a.computedAt : null,
    backfilled: !!(a && a.backfilled === true),
  };
}

// TASK-9444 slice 2 — checks 2 + 3, evaluated NOW (they depend on events after
// submit). Inputs are the two listeners this screen already runs: the
// peopleBackgroundChecks map (bg decision) and the candidateFlags ledger. Both
// are tenant-guarded; an unloaded bg map is passed as bgKnown:false so check 2
// stays silent instead of claiming "no decision". Advisory only, like slice 1.
function _hrApplicationAuditLiveItems(c) {
  const L = window.ApplicationAuditLive;
  if (!L || !c || !c.id) return [];
  try {
    const sameTenant = !!HRState._bgLoadedTid && HRState._bgLoadedTid === HRState.tenantId;
    const PS = window.PersonSections;
    return L.computeLiveItems(c, {
      bgRecord: sameTenant ? (HRState.bgRecordsByCandidateId?.[c.id] || null) : null,
      bgKnown: sameTenant,
      activeFlags: _hrCandidateFlags(c.id),
      labelFor: PS && PS.candidateFlagTypeLabel ? PS.candidateFlagTypeLabel : null,
      fmtDate: (ms) => _hrFmtAttestDate(ms),
    }).filter(i => i && typeof i.message === 'string' && i.message.trim());
  } catch (e) {
    console.warn('[HR] live application audit skipped:', e?.message || 'unknown');
    return [];
  }
}

function _hrRepaintOpenCandidateAudit() {
  const slot = document.getElementById('cand-audit-slot');
  if (!slot) return;
  const cid = slot.getAttribute('data-cand-id');
  const c = cid ? (HRState.candidates || []).find(x => x && x.id === cid) : null;
  if (c) slot.innerHTML = buildCandidateApplicationAuditSection(c);
}

function _hrApplicationAuditChip(c) {
  const { items, addr, live } = _hrApplicationAuditItems(c);
  const n = items.length + addr.length + live.length;
  if (!n) return '';
  const title = live.concat(items).map(i => '• ' + i.message).concat(addr.map(f => '• ' + f)).join('\n');
  return `<span class="candidate-status-badge" style="margin-left:6px;background:#FEF3C7;color:#78350F;font-weight:600;" title="${escapeHtml(title)}">🔎 ${n} to verify</span>`;
}

function buildCandidateApplicationAuditSection(c) {
  const { items, addr, live, computedAt, backfilled } = _hrApplicationAuditItems(c);
  if (!items.length && !addr.length && !live.length) return '';
  const row = (text) => `<div class="cand-row" style="display:flex;gap:8px;align-items:flex-start;">
      <span class="material-icons" style="font-size:16px;color:#B45309;margin-top:1px;">info</span>
      <div style="font-size:0.84rem;color:var(--text-primary);line-height:1.45;">${escapeHtml(text)}</div>
    </div>`;
  // TASK-9444 slice 2 — live checks first: they are about what happened since.
  const liveRows = live.length
    ? `${_candSubhead('update', 'Since the application was submitted', 'checked each time this opens')}${live.map(i => row(i.message)).join('')}${items.length ? _candSubhead('assignment', 'On the application', '') : ''}`
    : '';
  const auditRows = liveRows + items.map(i => row(i.message)).join('');
  const addrRows = addr.length
    // riskFlags are written by the apply form in the applicant's browser, not by
    // the server (COD r1 Adversary) — labelled as such.
    ? `${_candSubhead('place', 'Address check', 'recorded by the application form')}${addr.map(row).join('')}`
    : '';
  const when = computedAt ? _hrFmtAttestDate(computedAt) : '';
  const foot = `<div style="font-size:0.72rem;color:var(--text-secondary);line-height:1.45;">
      ${items.length || addr.length ? (backfilled
        ? `Application checks were run later, against the application as submitted${when ? ` (${escapeHtml(when)})` : ''}. `
        : `Application checks ran automatically when it was submitted${when ? ` (${escapeHtml(when)})` : ''}. `) : ''}${items.length ? 'Employer names in quotes are as the applicant typed them. ' : ''}These are facts to confirm, not a hiring recommendation, and they never change the applicant&rsquo;s stage. If something here leads you to decline because of a background report, use Reject&hellip; so the FCRA adverse-action steps run.
    </div>`;
  return _candCard({
    icon: 'fact_check',
    title: 'Things to Verify',
    note: `&mdash; ${items.length + addr.length + live.length} item${items.length + addr.length + live.length === 1 ? '' : 's'}`,
    attrs: 'data-no-print',
    body: `${auditRows}${addrRows}`,
    foot,
  });
}

// TASK-9444 slice 3b — THE PRINTED REVIEWER PAGE. Same facts as the on-screen
// Things to Verify card (stored audit + live checks + form checks, suppressed
// codes hidden), as a plain separate page that starts on its own sheet and says,
// on the page itself, that it is not part of the applicant's application.
// Only printCandidateApplication(id, { reviewer: true }) adds it.
function _hrAuditReviewerCount(c) {
  if (!c) return 0;
  const { items, addr, live } = _hrApplicationAuditItems(c);
  return items.length + addr.length + live.length;
}
function _hrAuditReviewerPrintHtml(c) {
  if (!c) return '';
  const { items, addr, live, computedAt, backfilled } = _hrApplicationAuditItems(c);
  if (!items.length && !addr.length && !live.length) return '';
  const li = (t) => `<li style="margin:0 0 6px;">${escapeHtml(t)}</li>`;
  const block = (title, rows) => rows.length
    ? `<div style="font-size:11pt;font-weight:700;margin:12px 0 4px;">${escapeHtml(title)}</div><ul style="margin:0;padding-left:18px;font-size:10.5pt;line-height:1.45;">${rows.join('')}</ul>`
    : '';
  const when = computedAt ? _hrFmtAttestDate(computedAt) : '';
  const name = String(c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || 'Applicant');
  return `<div class="cand-reviewer-print" style="break-before:page;page-break-before:always;padding-top:0.5in;font-family:Arial,Helvetica,sans-serif;color:#111;">
      <div style="border:2px solid #111;padding:8px 10px;margin-bottom:10px;">
        <div style="font-size:13pt;font-weight:700;">Reviewer notes: Things to Verify</div>
        <div style="font-size:10pt;margin-top:2px;">${escapeHtml(name)} · printed ${escapeHtml(new Date().toLocaleDateString())}</div>
        <div style="font-size:9.5pt;margin-top:6px;line-height:1.4;">For the hiring reviewer only. This page is NOT part of the applicant's employment application (49 CFR §391.21). Do not give it to the applicant and do not file it as the application.</div>
      </div>
      ${block('Since the application was submitted', live.map(i => li(i.message)))}
      ${block('On the application', items.map(i => li(i.message)))}
      ${block('Form checks (recorded by the application form)', addr.map(li))}
      <div style="font-size:9pt;margin-top:12px;line-height:1.4;color:#333;">${items.length || addr.length ? escapeHtml((backfilled ? 'Application checks were run later, against the application as submitted' : 'Application checks ran automatically when it was submitted') + (when ? ` (${when})` : '') + '. ') : ''}These are facts to confirm, not a hiring recommendation, and they never change the applicant's stage. A short list is not clearance; every required check still applies. A decline based on a background report goes through Reject, so the FCRA adverse-action steps run.</div>
      <div style="font-size:10pt;font-weight:700;margin-top:14px;border-top:1px solid #111;padding-top:6px;">REVIEWER COPY: remove this page before filing the application or giving a copy to the applicant.</div>
    </div>`;
}

// ── TASK-9761 C — DUPLICATE APPLICATION BANNER ─────────────────────────────
// The match is the SERVER's (findDuplicateApplications: same ssnHash AND dobHash);
// this only renders the ids + display fields it returns. Archiving a copy goes
// through archiveDuplicateApplication, which re-checks the match on fresh reads.
// TASK-9976 — the candidate doc no longer carries ssnHash (it moved to a server-only
// doc: an unsalted SSN hash IS the SSN). The applicant-typed SSN still leaves its
// masked last 4 (`ssnMasked`) on the doc, so that is the "an SSN is on file" hint.
// A legacy doc (not yet migrated) may still show ssnHash. Hint only: the server decides.
function _hrApplicantSsnOnFile(c) {
  if (!c) return false;
  if (typeof c.ssnHash === 'string' && c.ssnHash) return true;
  return /^\*{3}-\*{2}-\d{4}$/.test(String(c.ssnMasked || ''));
}

async function _hrLoadDuplicateBanner(c) {
  if (!c || !c.id || !document.getElementById('cand-dup-slot')) return;
  // A hand-entered candidate (no SSN on file) cannot match anything — skip the call.
  if (!_hrApplicantSsnOnFile(c) && c.archivedReason !== 'duplicate') return;
  let res = null;
  try {
    const fn = httpsCallable(functions, 'findDuplicateApplications');
    res = (await fn({ tenantId: HRState.tenantId, candidateId: c.id })).data || {};
  } catch (e) {
    console.warn('[HR] duplicate check skipped:', e?.code || e?.message || 'unknown');
    return;
  }
  const slot = document.getElementById('cand-dup-slot');
  if (!slot || slot.getAttribute('data-cand-id') !== c.id) return; // another applicant is open now
  slot.innerHTML = buildCandidateDuplicateBanner(c, res);
  if (!slot._dupWired) {
    slot._dupWired = true;
    slot.addEventListener('click', (e) => {
      const open = e.target.closest('[data-dup-open]');
      if (open) { _hrOpenDuplicate(open.getAttribute('data-dup-open')); return; }
      const arch = e.target.closest('[data-dup-archive-keep]');
      if (arch) _hrArchiveAsDuplicate(slot.getAttribute('data-cand-id'), arch.getAttribute('data-dup-archive-keep'), arch);
    });
  }
}

function _hrDupLine(s) {
  const when = s.submittedAt ? `submitted ${formatDateShort(s.submittedAt)}` : (s.createdAt ? `started ${formatDateShort(s.createdAt)}, not submitted` : '');
  const bits = [s.name || 'Unnamed applicant', when, s.status ? _hrDupStatusLabel(s.status) : ''].filter(Boolean);
  return escapeHtml(bits.join(' · '));
}
function _hrDupStatusLabel(st) {
  const k = String(st || '').toLowerCase();
  return k ? k.charAt(0).toUpperCase() + k.slice(1) : '';
}

function buildCandidateDuplicateBanner(c, res) {
  const r = res || {};
  const dups = Array.isArray(r.duplicates) ? r.duplicates.filter((s) => s && typeof s.candidateId === 'string') : [];
  const of = r.duplicateOf && typeof r.duplicateOf.candidateId === 'string' ? r.duplicateOf : null;
  if (!dups.length && !of) return '';
  const btn = (attrs, icon, label, title) => `<button type="button" class="btn btn-secondary btn-sm" ${attrs} title="${escapeHtml(title)}" style="min-height:36px;"><span class="material-icons" style="font-size:16px;">${icon}</span> ${escapeHtml(label)}</button>`;
  const archived = !!(c && (c.archived === true || c.archivedAt));
  const canArchiveThis = !archived && String(c?.status || '').toLowerCase() !== 'hired';
  const rows = dups.map((s) => `<div class="cand-row" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
      <div style="flex:1;min-width:180px;font-size:0.84rem;color:var(--text-primary);line-height:1.45;">${_hrDupLine(s)}</div>
      ${btn(`data-dup-open="${escapeHtml(s.candidateId)}"`, 'open_in_new', 'Open', 'Open the other application')}
      ${canArchiveThis ? btn(`data-dup-archive-keep="${escapeHtml(s.candidateId)}"`, 'inventory_2', 'Keep that one, archive this', 'Archive THIS application as a duplicate of that one. Nothing is deleted; Restore brings it back.') : ''}
    </div>`).join('');
  const ofRow = of ? `<div class="cand-row" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
      <div style="flex:1;min-width:180px;font-size:0.84rem;color:var(--text-primary);line-height:1.45;">${of.missing ? 'Archived as a duplicate of an application that is no longer on file.' : `Archived as a duplicate of: ${_hrDupLine(of)}`}</div>
      ${of.missing ? '' : btn(`data-dup-open="${escapeHtml(of.candidateId)}"`, 'open_in_new', 'Open', 'Open the application this one was archived in favour of')}
    </div>` : '';
  return _candCard({
    icon: 'content_copy',
    title: dups.length ? 'Another application from this person' : 'Duplicate',
    note: dups.length ? `&mdash; same SSN and date of birth` : '',
    attrs: 'data-no-print',
    body: `${ofRow}${rows}`,
    foot: dups.length ? 'Work from one file. If the applicant needs to add something to the signed application, use Send Back on that one rather than keeping both.' : '',
  });
}

async function _hrOpenDuplicate(id) {
  if (!id) return;
  try { closeModal('view-candidate'); } catch (_) {}
  await openCandidateApplication(id);
}

async function _hrArchiveAsDuplicate(candidateId, keepCandidateId, btnEl) {
  if (!candidateId || !keepCandidateId) return;
  const go = await window.showConfirm(
    'Archive this application as a duplicate?',
    'This copy moves to Archived and the other application from the same person stays where it is. Nothing is deleted, and Restore brings it back.',
    { confirmText: 'Archive duplicate', cancelText: 'Cancel' },
  );
  if (!go) return;
  if (btnEl) btnEl.disabled = true;
  try {
    const fn = httpsCallable(functions, 'archiveDuplicateApplication');
    await fn({ tenantId: HRState.tenantId, candidateId, keepCandidateId });
    window.showToast?.('Archived as a duplicate.', 'success');
    try { closeModal('view-candidate'); } catch (_) {}
  } catch (e) {
    if (btnEl) btnEl.disabled = false;
    window.showToast?.(e?.message || 'Could not archive this application.', 'error');
  }
}

// ── SEND BACK ───────────────────────────────────────────────────────────────

// Deliberately coarse, and phrased the way a dispatcher would say it out loud.
// These become the "sections flagged" line in the applicant's email; the NOTE is
// what actually tells them what to do.
// 🔴 These EXACT strings are also the keys of `_SECTION_TO_STEP` in
// public/apply.html — they decide which step the returned applicant lands on.
// Change one and the applicant silently lands on the default step instead of the
// one you flagged. CHANGE BOTH.
const _SEND_BACK_SECTIONS = [
  'Employment history',
  'Contact information',
  'Address history',
  'Driver license details',
  'Accidents / violations',
  'Driving experience',
  'Uploaded documents',
];

function getSendBackModalHTML(c) {
  const _escA = window.escAttr || escapeHtml;
  const hasEmail = !!(c.email && String(c.email).includes('@'));
  return `
    <div class="modal" id="modal-send-back" style="z-index:1050;">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Send back for correction &mdash; ${escapeHtml(c.name || '')}</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('send-back')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <div style="font-size:0.82rem;color:var(--text-secondary);margin-bottom:12px;line-height:1.5;">
            This reopens the application so the applicant can fix it themselves. Their original submission is kept exactly as they signed it, and they have to sign again before the correction counts.
          </div>
          <div class="form-group">
            <label>What do they need to fix? <span style="color:#DC2626;">*</span></label>
            <textarea id="send-back-note" rows="4" maxlength="2000" placeholder="e.g. &quot;Workin&quot; is not a valid business name — please list your actual work history for the last 10 years, with real company names and phone numbers."></textarea>
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">They see this word for word. Say what is wrong and what you want instead.</div>
          </div>
          <div class="form-group">
            <label>Sections to flag (optional)</label>
            <div style="display:flex;flex-wrap:wrap;gap:6px 14px;">
              ${_SEND_BACK_SECTIONS.map((s, i) => `<label class="radio-label" style="font-size:0.82rem;"><input type="checkbox" class="send-back-field" id="send-back-field-${i}" value="${_escA(s)}"> ${escapeHtml(s)}</label>`).join('')}
            </div>
          </div>
          ${hasEmail
            ? `<div style="font-size:0.78rem;color:#065F46;background:#ECFDF5;border:1px solid #A7F3D0;border-radius:6px;padding:8px 10px;">Emails <strong>${escapeHtml(c.email)}</strong> with your note and how to get back in (SSN + date of birth &mdash; no link needed).</div>`
            : `<div style="font-size:0.78rem;color:#92400E;background:#FFFBEB;border:1px solid #FDE68A;border-radius:6px;padding:8px 10px;">No email on file for this applicant. The application will be reopened, but <strong>nobody will be told</strong> &mdash; you will have to call them.</div>`}
          <input type="hidden" id="send-back-candidate-id" value="${_escA(c.id)}">
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.HRModule.closeModal('send-back')">Cancel</button>
          <button class="btn btn-primary" id="send-back-save-btn" onclick="window.HRModule.submitSendBack()">
            <span class="material-icons">reply</span> Send back
          </button>
        </div>
      </div>
    </div>`;
}

function openSendBackModal(id) {
  const candidate = HRState.candidates.find(c => c.id === id);
  if (!candidate) return;
  if (_hrArchivedStageBlock(candidate)) return; // TASK-9419
  if (candidate.applicationComplete !== true) {
    window.showToast?.('This application has not been submitted yet — the applicant can still edit it themselves.', 'info');
    return;
  }
  try { closeModal('view-candidate'); } catch (_) {}
  document.getElementById('modal-send-back')?.remove();
  document.body.insertAdjacentHTML('beforeend', getSendBackModalHTML(candidate));
  document.getElementById('modal-send-back').classList.remove('hidden');
  setTimeout(() => document.getElementById('send-back-note')?.focus(), 60);
}

async function submitSendBack() {
  const id = document.getElementById('send-back-candidate-id')?.value;
  const note = (document.getElementById('send-back-note')?.value || '').trim();
  if (!id) return;
  if (note.length < 5) {
    window.showToast?.('Tell the applicant what to fix — the note is the whole point.', 'error');
    document.getElementById('send-back-note')?.focus();
    return;
  }
  const fields = Array.from(document.querySelectorAll('.send-back-field'))
    .filter(el => el.checked).map(el => el.value);
  const btn = document.getElementById('send-back-save-btn');
  const restore = () => { if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">reply</span> Send back'; } };
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Sending...'; }
  try {
    const fn = httpsCallable(functions, 'returnApplicationToApplicant');
    const res = await fn({ tenantId: HRState.tenantId, candidateId: id, note, fields });
    closeModal('send-back');
    if (res.data?.emailed) {
      window.showToast?.(`Sent back — emailed ${res.data.emailedTo}`, 'success');
    } else {
      // Never claim delivery we did not get. This is the iOS-link lesson applied
      // to email: a channel that failed has to say so where the user is looking.
      window.showConfirm?.({
        icon: 'warning',
        title: 'Reopened, but not delivered',
        message: res.data?.warning || 'The applicant was not notified. Contact them yourself.',
        confirmText: 'Got it',
      }) || window.showToast?.(res.data?.warning || 'Reopened, but the applicant was not notified.', 'error');
    }
    try { window.logAudit?.('hr', 'APPLICATION_SENT_BACK', { candidateId: id, fields }); } catch (_) {}
  } catch (err) {
    console.error('[HR] send back failed:', err);
    const msg = String(err?.code || '').startsWith('functions/') ? (err?.message || '') : '';
    window.showToast?.(msg || 'Could not send the application back', 'error');
    restore();
  }
}

// ── TASK-9530 — EMAIL THE CANDIDATE A LINK TO FINISH THEIR OWN APPLICATION ──
// The server (sendApplicationLink) is the gate: owner, or the hr / compliance
// module, never view/upload-only. The link goes only to the email on file, is
// never shown here, and the applicant signs everything themselves.
function _hrCanSendApplicationLink(c) {
  if (!c || c.applicationComplete === true || isArchivedCandidate(c)) return false;
  // TASK-9692 (c) — mirror of the server: a draft with the applicant's SSN reopens only by
  // SSN + date of birth, so a link would open nothing and just lock the email.
  // TASK-9976 — ssnHash is server-only now; the masked last 4 is the client's hint.
  if (_hrApplicantSsnOnFile(c) && c.noUsSsn !== true) return false;
  const s = String(c.status || '').toLowerCase();
  return s !== 'rejected' && s !== 'withdrawn';
}

function _hrApplicationInvitePill(c) {
  const inv = c && c.applicationInvite;
  if (!inv || !inv.sentAt) return '';
  const when = _hrFmtAttestDate(inv.sentAt);
  // TASK-9692 (a) — the nightly job clears the lock of an expired link and stamps expiredAt.
  if (inv.expiredAt && !_hrInviteOutstanding(c)) {
    return `<span class="cand-roster-pill" data-app-link-pill style="background:#F3F4F6;color:#374151;border-color:#D1D5DB;" title="${escapeHtml(`The link emailed${when ? ' on ' + when : ''} expired. Send a new one if they still need it.`)}">
    <span class="material-icons">link_off</span> Link expired
  </span>`;
  }
  const opened = !!c.applicationInviteOpenedAt;
  const title = `Link emailed to ${inv.sentTo || 'the candidate'}${when ? ' on ' + when : ''}${inv.sentByName ? ' by ' + inv.sentByName : ''}${opened ? ' — they have opened it' : ''}`;
  return `<span class="cand-roster-pill" data-app-link-pill style="background:#EFF6FF;color:#1E3A8A;border-color:#BFDBFE;" title="${escapeHtml(title)}">
    <span class="material-icons">${opened ? 'drafts' : 'mark_email_unread'}</span> ${opened ? 'Link opened' : 'Link emailed'}${when ? ' ' + escapeHtml(when) : ''}
  </span>`;
}

// TASK-9692 — "Reminder 1/2 sent {date}" on an unsubmitted application. nudgesSent is written
// only by the nightly reminder pass (Admin SDK; firestore.rules fence it), nudgeOptOut only by
// the applicant's "Stop reminder emails" link. Display only — dates via _hrFmtAttestDate.
function _hrNudgeChip(c) {
  if (!c || c.applicationComplete === true) return '';
  const sent = Array.isArray(c.nudgesSent) ? c.nudgesSent.filter(x => x && x.at) : [];
  if (!sent.length && !c.nudgeOptOut) return '';
  const last = sent.length ? sent[sent.length - 1] : null;
  const when = last ? _hrFmtAttestDate(last.at) : '';
  const stopped = !!c.nudgeOptOut;
  const label = stopped ? 'Reminders stopped' : `Reminder ${Math.min(sent.length, 2)}/2 sent${when ? ' ' + when : ''}`;
  const title = stopped
    ? 'The applicant asked for no more reminder emails'
    : `We emailed this applicant a reminder to finish their application${when ? ' on ' + when : ''}. At most 2 are sent.`;
  return `<span class="hiring-nudge-chip" data-nudge-chip style="display:inline-flex;align-items:center;gap:3px;padding:1px 6px;margin-left:4px;font-size:0.68rem;background:${stopped ? '#F3F4F6' : '#EFF6FF'};color:${stopped ? '#374151' : '#1E3A8A'};border-radius:4px;font-weight:600;vertical-align:middle;" title="${escapeHtml(title)}">
    <span class="material-icons" style="font-size:12px;">${stopped ? 'notifications_off' : 'mark_email_unread'}</span>${escapeHtml(label)}
  </span>`;
}

// TASK-9530 (Chris 2026-10-05): while an application link is outstanding the candidate's email is
// LOCKED. firestore.rules refuse the edit; this mirrors the same test so the field is disabled
// instead of failing on save. The pointer (applicationInviteLinkHash) is Admin-SDK-only and stays
// set until the office cancels (or the application is submitted).
function _hrInviteOutstanding(c) {
  return !!(c && typeof c.applicationInviteLinkHash === 'string' && c.applicationInviteLinkHash && c.applicationComplete !== true);
}

async function cancelApplicationLink(id) {
  const c = HRState.candidates.find(x => x.id === id);
  if (!c) return;
  if (!_hrInviteOutstanding(c)) {
    window.showToast?.('There is no outstanding application link to cancel.', 'info');
    return;
  }
  const to = c.applicationInvite && c.applicationInvite.sentTo;
  const go = await window.showConfirm(
    'Cancel the application link?',
    `The link emailed${to ? ' to ' + to : ''} stops working right away${c.applicationInviteOpenedAt ? ', and the session it opened is closed (anything already saved is kept)' : ''}. Then you can change the email and send a new link.`,
    { confirmText: 'Cancel link', cancelText: 'Keep link', icon: 'link_off' }
  );
  if (!go) return;
  const btns = Array.from(document.querySelectorAll(`[data-cancel-app-link="${CSS.escape(id)}"]`));
  btns.forEach(b => { b.disabled = true; });
  try {
    const fn = httpsCallable(functions, 'cancelApplicationLink');
    await fn({ tenantId: HRState.tenantId, candidateId: id });
    // The snapshot listener is the source of truth; unlock the open form right now.
    c.applicationInviteLinkHash = null;
    delete c.applicationInvite;
    delete c.applicationInviteOpenedAt;
    const em = document.getElementById('candidate-email');
    if (em && HRState.editingCandidateId === id) em.disabled = false;
    document.getElementById('candidate-email-lock')?.remove();
    document.querySelectorAll('[data-app-link-pill]').forEach(p => p.remove());
    btns.forEach(b => b.remove());
    window.showToast?.('Application link cancelled. You can change the email and send a new link.', 'success');
  } catch (err) {
    console.error('[HR] cancel application link failed:', err);
    const msg = String(err?.code || '').startsWith('functions/') ? (err?.message || '') : '';
    window.showToast?.(msg || 'Could not cancel the application link', 'error');
    btns.forEach(b => { b.disabled = false; });
  }
}

// TASK-9891 follow-up — "The link works for 3 days." from the owner's setting
// (settings/linkSecurity.applyInviteLinkHours; range 72h-336h, default 168h —
// mirrors LINK_KINDS.applyInvite in functions/src/services/linkExpiry.js). The
// server reads the same doc at send; this is display only. A failed read keeps
// the old "7 days unless…" wording.
async function _hrApplyInviteLinkLine() {
  const fallback = 'The link works for 7 days unless the account owner changed it in Settings → Security.';
  const tid = HRState.tenantId || window.AppState?.tenant;
  if (!tid) return fallback;
  try {
    const snap = await getDoc(doc(db, 'tenants', tid, 'settings', 'linkSecurity'));
    const raw = snap.exists() ? (snap.data() || {}).applyInviteLinkHours : undefined;
    // Same parse as the server's clampLinkHours: a number, or a non-blank string.
    const n = typeof raw === 'number' ? raw : (typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : NaN);
    const hours = Number.isFinite(n) ? Math.min(336, Math.max(72, Math.round(n))) : 168;
    const label = hours % 24 === 0 ? `${hours / 24} days` : `${hours} hours`;
    return `The link works for ${label}. The account owner sets this in Settings → Security.`;
  } catch (e) {
    console.warn('[HR] application link length read failed:', e?.message || e);
    return fallback;
  }
}

async function sendApplicationLink(id) {
  const c = HRState.candidates.find(x => x.id === id);
  if (!c || !_hrCanSendApplicationLink(c)) return;
  const email = String(c.email || '').trim();
  if (!email.includes('@')) {
    window.showToast?.('Add an email address for this candidate first (Edit). The application link is sent by email.', 'error');
    return;
  }
  const again = !!(c.applicationInvite && c.applicationInvite.sentAt);
  const linkLine = await _hrApplyInviteLinkLine();
  const go = await window.showConfirm(
    again ? 'Send a new application link?' : 'Email the application link?',
    `${c.name || 'The candidate'} gets an email at ${email} with a link to finish and sign their own application. ${linkLine}${again ? ' The link sent earlier stops working.' : ''}`,
    { confirmText: 'Send link', cancelText: 'Cancel', icon: 'forward_to_inbox' }
  );
  if (!go) return;
  const btn = document.querySelector(`[data-send-app-link="${CSS.escape(id)}"]`);
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Sending...'; }
  try {
    const fn = httpsCallable(functions, 'sendApplicationLink');
    const res = await fn({ tenantId: HRState.tenantId, candidateId: id });
    if (res.data?.emailed) {
      window.showToast?.(`Application link emailed to ${res.data.sentTo}`, 'success');
    } else {
      // Never claim delivery we did not get.
      window.showToast?.(res.data?.warning || 'The link was not sent.', 'error');
    }
  } catch (err) {
    console.error('[HR] send application link failed:', err);
    const msg = String(err?.code || '').startsWith('functions/') ? (err?.message || '') : '';
    window.showToast?.(msg || 'Could not send the application link', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">forward_to_inbox</span> Resend Application Link'; }
  }
}

// ── STAFF CORRECTION (employment history) ───────────────────────────────────

// Mirrors the SERVER allowlist in functions/src/index.js (_CORRECTABLE_EMPLOYER_KEYS).
// The server is the enforcement; this list only decides what gets a text box.
const _CORRECTABLE_EMPLOYER_FIELDS = [
  { key: 'name', label: 'Employer name' },
  { key: 'position', label: 'Position' },
  { key: 'address', label: 'Street address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'reasonForLeaving', label: 'Reason for leaving' },
];

function getCorrectEmployerModalHTML(c, idx) {
  const _escA = window.escAttr || escapeHtml;
  const e = (Array.isArray(c.employers) ? c.employers : [])[idx] || {};
  return `
    <div class="modal" id="modal-correct-employer" style="z-index:1050;">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Correct a typo &mdash; ${escapeHtml(_empName(e) || 'employer')}</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('correct-employer')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <div style="font-size:0.82rem;color:#92400E;background:#FFFBEB;border:1px solid #FDE68A;border-radius:6px;padding:8px 10px;margin-bottom:12px;line-height:1.5;">
            This is a signed §391.21 record. Use this only for a transcription error you can see is wrong &mdash; a misspelled street, a transposed phone digit. If the <em>substance</em> is wrong (a bogus employer, missing years), <strong>send it back to the applicant</strong> instead. The original stays on file either way.
          </div>
          ${_CORRECTABLE_EMPLOYER_FIELDS.map(f => `
            <div class="form-group">
              <label>${escapeHtml(f.label)}</label>
              <input type="text" class="correct-emp-field" data-field="${_escA(f.key)}" data-label="${_escA(f.label)}" maxlength="500" value="${_escA(String(e[f.key] == null ? '' : e[f.key]))}">
            </div>`).join('')}
          <div class="form-group">
            <label>Why are you changing it? <span style="color:#DC2626;">*</span></label>
            <input type="text" id="correct-emp-reason" maxlength="500" placeholder="e.g. Applicant typed 'Willston' — corrected to 'Williston' per their license.">
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">Recorded permanently against this application with your name.</div>
          </div>
          <input type="hidden" id="correct-emp-candidate-id" value="${_escA(c.id)}">
          <input type="hidden" id="correct-emp-index" value="${_escA(String(idx))}">
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.HRModule.closeModal('correct-employer')">Cancel</button>
          <button class="btn btn-primary" id="correct-emp-save-btn" onclick="window.HRModule.submitEmployerCorrection()">
            <span class="material-icons">save</span> Record correction
          </button>
        </div>
      </div>
    </div>`;
}

function openCorrectEmployerModal(id, idx) {
  const candidate = HRState.candidates.find(c => c.id === id);
  if (!candidate) return;
  // COD Code Reviewer ❌ — mirror the server refusal. A correction is a record
  // ABOUT a signed application; on a draft there is nothing signed to protect
  // and freezing the draft as "the original" would misrepresent the file.
  if (candidate.applicationComplete !== true) {
    window.showToast?.('This application has not been submitted yet — there is nothing signed to correct.', 'info');
    return;
  }
  const i = parseInt(idx, 10);
  if (!Number.isInteger(i) || i < 0 || !Array.isArray(candidate.employers) || !candidate.employers[i]) {
    window.showToast?.('That employment row is no longer on the application — reopen the applicant.', 'error');
    return;
  }
  try { closeModal('view-candidate'); } catch (_) {}
  document.getElementById('modal-correct-employer')?.remove();
  document.body.insertAdjacentHTML('beforeend', getCorrectEmployerModalHTML(candidate, i));
  document.getElementById('modal-correct-employer').classList.remove('hidden');
  setTimeout(() => document.getElementById('correct-emp-reason')?.focus(), 60);
}

async function submitEmployerCorrection() {
  const id = document.getElementById('correct-emp-candidate-id')?.value;
  const idx = parseInt(document.getElementById('correct-emp-index')?.value || '', 10);
  const reason = (document.getElementById('correct-emp-reason')?.value || '').trim();
  if (!id || !Number.isInteger(idx)) return;
  if (reason.length < 5) {
    window.showToast?.('A reason is required — this is a signed record.', 'error');
    document.getElementById('correct-emp-reason')?.focus();
    return;
  }
  const candidate = HRState.candidates.find(c => c.id === id) || {};
  const current = (Array.isArray(candidate.employers) ? candidate.employers : [])[idx] || {};
  const corrections = [];
  document.querySelectorAll('.correct-emp-field').forEach((el) => {
    const key = el.dataset.field;
    const value = String(el.value || '').trim();
    if (String(current[key] == null ? '' : current[key]) === value) return; // unchanged
    corrections.push({ path: `employers[${idx}].${key}`, value, label: `${el.dataset.label} (employer ${idx + 1})` });
  });
  if (!corrections.length) {
    window.showToast?.('Nothing changed.', 'info');
    return;
  }
  const btn = document.getElementById('correct-emp-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Recording...'; }
  try {
    const fn = httpsCallable(functions, 'correctApplicationFields');
    const res = await fn({ tenantId: HRState.tenantId, candidateId: id, corrections, reason });
    closeModal('correct-employer');
    window.showToast?.(`${res.data?.corrected || corrections.length} correction${(res.data?.corrected || 1) === 1 ? '' : 's'} recorded`, 'success');
    try { window.logAudit?.('hr', 'APPLICATION_CORRECTED', { candidateId: id, fields: corrections.map(x => x.path) }); } catch (_) {}
  } catch (err) {
    console.error('[HR] correction failed:', err);
    const msg = String(err?.code || '').startsWith('functions/') ? (err?.message || '') : '';
    window.showToast?.(msg || 'Could not record the correction', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Record correction'; }
  }
}

function getViewCandidateModalHTML(candidate) {
  const c = candidate;
  const stage = stageForCandidate(c); // TASK-1360 — was `|| PIPELINE_STAGES[0]`, which badged drafts "New"
  // TASK-431 — resolve from flat OR nested license so apply.html candidates show CDL.
  const lic = resolveCandidateLicense(c);
  const endorsements = lic.endorsements.length ? lic.endorsements.join(', ') : 'None';
  // escapeHtml: CANDIDATE_SOURCES labels are static, but the `c.source`
  // fallback is a stored candidate-doc value and was previously interpolated raw.
  const source = escapeHtml(CANDIDATE_SOURCES.find(s => s.id === c.source)?.label || c.source || '');
  // TASK-1511 — resolve the Approve-to-Roster gate ONCE per render: the footer uses
  // it twice (the caption and the button itself). Candidate is passed so the SSN
  // (peopleTax) leg is only demanded when this application actually carries one.
  const _apprGate = _hrApproveToRosterGate(c);
  const _apprNote = _apprGate.ok ? '' : _hrApproveToRosterNote(_apprGate);
  // TASK-9425 — archived = read-only: no status pill action, no Reject / Potentials /
  // Send Back / Offer / Approve. Restore is the one way back (banner below).
  const _archived = isArchivedCandidate(c);
  return `
    <div class="modal" id="modal-view-candidate">
      <div class="modal-content modal-large candidate-application-print">
        <!-- v1.15.510 — status pill promoted to modal-header for at-a-glance visibility; opens QuickStatusModal -->
        <!-- v1.15.512 — Phase A: bumped hover affordance, larger chevron, one-time tooltip on first view per session -->
        <div class="modal-header" style="display:flex;align-items:center;gap:var(--spacing-md);flex-wrap:wrap;">
          <h3 style="margin:0;flex:1;min-width:0;">${escapeHtml(c.name || '')}</h3>
          ${_archived ? `
          <span class="candidate-status-pill" title="Archived — restore it to change its status" style="display:inline-flex;align-items:center;gap:6px;padding:6px 12px;background:#E5E7EB;color:#374151;border:1px solid #CBD5E1;border-radius:999px;font-size:0.82rem;font-weight:600;line-height:1;flex-shrink:0;" data-print-keep>
            <span class="material-icons" style="font-size:14px;">inventory_2</span>
            <span>Status: ${escapeHtml(stage.label)} · Archived</span>
          </span>` : `<button type="button" id="candidate-status-pill-${c.id}" class="candidate-status-pill" onclick="window.HRModule.closeModal('view-candidate');window.HRModule.openQuickStatusModal('${c.id}')" title="Change status" style="position:relative;display:inline-flex;align-items:center;gap:6px;padding:6px 12px;background:${stage.color}1a;color:${stage.color};border:1px solid ${stage.color}55;border-radius:999px;font-size:0.82rem;font-weight:600;cursor:pointer;line-height:1;flex-shrink:0;transition:background-color 0.15s ease,box-shadow 0.15s ease;" data-stage-color="${stage.color}" data-print-keep>
            <span class="material-icons" style="font-size:14px;">${stage.icon}</span>
            <span>Status: ${escapeHtml(stage.label)}</span>
            <span class="material-icons" style="font-size:16px;">expand_more</span>
          </button>`}
          <button class="btn-icon" onclick="window.HRModule.closeModal('view-candidate')" style="flex-shrink:0;" data-no-print>
            <span class="material-icons">close</span>
          </button>
        </div>
        <div data-print-only class="candidate-application-print-header" style="display:none;padding:0 var(--spacing-lg);margin-bottom:var(--spacing-md);">
          ${(() => {
            // TASK-1203 Slice B — §391.21(b)(1): the printed application must
            // identify the MOTOR CARRIER by name + address.
            const td = window.AppState?.tenantData || {};
            const carrierName = td.companyName || td.name || '';
            const carrierAddr = td.companyAddresses?.[0]?.address || td.companyAddress || td.address || '';
            return (carrierName || carrierAddr) ? `
            <div style="border-bottom:2px solid #0F172A;padding-bottom:8px;margin-bottom:6px;">
              <div style="font-size:1rem;font-weight:700;color:#0F172A;">${escapeHtml(carrierName)}</div>
              ${carrierAddr ? `<div style="font-size:0.78rem;color:#334155;">${escapeHtml(carrierAddr)}</div>` : ''}
              <div style="font-size:0.72rem;color:#64748b;margin-top:2px;">Application for Employment · 49 CFR §391.21</div>
            </div>` : '';
          })()}
          <div style="font-size:0.75rem;color:#64748b;border-bottom:1px solid #e2e8f0;padding-bottom:8px;">DispatchDeck Application · ${escapeHtml(c.name || '')} · Printed ${new Date().toLocaleString()}</div>
        </div>
        <div class="modal-body">
          ${_archived ? `
          <div data-no-print style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;background:#F1F5F9;border:1px solid #CBD5E1;border-left:5px solid #64748B;border-radius:8px;padding:10px 12px;margin-bottom:var(--spacing-md);color:#0F172A;">
            <span class="material-icons" style="color:#475569;">inventory_2</span>
            <span style="flex:1;min-width:200px;font-size:0.86rem;"><strong>Archived — read-only.</strong> Its status can't be changed and it can't be hired while archived. Restore it to put it back in the pipeline.</span>
            <button class="btn btn-sm" onclick="window.HRModule.closeModal('view-candidate');window.HRModule.restoreCandidate('${escJsAttr(String(c.id || ''))}')" style="background:#fff;border:1.5px solid #475569;color:#0F172A;font-weight:600;">
              <span class="material-icons" style="font-size:16px;">unarchive</span> Restore
            </button>
          </div>` : ''}
          ${''/* TASK-9423 — compliance-partner flags (live; repainted by the candidateFlags listener). */}
          <div id="cand-flag-slot" data-no-print data-cand-id="${escapeHtml(String(c.id || ''))}">${_hrCandidateFlagSectionHTML(c.id)}</div>
          ${c.status === 'rejected' && c.rejectionReason ? `<div style="color:var(--status-danger);font-size:0.85rem;margin-bottom:var(--spacing-md);">Reason: ${escapeHtml(c.rejectionReason)}</div>` : ''}
          ${''/* TASK-1212 — potentials context renders like rejectionReason does */}
          ${c.status === 'potentials' && c.potentialsNote ? `<div style="color:#B45309;font-size:0.85rem;margin-bottom:var(--spacing-md);"><span class="material-icons" style="font-size:15px;vertical-align:-2px;">star</span> Revisit: ${escapeHtml(c.potentialsNote)}</div>` : ''}
          <!-- TASK-1328 / TASK-1366 - no inline grid-template-columns: the
               hardcoded 1fr 1fr overrode BOTH the auto-fit minmax and its mobile
               single-column rule, so the right column was clipped mid-value on a
               430px phone ("Phone (361) 407-...", "CDL Class C"). CSS owns it.
               TASK-1366 — real empty states: "Position —" / "Experience —" /
               "Last Contact —" read as broken data. Each now says what's
               actually missing. formatCandidateDate returns an em-dash for an
               absent date, which _candField normalises to the empty-state copy. -->
          <div class="cand-sec">
            <div class="cand-card"><div class="cand-card-body"><div class="cand-grid">
              ${''/* TASK-4725 — "Applied for" when the applicant picked a job opening on the
                     apply form (the SERVER wrote position from that opening's title). */}
              ${_candField(c.appliedOpeningId ? 'Applied for' : 'Position', escapeHtml(c.position || ''), { empty: 'No position on file' })}
              ${_candField('Phone', c.phone ? escapeHtml(formatPhoneDisplay(c.phone)) : '', { empty: 'No phone' })}
              ${_candField('Email', escapeHtml(c.email || ''), { empty: 'No email' })}
              <!-- TASK-1218 — label truth: a Class D (or any non-A/B/C) license is NOT
                   a CDL. Label those "License" so a non-CDL applicant never reads as
                   "CDL: Class D". A/B/C keep "CDL". Bonus chip: applied-for position
                   is a CDL role but the class on file is non-A/B/C → "No CDL on file". -->
              ${_candField(
                _isCdlClassValue(lic.cdlClass) ? 'CDL' : 'License',
                `${lic.cdlClass ? 'Class ' + escapeHtml(lic.cdlClass) : _candEmpty('None on file')}${endorsements !== 'None' ? ' (' + escapeHtml(endorsements) + ')' : ''}${_noCdlOnFileChip(c)}`
              )}
              ${''/* TASK-4547 — the application has ALWAYS asked for date of birth
                   (apply.html #dob, required, and the resume flow hashes it — an
                   applicant cannot resume without it). It just never rendered
                   here, so whoever reviews the application could not see the one
                   field the applicant was obliged to give. It surfaced only in
                   the Clearinghouse subtitle, the DQ packet, and the EMPLOYEE
                   edit modal after hire. Carrier surface only: the partner portal
                   deliberately never projects SSN or DOB. */}
              ${_candField('Date of Birth', escapeHtml(formatDateShort(c.dob) || ''), { empty: 'Not provided' })}
              ${_candField('Experience', c.yearsExperience != null ? escapeHtml(c.yearsExperience + ' years') : '', { empty: 'Not stated' })}
              ${_candField('Source', source, { empty: 'Unknown' })}
              ${_candField('Applied', formatCandidateDate(c.appliedDate), { empty: 'No date recorded' })}
              ${_candField('Last Contact', formatCandidateDate(c.lastContactDate), { empty: 'Never contacted' })}
              <!-- TASK-1203 Slice B / TASK-1488 — §391.21(b)(2): the SSN renders
                   MASKED by default; the full SSN lives in the candidatesTax
                   vault (admin/hr-directory read-gated, server-only-written) and
                   an authorized viewer reveals it with a deliberate eye-icon
                   click. Every reveal writes an SSN_REVEALED auditLog row; the
                   revealed state is never persisted (re-open = masked again).
                   Applications submitted before the vault existed only have the
                   last-4 mask — the reveal explains that instead of failing. -->
              ${_candField('SSN', _candSsnValueHtml(c), { empty: 'Not provided' })}
            </div></div>
          </div>

          <!-- TASK-4550 — is this application out with the applicant right now,
               and what has changed since they signed it. Renders nothing when
               nothing has changed, so it never becomes noise; first in the body
               because "this record is mid-correction" changes how you read
               everything under it. -->
          ${buildCandidateApplicationIntegritySection(c)}

          <!-- TASK-9761 C — another application from the same person (server-matched
               on SSN + date of birth). Filled after open by _hrLoadDuplicateBanner. -->
          <div id="cand-dup-slot" data-no-print data-cand-id="${escapeHtml(String(c.id || ''))}"></div>

          <!-- TASK-9444 — automatic application audit: facts to verify
               (server-computed at submit) + the address-verification warnings. -->
          <div id="cand-audit-slot" data-no-print data-cand-id="${escapeHtml(String(c.id || ''))}">${buildCandidateApplicationAuditSection(c)}</div>

          <!-- TASK-1219 — upcoming interview callout / schedule affordance -->
          ${buildCandidateInterviewSection(c)}

          <!-- TASK-1213 — Notes & Calls feed (prominent: right under the vitals,
               above the pipeline detail — Chris's complaint was discoverability). -->
          ${buildCandidateNotesSection(c)}

          <!-- TASK-4791 — Activity: who opened this application and what has
               been done on it, carrier and compliance partner alike. -->
          ${buildCandidateActivitySection(c)}

          <!-- TASK-1366 — ONE "Background & Driving Record" card. It absorbs what
               used to be three separate sibling sections about the same subject:
                 • Background Check (the Checkr status + run CTA)
                 • Background Reports (the per-screening MVR/PSP/CDLIS/Clearinghouse grid)
                 • Driving Record — Self-Reported (the applicant's own answers)
               Ordered by authority: verified result first, applicant's own claims
               last, so "which one is the truth" is never a question again.
               TASK-298 role gating + the §382.701/PSP gap note moved verbatim into
               _buildCandidateScreeningsBlock; no gating logic changed. -->
          ${buildCandidateCheckrCard(c)}

          <!-- TASK-235 v2 — Driver License full details -->
          ${buildCandidateLicenseSection(c)}

          <!-- TASK-9699 — License Photo Check (Smart Tools read, reviewer-only, advisory) -->
          ${buildCandidateLicenseReadSection(c)}

          <!-- TASK-235 v2 — Credentials & Authority (med/TWIC/passport/DOT modes) -->
          ${buildCandidateCredentialsSection(c)}

          <!-- TASK-219 Slice 3 — custom carrier-question answers -->
          ${buildCandidateCustomAnswersSection(c)}

          <!-- TASK-995 — AI pre-screening interview transcript -->
          ${buildCandidateScreeningTranscriptSection(c)}

          <!-- TASK-235 v2 — Driving experience per equipment type -->
          ${buildCandidateDrivingExperienceSection(c)}

          <!-- TASK-1366 — the self-reported driving record (accidents/violations/
               forfeitures) is no longer its own card here; it renders as the
               "Self-Reported by the Applicant" sub-block of the Background &
               Driving Record card above, right under the verified screenings. -->

          <!-- TASK-235 — 10-yr employment history (FMCSR 391.21) -->
          <!-- TASK-1099 — pei:true adds the §391.23 status chips + Investigation header (candidate docs only) -->
          ${buildCandidateEmploymentHistorySection(c, { pei: true })}

          <!-- TASK-235 v2 — Residence history (current + previous addresses) -->
          ${buildCandidateResidenceSection(c)}

          <!-- TASK-1203 Slice C — 391.21(b)(12) certification + applicant signature -->
          ${buildCandidateCertSignatureSection(c)}

          <!-- TASK-1366 — resume + legacy flat notes were three more bespoke
               header/box treatments at the bottom of the modal. Same shell as
               everything else now. -->
          <!-- TASK-4551 — the CDL and med card, on the surface a reviewer
               actually reads. This is the "view the actual/physical CDL &
               Medcard right in the Application pipeline" ask; the edit modal
               carries the same panel for whoever is already in there. Rendered
               UNCONDITIONALLY, unlike the resume card above: when both are
               missing that IS the finding, and a card that disappears when
               empty cannot report an absence. -->
          ${_candCard({
            icon: 'badge',
            title: 'Application Documents',
            actions: '',
            body: _candidateAppDocsBodyHtml(c),
          })}
          ${(c.resumeUrl || c.resumeText) ? _candCard({
            icon: 'description',
            title: 'Resume',
            actions: c.resumeUrl ? `<a href="${_safeHttpUrl(c.resumeUrl)}" target="_blank" rel="noopener" class="cand-btn cand-btn-secondary" style="text-decoration:none;"><span class="material-icons">open_in_new</span> ${escapeHtml(c.resumeFileName || 'View resume')}</a>` : '',
            body: c.resumeText
              ? `<div style="font-size:0.82rem;max-height:200px;overflow-y:auto;white-space:pre-wrap;line-height:1.5;color:var(--text-primary);">${escapeHtml(c.resumeText)}</div>`
              : `<div>${_candEmpty('No resume text extracted — open the file above.')}</div>`,
          }) : ''}
          ${c.notes ? _candCard({
            icon: 'sticky_note_2',
            title: 'Imported Note',
            sub: 'Free-text note carried over from before the Notes &amp; Calls feed existed.',
            body: `<div style="white-space:pre-wrap;font-size:0.86rem;line-height:1.5;color:var(--text-primary);">${escapeHtml(c.notes)}</div>`,
          }) : ''}
        </div>
        <!-- v1.15.703 — Delete compressed to icon, Offer Letter shortened so all 5 actions fit one row -->
        <!-- TASK-431 — candidate-detail-actions class lets CSS tidy the cramped
             mobile row (stacks the primary actions, keeps the trash separate).
             TASK-1271 — declutter: the footer is now a COLUMN (helper caption on
             its own line above, then one action row), and every layout/skin
             decision moved out of inline styles into classes in styles.css so
             the media queries can restructure without !important fights.
             Hierarchy: exactly ONE primary (green Approve to Roster, or the
             "Added to Roster" pill once approved). Print / Edit / Offer Letter
             are uniform neutral outline secondaries — Edit was demoted from
             btn-primary so it stops competing with Approve.
             🔴 FCRA: "Reject…" stays a first-class labeled button, visually
             separate from the de-emphasized Delete icon. Reject routes through
             the mandatory §615 adverse-action intercept; Delete does NOT. They
             must never collapse into one cluster or an overflow menu. -->
        <div class="modal-footer candidate-detail-actions" data-no-print>
          ${(!c.approvedToRoster && !_archived) ? (_apprGate.ok ? `
          <div class="candidate-detail-actions__hint" title="Approving the hire or clearing the background check does not add this candidate to your roster — that is a separate deliberate step.">
            <span class="material-icons">info</span>
            <span>Not on the roster until you tap <strong>Approve to Roster</strong>.</span>
          </div>` : `
          <!-- TASK-1511 — the hire's writeBatch is rules-denied for this member, so
               the button is gone and this note names the module to ask for instead
               of letting them fill in the form and hit a raw permissions error.
               Self-contained amber pair (explicit light background + dark amber
               text) so the contrast holds in dark mode too — the footer's own
               background is theme-dependent. -->
          <div class="candidate-detail-actions__hint" style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-md);padding:8px 10px;color:#92400E;">
            <span class="material-icons" style="color:#D97706;">lock</span>
            <span>${_apprNote}</span>
          </div>`) : ''}
          <div class="candidate-detail-actions__row">
            <div class="candidate-detail-actions__danger">
              <button class="btn cand-action-delete" onclick="window.HRModule.deleteCandidate('${c.id}')" title="Archive this application (undoable — permanent delete lives in the archived view)" aria-label="Delete">
                <span class="material-icons">delete</span>
              </button>
              <!-- TASK-1212 — disposition actions: the bar was 100% forward motion.
                   Both just open the EXISTING quick-status modal pre-selected. -->
              ${(!_archived && c.status !== 'rejected') ? `
              <button class="btn cand-action-reject" onclick="window.HRModule.openQuickStatusModalPreset('${c.id}','rejected')" title="Decline this applicant (pick a reason)">
                <span class="material-icons">cancel</span> Reject…
              </button>` : ''}
              ${(!_archived && c.status !== 'potentials' && c.status !== 'hired') ? `
              <button class="btn cand-action-potentials" onclick="window.HRModule.openQuickStatusModalPreset('${c.id}','potentials')" title="Not now, but worth revisiting — keeps them out of the active pipeline">
                <span class="material-icons">star</span> Move to Potentials
              </button>` : ''}
            </div>
            <div class="candidate-detail-actions__main">
              <button class="btn cand-action-secondary" onclick="window.HRModule.printCandidateApplication('${c.id}')" title="Print or save this application as a PDF">
                <span class="material-icons">print</span> Print
              </button>
              ${_hrAuditReviewerCount(c) ? `<button class="btn cand-action-secondary" onclick="window.HRModule.printCandidateApplication('${c.id}', { reviewer: true })" title="Print the application with a separate Things to Verify page for the reviewer. Never give this copy to the applicant or file it as the application.">
                <span class="material-icons">fact_check</span> Print with reviewer notes
              </button>` : ''}
              ${!_archived ? `<button class="btn cand-action-secondary" onclick="window.HRModule.closeModal('view-candidate');window.HRModule.openEditCandidateModal('${c.id}')" title="Edit this application">
                <span class="material-icons">edit</span> Edit
              </button>` : ''}
              <!-- TASK-4550 — SEND BACK. Only on a SUBMITTED application: an
                   unsubmitted draft is already the applicant's to edit, and
                   "sending back" something they never sent is nonsense. Sits with
                   the neutral secondaries, NOT in the danger cluster — this is a
                   forward move (get the file right), not a disposition, and the
                   FCRA fencing note above is about Reject vs Delete only. -->
              ${(!_archived && c.applicationComplete === true && c.applicationReturnStatus !== 'returned' && c.status !== 'rejected' && c.status !== 'withdrawn' && c.status !== 'hired') ? `
              <button class="btn cand-action-secondary" onclick="window.HRModule.openSendBackModal('${c.id}')" title="Reopen this application so the applicant can correct it, with a note telling them what to fix">
                <span class="material-icons">reply</span> Send Back…
              </button>` : ''}
              ${c.applicationReturnStatus === 'returned' ? `
              <span class="cand-roster-pill" style="background:#FFF7ED;color:#92400E;border-color:#FDBA74;" title="Sent back to the applicant — waiting on their correction">
                <span class="material-icons">hourglass_top</span> Out for correction
              </span>` : ''}
              <!-- TASK-9530 — a candidate with no submitted application (entered by
                   hand, or a draft) gets an emailed link to finish and sign it
                   THEMSELVES. The office never sees the link. -->
              ${(_hrCanSendApplicationLink(c) || _hrInviteOutstanding(c)) ? `
              ${_hrCanSendApplicationLink(c) ? `
              <button class="btn cand-action-secondary" data-send-app-link="${escapeHtml(c.id)}" onclick="window.HRModule.sendApplicationLink('${c.id}')" title="Email ${escapeHtml(c.name || 'the candidate')} a link to finish and sign their own application">
                <span class="material-icons">forward_to_inbox</span> ${c.applicationInvite && c.applicationInvite.sentAt ? 'Resend Application Link' : 'Email Application Link'}
              </button>` : ''}
              ${_hrInviteOutstanding(c) ? `
              <button class="btn cand-action-secondary" data-cancel-app-link="${escapeHtml(c.id)}" onclick="window.HRModule.cancelApplicationLink('${c.id}')" title="Cancel the emailed application link (needed before you can change this candidate's email)">
                <span class="material-icons">link_off</span> Cancel Application Link
              </button>` : ''}
              ${_hrApplicationInvitePill(c)}` : ''}
              ${_hrNudgeChip(c)}
              ${(!_archived && c.status !== 'rejected' && c.status !== 'withdrawn') ? `
              <!-- TASK-321 Slice 2 — Generate Offer Letter PDF + auto-attach. -->
              <button class="btn cand-action-secondary" onclick="window.HRModule.closeModal('view-candidate');window.HRModule.openOfferLetterGenerateModal('${c.id}')" title="Generate offer letter PDF">
                <span class="material-icons">description</span> Offer Letter
              </button>` : ''}
              ${(!c.approvedToRoster && _apprGate.ok && !_archived) ? `
              <button class="btn cand-action-primary" onclick="window.HRModule.closeModal('view-candidate');window.HRModule.openApproveToRosterModal('${c.id}')">
                <span class="material-icons">person_add</span> Approve to Roster
              </button>` : c.approvedToRoster ? `
              <span class="cand-roster-pill">
                <span class="material-icons">check_circle</span> Added to Roster
              </span>` : ''}
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function getQuickStatusModalHTML(candidate) {
  const c = candidate;
  // TASK-1213 — optional note field for ALL stages (feeds the Notes & Calls
  // timeline). TASK-1212 — when Potentials is picked, the same field becomes
  // "Why / when to revisit" and persists to candidate.potentialsNote.
  // TASK-1219 — when Interview is picked, inline schedule fields appear.
  const isPotentials = c.status === 'potentials';
  return `
    <div class="modal" id="modal-quick-status" style="z-index:1050;">
      <div class="modal-content modal-small">
        <div class="modal-header">
          <h3>Update Status — ${escapeHtml(c.name || '')}</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('quick-status')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Status</label>
            <select id="quick-status-select" onchange="window.HRModule.onQuickStatusStageChange(this.value)">
              ${PIPELINE_STAGES.map(s => `<option value="${s.id}" ${c.status === s.id ? 'selected' : ''}>${s.label}</option>`).join('')}
            </select>
          </div>
          <div class="form-group" id="quick-rejection-group" style="display:${c.status === 'rejected' ? 'block' : 'none'};">
            <label>Rejection Reason</label>
            <select id="quick-rejection-reason" onchange="window.HRModule.syncQuickStatusDecisionCta()">
              <option value="">Select reason...</option>
              ${REJECTION_REASONS.map(r => `<option value="${r}" ${c.rejectionReason === r ? 'selected' : ''}>${r}</option>`).join('')}
            </select>
          </div>
          <div class="form-group" id="quick-interview-group" style="display:${c.status === 'interview' ? 'block' : 'none'};">
            <label>Schedule the interview (optional)</label>
            <input type="datetime-local" id="quick-interview-at" value="${_toDatetimeLocalValue(c.interviewAt)}" min="${_toDatetimeLocalValue(Timestamp.now())}" style="width:100%;margin-bottom:6px;">
            <select id="quick-interview-interviewer" title="Who conducts the interview (gets an SMS reminder an hour before)">
              ${_interviewerOptionsHtml(c.interviewerPersonId)}
            </select>
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">The interviewer gets an SMS reminder about an hour before.</div>
          </div>
          <div class="form-group" id="quick-status-note-group">
            <label id="quick-status-note-label">${isPotentials ? 'Why / when to revisit (optional)' : 'Add a note (optional)'}</label>
            <textarea id="quick-status-note" rows="2" maxlength="1000" placeholder="${isPotentials ? 'e.g. Solid hand, no openings — call back in spring' : 'e.g. Called — left voicemail'}">${isPotentials && c.potentialsNote ? escapeHtml(c.potentialsNote) : ''}</textarea>
          </div>
          <input type="hidden" id="quick-status-candidate-id" value="${c.id}">
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.HRModule.closeModal('quick-status')">Cancel</button>
          <button class="btn btn-primary" id="quick-status-save-btn" onclick="window.HRModule.saveQuickStatus()">
            <span class="material-icons">save</span> Save
          </button>
        </div>
      </div>
    </div>
  `;
}

// TASK-1213/1212/1219 — single stage-change handler for the quick-status modal
// (replaces the old inline rejection-group toggle; keeps the TASK-1155 CTA sync).
function onQuickStatusStageChange(value) {
  // TASK-9141 — BYPASS #2, intercepted at the moment of the pick rather than at
  // Save, so the operator sees the promote modal instead of a refusal after the
  // fact. Put the select BACK to the candidate's real stage first: if they close
  // the promote modal, the quick-status modal underneath must not be sitting
  // there reading "Hired" for a hire that never happened.
  if (String(value || '').toLowerCase() === 'hired') {
    const _qsId = document.getElementById('quick-status-candidate-id')?.value;
    const _qsCand = HRState.candidates.find(x => x.id === _qsId);
    if (_qsId && _hrHireIsBareFieldWrite(_qsCand)) {
      const _sel = document.getElementById('quick-status-select');
      if (_sel) _sel.value = (_qsCand && _qsCand.status) || 'new';
      try { closeModal('quick-status'); } catch (_) {}
      window.showToast?.(HIRE_NEEDS_PROMOTE_MSG, 'info');
      openApproveToRosterModal(_qsId);
      return;
    }
  }
  const rejGrp = document.getElementById('quick-rejection-group');
  if (rejGrp) rejGrp.style.display = value === 'rejected' ? 'block' : 'none';
  const ivGrp = document.getElementById('quick-interview-group');
  if (ivGrp) ivGrp.style.display = value === 'interview' ? 'block' : 'none';
  const noteLabel = document.getElementById('quick-status-note-label');
  const noteEl = document.getElementById('quick-status-note');
  if (noteLabel) noteLabel.textContent = value === 'potentials' ? 'Why / when to revisit (optional)' : 'Add a note (optional)';
  if (noteEl) {
    noteEl.placeholder = value === 'potentials' ? 'e.g. Solid hand, no openings — call back in spring' : 'e.g. Called — left voicemail';
    if (value === 'potentials' && !noteEl.value) {
      const id = document.getElementById('quick-status-candidate-id')?.value;
      const cand = HRState.candidates.find(x => x.id === id);
      if (cand?.potentialsNote) noteEl.value = cand.potentialsNote;
    }
  }
  syncQuickStatusDecisionCta();
}

// --- BG Notifier UI — REMOVED 2026-05-13 (architectural pivot) ---
// In-app HR Hiring panel for editing tenant.compliance.notifiers[] was
// removed because compliance-only carriers don't see HR/Hiring (Roster locked,
// HR sub-views gated to Hiring only), and paying carriers use the Roster
// HIRING tag for per-person opt-in. The partner-side editor lives in
// public/compliance-manager.html (per-managed-carrier view). The
// `tenant.compliance.notifiers[]` Firestore field is still consumed by the
// server-side fanout in `_notifyMvrComplete` and `checkrWebhook`.

// --- Rendering ---
function renderCandidatesList() {
  renderCandidateStatsBar();
  const container = document.getElementById('hiring-candidates-list');
  if (!container) return;

  // TASK-368 (2026-05-28): skeleton while first onSnapshot is in flight — was
  // a first-paint lie where this rendered "No candidates yet" before data
  // arrived. Same shape pattern as TASK-369 (Roster staff-login flash).
  if (!HRState.candidatesLoaded) {
    container.innerHTML = `
      <div class="hiring-candidates-loading" style="margin-top:var(--spacing-lg);display:flex;flex-direction:column;gap:8px;opacity:0.55;">
        ${[1,2,3].map(() => `
          <div style="background:var(--bg-card);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:14px 16px;display:flex;align-items:center;gap:12px;">
            <div style="width:40px;height:40px;border-radius:50%;background:var(--bg-elevated);flex-shrink:0;"></div>
            <div style="flex:1;display:flex;flex-direction:column;gap:6px;">
              <div style="height:14px;width:55%;background:var(--bg-elevated);border-radius:4px;"></div>
              <div style="height:12px;width:35%;background:var(--bg-elevated);border-radius:4px;"></div>
            </div>
          </div>
        `).join('')}
        <div style="text-align:center;color:var(--text-secondary);font-size:0.85rem;margin-top:6px;">Loading candidates…</div>
      </div>
    `;
    return;
  }

  // TASK-635 — recoverable listener-error state. Firestore does NOT error on an
  // empty collection (that arrives as a successful empty snapshot), so when
  // candidatesError is set we hit a REAL error (stale long-poll after the PWA
  // was backgrounded, auth-token refresh, transient unavailable). Show a
  // "couldn't load — retrying" state instead of a false "No applicants yet"
  // empty state. We keep last-known candidates, so if there ARE rows we render
  // them with a slim retry banner on top; only show the full retry card when we
  // genuinely have nothing cached.
  const retryBanner = HRState.candidatesError ? `
    <div class="hiring-retry-banner" role="status" style="display:flex;align-items:center;gap:10px;margin-bottom:var(--spacing-md);padding:10px 14px;background:#FEF3C7;border:1px solid #FCD34D;border-radius:var(--radius-md);color:#92400E;font-size:0.85rem;">
      <span class="material-icons" style="font-size:18px;animation:spin 1.2s linear infinite;">autorenew</span>
      <span style="flex:1;">Couldn't load the latest applicants — retrying…</span>
      <button class="btn btn-sm" onclick="window.HRModule.retryCandidatesNow()" style="background:transparent;border:1px solid #FCD34D;color:#92400E;padding:4px 10px;">Retry now</button>
    </div>` : '';

  if (HRState.candidatesError && HRState.candidates.length === 0) {
    container.innerHTML = `
      ${retryBanner}
      <div class="empty-state" style="margin-top:var(--spacing-lg);">
        <div class="empty-state-icon">⚠️</div>
        <div class="empty-state-title">Couldn't load applicants</div>
        <div class="empty-state-text">We're retrying automatically. Check your connection if this persists.</div>
        <button class="btn btn-secondary" onclick="window.HRModule.retryCandidatesNow()"><span class="material-icons">refresh</span> Retry now</button>
      </div>
    `;
    return;
  }

  // TASK-9293 — the list is ONE view at a time: Active (default) / Hired / Rejected &
  // Withdrawn / Archived. Kolsen asked three times for hired and rejected rows to leave the
  // Applications page. They leave the DEFAULT view and stay one tap away, with counts, so
  // nothing silently vanishes. This is a LIST FILTER on the resolved stage (TASK-1360) and
  // it never writes `archived` — `archived` feeds the purge path in expireStaleApplicants
  // and the FCRA skip there is expire-only, so archiving a hired or rejected candidate would
  // queue the §391.21 application / adverse-action record for deletion the day a tenant
  // enables purge (COD-binding). TASK-650's show-archived toggle is now the Archived view.
  const _view = _hrCandidateView();
  // TASK-9583 — duplicate incomplete drafts of one no-SSN applicant are folded
  // (display only; see _hrFoldNoSsnDuplicateDrafts). Counts and rows use the same pool.
  const _pool = _hrCandidatesForList();
  const viewCounts = _hrCandidateViewCounts(_pool);
  let filtered = _pool.filter(c => candidateListView(c) === _view);
  if (HRState.candidateFilter.status) {
    // TASK-1360 — filter on the RESOLVED stage, not the raw status, so the list agrees with the
    // stats pills. Two concrete effects: "New" now includes apply.html's submitted 'applied'
    // status (previously counted nowhere and filtered to nothing), and the never-submitted
    // bucket is selectable at all.
    filtered = filtered.filter(c => stageForCandidate(c).id === HRState.candidateFilter.status);
  }
  if (HRState.candidateFilter.search) {
    const q = HRState.candidateFilter.search.trim().toLowerCase();
    filtered = filtered.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.phone || '').includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.position || '').toLowerCase().includes(q)
    );
  }

  // TASK-1101 — sort control (ports the partner Applications sort). Options:
  // "action needed first" (unresolved pipeline stages, then newest),
  // Name A–Z and Date applied (newest). Applied after the filters above.
  // TASK-1215 — DEFAULT is now Date applied (newest). Legacy candidates with
  // no appliedDate/createdAt resolve to 0 in _hrCandidateMs and sink to the
  // bottom (Array.prototype.sort is stable, so they keep a stable order).
  const _sortMode = HRState.candidateFilter.sort || 'date';
  if (_sortMode === 'name') {
    filtered.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' }));
  } else if (_sortMode === 'date') {
    filtered.sort((a, b) => _hrCandidateMs(b) - _hrCandidateMs(a));
  } else {
    filtered.sort((a, b) =>
      (_hrCandidateActionScore(b) - _hrCandidateActionScore(a)) ||
      (_hrCandidateMs(b) - _hrCandidateMs(a)) ||
      (a.name || '').localeCompare(b.name || '', undefined, { numeric: true, sensitivity: 'base' })
    );
  }
  const _sortControl = `
    <div style="display:flex;align-items:center;gap:6px;">
      <label for="hiring-sort" style="font-size:0.8rem;color:var(--text-secondary);">Sort</label>
      <select id="hiring-sort" class="control-auto" onchange="window.HRModule.setCandidateSort(this.value)" style="font-size:0.82rem;padding:5px 8px;border:1px solid var(--border-light);border-radius:6px;background:var(--bg-card);color:var(--text-primary);">
        <option value="action" ${_sortMode === 'action' ? 'selected' : ''}>Action needed first</option>
        <option value="name" ${_sortMode === 'name' ? 'selected' : ''}>Name (A–Z)</option>
        <option value="date" ${_sortMode === 'date' ? 'selected' : ''}>Date applied (newest)</option>
      </select>
    </div>`;
  const _sortRow = `<div style="display:flex;justify-content:flex-end;margin-bottom:8px;padding:0 4px;">${_sortControl}</div>`;

  // TASK-9293 — view tabs (Active / Hired / Rejected & Withdrawn / Archived), each with its
  // live count, ALWAYS rendered so a hired or rejected row is never more than one tap away.
  // The Archived tab only appears once something is archived (TASK-650's original rule).
  const viewTabs = _hrRenderCandidateViewTabs(_view, viewCounts);

  if (filtered.length === 0) {
    const _hasFilter = !!(HRState.candidateFilter.status || HRState.candidateFilter.search);
    const _title = HRState.candidates.length === 0 ? 'No candidates yet'
      : (_hasFilter ? 'No matching candidates' : _hrEmptyViewTitle(_view));
    const _text = HRState.candidates.length === 0 ? 'Add your first candidate to start tracking applicants.'
      : (_hasFilter ? 'Try adjusting your filters.' : _hrEmptyViewText(_view, viewCounts));
    container.innerHTML = `
      ${retryBanner}
      ${viewTabs}
      <div class="empty-state" style="margin-top:var(--spacing-xl);">
        <div class="empty-state-icon">📋</div>
        <div class="empty-state-title">${_title}</div>
        <div class="empty-state-text">${_text}</div>
        ${_hasFilter ? `<button class="btn btn-secondary" onclick="window.HRModule.clearCandidateFilters()"><span class="material-icons">filter_list_off</span> Clear Filters</button>` : ''}
      </div>
    `;
    return;
  }

  // v1.15.491 — Hiring/Applicants list now mirrors the Roster people-list
  // pattern (`.roster-list` of horizontal `.roster-card` rows). Same renderer
  // serves desktop + mobile because `.roster-card` is responsive (rows wrap
  // on narrow widths; actions cluster moves to its own line under 768px).
  // TASK-635 — prepend the retry banner (empty unless a listener error is
  // unresolved) so last-known rows stay visible during a transient outage.
  container.innerHTML = retryBanner + viewTabs + _hrFoldNoticeHtml() + _sortRow + renderCandidateRosterList(filtered);
}

// ── TASK-9583 — one row per no-SSN applicant ─────────────────────────────────
// A Canadian / Mexican applicant with no U.S. SSN who closed the tab and started
// over used to leave a second INCOMPLETE row (the SSN+DOB dedup can't see them).
// The server now stamps `noSsnResumeKey` = hash(tenant, email, DOB) on every
// honoured no-SSN save. Rows sharing that key AND the same name are one person:
// the list shows the newest (a submitted application wins over drafts) and folds
// the OTHER incomplete drafts behind it.
// Why display-only and not a save-time merge: a merge would let anyone who knows
// an applicant's email + DOB + name write into that applicant's draft, and it
// can't be undone. Folding changes no data, never hides a SUBMITTED application,
// and one tap shows every row again. A different person never folds unless they
// share the email, the date of birth AND the name, and even then nothing is lost.
function _hrNoSsnFoldKey(c) {
  if (!c || c.noUsSsn !== true) return null;
  const key = typeof c.noSsnResumeKey === 'string' ? c.noSsnResumeKey : '';
  if (!key) return null;
  const raw = `${c.firstName || ''} ${c.lastName || ''}`.trim() || String(c.name || '');
  const name = raw.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z]/g, '');
  if (!name) return null;
  return `${key}|${name}`;
}
function _hrCandidateSavedMs(c) {
  const t = Date.parse((c && (c.lastSavedAt || c.submittedAt || c.createdAt)) || '');
  return Number.isFinite(t) ? t : 0;
}
// → { hidden: Set<id>, foldedInto: Map<keeperId, number> }. Archived rows never fold.
function _hrFoldNoSsnDuplicateDrafts(candidates) {
  const groups = new Map();
  (candidates || []).forEach(c => {
    if (!c || isArchivedCandidate(c)) return;
    // A row staff already rejected / withdrew is their decision: it never folds and
    // never becomes the row another draft folds under (it lives in another tab).
    const _st = String(c.status || '').toLowerCase().trim();
    if (_st === 'rejected' || _st === 'withdrawn') return;
    const k = _hrNoSsnFoldKey(c);
    if (!k) return;
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(c);
  });
  const hidden = new Set();
  const foldedInto = new Map();
  groups.forEach(list => {
    if (list.length < 2) return;
    const byNewest = list.slice().sort((a, b) => _hrCandidateSavedMs(b) - _hrCandidateSavedMs(a));
    const keeper = byNewest.find(c => c.applicationComplete === true) || byNewest[0];
    byNewest.forEach(c => {
      if (c === keeper || c.applicationComplete === true) return; // a submitted application is never hidden
      hidden.add(c.id);
      foldedInto.set(keeper.id, (foldedInto.get(keeper.id) || 0) + 1);
    });
  });
  return { hidden, foldedInto };
}
// The candidates the list + counts render from. With "show duplicates" on, all of them.
function _hrCandidatesForList() {
  const fold = _hrFoldNoSsnDuplicateDrafts(HRState.candidates);
  HRState._noSsnFold = fold;
  if (HRState.showFoldedDrafts === true || fold.hidden.size === 0) return HRState.candidates;
  return HRState.candidates.filter(c => !fold.hidden.has(c.id));
}
function _hrFoldNoticeHtml() {
  const fold = HRState._noSsnFold;
  const n = fold ? fold.hidden.size : 0;
  if (!n) return '';
  const showing = HRState.showFoldedDrafts === true;
  const what = n === 1 ? '1 duplicate incomplete draft' : `${n} duplicate incomplete drafts`;
  return `
    <div class="hiring-fold-notice" data-hiring-fold-notice style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;margin:0 4px 8px;padding:8px 12px;background:var(--bg-card);border:1px solid var(--border-light);border-radius:var(--radius-md);font-size:0.82rem;color:var(--text-secondary);">
      <span class="material-icons" style="font-size:16px;">layers</span>
      <span style="flex:1;min-width:200px;">${showing ? `Showing ${what}` : `${what} hidden`} — the same applicant without a U.S. SSN (same name, email and date of birth) started over.</span>
      <button type="button" class="btn btn-sm btn-secondary" data-hiring-fold-toggle onclick="window.HRModule.toggleFoldedDrafts()" style="min-height:36px;">${showing ? 'Hide duplicates' : 'Show them'}</button>
    </div>`;
}
function _hrFoldChip(c) {
  const fold = HRState._noSsnFold;
  if (!fold || !c) return '';
  const chip = (txt, title) => `<span class="hiring-fold-chip" data-hiring-fold-chip style="display:inline-flex;align-items:center;padding:1px 6px;font-size:0.65rem;background:#E5E7EB;color:#374151;border-radius:4px;font-weight:600;margin-left:6px;" title="${escapeHtml(title)}">${escapeHtml(txt)}</span>`;
  const n = fold.foldedInto.get(c.id) || 0;
  if (n) return chip(`+${n} earlier draft${n === 1 ? '' : 's'}`, 'The same applicant (no U.S. SSN) started over; the older incomplete draft is folded under this row.');
  if (fold.hidden.has(c.id)) return chip('Duplicate draft', 'An earlier incomplete draft of an applicant who started over.');
  return '';
}
function toggleFoldedDrafts() {
  HRState.showFoldedDrafts = HRState.showFoldedDrafts !== true;
  renderCandidatesList();
}

// TASK-650 — a candidate is "archived" (hidden from the default pending list)
// if the soft-archive flag is set OR an auto-expiry stamped archivedAt.
function isArchivedCandidate(c) {
  return !!(c && (c.archived === true || c.archivedAt));
}

// ── TASK-9293 — Applications list VIEWS ──────────────────────────────────────────────────────
// One candidate lands in exactly ONE view. Archived wins (it is orthogonal to stage — an
// archived hired candidate is still archived); then the RESOLVED stage (stageForCandidate,
// TASK-1360 — so 'applied' reads as New and a never-submitted 'draft' stays Active, where the
// inactivity cron now clears it — 30 days by default since TASK-9699). The partner portal's _candidateListView is the byte-identical
// model; tests/task9293-applications-list-views.unit.cjs runs one candidate table through both.
const HR_CANDIDATE_VIEWS = [
  { id: 'active', label: 'Active', icon: 'pending_actions', title: 'Applications still being worked' },
  { id: 'hired', label: 'Hired', icon: 'check_circle', title: 'Hired — the application stays on file (§391.21)' },
  { id: 'closed', label: 'Rejected / Withdrawn', icon: 'cancel', title: 'Rejected or withdrawn — the decision record stays on file' },
  { id: 'archived', label: 'Archived', icon: 'inventory_2', title: 'Auto-expired and manually archived candidates' },
];
function candidateListView(c) {
  if (isArchivedCandidate(c)) return 'archived';
  const s = stageForCandidate(c).id;
  if (s === 'hired') return 'hired';
  if (s === 'rejected' || s === 'withdrawn') return 'closed';
  return 'active';
}
// Which view a stage pill / status <select> choice lives in (non-archived candidates).
function viewForStageId(stageId) {
  const s = String(stageId || '').toLowerCase();
  if (s === 'hired') return 'hired';
  if (s === 'rejected' || s === 'withdrawn') return 'closed';
  return 'active';
}
function _hrCandidateView() {
  const v = HRState.candidateFilter.view;
  return HR_CANDIDATE_VIEWS.some(x => x.id === v) ? v : 'active';
}
function _hrCandidateViewCounts(candidates) {
  const counts = { active: 0, hired: 0, closed: 0, archived: 0 };
  (candidates || []).forEach(c => { counts[candidateListView(c)]++; });
  return counts;
}
function _hrRenderCandidateViewTabs(view, counts) {
  const tabs = HR_CANDIDATE_VIEWS.filter(v => v.id !== 'archived' || counts.archived > 0 || view === 'archived');
  return `
    <div class="hiring-view-tabs" role="tablist" aria-label="Application views" style="display:flex;flex-wrap:wrap;gap:6px;margin-bottom:8px;padding:0 4px;">
      ${tabs.map(v => {
        const on = v.id === view;
        return `<button type="button" role="tab" aria-selected="${on ? 'true' : 'false'}" class="btn btn-sm ${on ? '' : 'btn-secondary'}"
          data-hiring-view="${v.id}" onclick="window.HRModule.setCandidateView('${v.id}')" title="${escapeHtml(v.title)}"
          style="display:inline-flex;align-items:center;gap:6px;font-size:0.82rem;${on ? 'background:var(--brand-blue);color:#fff;border:1px solid var(--brand-blue);' : ''}">
          <span class="material-icons" style="font-size:16px;">${v.icon}</span>${v.label} <span style="opacity:.85;">(${counts[v.id] || 0})</span>
        </button>`;
      }).join('')}
    </div>`;
}
function _hrEmptyViewTitle(view) {
  return view === 'hired' ? 'No hired candidates'
    : view === 'closed' ? 'No rejected or withdrawn candidates'
    : view === 'archived' ? 'No archived candidates'
    : 'No active applications';
}
function _hrEmptyViewText(view, counts) {
  const parts = [];
  if (view !== 'hired' && counts.hired) parts.push(`${counts.hired} hired`);
  if (view !== 'closed' && counts.closed) parts.push(`${counts.closed} rejected / withdrawn`);
  if (view !== 'archived' && counts.archived) parts.push(`${counts.archived} archived`);
  if (view === 'active') {
    return parts.length ? `Everything here has been resolved — ${parts.join(', ')}. Use the tabs above to review them.` : 'Share the apply link or add a candidate to get started.';
  }
  return parts.length ? `Other views: ${parts.join(', ')}.` : 'Nothing here yet.';
}
// Switch views. A status filter that belongs to another view is dropped so the list can never be
// "Hired view + Reviewing filter = nothing". In the Archived view any status filter applies as-is.
function setCandidateView(view) {
  const next = HR_CANDIDATE_VIEWS.some(x => x.id === view) ? view : 'active';
  HRState.candidateFilter.view = next;
  const st = HRState.candidateFilter.status;
  if (st && next !== 'archived' && viewForStageId(st) !== next) {
    HRState.candidateFilter.status = null;
    const filterSelect = document.getElementById('hiring-status-filter');
    if (filterSelect) filterSelect.value = '';
  }
  renderCandidateStatsBar();
  renderCandidatesList();
}

// TASK-1101 — sort helpers for the carrier Hiring/Applicants list.
// Comparable epoch ms from appliedDate (YYYY-MM-DD string) or createdAt (Timestamp).
function _hrCandidateMs(c) {
  const v = (c && (c.appliedDate || c.createdAt)) || null;
  if (!v) return 0;
  if (typeof v.toDate === 'function') { const d = v.toDate(); return d ? d.getTime() : 0; }
  const s = String(v);
  const t = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(s) ? s + 'T00:00:00' : s);
  return isNaN(t) ? 0 : t;
}
// Higher = needs attention sooner. Resolved (hired/rejected/withdrawn) sink to the
// bottom; an outstanding offer sits just above them; everything still in-pipeline tops.
function _hrCandidateActionScore(c) {
  const s = String((c && c.status) || 'new').toLowerCase();
  if (s === 'hired' || s === 'rejected' || s === 'withdrawn' || s === 'declined') return 0;
  if (s === 'offer') return 2;
  // TASK-1360 — a never-submitted application is the applicant's to finish, not the carrier's to
  // action, so it ranks below live work but above a closed file. Kept identical to the partner
  // portal's _candidateStageRank so the two surfaces order the same candidates the same way.
  if (s === 'draft') return 1;
  return 3;
}

function setCandidateSort(mode) {
  const allowed = ['action', 'name', 'date'];
  // TASK-1215 — fallback pins to 'date' (newest applied), matching the init default.
  HRState.candidateFilter.sort = allowed.includes(mode) ? mode : 'date';
  renderCandidatesList();
}

// TASK-650's toggle, kept as an alias for any caller that still uses it: flips between the
// Archived view and the Active view.
function toggleShowArchivedCandidates() {
  setCandidateView(_hrCandidateView() === 'archived' ? 'active' : 'archived');
}

// TASK-635 — manual "Retry now" from the recoverable error banner. Resets the
// backoff so the user-initiated retry is immediate, then re-arms.
function retryCandidatesNow() {
  HRState._candRetryAttempt = 0;
  HRState._bgRetryAttempt = 0;
  if (HRState.tenantId) {
    _armCandidatesListener(HRState.tenantId);
    if (HRState.bgRecordsError) _armBgRecordsListener(HRState.tenantId);
  }
}

function renderCandidateStatsBar() {
  const bar = document.getElementById('hiring-stats-bar');
  if (!bar) return;
  const counts = {};
  PIPELINE_STAGES.forEach(s => counts[s.id] = 0);
  counts[HR_STAGE_DRAFT.id] = 0;
  counts[HR_STAGE_OTHER.id] = 0;
  // TASK-650 — archived candidates are excluded from the stage counts so the
  // pills reflect the live pipeline, not abandoned apps.
  // 🔴 TASK-1360 — this used to key off the RAW status (`counts[c.status]`), which is undefined
  // for any status that isn't a pipeline id — so it silently dropped BOTH never-submitted drafts
  // AND every submitted `applied` candidate (apply.html's submit status is 'applied', which is
  // not in PIPELINE_STAGES). Route through stageForCandidate so the pills count what the cards
  // actually show.
  _hrCandidatesForList().forEach(c => { if (!isArchivedCandidate(c)) counts[stageForCandidate(c).id]++; }); // TASK-9583 — folded duplicate drafts don't count twice
  const active = HRState.candidateFilter.status;
  // TASK-368 (2026-05-28): show em-dash placeholder during first-paint instead
  // of "0" — pre-fix the stats pills said "0 New, 0 Screening, ..." before
  // candidates first onSnapshot resolved, indistinguishable from a real
  // empty pipeline.
  const showCount = (n) => HRState.candidatesLoaded ? n : '—';
  // TASK-1360 — the display-only pills only appear when they'd be non-zero, so a carrier with a
  // clean pipeline sees no extra chrome. They're filterable like any other pill.
  const _pills = PIPELINE_STAGES.concat(
    [HR_STAGE_DRAFT, HR_STAGE_OTHER].filter(s => counts[s.id] > 0 || active === s.id)
  );
  bar.innerHTML = _pills.map(s => `
    <button class="hiring-stat-card ${active === s.id ? 'active' : ''}" style="--stat-color:${s.color}" onclick="window.HRModule.filterCandidatesByStatus('${active === s.id ? '' : s.id}')">
      <div class="hiring-stat-count">${showCount(counts[s.id])}</div>
      <div class="hiring-stat-label">${s.label}</div>
    </button>
  `).join('');
}

// Map Checkr report status → small visual chip for the Hiring table.
// Returns '' when no report has been run (cleaner than a noisy "—" cell).
//
// v1.15.508 — TASK-262 fix:
//  (1) Checkr's `assessment` for needs-review reports is the literal string
//      `'review'` (and `latestStatus` is also `'review'` because the webhook
//      stores `assessment || result || "completed"`). The old mapping only
//      knew `clear|eligible|consider` so review-state reports fell through
//      the entire chain and landed on the `embedConsentRecorded` branch ->
//      stale "BG Awaiting Run" chip. Added explicit `review` case -> orange
//      "Needs Review" so William Carter's card renders correctly.
//  (2) Once a final BG decision is recorded on `peopleBackgroundChecks`
//      (`cleared` or `adverse_action_initiated`), the lastDecisionPill in the
//      list-card already conveys that state. Suppress the BG chip in those
//      cases so a hired candidate doesn't show 3 conflicting badges
//      (Hired + BG Awaiting Run + Cleared — what Steven Alandt was hitting).
//      `held` + `awaiting_review` are in-flight decision states where the
//      report status is still operationally relevant, so we keep the chip.
// TASK-277 (2026-05-15) — Latency hint for pending BG/MVR reports.
// Returns { dayCount, tooltip, suffix } so we can inline "Day N" in the chip
// label and surface a richer hover/title tooltip. Returns null when not
// applicable (no orderDate or order < 1 BD ago).
function _bgLatencyHint(c, kind /* 'mvr' | 'bg' */) {
  try {
    const reports = c?.backgroundReports || {};
    const rec = reports[kind] || {};
    // Best-effort order-date sources, in priority order.
    const dateStr = rec.date || rec.requestedAt || rec.orderedAt || rec.createdAt
      || c?.checkrReportCreatedAt || c?.embedCheckrCandidateCreatedAt;
    if (!dateStr) return null;
    // Parse date-only strings (e.g. Checkr's "2026-06-09") as LOCAL midnight, not
    // UTC midnight. `new Date("2026-06-09")` parses as UTC, which in any negative-UTC
    // (US) timezone renders as the prior local day — that floored the order date back
    // a day and, combined with the inclusive business-day loop below, made the chip
    // read "Day 2" the morning a check was ordered. Full ISO timestamps (the
    // requestedAt/orderedAt/createdAt fallbacks) already carry an offset, so parse as-is.
    const _s = String(dateStr).trim();
    const ordered = /^\d{4}-\d{2}-\d{2}$/.test(_s) ? new Date(_s + 'T00:00:00') : new Date(dateStr);
    if (!ordered.getTime()) return null;
    const now = new Date();
    // Business-day count (inclusive of start day, skipping Sat/Sun).
    let bd = 0;
    const cur = new Date(ordered);
    cur.setHours(0, 0, 0, 0);
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);
    while (cur <= today) {
      const dow = cur.getDay();
      if (dow !== 0 && dow !== 6) bd++;
      cur.setDate(cur.getDate() + 1);
    }
    // Bail if the order date is in the future or fell on a weekend with no
    // weekdays yet elapsed — there's nothing meaningful to show as "Day 0."
    if (bd < 1) return null;
    const rawState = (c?.license?.state || c?.address?.state || '');
    const state = typeof rawState === 'string' ? rawState.toUpperCase().replace(/[^A-Z]/g, '') : '';
    const stateLabel = state ? `${state} ` : '';
    const isMvr = kind === 'mvr';
    // Typical window upper bounds (in business days). Used to flip the chip
    // into a "past typical" warning state.
    const upperBound = isMvr ? 10 : 5;
    const typical = isMvr ? '3–10 business days' : '1–5 business days';
    let tooltip, suffix;
    if (bd <= 2) {
      tooltip = `${stateLabel}${isMvr ? 'MVR' : 'background check'} typically takes ${typical}. Day ${bd}. No action needed yet.`;
      suffix = ` · Day ${bd}`;
    } else if (bd <= upperBound) {
      tooltip = `${stateLabel}${isMvr ? 'MVR' : 'background check'} typically takes ${typical}. Day ${bd} of the normal window.`;
      suffix = ` · Day ${bd}`;
    } else {
      tooltip = `${stateLabel}${isMvr ? 'MVR' : 'background check'} usually completes within ${typical}. Day ${bd} — past the typical window. Consider pinging Checkr if it doesn't move in 24h.`;
      suffix = ` · Day ${bd} ⚠`;
    }
    return { dayCount: bd, tooltip, suffix };
  } catch (_) {
    return null;
  }
}

// TASK-10244 — the employee card's Event History reads in plain words from the
// ONE shared map (bgCheckLabels.js, loaded lazily so the cold bundle does not
// grow). Returns an empty slot; the rows fill once the map is in. Display only.
let _hrBgHistSeq = 0;
function _hrBgHistorySlot(history) {
  const k = 'bgh' + (++_hrBgHistSeq);
  const fill = (tries) => {
    const el = document.querySelector(`[data-bg-history="${k}"]`);
    if (!el) { if (tries > 0) setTimeout(() => fill(tries - 1), 250); return; }
    const tone = { ok: '#059669', warn: '#D97706', bad: '#DC2626' };
    const rows = window.BgCheckLabels ? window.BgCheckLabels.historyRows(history)
      : history.slice().reverse().slice(0, 8).map(x => ({ label: String((x && x.event) || 'Update').replace(/[._]+/g, ' '), at: x && x.at })); // map failed to load: still show the events
    el.innerHTML = rows.map(r => `<div style="display:flex;align-items:center;gap:6px;padding:4px 0;font-size:0.75rem;border-bottom:1px solid var(--border-light);"><span class="material-icons" style="font-size:12px;color:#6B7280;">circle</span><span style="flex:1;${tone[r.tone] ? `color:${tone[r.tone]};font-weight:600;` : ''}">${escapeHtml(r.label)}</span><span style="color:var(--text-secondary);">${r.at && !isNaN(new Date(r.at)) ? new Date(r.at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span></div>`).join('');
  };
  // COD r1: if the map cannot load, still show the events (humanized), never an empty list.
  __ddChunkWrap(import('../bgCheckLabels.js?v=10244001')).then(() => fill(8), () => fill(8));
  return `<div data-bg-history="${k}"></div>`;
}

// TASK-559 — map the Checkr stall reason (needsActionKind, set by checkrWebhook)
// to a precise "action needed" label. Falls back to the generic ID/identity
// copy when the kind is missing/unknown.
function _bgNeedsActionLabel(kind) {
  switch (kind) {
    case 'driver_license_required': return '⏸ Action needed — candidate license confirmation';
    case 'driver_abstract_required': return '⏸ Action needed — candidate driver abstract / MVR';
    case 'suspended': return '⏸ Paused — report suspended (awaiting candidate info)';
    case 'paused': return '⏸ Paused — awaiting candidate info';
    case 'deferred': return '⏸ On hold — deferred pending candidate action';
    case 'id_required':
    default: return '⏸ Action needed — candidate ID/identity confirmation';
  }
}

function _bgChipForCandidate(c) {
  let reportStatus = (c.checkrReportStatus || c.bgCheckStatus || '').toLowerCase();
  let assessment = (c.checkrReportAssessment || c.bgCheckAssessment || '').toLowerCase();
  let reportId = c.checkrReportId;
  const rec = HRState.bgRecordsByCandidateId?.[c.id];
  // 2026-05-19 — Direct-invite flow (checkrInitiate) writes only to
  // peopleBackgroundChecks, never dual-writes chip fields to the candidate
  // doc, so without this fallback admin-invited candidates show no chip until
  // a terminal decision lands. Fall back to BG-record fields when the
  // candidate doc is empty.
  if (rec) {
    if (!reportStatus) reportStatus = (rec.latestStatus || '').toLowerCase();
    if (!assessment) assessment = (rec.latestAssessment || '').toLowerCase();
    if (!reportId) reportId = rec.latestReportId || null;
    // TASK-559 (COD: stale-status) — needs_candidate_action / upgrade_failed are
    // written ONLY to the live BG record (peopleBackgroundChecks), never dual-
    // written to the candidate doc. The candidate doc usually still reads
    // 'pending'/'invited', which would hide the stall chip. Let the live BG
    // record's stall status WIN over a stale candidate-side status.
    const recStatus = (rec.latestStatus || '').toLowerCase();
    if (recStatus === 'needs_candidate_action' || recStatus === 'upgrade_failed') {
      reportStatus = recStatus;
    }
  }
  // TASK-10029 — a screened two-stage applicant. Same test as the Run panel in
  // buildCandidateCheckrCard (bgCheckPending + checkrCandidateId — the server's
  // _runBackgroundCheckCore refuses without that id), so the list never promises
  // a Run button the applicant card will not show.
  const _twoStageReady = c.bgCheckPending === true && !!c.checkrCandidateId && !reportId;
  if (!reportStatus && !reportId && !c.embedCheckrCandidateId && !rec?.latestInvitationId && !_twoStageReady) return '';

  // Suppress chip when a terminal decision is logged — the decision pill is
  // the source of truth in that case.
  try {
    const lastDecision = Array.isArray(rec?.bgDecisionHistory) && rec.bgDecisionHistory.length > 0
      ? rec.bgDecisionHistory[rec.bgDecisionHistory.length - 1]?.decision
      : null;
    if (lastDecision === 'cleared' || lastDecision === 'adverse_action_initiated') {
      return '';
    }
  } catch (_) { /* non-fatal */ }

  // Decide chip style. Assessment overrides status when present.
  let label, bg, fg, icon;
  if (assessment === 'clear' || assessment === 'eligible') {
    label = 'Clear'; bg = '#D1FAE5'; fg = '#059669'; icon = 'verified';
  } else if (assessment === 'consider') {
    label = 'Consider'; bg = '#FEE2E2'; fg = '#991B1B'; icon = 'warning';
  } else if (assessment === 'review' || reportStatus === 'review') {
    label = 'Needs Review'; bg = '#FFEDD5'; fg = '#9A3412'; icon = 'rate_review';
  } else if (reportStatus === 'suspended') {
    label = 'Suspended'; bg = '#FEF3C7'; fg = '#92400E'; icon = 'pause_circle';
  } else if (reportStatus === 'needs_candidate_action') {
    // TASK-559 — Checkr paused the report waiting on the candidate.
    label = _bgNeedsActionLabel(rec?.needsActionKind); bg = '#FFFBEB'; fg = '#92400E'; icon = 'pause_circle';
  } else if (reportStatus === 'upgrade_failed') {
    label = 'Add-on failed'; bg = '#FEE2E2'; fg = '#991B1B'; icon = 'error_outline';
  } else if (reportStatus === 'canceled' || reportStatus === 'cancelled') {
    label = 'Canceled'; bg = '#F1F5F9'; fg = '#475569'; icon = 'cancel';
  } else if (reportStatus === 'complete' || reportStatus === 'completed') {
    label = 'Complete'; bg = '#DBEAFE'; fg = '#1E40AF'; icon = 'check_circle';
  } else if (reportStatus === 'pending') {
    label = 'BG Running'; bg = '#FEF3C7'; fg = '#92400E'; icon = 'hourglass_top';
  } else if (reportStatus === 'invited' || (rec?.latestInvitationId && !reportStatus)) {
    // Admin-sent Checkr invitation. If a report has been created the
    // screenings are already running (Checkr may not have advanced
    // latestStatus past 'invited' yet — webhook lag or the report just got
    // created). Otherwise the candidate hasn't clicked the link.
    if (reportId) {
      label = 'BG Running'; bg = '#FEF3C7'; fg = '#92400E'; icon = 'hourglass_top';
    } else {
      label = 'BG Invited'; bg = '#F1F5F9'; fg = '#475569'; icon = 'mail';
    }
  } else if (_twoStageReady) {
    // TASK-10029 — two-stage is the default for new accounts, so this is the
    // office's to-do: say what to do and where, in the list, not only inside
    // the applicant. The Run button is in the applicant's Background Check card.
    label = 'Ready to run check'; bg = '#DBEAFE'; fg = '#1E40AF'; icon = 'play_circle';
  } else if (c.embedConsentRecorded) {
    // Consent captured but no report status yet — two-stage waiting on Run.
    label = 'BG Awaiting Run'; bg = '#F1F5F9'; fg = '#475569'; icon = 'pending';
  } else {
    return '';
  }
  // TASK-277 — when pending, augment chip with Day-N suffix + latency tooltip.
  // Prefer MVR latency when an MVR is in flight (most common pending state for
  // CDL hiring); fall back to generic BG hint via the same helper.
  let extraSuffix = '';
  let titleAttr = 'Background check status';
  if (label === 'Ready to run check') {
    titleAttr = hrBgHandledByPartner()
      ? `Applicant signed the background check consent. Open the applicant and use "Request from your DOT manager" in the Background Check card.`
      : `Applicant signed the background check consent. Nothing is ordered or billed yet. Open the applicant and click "Run background check" in the Background Check card.`;
  }
  if (reportStatus === 'pending') {
    const hint = _bgLatencyHint(c, 'mvr') || _bgLatencyHint(c, 'bg');
    if (hint) {
      extraSuffix = hint.suffix;
      titleAttr = hint.tooltip;
    }
  }
  // TASK-10029 — the ready-to-run chip is a button: it opens the applicant and
  // scrolls to the Run button, so "where do I run it" is one tap, not a hunt.
  const mainChip = label === 'Ready to run check'
    ? `<button type="button" class="hiring-ready-to-run-chip" data-ready-to-run="${escapeHtml(c.id)}" onclick="event.stopPropagation();window.HRModule.openCandidateBgRun('${escJsAttr(c.id)}')" style="display:inline-flex;align-items:center;gap:3px;padding:4px 8px;min-height:26px;font-size:0.7rem;background:${bg};color:${fg};border:1px solid #93C5FD;border-radius:4px;margin-left:4px;font-weight:600;vertical-align:middle;cursor:pointer;font-family:inherit;line-height:inherit;" title="${escapeHtml(titleAttr)}">
    <span class="material-icons" style="font-size:12px;">${icon}</span>${escapeHtml(label)}
  </button>`
    : `<span style="display:inline-flex;align-items:center;gap:3px;padding:1px 6px;font-size:0.65rem;background:${bg};color:${fg};border-radius:4px;margin-left:4px;font-weight:600;vertical-align:middle;" title="${escapeHtml(titleAttr)}">
    <span class="material-icons" style="font-size:12px;">${icon}</span>${escapeHtml(label)}${escapeHtml(extraSuffix)}
  </span>`;
  // TASK-305 — When the parent report is still pending but the MVR
  // sub-screening completed early (TASK-299 propagates it), surface that
  // result as a second pill so the list view matches reality + matches the
  // MVR-complete email already firing. Repro: Malachi King 2026-05-18 —
  // candidate.backgroundReports.mvr.status='flagged' (result=consider)
  // while parent stays 'pending' for ~16 days waiting on county criminal.
  let mvrEarlyChip = '';
  if ((reportStatus === 'pending' || reportStatus === '') && !assessment) {
    // TASK-535 (S3) — honor getReportStatus precedence instead of reading the
    // raw `.status`, so a manual override / Checkr derivation is reflected here
    // exactly as the report card shows it (no stale raw value).
    const mvrStatus = (getReportStatus(c, 'mvr') || '').toLowerCase();
    if (mvrStatus === 'clear') {
      mvrEarlyChip = `<span style="display:inline-flex;align-items:center;gap:3px;padding:1px 6px;font-size:0.65rem;background:#D1FAE5;color:#059669;border-radius:4px;margin-left:4px;font-weight:600;vertical-align:middle;" title="MVR completed clear ahead of full report">
        <span class="material-icons" style="font-size:12px;">directions_car</span>MVR Clear
      </span>`;
    } else if (mvrStatus === 'flagged') {
      mvrEarlyChip = `<span style="display:inline-flex;align-items:center;gap:3px;padding:1px 6px;font-size:0.65rem;background:#FEE2E2;color:#991B1B;border-radius:4px;margin-left:4px;font-weight:600;vertical-align:middle;" title="MVR completed with consider/review result — needs admin triage">
        <span class="material-icons" style="font-size:12px;">directions_car</span>MVR Consider
      </span>`;
    } else if (mvrStatus === 'received') {
      mvrEarlyChip = `<span style="display:inline-flex;align-items:center;gap:3px;padding:1px 6px;font-size:0.65rem;background:#FFEDD5;color:#9A3412;border-radius:4px;margin-left:4px;font-weight:600;vertical-align:middle;" title="MVR returned, awaiting admin review">
        <span class="material-icons" style="font-size:12px;">directions_car</span>MVR Received
      </span>`;
    }
  }
  return mainChip + mvrEarlyChip;
}

// TASK-10029 — "Ready to run check" chip target: open the applicant, then bring
// the Background Check card's Run button into view (the card renders after the
// modal, so poll briefly; give up quietly — the modal is open either way).
async function openCandidateBgRun(id) {
  if (!id) return;
  await viewCandidate(id);
  for (let i = 0; i < 30; i++) {
    const btn = document.getElementById(`hr-run-bg-${id}`);
    if (btn) {
      btn.scrollIntoView({ block: 'center', behavior: 'smooth' });
      try { btn.focus({ preventScroll: true }); } catch (_) { /* non-fatal */ }
      return;
    }
    await new Promise(r => setTimeout(r, 100));
  }
}

// TASK-650-FF — client-side mirror of the server's hasConsumerReportOnFile
// (functions/src/scheduled/expireStaleApplicants.js). A candidate who has had a
// consumer report (MVR/PSP/CDLIS/Clearinghouse/criminal/Checkr) obtained is
// deliberately EXCLUDED from auto-expiry — silently lapsing a post-report
// applicant would be a constructive FCRA adverse action. Keep this field set in
// lockstep with the server helper.
// TASK-1152 → TASK-1155 — the FACT-BASED reportWasNotBasis stamp (report-bearing
// candidate declined for a NON-report reason) and its audit row are now
// computed SERVER-SIDE inside the setCandidateDecision callable /
// _applyCandidateRejection core (functions/src/index.js): firestore.rules
// blocks every client write of reportWasNotBasis*, so the old client-side
// _reportNotBasisStamp/_auditReportNotBasis were removed — the raw-SDK forgery
// gap they documented is closed.

// ── TASK-4584 REPORT-EVIDENCE STATUSES ───────────────────────────────────────
// ⚠️ DUPLICATED VERBATIM in functions/src/index.js, public/src/views/hr.js and
// public/src/views/compliance-partner.js. The two client files are separate
// bundles (Pitfall #1 — no cross-import of a `?v=` module) and the server can
// import from neither, so there is no shared home. tests/checkr-dead-invitation
// .unit.cjs slices all three copies by these sentinels and asserts they are
// BYTE-IDENTICAL. Mirrors the existing CDLIS_* duplication convention.
//
// THE QUESTION THIS ANSWERS: is a background-check STATUS STRING evidence that
// a consumer report exists? It is the third and weakest of the three paths the
// report-on-file predicates use. The other two — a reportId, and a
// backgroundReports.<screening>.status — are UNCHANGED and still catch every
// real report on their own. That layering is what keeps this narrowing safe.
//
// 🔴 WHY NARROW, AND ONLY THIS FAR. The predicate is deliberately broad ("an
// in-flight report still blocks a raw reject") and that is CORRECT for a LIVE
// invitation, which still counts exactly as it does today. But TASK-4584's
// 'invitation_deleted'/'invitation_expired' mean an invitation went out and is
// now DEAD with no report behind it. Counting those as report-bearing routes a
// candidate whose report NEVER RAN into the FCRA adverse-action flow. Before
// TASK-4584 the system could not tell a live invitation from a dead one at this
// level — there was no value to express it with. Now there is, so it must.
const BG_STATUS_NOT_REPORT_EVIDENCE = [
  'not_started', 'new',
  'invitation_deleted', 'invitation_expired',
];
function bgStatusIsReportEvidence(s) {
  const v = String(s === null || s === undefined ? '' : s).toLowerCase().trim();
  return !!v && BG_STATUS_NOT_REPORT_EVIDENCE.indexOf(v) === -1;
}
// ── END TASK-4584 REPORT-EVIDENCE STATUSES ───────────────────────────────────

function _hrHasConsumerReportOnFile(c) {
  if (!c) return false;
  const br = c.backgroundReports || {};
  for (const k of ['mvr', 'psp', 'cdlis', 'clearinghouse', 'criminal']) {
    if (bgStatusIsReportEvidence((br[k] || {}).status)) return true;
  }
  if (c.checkrReportId || c.latestReportId) return true;
  // TASK-4584 — same narrowing as the server fence this mirrors. It MUST move
  // in lockstep with functions/src/index.js `_applyCandidateRejection`: if the
  // client still called a dead invitation report-bearing, it would transform
  // Reject into "Start Adverse Action" and march the carrier into an AA wizard
  // the server would then refuse for having no report on file.
  return bgStatusIsReportEvidence(c.checkrReportStatus || c.bgCheckStatus);
}

// TASK-1155 (COD UX Lead, binding) — client mirror of the server fence: when
// the picked rejection reason is report-based (shared regex) AND a consumer
// report is on file, the Save/Reject CTA TRANSFORMS into "Start Adverse
// Action" so the AA wizard is the obvious path (extends the TASK-1152
// hard-route; the setCandidateDecision callable's jargon-free error stays the
// fallback, never the primary UX). No report on file → no transform: a
// report-sounding reason with no report is a plain decline (FCRA doesn't
// attach), matching the intercept's graceful fallback — TASK-428 boundary.
function _decisionCtaIsAdverseAction(candidateId, status, reason) {
  if (status !== 'rejected') return false;
  if (!_isReportBasedRejectionReason(reason)) return false;
  const cand = HRState.candidates.find(c => c.id === candidateId);
  return _hrHasConsumerReportOnFile(cand);
}
function _applyDecisionCta(btn, isAdverseAction, normalHtml) {
  if (!btn) return;
  if (isAdverseAction) {
    btn.innerHTML = '<span class="material-icons">gavel</span> Start Adverse Action';
    btn.style.background = '#DC2626';
    btn.style.borderColor = '#DC2626';
    btn.title = 'This reason relates to the background report — federal law (FCRA) requires the Adverse Action notices first. This opens the wizard that walks you through them.';
  } else {
    btn.innerHTML = normalHtml;
    btn.style.background = '';
    btn.style.borderColor = '';
    btn.title = '';
  }
}
function syncQuickStatusDecisionCta() {
  const btn = document.getElementById('quick-status-save-btn');
  if (!btn) return;
  const id = document.getElementById('quick-status-candidate-id')?.value;
  const status = document.getElementById('quick-status-select')?.value;
  const reason = document.getElementById('quick-rejection-reason')?.value || '';
  _applyDecisionCta(btn, _decisionCtaIsAdverseAction(id, status, reason),
    '<span class="material-icons">save</span> Save');
}
function syncCandidateFormDecisionCta() {
  const btn = document.getElementById('candidate-form-save-btn');
  if (!btn) return;
  const status = document.getElementById('candidate-status')?.value;
  const reason = document.getElementById('candidate-rejection-reason')?.value || '';
  const normalHtml = `<span class="material-icons">save</span> ${HRState.editingCandidateId ? 'Save Changes' : 'Add Candidate'}`;
  _applyDecisionCta(btn, _decisionCtaIsAdverseAction(HRState.editingCandidateId, status, reason), normalHtml);
}

// TASK-650-FF — informational nudge on a PENDING candidate that will NOT
// auto-expire because a consumer report is on file. Without it the carrier may
// think auto-expire is broken and never learn they must formally REJECT
// (adverse-action flow) rather than let the candidate linger. Caller gates on
// pending (non-terminal / non-archived) so terminal candidates don't show it.
function _actionRequiredReportOnFileBadge(c) {
  if (!_hrHasConsumerReportOnFile(c)) return '';
  return `<span class="hiring-report-on-file-badge" style="display:inline-flex;align-items:center;gap:3px;padding:1px 6px;font-size:0.65rem;background:#FEF3C7;color:#92400E;border-radius:4px;font-weight:600;vertical-align:middle;" title="A background report is on file, so this won't auto-expire. If you're declining BECAUSE of the report, use Reject — Adverse Action (FCRA). If your reason is unrelated to the report, a normal decline is fine.">
    <span class="material-icons" style="font-size:12px;">flag</span>Report on file — needs a decision
  </span>`;
}

// Initials: split on whitespace (handles "O'Connor", hyphens, apostrophes correctly),
// take first letter of first two space-separated words, uppercased. Empty → "?".
function _candidateInitials(name) {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const letters = parts.slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('');
  return letters || '?';
}

// BG decision badge config — shared by the list-card "last decision" pill
// and the in-modal `_buildBgDecisionPanel`. Lifted to module scope v1.15.491.
const _BG_DECISION_BADGES = {
  cleared: { color: '#065f46', bg: '#d1fae5', label: 'Cleared — Hiring Continued', icon: 'check_circle' },
  held: { color: '#78350f', bg: '#fef3c7', label: 'On Hold — Clarification Requested', icon: 'pause_circle' },
  awaiting_review: { color: '#1e3a8a', bg: '#dbeafe', label: 'Candidate Replied — Awaiting Re-review', icon: 'mark_email_unread' },
  adverse_action_initiated: { color: '#991b1b', bg: '#fee2e2', label: 'Adverse Action Initiated (Checkr)', icon: 'gavel' },
  aa_pre_notice_sent: { color: '#9A3412', bg: '#FFEDD5', label: 'Adverse Action — Pre-Notice Sent (5BD wait)', icon: 'schedule' },
  aa_finalized: { color: '#475569', bg: '#F1F5F9', label: 'Adverse Action Finalized', icon: 'gavel' },
};

// Pull the "last decision" entry from the bg history map for one candidate.
// Returns { label, byEmail, atStr, color, bg } or null when no decisions yet.
function _lastDecisionForCandidate(candidateId) {
  const rec = HRState.bgRecordsByCandidateId?.[candidateId];
  const history = Array.isArray(rec?.bgDecisionHistory) ? rec.bgDecisionHistory : [];
  if (history.length === 0) return null;
  const last = history[history.length - 1];
  const atTs = last.at?.toDate ? last.at.toDate() : (last.at?.seconds ? new Date(last.at.seconds * 1000) : null);
  const atStr = atTs
    ? atTs.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : '—';
  const cfg = _BG_DECISION_BADGES[last.decision] || null;
  const label = (cfg?.label || last.decision || '(unknown)').replace(/ — .*$/, '');
  return {
    label,
    byEmail: last.byEmail || last.by || 'unknown',
    atStr,
    color: cfg?.color || 'var(--text-secondary)',
    bg: cfg?.bg || '#f3f4f6',
  };
}

// v1.15.512 Phase A — Aging dot indicator for candidates stuck in a non-terminal
// stage longer than 5 days. Amber at 5+ days, red at 10+ days. Uses
// `statusChangedAt` (stamped by saveQuickStatus / saveCandidateForm) and falls
// back to `createdAt` for legacy records (no migration needed). Suppressed for
// terminal stages (hired/rejected/withdrawn) — they're "done" and shouldn't nag.
function _candidateAgingDotHtml(c) {
  const TERMINAL = new Set(['hired', 'rejected', 'withdrawn']);
  if (!c || TERMINAL.has(c.status)) return '';
  const ms = c.statusChangedAt?.toMillis?.()
          ?? (c.statusChangedAt?.seconds ? c.statusChangedAt.seconds * 1000 : null)
          ?? c.createdAt?.toMillis?.()
          ?? (c.createdAt?.seconds ? c.createdAt.seconds * 1000 : null)
          ?? 0;
  if (!ms) return '';
  const ageMs = Date.now() - ms;
  const ageDays = Math.floor(ageMs / 86400000);
  if (ageDays < 5) return '';
  // Red ≥10d, amber 5-9d.
  const isRed = ageDays >= 10;
  const color = isRed ? '#DC2626' : '#D97706';
  const title = `Stuck ${ageDays} day${ageDays === 1 ? '' : 's'} in this stage`;
  return `<span class="hiring-aging-dot" style="background:${color};" title="${escapeHtml(title)}" aria-label="${escapeHtml(title)}"></span>`;
}

// v1.15.491 — Roster-style horizontal row cards for the Hiring/Applicants list.
// Mirrors `public/src/views/roster.js` renderPeopleList() structure: a vertical
// stack (`.roster-list`) of `.roster-card.roster-card-clickable` rows. Each row:
//   [stage-tinted 48px initials avatar] [name + meta chips] [actions] [chevron]
// Click anywhere on the row → viewCandidate (matches Roster's edit-on-click).
// Preserves everything the old table/mobile-cards surfaced:
//   - Initials avatar tinted by PIPELINE_STAGES color
//   - Status-colored left border (stronger 4px to read like Roster's compliance-alert)
//   - Source chip + last-decision pill (when peopleBackgroundChecks has history)
//   - Quick-action icons (call/email/resume/swap-status) in the actions cluster
//   - Muted "—" for missing Position / CDL (now in meta row, opacity 0.35)
function renderCandidateRosterList(candidates) {
  return `
    <div class="roster-list hiring-roster-list">
      ${candidates.map(c => {
        const stage = stageForCandidate(c); // TASK-1360 — was `|| PIPELINE_STAGES[0]`, which badged drafts "New"
        const sourceLabel = CANDIDATE_SOURCES.find(s => s.id === c.source)?.label || c.source || '';
        const stageColor = stage.color;
        const initials = escapeHtml(_candidateInitials(c.name));
        const nameSafe = escapeHtml(c.name || '(unnamed)');
        const positionSafe = escapeHtml(c.position || '');
        const cdlSafe = escapeHtml(resolveCandidateLicense(c).cdlClass || '');
        const sourceSafe = escapeHtml(sourceLabel);
        const phoneDigits = c.phone ? c.phone.replace(/[^\d+]/g, '') : '';
        const phoneDisplay = c.phone ? escapeHtml(formatPhoneDisplay(c.phone)) : '';
        const emailSafe = escapeHtml(c.email || '');
        const resumeUrl = c.resumeUrl || '';
        const lastDecision = _lastDecisionForCandidate(c.id);
        const lastDecisionPill = lastDecision ? `
          <span class="hiring-last-decision-pill" style="background:${lastDecision.bg};color:${lastDecision.color};" title="Last decision by ${escapeHtml(lastDecision.byEmail)} on ${escapeHtml(lastDecision.atStr)}">
            <span class="material-icons" style="font-size:13px;">history</span>
            ${escapeHtml(lastDecision.label)}
          </span>
        ` : '';
        const agingDot = _candidateAgingDotHtml(c);
        // TASK-1219 — upcoming-interview chip on the Hiring list row.
        const interviewChip = _candInterviewUpcoming(c) ? `
          <span class="hiring-interview-chip" style="display:inline-flex;align-items:center;gap:3px;padding:1px 6px;font-size:0.68rem;background:#DBEAFE;color:#1E3A8A;border-radius:4px;font-weight:600;vertical-align:middle;" title="Interview ${escapeHtml(_fmtInterviewLong(_candInterviewMs(c)))}${c.interviewerName ? ' with ' + escapeHtml(c.interviewerName) : ''}">
            <span class="material-icons" style="font-size:12px;">event</span>Interview: ${escapeHtml(_fmtInterviewShort(_candInterviewMs(c)))}
          </span>` : '';
        // TASK-650 — archived (auto-expired or manually archived) candidates get
        // a dim treatment, an "Archived" badge, and Restore/Delete actions in
        // place of the normal pipeline actions.
        const archived = isArchivedCandidate(c);
        // TASK-1216 — soft-deleted candidates are labeled "Deleted" (distinct
        // from Auto-expired / Archived) in the archived view.
        const _archLabel = c.archivedReason === 'auto_expired' ? 'Auto-expired' : (c.archivedReason === 'deleted' ? 'Deleted' : (c.archivedReason === 'duplicate' ? 'Duplicate' : 'Archived'));
        const _archTitle = c.archivedReason === 'auto_expired' ? 'Auto-archived after a long period of inactivity' : (c.archivedReason === 'deleted' ? 'Deleted (kept in archive — restore to undo, or Delete forever to destroy)' : (c.archivedReason === 'duplicate' ? 'Archived as a duplicate of another application from the same person (TASK-9761)' : 'Manually archived'));
        const archivedBadge = archived ? `<span class="candidate-status-badge" style="margin-left:6px;background:${c.archivedReason === 'deleted' ? '#FEE2E2' : '#E5E7EB'};color:${c.archivedReason === 'deleted' ? '#991B1B' : '#4B5563'};" title="${_archTitle}. Restore to bring it back into the pipeline.">${_archLabel}</span>` : '';
        // TASK-650-FF — only nudge PENDING candidates (terminal/archived don't auto-expire anyway).
        const _terminalStatus = ['hired', 'rejected', 'withdrawn'].includes(c.status);
        const reportOnFileBadge = (!archived && !_terminalStatus) ? _actionRequiredReportOnFileBadge(c) : '';
        return `
        <div class="roster-card roster-card-clickable hiring-card${archived ? ' hiring-card-archived' : ''}" data-id="${c.id}" onclick="window.HRModule.viewCandidate('${c.id}')" style="border-left:4px solid ${archived ? '#9CA3AF' : stageColor};${archived ? 'opacity:0.7;' : ''}">
          <div class="roster-card-avatar" style="background:${archived ? '#9CA3AF' : stageColor};color:#fff;" aria-hidden="true">
            ${initials}
          </div>
          <div class="roster-card-info">
            <div class="roster-card-name">
              ${nameSafe}
              <!-- TASK-1360 (COD Code Reviewer) — the class used to interpolate
                   c.status RAW. saveDriverApplication persists a client-supplied
                   status from the PUBLIC unauthenticated apply form with only a
                   default, so that string was attacker-influenced and landed
                   inside a class attribute. Use the NORMALIZED stage id, which
                   can only ever be one of the canonical pipeline ids. -->
              <span class="candidate-status-badge status-${stage.id}" style="margin-left:6px;">${stage.label}</span>
              ${archivedBadge}
              ${_hrFoldChip(c)}
              ${_hrCandidateFlagChip(c.id)}
              ${_hrLicenseReadChip(c)}
              ${_hrApplicationAuditChip(c)}
              ${_hrNudgeChip(c)}
              ${agingDot}
              ${c.approvedToRoster
                ? '<span class="hiring-on-roster-badge">On Roster</span>'
                : '<span class="hiring-not-on-roster-badge" style="display:inline-flex;align-items:center;padding:1px 6px;font-size:0.65rem;background:#FEF3C7;color:#92400E;border-radius:4px;font-weight:600;" title="This candidate is not on your active roster yet. Approving the hire or clearing the background check does NOT add them — tap + Approve to Roster to create their roster record.">Not on roster yet</span>'}
              ${interviewChip}
              ${_bgChipForCandidate(c)}
              ${reportOnFileBadge}
              ${lastDecisionPill}
            </div>
            <div class="roster-card-meta">
              ${phoneDisplay ? `<span>${phoneDisplay}</span>` : ''}
              ${c.email ? `<span style="color:var(--text-secondary);">${emailSafe}</span>` : ''}
              <span class="${positionSafe ? '' : 'candidate-empty'}">${positionSafe || 'Position —'}</span>
              <span class="${cdlSafe ? '' : 'candidate-empty'}">${cdlSafe ? (_isCdlClassValue(cdlSafe) ? `CDL ${cdlSafe}` : `License ${cdlSafe}`) : 'CDL —'}</span>
              ${sourceSafe ? `<span class="candidate-source-chip">${sourceSafe}</span>` : ''}
              <span style="color:var(--text-secondary);">${formatCandidateDate(c.appliedDate)}</span>
            </div>
          </div>
          <div class="roster-card-actions">
            ${phoneDigits ? `<a class="btn-icon" href="tel:${phoneDigits}" onclick="event.stopPropagation();" title="Call ${phoneDisplay}"><span class="material-icons">phone</span></a>` : ''}
            ${c.email ? `<a class="btn-icon" href="mailto:${emailSafe}" onclick="event.stopPropagation();" title="Email ${emailSafe}"><span class="material-icons">mail</span></a>` : ''}
            ${resumeUrl ? `<a class="btn-icon" href="${_safeHttpUrl(resumeUrl)}" target="_blank" rel="noopener" onclick="event.stopPropagation();" title="Open resume"><span class="material-icons">description</span></a>` : ''}
            ${archived ? `
              <button class="btn-icon" onclick="event.stopPropagation();window.HRModule.restoreCandidate('${c.id}')" title="Restore to pipeline">
                <span class="material-icons">unarchive</span>
              </button>
              <button class="btn-icon" onclick="event.stopPropagation();window.HRModule.deleteCandidateForever('${c.id}')" title="Delete forever (permanent — destroys the record and resume)" style="color:var(--status-danger);">
                <span class="material-icons">delete_forever</span>
              </button>
            ` : `
              <button class="btn-icon" onclick="event.stopPropagation();window.HRModule.openQuickStatusModal('${c.id}')" title="Quick status change">
                <span class="material-icons">swap_horiz</span>
              </button>
            `}
          </div>
          <span class="roster-card-chevron material-icons">chevron_right</span>
        </div>
      `;
      }).join('')}
    </div>
  `;
}

// --- CRUD ---
function openAddCandidateModal() {
  HRState.editingCandidateId = null;
  HRState.pendingResumeFile = null;
  const existing = document.getElementById('modal-candidate-form');
  if (existing) existing.remove();
  document.body.insertAdjacentHTML('beforeend', getCandidateModalHTML(null));
  document.getElementById('modal-candidate-form').classList.remove('hidden');
}

function openEditCandidateModal(id) {
  const candidate = HRState.candidates.find(c => c.id === id);
  if (!candidate) return;
  if (_hrArchivedStageBlock(candidate)) return; // TASK-9419
  HRState.editingCandidateId = id;
  HRState.pendingResumeFile = null;
  const existing = document.getElementById('modal-candidate-form');
  if (existing) existing.remove();
  document.body.insertAdjacentHTML('beforeend', getCandidateModalHTML(candidate));
  // TASK-4611 — the panel ships as a placeholder and the real state is fetched
  // from the server; nothing about it is decided from the candidate doc.
  _installAppDocViewDelegate();
  if (candidate && candidate.id) _loadCandidateAppDocs(candidate.id, candidate);
  document.getElementById('modal-candidate-form').classList.remove('hidden');
  // TASK-1155 — a rejected candidate opens with a preselected reason; sync CTA.
  syncCandidateFormDecisionCta();
  // 2026-05-13 — Chris UX-dead-end fix: BG-completion email deep-links to this
  // modal but the BG decision panel only existed on the HR Compliance tab /
  // BG report viewer. Async-fetch peopleBackgroundChecks/{candidateId} and,
  // if a BG record exists, render the same Clear/Hold/AA panel into a slot
  // at the top of the modal body. Reuses _buildBgDecisionPanel + the existing
  // setBgDecision/requestBgClarification handlers — no reimplementation.
  _maybeInjectBgDecisionIntoEditCandidate(id).catch(e => {
    console.warn('[HR] BG-decision injection failed (non-fatal):', e?.message || e);
  });
}

// TASK-371 (2026-05-28) — FCRA-aware rejection intercept. Shared helper for
// saveCandidateForm + saveQuickStatus paths. Returns a BG report-id (truthy)
// if the candidate has a background check on file that would trigger FCRA
// Adverse Action obligations, or null if no BG report (skip the intercept).
// 3-field fallback matches the existing pattern at _maybeInjectBgDecision
// (line below): peopleBackgroundChecks.latestReportId is canonical, with
// candidate.checkrReportId + candidate.bgCheckReportId as legacy fallbacks.
async function _fcraRejectionReportId(candidateId) {
  if (!candidateId || !HRState.tenantId) return null;
  try {
    const snap = await getDoc(doc(db, 'tenants', HRState.tenantId, 'peopleBackgroundChecks', candidateId));
    const bgData = snap.exists() ? (snap.data() || {}) : {};
    const candidate = HRState.candidates.find(c => c.id === candidateId) || {};
    return bgData.latestReportId || candidate.checkrReportId || candidate.bgCheckReportId || null;
  } catch (e) {
    console.warn('[HR] FCRA reject intercept — BG fetch failed (treating as no BG):', e?.message);
    return null;
  }
}

// TASK-371 — handle the FCRA intercept decision. Shows the centered confirm
// modal; on confirm, writes the audit row + hands off to the existing AA
// wizard; on cancel, returns false so the caller bails on the save. Caller
// should ONLY invoke this when reason === 'Failed background check' AND
// a report-id is present.
// TASK-1152 — the "decline for a reason unrelated to the report" path from the
// BG report viewer. Closes the viewer and opens the standard status-change modal
// preset to Rejected. From there: any REPORT-BASED reason still hard-routes to
// the AA wizard (the intercept), and any ops reason declines cleanly + gets the
// fact-based reportWasNotBasis stamp — so the honest choice is one obvious click,
// not a hunt, and the compliance guardrail is never bypassable.
function declineNotBasedOnReport(candidateId) {
  if (!candidateId) return;
  try { document.getElementById('modal-bg-report-viewer')?.remove(); } catch (_) {}
  try { closeModal('view-candidate'); } catch (_) {}
  openQuickStatusModal(candidateId);
  // Preselect Rejected so the reason picker is visible immediately.
  setTimeout(() => {
    const sel = document.getElementById('quick-status-select');
    if (sel && sel.value !== 'rejected') { sel.value = 'rejected'; sel.dispatchEvent(new Event('change')); }
    const grp = document.getElementById('quick-rejection-group');
    if (grp) grp.style.display = 'block';
  }, 60);
}

async function _fcraRejectionIntercept(candidateId, reportId, rejectionReason) {
  const route = await window.showConfirm(
    'Background Check rejection requires a legal notice',
    'When you decline a candidate based on a background-check report, federal law (FCRA) requires a Pre-Adverse Action notice with a 5-business-day dispute window before the rejection becomes final. DispatchDeck has this built in — open the Adverse Action Wizard to start.',
    { confirmText: 'Open Adverse Action Wizard', cancelText: 'Pick a different reason' }
  );
  if (!route) return false;
  try {
    window.logAudit?.('hr', 'CANDIDATE_REJECTION_ROUTED_TO_AA', {
      entityType: 'candidate',
      entityId: candidateId,
      candidateId,
      reportId,
      rejectionReason,
      uid: window.AppState?.user?.uid || null,
      userEmail: window.AppState?.user?.email || null,
    });
  } catch (auditErr) {
    console.warn('[HR] FCRA intercept audit log skipped:', auditErr?.message);
  }
  try {
    await adverseActionBgDecision(candidateId, reportId);
  } catch (waErr) {
    console.error('[HR] FCRA intercept — AA wizard launch failed:', waErr);
    window.showToast?.('Could not open Adverse Action Wizard — please open it from the candidate detail.', 'error');
  }
  return true;
}

async function _maybeInjectBgDecisionIntoEditCandidate(candidateId) {
  if (!candidateId || !HRState.tenantId) return;
  const slot = document.getElementById('candidate-bg-decision-slot');
  if (!slot) return;
  let bgData = null;
  try {
    const snap = await getDoc(doc(db, 'tenants', HRState.tenantId, 'peopleBackgroundChecks', candidateId));
    if (snap.exists()) bgData = snap.data() || null;
  } catch (e) {
    // Permission errors here mean this admin can't read BG checks — leave slot empty.
    console.warn('[HR] BG-decision fetch failed', e?.message || e);
    return;
  }
  if (!bgData) return; // no BG check on file → no panel
  // Mirror the candidate-doc Embed-flow fallback used by openBgReportViewer.
  // Need a reportId for buttons; latestReportId is the canonical key.
  const candidate = HRState.candidates.find(c => c.id === candidateId) || {};
  const reportId = bgData.latestReportId || candidate.checkrReportId || null;
  if (!reportId) return; // BG invited but never completed → no decision to make
  const decision = bgData.bgDecision || null;
  const history = Array.isArray(bgData.bgDecisionHistory) ? bgData.bgDecisionHistory : [];
  // Stash viewer context so the reload-after-action path in clearBgDecision/
  // holdBgDecision/adverseActionBgDecision (which calls openBgReportViewer)
  // has a valid (bgRecordId, reportId) to resolve.
  window._bgViewerContext = { bgRecordId: candidateId, reportId };
  // Wrap in a header so the user understands what this section is — the
  // email said "review the BG check" and now they can see it inline.
  const completedAt = bgData.completedAt?.toDate ? bgData.completedAt.toDate()
                    : bgData.completedAt?.seconds ? new Date(bgData.completedAt.seconds * 1000)
                    : null;
  const completedStr = completedAt ? completedAt.toLocaleString() : '';
  const verdict = bgData.assessment || bgData.checkrAssessment || bgData.result || '';
  const verdictPill = verdict ? `
    <span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:6px;font-size:0.72rem;font-weight:600;background:${
      verdict === 'eligible' ? '#d1fae5' : (verdict === 'review' || verdict === 'escalated') ? '#fef3c7' : '#fee2e2'
    };color:${
      verdict === 'eligible' ? '#065f46' : (verdict === 'review' || verdict === 'escalated') ? '#78350f' : '#991b1b'
    };">${escapeHtml(verdict)}</span>` : '';
  slot.innerHTML = `
    <div style="background:#FAF5FF;border:1px solid #DDD6FE;border-radius:8px;padding:12px 14px;margin-bottom:var(--spacing-lg);">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;margin-bottom:10px;">
        <div style="font-weight:700;font-size:0.92rem;color:#5B21B6;display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;">fact_check</span> Background Check Returned ${verdictPill}
        </div>
        <button type="button" class="btn btn-sm" onclick="window.HRModule.openBgReportViewer('${escJsAttr(candidateId)}','${escJsAttr(reportId)}')" style="padding:4px 10px;font-size:0.75rem;background:transparent;color:#9333EA;border:1px solid #9333EA;display:inline-flex;align-items:center;gap:4px;">
          <span class="material-icons" style="font-size:14px;">description</span> View Full Report
        </button>
      </div>
      ${completedStr ? `<div style="font-size:0.74rem;color:var(--text-secondary);margin-bottom:8px;">Completed ${escapeHtml(completedStr)}</div>` : ''}
      ${_buildBgDecisionPanel(candidateId, reportId, decision, history)}
    </div>`;
}

async function saveCandidateForm(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;
  const name = capPersonName(document.getElementById('candidate-name').value.trim());
  if (!name) { window.showToast?.('Name is required', 'error'); return; }

  const phone = (document.getElementById('candidate-phone').value || '').replace(/\D/g, '');
  const email = document.getElementById('candidate-email').value.trim();
  const position = document.getElementById('candidate-position').value.trim();
  const cdlClass = document.getElementById('candidate-cdl').value;
  const cdlEndorsements = Array.from(document.querySelectorAll('.candidate-endorsement:checked')).map(cb => cb.value);
  const yearsExperience = document.getElementById('candidate-experience').value ? parseInt(document.getElementById('candidate-experience').value) : null;
  const source = document.getElementById('candidate-source').value;
  const appliedDateVal = document.getElementById('candidate-applied-date').value;
  const status = document.getElementById('candidate-status').value;
  const rejectionReason = status === 'rejected' ? document.getElementById('candidate-rejection-reason').value : '';
  const notes = document.getElementById('candidate-notes').value.trim();
  const resumeUrlInput = document.getElementById('candidate-resume-url').value.trim();
  const resumeText = (document.getElementById('candidate-resume-text')?.value || '').trim();

  if (status === 'rejected' && !rejectionReason) {
    window.showToast?.('Please select a rejection reason', 'error');
    return;
  }

  // TASK-371 — FCRA-aware rejection intercept. If the user picked "Failed
  // background check" AND a BG report exists on this candidate, hand off to
  // the Adverse Action Wizard instead of writing status=rejected directly.
  // The wizard owns the regulated sequence (pre-adverse notice → 5-BD wait →
  // final adverse). Bail out either way: either the wizard takes over (status
  // write happens there) or the user picked a different reason (no write).
  if (status === 'rejected'
      && _isReportBasedRejectionReason(rejectionReason)  // TASK-1152 — was exact 'Failed background check'; now ALL report-based reasons hard-route to AA (closes the synonym bypass)
      && HRState.editingCandidateId) {
    const reportId = await _fcraRejectionReportId(HRState.editingCandidateId);
    if (reportId) {
      const routed = await _fcraRejectionIntercept(HRState.editingCandidateId, reportId, rejectionReason);
      if (routed) {
        // AA wizard launched — close this form, drop the in-flight save.
        try { closeModal('edit-candidate'); } catch (_) {}
        return;
      }
      // User picked "Pick a different reason" — keep modal open, no save.
      return;
    }
    // No BG report on file — fall through to normal save path (graceful
    // fallback for the rare edge case the spec calls out).
    console.warn('[HR] Failed-BG rejection picked but no BG report on file for', HRState.editingCandidateId);
  }

  // Duplicate check
  checkForDuplicates(name, phone, email, HRState.editingCandidateId);

  const data = {
    name,
    phone,
    email,
    position,
    cdlClass,
    cdlEndorsements,
    yearsExperience,
    source,
    appliedDate: appliedDateVal ? Timestamp.fromDate(new Date(appliedDateVal + 'T00:00:00')) : Timestamp.now(),
    status,
    rejectionReason,
    notes,
    resumeText,
    updatedAt: Timestamp.now()
  };

  // Set lastContactDate if status is a contact stage
  if (['phone_screen', 'interview', 'offer'].includes(status)) {
    data.lastContactDate = Timestamp.now();
  }

  // TASK-650 — editing a candidate IS activity: stamp lastActivityAt + clear
  // the pre-expiry warning dedup so the inactivity clock restarts.
  data.lastActivityAt = Timestamp.now();
  data.expiryWarningSentAt = null;

  // TASK-1155 — REJECTION decisions are SERVER-owned. firestore.rules now
  // blocks a client status→'rejected' transition, any non-empty rejectionReason
  // change, and every reportWasNotBasis* write (the TASK-1152 fact-based stamp
  // + its audit row moved into the setCandidateDecision callable, which also
  // enforces the Adverse-Action correlation server-side). Strip the decision
  // fields from the direct write and route them through the callable instead.
  // An UNCHANGED rejected status+reason stays in `data` — identical values
  // don't hit the rules diff, so benign edits of a rejected candidate work.
  const _prevCand = HRState.candidates.find(c => c.id === HRState.editingCandidateId);
  // TASK-9530 -- an outstanding application link locks the email (firestore.rules + the disabled
  // field). Never put it in the write, so a stored-vs-typed whitespace difference can't trip the rule.
  if (HRState.editingCandidateId && _hrInviteOutstanding(_prevCand)) delete data.email;
  // TASK-9425 — an ARCHIVED application keeps its stage. A status change is refused
  // (the rules would refuse it too); an ordinary edit still saves, with the stage
  // fields dropped from the write so an absent-vs-'' rejectionReason can't read as
  // a stage change to the archived fence.
  if (HRState.editingCandidateId && isArchivedCandidate(_prevCand)) {
    if (String(_prevCand.status || '') !== String(status || '')) {
      window.showToast?.(HR_ARCHIVED_READ_ONLY_MSG, 'warning');
      return;
    }
    delete data.status;
    delete data.rejectionReason;
  }
  const _decisionChanged = HRState.editingCandidateId && isArchivedCandidate(_prevCand) ? false : status === 'rejected'
    && (!HRState.editingCandidateId || !_prevCand || _prevCand.status !== 'rejected'
        || String(_prevCand.rejectionReason || '') !== rejectionReason);
  if (_decisionChanged) {
    delete data.status;
    delete data.rejectionReason;
  }

  // TASK-9141 — BYPASS #1. SAME CARVE-OUT SHAPE AS THE REJECTION DECISION ABOVE:
  // `status:'hired'` is not this form's to write directly, because hiring is the
  // moment the §382.601(d) receipt + §382 Clearinghouse consent must be carried
  // onto the person doc and only the promote flow does that. Strip it, let every
  // OTHER edit on the form save normally, then hand the user the promote modal
  // (routed after the save, at the bottom of the try). On the ADD path there is
  // no doc yet, so park the new candidate at Offer — the honest pre-hire stage —
  // and promote from there; mirrors the TASK-1155 create-at-'new' precedent.
  const _hireNeedsPromote = status === 'hired'
    && _hrHireIsBareFieldWrite(HRState.editingCandidateId ? _prevCand : null);
  if (_hireNeedsPromote) {
    if (HRState.editingCandidateId) delete data.status;
    else data.status = 'offer';
  }

  // TASK-4550 — SAME CARVE-OUT SHAPE AS THE REJECTION DECISION ABOVE, for the
  // same reason: some fields are not this form's to write directly. On a
  // SUBMITTED application, name/phone/email are signed §391.21 content, and a
  // silent updateDoc here rewrites what the applicant's certification attests
  // to. Strip them out and route them through correctApplicationFields, which
  // freezes the original, records before/after + reason + actor, and audits.
  // A draft application is untouched — nothing has been attested yet.
  const _attestedChanges = [];
  if (HRState.editingCandidateId && _prevCand && _prevCand.applicationComplete === true) {
    for (const [k, label] of [['name', 'Name'], ['phone', 'Phone'], ['email', 'Email']]) {
      if (String(_prevCand[k] == null ? '' : _prevCand[k]) !== String(data[k] == null ? '' : data[k])) {
        _attestedChanges.push({ path: k, value: String(data[k] == null ? '' : data[k]), label });
      }
    }
  }
  let _correctionReason = '';
  if (_attestedChanges.length) {
    _correctionReason = (document.getElementById('candidate-correction-reason')?.value || '').trim();
    if (_correctionReason.length < 5) {
      window.showToast?.('This application is signed — say why you are changing the ' + _attestedChanges.map(x => x.label.toLowerCase()).join(' / ') + '.', 'error');
      document.getElementById('candidate-correction-reason')?.focus();
      return;
    }
    for (const ch of _attestedChanges) delete data[ch.path];
  }

  const btn = e.target.querySelector('button[type="submit"]');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }
  // TASK-9530 — Add only: email the new candidate the finish-your-application link.
  const _sendLinkAfterAdd = !HRState.editingCandidateId && !!document.getElementById('candidate-send-app-link')?.checked;

  try {
    let candidateId = HRState.editingCandidateId;
    if (candidateId) {
      // Edit — decision first (it's the point of the save; if the server
      // refuses, e.g. AA required, nothing else is half-written), then the
      // benign fields.
      if (_decisionChanged) {
        const setDecision = httpsCallable(functions, 'setCandidateDecision');
        await setDecision({ tenantId: HRState.tenantId, candidateId, decision: 'rejected', rejectionReason });
      }
      // TASK-4550 — signed-content corrections FIRST, same ordering rationale as
      // the decision above: if the server refuses (view/upload-only member, bad
      // field), nothing else is half-written and the form is still on screen.
      if (_attestedChanges.length) {
        const correctFn = httpsCallable(functions, 'correctApplicationFields');
        await correctFn({
          tenantId: HRState.tenantId,
          candidateId,
          corrections: _attestedChanges,
          reason: _correctionReason,
        });
      }
      await updateDoc(doc(db, 'tenants', HRState.tenantId, 'candidates', candidateId), data);
      // TASK-1213 — edit-form status changes land in the Notes & Calls feed too
      // (best-effort; the save already succeeded).
      if (_prevCand && _prevCand.status !== status) {
        _logCandidateStatusFeedEntry(candidateId, _prevCand.status, status, {
          detail: status === 'rejected' ? rejectionReason : '',
        });
      }
    } else {
      // Add
      data.createdAt = Timestamp.now();
      data.createdBy = window.AppState?.user?.email || 'unknown';
      data.resumeUrl = '';
      data.resumeFileName = '';
      data.resumeStoragePath = '';
      // TASK-1155 — a candidate can't be CREATED as rejected (rules fence
      // closing the delete-and-recreate forgery path). Create in the default
      // stage, then record the decision through the callable right after.
      if (_decisionChanged) data.status = 'new';
      const docRef = await addDoc(collection(db, 'tenants', HRState.tenantId, 'candidates'), data);
      candidateId = docRef.id;
      if (_decisionChanged) {
        const setDecision = httpsCallable(functions, 'setCandidateDecision');
        await setDecision({ tenantId: HRState.tenantId, candidateId, decision: 'rejected', rejectionReason });
      }
    }

    // Handle resume upload
    if (HRState.pendingResumeFile) {
      await uploadResume(HRState.tenantId, candidateId, HRState.pendingResumeFile);
      HRState.pendingResumeFile = null;
    } else if (resumeUrlInput && !HRState.editingCandidateId) {
      // External URL for new candidates
      await updateDoc(doc(db, 'tenants', HRState.tenantId, 'candidates', candidateId), {
        resumeUrl: resumeUrlInput,
        resumeFileName: 'External Link',
        resumeStoragePath: ''
      });
    } else if (resumeUrlInput && HRState.editingCandidateId) {
      const existing = HRState.candidates.find(c => c.id === candidateId);
      if (!existing?.resumeStoragePath) {
        await updateDoc(doc(db, 'tenants', HRState.tenantId, 'candidates', candidateId), {
          resumeUrl: resumeUrlInput,
          resumeFileName: 'External Link',
          resumeStoragePath: ''
        });
      }
    }

    window.showToast?.(HRState.editingCandidateId ? 'Candidate updated' : 'Candidate added', 'success');
    closeModal('candidate-form');

    // TASK-9530 — the snapshot for the new doc may not have landed; seed the cache
    // so sendApplicationLink finds it (same reason as the hire hand-off below).
    if (_sendLinkAfterAdd && candidateId && !_hireNeedsPromote) {
      if (!HRState.candidates.some(c => c.id === candidateId)) {
        HRState.candidates.unshift({ id: candidateId, ...data });
      }
      if (String(data.email || '').includes('@')) await sendApplicationLink(candidateId);
      else window.showToast?.('Candidate added. No application link was sent because there is no email address.', 'warning');
    }

    // TASK-9141 — the benign edits have landed; now hand over the hire itself.
    // On the ADD path the onSnapshot for the brand-new doc has not arrived yet,
    // and openApproveToRosterModal returns SILENTLY when it can't find the
    // candidate in HRState — exactly the fail-quiet shape this task exists to
    // kill. Seed the local cache so the modal always opens.
    if (_hireNeedsPromote && candidateId) {
      if (!HRState.candidates.some(c => c.id === candidateId)) {
        HRState.candidates.unshift({ id: candidateId, ...data });
      }
      window.showToast?.(HIRE_NEEDS_PROMOTE_MSG, 'info');
      openApproveToRosterModal(candidateId);
    }
  } catch (error) {
    console.error('[HR] Error saving candidate:', error);
    // TASK-1155 — surface the callable's jargon-free message (e.g. the
    // Adverse-Action requirement) instead of a generic failure.
    const _fnMsg = String(error?.code || '').startsWith('functions/') ? (error?.message || '') : '';
    window.showToast?.(_fnMsg || 'Error saving candidate', 'error');
  } finally {
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = `<span class="material-icons">save</span> ${HRState.editingCandidateId ? 'Save Changes' : 'Add Candidate'}`;
      // TASK-1155 — restore the Adverse-Action CTA transform if still applicable
      // (e.g. the callable refused and the modal stayed open).
      syncCandidateFormDecisionCta();
    }
  }
}

// TASK-1216 — candidate delete is a SOFT delete (Chris hit the hard-delete +
// phantom-Undo trap live). Reuses the TASK-650 archive machinery: the doc gets
// archived:true + archivedReason:'deleted' (hidden from the normal list, shows
// as "Deleted" in the archived view), the resume Storage file is KEPT, and the
// toast carries a real 15s Undo wired to the existing restoreCandidate.
// Permanent destruction happens ONLY via deleteCandidateForever (the explicit
// "Delete forever" in the archived view, scary confirm preserved there).
// Domain note: driver applications are §391.21-adjacent hiring records —
// one-click permanent destruction was a records-retention foot-gun.
async function deleteCandidate(id) {
  const candidate = HRState.candidates.find(c => c.id === id);
  // Already archived (the archived-view trash) → this IS the permanent path.
  if (candidate && isArchivedCandidate(candidate)) return deleteCandidateForever(id);
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'candidates', id), {
      archived: true,
      archivedAt: Timestamp.now(),
      archivedReason: 'deleted',
      archivedBy: window.AppState?.user?.uid || null,
      deletedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    closeModal('view-candidate');
    // Distinct copy from the status-change Undo toast ("Moved X to Y") so the
    // two can't be confused when both are on screen.
    window.showToast?.('Application archived — Undo', 'success', {
      label: 'Undo',
      callback: () => {
        restoreCandidate(id).catch(err => {
          console.warn('[HR] Undo archive failed:', err);
          window.showToast?.('Undo failed — restore it from the archived view', 'error');
        });
      }
    });
  } catch (error) {
    console.error('[HR] Error archiving candidate:', error);
    window.showToast?.('Error archiving candidate', 'error');
  }
}

// TASK-1216 — the ONLY permanent-destruction path (archived view "Delete
// forever"). Keeps the scary confirm + the resume Storage purge here.
async function deleteCandidateForever(id) {
  const candidate = HRState.candidates.find(c => c.id === id);
  const nameSafe = candidate?.name || 'this candidate';
  const ok = window.showConfirm
    ? await window.showConfirm(
        'Delete this application forever?',
        `${nameSafe}'s application record and resume file will be permanently destroyed. This cannot be undone.`,
        { confirmText: 'Delete forever', cancelText: 'Cancel' })
    : confirm('Permanently delete this application? This cannot be undone.');
  if (!ok) return;
  try {
    // Delete resume from Storage if exists
    if (candidate?.resumeStoragePath) {
      try {
        await deleteObject(ref(storage, candidate.resumeStoragePath));
      } catch (e) { console.warn('[HR] Could not delete resume file:', e); }
    }
    await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'candidates', id));
    // TASK-1488 — remove the §391.21 SSN vault doc with its application.
    // Best-effort: rules allow admin / non-view-only hr-directory to delete;
    // a bare hr-module deleter leaves an orphan (server-side sweep can reap it).
    try {
      await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'candidatesTax', id));
    } catch (e) {
      console.warn('[HR] candidatesTax cleanup skipped (orphan vault doc left):', e?.message || e);
    }
    window.showToast?.('Application permanently deleted', 'success');
    closeModal('view-candidate');
  } catch (error) {
    console.error('[HR] Error deleting candidate:', error);
    window.showToast?.('Error deleting candidate', 'error');
  }
}

// TASK-650 — restore an archived / auto-expired candidate back into the
// pipeline. Clears the archive flags + the expiry-warning dedup stamp, and
// stamps lastActivityAt so the inactivity clock restarts from now (a restore
// IS activity — it shouldn't immediately re-expire on the next cron run).
async function restoreCandidate(id) {
  try {
    // Null the archive fields (firebase-config.js doesn't re-export deleteField,
    // and isArchivedCandidate treats a null/falsy archivedAt as "not archived").
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'candidates', id), {
      archived: false,
      archivedAt: null,
      archivedReason: null,
      archivedBy: null,
      deletedAt: null, // TASK-1216 — clears the soft-delete stamp too
      expiryWarningSentAt: null,
      lastActivityAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    window.showToast?.('Candidate restored', 'success');
  } catch (error) {
    console.error('[HR] Error restoring candidate:', error);
    window.showToast?.('Error restoring candidate', 'error');
  }
}

// ── Apply Link ──

// TASK-4725 — the Copy Apply Link picker is rendered by the SHARED
// window.JobOpenings.pickerHtml (the partner portal renders the same one), so the
// two can no longer drift. Two defects fixed by that move:
//   • every link now carries an explicit `&position=` (the CDL link used to be
//     bare, so it showed a different first screen than `&position=cdl`), and
//   • only the carrier's ENABLED application types are offered (the carrier copy
//     used to offer all three even when one was switched off).
// With job openings, the admin picks the OPENING and DD derives the bucket and
// adds `&opening=<id>` — the carrier no longer has to know which of three links a
// posting belongs on. "Manage openings" is added HERE by the carrier host; the
// partner host never renders it (the partner sees openings, it doesn't author them).
function copyApplyLink() {
  const JO = window.JobOpenings;
  const openings = window.AppState?.settings?.jobOpenings;
  const enabledRaw = window.AppState?.tenantData?.enabledApplicantPositionTypes;
  const hasOpenings = !!(JO && JO.active(openings).length);
  const choices = JO
    ? JO.pickerHtml(openings, enabledRaw, {
      name: 'apply-position-type',
      labelStyle: 'display:flex;align-items:flex-start;gap:8px;cursor:pointer;padding:12px;border:1px solid var(--border-light);border-radius:8px;',
    })
    : '';
  document.getElementById('apply-link-position-modal')?.remove();
  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'apply-link-position-modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;">
      <div class="modal-header">
        <h3><span class="material-icons" style="vertical-align:middle;margin-right:6px;">link</span>Copy Apply Link</h3>
        <button class="btn-icon" onclick="document.getElementById('apply-link-position-modal')?.remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:16px;max-height:70vh;max-height:70dvh;overflow-y:auto;">
        <p style="color:var(--text-secondary);font-size:0.9rem;margin-bottom:14px;">
          ${hasOpenings
            ? 'Pick the job this link is for. The application opens with that job selected and asks for the right information for it.'
            : 'Pick the position you\'re hiring for — this configures the application form (DOT-required fields shown for CDL drivers, simpler form for non-driver roles).'}
        </p>
        <div style="display:flex;flex-direction:column;gap:8px;">${choices}</div>
        ${''/* Same gate as the HR Settings button: the tenant-doc write the manager makes
               is owner/admin-only by rule, so a non-admin would only hit "Save failed". */}
        ${_hrEffectiveAdminSafe() ? `<button type="button" class="btn btn-secondary" style="width:100%;margin-top:12px;" onclick="document.getElementById('apply-link-position-modal')?.remove(); window.openJobOpeningsModal && window.openJobOpeningsModal()">
          <span class="material-icons" style="vertical-align:middle;margin-right:6px;font-size:18px;">work_outline</span>${hasOpenings ? 'Manage job openings' : 'Add job openings'}
        </button>` : ''}
        <button class="btn btn-primary" style="width:100%;padding:12px;margin-top:10px;" onclick="window.HRModule._copyApplyLinkConfirm()">
          <span class="material-icons" style="vertical-align:middle;margin-right:6px;">content_copy</span>Copy Link
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

function _copyApplyLinkConfirm() {
  const JO = window.JobOpenings;
  const tenantId = HRState.tenantId;
  const openings = window.AppState?.settings?.jobOpenings;
  const picked = document.querySelector('input[name="apply-position-type"]:checked');
  const value = picked?.value || 't:cdl';
  const base = `https://dispatchdeck.app/apply.html?tenant=${encodeURIComponent(tenantId || '')}`;
  const url = (JO && JO.applyUrl(base, value, openings)) || `${base}&position=cdl`;
  const label = JO ? JO.copiedLabel(value, openings) : 'Apply';
  navigator.clipboard.writeText(url).then(() => {
    window.showToast?.(`${label} apply link copied!`, 'success');
    document.getElementById('apply-link-position-modal')?.remove();
  }).catch(() => {
    window.prompt('Copy this link:', url);
    document.getElementById('apply-link-position-modal')?.remove();
  });
}

// ── Background Report Helpers ──

// TASK-435 — display labels are UPPERCASE status chips. 'ordered' = in-flight
// Checkr work → "RUNNING" (pulses). 'pending' = not yet initiated → "NOT YET RUN"
// (no pulse). KEYS ARE UNCHANGED — only .label is display copy.
const REPORT_STATUSES = {
  pending: { label: 'NOT YET RUN', icon: 'radio_button_unchecked', color: '#94A3B8', bg: '#F1F5F9' },
  ordered: { label: 'RUNNING', icon: 'hourglass_top', color: '#D97706', bg: '#FFFBEB' },
  received: { label: 'RECEIVED', icon: 'check_circle', color: '#059669', bg: '#D1FAE5' },
  clear: { label: 'CLEAR', icon: 'verified', color: '#059669', bg: '#D1FAE5' },
  flagged: { label: 'CONSIDER', icon: 'warning', color: '#DC2626', bg: '#FEE2E2' },
};
// TASK-9300 (3) — 'not_started' is the read-side twin of 'pending' (see getReportStatus):
// no row at all vs an explicitly chosen 'pending'. It is deliberately NOT a key of
// REPORT_STATUSES, because every <select> on this surface is built from
// Object.entries(REPORT_STATUSES) and the server whitelist (_BG_VALID_STATUSES) would
// reject it. These two helpers are the ONLY sanctioned way to ask "has this screening
// been run?" and "how do I render this status?" — a consumer that writes
// `=== 'pending'` silently drops the not_started arm (the §382.701 banner did, before).
function isReportNotYetRun(status) { return status === 'pending' || status === 'not_started'; }
function reportStatusMeta(status) {
  return REPORT_STATUSES[status] || (status === 'not_started' ? REPORT_STATUSES.pending : null);
}

// Which Checkr packages cover which Background Reports keys.
// CDLIS + Clearinghouse are both outside Checkr, but for DIFFERENT reasons
// (TASK-1534): Clearinghouse is queried FMCSA-direct; a standalone CDLIS report
// is BOUGHT from a compliance vendor (Crown buys TMFS Corporation's). Do not
// collapse the two into "FMCSA-direct" — that phrasing is how we ended up
// telling a partner no separate CDLIS report exists.
// PSP is package-specific (gated on Melissa enabling it on prod).
const CHECKR_PACKAGE_COVERS = {
  checkrdirect_basic_plus_criminal: [],
  checkrdirect_essential_criminal: [],
  checkrdirect_complete_criminal: [],
  driver_pro: ['mvr'],
  standard_mvr: ['mvr'],
  commercial_mvr: ['mvr'],
  mvr_commercial: ['mvr'],
  psp: ['psp'],
  dot_driver_full: ['mvr'],
  dot_driver_no_verify: ['mvr'],
  dd_dot_mvr_essential: ['mvr'],
  // TASK-380 — Commercial MVR + PSP bundle (motor_vehicle_report + FMCSA PSP).
  commercial_mvr_psp: ['mvr', 'psp'],
  // TASK-828 — Checkr packages built 2026-06-23. dd_commercial_mvr = pure MVR.
  // TASK-1534 — it does NOT cover the 'cdlis' row and never did: the live
  // /v1/packages probe shows motor_vehicle_report only. A standalone CDLIS report
  // is bought outside Checkr and lands on this candidate through the manual
  // upload + status path. dd_full_dot_driver = criminal + MVR + PSP.
  dd_commercial_mvr: ['mvr'],
  dd_full_dot_driver: ['mvr', 'psp'],
};

// TASK-1279 — single-source "does this package include an MVR" check, exported on
// window.HRModule so the compliance-module MVR section (Pitfall #1: no cross-import
// of hr.js) can gate its "Request MVR" button on the SAME covers-map without
// duplicating it.
function checkrPackageCoversMvr(slug) {
  return (CHECKR_PACKAGE_COVERS[slug] || []).includes('mvr');
}

// TASK-1384 — MVR invitation/report evidence from the peopleBackgroundChecks doc.
// Person-subject report state lives ONLY on this doc (the webhook's status
// dual-write targets candidate docs), and creating a REAL invitation deletes the
// person doc's bgCheckRequestedAt stamp server-side — so this doc is the only
// truth for "an invitation actually went to the driver". Exported on
// window.HRModule for compliance.js (Pitfall #1: no cross-import of hr.js).
// Returns:
//   'signature'  — invitation is out, the driver still has to complete/sign it
//   'processing' — the driver finished their part / the report is running
//   null         — no in-flight MVR evidence (none, terminal, or non-MVR package)
// bgDoc undefined (not fetched yet / read denied) ⇒ null — callers keep their
// subject-doc-only classification while unknown (never misclassify mid-load).
function mvrBgDocPendingKind(bgDoc) {
  if (!bgDoc || typeof bgDoc !== 'object') return null;
  const SIGNATURE = ['invited', 'created', 'needs_candidate_action'];
  const PROCESSING = ['pending', 'completed'];
  const kindOf = (statusLc, hasInvite, hasReport) => {
    // Integrated-gate Code Reviewer (❌): direct/evergreen orders create the
    // report WITHOUT an invitation (byPackage carries status+reportId only), so
    // requiring invitationId classified a RUNNING paid report as nothing-pending
    // and re-armed the paid button — a double-billing hole. A report in flight
    // is 'processing' whether or not an invitation ever existed.
    if (SIGNATURE.includes(statusLc)) return hasInvite ? 'signature' : (hasReport ? 'processing' : null);
    if (PROCESSING.includes(statusLc)) return (hasInvite || hasReport) ? 'processing' : null;
    // TASK-4584 — 'invitation_deleted'/'invitation_expired' deliberately fall
    // through to null. They are NOT 'signature': the link they refer to no
    // longer exists at Checkr, so claiming "awaiting the driver's signature"
    // both keeps the paid Run-MVR button disabled forever and sends dispatch
    // chasing a Checkr email that was deleted. Do NOT add them to SIGNATURE.
    return null; // terminal (complete/clear/consider/canceled/expired/…) — nothing pending
  };
  // Scan EVERY MVR-covering byPackage entry, not just latestPackage — a later
  // non-MVR package (e.g. criminal-only) becomes latestPackage and would
  // otherwise hide a still-active MVR invitation, re-arming the paid button
  // (COD Code Reviewer). 'signature' outranks 'processing' when several match
  // (the state that still needs the driver is the one worth surfacing).
  let found = null;
  const byPackage = bgDoc.byPackage || {};
  for (const slug of Object.keys(byPackage)) {
    if (!checkrPackageCoversMvr(slug)) continue;
    const p = byPackage[slug] || {};
    const k = kindOf(String(p.status || '').toLowerCase(), !!p.invitationId, !!p.reportId);
    if (k === 'signature') return 'signature';
    if (k && !found) found = k;
  }
  if (found) return found;
  // Legacy latest* fallback (pre-byPackage tenants).
  if (!checkrPackageCoversMvr(bgDoc.latestPackage || '')) return null;
  return kindOf(String(bgDoc.latestStatus || '').toLowerCase(), !!bgDoc.latestInvitationId, !!(bgDoc.latestReportId || bgDoc.checkrReportId));
}

// TASK-675 — which report keys Checkr CAN drive at all (the UNION of every
// CHECKR_PACKAGE_COVERS value), regardless of THIS carrier's current package.
// A safety-critical result that Checkr is capable of producing (MVR / PSP) must
// NEVER be hand-set via a bare dropdown — even when Checkr isn't driving this
// particular row (partner ran it in Checkr's own dashboard, or the webhook→
// candidate stamp gap left it unlinked). Those rows route through the
// friction-gated, audited manual-record path (manualSetReportStatus → server
// overrideReportStatus with force + reason). CDLIS + Clearinghouse are NOT in
// this set: neither has an automated source we can call (TASK-1534 — Clearinghouse
// is FMCSA-direct; a standalone CDLIS is bought outside Checkr), so they keep
// the plain editable dropdown.
const CHECKR_AUTOMATABLE_REPORTS = new Set(['mvr', 'psp']);

// TASK-1093 — carrier master switch (Crown/Kolson): when
// settings.compliance.disableManualBgStatus is ON, the friction-gated manual
// status paths ("Set status manually" / "Override anyway" / "Already have one")
// are suppressed — statuses must come from a real Checkr run or an uploaded
// report. UI-only convenience; the REAL boundary is the server gate in
// _applyReportStatusOverride (functions/src/index.js), which rejects the
// hand-set write regardless of what the client renders.
function _manualBgStatusDisabled() {
  return window.AppState?.settings?.compliance?.disableManualBgStatus === true;
}
// Shared locked-state line rendered where the manual button would have been.
function _manualBgDisabledNoteHtml() {
  return `<div style="display:flex;align-items:center;gap:6px;font-size:0.7rem;color:var(--text-secondary);">
      <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">lock</span>
      <span>Manual entry disabled — run via Checkr or upload the report.</span>
    </div>`;
}

function getReportStatus(candidate, reportKey) {
  // Manual override wins if explicitly set to non-pending.
  const manualStatus = candidate.backgroundReports?.[reportKey]?.status;
  if (manualStatus && manualStatus !== 'pending') return manualStatus;

  // TASK-9300 (3) — the idle arm has TWO states, not one. No row at all (nobody has
  // touched this screening) is 'not_started'; a row someone explicitly set to
  // 'pending' stays 'pending'. Both read "NOT YET RUN" to the user, and both mean
  // the screening has not been run — so every consumer asks isReportNotYetRun(),
  // never `=== 'pending'` (the §382.701 gap banner and the "Self-reported ≠
  // verified" line both hang off that predicate). 'not_started' is read-side
  // only: the server whitelist has no such value and REPORT_STATUSES has no key
  // for it, so it can never be offered in a <select> or written back.
  const idle = manualStatus || 'not_started';

  // Otherwise derive from in-flight Checkr report when the package covers this key.
  const slug = (candidate.checkrPackageSlug || candidate.checkrPackage || '').toLowerCase();
  const covered = CHECKR_PACKAGE_COVERS[slug] || [];
  if (!covered.includes(reportKey)) return idle;

  const checkrStatus = (candidate.checkrReportStatus || candidate.bgCheckStatus || '').toLowerCase();
  if (checkrStatus === 'pending' || checkrStatus === 'running' || checkrStatus === 'in_progress') return 'ordered';

  const assessment = (candidate.checkrReportAssessment || candidate.bgCheckAssessment || '').toLowerCase();
  if (checkrStatus === 'complete' || checkrStatus === 'completed') {
    if (assessment === 'clear' || assessment === 'eligible') return 'clear';
    if (assessment === 'consider' || assessment === 'review') return 'flagged';
    return 'received';
  }
  return idle;
}

// TASK-9254 — A SCREENING ROW IS NOT THE REPORT'S VERDICT, and this screen only
// ever read rows.
//
// `getReportStatus` above returns `backgroundReports[key].status` the moment it is
// non-pending, before it reaches the consider/review→flagged mapping. That is
// CORRECT and deliberate: the server's per-screening writeback (checkrWebhook,
// TASK-1150) prefers the MVR sub-screening's OWN terminal result for the mvr row
// "so a criminal-bundle package's criminal-driven consider/review does NOT clobber
// a genuinely clear MVR" — and that same comment says the overall verdict "still
// drives the candidate's action-required state". Nothing on the hire path ever
// read the overall verdict, so the row label silently became the verdict.
//
// Live on 2026-09-16, Michael Jetton (`zmbVpQNwDU2QUUvP9gL6`, trippin-trucking):
// `backgroundReports.mvr.status = 'clear'` — his MVR genuinely was clear — under
// `checkrReportStatus = 'review'` and `checkrReportAssessment = 'review'`, with the
// package-level result `consider`. Approve to Roster rendered the GREEN banner,
// "All pre-employment reports clear", on a report Checkr is holding for a human.
//
// 🔴 ADDITIVE AND READ-ONLY BY DESIGN. It never downgrades a screening row, never
// writes, and never refuses a hire. TASK-428's rule holds — the fence belongs on
// the carry-forward, never on the hire; a recruiter who cannot hire at 4:55 on a
// Friday is the worse outcome. All this does is stop the green banner asserting a
// verdict that nobody on this screen ever rendered.
//
// 🔄 CORRECTED after COD (2026-09-16). The first cut of this predicate carried a
// comment claiming `bgDecision` "lives on a document the HR views never load".
// **That was wrong.** `HRState.bgRecordsByCandidateId` is populated from
// `peopleBackgroundChecks` and `_bgChipForCandidate` already reads it to suppress
// its own chip once a terminal decision is logged. Ignoring it meant this fired on
// a candidate somebody had ALREADY cleared — and made `confirmApproveToRoster`
// write "no bgDecision recorded" into a compliance audit row that was sometimes
// simply false. A false statement in an audit trail is worse than the missing
// warning this was written to fix. Found by COD Edge Case Hunter + Code Reviewer.
//
// Reads the decision the same way `_bgChipForCandidate` does — last entry of
// `bgDecisionHistory` — and falls back to the flat `bgDecision` the partner portal
// writes. Returns null when nothing is recorded.
function _hrBgTerminalDecision(candidate) {
  const rec = candidate && HRState.bgRecordsByCandidateId?.[candidate.id];
  if (!rec) return null;
  try {
    const hist = Array.isArray(rec.bgDecisionHistory) ? rec.bgDecisionHistory : null;
    const last = hist && hist.length > 0 ? hist[hist.length - 1]?.decision : null;
    return last || rec.bgDecision || null;
  } catch (_) { return rec.bgDecision || null; }
}

function _hrCheckrReportAwaitingAdjudication(candidate) {
  if (!candidate) return false;
  // Somebody already adjudicated this report — cleared, or moved into §615 adverse
  // action. Either way the decision exists and this is not an open question.
  const decided = _hrBgTerminalDecision(candidate);
  if (decided === 'cleared' || decided === 'adverse_action_initiated') return false;
  const status = String(candidate.checkrReportStatus || candidate.bgCheckStatus || '').toLowerCase();
  const assessment = String(candidate.checkrReportAssessment || candidate.bgCheckAssessment || '').toLowerCase();
  // 🔴 `'consider'` belongs in the STATUS set, not just the assessment set. The
  // webhook stores `checkrReportStatus = assessment || result || "completed"`
  // (`functions/src/index.js`, report.completed). A result-only consider with a
  // null assessment therefore lands as status `'consider'` with assessment `null` —
  // which the first cut of this predicate missed entirely, so the green banner it
  // was written to close still fired on exactly that shape. COD Edge Case Hunter.
  return status === 'review' || status === 'consider'
    || assessment === 'review' || assessment === 'consider';
}

function getReportRefInfo(candidate, reportKey) {
  const c = candidate;
  const dlNum = c.license?.number || c.dlNumber || '';
  const dlState = c.license?.state || c.dlState || '';
  const ssnLast4 = c.ssnMasked || c.ssn || '';
  const dob = c.dob || '';
  const name = c.firstName ? `${c.firstName} ${c.lastName || ''}`.trim() : (c.name || '');
  switch (reportKey) {
    case 'mvr':
    case 'cdlis':
      return dlNum ? `DL: ****${dlNum.slice(-4)} · ${dlState}` : (dlState ? `State: ${dlState}` : '');
    case 'psp':
      return name ? `${name}${dlState ? ' · ' + dlState : ''}` : '';
    case 'clearinghouse':
      return [ssnLast4 ? `SSN: ${ssnLast4}` : '', dob ? `DOB: ${dob}` : ''].filter(Boolean).join(' · ') || '';
    default: return '';
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// TASK-1534 — CDLIS JURISDICTION CORE (start)
// ───────────────────────────────────────────────────────────────────────────
// 🔴 THE BOUNDARY. A CDLIS report is the multi-jurisdiction POINTER record: one
// `TYPE: CURRENT (<state>)` row plus one `TYPE: AKA (<state>)` row for every
// other state the driver has held a CDL in. It is the INDEX that tells a carrier
// which states' MVRs §391.23(a)(1) obliges it to pull — so the state list is a
// REGULATED INPUT, not decoration. Kolsen (Crown) sent two TMFS Corporation
// samples proving the standalone artifact exists: a single-state MVR, and a
// CDLIS report carrying its own CDLIS ID and Requested/Completed dates whose
// whole body is that pointer record. The sample driver held a CURRENT CDL in one
// state and an AKA record in a second, non-adjacent one — a single-state MVR on
// that driver would have missed a jurisdiction outright.
//
// The uploaded PDF is NOT that input and never becomes it. The upload path
// (partnerUploadCarrierFile → partnerAttachCandidateReportDoc) validates `%PDF-`
// magic bytes and the content type — never the CONTENT, never the driver's
// identity. A stale, mismatched or wrong-driver CDLIS that silently decided a
// state did not need an MVR is a real §391.23 gap, not a display bug.
//
// So: every jurisdiction is a STRUCTURED row a HUMAN entered or confirmed off the
// report. A row with no `confirmedBy` is UNVERIFIED — it renders behind an
// explicit unverified marker and is EXCLUDED from every MVR obligation computed
// here (it is shown loudly for confirmation, never silently dropped). Nothing in
// this block parses a document, and nothing here may be changed to.
//
// 🔴 HOW FAR `confirmedBy` CAN BE TRUSTED TODAY — SLICE 1 IS DISPLAY ONLY.
// COD's Adversary lane caught this task about to commit its own original sin:
// claiming more than the evidence supports. There is NO server code for
// jurisdictions yet, so `confirmedBy` is NOT server-stamped — and
// firestore.rules grants `backgroundReports` wholesale to an HR-module holder
// and to a compliance-manager partner, with no sub-field fence. A client could
// therefore write a `confirmedBy` string it never earned and promote a
// jurisdiction out of the amber block into the red "MVR still needed" line.
// It is unreachable in practice right now — nothing anywhere writes
// `jurisdictions`, so every candidate renders the empty state — but it stops
// being unreachable the moment slice 2 lands. SLICE 2 IS THE GATE, not this
// file: the callable must derive `confirmedBy` server-side from the auth
// context and never accept it in the payload, and rules must fence
// `backgroundReports.cdlis.jurisdictions` to callable-only, mirroring the
// existing docPath / terminal-flip fences. Until both exist, treat a confirmed
// row as a display convention, not an authorization.
//
// 🏛️ COPY GUARDRAIL: CDLIS tells you WHERE TO LOOK. It does not substitute for
// pulling those states' MVRs (49 CFR §391.23(a)(1) / §391.25). No string in this
// block may drift toward asserting that a CDLIS report discharges that duty.
//
// 🏛️ LABEL TRUTH: the defensible statement is narrow — *Checkr* does not sell a
// standalone CDLIS (case 01668275 still unconfirmed); TMFS does and Crown buys
// it. Never write the general form ("there is no separate CDLIS report").
//
// ⚠️ This block is DUPLICATED VERBATIM in public/src/views/hr.js and
// public/src/views/compliance-partner.js. The two surfaces are separate bundles
// (Pitfall #1 — no cross-import of a `?v=` module) and the partner portal does
// not load personSections.js, so there is no shared home for it. This mirrors the
// existing HR_FMCSA_DIRECT_KEYS / FMCSA_DIRECT_KEYS duplication convention.
// tests/cdlis-jurisdictions.unit.cjs slices BOTH copies out by these sentinels,
// asserts they are byte-identical, and runs the same assertions against each.
// ═══════════════════════════════════════════════════════════════════════════
const CDLIS_SCOPE_NOTE = 'CDLIS shows where to look. It does not replace pulling an MVR from each state (49 CFR §391.23(a)(1) / §391.25).';
const CDLIS_UNVERIFIED_NOTE = 'Unverified — DispatchDeck never reads jurisdictions out of an uploaded file. A person has to confirm each one against the CDLIS report before it counts.';
const CDLIS_SOURCE_NOTE = 'Ordered outside Checkr (a compliance vendor or the state) and recorded here by hand.';
// TASK-9698 — Non-CDL Driver: there is no CDLIS report, so no report-based wording at all.
const CDLIS_UNVERIFIED_NOTE_NON_CDL = 'Unverified — a person has to confirm each state before it counts.';
// A Non-CDL row that already carries a file or a report id (recorded before the driver was
// classified) keeps it visible under a neutral name — never "CDLIS".
const CDLIS_NEUTRAL_RECORD = 'Prior-state record';
// TASK-9698 (Chris 2026-10-13, harvest-answers "crown-sid"; coordinator follow-up) — the list is
// KEPT and asks for every state with a license or permit in the last 3 years (§391.23(a)(1)
// covers ANY license, not only a CDL). For a CDL driver, Checkr's Commercial MVR searches CDLIS,
// which lists CDL states only, so a regular license is never found there — and a CDL state is
// never assumed covered either (a Checkr MVR can carry as few as one prior state, see TASK-9166
// below). A non-CDL driver (DqMatrix tier non_cdl_driver) has no CDLIS record at all, so the
// prompt drops the CDLIS sentence (opts.nonCdl). Drives-no-CMV and non-drivers get no list —
// the HOST decides that (DqMatrix.hasDqFile), this block only words it.
const CDLIS_EMPTY_NOTE = 'List every state where the driver held a regular (non-CDL) license or permit in the last 3 years. Checkr’s Commercial MVR searches CDLIS, which lists CDL states only, so a regular license is not found. Also add any CDL state the MVR did not return. Each state needs its own MVR (§391.23(a)(1)).';
const CDLIS_EMPTY_NOTE_NON_CDL = 'List every state where the driver held a license or permit in the last 3 years. Each state needs its own MVR (§391.23(a)(1)).';
const CDLIS_PANEL_TITLE = 'Other states — CDLIS and regular licenses, last 3 years';
const CDLIS_PANEL_TITLE_NON_CDL = 'Other states — licenses and permits, last 3 years';
const CDLIS_SCOPE_NOTE_NON_CDL = 'Each state needs its own MVR (49 CFR §391.23(a)(1)).';
// TASK-4549 — said plainly, because it is the whole point of the gate: adding a
// row is an attestation, and the name on it is not something this page chooses.
const CDLIS_ENTRY_NOTE = 'Adding a row records you as the person who checked it. Add one row for each state where the driver held a regular (non-CDL) license or permit in the last 3 years, and any CDL state the MVR did not return.';
const CDLIS_ENTRY_NOTE_NON_CDL = 'Adding a row records you as the person who checked it. Add one row for each state where the driver held a license or permit in the last 3 years.';
const CDLIS_ENTRY_TITLE = 'Add a state — regular (non-CDL) license or permit, last 3 years';
const CDLIS_ENTRY_TITLE_NON_CDL = 'Add a state — license or permit, last 3 years';

function _cdlisEsc(v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
// COD Code-Reviewer (integrated diff): this used to strip non-letters and take
// the first two characters, so "North Dakota" became the FAKE state "NO" — and a
// fake state reaches cdlisMvrGapStates() and is printed to a carrier as an MVR
// they are legally obliged to pull. Accept a real 2-letter jurisdiction or
// nothing; a value we cannot read is not a jurisdiction we may assert.
const _CDLIS_JURISDICTIONS = new Set(('AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO '
  + 'MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY '
  + 'PR VI GU AS MP '
  // AAMVA reciprocity: a CDLIS pointer can name a Canadian jurisdiction, and a
  // cross-border driver is exactly the case a single-state MVR misses.
  + 'AB BC MB NB NL NS NT NU ON PE QC SK YT').split(' '));
// A jurisdiction code is two letters or it is not a code. "North Dakota" is
// eleven characters — it used to be sliced down to the FAKE state "NO", which
// then reached the MVR-gap line as a driving record a carrier is told to pull.
function _cdlisState(v) {
  const s = String(v == null ? '' : v).trim().toUpperCase();
  return /^[A-Z]{2}$/.test(s) ? s : '';
}
// …but only a jurisdiction we RECOGNISE may carry an obligation. An unrecognised
// two-letter code still renders — in the unverified block, where a human sees it
// — because dropping it silently is the same §391.23 hazard as hiding an
// unconfirmed row: a jurisdiction nobody knows about is one nobody pulls.
function _cdlisKnownJurisdiction(s) {
  return _CDLIS_JURISDICTIONS.has(s);
}
function _cdlisFmtDate(v) {
  if (!v) return '';
  if (typeof v === 'string') return v.trim().slice(0, 40);
  let d = null;
  try {
    if (v.toDate) d = v.toDate();
    else if (v._seconds != null) d = new Date(v._seconds * 1000);
    else if (v.seconds != null) d = new Date(v.seconds * 1000);
    else d = new Date(v);
  } catch (_) { return ''; }
  return (d && !isNaN(d.getTime())) ? d.toISOString().slice(0, 10) : '';
}

// Normalize the stored structured list. Anything that is not a two-letter state
// is dropped; `confirmed` is derived ONLY from a non-empty `confirmedBy` — it is
// never read from the payload, so a caller cannot hand us a pre-confirmed row.
function cdlisNormalizeJurisdictions(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const state = _cdlisState(r.state);
    if (state.length !== 2) continue;
    const type = String(r.type || '').trim().toLowerCase() === 'current' ? 'current' : 'aka';
    const key = type + ':' + state;
    if (seen.has(key)) continue;
    seen.add(key);
    const confirmedBy = String(r.confirmedBy || '').trim();
    out.push({
      state, type,
      // Confirmation needs BOTH a human and a jurisdiction we can act on.
      confirmed: confirmedBy.length > 0 && _cdlisKnownJurisdiction(state),
      confirmedBy,
      confirmedAt: r.confirmedAt || null,
      // TASK-9438 — transcribed off the report; confirms nothing on its own.
      licenseNumber: typeof r.licenseNumber === 'string' ? r.licenseNumber.trim() : '',
      // TASK-4753 — server-derived provenance of a confirmed suggestion; display only.
      suggestedFrom: Array.isArray(r.suggestedFrom) ? r.suggestedFrom.filter((s) => typeof s === 'string') : [],
    });
  }
  out.sort((a, b) => (a.type === b.type
    ? (a.state < b.state ? -1 : a.state > b.state ? 1 : 0)
    : (a.type === 'current' ? -1 : 1)));
  return out;
}
// Only human-confirmed rows may drive an obligation.
function cdlisConfirmedStates(rows) {
  const out = [];
  for (const r of (rows || [])) if (r && r.confirmed && out.indexOf(r.state) === -1) out.push(r.state);
  return out;
}
function cdlisUnverifiedStates(rows) {
  const out = [];
  for (const r of (rows || [])) if (r && !r.confirmed && out.indexOf(r.state) === -1) out.push(r.state);
  return out;
}
// Confirmed jurisdictions with no MVR recorded against them. `coveredStates` is
// whatever the caller can actually prove (today: the DL state the MVR row was
// ordered against). UNVERIFIED rows never appear here — an unconfirmed pointer
// must not manufacture a §391.23 obligation, and must not silently satisfy one
// either; it is surfaced separately for a human to confirm.
function cdlisMvrGapStates(rows, coveredStates) {
  const covered = {};
  for (const s of (coveredStates || [])) {
    const st = _cdlisState(s);
    if (st.length === 2) covered[st] = true;
  }
  return cdlisConfirmedStates(rows).filter((s) => !covered[s]);
}

function _cdlisChip(row, covered) {
  const isCurrent = row.type === 'current';
  const tone = row.confirmed ? (covered ? '#059669' : '#B45309') : '#475569';
  const type = isCurrent ? 'CURRENT' : 'AKA';
  // COD Blast lane: an unrecognised two-letter code and a real-but-unconfirmed
  // jurisdiction were sitting in the same amber block, visually identical — so a
  // reader could take "listed" for "covered". Say which one it is. The code is
  // still shown (never silently dropped); it just cannot pass for a state we
  // know how to pull an MVR from.
  const unknown = !_cdlisKnownJurisdiction(row.state);
  const tail = unknown
    ? '<span style="font-weight:600;opacity:.75">' + type + ' &middot; unrecognised code</span>'
    : '<span style="font-weight:600;opacity:.75">' + type + '</span>';
  return '<span title="' + (unknown ? 'Not a jurisdiction DispatchDeck recognises - check it against the report' : '')
    + '" style="display:inline-flex;align-items:center;gap:4px;font-size:11px;font-weight:700;'
    + 'color:' + tone + ';background:#fff;border:1px ' + (unknown ? 'dashed' : 'solid') + ' ' + tone + '55;padding:2px 8px;border-radius:10px">'
    + _cdlisEsc(row.state) + tail + '</span>';
}

// TASK-9250 — WHAT THE DRIVER SAID ON THE SIGNED APPLICATION. apply.html asks
// every driver applicant whether they held a license or permit from any other
// state or province in the last 3 years (49 CFR 391.23(a)(1) — the regulation's
// own discovery source, independent of a CDLIS search). This block only SHOWS
// that answer next to the jurisdiction list: a self-reported state is a lead for
// a human to check, never a row. It creates no obligation, confirms nothing and
// never reaches cdlisMvrGapStates() — promoting a state is still the Add button
// below, which stamps the person who checked it (TASK-4549). opts.applicantStates
// = { held: true|false|null, states: [codes], licenseState: code, signed: bool }
// from the host; null / held null (an application from before the question)
// renders nothing. COD (integrated, Domain): a listed state with no row is the gap
// an auditor finds by reading the signed application, so it is shown in AMBER,
// not as a quiet note — still never an automatic row or obligation.
const CDLIS_APPLICANT_TITLE = 'On the signed application';
const CDLIS_APPLICANT_TITLE_DRAFT = 'Applicant\u2019s answer (application not submitted yet)';
function cdlisApplicantStatesHtml(rows, opts) {
  const a = (opts && opts.applicantStates) || null;
  if (!a || (a.held !== true && a.held !== false)) return '';
  const title = a.signed === true ? CDLIS_APPLICANT_TITLE : CDLIS_APPLICANT_TITLE_DRAFT;
  const box = (inner, warn) => '<div style="background:' + (warn ? '#FEF3C7' : '#EFF6FF') + ';border:1px solid ' + (warn ? '#F59E0B' : '#BFDBFE') + ';border-radius:6px;padding:7px 9px;font-size:11.5px;color:' + (warn ? '#92400E' : '#1E3A8A') + ';line-height:1.45">'
    + '<span style="font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase">' + _cdlisEsc(title) + '</span><br>' + inner + '</div>';
  if (a.held === false) {
    return box('The driver answered that they had no license or permit from any other state or province in the last 3 years.');
  }
  const lic = _cdlisState(a.licenseState);
  const listed = [];
  for (const s of (Array.isArray(a.states) ? a.states : [])) {
    const st = _cdlisState(s);
    if (st.length === 2 && listed.indexOf(st) === -1) listed.push(st);
  }
  if (!listed.length) {
    return box('The driver answered yes to a license or permit from another state or province in the last 3 years, but listed no state. Ask the driver which ones.', true);
  }
  const onRecord = {};
  for (const r of (rows || [])) if (r && r.state) onRecord[r.state] = true;
  const missing = listed.filter((s) => s !== lic && !onRecord[s]);
  return box('The driver listed a license or permit in the last 3 years from: <strong>' + _cdlisEsc(listed.join(', ')) + '</strong>.'
    + (missing.length
      ? ' Not on the list here yet: <strong>' + _cdlisEsc(missing.join(', ')) + '</strong>. Check each one, then confirm the suggestion or add it below; each state needs its own MVR (§391.23(a)(1)).'
      : ' Each one is already on the list here.'), missing.length > 0);
}

// TASK-4753 — SUGGEST AND CONFIRM (Chris 2026-09-22: "pre-fill the CDLIS
// jurisdictions for a person to confirm, never assert them"). DispatchDeck
// PROPOSES rows from data it already holds about the driver — the licence state
// on the signed application (and whether an MVR has come back for it), and the
// other states the driver listed under §391.21(b)(12) — so nobody has to retype
// them. 🔴 A suggestion is NOT a row: it is computed here, in the browser, from
// opts the host passes in, and it is never stored. It reaches no MVR gap, no
// checklist, no Checkr order and no export. It becomes a row only when a person
// clicks Confirm, which goes through the same callable / partner action as the
// Add button and is stamped server-side with that person as `confirmedBy`
// (plus `suggestedFrom`, which the SERVER re-derives from the candidate doc —
// the click cannot assert it). Edit copies the suggestion into the Add form so a
// person can change it before saving. Nothing here reads the uploaded file.
//
// Refuse-if-implausible, not just refuse-if-missing
// ([[lesson_confident_wrong_parse_defeats_refusal_guards]]):
//   • only a SIGNED application suggests anything — a draft is the driver's
//     unfinished answer, not a certified one;
//   • only a jurisdiction DispatchDeck recognises is suggested;
//   • nothing already on the record (as either type) is suggested again;
//   • CURRENT is suggested only when no CURRENT row exists — and if a different
//     CURRENT is already on record, the disagreement is SHOWN, never resolved;
//   • more than CDLIS_SUGGEST_MAX listed states in 3 years is not a list to
//     pre-fill one click at a time: it is withheld with a reason, and the person
//     enters them by hand.
const CDLIS_SUGGEST_MAX = 8;
const CDLIS_SUGGEST_TITLE = 'Suggested — not counted until you confirm';
const CDLIS_SUGGEST_NOTE = 'Pre-filled from what the driver reported on the signed application. A suggestion counts for nothing until you check it against the CDLIS report or the driver’s records. Confirm records you as the person who checked it; Edit lets you change it first. A state the driver left off will not appear here — the CDLIS report is how you find it.';
const _CDLIS_SUGGEST_SOURCE_LABELS = {
  application_license: 'license state the driver gave on the signed application',
  application_prior_states: 'the driver listed it on the signed application',
  mvr_license: 'an MVR came back for it',
};
function cdlisSuggestSourceText(sources) {
  const out = [];
  for (const s of (Array.isArray(sources) ? sources : [])) {
    if (Object.prototype.hasOwnProperty.call(_CDLIS_SUGGEST_SOURCE_LABELS, s)) out.push(_CDLIS_SUGGEST_SOURCE_LABELS[s]);
  }
  return out.join('; ');
}
// rows = cdlisNormalizeJurisdictions output. opts.applicantStates is the same
// object cdlisApplicantStatesHtml reads; opts.coveredStates the MVR-covered list.
// Returns { suggestions: [{state, type, sources}], notes: [string] }.
function cdlisSuggestedJurisdictions(rows, opts) {
  const o = opts || {};
  const a = o.applicantStates || null;
  const out = { suggestions: [], notes: [] };
  if (!a || a.signed !== true) return out;
  const onRecord = {};
  let currentOnRecord = '';
  for (const r of (rows || [])) {
    if (!r || !r.state) continue;
    onRecord[r.state] = true;
    if (r.type === 'current') currentOnRecord = r.state;
  }
  const covered = {};
  for (const s of (o.coveredStates || [])) { const st = _cdlisState(s); if (st) covered[st] = true; }
  const lic = _cdlisState(a.licenseState);
  if (lic && _cdlisKnownJurisdiction(lic)) {
    // The disagreement is checked on its own (COD Code Reviewer): the licence state
    // already sitting on the record as AKA must not hide a CURRENT that differs.
    if (currentOnRecord && currentOnRecord !== lic) {
      out.notes.push('The signed application gives ' + lic + ' as the license state, but ' + currentOnRecord + ' is recorded as CURRENT. Check the report — DispatchDeck will not pick one.');
    } else if (!currentOnRecord && !onRecord[lic]) {
      out.suggestions.push({ state: lic, type: 'current', sources: covered[lic] ? ['application_license', 'mvr_license'] : ['application_license'] });
    }
  }
  if (a.held === true) {
    const listed = [];
    for (const s of (Array.isArray(a.states) ? a.states : [])) {
      const st = _cdlisState(s);
      if (st && st !== lic && _cdlisKnownJurisdiction(st) && listed.indexOf(st) === -1) listed.push(st);
    }
    if (listed.length > CDLIS_SUGGEST_MAX) {
      out.notes.push('The application lists ' + listed.length + ' other states — more than DispatchDeck will pre-fill. Check the report and add each one by hand.');
    } else {
      for (const st of listed) if (!onRecord[st]) out.suggestions.push({ state: st, type: 'aka', sources: ['application_prior_states'] });
    }
  }
  return out;
}
// The suggestion rows. Rendered only for someone who may record (opts.canEdit),
// with data-cdlis-* hooks only — each host binds its own transport, same as Add.
function cdlisSuggestHtml(rows, opts) {
  const o = opts || {};
  const candId = String(o.candidateId || '').trim();
  if (!candId || !o.canEdit) return '';
  const s = cdlisSuggestedJurisdictions(rows, o);
  if (!s.suggestions.length && !s.notes.length) return '';
  const cand = _cdlisEsc(candId);
  const btn = 'font-size:12px;font-weight:600;padding:8px 14px;min-height:40px;border-radius:6px;background:#fff;cursor:pointer';
  const items = s.suggestions.map((x) => {
    const t = x.type === 'current' ? 'CURRENT' : 'AKA';
    const attrs = ' data-cdlis-sstate="' + _cdlisEsc(x.state) + '" data-cdlis-stype="' + _cdlisEsc(x.type) + '"';
    return '<div data-cdlis-suggestion="' + cand + '" style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;font-size:11.5px;padding:5px 0;border-top:1px dotted #94A3B855">'
      + '<span style="font-weight:700;min-width:24px;color:var(--text-primary)">' + _cdlisEsc(x.state) + '</span>'
      + '<span style="font-weight:600;font-size:10.5px;min-width:54px;color:var(--text-secondary)">' + t + '</span>'
      + '<span style="flex:1 1 140px;min-width:0;color:var(--text-secondary);overflow-wrap:anywhere">Suggested &middot; ' + _cdlisEsc(cdlisSuggestSourceText(x.sources)) + '</span>'
      + '<span style="display:inline-flex;gap:6px;flex:0 0 auto">'
      + '<button type="button" data-cdlis-sconfirm="' + cand + '"' + attrs
      + ' title="Record ' + _cdlisEsc(x.state) + ' as ' + t + '. Only confirm after checking it against the report or the driver’s records."'
      + ' style="' + btn + ';border:1px solid var(--brand-blue);color:var(--brand-blue)">Confirm</button>'
      + '<button type="button" data-cdlis-sedit="' + cand + '"' + attrs
      + ' style="' + btn + ';border:1px solid var(--border-medium);color:var(--text-primary)">Edit</button>'
      + '</span></div>';
  }).join('');
  const notes = s.notes.map((n) => '<div style="font-size:11px;color:#92400E;line-height:1.45">' + _cdlisEsc(n) + '</div>').join('');
  return '<div style="background:#F8FAFC;border:1px dashed #94A3B8;border-radius:6px;padding:7px 9px;display:flex;flex-direction:column;gap:4px">'
    + '<div style="font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--text-secondary)">' + _cdlisEsc(CDLIS_SUGGEST_TITLE) + '</div>'
    + items + notes
    + '<div style="font-size:10.5px;color:var(--text-secondary);line-height:1.4">' + _cdlisEsc(CDLIS_SUGGEST_NOTE) + '</div>'
    + '</div>';
}
// Edit: copy a suggestion into THIS card's Add form (state + type), focus the
// licence box, and leave the save to the person. Pure DOM, no transport — both
// hosts call it from their own listener.
function cdlisPrefillEntryFromSuggestion(btn) {
  if (!btn || !btn.closest) return false;
  const candId = btn.getAttribute('data-cdlis-sedit');
  const panel = btn.closest('[data-cdlis-panel]');
  if (!candId || !panel) return false;
  const state = _cdlisState(btn.getAttribute('data-cdlis-sstate'));
  const type = btn.getAttribute('data-cdlis-stype') === 'current' ? 'current' : 'aka';
  let sel = null; let typ = null; let lic = null;
  for (const el of panel.querySelectorAll('[data-cdlis-state]')) if (el.getAttribute('data-cdlis-state') === candId) sel = el;
  for (const el of panel.querySelectorAll('[data-cdlis-type]')) if (el.getAttribute('data-cdlis-type') === candId) typ = el;
  for (const el of panel.querySelectorAll('[data-cdlis-license]')) if (el.getAttribute('data-cdlis-license') === candId) lic = el;
  if (!sel || !typ || !state) return false;
  sel.value = state;
  typ.value = type;
  // COD r2 Code Reviewer: a number typed for another state must not ride along.
  if (lic) lic.value = '';
  try { sel.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) { /* old browser */ }
  try { (lic || sel).focus(); } catch (_) { /* detached */ }
  return true;
}
// Confirm: the list to send when a person confirms one suggestion. The callable
// REPLACES the list, so this is the stored rows plus the one row — flagged
// fromSuggestion so the server derives and records where it came from. Returns
// { next } or { error } (a reason to show), and never a confirmer: the server
// stamps that from whoever is signed in.
function cdlisRowsForConfirmedSuggestion(rows, state, type) {
  const st = _cdlisState(state);
  const ty = type === 'current' ? 'current' : 'aka';
  const list = Array.isArray(rows) ? rows : [];
  if (!st || !_cdlisKnownJurisdiction(st)) return { error: 'That suggestion is not a jurisdiction DispatchDeck recognises.' };
  if (list.some((r) => r && r.state === st)) return { error: st + ' is already on the record.' };
  const cur = list.find((r) => r && r.type === 'current');
  if (ty === 'current' && cur) return { error: 'This report already lists ' + cur.state + ' as the CURRENT jurisdiction. Remove it first if the report says otherwise.' };
  return { next: list.map((r) => ({ state: r.state, type: r.type })).concat([{ state: st, type: ty, fromSuggestion: true }]) };
}

// The CDLIS credential surface. `row` is candidate.backgroundReports.cdlis.
// opts: { coveredStates: [], hasDoc: bool }
function cdlisPanelHtml(row, opts) {
  const r = row || {};
  const o = opts || {};
  const nonCdl = o.nonCdl === true;
  const rows = cdlisNormalizeJurisdictions(r.jurisdictions);
  const confirmed = rows.filter((x) => x.confirmed);
  const unverified = rows.filter((x) => !x.confirmed);
  const coveredList = o.coveredStates || [];
  const covered = {};
  for (const s of coveredList) { const st = _cdlisState(s); if (st.length === 2) covered[st] = true; }
  const gaps = cdlisMvrGapStates(rows, coveredList);

  const reportId = String(r.cdlisReportId || r.reportId || '').trim().slice(0, 60);
  const requested = _cdlisFmtDate(r.requestedDate || r.requestedAt);
  const completed = _cdlisFmtDate(r.completedDate || r.completedAt);
  const idBits = [];
  if (reportId) idBits.push((nonCdl ? CDLIS_NEUTRAL_RECORD + ' ID ' : 'CDLIS ID ') + _cdlisEsc(reportId));
  if (requested) idBits.push('Requested ' + _cdlisEsc(requested));
  if (completed) idBits.push('Completed ' + _cdlisEsc(completed));
  const idLine = idBits.length
    ? '<div style="font-size:11px;color:var(--text-secondary);font-family:monospace;background:rgba(0,0,0,0.04);padding:2px 6px;border-radius:3px;user-select:all">'
      + idBits.join(' &middot; ') + '</div>'
    : '';

  const confirmedLine = confirmed.length
    ? '<div style="display:flex;gap:5px;flex-wrap:wrap;align-items:center">'
      + '<span style="font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--text-secondary)">Jurisdictions on record</span>'
      + confirmed.map((x) => _cdlisChip(x, !!covered[x.state])).join('') + '</div>'
    : '<div style="font-size:11.5px;color:var(--text-primary);line-height:1.45">' + _cdlisEsc(nonCdl ? CDLIS_EMPTY_NOTE_NON_CDL : CDLIS_EMPTY_NOTE) + '</div>';

  const unverifiedLine = unverified.length
    ? '<div style="background:#FEF3C7;border:1px solid #F59E0B;border-radius:6px;padding:7px 9px;display:flex;flex-direction:column;gap:5px">'
      + '<div style="display:flex;gap:5px;flex-wrap:wrap;align-items:center">'
      + '<span style="font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#92400E">Unverified &mdash; not counted</span>'
      + unverified.map((x) => _cdlisChip(x, false)).join('') + '</div>'
      + '<div style="font-size:11px;color:#92400E;line-height:1.45">' + _cdlisEsc(nonCdl ? CDLIS_UNVERIFIED_NOTE_NON_CDL : CDLIS_UNVERIFIED_NOTE) + '</div></div>'
    : '';

  const gapLine = gaps.length
    ? '<div style="background:#FEE2E2;border:1px solid #FCA5A5;border-radius:6px;padding:7px 9px;font-size:11.5px;color:#991B1B;line-height:1.45">'
      + '<strong>MVR still needed:</strong> ' + _cdlisEsc(gaps.join(', '))
      + '. §391.23(a)(1) wants a driving record from every state on this index &mdash; an MVR from one state does not cover the others.</div>'
    : '';

  return '<div data-cdlis-panel="' + _cdlisEsc(String(o.candidateId || '').trim()) + '" style="border-top:1px dashed #94A3B833;padding-top:6px;margin-top:2px;display:flex;flex-direction:column;gap:6px">'
    + '<div style="font-size:11.5px;font-weight:700;color:var(--text-primary)">' + _cdlisEsc(nonCdl ? CDLIS_PANEL_TITLE_NON_CDL : CDLIS_PANEL_TITLE) + '</div>'
    + '<div style="font-size:11px;color:var(--text-secondary);line-height:1.45">' + _cdlisEsc(nonCdl ? CDLIS_SCOPE_NOTE_NON_CDL : CDLIS_SCOPE_NOTE) + '</div>'
    + idLine
    + confirmedLine
    + unverifiedLine
    + gapLine
    + cdlisApplicantStatesHtml(rows, o)
    + cdlisSuggestHtml(rows, o)
    + cdlisEntryHtml(rows, o)
    + (nonCdl ? '' : cdlisIdentityHtml(r, o))
    + cdlisExternalHtml(r, nonCdl ? Object.assign({}, o, { canAttach: false }) : o)
    + (nonCdl ? '' : '<div style="font-size:10.5px;color:var(--text-secondary);line-height:1.4">' + _cdlisEsc(CDLIS_SOURCE_NOTE)
    + (o.hasDoc ? '' : ' No report is attached yet.') + '</div>')
    + '</div>';
}

// TASK-4549 — THE ENTRY SURFACE (slice 2). Slice 1 shipped this panel with no way
// to record anything, which is why every candidate rendered the empty state. This
// is that half, and it is deliberately DUMB: a fixed list of jurisdictions, a
// CURRENT/AKA picker, and a button. There is no free-text state box, no "detect
// the states for me", and nothing here reads the uploaded PDF — a jurisdiction is
// a code a human read off the CDLIS report, and the SERVER is what stamps who
// confirmed it (setCdlisJurisdictions / executePartnerAction
// 'cdlis.setJurisdictions' → _applyCdlisJurisdictions). The client sends state +
// type; it cannot send `confirmedBy`, and firestore.rules fences the sub-path so
// it cannot write the list directly either.
//
// The controls carry data-cdlis-* attributes ONLY — no inline handler, no
// surface-specific function name — because the two hosts reach different
// transports (the carrier a callable, the partner the action dispatcher). Each
// host binds its own delegated listener OUTSIDE this block; this block stays
// byte-identical across both copies (tests/cdlis-jurisdictions.unit.cjs enforces
// that).
const _CDLIS_JURISDICTION_LIST = Array.from(_CDLIS_JURISDICTIONS).sort();
// TASK-9438 — the entry list shows a licence number as its last 4 (the driver
// card's eye button reveals it). Same mask as window.PersonSections.cdlisMaskLicense.
function _cdlisMaskLicense(v) {
  const s = String(v == null ? '' : v).replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (!s) return '';
  return s.length <= 4 ? '••••' : '••••' + s.slice(-4);
}

function cdlisEntryHtml(rows, opts) {
  const o = opts || {};
  const candId = String(o.candidateId || '').trim();
  if (!candId || !o.canEdit) return '';
  const cand = _cdlisEsc(candId);
  const stateOpts = _CDLIS_JURISDICTION_LIST.map((s) => '<option value="' + s + '">' + s + '</option>').join('');
  const recorded = (rows || []).map((r) => {
    // An unrecognised code is shown with WHY it carries no obligation, rather
    // than as a row that merely looks the same as a confirmed one.
    const who = r.confirmedBy
      ? 'Confirmed by ' + _cdlisEsc(r.confirmedBy) + (r.suggestedFrom && r.suggestedFrom.length ? ' (suggestion: ' + _cdlisEsc(cdlisSuggestSourceText(r.suggestedFrom)) + ')' : '')
      : 'Not counted — not a jurisdiction DispatchDeck recognises';
    return '<div style="display:flex;align-items:center;gap:6px;font-size:11px;padding:4px 0;border-top:1px dotted #94A3B855">'
      + '<span style="font-weight:700;min-width:24px;color:var(--text-primary)">' + _cdlisEsc(r.state) + '</span>'
      + '<span style="font-weight:600;font-size:10.5px;opacity:.75;min-width:54px;color:var(--text-secondary)">' + (r.type === 'current' ? 'CURRENT' : 'AKA') + '</span>'
      + '<span style="flex:1;min-width:0;color:var(--text-secondary);overflow-wrap:anywhere">' + (r.licenseNumber ? 'Lic ' + _cdlisEsc(_cdlisMaskLicense(r.licenseNumber)) + ' &middot; ' : '') + who + '</span>'
      + '<button type="button" data-cdlis-cand="' + cand + '" data-cdlis-remove="' + _cdlisEsc(r.state) + '" data-cdlis-removetype="' + _cdlisEsc(r.type) + '"'
      + ' title="Take this state off the record. Only do this if the driver did not hold a license or permit there in the last 3 years."'
      + ' style="font-size:10.5px;font-weight:600;padding:4px 9px;min-height:30px;border:1px solid var(--border-medium);border-radius:5px;background:#fff;color:#B91C1C;cursor:pointer">Remove</button>'
      + '</div>';
  }).join('');
  return '<div style="border-top:1px dashed #94A3B833;padding-top:7px;display:flex;flex-direction:column;gap:5px">'
    + '<div style="font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--text-secondary)">' + _cdlisEsc(o.nonCdl === true ? CDLIS_ENTRY_TITLE_NON_CDL : CDLIS_ENTRY_TITLE) + '</div>'
    + recorded
    + '<div style="display:flex;gap:5px;flex-wrap:wrap;align-items:center">'
    + '<select class="control-auto" data-cdlis-state="' + cand + '" aria-label="Jurisdiction code" style="font-size:11.5px;padding:5px 7px;min-height:32px;border:1px solid var(--border-medium);border-radius:5px;background:#fff;color:var(--text-primary)">' + stateOpts + '</select>'
    + '<select class="control-auto" data-cdlis-type="' + cand + '" aria-label="Current or prior state" style="font-size:11.5px;padding:5px 7px;min-height:32px;border:1px solid var(--border-medium);border-radius:5px;background:#fff;color:var(--text-primary)">'
    + '<option value="aka">AKA</option><option value="current">CURRENT</option></select>'
    + '<input type="text" data-cdlis-license="' + cand + '" maxlength="25" autocomplete="off" placeholder="License # (optional)" aria-label="License number for this state (optional)"'
    + ' style="font-size:11.5px;padding:5px 7px;min-height:32px;width:150px;max-width:100%;border:1px solid var(--border-medium);border-radius:5px;background:#fff;color:var(--text-primary)">'
    + '<button type="button" data-cdlis-add="' + cand + '"'
    + ' style="font-size:11.5px;font-weight:600;padding:6px 12px;min-height:32px;border:1px solid var(--brand-blue);border-radius:5px;background:#fff;color:var(--brand-blue);cursor:pointer">Add</button>'
    + '</div>'
    + '<div style="font-size:10.5px;color:var(--text-secondary);line-height:1.4">' + _cdlisEsc(o.nonCdl === true ? CDLIS_ENTRY_NOTE_NON_CDL : CDLIS_ENTRY_NOTE) + '</div>'
    + '</div>';
}
// TASK-4554 — THE REPORT IDENTITY ENTRY (CDLIS ID · Requested · Completed).
// The line above has rendered these three since slice 1 with nothing anywhere
// able to write one. Same shape as the jurisdiction entry: data-cdlis-* hooks
// only, no host-specific handler, byte-identical across both bundles.
//
// It is a SEPARATE control group from the jurisdiction picker, and separate on
// purpose. These are TRANSCRIPTION fields — the identifier and the two dates
// printed on the report — so there is no list to pick from and nothing to
// derive, and unlike a jurisdiction they carry NO confirmer and promote
// nothing: recording a report ID never changes which MVRs the carrier owes.
// The two halves also save independently, because the server treats an absent
// half as "leave it exactly as it is" — so editing one can never wipe the
// other, and an empty box is a deliberate clear rather than an accident.
// Nothing here reads the uploaded PDF, same as everything else in this block.
const CDLIS_IDENTITY_TITLE = 'Report identity — copy it off the report';
const CDLIS_IDENTITY_NOTE = 'Copy these off the report so the file traces back to the vendor. They confirm nothing and change no MVR obligation. Clear a box and save to remove that value.';

function cdlisIdentityHtml(row, opts) {
  const r = row || {};
  const o = opts || {};
  const candId = String(o.candidateId || '').trim();
  if (!candId || !o.canEdit) return '';
  const cand = _cdlisEsc(candId);
  const reportId = String(r.cdlisReportId || r.reportId || '').trim().slice(0, 60);
  // A date box only accepts YYYY-MM-DD; anything else stored (a legacy value, a
  // Timestamp) renders in the read-only line above and is left for the server to
  // judge rather than half-shown in a control that cannot hold it.
  const asDay = (v) => (/^\d{4}-\d{2}-\d{2}$/.test(String(v || '')) ? String(v) : '');
  const requested = asDay(_cdlisFmtDate(r.requestedDate || r.requestedAt));
  const completed = asDay(_cdlisFmtDate(r.completedDate || r.completedAt));
  const box = 'font-size:11.5px;padding:5px 7px;min-height:32px;border:1px solid var(--border-medium);border-radius:5px;background:#fff;color:var(--text-primary)';
  const lbl = 'font-size:10.5px;font-weight:600;color:var(--text-secondary);display:inline-flex;align-items:center;gap:4px';
  return '<div data-cdlis-identity="' + cand + '" style="border-top:1px dashed #94A3B833;padding-top:7px;display:flex;flex-direction:column;gap:5px">'
    + '<div style="font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--text-secondary)">' + _cdlisEsc(CDLIS_IDENTITY_TITLE) + '</div>'
    + '<div style="display:flex;gap:5px;flex-wrap:wrap;align-items:center">'
    + '<input type="text" data-cdlis-reportid="' + cand + '" value="' + _cdlisEsc(reportId) + '" maxlength="60" placeholder="CDLIS ID"'
    + ' aria-label="CDLIS report ID" style="' + box + ';flex:1 1 130px;min-width:110px">'
    + '<label style="' + lbl + '">Requested'
    + '<input type="date" data-cdlis-requested="' + cand + '" value="' + _cdlisEsc(requested) + '" aria-label="Date the CDLIS report was requested" style="' + box + '">'
    + '</label>'
    + '<label style="' + lbl + '">Completed'
    + '<input type="date" data-cdlis-completed="' + cand + '" value="' + _cdlisEsc(completed) + '" aria-label="Date the CDLIS report was completed" style="' + box + '">'
    + '</label>'
    + '<button type="button" data-cdlis-idsave="' + cand + '"'
    + ' style="font-size:11.5px;font-weight:600;padding:6px 12px;min-height:32px;border:1px solid var(--brand-blue);border-radius:5px;background:#fff;color:var(--brand-blue);cursor:pointer">Save</button>'
    + '</div>'
    + '<div style="font-size:10.5px;color:var(--text-secondary);line-height:1.4">' + _cdlisEsc(CDLIS_IDENTITY_NOTE) + '</div>'
    + '</div>';
}
// TASK-9166 — THE REPORT FILE AND WHO VOUCHED FOR IT (the single-source de-risk).
// A self-serve carrier's only CDLIS source is Checkr, and Checkr's MVR object can
// hold one prior state at most — so a carrier must be able to put a CDLIS report
// it got ANYWHERE ELSE on the record. The server (recordExternalCdlisReport)
// stamps `external` = { docPath, source, attestedBy, attestedAt } from the auth
// context; this block only reads it back. Provenance is shown ONLY when it was
// attested for the file the row points at now — an attestation for an earlier
// file never vouches for a later one. And it is never a verdict: the wording is
// "on file: recorded by", never "clean", and recording a file changes neither
// the status nor a jurisdiction (the note says so).
//
// Same host contract as the two blocks above: data-cdlis-* hooks only, the host
// decides canAttach (may record) and canView (may open the file) and binds its
// own listener, and this block stays byte-identical across both bundles.
const CDLIS_EXTERNAL_TITLE = 'Record a CDLIS report you got outside Checkr';
const CDLIS_EXTERNAL_NOTE = 'Attach the report, say where it came from, and confirm it. Your name goes on the record. It does not change the status above or confirm any jurisdiction — record those off the report yourself. An earlier file is kept, never replaced.';
const CDLIS_EXTERNAL_ATTEST = 'Our company requested this report from the source above (it is not a copy the driver handed us), and it is for this driver.';
const CDLIS_EXTERNAL_UNATTESTED = 'A report file is attached, but nobody has recorded where it came from or vouched for it.';

function cdlisExternalHtml(row, opts) {
  const r = row || {};
  const o = opts || {};
  const nonCdl = o.nonCdl === true;
  const candId = String(o.candidateId || '').trim();
  const cand = _cdlisEsc(candId);
  const docPath = typeof r.docPath === 'string' ? r.docPath : '';
  const ext = (r.external && typeof r.external === 'object' && docPath && r.external.docPath === docPath) ? r.external : null;
  const history = Array.isArray(r.externalHistory) ? r.externalHistory.length : 0;
  let status = '';
  if (ext && String(ext.attestedBy || '').trim()) {
    const when = _cdlisFmtDate(ext.attestedAt);
    status = '<div style="background:#ECFDF5;border:1px solid #6EE7B7;border-radius:6px;padding:7px 9px;font-size:11.5px;color:#065F46;line-height:1.45">'
      + '<strong>' + (nonCdl ? CDLIS_NEUTRAL_RECORD + ' on file' : 'External CDLIS on file') + ': recorded by ' + _cdlisEsc(String(ext.attestedBy).slice(0, 200)) + '</strong>'
      + ' &middot; source ' + _cdlisEsc(String(ext.source || '').slice(0, 80) || 'not stated')
      + (when ? ' &middot; ' + _cdlisEsc(when) : '') + '</div>';
  } else if (docPath) {
    status = '<div style="background:#FEF3C7;border:1px solid #F59E0B;border-radius:6px;padding:7px 9px;font-size:11.5px;color:#92400E;line-height:1.45">'
      + _cdlisEsc(nonCdl ? CDLIS_NEUTRAL_RECORD + ' file attached, but nobody has recorded where it came from or vouched for it.' : CDLIS_EXTERNAL_UNATTESTED) + '</div>';
  }
  const view = (docPath && o.canView && candId)
    ? '<div><button type="button" data-cdlis-viewdoc="' + cand + '"'
      + ' style="font-size:11.5px;font-weight:600;padding:6px 12px;min-height:32px;border:1px solid var(--border-medium);border-radius:5px;background:#fff;color:var(--text-primary);cursor:pointer">' + (nonCdl ? 'View ' + CDLIS_NEUTRAL_RECORD.toLowerCase() : 'View report file') + '</button></div>'
    : '';
  const hist = history
    ? '<div style="font-size:10.5px;color:var(--text-secondary)">' + history + (nonCdl ? ' earlier file' : ' earlier CDLIS file') + (history === 1 ? '' : 's') + ' kept on record.</div>'
    : '';
  let form = '';
  if (candId && o.canAttach) {
    const box = 'font-size:11.5px;padding:5px 7px;min-height:32px;border:1px solid var(--border-medium);border-radius:5px;background:#fff;color:var(--text-primary)';
    form = '<div data-cdlis-external="' + cand + '" style="border-top:1px dashed #94A3B833;padding-top:7px;display:flex;flex-direction:column;gap:5px">'
      + '<div style="font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--text-secondary)">' + _cdlisEsc(CDLIS_EXTERNAL_TITLE) + '</div>'
      + '<input type="text" data-cdlis-extsource="' + cand + '" maxlength="80" placeholder="Where it came from — vendor or state (e.g. TMFS, ND DOT)"'
      + ' aria-label="Where the CDLIS report came from" style="' + box + ';width:100%;box-sizing:border-box">'
      + '<input type="file" data-cdlis-extfile="' + cand + '" accept="application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif"'
      + ' aria-label="CDLIS report file" style="font-size:11.5px;max-width:100%">'
      + '<label style="display:flex;gap:6px;align-items:flex-start;font-size:11.5px;color:var(--text-primary);line-height:1.4;cursor:pointer">'
      + '<input type="checkbox" data-cdlis-extattest="' + cand + '" style="margin-top:2px;min-width:16px;min-height:16px">'
      + '<span>' + _cdlisEsc(CDLIS_EXTERNAL_ATTEST) + '</span></label>'
      + '<div><button type="button" data-cdlis-extsave="' + cand + '"'
      + ' style="font-size:11.5px;font-weight:600;padding:6px 12px;min-height:32px;border:1px solid var(--brand-blue);border-radius:5px;background:#fff;color:var(--brand-blue);cursor:pointer">Record report</button></div>'
      + '<div style="font-size:10.5px;color:var(--text-secondary);line-height:1.4">' + _cdlisEsc(CDLIS_EXTERNAL_NOTE) + '</div>'
      + '</div>';
  }
  if (!status && !view && !hist && !form) return '';
  return '<div style="display:flex;flex-direction:column;gap:5px">' + status + view + hist + form + '</div>';
}
// ═══════════════════════════════════════════════════════════════════════════
// TASK-1534 — CDLIS JURISDICTION CORE (end)
// ═══════════════════════════════════════════════════════════════════════════

// TASK-1534 — the ONLY states we can prove an MVR covers for this candidate: the
// DL state the MVR was ordered against, and only once that row carries a result.
// Deliberately narrow — nothing here is inferred from the uploaded CDLIS file.
// TASK-9250 — the driver's own answer from the signed application, for the
// CDLIS panel's "On the signed application" line (display only; see
// cdlisApplicantStatesHtml). Absent on applications from before the question.
function _cdlisApplicantStatesForCandidate(candidate) {
  const lic = (candidate && candidate.license && typeof candidate.license === 'object') ? candidate.license : null;
  if (!lic || (lic.otherStatesHeld !== true && lic.otherStatesHeld !== false)) return null;
  return {
    held: lic.otherStatesHeld,
    states: Array.isArray(lic.priorStates) ? lic.priorStates.filter((s) => typeof s === 'string').slice(0, 20) : [],
    licenseState: typeof lic.state === 'string' ? lic.state : '',
    signed: candidate.applicationComplete === true,
  };
}
function _cdlisCoveredStatesForCandidate(candidate) {
  const c = candidate || {};
  const st = (c.license && c.license.state) || c.dlState || '';
  const mvr = (c.backgroundReports && c.backgroundReports.mvr) || {};
  const settled = ['received', 'clear', 'consider', 'flagged'].indexOf(String(mvr.status || '')) !== -1;
  return (st && settled) ? [st] : [];
}

// ─────────────────────────────────────────────────────────────────────────────
// TASK-4549 — CDLIS jurisdiction ENTRY, CARRIER side.
// The renderer lives in the shared core above and emits data-cdlis-* controls
// and nothing else; the transport lives HERE, because the carrier goes through
// the setCdlisJurisdictions callable while the partner portal goes through
// executePartnerAction('cdlis.setJurisdictions'). Both land on the same server
// core (_applyCdlisJurisdictions), which is where `confirmedBy` is stamped — the
// payload below carries state + type and could not carry a confirmer if it tried.
// ─────────────────────────────────────────────────────────────────────────────

// Mirror of the server gate (requireHrAccess: owner | admin | hr module), minus
// view/upload-only members. The candidates update arm in firestore.rules already
// excludes them, and recording a jurisdiction is an authoring act rather than an
// upload — so the button is hidden rather than shown-and-403'd (TASK-852).
function _hrCanRecordCdlis() {
  const m = window.AppState?.memberAccess;
  if (!m) return true; // owner / legacy member-less account — full access
  if (['revoked', 'inactive', 'pending'].includes(m.status)) return false;
  if (m.role === 'owner' || m.role === 'admin') return true;
  return Array.isArray(m.modules) && m.modules.includes('hr') && m.viewUploadOnly !== true;
}

// TASK-4553 — CLIENT mirror of the server guard added to overrideReportStatus.
// requireHrAccess admits any hr-module holder, so a view/upload-only member
// could reach every manual status control and only find out it was refused
// after clicking. The server now denies it (functions/src/index.js:40320) and
// this hides the control instead of showing it and 403-ing — the same choice
// _hrCanRecordCdlis made for CDLIS entry (TASK-852), and the reason TASK-428
// is a standing lesson: a denial the user cannot see coming is worse than the
// hole it closes.
//
// Setting a background-report status is an FCRA / §391.23 AUTHORING act; it
// stamps who attested to a screening result. "View & upload only" is meant to
// mean read the file and attach documents, not author a finding. Owner/admin
// are never restricted — the flag scopes an INVITED member, and denying them
// here would be stricter than the firestore rule this mirrors.
function _hrCanAuthorBgStatus() {
  return _hrCanRecordCdlis();
}

// The whole list as {state, type} — the callable REPLACES it, so an add or a
// remove is "send the list again". Read off the candidate doc, never off the
// rendered chips: those are display state, and a snapshot could have moved under
// them while the panel sat open.
function _hrCdlisRows(candidateId) {
  const c = HRState.candidates.find((x) => x.id === candidateId);
  const raw = c && c.backgroundReports && c.backgroundReports.cdlis
    ? c.backgroundReports.cdlis.jurisdictions : null;
  return cdlisNormalizeJurisdictions(raw).map((r) => ({ state: r.state, type: r.type }));
}

async function _hrSaveCdlisJurisdictions(candidateId, rows) {
  const tenantId = HRState.tenantId;
  if (!tenantId) throw new Error('No tenant context');
  const fn = httpsCallable(functions, 'setCdlisJurisdictions');
  const res = await fn({ tenantId, candidateId, jurisdictions: rows });
  // Optimistic local mirror so the card repaints before the snapshot lands. It
  // mirrors what the SERVER just reported back (including which rows it refused
  // to treat as confirmed) — it never invents a confirmation.
  const c = HRState.candidates.find((x) => x.id === candidateId);
  if (c) {
    if (!c.backgroundReports) c.backgroundReports = {};
    if (!c.backgroundReports.cdlis) c.backgroundReports.cdlis = {};
    const priorBy = {};
    const priorLic = {};
    const prior = Array.isArray(c.backgroundReports.cdlis.jurisdictions) ? c.backgroundReports.cdlis.jurisdictions : [];
    for (const p of prior) {
      if (p && p.state) {
        priorBy[String(p.type || '') + ':' + String(p.state)] = p.confirmedBy || '';
        if (typeof p.licenseNumber === 'string' && p.licenseNumber) priorLic[String(p.type || '') + ':' + String(p.state)] = p.licenseNumber;
      }
    }
    // TASK-9438 — a licence number sent on this call wins; otherwise the server
    // kept the stored one, so the mirror does too.
    for (const r of (rows || [])) {
      if (r && typeof r.licenseNumber === 'string' && r.licenseNumber) priorLic[r.type + ':' + r.state] = r.licenseNumber;
    }
    const me = window.AppState?.user?.email || 'you';
    const applied = (res && res.data && Array.isArray(res.data.jurisdictions)) ? res.data.jurisdictions : rows;
    c.backgroundReports.cdlis.jurisdictions = applied.map((r) => ({
      state: r.state,
      type: r.type,
      confirmedBy: r.confirmed === false ? '' : (priorBy[r.type + ':' + r.state] || me),
      ...(priorLic[r.type + ':' + r.state] ? { licenseNumber: priorLic[r.type + ':' + r.state] } : {}),
      // TASK-4753 — only what the SERVER reported back; the client never invents it.
      ...(Array.isArray(r.suggestedFrom) && r.suggestedFrom.length ? { suggestedFrom: r.suggestedFrom } : {}),
    }));
  }
  return res;
}

// TASK-4554 — the TRANSCRIPTION half, carrier side. Same callable, same gate:
// the payload carries `reportIdentity` and NO `jurisdictions` key at all, which
// is exactly what tells the server to leave the §391.23 pointer record alone.
// All three boxes are always sent, so a box the user emptied is a deliberate
// clear rather than an omission — that distinction lives in the payload SHAPE,
// not in a flag, and it is the whole reason saving one half cannot wipe the
// other. Rendering is gated by _hrCanRecordCdlis() through the panel's canEdit,
// so there is no second gate here.
async function _hrCdlisSaveIdentity(btn) {
  const candidateId = btn.getAttribute('data-cdlis-idsave');
  if (!candidateId || btn.disabled) return;
  // Scoped to this button's own group so two open cards can't cross-read.
  const box = btn.closest('[data-cdlis-identity]');
  if (!box) return;
  const val = (sel) => String(box.querySelector(sel)?.value || '').trim();
  const reportIdentity = {
    cdlisReportId: val('[data-cdlis-reportid]'),
    requestedDate: val('[data-cdlis-requested]'),
    completedDate: val('[data-cdlis-completed]'),
  };
  // The server refuses this too — say WHY here instead of letting the save fail.
  if (reportIdentity.requestedDate && reportIdentity.completedDate
      && reportIdentity.completedDate < reportIdentity.requestedDate) {
    window.showToast?.('The completed date is before the requested date — check the report.', 'error');
    return;
  }
  const tenantId = HRState.tenantId;
  if (!tenantId) { window.showToast?.('No tenant context', 'error'); return; }
  btn.disabled = true;
  try {
    const fn = httpsCallable(functions, 'setCdlisJurisdictions');
    const res = await fn({ tenantId, candidateId, reportIdentity });
    // Mirror what the SERVER applied (it normalises the dates), not what was typed.
    const applied = (res && res.data && res.data.reportIdentity) || reportIdentity;
    const c = HRState.candidates.find((x) => x.id === candidateId);
    if (c) {
      if (!c.backgroundReports) c.backgroundReports = {};
      if (!c.backgroundReports.cdlis) c.backgroundReports.cdlis = {};
      Object.assign(c.backgroundReports.cdlis, applied);
    }
    window.showToast?.('Report identity saved.', 'success');
  } catch (err) {
    console.error('[HR] setCdlisJurisdictions (report identity) failed', err);
    window.showToast?.(err?.message || 'Failed to save the CDLIS report identity', 'error');
  }
  btn.disabled = false;
  try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
}

// ─────────────────────────────────────────────────────────────────────────────
// TASK-9166 — record a CDLIS report obtained OUTSIDE Checkr, CARRIER side.
// The renderer (cdlisExternalHtml in the shared core) draws the form from
// canAttach/canView; the gate and the transport live here. The server
// (recordExternalCdlisReport) re-checks everything and stamps the attestor from
// the auth context — nothing below sends a name.
// ─────────────────────────────────────────────────────────────────────────────

// Client mirror of the server gate. ACCESS = MODULES (Chris 2026-09-24): the
// owner by tenants/{tid}.ownerId, an active member holding `hr` who is not
// view/upload-only, or the managing partner inside the carrier (?client=). No
// role arm. Demo is hidden because the server refuses it (the shared demo tenant
// has no members doc to carry modules). The UI may hide more than the server,
// never offer more.
function _hrCanAttestExternalCdlis() {
  const st = window.AppState || {};
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  if (_hrPartnerClientTenantId()) return true;
  if (st.isDemo === true || st.tenant === 'demo') return false;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true || ma.viewUploadOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('hr');
}
// Opening the file: the partner goes through getPartnerComplianceDocBytes (via
// viewComplianceDoc), a carrier through getComplianceDocBytes, which admits the
// candidates/ report folders to HR-module holders only.
function _hrCanViewCandidateReportDoc() {
  const st = window.AppState || {};
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  if (_hrPartnerClientTenantId()) return true;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('hr');
}

// ─────────────────────────────────────────────────────────────────────────────
// TASK-9438 — CDLIS on the HIRED driver's card, CARRIER host (HR → Employee
// Directory, and Compliance → People through window.HRModule.hrCdlisCardHtml).
// The shared renderer is window.PersonSections.renderCdlisDriverCard; this host
// resolves the data and decides who sees what:
//   • WHO SEES THE CARD: the same gate that may open the CDLIS file — the owner,
//     an active `hr` module holder, or the managing partner inside the carrier
//     (?client=). That is never wider than the candidates read rule (admin |
//     partner | hr), so nobody sees CDLIS here who could not already read it on
//     the applicant. Everyone else gets nothing — not even an empty card.
//   • DATA: candidates/{person.candidateId}.backgroundReports.cdlis — the record
//     is never copied onto the person (and never onto the broadcast `people`
//     doc). HRState.candidates when HR has it loaded, else one getDoc per
//     candidate, cached for the session and refreshed on the next open.
// ─────────────────────────────────────────────────────────────────────────────
const _HR_CDLIS_CAND = new Map();      // `${tenantId}/${candidateId}` -> candidate data | null (not found)
const _HR_CDLIS_INFLIGHT = new Map();  // `${tenantId}/${candidateId}` -> Promise
function _hrCdlisCandId(emp) {
  const cid = emp && typeof emp.candidateId === 'string' ? emp.candidateId : '';
  return /^[A-Za-z0-9_-]{1,128}$/.test(cid) ? cid : '';
}
function _hrCdlisRender(emp, cand, noCandidate) {
  const PS = window.PersonSections;
  if (!PS || typeof PS.renderCdlisDriverCard !== 'function') return '';
  const cdlis = cand && cand.backgroundReports && cand.backgroundReports.cdlis ? cand.backgroundReports.cdlis : null;
  const mvrRow = (cand && cand.backgroundReports && cand.backgroundReports.mvr) || {};
  const mvrOnFile = PS.cdlisMvrOnFile(emp, {
    states: cand ? _cdlisCoveredStatesForCandidate(cand) : [],
    date: _cdlisFmtDate(mvrRow.date || mvrRow.completedAt || mvrRow.updatedAt),
  });
  const docPath = cdlis && typeof cdlis.docPath === 'string' ? cdlis.docPath : '';
  const canOpen = _hrCanViewCandidateReportDoc();
  return PS.renderCdlisDriverCard(cdlis, {
    mvrOnFile,
    nonCdl: window.DqMatrix.getApplicableDqItems(emp).positionType === 'non_cdl_driver', // TASK-9698
    noCandidate: !!noCandidate,
    view: (docPath && canOpen) ? { onClick: `window.viewComplianceDoc('${window.escJsAttr(docPath)}','','cdlis',this)` } : null,
    canPrint: canOpen,
    printKey: emp.id,
    driverName: emp.name || [emp.firstName, emp.lastName].filter(Boolean).join(' '),
    carrierName: window.AppState?.companyName || window.AppState?.tenantData?.name || '',
  });
}
function hrCdlisCardHtml(emp, tenantId) {
  if (!emp || !emp.id) return '';
  if (!_hrCanViewCandidateReportDoc()) { window.PersonSections?.forgetCdlisPrint?.(emp.id); return ''; }
  const cid = _hrCdlisCandId(emp);
  if (!cid) return _hrCdlisRender(emp, null, true);
  const tid = tenantId || _hrPartnerClientTenantId() || HRState.tenantId || window.AppState?.tenantId || '';
  if (!tid) return '';
  // COD 2026-10-06 (Code) — HRState.candidates is only trusted for the tenant it
  // was loaded for, and the cache is keyed by tenant, so a tenant switch can
  // never paint another carrier's record.
  if (HRState.tenantId === tid) {
    const live = (HRState.candidates || []).find((c) => c && c.id === cid);
    if (live) return _hrCdlisRender(emp, live, false);
  }
  const key = tid + '/' + cid;
  // COD r2 (Code) — the slot names tenant AND person, so a read still in flight
  // for one carrier can never fill another carrier's card.
  const slot = `data-ps-cdlis-slot="${escapeHtml(key + '/' + emp.id)}"`;
  _hrCdlisFetch(emp, cid, tid);
  if (_HR_CDLIS_CAND.has(key)) {
    // The cached copy shows at once, INSIDE a slot, so the re-read just started
    // replaces it — and a denial (access revoked since) removes it.
    return `<div ${slot}>${_hrCdlisRender(emp, _HR_CDLIS_CAND.get(key), false)}</div>`;
  }
  return `<div ${slot} style="font-size:0.78rem;color:var(--text-secondary);padding:6px 0;">Loading CDLIS…</div>`;
}
function _hrCdlisFetch(emp, cid, tid) {
  const key = tid + '/' + cid;
  if (!tid || _HR_CDLIS_INFLIGHT.has(key)) return;
  const p = getDoc(doc(db, 'tenants', tid, 'candidates', cid))
    .then((snap) => {
      const data = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      _HR_CDLIS_CAND.set(key, data);
      return { ok: true, data };
    })
    .catch((err) => {
      // A denial means the rule is right and this viewer may not read the
      // record — show nothing rather than an error on a compliance card, and
      // forget anything cached for it.
      _HR_CDLIS_CAND.delete(key);
      console.warn('[HR] CDLIS card read failed', err && err.code);
      return { ok: false };
    })
    .then((res) => {
      _HR_CDLIS_INFLIGHT.delete(key);
      if (!res.ok) window.PersonSections?.forgetCdlisPrint?.(emp.id);
      const want = key + '/' + emp.id;
      document.querySelectorAll('[data-ps-cdlis-slot]').forEach((el) => {
        if (el.getAttribute('data-ps-cdlis-slot') !== want) return;
        if (!res.ok) { el.remove(); return; }
        el.outerHTML = _hrCdlisRender(emp, res.data, false);
      });
    });
  _HR_CDLIS_INFLIGHT.set(key, p);
}

async function _hrCdlisRecordExternal(btn) {
  const candidateId = btn.getAttribute('data-cdlis-extsave');
  if (!candidateId || btn.disabled) return;
  // Scoped to this button's own group so two open cards can't cross-read.
  const box = btn.closest('[data-cdlis-external]');
  if (!box) return;
  const source = String(box.querySelector('[data-cdlis-extsource]')?.value || '').trim();
  const file = box.querySelector('[data-cdlis-extfile]')?.files?.[0] || null;
  const attest = box.querySelector('[data-cdlis-extattest]')?.checked === true;
  if (!source) { window.showToast?.('Say where the report came from — the vendor or the state.', 'error'); return; }
  if (!file) { window.showToast?.('Choose the CDLIS report file first.', 'error'); return; }
  if (!attest) { window.showToast?.('Tick the box to confirm where the report came from and that it is for this driver.', 'error'); return; }
  if (file.size > _HR_PARTNER_UPLOAD_MAX_BYTES) {
    window.showToast?.('File too large (max 7.5 MB here) — compress it and try again.', 'error');
    return;
  }
  const tenantId = _hrPartnerClientTenantId() || HRState.tenantId;
  if (!tenantId) { window.showToast?.('No tenant context', 'error'); return; }
  btn.disabled = true;
  try {
    const dataBase64 = await window.FileReadUtil.readAsBase64(file);
    const fn = httpsCallable(functions, 'recordExternalCdlisReport');
    const res = await fn({
      tenantId, candidateId, source, attest: true, dataBase64,
      contentType: inferContentType(file, ''),
      fileName: file.name || 'cdlis',
    });
    const out = (res && res.data) || {};
    // Mirror what the SERVER recorded (its attestor, its path) so the card
    // repaints before the snapshot lands — never a locally invented name.
    const c = HRState.candidates.find((x) => x.id === candidateId);
    // Skip when the live snapshot already landed this file (COD Code): mirroring
    // again would archive the new file into its own history locally.
    if (c && out.docPath && !(c.backgroundReports && c.backgroundReports.cdlis && c.backgroundReports.cdlis.docPath === out.docPath)) {
      if (!c.backgroundReports) c.backgroundReports = {};
      const row = c.backgroundReports.cdlis || (c.backgroundReports.cdlis = {});
      if (row.docPath) {
        row.externalHistory = (Array.isArray(row.externalHistory) ? row.externalHistory : []).concat([{ docPath: row.docPath }]);
      }
      row.docPath = out.docPath;
      row.external = { docPath: out.docPath, source: out.source || source, attestedBy: out.attestedBy || '', attestedAt: new Date().toISOString().slice(0, 10) };
    }
    window.showToast?.('CDLIS report recorded.', 'success');
  } catch (err) {
    console.error('[HR] recordExternalCdlisReport failed', err);
    window.showToast?.(String(err?.message || 'Could not record the CDLIS report').replace(/^internal:\s*/, ''), 'error');
  }
  btn.disabled = false;
  try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
}

let _hrCdlisEntryBound = false;
function _hrBindCdlisEntry() {
  if (_hrCdlisEntryBound) return;
  _hrCdlisEntryBound = true;
  document.addEventListener('click', async (e) => {
    // TASK-9166 — the external-report controls: their own group, no shared attribute.
    const extBtn = e.target?.closest?.('[data-cdlis-extsave]');
    if (extBtn) { e.preventDefault(); await _hrCdlisRecordExternal(extBtn); return; }
    const viewBtn = e.target?.closest?.('[data-cdlis-viewdoc]');
    if (viewBtn) {
      e.preventDefault();
      const cid = viewBtn.getAttribute('data-cdlis-viewdoc');
      const c = HRState.candidates.find((x) => x.id === cid);
      const p = c && c.backgroundReports && c.backgroundReports.cdlis ? c.backgroundReports.cdlis.docPath : '';
      await viewComplianceDoc(typeof p === 'string' ? p : '', null, 'cdlis', viewBtn);
      return;
    }
    // TASK-4554 — the report-identity Save, first: it is its own control group
    // and shares no attribute with the jurisdiction picker below.
    const idBtn = e.target?.closest?.('[data-cdlis-idsave]');
    if (idBtn) { e.preventDefault(); await _hrCdlisSaveIdentity(idBtn); return; }
    // TASK-4753 — a suggestion's Edit only pre-fills the Add form below; nothing
    // is saved until the person presses Add.
    const sEdit = e.target?.closest?.('[data-cdlis-sedit]');
    if (sEdit) {
      e.preventDefault();
      if (!cdlisPrefillEntryFromSuggestion(sEdit)) window.showToast?.('Could not open the Add form for this state — pick it from the list below.', 'error');
      return;
    }
    // …and its Confirm is an Add of exactly that row, through the same callable.
    const sBtn = e.target?.closest?.('[data-cdlis-sconfirm]');
    const addBtn = sBtn ? null : e.target?.closest?.('[data-cdlis-add]');
    const rmBtn = (sBtn || addBtn) ? null : e.target?.closest?.('[data-cdlis-remove]');
    if (!sBtn && !addBtn && !rmBtn) return;
    const btn = sBtn || addBtn || rmBtn;
    const candidateId = sBtn
      ? sBtn.getAttribute('data-cdlis-sconfirm')
      : addBtn
        ? addBtn.getAttribute('data-cdlis-add')
        : rmBtn.getAttribute('data-cdlis-cand');
    if (!candidateId) return;
    e.preventDefault();
    if (btn.disabled) return;

    const rows = _hrCdlisRows(candidateId);
    let next;
    if (sBtn) {
      const r = cdlisRowsForConfirmedSuggestion(rows, sBtn.getAttribute('data-cdlis-sstate'), sBtn.getAttribute('data-cdlis-stype'));
      if (r.error) { window.showToast?.(r.error, 'error'); return; }
      next = r.next;
    } else if (addBtn) {
      // Scoped to the button's own row so two open cards can't cross-read.
      const box = addBtn.parentElement;
      const state = String(box?.querySelector('[data-cdlis-state]')?.value || '').trim().toUpperCase();
      const type = String(box?.querySelector('[data-cdlis-type]')?.value || 'aka').trim().toLowerCase() === 'current' ? 'current' : 'aka';
      if (!/^[A-Z]{2}$/.test(state)) { window.showToast?.('Pick a jurisdiction first.', 'error'); return; }
      if (rows.some((r) => r.state === state && r.type === type)) {
        window.showToast?.(`${state} is already recorded as ${type === 'current' ? 'CURRENT' : 'AKA'}.`, 'error');
        return;
      }
      const existingCurrent = rows.find((r) => r.type === 'current');
      if (type === 'current' && existingCurrent) {
        // A CDLIS report indexes exactly one CURRENT licence — the server refuses
        // two, so say WHY here instead of letting the save fail.
        window.showToast?.(`This report already lists ${existingCurrent.state} as the CURRENT jurisdiction. Remove it first if the report says otherwise.`, 'error');
        return;
      }
      // TASK-9438 — optional licence number off the report (the server validates it).
      const licenseNumber = String(box?.querySelector('[data-cdlis-license]')?.value || '').replace(/\s+/g, '').toUpperCase();
      if (licenseNumber && !/^[A-Z0-9-]{1,25}$/.test(licenseNumber)) {
        window.showToast?.('Enter the license number exactly as the report prints it — letters, digits and dashes only.', 'error');
        return;
      }
      next = rows.concat([licenseNumber ? { state, type, licenseNumber } : { state, type }]);
    } else {
      const state = String(rmBtn.getAttribute('data-cdlis-remove') || '').toUpperCase();
      const type = String(rmBtn.getAttribute('data-cdlis-removetype') || 'aka').toLowerCase();
      next = rows.filter((r) => !(r.state === state && r.type === type));
    }

    btn.disabled = true;
    try {
      await _hrSaveCdlisJurisdictions(candidateId, next);
      window.showToast?.(sBtn ? 'Jurisdiction confirmed and recorded under your name.' : addBtn ? 'Jurisdiction recorded.' : 'Jurisdiction removed.', 'success');
    } catch (err) {
      console.error('[HR] setCdlisJurisdictions failed', err);
      window.showToast?.(err?.message || 'Failed to save the CDLIS jurisdictions', 'error');
    }
    btn.disabled = false;
    try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
  });
}
// CDLIS + Clearinghouse can't auto-update — no Checkr package covers either, so
// both are recorded by hand. Tag them so a perpetual "Pending" isn't mistaken for
// a broken sync.
// 🔴 TASK-1534 — but they are NOT the same source, and the shared "FMCSA direct"
// tag was sending carriers to the wrong place for CDLIS. Clearinghouse IS
// FMCSA-direct (clearinghouse.fmcsa.dot.gov). A standalone CDLIS report is an
// AAMVA/state driver-history record ordered through a compliance vendor (Crown
// buys TMFS Corporation's) — not from FMCSA, and, as far as Checkr's own
// /v1/packages probe and case 01668275 go, not from Checkr either.
const HR_FMCSA_DIRECT_KEYS = new Set(['cdlis', 'clearinghouse']);
// TASK-9299 #9 (Kolsen 9/22, Chris-approved) — the grey "Manual — …" source chips are gone
// (HR_MANUAL_SOURCE_TAG deleted), same as the partner twin. Label only: the Clearinghouse card
// keeps its FMCSA "Run" link, and the CDLIS panel says the report is ordered outside Checkr.
// Robust date format for the manual-attestation stamp (Firestore Timestamp | {_seconds} | ISO).
function _hrFmtAttestDate(v) {
  if (!v) return '';
  let d;
  try {
    if (v.toDate) d = v.toDate();
    else if (v._seconds != null) d = new Date(v._seconds * 1000);
    else if (v.seconds != null) d = new Date(v.seconds * 1000);
    else d = new Date(v);
  } catch (_) { return ''; }
  return (d && !isNaN(d.getTime())) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

// TASK-1486 (COD Code Reviewer ⚠️) — ONE predicate for "may we offer a paid Checkr
// MVR order for this candidate right now?", so the new card-header "Run MVR" button
// and the Screenings MVR row can't drift apart. It was the drift that got flagged:
// the row deliberately blocks three cases the header didn't know about.
//   1. 💸 TASK-636 money guard — never re-offer a paid pull on a terminal row.
//   2. Checkr already OWNS the row (webhook-owned or a terminal report) — a second
//      order would double-charge for a record we already have.
//   3. Checkr isn't live for this carrier (no compliancePackageDefault), or we have
//      no DL state on file — the row shows "Add DL info" instead, and an MVR without
//      a state is not orderable.
// Mirrors buildReportCard's own checkrDriving derivation (TASK-535) rather than
// re-deriving it loosely: same slug→COVERS lookup, same webhook/terminal test.
function _hrCandMvrOrderOfferable(candidate) {
  if (!candidate) return false;
  if (['received', 'clear', 'flagged'].includes(getReportStatus(candidate, 'mvr'))) return false;
  const slug = (candidate.checkrPackageSlug || candidate.checkrPackage || '').toLowerCase();
  const covered = (CHECKR_PACKAGE_COVERS[slug] || []).includes('mvr');
  const row = candidate.backgroundReports?.mvr || {};
  const checkrStatusRaw = (candidate.checkrReportStatus || candidate.bgCheckStatus || '').toLowerCase();
  const checkrTerminal = checkrStatusRaw === 'complete' || checkrStatusRaw === 'completed';
  if (covered && (row.updatedBy === 'checkr-webhook' || checkrTerminal)) return false;
  if (!window.AppState?.tenantData?.compliancePackageDefault) return false;
  if (!(candidate.license?.state || candidate.dlState)) return false;
  return true;
}

// TASK-9697 (2) — partner twin: compliance-partner.js _candReportCardStatus. A CDLIS /
// Clearinghouse row with no recorded result but an uploaded proof reads "proof on file",
// never "NOT YET RUN" (Crown 10/7). DISPLAY ONLY: getReportStatus — and every gate and
// hire warning that reads it — is unchanged; nothing is written.
// Amber, not the green "received": an uploaded query proof is not a result (COD Domain).
const HR_PROOF_ON_FILE_META = { label: 'PROOF ON FILE — PICK THE RESULT', icon: 'task', color: '#B45309', bg: '#FFFBEB' };
function _hrReportCardProofOnFile(candidate, reportKey, status) {
  if (!isReportNotYetRun(status) || !HR_FMCSA_DIRECT_KEYS.has(reportKey)) return false;
  const r = candidate?.backgroundReports?.[reportKey] || {};
  return !!(r.docPath || r.docUrl || r.resultDocUrl || r.storagePath || r.scannedCopyUrl);
}
function buildReportCard(candidate, reportKey, label, icon, url) {
  const status = getReportStatus(candidate, reportKey);
  const _proofOnFile = _hrReportCardProofOnFile(candidate, reportKey, status);
  // TASK-10244 — an MVR row on a known package that has no MVR (e.g. Nationwide)
  // is not "coming": say so. DISPLAY ONLY — status and every gate are unchanged.
  const _pkgCovers = CHECKR_PACKAGE_COVERS[(candidate.checkrPackageSlug || candidate.checkrPackage || '').toLowerCase()];
  const _notInPkg = reportKey === 'mvr' && !_proofOnFile && isReportNotYetRun(status) && Array.isArray(_pkgCovers) && !_pkgCovers.includes('mvr');
  const s = _proofOnFile ? HR_PROOF_ON_FILE_META : _notInPkg ? { ...REPORT_STATUSES.pending, label: 'NOT PART OF THIS PACKAGE — ORDER AN MVR SEPARATELY' } : (reportStatusMeta(status) || REPORT_STATUSES.pending);
  const dateStr = candidate.backgroundReports?.[reportKey]?.date || '';
  const refInfo = getReportRefInfo(candidate, reportKey);

  // TASK-535 — Is Checkr actually driving this row? This MUST be independent of
  // manualStatus: once an admin sets a manual value the OLD logic flipped
  // checkrDriving to false, so a row already overridden looked manual-editable
  // again and the "Managed by Checkr"/conflict UI never fired on the exact rows
  // that needed it. Re-base on "package covers this key" + Checkr having actually
  // produced/owned a result for the row (webhook-owned row, OR a terminal
  // checkrReportStatus). manualStatus is no longer an input.
  const slug = (candidate.checkrPackageSlug || candidate.checkrPackage || '').toLowerCase();
  const covered = (CHECKR_PACKAGE_COVERS[slug] || []).includes(reportKey);
  const row = candidate.backgroundReports?.[reportKey] || {};
  const manualStatus = row.status;
  const checkrStatusRaw = (candidate.checkrReportStatus || candidate.bgCheckStatus || '').toLowerCase();
  const checkrTerminal = checkrStatusRaw === 'complete' || checkrStatusRaw === 'completed';
  const checkrDriving = covered && (row.updatedBy === 'checkr-webhook' || checkrTerminal);

  // Conflict: the webhook left a suggested status (its real result) but the row
  // shows a manual override that diverges from it. checkrSuggestedStatus is
  // written by the checkrWebhook shadow path (functions/src/index.js ~L34521).
  const checkrSuggested = row.checkrSuggestedStatus || '';
  const hasConflict = checkrDriving && !!checkrSuggested && checkrSuggested !== status;

  // v1.15.569 — status-light dot + inline ETA (COD hybrid A+B verdict).
  // Per-status visual: solid colored dot. 'ordered' pulses subtly to signal
  // in-flight work; terminal states (clear/flagged/received) are solid; pending
  // is a hollow ring. Uses _bgLatencyHint for ETA text on in-flight rows.
  const dotClass = status === 'ordered' ? 'bg-status-dot pulse' : 'bg-status-dot';
  const dotHollow = isReportNotYetRun(status) && !_proofOnFile ? ' hollow' : '';
  const latencyKind = reportKey === 'mvr' ? 'mvr' : 'bg';
  const hint = (status === 'ordered' || status === 'received') ? _bgLatencyHint(candidate, latencyKind) : null;
  const etaText = hint ? hint.suffix.replace(/^ · /, '') : '';
  const etaTitle = hint ? hint.tooltip : '';
  const statusLine = etaText
    ? `${s.label} · ${etaText}`
    : s.label;

  // TASK-298 / TASK-649 (#6B) — Run button gating:
  //  - The external state-portal MVR "Run" redirect is GONE (it bounced the user
  //    to a portal where they couldn't actually pull the report). MVR still needs
  //    DL state to offer the IN-PLATFORM "Run via Checkr" path, so when state is
  //    missing we surface the "Add DL info" empty-state (Edit Candidate) instead.
  //  - For MVR specifically, when the tenant has Checkr live
  //    (compliancePackageDefault set on the tenant doc) AND we have DL
  //    state, offer the in-app "Run via Checkr" / "Already have one" buttons
  //    that open the existing openBackgroundCheckModal.
  //  - CDLIS is not Checkr-routable (COD Code Reviewer 2026-05-18) and has no
  //    Run button. TASK-1534 — do not restate that as "FMCSA-direct": unlike
  //    Clearinghouse, a standalone CDLIS report is BOUGHT (Crown buys TMFS
  //    Corporation's) and recorded here by hand. Checkr not selling one is not
  //    the same claim as one not existing, which is the error we shipped to a
  //    compliance partner.
  // TASK-644 (c) — CDLIS has no self-serve Run URL (it's verified through the
  // state DMV / AAMVA, not a state MVR portal), so it must NOT nag for DL state
  // to enable a button that doesn't exist. Only MVR is state-portal-gated.
  const stateNeeded = (reportKey === 'mvr');
  const hasState = !!(candidate.license?.state || candidate.dlState);
  // TASK-1486 — centralized in _hrCandMvrOrderOfferable (same three conditions:
  // checkrActive && hasState && !checkrDriving) so the card header shares it. The
  // helper ALSO folds in the terminal-status guard, which the early return in
  // runButtonHtml below already applied — behaviour here is unchanged.
  const useCheckrRunForMvr = reportKey === 'mvr' && _hrCandMvrOrderOfferable(candidate);
  const runMissingState = stateNeeded && !hasState && !checkrDriving;
  const runButtonHtml = (() => {
    // TASK-636 — 💸 money guard: never offer a run / "Run via Checkr ($)" /
    // "Already have one" button on a report that's already in a terminal state.
    // The candidate card is PRE-HIRE — there is no legit re-pull here (the
    // §391.25 annual MVR re-pull lives on the PERSON post-hire via saveMvrPull,
    // NOT this card). Without this gate a manually-received MVR (checkrDriving
    // false) still rendered "Run via Checkr ($)" → a real path to a paid
    // DUPLICATE Checkr pull. Suppress regardless of which branch it'd hit.
    if (status === 'received' || status === 'clear' || status === 'flagged') return '';
    if (checkrDriving) return '';
    if (runMissingState) {
      return `<button class="btn btn-sm" title="Add the candidate's driver license state to run this report"
        onclick="window.HRModule.openEditCandidateModal('${candidate.id}')"
        style="font-size:0.7rem;padding:2px 8px;background:#fff;color:var(--brand-blue);border:1px solid var(--brand-blue);">
        <span class="material-icons" style="font-size:12px;vertical-align:-2px;">edit</span> Add DL info
      </button>`;
    }
    // TASK-1605 — the ONE reason _hrCandMvrOrderOfferable says no once DL state is on
    // file and Checkr doesn't own the row is "no screening package". That used to
    // render NOTHING here. Say so, with the link (Chris 2026-09-29).
    if (reportKey === 'mvr' && !window.AppState?.tenantData?.compliancePackageDefault) {
      return hrScreeningPackageStopHtml({ compact: true });
    }
    if (useCheckrRunForMvr) {
      // TASK-555 — onboarding override: a candidate who already has a current MVR
      // shouldn't be forced through a paid Checkr run. Offer a FREE "Already have
      // one" alongside the paid run. The free path marks the MVR row received via
      // the existing overrideReportStatus primitive (no Checkr, no charge); the
      // canonical §391.25 date log lives on the person record post-hire
      // (saveMvrPull). UNGATED — no billing/partner gate (it's a status log).
      // TASK-1093 — "Already have one" is a doc-less manual received write; when
      // the master switch is ON the server rejects it, so hide it (keep the run).
      // TASK-1366 (3) — the label now says what this button actually orders.
      // "Run via Checkr ($)" sat next to the card-header "Run Check" with no
      // way to tell them apart; this one preselects the commercial MVR+PSP
      // package, the header one runs the account's default. Handler and
      // arguments unchanged — label + weight only. It is the SECONDARY action
      // in this card (outline, not filled): the one filled button on the whole
      // Background & Driving Record card is the full background check.
      // TASK-1356 — the "($)" was a lie for a partner-managed carrier. When
      // `_resolveBgHandledBy` returns 'partner' the server INTERCEPTS the order,
      // never bills, and files an action item for the partner — so a
      // cost-conscious admin was being scared off a button that costs them
      // nothing. Absent is the DEFAULT for a managed carrier, so this was the
      // common case, and this is the pre-hire screen: the first place a new
      // carrier meets the request flow. Mirrors the person-side button, which
      // has always got this right (`Request MVR` + forward_to_inbox). Shipped
      // with TASK-1366 because two COD lanes flagged the inconsistency of
      // fixing the sibling button the same night and leaving this one lying.
      const _mvrPartnerHandles = hrBgHandledByPartner();
      return `<div class="cand-actions-minor">
        <button type="button" class="btn btn-sm"
          onclick="window.HRModule.openBackgroundCheckModal('${escJsAttr(candidate.id)}','candidate','mvr')"
          title="${_mvrPartnerHandles ? `Request ${escapeHtml(hrPartnerLabel())} run an MVR for this applicant${hrBgPaidByPartner() ? ' — no charge to you' : ' — billed to your card when it completes'}.` : 'Orders an MVR on its own through Checkr, instead of the full background package.'}"
          style="font-size:0.7rem;padding:3px 9px;background:#fff;color:var(--brand-blue);border:1px solid var(--brand-blue);font-weight:600;">
          ${_mvrPartnerHandles ? 'Request MVR' : 'Order MVR only ($)'}
        </button>
        ${(_manualBgStatusDisabled() || !_hrCanAuthorBgStatus()) ? '' : `<button type="button" class="btn btn-sm"
          onclick="window.HRModule.recordExistingCandidateMvr('${candidate.id}')"
          title="Already have a current MVR on file? Mark it received. Free — no charge, no re-pull."
          style="font-size:0.7rem;padding:3px 9px;background:transparent;color:var(--text-secondary);border:1px solid var(--border-medium);">
          Already have one
        </button>`}
      </div>`;
    }
    if (url) {
      return `<a href="${url}" target="_blank" rel="noopener" class="btn btn-sm" style="font-size:0.7rem;padding:2px 8px;background:var(--brand-blue);color:#fff;border:none;text-decoration:none;">Run</a>`;
    }
    return '';
  })();

  // Manual-result attestation note (server-stamped on a no-document result flip) OR a
  // forced override of a Checkr-driven row — surface the reason + who/when below the status.
  const _row = candidate.backgroundReports?.[reportKey] || {};
  const manualReason = _row.attestationReason || _row.overrideReason || '';
  const manualBy = _row.attestedBy || _row.overrideBy || '';
  const manualAt = _hrFmtAttestDate(_row.attestedAt || _row.overriddenAt);
  const manualLine = manualReason
    ? `<div style="font-size:0.68rem;color:var(--text-secondary);line-height:1.4;border-top:1px dashed ${s.color}33;padding-top:5px;">Recorded manually: ${escapeHtml(manualReason)}${manualBy ? ` — ${escapeHtml(manualBy)}` : ''}${manualAt ? `, ${escapeHtml(manualAt)}` : ''}</div>` : '';

  // TASK-969 — the MVR row is a first-class piece: purpose chip (order-time
  // §391.23/§391.25/ad-hoc stamp, webhook-echoed to backgroundReports.mvr.
  // purpose) + its own "View MVR" opener when a Checkr MVR report is linked
  // (row's own checkrReportId first; parent report id as the join fallback,
  // matching the compliance-side hydrate). Display + print only — adjudication
  // stays in the BG report viewer's FCRA decision panel.
  const _mvrRowPurpose = reportKey === 'mvr' ? (_row.purpose || null) : null;
  const _mvrRowPurposeChip = _mvrRowPurpose ? _mvrPurposePill(_mvrRowPurpose) : '';
  const _mvrRowReportId = (reportKey === 'mvr' && checkrDriving)
    ? (_row.checkrReportId || candidate.checkrReportId || null) : null;
  const _mvrRowViewLine = _mvrRowReportId
    ? `<div><button class="btn btn-sm" onclick="window.HRModule.openMvrReportViewer('${escJsAttr(candidate.id)}','${escJsAttr(_mvrRowReportId)}')" style="font-size:0.7rem;padding:2px 8px;background:#B45309;color:#fff;border:none;display:inline-flex;align-items:center;gap:4px;" title="Open the standalone MVR report (view + print)">
        <span class="material-icons" style="font-size:12px;">description</span> View MVR
      </button></div>` : '';

  return `
    <div style="background:${s.bg};border:1px solid ${s.color}22;border-radius:var(--radius-md);padding:10px 12px;display:flex;flex-direction:column;gap:6px;">
      <div style="display:flex;align-items:center;gap:8px;">
        <span class="${dotClass}${dotHollow}" style="--dot-color:${s.color};" title="${etaTitle || s.label}"></span>
        <span style="font-size:0.78rem;font-weight:600;color:var(--text-primary);flex:1;">${label}</span>
        ${_mvrRowPurposeChip}
        ${checkrDriving ? `<span style="font-size:0.65rem;font-weight:600;color:${s.color};background:#fff;border:1px solid ${s.color}55;padding:1px 6px;border-radius:10px;">via Checkr</span>` : ''}
      </div>
      ${refInfo ? `<div style="font-size:0.7rem;color:var(--text-secondary);font-family:monospace;background:rgba(0,0,0,0.04);padding:2px 6px;border-radius:3px;user-select:all;">${escapeHtml(refInfo)}</div>` : ''}
      <div style="font-size:0.72rem;font-weight:500;color:${s.color};" title="${etaTitle}">${statusLine}</div>
      ${runMissingState ? `<div style="font-size:0.7rem;color:var(--text-secondary);line-height:1.3;">Need DL # + state to run</div>` : ''}
      ${_mvrRowViewLine}
      ${_buildReportCardControls(candidate, reportKey, status, checkrDriving, hasConflict, checkrSuggested, runButtonHtml)}
      ${dateStr ? `<div style="font-size:0.68rem;color:var(--text-secondary);">Last updated: ${dateStr}</div>` : ''}
      ${manualLine}
      ${reportKey === 'cdlis' ? cdlisPanelHtml(_row, { coveredStates: _cdlisCoveredStatesForCandidate(candidate), applicantStates: _cdlisApplicantStatesForCandidate(candidate), hasDoc: !!(_row.docPath || _row.docUrl || _row.resultDocUrl), candidateId: candidate.id, canEdit: _hrCanRecordCdlis(), canAttach: _hrCanAttestExternalCdlis(), canView: _hrCanViewCandidateReportDoc(), nonCdl: window.DqMatrix.getApplicableDqItems(candidate).positionType === 'non_cdl_driver' }) : ''}
    </div>`;
}

// TASK-535 — controls row for a BG report card. Two modes:
//  • Checkr-driven row → read-only "Managed by Checkr" state (no bare editable
//    select that silently shadows the live result). When the manual override
//    diverges from Checkr's real result, show a conflict banner with an
//    "Apply Checkr result" action; always offer a friction-gated "Override
//    anyway" (FCRA/§391.23 confirm + reason) and "Reset to Checkr sync".
//  • Non-Checkr AUTOMATABLE row (MVR / PSP not driven by Checkr — partner-run or
//    unlinked webhook) → NO bare dropdown. A safety-critical result Checkr can
//    produce must not be flipped accidentally. The only hand-set path is the
//    friction-gated "Set status manually" button (FCRA/§391.23 confirm + status
//    pick + required reason → server overrideReportStatus force+reason, audited).
//    Existing "Run via Checkr ($)" / "Already have one" buttons (runButtonHtml)
//    stay alongside it.
//  • Non-Checkr NON-automatable row (CDLIS / Clearinghouse — FMCSA-direct, no API)
//    → the plain editable select exactly as before (no automated source exists).
function _buildReportCardControls(candidate, reportKey, status, checkrDriving, hasConflict, checkrSuggested, runButtonHtml) {
  if (!checkrDriving) {
    // TASK-675 — automatable (MVR/PSP) rows lose the bare dropdown. The status
    // can only change through the deliberate, logged manual-record flow.
    if (CHECKR_AUTOMATABLE_REPORTS.has(reportKey)) {
      const isResult = status === 'received' || status === 'clear' || status === 'flagged';
      // 🐛 TASK-1366 (1) — this row used to re-print the status label as a pill
      // here, so an untouched MVR rendered "NOT STARTED" TWICE: once as
      // buildReportCard's `statusLine` div (which also carries the ETA) and
      // again as this pill. statusLine is the single source of truth for the
      // row's state; the controls row is actions only now. (The local `cur =
      // REPORT_STATUSES[status]` that fed the pill is gone with it — nothing
      // else in this branch read it.)
      // TASK-1093 — master switch ON: no manual button. Locked note + the
      // legitimate paths (Run via Checkr) only. Server rejects the write too.
      if (_manualBgStatusDisabled() || !_hrCanAuthorBgStatus()) {
        return `
          <div class="cand-actions-minor">
            ${runButtonHtml}
          </div>
          ${_manualBgDisabledNoteHtml()}`;
      }
      return `
        <div class="cand-actions-minor">
          ${runButtonHtml}
          <button type="button" class="btn btn-sm" style="font-size:0.66rem;padding:3px 8px;background:transparent;color:var(--text-secondary);border:1px solid var(--border-medium);"
            onclick="window.HRModule.manualSetReportStatus('${candidate.id}','${reportKey}')"
            title="${reportKey.toUpperCase()} is a screening Checkr can run. Hand-setting the result is a deliberate, logged act (FCRA / DOT §391.23).">
            <span class="material-icons" style="font-size:13px;vertical-align:-2px;">edit_note</span> ${isResult ? 'Change status manually' : 'Set status manually'}
          </button>
        </div>`;
    }
    // CDLIS / Clearinghouse — no automated source; keep the plain dropdown.
    return `
      <div style="display:flex;align-items:center;gap:4px;flex-wrap:wrap;">
        <select class="control-auto" ${_hrCanAuthorBgStatus() ? '' : 'disabled title="View &amp; upload only — an admin or a full HR user sets this status"'} style="font-size:0.72rem;padding:2px 4px;border:1px solid var(--border-light);border-radius:4px;background:#fff;cursor:pointer;"
          onchange="window.HRModule.updateReportStatus('${candidate.id}','${reportKey}',this.value)">
          ${_hrReportCardProofOnFile(candidate, reportKey, status) ? `<option value="" disabled selected>${HR_PROOF_ON_FILE_META.label}</option>` : ''}
          ${Object.entries(REPORT_STATUSES).map(([k, v]) => `<option value="${k}" ${!_hrReportCardProofOnFile(candidate, reportKey, status) && (k === status || (k === 'pending' && status === 'not_started')) ? 'selected' : ''}>${v.label}</option>`).join('')}
        </select>
        ${runButtonHtml}
      </div>`;
  }
  const sugLabel = (reportStatusMeta(checkrSuggested) || {}).label || checkrSuggested.toUpperCase();
  const curLabel = (reportStatusMeta(status) || {}).label || String(status).toUpperCase();
  return `
    <div style="display:flex;align-items:center;gap:6px;font-size:0.7rem;color:var(--text-secondary);">
      <span class="material-icons" style="font-size:14px;color:var(--brand-blue);">lock</span>
      <span>Managed by Checkr — status set by the live report.</span>
    </div>
    ${hasConflict ? `
      <div style="background:#FFFBEB;border:1px solid #FCD34D;border-radius:6px;padding:8px 10px;font-size:0.7rem;color:#92400E;line-height:1.4;">
        <strong>Override conflict:</strong> Checkr returned <strong>${escapeHtml(sugLabel)}</strong>; this report is overridden to <strong>${escapeHtml(curLabel)}</strong>.
        ${_hrCanAuthorBgStatus() ? `<div style="margin-top:6px;display:flex;gap:6px;flex-wrap:wrap;">
          <button class="btn btn-sm" style="font-size:0.68rem;padding:3px 9px;background:var(--brand-blue);color:#fff;border:none;"
            onclick="window.HRModule.applyCheckrResult('${candidate.id}','${reportKey}')">Apply Checkr result</button>
        </div>` : ''}
      </div>` : ''}
    <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;margin-top:2px;">
      ${_hrCanAuthorBgStatus() ? `<button class="btn btn-sm" style="font-size:0.66rem;padding:2px 8px;background:#fff;color:var(--text-secondary);border:1px solid var(--border-light);"
        onclick="window.HRModule.resetReportToCheckr('${candidate.id}','${reportKey}')"
        title="Clear the manual value and let Checkr's live result drive this report again">Reset to Checkr sync</button>` : ''}
      ${(_manualBgStatusDisabled() || !_hrCanAuthorBgStatus()) ? '' : `<button class="btn btn-sm" style="font-size:0.66rem;padding:2px 8px;background:#fff;color:#B91C1C;border:1px solid #FCA5A5;"
        onclick="window.HRModule.overrideReportAnyway('${candidate.id}','${reportKey}')"
        title="Manually override the live Checkr result (FCRA / DOT §391.23 — logged)">Override anyway</button>`}
    </div>`;
}

// TASK-649 (#6B) — getMvrUrl removed: the per-state MVR record-request portals
// were the off-platform "Run" redirect that bounced the user somewhere they
// couldn't actually pull the MVR. MVR is now recorded in-platform (Run via
// Checkr / "Already have one" / status dropdown). No external state-portal link.

// TASK-535 — all manual BG-report status writes now go through the
// overrideReportStatus callable (server re-checks "is Checkr driving this row",
// enforces the FCRA/§391.23 friction gate + a server-stamped audit). The old
// direct client updateDoc was console-exploitable and silently shadowed the
// live Checkr result. `opts.force` + `opts.reason` are passed through for the
// gated "Override anyway" path.
async function _writeReportStatus(candidateId, reportKey, newStatus, opts = {}) {
  const tenantId = HRState.tenantId;
  if (!tenantId) throw new Error('No tenant context');
  const fn = httpsCallable(functions, 'overrideReportStatus');
  const res = await fn({ tenantId, candidateId, reportKey, newStatus, force: !!opts.force, reason: opts.reason || '' });
  // Optimistic local update so the card re-renders without waiting for the
  // admin-bgCheck snapshot. Mirror what the server wrote.
  const today = new Date().toISOString().split('T')[0];
  const candidate = HRState.candidates.find(c => c.id === candidateId);
  if (candidate) {
    if (!candidate.backgroundReports) candidate.backgroundReports = {};
    if (!candidate.backgroundReports[reportKey]) candidate.backgroundReports[reportKey] = {};
    const r = candidate.backgroundReports[reportKey];
    r.status = newStatus;
    r.date = today;
    r.updatedBy = window.AppState?.user?.email || 'you';
    if (newStatus === 'pending') {
      // Reset un-shadows: drop the divergence shadow + override stamps locally too.
      delete r.checkrSuggestedStatus; delete r.checkrSuggestedAt;
      delete r.overrideReason; delete r.overrideBy; delete r.overriddenAt;
    } else if (opts.force) {
      r.overrideReason = opts.reason || '';
    }
  }
  return res;
}

async function updateReportStatus(candidateId, reportKey, newStatus) {
  if (!_hrCanAuthorBgStatus()) {
    window.showToast?.('View & upload only: you can view applicants and upload screening documents, but only an admin or a full HR user can set a background report status.', 'error');
    return;
  }
  try {
    await _writeReportStatus(candidateId, reportKey, newStatus);
    window.showToast?.(`${reportKey.toUpperCase()} status updated`, 'success');
    try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
  } catch (err) {
    // TASK-1185 — PROOF_REQUIRED: this tenant's managing compliance partner has
    // turned ON "require an uploaded document to record any result", and — unlike
    // NEEDS_PROOF — NO attestation/force can substitute. Steer to upload/run; do
    // NOT offer the force-retry (the server rejects it regardless). Must be checked
    // BEFORE NEEDS_PROOF.
    if ((err?.message || '').includes('PROOF_REQUIRED')) {
      // TASK-9677 (6c) — CDLIS is a vendor report; only the Clearinghouse is an FMCSA query.
      const _proofHint = reportKey === 'cdlis' ? 'Upload the CDLIS report'
        : reportKey === 'clearinghouse' ? 'Upload the FMCSA query proof'
        : 'Run it through Checkr or upload the report';
      window.showToast?.(`Your compliance partner requires the actual document on file before this report can be recorded. ${_proofHint}, then set the status.`, 'error', 8000);
      try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
      return;
    }
    // TASK-644 — NEEDS_PROOF: a non-Checkr result flip (received/clear/consider)
    // with no document on file. Reuse the §391.23/FCRA confirm + required-reason
    // friction (same dialog as the Checkr "Override anyway" path), then retry
    // with force so the server records the attestation.
    if ((err?.message || '').includes('NEEDS_PROOF')) {
      await _recordReportResultWithAttestation(candidateId, reportKey, newStatus);
      return;
    }
    console.error('[HR] Error updating report status:', err);
    // A Checkr-driven row hits the server CHECKR_DRIVEN guard — steer the user
    // to the gated override / reset path instead of the bare dropdown.
    const msg = (err?.message || '').includes('CHECKR_DRIVEN')
      ? 'Checkr is managing this report. Use "Override anyway" or "Reset to Checkr sync".'
      : (err?.message || 'Error updating report status');
    window.showToast?.(msg, 'error');
    try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
  }
}

// TASK-644 — record a non-Checkr report RESULT without an attached document.
// Confirms the §391.23/FCRA responsibility (same friction as overrideReportAnyway),
// captures a required note, then re-issues the status write with force:true so
// the server stamps the no-doc attestation. The carrier always retains the
// liability for using the real screening result.
async function _recordReportResultWithAttestation(candidateId, reportKey, newStatus) {
  const ok = await (window.showConfirm
    ? window.showConfirm(
        'Record this result without a document?',
        'You\'re marking this report as a result (received / clear / consider) without an attached screening document. Under FCRA and DOT §391.23 you remain responsible for having the actual result on file. This is logged (who, when, why). Continue?')
    : Promise.resolve(confirm('Record this result without a document? This is logged (FCRA / §391.23).')));
  if (!ok) { try { window.HRModule.viewCandidate(candidateId); } catch (_) {} return; }
  await _promptForBgDecisionNotes(
    'Confirmation note (required)',
    'Confirm you have the actual screening result (e.g. "Paper CDLIS verification pulled from FMCSA portal 2026-06-17, on file"). This is recorded in the compliance audit.',
    async (reason) => {
      const clean = (reason || '').trim();
      if (!clean) throw new Error('A confirmation note is required.');
      await _writeReportStatus(candidateId, reportKey, newStatus, { force: true, reason: clean });
      window.showToast?.(`${reportKey.toUpperCase()} result recorded`, 'success');
      try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
    });
}

// TASK-555 — FREE "record an existing MVR" override for a candidate. A carrier
// onboarding a driver who already has a current MVR shouldn't be forced through a
// paid Checkr run. Marks the MVR report row "received" via the existing
// overrideReportStatus primitive — NO Checkr call, NO charge, NO billing/partner
// gate (it's a date/status log, not a screening order). UNGATED on purpose: even
// when the partner handles BG/MVR, the carrier may bookkeep a pull they already
// hold (Chris 2026-06-10); the partner still owns any actual paid RUN. The
// canonical §391.25 annual date log (mvrLastPullDate/mvrNextDueDate + reminder
// cron) lives on the person record post-hire via saveMvrPull.
async function recordExistingCandidateMvr(candidateId) {
  // TASK-1093 — master switch: manual status entry disabled (server rejects too).
  if (!_hrCanAuthorBgStatus()) {
    window.showToast?.('View & upload only: you can view applicants and upload screening documents, but only an admin or a full HR user can set a background report status.', 'error');
    return;
  }
  if (_manualBgStatusDisabled()) {
    window.showToast?.('Manual background-check status entry is disabled for this company. Run it via Checkr or upload the report.', 'error');
    return;
  }
  try {
    await _writeReportStatus(candidateId, 'mvr', 'received');
    window.showToast?.('MVR marked as received — no charge.', 'success');
  } catch (err) {
    console.error('[HR] recordExistingCandidateMvr failed', err);
    window.showToast?.(err?.message || 'Failed to record existing MVR', 'error');
  }
  try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
}

// TASK-535 — apply Checkr's real result (from the webhook shadow field) over a
// divergent manual override. Reads the suggested status, then resets to pending
// so getReportStatus re-derives the live Checkr value (un-shadow).
async function applyCheckrResult(candidateId, reportKey) {
  if (!_hrCanAuthorBgStatus()) {
    window.showToast?.('View & upload only: you can view applicants and upload screening documents, but only an admin or a full HR user can set a background report status.', 'error');
    return;
  }
  try {
    await _writeReportStatus(candidateId, reportKey, 'pending');
    window.showToast?.('Applied Checkr result', 'success');
  } catch (err) {
    console.error('[HR] applyCheckrResult failed', err);
    window.showToast?.(err?.message || 'Failed to apply Checkr result', 'error');
  }
  try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
}

// TASK-535 — "Reset to Checkr sync": clear the manual value so the live Checkr
// result drives the row again. Routed through the same server path (pending is
// always allowed, no force needed).
async function resetReportToCheckr(candidateId, reportKey) {
  if (!_hrCanAuthorBgStatus()) {
    window.showToast?.('View & upload only: you can view applicants and upload screening documents, but only an admin or a full HR user can set a background report status.', 'error');
    return;
  }
  try {
    await _writeReportStatus(candidateId, reportKey, 'pending');
    window.showToast?.(`${reportKey.toUpperCase()} reset to Checkr sync`, 'success');
  } catch (err) {
    console.error('[HR] resetReportToCheckr failed', err);
    window.showToast?.(err?.message || 'Failed to reset', 'error');
  }
  try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
}

// TASK-535 — friction-gated "Override anyway". Carriers DO need an escape hatch
// (Checkr delayed/suspended → pull a paper MVR to onboard today), but it must be
// a deliberate, reasoned, logged act — not a silent dropdown. Confirm citing
// FCRA/§391.23, capture a required reason, then call the server with force:true
// (server stamps actor/time/reason in the audit).
async function overrideReportAnyway(candidateId, reportKey) {
  // TASK-1093 — master switch: manual status entry disabled (server rejects too).
  if (!_hrCanAuthorBgStatus()) {
    window.showToast?.('View & upload only: you can view applicants and upload screening documents, but only an admin or a full HR user can set a background report status.', 'error');
    return;
  }
  if (_manualBgStatusDisabled()) {
    window.showToast?.('Manual background-check status entry is disabled for this company. Run it via Checkr or upload the report.', 'error');
    return;
  }
  const ok = await (window.showConfirm
    ? window.showConfirm(
        'Override the live Checkr result?',
        'This manually overrides what Checkr returned for this report. Under FCRA and DOT §391.23 you remain responsible for using the actual screening result in your hiring decision. The override is logged (who, when, why). Continue?')
    : Promise.resolve(confirm('Override the live Checkr result? This is logged (FCRA / §391.23).')));
  if (!ok) return;
  await _promptForBgDecisionNotes(
    'Override reason (required)',
    'Why are you overriding the Checkr result? (e.g. "Checkr suspended — paper MVR pulled from ND DOT 2026-06-10"). This is recorded in the compliance audit.',
    async (reason) => {
      const clean = (reason || '').trim();
      if (!clean) throw new Error('A reason is required to override the Checkr result.');
      // Pick the target status: the divergent manual value already shown, or
      // prompt-free default to the current displayed status. The card calls this
      // when a manual value exists; default to 'received' (admin will adjust via
      // the next deliberate action) only if nothing is set.
      const cand = HRState.candidates.find(c => c.id === candidateId);
      const cur = cand?.backgroundReports?.[reportKey]?.status;
      const target = (cur && cur !== 'pending') ? cur : 'received';
      await _writeReportStatus(candidateId, reportKey, target, { force: true, reason: clean });
      window.showToast?.('Override recorded', 'success');
      try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
    });
}

// TASK-675 — friction-gated MANUAL status set for an AUTOMATABLE report (MVR /
// PSP) that Checkr is NOT currently driving (partner ran it in Checkr's own
// dashboard, or the webhook→candidate stamp gap left it unlinked). The bare
// dropdown is gone for these rows; this is the ONLY hand-set path. A single
// modal collects (1) the target status and (2) a required §391.23/FCRA reason,
// then routes through _writeReportStatus with force:true so the server stamps
// the actor/time/reason audit (the same primitive the Checkr "Override anyway"
// path uses; force satisfies the server NEEDS_PROOF gate on result statuses).
function manualSetReportStatus(candidateId, reportKey) {
  // TASK-1093 — master switch: manual status entry disabled (server rejects too).
  if (!_hrCanAuthorBgStatus()) {
    window.showToast?.('View & upload only: you can view applicants and upload screening documents, but only an admin or a full HR user can set a background report status.', 'error');
    return;
  }
  if (_manualBgStatusDisabled()) {
    window.showToast?.('Manual background-check status entry is disabled for this company. Run it via Checkr or upload the report.', 'error');
    return;
  }
  // Guard against a double-tap opening two stacked modals.
  document.getElementById('_msrs-overlay')?.remove();
  const cand = HRState.candidates.find(c => c.id === candidateId);
  const curStatus = cand?.backgroundReports?.[reportKey]?.status || 'pending';
  const keyLabel = reportKey.toUpperCase();
  const overlay = document.createElement('div');
  overlay.id = '_msrs-overlay';
  overlay.className = 'modal';
  overlay.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:11000;background:rgba(0,0,0,0.5);position:fixed;inset:0;';
  const optionsHtml = Object.entries(REPORT_STATUSES)
    .map(([k, v]) => `<option value="${k}" ${k === curStatus ? 'selected' : ''}>${escapeHtml(v.label)}</option>`)
    .join('');
  overlay.innerHTML = `
    <div style="background:#fff;padding:20px 22px;border-radius:12px;max-width:480px;width:90%;box-shadow:0 8px 32px rgba(0,0,0,0.15);">
      <div style="font-weight:700;font-size:1rem;margin-bottom:6px;color:var(--text-primary);">Set ${escapeHtml(keyLabel)} status manually</div>
      <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:12px;line-height:1.45;">${escapeHtml(keyLabel)} is a screening Checkr can run, so hand-setting its result is a deliberate, logged act. Under FCRA and DOT §391.23 you remain responsible for having the actual screening result on file. This is recorded in the compliance audit (who, when, why).</div>
      <label style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Status</label>
      <select id="_msrs-status" class="form-control" style="width:100%;margin-bottom:12px;">${optionsHtml}</select>
      <label style="display:block;font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Reason (required)</label>
      <textarea id="_msrs-reason" placeholder='e.g. "Partner ran MVR in Checkr dashboard 2026-06-23; result on file." or "Paper PSP pulled from FMCSA portal."' style="width:100%;padding:10px 12px;border:1px solid var(--border-medium);border-radius:8px;font:inherit;font-size:0.9rem;min-height:80px;resize:vertical;"></textarea>
      <div id="_msrs-error" style="display:none;background:#FEE2E2;border-left:4px solid #DC2626;color:#991b1b;padding:8px 12px;border-radius:6px;font-size:0.82rem;line-height:1.45;margin-top:10px;"></div>
      <div style="display:flex;justify-content:flex-end;gap:8px;margin-top:14px;">
        <button id="_msrs-cancel" class="btn btn-secondary" style="padding:8px 14px;">Cancel</button>
        <button id="_msrs-ok" class="btn btn-primary" style="padding:8px 14px;">Save status</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  const statusSel = overlay.querySelector('#_msrs-status');
  const reasonEl = overlay.querySelector('#_msrs-reason');
  const cancelBtn = overlay.querySelector('#_msrs-cancel');
  const okBtn = overlay.querySelector('#_msrs-ok');
  const errBox = overlay.querySelector('#_msrs-error');
  reasonEl.focus();
  cancelBtn.onclick = () => overlay.remove();
  okBtn.onclick = async () => {
    const newStatus = statusSel.value;
    const clean = (reasonEl.value || '').trim();
    errBox.style.display = 'none';
    if (!clean) {
      errBox.textContent = 'A reason is required to manually set this report status.';
      errBox.style.display = 'block';
      return;
    }
    const okOriginal = okBtn.innerHTML;
    okBtn.disabled = true; cancelBtn.disabled = true; cancelBtn.style.opacity = '0.5';
    okBtn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;animation:spin 0.8s linear infinite;">progress_activity</span> Saving…';
    try {
      // force:true so the server records the §391.23/FCRA attestation and never
      // hits the bare-write NEEDS_PROOF rejection for a result status.
      await _writeReportStatus(candidateId, reportKey, newStatus, { force: true, reason: clean });
      overlay.remove();
      window.showToast?.(`${keyLabel} status set manually`, 'success');
      try { window.HRModule.viewCandidate(candidateId); } catch (_) {}
    } catch (err) {
      okBtn.disabled = false; cancelBtn.disabled = false; cancelBtn.style.opacity = '';
      okBtn.innerHTML = okOriginal;
      errBox.textContent = err?.message || 'Failed to set status. Please try again.';
      errBox.style.display = 'block';
    }
  };
}

// ── Citation Create/Edit Modal ──

// Back-compat readers: old citation docs stored linkedDvirId/Label as single
// strings. New docs store linkedDvirIds[]/linkedDvirLabels[] arrays so one
// DVIR can clear multiple citations on the same vehicle.
function getLinkedDvirIdsHR(c) {
  if (Array.isArray(c?.linkedDvirIds)) return c.linkedDvirIds.filter(Boolean);
  if (c?.linkedDvirId) return [c.linkedDvirId];
  return [];
}
function getLinkedDvirLabelsHR(c) {
  if (Array.isArray(c?.linkedDvirLabels)) return c.linkedDvirLabels;
  if (c?.linkedDvirLabel) return [c.linkedDvirLabel];
  return [];
}

const VIOLATION_CATEGORIES = [
  { code: '', label: 'Select violation type...' },
  { code: 'VEHICLE', label: 'Vehicle Condition' },
  { code: 'DRIVER', label: 'Driver Violation' },
  { code: 'HOS', label: 'Hours of Service' },
  { code: 'HAZMAT', label: 'Hazardous Materials' },
  { code: 'SIZE_WEIGHT', label: 'Size & Weight' },
  { code: 'OTHER', label: 'Other' },
];

// =============================================================================
// TASK-9938 (bundle headroom) — Record Citation, the PEI investigation modal, the medical
// exam modal, the intrastate exemption modal and the MVR pull modal live in the
// LAZY views/hrDqRecords.js (perf budget appColdLoadGzip). Bodies moved verbatim;
// each shim keeps the name window.HRModule calls, loads the module on first use
// and, once loaded, calls straight through synchronously. Deps are handed over,
// never imported (pitfall #1).
// =============================================================================
let _hrDqMod = null;
let _hrDqLoad = null;
function _hrDqDeps() {
  return {
    HRState, MED_RESTRICTION_OPTIONS, PEI_STATES, Timestamp, US_STATES_FOR_MVR,
    VIOLATION_CATEGORIES, _IX_NO_ACCESS_MSG, _empName, _empVerificationKey,
    _hrCanSetIntrastateExemption, _hrCanWriteRoster, _hrPartnerClientTenantId,
    _hrPartnerUploadPersonDoc, _peiBtnBusy, _peiPartitionEmployers, _peiRowScopeChipHtml,
    _peiStatusKey, _peiSummary, _peiSummaryChipHtml, _peiWindowScope, _promptAnnualReviewAfterMvr,
    _readFileAsBase64, _reapOrphanCredBlobHr, addDoc, arrayRemove, arrayUnion, closeModal,
    collection, db, doc, ensureCandidateInHrState, escJsAttr, escapeHtml, formatDateShort,
    functions, getDoc, getDocs, getLinkedDvirIdsHR, getLinkedDvirLabelsHR, httpsCallable, limit,
    loadEmployeeCitations, openEmployeeDetail, orderBy, photoErrorMessage, preparePdfOnlyUpload,
    prepareUploadFile, query, ref, storage, syncCitationCostEntry, updateDoc, uploadBytes,
    viewCandidate, where
  };
}
function _loadHrDq() {
  if (_hrDqMod) return Promise.resolve(_hrDqMod);
  if (!_hrDqLoad) {
    _hrDqLoad = __ddChunkWrap(import('./hrDqRecords.js?v=9999980')).then((m) => { m._bind(_hrDqDeps()); _hrDqMod = m; return m; }).catch((e) => {
      _hrDqLoad = null; // the next click tries again
      console.error('[HR] hrDqRecords load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _hrLazyLoadFailed: true });
    });
  }
  return _hrDqLoad;
}
function recordPriorEmployerVerification(...a) { return _hrDqMod ? _hrDqMod.recordPriorEmployerVerification(...a) : _hrLazyCall(_loadHrDq, 'recordPriorEmployerVerification', a); }
function openCitationModal(...a) { return _hrDqMod ? _hrDqMod.openCitationModal(...a) : _hrLazyCall(_loadHrDq, 'openCitationModal', a); }
function saveCitationFromHR(...a) { return _hrDqMod ? _hrDqMod.saveCitationFromHR(...a) : _hrLazyCall(_loadHrDq, 'saveCitationFromHR', a); }
function openMvrPullModal(...a) { return _hrDqMod ? _hrDqMod.openMvrPullModal(...a) : _hrLazyCall(_loadHrDq, 'openMvrPullModal', a); }
function saveMvrPull(...a) { return _hrDqMod ? _hrDqMod.saveMvrPull(...a) : _hrLazyCall(_loadHrDq, 'saveMvrPull', a); }
function openMvrGoodFaith(...a) { return _hrDqMod ? _hrDqMod.openMvrGoodFaith(...a) : _hrLazyCall(_loadHrDq, 'openMvrGoodFaith', a); }
function onMvrFilePicked(...a) { return _hrDqMod ? _hrDqMod.onMvrFilePicked(...a) : _hrLazyCall(_loadHrDq, 'onMvrFilePicked', a); }
function autofillMvrFromPdf(...a) { return _hrDqMod ? _hrDqMod.autofillMvrFromPdf(...a) : _hrLazyCall(_loadHrDq, 'autofillMvrFromPdf', a); }
function openPeiInvestigation(...a) { return _hrDqMod ? _hrDqMod.openPeiInvestigation(...a) : _hrLazyCall(_loadHrDq, 'openPeiInvestigation', a); }
function setPeiContactStatus(...a) { return _hrDqMod ? _hrDqMod.setPeiContactStatus(...a) : _hrLazyCall(_loadHrDq, 'setPeiContactStatus', a); }
function onPeiContactMethodChange(...a) { return _hrDqMod ? _hrDqMod.onPeiContactMethodChange(...a) : _hrLazyCall(_loadHrDq, 'onPeiContactMethodChange', a); }
function markPeiContacted(...a) { return _hrDqMod ? _hrDqMod.markPeiContacted(...a) : _hrLazyCall(_loadHrDq, 'markPeiContacted', a); }
function markPeiNotApplicable(...a) { return _hrDqMod ? _hrDqMod.markPeiNotApplicable(...a) : _hrLazyCall(_loadHrDq, 'markPeiNotApplicable', a); }
function restorePeiEmployer(...a) { return _hrDqMod ? _hrDqMod.restorePeiEmployer(...a) : _hrLazyCall(_loadHrDq, 'restorePeiEmployer', a); }
function savePeiResponse(...a) { return _hrDqMod ? _hrDqMod.savePeiResponse(...a) : _hrLazyCall(_loadHrDq, 'savePeiResponse', a); }
function printPeiRequestForm(...a) { return _hrDqMod ? _hrDqMod.printPeiRequestForm(...a) : _hrLazyCall(_loadHrDq, 'printPeiRequestForm', a); }
function addPeiAttempt(...a) { return _hrDqMod ? _hrDqMod.addPeiAttempt(...a) : _hrLazyCall(_loadHrDq, 'addPeiAttempt', a); }
function printPeiGoodFaithLog(...a) { return _hrDqMod ? _hrDqMod.printPeiGoodFaithLog(...a) : _hrLazyCall(_loadHrDq, 'printPeiGoodFaithLog', a); }
function emailPeiRequestForm(...a) { return _hrDqMod ? _hrDqMod.emailPeiRequestForm(...a) : _hrLazyCall(_loadHrDq, 'emailPeiRequestForm', a); }
function printPeiSheet(...a) { return _hrDqMod ? _hrDqMod.printPeiSheet(...a) : _hrLazyCall(_loadHrDq, 'printPeiSheet', a); }
function openMedExamModal(...a) { return _hrDqMod ? _hrDqMod.openMedExamModal(...a) : _hrLazyCall(_loadHrDq, 'openMedExamModal', a); }
function openIntrastateExemptionModal(...a) { return _hrDqMod ? _hrDqMod.openIntrastateExemptionModal(...a) : _hrLazyCall(_loadHrDq, 'openIntrastateExemptionModal', a); }
function saveIntrastateExemption(...a) { return _hrDqMod ? _hrDqMod.saveIntrastateExemption(...a) : _hrLazyCall(_loadHrDq, 'saveIntrastateExemption', a); }
function saveMedExam(...a) { return _hrDqMod ? _hrDqMod.saveMedExam(...a) : _hrLazyCall(_loadHrDq, 'saveMedExam', a); }

async function syncCitationCostEntry(tenant, citationId, citationData) {
  const costEntryId = `citation_${citationId}`;
  const costData = {
    source: 'citation',
    citationId,
    category: 'fines',
    amount: citationData.fineAmount || 0,
    date: citationData.inspectionDate || window.RecordDate.today(), // TASK-9923 (was UTC)
    description: `Citation: ${citationData.reportNumber || 'N/A'} — ${citationData.location || 'Unknown location'}${citationData.description ? ' — ' + citationData.description.slice(0, 80) : ''}`,
    vehicleId: citationData.vehicleId || '',
    vehicleDisplay: citationData.vehicleUnit || '',
    driverId: citationData.driverId || '',
    driverName: citationData.driverName || '',
    paidBy: citationData.paidBy || 'pending',
    driverPaid: citationData.paidBy === 'driver',
    driverDeductionPending: citationData.paidBy === 'driver' && !citationData.paidDate,
    paidDate: citationData.paidDate || '',
    updatedAt: Timestamp.now(),
  };

  await setDoc(doc(db, 'tenants', tenant, 'costEntries', costEntryId), costData, { merge: true });
}

async function deleteCitationRecord(empId, citationId) {
  if (!confirm('Delete this citation? This cannot be undone.')) return;
  try {
    const tenant = HRState.tenantId;
    await deleteDoc(doc(db, 'tenants', tenant, 'citations', citationId));
    // Also delete linked cost entry
    try { await deleteDoc(doc(db, 'tenants', tenant, 'costEntries', `citation_${citationId}`)); } catch (e) { /* may not exist */ }
    window.showToast?.('Citation deleted', 'success');
    document.getElementById('modal-hr-citation')?.remove();
    loadEmployeeCitations(empId);
  } catch (err) {
    console.error('[HR] Error deleting citation:', err);
    window.showToast?.('Error deleting citation', 'error');
  }
}

async function viewCandidate(id) {
  const candidate = HRState.candidates.find(c => c.id === id);
  if (!candidate) return;
  HRState.selectedCandidate = candidate;
  // Lazy-load the Checkr bgCheck doc (admin-only collection) so the view
  // modal can show current status. Stashed on `_bgCheck` to keep it off the
  // pipeline render which doesn't need it.
  candidate._bgCheck = await loadBgCheck(id);
  const existing = document.getElementById('modal-view-candidate');
  if (existing) existing.remove();
  document.body.insertAdjacentHTML('beforeend', getViewCandidateModalHTML(candidate));
  // TASK-4611 — same for the read-only detail view (the surface a reviewer
  // actually reads).
  _installAppDocViewDelegate();
  if (candidate && candidate.id) _loadCandidateAppDocs(candidate.id, candidate);
  document.getElementById('modal-view-candidate').classList.remove('hidden');
  // TASK-9761 C — duplicate-application banner (server match; ids only come back).
  _hrLoadDuplicateBanner(candidate).catch(() => {});
  // TASK-1213 — async-load the Notes & Calls feed into the freshly-inserted modal.
  loadCandidateNotesFeed(id).catch(e => console.warn('[HR] notes feed load failed:', e?.message));
  // TASK-4791 — activity log; markViewed records this open as a review.
  loadCandidateActivity(id, { markViewed: true }).catch(() => {});
  // v1.15.512 Phase A — first-view-per-session tooltip on the status pill.
  // Helps users discover that the header pill is interactive (it reads like a label
  // until you hover, which on mobile never happens). 4s auto-dismiss, also dismissed
  // by any document click. sessionStorage so it shows once per browser tab.
  try { _maybeShowStatusPillHint(id); } catch (_) {}
}

// TASK-1103 — resolve a candidate doc by id for POST-HIRE surfaces. The hiring
// listener only populates HRState.candidates while the HR view runs, so the
// Roster / Compliance / employee-detail "View Application" affordances resolve
// or fetch the doc here, seed it into HRState.candidates (viewCandidate reads
// from there), then reuse the existing candidate application modal + Print
// unchanged. Fail-closed on tenant mismatch (same pattern as
// ensureSubjectInHrState — never insert a cross-tenant doc into HRState).
async function ensureCandidateInHrState(candidateId) {
  if (!candidateId) return null;
  if (!Array.isArray(HRState.candidates)) HRState.candidates = [];
  const cached = HRState.candidates.find(c => c.id === candidateId);
  if (cached) return cached;
  const appTenant = window.AppState?.tenant || null;
  if (HRState.tenantId && appTenant && HRState.tenantId !== appTenant) return null;
  const tenantId = HRState.tenantId || appTenant;
  if (!tenantId) return null;
  if (!HRState.tenantId) HRState.tenantId = tenantId;
  const snap = await getDoc(doc(db, 'tenants', tenantId, 'candidates', candidateId));
  if (!snap.exists()) return null;
  const cand = { id: snap.id, ...snap.data() };
  HRState.candidates.push(cand);
  return cand;
}

// TASK-1103 — open the completed EMPLOYMENT APPLICATION (§391.21) by
// candidateId. Post-hire entry points: HR employee detail (Employment tab),
// Roster person modal, Compliance person detail — all via person.candidateId
// (stamped by Approve-to-Roster). Reuses the existing candidate view modal, so
// Print / save-as-PDF comes for free (printCandidateApplication).
// Firestore candidates read is gated admin | complianceManager | hasModule('hr')
// — a compliance-only member without HR/admin gets a clear toast, not a crash.
async function openCandidateApplication(candidateId) {
  if (!candidateId) { window.showToast?.('No digital application on file', 'info'); return; }
  try {
    const cand = await ensureCandidateInHrState(candidateId);
    if (!cand) { window.showToast?.('Application record not found', 'error'); return; }
    await viewCandidate(cand.id);
  } catch (err) {
    console.error('[HR] openCandidateApplication failed', err);
    const msg = String(err?.code || err?.message || '').toLowerCase();
    window.showToast?.(msg.includes('permission')
      ? 'Viewing applications requires HR or admin access'
      : 'Could not load the application', 'error');
  }
}

// TASK-1103 — person-keyed wrapper (HR employee detail + delegation targets).
async function openCandidateApplicationForPerson(personId) {
  const emp = (HRState.people || []).find(p => p.id === personId);
  const cid = emp && emp.candidateId;
  if (!cid) { window.showToast?.('No digital application on file for this person', 'info'); return; }
  await openCandidateApplication(cid);
}
function _peiBtnBusy(btn) {
  if (!btn || btn.tagName !== 'BUTTON') return () => {};
  const orig = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Saving…';
  return () => { try { btn.disabled = false; btn.innerHTML = orig; } catch (_) {} };
}
function _maybeShowStatusPillHint(candidateId) {
  if (typeof sessionStorage === 'undefined') return;
  if (sessionStorage.getItem('hiring_status_pill_hint_shown_v1') === '1') return;
  const pill = document.getElementById('candidate-status-pill-' + candidateId);
  if (!pill) return;
  sessionStorage.setItem('hiring_status_pill_hint_shown_v1', '1');
  const tip = document.createElement('div');
  tip.className = 'candidate-status-pill-hint';
  tip.setAttribute('role', 'tooltip');
  tip.textContent = 'Tap to change status';
  // Append to body and position relative to the pill so it works even when
  // the modal header re-flows on narrow screens.
  document.body.appendChild(tip);
  const positionTip = () => {
    const r = pill.getBoundingClientRect();
    tip.style.position = 'fixed';
    tip.style.top = (r.bottom + 6) + 'px';
    tip.style.left = Math.max(8, r.left + (r.width / 2) - (tip.offsetWidth / 2)) + 'px';
    tip.style.zIndex = '11000';
  };
  // Two-frame positioning so offsetWidth is measured after layout.
  requestAnimationFrame(() => { requestAnimationFrame(positionTip); });
  let dismissed = false;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    document.removeEventListener('click', dismiss, true);
    tip.classList.add('fade-out');
    setTimeout(() => tip.remove(), 220);
  };
  // First click anywhere on the document dismisses (after current click cycle).
  setTimeout(() => document.addEventListener('click', dismiss, true), 0);
  setTimeout(dismiss, 4000);
}

// --- Quick Status ---
// TASK-1212 — open the quick-status modal with a stage pre-selected (Reject… /
// Move to Potentials action-bar buttons). No new status machinery: this is the
// EXISTING modal, so the TASK-1152/1155 FCRA hard-routing applies unchanged.
function openQuickStatusModalPreset(id, presetStatus) {
  try { closeModal('view-candidate'); } catch (_) {}
  openQuickStatusModal(id);
  // Same deferred-preselect pattern as declineNotBasedOnReport (TASK-1152).
  setTimeout(() => {
    const sel = document.getElementById('quick-status-select');
    if (sel && sel.value !== presetStatus) {
      sel.value = presetStatus;
      onQuickStatusStageChange(presetStatus);
    }
    if (presetStatus === 'rejected') {
      document.getElementById('quick-rejection-reason')?.focus();
    } else if (presetStatus === 'potentials') {
      document.getElementById('quick-status-note')?.focus();
    }
  }, 60);
}

// TASK-9425 — archived applications are read-only for status / stage / hire. One
// message, one check, used by every entry point below (and the rules refuse the
// write regardless).
const HR_ARCHIVED_READ_ONLY_MSG = 'This application is archived, so it is read-only. Restore it first to change its status or hire them.';
function _hrArchivedStageBlock(candidate) {
  if (!isArchivedCandidate(candidate)) return false;
  window.showToast?.(HR_ARCHIVED_READ_ONLY_MSG, 'warning');
  return true;
}

function openQuickStatusModal(id) {
  const candidate = HRState.candidates.find(c => c.id === id);
  if (!candidate) return;
  if (_hrArchivedStageBlock(candidate)) return; // TASK-9425
  const existing = document.getElementById('modal-quick-status');
  if (existing) existing.remove();
  document.body.insertAdjacentHTML('beforeend', getQuickStatusModalHTML(candidate));
  document.getElementById('modal-quick-status').classList.remove('hidden');
  // TASK-1155 — the candidate's current reason may be preselected; sync the CTA.
  syncQuickStatusDecisionCta();
}

// TASK-1264 — re-entry guard for the quick-status Save. Module-scoped so a
// second saveQuickStatus() invocation (a double-tap during the dead air on
// slow/LTE) bails before firing a second status write / feed entry / rejection
// email. Mirrors the TASK-1190 _peiWriteInFlight in-flight pattern.
let _quickStatusSaveInFlight = false;
async function saveQuickStatus() {
  const id = document.getElementById('quick-status-candidate-id').value;
  const status = document.getElementById('quick-status-select').value;
  const rejectionReason = status === 'rejected' ? document.getElementById('quick-rejection-reason').value : '';
  if (status === 'rejected' && !rejectionReason) {
    window.showToast?.('Please select a rejection reason', 'error');
    return;
  }
  // TASK-1264 — busy-state + re-entry guard. Save had no in-flight feedback: on
  // a slow/LTE connection the dead air between tap and modal-close invited a
  // double-tap, which re-entered here and could fire TWO status writes, TWO
  // status-feed entries, and — on the Rejected path — TWO rejection emails to
  // the applicant. Set the flag + disable the Save button ("Saving…" spinner)
  // BEFORE the first await so BOTH the FCRA-intercept and the direct-write paths
  // are covered; try/finally restores on every early-return / throw (the success
  // path closes the modal, so the button vanishes with it — _peiBtnBusy's restore
  // no-ops safely on the detached node). Reuses the TASK-1190 _peiBtnBusy helper.
  if (_quickStatusSaveInFlight) return;
  _quickStatusSaveInFlight = true;
  const restoreSaveBtn = _peiBtnBusy(document.getElementById('quick-status-save-btn'));
  try {
    // TASK-371 — FCRA-aware rejection intercept. Same shape as saveCandidateForm
    // but intercepted UPSTREAM of _saveQuickStatusDirect so the undo path
    // (which also calls _saveQuickStatusDirect) does NOT re-trigger the modal
    // when undoing a successful non-BG rejection. Codex caught this at v1 COD.
    if (status === 'rejected'
        && _isReportBasedRejectionReason(rejectionReason)  // TASK-1152 — was exact 'Failed background check'; now ALL report-based reasons hard-route to AA (closes the synonym bypass)
        && id) {
      const reportId = await _fcraRejectionReportId(id);
      if (reportId) {
        const routed = await _fcraRejectionIntercept(id, reportId, rejectionReason);
        if (routed) {
          try { closeModal('quick-status'); } catch (_) {}
          return;
        }
        return; // user picked "different reason"
      }
      console.warn('[HR] Failed-BG quick-status pick but no BG report on file for', id);
    }
    // v1.15.512 Phase A — capture old status for undo + stamp statusChangedAt
    // for the aging-dot indicator. _saveQuickStatusDirect handles the actual write
    // so the undo path can reuse it without re-reading modal DOM.
    const candidateBefore = HRState.candidates.find(c => c.id === id);
    const oldStatus = candidateBefore?.status || null;
    const oldRejectionReason = candidateBefore?.rejectionReason || '';
    const statusChanged = oldStatus !== status;
    // TASK-1213 — optional note for ALL stages (feeds the timeline). TASK-1212 —
    // for Potentials the note ALSO persists to candidate.potentialsNote.
    const quickNote = (document.getElementById('quick-status-note')?.value || '').trim().slice(0, 1000);
    const extraFields = status === 'potentials' ? { potentialsNote: quickNote } : null;
    // TASK-1219 — inline interview scheduling when the stage flips to Interview.
    const ivAtLocal = status === 'interview' ? (document.getElementById('quick-interview-at')?.value || '') : '';
    const ivPersonId = status === 'interview' ? (document.getElementById('quick-interview-interviewer')?.value || '') : '';
    try {
      await _saveQuickStatusDirect(id, status, rejectionReason, { stampChangedAt: statusChanged, extraFields });
      closeModal('quick-status');

      // TASK-1213 CORE — every status change lands in the Notes & Calls feed
      // (rejection reason / potentials note included); a note with no status
      // change logs as a plain note. Best-effort — never blocks the save.
      if (statusChanged) {
        _logCandidateStatusFeedEntry(id, oldStatus, status, {
          detail: status === 'rejected' ? rejectionReason : '',
          note: quickNote,
        });
      } else if (quickNote) {
        _writeCandidateFeedDoc(id, { body: quickNote, type: 'note' })
          .then(() => _renderCandidateNotesFeed(id))
          .catch(e => console.warn('[HR] quick note write failed:', e?.message));
      }

      // TASK-1219 — schedule the interview if the inline fields were filled.
      // COD Code Reviewer (2026-07-16) — a silent failure here left the candidate
      // in Interview with no interviewAt; surface it and point at the card path.
      if (status === 'interview' && ivAtLocal) {
        _scheduleInterviewViaCallable(id, ivAtLocal, ivPersonId, '')
          .catch(e => {
            console.warn('[HR] inline interview schedule failed:', e?.message);
            window.showToast?.('Status saved, but the interview didn\'t schedule — use "Schedule interview" on the applicant card', 'error');
          });
      }

      if (statusChanged && oldStatus) {
        const stage = PIPELINE_STAGES.find(s => s.id === status);
        const stageLabel = stage?.label || status;
        const nameSafe = candidateBefore?.name || 'Candidate';
        // Reuse existing showToast(msg, type, action) — the toast pre-supports
        // an Undo button with 15s timer when an action is passed.
        window.showToast?.(`Moved ${nameSafe} to ${stageLabel}`, 'success', {
          label: 'Undo',
          callback: () => {
            _saveQuickStatusDirect(id, oldStatus, oldRejectionReason, { stampChangedAt: true })
              .then(() => {
                window.showToast?.('Status reverted', 'info');
                // TASK-1213 — the reversal is part of the story too.
                _logCandidateStatusFeedEntry(id, status, oldStatus, { undo: true });
              })
              .catch(err => {
                console.warn('[HR] Undo status failed:', err);
                // TASK-9141 — undoing BACK INTO 'hired' on a candidate that never
                // went through promote (one of the legacy damaged rows) hits the
                // choke point. Route it to promote rather than toasting a failure.
                if (err?.code === 'hr/hire-needs-promote') {
                  window.showToast?.(HIRE_NEEDS_PROMOTE_MSG, 'info');
                  openApproveToRosterModal(id);
                  return;
                }
                // TASK-1155 — undoing BACK INTO rejected routes through the
                // callable; surface its message (e.g. AA required) when present.
                const _undoMsg = String(err?.code || '').startsWith('functions/') ? (err?.message || '') : '';
                window.showToast?.(_undoMsg || 'Undo failed', 'error');
              });
          }
        });
      } else {
        window.showToast?.('Status updated', 'success');
      }

      // Send rejection email if candidate has email
      if (status === 'rejected') {
        const candidate = HRState.candidates.find(c => c.id === id);
        if (candidate?.email) {
          try {
            const sendCandidateEmail = httpsCallable(functions, 'sendCandidateEmail');
            const tenantDoc = window.AppState?.settings;
            const companyName = tenantDoc?.companyName || tenantDoc?.name || 'Our Company';
            await sendCandidateEmail({
              tenantId: HRState.tenantId,
              candidateEmail: candidate.email,
              candidateName: candidate.name,
              type: 'rejection',
              companyName,
              reason: rejectionReason !== 'Other' ? rejectionReason : ''
            });
            window.showToast?.('Rejection email sent', 'info');
          } catch (emailErr) {
            console.warn('[HR] Rejection email failed (non-blocking):', emailErr);
          }
        }
      }
    } catch (error) {
      console.error('[HR] Error updating status:', error);
      // TASK-9141 — the hire choke point in _saveQuickStatusDirect. Not an error
      // from the operator's point of view: close this modal and hand them the
      // promote flow, which is the click they actually meant.
      if (error?.code === 'hr/hire-needs-promote') {
        try { closeModal('quick-status'); } catch (_) {}
        window.showToast?.(HIRE_NEEDS_PROMOTE_MSG, 'info');
        openApproveToRosterModal(id);
        return;
      }
      // TASK-1155 — surface the setCandidateDecision callable's jargon-free
      // message (e.g. the Adverse-Action requirement) instead of a generic error.
      const _fnMsg = String(error?.code || '').startsWith('functions/') ? (error?.message || '') : '';
      window.showToast?.(_fnMsg || 'Error updating status', 'error');
    }
  } finally {
    // TASK-1264 — always clear the guard + restore the Save button, on every
    // path: FCRA early-returns (:routed/different-reason), success (button already
    // gone with the closed modal — restore no-ops), and any throw.
    _quickStatusSaveInFlight = false;
    restoreSaveBtn();
  }
}

// v1.15.512 Phase A — extracted write path so the Undo callback can re-invoke
// without touching the (now-closed) quick-status modal. `stampChangedAt`
// is true on the initial change AND on the undo so the aging-dot resets
// either direction.
async function _saveQuickStatusDirect(id, status, rejectionReason, opts = {}) {
  // TASK-9425 — the same choke point refuses an ARCHIVED application (every quick-
  // status write, Undo included, funnels here). Typed error so both callers' catch
  // surfaces it; firestore.rules and the reject callable refuse it too.
  if (isArchivedCandidate(HRState.candidates.find(x => x.id === id))) {
    const _e = new Error(HR_ARCHIVED_READ_ONLY_MSG);
    _e.code = 'hr/archived-read-only';
    throw _e;
  }
  // TASK-9141 — BYPASS #2's choke point. Every quick-status write funnels through
  // here, including the Undo callback, so this is the one place that cannot be
  // routed around inside this module. Throw a typed error rather than silently
  // rerouting: both callers already have a catch that surfaces the message, and
  // they open the promote modal from there (a silent reroute would let the Undo
  // path toast "Status reverted" for a write that never happened).
  if (String(status || '').toLowerCase() === 'hired'
      && _hrHireIsBareFieldWrite(HRState.candidates.find(x => x.id === id))) {
    const _e = new Error(HIRE_NEEDS_PROMOTE_MSG);
    _e.code = 'hr/hire-needs-promote';
    throw _e;
  }
  // TASK-1155 — REJECTION is server-owned: firestore.rules blocks the client
  // status→'rejected' transition, so the decision (incl. the Undo-back-into-
  // rejected path) goes through setCandidateDecision. The callable owns the
  // TASK-1152 fact-based reportWasNotBasis stamp + audit, the Adverse-Action
  // fence, and all activity/statusChangedAt stamps server-side.
  if (status === 'rejected') {
    const setDecision = httpsCallable(functions, 'setCandidateDecision');
    await setDecision({ tenantId: HRState.tenantId, candidateId: id, decision: 'rejected', rejectionReason: rejectionReason || '' });
    return;
  }
  // TASK-650 — a status change IS activity: stamp lastActivityAt + clear the
  // pre-expiry warning dedup so the inactivity clock restarts.
  // (Non-rejected writes keep clearing rejectionReason to '' — the rules fence
  // deliberately allows the clear so un-rejecting stays a direct write.)
  const updates = {
    status, rejectionReason: rejectionReason || '', updatedAt: Timestamp.now(),
    lastActivityAt: Timestamp.now(), expiryWarningSentAt: null,
    // TASK-1212 — e.g. { potentialsNote } when the stage is Potentials.
    ...(opts.extraFields || {}),
  };
  if (opts.stampChangedAt) updates.statusChangedAt = Timestamp.now();
  if (['phone_screen', 'interview', 'offer'].includes(status)) {
    updates.lastContactDate = Timestamp.now();
  }
  await updateDoc(doc(db, 'tenants', HRState.tenantId, 'candidates', id), updates);
}

// ── TASK-9141 — HIRING IS NEVER A PLAIN FIELD WRITE ─────────────────────────
//
// Marking a candidate 'hired' is not a pipeline stage move. It is the moment
// 49 CFR obligations attach, and the ONLY code that discharges them is the
// promote flow below (confirmApproveToRoster / reactivateExistingPerson):
// it creates or reactivates the PERSON, carries the §382.601(d) drug & alcohol
// policy RECEIPT and the §382 Clearinghouse CONSENT the applicant signed at
// apply time onto that person doc (_deriveApplicationDaClearinghouse, TASK-992),
// files the DQ documents, and writes the complianceTimeline row.
//
// Three paths used to write `status:'hired'` as a bare field update and skip all
// of it — the candidate edit form, the quick-status modal, and the partner
// portal's advance action (fixed server-side in _partnerHandlerAdvanceCandidate).
// Nothing errored, nothing logged, the card just read "Hired" while the driver's
// file was missing a DOT-required record. Live prod on 2026-09-10: 5 candidates
// hired that way, 2 missing the signed receipt and 5 missing the signed consent.
// All five HAD signed — the PNGs were sitting on the candidate doc the whole time.
//
// 🔴 THE FENCE IS ON THE CARRY-FORWARD, NOT ON THE HIRE. A recruiter who cannot
// mark someone hired at 4:55 on a Friday is a worse outcome than a missing
// receipt (TASK-428). So picking "Hired" is never REFUSED anywhere — it is
// REROUTED into the promote modal, which is the same click the user already
// meant. Nothing is lost if they cancel: the benign edits still save and the
// candidate keeps its previous stage.
//
// 🔴 DO NOT "FIX" THIS BY DROPPING 'hired' FROM PIPELINE_STAGES. Both selects
// render `selected` off `c.status`; removing the option would reset an
// already-hired candidate to the first option ("New") the next time anyone
// opened the form — a second, quieter data-loss bug.
//
// The rules twin is `hireStatusClientWriteBlocked()` in firestore.rules, which
// catches the fourth path these three do not: a raw SDK/console write.
const HIRE_NEEDS_PROMOTE_MSG = 'Hiring goes through Add to Roster — that step files the drug & alcohol policy receipt and Clearinghouse consent they signed on their application. Opening it now.';

// TRUE when writing status:'hired' for this candidate would be a bare field
// write that skips the carry-forward. Deliberately mirrors the rules fence:
//   • no candidate in hand (the "add a new candidate as Hired" form path) → true
//   • already went through promote (approvedToRoster AND a linked personId) →
//     false, so correcting a typo'd status, and the quick-status Undo back INTO
//     hired, both still work
//   • already sitting at 'hired' → false, so benign edits of one of the 5 legacy
//     damaged rows don't nag on every save (TASK-9142's backfill owns those)
//
// 🔴 BOTH halves of the promote shape, not just approvedToRoster (COD Code
// Reviewer). If this let `approvedToRoster:true` alone through while the rules
// fence still demanded a personId, the client would wave the write onward and
// the user would eat a raw Firestore permission error instead of the reroute —
// a client predicate that is LOOSER than its rules twin turns a guided handoff
// into an unexplained failure.
// personId by TYPE, matching the rules fence exactly — a numeric or null id is
// truthy-or-not in JS but is NOT a link as far as the rules are concerned, and a
// client predicate looser than its rules twin turns the reroute into a raw
// permission error.
function _hrHireIsBareFieldWrite(candidate) {
  if (!candidate) return true;
  const _linked = typeof candidate.personId === 'string' && candidate.personId.length > 0;
  if (candidate.approvedToRoster === true && _linked) return false;
  return String(candidate.status || '').toLowerCase() !== 'hired';
}

// =============================================================================
// TASK-9938 (bundle headroom) — the Approve to Roster modal, Reactivate, the hire write
// and the med-card upload link live in the LAZY views/hrApproveRoster.js (perf
// budget appColdLoadGzip). Bodies moved verbatim; each shim keeps the name
// window.HRModule / hr.js calls, loads the module on first use and, once loaded,
// calls straight through synchronously. Deps are handed over, never imported
// (pitfall #1).
// =============================================================================
let _hrAprMod = null;
let _hrAprLoad = null;
function _hrAprDeps() {
  return {
    CHECKR_PACKAGE_COVERS, HRState, Timestamp, _deriveHireCdlLicensed, _hrApproveToRosterGate,
    _hrApproveToRosterNote, _hrArchivedStageBlock, _hrCanApproveToRoster,
    _hrCheckrReportAwaitingAdjudication, _hrConfirmHireOverFlags, _hrFirstReviewHint, _hrRoleLabel,
    _promptAnnualReviewAfterMvr, capPersonName, closeModal, collection, db, doc, escapeHtml,
    functions, getDoc, getReportStatus, httpsCallable, loadBgCheck, renderCandidatesList,
    reportStatusMeta, resolveCandidateLicense, writeBatch
  };
}
function _loadHrApr() {
  if (_hrAprMod) return Promise.resolve(_hrAprMod);
  if (!_hrAprLoad) {
    _hrAprLoad = __ddChunkWrap(import('./hrApproveRoster.js?v=9142001')).then((m) => { m._bind(_hrAprDeps()); _hrAprMod = m; return m; }).catch((e) => {
      _hrAprLoad = null; // the next click tries again
      console.error('[HR] hrApproveRoster load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _hrLazyLoadFailed: true });
    });
  }
  return _hrAprLoad;
}
function openApproveToRosterModal(...a) { return _hrAprMod ? _hrAprMod.openApproveToRosterModal(...a) : _hrLazyCall(_loadHrApr, 'openApproveToRosterModal', a); }
function confirmApproveToRoster(...a) { return _hrAprMod ? _hrAprMod.confirmApproveToRoster(...a) : _hrLazyCall(_loadHrApr, 'confirmApproveToRoster', a); }
function syncApproveCdlMismatchWarning(...a) { return _hrAprMod ? _hrAprMod.syncApproveCdlMismatchWarning(...a) : _hrLazyCall(_loadHrApr, 'syncApproveCdlMismatchWarning', a); }
function sendMedUploadLink(...a) { return _hrAprMod ? _hrAprMod.sendMedUploadLink(...a) : _hrLazyCall(_loadHrApr, 'sendMedUploadLink', a); }

// ── Medical Exam Detail Modal (FMCSR 391.43) ──
// Captures the full medical examiner record — examiner identity, NRCME number,
// exam date, expiration, restrictions. Writes flat fields + medExamHistory[]
// + complianceTimeline event. Mirrors saveAnnualReview pattern.

const MED_RESTRICTION_OPTIONS = [
  'Wear corrective lenses',
  'Wear hearing aid',
  'Accompanied by waiver',
  'Driving within an exempt intracity zone',
  'Qualified by operation of CMV intrastate only',
  'Qualified by SPE certificate',
  'Wearing a multi-piece prosthesis',
  'Diabetes intracity only',
  'Stand-by waiver',
  'Lower limb prosthesis required',
];

// TASK-1403 (final COD) — who may SET/CLEAR the ND exemption. Mirrors the
// setIntrastateExemption callable EXACTLY (modules, not roles): tenants/{tid}.ownerId,
// or a member holding the hr OR compliance module who is not view/upload-only,
// read-only, or suspended/removed/revoked/inactive/pending. The shared demo tenant
// is refused by the server (no member doc to carry modules), so it's hidden there
// too. NOT hrEffectiveAdmin: a role-only admin without the modules is refused.
// Pure (explicit args) so the unit test executes it.
function _hrIntrastateGate({ tenantData, uid, memberAccess, isDemo, tenant } = {}) {
  if (isDemo === true || tenant === 'demo' || !tenant) return false;
  const td = tenantData || {};
  if (td.ownerId && uid && td.ownerId === uid) return true;
  const m = memberAccess;
  if (!m) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(m.status)) return false;
  if (m.readOnly === true || m.viewUploadOnly === true) return false;
  const mods = Array.isArray(m.modules) ? m.modules : [];
  return mods.includes('hr') || mods.includes('compliance');
}
const _IX_NO_ACCESS_MSG = 'Only the account owner, or someone with HR or Compliance access (not view & upload only), can change the ND exemption.';
function _hrCanSetIntrastateExemption() {
  const S = window.AppState || {};
  // Driver / public-link sessions are refused by the server too (same as
  // app.js hrCanManageComplianceBilling). Statuses fail closed a little wider than
  // the server (revoked/inactive/pending too) — the UI may hide more, never offer more.
  if (S.driverMode || S.isPublicAccess || S.driverModePin) return false;
  return _hrIntrastateGate({ tenantData: S.tenantData, uid: S.user?.uid, memberAccess: S.memberAccess, isDemo: S.isDemo, tenant: S.tenant });
}

function _readFileAsBase64(file) {
  // TASK-1383 — guarded shared reader (settles on every path: error, abort,
  // null result, sync throw, 30s timeout). Same strip-the-prefix contract.
  return window.FileReadUtil.readAsBase64(file);
}

// ── DQ File AI Audit ──
// Calls auditDqFile callable cloud function to get AI completeness analysis + remediation list.
async function runDqFileAudit(empId) {
  const emp = HRState.people.find(p => p.id === empId);
  if (!emp) return;

  window.showToast?.('Running AI audit on DQ file...', 'info');

  try {
    const auditFn = httpsCallable(functions, 'auditDqFile');
    const resp = await auditFn({ tenantId: HRState.tenantId, personId: empId });
    const data = resp?.data || {};

    if (!data.ok) {
      window.showToast?.(data.error || 'AI audit failed', 'error');
      return;
    }

    // TASK-934 — a classified Non-Driver has no DQ file to audit; the server
    // short-circuits without writing a score. Explain instead of "0% complete".
    if (data.notApplicable) {
      window.showToast?.('Non-Driver — DQ file not applicable (no DOT items to audit)', 'info');
      return;
    }

    Object.assign(emp, {
      dqFileScore: data.score,
      dqFileLastAudit: data.auditDate,
      dqFileMissingItems: Array.isArray(data.missingItems) ? data.missingItems : []
    });

    window.showToast?.(`DQ File audit complete — ${data.score}% complete`, 'success');

    if (HRState.selectedEmployee?.id === empId) {
      openEmployeeDetail(empId);
    }
  } catch (err) {
    console.error('[HR] DQ File audit error:', err);
    window.showToast?.(err.message || 'AI audit failed', 'error');
  }
}

// ── MVR Pull Modal (state-side driving record, separate from federal Clearinghouse) ──
// Records the actual MVR pull: date, state, result, violations, optional PDF.
// Pattern mirrors openClearinghouseModal/saveClearinghouseQuery for consistency.

const US_STATES_FOR_MVR = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA',
  'ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK',
  'OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC'
];

// TASK-993 — carrier-side one-click §391.25 annual-review recorder. Prompts the
// dispatcher to record the review determination right after an MVR pull (or at
// hire when an MVR is on file), writing the SAME annualReview* fields the manual
// modal writes (no new data model). Carrier is the §391.25 responsible party →
// NO disclaimer/ack (that's the partner-only safeguard). Guarded + never-throw.
async function _promptAnnualReviewAfterMvr(empId, reviewDate, mvrInfo) {
  try {
    if (!empId || !reviewDate || !window.showConfirm) return;
    const emp = HRState.people.find(p => p.id === empId);
    // Only prompt for drivers (annual review is a driver reg). If the person
    // isn't in local state yet (fresh hire), the CALLER has already gated it.
    if (emp) {
      const role = String(emp.crewType || emp.role || (emp.roles && emp.roles[0]) || '').toLowerCase();
      if (!/driver|cdl/.test(role)) return;
    }
    // COD (Code Reviewer + Domain) — a one-click "Qualified" must NEVER be
    // offered for an MVR that isn't clean. Only a clean, zero-violation record
    // gets the one-click Qualified path; anything with violations or a pending
    // result opens the FULL determination modal so a human picks the
    // determination (Qualified / Qualified w-monitoring / Not Qualified).
    const result = String(mvrInfo?.result || '').toLowerCase();
    const vCount = parseInt(mvrInfo?.violationCount, 10) || 0;
    const isClean = (result === 'clean' || result === 'clear') && vCount === 0;

    if (!isClean) {
      const openIt = await window.showConfirm(
        'Record §391.25 annual review?',
        "You reviewed this driver's MVR — it shows violations or is pending, so the §391.25 fitness determination needs your judgment. Open the annual-review form to record the determination?",
        { confirmText: 'Open review form', cancelText: 'Not now' });
      if (openIt) openAnnualReviewModal(empId);
      return;
    }

    const ok = await window.showConfirm(
      'Record §391.25 annual review?',
      "You reviewed this driver's clean MVR. Record the §391.25 annual driving-record review now — this marks the driver Qualified for the next 12 months. You can add notes or change the determination anytime from Annual Review.",
      { confirmText: 'Record — Qualified', cancelText: 'Not now' });
    if (!ok) return;
    await _quickRecordAnnualReview(empId, reviewDate, 'qualified');
  } catch (e) {
    console.warn('[HR] annual-review prompt failed (non-fatal):', e);
  }
}

// One-click write of a §391.25 annual review with the SAME fields/shape as
// saveAnnualReview. Idempotency guard: skips if an identical review (same date +
// determination) is already the most-recent history entry (double-click / re-prompt).
async function _quickRecordAnnualReview(empId, reviewDate, determination) {
  const tenant = HRState.tenantId;
  if (!tenant) return;
  const personRef = doc(db, 'tenants', tenant, 'people', empId);
  const personSnap = await getDoc(personRef);
  const existing = personSnap.exists() ? personSnap.data() : {};
  const reviewedBy = window.AppState?.user?.email || 'unknown';

  const prior = Array.isArray(existing.annualReviewHistory) ? existing.annualReviewHistory : [];
  const mostRecent = prior[0];
  if (mostRecent && mostRecent.date === reviewDate && mostRecent.determination === determination) {
    window.showToast?.('Annual review already recorded for that date', 'info');
    return;
  }

  const history = prior.slice(0, 23);
  const entry = {
    date: reviewDate,
    reviewedBy,
    determination,
    violations: '',
    correctiveAction: '',
    signature: reviewedBy,
    checklist: { mvr: true, psp: false, clearinghouse: false, cdl: false, medical: false },
    source: 'mvr_one_click',
    recordedBy: reviewedBy,
    recordedAt: new Date().toISOString()
  };
  history.unshift(entry);

  const nextDueDate = new Date(reviewDate);
  nextDueDate.setFullYear(nextDueDate.getFullYear() + 1);
  const nextDue = nextDueDate.toISOString().split('T')[0];

  const updateData = {
    annualReviewDate: reviewDate,
    annualReviewDetermination: determination,
    annualReviewedBy: reviewedBy,
    annualReviewNextDue: nextDue,
    annualReviewHistory: history
  };
  await updateDoc(personRef, updateData);

  const detLabels = { qualified: 'Qualified', qualified_monitoring: 'Qualified w/ Monitoring', not_qualified: 'Not Qualified' };
  await addDoc(collection(db, 'tenants', tenant, 'complianceTimeline'), {
    personId: empId,
    personName: existing.name || '',
    type: 'annual_review',
    description: `Annual record review — ${detLabels[determination] || determination} (recorded at MVR review)`,
    determination,
    reviewDate,
    reviewedBy,
    violations: null,
    correctiveAction: null,
    signature: reviewedBy,
    source: 'mvr_one_click',
    recordedBy: reviewedBy,
    timestamp: Timestamp.now(),
    createdAt: Timestamp.now()
  });

  const emp = HRState.people.find(p => p.id === empId);
  if (emp) Object.assign(emp, updateData);

  window.logAudit?.('hr', 'ANNUAL_REVIEW_COMPLETED', { personId: empId, determination, reviewDate, reviewedBy, source: 'mvr_one_click' });
  window.showToast?.('§391.25 annual review recorded', 'success');

  if (HRState.selectedEmployee?.id === empId) {
    openEmployeeDetail(empId);
  }
}

// ── Background Check (Checkr) Modal ──

// TASK-303 — slugs MUST match what's installed on the Checkr production
// account exactly. Stock packages from Checkr land prefixed with
// `checkrdirect_`; without that prefix the API returns 404 and the
// invite silently fails (functions/src/index.js:27178 createInvitation).
// Repro: Justin Estes 2026-05-18 — 'checkrdirect_basic_plus_criminal' 404'd, modal
// closed before TASK-302 fix surfaced the error toast. Confirmed installed
// slugs via stripe webhookEndpoints list against acct dispatchdeck-...
// TASK-1393 — NAMING RULE for every package label in this file and its mirrors.
// There are NINE label sources, not the four the spec listed — all must move
// together or the app speaks two vocabularies:
//   1. hr.js CHECKR_PACKAGES (this array — the carrier picker)
//   2. hr.js CHECKR_PACKAGE_LABELS (client canonical: BG card, chips, modals)
//   3. functions/src/index.js CHECKR_PACKAGE_LABELS ⚠️ drives the Stripe
//      invoice line-item description + the "check ordered" email
//   4. compliance-partner.js pkgDisplayName
//   5. compliance-partner.js BG_PACKAGE_CATALOG (keys = billable allowlist)
//   6. compliance-partner.js _AI_SLUG_LABELS (action-item text cleanup)
//   7. compliance-partner.js CHECKR_PARTNER_PACKAGES (partner Run-BG picker)
//   8. compliance-manager.html CHECKR_PACKAGES (legacy partner portal picker)
//   9. compliance-manager.html pkgDisplayName
// Chris-approved 2026-08-06; vendor tier jargon
// ("Basic Plus", "Essential", "Complete", "7×7") is banned from user-facing
// names — it describes Checkr's product ladder, not what a carrier gets.
//   • Standalone product  → `ACRONYM — Plain English`  (MVR — Driving Record,
//     PSP — Safety History). Acronym FIRST: DOT people scan for "MVR"/"PSP",
//     and the MVR card heading below (~L30945) already reads "MVR — Driving
//     Record", so this keeps one voice across the app.
//   • Combination         → `A + B` using those short names (Criminal + MVR,
//     MVR + PSP).
//   • Criminal tiers      → `Criminal — <what it adds>` (Basic / County /
//     Nationwide), i.e. the differentiator, not Checkr's ladder name.
//   • The everything bundle → `Complete Driver Screen`.
// 🔴 SLUGS ARE FROZEN (see the Justin Estes note above) — names only.
const CHECKR_PACKAGES = [
  {
    slug: 'checkrdirect_basic_plus_criminal',
    name: 'Criminal — Basic',
    price: '$29.99',
    description: 'National criminal database + sex offender registry. Faster, lower cost.',
    recommendedFor: 'Non-DOT roles, office staff',
  },
  {
    slug: 'checkrdirect_essential_criminal',
    name: 'Criminal — County',
    price: '$54.99',
    description: 'Adds county-level criminal history searches based on residence.',
    recommendedFor: 'Most CDL drivers',
  },
  {
    slug: 'checkrdirect_complete_criminal',
    name: 'Criminal — Nationwide',
    price: '$89.99',
    description: 'Federal + state + county + national + sex offender + SSN trace.',
    recommendedFor: 'DOT compliance / safety-sensitive roles',
    recommended: true,
  },
  {
    // TASK-380 — Commercial MVR + FMCSA PSP bundle (one invitation covers both).
    // NOT a criminal check — the description spells this out so a dispatcher
    // doesn't mistake it for a full new-hire screen (COD Skeptic catch).
    slug: 'commercial_mvr_psp',
    name: 'MVR + PSP', // TASK-1393 — was 'Commercial MVR + PSP'
    price: '$9.50', // TASK-826: repriced from $34.50 — live Checkr all-in is $9.50 (PSP bundled $0)
    description: 'Driving record (MVR) + FMCSA Pre-Employment Screening (PSP) crash & inspection history. Does NOT include a criminal check — pair with a criminal package above for a full screen.',
    recommendedFor: 'Driving record + PSP safety history, alongside a criminal check',
  },
  {
    // TASK-677 — real PROD Checkr package (slug 'standard_mvr', edited on the
    // live Checkr account 2026-06-18): National Criminal (Standard) + Global
    // Watchlist + Sex Offender + SSN Trace + Commercial MVR = $39.49. Slots
    // between Basic Plus (no MVR) and dd_dot_mvr_essential (adds county). NOT
    // MVR-only — the name + description spell out the criminal portion so a
    // dispatcher doesn't mistake it for a bare MVR (COD Skeptic trap that the
    // commercial_mvr_psp card already guards against). NO PSP, NO county criminal.
    slug: 'standard_mvr',
    name: 'Criminal + MVR', // TASK-1393 — was 'Criminal + Commercial MVR'
    price: '$39.49',
    description: 'National criminal + watchlist + sex offender + SSN trace, plus an MVR driving record. No PSP, no county-level criminal — pick Criminal — County/Nationwide for county searches, or MVR + PSP for PSP.',
    recommendedFor: 'Driver screen when you want criminal + driving record in one invite (no PSP)',
  },
  {
    // TASK-828 — Checkr Support built this PROD package 2026-06-23 (ticket
    // 01668248). Their email QUOTED it as Commercial MVR ($9.50) + CDLIS ($5.00)
    // = $14.50; TASK-1210's live /v1/packages probe found motor_vehicle_report
    // ONLY at $9.50, and TASK-1534 confirmed the row never delivers a CDLIS
    // artifact — so treat this as PURE MVR. NO criminal, NO PSP, NO CDLIS
    // report. This is the "basic MVR" the carrier picker was
    // missing: a clean cheap driving-record pull (e.g. the §391.25 annual MVR
    // on an EXISTING hired driver) WITHOUT being forced into the PSP bundle
    // (which is disabled once a hire date is set). MVR-intent preselects this.
    slug: 'dd_commercial_mvr',
    // TASK-1210 label-truth: live-probed $9.50, motor_vehicle_report ONLY — the
    // CDLIS claim is dropped until Checkr's written confirmation (case 01668275,
    // still open). TASK-1534: keep it dropped, and keep the reason NARROW —
    // *Checkr* not selling a standalone CDLIS does NOT generalize to the artifact
    // not existing. It does: Crown buys TMFS Corporation's, it carries its own
    // CDLIS ID + Requested/Completed dates, and it is a distinct §391.23 input.
    // TASK-1393 — renamed off "Commercial MVR". Despite the slug, this is NOT a
    // CDL-only product: TASK-1210's live /v1/packages probe found it contains
    // `motor_vehicle_report` ONLY, and TASK-1278 closed as no-build after Kegan
    // Boitnott's pull returned real data on a non-CDL Class D license. The word
    // "Commercial" was making dispatchers hunt for a second, basic MVR product
    // that deliberately doesn't exist — this ONE package serves both cases.
    name: 'MVR — Driving Record',
    price: '$9.50',
    description: 'The driving record on its own — no criminal, no PSP. Covers CDL and non-CDL drivers alike: the cheap, clean annual MVR pull (§391.25) for an existing driver.',
    recommendedFor: 'Any driving record on its own — annual MVR, insurance, non-CDL employees who drive',
  },
  {
    // TASK-828 — Checkr Support built this PROD package 2026-06-23 (ticket
    // 01668248): Essential 7×7 ($54.99) + Commercial MVR ($9.50) + FMCSA PSP =
    // $64.49 all-in. The complete one-invite DOT driver screen: full criminal
    // (SSN trace + SOR + watchlist + national + unlimited county 7yr) + MVR + PSP.
    slug: 'dd_full_dot_driver',
    name: 'Complete Driver Screen', // TASK-1393 — was 'Full DOT Driver (Criminal + MVR + PSP)'
    price: '$64.49',
    description: 'Everything in one invite: county-level criminal (7-year) + MVR driving record + FMCSA PSP safety history. The complete DOT new-hire driver screen.',
    recommendedFor: 'A complete DOT driver screen in a single invitation',
    recommended: true,
  },
];

// TASK-1308 — carrier-facing disclosure for the TASK-1307 access-fee cushion.
// MVR pulls incur variable Checkr passthrough fees (state DMV fees, and Checkr's
// own multi-state/CDLIS lookup surcharge — a COST INSIDE THE MVR PULL, not a
// CDLIS report the carrier receives; TASK-1534) that
// DD recovers via a cushion at billing. Keep the base-price headline (the GTM
// signal) but disclose the add-on so the invoice never surprises the carrier.
// Gate on the same COVERS map the server cushion uses (functions
// CHECKR_PACKAGE_COVERS) — MVR-bearing packages only.
// TASK-1394 — cushion is now STATE-AWARE server-side: per-state DMV fee
// ($2.88–$25.00, Checkr's published Jul-2026 table) + a $15 CDLIS/multi-state
// adder. This constant is the WORST CASE ($25 max state + $15 adder) and must
// track the server's CHECKR_STATE_MVR_FEE_MAX_CENTS + CHECKR_MVR_MULTISTATE_
// ADDER_CENTS (functions/src/index.js) — single source for every disclosure
// string below, do not retype the figure elsewhere.
const CHECKR_ACCESS_FEE_CUSHION_MAX_USD = 40;
// TASK-9853 — MIRROR of the server CHECKR_PACKAGE_SCREENINGS entries containing
// 'criminal' (packageHasCriminal). Parity asserted by tests/task9853-canada-zip.unit.cjs.
const _HR_CRIMINAL_SLUGS = new Set(['checkrdirect_basic_plus_criminal', 'checkrdirect_essential_criminal', 'checkrdirect_complete_criminal', 'driver_pro', 'standard_mvr', 'dot_driver_full', 'dot_driver_no_verify', 'dd_dot_mvr_essential', 'dd_full_dot_driver']);
function _pkgHasMvr(slug) {
  const covers = CHECKR_PACKAGE_COVERS[slug];
  return Array.isArray(covers) && covers.includes('mvr');
}
// TASK-1534 — this line used to name CDLIS. On an MVR price line that read as
// "this package includes a CDLIS report". It does not: the surcharge is Checkr's
// own multi-state lookup cost inside the MVR pull, not an artifact you receive.
const _CHECKR_ACCESS_FEE_NOTE = `+ state DMV / multi-state lookup fees (up to $${CHECKR_ACCESS_FEE_CUSHION_MAX_USD})`;

// TASK-307 — central slug→human-label map. Raw slugs like
// 'dd_dot_mvr_essential' leak into modals + emails + audit chips and
// mean nothing to a dispatcher. Single source of truth; any new package
// added to CHECKR_PACKAGES (or to the backend wholesale map) should also
// add a label here. Unmapped slugs fall back to a title-cased version
// of the slug (better than nothing). Lifted from inline lookups at
// _bgCardForCandidate (~L10327) and _buildBgDecisionPanel (~L10995).
// TASK-1393 — every value below follows the naming rule documented on
// CHECKR_PACKAGES above. The retired/label-only slugs (dot_driver_*,
// dd_dot_mvr_essential, mvr_commercial, psp, driver_pro) are renamed too, so a
// dispatcher reading an OLD pull doesn't hit different vocabulary than the live
// catalog. Keys are frozen — this is display copy only.
const CHECKR_PACKAGE_LABELS = {
  dot_driver_full: 'Complete Driver Screen + Employment History',
  dot_driver_no_verify: 'Complete Driver Screen (no Employment History)',
  dd_dot_mvr_essential: 'Criminal + MVR — County',
  checkrdirect_basic_plus_criminal: 'Criminal — Basic',
  checkrdirect_essential_criminal: 'Criminal — County',
  checkrdirect_complete_criminal: 'Criminal — Nationwide',
  mvr_commercial: 'MVR — Driving Record',
  standard_mvr: 'Criminal + MVR', // TASK-677 — Checkr edited the package 2026-06-18 to add National Criminal + Watchlist + SOR + SSN; no longer MVR-only
  psp: 'PSP — Safety History',
  driver_pro: 'Driver Pro',
  commercial_mvr_psp: 'MVR + PSP', // TASK-380
  dd_commercial_mvr: 'MVR — Driving Record', // TASK-828; TASK-1210 label-truth; TASK-1393 rename (CDL + non-CDL)
  dd_full_dot_driver: 'Complete Driver Screen', // TASK-828
};
// TASK-1102 — acronym-aware so an unmapped slug renders "DD MVR Central",
// not "Dd Mvr Central". Parity with compliance-partner `_humanizePackageSlug`
// + functions `_complianceCheckLabel`.
const _PKG_ACRONYMS = new Set(['dd', 'mvr', 'psp', 'cdl', 'dot', 'cdlis', 'ssn', 'dvir']);
function _prettyPackageSlug(slug) {
  if (!slug || typeof slug !== 'string') return '—';
  if (CHECKR_PACKAGE_LABELS[slug]) return CHECKR_PACKAGE_LABELS[slug];
  // Humanize the slug as a fallback so partners don't see snake_case.
  return slug.replace(/^checkrdirect_/, '').replace(/_/g, ' ').trim().split(/\s+/)
    .map(w => _PKG_ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : (w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

// =============================================================================
// TASK-9938 (bundle headroom) — the Checkr order modal, hire-date correction, the BG and
// MVR report viewers and the adjudication actions live in the LAZY
// views/hrBgCheck.js (perf budget appColdLoadGzip). Bodies moved verbatim; each
// shim below keeps the name window.HRModule / compliance.js / hr.js call, loads
// the module on first use and, once it is loaded, calls straight through
// (synchronously — so a print window still opens inside the click). Deps are
// handed over, never imported (pitfall #1).
// =============================================================================
let _hrBgMod = null;
let _hrBgLoad = null;
function _hrBgDeps() {
  return {
    CHECKR_PACKAGES, CHECKR_PACKAGE_COVERS, HRState, MVR_PURPOSE_DISPLAY, Timestamp,
    _CHECKR_ACCESS_FEE_NOTE, _HR_CRIMINAL_SLUGS, _buildBgDecisionPanel, _carrierCanReject,
    _hrEffectiveAdminSafe, _maybeInjectBgDecisionIntoEditCandidate, _mvrPurposePill, _pkgHasMvr,
    _prettyPackageSlug, buildComplianceTabHTML, collection, db, doc, ensureBgCheck, escapeHtml,
    formatDateShort, functions, getDoc, getDocs, getReportStatus, hrBgHandledByPartner,
    hrBgPaidByPartner, hrCanOpenMvrReport, hrCanOrderBackgroundCheck, hrGetsFullCheckrReport,
    hrPartnerLabel, httpsCallable, limit, loadBgCheck, loadEmployeeCitations, query,
    resolveCandidateLicense, where
  };
}
function _loadHrBg() {
  if (_hrBgMod) return Promise.resolve(_hrBgMod);
  if (!_hrBgLoad) {
    // TASK-10244 — the shared plain-language map rides along (never blocks the screen).
    _hrBgLoad = Promise.all([__ddChunkWrap(import('./hrBgCheck.js?v=9999986')), __ddChunkWrap(import('../bgCheckLabels.js?v=10244001')).catch(() => null)]).then(([m]) => { m._bind(_hrBgDeps()); _hrBgMod = m; return m; }).catch((e) => {
      _hrBgLoad = null; // the next click tries again
      console.error('[HR] hrBgCheck load failed:', e);
      if (window.showToast) window.showToast('Could not open that screen. Check your connection and try again.', 'error');
      throw Object.assign(e instanceof Error ? e : new Error(String(e)), { _hrLazyLoadFailed: true });
    });
  }
  return _hrBgLoad;
}
function adverseActionBgDecision(...a) { return _hrBgMod ? _hrBgMod.adverseActionBgDecision(...a) : _hrLazyCall(_loadHrBg, 'adverseActionBgDecision', a); }
function _promptForBgDecisionNotes(...a) { return _hrBgMod ? _hrBgMod._promptForBgDecisionNotes(...a) : _hrLazyCall(_loadHrBg, '_promptForBgDecisionNotes', a); }
function runHrTwoStageBgCheck(...a) { return _hrBgMod ? _hrBgMod.runHrTwoStageBgCheck(...a) : _hrLazyCall(_loadHrBg, 'runHrTwoStageBgCheck', a); }
function openHrScreeningPackagePicker(...a) { return _hrBgMod ? _hrBgMod.openHrScreeningPackagePicker(...a) : _hrLazyCall(_loadHrBg, 'openHrScreeningPackagePicker', a); }
function cancelHrBgRequest(...a) { return _hrBgMod ? _hrBgMod.cancelHrBgRequest(...a) : _hrLazyCall(_loadHrBg, 'cancelHrBgRequest', a); }
function cancelPersonBgRequest(...a) { return _hrBgMod ? _hrBgMod.cancelPersonBgRequest(...a) : _hrLazyCall(_loadHrBg, 'cancelPersonBgRequest', a); }
function openBackgroundCheckModal(...a) { return _hrBgMod ? _hrBgMod.openBackgroundCheckModal(...a) : _hrLazyCall(_loadHrBg, 'openBackgroundCheckModal', a); }
function runBackgroundCheck(...a) { return _hrBgMod ? _hrBgMod.runBackgroundCheck(...a) : _hrLazyCall(_loadHrBg, 'runBackgroundCheck', a); }
function openHireDateCorrection(...a) { return _hrBgMod ? _hrBgMod.openHireDateCorrection(...a) : _hrLazyCall(_loadHrBg, 'openHireDateCorrection', a); }
function submitHireDateCorrection(...a) { return _hrBgMod ? _hrBgMod.submitHireDateCorrection(...a) : _hrLazyCall(_loadHrBg, 'submitHireDateCorrection', a); }
function openBgReportViewer(...a) { return _hrBgMod ? _hrBgMod.openBgReportViewer(...a) : _hrLazyCall(_loadHrBg, 'openBgReportViewer', a); }
function openBgReportViewerCompliance(...a) { return _hrBgMod ? _hrBgMod.openBgReportViewerCompliance(...a) : _hrLazyCall(_loadHrBg, 'openBgReportViewerCompliance', a); }
function closeBgReportViewer(...a) { return _hrBgMod ? _hrBgMod.closeBgReportViewer(...a) : _hrLazyCall(_loadHrBg, 'closeBgReportViewer', a); }
function openMvrReportViewer(...a) { return _hrBgMod ? _hrBgMod.openMvrReportViewer(...a) : _hrLazyCall(_loadHrBg, 'openMvrReportViewer', a); }
function openMvrReportViewerCompliance(...a) { return _hrBgMod ? _hrBgMod.openMvrReportViewerCompliance(...a) : _hrLazyCall(_loadHrBg, 'openMvrReportViewerCompliance', a); }
function openMvrReportFromBgViewer(...a) { return _hrBgMod ? _hrBgMod.openMvrReportFromBgViewer(...a) : _hrLazyCall(_loadHrBg, 'openMvrReportFromBgViewer', a); }
function closeMvrReportViewer(...a) { return _hrBgMod ? _hrBgMod.closeMvrReportViewer(...a) : _hrLazyCall(_loadHrBg, 'closeMvrReportViewer', a); }
function printMvrReport(...a) { return _hrBgMod ? _hrBgMod.printMvrReport(...a) : _hrLazyCall(_loadHrBg, 'printMvrReport', a); }
function clearBgDecision(...a) { return _hrBgMod ? _hrBgMod.clearBgDecision(...a) : _hrLazyCall(_loadHrBg, 'clearBgDecision', a); }
function holdBgDecision(...a) { return _hrBgMod ? _hrBgMod.holdBgDecision(...a) : _hrLazyCall(_loadHrBg, 'holdBgDecision', a); }
function cancelAdverseAction(...a) { return _hrBgMod ? _hrBgMod.cancelAdverseAction(...a) : _hrLazyCall(_loadHrBg, 'cancelAdverseAction', a); }
function sendFinalAdverseAction(...a) { return _hrBgMod ? _hrBgMod.sendFinalAdverseAction(...a) : _hrLazyCall(_loadHrBg, 'sendFinalAdverseAction', a); }
function dotRemoteFinalAdverseAction(...a) { return _hrBgMod ? _hrBgMod.dotRemoteFinalAdverseAction(...a) : _hrLazyCall(_loadHrBg, 'dotRemoteFinalAdverseAction', a); }

// ═══════════════════════════════════════════════════════════════════════════
// TASK-969 — MVR as a FIRST-CLASS, separate, printable piece (Crown legacy
// parity: "treat the MVR like its own separate printable piece").
//
// The MVR no longer renders as a subsection buried in the BG snapshot — it has
// its own viewer modal (below) with its own clone-print path (printMvrReport,
// pattern of printDqPacket) and an order-time PURPOSE label (§391.23
// pre-employment / §391.25 annual / ad-hoc — persisted server-side at order
// time, never derived client-side; see functions _deriveMvrPurpose).
//
// 🔴 FCRA (COD Domain): the MVR is a consumer report under §391.23(e). This
// standalone surface is DISPLAY + PRINT ONLY — it renders NO adjudication
// buttons. A non-clear MVR shows a banner routing the reviewer to the FULL
// background-check report viewer, whose _buildBgDecisionPanel owns the ONE
// pre-adverse → wait → final-adverse sequence. No second decision path exists.
// ═══════════════════════════════════════════════════════════════════════════

// Purpose enum → display config. Enum values are server-authored
// (functions/src/index.js MVR_PURPOSE_VALUES); anything else renders nothing.
const MVR_PURPOSE_DISPLAY = {
  pre_employment: { short: 'Pre-employment', full: 'Pre-employment — 49 CFR §391.23', color: '#1D4ED8', bg: '#DBEAFE' },
  annual: { short: 'Annual review', full: 'Annual review — 49 CFR §391.25', color: '#047857', bg: '#D1FAE5' },
  ad_hoc: { short: 'Ad-hoc', full: 'Ad-hoc / for-cause pull', color: '#475569', bg: '#F1F5F9' },
};

function _mvrPurposePill(purpose, { full = false } = {}) {
  const cfg = MVR_PURPOSE_DISPLAY[purpose];
  if (!cfg) return '';
  return `<span title="${escapeHtml(cfg.full)}" style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:${cfg.bg};color:${cfg.color};border-radius:6px;font-size:0.72rem;font-weight:600;white-space:nowrap;">${escapeHtml(full ? cfg.full : cfg.short)}</span>`;
}

// ============================================================================
// BG Decision UI — Phase 1+2 (2026-05-12)
// Renders the Clear / Hold / Adverse Action button row inside the viewer +
// the click handlers. Skeptic verdict: Adverse Action delegates to Checkr's
// dashboard (no homegrown FCRA state machine). Hold mints a clarification
// token + SMS to the candidate. Clear records the decision + fires roster
// .compliance notification fanout via the existing checkrWebhook code path
// (decision flips don't re-fire notifications — they're for hiring-pipeline
// transition, not external comms).
// ============================================================================

// TASK-660 — Whether THIS carrier may issue a Reject / FCRA Adverse Action.
// Mirrors the server resolver (_resolveCarrierCanReject) and the roster-side
// _carrierCanTerminate UI mirror: a partner-managed carrier
// (tenantData.complianceManagerUid present) whose partner set
// compliance.carrierCanReject === false is BLOCKED from rejecting — the partner
// makes the reject call from its own portal. The carrier KEEPS Clear + Hold.
// Unmanaged carriers / default (absent) → allowed. UI mirror only; the server
// callables re-check via _assertCarrierCanReject.
function _carrierCanReject() {
  const td = window.AppState?.tenantData || {};
  const hasManager = !!(td.complianceManagerUid && String(td.complianceManagerUid).trim());
  if (!hasManager) return true;
  return td.compliance?.carrierCanReject === false ? false : true;
}

// TASK-667 — UI mirror of the server resolver _resolveCanCarrierEditTrainings.
// A partner-managed carrier (tenantData.complianceManagerUid present) may edit
// the Required Safety Training list ONLY when the partner has opted IN via
// compliance.allowClientEditTrainings === true. Unmanaged carriers always own
// their list. INVERSE default vs carrierCanTerminate/Reject: managed default =
// advisor owns the list (read-only for the carrier) unless explicitly shared.
// UI mirror only — updateSafetyTrainings re-checks server-side.
function _canCarrierEditTrainings() {
  const td = window.AppState?.tenantData || {};
  const hasManager = !!(td.complianceManagerUid && String(td.complianceManagerUid).trim());
  if (!hasManager) return true;
  return td.compliance?.allowClientEditTrainings === true;
}

// TASK-9911 M2 — HR → Safety → Required trainings is a door to the one
// "Skills & certifications" screen (roster.js, window.RosterModule). HR is the
// host here. HRState.people can be partial (Compliance seeds one subject), so
// the screen reads the crew itself (loadPeople) — COD r1.
function _safetyCertDoorCountText() {
  const n = _getSafetyTrainingsList().length;
  return n ? `(${n} active)` : '(none yet)';
}
function refreshSafetyCertDoor() {
  const el = document.getElementById('safety-cert-door-count');
  if (el) el.textContent = _safetyCertDoorCountText();
}
function openSkillsAndCerts() {
  const open = window.RosterModule?.showSkillsAndCerts;
  if (typeof open !== 'function') { window.showToast?.('Still loading — try again in a moment.', 'error'); return; }
  open({ focus: 'certs', loadPeople: true });
}

// TASK-243 — FCRA AA workflow state on a BG record. Returns an object the
// decision panel uses to swap UI between Pre-AA / In-Flight / Finalized states.
function _aaWorkflowState(empId) {
  const rec = HRState.bgRecordsByCandidateId?.[empId];
  const aa = rec?.adverseAction || null;
  if (!aa) return { phase: 'none' };
  if (aa.finalNoticeAt) return { phase: 'finalized', aa };
  if (aa.preNoticeAt) return { phase: 'pre-sent', aa };
  return { phase: 'none' };
}

function _aaCountdownDaysRemaining(deadlineTs) {
  if (!deadlineTs) return null;
  const d = deadlineTs?.toDate ? deadlineTs.toDate() : (deadlineTs?.seconds ? new Date(deadlineTs.seconds * 1000) : null);
  if (!d) return null;
  const ms = d.getTime() - Date.now();
  if (ms <= 0) return 0;
  // Convert to business-day estimate. Naive: ceil(ms / 24h) but skip weekends.
  // Counts whole calendar days remaining, then subtracts weekend days.
  const totalDays = Math.ceil(ms / 86400000);
  let business = 0;
  const cur = new Date();
  for (let i = 0; i < totalDays; i++) {
    cur.setDate(cur.getDate() + 1);
    const day = cur.getDay();
    if (day !== 0 && day !== 6) business++;
  }
  return business;
}

function _fmtAaDate(ts) {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : (ts?.seconds ? new Date(ts.seconds * 1000) : null);
  if (!d) return '—';
  return d.toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric' });
}

function _buildBgDecisionPanel(empId, reportId, decision, history) {
  // Shared with the list-card "last decision" pill — see _BG_DECISION_BADGES.
  const decisionBadges = _BG_DECISION_BADGES;
  const cfg = decision ? decisionBadges[decision] : null;
  const aaState = _aaWorkflowState(empId);

  // v1.15.478 — Always-visible "last decision" row.
  // History is appended via arrayUnion (chronological, oldest-first), so the
  // most recent entry is at the end. We surface it as a small pill BEFORE the
  // button row so a teammate re-opening the candidate sees AA-already-initiated
  // before mis-clicking Clear during an FCRA dispute window.
  let lastDecisionHtml = '';
  if (history.length > 0) {
    const last = history[history.length - 1];
    const lastAt = last.at?.toDate ? last.at.toDate() : (last.at?.seconds ? new Date(last.at.seconds * 1000) : null);
    const lastAtStr = lastAt
      ? lastAt.toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'short' })
      : '—';
    const lastCfg = decisionBadges[last.decision] || null;
    const lastLabel = (lastCfg?.label || last.decision || '(unknown)').replace(/ — .*$/, '');
    const lastActor = last.byEmail || last.by || 'unknown';
    const pillBg = lastCfg?.bg || '#f3f4f6';
    const pillColor = lastCfg?.color || 'var(--text-secondary)';
    lastDecisionHtml = `
      <div style="display:flex;align-items:center;gap:6px;padding:6px 10px;background:${pillBg};color:${pillColor};border-radius:6px;font-size:0.78rem;margin-bottom:10px;flex-wrap:wrap;">
        <span style="font-weight:600;">Last decision:</span>
        <span style="font-weight:600;">${escapeHtml(lastLabel)}</span>
        <span style="opacity:0.85;">by ${escapeHtml(lastActor)} on ${escapeHtml(lastAtStr)}</span>
      </div>`;
  }

  const historyHtml = history.length > 0 ? `
    <details style="margin-top:10px;font-size:0.78rem;color:var(--text-secondary);">
      <summary style="cursor:pointer;color:var(--text-secondary);font-weight:600;">Decision history (${history.length})</summary>
      <div style="margin-top:6px;border:1px solid var(--border-light);border-radius:6px;overflow:hidden;">
        ${history.slice().reverse().slice(0, 10).map(h => {
          const at = h.at?.toDate ? h.at.toDate() : (h.at?.seconds ? new Date(h.at.seconds * 1000) : null);
          const atStr = at ? at.toLocaleString() : '—';
          const decLabel = (decisionBadges[h.decision]?.label || h.decision || '(unknown)').replace(/ — .*$/, '');
          return `
            <div style="padding:6px 10px;border-bottom:1px solid var(--border-light);display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;">
              <span style="font-weight:600;color:var(--text-primary);">${escapeHtml(decLabel)}</span>
              <span style="color:var(--text-secondary);">${escapeHtml(h.byEmail || h.by || '—')} · ${escapeHtml(atStr)}</span>
              ${h.notes ? `<div style="width:100%;color:var(--text-secondary);font-size:0.75rem;">${escapeHtml(h.notes)}</div>` : ''}
            </div>`;
        }).join('')}
      </div>
    </details>` : '';

  // TASK-243 — render the FCRA AA panel based on workflow phase.
  let aaPanelHtml = '';
  if (aaState.phase === 'finalized') {
    const finalAt = _fmtAaDate(aaState.aa.finalNoticeAt);
    aaPanelHtml = `
      <div style="margin-top:14px;padding:12px 14px;background:#F1F5F9;border:1px solid #CBD5E1;border-radius:8px;">
        <div style="display:flex;align-items:center;gap:8px;font-weight:700;font-size:0.9rem;color:#475569;">
          <span class="material-icons" style="font-size:18px;">gavel</span>
          Adverse Action finalized on ${escapeHtml(finalAt)}
        </div>
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:6px;line-height:1.5;">
          Pre-AA notice sent ${escapeHtml(_fmtAaDate(aaState.aa.preNoticeAt))} — final AA notice sent ${escapeHtml(finalAt)}. FCRA workflow complete.
        </div>
      </div>`;
  } else if (aaState.phase === 'pre-sent') {
    const deadline = aaState.aa.disputeDeadline;
    const daysLeft = _aaCountdownDaysRemaining(deadline);
    const deadlineStr = _fmtAaDate(deadline);
    const canSendFinal = (daysLeft === 0);
    const finalBtnTip = canSendFinal
      ? 'The 5 business-day dispute window has elapsed — you may now send the final AA notice.'
      : `Send Final AA available after ${escapeHtml(deadlineStr)} (${daysLeft} business day${daysLeft === 1 ? '' : 's'} remaining).`;
    aaPanelHtml = `
      <div style="margin-top:14px;padding:12px 14px;background:#FFEDD5;border:1px solid #FB923C;border-radius:8px;">
        <div style="display:flex;align-items:center;gap:8px;font-weight:700;font-size:0.9rem;color:#9A3412;">
          <span class="material-icons" style="font-size:18px;">schedule</span>
          Adverse Action in progress — ${daysLeft === 0 ? 'dispute window elapsed' : `${daysLeft} business day${daysLeft === 1 ? '' : 's'} remaining`}
        </div>
        <div style="font-size:0.78rem;color:#7C2D12;margin-top:6px;line-height:1.5;">
          Pre-AA notice sent ${escapeHtml(_fmtAaDate(aaState.aa.preNoticeAt))} to candidate. Dispute window ends ${escapeHtml(deadlineStr)}.
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:10px;">
          <button onclick="window.HRModule.cancelAdverseAction('${escJsAttr(empId)}','${escJsAttr(reportId)}')" style="padding:7px 12px;font-size:0.82rem;background:#fff;color:#9A3412;border:1px solid #FB923C;border-radius:6px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;font-weight:600;">
            <span class="material-icons" style="font-size:15px;">cancel</span> Cancel AA
          </button>
          <button
            onclick="window.HRModule.sendFinalAdverseAction('${escJsAttr(empId)}','${escJsAttr(reportId)}',${canSendFinal ? 'false' : 'true'})"
            title="${finalBtnTip}"
            ${canSendFinal ? '' : 'style="opacity:0.55;cursor:not-allowed;"'}
            style="padding:7px 12px;font-size:0.82rem;background:${canSendFinal ? '#DC2626' : '#9CA3AF'};color:#fff;border:none;border-radius:6px;${canSendFinal ? 'cursor:pointer;' : 'cursor:not-allowed;'}display:inline-flex;align-items:center;gap:6px;font-weight:600;">
            <span class="material-icons" style="font-size:15px;">gavel</span> Send Final AA
          </button>
        </div>
      </div>`;
  } else if (!_carrierCanReject()) {
    // TASK-660 — the compliance partner has taken exclusive control of the
    // reject call for this carrier. Hide the Reject — Adverse Action button
    // (the server would refuse it anyway via _assertCarrierCanReject) and show
    // a note. Clear + Hold remain available below.
    aaPanelHtml = `
      <div style="margin-top:12px;padding:10px 14px;background:#F1F5F9;border:1px solid #CBD5E1;border-radius:8px;font-size:0.8rem;color:#475569;display:flex;align-items:flex-start;gap:8px;line-height:1.5;">
        <span class="material-icons" style="font-size:16px;color:#64748B;flex-shrink:0;">verified_user</span>
        <span>Your compliance partner handles adverse actions for your drivers.</span>
      </div>`;
  } else {
    aaPanelHtml = `
      <div style="margin-top:12px;display:flex;flex-direction:column;gap:8px;">
        <button onclick="window.HRModule.adverseActionBgDecision('${escJsAttr(empId)}','${escJsAttr(reportId)}')" style="padding:8px 14px;font-size:0.85rem;background:transparent;color:#DC2626;border:1px solid #DC2626;border-radius:6px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;font-weight:600;">
          <span class="material-icons" style="font-size:16px;">gavel</span> Reject — because of this report (Adverse Action)
        </button>${(HRState.candidates || []).some(c => c.id === empId) ? `
        <button onclick="window.HRModule.declineNotBasedOnReport('${escJsAttr(empId)}')" style="padding:8px 14px;font-size:0.85rem;background:transparent;color:#475569;border:1px solid #CBD5E1;border-radius:6px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;font-weight:600;">
          <span class="material-icons" style="font-size:16px;">person_off</span> Decline — for a reason unrelated to this report
        </button>
        <div style="font-size:0.72rem;color:#64748B;line-height:1.4;">
          Use <strong>Adverse Action</strong> only if the report is your reason — it sends the federally-required FCRA notices + 5-day dispute window. If you're passing for any other reason (took another job, no-show, better fit), use <strong>Decline</strong>; we'll note on the file that the report wasn't the basis.
        </div>
        <button onclick="window.HRModule.dotRemoteFinalAdverseAction('${escJsAttr(empId)}','${escJsAttr(reportId)}')" style="padding:10px 14px;min-height:44px;font-size:0.82rem;background:transparent;color:#7F1D1D;border:1px solid #FCA5A5;border-radius:6px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;font-weight:600;align-self:flex-start;" title="FCRA §604(b)(3)(B): DOT-regulated position, applicant applied only by mail / phone / online. Skips the pre-adverse notice — you confirm both facts first and the record says so.">
          <span class="material-icons" style="font-size:16px;">local_shipping</span> Decline on BG report (remote DOT applicant)
        </button>` : ''}
      </div>`;
  }

  // When AA is pre-sent or finalized, hide the Clear/Hold buttons — the
  // candidate is in the FCRA workflow and clicking Clear mid-flow would be
  // a compliance nightmare.
  const showClearHoldButtons = (aaState.phase === 'none');

  return `
    <div style="background:#fff;border:1px solid var(--border-light);border-radius:8px;padding:14px 16px;margin-bottom:16px;">
      <div style="font-weight:700;font-size:0.95rem;color:var(--text-primary);margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:18px;color:#9333EA;">how_to_reg</span> Hiring Decision
      </div>
      ${lastDecisionHtml}
      ${cfg ? `
        <div style="display:inline-flex;align-items:center;gap:6px;padding:4px 10px;background:${cfg.bg};color:${cfg.color};border-radius:6px;font-size:0.82rem;font-weight:600;margin-bottom:10px;">
          <span class="material-icons" style="font-size:16px;">${cfg.icon}</span> ${escapeHtml(cfg.label)}
        </div>` : `
        <div style="font-size:0.82rem;color:var(--text-secondary);margin-bottom:10px;">
          No decision recorded yet. Choose one:
        </div>`}
      ${showClearHoldButtons ? `
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          <button onclick="window.HRModule.clearBgDecision('${escJsAttr(empId)}','${escJsAttr(reportId)}')" style="padding:8px 14px;font-size:0.85rem;background:#059669;color:#fff;border:none;border-radius:6px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;font-weight:600;">
            <span class="material-icons" style="font-size:16px;">check_circle</span> Clear — Approve Hire
          </button>
          <button onclick="window.HRModule.holdBgDecision('${escJsAttr(empId)}','${escJsAttr(reportId)}')" style="padding:8px 14px;font-size:0.85rem;background:#D97706;color:#fff;border:none;border-radius:6px;cursor:pointer;display:inline-flex;align-items:center;gap:6px;font-weight:600;">
            <span class="material-icons" style="font-size:16px;">pause_circle</span> Hold — Ask Candidate
          </button>
        </div>` : ''}
      ${aaPanelHtml}
      ${showClearHoldButtons ? `
        <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:8px;line-height:1.5;">
          <strong>Clear</strong> approves the hire and stamps the BG record.
          <strong>Hold</strong> texts the candidate a one-time link to clarify a question you'll write.
          <strong>Reject — Adverse Action</strong> is a legally regulated rejection under the federal Fair Credit Reporting Act. DispatchDeck sends the pre-adverse-action notice to the candidate, starts the 5-business-day dispute window, and (after the wait) lets you send the final AA notice.
        </div>` : ''}
      ${historyHtml}
    </div>`;
}

// TASK-309 — _confirmAdverseActionHandoff() deleted 2026-05-18. This was
// the pre-TASK-243 hand-off modal that told carriers "DispatchDeck does
// not run that workflow itself — your compliance advisor does." Since
// 2026-05-14 v1.15.543, DD runs the entire FCRA AA workflow in-app via
// the 2-step wizard at L14186+ (sendPreAdverseActionNotice + 5-BD
// dispute window + sendFinalAdverseActionNotice callables). All
// callers of the legacy handoff were rewired to the wizard in that
// same release — this function had zero callsites. Deleted to remove
// a contradicting-the-product source of carrier confusion.


// Print/Save Snapshot — clones the BG report viewer's content into a clean
// print-only container appended directly to <body>, then calls window.print().
// We don't print the modal in place because position:fixed + viewport-unit
// max-heights (dvh) render blank in Chromium print preview (first attempt
// shipped that way and produced a blank PDF — see ledger 2026-05-19).
function printBgSnapshot(empId, btn) {
  // TASK-431 — loading feedback: disable + spinner the trigger button while we
  // build the print clone and the browser's print dialog is opening. Restored
  // on afterprint (or the safety timeout). btn is optional (callable directly).
  btn = btn || document.getElementById('btn-print-bg-snapshot');
  let restoreBtn = () => {};
  if (btn) {
    const originalHtml = btn.innerHTML;
    btn.disabled = true;
    btn.style.opacity = '0.7';
    btn.innerHTML = '<span class="material-icons" style="font-size:16px;animation:spin 0.8s linear infinite;">progress_activity</span> Preparing…';
    let restored = false;
    restoreBtn = () => {
      if (restored) return;
      restored = true;
      btn.disabled = false;
      btn.style.opacity = '';
      btn.innerHTML = originalHtml;
    };
  }

  const src = document.getElementById('bg-report-viewer-body');
  if (!src) { restoreBtn(); return; }
  document.getElementById('bg-snapshot-print-container')?.remove();

  const container = document.createElement('div');
  container.id = 'bg-snapshot-print-container';
  // Clone so the originals (incl. event handlers and onclick attrs) aren't
  // disturbed when the user closes the modal afterwards.
  const clone = src.cloneNode(true);
  clone.id = 'bg-snapshot-print-clone';
  clone.style.padding = '24px';
  // Strip interactive chrome from the clone so the printout is presentational.
  clone.querySelectorAll('.no-print, button').forEach(el => el.remove());
  // The interim-snapshot header was set to display:none in screen view via
  // inline style; promote it for print.
  const header = clone.querySelector('.bg-snapshot-print-header');
  if (header) header.style.display = 'block';
  // TASK-969 — promote print-only notes (e.g. the MVR pointer card's "filed as
  // a separate document" line) the same way.
  clone.querySelectorAll('[data-print-only]').forEach(el => { el.style.display = 'block'; });
  container.appendChild(clone);
  document.body.appendChild(container);

  const cleanup = () => {
    container.remove();
    restoreBtn();
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  // Safari fires afterprint inconsistently — belt-and-suspenders timeout.
  setTimeout(cleanup, 60000);
  // Restore the button shortly after the (blocking) print dialog opens so it
  // isn't stuck on "Preparing…" if afterprint never fires.
  setTimeout(restoreBtn, 1500);
  window.print();
}

// ============================================================================
// TASK-334 Slice 1 — Driver Qualification (DQ) File Export
//
// Insurance-ready packet for underwriters. ONE deliverable, TWO clearly-labeled
// sections that NEVER co-mingle data from different sources:
//   - Section A: DispatchDeck-collected, self-reported by the driver (license,
//     residence, employment history, etc.). Banner labels it explicitly.
//   - Section B: Checkr verified report (third-party / FCRA-governed). Linked
//     from the existing BG section above — NOT embedded inline to preserve
//     FCRA scope separation. Underwriter combines the two PDFs themselves.
//
// Origin: 2026-05-20. Cassia @ MMA rejected an interim Checkr snapshot for
// Malachi King because it lacked driver identity/med-card/employment data
// that William Carter's full Checkr PDF had. First instinct ("enrich the
// snapshot") hit FCRA + Checkr ToS exposure — COD Architect ❌ + Skeptic 🔴
// hard-blocked co-mingle. This builds the separate-segmented packet instead.
// Do NOT modify the Checkr snapshot renderer at openBgReportViewer (line 14219).
// ============================================================================

function _dqEsc(s) { return escapeHtml(String(s ?? '')); }
function _dqFmtDate(d) { return formatDateShort(d) || '—'; }

// TASK-334 Slice 1 follow-up (2026-05-26) — DQ-only augmentation helpers.
// These render Section A fields that the SHARED candidate builders don't
// surface (because those builders are used in other contexts where the extra
// FMCSR weight isn't appropriate). Keeps shared builders scope-pure while
// satisfying Cassia @ MMA's missing-fields list. Each helper returns '' when
// the underlying data isn't present so we don't leak empty cards into the
// packet (Edge Case Hunter ask #1 / "Not collected" inline pattern).

// Hazmat endorsement validity — separate from license expiry per FMCSR.
// Reads hazmatTrainingExpiry (49 CFR 172.704 — recurrent training every 3 yr)
// and/or hazmatCertExpiry (TSA HME validity). Renders only if the driver has
// an H/X endorsement OR carries either expiry on file.
function _dqBuildHazmatRow(e) {
  const endorsements = Array.isArray(e?.license?.endorsements) ? e.license.endorsements : [];
  const hasHazmatEndorsement = endorsements.some(s => /^[HX]$/i.test(String(s).trim()));
  const trainingExpiry = e.hazmatTrainingExpiry || '';
  const certExpiry = e.hazmatCertExpiry || '';
  if (!hasHazmatEndorsement && !trainingExpiry && !certExpiry) return '';
  const trainingCell = trainingExpiry
    ? `<div class="hr-info-item"><div class="label">Hazmat Training Expires <span style="font-size:0.7rem;color:var(--text-secondary);">(49 CFR 172.704)</span></div><div class="value">${_dqEsc(_dqFmtDate(trainingExpiry))}</div></div>`
    : `<div class="hr-info-item"><div class="label">Hazmat Training Expires <span style="font-size:0.7rem;color:var(--text-secondary);">(49 CFR 172.704)</span></div><div class="value" style="color:var(--text-secondary);font-style:italic;">Not collected</div></div>`;
  const certCell = certExpiry
    ? `<div class="hr-info-item"><div class="label">TSA HME Expires</div><div class="value">${_dqEsc(_dqFmtDate(certExpiry))}</div></div>`
    : `<div class="hr-info-item"><div class="label">TSA HME Expires</div><div class="value" style="color:var(--text-secondary);font-style:italic;">Not collected</div></div>`;
  return `
    <div style="margin-bottom:var(--spacing-lg);">
      <div class="label" style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:16px;color:#D97706;">science</span> Hazmat Endorsement (H/X)
      </div>
      <div class="hr-info-grid" style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-md);background:#fff;border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-md);">
        ${trainingCell}
        ${certCell}
      </div>
    </div>`;
}

// Med examiner credentials — name + NRCME registry # + last-exam date.
// FMCSR 391.43/45 requires the examiner be on the National Registry; insurers
// want the name + NRCME on the DQ file alongside the bare expiry date.
function _dqBuildMedExaminerRow(e) {
  e = window.DqMatrix?.medDetail ? window.DqMatrix.medDetail(e, HRState.tenantId) : e; // TASK-1496-S6
  const name = e.medExaminerName || '';
  const nrcme = e.medExaminerNRCME || '';
  const examDate = e.medExamLastDate || '';
  if (!name && !nrcme && !examDate) return '';
  const nameCell = name
    ? `<div class="hr-info-item"><div class="label">Examiner Name</div><div class="value">${_dqEsc(name)}</div></div>`
    : `<div class="hr-info-item"><div class="label">Examiner Name</div><div class="value" style="color:var(--text-secondary);font-style:italic;">Not collected</div></div>`;
  const nrcmeCell = nrcme
    ? `<div class="hr-info-item"><div class="label">NRCME Registry #</div><div class="value" style="font-variant-numeric:tabular-nums;">${_dqEsc(nrcme)}</div></div>`
    : `<div class="hr-info-item"><div class="label">NRCME Registry #</div><div class="value" style="color:var(--text-secondary);font-style:italic;">Not collected</div></div>`;
  const examCell = examDate
    ? `<div class="hr-info-item"><div class="label">Exam Date</div><div class="value">${_dqEsc(_dqFmtDate(examDate))}</div></div>`
    : `<div class="hr-info-item"><div class="label">Exam Date</div><div class="value" style="color:var(--text-secondary);font-style:italic;">Not collected</div></div>`;
  return `
    <div style="margin-bottom:var(--spacing-lg);">
      <div class="label" style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:16px;color:#059669;">medical_services</span> DOT Medical Examiner (FMCSR 391.43)
      </div>
      <div class="hr-info-grid" style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:var(--spacing-md);background:#fff;border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-md);">
        ${nameCell}
        ${nrcmeCell}
        ${examCell}
      </div>
    </div>`;
}

// FMCSA D&A Clearinghouse pre-employment query result + date + consent.
// Insurers want to see this called out separately from the rest of credentials
// because it gates the whole hire under 49 CFR 382.
function _dqBuildClearinghouseRow(e) {
  const status = e.clearinghouseStatus || '';
  const lastQuery = e.clearinghouseLastQueryDate || '';
  const consent = e.clearinghouseConsentDate || '';
  if (!status && !lastQuery && !consent) return '';
  const STATUS_LABELS = {
    not_started: 'Not started',
    consent_on_file: 'Consent on file — query pending',
    queried_no_actions: 'Query complete — no prohibitions',
    queried_prohibited: 'Query complete — prohibited (RTD required)',
    rtd_complete: 'Return-to-Duty complete',
  };
  const statusLabel = STATUS_LABELS[status] || (status ? status : 'Not collected');
  const statusColor = status === 'queried_no_actions' ? '#065F46'
    : status === 'queried_prohibited' ? '#991B1B'
    : status === 'rtd_complete' ? '#1E40AF'
    : 'var(--text-secondary)';
  return `
    <div style="margin-bottom:var(--spacing-lg);">
      <div class="label" style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:16px;color:#5B21B6;">vaccines</span> Drug & Alcohol Clearinghouse (49 CFR 382)
      </div>
      <div class="hr-info-grid" style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-md);background:#fff;border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-md);">
        <div class="hr-info-item"><div class="label">Pre-Employment Query</div><div class="value" style="font-weight:600;color:${statusColor};">${_dqEsc(statusLabel)}</div></div>
        <div class="hr-info-item"><div class="label">Last Query Date</div><div class="value">${lastQuery ? _dqEsc(_dqFmtDate(lastQuery)) : '<span style="color:var(--text-secondary);font-style:italic;">Not collected</span>'}</div></div>
        <div class="hr-info-item" style="grid-column:1/-1;"><div class="label">Driver Consent on File</div><div class="value">${consent ? _dqEsc(_dqFmtDate(consent)) : '<span style="color:var(--text-secondary);font-style:italic;">Not collected</span>'}</div></div>
      </div>
    </div>`;
}

// SAP Return-to-Duty / follow-up testing. Only renders when there's evidence
// of a D&A violation in history (per spec: "gated by violation presence"). We
// detect violation presence via clearinghouseStatus === 'queried_prohibited'
// or 'rtd_complete', or via an explicit `sapStatus`/`sapReturnToDutyDate`.
// Suppressing this block when no violation is on file keeps the DQ packet from
// suggesting violations that don't exist.
function _dqBuildSapRow(e) {
  const chStatus = e.clearinghouseStatus || '';
  const sapStatus = e.sapStatus || '';
  const sapRtdDate = e.sapReturnToDutyDate || '';
  const sapFollowupDate = e.sapFollowupNextDate || '';
  const violationPresent = chStatus === 'queried_prohibited'
    || chStatus === 'rtd_complete'
    || !!sapStatus
    || !!sapRtdDate;
  if (!violationPresent) return '';
  return `
    <div style="margin-bottom:var(--spacing-lg);">
      <div class="label" style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:16px;color:#B45309;">restart_alt</span> SAP Return-to-Duty / Follow-up (49 CFR 40 Subpart O)
      </div>
      <div class="hr-info-grid" style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-md);background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-md);padding:var(--spacing-md);">
        <div class="hr-info-item"><div class="label">SAP Status</div><div class="value">${sapStatus ? _dqEsc(sapStatus) : '<span style="color:var(--text-secondary);font-style:italic;">Not collected</span>'}</div></div>
        <div class="hr-info-item"><div class="label">Return-to-Duty Date</div><div class="value">${sapRtdDate ? _dqEsc(_dqFmtDate(sapRtdDate)) : '<span style="color:var(--text-secondary);font-style:italic;">Not collected</span>'}</div></div>
        <div class="hr-info-item" style="grid-column:1/-1;"><div class="label">Next Follow-up Test</div><div class="value">${sapFollowupDate ? _dqEsc(_dqFmtDate(sapFollowupDate)) : '<span style="color:var(--text-secondary);font-style:italic;">Not collected</span>'}</div></div>
      </div>
    </div>`;
}

// Prior-employer FMCSR 391.23 investigation table on the insurance-facing DQ
// packet.
//
// 🔴 TASK-4751 — this used to `return ''` whenever no employer carried a
// recorded response, which on the DQ-packet path was ALWAYS: measured
// 2026-09-08, 0 of 472 person docs carry `employerVerifications` (they live on
// the candidate doc), and 9 people carry employment history at all. So a driver
// with three un-investigated in-window employers printed a packet in which the
// §391.23 section did not exist — indistinguishable from a driver whose
// investigation was complete. An underwriter cannot see an omission.
//
// The table now lists every employer inside the §391.23 three-year window
// whether or not anything was recorded, and says "Not investigated — no record
// on file" out loud. Out-of-window employers are listed separately as §391.21
// record so nobody reads their absence from the required set as a gap.
function _dqBuildPriorEmployerDARow(e) {
  const { inWindow, outOfWindow } = _peiPartitionEmployers(e);
  // 🔴 We could not reach the candidate doc that holds `employerVerifications`
  // (no candidateId, or the read failed). Say THAT. Printing "Not investigated"
  // off a record we never loaded would assert a compliance gap we have no
  // evidence for — the same class of lie as printing nothing.
  if (e && e._peiRecordUnavailable) {
    if (inWindow.length === 0 && outOfWindow.length === 0) return '';
    return `
    <div style="margin-bottom:var(--spacing-lg);">
      <div class="label" style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:16px;color:#B45309;">forward_to_inbox</span> Prior-Employer Safety Performance History (FMCSR 391.23 &mdash; previous 3 years)
      </div>
      <div style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-md);padding:12px 14px;font-size:0.82rem;color:#7C2D12;line-height:1.5;">
        <strong style="color:#92400E;">Investigation record not available on this packet.</strong>
        ${inWindow.length} of the ${inWindow.length + outOfWindow.length} listed employer${inWindow.length + outOfWindow.length === 1 ? '' : 's'} fall${inWindow.length === 1 ? 's' : ''} inside the §391.23 three-year window, but this driver's employment application could not be linked, so the per-employer investigation status could not be read. Open the driver's §391.23 Investigation panel to confirm before relying on this packet.
      </div>
    </div>`;
  }
  // TASK-477 — source from the canonical employerVerifications map (carrier-
  // recorded §391.23 responses) and fall back to any legacy on-entry data.
  const vmap = (e && e.employerVerifications) || {};
  const rowOf = (emp) => {
    const name = _empName(emp) || '(no name)';
    const v = vmap[_empVerificationKey(name, emp.dateFrom)];
    let resp = emp.priorEmployerDAResponse || '';
    let date = emp.priorEmployerDAResponseDate || '';
    if (v) {
      const k = _peiStatusKey(v);
      if (k === 'not_applicable') resp = 'Marked not applicable' + (v.naReason ? ` — ${v.naReason}` : '');
      else if (k === 'contacted') resp = 'Contacted — awaiting response';
      else if (k === 'no_response') resp = 'No response — good-faith attempt documented';
      else {
        const da = v.daViolationReported || 'unknown';
        resp = da === 'yes' ? 'Violation reported' : da === 'no' ? 'No violation reported' : 'Unknown';
      }
      try { const t = v.verifiedAt?.toDate ? v.verifiedAt.toDate() : (v.verifiedAt?._seconds ? new Date(v.verifiedAt._seconds * 1000) : (v.verifiedAt instanceof Date ? v.verifiedAt : null)); if (t) date = t.toISOString().slice(0, 10); } catch (_) {}
    }
    const has = !!(v || emp.priorEmployerDAResponse || emp.priorEmployerDAResponseDate);
    return { name, resp: has ? (resp || 'Unknown') : '', date, has };
  };
  const reqRows = inWindow.map(rowOf);
  const recRows = outOfWindow.map(rowOf).filter(r => r.has);
  if (reqRows.length === 0 && recRows.length === 0) {
    // Nothing listed inside the window and nothing recorded outside it. Still
    // say something — §391.23(c)(4) makes "no investigation was possible" an
    // affirmative filing, not a blank.
    if (!Array.isArray(e.employers) || e.employers.filter(x => x && _empName(x)).length === 0) return '';
    return `
    <div style="margin-bottom:var(--spacing-lg);">
      <div class="label" style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:16px;color:#B45309;">forward_to_inbox</span> Prior-Employer Safety Performance History (FMCSR 391.23)
      </div>
      <div style="background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-md);padding:12px 14px;font-size:0.82rem;color:#7C2D12;line-height:1.5;">
        <strong style="color:#92400E;">No employer on this application falls inside the §391.23 three-year window.</strong>
        Every listed employer ended more than 3 years before the application date, so no prior-employer inquiry is required — but <strong>§391.23(c)(4)</strong> requires documentation that no investigation was possible in the driver investigation history file.
      </div>
    </div>`;
  }
  const cell = (r) => `
      <div style="display:grid;grid-template-columns:2fr 1.5fr 1fr;gap:8px;padding:8px 12px;border-bottom:1px solid var(--border-light);align-items:center;font-size:0.85rem;">
        <div style="font-weight:600;color:var(--text-primary);">${_dqEsc(r.name)}</div>
        <div style="color:${r.has ? 'var(--text-secondary)' : '#991B1B'};${r.has ? '' : 'font-weight:600;'}">${r.has ? _dqEsc(r.resp) : 'Not investigated — no §391.23 record on file'}</div>
        <div style="color:var(--text-secondary);text-align:right;font-variant-numeric:tabular-nums;">${_dqEsc(r.date ? _dqFmtDate(r.date) : '—')}</div>
      </div>`;
  const head = (a, b, c) => `
        <div style="display:grid;grid-template-columns:2fr 1.5fr 1fr;gap:8px;padding:6px 12px;background:var(--bg-page);font-size:0.7rem;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-secondary);font-weight:600;">
          <div>${a}</div><div>${b}</div><div style="text-align:right;">${c}</div>
        </div>`;
  const missing = reqRows.filter(r => !r.has).length;
  const banner = missing ? `
      <div style="background:#FEF2F2;border:1px solid #FECACA;border-radius:var(--radius-md);padding:8px 12px;margin-bottom:8px;font-size:0.8rem;color:#7F1D1D;line-height:1.45;">
        <strong style="color:#991B1B;">${missing} of ${reqRows.length}</strong> required §391.23 investigation${reqRows.length === 1 ? '' : 's'} ${missing === 1 ? 'has' : 'have'} no record on file. §391.23(c)(1)–(2) requires a written record per employer contacted &mdash; or documented good-faith attempts &mdash; within 30 days of the date employment begins.
      </div>` : '';
  const recordBlock = recRows.length ? `
      <div style="margin-top:10px;font-size:0.7rem;text-transform:uppercase;letter-spacing:0.05em;color:var(--text-secondary);font-weight:600;">Recorded beyond the 3-year window &mdash; §391.21 record, no inquiry required</div>
      <div style="background:#fff;border:1px solid var(--border-light);border-radius:var(--radius-md);overflow:hidden;margin-top:6px;">
        ${head('Employer', 'Response', 'Received')}${recRows.map(cell).join('')}
      </div>` : '';
  return `
    <div style="margin-bottom:var(--spacing-lg);">
      <div class="label" style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:16px;color:#2563EB;">forward_to_inbox</span> Prior-Employer Safety Performance History (FMCSR 391.23 &mdash; previous 3 years)
      </div>
      ${banner}
      ${reqRows.length ? `<div style="background:#fff;border:1px solid var(--border-light);border-radius:var(--radius-md);overflow:hidden;">
        ${head('Employer', 'Investigation status', 'Recorded')}${reqRows.map(cell).join('')}
      </div>` : ''}
      ${recordBlock}
    </div>`;
}

// FMCSR 391.21 10-year history gap warning. Conservative per COD Skeptic 🟡
// 2026-05-26 — we ONLY flag the simple case: total elapsed time covered by
// the earliest employment record is < 10 years (the spec says "if app
// collected <10 years"). No per-gap analysis (that's Checkr's job). Bolded
// red with warning icon per UX Lead ✅. Only renders for CDL drivers
// (license.class present and not 'D'/'O') AND when at least one employer is
// on file. Quiet on non-CDL or empty employment lists.
function _dqBuildEmploymentHistoryGapWarning(e) {
  const employers = Array.isArray(e.employers) ? e.employers.filter(x => x && x.dateFrom) : [];
  if (employers.length === 0) return '';
  const licClass = String(e?.license?.class || '').toUpperCase();
  // CDL = A, B, or C. Treat unknown as CDL (defensive — DOT regulated assumed
  // unless proven otherwise) so the warning shows when in doubt.
  const isCdl = !licClass || /^[ABC]$/.test(licClass);
  if (!isCdl) return '';
  let earliest = null;
  for (const emp of employers) {
    const dt = new Date(String(emp.dateFrom) + (String(emp.dateFrom).length === 10 ? 'T00:00:00' : ''));
    if (!isNaN(dt.getTime()) && (earliest === null || dt < earliest)) earliest = dt;
  }
  if (!earliest) return '';
  const yearsCovered = (Date.now() - earliest.getTime()) / (365.25 * 24 * 3600 * 1000);
  if (yearsCovered >= 10) return '';
  const yrsStr = yearsCovered.toFixed(1);
  return `
    <div style="margin:-8px 0 var(--spacing-lg) 0;padding:10px 14px;background:#FEF2F2;border:2px solid #FECACA;border-radius:8px;display:flex;align-items:flex-start;gap:10px;">
      <span class="material-icons" style="color:#991B1B;font-size:20px;flex-shrink:0;margin-top:1px;">warning</span>
      <div style="font-size:0.85rem;color:#7F1D1D;line-height:1.5;">
        <strong style="color:#991B1B;">FMCSR 391.21 — employment history coverage gap.</strong>
        Application only captures <strong>${_dqEsc(yrsStr)} years</strong> of prior employment. FMCSR 391.21(b)(10)–(b)(11) requires <strong>10 years</strong> of employment history for CDL drivers (3 years under (b)(10) plus the preceding 7 years of CMV employment under (b)(11)). Collect additional prior-employer records before submitting to insurance.
      </div>
    </div>`;
}

function getDqPacketHTML(emp) {
  const e = emp || {};
  const printed = new Date().toLocaleString();
  const fullName = e.name || [e.firstName, e.lastName].filter(Boolean).join(' ') || 'Unnamed driver';

  // Section A — banner styling: green so it visually segregates from Checkr's purple.
  const sectionABanner = `
    <div style="background:#ECFDF5;border:2px solid #059669;border-radius:8px;padding:14px 18px;margin-bottom:16px;">
      <div style="font-size:0.78rem;text-transform:uppercase;letter-spacing:0.08em;color:#065F46;font-weight:700;">Section A — DispatchDeck-Collected</div>
      <div style="font-size:0.95rem;font-weight:700;color:#064E3B;margin-top:4px;">Driver Qualification File (self-reported)</div>
      <div style="font-size:0.78rem;color:#065F46;margin-top:6px;line-height:1.4;">
        Data below was collected via the DispatchDeck employment application and self-reported by the candidate.
        It is <strong>not third-party verified.</strong> See Section B (Checkr report) for verified background information.
      </div>
    </div>`;

  // Identity panel (inline — no shared builder exists today).
  const dobLine = e.dob ? `<div class="hr-info-item"><div class="label">DOB</div><div class="value">${_dqEsc(_dqFmtDate(e.dob))}</div></div>` : '';
  const phoneLine = e.phone ? `<div class="hr-info-item"><div class="label">Phone</div><div class="value">${_dqEsc(formatPhoneDisplay ? formatPhoneDisplay(e.phone) : e.phone)}</div></div>` : '';
  const emailLine = e.email ? `<div class="hr-info-item"><div class="label">Email</div><div class="value">${_dqEsc(e.email)}</div></div>` : '';
  const hireDateLine = e.hireDate ? `<div class="hr-info-item"><div class="label">Hire Date</div><div class="value">${_dqEsc(_dqFmtDate(e.hireDate))}</div></div>` : '';
  const identityHtml = `
    <div style="margin-bottom:var(--spacing-lg);">
      <div class="label" style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;margin-bottom:8px;display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:16px;color:#2563EB;">person</span> Identity
      </div>
      <div class="hr-info-grid" style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-md);background:#fff;border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-md);">
        <div class="hr-info-item"><div class="label">Full Name</div><div class="value" style="font-weight:600;">${_dqEsc(fullName)}</div></div>
        ${dobLine}${phoneLine}${emailLine}${hireDateLine}
      </div>
    </div>`;

  // Section A bottom — reuse existing builders. Each returns '' if no data,
  // so missing fields don't produce empty cards (Edge Case Hunter ask #1).
  const licenseHtml = buildCandidateLicenseSection(e);
  const credsHtml = buildCandidateCredentialsSection(e);
  // TASK-334 follow-up (2026-05-26) — DQ-only FMCSR/insurer augmentations.
  // Inserted between credentials and driving experience so med/D&A data sits
  // alongside the rest of the medical-credentials block. Each helper returns
  // '' when data is absent so missing-data sections don't emit empty cards.
  const hazmatHtml = _dqBuildHazmatRow(e);
  const medExaminerHtml = _dqBuildMedExaminerRow(e);
  const clearinghouseHtml = _dqBuildClearinghouseRow(e);
  const sapHtml = _dqBuildSapRow(e);
  const drivingHtml = buildCandidateDrivingExperienceSection(e);
  // FMCSR 391.21 10-yr employment coverage flag — rendered AS A WARNING ABOVE
  // the employment table so the underwriter spots it before scanning rows.
  const empGapWarning = _dqBuildEmploymentHistoryGapWarning(e);
  const employmentHtml = buildCandidateEmploymentHistorySection(e);
  // Prior-employer 391.23 D&A response — rendered AFTER the employment table
  // because it's a per-employer follow-up question (auditor cross-references).
  const priorDaHtml = _dqBuildPriorEmployerDARow(e);
  const residenceHtml = buildCandidateResidenceSection(e);

  // Section B — pointer banner only. The Checkr report stays at the existing
  // BG section above; underwriter downloads the canonical Checkr PDF when
  // ready. Embedding the snapshot inline would re-introduce the co-mingle
  // problem this packet exists to prevent.
  const hasBgReport = !!(e.bgCheckReportId || e._bgLatestReportId);
  const sectionBBanner = `
    <div style="background:#FAF5FF;border:2px solid #9333EA;border-radius:8px;padding:14px 18px;margin:24px 0 16px;page-break-before:always;">
      <div style="font-size:0.78rem;text-transform:uppercase;letter-spacing:0.08em;color:#581C87;font-weight:700;">Section B — Third-Party Verified</div>
      <div style="font-size:0.95rem;font-weight:700;color:#3B0764;margin-top:4px;">Checkr Background Check Report</div>
      <div style="font-size:0.78rem;color:#581C87;margin-top:6px;line-height:1.4;">
        ${hasBgReport
          ? 'A Checkr consumer report is on file for this driver. The Checkr PDF is the canonical FCRA-governed deliverable — download or view from the Background Check section in DispatchDeck and submit it alongside this Section A packet.'
          : 'No Checkr background check on file. Open the Background Check section to run one.'}
      </div>
    </div>`;

  return `
    <div class="dq-packet-body" data-emp-id="${_dqEsc(e.id || '')}">
      <div data-print-only class="dq-packet-print-header" style="display:none;padding:0 0 12px 0;margin-bottom:16px;border-bottom:1px solid #cbd5e1;">
        <div style="font-size:1.1rem;font-weight:700;color:#0F172A;">Driver Qualification File · ${_dqEsc(fullName)}</div>
        <div style="font-size:0.78rem;color:#64748b;margin-top:4px;">Generated by DispatchDeck on ${_dqEsc(printed)}</div>
      </div>
      ${sectionABanner}
      ${identityHtml}
      ${licenseHtml}
      ${credsHtml}
      ${hazmatHtml}
      ${medExaminerHtml}
      ${clearinghouseHtml}
      ${sapHtml}
      ${drivingHtml}
      ${empGapWarning}
      ${employmentHtml}
      ${priorDaHtml}
      ${residenceHtml}
      ${sectionBBanner}
    </div>`;
}

async function openDqPacketViewer(empId) {
  if (!empId || !HRState.tenantId) return;
  // Find the employee in state. The full list lives in HR_EMPLOYEES /
  // window._hrEmployees depending on session — match the existing pattern.
  let emp = (window._hrEmployees || HR_EMPLOYEES || []).find(p => p.id === empId);
  if (!emp) {
    // Fallback: read directly.
    try {
      const snap = await getDoc(doc(db, 'tenants', HRState.tenantId, 'people', empId));
      if (snap.exists()) emp = { id: snap.id, ...snap.data() };
    } catch (_) {}
  }
  if (!emp) { window.showToast?.('Could not load driver', 'error'); return; }

  // Pull current BG state so Section B can correctly say report-on-file.
  // TASK-9235 — resolves person → linked applicant record (hire-time reference).
  try {
    const _pbg = await loadPersonBgCheck(emp);
    if (_pbg && Object.keys(_pbg).length) emp._bgLatestReportId = _pbg.latestReportId || null;
  } catch (_) {}

  // 🔴 TASK-4751 — the §391.23 investigation record lives on the CANDIDATE doc,
  // never on the person: measured 2026-09-08, 0 of 472 live person docs carry
  // `employerVerifications`. Rendering the packet off the person alone would
  // print "Not investigated" for a driver whose investigation IS complete —
  // the exact inverse of the silence this task set out to remove, and worse,
  // because it accuses the carrier of a gap that does not exist. Nor does a
  // person doc carry an application date, so the 3-year window would anchor on
  // `createdAt`/`hireDate` instead of the day the applicant signed.
  //
  // Merge BOTH onto a shallow copy — never mutate the roster object — and set
  // `_peiRecordUnavailable` when we could not resolve them, so the packet says
  // "record not loaded" instead of asserting a negative it cannot support.
  emp = { ...emp };
  if (emp.candidateId) {
    try {
      const cand = await ensureCandidateInHrState(emp.candidateId);
      if (cand) {
        emp.employerVerifications = cand.employerVerifications || {};
        if (cand.appliedDate) emp.appliedDate = cand.appliedDate;
        if (cand.submittedAt) emp.submittedAt = cand.submittedAt;
        // 🔴 Take the employer LIST from the same snapshot as the verifications.
        // `_empVerificationKey` is derived from name+dateFrom, so pairing the
        // person's hire-time COPY of employers[] against the candidate's CURRENT
        // verification map means any post-hire correction (TASK-4550 lets HR fix
        // a misspelled employer name in place) shifts the key on one side only
        // and the packet prints "Not investigated" for an employer that WAS
        // investigated. The candidate doc is the signed §391.21 record; the
        // person copy is a derivative. Read both halves from the canonical one.
        if (Array.isArray(cand.employers) && cand.employers.length) emp.employers = cand.employers;
        else if (!Array.isArray(emp.employers)) emp.employers = [];
        // TASK-1496-S5 — gaps too: new hires carry no copy on the person.
        if (cand.employmentGaps?.length) emp.employmentGaps = cand.employmentGaps;
      } else {
        emp._peiRecordUnavailable = true;
      }
    } catch (_) { emp._peiRecordUnavailable = true; }
    // TASK-1496-S5 — DENIED != ABSENT: application unreadable or missing, no copy → say so.
    if (emp._peiRecordUnavailable && !emp.employers?.length) window.showToast?.('Employment history could not be loaded', 'warning');
  } else if (Array.isArray(emp.employers) && emp.employers.length) {
    // Employment history on file with no candidate link — the investigation
    // record is unreachable from here, so do not claim it is missing.
    emp._peiRecordUnavailable = true;
  }

  document.getElementById('modal-dq-packet')?.remove();
  const modal = document.createElement('div');
  modal.className = 'modal modal-fullscreen dq-packet-modal';
  modal.id = 'modal-dq-packet';
  modal.innerHTML = `
    <div class="modal-content dq-packet-print" style="max-width:760px;display:flex;flex-direction:column;max-height:92vh;max-height:92dvh;">
      <div class="modal-header" style="background:linear-gradient(135deg,#059669,#10B981);color:#fff;flex-shrink:0;">
        <h3 style="color:#fff;display:flex;align-items:center;gap:8px;margin:0;">
          <span class="material-icons">folder_special</span> Driver Qualification Packet
        </h3>
        <button class="btn-icon" onclick="window.HRModule.closeDqPacketViewer()" style="color:#fff;" data-no-print>
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:20px;overflow-y:auto;flex:1;min-height:0;">
        ${getDqPacketHTML(emp)}
      </div>
      <div class="modal-footer" style="padding:12px 20px;border-top:1px solid var(--border-light);display:flex;justify-content:space-between;gap:8px;flex-wrap:wrap;" data-no-print>
        <div style="font-size:0.75rem;color:var(--text-secondary);align-self:center;">
          For insurance underwriters: print this Section A, attach the Checkr PDF separately.
        </div>
        <div style="display:flex;gap:8px;">
          <button class="btn btn-secondary" onclick="window.HRModule.closeDqPacketViewer()">Close</button>
          <button class="btn btn-primary" onclick="window.HRModule.printDqPacket('${escJsAttr(empId)}')" style="background:#059669;">
            <span class="material-icons">print</span> Print / Save PDF
          </button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(modal);
}

function closeDqPacketViewer() {
  document.getElementById('modal-dq-packet')?.remove();
}

function printDqPacket(empId) {
  const src = document.querySelector('.dq-packet-print');
  if (!src) return;
  document.getElementById('dq-packet-print-container')?.remove();

  const container = document.createElement('div');
  container.id = 'dq-packet-print-container';
  const clone = src.cloneNode(true);
  clone.id = 'dq-packet-print-clone';
  clone.classList.remove('modal-content');
  clone.querySelectorAll('[data-no-print], .no-print').forEach(el => el.remove());
  clone.querySelectorAll('button').forEach(el => el.remove());
  const header = clone.querySelector('[data-print-only]');
  if (header) header.style.display = 'block';
  // TASK-1366 — the DQ packet's employment entries already render `open` (it
  // calls buildCandidateEmploymentHistorySection WITHOUT pei, and only the
  // pei/candidate-modal path starts collapsed), so this is defensive: if that
  // call ever gains pei:true, the §391.51 packet must still print in full.
  clone.querySelectorAll('details.cand-emp').forEach(d => { d.open = true; });
  container.appendChild(clone);
  document.body.appendChild(container);

  const cleanup = () => {
    container.remove();
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  setTimeout(cleanup, 60000);
  window.print();
}

// Candidate Application print — same clone-to-body pattern as printBgSnapshot.
// The candidate modal's modal-content has position:fixed + max-height:dvh, both
// of which Chromium-printed produces blank PDFs (see ledger 2026-05-19).
async function printCandidateApplication(candidateId, opts) {
  const src = document.querySelector('.candidate-application-print');
  if (!src) return;
  // TASK-9444 slice 3b — opt-in reviewer page. The plain Print stays exactly the
  // §391.21 application (it is what goes in the DQ file and what an applicant may
  // be given), so the Things to Verify page only prints when asked for.
  const _reviewerHtml = (opts && opts.reviewer === true)
    ? _hrAuditReviewerPrintHtml((HRState.candidates || []).find(x => x && x.id === candidateId))
    : '';
  document.getElementById('candidate-application-print-container')?.remove();

  // TASK-1488 — 49 CFR §391.21(b)(2) requires the employment application to
  // CONTAIN the applicant's SSN, so the PRINTED/PDF application carries the
  // FULL SSN (printing is a deliberate act by an authorized user — do NOT mask
  // the print output). The full value is fetched from the candidatesTax vault
  // at print time — only readable by admin / hr-directory (an unauthorized
  // printer gets the masked screen value) — and the fetch writes an
  // SSN_REVEALED audit row (source 'carrier_print'). Legacy applications with
  // no vault doc print the last-4 mask (the full SSN was never stored).
  let _printSsnFull = null;
  if (candidateId && _hrCanSeeSecureTax()) {
    try {
      _printSsnFull = await _fetchCandidateSsnFull(candidateId, 'carrier_print');
    } catch (e) {
      console.warn('[HR] print SSN fetch failed — printing masked:', e?.message || e);
    }
  }

  const container = document.createElement('div');
  container.id = 'candidate-application-print-container';
  const clone = src.cloneNode(true);
  clone.id = 'candidate-application-print-clone';
  // Drop modal positioning classes — we want a static block flow that pages.
  clone.classList.remove('modal-content', 'modal-large');
  // Strip no-print chrome (close button, footer with Print/Edit buttons).
  clone.querySelectorAll('[data-no-print], .no-print').forEach(el => el.remove());
  // Strip remaining interactive buttons (BG action surfaces, etc.) — but keep
  // anything tagged data-print-keep (e.g. the status pill chip).
  clone.querySelectorAll('button:not([data-print-keep])').forEach(el => el.remove());
  // Inert kept buttons (status pill) — they shouldn't be clickable on paper.
  clone.querySelectorAll('button[data-print-keep]').forEach(el => {
    el.removeAttribute('onclick');
    el.style.cursor = 'default';
  });
  // Promote the print-only timestamp header from display:none → block.
  const header = clone.querySelector('[data-print-only]');
  if (header) header.style.display = 'block';
  // TASK-1366 — employment entries collapse to a summary row on screen. This
  // modal IS the §391.21 printed application, so every entry must be expanded
  // on paper regardless of what the reviewer had open. (The @media print rule
  // in styles.css is the CSS backstop; `open` is the deterministic one.)
  clone.querySelectorAll('details.cand-emp').forEach(d => { d.open = true; });
  // TASK-1488 — swap the full SSN into the PRINT CLONE only (the on-screen
  // modal stays masked unless the viewer explicitly revealed it). textContent,
  // never innerHTML — the value can't become a markup sink.
  if (_printSsnFull) {
    const ssnSpan = clone.querySelector('[data-ssn-candidate]');
    if (ssnSpan) ssnSpan.textContent = _printSsnFull;
  }
  // TASK-9430 (Crown 7/22) — no browser URL/date/title/page-number lines on the
  // printed application. Browsers draw those in the PAGE MARGIN, so this print uses
  // a 0 page margin and rebuilds the white space inside the container: side padding,
  // plus a table whose empty thead/tfoot rows repeat on EVERY printed page. The
  // style tag lives inside the container, which cleanup() removes, so the global
  // styles.css @page margin is back for every other print.
  const pageStyle = document.createElement('style');
  pageStyle.textContent = '@media print { @page { margin: 0; } html, body { margin: 0 !important; padding: 0 !important; }'
    + ' body > #candidate-application-print-container { padding: 0 0.5in !important; } }'
    + ' #candidate-application-print-container table.cand-print-page { width: 100%; border-collapse: collapse; }'
    + ' #candidate-application-print-container table.cand-print-page > thead > tr > td,'
    + ' #candidate-application-print-container table.cand-print-page > tfoot > tr > td { height: 0.5in; padding: 0; }'
    + ' #candidate-application-print-container table.cand-print-page > tbody > tr > td { padding: 0; vertical-align: top; }';
  const pageTable = document.createElement('table');
  pageTable.className = 'cand-print-page';
  pageTable.setAttribute('role', 'presentation');
  pageTable.innerHTML = '<thead><tr><td></td></tr></thead><tfoot><tr><td></td></tr></tfoot><tbody><tr><td></td></tr></tbody>';
  pageTable.querySelector('tbody td').appendChild(clone);
  container.appendChild(pageStyle);
  container.appendChild(pageTable);
  // COD (Independent): a page break inside the margin table's cell is ignored, so the
  // reviewer page goes AFTER the table, as its own block that starts a new sheet.
  if (_reviewerHtml) container.insertAdjacentHTML('beforeend', _reviewerHtml);
  document.body.appendChild(container);

  const cleanup = () => {
    container.remove();
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  // Safari fires afterprint inconsistently — belt-and-suspenders timeout.
  setTimeout(cleanup, 60000);
  window.print();
}

async function downloadBgReportPdf(empId, reportId, btnEl, tenantIdOverride) {
  // TASK-1284 — hard re-entrancy guard (covers keyboard re-activation too, which
  // pointer-events:none doesn't block): if this button is already mid-fetch, bail
  // before opening a second tab / firing a second callable.
  if (btnEl && btnEl.getAttribute && btnEl.getAttribute('aria-busy') === 'true') return;
  // Synchronous in-flight feedback + disable. No-op when no element is passed.
  const restore = _hrBusyDocLink(btnEl);
  // Pre-open a placeholder tab IN THE CLICK GESTURE. getCheckrReportPdf takes a few
  // seconds and returns inline base64 (the runtime SA can't sign a URL), so the old
  // window.open() ran AFTER the await → mobile Safari popup-blocked it silently and
  // the PDF never opened (Chris's symptom). Opening the tab now, then navigating it
  // post-await, keeps the gesture. (iOS blob-in-tab is still imperfect — the durable
  // fix is a server signed URL via an IAM tokenCreator grant; tracked as the follow-up.)
  let win = null;
  try { win = window.open('', '_blank'); } catch (_) { win = null; }
  if (win) {
    try { win.opener = null; } catch (_) {} // sever the reverse-tabnabbing handle before navigating
    try { win.document.write('<p style="font:14px sans-serif;padding:20px;color:#334155">Loading MVR…</p>'); } catch (_) {}
  }
  let revokeUrl = null;
  try {
    const fn = httpsCallable(functions, 'getCheckrReportPdf');
    const result = await fn({
      tenantId: (typeof tenantIdOverride === 'string' && tenantIdOverride) || HRState.tenantId, // TASK-9295 — compliance.js host passes its tenant
      candidateId: empId,
      reportId: reportId,
    });
    const data = result.data || {};
    if (!data.ok) throw new Error(data.error || 'PDF fetch failed');
    // Resolve ONE url to open: a real signedUrl if the server ever returns one
    // (works today only if the SA is granted tokenCreator), else the inline
    // base64 → blob URL (the current real path). No dead branch either way.
    let url = data.signedUrl || null;
    if (!url && data.inline && data.base64) {
      const bin = atob(data.base64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const blob = new Blob([arr], { type: 'application/pdf' });
      url = URL.createObjectURL(blob);
      revokeUrl = url;
    }
    if (!url) throw new Error('No PDF data returned');
    if (win) { win.location = url; }
    else { window.open(url, '_blank', 'noopener'); } // placeholder blocked before we got here — best-effort
    window.showToast?.('Opening PDF…', 'success');
    if (revokeUrl) setTimeout(() => URL.revokeObjectURL(revokeUrl), 60000);
  } catch (e) {
    if (win) { try { win.close(); } catch (_) {} } // don't strand a blank tab on failure
    if (revokeUrl) { try { URL.revokeObjectURL(revokeUrl); } catch (_) {} }
    console.error('[HR] downloadBgReportPdf failed:', e);
    // TASK-306 — friendly copy for the "report still running" race window.
    // Even with the button gated on completedAt, a tiny race exists between
    // parent.status='complete' arriving and Checkr's PDF document_ids[]
    // being populated. Replace the raw Firebase 404 with something a
    // dispatcher can act on instead of escalating.
    const raw = (e?.message || '').toLowerCase();
    let userMsg = e.message || 'PDF fetch failed';
    if (raw.includes('no document_ids') || raw.includes('not generated yet') || raw.includes('pdf not generated')) {
      userMsg = 'PDF not generated yet — Checkr is still finalizing the report. Try again in a few minutes.';
    } else if (raw.includes('404') || e?.code === 'not-found') {
      userMsg = 'PDF unavailable — Checkr may still be processing. Try again shortly or contact support if this persists.';
    }
    window.showToast?.(userMsg, 'error');
  } finally {
    restore(); // TASK-1284 — always un-spin/re-enable the button
  }
}

// TASK-9295 — "View MVR report" on the MVR row (Chris 2026-09-30: the carrier's
// HR / compliance people and the managing partner open the ACTUAL MVR).
// Client mirror of the getCheckrReportPdf gate (_checkrReportPdfAccess) — ACCESS =
// MODULES: the owner by tenants/{tid}.ownerId, an active, not-read-only member
// holding `hr` or `compliance`, or the managing compliance partner (?client=
// session). The stored-document leg (getComplianceDocBytes) admits the same set.
// The UI may hide more than the server, never offer more; the server re-checks.
// leg 'doc' (uploaded document → getComplianceDocBytes): that gate
// (_assertHrOrComplianceModuleMember) reads the members doc only, so an owner with
// NO members doc is not offered the doc leg (COD 2026-09-30) — the Checkr leg admits
// them by ownerId.
function hrCanOpenMvrReport(leg) {
  const st = window.AppState || {};
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  if (_hrPartnerClientTenantId()) return true;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (leg !== 'doc' && uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true) return false;
  const mods = Array.isArray(ma.modules) ? ma.modules : [];
  return mods.includes('hr') || mods.includes('compliance');
}

// TASK-9295 — does this session get the WHOLE Checkr report? Mirror of the server's
// CHECKR_PDF_FULL_REPORT_LANES (dispatcher decision 2026-09-30): the owner, the
// managing partner (?client= session) and an hr-module member who is NOT view & upload
// only. Every other session that hrCanOpenMvrReport admits (compliance module, view &
// upload only) gets MVR-only packages.
function hrGetsFullCheckrReport() {
  const st = window.AppState || {};
  if (!hrCanOpenMvrReport()) return false;
  if (_hrPartnerClientTenantId()) return true;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess || {};
  const mods = Array.isArray(ma.modules) ? ma.modules : [];
  return mods.includes('hr') && ma.viewUploadOnly !== true;
}

// TASK-9295 — the HOST side of PersonSections.mvrViewButtonHtml for the two
// carrier surfaces (hr.js + compliance.js). Returns the inline onclick JS for the
// MVR on file, or null (no button) when this session may not open it or nothing
// is on file. Source pick is the shared PersonSections.mvrReportSource:
//   'checkr' → downloadBgReportPdf → getCheckrReportPdf (audited, 5-min URL);
//   'doc'    → window.viewComplianceDoc → getComplianceDocBytes (audited).
// Every id goes through escJsAttr (a JS string inside an attribute — escapeHtml
// gives that position no protection).
// `bg` (optional) is the host's loaded peopleBackgroundChecks record. For a session
// that gets MVR-only packages, a Checkr report KNOWN to be a wider package is drawn
// disabled with the reason (hrMvrReportButton); unknown (bg not loaded) is left to
// the server, which refuses with the same message.
function hrMvrReportOnClick(person, tenantIdOverride, bg) {
  const b = hrMvrReportButton(person, tenantIdOverride, bg);
  return b && b.onClick ? b.onClick : null;
}
// → { onClick } | { disabledReason } | null — spread into renderDqScorecard opts as
// mvrViewOnClick / mvrViewDisabledReason.
function hrMvrReportButton(person, tenantIdOverride, bg) {
  if (!person || !person.id) return null;
  person = _hrMvrDq(person); // TASK-1496-S7
  if (!hrCanOpenMvrReport()) return null;
  const PS = window.PersonSections;
  if (!PS || typeof PS.mvrReportSource !== 'function') return null;
  const docPath = (typeof person.mvrLatestPdfPath === 'string' && person.mvrLatestPdfPath) ? person.mvrLatestPdfPath : '';
  const rid = (typeof person.mvrCheckrReportId === 'string' && person.mvrCheckrReportId) ? person.mvrCheckrReportId : '';
  let src = PS.mvrReportSource({ checkrReportId: rid, hasDoc: !!docPath, lastSource: person.mvrLastSource });
  // A session the doc gate would refuse (owner with no members doc) falls back to the
  // Checkr report when there is one, else gets no button.
  if (src === 'doc' && !hrCanOpenMvrReport('doc')) src = rid ? 'checkr' : null;
  const J = (v) => window.escJsAttr(v);
  if (src === 'checkr' && !hrGetsFullCheckrReport()
      && PS.reportPackageKnown && PS.reportPackageKnown(bg, rid) && !PS.reportIsMvrOnly(bg, rid)) {
    // Known wider package for an MVR-only lane: the uploaded document if there is one,
    // else a disabled button that says why.
    if (docPath && hrCanOpenMvrReport('doc')) src = 'doc';
    else return { disabledReason: PS.MVR_ONLY_REFUSAL || 'This report includes more than the MVR — ask an HR admin.' };
  }
  if (src === 'checkr') {
    const tid = (typeof tenantIdOverride === 'string' && tenantIdOverride) ? `,'${J(tenantIdOverride)}'` : '';
    return { onClick: `window.HRModule.downloadBgReportPdf('${J(person.id)}','${J(rid)}',this${tid})` };
  }
  if (src === 'doc') return { onClick: `window.viewComplianceDoc('${J(docPath)}','','mvr',this)` };
  return null;
}

// TASK-996 — open a stored compliance document (ISNetworld / safety cert, MVR,
// license, med card, inspector-qual) via the HR-gated getComplianceDocBytes
// byte-stream callable. The upload paths that used getSignedUrl persisted DEAD
// links (this SA can't signBlob → SignatureDoesNotMatch); this callable reads
// the object with the Admin SDK and returns base64, which we open as a Blob URL.
// Falls back to a legacy persisted URL (old client getDownloadURL entries with
// no path) if the callable can't resolve. Exposed on window so compliance.js /
// roster.js can call it without a cross-module ?v= import (pitfall #1).
// TASK-1070 — local point-of-action feedback for the View-document links: spin the
// clicked anchor's icon (or, if it has none, just disable the link) + block re-clicks
// for the duration. Returns a restore fn; safe no-op when no element is passed. UI-only.
function _hrBusyDocLink(anchorEl) {
  if (!anchorEl || !anchorEl.style) return () => {};
  const icon = anchorEl.querySelector && anchorEl.querySelector('.material-icons');
  let origText, hadSpin;
  if (icon) {
    origText = icon.textContent;
    hadSpin = icon.classList.contains('spin');
    icon.textContent = 'hourglass_empty';
    icon.classList.add('spin');
  }
  const origPe = anchorEl.style.pointerEvents;
  anchorEl.style.pointerEvents = 'none';
  anchorEl.setAttribute('aria-busy', 'true');
  let restored = false;
  return () => {
    if (restored) return;
    restored = true;
    if (icon) {
      icon.textContent = origText;
      if (!hadSpin) icon.classList.remove('spin');
    }
    anchorEl.style.pointerEvents = origPe;
    anchorEl.removeAttribute('aria-busy');
  };
}

// TASK-9297 — the managed carrier a compliance partner is sitting inside
// ("Partner access · View only", app.js ?client=), or null for every other session.
// getComplianceDocBytes binds tenantId from the AUTH CLAIM, and a partner's claim
// is their OWN sandbox tenant, never the carrier's — so every carrier-side View
// link 403'd "Document path is outside your tenant scope" (Kolsen 9/16; reproduced
// on prod 1.15.1954). In that session the doc goes through
// getPartnerComplianceDocBytes, the callable the partner portal already uses for
// these same files: it re-authorizes the tenant server-side
// (_requireComplianceManagerFor: role + managedTenants) and applies the PARTNER
// path fence (credential docs only, never opts.docRows). Nothing is opened here
// that the partner cannot already open from the portal. The tenantId is UX, not
// the boundary: the server refuses any tenant outside the caller's managedTenants.
function _hrPartnerClientTenantId() {
  try {
    if (typeof window.isPartnerClientSession !== 'function' || !window.isPartnerClientSession()) return null;
    const t = window.AppState && window.AppState.partnerClientContext && window.AppState.partnerClientContext.tenantId;
    return (typeof t === 'string' && t) ? t : null;
  } catch (_) { return null; }
}

// TASK-4748 — who may LOG a Clearinghouse query from the carrier screens, decided
// BEFORE the form is drawn. Kolsen (Crown) filled the whole "Log Clearinghouse
// Query" form, attached the FMCSA snip, and was refused on Save, because the
// button was offered to a session the write gate then rejected. The mode is
// derived from the same facts the server judges, and both carrier surfaces
// (hr.js, compliance.js) draw the section from it:
//   'partner'  — a managing compliance partner working inside the carrier
//                (?client= session). There is exactly ONE partner grant
//                (_users.role === 'complianceManager' && tenantId in
//                managedTenants); the "View only" banner text that made it
//                look like a level was a static label. The save goes through
//                executePartnerAction('clearinghouse.logQuery') (TASK-9449).
//   'carrier'  — a carrier member the people/{id} update rule admits:
//                owner/admin/dispatcher, or a roster-module holder who is not
//                "view & upload only". Owner/legacy sessions carry no members
//                doc and are admitted the way _compCanWriteRoster does.
//   'readonly' — everyone else (compliance-only or hr-only member, view/upload-
//                only, revoked/inactive/pending). The section renders read-only
//                with a note naming who can log it, and the opener refuses.
// Pure and exported so the two surfaces share one table and a unit test pins it.
// The server re-checks on every save; this decides only what is DRAWN.
//
// COD 2026-09-30 — the carrier save is TWO writes, and both gates must pass or
// the save half-lands (person written, then refused) and a retry duplicates the
// history row:
//   people/{id} update   = isTenantMember && (isDispatcherOrHigher || roster && !viewUploadOnly)
//   complianceTimeline   = isPartnerElevated (owner/admin) || hasModule hr || hasModule compliance
// And isTenantMember is `exists(members/{uid})` (or the demo tenant): a session
// with NO members doc is refused, so null memberAccess is 'readonly' unless demo.
function clearinghouseLogMode(memberAccess, partnerTenantId, isDemo) {
  if (partnerTenantId) return 'partner';
  if (isDemo) return 'carrier';
  const m = memberAccess;
  if (!m) return 'readonly';
  if (['revoked', 'inactive', 'pending'].includes(m.status)) return 'readonly';
  const mods = Array.isArray(m.modules) ? m.modules : [];
  const isAdmin = m.role === 'owner' || m.role === 'admin';
  const peopleOk = isAdmin || m.role === 'dispatcher' || (mods.includes('roster') && m.viewUploadOnly !== true);
  const timelineOk = isAdmin || mods.includes('hr') || mods.includes('compliance');
  return (peopleOk && timelineOk) ? 'carrier' : 'readonly';
}
const CLEARINGHOUSE_READONLY_MSG = 'View only — logging a query needs Roster plus Compliance or HR access. Ask an owner or admin to log it, or to grant that access.';
// TASK-1320 — may this session RECORD a Break-in-Service work history? Client
// mirror of the recordBreakInServiceGapAccount gate (ACCESS = MODULES, Chris
// 2026-09-24): the owner by tenants/{tid}.ownerId, an active member holding `hr`
// or `compliance`, or the managing compliance partner (?client= session). Driver
// / public sessions never. The UI may hide more than the server, never offer more.
function hrCanRecordBisGap() {
  const st = window.AppState || {};
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  if (_hrPartnerClientTenantId()) return true;
  if (st.isDemo === true || st.tenant === 'demo') return true;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true) return false;
  const mods = Array.isArray(ma.modules) ? ma.modules : [];
  return mods.includes('hr') || mods.includes('compliance');
}

// TASK-1320 — open the work-during-the-break modal for a returned driver and save
// through the server (the only writer; it validates coverage and audits). Shared
// with compliance.js via window.HRModule. `people` is the HOST's roster array and
// `afterSave(personId)` its re-render, so each surface refreshes itself.
async function openBisGapAccount(personId, people, afterSave, tenantIdHint) {
  const list = Array.isArray(people) ? people : HRState.people;
  const emp = (list || []).find(p => p.id === personId);
  if (!emp) { window.showToast?.('Driver not found', 'error'); return; }
  if (!hrCanRecordBisGap()) { window.showToast?.('Recording a work history needs HR or Compliance access.', 'error'); return; }
  const bis = window.PersonSections.resolveBreakInService(emp);
  if (bis.state === 'none') { window.showToast?.('No open break in service for this driver.', 'info'); return; }
  const tenantId = _hrPartnerClientTenantId() || tenantIdHint || HRState.tenantId || window.AppState?.tenant;
  let existing = null;
  try {
    const s = await getDoc(doc(db, 'tenants', tenantId, 'people', personId, 'breakInServiceAccounts', bis.returnDate));
    if (s.exists()) existing = s.data();
  } catch (_) { /* no read access or none on file — start blank; the server decides */ }
  const res = await window.PersonSections.openBisGapAccountModal(emp, {
    personName: emp.name || [emp.firstName, emp.lastName].filter(Boolean).join(' '),
    existing,
    save: async (payload) => {
      const fn = httpsCallable(functions, 'recordBreakInServiceGapAccount');
      const out = await fn({ tenantId, personId, ...payload });
      return out && out.data;
    },
  });
  if (!res || !res.saved) return;
  try {
    const fresh = await getDoc(doc(db, 'tenants', tenantId, 'people', personId));
    if (fresh.exists()) Object.assign(emp, fresh.data());
  } catch (_) { /* saved server-side; the next load refreshes the local copy */ }
  window.showToast?.('Work history saved', 'success');
  if (typeof afterSave === 'function') afterSave(personId);
  else if (HRState.selectedEmployee?.id === personId) openEmployeeDetail(personId);
}

function _hrClearinghouseLogMode() {
  const S = window.AppState || {};
  return clearinghouseLogMode(S.memberAccess, _hrPartnerClientTenantId(), S.isDemo === true || S.tenant === 'demo');
}

// TASK-9449 — a managing partner's UPLOADS from the carrier screens. storage.rules
// grants hr/{tid}/{pid}/ and people/{pid}/medExam/ to carrier members only, and
// stays that way: in a partner session the file goes to the uploadPersonComplianceDoc
// callable, which writes it create-only into this driver's own folder, sets the
// pointer itself and audits it in the partner's name. Carrier members never come
// here (every caller checks _hrPartnerClientTenantId() first) and keep their
// direct uploads unchanged. Exposed on window.HRModule so compliance.js reuses
// the same path (Pitfall #1 — no cross-import).
// 7.5 MB: the file travels base64 inside a callable request capped at 10 MB.
const _HR_PARTNER_UPLOAD_MAX_BYTES = Math.floor(7.5 * 1024 * 1024);
async function _hrPartnerUploadPersonDoc(personId, slot, file) {
  const tenantId = _hrPartnerClientTenantId();
  if (!tenantId) throw new Error('Not in a partner session.');
  if (!file) throw new Error('No file selected.');
  if (file.size > _HR_PARTNER_UPLOAD_MAX_BYTES) {
    throw new Error('File too large (max 7.5 MB here) — compress it and try again.');
  }
  const dataBase64 = await window.FileReadUtil.readAsBase64(file);
  const fn = httpsCallable(functions, 'uploadPersonComplianceDoc');
  const res = await fn({
    tenantId, personId, slot, dataBase64,
    contentType: inferContentType(file, ''),
    fileName: file.name || 'document',
  });
  return (res && res.data) || {};
}

// TASK-9449 — a managing partner recording a Clearinghouse query from the
// carrier's modal goes through executePartnerAction('clearinghouse.logQuery'),
// the SAME server handler the partner portal uses, so its "proof required" gate
// (TASK-1295: no FMCSA query document on file = no result recorded, when the
// firm requires proof) applies here too. The screenshot, if attached, is
// uploaded FIRST so the handler sees it. firestore.rules no longer lets a partner
// write the Clearinghouse result keys directly, so this is the only way in.
async function _hrPartnerSaveClearinghouseQuery(q) {
  const tenantId = _hrPartnerClientTenantId();
  if (!tenantId) throw new Error('Not in a partner session.');
  let pdfPath = '';
  if (q.file) {
    const up = await _hrPartnerUploadPersonDoc(q.personId, 'clearinghouse', q.file);
    pdfPath = up.storagePath || '';
  }
  const fn = httpsCallable(functions, 'executePartnerAction');
  const res = await fn({
    tenantId,
    action: 'clearinghouse.logQuery',
    payload: {
      personId: q.personId,
      queryType: q.queryType,
      result: q.queryResult,
      queryDate: q.queryDate,
      queryId: q.queryId || '',
      notes: q.notes || '',
      consentDate: q.consentDate || '',
      ...(pdfPath ? { pdfPath } : {}),
      ...(q.fullConsentAttested ? { fullConsentAttested: true } : {}),
    },
  });
  return (res && res.data) || {};
}

// Plain-English copy for a refused partner Clearinghouse save.
function _hrPartnerChErrorText(err) {
  const msg = String((err && err.message) || '');
  if (/PROOF_REQUIRED/.test(msg)) {
    return 'Your compliance firm requires the FMCSA query screenshot before a query can be recorded. Attach it and save again.';
  }
  return msg.replace(/^internal:\s*/, '') || 'Error saving query';
}

// TASK-9855 slice 1 — open a DQ/PII file through the Storage SDK on its PATH
// (getBlob → object URL; storage.rules decide). STRICT: unlike the general
// window.StorageFile policy, a network failure does NOT fall back to the stored
// token URL — for PII the only way in is the rules-checked read. Call before any
// await in a click handler (the tab is opened synchronously for popup blockers).
async function _hrOpenStorageStrict(input) {
  const SF = window.StorageFile;
  if (!SF) throw new Error('The file viewer is still loading. Try again.');
  let win = null;
  try { win = window.open('', '_blank'); if (win) win.opener = null; } catch (_) { /* ignore */ }
  try {
    const res = await SF.resolve(input);
    if (!res || res.kind !== 'blob') {
      const err = new Error('Could not load the document. Check your connection and try again.');
      err.sfReason = 'failed';
      throw err;
    }
    if (win && !win.closed) win.location.href = res.objectUrl;
    else window.open(res.objectUrl, '_blank', 'noopener');
  } catch (e) {
    try { if (win && !win.closed) win.close(); } catch (_) { /* ignore */ }
    throw e;
  }
}
window._hrOpenStorageStrict = _hrOpenStorageStrict;

async function viewComplianceDoc(storagePath, legacyUrl, fileName, anchorEl) {
  // TASK-1070 — spin the clicked link's icon + disable it for the duration (guards
  // double-click). Restored in finally so a denied/missing doc never leaves a stuck
  // spinner. UI-only — the byte-stream/auth path below is untouched.
  const restoreLink = _hrBusyDocLink(anchorEl);
  // Only ever open an https:// URL (blocks javascript:/data: — a legacy *Url field
  // is client-writable via the compliance allowlist, so treat it as untrusted).
  const _openUrl = (u) => { try { if (!/^https:\/\//i.test(String(u || ''))) return false; window.open(u, '_blank', 'noopener'); return true; } catch (_) { return false; } };
  // TASK-996 (COD Adversary) — the legacy-URL fallback is ONLY for a record with
  // NO storagePath (an old dead getSignedUrl link). NEVER fall back to legacyUrl on
  // a callable ERROR: getComplianceDocBytes throwing permission-denied means the
  // server HR-gate denied this viewer, and opening the raw bearer URL would defeat
  // exactly that gate (a bearer token URL bypasses Storage rules).
  try {
    if (!storagePath) {
      // TASK-9855 slice 1 — a legacy record with only a Firebase token URL still
      // has its object PATH inside that URL. A CARRIER session opens it by that
      // path through the SAME HR-gated getComplianceDocBytes door the path-only
      // rows use below (owner-aware; an SDK read would deny the owner, who has no
      // members doc), never by the bearer URL. A PARTNER session keeps the old
      // link for now: partner surfaces move in slice 3 with the other out-of-app
      // readers.
      const derived = window.StorageFile?.pathFromUrl?.(legacyUrl) || null;
      if (derived && !_hrPartnerClientTenantId()) {
        storagePath = derived.path;
      } else {
        if (legacyUrl && _openUrl(legacyUrl)) return;
        window.showToast?.('No document on file', 'error');
        return;
      }
    }
    window.showToast?.('Opening document…', 'info');
    // TASK-9297 — partner inside a managed carrier → the partner-scoped twin
    // (see _hrPartnerClientTenantId). Every other session is unchanged.
    const _ptid = _hrPartnerClientTenantId();
    const fn = httpsCallable(functions, _ptid ? 'getPartnerComplianceDocBytes' : 'getComplianceDocBytes');
    const result = await fn(_ptid ? { tenantId: _ptid, storagePath } : { storagePath });
    const data = result.data || {};
    if (!(data.ok && data.inline && data.base64)) throw new Error('No document data returned');
    const bin = atob(data.base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: data.contentType || 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    console.error('[HR] viewComplianceDoc failed:', e);
    // NO legacy-URL fallback here — a denial must NOT open the bearer URL.
    window.showToast?.(e?.sfReason === 'denied' ? 'You do not have access to this document' : (e?.message || 'Could not open the document'), 'error');
  } finally {
    restoreLink();
  }
}
window.viewComplianceDoc = viewComplianceDoc;

// TASK-1154 — open the ACTUAL signed §382.601(d) D&A policy receipt PDF (frozen
// full policy text + embedded signature + audit block) via the CARRIER byte-
// stream callable (getSignedDaReceiptPdf; tenantId bound from the auth claim,
// HR/compliance-gated + audit-logged). base64 → Blob per the house pattern
// (viewComplianceDoc). Exposed as a window global so the Compliance module
// (compliance.js renderDaPolicyReceiptSection) reuses it without a cross-import
// (Pitfall #1). DEBOUNCED — the button disables while in flight (double-click =
// duplicate COMPLIANCE_DOC_VIEW audit rows).
async function viewSignedDaReceipt(personId, btn) {
  if (!personId) return;
  if (btn && btn.dataset.loading === '1') return; // debounce double-click
  let _orig = null;
  if (btn) {
    _orig = btn.innerHTML;
    btn.dataset.loading = '1';
    btn.disabled = true;
    btn.innerHTML = '<span class="material-icons spin" style="font-size:14px;vertical-align:-2px;">autorenew</span> Opening…';
  }
  try {
    window.showToast?.('Opening signed receipt…', 'info');
    const fn = httpsCallable(functions, 'getSignedDaReceiptPdf');
    const res = await fn({ personId });
    const data = res?.data || {};
    if (!(data.ok && data.pdfBase64)) throw new Error('No receipt data returned');
    const bin = atob(data.pdfBase64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    console.error('[HR] viewSignedDaReceipt failed:', e);
    window.showToast?.((e?.message || 'Could not open the signed receipt').replace(/^internal:\s*/, ''), 'error');
  } finally {
    if (btn) { delete btn.dataset.loading; btn.disabled = false; if (_orig != null) btn.innerHTML = _orig; }
  }
}
window.viewSignedDaReceipt = viewSignedDaReceipt;

// ── TASK-1089 — credential-doc VERSION HISTORY (carrier side) ────────────────
// Every credential re-upload now appends a version row to the Admin-SDK-only
// tenants/{tid}/people/{pid}/credDocs subcollection (server intake paths); the
// gated listPersonCredentialDocs callable projects it back as credDocVersions.
// This opens a small dated list — each version opens through the SAME
// viewComplianceDoc byte-stream path (getComplianceDocBytes; HR/compliance-
// gated + audit-logged) the current document uses. Used from both the HR
// employee detail and the Compliance person detail (window.viewCredDocHistory).
// NOTE: listPersonCredentialDocs is owner/admin (or partner) gated — a
// module-only member gets the server's permission toast, never a silent fail.
async function viewCredDocHistory(personId, credType, entryId, anchorEl) {
  const tenantId = HRState.tenantId || window.AppState?.tenant || null;
  if (!tenantId || !personId || !credType) return;
  // (2nd-COD UX) pre-check the callable's owner/admin gate so a module-only
  // member gets a plain answer instead of a raw permission error. Legacy
  // admins have no modules field; role null = pre-members-doc owner session.
  const _ma = window.AppState?.memberAccess;
  if (_ma && _ma.role && !['owner', 'admin'].includes(_ma.role) && Array.isArray(_ma.modules)) {
    window.showToast?.('Version history requires owner or admin access', 'info');
    return;
  }
  const restoreLink = _hrBusyDocLink(anchorEl);
  try {
    window.showToast?.('Loading version history…', 'info');
    const fn = httpsCallable(functions, 'listPersonCredentialDocs');
    const result = await fn({ tenantId, personId });
    const data = (result && result.data) || {};
    const key = (credType === 'training' && entryId) ? `training:${entryId}` : credType;
    const versions = (data.credDocVersions && Array.isArray(data.credDocVersions[key])) ? data.credDocVersions[key] : [];
    const removedN = (data.credDocDeletedCounts && data.credDocDeletedCounts[key]) ? Number(data.credDocDeletedCounts[key]) : 0;
    if (versions.length < 2) {
      window.showToast?.(removedN > 0
        ? `This is the only remaining upload — ${removedN} prior version${removedN === 1 ? ' was' : 's were'} removed (see the audit log).`
        : 'No prior versions on file — this is the only upload.', 'info');
      return;
    }
    // Minimal centered overlay (showConfirm-style: icon, title, centered content).
    const old = document.getElementById('cred-doc-history-overlay');
    if (old) old.remove();
    const wrap = document.createElement('div');
    wrap.id = 'cred-doc-history-overlay';
    wrap.style.cssText = 'position:fixed;inset:0;z-index:10050;background:rgba(15,23,42,0.45);display:flex;align-items:center;justify-content:center;padding:16px;';
    const rowsHtml = versions.map((v, idx) => {
      const when = v.uploadedAt ? new Date(v.uploadedAt).toLocaleDateString() : 'Date unknown';
      const by = v.uploadedBy ? escapeHtml(String(v.uploadedBy)) : (v.synthesized ? 'earlier upload' : '');
      const tag = v.current
        ? '<span style="font-size:0.62rem;font-weight:700;color:#059669;border:1px solid #059669;border-radius:6px;padding:0 6px;">CURRENT</span>'
        : '<span style="font-size:0.62rem;font-weight:600;color:var(--text-secondary);border:1px solid var(--border-light);border-radius:6px;padding:0 6px;">SUPERSEDED</span>';
      // TASK-1089 S2 — trash (deletable; server re-asserts retention) or a lock
      // w/ tooltip (retention-protected). INDEX-ONLY in the data attribute — the
      // row object is resolved from the closure (the _peiEmployerByIdx discipline;
      // never user-entered strings in inline handlers).
      const del = (v.deletable && v.id)
        ? `<button type="button" class="btn-icon" data-credhist-del="${idx}" title="Remove this version" style="color:#DC2626;padding:2px;"><span class="material-icons" style="font-size:18px;">delete_outline</span></button>`
        : (v.lockReason
          ? `<span class="material-icons" title="${escapeHtml(v.lockReason)}" style="font-size:17px;color:var(--text-secondary);cursor:help;">lock</span>`
          : '');
      return `<div style="display:flex;align-items:center;gap:10px;padding:8px 4px;border-bottom:1px solid var(--border-light);">
          <span class="material-icons" style="font-size:18px;color:var(--brand-blue);flex-shrink:0;">history</span>
          <div style="flex:1;min-width:0;text-align:left;">
            <div style="font-size:0.82rem;font-weight:600;color:var(--text-primary);">${escapeHtml(when)}${by ? ` <span style="font-weight:400;color:var(--text-secondary);">· ${by}</span>` : ''}</div>
            ${v.fileName ? `<div style="font-size:0.72rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(v.fileName)}</div>` : ''}
          </div>
          ${tag}
          ${v.storagePath ? `<a href="#" data-credhist-open="${idx}" style="color:var(--brand-blue);display:inline-flex;align-items:center;" title="Open this version"><span class="material-icons" style="font-size:18px;">open_in_new</span></a>` : ''}
          ${del}
        </div>`;
    }).join('');
    wrap.innerHTML = `
      <div role="dialog" aria-modal="true" style="background:var(--bg-primary,#fff);max-width:520px;width:100%;max-height:80vh;max-height:80dvh;overflow:auto;border-radius:12px;border:1px solid var(--border-light);box-shadow:0 20px 50px rgba(0,0,0,0.3);padding:16px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
          <span class="material-icons" style="color:var(--brand-blue);">history</span>
          <div style="font-weight:700;font-size:0.95rem;color:var(--text-primary);flex:1;">Version history — ${versions.length} versions</div>
          <button type="button" class="btn-icon" data-credhist-close="1" title="Close"><span class="material-icons">close</span></button>
        </div>
        <div style="font-size:0.74rem;color:var(--text-secondary);margin-bottom:6px;">Every upload is retained. Prior versions stay available for audit; each open is logged.${removedN > 0 ? ` <span style="font-style:italic;">${removedN} removed version${removedN === 1 ? '' : 's'} — the removal record stays in the audit log.</span>` : ''}</div>
        ${rowsHtml}
      </div>`;
    wrap.addEventListener('click', async (e) => {
      if (e.target === wrap || e.target.closest('[data-credhist-close]')) { wrap.remove(); return; }
      // (2nd-COD injection close) open-version rides the same index→closure
      // lookup — fileName/storagePath are user-influenced and must never sit
      // inside an inline onclick string.
      const openBtn = e.target.closest('[data-credhist-open]');
      if (openBtn) {
        e.preventDefault();
        const v = versions[parseInt(openBtn.getAttribute('data-credhist-open'), 10)];
        if (v && v.storagePath && typeof window.viewComplianceDoc === 'function') {
          window.viewComplianceDoc(v.storagePath, null, v.fileName || 'document', openBtn);
        }
        return;
      }
      // TASK-1089 S2 — per-version delete (index → closure row lookup, ids only).
      const delBtn = e.target.closest('[data-credhist-del]');
      if (delBtn) {
        const v = versions[parseInt(delBtn.getAttribute('data-credhist-del'), 10)];
        if (!v || !v.id) return;
        const done = await _hrDeleteCredDocVersion(tenantId, personId, v);
        if (done) {
          wrap.remove();
          // Re-fetch + re-open so the promoted/current flags are the server's truth.
          viewCredDocHistory(personId, credType, entryId, anchorEl);
        }
      }
    });
    document.body.appendChild(wrap);
  } catch (e) {
    console.error('[HR] viewCredDocHistory failed:', e);
    window.showToast?.(e?.message || 'Could not load version history', 'error');
  } finally {
    restoreLink();
  }
}
window.viewCredDocHistory = viewCredDocHistory;

// TASK-1089 S2 — RED double-confirm with a REQUIRED reason textarea (showConfirm's
// danger idiom + the mandatory "why" field; local overlay because showConfirm has
// no input support). Resolves the typed reason, or null on cancel/scrim. The
// reason goes to the SERVER only — it is never interpolated into this page's HTML.
function _hrCredDelReasonConfirm(meta) {
  return new Promise((resolve) => {
    document.getElementById('cred-doc-del-confirm')?.remove();
    const wrap = document.createElement('div');
    wrap.id = 'cred-doc-del-confirm';
    wrap.style.cssText = 'position:fixed;inset:0;z-index:10060;background:rgba(15,23,42,0.55);display:flex;align-items:center;justify-content:center;padding:16px;';
    const bits = [];
    if (meta && meta.fileName) bits.push(escapeHtml(String(meta.fileName)));
    if (meta && meta.uploadedAt) { try { bits.push(`uploaded ${escapeHtml(new Date(meta.uploadedAt).toLocaleDateString())}`); } catch (_) { /* non-fatal */ } }
    wrap.innerHTML = `
      <div role="alertdialog" aria-modal="true" style="background:var(--bg-primary,#fff);max-width:480px;width:100%;border-radius:12px;border:1px solid #FECACA;box-shadow:0 20px 50px rgba(0,0,0,0.3);overflow:hidden;">
        <div style="background:#FEF2F2;border-bottom:1px solid #FECACA;border-left:5px solid #DC2626;padding:14px 16px;display:flex;gap:10px;align-items:flex-start;">
          <span class="material-icons" style="color:#DC2626;font-size:22px;flex-shrink:0;" aria-hidden="true">warning</span>
          <div style="text-align:left;">
            <div style="font-weight:700;font-size:14.5px;color:#991B1B;">${meta && meta.current ? 'Remove the CURRENT version?' : 'Remove this version?'}</div>
            <div style="font-size:13px;color:#7F1D1D;line-height:1.5;margin-top:4px;">${bits.length ? bits.join(' · ') + ' — ' : ''}this removes the file for everyone${meta && meta.current ? ' and promotes the previous version to current' : ''}. The removal itself is permanently recorded with your name and reason.</div>
          </div>
        </div>
        <div style="padding:12px 16px 0;text-align:left;">
          <label style="display:block;font-size:12px;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Why is this version being removed? (required)</label>
          <textarea id="cred-del-reason" rows="3" maxlength="500" placeholder="e.g. wrong file uploaded, duplicate of the version below…" style="width:100%;box-sizing:border-box;font-size:13px;padding:8px;border:1px solid var(--border-light);border-radius:8px;resize:vertical;"></textarea>
          <div id="cred-del-reason-err" style="display:none;font-size:12px;color:#DC2626;margin-top:4px;">A reason is required.</div>
        </div>
        <div style="display:flex;justify-content:flex-end;gap:8px;padding:12px 16px;">
          <button type="button" class="btn btn-secondary" data-creddel="cancel">Go back</button>
          <button type="button" class="btn" data-creddel="ok" style="background:#DC2626;border:1px solid #DC2626;color:#fff;font-weight:700;">Remove version</button>
        </div>
      </div>`;
    const done = (v) => { wrap.remove(); resolve(v); };
    wrap.addEventListener('click', (e) => {
      const b = e.target.closest('[data-creddel]');
      if (b) {
        if (b.getAttribute('data-creddel') !== 'ok') { done(null); return; }
        const ta = wrap.querySelector('#cred-del-reason');
        const reason = ((ta && ta.value) || '').trim();
        if (!reason) {
          const err = wrap.querySelector('#cred-del-reason-err');
          if (err) err.style.display = 'block';
          if (ta) ta.focus();
          return;
        }
        done(reason);
        return;
      }
      if (e.target === wrap) done(null);
    });
    document.body.appendChild(wrap);
    try { wrap.querySelector('#cred-del-reason').focus(); } catch (_) { /* non-fatal */ }
  });
}

// TASK-1089 S2 — carrier per-version delete. Owner/admin-gated deleteCredDocVersion
// callable (the server retention hard-block is authoritative — the trash can is
// only shown on rows the lister flagged deletable, but the server re-checks).
// Returns true when the delete landed (caller re-fetches the history).
async function _hrDeleteCredDocVersion(tenantId, personId, v) {
  const reason = await _hrCredDelReasonConfirm(v);
  if (!reason) return false;
  try {
    const fn = httpsCallable(functions, 'deleteCredDocVersion');
    await fn({ tenantId, personId, versionDocId: v.id, reason });
    window.showToast?.('Version removed — the removal is recorded in the audit log.', 'success');
    return true;
  } catch (e) {
    console.error('[HR] deleteCredDocVersion failed:', e);
    window.showToast?.(e?.message || 'Could not remove this version', 'error');
    return false;
  }
}

// ── TASK-1118 — signed e-sign PDFs: byte-stream View, no more stored URLs ──
// The persisted signed URLs (2030 expiry) rotted whenever Google rotated the
// runtime SA's managed signing key (SignatureDoesNotMatch on every historical
// doc), and they were no-auth bearer URLs to signed/disciplinary PDFs in the
// any-member-readable signDocuments docs. New signings persist
// signedStoragePath only; the View buttons resolve a path and open the PDF
// through the getSignDocBytes callable (admin/owner OR hr OR
// dispatch/dispatcher+ — the e-sign sender set per firestore.rules).

// LEGACY people.signedDocuments[] entries (pre-TASK-1118, pre-backfill) carry
// ONLY the dead signedUrl — recover the storage path from its URL path
// component. Handles both the V2 signed-URL form
// (/<bucket>/hr/{tid}/signDocs/…) and the Firebase download form
// (/v0/b/<bucket>/o/hr%2F{tid}%2F…). Returns null on anything malformed or
// outside this tenant's signDocs tree (graceful fallback — the old link was
// dead anyway).
function _signDocPathFromUrl(signedUrl) {
  try {
    const u = new URL(String(signedUrl || ''));
    const p = decodeURIComponent(u.pathname || '');
    const idx = p.indexOf('/hr/');
    if (idx === -1) return null;
    const path = p.slice(idx + 1);
    const tenantId = HRState.tenantId === 'demo-tenant' ? 'demo' : HRState.tenantId;
    if (!path.startsWith(`hr/${tenantId}/signDocs/`)) return null;
    if (path.includes('..')) return null;
    return path;
  } catch (_) {
    return null;
  }
}

// Row data for the signed-doc View buttons, keyed by documentId. The Completed
// list mixes live signDocuments docs and historical person.signedDocuments[]
// entries, so the onclick passes only the key and the handler resolves the
// storage path here (never a URL through an HTML attribute).
const _signDocViewCache = new Map();

async function viewSignedDoc(docKey, anchorEl) {
  const d = _signDocViewCache.get(String(docKey));
  const storagePath = (d && typeof d.signedStoragePath === 'string' && d.signedStoragePath)
    ? d.signedStoragePath
    : _signDocPathFromUrl(d && d.signedUrl);
  if (!storagePath) {
    // Legacy entry whose stored URL is malformed/foreign — fail soft (the old
    // bearer link is signature-rotted and dead regardless).
    window.showToast?.('Could not locate the signed PDF for this document', 'error');
    return;
  }
  const restoreLink = _hrBusyDocLink(anchorEl);
  try {
    window.showToast?.('Opening document…', 'info');
    const fn = httpsCallable(functions, 'getSignDocBytes');
    const result = await fn({ storagePath });
    const data = result.data || {};
    if (!(data.ok && data.inline && data.base64)) throw new Error('No document data returned');
    const bin = atob(data.base64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: data.contentType || 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    console.error('[HR] viewSignedDoc failed:', e);
    // NEVER fall back to a stored bearer URL on a callable denial (same rule as
    // viewComplianceDoc — a permission-denied must not open the raw URL).
    window.showToast?.(e?.message || 'Could not open the document', 'error');
  } finally {
    restoreLink();
  }
}

// TASK-1029 — open a hiring-packet slot doc (I-9 / W-4 / direct deposit / custom
// items) from the Compliance tab. New uploads are PATH-ONLY, opened through the
// HR-gated getComplianceDocBytes byte-stream; a legacy persisted url is used
// ONLY when the slot has no storagePath at all (viewComplianceDoc's rule — never
// as a fallback for a callable denial).
function viewPacketDoc(personId, itemId, anchorEl) {
  const person = (HRState.people || []).find(p => p.id === personId);
  const slot = getHiringPacketItems().find(s => s.itemId === itemId)
    || ONBOARDING_DOC_SLOTS.find(s => s.itemId === itemId);
  if (!person || !slot) { window.showToast?.('Document not found', 'error'); return; }
  const { url, storagePath } = getHiringPacketDocFor(person, slot);
  viewComplianceDoc(storagePath || null, url || null, slot.label || 'Document', anchorEl);
}

// ── Phase 4: Clearinghouse Query Modal ──

// TASK-1045 — SHARED single-source helpers (TASK-1022 anti-drift). Exported on
// window.HRModule so the Compliance driver detail calls THESE at save time —
// never a second copy of the mapping (the carrier/partner enum drift TASK-1022
// fixed: flagged + full/pre_employment → 'refused'). Pure functions — no
// HRState, safe to call from any module (Pitfall #1 forbids importing hr.js).
// TASK-9449 — clearinghouseHistory has two writers with OPPOSITE orders (these
// carrier modals unshift newest-first; the partner clearinghouse.logQuery handler
// appends). Every reader/trimmer here sorts by query date first, so a partner's
// entry is shown and the 23-entry trim drops the OLDEST query, never the newest.
// Stable sort: equal dates keep their stored order. Shared with compliance.js.
function sortClearinghouseHistoryNewestFirst(history) {
  return (Array.isArray(history) ? history.slice() : [])
    .sort((a, b) => String((b && b.date) || '').localeCompare(String((a && a.date) || '')));
}

function deriveClearinghouseStatus(queryType, queryResult) {
  if (queryResult === 'clear') return 'clear';
  if (queryResult === 'flagged' && queryType === 'limited') return 'limited_hit';
  if (queryResult === 'flagged') return 'refused';
  return undefined;
}

// Annual cadence: next due = +1 calendar year from a yyyy-mm-dd date string.
// Used by both the Clearinghouse annual query and the §391.25 annual review.
function complianceNextDueOneYear(dateStr) {
  const d = new Date(dateStr);
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().split('T')[0];
}

function openClearinghouseModal(empId) {
  const emp = HRState.people.find(p => p.id === empId);
  if (!emp) return;

  // TASK-1090 — a NonCDL person cannot be queried in the FMCSA Clearinghouse
  // (§382.701 covers CDL drivers). The compliance-tab card renders an N/A state
  // for these tiers, but guard the opener too (other callers exist).
  const _chOpenApp = window.DqMatrix.getApplicableDqItems(emp);
  if (!_chOpenApp.items.clearinghouse) {
    window.showToast?.(`${emp.name || 'This person'} is classified ${window.DqMatrix.LABELS[_chOpenApp.positionType] || 'Non-CDL'} — the FMCSA Clearinghouse applies to CDL drivers only (§382.701).`, 'error');
    return;
  }

  // TASK-4748 — never draw a form this session cannot save. The section hides
  // the button in read-only mode; this guards a stale button / other callers.
  if (_hrClearinghouseLogMode() === 'readonly') {
    window.showToast?.(CLEARINGHOUSE_READONLY_MSG, 'error');
    return;
  }

  const existing = document.getElementById('modal-clearinghouse');
  if (existing) existing.remove();

  const chStatus = emp.clearinghouseStatus || 'not_started';
  const consentDate = emp.clearinghouseConsentDate || '';

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-clearinghouse';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:520px;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
      <div class="modal-header" style="background:linear-gradient(135deg, #7c3aed, #a78bfa);color:#fff;flex-shrink:0;">
        <h3 style="color:#fff;display:flex;align-items:center;gap:8px;">
          <span class="material-icons">vaccines</span> Log Clearinghouse Query
        </h3>
        <button class="btn-icon" onclick="window.HRModule.closeModal('clearinghouse')" title="Close" aria-label="Close" style="color:#fff;background:rgba(255,255,255,0.2);border-radius:8px;width:44px;height:44px;min-width:44px;display:inline-flex;align-items:center;justify-content:center;transition:background 0.15s;" onmouseover="this.style.background='rgba(255,255,255,0.35)';" onmouseout="this.style.background='rgba(255,255,255,0.2)';">
          <span class="material-icons" style="font-size:24px;">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:20px;overflow-y:auto;flex:1;min-height:0;">
        <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
          Record a Clearinghouse query for <strong>${escapeHtml(emp.name)}</strong>
        </div>

        <input type="hidden" id="ch-emp-id" value="${empId}">

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Query Type *</label>
            <select id="ch-query-type" class="form-control">
              <option value="">Select type...</option>
              <option value="pre_employment">Pre-Employment Full Query</option>
              <option value="limited">Annual Limited Query</option>
              <option value="full">Annual Full Query</option>
            </select>
          </div>
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Result *</label>
            <select id="ch-query-result" class="form-control">
              <option value="">Select result...</option>
              <option value="clear">Clear</option>
              <option value="flagged">Flagged / Violation Found</option>
            </select>
          </div>
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Query Date *</label>
            <input type="date" id="ch-query-date" class="form-control" value="${new Date().toISOString().split('T')[0]}">
          </div>
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Query ID</label>
            <input type="text" id="ch-query-id" class="form-control" placeholder="Optional reference #">
          </div>
          <div class="form-group" style="grid-column:1/-1;">
            <label style="font-size:0.8rem;font-weight:600;">General Consent Date</label>
            <input type="date" id="ch-consent-date" class="form-control" value="${consentDate}">
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">Date the driver signed general consent for Clearinghouse queries</div>
          </div>
          <div class="form-group" style="grid-column:1/-1;">
            <label style="font-size:0.8rem;font-weight:600;">Notes</label>
            <textarea id="ch-query-notes" class="form-control" rows="2" placeholder="Optional notes..."></textarea>
          </div>
          <div class="form-group" style="grid-column:1/-1;">
            <label style="font-size:0.8rem;font-weight:600;">Query screenshot <span id="ch-file-req" style="font-weight:500;color:#DC2626;">(required)</span></label>
            <input type="file" id="ch-file" class="form-control" accept=".pdf,.jpg,.jpeg,.png" style="font-size:0.82rem;">
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">FMCSA query confirmation screenshot or PDF — filed to this employee's documents (Clearinghouse Query). Max 10MB.</div>
          </div>
        </div>

        <div style="background:#F1F5F9;border-radius:8px;padding:10px 12px;margin-top:8px;font-size:0.75rem;color:var(--text-secondary);">
          <strong>Current status:</strong> ${escapeHtml(String(chStatus).replace(/_/g, ' '))} &bull;
          <a href="https://clearinghouse.fmcsa.dot.gov/" target="_blank" rel="noopener" style="color:var(--brand-blue);">Open FMCSA Clearinghouse</a>
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);display:flex;gap:var(--spacing-sm);justify-content:flex-end;flex-shrink:0;">
        <button class="btn btn-secondary" onclick="window.HRModule.closeModal('clearinghouse')">Cancel</button>
        <button class="btn" id="btn-save-ch-query" style="background:#7c3aed;color:#fff;" onclick="window.HRModule.saveClearinghouseQuery()">
          <span class="material-icons">save</span> Save Query
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // TASK-1092 — async check whether a clearinghouse doc is already on file
  // (people/{id}/documents, category 'clearinghouse' — the same collection
  // uploadEmployeeDocument writes). Date-only saves skip the red warning when
  // one exists; the label relaxes to "(optional)". A failed check leaves the
  // flag at '0' → fail toward warning, never toward silence.
  modal.dataset.chDocOnFile = '0';
  (async () => {
    try {
      const snap = await getDocs(query(
        collection(db, 'tenants', HRState.tenantId, 'people', empId, 'documents'),
        where('category', '==', 'clearinghouse')
      ));
      if (!snap.empty && document.getElementById('modal-clearinghouse') === modal) {
        modal.dataset.chDocOnFile = '1';
        const req = document.getElementById('ch-file-req');
        if (req) { req.textContent = '(optional — a query document is already on file)'; req.style.color = 'var(--text-secondary)'; }
      }
    } catch (_) { /* fail toward the warning */ }
  })();
}

async function saveClearinghouseQuery() {
  const empId = document.getElementById('ch-emp-id')?.value;
  // TASK-1090 — defense in depth behind the opener guard: never record a
  // Clearinghouse query for a person whose classification excludes it.
  const _chSaveEmp = HRState.people.find(p => p.id === empId);
  if (_chSaveEmp && !window.DqMatrix.getApplicableDqItems(_chSaveEmp).items.clearinghouse) {
    window.showToast?.('The FMCSA Clearinghouse applies to CDL drivers only (§382.701) — this person cannot be queried.', 'error');
    return;
  }
  const queryType = document.getElementById('ch-query-type')?.value;
  const queryResult = document.getElementById('ch-query-result')?.value;
  const queryDate = document.getElementById('ch-query-date')?.value;
  const queryId = document.getElementById('ch-query-id')?.value?.trim() || '';
  const consentDate = document.getElementById('ch-consent-date')?.value || '';
  const notes = document.getElementById('ch-query-notes')?.value?.trim() || '';

  if (!queryType) { window.showToast?.('Please select a query type', 'error'); return; }
  if (!queryResult) { window.showToast?.('Please select a result', 'error'); return; }
  if (!queryDate) { window.showToast?.('Please enter the query date', 'error'); return; }

  // TASK-1092 — screenshot proof of the query. Validate a selected file up
  // front; a save with NO file and NO doc on file requires an explicit RED-
  // warning acknowledgement (Chris-resolved: block-with-red-warning, not a
  // silent hard-block). Fires BEFORE the button disables, so a declined
  // confirm leaves the modal fully interactive.
  const chFile = document.getElementById('ch-file')?.files?.[0] || null;
  if (chFile) {
    if (chFile.size > 10 * 1024 * 1024) { window.showToast?.('Screenshot too large. Max 10MB.', 'error'); return; }
    const chExt = chFile.name.split('.').pop().toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(chExt)) { window.showToast?.('Only PDF, JPG, and PNG files allowed.', 'error'); return; }
  } else {
    const chDocOnFile = document.getElementById('modal-clearinghouse')?.dataset?.chDocOnFile === '1';
    if (!chDocOnFile) {
      const warnMsg = 'Safety networks (TPS Alert, ISNetworld, Veriforce) demand proof of the last query date — uploading the FMCSA query screenshot is strongly recommended. Save the date-only record anyway?';
      const proceed = window.showConfirm
        ? await window.showConfirm('No Clearinghouse screenshot uploaded', warnMsg, {
          danger: true,
          icon: 'warning',
          iconColor: '#DC2626',
          confirmText: 'Save without screenshot',
          cancelText: 'Go back',
        })
        : window.confirm(warnMsg);
      if (!proceed) return;
    }
  }

  // TASK-290 (v1.15.696) — Soft-gate for Full Query / Pre-Employment Full Query.
  // FMCSA's Clearinghouse portal is the actual enforcement point (the carrier
  // can't run a full query on FMCSA's site without uploading driver consent).
  // DD is record-keeping; this attestation gives the carrier audit-defense
  // documentation that someone affirmed consent at log time. Limited queries
  // skip the gate (no separate consent required).
  let fullConsentAttestation = null;
  if (queryType === 'full' || queryType === 'pre_employment') {
    const consentMessage =
      "Full Query requires specific driver consent (signed release on file). " +
      "FMCSA's Clearinghouse portal enforces consent uploads on their side, so by the " +
      "time you're logging it here it's already been accepted by FMCSA. DispatchDeck " +
      "records the attestation so you have audit-defense documentation.";
    const ok = window.showConfirm
      ? await window.showConfirm('Full Query consent verification', consentMessage, {
        confirmText: 'Yes — proceed and attest',
        cancelText: 'No — cancel',
      })
      : window.confirm(consentMessage);
    if (!ok) return; // user canceled — no state change, button not yet disabled
    fullConsentAttestation = {
      fullConsentAcknowledgedAt: new Date().toISOString(),
      fullConsentAcknowledgedBy: window.AppState?.user?.uid || null,
      fullConsentAcknowledgedByEmail: window.AppState?.user?.email || 'unknown',
    };
  }

  const btn = document.getElementById('btn-save-ch-query');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  // TASK-9449 — managing partner: the server records it (proof gate applies).
  if (_hrPartnerClientTenantId()) {
    try {
      await _hrPartnerSaveClearinghouseQuery({
        personId: empId, queryType, queryResult, queryDate, queryId, consentDate, notes,
        file: chFile, fullConsentAttested: !!fullConsentAttestation,
      });
      try {
        const fresh = await getDoc(doc(db, 'tenants', HRState.tenantId, 'people', empId));
        const emp = HRState.people.find(p => p.id === empId);
        if (emp && fresh.exists()) Object.assign(emp, fresh.data());
      } catch (_) { /* the save landed; a stale local copy refreshes on the next load */ }
      if (HRState.employeeDocsLoaded === empId) HRState.employeeDocsLoaded = null;
      window.showToast?.('Clearinghouse query saved', 'success');
      closeModal('clearinghouse');
      if (HRState.selectedEmployee?.id === empId) openEmployeeDetail(empId);
    } catch (err) {
      console.error('[HR] partner clearinghouse save failed:', err);
      window.showToast?.(_hrPartnerChErrorText(err), 'error');
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Save Query'; }
    }
    return;
  }

  try {
    const tenant = HRState.tenantId;
    const personRef = doc(db, 'tenants', tenant, 'people', empId);
    const personSnap = await getDoc(personRef);
    const existing = personSnap.exists() ? personSnap.data() : {};
    const history = sortClearinghouseHistoryNewestFirst(existing.clearinghouseHistory).slice(0, 23); // TASK-9449

    // Build history entry. Attestation fields (TASK-290) stamped only on
    // full / pre-employment queries; limited queries get plain shape. Uses
    // ISO strings to match the existing `recordedAt` convention (avoids
    // Firestore Timestamp/ISO type-mixing inside the same array element).
    const entry = {
      type: queryType,
      result: queryResult,
      date: queryDate,
      queryId,
      notes,
      recordedBy: window.AppState?.user?.email || 'unknown',
      recordedAt: new Date().toISOString(),
      ...(fullConsentAttestation || {})
    };
    history.unshift(entry);

    // Determine new status based on result (TASK-1045 — shared single-source
    // mapping, also called by compliance.js via window.HRModule)
    const newStatus = deriveClearinghouseStatus(queryType, queryResult);

    // Calculate next due date (annual query = +1 year from query date)
    const nextDue = complianceNextDueOneYear(queryDate);

    const updateData = {
      clearinghouseStatus: newStatus,
      clearinghouseLastQueryDate: queryDate,
      clearinghouseNextDue: nextDue,
      clearinghouseHistory: history
    };
    if (consentDate) {
      updateData.clearinghouseConsentDate = consentDate;
      // 2nd-COD Domain ⚠️ (TASK-1022, 2026-07-08): only default to
      // 'consent_on_file' when there's NO meaningful query result (pure consent
      // capture). The old `|| !existing.clearinghouseStatus` leg let a driver's
      // FIRST-ever logged query with a consentDate downgrade a computed
      // 'refused'/'limited_hit' (a §382-prohibited result) to 'consent_on_file'
      // — masking a violation. A real query-derived status is authoritative.
      if (newStatus === 'not_started') {
        updateData.clearinghouseStatus = 'consent_on_file';
      }
    }

    await updateDoc(personRef, updateData);

    // Immutable audit trail
    await addDoc(collection(db, 'tenants', tenant, 'complianceTimeline'), {
      personId: empId,
      personName: existing.name || '',
      type: 'clearinghouse_query',
      description: `${queryType === 'pre_employment' ? 'Pre-Employment' : queryType === 'limited' ? 'Limited' : 'Full'} query — ${queryResult === 'clear' ? 'Clear' : 'Flagged'}`,
      queryType,
      queryResult,
      queryDate,
      queryId,
      recordedBy: window.AppState?.user?.email || 'unknown',
      timestamp: Timestamp.now(),
      createdAt: Timestamp.now()
    });

    // Update local state
    const emp = HRState.people.find(p => p.id === empId);
    if (emp) {
      Object.assign(emp, updateData);
    }

    window.logAudit?.('hr', 'CLEARINGHOUSE_QUERY_LOGGED', {
      personId: empId, queryType, queryResult, queryDate
    });

    // TASK-1092 — attach the query screenshot to the employee's documents.
    // Reuses the EXISTING uploadEmployeeDocument plumbing exactly (same
    // hr/{tid}/{pid}/ storage path, same people/{id}/documents field shape,
    // category 'clearinghouse' — already in the ALLOWED_DOC_CATEGORIES /
    // rules allowlist; no new paths). Runs AFTER the query record committed;
    // a failed upload never rolls back the §382.701 record.
    if (chFile) {
      let chStorageRef = null;
      try {
        const chTs = Date.now();
        const chSafeName = chFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const chStoragePath = `hr/${tenant}/${empId}/${chTs}_${chSafeName}`;
        chStorageRef = ref(storage, chStoragePath);
        await uploadBytes(chStorageRef, chFile, { contentType: inferContentType(chFile) });
        try {
          await addDoc(
            collection(db, 'tenants', tenant, 'people', empId, 'documents'),
            {
              fileName: chFile.name,
              storagePath: chStoragePath, // TASK-1029 — path-only; view via getComplianceDocBytes
              mimeType: chFile.type,
              isPdf: chFile.type === 'application/pdf',
              sizeBytes: chFile.size,
              category: 'clearinghouse',
              uploadedAt: Timestamp.now(),
              uploadedBy: window.AppState?.userName || 'Unknown'
            }
          );
          // Keep the Documents tab fresh if it was already loaded for this person.
          if (HRState.employeeDocsLoaded === empId) HRState.employeeDocsLoaded = null;
        } catch (chAddErr) {
          // Orphan-blob cleanup — the /hr/{tid}/{pid}/ path allows client delete.
          try { await deleteObject(chStorageRef); } catch (_) { /* best-effort */ }
          throw chAddErr;
        }
      } catch (chUpErr) {
        console.error('[HR] clearinghouse screenshot upload failed (query record saved):', chUpErr);
        window.showToast?.('Query saved, but the screenshot upload failed — attach it from the Documents tab.', 'error');
      }
    }

    window.showToast?.('Clearinghouse query saved', 'success');
    closeModal('clearinghouse');

    // Refresh the employee detail view
    if (HRState.selectedEmployee?.id === empId) {
      openEmployeeDetail(empId);
    }
  } catch (err) {
    console.error('[HR] Error saving clearinghouse query:', err);
    // TASK-4748 — the form is no longer offered to a session the people rule
    // refuses (clearinghouseLogMode), so a denial here means access changed
    // mid-session. Say so plainly rather than "Error saving query".
    const denied = err?.code === 'permission-denied';
    window.showToast?.(denied
      ? 'You do not have permission to update driver records — ask an owner or admin to log this query.'
      : 'Error saving query', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Save Query'; }
  }
}

// ── Phase 5: Annual Record Review Modal ──

function openAnnualReviewModal(empId) {
  const emp = HRState.people.find(p => p.id === empId);
  if (!emp) return;

  const existing = document.getElementById('modal-annual-review');
  if (existing) existing.remove();

  const today = new Date().toISOString().split('T')[0];

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-annual-review';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:580px;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
      <div class="modal-header" style="background:linear-gradient(135deg, #2563EB, #3B82F6);color:#fff;flex-shrink:0;">
        <h3 style="color:#fff;display:flex;align-items:center;gap:8px;">
          <span class="material-icons">rate_review</span> Annual Record Review
        </h3>
        <button class="btn-icon" onclick="window.HRModule.closeModal('annual-review')" style="color:#fff;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:20px;overflow-y:auto;flex:1;min-height:0;">
        <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
          Conduct annual MVR / driving record review for <strong>${escapeHtml(emp.name)}</strong> per CFR 391.25
        </div>

        <input type="hidden" id="ar-emp-id" value="${empId}">

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Review Date *</label>
            <input type="date" id="ar-review-date" class="form-control" value="${today}">
          </div>
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Reviewed By *</label>
            ${/* TASK-4838 — RAW attribute interpolation of the signed-in user's Auth
                 displayName. Its twin at :28170 already escapes; this one did not, so a
                 quote in your own display name truncated the prefilled reviewer value and
                 the annual review was signed under a half name. Self-scoped, so never an
                 XSS on anyone else — this is a data-integrity fix. Read back at :33029 via
                 .value (decoded), so escaping cannot corrupt what Firestore receives. */''}
            <input type="text" id="ar-reviewed-by" class="form-control" value="${window.escAttr(window.AppState?.user?.displayName || window.AppState?.user?.email || '')}" placeholder="Reviewer name">
          </div>
          <div class="form-group" style="grid-column:1/-1;">
            <label style="font-size:0.8rem;font-weight:600;">Determination *</label>
            <select id="ar-determination" class="form-control">
              <option value="">Select determination...</option>
              <option value="qualified">Qualified — No disqualifying violations</option>
              <option value="qualified_monitoring">Qualified w/ Monitoring — Minor violations, driver may continue with oversight</option>
              <option value="not_qualified">Not Qualified — Disqualifying violations found</option>
            </select>
          </div>
        </div>

        <div style="margin-top:12px;background:var(--bg-page);border-radius:8px;padding:12px;">
          <div style="font-weight:600;font-size:0.82rem;margin-bottom:8px;">Review Checklist</div>
          <label style="display:flex;align-items:center;gap:8px;font-size:0.8rem;padding:4px 0;cursor:pointer;">
            <input type="checkbox" id="ar-check-mvr"> MVR obtained and reviewed
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:0.8rem;padding:4px 0;cursor:pointer;">
            <input type="checkbox" id="ar-check-psp"> PSP report reviewed (if applicable)
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:0.8rem;padding:4px 0;cursor:pointer;">
            <input type="checkbox" id="ar-check-clearinghouse"> Clearinghouse query completed
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:0.8rem;padding:4px 0;cursor:pointer;">
            <input type="checkbox" id="ar-check-cdl"> CDL status verified (valid, not suspended/revoked)
          </label>
          <label style="display:flex;align-items:center;gap:8px;font-size:0.8rem;padding:4px 0;cursor:pointer;">
            <input type="checkbox" id="ar-check-medical"> Medical card current
          </label>
        </div>

        <div style="display:grid;grid-template-columns:1fr;gap:12px;margin-top:12px;">
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Violations Found</label>
            <textarea id="ar-violations" class="form-control" rows="2" placeholder="List any violations found on MVR (leave blank if none)..."></textarea>
          </div>
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Corrective Action</label>
            <textarea id="ar-corrective-action" class="form-control" rows="2" placeholder="Required corrective action (if any)..."></textarea>
          </div>
          <div class="form-group">
            <label style="font-size:0.8rem;font-weight:600;">Reviewer Signature</label>
            <input type="text" id="ar-signature" class="form-control" placeholder="Type full name as electronic signature">
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:2px;">By typing your name, you certify this review was conducted per CFR 391.25</div>
          </div>
        </div>

        <div style="background:#DBEAFE;border-radius:8px;padding:10px 12px;margin-top:8px;font-size:0.75rem;color:#1E40AF;">
          <strong>CFR 391.25:</strong> Each motor carrier shall at least once every 12 months review the driving record of each driver it employs to determine whether that driver meets minimum requirements for safe driving.
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);display:flex;gap:var(--spacing-sm);justify-content:flex-end;flex-shrink:0;">
        <button class="btn btn-secondary" onclick="window.HRModule.closeModal('annual-review')">Cancel</button>
        <button class="btn" id="btn-save-ar" style="background:#2563EB;color:#fff;" onclick="window.HRModule.saveAnnualReview()">
          <span class="material-icons">save</span> Save Review
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

async function saveAnnualReview() {
  const empId = document.getElementById('ar-emp-id')?.value;
  const reviewDate = document.getElementById('ar-review-date')?.value;
  const reviewedBy = document.getElementById('ar-reviewed-by')?.value?.trim();
  const determination = document.getElementById('ar-determination')?.value;
  const violations = document.getElementById('ar-violations')?.value?.trim() || '';
  const correctiveAction = document.getElementById('ar-corrective-action')?.value?.trim() || '';
  const signature = document.getElementById('ar-signature')?.value?.trim() || '';

  // Checklist
  const checkMvr = document.getElementById('ar-check-mvr')?.checked || false;
  const checkPsp = document.getElementById('ar-check-psp')?.checked || false;
  const checkClearinghouse = document.getElementById('ar-check-clearinghouse')?.checked || false;
  const checkCdl = document.getElementById('ar-check-cdl')?.checked || false;
  const checkMedical = document.getElementById('ar-check-medical')?.checked || false;

  if (!reviewDate) { window.showToast?.('Please enter the review date', 'error'); return; }
  if (!reviewedBy) { window.showToast?.('Please enter the reviewer name', 'error'); return; }
  if (!determination) { window.showToast?.('Please select a determination', 'error'); return; }
  if (!signature) { window.showToast?.('Please sign the review', 'error'); return; }

  const btn = document.getElementById('btn-save-ar');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const tenant = HRState.tenantId;
    const personRef = doc(db, 'tenants', tenant, 'people', empId);
    const personSnap = await getDoc(personRef);
    const existing = personSnap.exists() ? personSnap.data() : {};
    const history = (existing.annualReviewHistory || []).slice(0, 23);

    // Build history entry
    const entry = {
      date: reviewDate,
      reviewedBy,
      determination,
      violations,
      correctiveAction,
      signature,
      checklist: { mvr: checkMvr, psp: checkPsp, clearinghouse: checkClearinghouse, cdl: checkCdl, medical: checkMedical },
      recordedBy: window.AppState?.user?.email || 'unknown',
      recordedAt: new Date().toISOString()
    };
    history.unshift(entry);

    // Next due = +1 year (TASK-1045 — shared single-source helper)
    const nextDue = complianceNextDueOneYear(reviewDate);

    const updateData = {
      annualReviewDate: reviewDate,
      annualReviewDetermination: determination,
      annualReviewedBy: reviewedBy,
      annualReviewNextDue: nextDue,
      annualReviewHistory: history
    };

    await updateDoc(personRef, updateData);

    // Immutable audit trail
    const detLabels = { qualified: 'Qualified', qualified_monitoring: 'Qualified w/ Monitoring', not_qualified: 'Not Qualified' };
    await addDoc(collection(db, 'tenants', tenant, 'complianceTimeline'), {
      personId: empId,
      personName: existing.name || '',
      type: 'annual_review',
      description: `Annual record review — ${detLabels[determination] || determination}`,
      determination,
      reviewDate,
      reviewedBy,
      violations: violations || null,
      correctiveAction: correctiveAction || null,
      signature,
      recordedBy: window.AppState?.user?.email || 'unknown',
      timestamp: Timestamp.now(),
      createdAt: Timestamp.now()
    });

    // Update local state
    const emp = HRState.people.find(p => p.id === empId);
    if (emp) {
      Object.assign(emp, updateData);
    }

    window.logAudit?.('hr', 'ANNUAL_REVIEW_COMPLETED', {
      personId: empId, determination, reviewDate, reviewedBy
    });

    window.showToast?.('Annual review saved', 'success');
    closeModal('annual-review');

    // Refresh the employee detail view
    if (HRState.selectedEmployee?.id === empId) {
      openEmployeeDetail(empId);
    }
  } catch (err) {
    console.error('[HR] Error saving annual review:', err);
    window.showToast?.('Error saving review', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Save Review'; }
  }
}

function switchResumeTab(tab) {
  // Toggle tab content
  document.querySelectorAll('.resume-tab-content').forEach(el => el.style.display = 'none');
  const target = document.getElementById(`resume-tab-${tab}`);
  if (target) target.style.display = '';

  // Toggle tab button styles
  document.querySelectorAll('.resume-tab').forEach(btn => {
    if (btn.dataset.tab === tab) {
      btn.style.background = 'var(--action-primary)';
      btn.style.color = '#fff';
    } else {
      btn.style.background = 'var(--bg-elevated)';
      btn.style.color = 'var(--text-secondary)';
    }
  });
}

// --- Resume ---
function handleResumeFileSelect(e) {
  const file = e.target.files[0];
  if (!file) return;
  const maxSize = 10 * 1024 * 1024; // 10MB
  const allowedExts = ['.pdf', '.doc', '.docx', '.rtf', '.jpg', '.jpeg', '.png'];
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  if (file.size > maxSize) { window.showToast?.('File too large (max 10MB)', 'error'); return; }
  if (!allowedExts.includes(ext)) { window.showToast?.('Unsupported file type. Use PDF, DOC, DOCX, RTF, JPG, or PNG.', 'error'); return; }
  HRState.pendingResumeFile = file;
  const nameEl = document.getElementById('resume-file-name');
  if (nameEl) nameEl.innerHTML = `<span class="material-icons" style="font-size:16px;color:var(--status-success);vertical-align:text-bottom;">check_circle</span> ${escapeHtml(file.name)}`;
}

async function uploadResume(tenantId, candidateId, file) {
  const candidate = HRState.candidates.find(c => c.id === candidateId);
  // Delete old resume if replacing
  if (candidate?.resumeStoragePath) {
    try { await deleteObject(ref(storage, candidate.resumeStoragePath)); } catch (e) { /* ok */ }
  }
  const storagePath = `resumes/${tenantId}/${candidateId}/${Date.now()}_${file.name}`;
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file, { contentType: inferContentType(file) });
  const downloadURL = await getDownloadURL(storageRef);
  await updateDoc(doc(db, 'tenants', tenantId, 'candidates', candidateId), {
    resumeUrl: downloadURL,
    resumeFileName: file.name,
    resumeStoragePath: storagePath,
    // TASK-650 — a doc upload is activity → restart the inactivity clock.
    lastActivityAt: Timestamp.now(),
    expiryWarningSentAt: null,
  });
}

async function removeResume(candidateId) {
  if (!candidateId || !confirm('Remove resume?')) return;
  const candidate = HRState.candidates.find(c => c.id === candidateId);
  try {
    if (candidate?.resumeStoragePath) {
      await deleteObject(ref(storage, candidate.resumeStoragePath));
    }
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'candidates', candidateId), {
      resumeUrl: '',
      resumeFileName: '',
      resumeStoragePath: ''
    });
    window.showToast?.('Resume removed', 'success');
    // Refresh the edit modal if open
    if (HRState.editingCandidateId === candidateId) {
      openEditCandidateModal(candidateId);
    }
  } catch (error) {
    console.error('[HR] Error removing resume:', error);
    window.showToast?.('Error removing resume', 'error');
  }
}

// --- Filtering ---
function filterCandidatesByStatus(status) {
  HRState.candidateFilter.status = status || null;
  // TASK-9293 — the stats pills count every non-archived candidate, so tapping "Hired (16)"
  // must SHOW the 16: follow the stage into its view. In the Archived view the pill filters
  // within archived rows instead.
  if (status && _hrCandidateView() !== 'archived') {
    const target = viewForStageId(status);
    if (target !== _hrCandidateView()) HRState.candidateFilter.view = target;
  }
  const filterSelect = document.getElementById('hiring-status-filter');
  if (filterSelect) filterSelect.value = status || '';
  renderCandidateStatsBar();
  renderCandidatesList();
}

let _searchDebounce = null;
function searchCandidates(query) {
  clearTimeout(_searchDebounce);
  _searchDebounce = setTimeout(() => {
    HRState.candidateFilter.search = query;
    renderCandidatesList();
  }, 250);
}

function clearCandidateFilters() {
  // TASK-9293 — "Clear Filters" clears status + search and returns to the Active view; the
  // user's sort choice survives (it is a preference, not a filter).
  HRState.candidateFilter = { status: null, search: '', view: 'active', sort: HRState.candidateFilter.sort || 'date' };
  const searchInput = document.getElementById('hiring-search');
  if (searchInput) searchInput.value = '';
  const filterSelect = document.getElementById('hiring-status-filter');
  if (filterSelect) filterSelect.value = '';
  renderCandidatesList();
}

// --- Utilities ---
function toggleRejectionReason() {
  const status = document.getElementById('candidate-status')?.value;
  const group = document.getElementById('rejection-reason-group');
  if (group) group.style.display = status === 'rejected' ? 'block' : 'none';
  // TASK-1155 — status change can flip the CTA (Save ↔ Start Adverse Action).
  syncCandidateFormDecisionCta();
}

function checkForDuplicates(name, phone, email, excludeId) {
  const dupes = HRState.candidates.filter(c => {
    if (excludeId && c.id === excludeId) return false;
    if (name && c.name && c.name.toLowerCase() === name.toLowerCase()) return true;
    if (phone && c.phone && c.phone === phone) return true;
    if (email && c.email && c.email.toLowerCase() === email.toLowerCase()) return true;
    return false;
  });
  if (dupes.length > 0) {
    window.showToast?.(`Possible duplicate: ${dupes.map(d => d.name).join(', ')}`, 'info');
  }
}

function formatCandidateStatus(status) {
  const stage = PIPELINE_STAGES.find(s => s.id === status);
  return stage ? stage.label : status || '—';
}

function formatCandidateDate(ts) {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateForInput(ts) {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// TASK-862 — delegates to shared window.Fmt.phone (sharedFormat.js); byte-identical
// output (same 10-/11-digit cases, same raw-input fallthrough).
function formatPhoneDisplay(phone) {
  return window.Fmt.phone(phone);
}

// ============================================
// TASK-1039 (2026-07-09) — "Sync Roster to QuickBooks" REMOVED.
// The old TASK-761 syncRosterToQB() + _qbSyncConfirm() UI called the server
// callable qboPushEmployees, which writes the QBO ACCOUNTING Employee entity —
// invisible to QBO Payroll — while reporting "synced". Intuit exposes no public
// payroll-write API, so the feature was misleading and Chris killed it entirely.
// The server callable qboPushEmployees still exists (orphaned, no UI caller).
// The supported path is the TASK-843 exportQboPayrollCsv() export below.
// Re-introduce roster→payroll sync only when embedded payroll (TASK-850) ships.
// ============================================

// ============================================
// PAYROLL ROSTER EXPORT
// ============================================

function toggleRosterExportMenu(e) {
  e?.stopPropagation();
  const menu = document.getElementById('roster-export-menu');
  if (!menu) return;
  const isOpen = menu.style.display !== 'none';
  menu.style.display = isOpen ? 'none' : 'block';
  if (!isOpen) {
    const close = (ev) => {
      if (!menu.contains(ev.target)) {
        menu.style.display = 'none';
        document.removeEventListener('click', close);
      }
    };
    setTimeout(() => document.addEventListener('click', close), 0);
  }
}

function escapeCSVField(val) {
  if (val == null) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

// Formula-injection-safe CSV cell. A roster value like "=HYPERLINK(...)" would
// execute when Lacey opens the file in Excel — prefix any value that starts with
// = + - @ (or a leading tab/CR) with a single quote, THEN apply normal escaping.
// Use this (not escapeCSVField) for any export that leaves DispatchDeck.
function csvSafeCell(val) {
  let s = (val == null) ? '' : String(val);
  if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
  if (/[",\n\r]/.test(s)) s = '"' + s.replace(/"/g, '""') + '"';
  return s;
}

// TASK-1415 — every money string carries its own period. The four hr* money
// fields have four DIFFERENT periodicities (per hour / per year / per day /
// per paycheck) and rendered as bare dollar amounts side by side; a system of
// record whose money fields don't state their period mis-enters into any
// external payroll (Gusto asks for the period explicitly). Piece rate already
// carries its unit.
// TASK-1481 — comp resolver: peopleComp doc first (window.PersonComp, loaded
// once via clock.js's side-effect import — Pitfall #1, never import it here),
// legacy person-doc fields as fallback. Every hr.js surface that READS a comp
// field (wage/per-diem/insurance/deductions/FLSA) must resolve through this;
// reading emp.hrHourlyRate off a raw HRState.people entry is how a read site
// silently pays $0 after the TASK-1481 strip step.
function _hrComp(person) {
  return (window.PersonComp && person) ? window.PersonComp.effective(person) : person;
}

// ===========================================================================
// TASK-4865 — fuel-card PIN: REVEAL-ON-DEMAND.
// ===========================================================================
// The PIN used to render in the clear in the Employee Details header, off a
// field that lived on the BROADCAST `people` doc. It now lives in the
// admin-gated `peopleCard` vault (public/src/personCard.js) and is fetched
// one person at a time, only when someone asks for it.
//
// 🔴 DENIED ≠ ABSENT is the whole safety story in both handlers below. A
// failed/denied load must NOT leave an empty box that a later Save then
// persists as "cleared" — so `revealed` is stamped ONLY when the value is
// actually known (vault read succeeded, or the legacy person-doc value is
// still there pre-migration). Anything else toasts and leaves the box in its
// "unchanged" state.
let _hrPinRevealTimer = null;

function _hrPinKnown(res, person) {
  if (res && res.ok) return true;
  // 'stale-tenant' means the accessor dropped the result because the tenant
  // changed mid-flight — the person on screen is not the person we asked
  // about, so "known" is never the right answer.
  if (res && res.code === 'stale-tenant') return false;
  return !!(window.PersonCard && window.PersonCard.personHasOwnPin(person));
}

// TASK-9752 — every PIN view goes through the server-authored revealFuelCardPin
// callable: it reads the vault with the Admin SDK and commits a
// FUEL_CARD_PIN_VIEWED auditLog row (who / whose / when / which screen) in the
// same transaction that hands the PIN back — so a view can't happen without its
// row. Nothing is cached client-side: a second Show is a second call and a
// second row. Demo (shared sandbox, fake people, no members doc to gate on)
// keeps the old local vault read — there is no real credential there to log.
// Returns { ok, code, pin, known }: `known` is true only when the server
// answered (pin may then be null = "no PIN on file").
async function _hrFetchFuelCardPin(empId, screen) {
  const tid = HRState.tenantId;
  if (!tid) return { ok: false, code: 'no-tenant', pin: null, known: false };
  if (tid === 'demo') {
    const res = await (window.PersonCard?.load?.(tid, empId) ?? { ok: false, code: 'accessor-missing' });
    const person = HRState.people.find(p => p.id === empId) || { id: empId };
    if (!_hrPinKnown(res, person)) return { ok: false, code: res.code, pin: null, known: false };
    return { ok: true, code: null, pin: window.PersonCard?.pin?.(person) || null, known: true };
  }
  try {
    const fn = httpsCallable(functions, 'revealFuelCardPin');
    const out = await fn({ tenantId: tid, personId: empId, screen });
    // Tenant switched while in flight — the person on screen is not the one we asked about.
    if (HRState.tenantId !== tid) return { ok: false, code: 'stale-tenant', pin: null, known: false };
    const d = (out && out.data) || {};
    return { ok: true, code: null, pin: d.pin ? String(d.pin) : null, known: true };
  } catch (err) {
    const raw = String((err && err.code) || 'unknown');
    return { ok: false, code: raw.replace(/^functions\//, ''), message: (err && err.message) || '', pin: null, known: false };
  }
}

function _hrPinFetchCopy(res) {
  if (res.code === 'permission-denied') return "You don't have access to the fuel-card PIN — the owner or someone with the HR module can show it.";
  if (res.code === 'resource-exhausted') return res.message || 'Too many PIN views — try again in a few minutes.';
  if (res.code === 'unavailable' && res.message) return res.message;
  return "Couldn't load the fuel-card PIN. Check your connection and try again.";
}

// Employee Details header — swaps the "Show" button for the value.
async function revealFuelCardPin(empId) {
  const slot = document.getElementById('hr-fuelpin-slot');
  if (!slot || slot.dataset.person !== String(empId)) return;
  const _restore = slot.innerHTML;
  slot.innerHTML = '&middot; PIN <span style="opacity:0.7;">loading&hellip;</span>';
  const startTid = HRState.tenantId;
  const res = await _hrFetchFuelCardPin(empId, 'hr-employee-header');
  // COD Code Reviewer: render ONLY into the exact node we started from, under
  // the same tenant. A re-render (navigate away and back, or tenant A→B→A
  // mid-call) makes a new node, so a stale answer is dropped, never shown.
  const live = document.getElementById('hr-fuelpin-slot');
  const sameView = live === slot && slot.isConnected && HRState.tenantId === startTid;
  if (!res.known) {
    if (sameView) slot.innerHTML = _restore;
    if (res.code !== 'stale-tenant') window.showToast?.(_hrPinFetchCopy(res), 'warning');
    return;
  }
  if (!sameView) return;
  const pin = res.pin;
  slot.innerHTML = pin
    ? `&middot; PIN: <strong>${escapeHtml(pin)}</strong> <span style="opacity:0.7;font-size:0.75em;" title="Every PIN view is recorded in the activity log">(view logged)</span>`
    : '&middot; <span style="opacity:0.7;">no PIN on file</span>';
  // COD UX lane — auto-hide after 30s. HR runs on a shared office desk; a
  // revealed credential left on screen while someone walks away is the same
  // shoulder-surfing exposure the default-render had, just delayed.
  if (pin) {
    clearTimeout(_hrPinRevealTimer);
    _hrPinRevealTimer = setTimeout(() => {
      if (slot.isConnected && slot.dataset.person === String(empId)) slot.innerHTML = _restore;
    }, 30000);
  }
}

// Employment edit form — loads the value INTO the box so it can be read or
// changed. Only after this (or typing) does the box mean anything on save.
async function revealFuelCardPinInput(empId) {
  const el = document.getElementById('emp-fuel-card-pin');
  const btn = document.getElementById('btn-reveal-fuel-pin');
  if (!el) return;
  if (btn) { btn.disabled = true; btn.textContent = '…'; }
  const startTid = HRState.tenantId;
  const res = await _hrFetchFuelCardPin(empId, 'hr-employment-edit');
  if (btn) { btn.disabled = false; btn.textContent = 'Show'; }
  if (!res.known) {
    // Box stays "unchanged" — a save now leaves the stored PIN alone.
    if (res.code !== 'stale-tenant') {
      window.showToast?.(`${_hrPinFetchCopy(res)} Leaving the box empty keeps the stored PIN exactly as it is.`, 'warning');
    }
    return;
  }
  // The form may have been closed or re-rendered (other person / tenant) mid-call:
  // only fill the exact box we started from, under the same tenant.
  if (!el.isConnected || HRState.tenantId !== startTid) return;
  el.value = res.pin || '';
  el.dataset.revealed = '1';
  el.placeholder = 'PIN';
  if (btn) btn.textContent = 'Shown';
}

function getPayrollWage(rawEmp) {
  const emp = _hrComp(rawEmp); // TASK-1481 — comp-first resolution
  if (emp.payType === 'pieceRate' && emp.payRate) {
    const unitLabel = (emp.payRateUnit || 'Each').replace(/^Per\s*/i, '/').toLowerCase();
    return `$${parseFloat(emp.payRate).toFixed(2)}${unitLabel}`;
  }
  if (emp.hrEmploymentType === 'salary' && emp.hrAnnualSalary) {
    return `$${Number(emp.hrAnnualSalary).toLocaleString()} / yr`;
  }
  if (emp.hrEmploymentType === 'salary') return 'Salary';
  if (emp.hrHourlyRate) return `$${parseFloat(emp.hrHourlyRate).toFixed(2)} / hr`;
  return '';
}

function splitName(fullName) {
  const parts = (fullName || '').trim().split(/\s+/);
  return { first: parts[0] || '', last: parts.slice(1).join(' ') || '' };
}

async function exportPayrollRoster() {
  // W-9 status lives in admin-only secure subcollection — bulk-load before
  // building the CSV.
  await loadAllSecureTax();

  // Get active non-terminated employees
  const allPeople = HRState.people || [];
  const active = allPeople.filter(p => p.active !== false && !p.terminated).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const terminated = allPeople.filter(p => p.terminated).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (active.length === 0 && terminated.length === 0) {
    window.showToast?.('No employees to export', 'error');
    return;
  }

  const header = ['FIRST', 'LAST', 'PHONE', 'ADDRESS', 'EMAIL', 'WAGE', 'Per Diem', 'Health Insurance', 'Deductions', 'HIRE', 'FLSA STATUS', 'DL Doc', 'Med Card Doc', 'W-9', 'CDL Expiry', 'Med Card Expiry', 'DL Expiry'];
  const rows = [header.join(',')];

  const buildRow = (rawEmp) => {
    const emp = _hrComp(rawEmp); // TASK-1481 — WAGE/Per Diem/Health/Deductions/FLSA cells read comp
    const { first, last } = splitName(emp.name);
    const hireDate = emp.hireDate ? formatDate(emp.hireDate?.toDate?.() || emp.hireDate) : '';
    return [
      escapeCSVField(first),
      escapeCSVField(last),
      escapeCSVField(formatPhoneDisplay(emp.phone)),
      escapeCSVField(emp.address || ''),
      escapeCSVField(emp.personalEmail || emp.email || ''),
      escapeCSVField(getPayrollWage(emp)),
      // TASK-1415 — absent values stay omitted (never "$0.00 / day").
      escapeCSVField(emp.hrPerDiem ? `$${parseFloat(emp.hrPerDiem).toFixed(2)} / day` : ''),
      escapeCSVField(emp.hrBCBS ? `$${parseFloat(emp.hrBCBS).toFixed(2)} / paycheck` : ''),
      escapeCSVField(emp.hrDeductions || ''),
      escapeCSVField(hireDate),
      escapeCSVField(flsaStatusShortLabel(emp)),
      (emp.dlDocumentUrl || emp.dlDocumentPath) ? 'X' : '',
      getEffectiveMedCardDoc(emp) ? 'X' : '',
      emp.secureTax?.w9Collected ? 'X' : '',
      escapeCSVField(emp.cdlExpiryDate || ''),
      escapeCSVField(emp.medCardExpiryDate || ''),
      escapeCSVField(emp.dlExpiryDate || '')
    ].join(',');
  };

  active.forEach(emp => rows.push(buildRow(emp)));

  if (terminated.length > 0) {
    rows.push(''); // blank row
    rows.push('NO LONGER EMPLOYED');
    terminated.forEach(emp => {
      const row = buildRow(emp);
      rows.push(row);
    });
  }

  // TASK-1000 \u2014 FLSA/OT disclaimer footer (copy-only; mirrors the "not legal
  // advice" pattern used in dotAdvisor.js/crm.js). The classification UI already
  // explains the exempt/non-exempt test; this frames the exported numbers for
  // the accountant/payroll provider who receives the file.
  rows.push('');
  rows.push(escapeCSVField('OT is calculated per each worker\u2019s configured FLSA status and your OT threshold. Verify classifications; this is not tax or legal advice.'));

  const dateStr = new Date().toISOString().split('T')[0];
  const blob = new Blob(['\uFEFF' + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `payroll-roster-${dateStr}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);

  // Close menu
  const menu = document.getElementById('roster-export-menu');
  if (menu) menu.style.display = 'none';

  window.showToast?.('Payroll roster exported', 'success');
}

// TASK-843 — Export the roster as a QuickBooks Online Payroll "Import Employees"
// CSV. QBO's Accounting-API Employee entity (the old "Sync to QB" button) does
// NOT reach the Payroll roster when QBO Payroll is on; the supported bulk path is
// QBO's native Import Employees (Payroll → Employees → Add employee ▾ → Import
// Employees), which does add AND update. So we hand Lacey a file in that template.
//
// ⚠️ HEADER ROW: these column names are the documented US QBO template; confirm
// them byte-for-byte against the empty template downloaded from the tenant's own
// QBO (Export Employees → Empty template) before relying on the import — QBO
// matches on exact headers. The mapping below is the contract; tweak QBO_COLUMNS
// if the downloaded template differs.
async function exportQboPayrollCsv() {
  // SSN lives in the admin-gated peopleTax subcollection. Only include it when the
  // viewer is allowed to see secure tax (founder + hr-directory, TASK-815).
  const includeSsn = _hrCanSeeSecureTax();
  if (includeSsn) await loadAllSecureTax();

  // W-2 employees only — active, not terminated, classified W-2 (window.WorkerClass).
  // Pushing a 1099/owner-operator into a payroll roster is a DOL/IRS
  // misclassification assertion, so they are excluded.
  // TASK-9178 — resolved by the ONE rule (window.WorkerClass), the same one the
  // Gusto import and the QBO sync use; unclassified people stay out (explicit
  // W-2 only), exactly as before.
  const _wc = window.WorkerClass;
  if (!_wc) { window.showToast?.('Worker classification rule not loaded — reload the page and try again.', 'error'); return; }
  const employees = (HRState.people || [])
    .filter(p => p.active !== false && !p.terminated && _wc.resolve(p).isW2)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (employees.length === 0) {
    window.showToast?.('No active W-2 employees to export. (1099 contractors are not included in a payroll roster.)', 'error');
    return;
  }

  // Legal name → first/middle/last (W-2 must match legal name; display name fallback).
  const splitName3 = (full) => {
    const parts = String(full || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return { first: '', middle: '', last: '' };
    if (parts.length === 1) return { first: parts[0], middle: '', last: '' };
    if (parts.length === 2) return { first: parts[0], middle: '', last: parts[1] };
    return { first: parts[0], middle: parts.slice(1, -1).join(' '), last: parts[parts.length - 1] };
  };
  // QBO wants MM/DD/YYYY; tolerate Firestore Timestamp | Date | ISO string.
  const toMDY = (val) => {
    if (!val) return '';
    let d = (val && typeof val.toDate === 'function') ? val.toDate() : (val instanceof Date ? val : new Date(val));
    if (!d || isNaN(d.getTime())) return '';
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  };
  const fmtSsn = (s) => {
    if (!s) return '';
    const digits = String(s).replace(/\D/g, '');
    return digits.length === 9 ? `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}` : String(s);
  };

  // Column definition = the contract with QBO's importer. {header, value}.
  const QBO_COLUMNS = [
    { header: 'First Name',  value: e => splitName3(e.legalName || e.name).first },
    { header: 'Middle Name', value: e => splitName3(e.legalName || e.name).middle },
    { header: 'Last Name',   value: e => splitName3(e.legalName || e.name).last },
    { header: 'Social Security Number', value: e => includeSsn ? fmtSsn(e.secureTax?.ssn) : '' },
    // TASK-9359 — canonical `dob` (overlaid from the peopleIdentity vault; the
    // legacy dateOfBirth alias is folded into it). dob FIRST so a stale alias
    // can never outrank the vault value.
    { header: 'Date of Birth', value: e => toMDY(e.dob || e.dateOfBirth) },
    { header: 'Hire Date',     value: e => toMDY(e.hireDate) },
    { header: 'Address',       value: e => e.address || '' },
    { header: 'City',          value: e => e.city || '' },
    { header: 'State',         value: e => e.state || '' },
    { header: 'ZIP Code',      value: e => e.zip || e.postalCode || '' },
    { header: 'Phone',         value: e => e.phone || '' },
    { header: 'Email',         value: e => e.personalEmail || e.email || '' },
  ];

  const rows = [QBO_COLUMNS.map(c => csvSafeCell(c.header)).join(',')];
  employees.forEach(e => {
    rows.push(QBO_COLUMNS.map(c => csvSafeCell(c.value(e))).join(','));
  });

  const dateStr = new Date().toISOString().split('T')[0];
  const blob = new Blob(['﻿' + rows.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `qb-payroll-import-${dateStr}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);

  // PII-safe: count only, never row contents.
  console.log(`[HR] QBO payroll CSV exported: ${employees.length} employees, SSN ${includeSsn ? 'included' : 'omitted'}`);

  const menu = document.getElementById('roster-export-menu');
  if (menu) menu.style.display = 'none';

  window.showToast?.(`Roster exported (${employees.length} employees) — open it alongside QuickBooks to copy each employee in.`, 'success');
}

async function printPayrollRoster() {
  await loadAllSecureTax();

  const allPeople = HRState.people || [];
  const active = allPeople.filter(p => p.active !== false && !p.terminated).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const terminated = allPeople.filter(p => p.terminated).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  if (active.length === 0 && terminated.length === 0) {
    window.showToast?.('No employees to print', 'error');
    return;
  }

  // Close menu
  const menu = document.getElementById('roster-export-menu');
  if (menu) menu.style.display = 'none';

  const companyName = window.AppState?.companyName || 'Company';
  const dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const buildPrintRow = (rawEmp, showTermInfo = false) => {
    const emp = _hrComp(rawEmp); // TASK-1481 — wage/per-diem/insurance/FLSA cells read comp
    const { first, last } = splitName(emp.name);
    const hireDate = emp.hireDate ? formatDate(emp.hireDate?.toDate?.() || emp.hireDate) : '';
    const phone = formatPhoneDisplay(emp.phone);
    const wage = getPayrollWage(emp);
    const perDiem = emp.hrPerDiem ? `$${parseFloat(emp.hrPerDiem).toFixed(2)} / day` : '';
    const bcbs = emp.hrBCBS ? `$${parseFloat(emp.hrBCBS).toFixed(2)} / paycheck` : '';

    const today = new Date().toISOString().split('T')[0];
    const expiryCell = (date) => {
      if (!date) return '<td></td>';
      const isExpired = date < today;
      return `<td style="${isExpired ? 'color:#dc2626;font-weight:600;' : ''}">${escapeHtml(date)}${isExpired ? ' ⚠' : ''}</td>`;
    };

    return `<tr${showTermInfo ? ' style="color:#94a3b8;"' : ''}>
      <td style="font-weight:600;">${escapeHtml(first)}</td>
      <td>${escapeHtml(last)}</td>
      <td>${escapeHtml(phone)}</td>
      <td>${escapeHtml(emp.address || '')}</td>
      <td>${escapeHtml(emp.personalEmail || emp.email || '')}</td>
      <td>${escapeHtml(wage)}</td>
      <td>${perDiem}</td>
      <td>${bcbs}</td>
      <td>${escapeHtml(emp.hrDeductions || '')}</td>
      <td>${hireDate}</td>
      <td>${flsaStatusShortLabel(emp, '')}</td>
      <td style="text-align:center;">${(emp.dlDocumentUrl || emp.dlDocumentPath) ? '✓' : ''}</td>
      <td style="text-align:center;">${getEffectiveMedCardDoc(emp) ? '✓' : ''}</td>
      <td style="text-align:center;">${emp.secureTax?.w9Collected ? '✓' : ''}</td>
      ${expiryCell(emp.cdlExpiryDate)}
      ${window.DqMatrix?.getApplicableDqItems?.(emp)?.items?.medCard === false ? `<td>${escapeHtml(emp.medCardExpiryDate)}</td>` : expiryCell(emp.medCardExpiryDate)}
      ${expiryCell(emp.dlExpiryDate)}
    </tr>`;
  };

  const activeRows = active.map(emp => buildPrintRow(emp)).join('');
  const terminatedRows = terminated.length > 0 ? `
    <tr><td colspan="17" style="padding:16px 0 4px;border:none;">&nbsp;</td></tr>
    <tr><td colspan="17" style="font-weight:700;color:#dc2626;padding:8px 6px;border-bottom:2px solid #dc2626;background:#fef2f2;">NO LONGER EMPLOYED</td></tr>
    ${terminated.map(emp => buildPrintRow(emp, true)).join('')}
  ` : '';

  const totalCount = active.length + terminated.length;

  const printHtml = `<!DOCTYPE html>
<html><head><title>Payroll Roster - ${escapeHtml(companyName)}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 20px; color: #1e293b; }
  h1 { font-size: 1.4rem; margin: 0 0 4px; }
  .subtitle { color: #64748b; font-size: 0.9rem; margin-bottom: 16px; }
  table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
  th { background: #f1f5f9; color: #334155; font-weight: 600; text-align: left; padding: 8px 6px; border-bottom: 2px solid #cbd5e1; white-space: nowrap; }
  td { padding: 6px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  tr:nth-child(even) td { background: #f8fafc; }
  .footer { margin-top: 16px; font-size: 0.75rem; color: #94a3b8; text-align: right; }
  .print-btn { position: fixed; top: 12px; right: 12px; padding: 8px 20px; background: #3b82f6; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 0.9rem; }
  .print-btn:hover { background: #2563eb; }
  @media print { .print-btn { display: none; } body { padding: 10px; } table { font-size: 0.7rem; } }
</style></head>
<body>
  <button class="print-btn" onclick="window.print()">Print / Save PDF</button>
  <h1>${escapeHtml(companyName)}</h1>
  <div class="subtitle">Payroll Roster &bull; ${dateStr} &bull; ${active.length} active employee${active.length !== 1 ? 's' : ''}${terminated.length > 0 ? `, ${terminated.length} former` : ''}</div>
  <table>
    <thead><tr>
      <th>FIRST</th><th>LAST</th><th>PHONE</th><th>ADDRESS</th><th>EMAIL</th><th>WAGE</th><th>Per Diem</th><th>Health Ins</th><th>Deductions</th><th>HIRE</th><th>FLSA</th><th>DL Doc</th><th>Med Card</th><th>W-9</th><th>CDL Exp</th><th>Med Exp</th><th>DL Exp</th>
    </tr></thead>
    <tbody>${activeRows}${terminatedRows}</tbody>
  </table>
  <div class="footer" style="text-align:left;margin-top:12px;">OT is calculated per each worker’s configured FLSA status and your OT threshold. Verify classifications; this is not tax or legal advice.</div>
  <div class="footer">Generated from DispatchDeck &bull; ${dateStr}</div>
</body></html>`;

  const w = window.open('', '_blank');
  if (w) {
    w.document.write(printHtml);
    w.document.close();
  }
}

// ============================================
// ISNET HELPERS
// ============================================

function splitNameISNet(fullName) {
  if (!fullName) return { first: '', last: '' };
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  const last = parts.pop();
  return { first: parts.join(' '), last };
}

function getISNetJobTitle(emp) {
  return getPositionTitle(emp.hrPosition) || '';
}

// TASK-538 — humanize a role/crewType slug for display only (never mutates p.role).
// Reuses the canonical crewTypes labeler (window.getTypeLabel) used by dispatch /
// maintenance / pretrip pickers; falls back to a small map + Title-Case so unknown
// slugs (cdl_driver → "Cdl Driver" → "CDL Driver") never render the raw enum.
const _HR_ROLE_LABELS = {
  cdl_driver: 'CDL Driver', non_cdl_driver: 'Non-CDL Driver', laborer: 'Laborer',
  admin: 'Admin', owner: 'Owner', dispatcher: 'Dispatcher', mechanic: 'Mechanic',
  office: 'Office', foreman: 'Foreman', supervisor: 'Supervisor', operator: 'Operator'
};
function _hrRoleLabel(slug) {
  if (!slug) return '';
  const raw = String(slug).trim();
  if (!raw) return '';
  if (window.getTypeLabel) {
    // roster.js getTypeLabel(slug, kind) — returns the configured label if the
    // slug is a known crewType; otherwise its own title-case fallback.
    const lbl = window.getTypeLabel(raw, 'crewTypes');
    if (lbl && lbl !== raw) return lbl;
  }
  const key = raw.toLowerCase();
  if (_HR_ROLE_LABELS[key]) return _HR_ROLE_LABELS[key];
  // Title-Case fallback for unknown slugs (snake_case or kebab-case → words).
  return raw.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatDateShort(dateStr) {
  if (!dateStr) return '';
  // TASK-541 — date-only strings (YYYY-MM-DD) must parse as LOCAL, not UTC.
  // `new Date('2026-05-04')` parses as UTC midnight → renders the PRIOR day in
  // any negative-UTC tz (Central) → DOB/DL/med-card showed −1 day vs the raw
  // <input type=date> in the Edit modal. Only date-only strings get this branch;
  // full ISO timestamps / datetime strings / Date objects stay on the existing
  // path (force-parsing a full timestamp as local would mangle it).
  let d;
  if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, day] = dateStr.split('-').map(Number);
    d = new Date(y, m - 1, day);
    // Reject impossible dates (e.g. 2023-02-30 rolls over to Mar 2 in JS) —
    // round-trip check so they fall back to the raw string like the old path.
    if (d.getFullYear() !== y || d.getMonth() !== m - 1 || d.getDate() !== day) return dateStr;
  } else {
    d = new Date(dateStr);
  }
  if (isNaN(d.getTime())) return escapeHtml(dateStr);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
}

// TASK-513 HOTFIX — local copy of compliance.js daysUntilExpiry (compliance.js:267).
// computeRequiredTrainingState (Safety Roster) called daysUntilExpiry but it's
// a compliance.js function NOT in hr.js scope → ReferenceError threw on EVERY
// HR employee Compliance-tab open (buildComplianceTabHTML). hr.js already keeps
// local copies of formatDateShort/splitNameISNet (no cross-module ?v= import —
// Pitfall #1), so add this one the same way. Identical implementation.
function daysUntilExpiry(dateStr) {
  if (!dateStr) return null;
  // TASK-983/541 — a bare YYYY-MM-DD parses as UTC midnight → the day-diff comes
  // out one day early in CT (compliance expiry flips a day too soon). Local-parse
  // date-only strings; full timestamps / Date objects stay on the existing path.
  const d = (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr))
    ? new Date(dateStr + 'T00:00:00')
    : new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
}

// TASK-9531 — Employee Profile shows every identifier in one place, sensitive numbers
// masked with an eye to reveal (Chris 2026-10-06). `value` is what this viewer already
// holds: the SSN comes from emp.secureTax (the admin-gated peopleTax read, only for a
// _hrCanSeeSecureTax viewer), the licence number from the peopleIdentity overlay. The
// markup carries only the masked text; window.SensitiveValue writes the full value with
// textContent on click. Same row shape as buildComplianceTabHTML's row().
//
// SSN (integration COD, Code Reviewer 🔴): the eye NEVER shows the in-memory value.
// Every reveal goes through the server-authored revealPersonSsn callable, which reads
// the peopleTax vault and writes a fail-closed SSN_REVEALED audit row first — the
// same trail as the partner person.revealSsn action and revealCandidateSsn. Only the
// mask is built from the held value. Demo (fake data, no members doc) reveals locally.
function _hrSensitiveRow(label, value, kind, personId) {
  const sv = window.SensitiveValue;
  const v = value == null ? '' : String(value).trim();
  let inner;
  if (!v) inner = '—';
  else if (!sv) inner = escapeHtml(kind === 'ssn' ? maskSSN(v) : '••••'); // module failed to load: never the full value
  else if (kind === 'ssn' && HRState.tenantId !== 'demo') {
    const tid = HRState.tenantId;
    inner = personId
      ? (sv.html({ masked: sv.maskSsn(v), kind: 'ssn', noun: 'SSN (recorded in the audit log)', fetchFull: () => _hrRevealPersonSsn(tid, personId) }) || '—')
      : escapeHtml(sv.maskSsn(v));
  }
  else inner = sv.html({ full: v, kind, noun: label === 'SSN' ? 'SSN' : label.toLowerCase() }) || '—';
  return `
    <div style="display:flex;justify-content:space-between;align-items:center;padding:6px 0;border-bottom:1px solid var(--border-light);">
      <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;">${escapeHtml(label)}</span>
      <span style="font-size:0.8rem;color:var(--text-primary);font-weight:500;">${inner}</span>
    </div>`;
}
// Audited full-SSN fetch for the profile eye. Returns the SSN, or null (the user is
// told why). Never re-uses a value across reveals — SensitiveValue drops it on hide.
async function _hrRevealPersonSsn(tid, personId) {
  if (!tid || HRState.tenantId !== tid) return null; // rendered for another company
  try {
    const fn = httpsCallable(functions, 'revealPersonSsn');
    const res = await fn({ tenantId: tid, personId });
    if (HRState.tenantId !== tid) return null;
    return (res && res.data && res.data.ssn) ? String(res.data.ssn) : null;
  } catch (err) {
    const code = String((err && err.code) || '');
    if (code === 'functions/failed-precondition') {
      window.showToast?.((err && err.message) || 'No full SSN is on file for this person.', 'info');
    } else if (code === 'functions/permission-denied') {
      window.showToast?.('Revealing an SSN needs the HR Directory module.', 'error');
    } else {
      console.error('[HR] revealPersonSsn', code || err);
      window.showToast?.((err && err.message) || 'Could not reveal the SSN — try again.', 'error');
    }
    return null;
  }
}
// One display line for the home address (HR edit form's free-text `address`, plus any
// imported city / state / zip the line does not already contain).
function _hrCurrentAddress(emp) {
  const line = String((emp && emp.address) || '').trim();
  const tail = [emp && emp.city, emp && emp.state, emp && (emp.zip || emp.postalCode)]
    .map(x => String(x || '').trim()).filter(Boolean)
    .filter(part => !line.toLowerCase().includes(part.toLowerCase()));
  return [line, tail.join(', ')].filter(Boolean).join(', ');
}

function maskSSN(ssn) {
  if (!ssn || ssn.length < 4) return ssn || '';
  return `***-**-${ssn.replace(/\D/g, '').slice(-4)}`;
}

// ============================================
// SECURE TAX COLLECTION (peopleTax)
// ============================================
// Tax PII (SSN, EIN, business name, tax address, W9) lives in
// tenants/{t}/peopleTax/{personId}. Firestore rules restrict it to admins, so
// it is NOT included in the broadcast people-collection snapshots that ship
// to driver browsers via clock.js / jobboard.js / etc. Top-level collection
// (rather than subcollection) so bulk loads for print/export are a single
// query. Lazy-loaded onto emp.secureTax when an admin opens an employee
// detail tab or runs a bulk export.
// hrW4Exempt (W-4 federal income-tax withholding election) is admin-only by IRS
// Pub. 15 §9 — "W-4 information is confidential; use only for income tax
// withholding." It must NOT ride the broadcast people doc (drivers/dispatchers/
// compliance partners can read those), so it lives here next to w9Collected.
const SECURE_TAX_FIELDS = ['ssn', 'ssnHash', 'ein', 'tinType', 'businessName', 'taxStreet', 'taxCity', 'taxState', 'taxZip', 'w9Collected', 'w9CollectedAt', 'hrW4Exempt'];

async function loadSecureTax(empId) {
  if (!empId) return {};
  const tenant = HRState.tenantId;
  try {
    const snap = await getDoc(doc(db, 'tenants', tenant, 'peopleTax', empId));
    return snap.exists() ? snap.data() : {};
  } catch (e) {
    console.warn('[HR] loadSecureTax failed', empId, e?.message || e);
    return {};
  }
}

async function ensureSecureTax(emp) {
  if (!emp) return null;
  if (emp.secureTax !== undefined) return emp;
  emp.secureTax = await loadSecureTax(emp.id);
  return emp;
}

async function saveSecureTax(empId, fields) {
  const tenant = HRState.tenantId;
  await setDoc(doc(db, 'tenants', tenant, 'peopleTax', empId), fields, { merge: true });
  const emp = HRState.people.find(p => p.id === empId);
  if (emp) emp.secureTax = { ...(emp.secureTax || {}), ...fields };
}

async function loadAllSecureTax() {
  const tenant = HRState.tenantId;
  try {
    const snap = await getDocs(collection(db, 'tenants', tenant, 'peopleTax'));
    const byId = new Map();
    snap.forEach(doc => byId.set(doc.id, doc.data()));
    HRState.people.forEach(p => {
      if (p.secureTax === undefined) p.secureTax = byId.get(p.id) || {};
    });
  } catch (e) {
    console.warn('[HR] loadAllSecureTax failed', e?.message || e);
    HRState.people.forEach(p => { if (p.secureTax === undefined) p.secureTax = {}; });
  }
}

// ============================================
// BACKGROUND CHECKS (peopleBackgroundChecks)
// ============================================
// Admin-only collection — drivers/dispatchers cannot read at the rule layer.
// Same lazy-load pattern as secureTax.

async function loadBgCheck(empId) {
  if (!empId) return {};
  const tenant = HRState.tenantId;
  try {
    const snap = await getDoc(doc(db, 'tenants', tenant, 'peopleBackgroundChecks', empId));
    return snap.exists() ? snap.data() : {};
  } catch (e) {
    console.warn('[HR] loadBgCheck failed', empId, e?.message || e);
    return {};
  }
}

// TASK-9235 — a PERSON's background check. Hiring writes a reference on
// peopleBackgroundChecks/{personId} (legacy hires: a frozen copy); the report and
// its decision live on the linked applicant record. Read both and let the shared
// picker choose: the person's own record when it owns activity (a roster re-pull,
// a separation AA), else the linked applicant record. The returned object carries
// `_bgRecordId` — the id every decision / report-viewer call must target — and
// `_bgCopyDecision` when a legacy copy holds a decision the applicant record lacks.
// Link = people.candidateId, cross-checked against the candidate's personId when
// the candidate is loaded (a mismatch → no link). HR can read both records; the
// server re-verifies the two-way link on every decision and report open.
async function loadPersonBgCheck(emp) {
  if (!emp || !emp.id) return {};
  const own = await loadBgCheck(emp.id);
  let linkedId = (typeof emp.candidateId === 'string' && emp.candidateId && emp.candidateId !== emp.id) ? emp.candidateId : null;
  if (linkedId) {
    const cand = (HRState.candidates || []).find(c => c.id === linkedId);
    if (cand && cand.personId && cand.personId !== emp.id) linkedId = null;
  }
  const PS = window.PersonSections;
  if (!linkedId || !PS?.pickPersonBgRecord) return own;
  const linked = await loadBgCheck(linkedId);
  const picked = PS.pickPersonBgRecord({ personId: emp.id, linkedId, own, linked });
  if (picked.copyDecision) picked.data._bgCopyDecision = picked.copyDecision;
  return picked.data;
}

// The peopleBackgroundChecks doc id a person's BG actions must target (TASK-9235).
function _bgRecordIdFor(emp) {
  return (emp && emp.bgCheck && emp.bgCheck._bgRecordId) || (emp && emp.id) || null;
}

async function ensureBgCheck(emp) {
  if (!emp) return null;
  if (emp.bgCheck !== undefined) return emp;
  emp.bgCheck = await loadPersonBgCheck(emp);
  return emp;
}

// ============================================
// ISNET TAB — EMPLOYEE DETAIL
// ============================================

function maskEIN(ein) {
  if (!ein) return '—';
  const digits = ein.replace(/\D/g, '');
  return digits.length >= 4 ? '**-***' + digits.slice(-4) : ein;
}

// ============================================
// TASK-4866 - CARD NAMES ("Lacey can't name the credit cards")
// ============================================
// Chris, relaying Lacey (AP, trippin-trucking): she wanted to "name these
// credit cards (like the type)" instead of reading the two generic strings this
// tab has always printed - "Company Credit Card 7217" and "Fuel Card 4804".
//
// THE MEASUREMENT THAT DECIDED THE DESIGN (admin SDK, read-only, 2026-09-10):
// a named-card registry ALREADY EXISTS - `tenants/{tid}.linesOfCredit[]` with
// `subtype:'credit_card'`, shipped 2026-03-16, edited under BI -> Financing ->
// "Debt Accounts". Across ALL 95 tenants it holds ZERO rows. In the same
// database, the two dumb checkboxes on THIS tab hold 36 real assignments
// (9 credit / 27 fuel, two tenants). Same people, same six months. Card data
// gets entered where the PERSON is, never under an accounting heading called
// "Debt Accounts". So this was never a missing feature - it was a registry
// nobody could find.
//
// THE FIX IS THEREFORE PLACEMENT, NOT A NEW STORE. The name box added below
// sits beside the last-4 she already types, and saving it seeds the SAME
// `linesOfCredit` registry through window.BIModule.upsertCreditCardByName
// (never a cross-import with a `?v=` buster - pitfall #1). BI's Financing list,
// the TASK-362 expense card picker (`jobboard.js` `_showCardPicker`, which has
// consequently never rendered once in production) and reconciliation's card
// attribution all light up as a side effect of ordinary HR work.
//
// THE EMPTY CASE IS THE DEFAULT CASE, AND IT MUST BE INVISIBLE. 95 tenants have
// no registry rows and 456 people have no card name. If this tab started
// reading a registry instead of printing a label, every one of those screens
// would go blank - a regression wearing a fix. `cardLabel` therefore falls all
// the way back to the exact literal that ships today, so a tenant that never
// names anything sees a byte-identical screen forever.
//
// Resolution order, per slot: registry row by FK (survives a rename in BI) ->
// the name denormalized onto the person (survives a registry read being denied,
// and renders with zero extra reads) -> today's generic literal.
//
// The pure core is a factory with no DOM and no Firebase so
// tests/hr-card-names.unit.cjs can extract it and run it in node:vm - the same
// harness as tests/person-card.unit.cjs.
function _hrCardCoreFactory() {
  'use strict';
  // The strings this tab has printed since it existed. A slot with no name
  // resolves to exactly these - that is the no-migration guarantee.
  const GENERIC = { credit: 'Company Credit Card', fuel: 'Fuel Card' };
  const FIELDS = {
    credit: { has: 'hasCreditCard', last4: 'creditCardLast4', name: 'creditCardName', id: 'creditCardId' },
    fuel:   { has: 'hasFuelCard',   last4: 'fuelCardLast4',   name: 'fuelCardName',   id: 'fuelCardId' },
  };
  function fields(kind) { return FIELDS[kind] || FIELDS.credit; }

  function registryRow(registry, id) {
    if (!id || !Array.isArray(registry)) return null;
    return registry.find(r => r && r.id === id && !r.archived) || null;
  }

  // TASK-9291 — the same lookup INCLUDING archived rows. The two are not
  // interchangeable: `registryRow` must keep excluding archived so a retired
  // card cannot supply a LABEL (a guarantee tests/hr-card-names.unit.cjs
  // asserts), but an additional-card ASSIGNMENT to a retired card has to stay
  // visible or the only way to notice it and remove it disappears with it.
  function registryAny(registry, id) {
    if (!id || !Array.isArray(registry)) return null;
    return registry.find(r => r && r.id === id) || null;
  }

  // The one label resolver every card surface in this file uses.
  function cardLabel(person, kind, registry) {
    const generic = GENERIC[kind] || GENERIC.credit;
    if (!person) return generic;
    const f = fields(kind);
    const hit = registryRow(registry, person[f.id]);
    const fromRegistry = hit ? String(hit.name || '').trim() : '';
    if (fromRegistry) return fromRegistry;
    const stored = String(person[f.name] || '').trim();
    if (stored) return stored;
    return generic;
  }

  // True when the label came from a human, not from the fallback literal - the
  // surfaces use this to decide whether to ALSO print the generic term as a
  // qualifier, so "which one is the fuel card" never gets lost to a rename.
  function isNamed(person, kind, registry) {
    return cardLabel(person, kind, registry) !== (GENERIC[kind] || GENERIC.credit);
  }

  // What the name box prefills with. An UNNAMED slot prefills EMPTY - never
  // with the generic literal, or the next routine save would register a card
  // actually called "Company Credit Card".
  function nameInputValue(person, kind, registry) {
    return isNamed(person, kind, registry) ? cardLabel(person, kind, registry) : '';
  }

  // Datalist entries for one slot: the tenant's already-registered cards of
  // that type. Empty registry -> empty list, which is a datalist that simply
  // never opens. It is a plain text box either way, so it can never dead-end
  // the way a disabled <select> over an empty list would.
  function suggestions(registry, kind) {
    if (!Array.isArray(registry)) return [];
    const seen = new Set();
    return registry
      .filter(r => r && !r.archived && (kind === 'fuel' ? r.cardType === 'fuel' : r.cardType !== 'fuel'))
      .map(r => ({ name: String(r.name || '').trim(), lastFourDigits: String(r.lastFourDigits || '') }))
      .filter(r => r.name && !seen.has(r.name.toLowerCase()) && seen.add(r.name.toLowerCase()));
  }

  // Last four digits ONLY, from whatever was typed or pasted. A full PAN must
  // never land on the `people` doc - firestore.rules calls that collection
  // "BROADCAST to every session incl. driver PINs".
  function last4(raw) {
    const d = String(raw === 0 ? '0' : (raw || '')).replace(/\D/g, '');
    return d ? d.slice(-4) : null;
  }

  // A card name is a label, not a credential, but it is tenant-supplied text
  // that becomes an HTML attribute and a Firestore value. Cap it and drop
  // control characters, which fail OPEN through HTML escapers (the family
  // scripts/check-control-chars.cjs exists to catch). Written as an explicit
  // charCode filter rather than a regex class so no escape sequence in this
  // file can itself smuggle one in.
  function cleanName(raw) {
    return String(raw || '')
      .split('')
      .filter((c) => c.charCodeAt(0) >= 32 && c.charCodeAt(0) !== 127)
      .join('')
      .trim()
      .slice(0, 60);
  }

  // =========================================================================
  // TASK-9291 — cards BEYOND the two legacy slots.
  // =========================================================================
  // Chris, 2026-09-22, entering his real bank cards: "we need to be able to
  // assign more than one credit card people". Measured the same day with the
  // admin SDK (read-only, 83 tenants / 456 people): NINE people are already
  // at the two-slot ceiling, all at trippin-trucking.
  //
  // 🔴 THE TAIL IS AN ASSIGNMENT, NOT MORE SLOTS. `person.additionalCardIds`
  // is an ordered array of OPAQUE `tenants/{tid}.linesOfCredit[].id` values.
  // No last-4, no name, no bank, no PIN is copied onto the person for these —
  // every displayable fact is resolved from the registry at render time, so a
  // rename or a re-issue in BI → Financing follows through and there is
  // nothing on the broadcast doc to drift. (The live data proves the
  // denormalised shape drifts: on 2026-09-22 two registry rows were both
  // named "ASB- Michael Goodman" and the person was bound to the CAPITAL ONE
  // one.) No PIN rides along either: a per-card PIN map was designed and CUT
  // on COD's 🔴 — public/src/personCard.js has the reasoning and the exact
  // firestore.rules change it waits on.
  //
  // 🔴 THE TWO LEGACY SLOTS ARE NOT TOUCHED. Not re-modelled, not migrated,
  // not re-projected. 429 of 456 people hold no card at all and every surface
  // must render byte-identically for them — which is true BY CONSTRUCTION
  // here, because an empty/absent `additionalCardIds` makes this return `[]`
  // and every call site appends nothing. Unifying the slots into this list
  // would need a registry row per existing fuel assignment and there are ZERO
  // cardType:'fuel' rows in ANY tenant, so it would mean minting 27 rows out
  // of measurably drifted data. That is the migration this deliberately is not.
  const MAX_ADDITIONAL_CARDS = 12;

  function additionalIds(person) {
    if (!person || !Array.isArray(person.additionalCardIds)) return [];
    // Never show a legacy slot twice: the two FKs already render above.
    const taken = new Set([person.creditCardId, person.fuelCardId].filter(Boolean).map(String));
    const seen = new Set();
    const out = [];
    for (const raw of person.additionalCardIds) {
      const id = String(raw || '').trim();
      if (!id || taken.has(id) || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
      if (out.length >= MAX_ADDITIONAL_CARDS) break;
    }
    return out;
  }

  // One render-ready row per additional card. A dangling or ARCHIVED FK is
  // never silently dropped — it degrades to the generic literal and is marked,
  // because an assignment you cannot see is an assignment you cannot remove.
  function additionalAssignments(person, registry) {
    return additionalIds(person).map((cardId) => {
      const live = registryRow(registry, cardId);
      const any = live || registryAny(registry, cardId);
      const type = (any && any.cardType === 'fuel') ? 'fuel' : 'credit';
      const name = any ? String(any.name || '').trim() : '';
      return {
        cardId,
        type,
        label: name || GENERIC[type] || GENERIC.credit,
        last4: any ? String(any.lastFourDigits || '') : '',
        named: !!name,
        archived: !!(any && any.archived),
        missing: !any,
      };
    });
  }

  // What the "add another card" picker offers: every live registry card this
  // person does not already hold, in either a legacy slot or the tail. An
  // empty result means the picker renders its empty-state copy rather than a
  // dropdown with nothing in it.
  function additionalOptions(person, registry) {
    const held = new Set(additionalIds(person));
    if (person) {
      if (person.creditCardId) held.add(String(person.creditCardId));
      if (person.fuelCardId) held.add(String(person.fuelCardId));
    }
    if (!Array.isArray(registry)) return [];
    return registry
      .filter(r => r && r.id && !r.archived && !held.has(String(r.id)))
      .map(r => ({
        id: String(r.id),
        name: String(r.name || '').trim(),
        lastFourDigits: String(r.lastFourDigits || ''),
        cardType: r.cardType === 'fuel' ? 'fuel' : 'credit',
      }));
  }

  // The array that goes back on the person doc. Ids only, de-duplicated, the
  // legacy FKs excluded, capped. Returns `null` for "nothing assigned" so the
  // save can write null rather than an empty array — one fewer key shape for a
  // reader to have to reason about, and it keeps a never-edited person's doc
  // free of the field entirely.
  function normalizeAdditionalIds(ids, person) {
    const probe = { creditCardId: person && person.creditCardId, fuelCardId: person && person.fuelCardId,
                    additionalCardIds: Array.isArray(ids) ? ids : [] };
    const out = additionalIds(probe);
    return out.length ? out : null;
  }

  // =========================================================================
  // TASK-9503 — the REVERSE lookup: who, among `people`, holds registry card
  // `card`. Chris 2026-09-30: "maybe show the card as 'unclaimed'?" — TITS
  // entered two shared cards (Capital One Venture 2725, BofA 4304) assigned to
  // nobody and no screen says which cards nobody carries.
  //
  // A person holds the card when ANY of:
  //   • a legacy slot FK points at it (`creditCardId` / `fuelCardId`) — either
  //     slot, because the FK is explicit and a credit-slot FK aimed at a fuel
  //     row is still that row;
  //   • it is in the TASK-9291 tail (`additionalCardIds`, via additionalIds so
  //     the same de-dupe/cap rules apply);
  //   • the matching legacy slot is ON, carries NO FK, and its last-4 equals the
  //     card's — but ONLY when that last-4 names exactly ONE live card of that
  //     type. TASK-9292 lesson: first-match-wins on a duplicate last-4 put a
  //     month of charges on the wrong cardholder; a "Held by" line that guesses
  //     is the same bug in a smaller font. (cardType, last-4) is the identity
  //     `upsertCreditCardByName` in bi.js already uses for a physical card, so
  //     the slot's declared type scopes the uniqueness check.
  //
  // `people` is whatever the caller considers CURRENT — BI hands in
  // `BiState.people`, which already drops `noLongerEmployed`, so a card held
  // only by someone who left comes back empty (= Unclaimed: a card to collect
  // or reassign). This function filters nothing by employment on purpose; the
  // caller's list is the policy. Pure, no DOM, no Firebase, no throw.
  function holdersOf(card, people, registry) {
    if (!card || !card.id || !Array.isArray(people)) return [];
    const id = String(card.id);
    const type = card.cardType === 'fuel' ? 'fuel' : 'credit';
    const l4 = String(card.lastFourDigits || '');
    const live = Array.isArray(registry) ? registry.filter(r => r && r.id && !r.archived) : [];
    const sameLast4 = l4
      ? live.filter(r => ((r.cardType === 'fuel') ? 'fuel' : 'credit') === type && String(r.lastFourDigits || '') === l4)
      : [];
    const last4Unique = !!l4 && !card.archived && sameLast4.length === 1 && String(sameLast4[0].id) === id;
    const f = fields(type);
    return people.filter((p) => {
      if (!p) return false;
      if (p.creditCardId && String(p.creditCardId) === id) return true;
      if (p.fuelCardId && String(p.fuelCardId) === id) return true;
      if (additionalIds(p).includes(id)) return true;
      if (!last4Unique) return false;
      return !!p[f.has] && !p[f.id] && String(p[f.last4] || '') === l4;
    });
  }

  return { GENERIC, FIELDS, fields, cardLabel, isNamed, nameInputValue, suggestions, last4, cleanName,
           MAX_ADDITIONAL_CARDS, registryAny, additionalIds, additionalAssignments, additionalOptions,
           normalizeAdditionalIds, holdersOf };
}
const _hrCard = _hrCardCoreFactory();
// TASK-9503 — the ONE card core, shared with bi.js (Debt Accounts "Held by" /
// "Unclaimed"). hr.js is in app.js's static import graph, so this is set before
// any view renders; bi.js reads `window.HrCardCore` at call time and never
// imports hr.js (Pitfall #1 — a second `?v=` import is a second instance).
window.HrCardCore = _hrCard;

// The tenant's registered cards, for label resolution and the name datalist.
// Prefers BI's live array when the BI view is open, else the tenant snapshot
// app.js caches at login - the SAME source jobboard.js already reads for the
// expense card picker. NEVER throws: a card name is a nicety and must not be
// able to break an employment screen.
// 🔴 TASK-9291 — this now returns ARCHIVED rows TOO, carrying `archived:true`.
// Filtering them out HERE was an integration bug the pure-resolver tests could
// not see (COD Code Reviewer + Independent, 2026-09-22, second pass): a person
// assigned a card the tenant later RETIRED rendered as "card not in the company
// list" with no name and no last-4, because `registryAny` had nothing to find —
// so `additionalAssignments`' whole "(retired card)" branch was dead in
// production while green in the unit suite (whose fixture registry does include
// an archived row).
//
// Filtering ARCHIVED is the CONSUMER's job, and every consumer already does it:
// `registryRow` (label resolution) excludes archived, `suggestions` (the legacy
// datalist) excludes archived, and `additionalOptions` (the assign picker)
// excludes archived. So widening here changes nothing for them and is the only
// way an assignment to a retired card can be shown — and therefore removed.
function _hrCardRegistry() {
  try {
    // includeArchived — bi.js already supports it (TASK-4866, for the AP card
    // manager). Read-only accessor, no cross-import (Pitfall #1).
    const viaBI = window.BIModule && window.BIModule.listCreditCards
      && window.BIModule.listCreditCards({ includeArchived: true });
    if (Array.isArray(viaBI)) return viaBI;
  } catch (_) { /* BI view not initialized - fall through */ }
  try {
    const arr = window.AppState && window.AppState.tenantData && window.AppState.tenantData.linesOfCredit;
    return (Array.isArray(arr) ? arr : [])
      .filter(l => l && l.subtype === 'credit_card')
      .map(l => ({ id: l.id, name: l.name || '', lastFourDigits: l.lastFourDigits || '', cardType: l.cardType === 'fuel' ? 'fuel' : 'credit', archived: !!l.archived }));
  } catch (_) { return []; }
}
function _hrCardLabel(person, kind) { return _hrCard.cardLabel(person, kind, _hrCardRegistry()); }
function _hrCardIsNamed(person, kind) { return _hrCard.isNamed(person, kind, _hrCardRegistry()); }
// TASK-9291 — the cards this person holds BEYOND the two legacy slots.
//
// 🔴 APPENDED INLINE, WITH NO INTERVENING WHITESPACE, at both display sites
// (the employee-detail header and the employment print rows) — glued onto the
// END of the existing fuel-slot line rather than placed on a line of its own.
// That is deliberate and it is the difference between "visually identical" and
// BYTE-identical: a `${…}` on its own template line contributes a newline plus
// its indent even when it evaluates to '', so the 429 of 456 people who hold no
// card would have got two characters of new whitespace each. They get zero.
// (Caught by COD's Code Reviewer on the integrated diff, 2026-09-22 — the
// explanation that used to live inline here is why: it was itself the
// whitespace.) Every displayed fact (name, last-4, credit-vs-fuel) resolves
// from the tenant registry at render time; none of it is denormalised onto the
// broadcast person doc, which is the whole point of the assignment model.
function _hrCardAdditional(person) { return _hrCard.additionalAssignments(person, _hrCardRegistry()); }
function _hrCardIcon(type) { return type === 'fuel' ? 'local_gas_station' : 'credit_card'; }

// A registry row the tenant retired (or one whose id no longer resolves) is
// still shown as assigned — with the reason — because the only way to notice
// and remove a stale assignment is to be able to see it.
function _hrCardStatusNote(c) {
  if (c.missing) return ' <span style="font-size:0.72rem;opacity:0.75;">(card not in the company list)</span>';
  if (c.archived) return ' <span style="font-size:0.72rem;opacity:0.75;">(retired card)</span>';
  return '';
}

// ===========================================================================
// TASK-9291 — the "Additional cards" editor on the Employment tab.
// ===========================================================================
// 🔴 EDIT STATE LIVES HERE, NOT IN THE DOM, for the TASK-4633 reason: a save
// must never infer "remove every card" from a control that was not rendered.
// `_hrExtraCardEdit.personId` is the proof the editor was actually drawn for
// THIS person; saveEmploymentFields refuses to write the list when it does not
// match, and leaves the stored assignment exactly as it is.
//
// 🔴 NO PIN BOX HERE. A per-card PIN was designed and cut on COD's 🔴 —
// `firestore.rules` has no denylist arm for a `cardPins` key on the broadcast
// `people` doc, so a credential under that name could be landed there and read
// by every driver-PIN session. public/src/personCard.js carries the full
// reasoning and the exact rules change it waits on. Until then these rows
// carry an ASSIGNMENT only, and the legacy fuel slot above keeps the one PIN
// box that exists.
let _hrExtraCardEdit = { personId: null, ids: [] };

function _hrAdditionalCardRowHTML(c, empId) {
  return `
    <div data-extra-card="${escapeHtml(c.cardId)}" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:6px 0;border-top:1px solid var(--border-light);">
      <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">${_hrCardIcon(c.type)}</span>
      <span style="font-size:0.82rem;flex:1 1 150px;min-width:120px;">${escapeHtml(c.label)}${c.last4 ? ` <span style="color:var(--text-secondary);">••••${escapeHtml(c.last4)}</span>` : ''}${_hrCardStatusNote(c)}</span>
      <button type="button" class="btn btn-sm" style="padding:2px 8px;font-size:0.72rem;background:transparent;border:1px solid #DC2626;color:#DC2626;white-space:nowrap;"
        onclick="window.HRModule.removeAdditionalCard('${escJsAttr(String(empId))}','${escJsAttr(c.cardId)}')"
        title="Unassign this card from this person. The card itself stays in the company card list.">Remove</button>
    </div>`;
}

// The mutable inner list — re-rendered on every add/remove. Kept separate from
// the outer shell so a mutation never has to rebuild the whole employment tab.
function _hrAdditionalCardsListHTML(empId) {
  const probe = {
    id: empId,
    additionalCardIds: _hrExtraCardEdit.ids,
    // The legacy FKs are read off the live person so the de-dupe in
    // additionalAssignments still applies while editing.
    creditCardId: (HRState.people.find(p => p.id === empId) || {}).creditCardId,
    fuelCardId: (HRState.people.find(p => p.id === empId) || {}).fuelCardId,
  };
  const rows = _hrCard.additionalAssignments(probe, _hrCardRegistry());
  if (!rows.length) {
    return `<div style="font-size:0.75rem;color:var(--text-secondary);padding:6px 0;">No additional cards assigned.</div>`;
  }
  return rows.map(c => _hrAdditionalCardRowHTML(c, empId)).join('');
}

function _hrAdditionalCardsOptionsHTML(empId) {
  const person = HRState.people.find(p => p.id === empId) || { id: empId };
  const probe = { ...person, additionalCardIds: _hrExtraCardEdit.ids };
  const opts = _hrCard.additionalOptions(probe, _hrCardRegistry());
  if (!opts.length) return '';
  return opts.map(o => `<option value="${escapeHtml(o.id)}">${escapeHtml(o.name || (o.cardType === 'fuel' ? 'Fuel Card' : 'Company Credit Card'))}${o.lastFourDigits ? ' ••••' + escapeHtml(o.lastFourDigits) : ''}</option>`).join('');
}

function _hrAdditionalCardsEditorHTML(emp) {
  const empId = emp && emp.id;
  // (Re)arm the edit state for THIS person every time the form is drawn.
  _hrExtraCardEdit = { personId: empId || null, ids: _hrCard.additionalIds(emp) };
  const optionsHTML = _hrAdditionalCardsOptionsHTML(empId);
  const atCap = _hrExtraCardEdit.ids.length >= _hrCard.MAX_ADDITIONAL_CARDS;
  // 🔴 NOTHING ASSIGNED AND NOTHING TO ASSIGN → RENDER NOTHING. Measured
  // 2026-09-22: the card registry is non-empty in exactly ONE of 83 tenants, so
  // without this the other 82 get a heading, an empty-state line and a dead
  // "every card is already assigned" sentence on every employment edit form —
  // a section about a feature they cannot use. Same stance as the TASK-4866
  // datalist, which is simply empty rather than a disabled dropdown over
  // nothing. This does NOT hide a populated list: as soon as the tenant has one
  // registered card, or this person has one assignment, the section appears.
  // (COD Code Reviewer + Independent, integrated pass: they were right that the
  // EDIT form is not byte-identical for a cardless person. It cannot be and
  // still offer the control — but it can be absent wherever the control would
  // do nothing, which is almost everywhere.)
  // TASK-10211 (Wolverine 2026-10-29) — the hidden section read as "one card
  // each". Keep it hidden, but say in one line that more cards are allowed.
  if (!_hrExtraCardEdit.ids.length && !optionsHTML) return `<div id="emp-extra-cards-hint" style="padding-left:28px;font-size:0.72rem;color:var(--text-secondary);">More than one card? Add them in AP &rarr; Cards, then assign up to ${_hrCard.MAX_ADDITIONAL_CARDS} extra here.</div>`;
  return `
      <div style="padding-left:28px;margin-top:4px;">
        <div style="font-size:0.78rem;font-weight:600;display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:16px;">add_card</span> Additional cards
        </div>
        <div id="emp-extra-cards">${_hrAdditionalCardsListHTML(empId)}</div>
        <div id="emp-extra-card-add-row" style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:6px;">
          ${optionsHTML && !atCap ? `
          <select id="emp-extra-card-select" style="padding:6px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:0.82rem;max-width:260px;flex:1 1 180px;">
            ${optionsHTML}
          </select>
          <button type="button" class="btn btn-sm btn-secondary" style="white-space:nowrap;" onclick="window.HRModule.addAdditionalCard('${escJsAttr(String(empId || ''))}')">
            <span class="material-icons" style="font-size:14px;">add</span> Assign card
          </button>` : `
          <span style="font-size:0.75rem;color:var(--text-secondary);">${atCap
            ? `Maximum of ${_hrCard.MAX_ADDITIONAL_CARDS} additional cards reached.`
            : 'Every card in your company card list is already assigned to this person. Add more cards above, or in AP &rarr; Cards.'}</span>`}
        </div>
        <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:6px;line-height:1.5;">
          Assigned cards show on this person&rsquo;s record and are the only cards they see when they submit an expense. One card can be assigned to several people.
          Add or rename the cards themselves in AP &rarr; Cards; here you just say who carries them.
        </div>
      </div>`;
}

// Re-render just the list + the add row after a mutation, preserving anything
// already typed into the PIN boxes.
function _hrRepaintAdditionalCards(empId) {
  const list = document.getElementById('emp-extra-cards');
  if (list) list.innerHTML = _hrAdditionalCardsListHTML(empId);
  const addRow = document.getElementById('emp-extra-card-add-row');
  if (addRow) {
    const optionsHTML = _hrAdditionalCardsOptionsHTML(empId);
    const atCap = _hrExtraCardEdit.ids.length >= _hrCard.MAX_ADDITIONAL_CARDS;
    addRow.innerHTML = (optionsHTML && !atCap)
      ? `<select id="emp-extra-card-select" style="padding:6px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:0.82rem;max-width:260px;flex:1 1 180px;">${optionsHTML}</select>
         <button type="button" class="btn btn-sm btn-secondary" style="white-space:nowrap;" onclick="window.HRModule.addAdditionalCard('${escJsAttr(String(empId || ''))}')"><span class="material-icons" style="font-size:14px;">add</span> Assign card</button>`
      : `<span style="font-size:0.75rem;color:var(--text-secondary);">${atCap
            ? `Maximum of ${_hrCard.MAX_ADDITIONAL_CARDS} additional cards reached.`
            : 'Every card in your company card list is already assigned to this person. Add more cards above, or in AP &rarr; Cards.'}</span>`;
  }
}

function addAdditionalCard(empId) {
  if (_hrExtraCardEdit.personId !== empId) return;
  const sel = document.getElementById('emp-extra-card-select');
  const id = sel && sel.value ? String(sel.value).trim() : '';
  if (!id) return;
  if (_hrExtraCardEdit.ids.length >= _hrCard.MAX_ADDITIONAL_CARDS) return;
  if (_hrExtraCardEdit.ids.includes(id)) return;
  _hrExtraCardEdit.ids = _hrExtraCardEdit.ids.concat([id]);
  _hrRepaintAdditionalCards(empId);
}

// TASK-9593 — "Add card" on the person page's Cards section (Chris 2026-10-02:
// "she should be able to 'add other credit cards here'"). It opens the SAME
// Employment editor and lands on its card picker, so there is ONE write path
// for an assignment (saveEmploymentFields, with its staleness + personId
// guards) rather than a second direct write that a still-open editor's Save
// could silently undo. Shown only when there is a live company card to pick.
// The write itself is office-only in firestore.rules (people update: roster /
// hr / billing module or owner/admin; a driver session cannot write people).
function _hrCanAddCardHere(emp) {
  if (!emp || !emp.id) return false;
  return _hrCard.additionalOptions(emp, _hrCardRegistry()).length > 0;
}

async function addCardFromPersonPage(empId) {
  await editEmploymentTab(empId);
  const sel = document.getElementById('emp-extra-card-select');
  if (!sel) return;
  try { sel.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) { /* old browsers */ }
  try { sel.focus({ preventScroll: true }); } catch (_) { sel.focus(); }
}

// Reversible and confirmed: it takes effect on Save, it removes an assignment
// and nothing else, and the card itself stays in the company registry.
async function removeAdditionalCard(empId, cardId) {
  if (_hrExtraCardEdit.personId !== empId) return;
  const rows = _hrCard.additionalAssignments(
    { id: empId, additionalCardIds: _hrExtraCardEdit.ids }, _hrCardRegistry());
  const hit = rows.find(r => r.cardId === cardId);
  const ok = await (window.showConfirm?.(
    'Unassign this card?',
    `${hit ? hit.label : 'This card'} will no longer be listed on this person's record, and they won't see it first in the expense picker. The card stays in your company card list. Takes effect when you hit Save.`,
    { confirmText: 'Unassign', cancelText: 'Keep', danger: true, icon: 'warning', iconColor: '#DC2626' }
  ) ?? true);
  if (!ok) return;
  // 🔴 RE-CHECK AFTER THE AWAIT — COD Code Reviewer + Independent, 2026-09-22.
  // The first guard is a check-then-act across a dialog the user can sit on for
  // minutes. Open person A, hit Remove, then open person B's Employment tab
  // (which re-arms the singleton to B) and only THEN confirm: the pre-await
  // guard already passed, so the mutation landed on B's working list and B's
  // Save persisted A's decision. The id is shared across people, so nothing
  // downstream could tell. Re-reading `personId` here makes the confirm a
  // no-op the moment the editor is pointed anywhere else.
  if (_hrExtraCardEdit.personId !== empId) return;
  if (!_hrExtraCardEdit.ids.includes(cardId)) return;
  _hrExtraCardEdit.ids = _hrExtraCardEdit.ids.filter(id => id !== cardId);
  _hrRepaintAdditionalCards(empId);
}

function buildEmploymentTabHTML(rawEmp) {
  const emp = _hrComp(rawEmp); // TASK-1481 — every Employment Details row reads comp
  // TASK-4866 - the LABEL is now tenant-supplied (a card name), so it is
  // escaped. Every pre-existing caller passes a literal, so this is a no-op for
  // them. `note` is the optional muted qualifier a NAMED card carries, so
  // renaming "Fuel Card" to "Sunoco Fleet" never loses which slot it is.
  const row = (label, value, note) => `
    <div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border-light);">
      <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;">${escapeHtml(String(label))}${note ? `<span style="font-weight:400;"> · ${escapeHtml(String(note))}</span>` : ''}</span>
      <span style="font-size:0.8rem;color:var(--text-primary);font-weight:500;">${escapeHtml(String(value || '—'))}</span>
    </div>`;

  const empType = emp.hrEmploymentType === 'salary' ? 'Salary' : emp.hrEmploymentType === 'hourly' ? 'Hourly' : '—';
  const payType = emp.payType === 'pieceRate' ? 'Piece Rate' : 'Hourly';
  const wage = getPayrollWage(emp);
  const hireDate = emp.hireDate ? formatDate(emp.hireDate?.toDate?.() || emp.hireDate) : '—';

  // TASK-1103 — post-hire access to the completed §391.21 application (was only
  // reachable from the pre-hire pipeline). candidateId is stamped on promote.
  const applicationCard = emp.candidateId ? `
    <div style="margin-bottom:var(--spacing-md);">
      <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;margin-bottom:var(--spacing-sm);">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">description</span> Hiring Application
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
        <div style="font-size:0.78rem;color:var(--text-secondary);line-height:1.4;flex:1;min-width:180px;">Completed DispatchDeck employment application (FMCSR 391.21) from hiring. Opens the full application — print or save as PDF from there.</div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          <button class="btn btn-sm btn-secondary" onclick="window.HRModule.openCandidateApplication('${escJsAttr(emp.candidateId)}')" style="font-size:0.75rem;padding:4px 12px;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:14px;">open_in_new</span> View / Print Application
          </button>
          <button class="btn btn-sm btn-secondary" onclick="window.HRModule.openPeiInvestigation('${escJsAttr(emp.candidateId)}')" style="font-size:0.75rem;padding:4px 12px;display:inline-flex;align-items:center;gap:4px;" title="Per-employer §391.23 contact status, response capture, N/A, and a printable status sheet">
            <span class="material-icons" style="font-size:14px;">travel_explore</span> SPH Investigation (§391.23)
          </button>
        </div>
      </div>
    </div>` : '';

  return `
    <div style="display:flex;justify-content:flex-end;margin-bottom:var(--spacing-sm);">
      <button class="btn btn-sm btn-secondary" onclick="window.HRModule.editEmploymentTab('${emp.id}')" style="padding:4px 12px;font-size:0.75rem;">
        <span class="material-icons" style="font-size:14px;">edit</span> Edit
      </button>
    </div>
    ${applicationCard}
    <div style="margin-bottom:var(--spacing-md);">
      <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;margin-bottom:var(--spacing-sm);">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">work</span> Employment Details
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
        ${row('Employment Type', empType)}
        ${row('Pay Type', payType)}
        ${row('Wage', wage)}
        ${emp.payType === 'pieceRate' && emp.payRate ? row('Pay Rate', `$${parseFloat(emp.payRate).toFixed(2)} ${emp.payRateUnit || 'Each'}`) : ''}
        ${emp.hrBCBS ? row('Health Insurance', `$${parseFloat(emp.hrBCBS).toFixed(2)} / paycheck`) : ''}
        ${emp.hrPerDiem ? row('Per Diem', `$${parseFloat(emp.hrPerDiem).toFixed(2)} / day`) : ''}
        ${/* TASK-1483 — enrolment only; the rate is the company's, per work date */''}
        ${(parseFloat(emp.hrVehicleMileageRate) || 0) > 0 ? row(window.VehicleReimb?.payTypeNameOf?.(window.AppState?.settings || {}) || 'Vehicle Reimbursement', 'Enrolled — paid at the company rate') : ''}
        ${row('Hire Date', hireDate)}
        ${emp.hrDeductions ? row('Deductions', emp.hrDeductions) : ''}
        ${row('FLSA Status', flsaStatusShortLabel(emp))}
        ${emp.paychexWorkerId ? row('Paychex Worker ID', String(emp.paychexWorkerId)) : ''}
      </div>
    </div>
    <div style="margin-bottom:var(--spacing-md);">
      <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;margin-bottom:var(--spacing-sm);">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">credit_card</span> Cards${_hrCanAddCardHere(emp) ? `
        <button type="button" class="btn btn-sm btn-secondary" style="margin-left:auto;padding:2px 10px;font-size:0.75rem;" onclick="window.HRModule.addCardFromPersonPage('${escJsAttr(String(emp.id || ''))}')" title="Pick a card from your company card list. A card can be assigned to several people (a shared fuel card), and a person can carry any number of cards.">
          <span class="material-icons" style="font-size:14px;">add_card</span> Add card
        </button>` : ''}
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
        ${/* TASK-4866 - the label is the NAME she gave the card. With no name
             and no registry (the state of all 95 tenants on 2026-09-10) these
             two lines render byte-identical to what they always have. */''}
        ${row(_hrCardLabel(emp, 'credit'), emp.hasCreditCard ? `Yes${emp.creditCardLast4 ? ' (••••' + emp.creditCardLast4 + ')' : ''}` : 'No', _hrCardIsNamed(emp, 'credit') ? 'Company credit card' : '')}
        ${row(_hrCardLabel(emp, 'fuel'), emp.hasFuelCard ? `Yes${emp.fuelCardLast4 ? ' (••••' + emp.fuelCardLast4 + ')' : ''}` : 'No', _hrCardIsNamed(emp, 'fuel') ? 'Fuel card' : '')}${_hrCardAdditional(emp).map(c => row(c.label, `Yes${c.last4 ? ' (••••' + c.last4 + ')' : ''}${c.archived ? ' — retired card' : ''}${c.missing ? ' — not in the company card list' : ''}`, c.type === 'fuel' ? 'Fuel card' : 'Company credit card')).join('')}
      </div>
    </div>
    ${_hrIssuedEquipmentSectionHTML(emp)}
  `;
}

async function editEmploymentTab(empId) {
  let emp = HRState.people.find(p => p.id === empId);
  if (!emp) return;
  const container = document.getElementById('hr-detail-tab-employment');
  if (!container) return;

  // TASK-1191 (Slice 3) — STALENESS GUARD. The suspected hrBCBS-wipe vector is an
  // edit form prefilled from a stale HRState.people copy, then saved, silently
  // nulling insurance/pay. Fetch a FRESH person doc before rendering the form and
  // FAIL CLOSED: if the read fails, block the edit form entirely rather than
  // render stale values a save would then persist over the live record.
  container.innerHTML = `<div style="padding:var(--spacing-lg);text-align:center;color:var(--text-secondary);font-size:0.85rem;"><span class="btn-spinner"></span> Loading latest employment info&hellip;</div>`;
  try {
    const snap = await getDoc(doc(db, 'tenants', HRState.tenantId, 'people', empId));
    if (!snap.exists()) throw new Error('Person record not found');
    const fresh = { id: snap.id, ...snap.data() };

    // TASK-1481 — ALSO fetch the fresh peopleComp doc and overlay it, for the
    // same TASK-1191 reason the person doc is fetched fresh: this form is what
    // saveEmploymentFields' confirm-before-clear diffs against, and after the
    // strip step the person doc alone carries NO pay fields — prefilling from
    // it would render a blank form whose save then nulls the real comp doc.
    // FAIL CLOSED on transient errors (a form prefilled from a stale/missing
    // overlay is exactly the wipe vector); permission-denied alone continues —
    // a denied editor's comp WRITE is denied by the same rule, so their save
    // cannot wipe the comp store, and pre-strip the legacy fields still
    // prefill correctly.
    try {
      const compSnap = await getDoc(doc(db, 'tenants', HRState.tenantId, 'peopleComp', empId));
      if (compSnap.exists() && window.PersonComp) {
        const compFresh = window.PersonComp.pickComp(compSnap.data());
        Object.keys(compFresh).forEach(k => { fresh[k] = compFresh[k]; });
      }
    } catch (compErr) {
      if (compErr?.code !== 'permission-denied') throw compErr;
    }

    const idx = HRState.people.findIndex(p => p.id === empId);
    if (idx >= 0) {
      // Fresh money/pay fields win — this cache is what saveEmploymentFields reads
      // for its before/after diff + confirm-before-clear.
      HRState.people[idx] = { ...HRState.people[idx], ...fresh };
      emp = HRState.people[idx];
    } else {
      emp = fresh;
    }
  } catch (err) {
    console.error('[HR] Could not load fresh employment info — blocking edit:', err);
    container.innerHTML = `
      <div style="padding:var(--spacing-lg);text-align:center;">
        <div style="color:var(--status-danger);font-weight:700;margin-bottom:8px;">Couldn't load the latest employment info</div>
        <div style="color:var(--text-secondary);font-size:0.85rem;margin-bottom:14px;line-height:1.4;">Editing is blocked to protect pay and insurance data from being overwritten with stale values. Check your connection and try again.</div>
        <button class="btn btn-sm btn-primary" onclick="window.HRModule.editEmploymentTab('${empId}')" style="margin-right:8px;">Retry</button>
        <button class="btn btn-sm btn-secondary" onclick="window.HRModule.cancelEmploymentEdit('${empId}')">Cancel</button>
      </div>`;
    return;
  }

  const inputStyle = 'border:1px solid var(--border-light);border-radius:6px;padding:6px 10px;font-size:0.82rem;background:var(--bg-primary);color:var(--text-primary);width:100%;box-sizing:border-box;';
  const labelStyle = 'font-size:0.78rem;color:var(--text-secondary);font-weight:600;margin-bottom:2px;';
  const field = (label, id, value, type = 'text', extra = '') => `
    <div style="margin-bottom:8px;">
      <div style="${labelStyle}">${label}</div>
      <input type="${type}" id="${id}" value="${escapeHtml(String(value || ''))}" style="${inputStyle}" ${extra} />
    </div>`;
  const selectField = (label, id, value, options) => `
    <div style="margin-bottom:8px;">
      <div style="${labelStyle}">${label}</div>
      <select id="${id}" style="${inputStyle}">
        ${options.map(o => `<option value="${o.value}" ${value === o.value ? 'selected' : ''}>${o.label}</option>`).join('')}
      </select>
    </div>`;

  // TASK-9804 — Paychex Worker ID box: shown when the company turned on the
  // Paychex file (Payroll Prep → Paychex settings) or the person already has
  // one. A failed settings read only hides the box; it never blocks the edit.
  let _pxShow = !!(emp.paychexWorkerId);
  if (!_pxShow) {
    try { _pxShow = !!((await window.PayrollModule?.loadPaychexSettings?.(HRState.tenantId)) || {}).enabled; } catch (_) { _pxShow = false; }
  }

  const hireDateVal = formatDateForInput(emp.hireDate);

  container.innerHTML = `
    <div style="padding:var(--spacing-sm);">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
        <div style="font-weight:700;font-size:1rem;color:var(--text-primary);">Edit Employment Info</div>
        <div style="display:flex;gap:8px;">
          <button class="btn btn-sm btn-secondary" onclick="window.HRModule.cancelEmploymentEdit('${empId}')">Cancel</button>
          <button class="btn btn-sm btn-primary" id="btn-save-employment" onclick="window.HRModule.saveEmploymentFields('${empId}')">
            <span class="material-icons" style="font-size:14px;">save</span> Save
          </button>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 var(--spacing-md);">
        ${selectField('Employment Type', 'emp-type', emp.hrEmploymentType || '', [
          { value: '', label: 'Select...' },
          { value: 'salary', label: 'Salary' },
          { value: 'hourly', label: 'Hourly' }
        ])}
        ${/* TASK-4633 — W-2 vs 1099. Contractors are EXCLUDED from the Gusto
             payroll import (payroll.js buildGustoRows) and paid through
             Gusto → Pay → Pay contractors instead. Before this existed, a
             contractor with clock hours silently exported as a W-2 employee
             row — a double payment with the wrong tax treatment. This is a
             classification, NOT a comp field: it stays on the person doc so
             payroll prep can read it, and it is audited on change.
             TASK-9178 — this used to edit a SEPARATE field (`workerType`) from
             the Tax tab's `taxClassification`, and the two drifted: a person
             could be a 1099 contractor on the Tax tab and a W-2 row in Gusto.
             Both controls now edit the ONE classification (window.WorkerClass),
             pre-filled from the resolved value. Unset renders as "Not set" and
             saves as unset — it is no longer silently stamped 'employee'. */''}
        ${/* TASK-9178 COD r2/r3 — options + selection come from the shared
             WorkerClass.selectModel: a record that is not in sync opens on a
             "— Confirm classification —" placeholder, so picking ANY value
             (including the current one) is one deliberate change; leaving it
             writes nothing. data-touched marks a deliberate pick. */''}
        ${_hrClassSelect(selectField, 'Worker Classification', 'emp-worker-class', emp, 'Not set (exports as W-2)')}
        ${_pxShow ? field('Paychex Worker ID', 'emp-paychex-worker-id', emp.paychexWorkerId, 'text', 'data-orig="' + escapeHtml(String(emp.paychexWorkerId || '')) + '" maxlength="10" autocomplete="off" placeholder="Employee ID in Paychex Flex" title="Paychex matches hours to people by this ID — copy it from the employee in Paychex Flex"') : ''}
        ${selectField('Pay Type', 'emp-pay-type', emp.payType || 'hourly', [
          { value: 'hourly', label: 'Hourly' },
          { value: 'pieceRate', label: 'Piece Rate' }
        ])}
        <div id="emp-piece-rate-fields" style="display:${emp.payType === 'pieceRate' ? 'contents' : 'none'};">
          ${field('Pay Rate ($)', 'emp-pay-rate', emp.payRate, 'number', 'step="0.01" min="0" placeholder="4.50"')}
          ${selectField('Pay Rate Unit', 'emp-pay-rate-unit', emp.payRateUnit || '', [
            { value: '', label: 'Select...' },
            { value: 'Per Load', label: 'Per Load' },
            { value: 'Per Ton', label: 'Per Ton' },
            { value: 'Per Yard', label: 'Per Yard' },
            { value: 'Per Mile', label: 'Per Mile' },
            { value: 'Per Day', label: 'Per Day' },
            { value: 'Each', label: 'Each' }
          ])}
        </div>
        ${field('Hourly Rate ($)', 'emp-hourly', emp.hrHourlyRate, 'number', 'step="0.01" min="0" placeholder="16.00"')}
        ${field('Annual Salary ($)', 'emp-salary', emp.hrAnnualSalary, 'number', 'step="1" min="0" placeholder="85000"')}
        ${field('Health Insurance ($ per paycheck)', 'emp-bcbs', emp.hrBCBS, 'number', 'step="0.01" min="0" placeholder="0.00"')}
        ${field('Per Diem ($ per day)', 'emp-perdiem', emp.hrPerDiem, 'number', 'step="0.01" min="0" placeholder="0.00"')}
        ${/* TASK-1483 — personal-vehicle reimbursement enrolment. A SEPARATE
             concept from per diem: per diem pays on days >= 8h; this pays miles
             the person reports at clock-out, at the company mileage rate in
             effect that day (Workforce → Claims). Stored as
             people.hrVehicleMileageRate > 0 (absent/0 = not enrolled). */''}
        <div style="grid-column:1/-1;margin:-4px 0 10px;">
          <label style="display:flex;align-items:flex-start;gap:8px;cursor:pointer;font-size:0.85rem;">
            <input type="checkbox" id="emp-vehicle-reimb" ${(parseFloat(emp.hrVehicleMileageRate) || 0) > 0 ? 'checked' : ''} style="margin-top:2px;">
            <span><strong>${escapeHtml(window.VehicleReimb?.payTypeNameOf?.(window.AppState?.settings || {}) || 'Vehicle Reimbursement')}</strong> (personal vehicle) — this person can report miles driven in their own vehicle at clock-out, paid at the company mileage rate.
              <span style="display:block;font-size:0.72rem;color:var(--text-secondary);">Not the same as per diem. If their per diem above was really "truck pay", clear it so they aren't paid twice.</span></span>
          </label>
        </div>
        ${field('Hire Date', 'emp-hire-date', hireDateVal, 'date')}
        ${/* TASK-1628 (COD UX ⚠️) — this is where an admin EXPECTS to fix a hire
             date, and blanking the box above is silently ignored on save. Put the
             real action here too; same admin-gated modal as the PSP block. */''}
        ${hireDateVal ? `<div style="grid-column:1/-1;font-size:0.72rem;color:var(--text-secondary);margin:-10px 0 12px;">
          Blanking this box does nothing on save.${_hrEffectiveAdminSafe() ? '' : ' Removing a hire date takes an owner or admin.'}
          ${_hrEffectiveAdminSafe() ? `<button class="btn btn-sm" style="background:#0F766E;color:#fff;margin-left:6px;vertical-align:middle;" onclick="window.HRModule.openHireDateCorrection('${escJsAttr(empId)}', 'person')">
            <span class="material-icons" style="font-size:14px;">edit_calendar</span> Correct hire date
          </button>` : ''}
        </div>` : ''}
        ${field('Pay Change Effective From', 'emp-rate-effective', _payEffectiveNowLocal(), 'datetime-local')}
      </div>
      <div style="font-size:0.72rem;color:var(--text-muted);margin:-4px 0 8px;">
        Only used when a pay field above changes: payroll applies the OLD rate to hours before this
        moment and the NEW rate after it — mid-shift changes split at the exact time (TASK-1558).
        Back-date it to when the raise actually took effect; leave as-is for "from now".
      </div>
      <div style="margin-top:8px;">
        <div style="${labelStyle}">Deductions</div>
        <div style="background:var(--bg-secondary);border-radius:6px;padding:8px 10px;font-size:0.82rem;color:var(--text-secondary);border:1px solid var(--border-light);">
          ${emp.hrDeductions ? escapeHtml(emp.hrDeductions) : '<em>Manage in Time Clock → Adjustments tab</em>'}
        </div>
      </div>
      ${_hrCanManageRecurringDeductions() ? `
      <div style="margin-top:12px;">
        ${/* TASK-4848 — this block used to be titled "Recurring Deductions" and
             queried type=='deduction' only, so a recurring REIMBURSEMENT that
             pays every single period (Brian Reid's $50 phone stipend) was
             structurally invisible here. Payroll saw it in the REIMBURSEMENT
             column and nobody could find where it was configured. The title and
             the query now both mean "everything that recurs for this person". */''}
        <div style="${labelStyle}">Recurring Adjustments <span style="font-weight:400;color:var(--text-secondary);">(applies every pay period)</span></div>
        <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:6px;padding:8px 10px;">
          <div id="emp-recurring-deductions-list" style="display:flex;flex-direction:column;">
            <div style="font-size:0.8rem;color:var(--text-secondary);padding:4px 0;">Loading&hellip;</div>
          </div>
          ${/* TASK-4848 — Add stays DEDUCTION-ONLY on purpose. Adding a recurring
               reimbursement / per-diem / bonus needs the category picker and the
               per-diem precedence warning that live on Time Clock -> Adjustments;
               a second half-built creator here would be a third place to get
               payroll wrong. The LIST shows every type; the ADD button does not. */''}
          <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:10px;font-weight:600;">Add a recurring deduction</div>
          <div style="display:flex;gap:8px;margin-top:4px;align-items:center;flex-wrap:wrap;">
            <input type="text" id="emp-recdeduct-label" placeholder="Label (e.g. Child Support)" maxlength="60" style="${inputStyle}flex:1;min-width:140px;width:auto;" />
            <input type="number" id="emp-recdeduct-amount" min="0" step="0.01" placeholder="0.00" style="${inputStyle}max-width:110px;width:auto;" />
            <button class="btn btn-sm btn-secondary" id="btn-add-recurring-deduction" onclick="window.HRModule.addRecurringDeduction('${escJsAttr(empId)}')" style="white-space:nowrap;">
              <span class="material-icons" style="font-size:14px;">add</span> Add
            </button>
          </div>
          <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:6px;">Saved immediately (no need to hit Save above). Applies every pay period &mdash; shows in Time Clock &rarr; Adjustments and the payroll CSV deductions columns. To add a recurring <strong>reimbursement, per diem or bonus</strong>, use Time Clock &rarr; Workforce &rarr; Adjustments &mdash; it will then appear in the list above.</div>
        </div>
      </div>` : ''}
      ${/* TASK-4866 - "Card name" is the whole point of the task. It sits BESIDE
           the last-4 she already types, because that is where card data has
           actually been entered for six months (36 assignments here, 0 rows in
           the BI registry it was supposed to replace). The <datalist> offers the
           cards this tenant has already registered; typing a new one registers
           it. On an empty registry the list is empty and this is simply a text
           box - never a disabled dropdown with nothing in it. */''}
      <div style="margin-top:12px;display:flex;flex-direction:column;gap:8px;">
        <datalist id="hr-cardnames-credit">${_hrCard.suggestions(_hrCardRegistry(), 'credit').map(c => `<option value="${escapeHtml(c.name)}">${c.lastFourDigits ? escapeHtml('****' + c.lastFourDigits) : ''}</option>`).join('')}</datalist>
        <datalist id="hr-cardnames-fuel">${_hrCard.suggestions(_hrCardRegistry(), 'fuel').map(c => `<option value="${escapeHtml(c.name)}">${c.lastFourDigits ? escapeHtml('****' + c.lastFourDigits) : ''}</option>`).join('')}</datalist>
        <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:0.82rem;">
          <input type="checkbox" id="emp-has-credit-card" ${emp.hasCreditCard ? 'checked' : ''}
            onchange="document.getElementById('emp-credit-card-fields').style.display=this.checked?'flex':'none';">
          <span class="material-icons" style="font-size:16px;">credit_card</span> Company Credit Card
        </label>
        <div id="emp-credit-card-fields" style="display:${emp.hasCreditCard ? 'flex' : 'none'};gap:8px;padding-left:28px;flex-wrap:wrap;">
          <input type="text" id="emp-credit-card-name" list="hr-cardnames-credit" maxlength="60" placeholder="Card name (e.g. Chase Visa)" value="${escapeHtml(_hrCard.nameInputValue(emp, 'credit', _hrCardRegistry()))}" style="${inputStyle}max-width:230px;flex:1 1 180px;" />
          <input type="text" id="emp-credit-card-last4" maxlength="4" inputmode="numeric" placeholder="Last 4" value="${escapeHtml(emp.creditCardLast4 || '')}" style="${inputStyle}max-width:120px;" />
        </div>
        <label style="display:flex;align-items:center;gap:6px;cursor:pointer;font-size:0.82rem;">
          <input type="checkbox" id="emp-has-fuel-card" ${emp.hasFuelCard ? 'checked' : ''}
            onchange="document.getElementById('emp-fuel-card-fields').style.display=this.checked?'flex':'none';">
          <span class="material-icons" style="font-size:16px;">local_gas_station</span> Fuel Card
        </label>
        <div id="emp-fuel-card-fields" style="display:${emp.hasFuelCard ? 'flex' : 'none'};gap:8px;padding-left:28px;flex-wrap:wrap;">
          <input type="text" id="emp-fuel-card-name" list="hr-cardnames-fuel" maxlength="60" placeholder="Card name (e.g. Sunoco Fleet)" value="${escapeHtml(_hrCard.nameInputValue(emp, 'fuel', _hrCardRegistry()))}" style="${inputStyle}max-width:230px;flex:1 1 180px;" />
          <input type="text" id="emp-fuel-card-last4" maxlength="4" inputmode="numeric" placeholder="Last 4" value="${escapeHtml(emp.fuelCardLast4 || '')}" style="${inputStyle}max-width:120px;" />
          ${/* TASK-4865 — the PIN box NO LONGER PREFILLS. It used to render the
               live credential into the DOM off the broadcast person doc. It now
               starts EMPTY and empty means UNCHANGED (saveEmploymentFields only
               writes the PIN when this box was revealed or typed in) — so a
               routine employment save can never blank someone's PIN, which is the
               TASK-1191 wipe vector wearing a security fix. "Show" fetches the
               value from the admin-gated peopleCard vault on demand; once shown,
               the box is authoritative and clearing it clears the PIN. */''}
          <input type="text" id="emp-fuel-card-pin" maxlength="6" placeholder="PIN (unchanged)" value="" autocomplete="off" data-dirty="0" data-revealed="0" oninput="this.dataset.dirty='1';" style="${inputStyle}max-width:140px;" />
          <button type="button" class="btn btn-sm btn-secondary" id="btn-reveal-fuel-pin" style="padding:2px 10px;font-size:0.72rem;white-space:nowrap;" onclick="window.HRModule.revealFuelCardPinInput('${escJsAttr(empId)}')" title="Load the stored fuel-card PIN into this box so you can read or change it (the view is recorded in the activity log). Leave the box empty to keep the PIN exactly as it is.">Show</button>
        </div>${_hrAdditionalCardsEditorHTML(emp)}
        <div style="font-size:0.72rem;color:var(--text-secondary);padding-left:28px;line-height:1.5;">
          Naming a card adds it to your company card list, so the name shows here instead of
          &ldquo;Company Credit Card&rdquo;, and drivers can pick it when they submit an expense.
          Leave a name blank to go back to the generic label. Last 4 digits only &mdash; never the full number.
        </div>
      </div>
    </div>`;

  // Toggle piece rate fields visibility when pay type changes
  setTimeout(() => {
    const payTypeEl = document.getElementById('emp-pay-type');
    if (payTypeEl) {
      payTypeEl.addEventListener('change', () => {
        const prFields = document.getElementById('emp-piece-rate-fields');
        if (prFields) prFields.style.display = payTypeEl.value === 'pieceRate' ? 'contents' : 'none';
      });
    }
  }, 0);

  // TASK-941 — load this person's recurring `payAdjustments`.
  // TASK-4848 — every recurring TYPE now, not deductions only.
  if (_hrCanManageRecurringDeductions()) loadRecurringAdjustments(empId);
}

// ============================================
// RECURRING DEDUCTIONS (TASK-941)
// Manage the person's recurring `payAdjustments` deduction entries directly
// from Edit Employment Info — NOT a person-doc array (COD ❌'d a 3rd data
// store). Docs written here match clock.js saveAdjustment's adjDoc shape
// exactly, so the Time Clock Adjustments tab and payroll CSV DEDUCTIONS
// columns (clock.js exportPayrollCSV recurring filter) pick them up unchanged.
// ============================================

// Client-side mirror of firestore.rules payAdjustments create:
// isTenantAdmin || hasModule('billing'). Fail closed (hide the section)
// so a non-billing HR member never taps into a raw rules denial.
function _hrCanManageRecurringDeductions() {
  // TASK-9491 — payAdjustments create is now firestore.rules canEditMoney (owner
  // via ownerId, or `billing` and not view-only); the admin ROLE no longer passes.
  if (typeof window.canEditMoney === 'function') return window.canEditMoney() === true;
  if (typeof window.hrEffectiveAdmin === 'function' && window.hrEffectiveAdmin()) return true;
  const ma = window.AppState?.memberAccess;
  return !!(ma && Array.isArray(ma.modules) && ma.modules.includes('billing'));
}

// TASK-4848 — the payAdjustments TYPE VOCABULARY, in one place.
// `payAdjustments.type` is written as lower-case by clock.js saveAdjustment and
// by addRecurringDeduction below, but legacy capitalized values are real in the
// data (clock.js loadPayAdjustmentTotals:5296 handles 'Deduction'/'Per-Diem'/
// 'Bonus'/'Reimbursement' explicitly), so every read here normalizes case first.
//
// 🔴 SIGN RULE — deliberately identical to the one payroll already runs
// (clock.js netTotal, ~:8225: `if (adj.type === 'deduction') -= else +=`).
// DEDUCTION is the only type that subtracts. An unknown/other type therefore
// adds, matching what the money side does, and is labelled with its raw type so
// it is still identifiable rather than silently mislabelled "Deduction".
const _HR_ADJ_TYPES = {
  'deduction': { label: 'Deduction', sign: -1 },
  'reimbursement': { label: 'Reimbursement', sign: 1 },
  'per-diem': { label: 'Per Diem', sign: 1 },
  'bonus': { label: 'Bonus', sign: 1 }
};

function _hrAdjTypeMeta(rawType) {
  const key = String(rawType || '').trim().toLowerCase();
  if (_HR_ADJ_TYPES[key]) return { key, ..._HR_ADJ_TYPES[key] };
  // Unknown type: still additive (payroll's rule), but say what it actually is.
  const label = key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Adjustment';
  return { key, label, sign: 1 };
}

// TASK-4848 — was `loadRecurringDeductions`, and it queried type=='deduction'.
// That filter is what hid Brian Reid's recurring $50 Phone REIMBURSEMENT from
// the one block on this screen that claims to list what recurs for a person.
// Now: every recurring payAdjustment for this person, whatever its type.
// (personId + recurring are both equality filters, so this needs no composite
// index — it is strictly a subset of the query it replaces.)
async function loadRecurringAdjustments(empId) {
  const host = document.getElementById('emp-recurring-deductions-list');
  if (!host) return;
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', HRState.tenantId, 'payAdjustments'),
      where('personId', '==', empId),
      where('recurring', '==', true)
    ));
    const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    _renderRecurringAdjustmentRows(empId, items);
  } catch (error) {
    console.error('[HR] Error loading recurring adjustments:', error);
    const el = document.getElementById('emp-recurring-deductions-list');
    if (el) el.innerHTML = '<div style="font-size:0.8rem;color:var(--status-danger);padding:4px 0;">Could not load recurring adjustments.</div>';
  }
}

// 🔴 TASK-4848 — THIS RENDERER AND THE QUERY ABOVE ARE ONE ATOMIC CHANGE.
// The previous version hardcoded `-$` in --status-danger red for EVERY row and
// labelled it `category || 'Deduction'`. Widening the query without rewriting
// this would have drawn Brian's $50 reimbursement as a $50 deduction from his
// pay — worse than not showing it at all. Sign AND color AND an explicit type
// chip now carry the direction, so a reimbursement can never read as a
// deduction. Changes display only: no amount, no sign, no payroll math moves.
function _renderRecurringAdjustmentRows(empId, items, opts) {
  const host = document.getElementById('emp-recurring-deductions-list');
  if (!host) return;
  if (!items.length) {
    host.innerHTML = '<div style="font-size:0.8rem;color:var(--text-secondary);padding:4px 0;"><em>No recurring adjustments</em></div>';
    return;
  }
  // TASK-1591 — `payAdjustments` delete is OWNER-only in firestore.rules
  // (payroll integrity), but this Remove button rendered for every HR viewer. A
  // non-owner admin could click it all day and only ever get "Error removing
  // recurring deduction" — technically not silent, but it never says WHY, so it
  // reads as a broken product rather than a deliberate boundary.
  //
  // This is the same defect, and the same fix, that `_hrIsOwner()` was written for
  // (see its comment: delete buttons rendered to every HR viewer that the rules
  // reject). Reusing it rather than inventing a second owner check.
  const canRemove = _hrIsOwner();
  // TASK-4848 — the person's HR per-diem RATE, because a recurring manual
  // per-diem row is superseded by it and pays nothing (TASK-939 precedence,
  // made visible by TASK-4628). Surfacing such a row as "+$35.00 / period"
  // would be a brand-new lie on a screen whose whole job this task is to make
  // honest, so it gets the same NOT PAID treatment the Adjustments tab gives it.
  const perDiemRate = (opts && opts.perDiemRate != null)
    ? (parseFloat(opts.perDiemRate) || 0)
    : _hrRecurringPerDiemRate(empId);
  host.innerHTML = items.map(it => {
    const meta = _hrAdjTypeMeta(it.type);
    const amount = parseFloat(it.amount) || 0;
    const isAdd = meta.sign > 0;
    // Superseded per-diem: pays $0 today. No sign, no green, no "/ period".
    const superseded = meta.key === 'per-diem' && perDiemRate > 0 && amount > 0;
    // TASK-4848 follow-up (browser verification, 2026-09-09): --status-success
    // #059669 measures 3.77:1 on this white card, and the chip on its own 10%
    // tint 3.34:1. Both are ~13px at weight 600-700, so neither counts as large
    // text and neither clears AA — while the DEDUCTION red beside them sits at
    // 4.83:1. --status-success-emphasis is the same hue at 5.35:1, so the two
    // directions read at matching strength.
    //
    // COD's Architect and Skeptic lanes both rejected the first cut of this,
    // which hardcoded the hex right here: an a11y override belongs in the token
    // layer, not in a view renderer. The token is a SECOND one rather than a
    // retone of --status-success on purpose — that has ~100 uses app-wide and
    // changing it is its own review with its own blast radius.
    const ADD_GREEN = 'var(--status-success-emphasis)';
    const SUPERSEDED_AMBER = '#B45309';
    const amountColor = superseded ? SUPERSEDED_AMBER : (isAdd ? ADD_GREEN : 'var(--status-danger)');
    // A superseded per-diem pays nothing, so its type chip must not be the
    // additive green sitting inches from an amber NOT PAID badge — the row would
    // argue with itself. Chip follows the badge.
    const chipFg = superseded ? SUPERSEDED_AMBER : (isAdd ? ADD_GREEN : 'var(--status-danger)');
    const chipBg = superseded ? 'rgba(217,119,6,0.12)' : (isAdd ? 'rgba(4,120,87,0.10)' : 'rgba(220,38,38,0.10)');
    // (The chip tints stay literal rgba — they are alpha washes of the same hue,
    //  and CSS has no way to add alpha to a var() colour without color-mix, which
    //  this codebase does not use anywhere yet.)
    const chipBorder = superseded ? 'rgba(217,119,6,0.45)' : (isAdd ? 'rgba(4,120,87,0.35)' : 'rgba(220,38,38,0.35)');
    const amountText = superseded
      ? `$${amount.toFixed(2)}`
      : `${isAdd ? '+' : '&minus;'}$${amount.toFixed(2)}`;
    return `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);${superseded ? 'background:rgba(217,119,6,0.05);' : ''}">
      <div style="flex:1;min-width:0;">
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span style="font-size:0.82rem;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(it.category || meta.label)}</span>
          ${/* The type chip is the actual fix Lacey asked for: the payroll report
               said "reimbursement" and no screen in HR ever said the word. */''}
          <span style="font-size:0.68rem;font-weight:700;letter-spacing:0.02em;text-transform:uppercase;padding:1px 6px;border-radius:4px;white-space:nowrap;color:${chipFg};background:${chipBg};border:1px solid ${chipBorder};">${escapeHtml(meta.label)}</span>
          ${superseded ? `
          <span style="display:inline-flex;align-items:center;gap:3px;font-size:0.68rem;color:#B45309;background:rgba(217,119,6,0.12);border:1px solid rgba(217,119,6,0.45);padding:1px 6px;border-radius:4px;font-weight:700;white-space:nowrap;">
            <span class="material-icons" style="font-size:12px;">report_problem</span>NOT PAID
          </span>` : ''}
        </div>
        ${superseded ? `
        <div style="font-size:0.72rem;color:#B45309;margin-top:3px;line-height:1.35;">This row pays nothing &mdash; the Per Diem rate of $${perDiemRate.toFixed(2)}/day above takes precedence over a manual per-diem amount.</div>` : ''}
      </div>
      <span style="font-size:0.82rem;font-weight:600;color:${amountColor};white-space:nowrap;">${amountText}${superseded ? '' : ' <span style="font-weight:400;color:var(--text-secondary);">/ period</span>'}</span>
      ${canRemove ? `
        ${/* JS-STRING-INSIDE-AN-ATTRIBUTE position: escapeHtml/escAttr give it no
             protection at all (the parser decodes &#39; before the handler is
             compiled). The 3rd arg is the type LABEL so the confirm + toast can
             say "reimbursement" instead of calling everything a deduction. */''}
        <button class="btn btn-sm btn-tertiary" onclick="window.HRModule.removeRecurringAdjustment('${escJsAttr(empId)}', '${escJsAttr(it.id)}', '${escJsAttr(meta.label.toLowerCase())}')" title="Remove" style="padding:2px 6px;">
          <span class="material-icons" style="font-size:14px;color:var(--status-danger);">close</span>
        </button>` : ''}
    </div>`;
  }).join('');
  if (!canRemove && items.length) {
    host.insertAdjacentHTML('beforeend',
      `<div style="font-size:0.72rem;color:var(--text-secondary);padding-top:6px;">Removing a recurring adjustment is owner-only.</div>`);
  }
}

// TASK-4848 — resolve the person's HR per-diem RATE through the comp resolver
// (TASK-1481: reading emp.hrPerDiem off a raw HRState.people entry reads $0
// after the strip step). Never throws — a missing rate just means "no conflict".
function _hrRecurringPerDiemRate(empId) {
  try {
    const person = (HRState && Array.isArray(HRState.people))
      ? HRState.people.find(p => p.id === empId)
      : null;
    if (!person) return 0;
    return parseFloat(_hrComp(person)?.hrPerDiem) || 0;
  } catch (_) { return 0; }
}

async function addRecurringDeduction(empId) {
  const btn = document.getElementById('btn-add-recurring-deduction');
  if (btn && btn.disabled) return; // COD: in-flight guard — no double-click dupes
  const labelEl = document.getElementById('emp-recdeduct-label');
  const amountEl = document.getElementById('emp-recdeduct-amount');
  const label = labelEl?.value?.trim() || '';
  const amount = parseFloat(amountEl?.value || '0');
  if (!label) { window.showToast?.('Enter a deduction label', 'error'); return; }
  if (!(amount > 0)) { window.showToast?.('Enter an amount greater than 0', 'error'); return; }

  const emp = HRState.people.find(p => p.id === empId);
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span>'; }
  try {
    // Doc shape mirrors clock.js saveAdjustment adjDoc EXACTLY — the payroll
    // readers (loadDetailAdjustments / exportPayrollCSV) require `date` present
    // and filter on type === 'deduction' + recurring === true, labeling by
    // `category`.
    await addDoc(collection(db, 'tenants', HRState.tenantId, 'payAdjustments'), {
      personId: empId,
      personName: emp?.name || 'Unknown',
      type: 'deduction',
      category: label,
      amount,
      recurring: true,
      date: Timestamp.now(),
      notes: null,
      createdAt: Timestamp.now(),
      createdBy: window.AppState?.user?.email || 'unknown'
    });
    if (labelEl) labelEl.value = '';
    if (amountEl) amountEl.value = '';
    window.showToast?.('Recurring deduction added', 'success');
    await loadRecurringAdjustments(empId);
  } catch (error) {
    console.error('[HR] Error adding recurring deduction:', error);
    window.showToast?.('Error adding recurring deduction', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">add</span> Add'; }
  }
}

// TASK-4848 — was `removeRecurringDeduction`. The list is no longer
// deduction-only, so the confirm and the toast must NAME the thing being
// removed: telling someone "this deduction will stop applying" while they
// delete a $50 phone reimbursement is the same class of lie this task exists to
// kill. `typeLabel` comes from the row that rendered the button (lower-cased,
// e.g. "reimbursement"); it is COPY ONLY and never touches the delete path,
// which is still a plain delete-by-doc-id.
async function removeRecurringAdjustment(empId, adjId, typeLabel) {
  const kind = String(typeLabel || '').trim().toLowerCase() || 'adjustment';
  const Kind = kind.charAt(0).toUpperCase() + kind.slice(1);
  const ok = await (window.showConfirm?.(`Remove Recurring ${Kind}`, `This ${kind} will stop applying to future pay periods.`, { confirmText: 'Remove', danger: true })
    ?? confirm(`Remove this recurring ${kind}?`));
  if (!ok) return;
  try {
    // NOTE: firestore.rules payAdjustments delete is OWNER-only (payroll
    // integrity) — matches the Time Clock Adjustments delete surface.
    // TASK-1591 — the button is now hidden for non-owners, but the handler is
    // reachable from the console, and "Error removing recurring deduction" never
    // told anyone WHY. Say the actual reason instead of letting the rule denial
    // surface as a generic failure.
    await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'payAdjustments', adjId));
    window.showToast?.(`Recurring ${kind} removed`, 'success');
    await loadRecurringAdjustments(empId);
  } catch (error) {
    console.error('[HR] Error removing recurring adjustment:', error);
    // Name the boundary when that is what it is. A payroll denial that reads as a
    // generic error sends someone hunting for a bug that isn't there.
    const denied = /permission|insufficient/i.test(String(error?.code || error?.message || ''));
    window.showToast?.(denied
      ? `Only the account owner can remove a recurring ${kind}.`
      : `Error removing recurring ${kind}`, 'error');
  }
}

function cancelEmploymentEdit(empId) {
  // TASK-9291 — DISARM the additional-cards working state the moment the edit
  // form goes away. The guard in saveEmploymentFields is `personId === empId`,
  // and leaving a stale armed state behind is the only way that guard could
  // ever match a form that is no longer on screen. Disarming makes the guard
  // fail CLOSED (the key is omitted, the stored assignment survives) instead
  // of matching by coincidence.
  _hrExtraCardEdit = { personId: null, ids: [] };
  const emp = HRState.people.find(p => p.id === empId);
  if (!emp) return;
  const container = document.getElementById('hr-detail-tab-employment');
  if (container) container.innerHTML = buildEmploymentTabHTML(emp);
}

// ── TASK-1558: effective-dated pay-rate history ─────────────────────────────
// tenants/{tid}/payRates/{personId} = { personId, history: [entry…] } —
// APPEND-ONLY (arrayUnion; firestore.rules enforce non-shrinking). Payroll
// (clock.js buildPayrollPrepData / exportPayrollCSV / printWorkforceReport via
// window.PayRates) resolves the rate in effect at each clock entry's moment
// from this history; the person doc's hr* fields stay authoritative for
// "current" display. A parallel SIBLING collection, NOT fields on the person
// doc — `people` is broadcast to every session incl. driver PINs, and a full
// rate history is more sensitive than the current rate that already leaks
// (peopleTax precedent; also deliberately NOT in peopleTax, which is tax-only).
//
// BEST-EFFORT BY DESIGN: a denied/failed history write must never block a pay
// edit (the wage record stays fixable — flag, never trap). On failure payroll
// simply keeps today's behavior (current rate for the whole period) and the
// admin sees a warning toast.

function _payEffectiveNowLocal() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

async function _appendPayRateHistory(empId, beforePerson, afterFields, effectiveFromDate, source) {
  const norm = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };
  const pick = (key) => (key in afterFields ? afterFields[key] : beforePerson[key]);
  try {
    const ref = doc(db, 'tenants', HRState.tenantId, 'payRates', empId);
    // 2nd-COD (integration) — rules now require the appended entry's setBy to
    // equal the caller's token email and setAt to sit in a tight now-window.
    // No email = no authorable provenance: abort into the warn path rather
    // than write an entry the rules will deny anyway.
    const setBy = window.AppState?.user?.email;
    if (!setBy) throw new Error('no authenticated email for pay-rate provenance');
    const setAt = Timestamp.now();
    const entries = [];
    // Seed a BASELINE on first write so hours BEFORE the first effective-dated
    // change resolve to the value the person doc held when history began —
    // this is what stops an after-the-period edit from reaching back (Steven).
    // 2nd-COD (integration) — a FAILED read must ABORT, not fabricate: the old
    // "treat as no history" path could append a duplicate baseline whose later
    // setAt would win the tie-sort and quietly re-price everything before the
    // first change.
    const snap = await getDoc(ref);
    const hasHistory = !!(snap.exists() && Array.isArray(snap.data()?.history) && snap.data().history.length);
    if (!hasHistory) {
      entries.push({
        effectiveFrom: null,
        employmentType: beforePerson.hrEmploymentType || null,
        hourlyRate: norm(beforePerson.hrHourlyRate),
        annualSalary: norm(beforePerson.hrAnnualSalary),
        perDiem: norm(beforePerson.hrPerDiem),
        payRate: norm(beforePerson.payRate),
        payRateUnit: beforePerson.payRateUnit || null,
        setBy, setAt, source: 'baseline',
      });
    }
    const eff = (effectiveFromDate instanceof Date && !isNaN(effectiveFromDate.getTime()))
      ? effectiveFromDate : new Date();
    entries.push({
      effectiveFrom: Timestamp.fromDate(eff),
      employmentType: pick('hrEmploymentType') || null,
      hourlyRate: norm(pick('hrHourlyRate')),
      annualSalary: norm(pick('hrAnnualSalary')),
      perDiem: norm(pick('hrPerDiem')),
      payRate: norm(pick('payRate')),
      payRateUnit: pick('payRateUnit') || null,
      setBy, setAt, source,
    });
    await setDoc(ref, { personId: empId, history: arrayUnion(...entries), updatedAt: Timestamp.now() }, { merge: true });
    return true;
  } catch (err) {
    console.warn('[HR] pay-rate history append failed (payroll will use the current rate for the whole period):', err?.message || err);
    window.showToast?.('Pay saved, but the rate history could not be recorded — payroll will apply the current rate to the whole period', 'warning');
    return false;
  }
}

async function saveEmploymentFields(empId) {
  const btn = document.getElementById('btn-save-employment');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const val = (id) => document.getElementById(id)?.value?.trim() || '';
    const hasCreditCard = document.getElementById('emp-has-credit-card')?.checked || false;
    const hasFuelCard = document.getElementById('emp-has-fuel-card')?.checked || false;
    const hireDate = val('emp-hire-date');

    const payTypeVal = val('emp-pay-type') || 'hourly';
    // TASK-4633 — worker classification decides W-2 vs 1099, so it must never be
    // set by the ABSENCE of a control. saveEmploymentFields is exported on
    // window.HRModule; if a future edit surface reuses it without rendering the
    // Worker Type select, a bare val() would read '' and silently rewrite every
    // contractor to 'employee' — putting them straight back into the payroll
    // import this task just removed them from. Fall back to what they already
    // are, and only ever change it on an explicit choice.
    // TASK-9178 — the control now edits the ONE classification. No control →
    // no classification write at all (the stored pair is left exactly as is).
    // Control present → write BOTH fields from the chosen value only on a real
    // change, or when the user deliberately picked a value for a conflicted /
    // one-field record (WorkerClass.writeForSave). An unrelated Employment save
    // leaves both fields untouched and the CLASS CONFLICT flag visible.
    const _wcEl = document.getElementById('emp-worker-class');
    let _wcWrite = null;
    let _wcPriorRes = null;
    const _wcPriorPerson = HRState.people.find(p => p.id === empId) || {};
    // Snapshot the raw pair NOW — the cached person object is mutated after the write.
    const _wcPriorRaw = { taxClassification: _wcPriorPerson.taxClassification ?? null, workerType: _wcPriorPerson.workerType ?? null };
    if (_wcEl) {
      if (!window.WorkerClass) throw new Error('Worker classification rule not loaded — reload the page.');
      _wcPriorRes = window.WorkerClass.resolve(_wcPriorPerson);
      _wcWrite = window.WorkerClass.writeForSave(_wcPriorPerson, val('emp-worker-class') || null, _wcEl.dataset.touched === '1');
    }
    // TASK-4866 - resolve the two card slots BEFORE the person write, so the
    // registry FK is known by the time updateData is assembled.
    //
    // FAILURE POLICY, and it is the TASK-1191 wipe class in miniature: naming a
    // card is a nicety riding on a PAYROLL-BEARING save. `upsertCreditCardByName`
    // never throws and returns {ok:false} on a denied tenant-doc write (the rule
    // is `isTenantAdmin`, so an hr-module STAFF member legitimately cannot seed
    // the registry). When that happens the NAME still saves on the person - which
    // is all Lacey asked for - and the existing FK is PRESERVED rather than
    // nulled. A failed registry write must never silently unlink a card that was
    // already linked.
    const _priorPerson = HRState.people.find(p => p.id === empId) || {};
    const _cardName = {
      credit: hasCreditCard ? _hrCard.cleanName(val('emp-credit-card-name')) : '',
      fuel: hasFuelCard ? _hrCard.cleanName(val('emp-fuel-card-name')) : '',
    };
    const _cardLast4 = {
      credit: hasCreditCard ? _hrCard.last4(val('emp-credit-card-last4')) : null,
      fuel: hasFuelCard ? _hrCard.last4(val('emp-fuel-card-last4')) : null,
    };
    const _cardId = { credit: null, fuel: null };
    let _registryWriteFailed = false;
    for (const kind of ['credit', 'fuel']) {
      if (!_cardName[kind]) continue; // no name -> no registry row, no FK
      const prior = _priorPerson[kind === 'fuel' ? 'fuelCardId' : 'creditCardId'] || null;
      let res = null;
      try {
        res = await window.BIModule?.upsertCreditCardByName?.(HRState.tenantId, {
          name: _cardName[kind], lastFourDigits: _cardLast4[kind], cardType: kind,
        });
      } catch (_) { res = null; }
      if (res && res.ok && res.id) _cardId[kind] = res.id;
      else { _cardId[kind] = prior; if (res && res.ok !== true) _registryWriteFailed = true; }
    }

    // TASK-1483 — personal-vehicle enrolment. Only written when the box is on
    // screen AND its state changed, so an unrelated save by someone without
    // the hr gate (firestore.rules) never touches the key.
    const _pvEl = document.getElementById('emp-vehicle-reimb');
    const _pvPrior = parseFloat((HRState.people.find(p => p.id === empId) || {}).hrVehicleMileageRate) || 0;
    let _pvWrite = null;
    let _pvClearedPerDiem = false; // already confirmed above — skip the generic clear prompt
    if (_pvEl && (_pvEl.checked === true) !== (_pvPrior > 0)) {
      if (_pvEl.checked) {
        const _pvCur = window.VehicleReimb?.currentPolicy?.(window.VehicleReimb.policyOf(window.AppState?.settings || {}));
        if (!_pvCur) {
          window.showToast?.('Set the company mileage rate first (Workforce → Claims), then enrol people.', 'error');
          if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
          return;
        }
        // Value = the company rate at enrolment (display only — payroll always
        // prices each day at the rate in effect that day).
        _pvWrite = { hrVehicleMileageRate: _pvCur.ratePerMile };
        // 🔴 Migration guard (spec): per diem that was really truck pay must be
        // cleared or the person is paid twice. Ask, don't assume — some people
        // genuinely get both.
        const _pdNow = parseFloat(val('emp-perdiem')) || 0;
        if (_pdNow > 0) {
          const clearPd = await (window.showConfirm?.(
            'Clear their per diem too?',
            `This person also has Per Diem of $${_pdNow.toFixed(2)}/day. If that per diem was really pay for using their own vehicle, clear it now — otherwise they are paid twice. If it is a genuine living allowance, keep it.`,
            { confirmText: 'Clear per diem', cancelText: 'Keep per diem', icon: 'warning' }
          ) ?? false);
          if (clearPd) { const _pdEl = document.getElementById('emp-perdiem'); if (_pdEl) _pdEl.value = ''; _pvClearedPerDiem = true; }
        }
      } else {
        _pvWrite = { hrVehicleMileageRate: null };
      }
    }

    // TASK-9804 — Paychex Worker ID (Paychex matches hours by it). Same shape
    // as the vehicle enrolment above: written only when the box is on screen
    // AND the value changed, so a save by someone without the hr gate
    // (firestore.rules) never touches the key. Pay routing, so audited.
    const _pxEl = document.getElementById('emp-paychex-worker-id');
    // COD r1: compare with what the form OPENED with (data-orig), not live state — an
    // untouched stale form must never overwrite an ID someone else just corrected.
    const _pxOrig = _pxEl ? String(_pxEl.dataset.orig || '').trim() : '';
    const _pxPrior = String((HRState.people.find(p => p.id === empId) || {}).paychexWorkerId || '').trim();
    let _pxWrite = null;
    if (_pxEl) {
      const _pxNew = String(_pxEl.value || '').trim();
      if (_pxNew !== _pxOrig) {
        if (_pxPrior !== _pxOrig) {
          window.showToast?.(`This person's Paychex Worker ID was changed to ${_pxPrior || '(blank)'} while you were editing. Reopen Edit and try again.`, 'error');
          if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
          return;
        }
        const _pxOk = window.PaychexExport ? window.PaychexExport.validWorkerId(_pxNew) : /^[A-Za-z0-9][A-Za-z0-9-]{0,9}$/.test(_pxNew);
        // Case-insensitive, active people only (an ex-employee's old ID can be reused; Payroll Prep still refuses a shared ID among people being paid).
        const _pxDup = _pxNew ? HRState.people.find(p => p.id !== empId && p.active !== false
          && String(p.paychexWorkerId || '').trim().toUpperCase() === _pxNew.toUpperCase()) : null;
        if ((_pxNew && !_pxOk) || _pxDup) {
          window.showToast?.(_pxDup
            ? `Paychex Worker ID ${_pxNew} is already on ${_pxDup.name || 'another person'} — each person needs their own.`
            : 'Paychex Worker ID is 1-10 letters, digits or dashes, not starting with a dash.', 'error');
          if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
          return;
        }
        _pxWrite = { paychexWorkerId: _pxNew || null };
      }
    }

    const updateData = {
      ...(_pvWrite || {}),
      ...(_pxWrite || {}),
      hrEmploymentType: val('emp-type') || null,
      payType: payTypeVal,
      payRate: payTypeVal === 'pieceRate' ? (parseFloat(val('emp-pay-rate')) || null) : null,
      payRateUnit: payTypeVal === 'pieceRate' ? (val('emp-pay-rate-unit') || null) : null,
      hrHourlyRate: parseFloat(val('emp-hourly')) || null,
      hrAnnualSalary: parseFloat(val('emp-salary')) || null,
      hrBCBS: parseFloat(val('emp-bcbs')) || null,
      hrPerDiem: parseFloat(val('emp-perdiem')) || null,
      // TASK-9178 — taxClassification + its derived workerType, from one value
      // (see _wcWrite above); absent when nothing about it changed.
      ...(_wcWrite || {}),
      hasCreditCard: hasCreditCard,
      // TASK-4866 - last-4 is normalised to DIGITS ONLY here. The box is
      // maxlength=4, but a raw-SDK or autofill write is not, and `people` is the
      // collection firestore.rules calls "BROADCAST to every session incl.
      // driver PINs" - a full PAN must never be able to land on it.
      creditCardLast4: hasCreditCard ? _hrCard.last4(val('emp-credit-card-last4')) : null,
      hasFuelCard: hasFuelCard,
      fuelCardLast4: hasFuelCard ? _hrCard.last4(val('emp-fuel-card-last4')) : null,
      // TASK-4866 - the NAME (denormalised so the label renders with no extra
      // read, and survives a registry read being denied) plus the registry FK
      // (so a rename in BI -> Financing follows through, and so the TASK-362
      // expense card picker can pre-select by id instead of guessing on last-4).
      // Both null when the slot is off or the name was cleared: that is how you
      // go back to the generic label.
      creditCardName: _cardName.credit || null,
      creditCardId: _cardName.credit ? (_cardId.credit || null) : null,
      fuelCardName: _cardName.fuel || null,
      fuelCardId: _cardName.fuel ? (_cardId.fuel || null) : null,
    };

    // TASK-9291 — the cards beyond the two slots above. IDS ONLY: no last-4,
    // no name, no bank, no PIN is denormalised onto the broadcast `people`
    // doc for these, so there is nothing here that can drift from the registry
    // and nothing a driver-PIN session learns that it could not already read
    // out of `tenants/{tid}.linesOfCredit` itself.
    //
    // 🔴 ONLY WRITTEN WHEN THE EDITOR WAS ACTUALLY RENDERED FOR THIS PERSON
    // (TASK-4633's lesson: a field must never be set by the ABSENCE of a
    // control). saveEmploymentFields is exported on window.HRModule; a future
    // surface that reuses it without drawing the Additional-cards list would
    // otherwise read an empty working list and silently unassign every card.
    // Key omitted entirely in that case, so the stored value survives.
    if (_hrExtraCardEdit.personId === empId) {
      updateData.additionalCardIds = _hrCard.normalizeAdditionalIds(_hrExtraCardEdit.ids, {
        creditCardId: updateData.creditCardId, fuelCardId: updateData.fuelCardId,
      });
    }

    // TASK-4865 — the fuel-card PIN is resolved SEPARATELY and NEVER joins
    // `updateData`, because `updateData` is what lands on the broadcast
    // `people` doc. Three cases, and the default one is the safe one:
    //   • fuel card unchecked        → explicit null (clear the vault entry)
    //   • box revealed or typed in   → whatever is in the box ('' = clear)
    //   • box untouched and empty    → UNDEFINED = "unchanged", the key is
    //     dropped by pickCard and the stored PIN survives the save. This is
    //     the whole reason the box no longer prefills: a blank box on a
    //     routine employment save must not wipe a credential.
    const _pinEl = document.getElementById('emp-fuel-card-pin');
    let _fuelCardPin;
    if (!hasFuelCard) {
      _fuelCardPin = null;
    } else if (_pinEl && (_pinEl.dataset.dirty === '1' || _pinEl.dataset.revealed === '1')) {
      _fuelCardPin = val('emp-fuel-card-pin') || null;
    }
    if (hireDate) updateData.hireDate = Timestamp.fromDate(new Date(hireDate + 'T12:00:00'));

    // TASK-1191 — snapshot the previous pay-field values BEFORE the write (and
    // before the local Object.assign below mutates the cached object). Source is
    // HRState.people, which editEmploymentTab (Slice 3) refreshed via getDoc, so
    // this reflects the freshest known values — the correct "before" for both the
    // confirm-before-clear check (Slice 1) and the audit diff (Slice 2).
    // TASK-1481 — comp-first "before": editEmploymentTab refreshed BOTH the
    // person doc and the peopleComp doc into HRState.people[idx], and the
    // accessor overlay keeps the confirm-before-clear + audit diff truthful
    // after the strip step (when the person doc alone would read all-null and
    // the wipe guard would silently stop guarding).
    const _beforePerson = _hrComp(HRState.people.find(p => p.id === empId) || {});
    const _payFields = ['hrBCBS', 'hrHourlyRate', 'hrAnnualSalary', 'hrPerDiem', 'payRate'];
    const _beforeSnap = {};
    _payFields.forEach(k => { _beforeSnap[k] = _beforePerson[k]; });

    // Slice 1 — CONFIRM BEFORE CLEAR. When a previously-SET money field would go
    // nonzero → null on this save, ask per-field. The wipe that cost Justin his
    // $305 insurance deduction happened silently; this makes clearing deliberate.
    // Safety: preserve is the FALLBACK outcome — the styled danger "Remove" button
    // (showConfirm confirm=true) must be clicked deliberately to clear; the "Keep"
    // button, a backdrop click, or a missing showConfirm all preserve the value,
    // so an accidental tap/Enter can NEVER wipe pay data.
    const _clearFieldMeta = {
      hrBCBS: { label: 'Health Insurance', deduction: true },
      hrHourlyRate: { label: 'Hourly Rate' },
      hrAnnualSalary: { label: 'Annual Salary' },
      hrPerDiem: { label: 'Per Diem' },
      payRate: { label: 'Pay Rate' },
    };
    for (const key of _payFields) {
      const prev = parseFloat(_beforeSnap[key]);
      const next = updateData[key];
      const nextIsCleared = (next === null || next === undefined || !(parseFloat(next) > 0));
      // payRate legitimately nulls when the pay type is switched away from piece
      // rate — that's an intentional change, not an accidental wipe. Only guard it
      // when the record is still piece-rate (i.e. the rate box was blanked).
      if (key === 'payRate' && payTypeVal !== 'pieceRate') continue;
      if (key === 'hrPerDiem' && _pvClearedPerDiem) continue; // TASK-1483 — confirmed in the enrolment prompt
      if (!(prev > 0) || !nextIsCleared) continue;
      const meta = _clearFieldMeta[key];
      const amtStr = '$' + prev.toFixed(2);
      const msg = meta.deduction
        ? `This will stop deducting ${meta.label} (${amtStr}) from this employee's pay. Keep it, or remove the deduction?`
        : `This will clear this employee's ${meta.label} (${amtStr}). Keep it, or remove?`;
      // confirm=true → "Remove" (clear); false/backdrop/missing → "Keep" (preserve).
      const remove = await (window.showConfirm?.(
        `Remove ${meta.label}?`,
        msg,
        { confirmText: 'Remove', cancelText: `Keep ${amtStr}`, danger: true, icon: 'warning', iconColor: '#DC2626' }
      ) ?? false);
      if (!remove) {
        // Preserve the prior value (and payRateUnit alongside payRate).
        updateData[key] = _beforeSnap[key];
        if (key === 'payRate') updateData.payRateUnit = _beforePerson.payRateUnit || null;
      }
    }

    // TASK-1481 STRIP DONE (2026-08-13) — the 8 dollar/pay comp fields no longer
    // land on the driver-readable `people` doc (firestore.rules now REJECTS a
    // people write that sets them). Write a FILTERED COPY so `updateData` stays
    // intact for the peopleComp dual-write, the pay-edit audit and the
    // pay-history append below (all of which read the comp values). The two
    // classification booleans (flsaStatus/hrOvertimeExempt) are NOT stripped —
    // a driver's own OT view reads them off `people` (arbitration COD).
    const _STRIP_FROM_PEOPLE = ['hrHourlyRate', 'hrAnnualSalary', 'hrPerDiem', 'hrBCBS', 'hrDeductions', 'payType', 'payRate', 'payRateUnit'];
    const _peopleUpdate = { ...updateData };
    _STRIP_FROM_PEOPLE.forEach(k => delete _peopleUpdate[k]);
    // TASK-9804 (COD r2) — re-read the stored Worker ID right before the write: the
    // confirm dialogs above can sit open while someone else corrects it.
    if (_pxWrite) {
      const _pxNow = String(((await getDoc(doc(db, 'tenants', HRState.tenantId, 'people', empId))).data() || {}).paychexWorkerId || '').trim();
      if (_pxNow !== _pxOrig) {
        window.showToast?.(`This person's Paychex Worker ID was changed to ${_pxNow || '(blank)'} while you were editing. Nothing was saved — reopen Edit and try again.`, 'error');
        if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
        return;
      }
    }
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'people', empId), _peopleUpdate);
    // TASK-1483 — enrolment is a pay entitlement: audit old → new.
    if (_pvWrite) {
      try {
        window.logAudit?.('hr', 'PERSON_VEHICLE_REIMB_ENROLLMENT', {
          entityType: 'person', entityId: empId, source: 'hr-edit-employment',
          old: _pvPrior > 0 ? _pvPrior : null, new: _pvWrite.hrVehicleMileageRate,
        });
      } catch (e) { /* audit is best-effort; never blocks a save */ }
    }
    if (_pxWrite) {
      try {
        window.logAudit?.('hr', 'PERSON_PAYCHEX_WORKER_ID', {
          entityType: 'person', entityId: empId, source: 'hr-edit-employment',
          old: _pxPrior || null, new: _pxWrite.paychexWorkerId,
        });
      } catch (e) { /* audit is best-effort; never blocks a save */ }
    }

    // TASK-1481 — DUAL-WRITE the comp subset to peopleComp (pickComp inside
    // writeComp filters updateData to exactly the 10 comp fields; hireDate,
    // card fields, hrEmploymentType etc. never land there). Once a comp doc
    // exists its fields WIN over the person doc, so a failed comp write means
    // payroll can read a STALE rate — never silent. permission-denied gets
    // actionable-warning copy (either the rules half hasn't deployed yet, or
    // this editor lacks the comp write grant — an admin re-save fixes both);
    // anything else is a retryable error.
    const _compRes = await (window.PersonComp?.writeComp?.(HRState.tenantId, empId, updateData) ?? { ok: true });
    if (!_compRes.ok) {
      if (_compRes.code === 'permission-denied') {
        window.showToast?.("Pay saved, but your access level couldn't update the secure pay store. If payroll shows old values, have an admin re-save this employee's Employment Info.", 'warning');
      } else {
        window.showToast?.('Pay saved to the legacy record but NOT the secure pay store — payroll may use stale values. Retry the save.', 'error');
      }
    }

    // TASK-4865 — route the fuel-card PIN to the admin-gated `peopleCard`
    // vault. NOT a dual-write: the whole point is that the credential stops
    // landing on the broadcast `people` doc, so this is the ONLY place it is
    // written from the client. `_fuelCardPin === undefined` means "unchanged"
    // and pickCard drops it, so this is a no-op on a routine save.
    // Failure is never silent: once a vault doc exists it WINS over the legacy
    // person-doc value for every reader, so a failed write means the PIN on
    // screen is stale, not merely unsaved.
    if (_fuelCardPin !== undefined) {
      const _cardRes = await (window.PersonCard?.writeCard?.(HRState.tenantId, empId, { fuelCardPin: _fuelCardPin }) ?? { ok: false, code: 'accessor-missing' });
      if (!_cardRes.ok) {
        if (_cardRes.code === 'permission-denied') {
          window.showToast?.("Saved, but your access level couldn't update the fuel-card PIN. Ask an admin to set it.", 'warning');
        } else {
          window.showToast?.('Saved, but the fuel-card PIN did NOT save. Re-open Employment Info and try again.', 'error');
        }
      } else {
        // The VALUE is never logged — only that it changed, and by whom.
        try {
          window.logAudit?.('hr', _fuelCardPin === null ? 'PERSON_FUEL_CARD_PIN_CLEARED' : 'PERSON_FUEL_CARD_PIN_SET', {
            entityType: 'person', entityId: empId, source: 'hr-edit-employment',
          });
        } catch (e) { /* audit is best-effort; never blocks a save */ }
      }
    }

    // Slice 2 — audit the pay-field edit with a before/after diff of the 5 fields
    // (changed keys only). Uses window.logAudit (shared helper, app.js) — the
    // diffs are NEVER console.logged (payroll/PII Secrets lane).
    try {
      const _beforeDiff = {}, _afterDiff = {};
      const _norm = (v) => (v === null || v === undefined || v === '') ? null : (Number.isNaN(parseFloat(v)) ? v : parseFloat(v));
      _payFields.forEach(k => {
        const b = _norm(_beforeSnap[k]);
        const a = _norm(updateData[k]);
        if (b !== a) { _beforeDiff[k] = b; _afterDiff[k] = a; }
      });
      // TASK-4633 — worker classification is a money-governing setting behind a
      // shared Save button (the workweek-flip family). Log old → new explicitly
      // so a wrong 1099/W-2 is a 30-second audit lookup, not an archaeology dig.
      // TASK-9178 — the classification is now ONE value written as a pair;
      // `_wcWrite` is non-null only when the stored pair actually changes.
      if (_wcWrite) {
        _beforeDiff.taxClassification = _wcPriorRaw.taxClassification;
        _beforeDiff.workerType = _wcPriorRaw.workerType;
        _afterDiff.taxClassification = _wcWrite.taxClassification;
        _afterDiff.workerType = _wcWrite.workerType;
      }
      if (Object.keys(_afterDiff).length) {
        window.logAudit?.('hr', 'PERSON_PAY_FIELDS_EDIT', {
          entityType: 'person',
          entityId: empId,
          source: 'hr-edit-employment',
          before: _beforeDiff,
          after: _afterDiff,
          // TASK-1481 — record whether the peopleComp dual-write landed, so a
          // future "why does payroll show the old rate" is a 30-second audit
          // lookup (a FAILED comp write = peopleComp is stale and WINS).
          dualWrite: _compRes.ok ? 'peopleComp-ok' : `peopleComp-FAILED-${_compRes.code || 'unknown'}`,
          redFlags: { clearedToNull: Object.keys(_afterDiff).filter(k => _afterDiff[k] === null) },
        });
      }
      // TASK-9178 — the SAME audit action the Tax tab writes, so every change
      // to W-2 / 1099 status is one query regardless of which screen made it.
      if (_wcWrite) {
        window.logAudit?.('hr', 'EMPLOYEE_TAX_CLASSIFICATION_CHANGED', {
          personId: empId,
          personName: _beforePerson.name || null,
          changes: {
            taxClassification: { before: _wcPriorRes ? _wcPriorRes.classification : null, after: _wcWrite.taxClassification },
            workerClassRaw: {
              before: { ..._wcPriorRaw, conflict: !!(_wcPriorRes && _wcPriorRes.conflict) },
              after: { taxClassification: _wcWrite.taxClassification, workerType: _wcWrite.workerType },
            },
          },
          via: 'employment-tab',
        });
      }
    } catch (auditErr) { console.warn('[HR] pay-field edit audit skipped:', auditErr?.message || auditErr); }

    // TASK-1558 — append the effective-dated pay-history entry when any pay
    // BASIS field changed (rate/salary/type/per-diem/piece-rate). Best-effort:
    // runs AFTER the person write succeeded and never blocks it. Reads the
    // FINAL updateData (post confirm-before-clear), so a preserved value never
    // fabricates a change.
    try {
      const _n = (v) => (v === null || v === undefined || v === '') ? null : (Number.isNaN(parseFloat(v)) ? v : parseFloat(v));
      const _basisChanged =
        (_beforePerson.hrEmploymentType || null) !== (updateData.hrEmploymentType || null)
        || _n(_beforeSnap.hrHourlyRate) !== _n(updateData.hrHourlyRate)
        || _n(_beforeSnap.hrAnnualSalary) !== _n(updateData.hrAnnualSalary)
        || _n(_beforeSnap.hrPerDiem) !== _n(updateData.hrPerDiem)
        || _n(_beforeSnap.payRate) !== _n(updateData.payRate);
      if (_basisChanged) {
        const _effRaw = val('emp-rate-effective');
        await _appendPayRateHistory(empId, _beforePerson, updateData,
          _effRaw ? new Date(_effRaw) : new Date(), 'hr-edit-employment');
      }
    } catch (histErr) { console.warn('[HR] pay history skipped:', histErr?.message || histErr); }

    const emp = HRState.people.find(p => p.id === empId);
    if (emp) {
      Object.assign(emp, updateData);
      if (hireDate) emp.hireDate = new Date(hireDate + 'T12:00:00');
    }

    window.showToast?.('Employment info saved', 'success');
    // TASK-4866 - the card NAME saved on the person either way; what failed is
    // adding it to the company-wide card list, which `firestore.rules` gates on
    // isTenantAdmin. Say exactly that, because the name WILL show on this tab
    // and the user would otherwise have no idea the shared list is out of sync.
    if (_registryWriteFailed) {
      window.showToast?.("Card name saved for this employee, but it couldn't be added to the company card list \u2014 that needs an admin. Drivers won't see it on the expense form yet.", 'warning');
    }
    cancelEmploymentEdit(empId);
    // TASK-1123 — the modal header (Current Pay, card last4s, hired date) is
    // built once by openEmployeeDetail and went stale after an Employment
    // save. Surgically re-render JUST the header block so the new values show
    // immediately WITHOUT reopening the modal (a full openEmployeeDetail
    // would kick the user off the Employment tab back to Reviews and re-fire
    // logDriverFileView). Several header lines are conditionally rendered
    // (appear/disappear), so the whole block re-renders, not a text patch.
    if (emp) {
      const headerEl = document.querySelector('#employee-detail-modal .hr-employee-detail-header');
      if (headerEl) {
        // TASK-1207 — the builder also returns the flag-reason callout as a
        // SIBLING of the header div; drop the stale one first so the outerHTML
        // replacement can't duplicate it, then re-hydrate the reason line.
        // TASK-9424 — the sibling is now the #hr-flag-panel wrapper (always present).
        document.getElementById('hr-flag-panel')?.remove();
        document.getElementById('hr-flag-reason-callout')?.remove();
        headerEl.outerHTML = buildEmployeeDetailHeaderHTML(emp);
        _hydrateHrFlagReason(emp);
      }
    }
    renderEmployeeDirectory();
  } catch (error) {
    console.error('[HR] Error saving employment fields:', error);
    window.showToast?.('Error saving employment info', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
  }
}

function buildTaxTabHTML(rawEmp) {
  const emp = _hrComp(rawEmp); // TASK-1481 — the FLSA select pre-fills from comp
  const fieldRow = (label, value) => `
    <div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border-light);">
      <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;">${label}</span>
      <span style="font-size:0.8rem;color:var(--text-primary);font-weight:500;">${escapeHtml(value || '—')}</span>
    </div>`;

  // TASK-9178 — the ONE classification rule; a conflict is said out loud.
  const _wcRes = window.WorkerClass ? window.WorkerClass.resolve(emp) : { classification: null, is1099: false, conflict: false };
  const bizLabels = { sole_prop: 'Sole Proprietor', llc: 'LLC', s_corp: 'S-Corp', c_corp: 'C-Corp', partnership: 'Partnership' };
  const classification = (window.WorkerClass ? window.WorkerClass.label(_wcRes.classification, '—') : '—')
    + (_wcRes.conflict ? ' — CONFLICT: an older record says otherwise. Edit, pick the classification from the list, and Save to confirm'
      : (!_wcRes.inSync && _wcRes.classification ? ' — unconfirmed: Edit, pick it from the list, and Save to confirm' : ''));
  const bizType = bizLabels[emp.businessType] || '—';
  const sec = emp.secureTax || {};
  const tinDisplay = sec.tinType === 'ein' ? maskEIN(sec.ein) : (sec.ssn ? maskSSN(sec.ssn) : '—');
  const tinLabel = sec.tinType === 'ein' ? 'EIN' : 'SSN';
  const w9CollectedAt = sec.w9CollectedAt;
  const w9Status = sec.w9Collected
    ? `<span style="color:#16A34A;">Collected${w9CollectedAt ? ' — ' + (w9CollectedAt.toDate ? w9CollectedAt.toDate() : new Date(w9CollectedAt)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}</span>`
    : '<span style="color:#DC2626;">Not Collected</span>';

  const showBiz = _wcRes.is1099;
  const address = [sec.taxStreet, sec.taxCity, sec.taxState, sec.taxZip].filter(Boolean).join(', ') || '—';

  return `
    <div style="display:flex;justify-content:flex-end;margin-bottom:var(--spacing-sm);">
      <button class="btn btn-sm btn-secondary" onclick="window.HRModule.editTaxTab('${emp.id}')" style="padding:4px 12px;font-size:0.75rem;">
        <span class="material-icons" style="font-size:14px;">edit</span> Edit
      </button>
    </div>
    <div style="margin-bottom:var(--spacing-md);">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">receipt_long</span> Tax Classification
        </div>
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 var(--spacing-md);">
          ${fieldRow('Classification', classification)}
          ${fieldRow('Legal Name', emp.legalName)}
          ${showBiz ? fieldRow('Business Name', sec.businessName) : ''}
          ${showBiz ? fieldRow('Business Type', bizType) : ''}
          ${fieldRow(`${tinLabel}`, tinDisplay)}
        </div>
        <div style="display:flex;justify-content:space-between;padding:6px 0;">
          <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;">W-9 Status</span>
          <span style="font-size:0.8rem;font-weight:500;">${w9Status}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding:6px 0;">
          <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;">W-4 Withholding</span>
          <span style="font-size:0.8rem;font-weight:500;">${sec.hrW4Exempt ? '<span style="color:#D97706;">Claimed Exempt</span>' : '<span style="color:var(--text-secondary);">Standard</span>'}</span>
        </div>
        ${fieldRow('Mailing Address', address)}
      </div>
    </div>
  `;
}

async function editTaxTab(empId) {
  const rawEmp = HRState.people.find(p => p.id === empId);
  if (!rawEmp) return;
  // ensureSecureTax mutates/caches secureTax on the REAL HRState entry; the
  // comp overlay is applied on a copy afterwards (TASK-1481 — the FLSA select
  // below pre-fills from flsaStatus, a comp field).
  await ensureSecureTax(rawEmp);
  const emp = _hrComp(rawEmp);
  const sec = emp.secureTax || {};
  const inputStyle = 'border:1px solid var(--border-light);border-radius:6px;padding:6px 10px;font-size:0.82rem;background:var(--bg-primary);color:var(--text-primary);width:100%;box-sizing:border-box;';
  const labelStyle = 'font-size:0.78rem;color:var(--text-secondary);font-weight:600;margin-bottom:2px;';
  const field = (label, id, value, type = 'text') => `
    <div style="margin-bottom:8px;">
      <div style="${labelStyle}">${label}</div>
      <input type="${type}" id="${id}" value="${escapeHtml(value || '')}" style="${inputStyle}" />
    </div>`;
  const selectField = (label, id, value, options) => `
    <div style="margin-bottom:8px;">
      <div style="${labelStyle}">${label}</div>
      <select id="${id}" style="${inputStyle}">
        ${options.map(o => `<option value="${o.value}" ${value === o.value ? 'selected' : ''}>${o.label}</option>`).join('')}
      </select>
    </div>`;

  const container = document.getElementById('hr-detail-tab-tax');
  if (!container) return;

  container.innerHTML = `
    <div style="padding:var(--spacing-sm);">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
        <div style="font-weight:700;font-size:1rem;color:var(--text-primary);">Edit Tax & Classification</div>
        <div style="display:flex;gap:8px;">
          <button class="btn btn-sm btn-secondary" onclick="window.HRModule.cancelTaxEdit('${empId}')">Cancel</button>
          <button class="btn btn-sm btn-primary" id="btn-save-tax" onclick="window.HRModule.saveTaxFields('${empId}')">
            <span class="material-icons" style="font-size:14px;">save</span> Save
          </button>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 var(--spacing-md);">
        ${/* TASK-9178 — pre-filled from the ONE rule, so a person classified
             only on the legacy Worker Type field shows (and re-saves) what
             payroll actually treats them as, rather than a blank. */''}
        ${_hrClassSelect(selectField, 'Tax Classification', 'tax-classification', emp, 'Select...')}
        ${field('Legal Name', 'tax-legal-name', emp.legalName)}
        ${field('Business Name', 'tax-business-name', sec.businessName)}
        ${selectField('Business Type', 'tax-business-type', emp.businessType || '', [
          { value: '', label: 'Select...' },
          { value: 'sole_prop', label: 'Sole Proprietor' },
          { value: 'llc', label: 'LLC' },
          { value: 's_corp', label: 'S-Corp' },
          { value: 'c_corp', label: 'C-Corp' },
          { value: 'partnership', label: 'Partnership' }
        ])}
        ${selectField('TIN Type', 'tax-tin-type', sec.tinType || '', [
          { value: '', label: 'Select...' },
          { value: 'ssn', label: 'SSN' },
          { value: 'ein', label: 'EIN' }
        ])}
        ${field('EIN', 'tax-ein', sec.ein)}
      </div>
      <div style="margin:8px 0;">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
          <input type="checkbox" id="tax-same-address" onchange="window.HRModule.fillTaxFromHomeAddress('${empId}', this.checked)" />
          <span style="font-size:0.82rem;font-weight:600;">Same as home address</span>
          ${emp.address ? `<span style="font-size:0.75rem;color:var(--text-secondary);">(${escapeHtml(emp.address)})</span>` : ''}
        </label>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 var(--spacing-md);">
        ${field('Street Address', 'tax-street', sec.taxStreet)}
        ${field('City', 'tax-city', sec.taxCity)}
        ${field('State', 'tax-state', sec.taxState)}
        ${field('ZIP', 'tax-zip', sec.taxZip)}
      </div>
      <div style="margin-top:8px;">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
          <input type="checkbox" id="tax-w9-collected" ${sec.w9Collected ? 'checked' : ''} />
          <span style="font-size:0.82rem;font-weight:600;">W-9 Collected</span>
        </label>
      </div>
      <div style="margin:8px 0;">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
          <input type="checkbox" id="tax-w4-exempt" ${sec.hrW4Exempt ? 'checked' : ''} />
          <span style="font-size:0.82rem;font-weight:600;">Claimed Exempt on W-4</span>
          <span style="font-size:0.75rem;color:var(--text-secondary);">(no federal income tax withheld)</span>
        </label>
        <p style="margin:4px 0 0;font-size:0.75rem;color:var(--text-secondary);line-height:1.4;">
          Shows on the payroll export so your CPA knows who filed exempt. This is the employee's federal tax-withholding election — separate from FLSA overtime status below.
        </p>
      </div>
      <div style="margin-bottom:8px;">
        <div style="${labelStyle}">FLSA Status</div>
        <select id="tax-flsa-status" style="${inputStyle}" data-prev-value="${resolveFlsaStatus(emp)}" onchange="window.HRModule && window.HRModule.warnFlsaChange && window.HRModule.warnFlsaChange(this);">
          ${flsaStatusOptionsHtml(emp)}
        </select>
        <p style="margin:4px 0 0;font-size:0.75rem;color:var(--text-secondary);line-height:1.4;">
          ${flsaStatusHelpHtml()}
        </p>
      </div>
    </div>`;
}

function fillTaxFromHomeAddress(empId, checked) {
  const emp = HRState.people.find(p => p.id === empId);
  if (!emp || !checked) {
    // Uncheck — clear the fields
    if (!checked) {
      document.getElementById('tax-street').value = '';
      document.getElementById('tax-city').value = '';
      document.getElementById('tax-state').value = '';
      document.getElementById('tax-zip').value = '';
    }
    return;
  }
  const addr = (emp.address || '').trim();
  if (!addr) {
    window.showToast?.('No home address on file — edit employee info first', 'error');
    document.getElementById('tax-same-address').checked = false;
    return;
  }
  // Try to parse "Street, City, ST ZIP" or "Street City ST ZIP" or "Street City ST"
  // Common formats: "602 53rd St E Apt 201 Williston ND 58801" or "123 Main St, Williston, ND 58801"
  const zipMatch = addr.match(/\b(\d{5}(-\d{4})?)\s*$/);
  const zip = zipMatch ? zipMatch[1] : '';
  const withoutZip = zip ? addr.slice(0, addr.lastIndexOf(zip)).trim().replace(/,\s*$/, '') : addr;
  // State is last 2-letter word
  const stateMatch = withoutZip.match(/\b([A-Z]{2})\s*$/i);
  const state = stateMatch ? stateMatch[1].toUpperCase() : '';
  const withoutState = state ? withoutZip.slice(0, withoutZip.length - state.length).trim().replace(/,\s*$/, '') : withoutZip;
  // If comma-separated, split on last comma for city
  const commaIdx = withoutState.lastIndexOf(',');
  let street, city;
  if (commaIdx > 0) {
    street = withoutState.slice(0, commaIdx).trim();
    city = withoutState.slice(commaIdx + 1).trim();
  } else {
    // No comma — try splitting on last known city word (best effort: last 1-2 words before state)
    const words = withoutState.split(/\s+/);
    if (words.length >= 3) {
      city = words.pop();
      street = words.join(' ');
    } else {
      street = withoutState;
      city = '';
    }
  }
  document.getElementById('tax-street').value = street;
  document.getElementById('tax-city').value = city;
  document.getElementById('tax-state').value = state;
  document.getElementById('tax-zip').value = zip;
}

function cancelTaxEdit(empId) {
  const emp = HRState.people.find(p => p.id === empId);
  if (!emp) return;
  const container = document.getElementById('hr-detail-tab-tax');
  if (container) container.innerHTML = buildTaxTabHTML(emp);
}

async function saveTaxFields(empId) {
  const btn = document.getElementById('btn-save-tax');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const val = (id) => document.getElementById(id)?.value?.trim() || '';
    const w9Collected = document.getElementById('tax-w9-collected')?.checked || false;
    const w4Exempt = document.getElementById('tax-w4-exempt')?.checked || false;
    const flsaStatus = val('tax-flsa-status') || 'non_exempt';
    // TASK-9394 — mca_exempt is OT-exempt too (hours still tracked).
    const overtimeExempt = flsaStatus === 'ot_exempt' || flsaStatus === 'mca_exempt' || flsaStatus === 'exempt';

    // Capture before-state for audit log on FLSA / OT-exempt changes (TASK 2026-05-07).
    // Person doc has no field-level history, and back-pay liability hinges on
    // when these flags flipped — log who/when/before/after so future
    // misclassification investigations don't have to triangulate from updatedAt.
    // TASK-1481 — comp-first "before" so the audit diff stays truthful after
    // the fields leave the person doc.
    const beforeEmp = _hrComp(HRState.people.find(p => p.id === empId) || {});
    const flsaBefore = beforeEmp.flsaStatus || (beforeEmp.hrOvertimeExempt ? 'ot_exempt' : 'non_exempt');
    const overtimeExemptBefore = beforeEmp.hrOvertimeExempt === true;
    // TASK-9178 — the ONE rule. Refuse rather than write a classification
    // without it (a missing select option set would otherwise save a blank).
    if (!window.WorkerClass) throw new Error('Worker classification rule not loaded — reload the page.');
    const _wcBefore = window.WorkerClass.resolve(beforeEmp);
    const taxClassBefore = _wcBefore.classification;
    // TASK-9178 COD r2 — null unless the classification really changed or the
    // user deliberately picked one; an FLSA/W-9-only save leaves both fields
    // (and any CLASS CONFLICT) exactly as they were.
    const _taxSel = document.getElementById('tax-classification');
    const _wcTaxWrite = _taxSel
      ? window.WorkerClass.writeForSave(beforeEmp, val('tax-classification') || null, _taxSel.dataset.touched === '1')
      : null;
    const w4ExemptBefore = beforeEmp.secureTax?.hrW4Exempt === true;

    // Preserve existing w9CollectedAt or set new one
    let w9CollectedAt = null;
    if (w9Collected) {
      const existingEmp = HRState.people.find(p => p.id === empId);
      w9CollectedAt = existingEmp?.secureTax?.w9CollectedAt || Timestamp.now();
    }

    // Non-PII classification fields stay on the person doc.
    // TASK-1481 (strip decided 2026-08-13) — flsaStatus/hrOvertimeExempt are
    // EXCLUDED from the strip and INTENTIONALLY remain here on `people`: a
    // driver's own My-Timesheet OT calc reads them (clock.js isPayrollExempt)
    // and must match payroll, and a driver-PIN session can't read peopleComp.
    // They dual-write to peopleComp too (so an authorized editor's change
    // propagates); the people-doc denylist deliberately does NOT list them.
    // Only the 8 dollar/pay fields are denied on `people`.
    const personUpdate = {
      // TASK-9178 — taxClassification AND its derived workerType mirror, from
      // one value, so the pair can never disagree again (absent = untouched).
      ...(_wcTaxWrite || {}),
      legalName: val('tax-legal-name') || null,
      businessType: val('tax-business-type') || null,
      flsaStatus: flsaStatus,
      hrOvertimeExempt: overtimeExempt,
    };

    // Tax PII goes to the admin-only secure/tax subcollection.
    const secureUpdate = {
      businessName: val('tax-business-name') || null,
      tinType: val('tax-tin-type') || null,
      ein: val('tax-ein') || null,
      taxStreet: val('tax-street') || null,
      taxCity: val('tax-city') || null,
      taxState: val('tax-state') || null,
      taxZip: val('tax-zip') || null,
      w9Collected: w9Collected,
      w9CollectedAt: w9CollectedAt,
      // W-4 exempt election — confidential (IRS Pub. 15 §9), admin-only.
      hrW4Exempt: w4Exempt,
    };

    // TASK-815 — the Tax tab is secure-tax PII; only write peopleTax if the user
    // can EDIT it (admin OR non-view-only hr-directory holder). A view-only
    // hr-directory member can SEE the tab but the rules deny the write, so skip
    // it rather than throw a raw "insufficient permissions".
    const taxWrites = [updateDoc(doc(db, 'tenants', HRState.tenantId, 'people', empId), personUpdate)];
    if (_hrCanEditSecureTax()) taxWrites.push(saveSecureTax(empId, secureUpdate));
    await Promise.all(taxWrites);

    // TASK-1481 — DUAL-WRITE the comp fields (flsaStatus / hrOvertimeExempt)
    // to peopleComp. Once a comp doc exists its fields WIN over the person doc
    // (accessor merge), so a failed comp write means payroll's exempt gate can
    // read a STALE value — never silent; permission-denied gets
    // actionable-warning copy, the rest a retry error.
    const _compRes = await (window.PersonComp?.writeComp?.(
      HRState.tenantId, empId, { flsaStatus: flsaStatus, hrOvertimeExempt: overtimeExempt }
    ) ?? { ok: true });
    if (!_compRes.ok) {
      if (_compRes.code === 'permission-denied') {
        window.showToast?.("Saved, but your access level couldn't update the secure pay store. If payroll shows the old FLSA status, have an admin re-save this employee.", 'warning');
      } else {
        window.showToast?.('FLSA status saved to the legacy record but NOT the secure pay store — payroll may use a stale value. Retry the save.', 'error');
      }
    }

    // Field-level audit log — only fires when payroll-impacting fields actually
    // changed. Captures actor + before/after so future back-pay investigations
    // can answer "when did this flip?" without triangulating from updatedAt.
    const flsaChanged = flsaStatus !== flsaBefore;
    const otExemptChanged = overtimeExempt !== overtimeExemptBefore;
    // TASK-9178 — any classification write (a change, or a deliberate confirm
    // of a conflicted/legacy record) is money-governing and leaves an old → new row.
    const taxClassChanged = !!_wcTaxWrite;
    const w4ExemptChanged = w4Exempt !== w4ExemptBefore;
    if (flsaChanged || otExemptChanged || taxClassChanged || w4ExemptChanged) {
      const changes = {};
      if (flsaChanged) changes.flsaStatus = { before: flsaBefore, after: flsaStatus };
      if (otExemptChanged) changes.hrOvertimeExempt = { before: overtimeExemptBefore, after: overtimeExempt };
      if (taxClassChanged) {
        changes.taxClassification = { before: taxClassBefore, after: personUpdate.taxClassification };
        changes.workerClassRaw = {
          before: { taxClassification: beforeEmp.taxClassification ?? null, workerType: beforeEmp.workerType ?? null, conflict: _wcBefore.conflict },
          after: { taxClassification: personUpdate.taxClassification, workerType: personUpdate.workerType },
        };
      }
      if (w4ExemptChanged) changes.hrW4Exempt = { before: w4ExemptBefore, after: w4Exempt };
      try {
        await window.logAudit?.('hr', 'EMPLOYEE_TAX_CLASSIFICATION_CHANGED', {
          personId: empId,
          personName: beforeEmp.name || null,
          changes,
          via: 'tax-tab',
        });
      } catch (e) {
        console.warn('[HR] audit log write failed (non-fatal):', e?.message);
      }
    }

    // Update local state
    const emp = HRState.people.find(p => p.id === empId);
    if (emp) Object.assign(emp, personUpdate);

    window.showToast?.('Tax info saved', 'success');
    cancelTaxEdit(empId);
    renderEmployeeDirectory();
  } catch (error) {
    console.error('[HR] Error saving tax fields:', error);
    window.showToast?.('Error saving tax info', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
  }
}

function buildSignaturesTabHTML(emp) {
  return `
    <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);">
      <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">draw</span> Document Signatures
      </div>
      <button class="btn btn-sm btn-primary" onclick="window.HRModule.showSendForSignature('${emp.id}')" style="padding:6px 12px;font-size:0.75rem;">
        <span class="material-icons" style="font-size:14px;">note_add</span> New Document
      </button>
    </div>

    <div id="hr-pending-sign-docs">
      <div id="hr-pending-sign-list">
        <div style="padding:12px;color:var(--text-secondary);font-size:0.82rem;text-align:center;">Loading...</div>
      </div>
    </div>

    <div id="hr-signed-docs-section" style="display:none;margin-top:var(--spacing-md);">
      <div style="font-weight:600;font-size:0.82rem;color:var(--text-secondary);margin-bottom:var(--spacing-xs);display:flex;align-items:center;gap:4px;">
        <span class="material-icons" style="font-size:14px;color:#059669;">task_alt</span> Completed
      </div>
      <div id="hr-signed-docs-list"></div>
    </div>
  `;
}

// TASK-1640 — how the signer's initials were captured. Typed and drawn
// initials are stored as the same PNG, so the method is only knowable from
// the flag submitDocumentSignature records on the signer's field. Reads the
// live signatureFields entry first, then the person's denormalized copy for
// historical rows. Records signed before this shipped simply have neither and
// show no line — absent means "not captured", never "drawn".
// Returns one of two fixed labels, so nothing off the stored record reaches
// the markup. A literal-object lookup would inherit Object.prototype, so a
// stored value of 'constructor' would render a function body into the row;
// an explicit compare can only ever return a constant.
function _initialsMethodText(m) {
  if (m === 'typed') return 'Initials typed';
  if (m === 'drawn') return 'Initials hand-drawn';
  return '';
}
function _initialsMethodLabel(d) {
  let m = null;
  if (Array.isArray(d?.signatureFields)) {
    const f = d.signatureFields.find(x => (x.type || 'signature') === 'signature' && x.initialsMethod);
    if (f) m = f.initialsMethod;
  }
  if (!m) m = d?.initialsMethod;
  return _initialsMethodText(m);
}

// TASK-1052 — one green "signed" row (used for live completed signDocuments AND
// historical person.signedDocuments entries whose collection doc is gone).
function _signedDocRowHtml(d) {
  // TASK-1640 — resolved once; used in the "Signed <date>" caption below.
  const methodLabel = _initialsMethodLabel(d);
  const signedTs = d.completedAt || d.signedAt;
  const signedDate = signedTs?.toDate?.() ? signedTs.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : (signedTs ? new Date(signedTs).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A');
  // TASK-1118 — View goes through the getSignDocBytes byte-stream callable
  // (stored signed URLs rot on SA key rotation). The row is cached by key in
  // _signDocViewCache; legacy entries with only a signedUrl get their storage
  // path parsed from it inside viewSignedDoc.
  const viewKey = d._docId || d.documentId || '';
  const canView = !!(viewKey && (d.signedStoragePath || d.signedUrl));
  return `
    <div style="display:flex;align-items:center;gap:10px;padding:10px;background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:6px;">
      <span class="material-icons" style="font-size:20px;color:#059669;">check_circle</span>
      <div style="flex:1;min-width:0;">
        <div style="font-weight:600;font-size:0.85rem;">${escapeHtml(d.title || 'Document')}</div>
        <div style="font-size:0.75rem;color:var(--text-secondary);">Signed ${signedDate}${methodLabel ? ' &middot; ' + methodLabel : ''}</div>
      </div>
      ${canView ? `<button class="btn btn-sm btn-secondary" onclick="window.HRModule.viewSignedDoc('${escJsAttr(viewKey)}', this)" style="padding:4px 8px;font-size:0.75rem;">
        <span class="material-icons" style="font-size:14px;">picture_as_pdf</span> View
      </button>` : ''}
      <!-- TASK-1634 (4)(5) — Chris's call: signed compliance records get NO
           trash icon at all, not even a disabled/grey one. Recoloring or
           merely disabling a control that destroys a signed retention
           artifact makes it more inviting, not safer. The real backstop is
           firestore.rules (status:'completed'/'signed' can't be deleted by
           any client role) — this is belt, rules are suspenders. -->
    </div>`;
}

// TASK-1052 — the Document Signatures panel is now LIVE (onSnapshot) instead of
// a one-shot getDocs: signing a doc flips the panel to ✅ signed with the
// signed-PDF link WITHOUT a manual refresh, and completed docs no longer
// vanish from the list (the old query filtered to sent/partially_signed only).
let _signDocsUnsub = null;
function loadPendingSignDocs(personId) {
  const list = document.getElementById('hr-pending-sign-list');
  if (!list) return;
  if (_signDocsUnsub) { try { _signDocsUnsub(); } catch (_) {} _signDocsUnsub = null; }

  const tenantId = HRState.tenantId === 'demo-tenant' ? 'demo' : HRState.tenantId;
  const q = query(collection(db, `tenants/${tenantId}/signDocuments`),
    where('personId', '==', personId));
  _signDocsUnsub = onSnapshot(q, (snap) => {
    // Self-clean: if the panel is no longer in the DOM (detail closed / tab
    // switched away), drop the listener.
    if (!document.getElementById('hr-pending-sign-list')) {
      try { _signDocsUnsub?.(); } catch (_) {}
      _signDocsUnsub = null;
      return;
    }
    _renderSignDocsPanel(personId, snap.docs.map(d => ({ id: d.id, ...d.data() })));
  }, (err) => {
    console.error('[HR] signDocuments subscribe error:', err);
    const el = document.getElementById('hr-pending-sign-list');
    if (el) el.innerHTML = '<div style="padding:12px;color:var(--text-secondary);font-size:0.82rem;text-align:center;">No pending documents</div>';
    _renderSignedDocsSection(personId, []);
  });
}

function _renderSignDocsPanel(personId, docs) {
  const list = document.getElementById('hr-pending-sign-list');
  if (!list) return;

  const pendingDocs = docs.filter(d => d.status === 'sent' || d.status === 'partially_signed');
  const completedDocs = docs.filter(d => d.status === 'completed' || d.status === 'signed');
  // TASK-1634 🔴 blast-radius trap — a soft-cancelled doc's status ('cancelled')
  // matched NEITHER bucket above, so it silently vanished from the panel: the
  // exact disappearing-record symptom this task exists to fix, reproduced by a
  // different mechanism. Give it its own branch (verification item (e)).
  const cancelledDocs = docs.filter(d => d.status === 'cancelled');

  _renderSignedDocsSection(personId, completedDocs);

  try {
    if (pendingDocs.length === 0 && cancelledDocs.length === 0) {
      list.innerHTML = '<div style="padding:12px;color:var(--text-secondary);font-size:0.82rem;text-align:center;">No pending documents</div>';
      return;
    }

    const cancelledHtml = cancelledDocs.sort((a, b) => {
      const aTime = a.cancelledAt?.toDate?.() || new Date(0);
      const bTime = b.cancelledAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    }).map(data => {
      const cancelledDate = data.cancelledAt?.toDate?.() ? data.cancelledAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
      return `
        <div style="display:flex;align-items:center;gap:10px;padding:10px;background:#F8FAFC;border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:6px;opacity:0.8;">
          <span class="material-icons" style="font-size:20px;color:#94A3B8;">cancel</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;font-size:0.85rem;">${escapeHtml(data.title || 'Document')}</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);">Withdrawn ${cancelledDate}</div>
          </div>
        </div>`;
    }).join('');

    const pendingHtml = pendingDocs.sort((a, b) => {
      const aTime = a.sentAt?.toDate?.() || new Date(0);
      const bTime = b.sentAt?.toDate?.() || new Date(0);
      return bTime - aTime;
    }).map(data => {
      const d = { id: data.id };
      // TASK-1118 — cache the doc so a partially-signed row's PDF button can
      // resolve signedStoragePath through viewSignedDoc (no stored URL).
      _signDocViewCache.set(String(data.id), data);
      const sentDate = data.sentAt?.toDate?.() ? data.sentAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
      const expired = data.expiresAt?.toDate?.() && data.expiresAt.toDate() < new Date();

      // Count signatures — SIGNER fields only (TASK-1051 adds date/initials
      // field types to the same array; they aren't signers)
      const fields = (data.signatureFields || []).filter(f => (f.type || 'signature') === 'signature');
      const totalSigners = fields.length;
      const signedCount = fields.filter(f => f.status === 'signed').length;

      // Overall status flag
      let statusIcon, statusColor, statusBg, statusBorder, statusLabel;
      if (expired) {
        statusIcon = 'timer_off';
        statusColor = '#DC2626';
        statusBg = '#FEF2F2';
        statusBorder = '#FECACA';
        statusLabel = 'Expired';
      } else if (data.status === 'partially_signed') {
        statusIcon = 'pending_actions';
        statusColor = '#2563EB';
        statusBg = '#EFF6FF';
        statusBorder = '#BFDBFE';
        statusLabel = `${signedCount} of ${totalSigners} signed`;
      } else {
        statusIcon = 'send';
        statusColor = '#F59E0B';
        statusBg = '#FFFBEB';
        statusBorder = '#FDE68A';
        statusLabel = 'Sent';
      }

      // Signature progress flag
      let sigFlagHtml = '';
      if (totalSigners > 0) {
        if (signedCount === 0) {
          sigFlagHtml = `<div style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:6px;font-size:0.7rem;font-weight:600;background:#F1F5F9;color:#64748B;margin-top:6px;">
            <span class="material-icons" style="font-size:12px;">flag</span> No signatures yet
          </div>`;
        } else if (signedCount < totalSigners) {
          sigFlagHtml = `<div style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:6px;font-size:0.7rem;font-weight:600;background:#DBEAFE;color:#1E40AF;margin-top:6px;">
            <span class="material-icons" style="font-size:12px;">flag</span> ${signedCount} of ${totalSigners} signed
          </div>`;
        }
      }

      // Per-signer badges
      let signerBadges = '';
      if (totalSigners > 0) {
        signerBadges = '<div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:4px;">' + fields.map((f, i) => {
          const signed = f.status === 'signed';
          // TASK-1640 — a signer who has already signed carries how their
          // initials were captured; show it on their own badge so a
          // part-signed doc surfaces it without waiting for completion.
          const methodTxt = signed ? _initialsMethodText(f.initialsMethod) : '';
          return `<span style="display:inline-flex;align-items:center;gap:3px;padding:2px 6px;border-radius:4px;font-size:0.68rem;font-weight:600;background:${signed ? '#DCFCE7' : '#F1F5F9'};color:${signed ? '#166534' : '#64748B'};">
            <span class="material-icons" style="font-size:11px;">${signed ? 'check_circle' : 'radio_button_unchecked'}</span>
            ${escapeHtml(f.signerName || ('Signer ' + (i + 1)))}${methodTxt ? ' &middot; ' + methodTxt : ''}
          </span>`;
        }).join('') + '</div>';
      }

      // TASK-1634 (2) — surface a real delivery failure on the row itself, not
      // just a toast that's gone in 1.8s. deliveryResults is written by
      // sendWithFields right after the send loop; a doc from before this task
      // (or one whose persist-back failed) simply has none, which reads as
      // no-flag rather than a false "all clear."
      let deliveryFlagHtml = '';
      const anyDeliveryFailed = Array.isArray(data.deliveryResults) &&
        data.deliveryResults.some(r => r.sms === 'failed' || r.email === 'failed');
      if (anyDeliveryFailed) {
        deliveryFlagHtml = `<div style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border-radius:6px;font-size:0.7rem;font-weight:600;background:#FEF2F2;color:#DC2626;margin-top:6px;">
          <span class="material-icons" style="font-size:12px;">error_outline</span> Delivery failed for one or more signers
        </div>`;
      }

      return `
        <div style="display:flex;align-items:flex-start;gap:10px;padding:12px;background:${statusBg};border:1px solid ${statusBorder};border-radius:var(--radius-md);margin-bottom:6px;">
          <span class="material-icons" style="font-size:22px;color:${statusColor};margin-top:1px;">${statusIcon}</span>
          <div style="flex:1;min-width:0;">
            <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
              <span style="font-weight:600;font-size:0.85rem;">${escapeHtml(data.title || 'Document')}</span>
              <span style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;border-radius:10px;font-size:0.68rem;font-weight:700;letter-spacing:0.02em;background:${expired ? '#FEE2E2' : data.status === 'partially_signed' ? '#DBEAFE' : '#FEF3C7'};color:${statusColor};">${statusLabel}</span>
            </div>
            <div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;">Sent ${sentDate} via ${data.sentVia || 'link'}</div>
            ${sigFlagHtml}
            ${deliveryFlagHtml}
            ${signerBadges}
          </div>
          <div style="display:flex;gap:6px;align-items:center;">
            ${(data.signedStoragePath || data.signedUrl) ? `<button class="btn btn-sm btn-secondary" onclick="window.HRModule.viewSignedDoc('${escJsAttr(d.id)}', this)" style="padding:4px 8px;font-size:0.75rem;">
              <span class="material-icons" style="font-size:14px;">picture_as_pdf</span>
            </button>` : (data.pdfUrl ? `<a href="${_safeHttpUrl(data.pdfUrl)}" target="_blank" class="btn btn-sm btn-secondary" style="padding:4px 8px;font-size:0.75rem;">
              <span class="material-icons" style="font-size:14px;">picture_as_pdf</span>
            </a>` : '')}
            <button class="btn-icon" onclick="window.HRModule.withdrawSignDocument('${d.id}','${personId}')" title="Withdraw — the signer's link stops working" style="color:var(--status-danger);padding:2px;">
              <span class="material-icons" style="font-size:16px;">delete_outline</span>
            </button>
          </div>
        </div>`;
    }).join('');

    list.innerHTML = pendingHtml + cancelledHtml;
  } catch (err) {
    console.error('[HR] _renderSignDocsPanel error:', err);
    list.innerHTML = '<div style="padding:12px;color:var(--text-secondary);font-size:0.82rem;text-align:center;">No pending documents</div>';
  }
}

// TASK-1052 — render the "Completed" section: live completed/signed
// signDocuments (with signed-PDF link + delete) merged with historical
// person.signedDocuments entries whose collection doc no longer exists.
function _renderSignedDocsSection(personId, completedDocs) {
  const section = document.getElementById('hr-signed-docs-section');
  const listEl = document.getElementById('hr-signed-docs-list');
  if (!section || !listEl) return;
  try {
    const emp = HRState.people.find(p => p.id === personId);
    const liveIds = new Set(completedDocs.map(d => d.id));
    const historical = (emp?.signedDocuments || []).filter(d => d.documentId && !liveIds.has(d.documentId));
    const rows = [
      ...completedDocs.map(d => ({ ...d, _docId: d.id, _personId: personId })),
      ...historical,
    ].sort((a, b) => {
      const at = (a.completedAt || a.signedAt)?.toDate?.() || new Date(a.completedAt || a.signedAt || 0);
      const bt = (b.completedAt || b.signedAt)?.toDate?.() || new Date(b.completedAt || b.signedAt || 0);
      return bt - at;
    });
    if (rows.length === 0) {
      section.style.display = 'none';
      listEl.innerHTML = '';
      return;
    }
    // TASK-1118 — cache the row data so the View button's onclick only carries
    // the document key (viewSignedDoc resolves signedStoragePath / legacy URL).
    rows.forEach(r => {
      const k = r._docId || r.documentId;
      if (k) _signDocViewCache.set(String(k), r);
    });
    section.style.display = '';
    listEl.innerHTML = rows.map(_signedDocRowHtml).join('');
  } catch (err) {
    console.error('[HR] _renderSignedDocsSection error:', err);
  }
}

// ============================================================
// TASK-1638 — HR DOCUMENT LIBRARY (tenant-scoped reusable e-sign documents)
// ============================================================
// Parity port of the compliance portal's advisor Document Library
// (TASK-963 Slice 2, compliance-partner.js renderLibraryView) onto the
// carrier/HR side. The portal is the reference implementation; HR is what
// changes. Before this, HR's Signatures tab could ONLY send a freshly
// uploaded PDF — the same handbook acknowledgement was re-uploaded and
// re-placed for every employee, one at a time.
//
// Data model — tenants/{tid}/signDocTemplates/{docId}
//   { title, storagePath, pdfUrl, signatureFields (null until saved),
//     createdAt, updatedAt, createdByUid }
// Storage — hr/{tid}/signDocTemplates/{docId}_{ts}_{name}.pdf
//   Already governed by the existing hr/{tenantId}/{employeeId}/{fileName}
//   storage rule (PDF-only, <10MB) — the same shape the send path
//   hr/{tid}/signDocs/... already uses. No storage.rules change.
//
// 🔴 BLANK TEMPLATES ONLY — a saved placement stores GEOMETRY ONLY.
// _signLibStripFields drops signerName / signerPhone / signerEmail /
// accessToken / personId before write. A template is reused for the NEXT
// employee; carrying the previous signer's contact details into it would
// leak one person's phone/email onto another person's send. This mirrors
// the portal's "never a doc pre-filled with a client's info" rule.
//
// Unlike the PARTNER library this needs no callable: the partner library is
// partner-global under deny-all rules (server-only), whereas the carrier
// side already writes tenants/{tid}/signDocuments + hr/ storage straight
// from the client. firestore.rules gates signDocTemplates with the SAME
// dispatch-module arm as signDocuments, so no role gains a new capability.
// Partner-scoped data (_partnerDocLibrary) is never read here.
const _SIGN_LIB_MAX_DOCS = 200;
const _SIGN_LIB = { docs: null, busy: false };

/** Geometry-only projection of placed fields — the ONLY thing a template stores. */
function _signLibStripFields(fields) {
  return (Array.isArray(fields) ? fields : []).map((f) => {
    const type = f.type || 'signature';
    const out = {
      id: String(f.id || ''),
      type: String(type),
      page: Math.max(0, Math.floor(Number(f.page) || 0)),
      x: Math.max(0, Math.min(100, Number(f.x) || 0)),
      y: Math.max(0, Math.min(100, Number(f.y) || 0)),
      width: Math.max(10, Math.min(800, Number(f.width) || 200)),
      height: Math.max(10, Math.min(400, Number(f.height) || 60)),
    };
    // Aux boxes keep their signer BINDING (an id like 'signer1'), never any
    // signer identity — the binding is remapped on load.
    if (type !== 'signature' && f.signerId) out.signerId = String(f.signerId);
    return out;
  }).filter((f) => f.id);
}

async function _signLibFetch(force) {
  if (_SIGN_LIB.docs && !force) return _SIGN_LIB.docs;
  const tenantId = HRState.tenantId;
  const snap = await getDocs(query(
    collection(db, 'tenants', tenantId, 'signDocTemplates'),
    limit(_SIGN_LIB_MAX_DOCS)
  ));
  const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  docs.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
  _SIGN_LIB.docs = docs;
  return docs;
}

/** Fill the "…or send a saved document" picker in the step-1 send modal. */
async function _populateSignLibraryPicker() {
  const escA = window.escAttr || escapeHtml;
  if (!document.getElementById('sign-doc-library')) return;
  let docs;
  try {
    docs = await _signLibFetch(false);
  } catch (err) {
    console.error('[HR] sign library load', err);
    return;
  }
  const cur = document.getElementById('sign-doc-library'); // stale guard — modal may have closed
  if (!cur) return;
  const keep = cur.value;
  cur.innerHTML = '<option value="">— Choose a saved document —</option>' + docs.map((d) => {
    const tpl = (Array.isArray(d.signatureFields) && d.signatureFields.length) ? ' · placement saved' : '';
    return `<option value="${escA(d.id)}">${escapeHtml(d.title || 'Document')}${escapeHtml(tpl)}</option>`;
  }).join('');
  if (keep && docs.some((d) => d.id === keep)) cur.value = keep;
}

/** Step-1 picker change — upload and library stay mutually exclusive so the
 *  send source is never ambiguous (portal: _cpSendSignature, same rule). */
function onSignLibrarySelect() {
  const sel = document.getElementById('sign-doc-library');
  if (!sel) return;
  _pendingSignLibraryDoc = null;
  if (!sel.value) return;
  const d = (_SIGN_LIB.docs || []).find((x) => x.id === sel.value);
  if (!d) {
    window.showToast?.('That saved document is no longer available — reopen this window', 'error');
    sel.value = '';
    return;
  }
  _pendingSignLibraryDoc = d;
  if (_pendingSignDocFile) clearSignDocFile();
  const t = document.getElementById('sign-doc-title');
  if (t && !t.value.trim()) t.value = d.title || '';
}

// ---- Library manager modal -------------------------------------------------

function showSignDocLibrary() {
  let modal = document.getElementById('modal-sign-doc-library');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-sign-doc-library';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `
    <div class="modal-content" style="max-width:640px;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
      <div class="modal-header" style="flex-shrink:0;">
        <h3><span class="material-icons">library_books</span> Document Library</h3>
        <button class="btn-icon" onclick="window.HRModule.closeSignDocLibrary()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:var(--spacing-md);overflow-y:auto;flex:1;min-height:0;">
        <div style="font-size:0.82rem;color:var(--text-secondary);margin-bottom:var(--spacing-md);">
          Blank PDFs you send for signature again and again — the handbook acknowledgement, a policy receipt, a company form.
          Pick one from the Send window instead of re-uploading it for every employee.
        </div>
        <div style="padding:12px;background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:var(--spacing-md);">
          <div class="form-group" style="margin-bottom:8px;">
            <label style="font-weight:600;font-size:0.82rem;color:var(--text-primary);">PDF document (max 10MB)</label>
            <input type="file" id="sign-lib-file" accept="application/pdf,.pdf" style="margin-top:6px;font-size:0.8rem;width:100%;">
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">
              <strong>Blank documents only</strong> — never upload one already filled in with an employee's information.
              The next person you send it to would inherit it.
            </div>
          </div>
          <div class="form-group" style="margin-bottom:8px;">
            <label for="sign-lib-title" style="font-weight:600;font-size:0.82rem;color:var(--text-primary);">Name</label>
            <input type="text" id="sign-lib-title" maxlength="160" placeholder="e.g. Employee Handbook Acknowledgement" class="form-control">
          </div>
          <div id="sign-lib-err" style="font-size:0.78rem;color:var(--status-danger);min-height:1px;margin-bottom:6px;"></div>
          <button class="btn btn-sm btn-primary" id="sign-lib-upload-btn" onclick="window.HRModule.signLibUpload()">
            <span class="material-icons" style="font-size:14px;">add</span> Add to library
          </button>
        </div>
        <div style="font-weight:700;font-size:0.82rem;color:var(--text-primary);margin-bottom:6px;">Your documents</div>
        <div id="sign-lib-list">
          <div style="padding:12px;color:var(--text-secondary);font-size:0.82rem;text-align:center;">Loading...</div>
        </div>
      </div>
      <div class="modal-form-footer" style="flex-shrink:0;">
        <button class="btn btn-secondary" onclick="window.HRModule.closeSignDocLibrary()">Done</button>
      </div>
    </div>
  `;
  modal.classList.remove('hidden');

  _signLibFetch(true).then(() => {
    if (document.getElementById('sign-lib-list')) _signLibPaintList();
  }).catch((err) => {
    console.error('[HR] signDocTemplates list error:', err);
    const l = document.getElementById('sign-lib-list');
    if (l) l.innerHTML = '<div style="padding:12px;color:var(--status-danger);font-size:0.82rem;text-align:center;">Couldn\'t load your library.</div>';
  });
}

function closeSignDocLibrary() {
  document.getElementById('modal-sign-doc-library')?.classList.add('hidden');
  // The step-1 picker may be stale (added/renamed/deleted) — repaint it.
  _populateSignLibraryPicker();
}

function _signLibPaintList() {
  const escA = window.escAttr || escapeHtml;
  const list = document.getElementById('sign-lib-list');
  if (!list) return;
  const docs = _SIGN_LIB.docs || [];
  if (!docs.length) {
    list.innerHTML = '<div style="padding:12px;color:var(--text-secondary);font-size:0.82rem;text-align:center;">No documents yet. Add the blank PDFs you send for signature most often.</div>';
    return;
  }
  list.innerHTML = docs.map((d) => {
    const hasTpl = Array.isArray(d.signatureFields) && d.signatureFields.length;
    const added = d.createdAt?.toDate?.()
      ? d.createdAt.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : '';
    const dl = d.pdfUrl
      ? `<a href="${_safeHttpUrl(d.pdfUrl)}" target="_blank" rel="noopener" class="btn btn-sm btn-secondary" style="padding:4px 8px;font-size:0.72rem;"><span class="material-icons" style="font-size:14px;">picture_as_pdf</span></a>`
      : '';
    return `
      <div style="display:flex;align-items:center;gap:8px;padding:10px;background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:6px;flex-wrap:wrap;">
        <div style="flex:1;min-width:160px;">
          <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">${escapeHtml(d.title || 'Document')}</div>
          <div style="font-size:0.74rem;color:var(--text-secondary);">${added ? 'Added ' + escapeHtml(added) : ''}</div>
        </div>
        <span style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;border-radius:10px;font-size:0.68rem;font-weight:700;background:${hasTpl ? '#DCFCE7' : '#F1F5F9'};color:${hasTpl ? '#166534' : '#475569'};" title="${hasTpl ? 'Signature placement saved — sends start from it' : 'Place a field on the first send and save it'}">
          <span class="material-icons" style="font-size:11px;">${hasTpl ? 'check_circle' : 'radio_button_unchecked'}</span>${hasTpl ? 'Placement saved' : 'No placement yet'}
        </span>
        ${dl}
        <button class="btn btn-sm btn-secondary" onclick="window.HRModule.signLibRename('${escJsAttr(d.id)}')" title="Rename" style="padding:4px 8px;font-size:0.72rem;">
          <span class="material-icons" style="font-size:14px;">edit</span>
        </button>
        <button class="btn-icon" onclick="window.HRModule.signLibDelete('${escJsAttr(d.id)}')" title="Delete from library" style="color:var(--status-danger);padding:2px;">
          <span class="material-icons" style="font-size:16px;">delete_outline</span>
        </button>
      </div>`;
  }).join('');
}

async function signLibUpload() {
  if (_SIGN_LIB.busy) return; // double-click guard
  const errEl = document.getElementById('sign-lib-err');
  const setErr = (m, ok) => { if (errEl) { errEl.style.color = ok ? '#059669' : 'var(--status-danger)'; errEl.textContent = m || ''; } };
  setErr('');
  const fileInput = document.getElementById('sign-lib-file');
  const file = fileInput && fileInput.files && fileInput.files[0];
  const title = (document.getElementById('sign-lib-title')?.value || '').trim().slice(0, 160);
  if (!file) return setErr('Choose a PDF to add.');
  // PDF-only — MIME *and* extension (mirrors the portal's _libUpload check).
  const type = (file.type || '').toLowerCase();
  if (type !== 'application/pdf' || !/\.pdf$/i.test(file.name || '')) return setErr('Only PDF files are allowed.');
  // 10MB — the storage-rule ceiling for hr/{tid}/... (the portal's 7MB cap
  // exists only because its upload rides a base64 callable; ours does not).
  if (file.size > 10 * 1024 * 1024) return setErr('PDF is too large — 10MB maximum.');
  if ((_SIGN_LIB.docs || []).length >= _SIGN_LIB_MAX_DOCS) {
    return setErr(`Library is full (max ${_SIGN_LIB_MAX_DOCS} documents) — delete some first.`);
  }

  _SIGN_LIB.busy = true;
  const btn = document.getElementById('sign-lib-upload-btn');
  const orig = btn ? btn.innerHTML : '';
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Adding...'; }
  try {
    const tenantId = HRState.tenantId;
    const safeName = (file.name || 'document.pdf').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120) || 'document.pdf';
    const docRef = doc(collection(db, 'tenants', tenantId, 'signDocTemplates'));
    const storagePath = `hr/${tenantId}/signDocTemplates/${docRef.id}_${Date.now()}_${safeName}`;
    await uploadBytes(ref(storage, storagePath), file, { contentType: 'application/pdf' });
    const pdfUrl = await getDownloadURL(ref(storage, storagePath));
    await setDoc(docRef, {
      title: title || safeName.replace(/\.pdf$/i, ''),
      storagePath,
      pdfUrl,
      signatureFields: null, // no template until a placement is saved
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      createdByUid: window.AppState?.currentUser?.uid || null,
    });
    if (fileInput) fileInput.value = '';
    const t = document.getElementById('sign-lib-title'); if (t) t.value = '';
    setErr('Added to your library.', true);
    await _signLibFetch(true);
    _signLibPaintList();
  } catch (err) {
    console.error('[HR] signLibUpload error:', err);
    setErr(err?.message ? String(err.message).replace(/^[A-Z_]+:\s*/, '') : 'Could not add the document.');
  } finally {
    _SIGN_LIB.busy = false;
    if (btn) { btn.disabled = false; btn.innerHTML = orig; }
  }
}

async function signLibRename(docId) {
  if (_SIGN_LIB.busy || !docId) return;
  const d = (_SIGN_LIB.docs || []).find((x) => x.id === docId);
  const next = window.prompt('New name for this document:', (d && d.title) || '');
  if (next == null) return; // cancelled
  const title = next.trim().slice(0, 160);
  if (!title) return;
  _SIGN_LIB.busy = true;
  try {
    await updateDoc(doc(db, 'tenants', HRState.tenantId, 'signDocTemplates', docId), {
      title, updatedAt: Timestamp.now(),
    });
    if (d) d.title = title;
    _signLibPaintList();
  } catch (err) {
    console.error('[HR] signLibRename error:', err);
    window.showToast?.('Could not rename the document', 'error');
  } finally { _SIGN_LIB.busy = false; }
}

async function signLibDelete(docId) {
  if (_SIGN_LIB.busy || !docId) return;
  const d = (_SIGN_LIB.docs || []).find((x) => x.id === docId);
  // Documents already SENT are unaffected: sendWithFields uploads its own copy
  // of the bytes into hr/{tid}/signDocs/, it never points at the template file.
  if (!confirm(`Delete "${(d && d.title) || 'this document'}" from the library? Documents already sent for signature are unaffected.`)) return;
  _SIGN_LIB.busy = true;
  try {
    // Firestore row FIRST, then the blob (COD Code Reviewer): the reverse
    // order leaves a listed template pointing at a file that no longer exists
    // whenever the Firestore delete fails. This way a failed blob delete leaves
    // only an unreferenced object that no UI ever surfaces.
    await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'signDocTemplates', docId));
    if (d?.storagePath) {
      try { await deleteObject(ref(storage, d.storagePath)); }
      catch (e) { console.warn('[HR] template file already gone:', e?.code || e); }
    }
    _SIGN_LIB.docs = (_SIGN_LIB.docs || []).filter((x) => x.id !== docId);
    if (_pendingSignLibraryDoc && _pendingSignLibraryDoc.id === docId) _pendingSignLibraryDoc = null;
    _signLibPaintList();
  } catch (err) {
    console.error('[HR] signLibDelete error:', err);
    window.showToast?.('Could not delete the document', 'error');
  } finally { _SIGN_LIB.busy = false; }
}

/** Bytes for the placement/send step: the step-1 upload, or a COPY of the
 *  library template. Returning a real File keeps sendWithFields unchanged —
 *  it always uploads its own object under hr/{tid}/signDocs/, so a template
 *  deleted later can never orphan a document that was already sent. */
async function _resolveSignSourceFile() {
  if (_pendingSignDocFile) return _pendingSignDocFile;
  const lib = _pendingSignLibraryDoc;
  if (!lib || !lib.pdfUrl) throw new Error('No PDF source selected');
  // COD Adversary residual — `pdfUrl` is a free-form string on the doc, so a
  // dispatcher-level insider with raw SDK access could point it anywhere and
  // this fetch would follow. It is written only by signLibUpload's own
  // getDownloadURL, so pin it to the Firebase Storage host before fetching.
  let libHost = '';
  try { libHost = new URL(String(lib.pdfUrl)).hostname; } catch (_) { libHost = ''; }
  if (libHost !== 'firebasestorage.googleapis.com') {
    throw new Error('This saved document has an invalid file link');
  }
  const resp = await fetch(lib.pdfUrl);
  if (!resp.ok) throw new Error(`library pdf fetch ${resp.status}`);
  const buf = await resp.arrayBuffer();
  const base = String(lib.title || 'document').replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 100) || 'document';
  return new File([buf], `${base.replace(/\.pdf$/i, '')}.pdf`, { type: 'application/pdf' });
}

/** Apply a library doc's SAVED PLACEMENT (geometry only) to the placement
 *  state. Signer identity is never in a template — signer 1 binds to the
 *  employee whose detail this is; a second signer is filled in on its card. */
function _applySavedPlacementTemplate(person, personId) {
  const tpl = (_pendingSignLibraryDoc && Array.isArray(_pendingSignLibraryDoc.signatureFields))
    ? _pendingSignLibraryDoc.signatureFields : null;
  if (!tpl || !tpl.length) return false;
  const maxPage = Math.max(0, (_placementState.totalPages || 1) - 1);
  const clampPage = (p) => Math.max(0, Math.min(maxPage, Math.floor(Number(p) || 0)));
  const pct = (v, dflt) => Math.max(0, Math.min(100, Number.isFinite(Number(v)) ? Number(v) : dflt));
  // COD Code Reviewer (round 2) — firestore.rules can clamp the doc's TOP-LEVEL
  // keys but cannot inspect list ELEMENTS, so a raw-SDK writer can still persist
  // a huge/negative width or height inside signatureFields. _signLibStripFields
  // clamps on the way OUT; clamp on the way IN too, or a forged template paints
  // an unusable placement canvas. Same bounds as the strip.
  const dim = (v, dflt, max) => Math.max(10, Math.min(max, Number(v) > 0 ? Number(v) : dflt));
  const sigTpl = tpl.filter((f) => (f.type || 'signature') === 'signature').slice(0, 2);
  if (!sigTpl.length) return false;

  const idMap = {};
  const fields = sigTpl.map((f, i) => {
    const id = 'signer' + (i + 1);
    idMap[f.id] = id;
    return {
      id,
      type: 'signature',
      page: clampPage(f.page),
      x: pct(f.x, 10),
      y: pct(f.y, 80),
      width: dim(f.width, 200, 800),
      height: dim(f.height, 60, 400),
      signerName: i === 0 ? person.name : '',
      signerPhone: i === 0 ? (person.phone || '') : '',
      signerEmail: i === 0 ? (person.email || '') : '',
      status: 'pending',
      signedAt: null,
      personId: i === 0 ? personId : null,
    };
  });

  let auxSeq = 0;
  tpl.forEach((f) => {
    const type = f.type || 'signature';
    if (type === 'signature' || !_AUX_FIELD_META[type]) return;
    auxSeq++;
    fields.push({
      id: `${type}_${Date.now().toString(36)}_${auxSeq}_${Math.floor(Math.random() * 10000)}`,
      type,
      signerId: idMap[f.signerId] || 'signer1',
      page: clampPage(f.page),
      x: pct(f.x, 40),
      y: pct(f.y, 70),
      width: dim(f.width, _AUX_FIELD_META[type].w, 800),
      height: dim(f.height, _AUX_FIELD_META[type].h, 400),
      status: 'pending',
    });
  });

  _placementState.fields = fields;
  _placementState.currentPage = fields[0].page;
  return true;
}

/** Roster <option>s for the signer cards — mirrors the portal's signer picker
 *  (active people, role + a "no phone/email" tail; never the raw contact). */
function _signerRosterOptions() {
  const escA = window.escAttr || escapeHtml;
  return (HRState.people || [])
    .filter((p) => p && p.active !== false && !p.terminationDate && p.terminated !== true && (p.name || p.firstName))
    .slice()
    .sort((a, b) => String(a.name || a.firstName || '').localeCompare(String(b.name || b.firstName || '')))
    .map((p) => {
      const nm = (p.name || [p.firstName, p.lastName].filter(Boolean).join(' ') || 'Unnamed').trim();
      const role = (p.crewType || p.role || (Array.isArray(p.roles) ? p.roles[0] : '') || '').toString();
      const hasPhone = !!(p.phone || p.mobilePhone || p.cellPhone || p.mobile);
      const hasEmail = !!(p.email || p.personalEmail);
      const tail = [role, (!hasPhone && !hasEmail) ? 'no phone/email' : ''].filter(Boolean).join(' · ');
      return `<option value="${escA(p.id)}">${escapeHtml(nm)}${tail ? ' — ' + escapeHtml(tail) : ''}</option>`;
    }).join('');
}

/** Fill a signer card from the roster instead of typing name/phone/email. */
function fillSignerFromRoster(idx, personId) {
  const f = _placementState.fields[idx];
  if (!f || !personId) return;
  const p = (HRState.people || []).find((x) => x.id === personId);
  if (!p) return;
  f.signerName = (p.name || [p.firstName, p.lastName].filter(Boolean).join(' ') || '').trim();
  f.signerPhone = String(p.phone || p.mobilePhone || p.cellPhone || p.mobile || '').trim();
  f.signerEmail = String(p.email || p.personalEmail || '').trim();
  f.personId = p.id;
  const wrapper = document.getElementById('sig-pdf-wrapper');
  if (wrapper) _renderFieldOverlays(wrapper);
  _renderSignersPanel();
}

async function showSendForSignature(personId) {
  const person = HRState.people.find(p => p.id === personId);
  if (!person) return;

  // Create modal dynamically
  let modal = document.getElementById('modal-send-for-signature');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-send-for-signature';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }

  modal.innerHTML = `
    <div class="modal-content" style="max-width:500px;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
      <div class="modal-header" style="flex-shrink:0;">
        <h3><span class="material-icons">draw</span> Send Document for Signature</h3>
        <button class="btn-icon" onclick="document.getElementById('modal-send-for-signature').classList.add('hidden')">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:var(--spacing-md);overflow-y:auto;flex:1;min-height:0;">
        <div class="form-group">
          <label style="font-weight:600;font-size:0.85rem;">Primary Signer</label>
          <div style="padding:8px 12px;background:var(--bg-secondary);border-radius:var(--radius-md);font-size:0.9rem;">
            ${escapeHtml(person.name)} ${person.phone ? `(${escapeHtml(person.phone)})` : ''} ${person.email ? `(${escapeHtml(person.email)})` : ''}
          </div>
        </div>
        <div class="form-group">
          <label for="sign-doc-title" style="font-weight:600;font-size:0.85rem;">Document Title *</label>
          <input type="text" id="sign-doc-title" placeholder="W-4 Form, Employee Handbook, etc." class="form-control">
        </div>
        <div class="form-group">
          <label style="font-weight:600;font-size:0.85rem;">PDF Upload</label>
          <input type="file" id="sign-doc-file" accept=".pdf" style="display:none" onchange="window.HRModule.handleSignDocFile(event)">
          <div id="sign-doc-upload-area" onclick="document.getElementById('sign-doc-file').click()" style="border:2px dashed var(--border-light);border-radius:var(--radius-md);padding:24px;text-align:center;cursor:pointer;transition:border-color 0.2s;">
            <span class="material-icons" style="font-size:36px;color:var(--text-muted);">cloud_upload</span>
            <p style="margin:8px 0 0;color:var(--text-secondary);font-size:0.85rem;">Click to select PDF (max 10MB)</p>
          </div>
          <div id="sign-doc-file-preview" class="hidden" style="margin-top:8px;padding:10px;background:var(--bg-elevated);border-radius:var(--radius-md);display:flex;align-items:center;gap:8px;">
            <span class="material-icons" style="color:#F59E0B;">picture_as_pdf</span>
            <span id="sign-doc-file-name" style="flex:1;font-size:0.85rem;"></span>
            <button type="button" class="btn-icon" onclick="window.HRModule.clearSignDocFile()" title="Remove">
              <span class="material-icons" style="font-size:16px;">close</span>
            </button>
          </div>
        </div>
        <div class="form-group">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;flex-wrap:wrap;">
            <label for="sign-doc-library" style="font-weight:600;font-size:0.85rem;">&hellip;or send a saved document</label>
            <button type="button" class="btn btn-sm btn-secondary" onclick="window.HRModule.showSignDocLibrary()" style="padding:4px 10px;font-size:0.72rem;">
              <span class="material-icons" style="font-size:14px;">library_books</span> Manage library
            </button>
          </div>
          <select id="sign-doc-library" class="form-control" onchange="window.HRModule.onSignLibrarySelect()" style="margin-top:6px;">
            <option value="">&mdash; Choose a saved document &mdash;</option>
          </select>
          <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">
            Blank documents you reuse across employees. A saved signature placement loads automatically.
          </div>
        </div>
        <div class="form-group">
          <label for="sign-doc-notes" style="font-weight:600;font-size:0.85rem;">Notes (optional)</label>
          <textarea id="sign-doc-notes" rows="2" placeholder="Instructions or context for the signer..." class="form-control" style="resize:vertical;"></textarea>
        </div>
        <div class="form-group">
          <label style="font-weight:600;font-size:0.85rem;">Send via</label>
          <div style="display:flex;gap:12px;">
            <label class="radio-label"><input type="radio" name="sign-send-via" value="sms" ${(person.phone && !person.email) ? 'checked' : ''}> SMS</label>
            <label class="radio-label"><input type="radio" name="sign-send-via" value="email" ${(person.email && !person.phone) ? 'checked' : ''}> Email</label>
            <label class="radio-label"><input type="radio" name="sign-send-via" value="both" ${(person.phone && person.email) ? 'checked' : ''}> Both</label>
          </div>
          <!-- TASK-1611 slice B — sender-side hint; the SMS body stays 1 segment. See commit. -->
          <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:6px;line-height:1.35;">
            <span class="material-icons" style="font-size:13px;vertical-align:-2px;">info</span>
            On an iPhone, a texted link is <strong>not tappable</strong> until that person has replied to this number at least once &mdash; Apple blocks links from unknown senders, so they have to copy and paste it. Ask them to text <strong>START</strong> back once and every link after that works. Email links always work.
          </div>
        </div>
      </div>
      <div class="modal-form-footer">
        <button class="btn btn-secondary" onclick="document.getElementById('modal-send-for-signature').classList.add('hidden')">Cancel</button>
        <button class="btn btn-primary" id="btn-next-place-signatures" onclick="window.HRModule.openPlacementModal('${personId}')">
          <span class="material-icons" style="font-size:16px;">arrow_forward</span> Next: Place Signatures
        </button>
      </div>
    </div>
  `;

  modal.classList.remove('hidden');

  // TASK-1638 — reset the step-1 source selection and fill the library picker
  // (async + non-blocking: a read failure just leaves the placeholder option).
  _pendingSignDocFile = null;
  _pendingSignLibraryDoc = null;
  _pendingSignSourceFile = null;
  _populateSignLibraryPicker();
}

let _pendingSignDocFile = null;
// TASK-1638 — step-1 source state. _pendingSignDocFile is an uploaded File;
// _pendingSignLibraryDoc is a picked library template (mutually exclusive with
// the upload); _pendingSignSourceFile is whichever bytes the send uploads.
let _pendingSignLibraryDoc = null;
let _pendingSignSourceFile = null;
let _placementState = {
  pdfDoc: null,
  pdfUrl: null,
  fields: [],
  currentPage: 0,
  totalPages: 0,
  personId: null,
  dragging: null,
  dragOffset: { x: 0, y: 0 }
};

function handleSignDocFile(event) {
  const file = event.target.files[0];
  if (!file) return;
  if (!file.name.toLowerCase().endsWith('.pdf')) {
    window.showToast?.('Only PDF files are allowed', 'error');
    event.target.value = '';
    return;
  }
  if (file.size > 10 * 1024 * 1024) {
    window.showToast?.('File too large — 10MB maximum', 'error');
    event.target.value = '';
    return;
  }
  _pendingSignDocFile = file;
  // TASK-1638 — an upload clears any library pick: the send source must be
  // unambiguous (same rule as the portal's _cpSendSignature).
  _pendingSignLibraryDoc = null;
  const libSel = document.getElementById('sign-doc-library');
  if (libSel) libSel.value = '';
  document.getElementById('sign-doc-file-name').textContent = file.name;
  document.getElementById('sign-doc-file-preview').classList.remove('hidden');
  document.getElementById('sign-doc-upload-area').style.display = 'none';
}

function clearSignDocFile() {
  _pendingSignDocFile = null;
  _pendingSignSourceFile = null;
  document.getElementById('sign-doc-file').value = '';
  document.getElementById('sign-doc-file-preview').classList.add('hidden');
  document.getElementById('sign-doc-upload-area').style.display = '';
}

/** Load pdf.js from CDN if not already present */
async function _ensurePdfJs() {
  if (window.pdfjsLib) return;
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = '/vendor/pdf-3.11.174.min.js';
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = '/vendor/pdf.worker-3.11.174.min.js';
      resolve();
    };
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

// TASK-10175 S4 — Signature Packets card (builder + tracker): LAZY hrSignPackets.js.
let _spLoad = null;
function _spCall(fn) {
  _spLoad = _spLoad || __ddChunkWrap(import('./hrSignPackets.js?v=2')).catch((e) => { _spLoad = null; throw e; });
  return _spLoad.then((m) => m[fn]({ HRState, db, functions, httpsCallable, doc, updateDoc, Timestamp, escapeHtml,
    signLib: _signLibFetch, signLibStrip: _signLibStripFields, showLibrary: showSignDocLibrary, ensurePdfJs: _ensurePdfJs }))
    .catch((e) => { console.error('[HR] sign packets', e); if (fn === 'open') window.showToast?.('Could not open that screen. Check your connection and try again.', 'error'); });
}

/** Step 2: Open the placement modal after step 1 is validated */
async function openPlacementModal(personId) {
  const title = document.getElementById('sign-doc-title')?.value?.trim();
  if (!title) { window.showToast?.('Enter a document title', 'error'); return; }
  if (!_pendingSignDocFile && !_pendingSignLibraryDoc) { window.showToast?.('Select a PDF to upload, or pick a saved document', 'error'); return; }

  const person = HRState.people.find(p => p.id === personId);
  if (!person) return;

  // Hide step 1 modal
  document.getElementById('modal-send-for-signature').classList.add('hidden');

  // Show placement modal
  let modal = document.getElementById('modal-sig-placement');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-sig-placement';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }

  modal.innerHTML = `
    <div class="modal-content modal-fullscreen" style="max-width:800px;display:flex;flex-direction:column;max-height:90dvh;">
      <div class="modal-header" style="flex-shrink:0;">
        <h3><span class="material-icons">place</span> Place Signature Fields</h3>
        <button class="btn-icon" onclick="window.HRModule.closePlacementModal()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="padding:8px 16px;background:var(--bg-secondary);border-bottom:1px solid var(--border-light);font-size:0.8rem;color:var(--text-secondary);flex-shrink:0;">
        Use "Place on this page" next to a signer to drop their signature field on the current page, then drag to position. Max 2 signers — add Auto-Date and Initials boxes from each signer's card below.
      </div>
      <div style="padding:8px 16px;display:flex;align-items:center;gap:8px;flex-shrink:0;border-bottom:1px solid var(--border-light);">
        <button class="btn btn-sm btn-secondary" id="sig-prev-page" onclick="window.HRModule.sigPlacementPage(-1)" disabled style="padding:4px 8px;">
          <span class="material-icons" style="font-size:16px;">chevron_left</span>
        </button>
        <span id="sig-page-indicator" style="font-size:0.82rem;font-weight:600;min-width:80px;text-align:center;">Page 1 / 1</span>
        <button class="btn btn-sm btn-secondary" id="sig-next-page" onclick="window.HRModule.sigPlacementPage(1)" disabled style="padding:4px 8px;">
          <span class="material-icons" style="font-size:16px;">chevron_right</span>
        </button>
        <div style="flex:1;"></div>
        <button class="btn btn-sm" id="btn-add-sig-field" onclick="window.HRModule.addSignatureField()" style="padding:4px 10px;font-size:0.78rem;background:#DBEAFE;color:#1D4ED8;border:1px solid #93C5FD;border-radius:6px;">
          <span class="material-icons" style="font-size:14px;">add</span> Add Signer
        </button>
      </div>
      <div id="sig-placement-canvas-area" style="flex:1;overflow:auto;background:#94a3b8;position:relative;display:flex;justify-content:center;padding:12px;">
        <div style="text-align:center;padding:40px;color:#fff;">
          <div class="spinner" style="border-top-color:#fff;margin:0 auto 12px;"></div>
          Loading PDF...
        </div>
      </div>
      <div id="sig-signers-panel" style="flex-shrink:0;padding:12px 16px;border-top:1px solid var(--border-light);max-height:200px;overflow-y:auto;background:var(--bg-primary);">
        <!-- Signer assignment cards rendered here -->
      </div>
      <div class="modal-form-footer" style="flex-shrink:0;">
        <button class="btn btn-secondary" onclick="window.HRModule.closePlacementModal()">Back</button>
        <button class="btn btn-primary" id="btn-send-with-fields" onclick="window.HRModule.sendWithFields('${personId}')">
          <span class="material-icons" style="font-size:16px;">send</span> Send
        </button>
      </div>
    </div>
  `;

  modal.classList.remove('hidden');

  // Reset state
  _placementState = {
    pdfDoc: null,
    pdfUrl: null,
    fields: [],
    currentPage: 0,
    totalPages: 0,
    personId: personId,
    dragging: null,
    dragOffset: { x: 0, y: 0 }
  };

  // Auto-add first signer from the person
  _placementState.fields.push({
    id: 'signer1',
    type: 'signature',
    page: 0,
    x: 10, // percentage
    y: 80, // percentage
    width: 200,
    height: 60,
    signerName: person.name,
    signerPhone: person.phone || '',
    signerEmail: person.email || '',
    status: 'pending',
    signedAt: null,
    personId: personId
  });

  // Load PDF. TASK-1638 — the bytes come from the step-1 upload OR from a COPY
  // of the picked library template; either way sendWithFields uploads its own
  // object under hr/{tid}/signDocs/, so deleting a library doc later can never
  // orphan a document that was already sent.
  try {
    await _ensurePdfJs();
    _pendingSignSourceFile = await _resolveSignSourceFile();
    const arrayBuf = await _pendingSignSourceFile.arrayBuffer();
    _placementState.pdfDoc = await window.pdfjsLib.getDocument({ data: arrayBuf }).promise;
    _placementState.totalPages = _placementState.pdfDoc.numPages;
    // Saved-placement prefill. Runs AFTER the doc loads because clamping the
    // template's page index needs totalPages; it REPLACES the default signer-1
    // field pushed above.
    if (_applySavedPlacementTemplate(person, personId)) {
      window.showToast?.('Saved placement loaded — drag to adjust', 'success');
    }
    _renderPlacementPage();
    _renderSignersPanel();
  } catch (err) {
    console.error('[HR] PDF load error:', err);
    window.showToast?.('Failed to load PDF preview', 'error');
  }
}

function closePlacementModal() {
  document.getElementById('modal-sig-placement')?.classList.add('hidden');
  // Reopen step 1 so they can go back
  document.getElementById('modal-send-for-signature')?.classList.remove('hidden');
}

async function _renderPlacementPage() {
  const area = document.getElementById('sig-placement-canvas-area');
  if (!area || !_placementState.pdfDoc) return;

  const pageNum = _placementState.currentPage + 1;
  const page = await _placementState.pdfDoc.getPage(pageNum);
  // Render at a scale that fits ~700px wide
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = Math.min(700 / baseViewport.width, 2);
  const viewport = page.getViewport({ scale });

  area.innerHTML = '';
  const wrapper = document.createElement('div');
  wrapper.id = 'sig-pdf-wrapper';
  wrapper.style.cssText = `position:relative;width:${viewport.width}px;height:${viewport.height}px;flex-shrink:0;cursor:crosshair;`;

  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  canvas.style.cssText = 'display:block;width:100%;height:100%;border-radius:4px;box-shadow:0 2px 12px rgba(0,0,0,0.2);';
  wrapper.appendChild(canvas);

  area.appendChild(wrapper);
  await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;

  // Add click handler to place fields
  wrapper.addEventListener('mousedown', (e) => _onPlacementMouseDown(e, wrapper));
  wrapper.addEventListener('mousemove', (e) => _onPlacementMouseMove(e, wrapper));
  wrapper.addEventListener('mouseup', () => _onPlacementMouseUp());
  // Touch support
  wrapper.addEventListener('touchstart', (e) => _onPlacementTouchStart(e, wrapper), { passive: false });
  wrapper.addEventListener('touchmove', (e) => _onPlacementTouchMove(e, wrapper), { passive: false });
  wrapper.addEventListener('touchend', () => _onPlacementMouseUp());

  // Render existing fields on this page
  _renderFieldOverlays(wrapper);

  // Update page controls
  document.getElementById('sig-page-indicator').textContent = `Page ${pageNum} / ${_placementState.totalPages}`;
  document.getElementById('sig-prev-page').disabled = _placementState.currentPage === 0;
  document.getElementById('sig-next-page').disabled = _placementState.currentPage >= _placementState.totalPages - 1;
}

// Signature-type fields only (TASK-1051 — the fields array now also carries
// date/initials aux fields; signers are the signature-type entries).
function _sigTypeFields() {
  return _placementState.fields.filter(f => (f.type || 'signature') === 'signature');
}

function _renderFieldOverlays(wrapper) {
  // Remove old overlays
  wrapper.querySelectorAll('.sig-field-overlay').forEach(el => el.remove());

  const currentPage = _placementState.currentPage;
  const sigOrdinalById = {};
  _sigTypeFields().forEach((f, i) => { sigOrdinalById[f.id] = i; });

  _placementState.fields.forEach((field, idx) => {
    if (field.page !== currentPage) return;

    const type = field.type || 'signature';
    // Color by the OWNING signer (signer 1 = blue, signer 2 = amber)
    const ownerOrd = type === 'signature'
      ? (sigOrdinalById[field.id] || 0)
      : (sigOrdinalById[field.signerId] || 0);
    const border = ownerOrd === 0 ? '#2563eb' : '#f59e0b';
    const bg = ownerOrd === 0 ? 'rgba(37,99,235,0.12)' : 'rgba(245,158,11,0.12)';
    const color = ownerOrd === 0 ? '#1D4ED8' : '#B45309';
    const w = field.width || (type === 'date' ? 110 : type === 'initials' ? 70 : type === 'name' ? 200 : 200);
    const h = field.height || (type === 'date' ? 28 : type === 'initials' ? 36 : type === 'name' ? 24 : 60);
    let label;
    if (type === 'date') label = 'Date (auto)';
    else if (type === 'initials') label = 'Initials';
    else if (type === 'name') {
      // Show the owning signer's name so the placer sees exactly what stamps.
      const owner = _placementState.fields.find(sf => sf.id === field.signerId);
      label = (owner && owner.signerName) ? owner.signerName + ' (name)' : 'Printed Name';
    }
    else label = field.signerName || ('Signer ' + (ownerOrd + 1));

    const overlay = document.createElement('div');
    overlay.className = 'sig-field-overlay';
    overlay.dataset.fieldIdx = idx;
    overlay.style.cssText = `
      position:absolute;
      left:${field.x}%;
      top:${field.y}%;
      width:${w}px;
      height:${h}px;
      border:2px ${type === 'signature' ? 'solid' : 'dashed'} ${border};
      background:${bg};
      border-radius:6px;
      cursor:grab;
      display:flex;
      align-items:center;
      justify-content:center;
      font-size:0.68rem;
      font-weight:700;
      color:${color};
      user-select:none;
      z-index:10;
    `;
    overlay.innerHTML = `
      <span style="pointer-events:none;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:calc(100% - 4px);">${escapeHtml(label)}</span>
      <button onclick="event.stopPropagation();window.HRModule.removeSignatureField(${idx})" style="position:absolute;top:-8px;right:-8px;width:20px;height:20px;border-radius:50%;background:#DC2626;color:#fff;border:none;font-size:12px;cursor:pointer;display:flex;align-items:center;justify-content:center;line-height:1;">×</button>
    `;
    wrapper.appendChild(overlay);
  });
}

function _onPlacementMouseDown(e, wrapper) {
  const target = e.target.closest('.sig-field-overlay');
  if (target) {
    // Start dragging existing field
    e.preventDefault();
    const idx = parseInt(target.dataset.fieldIdx);
    const rect = wrapper.getBoundingClientRect();
    const fieldRect = target.getBoundingClientRect();
    _placementState.dragging = idx;
    _placementState.dragOffset = {
      x: e.clientX - fieldRect.left,
      y: e.clientY - fieldRect.top
    };
    target.style.cursor = 'grabbing';
    return;
  }

  // Not clicking a field — check if we can place a new one by clicking empty area
  // We don't auto-add on click anymore; user uses the "Add Signer" button
}

function _onPlacementTouchStart(e, wrapper) {
  const touch = e.touches[0];
  const target = document.elementFromPoint(touch.clientX, touch.clientY)?.closest('.sig-field-overlay');
  if (target) {
    e.preventDefault();
    const idx = parseInt(target.dataset.fieldIdx);
    const rect = wrapper.getBoundingClientRect();
    const fieldRect = target.getBoundingClientRect();
    _placementState.dragging = idx;
    _placementState.dragOffset = {
      x: touch.clientX - fieldRect.left,
      y: touch.clientY - fieldRect.top
    };
  }
}

function _onPlacementMouseMove(e, wrapper) {
  if (_placementState.dragging === null) return;
  e.preventDefault();
  const rect = wrapper.getBoundingClientRect();
  const xPx = e.clientX - rect.left - _placementState.dragOffset.x;
  const yPx = e.clientY - rect.top - _placementState.dragOffset.y;
  const xPct = Math.max(0, Math.min(100, (xPx / rect.width) * 100));
  const yPct = Math.max(0, Math.min(100, (yPx / rect.height) * 100));

  _placementState.fields[_placementState.dragging].x = Math.round(xPct * 10) / 10;
  _placementState.fields[_placementState.dragging].y = Math.round(yPct * 10) / 10;

  // Move the overlay element directly for smooth dragging
  const overlay = wrapper.querySelector(`.sig-field-overlay[data-field-idx="${_placementState.dragging}"]`);
  if (overlay) {
    overlay.style.left = xPct + '%';
    overlay.style.top = yPct + '%';
  }
}

function _onPlacementTouchMove(e, wrapper) {
  if (_placementState.dragging === null) return;
  e.preventDefault();
  const touch = e.touches[0];
  const rect = wrapper.getBoundingClientRect();
  const xPx = touch.clientX - rect.left - _placementState.dragOffset.x;
  const yPx = touch.clientY - rect.top - _placementState.dragOffset.y;
  const xPct = Math.max(0, Math.min(100, (xPx / rect.width) * 100));
  const yPct = Math.max(0, Math.min(100, (yPx / rect.height) * 100));

  _placementState.fields[_placementState.dragging].x = Math.round(xPct * 10) / 10;
  _placementState.fields[_placementState.dragging].y = Math.round(yPct * 10) / 10;

  const overlay = wrapper.querySelector(`.sig-field-overlay[data-field-idx="${_placementState.dragging}"]`);
  if (overlay) {
    overlay.style.left = xPct + '%';
    overlay.style.top = yPct + '%';
  }
}

function _onPlacementMouseUp() {
  if (_placementState.dragging !== null) {
    const wrapper = document.getElementById('sig-pdf-wrapper');
    const overlay = wrapper?.querySelector(`.sig-field-overlay[data-field-idx="${_placementState.dragging}"]`);
    if (overlay) overlay.style.cursor = 'grab';
  }
  _placementState.dragging = null;
}

function addSignatureField() {
  const sigCount = _sigTypeFields().length;
  if (sigCount >= 2) {
    window.showToast?.('Maximum 2 signers allowed', 'error');
    return;
  }
  _placementState.fields.push({
    id: 'signer' + (sigCount + 1),
    type: 'signature',
    page: _placementState.currentPage,
    x: 10 + (sigCount * 30),
    y: 70,
    width: 200,
    height: 60,
    signerName: '',
    signerPhone: '',
    signerEmail: '',
    status: 'pending',
    signedAt: null,
    personId: null
  });
  const wrapper = document.getElementById('sig-pdf-wrapper');
  if (wrapper) _renderFieldOverlays(wrapper);
  _renderSignersPanel();
}

// TASK-1051 — add an aux box bound to a signer:
//   date     — stamped SERVER-side with the actual signing date
//   initials — drawn once by the signer on the signing page, stamped at each box
//   name     — the signer's PRINTED NAME, stamped SERVER-side from the stored
//              signerName (TASK-1062, Chris D&A-receipt dogfood). Like Auto-Date
//              it needs no signer input and can't be forged — the name comes
//              from the roster/send record, not the signing POST.
const _AUX_FIELD_META = {
  date:     { label: 'Auto-Date', w: 110, h: 28 },
  initials: { label: 'Initials',  w: 70,  h: 36 },
  name:     { label: 'Printed Name', w: 200, h: 24 },
};
function addAuxField(sigIdx, type) {
  const meta = _AUX_FIELD_META[type];
  if (!meta) return;
  const sig = _placementState.fields[sigIdx];
  if (!sig || (sig.type || 'signature') !== 'signature') return;
  const existing = _placementState.fields.filter(f => f.type === type && f.signerId === sig.id).length;
  if (existing >= 4) {
    window.showToast?.(`Maximum 4 ${meta.label} boxes per signer`, 'error');
    return;
  }
  _placementState.fields.push({
    id: `${type}_${Date.now().toString(36)}_${Math.floor(Math.random() * 10000)}`,
    type,
    signerId: sig.id,
    page: _placementState.currentPage,
    x: 40 + (existing * 8),
    y: 70 + (existing * 5),
    width: meta.w,
    height: meta.h,
    status: 'pending'
  });
  const wrapper = document.getElementById('sig-pdf-wrapper');
  if (wrapper) _renderFieldOverlays(wrapper);
  _renderSignersPanel();
  window.showToast?.(`${meta.label} box placed on page ${_placementState.currentPage + 1} — drag to position`, 'success');
}

function removeSignatureField(idx) {
  const field = _placementState.fields[idx];
  if (!field) return;
  const type = field.type || 'signature';

  if (type !== 'signature') {
    // Aux (date/initials) box — just remove it
    _placementState.fields.splice(idx, 1);
  } else {
    // Don't allow removing the last signer
    if (_sigTypeFields().length === 1) {
      window.showToast?.('At least one signer is required', 'error');
      return;
    }
    const removedId = field.id;
    _placementState.fields.splice(idx, 1);
    // Drop this signer's aux boxes too (date/initials/name)
    _placementState.fields = _placementState.fields.filter(f =>
      !(_AUX_FIELD_META[f.type] && f.signerId === removedId));
    // Re-index signer IDs and remap aux signerId references (TASK-1051: the
    // old blind `'signer' + (i+1)` re-index would orphan aux bindings)
    const remap = {};
    let n = 0;
    _placementState.fields.forEach((f) => {
      if ((f.type || 'signature') === 'signature') { n++; remap[f.id] = 'signer' + n; }
    });
    _placementState.fields.forEach((f) => {
      if ((f.type || 'signature') === 'signature') f.id = remap[f.id];
      else if (f.signerId && remap[f.signerId]) f.signerId = remap[f.signerId];
    });
  }
  const wrapper = document.getElementById('sig-pdf-wrapper');
  if (wrapper) _renderFieldOverlays(wrapper);
  _renderSignersPanel();
}

function _renderSignersPanel() {
  const panel = document.getElementById('sig-signers-panel');
  if (!panel) return;

  // Signer cards only for SIGNATURE-type fields; each carries its own
  // Auto-Date / Initials controls + chips for its placed aux boxes (TASK-1051).
  // TASK-1638 — portal parity: an EXTRA signer can be picked off the roster
  // instead of re-typing name/phone/email. Signer 1 gets no picker on purpose —
  // it is bound to the employee whose detail this is, and the signDocuments row
  // is filed under that personId (repointing it would hide the doc on the
  // person you sent it from).
  const rosterOpts = _signerRosterOptions();
  let sigOrd = -1;
  panel.innerHTML = _placementState.fields.map((field, idx) => {
    if ((field.type || 'signature') !== 'signature') return '';
    sigOrd++;
    const auxChips = _placementState.fields.map((f, fIdx) => {
      if (!_AUX_FIELD_META[f.type] || f.signerId !== field.id) return '';
      const label = _AUX_FIELD_META[f.type].label;
      const icon = f.type === 'date' ? 'event' : f.type === 'initials' ? 'gesture' : 'badge';
      return `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:10px;font-size:0.7rem;font-weight:600;background:#F1F5F9;color:#334155;">
        <span class="material-icons" style="font-size:12px;">${icon}</span>${label} · p${(f.page || 0) + 1}
        <button onclick="window.HRModule.removeSignatureField(${fIdx})" title="Remove" style="background:none;border:none;color:#94A3B8;cursor:pointer;padding:0;font-size:12px;line-height:1;">×</button>
      </span>`;
    }).join('');
    return `
    <div style="padding:10px;background:var(--bg-secondary);border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:8px;border-left:4px solid ${sigOrd === 0 ? '#2563eb' : '#f59e0b'};">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px;flex-wrap:wrap;">
        <div style="font-weight:700;font-size:0.82rem;color:var(--text-primary);">
          Signer ${sigOrd + 1} ${sigOrd === 0 ? '(Primary)' : ''} — Page ${field.page + 1}
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          <button class="btn btn-sm" onclick="window.HRModule.placeFieldOnCurrentPage(${idx})" title="Move this signer's signature field to the page you're currently viewing" style="padding:3px 8px;font-size:0.72rem;background:#DBEAFE;color:#1D4ED8;border:1px solid #93C5FD;border-radius:6px;white-space:nowrap;flex-shrink:0;">
            <span class="material-icons" style="font-size:13px;vertical-align:-2px;">place</span> Place on this page
          </button>
          <button class="btn btn-sm" onclick="window.HRModule.addAuxField(${idx},'date')" title="Add an Auto-Date box — the signing date is stamped automatically when this signer signs" style="padding:3px 8px;font-size:0.72rem;background:#D1FAE5;color:#065F46;border:1px solid #6EE7B7;border-radius:6px;white-space:nowrap;flex-shrink:0;">
            <span class="material-icons" style="font-size:13px;vertical-align:-2px;">event</span> Auto-Date
          </button>
          <button class="btn btn-sm" onclick="window.HRModule.addAuxField(${idx},'initials')" title="Add an Initials box — this signer draws their initials once and they're stamped here" style="padding:3px 8px;font-size:0.72rem;background:#FEF3C7;color:#92400E;border:1px solid #FCD34D;border-radius:6px;white-space:nowrap;flex-shrink:0;">
            <span class="material-icons" style="font-size:13px;vertical-align:-2px;">gesture</span> Initials
          </button>
          <button class="btn btn-sm" onclick="window.HRModule.addAuxField(${idx},'name')" title="Add a Printed Name box — this signer's typed name is stamped automatically when they sign (e.g. the 'Printed Name' line on a DOT D&A receipt)" style="padding:3px 8px;font-size:0.72rem;background:#E0E7FF;color:#3730A3;border:1px solid #A5B4FC;border-radius:6px;white-space:nowrap;flex-shrink:0;">
            <span class="material-icons" style="font-size:13px;vertical-align:-2px;">badge</span> Printed Name
          </button>
        </div>
      </div>
      ${auxChips ? `<div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:6px;">${auxChips}</div>` : ''}
      ${(sigOrd > 0 && rosterOpts) ? `<div style="margin-bottom:6px;">
        <select class="form-control" style="font-size:0.8rem;padding:6px 8px;width:100%;" onchange="window.HRModule.fillSignerFromRoster(${idx}, this.value)">
          <option value="">&mdash; Fill from roster (or type below) &mdash;</option>
          ${rosterOpts}
        </select>
      </div>` : ''}
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:6px;">
        <input type="text" placeholder="Name *" value="${escapeHtml(field.signerName)}" class="form-control" style="font-size:0.8rem;padding:6px 8px;"
          onchange="window.HRModule.updateSignerField(${idx},'signerName',this.value)">
        <input type="tel" placeholder="Phone" value="${escapeHtml(field.signerPhone)}" class="form-control" style="font-size:0.8rem;padding:6px 8px;"
          onchange="window.HRModule.updateSignerField(${idx},'signerPhone',this.value)">
      </div>
      <div style="margin-top:6px;">
        <input type="email" placeholder="Email" value="${escapeHtml(field.signerEmail)}" class="form-control" style="font-size:0.8rem;padding:6px 8px;width:100%;"
          onchange="window.HRModule.updateSignerField(${idx},'signerEmail',this.value)">
      </div>
    </div>`;
  }).join('');
}

function updateSignerField(idx, key, value) {
  if (_placementState.fields[idx]) {
    _placementState.fields[idx][key] = value.trim();
    // Update overlay label if name changed
    if (key === 'signerName') {
      const wrapper = document.getElementById('sig-pdf-wrapper');
      if (wrapper) _renderFieldOverlays(wrapper);
    }
  }
}

/** Move a signer's signature field onto the page currently being viewed.
 * Fixes the multi-page bug where a field stuck on page 1 could never reach
 * the last page (where DQ-file signature blocks live). Sets field.page to the
 * current page; gives it a sensible default x/y if it has none yet, then re-renders. */
function placeFieldOnCurrentPage(idx) {
  const field = _placementState.fields[idx];
  if (!field) return;
  field.page = _placementState.currentPage;
  // Default position if the field has never been placed (null/undefined coords)
  if (typeof field.x !== 'number') field.x = 10 + (idx * 30);
  if (typeof field.y !== 'number') field.y = 70;
  const wrapper = document.getElementById('sig-pdf-wrapper');
  if (wrapper) _renderFieldOverlays(wrapper);
  _renderSignersPanel();
  window.showToast?.(`Signer ${idx + 1} field placed on page ${field.page + 1} — drag to position`, 'success');
}

function sigPlacementPage(delta) {
  const newPage = _placementState.currentPage + delta;
  if (newPage < 0 || newPage >= _placementState.totalPages) return;
  _placementState.currentPage = newPage;
  _renderPlacementPage();
}

/** Step 3: Validate signers and send */
async function sendWithFields(personId) {
  // TASK-1634 — a missing radio-group selection previously fell back to
  // `|| 'sms'` silently, so a rendering glitch (or a future markup change that
  // drops the radios) would degrade every send to SMS-only with no signal.
  // Fail explicitly instead — there is no safe default to pick on the user's
  // behalf here.
  const sendViaEl = document.querySelector('input[name="sign-send-via"]:checked');
  if (!sendViaEl) {
    window.showToast?.('Choose how to send this document (SMS, Email, or Both)', 'error');
    return;
  }
  const sendVia = sendViaEl.value;

  // Validate all signers have names (signature-type fields only — date/initials
  // aux boxes have no signer identity of their own, TASK-1051), AND that the
  // CHOSEN channel is actually deliverable for each signer — TASK-1634 (3):
  // previously this only checked phone OR email existed, so picking "Email"
  // for a signer with only a phone on file silently sent nothing.
  const signerFields = _sigTypeFields();
  for (let i = 0; i < signerFields.length; i++) {
    const f = signerFields[i];
    if (!f.signerName?.trim()) {
      window.showToast?.(`Signer ${i + 1} needs a name`, 'error');
      return;
    }
    const hasPhone = !!f.signerPhone?.trim();
    const hasEmail = !!f.signerEmail?.trim();
    if (sendVia === 'sms' && !hasPhone) {
      window.showToast?.(`Signer ${i + 1} (${f.signerName}) has no phone number on file — can't send by SMS`, 'error');
      return;
    }
    if (sendVia === 'email' && !hasEmail) {
      window.showToast?.(`Signer ${i + 1} (${f.signerName}) has no email on file — can't send by email`, 'error');
      return;
    }
    if (sendVia === 'both' && !hasPhone && !hasEmail) {
      window.showToast?.(`Signer ${i + 1} needs a phone or email`, 'error');
      return;
    }
  }

  const title = document.getElementById('sign-doc-title')?.value?.trim();
  const notes = document.getElementById('sign-doc-notes')?.value?.trim() || '';

  const btn = document.getElementById('btn-send-with-fields');
  const origHtml = btn.innerHTML;
  btn.disabled = true;
  btn.innerHTML = '<span class="btn-spinner"></span> Sending...';

  try {
    const tenantId = HRState.tenantId;
    const timestamp = Date.now();
    // TASK-1638 — the bytes are whatever the placement step resolved: the
    // step-1 upload, or a copy of the picked library template.
    const sourceFile = _pendingSignSourceFile || _pendingSignDocFile;
    if (!sourceFile) throw new Error('No document selected');
    const storagePath = `hr/${tenantId}/signDocs/${timestamp}_${sourceFile.name}`;

    // Upload PDF to storage
    const fileRef = ref(storage, storagePath);
    await uploadBytes(fileRef, sourceFile, { contentType: 'application/pdf' });
    const pdfUrl = await getDownloadURL(fileRef);

    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 72);

    // Build signature fields with individual access tokens
    const signatureFields = [];
    const baseUrl = window.location.origin;
    const docId = `sign_${timestamp}`;

    for (let i = 0; i < _placementState.fields.length; i++) {
      const f = _placementState.fields[i];
      const fType = f.type || 'signature';

      if (fType !== 'signature') {
        // TASK-1051 — Auto-Date / Initials box: persisted with its signer
        // binding; NO access token (only real signers get signing links).
        signatureFields.push({
          id: f.id,
          type: fType,
          signerId: f.signerId || 'signer1',
          page: f.page,
          x: f.x,
          y: f.y,
          width: f.width,
          height: f.height,
          status: 'pending'
        });
        continue;
      }

      // TASK-1421 — raw crypto.randomUUID() throws outside a secure context
      // (it is undefined there). The shared minter falls back to
      // getRandomValues and only throws when there is no WebCrypto at all.
      const accessToken = window.mintAccessTokenId('dsign');

      signatureFields.push({
        id: f.id,
        type: 'signature',
        page: f.page,
        x: f.x,
        y: f.y,
        width: f.width,
        height: f.height,
        signerName: f.signerName.trim(),
        signerPhone: f.signerPhone.trim(),
        signerEmail: f.signerEmail.trim(),
        personId: f.personId || null,
        status: 'pending',
        signedAt: null,
        accessToken
      });

      // Create access token for this signer
      await setDoc(doc(db, 'accessTokens', accessToken), {
        type: 'document_sign',
        tenantId,
        documentId: docId,
        personId: f.personId || personId,
        fieldId: f.id,
        expiresAt: Timestamp.fromDate(expiresAt),
        consumed: false,
        createdAt: Timestamp.now()
      });
    }

    // Save the sign document
    await setDoc(doc(db, 'tenants', tenantId, 'signDocuments', docId), {
      title,
      notes,
      storagePath,
      pdfUrl,
      personId,
      personName: HRState.people.find(p => p.id === personId)?.name || '',
      status: 'sent',
      sentVia: sendVia,
      sentAt: Timestamp.now(),
      expiresAt: Timestamp.fromDate(expiresAt),
      createdAt: Timestamp.now(),
      signatureFields
    });

    // Send notifications to each signer.
    // TASK-1634 (2) — the old code fired the toast UNCONDITIONALLY after this
    // loop and counted signature FIELDS, not deliveries, so a total send
    // failure (both callables throwing, swallowed into console.error only)
    // still showed a green "Document sent" toast. Track per-channel,
    // per-signer results here and report what actually happened.
    const companyName = window.AppState?.tenantData?.companyName || window.AppState?.tenantData?.name || 'Your employer';
    const deliveryResults = [];

    for (const field of signatureFields) {
      if ((field.type || 'signature') !== 'signature') continue; // aux boxes get no notification (TASK-1051)
      const signUrl = `${baseUrl}/sign-doc.html?token=${field.accessToken}`;
      // 'skipped' = channel not attempted (either not part of sendVia, or the
      // signer has no address for it — already blocked at validation for a
      // SINGLE-channel pick, but 'both' can still legitimately skip one leg).
      const result = { signerName: field.signerName, sms: 'skipped', email: 'skipped' };

      // SMS
      if (sendVia === 'sms' || sendVia === 'both') {
        if (field.signerPhone) {
          try {
            // TASK-9826 — the server resolves the signer's phone from this
            // document + field (its live sign token must name this tenant, doc
            // and field) and builds the text and link itself:
            // `<company> sent you "<title>" to sign.` with the link alone on the
            // last line (iOS long-press — see _tenantSmsSignBody). No phone
            // number or message leaves the browser. Owner or `hr` module; rate
            // limited; at most 3 texts per signer.
            const sendSms = httpsCallable(functions, 'sendSignRequestSms');
            const smsRes = await sendSms({ tenantId, documentId: docId, fieldId: field.id, origin: baseUrl });
            // A soft failure (STOP opt-out, no SMS pack, no credits) returns
            // ok:false rather than throwing — that is not a delivery.
            result.sms = smsRes?.data?.ok === false ? 'failed' : 'sent';
          } catch (smsErr) {
            console.error('[HR] SMS send error for', field.signerName, smsErr);
            result.sms = 'failed';
          }
        }
      }

      // Email
      if (sendVia === 'email' || sendVia === 'both') {
        if (field.signerEmail) {
          try {
            const sendEmail = httpsCallable(functions, 'sendSignDocEmail');
            await sendEmail({
              tenantId: HRState.tenantId,
              to: field.signerEmail,
              signerName: field.signerName,
              title,
              notes,
              signUrl,
              companyName
            });
            result.email = 'sent';
          } catch (emailErr) {
            console.error('[HR] Email send error for', field.signerName, emailErr);
            result.email = 'failed';
          }
        }
      }

      deliveryResults.push(result);
    }

    // Persist ONLY {channel, status} strings — never the raw smsErr/emailErr
    // payloads above, which can carry provider response bodies (phone/email
    // fragments). A client-reported result isn't authoritative for anything
    // downstream of trust (it's not used to flip `status`/signatureFields,
    // which stay server-owned per the TASK-1084 lock) — it exists purely so
    // the row can show the signer what actually went out.
    try {
      await updateDoc(doc(db, 'tenants', tenantId, 'signDocuments', docId), { deliveryResults });
    } catch (persistErr) {
      console.error('[HR] Failed to persist delivery results:', persistErr);
    }

    const anySent = deliveryResults.some(r => r.sms === 'sent' || r.email === 'sent');
    const anyFailed = deliveryResults.some(r => r.sms === 'failed' || r.email === 'failed');
    const sentChannels = new Set();
    deliveryResults.forEach(r => {
      if (r.sms === 'sent') sentChannels.add('SMS');
      if (r.email === 'sent') sentChannels.add('email');
    });
    if (deliveryResults.length === 0) {
      window.showToast?.('Document saved — no signers to notify', 'success');
    } else if (anySent && !anyFailed) {
      window.showToast?.(`Document sent via ${[...sentChannels].join(' + ')} to ${deliveryResults.length} signer(s)`, 'success');
    } else if (anySent && anyFailed) {
      window.showToast?.(`Document saved, but some deliveries failed — check the signer list`, 'warning');
    } else {
      window.showToast?.(`Document saved, but no notification went out — delivery failed`, 'error');
    }
    document.getElementById('modal-sig-placement')?.classList.add('hidden');
    document.getElementById('modal-send-for-signature')?.classList.add('hidden');

    // TASK-1638 — save-placement prompt (portal parity, _cpDoSignatureSend): a
    // LIBRARY doc that had NO saved placement, just sent with fields placed →
    // offer to persist the coords so the next send starts from them. Never
    // prompts for a one-off upload, and never when a placement already existed.
    const savedLibDoc = _pendingSignLibraryDoc;
    const placedFields = _placementState.fields.slice();
    _pendingSignDocFile = null;
    _pendingSignLibraryDoc = null;
    _pendingSignSourceFile = null;
    _placementState.fields = [];
    if (savedLibDoc && !(Array.isArray(savedLibDoc.signatureFields) && savedLibDoc.signatureFields.length) && placedFields.length) {
      if (confirm(`Save this signature placement to "${savedLibDoc.title || 'this document'}" so future sends start with it?`)) {
        try {
          // 🔴 GEOMETRY ONLY — _signLibStripFields drops signerName/signerPhone/
          // signerEmail/personId/accessToken. The template is reused for the NEXT
          // employee; carrying this signer's contact details into it would leak
          // one person's phone/email onto another person's send.
          const strippedFields = _signLibStripFields(placedFields);
          if (strippedFields.length) {
            await updateDoc(doc(db, 'tenants', tenantId, 'signDocTemplates', savedLibDoc.id), {
              signatureFields: strippedFields,
              templateSavedAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            });
            const cachedTpl = (_SIGN_LIB.docs || []).find(d => d.id === savedLibDoc.id);
            if (cachedTpl) cachedTpl.signatureFields = strippedFields;
            window.showToast?.('Placement saved to your document library', 'success');
          }
        } catch (tplErr) {
          console.error('[HR] save placement template error:', tplErr);
          window.showToast?.('Sent, but the placement could not be saved to the library', 'warning');
        }
      }
    }

    // Refresh the employee detail
    setTimeout(() => openEmployeeDetail(personId), 500);

  } catch (err) {
    console.error('[HR] sendWithFields error:', err);
    window.showToast?.('Failed to send document', 'error');
  } finally {
    btn.disabled = false;
    btn.innerHTML = origHtml;
  }
}

// TASK-1634 (4) — Chris's product call: the trash button IS the unsend, not a
// second button. Previously this hard-`deleteDoc`'d the row, which stopped
// the signer (loadSignDocument/submitDocumentSignature 404 on a missing doc)
// but as an accident, not a designed state: the signer saw a generic red
// "Document not found" error card, the minted accessTokens were left live and
// orphaned until their 72h expiry, the Storage PDF was never revoked, and
// nothing was written to the audit log. Soft-cancel via a server callable
// instead: `status` is locked against ANY client update by firestore.rules
// (the TASK-1084 completion-forgery lock, rules ~3245), so a naive
// `updateDoc({status:'cancelled'})` here would just be silently rejected —
// the Admin SDK inside cancelSignDocument is what can actually flip it.
async function withdrawSignDocument(docId, personId) {
  if (!confirm('Withdraw this document? The signer will no longer be able to open or sign it.')) return;
  try {
    const tenantId = HRState.tenantId === 'demo-tenant' ? 'demo' : HRState.tenantId;
    const cancel = httpsCallable(functions, 'cancelSignDocument');
    const result = await cancel({ tenantId, docId });
    if (result?.data?.ok === false) {
      window.showToast?.('Document not found — it may have already been removed', 'error');
    } else {
      window.showToast?.('Document withdrawn', 'success');
    }
    // Refresh the list
    loadPendingSignDocs(personId);
  } catch (err) {
    console.error('[HR] withdrawSignDocument error:', err);
    window.showToast?.(err?.message || 'Failed to withdraw document', 'error');
  }
}

// Keep legacy sendForSignature as a no-op redirect
async function sendForSignature(personId) {
  // Legacy — now handled by openPlacementModal -> sendWithFields
  openPlacementModal(personId);
}

// TASK-1067 — inner markup for the §382.601(d) Policy Receipt row (header +
// send/resend button + signed/unsigned status). Split out of buildComplianceTabHTML
// so _refreshOpenDaReceiptSection can re-render JUST this row live (in place)
// when the driver submits, without rebuilding the whole compliance tab.
function _daReceiptSectionInner(emp) {
  const rec = emp.drugAlcoholPolicyReceipt;
  const signed = rec?.signedDateStr;
  // TASK-9696 — a receipt is owed by a person a testing program covers. Not
  // covered + nothing signed → say so instead of "FMCSA requires this".
  const _cov = window.DqMatrix.testingCoverageFor(emp);
  const _progChip = rec?.program && window.DqMatrix.PROGRAMS[rec.program]
    ? `<span style="font-size:0.7rem;font-weight:600;padding:1px 6px;border-radius:6px;background:#EFF6FF;color:#1D4ED8;margin-left:6px;">${escapeHtml(window.DqMatrix.PROGRAMS[rec.program].short)}</span>` : '';
  // TASK-1154 — a driver-signed receipt carries an immutable artifactId (the
  // policyReceipts doc); legacy partner_portal-RECORDED receipts (recordDaReceipt)
  // have none, so there is no signed PDF to render. Button enabled only when an
  // artifact exists; otherwise shown DISABLED with a tooltip (never hidden).
  const hasArtifact = !!rec?.artifactId;
  return `
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span class="material-icons" style="font-size:18px;color:#7c3aed;">assignment_turned_in</span> Policy Receipt
          <span class="compliance-cite-badge">49 CFR §382.601(d)</span>
        </div>
        <button class="btn btn-sm btn-secondary" onclick="window.HRModule.sendIncumbentDaReceiptLink('${emp.id}', this)" style="padding:4px 10px;font-size:0.75rem;">
          <span class="material-icons" style="font-size:14px;">send</span> ${signed ? 'Resend Link' : 'Send Link'}
        </button>
      </div>
      ${signed
        ? `<div style="display:flex;align-items:center;gap:8px;padding:8px;background:#F0FDF4;border:1px solid #BBF7D0;border-radius:var(--radius-md);">
            <span class="material-icons" style="font-size:16px;color:#059669;">check_circle</span>
            <div style="font-size:0.82rem;color:#14532D;flex:1;min-width:0;">Signed ${escapeHtml(rec.signedDateStr)} (policy v${escapeHtml(rec.policyVersion || '?')})${_progChip}</div>
            ${hasArtifact
              ? `<button class="btn btn-sm btn-secondary" onclick="window.viewSignedDaReceipt('${emp.id}', this)" style="padding:4px 10px;font-size:0.72rem;flex-shrink:0;">
                  <span class="material-icons" style="font-size:14px;">picture_as_pdf</span> View signed receipt
                </button>`
              : `<button class="btn btn-sm btn-secondary" disabled title="Signed via legacy portal (PDF unavailable)" style="padding:4px 10px;font-size:0.72rem;flex-shrink:0;opacity:0.55;cursor:not-allowed;">
                  <span class="material-icons" style="font-size:14px;">picture_as_pdf</span> View signed receipt
                </button>`}
          </div>`
        : (_cov.any
          ? `<div style="display:flex;align-items:center;gap:8px;padding:8px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-md);">
            <span class="material-icons" style="font-size:16px;color:#D97706;">warning</span>
            <div style="font-size:0.82rem;color:#92400E;">No signed receipt on file. Required for everyone in the testing program (${escapeHtml(window.DqMatrix.coverageLabel(_cov))}).</div>
          </div>`
          : `<div style="display:flex;align-items:center;gap:8px;padding:8px;background:#F1F5F9;border:1px solid #E2E8F0;border-radius:var(--radius-md);">
            <span class="material-icons" style="font-size:16px;color:#64748B;">info</span>
            <div style="font-size:0.82rem;color:#475569;">Not in a drug &amp; alcohol testing program (not a CDL driver, no PHMSA or company-program flag) — no policy receipt is owed. Set the classification or program flags if that is wrong.</div>
          </div>`)}`;
}

// TASK-1067 — if an employee detail is open with the Compliance tab rendered, flip
// the §382.601(d) Policy Receipt row to "Signed <date>" the moment the driver
// submits. drugAlcoholPolicyReceipt is set SERVER-side when the driver signs, so
// the people onSnapshot (subscribeToData) fires with the new field; this re-renders
// only the receipt row in place (no whole-tab rebuild, no lost scroll/tab state).
// TASK-9359 — the identity vault snapshot can land AFTER an employee detail
// opened. Re-point the open detail at the (overlaid) person and re-render its
// read-only Compliance tab — never while the tab is in EDIT mode (that would
// throw away typed input).
function _hrRefreshOpenIdentityViews() {
  const selId = HRState.selectedEmployee?.id;
  if (!selId) return;
  const fresh = HRState.people.find(p => p.id === selId);
  if (!fresh) return;
  HRState.selectedEmployee = fresh;
  const container = document.getElementById('hr-detail-tab-compliance');
  if (container && container.innerHTML && !document.getElementById('btn-save-compliance')) {
    container.innerHTML = buildComplianceTabHTML(fresh);
    try { loadEmployeeCitations(selId); } catch (_) { /* best-effort */ }
  }
}

function _refreshOpenDaReceiptSection() {
  const container = document.getElementById('hr-da-receipt-section');
  if (!container) return;
  const selId = HRState.selectedEmployee?.id;
  if (!selId) return;
  const fresh = HRState.people.find(p => p.id === selId);
  if (!fresh) return;
  // Keep the open detail's backing object fresh so a later tab switch (which
  // rebuilds the tab via buildComplianceTabHTML) also shows the signed state.
  HRState.selectedEmployee = fresh;
  container.innerHTML = _daReceiptSectionInner(fresh);
}

// TASK-1555 — resolve WHICH peopleBackgroundChecks doc + report id the MVR
// viewer should open for a person. The person-keyed bg doc is a one-shot mirror
// taken at promote time (approveToRoster), so a Checkr MVR ordered AFTER hire
// exists only on the CANDIDATE-keyed doc — while the webhook still stamps
// person.mvrCheckrReportId (admin-SDK-written, not client-forgeable). Fall back
// to that stamp + person.candidateId so the carrier can open a report they paid
// for. Never fall back on the stamp ALONE: _verifyReportScope (server) requires
// the bg doc at recordId to exist AND list the reportId, so without candidateId
// the button would render and then 404 — no button beats a dead one.
// TASK-1496-S7 — MVR detail (result, violations, pull log, good-faith efforts,
// Checkr id, source/state/purpose, PDF path) resolves legacy-first through the
// peopleDq sibling, like the S6 medical detail.
function _hrMvrDq(e) { return e && window.DqMatrix?.mvrDetail ? window.DqMatrix.mvrDetail(e, HRState.tenantId) : e; }

function _mvrViewTarget(emp) {
  if (!emp) return null;
  emp = _hrMvrDq(emp); // TASK-1496-S7
  const docReportId = emp.bgCheck?.backgroundReports?.mvr?.checkrReportId || null;
  // TASK-9235 — emp.bgCheck may now be the LINKED applicant record (hire-time
  // reference); target the record the data actually came from.
  if (docReportId) {
    const rid = (emp.bgCheck && emp.bgCheck._bgRecordId) || emp.id;
    return { recordId: rid, reportId: docReportId, fromCandidateDoc: rid !== emp.id };
  }
  if (emp.mvrCheckrReportId && emp.candidateId) {
    return { recordId: emp.candidateId, reportId: emp.mvrCheckrReportId, fromCandidateDoc: true };
  }
  return null;
}

// TASK-1556 — label for the red MVR result chip. The Checkr webhook maps
// consider/review → mvrLastResult:'violations' but never stamps
// mvrViolationCount (only the manual saveMvrPull path writes it), so
// Number(undefined || 0) fabricated a red "0 Violations" chip on every
// Checkr-flagged MVR — self-contradictory, and a dispatcher reads "0" as
// clean-ish. Only claim a count when a real one exists; otherwise say what we
// actually know. Checkr flags MVRs for suspensions and accidents too, so the
// no-count copy names neither a number nor a cause.
function _mvrFlaggedChipLabel(emp) {
  const raw = _hrMvrDq(emp)?.mvrViolationCount; // TASK-1496-S7
  const n = (raw === null || raw === undefined || raw === '') ? NaN : Number(raw);
  if (Number.isFinite(n)) return `${n} Violation${n === 1 ? '' : 's'}`;
  return 'Needs Review — see report';
}

function buildComplianceTabHTML(emp) {
  emp = _hrMvrDq(emp); // TASK-1496-S7 — MVR detail legacy-first for the whole tab
  // TASK-9359 — this viewer cannot read the peopleIdentity vault and the person
  // doc carries no legacy copy: "Restricted", never a misleading "—".
  const _idRestricted9359 = !!(window.PersonIdentity && window.PersonIdentity.identityUnknown(emp));
  // TASK-1090 — Clearinghouse applicability (§382.701 covers CDL drivers only).
  // The DQ checklist below already matrix-gates its rows (TASK-934), but the
  // red pre-employment banner + the Clearinghouse card rendered for EVERYONE —
  // offering a query that can never be run for a NonCDL person. Unclassified
  // people resolve to 'cdl' (full surface — fail-safe).
  const _chApplies = window.DqMatrix.getApplicableDqItems(emp).items.clearinghouse;
  // TASK-4748 — decide who may LOG a query before any button is drawn.
  const _chMode = _hrClearinghouseLogMode();
  const _chCanLog = _chMode !== 'readonly';
  const _chReadonlyNote = `<span style="display:inline-flex;align-items:center;gap:4px;font-size:0.75rem;color:var(--text-secondary);font-weight:600;" title="${escapeHtml(CLEARINGHOUSE_READONLY_MSG)}"><span class="material-icons" style="font-size:14px;">visibility</span> View only</span>`;

  // Pre-employment Clearinghouse banner — 49 CFR §382.701(a) requires a full query
  // before this driver performs safety-sensitive functions. If the person is hired
  // (any hireDate or rehireDate) and no Clearinghouse query has been logged yet,
  // surface as a red banner with a CTA that opens the existing query modal.
  const empHireDate = emp.hireDate?.toDate?.() || emp.hireDate || emp.rehireDate?.toDate?.() || emp.rehireDate;
  const preEmpPending = _chApplies && !!empHireDate && !emp.clearinghouseLastQueryDate;
  const preEmpBannerHtml = preEmpPending ? `
    <div style="background:linear-gradient(135deg,#FEF2F2,#FFE4E6);border:1px solid #FCA5A5;border-left:4px solid #DC2626;border-radius:8px;padding:12px 14px;margin-bottom:var(--spacing-md);display:flex;align-items:flex-start;gap:10px;">
      <span class="material-icons" style="color:#DC2626;font-size:22px;flex-shrink:0;">warning</span>
      <div style="flex:1;min-width:0;">
        <div style="font-weight:700;font-size:0.9rem;color:#991B1B;">Pre-Employment Clearinghouse Query required</div>
        <div style="font-size:0.78rem;color:#7F1D1D;margin-top:2px;">Per 49 CFR §382.701(a), the employer must run a full Drug &amp; Alcohol Clearinghouse query before this driver performs safety-sensitive functions. No query has been logged yet.</div>
        <div style="margin-top:8px;display:flex;flex-wrap:wrap;gap:6px;align-items:center;">
          <a href="https://clearinghouse.fmcsa.dot.gov/" target="_blank" rel="noopener" style="display:inline-flex;align-items:center;gap:6px;padding:6px 12px;background:#DC2626;color:#fff;text-decoration:none;border-radius:6px;font-size:0.78rem;font-weight:700;">
            <span class="material-icons" style="font-size:14px;">open_in_new</span> Run at FMCSA
          </a>
          ${_chCanLog ? `<button class="btn" onclick="window.HRModule.openClearinghouseModal('${escJsAttr(emp.id || '')}')" style="display:inline-flex;align-items:center;gap:6px;padding:6px 12px;background:#fff;color:#991B1B;border:1px solid #FCA5A5;border-radius:6px;font-size:0.78rem;font-weight:700;cursor:pointer;">
            <span class="material-icons" style="font-size:14px;">edit_note</span> Log query result
          </button>` : `<span style="font-size:0.78rem;color:#7F1D1D;">${escapeHtml(CLEARINGHOUSE_READONLY_MSG)}</span>`}
        </div>
      </div>
    </div>` : '';

  // TASK-1482 — driver record portability panel (pending / ported / idle) +
  // "Ported — review & confirm" chips on the sections that carry ported values.
  const recordPortHtml = _recordPortSectionHTML(emp);
  const cdlPortChip = _portChipFor(emp, ['dlState', 'dlNumber', 'dlIssueDate', 'dlExpiryDate', 'cdlLicensed', 'cdlClass', 'cdlEndorsements', 'cdlRestrictions', 'cdlIssueDate', 'cdlExpiryDate'], ['license']);
  const medPortChip = _portChipFor(emp, ['medCardExpiryDate', 'medCardNumber'], ['medCard']);

  const row = (label, value) => `
    <div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border-light);">
      <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;">${label}</span>
      <span style="font-size:0.8rem;color:var(--text-primary);font-weight:500;">${escapeHtml(String(value || '—'))}</span>
    </div>`;

  const expiryBadge = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const days = Math.ceil((d - now) / 86400000);
    if (days < 0) return '<span style="color:#DC2626;font-weight:600;font-size:0.75rem;">EXPIRED</span>';
    if (days < 30) return `<span style="color:#F59E0B;font-weight:600;font-size:0.75rem;">Expires in ${days}d</span>`;
    return `<span style="color:#059669;font-weight:600;font-size:0.75rem;">Valid (${days}d)</span>`;
  };

  // CDL Section
  // TASK-1053 — each compliance section sits in its own .compliance-section-card
  // (shared class in styles.css, mirrors the partner portal's .cp-dd-block) with
  // a hardcoded CFR-cite pill in the header. Presentational only — content and
  // onclick handlers unchanged.
  const cdlHtml = `
    <div class="compliance-section-card">
      <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;margin-bottom:var(--spacing-sm);">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">badge</span> CDL Information
        <span class="compliance-cite-badge">49 CFR §391.11</span>
        ${cdlPortChip}
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
        ${row('CDL Class', emp.cdlClass && emp.cdlClass !== 'none' ? 'Class ' + emp.cdlClass : 'Not on file')}
        ${row('Endorsements', emp.cdlEndorsements || 'None')}
        ${row('Issue Date', formatDateShort(emp.cdlIssueDate) || 'N/A')}
        ${row('Expiry Date', (formatDateShort(emp.cdlExpiryDate) || 'N/A'))}
        ${emp.cdlExpiryDate ? `<div style="padding:4px 0;">${expiryBadge(emp.cdlExpiryDate)}</div>` : ''}
        <!-- TASK-4605 Slice 2 — shared front/back licence card (Pitfall #8:
             renderer renders, host authorizes). canEdit unconditionally true,
             matching the exact prior behavior of this single-slot card (no
             narrower client gate existed here before this task; the underlying
             Firestore people-update rule is isDispatcherOrHigher ||
             (hasModule('roster') && !isViewUploadOnly) regardless of surface —
             a pre-existing surface/rule mismatch this task does not change).
             Bound below via bindLicenseImages once this string lands in the
             DOM (real functions, never inline onclick — TASK-4605's own
             HTML-entity-in-onclick fix). -->
        ${/* TASK-4605 (integrated-diff COD) — _hrCanWriteRoster() (:123) exists
             precisely to stop this: its own comment says it mirrors the server gate
             so a view/upload-only roster member is not shown an upload that will be
             denied downstream. Hardcoding true regressed that decision. */ ''}
        ${window.PersonSections.renderLicenseImages(emp, { canEdit: _hrCanWriteRoster(), canOpen: true })}
        ${emp.dlDocumentPath ? `<div style="padding:2px 0 0;text-align:right;">
          <a href="#" onclick="event.preventDefault();window.viewCredDocHistory('${emp.id}','license',null,this);return false;" title="All uploaded versions of this document" style="font-size:0.72rem;color:var(--text-secondary);display:inline-flex;align-items:center;gap:3px;cursor:pointer;">
            <span class="material-icons" style="font-size:14px;">history</span> History
          </a>
        </div>` : ''}
      </div>
    </div>`;

  // Medical Card Section
  // TASK-9476 (Crown) — a classified Non-Driver has no DOT med card requirement
  // (DqMatrix `medCard`, pinned to services/applicantRequirements.js DQ_MED_CARD).
  // The section stays (the record is still viewable/editable) but reads N/A
  // instead of a red EXPIRED off a stray date. Unclassified → CDL tier → applies.
  const _medApp = window.DqMatrix.getApplicableDqItems(emp);
  const _medApplies = _medApp.items.medCard !== false;
  // TASK-1403 — ND intrastate exemption: N/A with the citation, never hidden.
  const _medIx = _medApp.intrastateExemption;
  const _medIxActive = !!(_medIx && _medIx.active);
  // Carrier-only attestation: offered to a classified Non-CDL Driver, never in a
  // partner client session (the callable refuses partners and re-checks the gate).
  const _medIxOffer = !_hrPartnerClientTenantId() && _medApp.explicit && _medApp.positionType === 'non_cdl_driver' && _hrCanSetIntrastateExemption();
  const _med = window.DqMatrix?.medDetail ? window.DqMatrix.medDetail(emp, HRState.tenantId) : emp; // TASK-1496-S6 (legacy-first)
  const medExamHistory = Array.isArray(_med.medExamHistory) ? _med.medExamHistory : [];
  // TASK-647 — ONE effective med-card doc (most-recent of medCertPdfUrl /
  // medCardDocumentUrl), so only ONE "View" link renders (used to double up).
  const effMed = getEffectiveMedCardDoc(emp);
  const medHtml = `
    <div class="compliance-section-card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);gap:6px;flex-wrap:wrap;">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span class="material-icons" style="font-size:18px;color:#059669;">medical_services</span> Medical Card
          <span class="compliance-cite-badge">49 CFR §391.43</span>
          ${(_medApplies || _medIxActive) ? '' : '<span style="display:inline-flex;align-items:center;padding:2px 8px;background:var(--bg-page);color:var(--text-secondary);border:1px solid var(--border-light);border-radius:6px;font-size:0.7rem;font-weight:600;">N/A — Non-Driver</span>'}
          ${medPortChip}
          ${emp.medCardPendingReview ? '<span style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;background:#FEF3C7;color:#92400E;border-radius:6px;font-size:0.7rem;font-weight:600;"><span class="material-icons" style="font-size:12px;">pending</span> Pending review</span>' : ''}
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          ${_medIxOffer ? `<button class="btn btn-sm" id="btn-intrastate-${emp.id}" onclick="window.HRModule.openIntrastateExemptionModal('${emp.id}')" style="padding:4px 10px;font-size:0.75rem;background:#fff;color:var(--text-primary);border:1px solid var(--border-light);" title="North Dakota intrastate med-card exemption">
            <span class="material-icons" style="font-size:14px;">map</span> ND exemption
          </button>` : ''}
          <button class="btn btn-sm" onclick="window.HRModule.sendMedUploadLink('${emp.id}')" style="padding:4px 10px;font-size:0.75rem;background:#fff;color:#0284C7;border:1px solid #0284C7;" title="Send the driver an SMS link to upload their new med card photo">
            <span class="material-icons" style="font-size:14px;">sms</span> Send Upload Link
          </button>
          <button class="btn btn-sm" onclick="window.HRModule.openMedExamModal('${emp.id}')" style="padding:4px 10px;font-size:0.75rem;background:#047857;color:#fff;border:none;">
            <span class="material-icons" style="font-size:14px;">${_med.medExaminerName ? 'edit' : 'add'}</span> ${_med.medExaminerName ? 'New Exam' : 'Record Exam'}
          </button>
        </div>
      </div>
      ${window.DqMatrix.intrastateNoteHtml(emp, { fmtDate: formatDateShort })}
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
        ${_med.medExaminerName ? `${row('Examiner', _med.medExaminerName)}${_med.medExaminerNRCME ? row('NRCME #', _med.medExaminerNRCME) : ''}` : (_med.medCardNumber ? row('Provider', _med.medCardNumber) : '')}
        ${emp.medExamLastDate ? row('Last Exam', formatDateShort(emp.medExamLastDate)) : ''}
        <div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border-light);">
          <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;">Expiry Date</span>
          <span style="font-size:0.8rem;display:flex;align-items:center;gap:6px;">
            <input type="date" value="${emp.medCardExpiryDate || ''}"
              onchange="window.HRModule.updateMedCardExpiry('${emp.id}', this.value)"
              style="border:1px solid var(--border-light);border-radius:6px;padding:4px 8px;font-size:0.82rem;background:var(--bg-primary);color:var(--text-primary);" />
          </span>
        </div>
        ${emp.medCardExpiryDate ? `<div style="padding:4px 0;">${_medApplies ? expiryBadge(emp.medCardExpiryDate) : _medIxActive ? '<span style="color:var(--text-secondary);font-weight:600;font-size:0.75rem;">Not required while the ND intrastate exemption applies.</span>' : '<span style="color:var(--text-secondary);font-weight:600;font-size:0.75rem;">Not required for this role (Non-Driver). If they drive a CMV, change their DOT role.</span>'}</div>` : ''}
        ${Array.isArray(_med.medRestrictions) && _med.medRestrictions.length > 0 ? `
          <div style="padding:6px 0;border-bottom:1px solid var(--border-light);">
            <div style="font-size:0.78rem;color:var(--text-secondary);font-weight:600;margin-bottom:4px;">Restrictions</div>
            <div style="display:flex;flex-wrap:wrap;gap:4px;">
              ${_med.medRestrictions.map(r => `<span style="display:inline-block;padding:2px 8px;background:#FEF3C7;color:#92400E;border-radius:4px;font-size:0.72rem;">${escapeHtml(r)}</span>`).join('')}
            </div>
          </div>` : ''}
        ${medExamHistory.length > 1 ? `
          <div style="margin-top:8px;">
            <div style="font-size:0.72rem;color:var(--text-secondary);font-weight:600;margin-bottom:4px;">Exam History</div>
            ${medExamHistory.slice(1, 6).map(h => `
              <div style="display:flex;align-items:center;gap:6px;padding:4px 0;font-size:0.75rem;border-bottom:1px solid var(--border-light);">
                <span class="material-icons" style="font-size:12px;color:#059669;">medical_services</span>
                <span style="flex:1;">${escapeHtml(h.examinerName || '—')} <span style="color:var(--text-secondary);">(${escapeHtml(h.examinerNRCME || 'no NRCME')})</span></span>
                ${h.certPdfUrl ? `<a href="${escapeHtml(h.certPdfUrl)}" target="_blank" rel="noopener" style="color:var(--brand-blue);"><span class="material-icons" style="font-size:14px;">picture_as_pdf</span></a>` : ''}
                <span style="color:var(--text-secondary);">${h.examDate || ''}</span>
              </div>
            `).join('')}
          </div>` : ''}
        ${effMed ? `
          <div style="padding:6px 0;border-top:1px solid var(--border-light);margin-top:4px;display:flex;align-items:center;justify-content:space-between;">
            <a href="#" onclick="event.preventDefault();window.viewComplianceDoc('${escJsAttr(effMed.path || '')}','${escJsAttr(effMed.url || '')}','medical-card',this);return false;" style="font-size:0.8rem;color:var(--brand-blue);display:flex;align-items:center;gap:4px;cursor:pointer;">
              <span class="material-icons" style="font-size:16px;color:#059669;">check_circle</span> View Medical Card${effMed.source === 'medCertPdfUrl' ? ' <span style="color:var(--text-secondary);font-weight:400;">(self-uploaded)</span>' : ''}
            </a>
            <a href="#" onclick="event.preventDefault();window.viewCredDocHistory('${emp.id}','medCard',null,this);return false;" title="All uploaded versions of this document" style="font-size:0.72rem;color:var(--text-secondary);display:inline-flex;align-items:center;gap:3px;cursor:pointer;margin-left:auto;">
              <span class="material-icons" style="font-size:14px;">history</span> History
            </a>
            <button class="btn-icon" onclick="window.HRModule.${effMed.source === 'medCertPdfUrl' ? 'removeMedCertPdf' : 'removeMedCardDocument'}('${emp.id}')" title="Remove">
              <span class="material-icons" style="font-size:16px;color:#DC2626;">delete</span>
            </button>
          </div>` : `
          <div style="padding:8px 0;text-align:center;">
            <button class="btn btn-sm btn-secondary" onclick="window.HRModule.uploadMedCardDocument('${emp.id}')" style="font-size:0.75rem;">
              <span class="material-icons" style="font-size:14px;">upload_file</span> Upload Medical Card
            </button>
          </div>`}
      </div>
    </div>`;

  // Drug Tests Section — HR owns this data
  const drugTests = (HRState.drugTests || []).filter(t => t.personId === emp.id);
  const drugTestsHtml = drugTests.length > 0
    ? drugTests.sort((a, b) => (b.testDate || '').localeCompare(a.testDate || '')).map(t => {
        const passed = t.result === 'negative' || t.result === 'pass';
        return `
          <div style="display:flex;align-items:center;gap:8px;padding:8px;background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:4px;">
            <span class="material-icons" style="font-size:16px;color:${passed ? '#059669' : '#DC2626'};">${passed ? 'check_circle' : 'cancel'}</span>
            <div style="flex:1;min-width:0;">
              <div style="font-size:0.82rem;font-weight:600;">${escapeHtml(t.testType || 'Test')}
                <span style="font-size:0.7rem;font-weight:600;padding:1px 6px;border-radius:6px;margin-left:4px;${t.program ? 'background:#EFF6FF;color:#1D4ED8;' : 'background:#F1F5F9;color:#64748B;'}" title="${t.program ? escapeHtml(window.DqMatrix.PROGRAMS[t.program]?.label || t.program) : 'Recorded before program tracking — the program is not inferred'}">${t.program ? escapeHtml(window.DqMatrix.PROGRAMS[t.program]?.short || t.program) : 'Program not recorded'}</span>
              </div>
              <div style="font-size:0.75rem;color:var(--text-secondary);">${passed ? 'Negative (Pass)' : escapeHtml(t.result || 'Positive')} &bull; ${formatDateShort(t.testDate) || 'N/A'}</div>
              ${t.notes ? `<div style="font-size:0.72rem;color:var(--text-secondary);">${escapeHtml(t.notes)}</div>` : ''}
            </div>
            <button class="btn-icon" onclick="window.HRModule.viewDrugTestDocument('${t.id}')" title="View test result / MRO report" style="padding:2px;">
              <span class="material-icons" style="font-size:14px;color:var(--text-secondary);">description</span>
            </button>
            ${(t.ccfDocumentPath || t.ccfDocumentUrl) ? `<button class="btn-icon" onclick="window.HRModule.viewDrugTestDocument('${t.id}', 'ccf')" title="View Custody &amp; Control Form (CCF)" style="padding:2px;">
              <span class="material-icons" style="font-size:14px;color:#7c3aed;">assignment</span>
            </button>` : ''}
            ${_hrIsOwnerOrAdmin() ? `<button class="btn-icon" onclick="window.HRModule.deleteDrugTestRecord('${t.id}')" title="Delete" style="padding:2px;">
              <span class="material-icons" style="font-size:14px;color:#DC2626;">delete</span>
            </button>` : ''}
          </div>`;
      }).join('')
    : '<div style="padding:12px;color:var(--text-secondary);font-size:0.85rem;text-align:center;">No drug tests on file</div>';

  const drugHtml = `
    <div class="compliance-section-card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span class="material-icons" style="font-size:18px;color:#7c3aed;">science</span> Drug & Alcohol Tests
          ${drugTests.length > 0 ? `<span style="font-size:0.75rem;color:var(--text-secondary);">${drugTests.length}</span>` : ''}
          <span class="compliance-cite-badge">49 CFR Part 382</span>
        </div>
        <button class="btn btn-sm btn-primary" onclick="window.HRModule.showDrugTestModal('${emp.id}')" style="padding:4px 10px;font-size:0.75rem;">
          <span class="material-icons" style="font-size:14px;">add</span> Add Test
        </button>
      </div>
      ${drugTestsHtml}
    </div>

    <!-- TASK-DA-v1 Slice 2 — §382.601(d) signed-receipt status + send-link button.
         TASK-1067 — stable id container + _daReceiptSectionInner helper so the row
         live-refreshes to "Signed <date>" when the driver submits (see
         _refreshOpenDaReceiptSection, driven by the people onSnapshot). -->
    <div class="compliance-section-card" id="hr-da-receipt-section">
      ${_daReceiptSectionInner(emp)}
    </div>`;

  // Training Section — delegates to ComplianceModule
  const trainings = emp.isnetTrainings || [];
  const trainingHtml = `
    <div class="compliance-section-card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#7c3aed;">school</span> Training & Certifications
          ${trainings.length > 0 ? `<span style="font-size:0.75rem;color:var(--text-secondary);">${trainings.length}</span>` : ''}
        </div>
        <button class="btn btn-sm btn-primary" onclick="window.HRModule.addIsnetTraining('${emp.id}')" style="padding:4px 10px;font-size:0.75rem;">
          <span class="material-icons" style="font-size:14px;">add</span> Add
        </button>
      </div>
      ${trainings.length > 0 ? trainings.map((t, idx) => `
        <div style="display:flex;align-items:flex-start;gap:8px;padding:8px;background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);margin-bottom:4px;">
          <span class="material-icons" style="font-size:18px;color:#7c3aed;margin-top:2px;">school</span>
          <div style="flex:1;min-width:0;">
            <div style="font-weight:600;font-size:0.82rem;">${escapeHtml(t.trainingName || 'Untitled')}</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);">
              ${t.organization ? escapeHtml(t.organization) + ' &bull; ' : ''}${t.issueDate ? formatDateShort(t.issueDate) : ''}${t.expiryDate ? ' — ' + formatDateShort(t.expiryDate) : ''}
            </div>
            ${t.expiryDate ? `<div style="margin-top:2px;">${expiryBadge(t.expiryDate)}</div>` : ''}
          </div>
          <button class="btn-icon" onclick="window.HRModule.editIsnetTraining('${emp.id}',${idx},'${escJsAttr(t.id || '')}')" title="Edit" style="padding:2px;">
            <span class="material-icons" style="font-size:14px;">edit</span>
          </button>
          <button class="btn-icon" onclick="window.HRModule.deleteIsnetTraining('${emp.id}',${idx},'${escJsAttr(t.id || '')}')" title="Delete" style="padding:2px;">
            <span class="material-icons" style="font-size:14px;color:#DC2626;">delete</span>
          </button>
        </div>
      `).join('') : '<div style="padding:12px;color:var(--text-secondary);font-size:0.85rem;text-align:center;">No training records on file</div>'}
    </div>`;

  // TASK-513 — Required Trainings (Safety Roster) required-vs-has summary.
  const reqConfig = _getSafetyTrainingsList();
  // TASK-9911 M2 — rendered by the shared PersonSections.renderSkillsAndCerts
  // (same section as Roster's person form + Compliance). HR is the host: its
  // own state calc, its own action buttons and gate (_canCarrierEditTrainings).
  const requiredTrainingHtml = (() => {
    const PS = window.PersonSections;
    if (!PS || typeof PS.renderSkillsAndCerts !== 'function') return '';
    const canExempt = _canCarrierEditTrainings();
    const skillRows = PS.skillsCertsSkillRows(emp);
    // COD r1: escJsAttr or no buttons — escapeHtml is not JS-string safe.
    const jsq = (v) => window.escJsAttr(String(v));
    const canJs = typeof window.escJsAttr === 'function';
    const body = PS.renderSkillsAndCerts({
      skills: skillRows ? { rows: skillRows, editable: false, today: PS.skillsCertsToday(), note: 'No card needed. Ticked in Roster → Skills.' } : null,
      certs: reqConfig.length ? {
        rows: reqConfig.map(c => ({ itemId: c.itemId, label: c.label, ...computeRequiredTrainingState(emp, c) })),
        formatDate: formatDateShort,
        actionsHtml: (r) => {
          // TASK-1353 — tier-scoped rows: no Mark-N/A / edit controls.
          if (r.state === 'na' || !canExempt || !canJs) return '';
          const isExempt = r.state === 'exempt';
          // TASK-919 / TASK-1031 — per-employee N/A toggle, worded.
          const toggle = `<button class="btn btn-sm" title="${isExempt ? 'Clear Not-Applicable exemption' : 'Mark Not Applicable for this employee'}" onclick="window.HRModule.toggleTrainingExemption('${jsq(emp.id)}','${jsq(r.itemId)}',${isExempt ? 'false' : 'true'})" style="font-size:0.72rem;padding:2px 8px;white-space:nowrap;">${isExempt ? '<span class="material-icons" style="font-size:13px;vertical-align:middle;">undo</span> Undo' : 'Mark N/A'}</button>`;
          // Pencil — log/upload the cert right here (matrix's openMatrixCertUpload).
          const editBtn = !isExempt
            ? `<button class="btn-icon" title="${r.state === 'missing' ? 'Log / upload this certificate' : 'Edit this training record'}" onclick="window.HRModule.openMatrixCertUpload('${jsq(emp.id)}','${jsq(r.itemId)}')" style="padding:2px;"><span class="material-icons" style="font-size:14px;">edit</span></button>`
            : '';
          return editBtn + toggle;
        },
      } : null,
    });
    if (!body) return '';
    return `
    <div class="compliance-section-card" id="hr-skills-certs-card">
      <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;margin-bottom:var(--spacing-sm);">
        <span class="material-icons" style="font-size:18px;color:#7c3aed;">verified_user</span> Skills &amp; certifications
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">${body}</div>
    </div>`;
  })();

  // DVIR/JSA Accountability — TASK-1003: the missed counts drill into the dated
  // list of the actual missed events. The headline number is derived from the
  // SAME flag lookup the drill-in uses (getEmployeeAccountabilityFlags), so a
  // clickable count ALWAYS equals its list length (no drift). Legacy persistent
  // counters (missedDvirCount with no per-event records) fall back to a static,
  // non-clickable number — there's nothing dated to itemize, so no dead click.
  const dvirFlagsForEmp = getEmployeeAccountabilityFlags(emp, 'dvir');
  const jsaFlagsForEmp = getEmployeeAccountabilityFlags(emp, 'jsa');
  const missedDvirs = dvirFlagsForEmp.length || (emp.missedDvirCount || 0);
  const missedJsas = jsaFlagsForEmp.length || (emp.missedJsaCount || 0);
  const dvirDrillable = dvirFlagsForEmp.length > 0;
  const jsaDrillable = jsaFlagsForEmp.length > 0;
  const hasIssues = missedDvirs > 0 || missedJsas > 0;
  const acctRow = (count, label, drillable, type) => {
    const inner = `<span class="material-icons" style="font-size:16px;">warning</span> ${count} missed ${label}${count > 1 ? 's' : ''}`;
    if (drillable) {
      // onclick passes ONLY the literal 'dvir'/'jsa' — never user data. The drill
      // resolves the employee from HRState.selectedEmployee, so no emp.id/name is
      // interpolated into the JS-context attribute (window.escapeHtml is text-node
      // safe, NOT attribute-quote safe — COD Adversary TASK-1003).
      return `<button type="button" onclick="window.HRModule.openMissedEventsDrill('${type}')" title="View the dated ${label} events that were missed" style="display:flex;align-items:center;gap:6px;padding:6px 0;color:#DC2626;font-size:0.82rem;font-weight:600;background:none;border:none;cursor:pointer;text-align:left;width:100%;text-decoration:underline;text-underline-offset:2px;">
        ${inner}
        <span class="material-icons" style="font-size:16px;margin-left:auto;">chevron_right</span>
      </button>`;
    }
    return `<div style="display:flex;align-items:center;gap:6px;padding:4px 0;color:#DC2626;font-size:0.82rem;font-weight:600;">${inner}</div>`;
  };
  const accountabilityHtml = `
    <div class="compliance-section-card">
      <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;margin-bottom:var(--spacing-sm);">
        <span class="material-icons" style="font-size:18px;color:${hasIssues ? '#DC2626' : '#059669'};">checklist</span> DVIR & JSA Accountability
      </div>
      ${hasIssues ? `
        <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
          ${missedDvirs > 0 ? acctRow(missedDvirs, 'DVIR', dvirDrillable, 'dvir') : ''}
          ${missedJsas > 0 ? acctRow(missedJsas, 'JSA', jsaDrillable, 'jsa') : ''}
        </div>
      ` : `<div style="display:flex;align-items:center;gap:6px;padding:8px;color:#059669;font-size:0.85rem;">
        <span class="material-icons" style="font-size:16px;">check_circle</span> No accountability issues
      </div>`}
    </div>`;

  // ISNet / Employee Profile section (merged from ISNet tab)
  const profileHtml = `
    <div class="compliance-section-card">
      <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;margin-bottom:var(--spacing-sm);">
        <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">person</span> Employee Profile
        <button class="btn btn-sm" onclick="window.HRModule.editComplianceTab('${emp.id}')" style="margin-left:auto;padding:4px 14px;font-size:0.75rem;background:var(--brand-blue);color:#fff;border:none;border-radius:6px;">
          <span class="material-icons" style="font-size:14px;">edit</span> Edit
        </button>
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 var(--spacing-md);">
          ${_hrSensitiveRow('SSN', _hrCanSeeSecureTax() ? emp.secureTax?.ssn : '', 'ssn', emp.id)}
          ${row('Date of Birth', _idRestricted9359 ? 'Restricted' : formatDateShort(emp.dob))}
          ${row('Phone', emp.phone)}
          ${row('Email', emp.email)}
          ${emp.personalEmail && String(emp.personalEmail).toLowerCase() !== String(emp.email || '').toLowerCase() ? row('Personal Email', emp.personalEmail) : ''}
          ${row('Current Address', _hrCurrentAddress(emp))}
          ${row('Employee ID', emp.employeeId)}
          ${row('ISNetworld #', emp.isnetNickname)}
          ${row('Employee Type', emp.isnetEmployeeType)}
          ${row('Go To Field', emp.goToField)}
          ${row('DL State', _idRestricted9359 ? 'Restricted' : emp.dlState)}
          ${_idRestricted9359 ? row('DL Number', 'Restricted') : _hrSensitiveRow('DL Number', emp.dlNumber, 'id')}
          ${!_idRestricted9359 && emp.cdlNumber && emp.cdlNumber !== emp.dlNumber ? _hrSensitiveRow('CDL Number', emp.cdlNumber, 'id') : ''}
          ${row('DL Expiry', _idRestricted9359 ? 'Restricted' : formatDateShort(emp.dlExpiryDate))}
          ${emp.dlExpiryDate ? `<div style="padding:4px 0;">${expiryBadge(emp.dlExpiryDate)}</div>` : '<div></div>'}
          ${row('Safety Training', emp.safetyTrainingVerified)}
          ${row('Supervisor', emp.supervisorRole)}
          ${row('Reports to', emp.reportsTo ? ((HRState.people || []).find(p => p.id === emp.reportsTo)?.name || emp.reportsTo) : '')}
        </div>
        ${row('Specialized Cert', emp.specializedCertification)}
        ${row('Reasonable Suspicion', emp.reasonableSuspicionTraining)}
        ${row('Testing program', window.DqMatrix.coverageLabel(window.DqMatrix.testingCoverageFor(emp)))}
        <div style="padding:4px 0;">${window.DqMatrix.randomPoolChipHtml(emp)}</div>
      </div>
    </div>`;

  // Citations / Vehicle Exam Reports (merged from ISNet tab)
  const citationsHtml = `
    <div class="compliance-section-card">
      <div style="display:flex;align-items:center;gap:6px;margin-bottom:var(--spacing-sm);">
        <span class="material-icons" style="font-size:18px;color:#DC2626;">gavel</span>
        <span style="font-weight:700;font-size:0.9rem;color:var(--text-primary);flex:1;">Vehicle Exam Reports / Citations</span>
        <button class="btn btn-sm btn-secondary" onclick="window.HRModule.openCitationModal('${emp.id}')" style="font-size:0.72rem;padding:3px 8px;">
          <span class="material-icons" style="font-size:14px;">add</span> Add
        </button>
      </div>
      <div id="hr-employee-citations-list" style="font-size:0.8rem;color:var(--text-secondary);">Loading...</div>
    </div>`;

  // Clearinghouse Section
  const chConsent = emp.clearinghouseConsentDate ? formatDateShort(emp.clearinghouseConsentDate) : null;
  const chLastQuery = emp.clearinghouseLastQueryDate ? formatDateShort(emp.clearinghouseLastQueryDate) : null;
  const chStatus = emp.clearinghouseStatus || 'not_started';
  const chNextDue = emp.clearinghouseNextDue ? formatDateShort(emp.clearinghouseNextDue) : null;
  const CH_STATES = {
    not_started: { label: 'Not Started', color: '#94A3B8', icon: 'radio_button_unchecked', bg: '#F1F5F9' },
    consent_on_file: { label: 'General Consent on File', color: '#3B82F6', icon: 'description', bg: '#DBEAFE' },
    full_query_requested: { label: 'Full Query Requested', color: '#D97706', icon: 'hourglass_top', bg: '#FFFBEB' },
    consent_pending: { label: 'Driver Consent Pending', color: '#D97706', icon: 'pending', bg: '#FFFBEB' },
    clear: { label: 'Clear', color: '#059669', icon: 'verified', bg: '#D1FAE5' },
    limited_hit: { label: 'Limited Hit — Needs Full Query', color: '#DC2626', icon: 'warning', bg: '#FEE2E2' },
    refused: { label: 'Refused / Blocked', color: '#DC2626', icon: 'block', bg: '#FEE2E2' },
  };
  const chState = CH_STATES[chStatus] || CH_STATES.not_started;
  const chHistory = sortClearinghouseHistoryNewestFirst(emp.clearinghouseHistory); // TASK-9449

  // TASK-1090 — explicit non-CDL tier: render a clear "Clearinghouse N/A" card
  // (no actionable Log Query); previously recorded data stays visible read-only.
  const clearinghouseHtml = !_chApplies ? `
    <div class="compliance-section-card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);flex-wrap:wrap;gap:8px;">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span class="material-icons" style="font-size:18px;color:#94A3B8;">vaccines</span> FMCSA Clearinghouse
          <span class="compliance-cite-badge">49 CFR §382.701</span>
        </div>
        <span style="display:inline-flex;align-items:center;gap:4px;padding:2px 10px;border-radius:999px;background:#F1F5F9;color:#64748B;border:1px solid #E2E8F0;font-size:0.75rem;font-weight:600;" title="The FMCSA Drug & Alcohol Clearinghouse applies to CDL drivers under 49 CFR §382 — a non-CDL person cannot be queried.">Non-CDL — N/A</span>
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);font-size:0.8rem;color:var(--text-secondary);">
        The FMCSA Clearinghouse applies to CDL drivers (49 CFR §382.701) — a Clearinghouse query cannot be run for this classification.
        ${(chLastQuery || chConsent) ? `<div style="margin-top:6px;">${row('Last Query (read-only)', chLastQuery || 'Never')}${chConsent ? row('General Consent Date', chConsent) : ''}</div>` : ''}
      </div>
    </div>` : `
    <div class="compliance-section-card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span class="material-icons" style="font-size:18px;color:#7c3aed;">vaccines</span> FMCSA Clearinghouse
          <span class="compliance-cite-badge">49 CFR §382.701</span>
        </div>
        ${_chCanLog ? `<button class="btn btn-sm btn-primary" onclick="window.HRModule.openClearinghouseModal('${emp.id}')" style="padding:4px 10px;font-size:0.75rem;">
          <span class="material-icons" style="font-size:14px;">add</span> Log Query
        </button>` : _chReadonlyNote}
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
        <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);">
          <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;flex:1;">Status</span>
          <span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:${chState.bg};color:${chState.color};border-radius:6px;font-size:0.75rem;font-weight:600;">
            <span class="material-icons" style="font-size:14px;">${chState.icon}</span> ${chState.label}
          </span>
        </div>
        ${row('General Consent Date', chConsent || 'Not on file')}
        ${row('Last Query', chLastQuery || 'Never')}
        ${chNextDue ? `
          <div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border-light);">
            <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;">Next Annual Query Due</span>
            <span style="font-size:0.8rem;">${chNextDue} ${expiryBadge(emp.clearinghouseNextDue)}</span>
          </div>` : ''}
        ${chHistory.length > 0 ? `
          <div style="margin-top:8px;">
            <div style="font-size:0.72rem;color:var(--text-secondary);font-weight:600;margin-bottom:4px;">Query History</div>
            ${chHistory.slice(0, 5).map(h => `
              <div style="display:flex;align-items:center;gap:6px;padding:4px 0;font-size:0.75rem;border-bottom:1px solid var(--border-light);">
                <span class="material-icons" style="font-size:12px;color:${h.result === 'clear' ? '#059669' : h.result === 'flagged' ? '#DC2626' : '#6B7280'};">
                  ${h.result === 'clear' ? 'check_circle' : h.result === 'flagged' ? 'warning' : 'schedule'}
                </span>
                <span style="flex:1;">${h.type === 'full' ? 'Full Query' : h.type === 'limited' ? 'Limited Query' : h.type === 'pre_employment' ? 'Pre-Employment' : h.type || 'Query'}</span>
                <span style="color:${h.result === 'clear' ? '#059669' : h.result === 'flagged' ? '#DC2626' : 'var(--text-secondary)'};font-weight:600;">${h.result === 'clear' ? 'Clear' : h.result === 'flagged' ? 'Flagged' : h.result || '—'}</span>
                <span style="color:var(--text-secondary);">${h.date || ''}</span>
              </div>
            `).join('')}
          </div>` : ''}
        <div style="padding:8px 0;text-align:center;">
          <a href="https://clearinghouse.fmcsa.dot.gov/" target="_blank" rel="noopener" class="btn btn-sm btn-secondary" style="font-size:0.72rem;padding:3px 8px;">
            <span class="material-icons" style="font-size:12px;">open_in_new</span> Open FMCSA Clearinghouse
          </a>
        </div>
      </div>
    </div>`;

  // MVR Pull Section (state-side driving record — separate from federal Clearinghouse)
  const mvrLastDate = emp.mvrLastPullDate ? formatDateShort(emp.mvrLastPullDate) : null;
  const mvrNextDue = emp.mvrNextDueDate ? formatDateShort(emp.mvrNextDueDate) : null;
  const mvrLastResult = emp.mvrLastResult || '';
  const mvrLastState = emp.mvrLastState || '';
  const mvrPullHistory = Array.isArray(emp.mvrPullHistory) ? emp.mvrPullHistory : [];
  const MVR_COLORS = {
    clean: { label: 'Clean', color: '#059669', bg: '#D1FAE5', icon: 'check_circle' },
    // TASK-1556 — never invent a count; see _mvrFlaggedChipLabel.
    violations: { label: _mvrFlaggedChipLabel(emp), color: '#DC2626', bg: '#FEE2E2', icon: 'warning' },
    pending: { label: 'Pending', color: '#D97706', bg: '#FFFBEB', icon: 'hourglass_top' },
  };
  const mvrState = MVR_COLORS[mvrLastResult] || null;

  // TASK-1279 — surface an IN-FLIGHT MVR order so the card doesn't read "Never
  // pulled" with the paid Run button live (the double-charge trap Chris hit: he
  // ordered a Commercial MVR, the invite went out awaiting signature, but the card
  // showed nothing + "Run MVR ($)" stayed active). The person doc already carries
  // the pending signal (bgCheckRequestedAt / an in-flight checkr status / bgCheckPending);
  // no server change needed. GATE on the pending package actually COVERING mvr so a
  // criminal-only order doesn't false-block the MVR button; unknown slug ⇒ don't
  // disable (the MVR order paths do stamp a slug, so that's a rare edge).
  const _mvrPendSlug = emp.bgCheckRequestedPackageSlug || emp.checkrPackageSlug || emp.bgCheckPackageSlug || '';
  const _mvrPendCoversMvr = (CHECKR_PACKAGE_COVERS[_mvrPendSlug] || []).includes('mvr');
  const _mvrPendStatusLc = String(emp.checkrReportStatus || emp.bgCheckStatus || '').toLowerCase();
  const _mvrPendInFlight =
    (!!emp.bgCheckRequestedAt && !emp.checkrReportId) ||
    ['invited', 'created', 'pending', 'needs_candidate_action'].includes(_mvrPendStatusLc) ||
    emp.bgCheckPending === true;
  // TASK-1384 — invitation evidence from the bg doc (peopleBackgroundChecks).
  // Needed twice over: (1) a partner-ROUTED request (bgCheckRequestedAt stamp,
  // no invitation, no email to the driver) must stop rendering as "awaiting
  // signature" — the 8/05 incident had dispatch chasing two drivers for a
  // Checkr email that never existed; (2) creating a REAL invitation deletes the
  // stamp server-side and person docs never get the checkrReportStatus
  // dual-write (candidates only), so without this doc a just-ordered invitation
  // renders as nothing pending and the paid button re-arms mid-flight.
  // emp.bgCheck is loaded before this builder runs (TASK-1147); undefined ⇒
  // unknown ⇒ subject-doc-only classification (current behavior while loading).
  const _mvrBgKind = mvrBgDocPendingKind(emp.bgCheck); // 'signature' | 'processing' | null
  // Only "in flight" until a completed pull lands — completion hands off to the normal
  // result state below. Check BOTH date fields (legacy records store the pull under
  // mvrLastPulled, not mvrLastPullDate — COD: else a legacy-completed MVR with a stale
  // bgCheckRequestedAt would wrongly show "in progress").
  const _mvrInFlight = !(emp.mvrLastPullDate || emp.mvrLastPulled)
    && ((_mvrPendCoversMvr && _mvrPendInFlight) || !!_mvrBgKind);
  // TASK-1384 — discriminate WHICH pending state, so the copy never claims a
  // signature step that isn't real. Priority: real invitation/report evidence
  // (bg doc, then subject-doc status) beats the request stamp — if an
  // invitation exists, "awaiting signature" is true and any leftover stamp is
  // residue of the best-effort server clear.
  let _mvrPendKind = null;
  if (_mvrInFlight) {
    if (_mvrBgKind) _mvrPendKind = _mvrBgKind;
    else if (['invited', 'created', 'needs_candidate_action'].includes(_mvrPendStatusLc)) _mvrPendKind = 'signature';
    else if (_mvrPendStatusLc === 'pending') _mvrPendKind = 'processing';
    else if (emp.bgCheckRequestedAt && !emp.checkrReportId) _mvrPendKind = 'requested';
    else _mvrPendKind = 'consent'; // bgCheckPending two-stage — consent captured, order not yet placed
  }
  // Per-state copy. 'requested' must NOT read as awaiting-signature: no
  // invitation exists and nothing has been sent to the driver — the ball is
  // with the partner. Distinct purple badge so it scans differently at a glance.
  // &quot;-escape too: these land inside double-quoted title attributes, and
  // escapeHtml (div.innerHTML) does not escape quotes. Renders as " in text.
  const _mvrFirstName = escapeHtml((emp.name || 'the driver').split(/\s+/)[0]).replace(/"/g, '&quot;');
  const _mvrPartnerEsc = escapeHtml(hrPartnerLabel()).replace(/"/g, '&quot;');
  const _MVR_PEND_UI = {
    requested: {
      icon: 'forward_to_inbox', bg: '#F3E8FF', color: '#7C3AED',
      label: `MVR requested — pending ${_mvrPartnerEsc}`,
      btnLabel: 'MVR requested',
      btnTitle: `This MVR was requested from ${_mvrPartnerEsc} and is on their worklist — nothing has been sent to the driver yet. The result lands here automatically once they run it.`,
      hint: `The request is with ${_mvrPartnerEsc} — no signature is pending with the driver. Recording an existing pull is still free and instant.`,
    },
    signature: {
      icon: MVR_COLORS.pending.icon, bg: MVR_COLORS.pending.bg, color: MVR_COLORS.pending.color,
      label: `MVR ordered — awaiting ${_mvrFirstName}'s signature`,
      btnLabel: 'MVR in progress',
      btnTitle: 'An MVR order is already in progress for this driver (awaiting their signature). The result lands here automatically — no need to re-order.',
      hint: 'An MVR order is already in flight for this driver — the result lands here automatically, no need to re-order. Recording an existing pull is still free and instant.',
    },
    processing: {
      icon: MVR_COLORS.pending.icon, bg: MVR_COLORS.pending.bg, color: MVR_COLORS.pending.color,
      label: 'MVR ordered — report processing',
      btnLabel: 'MVR in progress',
      btnTitle: `An MVR order is in progress — ${_mvrFirstName} already completed their part and the report is processing. The result lands here automatically — no need to re-order.`,
      hint: 'An MVR report is processing — the result lands here automatically, no need to re-order. Recording an existing pull is still free and instant.',
    },
    consent: {
      icon: MVR_COLORS.pending.icon, bg: MVR_COLORS.pending.bg, color: MVR_COLORS.pending.color,
      label: 'Check authorized — order not yet placed',
      btnLabel: 'Check pending',
      btnTitle: 'This driver already authorized a background check that includes the MVR — the order has not been placed yet, so do not start a second one.',
      hint: 'A background check covering the MVR is already authorized for this driver — no need to start another. Recording an existing pull is still free and instant.',
    },
  };
  const _mvrPendUi = _MVR_PEND_UI[_mvrPendKind] || _MVR_PEND_UI.signature;

  // TASK-555 — two MVR paths, explicitly framed:
  //  • FREE "record an existing pull" (openMvrPullModal → saveMvrPull): a pure
  //    date log, NO Checkr, NO charge, NO billing/partner gate. Satisfies the DQ
  //    "MVR within 12 months" + starts the §391.25 reminder cron. UNGATED — even
  //    when the partner handles BG/MVR, the carrier may still bookkeep a pull they
  //    already have (Chris decided 2026-06-10). This is the primary path for an
  //    already-hired driver, since the cheap Checkr MVR (commercial_mvr_psp) is
  //    PSP-blocked post-hire (TASK-554).
  //  • PAID "Run a new MVR ($)" (openBackgroundCheckModal intent='mvr'): keeps all
  //    the existing gates — CC/payment (needs_payment_method), partner-route
  //    (hrBgHandledByPartner → request from DOT manager), billing routing, and the
  //    TASK-554 post-hire PSP guard. Surfaced only when Checkr is live for the
  //    tenant; the modal itself is authoritative on every gate.
  const mvrCheckrActive = !!window.AppState?.tenantData?.compliancePackageDefault;
  const mvrPartnerHandles = hrBgHandledByPartner();
  const mvrFreeBtn = `<button class="btn btn-sm" onclick="window.HRModule.openMvrPullModal('${emp.id}')" style="padding:4px 10px;font-size:0.75rem;background:#0284C7;color:#fff;border:none;" title="Already have a current MVR? Log the pull/next-due date. Free — no charge, no re-pull.">
          <span class="material-icons" style="font-size:14px;">${mvrLastDate ? 'refresh' : 'edit_note'}</span> ${mvrLastDate ? 'New Pull' : 'Record existing'}
        </button>`;
  // Paid run: hidden entirely when Checkr isn't live. When the partner handles
  // BG/MVR, label it as a request to the partner (modal routes it server-side).
  // TASK-1279 — while an MVR order is in flight, disable the paid button (prevents a
  // second $ order / double-charge); the free "Record existing" path stays live.
  const mvrPaidBtn = _mvrInFlight
    ? `<button class="btn btn-sm" disabled style="padding:4px 10px;font-size:0.75rem;background:#F3F4F6;color:#9CA3AF;border:1px solid #E5E7EB;cursor:not-allowed;" title="${_mvrPendUi.btnTitle}">
          <span class="material-icons" style="font-size:14px;">${_mvrPendKind === 'requested' ? 'forward_to_inbox' : 'hourglass_top'}</span> ${_mvrPendUi.btnLabel}
        </button>`
    : mvrCheckrActive
    ? `<button class="btn btn-sm" onclick="window.HRModule.openBackgroundCheckModal('${escJsAttr(emp.id)}','person','mvr')" style="padding:4px 10px;font-size:0.75rem;background:transparent;color:#9333EA;border:1px solid #9333EA;" title="${mvrPartnerHandles ? 'Request your DOT manager run a new MVR via Checkr' : 'Order a new MVR via Checkr (paid)'}">
          <span class="material-icons" style="font-size:14px;">${mvrPartnerHandles ? 'forward_to_inbox' : 'directions_car'}</span> ${mvrPartnerHandles ? 'Request MVR' : 'Run MVR ($)'}
        </button>`
    // TASK-1605 — "hidden entirely" was a silent absence; now it says why, with the link.
    : hrScreeningPackageStopHtml({ compact: true });

  // TASK-969 — MVR as its own first-class piece: when a Checkr MVR report is
  // linked (the TASK-832 canonical capture stamps backgroundReports.mvr.
  // checkrReportId on the bg doc), the MVR card gets its OWN "View MVR" button
  // opening the standalone MVR viewer/print — no detour through the full BG
  // report. Purpose label (order-time §391.23/§391.25/ad-hoc stamp) renders as
  // a row when known (webhook echoes it to person.mvrLastPurpose).
  const _mvrBgDoc = emp.bgCheck || {};
  // TASK-1555 — a post-hire Checkr order never reaches the person-keyed bg doc
  // (promote-time one-shot mirror), so key the button off _mvrViewTarget, which
  // falls back to the candidate-keyed doc via the person's own webhook stamp.
  const _mvrView = _mvrViewTarget(emp);
  const _escA = window.escAttr || escapeHtml;
  const mvrViewBtn = _mvrView
    ? `<button class="btn btn-sm" onclick="window.HRModule.openMvrReportViewer('${escJsAttr(emp.id)}','${escJsAttr(_mvrView.reportId)}',false,${_mvrView.fromCandidateDoc ? `'${escJsAttr(_mvrView.recordId)}'` : 'null'})" style="padding:4px 10px;font-size:0.75rem;background:#B45309;color:#fff;border:none;" title="Open the standalone MVR report (view + print)">
          <span class="material-icons" style="font-size:14px;">description</span> View MVR
        </button>`
    : '';
  const mvrPurposeVal = emp.mvrLastPurpose || _mvrBgDoc.backgroundReports?.mvr?.purpose || null;

  const mvrHtml = `
    <div class="compliance-section-card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);gap:6px;flex-wrap:wrap;">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span class="material-icons" style="font-size:18px;color:#0284C7;">drive_eta</span> MVR — Driving Record
          <span class="compliance-cite-badge">49 CFR §391.25</span>
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          ${mvrViewBtn}
          ${mvrFreeBtn}
          ${mvrPaidBtn}
        </div>
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
        ${_mvrInFlight ? `
          <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);flex-wrap:wrap;">
            <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;flex:1;">Status</span>
            <span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:${_mvrPendUi.bg};color:${_mvrPendUi.color};border-radius:6px;font-size:0.75rem;font-weight:600;">
              <span class="material-icons" style="font-size:14px;">${_mvrPendUi.icon}</span> ${_mvrPendUi.label}
            </span>
            ${(_mvrPendKind === 'requested' && _hrIsOwnerOrAdmin()) ? `
            <button class="btn btn-sm" onclick="window.HRModule.cancelPersonBgRequest('${emp.id}')" style="padding:2px 8px;font-size:0.7rem;background:transparent;color:#DC2626;border:1px solid #FCA5A5;" title="Withdraw this request — removes it from ${_mvrPartnerEsc}'s worklist. You can request it again at any time.">
              <span class="material-icons" style="font-size:12px;">close</span> Cancel request
            </button>` : ''}
          </div>` : ''}
        ${mvrState ? `
          <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);">
            <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;flex:1;">Latest Result</span>
            <span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:${mvrState.bg};color:${mvrState.color};border-radius:6px;font-size:0.75rem;font-weight:600;">
              <span class="material-icons" style="font-size:14px;">${mvrState.icon}</span> ${mvrState.label}
            </span>
          </div>` : ''}
        ${row('Last Pull', mvrLastDate || 'Never pulled')}
        ${/* TASK-9429 — a pull date with no MVR record behind it (saved before the upload
              gate) is not a current MVR. Same predicate as the DQ scorecard + partner
              portal; a linked Checkr MVR report (View MVR) also counts as the record. */
          ((emp.mvrLastPullDate || emp.mvrLastPulled) && !_mvrView && window.DqMatrix && !window.DqMatrix.mvrRecordIsOnFile(emp)) ? `
          <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);flex-wrap:wrap;">
            <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;flex:1;">MVR document</span>
            <span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:#FFFBEB;color:#92400E;border:1px solid #FCD34D;border-radius:6px;font-size:0.75rem;font-weight:600;"><span class="material-icons" style="font-size:14px;">warning_amber</span> Not on file — upload the MVR; a pull date alone is not proof</span>
          </div>` : ''}
        ${MVR_PURPOSE_DISPLAY[mvrPurposeVal] ? `
          <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);">
            <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;flex:1;">Purpose</span>
            ${_mvrPurposePill(mvrPurposeVal, { full: true })}
          </div>` : ''}
        ${mvrLastState ? row('State / Province', (window.LicenseJurisdiction ? window.LicenseJurisdiction.jurisdictionLabel(mvrLastState) : mvrLastState)) : ''}
        ${/* TASK-9822 — §391.23(b) good-faith efforts (shared renderer; HR authorizes with the roster-write gate recordMvrGoodFaithEffort enforces). */
          (window.PersonSections && typeof window.PersonSections.renderMvrGoodFaith === 'function')
            ? window.PersonSections.renderMvrGoodFaith(emp.mvrGoodFaithEfforts, { canRecord: _hrCanWriteRoster(), recordOnClick: 'window.HRModule.openMvrGoodFaith(' + JSON.stringify(String(emp.id)) + ')' })
            : ''}
        ${mvrNextDue ? `
          <div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border-light);">
            <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;">Next Pull Due</span>
            <span style="font-size:0.8rem;">${mvrNextDue} ${expiryBadge(emp.mvrNextDueDate)}</span>
          </div>` : ''}
        ${(emp.mvrLatestPdfPath || emp.mvrLatestPdfUrl) ? `
          <div style="padding:6px 0;border-bottom:1px solid var(--border-light);display:flex;align-items:center;justify-content:space-between;gap:8px;">
            <a href="#" onclick="event.preventDefault();window.viewComplianceDoc('${escJsAttr(emp.mvrLatestPdfPath || '')}','${escJsAttr(emp.mvrLatestPdfUrl || '')}','mvr',this);return false;" style="font-size:0.8rem;color:var(--brand-blue);display:inline-flex;align-items:center;gap:4px;cursor:pointer;">
              <span class="material-icons" style="font-size:16px;">picture_as_pdf</span> View latest MVR PDF
            </a>
            <a href="#" onclick="event.preventDefault();window.viewCredDocHistory('${emp.id}','mvr',null,this);return false;" title="All uploaded versions of this document" style="font-size:0.72rem;color:var(--text-secondary);display:inline-flex;align-items:center;gap:3px;cursor:pointer;">
              <span class="material-icons" style="font-size:14px;">history</span> History
            </a>
          </div>` : ''}
        ${emp.mvrLatestViolations ? `
          <div style="padding:8px;background:#FEF2F2;border-left:3px solid #DC2626;border-radius:6px;margin-top:6px;font-size:0.78rem;color:#7F1D1D;white-space:pre-wrap;">
            ${escapeHtml(emp.mvrLatestViolations)}
          </div>` : ''}
        ${mvrPullHistory.length > 0 ? `
          <div style="margin-top:8px;">
            <div style="font-size:0.72rem;color:var(--text-secondary);font-weight:600;margin-bottom:4px;">Pull History</div>
            ${mvrPullHistory.slice(0, 5).map(h => {
              const hState = MVR_COLORS[h.result] || { label: h.result || '—', color: '#6B7280', icon: 'help', bg: '#F1F5F9' };
              const vCount = Number(h.violationCount || 0);
              const labelText = h.result === 'violations' ? `${vCount} Violation${vCount === 1 ? '' : 's'}` : (hState.label || h.result);
              return `
              <div style="display:flex;align-items:center;gap:6px;padding:4px 0;font-size:0.75rem;border-bottom:1px solid var(--border-light);">
                <span class="material-icons" style="font-size:12px;color:${hState.color};">${hState.icon}</span>
                <span style="flex:1;font-weight:500;">${escapeHtml(labelText)} ${h.state ? `<span style="color:var(--text-secondary);">(${escapeHtml(h.state)})</span>` : ''}</span>
                ${h.pdfUrl ? `<a href="${_safeHttpUrl(h.pdfUrl)}" target="_blank" rel="noopener" style="color:var(--brand-blue);"><span class="material-icons" style="font-size:14px;">picture_as_pdf</span></a>` : ''}
                <span style="color:var(--text-secondary);">${escapeHtml(h.date || '')}</span>
              </div>`;
            }).join('')}
          </div>` : ''}
        ${!mvrLastDate ? `<div style="padding:14px 12px;text-align:center;">
          <div style="color:var(--text-secondary);font-size:0.82rem;margin-bottom:10px;">
            No MVR on file. FMCSR 391.25 requires an MVR pull at least once every 12 months.
          </div>
          <div style="display:flex;flex-direction:column;gap:6px;align-items:center;">
            <button class="btn btn-sm" onclick="window.HRModule.openMvrPullModal('${emp.id}')" style="font-size:0.78rem;padding:6px 14px;background:#0284C7;color:#fff;border:none;">
              <span class="material-icons" style="font-size:15px;vertical-align:-3px;">edit_note</span> Already have one — record the date <span style="opacity:0.85;font-weight:600;">(Free)</span>
            </button>
            ${_mvrInFlight
              ? `<button class="btn btn-sm" disabled style="font-size:0.78rem;padding:6px 14px;background:#F3F4F6;color:#9CA3AF;border:1px solid #E5E7EB;cursor:not-allowed;">
              <span class="material-icons" style="font-size:15px;vertical-align:-3px;">${_mvrPendKind === 'requested' ? 'forward_to_inbox' : 'hourglass_top'}</span> ${_mvrPendUi.label}
            </button>
            <div style="font-size:0.7rem;color:var(--text-secondary);max-width:340px;line-height:1.35;">${_mvrPendUi.hint}</div>`
              : mvrCheckrActive ? `<button class="btn btn-sm" onclick="window.HRModule.openBackgroundCheckModal('${escJsAttr(emp.id)}','person','mvr')" style="font-size:0.78rem;padding:6px 14px;background:transparent;color:#9333EA;border:1px solid #9333EA;">
              <span class="material-icons" style="font-size:15px;vertical-align:-3px;">${mvrPartnerHandles ? 'forward_to_inbox' : 'directions_car'}</span> ${mvrPartnerHandles ? 'Request a new MVR from ' + escapeHtml(hrPartnerLabel()) : 'Run a new MVR ($)'}
            </button>
            <div style="font-size:0.7rem;color:var(--text-secondary);max-width:340px;line-height:1.35;">Recording an existing pull is free and instant — no charge, no re-pull. ${mvrPartnerHandles ? 'A new paid pull is run by ' + escapeHtml(hrPartnerLabel()) + '.' : 'Running a new MVR orders a fresh report via Checkr (paid).'}</div>` : `<div style="font-size:0.7rem;color:var(--text-secondary);max-width:340px;line-height:1.35;">Recording an existing pull is free and instant — no charge, no re-pull.</div>`}
          </div>
        </div>` : ''}
      </div>
    </div>`;

  // Background Check (Checkr) Section
  const bg = emp.bgCheck || {};
  const bgStatus = bg.latestStatus || bg.latestInvitationStatus || null;
  const bgInvitationStatus = bg.latestInvitationStatus || null;
  const bgInvitationUrl = bg.invitationUrl || null;
  const bgPackage = bg.latestPackage || null;
  const bgInvitedAt = bg.invitedAt ? formatDateShort(bg.invitedAt.toDate ? bg.invitedAt.toDate().toISOString().split('T')[0] : bg.invitedAt) : null;
  const bgCompletedAt = bg.completedAt ? formatDateShort(bg.completedAt.toDate ? bg.completedAt.toDate().toISOString().split('T')[0] : bg.completedAt) : null;
  const bgHistory = Array.isArray(bg.history) ? bg.history : [];
  const BG_COLORS = {
    clear: { label: 'Clear', color: '#059669', bg: '#D1FAE5', icon: 'check_circle' },
    eligible: { label: 'Eligible', color: '#059669', bg: '#D1FAE5', icon: 'check_circle' },
    consider: { label: 'Consider', color: '#D97706', bg: '#FFFBEB', icon: 'warning' },
    escalated: { label: 'Escalated', color: '#D97706', bg: '#FFFBEB', icon: 'warning' },
    suspended: { label: 'Suspended', color: '#DC2626', bg: '#FEE2E2', icon: 'pause_circle' },
    // TASK-559 — Checkr paused waiting on candidate. Label refined below by kind.
    needs_candidate_action: { label: '⏸ Action needed — candidate ID/identity confirmation', color: '#D97706', bg: '#FFFBEB', icon: 'pause_circle' },
    upgrade_failed: { label: 'Add-on screening failed to attach', color: '#DC2626', bg: '#FEE2E2', icon: 'error_outline' },
    canceled: { label: 'Canceled', color: '#6B7280', bg: '#F1F5F9', icon: 'cancel' },
    invited: { label: 'Invited (waiting on candidate)', color: '#2563EB', bg: '#DBEAFE', icon: 'mail' },
    created: { label: 'Invitation Sent', color: '#2563EB', bg: '#DBEAFE', icon: 'mail' },
    completed: { label: 'Form Completed', color: '#2563EB', bg: '#DBEAFE', icon: 'hourglass_top' },
    expired: { label: 'Invitation Expired', color: '#DC2626', bg: '#FEE2E2', icon: 'timer_off' },
    // TASK-4584 — invitation dead at Checkr, no report ordered. See the
    // matching pair in buildCandidateCheckrCard's STATE map.
    invitation_expired: { label: 'Invitation Expired — send a new one', color: '#DC2626', bg: '#FEE2E2', icon: 'timer_off' },
    invitation_deleted: { label: 'Invitation Canceled — send a new one', color: '#DC2626', bg: '#FEE2E2', icon: 'link_off' },
  };
  let bgState = BG_COLORS[bgStatus] || BG_COLORS[bgInvitationStatus] || null;
  // TASK-559 — refine the generic needs_candidate_action label by the specific
  // Checkr stall reason (id/license/abstract/suspended/paused/deferred).
  if (bgStatus === 'needs_candidate_action' && bgState) {
    bgState = { ...bgState, label: _bgNeedsActionLabel(bg.needsActionKind) };
  }

  // TASK-231 + TASK-232 v1.15.397: surface "View Full Report" whenever a
  // reportId exists. Earlier we gated on a status allowlist, but the bg
  // doc's `latestStatus` is webhook-maintained and can be stale (Kino
  // Carter example 2026-05-11 — Checkr report completed, webhook never
  // updated the doc, status sat at "pending"). The modal itself fetches
  // live state from Checkr via getCheckrReportDetail and renders pending
  // screenings gracefully, so having a reportId is sufficient permission
  // to open it. Mirrors the partner-side gate in
  // public/compliance-manager.html (buildPartnerCheckrCard row).
  const bgHasCompletedReport = !!bg.latestReportId;

  // TASK-652 (F) — suppress the duplicate "New Check" re-request when a
  // completed OR in-flight report already exists for this subject, and route
  // review/consider outcomes to adjudication instead. A fresh re-request when a
  // Checkr candidate already exists is exactly what triggers the 400 "ssn
  // already used" collision the rest of this task fixes — so don't even offer
  // it. "In-flight" = invited/created/completed(form)/pending; "terminal" =
  // clear/eligible/consider/review/escalated/suspended/etc. We only offer a NEW
  // run when there is genuinely nothing on file (no candidate link, no report).
  const _bgStatusLc = String(bgStatus || bgInvitationStatus || '').toLowerCase();
  const _bgInFlight = ['invited', 'created', 'completed', 'pending', 'needs_candidate_action'].includes(_bgStatusLc);
  const _bgNeedsReview = ['consider', 'review', 'escalated'].includes(_bgStatusLc);
  // existing candidate link via either field the source-of-truth stamp writes.
  const _bgHasExistingCandidate = !!(bg.candidateId || emp.checkrCandidateId || emp.embedCheckrCandidateId);
  const _bgSuppressNewCheck = _bgHasExistingCandidate && (bgHasCompletedReport || _bgInFlight);

  const bgCheckHtml = `
    <div class="compliance-section-card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);gap:6px;flex-wrap:wrap;">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#9333EA;">fact_check</span> Background Check
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          ${bgHasCompletedReport ? `
            <button class="btn btn-sm" onclick="window.HRModule.openBgReportViewer('${escJsAttr(_bgRecordIdFor(emp))}','${escJsAttr(bg.latestReportId)}')" style="padding:4px 10px;font-size:0.75rem;background:transparent;color:#9333EA;border:1px solid #9333EA;">
              <span class="material-icons" style="font-size:14px;">description</span> View Full Report
            </button>` : ''}
          ${_bgSuppressNewCheck
            ? (_bgNeedsReview
              ? `<button class="btn btn-sm" onclick="window.HRModule.openBgReportViewer('${escJsAttr(_bgRecordIdFor(emp))}','${escJsAttr(bg.latestReportId || '')}')" style="padding:4px 10px;font-size:0.75rem;background:#D97706;color:#fff;border:none;">
                  <span class="material-icons" style="font-size:14px;">flag</span> Action Required: Review Report
                </button>`
              : '' /* in-flight / clean: no re-request button — a check is already on file */)
            : `<button class="btn btn-sm btn-primary" onclick="window.HRModule.openBackgroundCheckModal('${escJsAttr(emp.id)}')" style="padding:4px 10px;font-size:0.75rem;background:#9333EA;color:#fff;border:none;">
                <span class="material-icons" style="font-size:14px;">${bg.candidateId ? 'refresh' : 'add'}</span> ${bg.candidateId ? 'New Check' : 'Run Check'}
              </button>`}
        </div>
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
        ${bgState ? `
          <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);">
            <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;flex:1;">Status</span>
            <span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:${bgState.bg};color:${bgState.color};border-radius:6px;font-size:0.75rem;font-weight:600;">
              <span class="material-icons" style="font-size:14px;">${bgState.icon}</span> ${bgState.label}
            </span>
          </div>` : ''}
        ${bg._bgCopyDecision ? row('Earlier decision', /* row() escapes the value */
          (bg._bgCopyDecision.decision === 'cleared' ? 'Cleared' : bg._bgCopyDecision.decision === 'held' ? 'On hold' : String(bg._bgCopyDecision.decision).replace(/_/g, ' '))
          + (bg._bgCopyDecision.sameReport
            ? ' — recorded on the old roster copy of this report'
            : ' — recorded on the old roster copy BEFORE this report arrived; this report still needs a decision')) : ''}
        ${bgPackage ? row('Package', _prettyPackageSlug(bgPackage)) : ''}
        ${bgInvitedAt ? row('Invited', bgInvitedAt) : ''}
        ${bgCompletedAt ? row('Completed', bgCompletedAt) : ''}
        ${bgInvitationUrl && (bgInvitationStatus === 'created' || bgInvitationStatus === 'invited') ? `
          <div style="padding:6px 0;border-bottom:1px solid var(--border-light);">
            <a href="${escapeHtml(bgInvitationUrl)}" target="_blank" rel="noopener" style="font-size:0.8rem;color:var(--brand-blue);display:inline-flex;align-items:center;gap:4px;">
              <span class="material-icons" style="font-size:16px;">open_in_new</span> Open candidate form (for testing/preview)
            </a>
          </div>` : ''}
        ${bgHistory.length > 0 ? `
          <div style="margin-top:8px;">
            <div style="font-size:0.72rem;color:var(--text-secondary);font-weight:600;margin-bottom:4px;">Event History</div>
            ${_hrBgHistorySlot(bgHistory)}
          </div>` : ''}
        ${!bg.candidateId ? `<div style="padding:12px;text-align:center;color:var(--text-secondary);font-size:0.82rem;">
          No background check on file. Click "Run Check" to send the candidate a Checkr-hosted form.
        </div>` : ''}
      </div>
    </div>`;

  // Annual Record Review Section
  const arDate = emp.annualReviewDate ? formatDateShort(emp.annualReviewDate) : null;
  const arNextDue = emp.annualReviewNextDue ? formatDateShort(emp.annualReviewNextDue) : null;
  const arDetermination = emp.annualReviewDetermination || '';
  const arHistory = emp.annualReviewHistory || [];
  const AR_COLORS = {
    qualified: { label: 'Qualified', color: '#059669', bg: '#D1FAE5', icon: 'check_circle' },
    qualified_monitoring: { label: 'Qualified w/ Monitoring', color: '#D97706', bg: '#FFFBEB', icon: 'visibility' },
    not_qualified: { label: 'Not Qualified', color: '#DC2626', bg: '#FEE2E2', icon: 'cancel' },
  };
  const arState = AR_COLORS[arDetermination] || null;

  const annualReviewHtml = `
    <div class="compliance-section-card">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          <span class="material-icons" style="font-size:18px;color:#2563EB;">rate_review</span> Annual Record Review
          <span class="compliance-cite-badge">49 CFR §391.25(b)</span>
        </div>
        <button class="btn btn-sm btn-primary" onclick="window.HRModule.openAnnualReviewModal('${emp.id}')" style="padding:4px 10px;font-size:0.75rem;">
          <span class="material-icons" style="font-size:14px;">${arDate ? 'edit' : 'add'}</span> ${arDate ? 'New Review' : 'Start Review'}
        </button>
      </div>
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
        ${arState ? `
          <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid var(--border-light);">
            <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;flex:1;">Determination</span>
            <span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;background:${arState.bg};color:${arState.color};border-radius:6px;font-size:0.75rem;font-weight:600;">
              <span class="material-icons" style="font-size:14px;">${arState.icon}</span> ${arState.label}
            </span>
          </div>` : ''}
        ${row('Last Review', arDate || 'Never conducted')}
        ${row('Reviewed By', emp.annualReviewedBy || '—')}
        ${arNextDue ? `
          <div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border-light);">
            <span style="font-size:0.8rem;color:var(--text-secondary);font-weight:600;">Next Review Due</span>
            <span style="font-size:0.8rem;">${arNextDue} ${expiryBadge(emp.annualReviewNextDue)}</span>
          </div>` : ''}
        ${emp.annualReviewNotes ? `
          <div style="padding:8px;background:#EFF6FF;border-left:3px solid #2563EB;border-radius:6px;margin-top:6px;font-size:0.78rem;color:#1E3A8A;white-space:pre-wrap;">
            ${escapeHtml(emp.annualReviewNotes)}
          </div>` : ''}
        ${arHistory.length > 0 ? `
          <div style="margin-top:8px;">
            <div style="font-size:0.72rem;color:var(--text-secondary);font-weight:600;margin-bottom:4px;">Review History</div>
            ${arHistory.slice(0, 5).map(h => {
              const hState = AR_COLORS[h.determination] || { label: h.determination || '—', color: '#6B7280', icon: 'help' };
              return `
              <div style="display:flex;align-items:center;gap:6px;padding:4px 0;font-size:0.75rem;border-bottom:1px solid var(--border-light);">
                <span class="material-icons" style="font-size:12px;color:${hState.color};">${hState.icon}</span>
                <span style="flex:1;font-weight:500;">${escapeHtml(hState.label)}</span>
                <span style="color:var(--text-secondary);">${escapeHtml(h.date)} &bull; ${escapeHtml(h.reviewedBy)}</span>
              </div>`;
            }).join('')}
          </div>` : ''}
        ${!arDate ? `<div style="padding:12px;text-align:center;color:var(--text-secondary);font-size:0.82rem;">
          No annual review on file. CFR 391.25 requires annual MVR review for all CDL drivers.
        </div>` : ''}
      </div>
    </div>`;

  // TASK-334 Slice 1 — "Generate DQ Packet" button. Top-level action so
  // dispatchers can produce an insurance-ready packet without scrolling to
  // each section. Sits next to the BG check section in the tab order, but as
  // its own prominent CTA so safety mgr can find it on first try.
  const editBtn = `
    <div class="hr-emp-detail-verify-actions" style="display:flex;justify-content:flex-end;gap:8px;margin-bottom:var(--spacing-md);flex-wrap:wrap;">
      <!-- TASK-853 — RESPOND to an inbound §391.23/§40.25 DOT-records request for
           THIS driver (e.g. an AssureHire/Mitratech SPH inquiry). Produces a
           DRAFT verification, answer half pre-filled from the DD record, that the
           carrier reviews + signs before release. -->
      <button class="btn btn-sm" onclick="window.HRModule.generateSphResponse('${escJsAttr(emp.id)}')" style="background:transparent;border:1.5px solid #0F766E;color:#0F766E;font-weight:600;display:inline-flex;align-items:center;gap:6px;padding:6px 14px;" title="Another carrier asked you for this driver's records? Generates a pre-filled §391.23/§40.25 verification DRAFT to review and sign. (Not the §382.601(d) D&amp;A policy receipt — that's under Policy Receipt.)">
        <span class="material-icons" style="font-size:16px;">assignment_turned_in</span> Employment/Safety Verification
      </button>
      <button class="btn btn-sm" onclick="window.HRModule.openDqPacketViewer('${escJsAttr(emp.id)}')" style="background:transparent;border:1.5px solid #0F766E;color:#0F766E;font-weight:600;display:inline-flex;align-items:center;gap:6px;padding:6px 14px;" title="Generate insurance-ready driver qualification packet">
        <span class="material-icons" style="font-size:16px;">folder_special</span> Generate DQ Packet
      </button>
    </div>`;

  // DQ File Audit Section (FMCSR 391.51) — TASK-1144: extracted VERBATIM into the
  // shared window.PersonSections.renderDqScorecard (renderer renders / host
  // authorizes; window.* global, not a cross-?v= import — pitfall #1). HR is the
  // extraction source, so it passes ITS OWN values in and stays byte-identical:
  //   • dqApplicability — the same shared getApplicableDqItems resolve;
  //   • hasPreEmploymentDrugTest — computed off HRState.drugTests (HR's store);
  //   • auditDateLabel — HR's formatDateShort(emp.dqFileLastAudit);
  //   • runAuditOnClick — HR's Run-AI-Audit callable (the carrier passes null →
  //     no button, since it has no runDqFileAudit).
  const dqFileHtml = window.PersonSections.renderDqScorecard(emp, {
    mode: 'hr',
    dqApplicability: window.DqMatrix.getApplicableDqItems(emp),
    // TASK-1319 — accepts pre_employment (the spelling actually written) + requires a passing result.
    // TASK-1320 — `emp` is the third arg so the shared predicate can apply the
    // §382.301 Break-in-Service date gate off the server-stamped, rules-locked
    // anchor. Owner 3 of 4; must stay in lockstep with compliance.js:4033 and the
    // two server owners (ATOMIC-LANDING rule).
    hasPreEmploymentDrugTest: window.PersonSections.hasPassingPreEmploymentTest(HRState.drugTests, emp.id, emp),
    auditDateLabel: emp.dqFileLastAudit ? formatDateShort(emp.dqFileLastAudit) : null,
    runAuditOnClick: `window.HRModule.runDqFileAudit('${emp.id}')`,
    // TASK-9295 — "View MVR report" on the MVR row; null (no button) when this
    // session may not open it or no MVR is on file (host authorizes).
    ...(() => { const b = hrMvrReportButton(emp, null, emp.bgCheck) || {}; return { mvrViewOnClick: b.onClick || null, mvrViewDisabledReason: b.disabledReason || null }; })(),
    // TASK-1320 — the work-during-the-break opener, only for a session the
    // server will let record it (renderer renders, host authorizes).
    bisGapAccountOnClick: hrCanRecordBisGap() ? `window.HRModule.openBisGapAccount('${window.escJsAttr(emp.id)}')` : null,
  });
  // TASK-9438 — CDLIS row on the driver card (CDL tier only; '' for a viewer
  // who may not read the candidate record — hrCdlisCardHtml decides).
  // TASK-9698 — every CMV driver (CDL + Non-CDL Driver), not only CDL (§391.23(a)(1)).
  const cdlisCardHtml = window.DqMatrix.hasDqFile(emp)
    ? hrCdlisCardHtml(emp, HRState.tenantId) : '';

  // Onboarding Documents Section — dedicated upload slots for paperwork-bound
  // checklist items that don't already have their own section (CDL, med card,
  // drug tests, background check are covered above).
  const onboardingDocsHtml = `
    <div class="compliance-section-card">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:var(--spacing-sm);flex-wrap:wrap;">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#0284C7;">folder_open</span> Onboarding Documents
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          <button class="btn btn-sm btn-secondary" onclick="window.HRModule.requestSevenDayStatement('${emp.id}')" title="Send this driver a phone link to complete + sign their FMCSA §395.8(j)(2) 7-day on-duty hours statement — files a dated PDF into their DQ file" style="padding:4px 10px;font-size:0.72rem;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:14px;">schedule_send</span> Request 7-Day Hours
          </button>
          <button class="btn btn-sm btn-secondary" onclick="window.HRModule.sendHiringPacketLink('${emp.id}')" title="Email a self-serve packet link to ${escapeHtml(emp.name || emp.firstName || 'this employee')}" style="padding:4px 10px;font-size:0.72rem;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:14px;">send</span> Send Packet Link
          </button>
        </div>
      </div>
      ${(() => {
        // TASK-1304 — 7-day on-duty statement request/file status (point-in-time; §395.8(j)(2)).
        const filed = emp.sevenDayStatementLastFiledDate || (emp.sevenDayStatementLastFiledAt ? formatDateShort(emp.sevenDayStatementLastFiledAt) : '');
        const reqAt = emp.sevenDayStatementLastRequestedAt ? formatDateShort(emp.sevenDayStatementLastRequestedAt) : '';
        if (filed) return `<div style="font-size:0.72rem;color:var(--text-secondary);margin:0 2px 8px;"><span class="material-icons" style="font-size:12px;vertical-align:middle;color:#059669;">check_circle</span> 7-Day On-Duty Statement on file — dated ${escapeHtml(String(filed))}</div>`;
        if (reqAt) return `<div style="font-size:0.72rem;color:var(--text-secondary);margin:0 2px 8px;"><span class="material-icons" style="font-size:12px;vertical-align:middle;color:#D97706;">hourglass_empty</span> 7-Day On-Duty Statement requested ${escapeHtml(reqAt)} — awaiting driver</div>`;
        return '';
      })()}
      <div style="background:var(--bg-primary);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm);">
        ${getHiringPacketItems().map(slot => {
          const { url, storagePath, uploadedAt: uploadedAtRaw } = getHiringPacketDocFor(emp, slot);
          const uploadedAt = uploadedAtRaw?.toDate?.() || uploadedAtRaw;
          // TASK-1029 — new uploads are PATH-ONLY (no persisted bearer url), so
          // presence = url OR storagePath. "View" opens through viewPacketDoc →
          // the HR-gated byte-stream callable (legacy url only when no path).
          const hasDoc = !!(url || storagePath);
          // Tenant-supplied custom labels — escape on render. Built-in labels
          // are hardcoded English; safe but escape uniformly.
          const safeLabel = escapeHtml(slot.label || '');
          // Slice 2 — show "Download blank" button when carrier has uploaded
          // a blank-PDF template AND employee hasn't uploaded their completed
          // version. Hidden post-upload (UX Lead's call) to reduce mobile
          // clutter; driver hits delete first if they need to re-reference.
          const showDownloadBlank = !!slot.templateUrl && !hasDoc;
          // TASK-1276 (Chris dogfooding) — DD already OWNS the §391.21 application an
          // online applicant filled out on apply.html; the roster person links back via
          // `candidateId` and TASK-1103's openCandidateApplicationForPerson renders the
          // full application (Print/save-as-PDF button and all). So for an online
          // applicant with nothing manually uploaded, auto-satisfy this slot with a
          // "View application" instead of demanding a manual upload of a doc the system
          // generated itself. A wet-signed scan can still be attached on top.
          const showDigitalApp = slot.itemId === 'online_application' && !!emp.candidateId && !hasDoc;
          const rowDone = hasDoc || showDigitalApp;
          return `
            <div style="border-bottom:1px solid var(--border-light);">
            <div style="display:flex;align-items:center;gap:8px;padding:8px 0;">
              <span class="material-icons" style="font-size:16px;color:${rowDone ? '#059669' : 'var(--text-muted)'};">${rowDone ? 'check_circle' : 'radio_button_unchecked'}</span>
              <span style="flex:1;font-size:0.8rem;color:var(--text-primary);font-weight:500;">${safeLabel}${showDigitalApp ? ` <span style="font-size:0.62rem;font-weight:700;color:#0369A1;background:#E0F2FE;border-radius:4px;padding:1px 5px;margin-left:4px;vertical-align:middle;">DIGITAL</span>` : ''}</span>
              ${hasDoc ? `
                ${(() => {
                  // TASK-1381 follow-up (found in the post-deploy verification
                  // pass): the PRIMARY file showed only a date while the extra
                  // files show filenames — so in the exact two-bank-account
                  // case this task exists for, the carrier can't tell which
                  // form is which without opening each one. Derive the name
                  // from the storage path's last segment.
                  let fn = '';
                  try { fn = decodeURIComponent(String(storagePath || '').split('/').pop() || ''); } catch (_) { fn = String(storagePath || '').split('/').pop() || ''; }
                  fn = fn.replace(/^\d{10,}[-_]/, ''); // strip an upload-timestamp prefix
                  return fn ? `<span title="${escapeHtml(fn)}" style="font-size:0.72rem;color:var(--text-primary);margin-right:6px;max-width:170px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(fn)}</span>` : '';
                })()}
                <span style="font-size:0.72rem;color:var(--text-secondary);margin-right:6px;">${uploadedAt ? formatDateShort(uploadedAt) : 'on file'}</span>
                <a href="#" onclick="event.preventDefault(); window.HRModule.viewPacketDoc('${escJsAttr(emp.id)}', '${escJsAttr(slot.itemId)}', this)" style="font-size:0.78rem;color:var(--brand-blue);display:inline-flex;align-items:center;gap:4px;">
                  <span class="material-icons" style="font-size:14px;">visibility</span> View
                </a>
                <button class="btn-icon" onclick="window.HRModule.uploadOnboardingDoc('${escJsAttr(emp.id)}', '${escJsAttr(slot.itemId)}')" title="Add another file to ${safeLabel} (e.g. a second form)">
                  <span class="material-icons" style="font-size:14px;color:var(--text-muted);">upload_file</span>
                </button>
                <button class="btn-icon" onclick="window.HRModule.removeOnboardingDoc('${escJsAttr(emp.id)}', '${escJsAttr(slot.itemId)}')" title="Remove ${safeLabel}">
                  <span class="material-icons" style="font-size:14px;color:#DC2626;">delete</span>
                </button>
              ` : showDigitalApp ? `
                <span style="font-size:0.72rem;color:var(--text-secondary);margin-right:6px;">filled online</span>
                <a href="#" onclick="event.preventDefault(); window.HRModule.openCandidateApplicationForPerson('${emp.id}')" style="font-size:0.78rem;color:var(--brand-blue);display:inline-flex;align-items:center;gap:4px;" title="Open the digital application (view / print / save as PDF)">
                  <span class="material-icons" style="font-size:14px;">description</span> View application
                </a>
                <button class="btn-icon" onclick="window.HRModule.uploadOnboardingDoc('${escJsAttr(emp.id)}', '${escJsAttr(slot.itemId)}')" title="Attach a signed or scanned copy (optional)">
                  <span class="material-icons" style="font-size:14px;color:var(--text-muted);">upload_file</span>
                </button>
              ` : `
                ${showDownloadBlank ? `
                  <a href="${escapeHtml(slot.templateUrl)}" target="_blank" rel="noopener" class="btn btn-sm btn-secondary" style="padding:3px 8px;font-size:0.72rem;text-decoration:none;display:inline-flex;align-items:center;gap:3px;" title="Download blank ${safeLabel}">
                    <span class="material-icons" style="font-size:13px;">download</span> Blank
                  </a>
                ` : ''}
                <button class="btn btn-sm btn-primary" onclick="window.HRModule.uploadOnboardingDoc('${escJsAttr(emp.id)}', '${escJsAttr(slot.itemId)}')" style="padding:3px 8px;font-size:0.72rem;">
                  <span class="material-icons" style="font-size:13px;">upload_file</span> Upload
                </button>
              `}
            </div>
            <div id="hr-slot-extra-files-${escapeHtml(slot.itemId)}" data-slot-item="${escapeHtml(slot.itemId)}" data-person-id="${escapeHtml(emp.id)}" style="display:none;"></div>
            </div>
          `;
        }).join('')}
        <div style="padding:8px 0 0;font-size:0.72rem;color:var(--text-secondary);">
          Uploads here auto-check the matching item on this person's New Hire Checklist.
        </div>
      </div>
    </div>`;

  // Section order — Onboarding Documents promoted from position 12 → 4 (right after
  // profile) 2026-05-20: most-actionable section for a new hire goes first; compliance
  // items (CDL/Med/MVR/Drug/etc.) stay clustered together below. Chris ask: forms-fest
  // on Day 1 → start collecting at offer-accepted instead.
  // TASK-1381 — the per-slot extra-file lists (multi-file slots) render ASYNC:
  // the documents subcollection lazy-loads behind the employeeDocsLoaded gate
  // and packetDocs needs its own read. Every caller inserts this HTML into the
  // DOM synchronously, so a 0-timeout after build reliably finds the
  // hr-slot-extra-files-* containers (and no-ops when the HTML wasn't mounted).
  setTimeout(() => { _populateOnboardingSlotFiles(emp.id).catch(e => console.warn('[HR] slot extra-files populate failed (non-fatal):', e)); }, 0);

  // TASK-4605 Slice 2 — bind the shared licence card's Upload/Remove/View
  // affordances via real functions (bindLicenseImages contract). Same
  // 0-timeout-after-build pattern as the onboarding slot-file populate above:
  // every caller of buildComplianceTabHTML inserts the returned string into
  // #hr-detail-tab-compliance synchronously, so a 0-timeout reliably finds it
  // (and no-ops on the rare caller that never mounted it).
  setTimeout(() => {
    const container = document.getElementById('hr-detail-tab-compliance');
    if (!container) return;
    window.PersonSections.bindLicenseImages(container, {
      onOpen: (path) => viewComplianceDoc(path, '', 'drivers-license'),
      onUpload: (credType) => uploadDlDocument(emp.id, credType),
      onDelete: (credType) => removeDlDocument(emp.id, credType),
    });
  }, 0);

  return `${preEmpBannerHtml}${recordPortHtml}${editBtn}${profileHtml}${onboardingDocsHtml}${cdlHtml}${medHtml}${mvrHtml}${clearinghouseHtml}${bgCheckHtml}${drugHtml}${annualReviewHtml}${dqFileHtml}${cdlisCardHtml}${requiredTrainingHtml}${trainingHtml}${accountabilityHtml}${citationsHtml}`;
}

async function editComplianceTab(empId) {
  const emp = HRState.people.find(p => p.id === empId);
  if (!emp) return;
  await ensureSecureTax(emp);

  const inputStyle = 'border:1px solid var(--border-light);border-radius:6px;padding:6px 10px;font-size:0.82rem;background:var(--bg-primary);color:var(--text-primary);width:100%;box-sizing:border-box;';
  const labelStyle = 'font-size:0.78rem;color:var(--text-secondary);font-weight:600;margin-bottom:2px;';
  const field = (label, id, value, type = 'text', placeholder = '', reveal = false, readOnly = false) => {
    // TASK-815 — readOnly: a view-only hr-directory holder may SEE SSN but not
    // edit it. Render the input disabled so the UI matches the firestore.rules
    // write-deny (no editable affordance, no save attempt).
    const roAttr = readOnly ? ' readonly disabled' : '';
    if (reveal) {
      // Masked field (e.g. SSN) with an eye toggle to reveal/hide the value.
      // Reveal is display-only — it flips the input type, the value is already loaded.
      const revealInputStyle = inputStyle + 'padding-right:34px;' + (readOnly ? 'opacity:0.7;' : '');
      return `
    <div style="margin-bottom:8px;">
      <div style="${labelStyle}">${label}${readOnly ? ' <span style="font-weight:400;color:var(--text-muted);font-size:0.72rem;">(view only)</span>' : ''}</div>
      <div style="position:relative;">
        <input type="${type}" id="${id}" value="${escapeHtml(value || '')}" placeholder="${placeholder}" style="${revealInputStyle}"${roAttr} />
        <button type="button" id="${id}-reveal" onclick="window.HRModule.toggleFieldReveal('${id}')" aria-label="Show ${label}" title="Show ${label}" style="position:absolute;top:50%;right:6px;transform:translateY(-50%);background:none;border:none;padding:2px;cursor:pointer;color:var(--text-secondary);display:flex;align-items:center;">
          <span class="material-icons" style="font-size:18px;">visibility_off</span>
        </button>
      </div>
    </div>`;
    }
    return `
    <div style="margin-bottom:8px;">
      <div style="${labelStyle}">${label}</div>
      <input type="${type}" id="${id}" value="${escapeHtml(value || '')}" placeholder="${placeholder}" style="${inputStyle}"${roAttr} />
    </div>`;
  };
  // TASK-9359 — the identity kit comes from the peopleIdentity vault (overlaid
  // onto emp). When this viewer CANNOT KNOW it the inputs render disabled with a
  // "Restricted" placeholder, and PersonIdentity.updatePerson drops them, so a
  // blank can never overwrite a value hidden from this user.
  const _idUnknown = !!(window.PersonIdentity && window.PersonIdentity.identityUnknown(emp));
  const selectField = (label, id, value, options) => `
    <div style="margin-bottom:8px;">
      <div style="${labelStyle}">${label}</div>
      <select id="${id}" style="${inputStyle}">
        ${options.map(o => `<option value="${o.value}" ${value === o.value ? 'selected' : ''}>${o.label}</option>`).join('')}
      </select>
    </div>`;

  // TASK-607 — "Reports to" (direct supervisor) editor, moved here from the
  // roster person form. Builds the supervisor picker from HRState.people
  // (excludes self; keeps an inactive/terminated saved supervisor visible so
  // re-saving doesn't silently clear the assignment). Saved on save to the
  // same people/{pid}.reportsTo field by saveComplianceFields. The (?) tooltip
  // explains what the field actually gates (HR-note scoping + My Team view).
  const reportsToField = (e) => {
    const current = e.reportsTo || '';
    const all = (HRState.people || []).filter(p => p && p.id && p.id !== e.id);
    const isActive = p => p.active !== false && p.terminated !== true;
    const visible = all.filter(isActive);
    if (current) {
      const saved = all.find(p => p.id === current);
      if (saved && !isActive(saved) && !visible.includes(saved)) visible.push(saved);
    }
    const opts = visible
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      .map(p => `<option value="${escapeHtml(p.id)}" ${current === p.id ? 'selected' : ''}>${escapeHtml(isActive(p) ? (p.name || '(unnamed)') : `${p.name || '(unnamed)'} (inactive)`)}</option>`)
      .join('');
    const tooltip = "Sets this person's direct supervisor. Supervisors can add HR notes only about their direct reports and get a My Team view.";
    return `
    <div style="margin-bottom:8px;">
      <div style="${labelStyle}">Reports to
        <span class="material-icons" title="${escapeHtml(tooltip)}" aria-label="${escapeHtml(tooltip)}" style="font-size:14px;vertical-align:middle;color:var(--text-secondary);cursor:help;">help_outline</span>
      </div>
      <select id="comp-reports-to" style="${inputStyle}">
        <option value="">No supervisor</option>
        ${opts}
      </select>
    </div>`;
  };

  const container = document.getElementById('hr-detail-tab-compliance');
  if (!container) return;

  container.innerHTML = `
    <div style="padding:var(--spacing-sm);">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:var(--spacing-md);">
        <div style="font-weight:700;font-size:1rem;color:var(--text-primary);">Edit Compliance Info</div>
        <div style="display:flex;gap:8px;">
          <button class="btn btn-sm btn-secondary" onclick="window.HRModule.cancelComplianceEdit('${empId}')">Cancel</button>
          <button class="btn btn-sm btn-primary" id="btn-save-compliance" onclick="window.HRModule.saveComplianceFields('${empId}')">
            <span class="material-icons" style="font-size:14px;">save</span> Save
          </button>
        </div>
      </div>

      <details open style="margin-bottom:var(--spacing-md);">
        <summary style="font-weight:600;font-size:0.9rem;cursor:pointer;padding:8px 0;border-bottom:1px solid var(--border-light);">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">person</span> Employee Profile
        </summary>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 var(--spacing-md);padding-top:8px;">
          ${_hrCanSeeSecureTax() ? field('SSN', 'comp-ssn', emp.secureTax?.ssn, 'password', 'XXX-XX-XXXX', true, !_hrCanEditSecureTax()) : ''}
          ${field('Date of Birth', 'comp-dob', _idUnknown ? '' : emp.dob, 'date', _idUnknown ? 'Restricted' : '', false, _idUnknown)}
          ${field('Employee ID', 'comp-employee-id', emp.employeeId)}
          ${field('ISNetworld #', 'comp-isnet-nickname', emp.isnetNickname)}
          ${selectField('Employee Type', 'comp-isnet-emp-type', emp.isnetEmployeeType || '', [
            { value: '', label: 'Select...' },
            { value: 'FMCSA', label: 'FMCSA' },
            { value: 'PHMSA', label: 'PHMSA' },
            { value: 'Other', label: 'Other' }
          ])}
          ${selectField('Go To Field', 'comp-go-to-field', emp.goToField || '', [
            { value: '', label: 'Select...' },
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' }
          ])}
          ${field('DL State', 'comp-dl-state', _idUnknown ? '' : emp.dlState, 'text', _idUnknown ? 'Restricted' : '', false, _idUnknown)}
          ${field('DL Number', 'comp-dl-number', _idUnknown ? '' : emp.dlNumber, 'text', _idUnknown ? 'Restricted' : '', false, _idUnknown)}
          ${field('DL Expiry', 'comp-dl-expiry', _idUnknown ? '' : emp.dlExpiryDate, 'date', _idUnknown ? 'Restricted' : '', false, _idUnknown)}
          ${selectField('Safety Training Verified', 'comp-safety-verified', emp.safetyTrainingVerified || '', [
            { value: '', label: 'Select...' },
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' },
            { value: 'N/A', label: 'N/A' }
          ])}
          ${selectField('Supervisor Role', 'comp-supervisor', emp.supervisorRole || '', [
            { value: '', label: 'Select...' },
            { value: 'Yes', label: 'Yes' },
            { value: 'No', label: 'No' }
          ])}
          ${reportsToField(emp)}
          ${field('Specialized Certification', 'comp-spec-cert', emp.specializedCertification)}
        </div>
        ${selectField('Reasonable Suspicion Training', 'comp-suspicion-training', emp.reasonableSuspicionTraining || '', [
          { value: '', label: 'Select...' },
          { value: 'Yes', label: 'Yes' },
          { value: 'No', label: 'No' },
          { value: 'N/A', label: 'N/A' }
        ])}
      </details>

      <details open style="margin-bottom:var(--spacing-md);">
        <summary style="font-weight:600;font-size:0.9rem;cursor:pointer;padding:8px 0;border-bottom:1px solid var(--border-light);">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">badge</span> CDL Information
        </summary>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 var(--spacing-md);padding-top:8px;">
          ${selectField('CDL Class', 'comp-cdl-class', emp.cdlClass || '', [
            { value: '', label: 'None' },
            { value: 'A', label: 'Class A' },
            { value: 'B', label: 'Class B' },
            { value: 'C', label: 'Class C' }
          ])}
          ${field('Endorsements', 'comp-cdl-endorsements', emp.cdlEndorsements)}
          ${field('CDL Issue Date', 'comp-cdl-issue', emp.cdlIssueDate, 'date')}
          ${field('CDL Expiry', 'comp-cdl-expiry', emp.cdlExpiryDate, 'date')}
        </div>
      </details>

      <details open>
        <summary style="font-weight:600;font-size:0.9rem;cursor:pointer;padding:8px 0;border-bottom:1px solid var(--border-light);">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">medical_services</span> Medical Card
        </summary>
        <div style="padding-top:8px;">
          ${field('Med Card Provider', 'comp-med-provider', (window.DqMatrix?.medDetail ? window.DqMatrix.medDetail(emp, HRState.tenantId) : emp).medCardNumber)}
          ${field('Med Card Expiry', 'comp-med-expiry', emp.medCardExpiryDate, 'date')}
        </div>
      </details>

      <!-- TASK-DA-v1 Slice 3 / TASK-9696 — testing program + random pool. Coverage is
           DERIVED (CDL tier → DOT-FMCSA; the two flags below → DOT-PHMSA / company). The
           flags are server-owned (setTestingProgram callable, audited); the pool checkbox
           is only live for a covered person. A stale "in pool" flag on someone no program
           covers is shown as a conflict and cleared only on an explicit tick — never silently. -->
      <details open style="margin-bottom:var(--spacing-md);">
        <summary style="font-weight:600;font-size:0.9rem;cursor:pointer;padding:8px 0;border-bottom:1px solid var(--border-light);">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">shuffle</span> Drug & Alcohol Testing Program
        </summary>
        ${(() => {
          const st = window.DqMatrix.randomPoolStatusFor(emp);
          const tp = (emp.testingProgram && typeof emp.testingProgram === 'object') ? emp.testingProgram : {};
          return `
        <div style="padding-top:8px;display:flex;flex-direction:column;gap:8px;">
          <div style="font-size:0.76rem;color:var(--text-secondary);line-height:1.4;">DOT–FMCSA coverage comes from the Driver Classification (CDL Driver only). Tick what else applies to this person.</div>
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
            <input type="checkbox" id="comp-tp-phmsa" ${tp.phmsa === true ? 'checked' : ''} style="width:18px;height:18px;">
            <span style="font-size:0.88rem;">PHMSA pipeline covered functions (49 CFR Part 199)</span>
          </label>
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
            <input type="checkbox" id="comp-tp-company" ${tp.companyNonDot === true ? 'checked' : ''} style="width:18px;height:18px;">
            <span style="font-size:0.88rem;">Company (non-DOT) testing program</span>
          </label>
          <div>${window.DqMatrix.randomPoolChipHtml(emp)}</div>
          <label style="display:flex;align-items:center;gap:8px;cursor:${st.covered ? 'pointer' : 'not-allowed'};${st.covered ? '' : 'opacity:0.6;'}">
            <input type="checkbox" id="comp-in-random-pool" ${st.covered && st.inPool ? 'checked' : ''} ${st.covered ? '' : 'disabled'} style="width:18px;height:18px;">
            <span style="font-size:0.88rem;">In the random selection pool</span>
          </label>
          <span style="font-size:0.74rem;color:var(--text-secondary);">Only a DOT-covered person (CDL Driver or PHMSA) can be in the pool. Untick to opt a covered person out (SAP process, leave).</span>
          ${st.conflict ? `
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;padding:8px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:8px;">
            <input type="checkbox" id="comp-clear-pool-flag" style="width:18px;height:18px;">
            <span style="font-size:0.82rem;color:#92400E;">Clear the stale "in pool" flag on this person (no DOT program covers them). Leave unticked to review first.</span>
          </label>` : ''}
        </div>`;
        })()}
      </details>

      <!-- TASK-DA-v1 Slice 4 — G5 §382.603 supervisor 60+60 training. Gated on
           supervisorRole === 'Yes' since training is only required for
           supervisors who make reasonable-suspicion test calls. -->
      <details ${emp.supervisorRole === 'Yes' ? 'open' : ''} style="margin-bottom:var(--spacing-md);${emp.supervisorRole === 'Yes' ? '' : 'opacity:0.6;'}">
        <summary style="font-weight:600;font-size:0.9rem;cursor:pointer;padding:8px 0;border-bottom:1px solid var(--border-light);">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">school</span> Supervisor 60+60 Training (§382.603)
          ${emp.supervisorRole !== 'Yes' ? '<span style="font-size:0.74rem;color:var(--text-secondary);font-weight:400;">(only required if Supervisor = Yes)</span>' : ''}
        </summary>
        <div style="padding-top:8px;">
          <p style="font-size:0.74rem;color:var(--text-secondary);margin-bottom:8px;">Each supervisor must receive at least 60 minutes of training on alcohol misuse and 60 minutes on controlled substances use (one-time, not recurrent).</p>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 var(--spacing-md);">
            ${field('Alcohol-misuse training completed', 'comp-sup-alcohol-date', emp.supervisorTraining?.reasonableSuspicion60min?.completedAt || '', 'date')}
            ${field('Controlled-substances training completed', 'comp-sup-controlled-date', emp.supervisorTraining?.postAccident60min?.completedAt || '', 'date')}
            ${field('Training provider (optional)', 'comp-sup-provider', emp.supervisorTraining?.provider || '')}
            ${field('Certificate URL (optional)', 'comp-sup-cert-url', emp.supervisorTraining?.reasonableSuspicion60min?.docUrl || '')}
          </div>
        </div>
      </details>
    </div>`;
}

// TASK-DA-v1 slice 3 — inRandomPool default predicate. Uses the standard
// role-fallback chain (Code Reviewer catch: crewType || role || roles[0])
// so legacy records with only a `role` field still classify correctly. A
// CDL driver defaults to IN the pool; non-CDL or non-driver defaults OUT.
// If the field has been explicitly set on the person doc, that wins.
// TASK-9696 — ONE predicate (window.DqMatrix.randomPoolStatusFor, twin of
// functions/src/services/testingProgram.js): membership FOLLOWS COVERAGE. A
// person no DOT program covers is never in the pool, whatever the stale boolean
// says (the old version returned `inRandomPool` before looking at the tier, which
// is how laborers stayed in the §382.305 draw). Kept as a thin wrapper so older
// call sites read the same answer as Compliance, the partner portal and the server.
function _isDriverInRandomPoolByDefault(emp) {
  if (!emp) return false;
  return window.DqMatrix.randomPoolStatusFor(emp).inPool;
}

function cancelComplianceEdit(empId) {
  const emp = HRState.people.find(p => p.id === empId);
  if (!emp) return;
  const container = document.getElementById('hr-detail-tab-compliance');
  if (container) container.innerHTML = buildComplianceTabHTML(emp);
  loadEmployeeCitations(empId);
}

// Reveal/hide toggle for masked fields (SSN). Display-only: flips the input
// type between password (masked) and text (revealed) and swaps the eye icon.
// The value is already loaded into the field; this never logs or copies it.
function toggleFieldReveal(fieldId) {
  const input = document.getElementById(fieldId);
  if (!input) return;
  const wasRevealed = input.type === 'text';
  input.type = wasRevealed ? 'password' : 'text';
  const btn = document.getElementById(`${fieldId}-reveal`);
  if (btn) {
    const icon = btn.querySelector('.material-icons');
    if (icon) icon.textContent = wasRevealed ? 'visibility_off' : 'visibility';
    // Keep the a11y label in sync with the action the button will perform next.
    const base = (btn.getAttribute('aria-label') || '').replace(/^(Show|Hide) /, '');
    btn.setAttribute('aria-label', (wasRevealed ? 'Show ' : 'Hide ') + base);
    btn.setAttribute('title', (wasRevealed ? 'Show ' : 'Hide ') + base);
  }
}

async function saveComplianceFields(empId) {
  const btn = document.getElementById('btn-save-compliance');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const val = (id) => document.getElementById(id)?.value?.trim() || '';
    const ssnRaw = val('comp-ssn');
    // TASK-607 — "Reports to" (direct supervisor), moved here from the roster
    // person form. Empty → null. Reject self-pick (defense-in-depth; the
    // dropdown already excludes self). Capture the prior value so we can fire
    // the PERSON_REPORTS_TO_SET audit row only when it actually changes.
    const empBefore = HRState.people.find(p => p.id === empId);
    const oldReportsTo = empBefore?.reportsTo || null;
    const reportsToVal = val('comp-reports-to') || null;
    if (reportsToVal && reportsToVal === empId) {
      window.showToast?.("A person can't report to themselves", 'error');
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
      document.getElementById('comp-reports-to')?.focus();
      return;
    }
    const updateData = {
      dob: val('comp-dob') || null,
      employeeId: val('comp-employee-id') || null,
      isnetNickname: val('comp-isnet-nickname') || null,
      isnetEmployeeType: val('comp-isnet-emp-type') || null,
      goToField: val('comp-go-to-field') || null,
      dlState: val('comp-dl-state')?.toUpperCase() || null,
      dlNumber: val('comp-dl-number') || null,
      dlExpiryDate: val('comp-dl-expiry') || null,
      safetyTrainingVerified: val('comp-safety-verified') || null,
      supervisorRole: val('comp-supervisor') || null,
      reportsTo: reportsToVal,
      specializedCertification: val('comp-spec-cert') || null,
      reasonableSuspicionTraining: val('comp-suspicion-training') || null,
      cdlClass: val('comp-cdl-class') || null,
      cdlEndorsements: val('comp-cdl-endorsements') || null,
      cdlIssueDate: val('comp-cdl-issue') || null,
      cdlExpiryDate: val('comp-cdl-expiry') || null,
      medCardNumber: val('comp-med-provider') || null,
      medCardExpiryDate: val('comp-med-expiry') || null,
    };
    // TASK-DA-v1 slice 3 / TASK-9696 — random pool membership (§382.305(j)).
    // The checkbox is LIVE only for a DOT-covered person; for anyone else the
    // stored flag is left alone unless the explicit "clear the stale flag" box
    // was ticked (never a silent flip — TASK-1320 stance).
    {
      const poolBox = document.getElementById('comp-in-random-pool');
      if (poolBox && !poolBox.disabled) updateData.inRandomPool = poolBox.checked === true;
      else if (document.getElementById('comp-clear-pool-flag')?.checked === true) updateData.inRandomPool = false;
    }
    // TASK-9696 — testing-program flags are SERVER-OWNED (rules deny the key);
    // they go through setTestingProgram, audited, only when they changed.
    const _tpPrev = (empBefore?.testingProgram && typeof empBefore.testingProgram === 'object') ? empBefore.testingProgram : {};
    const _tpNext = {
      phmsa: document.getElementById('comp-tp-phmsa')?.checked === true,
      companyNonDot: document.getElementById('comp-tp-company')?.checked === true,
    };
    const _tpChanged = !!document.getElementById('comp-tp-phmsa')
      && (_tpNext.phmsa !== (_tpPrev.phmsa === true) || _tpNext.companyNonDot !== (_tpPrev.companyNonDot === true));
    // TASK-DA-v1 slice 4 — §382.603 supervisor 60+60 training. Only save if
    // any field is present; an empty save leaves existing values alone.
    const supAlcoholDate = val('comp-sup-alcohol-date');
    const supControlledDate = val('comp-sup-controlled-date');
    const supProvider = val('comp-sup-provider');
    const supCertUrl = val('comp-sup-cert-url');
    if (supAlcoholDate || supControlledDate || supProvider || supCertUrl) {
      updateData.supervisorTraining = {
        reasonableSuspicion60min: {
          completedAt: supAlcoholDate || null,
          docUrl: supCertUrl || null,
        },
        postAccident60min: {
          completedAt: supControlledDate || null,
          docUrl: supCertUrl || null,
        },
        provider: supProvider || null,
        cfrCitation: '49 CFR §382.603',
      };
    }

    // TASK-9359 (2nd COD, Code Reviewer) — an identity input that rendered
    // LOCKED (vault not readable / still loading) is not "the user cleared it":
    // its key is omitted, so a vault snapshot that lands after the editor opened
    // can never turn the locked blank into a clear.
    [['comp-dob', 'dob'], ['comp-dl-state', 'dlState'], ['comp-dl-number', 'dlNumber'], ['comp-dl-expiry', 'dlExpiryDate']]
      .forEach(([elId, key]) => { if (document.getElementById(elId)?.disabled) delete updateData[key]; });
    // TASK-9422 — partner session: write only what changed (see app.js
    // partnerDropUnchangedKeys). No-op for every carrier member.
    window.partnerDropUnchangedKeys?.(updateData, empBefore);
    // TASK-9359 — the identity kit routes to the peopleIdentity vault in the same
    // atomic batch as the people-doc fields (never onto the broadcast doc); a
    // viewer who cannot know it has it dropped (no blank-over-hidden wipe).
    const writes = Object.keys(updateData).length
      ? [window.PersonIdentity.updatePerson(HRState.tenantId, empId, updateData, empBefore || { id: empId })]
      : [];
    // SSN goes to the secure peopleTax subcollection (TASK-815: gated on
    // hr-directory). Only write it if the user can EDIT secure-tax — a view-only
    // hr-directory holder sees a read-only SSN field, so skip the write rather
    // than throw a raw "insufficient permissions" the rules would deny anyway.
    if (ssnRaw && _hrCanEditSecureTax()) writes.push(saveSecureTax(empId, { ssn: ssnRaw }));
    await Promise.all(writes);

    // TASK-9696 — testing-program flags (server-owned, audited). Non-fatal for the
    // rest of the save, but the user is told when it did not land.
    if (_tpChanged && HRState.tenantId !== 'demo') {
      try {
        const setTp = httpsCallable(functions, 'setTestingProgram');
        const r = await setTp({ tenantId: HRState.tenantId, personId: empId, phmsa: _tpNext.phmsa, companyNonDot: _tpNext.companyNonDot, reason: 'Set from HR compliance form' });
        updateData.testingProgram = { ..._tpPrev, ..._tpNext, setAt: new Date().toISOString() };
        if (r?.data?.pool?.conflict) window.showToast?.('Saved — this person is flagged "in pool" but no DOT program covers them. Review the flag.', 'warning');
      } catch (tpErr) {
        console.error('[HR] setTestingProgram failed:', tpErr);
        window.showToast?.(`Testing-program flags not saved: ${(tpErr?.message || '').replace(/^[a-z-]+:\s*/i, '').slice(0, 160) || 'server error'}`, 'error');
      }
    }

    // TASK-607 — dedicated audit row when reportsTo changes, moved here from
    // the old roster savePerson path so the supervisor-assignment trail
    // survives the field's relocation. Answers "who set this person's
    // supervisor and when?" for tech-support traces.
    if (oldReportsTo !== reportsToVal) {
      window.logAudit?.('hr', 'PERSON_REPORTS_TO_SET', {
        personId: empId,
        personName: empBefore?.name || null,
        oldReportsTo,
        newReportsTo: reportsToVal,
      });
    }

    // Update local state. TASK-9359 — the identity fields are NOT copied in: the
    // vault cache re-overlays them (PersonIdentity.updatePerson already primed
    // it), and copying them here would mask the raw legacy copy.
    const emp = HRState.people.find(p => p.id === empId);
    if (emp) {
      const { dob: _d, dlState: _s, dlNumber: _n, dlExpiryDate: _x, ...nonIdentity } = updateData;
      Object.assign(emp, nonIdentity);
      try { window.PersonIdentity.overlayList([emp]); } catch (_) { /* best-effort */ }
    }

    window.showToast?.('Compliance info saved', 'success');

    // Re-render read-only view
    cancelComplianceEdit(empId);
    renderEmployeeDirectory();
    updateComplianceBadges();
    renderComplianceDashboard();
  } catch (error) {
    console.error('[HR] Error saving compliance fields:', error);
    window.showToast?.('Error saving compliance info', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">save</span> Save'; }
  }
}

function buildDocumentsTabHTML(emp) {
  const docs = [];

  // DL document — read-only pointer; canonical upload home is the Compliance tab (TASK-643)
  docs.push({
    name: "Driver's License",
    description: 'State-issued driver license scan',
    icon: 'badge',
    color: 'var(--brand-blue)',
    uploaded: !!(emp.dlDocumentUrl || emp.dlDocumentPath), // TASK-996 — path-only counts
    url: emp.dlDocumentUrl || '',
    path: emp.dlDocumentPath || '',
    manageInCompliance: true
  });

  // Med Card document — read-only pointer; canonical upload home is the Compliance tab (TASK-643)
  // TASK-647 — read the EFFECTIVE doc (most-recent of medCertPdfUrl /
  // medCardDocumentUrl) so a driver SMS self-upload shows here too, not just
  // the formally-attached medCardDocumentUrl.
  const docTabMedCard = getEffectiveMedCardDoc(emp);
  docs.push({
    name: 'Medical Card',
    description: 'DOT Medical Examiner Certificate',
    icon: 'medical_services',
    color: '#059669',
    uploaded: !!docTabMedCard,
    url: docTabMedCard?.url || '',
    path: docTabMedCard?.path || '',
    manageInCompliance: true
  });

  // Compliance docs from complianceDocs array
  const compDocs = emp.complianceDocs || [];
  compDocs.forEach((d, i) => {
    docs.push({
      name: d.name || 'Document',
      description: d.expiresAt ? `Expires: ${formatDateShort(d.expiresAt)}` : 'No expiry set',
      icon: 'description',
      color: '#7c3aed',
      // TASK-9234 — path-only. `url` is null on every row written after the fix
      // and is never read by the opener, so storagePath is what makes a row
      // viewable here too (same fail-closed rule as _renderComplianceDocRows).
      uploaded: !!d.storagePath,
      url: '',
      path: d.storagePath || '',
      expiry: d.expiresAt,
      isComplianceDoc: true,
      index: i
    });
  });

  const docCards = docs.map(d => {
    const today = new Date().toISOString().split('T')[0];
    const isExpired = d.expiry && d.expiry < today;
    return `
      <div style="display:flex;align-items:center;gap:10px;padding:10px;background:var(--bg-primary);border:1px solid ${isExpired ? '#DC2626' : 'var(--border-light)'};border-radius:var(--radius-md);margin-bottom:6px;">
        <span class="material-icons" style="font-size:22px;color:${d.uploaded ? d.color : 'var(--text-muted)'};">${d.icon}</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">${escapeHtml(d.name)}</div>
          <div style="font-size:0.75rem;color:var(--text-secondary);">${escapeHtml(d.description)}</div>
          ${isExpired ? '<div style="font-size:0.72rem;color:#DC2626;font-weight:600;">EXPIRED</div>' : ''}
        </div>
        ${d.manageInCompliance ? `
          ${d.uploaded ? `<a href="#" onclick="event.preventDefault();window.viewComplianceDoc('${escJsAttr(d.path || '')}','${escJsAttr(d.url || '')}',null,this);return false;" class="btn-icon" title="View" style="padding:4px;cursor:pointer;">
            <span class="material-icons" style="font-size:16px;color:var(--brand-blue);">open_in_new</span>
          </a>` : '<span style="font-size:0.75rem;color:var(--text-secondary);">Not uploaded</span>'}
          <button class="btn btn-sm btn-secondary" onclick="window.HRModule.switchDetailTab('compliance')" style="font-size:0.75rem;padding:4px 10px;">
            Manage in Compliance <span class="material-icons" style="font-size:14px;">arrow_forward</span>
          </button>
        ` : d.uploaded ? `
          ${d.isComplianceDoc && d.index != null ? `
          <a href="#" data-no-print onclick="event.preventDefault();window.HRModule.openComplianceDocFile('${escJsAttr(emp.id)}', ${d.index}, this);return false;" class="btn-icon" title="View" style="padding:4px;cursor:pointer;">
            <span class="material-icons" style="font-size:16px;color:var(--brand-blue);">open_in_new</span>
          </a>` : `
          <a ${window.StorageFile ? window.StorageFile.linkAttrs({ url: d.url, path: d.path }) : `href="${_safeHttpUrl(d.url)}" target="_blank"`} class="btn-icon" title="View" style="padding:4px;">
            <span class="material-icons" style="font-size:16px;color:var(--brand-blue);">open_in_new</span>
          </a>`}
          ${d.removeAction ? `<button class="btn-icon" onclick="${d.removeAction}" title="Remove" style="padding:4px;">
            <span class="material-icons" style="font-size:16px;color:#DC2626;">delete</span>
          </button>` : ''}
        ` : `
          ${d.uploadAction ? `<button class="btn btn-sm btn-secondary" onclick="${d.uploadAction}" style="font-size:0.75rem;padding:4px 10px;">
            <span class="material-icons" style="font-size:14px;">upload_file</span> Upload
          </button>` : '<span style="font-size:0.75rem;color:var(--text-secondary);">Not uploaded</span>'}
        `}
      </div>`;
  }).join('');

  return `
    <div style="margin-bottom:var(--spacing-md);">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">folder</span> Required Documents
        </div>
      </div>
      ${docCards}
    </div>

    <div>
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:var(--spacing-sm);gap:8px;flex-wrap:wrap;">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:#7c3aed;">cloud_upload</span> Uploaded Files
        </div>
        <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
          ${_hrIsOwnerOrAdmin() ? `
          <button class="btn btn-sm btn-secondary" onclick="window.HRModule.downloadFullDqFile('${emp.id}')" title="Download every uploaded DQ document as a ZIP (49 CFR §391.51). Contains SSN/DOB/MVR/drug-test PII — owner/admin only." style="padding:4px 10px;font-size:0.75rem;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:14px;">folder_zip</span> Download Full DQ File (ZIP)
          </button>` : ''}
          <select id="hr-emp-doc-category" class="control-auto" title="Document category — Medical/Exposure protected under OSHA 1910.1020; Clearinghouse query results retained 3 years per FMCSA 49 CFR 382" style="padding:3px 6px;font-size:0.75rem;border:1px solid var(--border-light);border-radius:6px;background:#fff;">
            <optgroup label="DOT Driver Qualification File">
              <option value="cdl">CDL / License (391.11)</option>
              <option value="mec">MEC / Medical Card (391.41)</option>
              <option value="mvr">MVR (391.25)</option>
              <option value="mvr_annual_review">Annual MVR Review (391.25)</option>
              <option value="employment_app">Employment Application (391.21)</option>
              <option value="road_test">Road Test / Certificate (391.31)</option>
              <option value="prev_employer_safety">Previous Employer Safety Performance (391.23)</option>
              <option value="training">Training Certificate</option>
              <option value="drug_alcohol">Drug / Alcohol Records (49 CFR 382)</option>
              <option value="on_duty_hours">7-Day On-Duty Statement (395.8(j))</option>
            </optgroup>
            <optgroup label="Retention / Other">
              <option value="general" selected>General</option>
              <option value="medical">Medical (OSHA 1910.1020)</option>
              <option value="exposure">Exposure (OSHA 1910.1020)</option>
              <option value="clearinghouse">Clearinghouse Query (49 CFR 382)</option>
            </optgroup>
          </select>
          <button class="btn btn-sm btn-primary" onclick="window.HRModule.uploadEmployeeDocument('${emp.id}')" style="padding:4px 10px;font-size:0.75rem;">
            <span class="material-icons" style="font-size:14px;">upload_file</span> Upload
          </button>
        </div>
      </div>
      <div id="hr-employee-docs-list">
        <div class="empty-state-small">Loading files...</div>
      </div>
    </div>
  `;
}

// Load citations for an employee (called after compliance tab renders)
async function loadEmployeeCitations(empId) {
  const container = document.getElementById('hr-employee-citations-list');
  if (!container) return;

  try {
    const tenant = HRState.tenantId;
    let snap;
    try {
      snap = await getDocs(query(collection(db, 'tenants', tenant, 'citations'), where('driverId', '==', empId), orderBy('createdAt', 'desc')));
    } catch (indexErr) {
      // Fallback without orderBy if composite index doesn't exist
      snap = await getDocs(query(collection(db, 'tenants', tenant, 'citations'), where('driverId', '==', empId)));
    }

    if (snap.empty) {
      container.innerHTML = '<div style="color:var(--text-secondary);font-size:0.8rem;padding:8px 0;">No exam reports on file.</div>';
      return;
    }

    // 49 CFR 396 — proof of repair must be sent to the issuing state within
    // 15 days. Surface overdue/upcoming-deadline citations with a badge so
    // the carrier admin doesn't miss the audit window.
    const todayIso = new Date().toISOString().split('T')[0];
    container.innerHTML = snap.docs.map(d => {
      const c = d.data();
      const dateStr = c.inspectionDate ? new Date(c.inspectionDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
      const oos = (c.vehicleOOS || c.driverOOS) ? '<span style="background:#FEE2E2;color:#991B1B;padding:1px 6px;border-radius:4px;font-size:0.65rem;font-weight:700;">OOS</span>' : '';
      const statusColor = c.repairStatus === 'completed' ? '#059669' : c.repairStatus === 'in-progress' ? '#D97706' : '#DC2626';
      const fineStr = c.fineAmount ? `<span style="font-size:0.72rem;font-weight:600;color:#7C3AED;">$${Number(c.fineAmount).toFixed(2)}</span>` : '';
      const paidByBadge = c.paidBy === 'company' ? '<span style="background:#DBEAFE;color:#1E40AF;padding:1px 5px;border-radius:3px;font-size:0.6rem;font-weight:700;">CO</span>'
        : c.paidBy === 'driver' ? '<span style="background:#FEF3C7;color:#92400E;padding:1px 5px;border-radius:3px;font-size:0.6rem;font-weight:700;">DR</span>' : '';

      // 15-day repair-deadline urgency badge
      let deadlineBadge = '';
      if (c.repairStatus !== 'completed' && c.repairDeadline) {
        const daysLeft = Math.floor((new Date(c.repairDeadline + 'T00:00:00').getTime() - new Date(todayIso + 'T00:00:00').getTime()) / 86400000);
        if (daysLeft < 0) {
          deadlineBadge = `<span title="Proof of repair past due — 49 CFR 396 requires sending to state within 15 days" style="background:#7F1D1D;color:#fff;padding:1px 6px;border-radius:4px;font-size:0.65rem;font-weight:700;">OVERDUE ${Math.abs(daysLeft)}d</span>`;
        } else if (daysLeft <= 3) {
          deadlineBadge = `<span title="Repair-proof deadline approaching" style="background:#FEF3C7;color:#92400E;padding:1px 6px;border-radius:4px;font-size:0.65rem;font-weight:700;">DUE ${daysLeft}d</span>`;
        }
      }
      // Linked DVIR check — flag missing-link when there's any defect/OOS
      let missingDvirBadge = '';
      const _linkedIds = getLinkedDvirIdsHR(c);
      if (!_linkedIds.length && c.repairStatus !== 'completed' && (c.vehicleOOS || (c.description && c.description.trim()))) {
        missingDvirBadge = `<span title="No DVIR linked — auditor expects DVIR + repair proof for this inspection" style="background:#FEF2F2;color:#991B1B;border:1px dashed #FCA5A5;padding:1px 6px;border-radius:4px;font-size:0.65rem;font-weight:700;">NO DVIR</span>`;
      }
      return `
        <div style="padding:8px 0;border-bottom:1px solid var(--border-light);display:flex;align-items:center;justify-content:space-between;cursor:pointer;" onclick="window.HRModule.openCitationModal('${empId}', '${d.id}')">
          <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
            <span style="font-weight:600;color:var(--text-primary);">${dateStr}</span>
            <span style="color:var(--text-secondary);">${c.vehicleUnit || 'No vehicle'}</span>
            ${oos} ${deadlineBadge} ${missingDvirBadge} ${fineStr} ${paidByBadge}
          </div>
          <div style="display:flex;align-items:center;gap:6px;">
            <span style="font-size:0.7rem;font-weight:600;color:${statusColor};text-transform:uppercase;">${c.repairStatus || 'pending'}</span>
            <span class="material-icons" style="font-size:14px;color:var(--text-muted);">chevron_right</span>
          </div>
        </div>
      `;
    }).join('');
  } catch (err) {
    console.error('[HR] Error loading employee citations:', err);
    container.innerHTML = '<div style="color:var(--text-secondary);font-size:0.8rem;">Error loading citations.</div>';
  }
}

// ============================================
// TRAINING CRUD (isnetTrainings[] array)
// ============================================

function addIsnetTraining(personId) {
  showIsnetTrainingModal(personId, null);
}

// TASK-4573b — rows pass the entry id; the index is only the fallback for a
// legacy id-less entry. Resolved against the live array at click time.
function _isnetRow(personId, index, entryId) {
  const rows = (HRState.people.find(p => p.id === personId) || {}).isnetTrainings || [];
  const i = entryId ? rows.findIndex(t => t && t.id === entryId) : (rows[index] && !rows[index].id ? index : -1); // an id-less button only ever means an id-less row
  if (rows[i]) return { training: rows[i], index: i };
  window.showToast?.('That record changed. Close this and reopen the person.', 'error');
}

function editIsnetTraining(personId, index, entryId) {
  const row = _isnetRow(personId, index, entryId);
  if (row) showIsnetTrainingModal(personId, { ...row.training, _index: row.index });
}

// The row the open Edit modal is editing (a copy taken when it opened). The save
// finds it again by id inside a transaction (trainingAudit.js saveTrainingTx).
let _isnetEditing = null;

// TASK-513 — required-trainings list lives in app.js (settings.safetyTrainings).
// Read the canonical list via window._stGetCurrent (exposed by app.js) so we
// don't drift. Returns active items only for the picker.
function _getSafetyTrainingsList() {
  try { return (window._stGetCurrent?.() || []).filter(i => i && i.archived !== true); }
  catch (_) { return []; }
}

function _buildRequiredTrainingOptions(selectedId) {
  const list = _getSafetyTrainingsList();
  const opts = list.map(i =>
    `<option value="${escapeHtml(i.itemId)}" ${i.itemId === selectedId ? 'selected' : ''}>${escapeHtml(i.label)}</option>`
  ).join('');
  return `<option value="" ${!selectedId ? 'selected' : ''}>— Not linked —</option>${opts}`;
}

// When the admin picks a required training, auto-fill the name field if blank.
function _onRequiredTrainingPick() {
  const sel = document.getElementById('isnet-required-training');
  const nameEl = document.getElementById('isnet-training-name');
  if (!sel || !nameEl) return;
  const item = _getSafetyTrainingsList().find(i => i.itemId === sel.value);
  if (item && !nameEl.value.trim()) nameEl.value = item.label;
  _trnLazy(m => m.renderDupWarning(() => HRState.people)); // TASK-4573 duplicate warning (add only)
}

function showIsnetTrainingModal(personId, training) {
  const isEdit = !!training;
  const t = training || {};
  _isnetEditing = isEdit ? { personId, entry: { ...t }, index: t._index } : null;

  const modalHtml = `
    <div class="modal-header">
      <h3><span class="material-icons">school</span> ${isEdit ? 'Edit' : 'Add'} Training Record</h3>
      <button class="modal-close" onclick="window.HRModule.closeModal('isnet-training')">
        <span class="material-icons">close</span>
      </button>
    </div>
    <div class="modal-body">
      <div class="form-group">
        <label for="isnet-required-training">Required Training (Safety Roster)</label>
        <select id="isnet-required-training" class="form-control" onchange="window.HRModule._onRequiredTrainingPick&&window.HRModule._onRequiredTrainingPick()">
          ${_buildRequiredTrainingOptions(t.requiredTrainingId)}
        </select>
        <div style="font-size:0.7rem;color:var(--text-muted);margin-top:4px;">Link this cert to a required training so it shows on the Safety Roster matrix. Optional.</div>
        ${isEdit ? '' : `<div id="isnet-dup-warning" data-person-id="${escapeHtml(personId)}" style="display:none;"></div>`}
      </div>
      <div class="form-group">
        <label for="isnet-training-name">Training / Course Name *</label>
        <input type="text" id="isnet-training-name" class="form-control" placeholder="e.g. H2S Alive" value="${escapeHtml(t.trainingName || '')}">
      </div>
      <div class="form-group">
        <label for="isnet-cert-name">Certification Name</label>
        <input type="text" id="isnet-cert-name" class="form-control" placeholder="e.g. H2S Safety Certificate" value="${escapeHtml(t.certificationName || '')}">
      </div>
      <div class="form-group">
        <label for="isnet-training-org">Organization</label>
        <input type="text" id="isnet-training-org" class="form-control" placeholder="e.g. Enform" value="${escapeHtml(t.organization || '')}">
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--spacing-sm);">
        <div class="form-group">
          <label for="isnet-training-issue">Issue Date</label>
          <input type="date" id="isnet-training-issue" class="form-control" value="${t.issueDate || ''}">
        </div>
        <div class="form-group">
          <label for="isnet-training-expiry">Expiry Date</label>
          <input type="date" id="isnet-training-expiry" class="form-control" value="${t.expiryDate || ''}">
        </div>
      </div>
      <div class="form-group">
        <label for="isnet-cert-number">Cert / Course Number</label>
        <input type="text" id="isnet-cert-number" class="form-control" placeholder="CERT-12345" value="${escapeHtml(t.certCourseNumber || '')}">
      </div>
      <div class="form-group" style="display:flex;align-items:flex-start;gap:8px;">
        <input type="checkbox" id="isnet-dot-relevant" ${t.dotRelevant === true ? 'checked' : ''} style="margin-top:3px;width:16px;height:16px;flex-shrink:0;accent-color:var(--brand-blue);">
        <label for="isnet-dot-relevant" style="margin:0;cursor:pointer;">DOT-relevant — include in DQ file
          <div style="font-size:0.7rem;color:var(--text-muted);font-weight:400;margin-top:2px;">Adds this certificate to the driver's §391.51 DQ file packet. Leave off for non-DOT site certs (H2S, confined space, fit test). When linked to a Required Training above, that training's DQ-file setting wins.</div>
        </label>
      </div>
      <div class="form-group">
        <label for="isnet-training-file">Scanned Copy</label>
        <input type="file" id="isnet-training-file" class="form-control" accept="image/*,.pdf">
        ${t.hasScannedCopy ? `<div style="font-size:0.75rem;color:#7c3aed;margin-top:4px;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:14px;">attach_file</span> Existing file attached${(t.scannedCopyPath || t.scannedCopyUrl) ? ` — <a href="#" onclick="event.preventDefault();window.viewComplianceDoc('${escJsAttr(t.scannedCopyPath || '')}','${escJsAttr(t.scannedCopyUrl || '')}','certificate',this);return false;" style="color:#7c3aed;cursor:pointer;">View</a>` : ''}</div>` : ''}
      </div>
    </div>
    <div class="modal-footer">
      <button class="btn btn-secondary" onclick="window.HRModule.closeModal('isnet-training')">Cancel</button>
      <button class="btn btn-primary" id="btn-save-isnet-training" onclick="window.HRModule.saveIsnetTraining('${personId}', ${isEdit ? t._index : -1})">
        <span class="material-icons">save</span> Save
      </button>
    </div>
  `;

  let modal = document.getElementById('isnet-training-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'isnet-training-modal';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  modal.innerHTML = `<div class="modal-content" style="max-width: 440px;">${modalHtml}</div>`;
  modal.classList.remove('hidden');
}

// TASK-4573 — training audit rows + the duplicate warning live in a LAZY chunk.
// TASK-4573b — so do save/delete-by-id (the save awaits the chunk; a failed load
// fails the save loudly instead of falling back to writing by index).
const _trnLoad = () => __ddChunkWrap(import('./trainingAudit.js?v=4573005'));
const _trnLazy = (f) => _trnLoad().then(f).catch(e => console.warn('[trainingAudit]', e));

async function saveIsnetTraining(personId, editIndex) {
  const trainingName = document.getElementById('isnet-training-name')?.value?.trim();
  if (!trainingName) {
    window.showToast?.('Training name is required', 'error');
    return;
  }

  const btn = document.getElementById('btn-save-isnet-training');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    const certificationName = document.getElementById('isnet-cert-name')?.value?.trim() || '';
    const organization = document.getElementById('isnet-training-org')?.value?.trim() || '';
    const issueDate = document.getElementById('isnet-training-issue')?.value || '';
    const expiryDate = document.getElementById('isnet-training-expiry')?.value || '';
    const certCourseNumber = document.getElementById('isnet-cert-number')?.value?.trim() || '';
    const requiredTrainingId = document.getElementById('isnet-required-training')?.value || '';
    const fileInput = document.getElementById('isnet-training-file');
    const file = fileInput?.files?.[0];
    const emp = HRState.people.find(p => p.id === personId);

    // TASK-4573b — the row this Edit modal opened (a copy, never a bare index).
    // A lost/foreign target matches nothing in the chunk -> refused, never an add.
    const ed = _isnetEditing; // captured BEFORE any await: a modal reopened meanwhile can't retarget this save (COD r1)
    const dotRelevant = document.getElementById('isnet-dot-relevant')?.checked === true; // read once, not per txn retry
    const m = await _trnLoad();

    // Step 1 — write the entry (metadata only) BY ID, inside a transaction that
    // re-reads the person; refused (nothing written) when the entry is gone.
    // Existing cert-doc fields are carried forward FROM THE FRESH ROW. NOTE
    // (TASK-513 IDOR ATTACK 1): docUrl/storagePath/uploadedAt are NEVER set
    // client-side here — they are server-derived by the uploadSafetyCert
    // callable; we only carry forward whatever the server previously wrote.
    // A legacy id-less entry gets its id on this first save.
    const personRef = doc(db, 'tenants', HRState.tenantId, 'people', personId);
    const target = editIndex >= 0 ? m.trainingTarget(ed?.personId === personId && ed.entry, ed?.index) : null;
    const { next: currentTrainings, record, prev: prevRecord } = await m.saveTrainingTx({ db, runTransaction }, personRef, target, (existing) => ({
      id: existing?.id || m.newTrainingId(),
      trainingName,
      certificationName: certificationName || null,
      organization: organization || null,
      issueDate: issueDate || null,
      expiryDate: expiryDate || null,
      certCourseNumber: certCourseNumber || null,
      requiredTrainingId: requiredTrainingId || null,
      // TASK-1085 — per-record §391.51 DQ-file flag (default unchecked). Matters
      // for UNLINKED records; when requiredTrainingId is set, the linked config
      // item's DQ-file setting wins at packet time (server resolution order).
      dotRelevant,
      hasScannedCopy: existing?.hasScannedCopy || false,
      docUrl: existing?.docUrl || null,
      storagePath: existing?.storagePath || null,
      uploadedAt: existing?.uploadedAt || null,
      scannedCopyUrl: existing?.scannedCopyUrl || existing?.docUrl || null,
      scannedCopyPath: existing?.scannedCopyPath || existing?.storagePath || null,
    }));
    const entryId = record.id;
    if (emp) emp.isnetTrainings = currentTrainings;
    // TASK-4573 — audit row (non-blocking). The matrix save routes through here too.
    _trnLazy(m => m.auditTraining('hr', personId, record, prevRecord));

    // Step 2 — if a new file was picked, upload it through the SERVER callable
    // (server-derives storagePath under tenants/{tid}/safety-certs/). The
    // entry already exists (step 1) so the callable can find it by id.
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        window.showToast?.('File too large (max 10MB) — record saved without the scan', 'error');
      } else {
        try {
          const fileBase64 = await _readFileAsBase64(file);
          const uploadFn = httpsCallable(functions, 'uploadSafetyCert');
          await uploadFn({
            tenantId: HRState.tenantId,
            personId,
            entryId,
            fileBase64,
            fileName: file.name,
            mimeType: file.type || inferContentType(file),
          });
          // Reflect the server-written doc fields into local state + the array.
          const fresh = await getDoc(personRef);
          const refreshed = fresh.data()?.isnetTrainings || currentTrainings;
          if (emp) emp.isnetTrainings = refreshed;
        } catch (upErr) {
          console.error('[HR] Safety cert upload failed:', upErr);
          window.showToast?.('Record saved, but the certificate upload failed: ' + (upErr?.message || 'unknown'), 'error');
        }
      }
    }

    // TASK-723 must-fix #2 — capture the matrix-save context BEFORE closeModal
    // (closeModal('isnet-training') clears _matrixSaveContext). If this save came
    // from the interactive matrix panel, re-render it in place from the refreshed
    // HRState.people (emp.isnetTrainings updated above) instead of bouncing to the
    // employee detail view.
    const fromMatrix = !!(_matrixSaveContext && _matrixSaveContext.personId === personId);
    const matrixPanel = document.getElementById('safety-matrix-panel');
    const matrixOpen = matrixPanel && matrixPanel.style.display !== 'none';
    window.showToast?.('Training record saved', 'success');
    closeModal('isnet-training');
    if (fromMatrix && matrixOpen) {
      _renderSafetyMatrixPanelBody();
    } else {
      openEmployeeDetail(personId);
    }
  } catch (error) {
    console.error('[HR] Error saving ISNet training:', error);
    window.showToast?.(error?.code === 'training-gone' ? error.message : error?.code === 'permission-denied' ? 'You need the HR or Compliance module to change training records' : 'Error saving training record', 'error');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons">save</span> Save'; }
  }
}

async function deleteIsnetTraining(personId, index, entryId) {
  // TASK-4573b — the row as shown when Delete was pressed; removed BY ID inside a
  // transaction, refused when it is no longer there (never splice a bare index).
  const row = _isnetRow(personId, index, entryId);
  if (!row) return;
  if (!confirm('Delete this training record?')) return;

  try {
    const m = await _trnLoad();
    const personRef = doc(db, 'tenants', HRState.tenantId, 'people', personId);
    // TASK-9912 — write the record FIRST, then remove the file. firestore.rules
    // refuse a training write without the HR or Compliance module; deleting the
    // file before a refused write left the row pointing at a deleted cert.
    const { next: currentTrainings, removed: training } = await m.deleteTrainingTx({ db, runTransaction }, personRef, m.trainingTarget(row.training, row.index));
    if (training) _trnLazy(m => m.auditTraining('hr', personId, training, null, true)); // TASK-4573

    if (training?.scannedCopyPath) {
      try { await deleteObject(ref(storage, training.scannedCopyPath)); } catch (e) { /* file may not exist */ }
    }

    // Update local state
    const emp = HRState.people.find(p => p.id === personId);
    if (emp) emp.isnetTrainings = currentTrainings;

    window.showToast?.('Training record deleted', 'success');
    openEmployeeDetail(personId);
  } catch (error) {
    console.error('[HR] Error deleting ISNet training:', error);
    window.showToast?.(error?.code === 'training-gone' ? error.message : error?.code === 'permission-denied' ? 'You need the HR or Compliance module to change training records' : 'Error deleting training record', 'error');
  }
}

// TASK-919 — small reason-capture modal for marking a required training "Not
// Applicable" for one employee. Promise resolves to the trimmed reason, or null
// if cancelled. Uses class="modal" (per lessons: NOT "modal-overlay").
function _promptExemptionReason(trainingLabel) {
  return new Promise((resolve) => {
    const existing = document.getElementById('training-exempt-modal');
    if (existing) existing.remove();
    const modal = document.createElement('div');
    modal.id = 'training-exempt-modal';
    modal.className = 'modal';
    modal.style.cssText = 'display:flex;align-items:center;justify-content:center;';
    modal.innerHTML = `
      <div class="modal-content" style="max-width:420px;width:92%;">
        <div class="modal-header" style="padding:14px 16px;"><h3 style="margin:0;font-size:1rem;">Mark Not Applicable</h3></div>
        <div class="modal-body" style="padding:16px;">
          <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 10px;">Why is <strong>${escapeHtml(trainingLabel)}</strong> not applicable for this employee? This reason is recorded in the audit log.</p>
          <textarea id="training-exempt-reason" rows="3" maxlength="500" placeholder="e.g. Office/admin staff — not a field role" style="width:100%;box-sizing:border-box;"></textarea>
        </div>
        <div class="form-actions" style="display:flex;justify-content:flex-end;gap:8px;padding:12px 16px;">
          <button class="btn btn-secondary" id="training-exempt-cancel">Cancel</button>
          <button class="btn btn-primary" id="training-exempt-confirm">Mark N/A</button>
        </div>
      </div>`;
    document.body.appendChild(modal);
    const ta = modal.querySelector('#training-exempt-reason');
    setTimeout(() => ta?.focus(), 50);
    const close = (val) => { modal.remove(); resolve(val); };
    modal.querySelector('#training-exempt-cancel').onclick = () => close(null);
    modal.querySelector('#training-exempt-confirm').onclick = () => {
      const v = (ta.value || '').trim();
      if (!v) { ta.style.borderColor = '#DC2626'; ta.focus(); return; }
      close(v);
    };
    modal.addEventListener('click', (e) => { if (e.target === modal) close(null); });
  });
}

// TASK-919 — set/clear a per-employee Required-Training exemption via the
// setTrainingExemption callable (server merges/deletes; firestore.rules blocks any
// direct client write). Gated on _canCarrierEditTrainings() — a partner-managed
// carrier whose partner has NOT shared edit rights can't set (only the partner can).
// TASK-1031 — double-submit guard keyed by person+item while the callable is in flight.
const _trainingExemptionBusy = new Set();
async function toggleTrainingExemption(personId, itemId, exempt) {
  if (!_canCarrierEditTrainings()) {
    window.showToast?.('Required trainings are managed by your compliance partner.', 'info');
    return;
  }
  const _busyKey = `${personId}::${itemId}`;
  if (_trainingExemptionBusy.has(_busyKey)) return; // already in flight
  // TASK-1031 (COD): claim the key BEFORE the reason/confirm prompt so a rapid
  // second click can't open a duplicate modal / double-fire the callable. Cleared
  // in finally on every path incl. cancel.
  _trainingExemptionBusy.add(_busyKey);
  try {
    let reason = '';
    if (exempt) {
      const cfg = _getSafetyTrainingsList().find(i => i && i.itemId === itemId);
      reason = await _promptExemptionReason(cfg?.label || 'this training');
      if (reason == null) return; // cancelled
    } else {
      const ok = await (window.showConfirm
        ? window.showConfirm('Clear exemption?', 'This employee will be required to have this training again.')
        : Promise.resolve(confirm('Clear exemption? This employee will be required to have this training again.')));
      if (!ok) return;
    }
    const fn = httpsCallable(functions, 'setTrainingExemption');
    await fn({ tenantId: HRState.tenantId, personId, itemId, exempt: !!exempt, reason });
    // Optimistic local-state update so the re-render reflects immediately.
    const emp = HRState.people.find(p => p.id === personId);
    if (emp) {
      emp.safetyTrainingExemptions = emp.safetyTrainingExemptions || {};
      if (exempt) emp.safetyTrainingExemptions[itemId] = { reason, exemptedAt: new Date().toISOString() };
      else delete emp.safetyTrainingExemptions[itemId];
    }
    window.showToast?.(exempt ? 'Marked Not Applicable' : 'Exemption cleared', 'success');
    openEmployeeDetail(personId);
  } catch (err) {
    console.error('[HR] setTrainingExemption', err);
    window.showToast?.('Error updating exemption: ' + (err?.message || ''), 'error');
  } finally {
    _trainingExemptionBusy.delete(_busyKey);
  }
}

// ============================================
// DRUG TEST CRUD (HR owns this — no ComplianceModule dependency)
// ============================================

const DRUG_TEST_TYPES = [
  { value: 'pre_employment', label: 'Pre-Employment' },
  { value: 'random', label: 'Random' },
  { value: 'post_accident', label: 'Post-Accident' },
  { value: 'reasonable_suspicion', label: 'Reasonable Suspicion' },
  { value: 'return_to_duty', label: 'Return to Duty' },
  { value: 'follow_up', label: 'Follow-Up' },
  { value: 'other', label: 'Other' }
];

// TASK-1086 — a test carries TWO distinct docs: the verified RESULT / MRO
// report (pendingDrugTestFile → documentPath) and the Custody & Control Form
// (pendingDrugTestCcfFile → ccfDocumentPath). Both optional individually.
let pendingDrugTestFile = null;
let pendingDrugTestCcfFile = null;

function showDrugTestModal(personId) {
  const person = HRState.people.find(p => p.id === personId);
  if (!person) return;
  // Clear any stale attachments from a previously-canceled modal.
  pendingDrugTestFile = null;
  pendingDrugTestCcfFile = null;
  const today = new Date().toISOString().split('T')[0];

  let modal = document.getElementById('hr-drug-test-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'hr-drug-test-modal';
    modal.className = 'modal hidden';
    document.body.appendChild(modal);
  }
  modal.classList.remove('hidden');
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;margin:auto;border-radius:var(--radius-xl);overflow:hidden;display:flex;flex-direction:column;max-height:90vh;max-height:90dvh;">
      <div style="padding:16px 20px;background:linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);color:#fff;flex-shrink:0;">
        <div style="display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:1.1rem;font-weight:700;">Add Drug Test — ${escapeHtml(person.name)}</h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('hr-drug-test')" style="color:#fff;">
            <span class="material-icons">close</span>
          </button>
        </div>
      </div>
      <div style="padding:20px;display:flex;flex-direction:column;gap:14px;overflow-y:auto;flex:1;min-height:0;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Test Date *</label>
            <input type="date" id="hr-drug-test-date" class="form-control" value="${today}" required>
          </div>
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Test Type *</label>
            <select id="hr-drug-test-type" class="form-control" required>
              ${DRUG_TEST_TYPES.map(t => `<option value="${t.value}">${t.label}</option>`).join('')}
            </select>
          </div>
        </div>
        <!-- TASK-9696 — every test record says which program it belongs to -->
        ${(() => {
          const cov = window.DqMatrix.testingCoverageFor(person);
          const opts = window.DqMatrix.PROGRAM_KEYS.map(k => `<option value="${k}" ${cov.defaultProgram === k ? 'selected' : ''}>${escapeHtml(window.DqMatrix.PROGRAMS[k].label)}</option>`).join('');
          return `
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Testing Program *</label>
          <select id="hr-drug-test-program" class="form-control" required>
            ${cov.defaultProgram ? '' : '<option value="" selected>— Choose the program —</option>'}${opts}
          </select>
          <div style="font-size:0.72rem;color:${cov.any ? 'var(--text-secondary)' : '#92400E'};margin-top:4px;">${cov.any ? 'This person: ' + escapeHtml(window.DqMatrix.coverageLabel(cov)) : 'This person is not in a testing program (not a CDL driver, no PHMSA / company flag). Record the test under the program it was actually run in, and fix the classification or flags.'}</div>
        </div>`;
        })()}
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Result *</label>
          <select id="hr-drug-test-result" class="form-control" required>
            <option value="pass">Negative (Pass)</option>
            <option value="pending">Pending</option>
            <option value="fail">Positive (Fail)</option>
          </select>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Lab / Facility</label>
            <input type="text" id="hr-drug-test-lab" class="form-control" placeholder="e.g. Quest Diagnostics">
          </div>
          <div>
            <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Collector</label>
            <input type="text" id="hr-drug-test-collector" class="form-control" placeholder="e.g. MRO name">
          </div>
        </div>
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Test Result / MRO Report (PDF or image)</label>
          <div id="hr-drug-test-file-preview" style="margin-bottom:6px;"></div>
          <label class="btn btn-secondary btn-sm" style="cursor:pointer;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:16px;">attach_file</span> Choose File
            <input type="file" id="hr-drug-test-file" accept="image/*,application/pdf" style="display:none;" onchange="window.HRModule.handleDrugTestFile(event)">
          </label>
        </div>
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Custody &amp; Control Form (CCF) (PDF or image)</label>
          <div id="hr-drug-test-ccf-preview" style="margin-bottom:6px;"></div>
          <label class="btn btn-secondary btn-sm" style="cursor:pointer;display:inline-flex;align-items:center;gap:4px;">
            <span class="material-icons" style="font-size:16px;">attach_file</span> Choose File
            <input type="file" id="hr-drug-test-ccf-file" accept="image/*,application/pdf" style="display:none;" onchange="window.HRModule.handleDrugTestFile(event, 'ccf')">
          </label>
        </div>
        <div>
          <label style="font-size:0.75rem;font-weight:600;color:var(--text-secondary);display:block;margin-bottom:4px;">Notes</label>
          <input type="text" id="hr-drug-test-notes" class="form-control" placeholder="Optional notes">
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom,#F8FAFC,#fff);display:flex;gap:12px;justify-content:flex-end;flex-shrink:0;">
        <button class="btn btn-secondary" onclick="window.HRModule.closeModal('hr-drug-test')">Cancel</button>
        <button class="btn btn-primary" id="hr-drug-test-save-btn" onclick="window.HRModule.saveDrugTest('${personId}')">
          <span class="material-icons" style="font-size:18px;">save</span> Save
        </button>
      </div>
    </div>
  `;
}

function handleDrugTestFileUpload(event, kind) {
  const file = event.target.files?.[0];
  if (!file) return;
  event.target.value = '';

  const nameLower = file.name.toLowerCase();
  const isPdf = file.type === 'application/pdf' || nameLower.endsWith('.pdf');
  const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.bmp', '.tiff', '.tif'];
  const isImage = file.type.startsWith('image/') || imageExts.some(ext => nameLower.endsWith(ext));
  if (!isPdf && !isImage) {
    window.showToast?.('Only images and PDFs allowed', 'error');
    return;
  }
  if (file.size > 10 * 1024 * 1024) {
    window.showToast?.('File too large (max 10MB)', 'error');
    return;
  }

  const isCcf = kind === 'ccf';
  if (isCcf) pendingDrugTestCcfFile = file; else pendingDrugTestFile = file;
  const preview = document.getElementById(isCcf ? 'hr-drug-test-ccf-preview' : 'hr-drug-test-file-preview');
  if (preview) {
    preview.innerHTML = `
      <div style="display:flex;align-items:center;gap:8px;padding:6px 10px;background:var(--bg-secondary);border-radius:var(--radius-md);font-size:0.8rem;">
        <span class="material-icons" style="font-size:18px;color:${isPdf ? '#DC2626' : 'var(--brand-blue)'};">${isPdf ? 'picture_as_pdf' : 'image'}</span>
        <span style="flex:1;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(file.name)}</span>
        <button class="btn-icon" onclick="window.HRModule.removeDrugTestFile(${isCcf ? "'ccf'" : ''})" title="Remove">
          <span class="material-icons" style="font-size:16px;color:var(--text-secondary);">close</span>
        </button>
      </div>
    `;
  }
}

function removeDrugTestFile(kind) {
  if (kind === 'ccf') pendingDrugTestCcfFile = null; else pendingDrugTestFile = null;
  const preview = document.getElementById(kind === 'ccf' ? 'hr-drug-test-ccf-preview' : 'hr-drug-test-file-preview');
  if (preview) preview.innerHTML = '';
}

async function saveDrugTestRecord(personId) {
  const testDate = document.getElementById('hr-drug-test-date')?.value?.trim();
  const testType = document.getElementById('hr-drug-test-type')?.value?.trim();
  const result = document.getElementById('hr-drug-test-result')?.value?.trim();
  const lab = document.getElementById('hr-drug-test-lab')?.value?.trim() || '';
  const collector = document.getElementById('hr-drug-test-collector')?.value?.trim() || '';
  const notes = document.getElementById('hr-drug-test-notes')?.value?.trim() || '';
  const program = window.DqMatrix.normalizeProgram(document.getElementById('hr-drug-test-program')?.value); // TASK-9696

  if (!testDate || !testType || !result) {
    window.showToast?.('Date, type, and result are required', 'error');
    return;
  }
  if (!program) {
    window.showToast?.('Pick the testing program this test belongs to (DOT–FMCSA, DOT–PHMSA or company)', 'error');
    return;
  }

  const person = HRState.people.find(p => p.id === personId);
  const btn = document.getElementById('hr-drug-test-save-btn');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  try {
    // TASK-10081 — store the PATH only. A getDownloadURL token link is a bearer
    // credential that outlives the drugTests read gate and SEC-STORAGE-DQ, so it
    // is never fetched or saved here; viewers open the file by path.
    let documentPath = '';
    let ccfDocumentPath = '';

    // TASK-1086 — documentPath = test RESULT / MRO report; ccfDocumentPath =
    // Custody & Control Form. Both leaves MUST keep the `${personId}_` prefix
    // (the server-side IDOR fence keys off it).
    if (pendingDrugTestFile) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const ext = pendingDrugTestFile.name.split('.').pop() || 'pdf';
      const fileName = `${personId}_${testDate}_${Date.now()}.${ext}`;
      const path = `tenants/${HRState.tenantId}/drugTests/${year}/${month}/${fileName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, pendingDrugTestFile, { contentType: inferContentType(pendingDrugTestFile) });
      documentPath = path;
    }
    if (pendingDrugTestCcfFile) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const ext = pendingDrugTestCcfFile.name.split('.').pop() || 'pdf';
      const fileName = `${personId}_${testDate}_${Date.now()}_ccf.${ext}`;
      const path = `tenants/${HRState.tenantId}/drugTests/${year}/${month}/${fileName}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, pendingDrugTestCcfFile, { contentType: inferContentType(pendingDrugTestCcfFile) });
      ccfDocumentPath = path;
    }

    const data = {
      personId,
      personName: person?.name || '',
      testDate,
      testType,
      program, // TASK-9696 — dot_fmcsa | dot_phmsa | company
      result,
      lab,
      collector,
      notes,
      documentPath,
      ccfDocumentPath,
      createdAt: Timestamp.now()
    };

    await addDoc(collection(db, 'tenants', HRState.tenantId, 'drugTests'), data);
    const _hadReceipt = !!(documentPath || ccfDocumentPath); // TASK-1274 — confirm the upload landed
    pendingDrugTestFile = null;
    pendingDrugTestCcfFile = null;
    window.showToast?.(_hadReceipt ? 'Drug test saved — receipt uploaded ✓' : 'Drug test recorded ✓', 'success');

    closeModal('hr-drug-test');
    // TASK-1274 (Chris dogfooding) — re-render, then stay on the Compliance tab so the
    // new test (+ its receipt) is right there, instead of bouncing to the top Reviews tab.
    // The record is already saved; isolate the re-render so a render hiccup can't
    // masquerade as a save failure in the catch below.
    try {
      await openEmployeeDetail(personId);
      switchDetailTab('compliance');
    } catch (_) { /* save succeeded; ignore a cosmetic re-render error */ }
  } catch (err) {
    console.error('[HR] Drug test save error:', err);
    window.showToast?.('Error saving drug test', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:18px;">save</span> Save'; }
  }
}

// TASK-DA-v1 Slice 2 — §382.601(d) incumbent receipt link sender.
// Admin clicks "Send Link" on the HR person card → server callable mints
// a 30-day single-use token, emails the driver (if email on file), and
// returns the link so the admin can also text/hand it off manually.
async function sendIncumbentDaReceiptLink(personId, btn) {
  if (!personId) return;
  // TASK-1080 — in-flight feedback + double-submit guard: the callable takes ~1-3s
  // and previously ran with ZERO UI (button stayed active → looked dead, double-click
  // could double-mint). Disable + "Sending…" on the clicked button, restore in finally.
  if (btn && btn.dataset.sending === '1') return; // already minting
  const person = HRState.people.find(p => p.id === personId);
  if (!person) {
    window.showToast?.('Driver not found', 'error');
    return;
  }
  let _origHtml = null;
  if (btn) {
    _origHtml = btn.innerHTML;
    btn.dataset.sending = '1';
    btn.disabled = true;
    btn.innerHTML = '<span class="material-icons spin" style="font-size:14px;vertical-align:-2px;">autorenew</span> Sending…';
  }
  try {
    const fn = httpsCallable(functions, 'mintIncumbentDrugAlcoholReceiptToken');
    let res;
    // TASK-9696 — the server refuses a receipt for a person no testing program
    // covers (NOT_COVERED); the sender may override knowingly, same as the DER gate.
    // COD 10/12 — the server checks the DER FIRST, so either refusal can come back on
    // any attempt: handle both in one retry loop (each override asked at most once).
    let _allowNotCovered = false;
    let _allowMissingDer = false;
    for (let _attempt = 0; _attempt < 3 && !res; _attempt++) {
      try {
        res = await fn({ tenantId: HRState.tenantId, personId, ...(_allowMissingDer ? { allowMissingDer: true } : {}), allowNotCovered: _allowNotCovered });
      } catch (e) {
        const _msg = e?.message || '';
        if (!_allowMissingDer && /DER_MISSING/.test(_msg)) {
          // TASK-9143 - no DER on file: the server refuses; sender may override knowingly.
          const go = !window.showConfirm ? window.confirm('No DER is set - the notice would carry a placeholder contact. Send anyway?') : await window.showConfirm(
            'No Designated Employer Representative set',
            'The D&A policy notice would go out with a placeholder instead of a real contact (49 CFR §382.601(b)(1)), and a receipt signed against it cannot be fixed later.\n\nSet the DER under Drug & Alcohol settings first, or send anyway.',
            { confirmText: 'Send anyway', cancelText: 'Cancel', danger: true }
          );
          if (!go) return;
          _allowMissingDer = true;
          continue;
        }
        if (!_allowNotCovered && /NOT_COVERED/.test(_msg)) {
          const goNc = window.showConfirm
            ? await window.showConfirm('Not in a testing program', `${person.name || 'This person'} is not a CDL driver and has no PHMSA or company-program flag, so no drug & alcohol policy receipt is owed. Fix the classification or testing-program flags first, or send anyway.`, { confirmText: 'Send anyway', cancelText: 'Cancel', danger: true })
            : window.confirm('Not in a testing program - send the receipt anyway?');
          if (!goNc) return;
          _allowNotCovered = true;
          continue;
        }
        throw e;
      }
    }
    const data = res?.data || {};
    if (!data.ok) throw new Error('Could not generate receipt link');
    // TASK-9145 — emailStatus is the email leg's REAL outcome; emailedTo alone
    // was set even when SendGrid refused.
    const emailedNote = data.emailStatus === 'failed' || data.emailStatus === 'not_configured'
      ? `The email to ${data.emailedTo} did NOT go out${data.emailError ? ` (${data.emailError})` : ''}. Copy this link and send it yourself: `
      : data.emailedTo
        ? `Emailed to ${data.emailedTo}. `
        : 'No email on file — copy this link and send via SMS or hand-off: ';
    // Show confirm modal with link so admin can copy
    await window.showConfirm?.(
      'Receipt link generated',
      `${emailedNote}\n\nLink (expires in 30 days):\n${data.link}\n\nThe driver signs at this page. You'll see "Signed" appear here once they submit.`,
      { confirmText: 'OK', cancelText: null, danger: false }
    );
    // Best-effort clipboard copy
    try { await navigator.clipboard?.writeText(data.link); window.showToast?.('Link copied to clipboard', 'success'); } catch (_) { /* ignored */ }
  } catch (err) {
    console.error('[HR] Send incumbent DA receipt link error:', err);
    const msg = (err?.message || '').replace(/^internal:\s*/, '').slice(0, 200);
    window.showToast?.(`Could not generate link: ${msg || 'server error'}`, 'error');
  } finally {
    if (btn) { delete btn.dataset.sending; btn.disabled = false; if (_origHtml != null) btn.innerHTML = _origHtml; }
  }
}

// TASK-DA-v1 Slice 1 — §382.401(b)(1) 5-yr retention guardrail (G6).
// Records matching the protected classes (positive drug, alcohol ≥0.02,
// refusal) cannot be deleted via the standard client path — Firestore
// rules block it. The break-glass callable handles legitimate corrections
// (typo, wrong driver mis-stamped) with a forensic audit log entry.
function _isProtectedDrugAlcoholRecord(test) {
  if (!test) return false;
  const result = String(test.result || '').toLowerCase();
  if (['fail', 'positive', 'refusal'].includes(result)) return true;
  const ac = Number(test.alcoholConcentration);
  if (Number.isFinite(ac) && ac >= 0.02) return true;
  return false;
}

async function deleteDrugTestRecord(testId) {
  const test = HRState.drugTests.find(t => t.id === testId);
  if (!test) {
    window.showToast?.('Drug test not found', 'error');
    return;
  }

  if (_isProtectedDrugAlcoholRecord(test)) {
    // Hard-stop modal — FMCSA §382.401(b)(1) requires 5-year retention.
    // Admin can break-glass with a written reason; the callable audit-logs it.
    const proceed = await window.showConfirm?.(
      'FMCSA requires 5-year retention',
      `This record is protected by 49 CFR §382.401(b)(1) — FMCSA requires keeping it for at least 5 years.\n\n` +
      `Protected records (§382.401(b)(1)(i-iii)):\n` +
      `• Alcohol test results with concentration ≥ 0.02\n` +
      `• Verified positive controlled substances test results\n` +
      `• Refusals to take required tests\n\n` +
      `This record matches one of those categories. Deletion is restricted.\n\n` +
      `If this is a correction (wrong driver, typo, duplicate), you can use "Break Glass Delete." Every break-glass deletion writes a permanent audit-log entry with your name, the reason, and the full record snapshot — the audit trail is the regulatory defense.`,
      { confirmText: 'Break Glass Delete (audit-logged)', cancelText: 'Cancel', danger: true }
    );
    if (proceed === false) return;

    // Prompt for reason — must be ≥ 20 chars per server callable.
    let reason = (window.prompt('Type a written reason for this deletion (minimum 20 characters). This will be permanently recorded in the audit log:') || '').trim();
    if (reason.length < 20) {
      window.showToast?.('Reason must be at least 20 characters — deletion canceled', 'error');
      return;
    }
    try {
      const fn = httpsCallable(functions, 'adminBreakGlassDeleteDrugAlcoholRecord');
      await fn({ tenantId: HRState.tenantId, recordId: testId, reason });
      window.showToast?.('Drug test deleted (audit-logged)', 'success');
    } catch (err) {
      console.error('[HR] Break-glass delete error:', err);
      const msg = (err?.message || '').replace(/^internal:\s*/, '').slice(0, 200);
      window.showToast?.(`Could not delete: ${msg || 'server error'}`, 'error');
    }
    return;
  }

  // Standard delete path — non-protected records (negative tests, etc.).
  // Firestore-first, Storage-after (Code Reviewer catch: prevents Storage
  // orphan if Firestore delete fails / rules deny).
  if (!confirm('Delete this drug test record?')) return;
  try {
    await deleteDoc(doc(db, 'tenants', HRState.tenantId, 'drugTests', testId));
    // TASK-1086 — a record may carry BOTH a result doc and a CCF; clean up both.
    if (test?.documentPath) {
      try { await deleteObject(ref(storage, test.documentPath)); } catch (e) { /* ok if already gone */ }
    }
    if (test?.ccfDocumentPath) {
      try { await deleteObject(ref(storage, test.ccfDocumentPath)); } catch (e) { /* ok if already gone */ }
    }
    window.showToast?.('Drug test deleted', 'success');
  } catch (err) {
    console.error('[HR] Drug test delete error:', err);
    window.showToast?.('Error deleting', 'error');
  }
}

function viewDrugTestDocument(testId, kind) {
  const test = HRState.drugTests.find(t => t.id === testId);
  if (!test) return;
  // TASK-1086 — kind 'ccf' opens the Custody & Control Form; default opens the
  // test result / MRO report (legacy single-doc records live in documentUrl).
  // TASK-9855 slice 1 — open by PATH (documentPath / ccfDocumentPath, else the
  // path inside the stored URL) through the Storage SDK, so storage.rules
  // (isDqDocWriter) decide. Never the bearer URL — not on a denial, not on a
  // network error. Only a non-Storage https link (none expected) opens as-is.
  const urlKey = kind === 'ccf' ? 'ccfDocumentUrl' : 'documentUrl';
  const fref = window.StorageFile?.fileRef?.(test, { urlKey }) || null;
  if (!fref) { window.showToast?.('No document on file', 'error'); return; }
  if (!fref.path) {
    if (/^https:\/\//i.test(String(fref.url || ''))) window.open(fref.url, '_blank', 'noopener');
    else window.showToast?.('No document on file', 'error');
    return;
  }
  _hrOpenStorageStrict({ path: fref.path, bucket: fref.bucket }).catch((e) => {
    console.error('[HR] viewDrugTestDocument failed:', e);
    window.showToast?.(e?.sfReason === 'denied' ? 'You do not have access to this document' : (e?.message || 'Could not open the document'), 'error');
  });
}

// ============================================
// MEDICAL CARD CRUD (HR owns this)
// ============================================

// TASK-647 — ONE DOT Medical Examiner's Certificate, stored in TWO fields by
// four writers (medCertPdfUrl ← Record-Exam + driver SMS self-upload;
// medCardDocumentUrl ← Upload-button + partner doc-intake). Read sites used to
// diverge (compliance/partner view + payroll only saw medCardDocumentUrl, so a
// driver's SMS self-upload was invisible → silent DQ-file gap). This is the ONE
// shared reader both hr.js and compliance.js use (compliance.js calls it via
// window.HRModule.getEffectiveMedCardDoc, with an identical inline fallback) so
// the card renders the SAME doc everywhere and ONLY one link shows.
//
// Recency / fallback (B-lite, no backfill, no rules change):
//   • both fields populated → pick the most-recent using whatever timestamps
//     exist (medCardSelfUploadedAt for medCertPdfUrl; medCardDocumentUploadedAt
//     for medCardDocumentUrl — added going forward by the writers).
//   • only one timestamp present → the timestamped one is treated as newer.
//   • both populated, NO timestamps (legacy docs predating this fix) → prefer
//     medCardDocumentUrl. Rationale: it's the field the explicit "Upload Medical
//     Card" / partner doc-intake writers populate (a deliberate file attach),
//     whereas a legacy medCertPdfUrl with no medCardSelfUploadedAt came from an
//     old Record-Exam save; the deliberately-attached doc is the safer default.
//   • only one field populated → that one (with its url/source/timestamp).
// Returns { url, source, uploadedAt, pendingReview } or null when neither exists.
function getEffectiveMedCardDoc(emp) {
  if (!emp) return null;
  emp = window.DqMatrix?.medDetail ? window.DqMatrix.medDetail(emp, HRState.tenantId) : emp; // TASK-1496-S6
  const toMs = (t) => {
    if (!t) return null;
    if (typeof t.toDate === 'function') { try { return t.toDate().getTime(); } catch (_) { return null; } }
    if (t.seconds != null) return t.seconds * 1000;
    const ms = new Date(t).getTime();
    return isNaN(ms) ? null : ms;
  };

  const certUrl = emp.medCertPdfUrl || '';
  const certPath = emp.medCertPdfPath || '';   // TASK-996 — path-only self-uploads
  const docUrl = emp.medCardDocumentUrl || '';
  const docPath = emp.medCardDocumentPath || '';
  // TASK-647: cert side is stamped by EITHER the driver SMS self-upload
  // (medCardSelfUploadedAt) OR a Record-Exam attach (medCertPdfUploadedAt).
  // Use the MAX (most-recent cert event) — a Record-Exam AFTER a self-upload
  // leaves the older medCardSelfUploadedAt set, so `||` would return the stale
  // one and hide the fresh exam cert (COD Adversary). Max picks the latest.
  const certAt = Math.max(toMs(emp.medCardSelfUploadedAt) || 0, toMs(emp.medCertPdfUploadedAt) || 0) || null;
  const docAt = toMs(emp.medCardDocumentUploadedAt);
  const pendingReview = !!emp.medCardPendingReview;

  // TASK-996 — present if EITHER a (legacy) URL or a storagePath exists; path-only
  // entries (no URL) are viewed via the getComplianceDocBytes byte-stream callable.
  const cert = (certUrl || certPath) ? { url: certUrl || null, path: certPath || null, source: 'medCertPdfUrl', uploadedAt: certAt, pendingReview } : null;
  const docu = (docUrl || docPath) ? { url: docUrl || null, path: docPath || null, source: 'medCardDocumentUrl', uploadedAt: docAt, pendingReview: false } : null;

  if (cert && !docu) return cert;
  if (docu && !cert) return docu;
  if (!cert && !docu) return null;

  // Both populated — most-recent wins by timestamp.
  if (cert.uploadedAt != null && docu.uploadedAt != null) {
    return cert.uploadedAt >= docu.uploadedAt ? cert : docu;
  }
  if (cert.uploadedAt != null) return cert; // only cert is timestamped → newer
  if (docu.uploadedAt != null) return docu; // only doc is timestamped → newer
  // Neither timestamped (legacy) → prefer the deliberately-attached doc field.
  return docu;
}

async function uploadMedCardDocument(personId) {
  const tenantId = HRState.tenantId;
  if (!tenantId || !personId) return;

  // Reuse or create a persistent file input (some browsers block click() on transient inputs)
  let input = document.getElementById('hr-medcard-file-input');
  if (!input) {
    input = document.createElement('input');
    input.type = 'file';
    input.id = 'hr-medcard-file-input';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
    document.body.appendChild(input);
  }
  // Reset so same file can be re-selected
  input.value = '';

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      window.showToast?.('File too large (max 10MB)', 'error');
      return;
    }

    try {
      window.showToast?.('Uploading...', 'info');
      let storagePath, uploadedAt;
      if (_hrPartnerClientTenantId()) {
        // TASK-9449 — managing partner: server upload (create-only) + pointer + audit.
        const up = await _hrPartnerUploadPersonDoc(personId, 'medCard', file);
        storagePath = up.storagePath;
        uploadedAt = up.uploadedAt || new Date().toISOString();
      } else {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      storagePath = `hr/${tenantId}/${personId}/medcard_${Date.now()}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: inferContentType(file) });
      // TASK-996 — PATH-ONLY (no bearer URL in the member-readable people doc);
      // view via the HR-gated getComplianceDocBytes byte-stream callable.
      uploadedAt = new Date().toISOString();
      await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
        medCardDocumentUrl: null,
        medCardDocumentPath: storagePath,
        medCardDocumentUploadedAt: uploadedAt // TASK-647 — recency for getEffectiveMedCardDoc
      });
      }

      const person = HRState.people.find(p => p.id === personId);
      if (person) {
        person.medCardDocumentUrl = null; // TASK-996 — path-only
        person.medCardDocumentPath = storagePath;
        person.medCardDocumentUploadedAt = uploadedAt;
      }

      window.showToast?.('Medical card uploaded', 'success');
      await openEmployeeDetail(personId);
      switchDetailTab('compliance');
    } catch (err) {
      console.error('[HR] Med card upload error:', err);
      // TASK-9449 — a partner's server refusal (size/type/permission) is actionable.
      window.showToast?.((_hrPartnerClientTenantId() && err?.message) ? String(err.message).replace(/^internal:\s*/, '') : 'Upload failed', 'error');
    }
  };

  // Small delay helps mobile Safari trigger the file picker
  setTimeout(() => input.click(), 50);
}

async function removeMedCardDocument(personId) {
  const tenantId = HRState.tenantId;
  if (!tenantId || !personId) return;

  const person = HRState.people.find(p => p.id === personId);
  if (!person) return;

  if (!confirm('Remove medical card file?')) return;

  try {
    if (person.medCardDocumentPath) {
      await deleteObject(ref(storage, person.medCardDocumentPath)).catch(e => console.warn('[HR] Old med card delete failed:', e.message));
    }

    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
      medCardDocumentUrl: '',
      medCardDocumentPath: ''
    });

    person.medCardDocumentUrl = '';
    person.medCardDocumentPath = '';

    window.showToast?.('Medical card removed', 'success');
    await openEmployeeDetail(personId);
    switchDetailTab('compliance');
  } catch (err) {
    console.error('[HR] Med card remove error:', err);
    window.showToast?.('Failed to remove', 'error');
  }
}

// TASK-647-FF — clear a med card that lives in the CERT field (medCertPdfUrl),
// i.e. the effective doc whose source === 'medCertPdfUrl' (a driver SMS
// self-upload OR a Record-Exam attach). removeMedCardDocument only clears the
// medCardDocumentUrl side, so without this HR could SUPERSEDE a wrong cert-side
// doc but never CLEAR it (e.g. a driver uploaded the wrong scan). Mirrors
// removeMedCardDocument (confirm → updateDoc → re-render on Compliance tab).
// We clear the active cert pointer + its self-upload state; medExamHistory keeps
// its own per-exam certPdfUrl, so the recorded exam record is untouched.
async function removeMedCertPdf(personId) {
  const tenantId = HRState.tenantId;
  if (!tenantId || !personId) return;

  const person = HRState.people.find(p => p.id === personId);
  if (!person) return;

  if (!confirm('Remove medical card file?')) return;

  try {
    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
      medCertPdfUrl: null,
      medCertPdfUploadedAt: null,
      medCardSelfUploadedAt: null,
      medCardPendingReview: false
    });

    person.medCertPdfUrl = null;
    person.medCertPdfUploadedAt = null;
    person.medCardSelfUploadedAt = null;
    person.medCardPendingReview = false;

    window.showToast?.('Medical card removed', 'success');
    await openEmployeeDetail(personId);
    switchDetailTab('compliance');
  } catch (err) {
    console.error('[HR] Med cert remove error:', err);
    window.showToast?.('Failed to remove', 'error');
  }
}

async function updateMedCardExpiry(personId, dateValue) {
  const tenantId = HRState.tenantId;
  if (!tenantId || !personId) return;

  try {
    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
      medCardExpiryDate: dateValue || ''
    });

    const person = HRState.people.find(p => p.id === personId);
    if (person) {
      person.medCardExpiryDate = dateValue || '';
    }

    window.showToast?.(dateValue ? 'Med card expiry updated' : 'Med card expiry cleared', 'success');
  } catch (err) {
    console.error('[HR] Med card expiry update error:', err);
    window.showToast?.('Failed to update', 'error');
  }
}

// ============================================
// DRIVER'S LICENSE CRUD (HR owns this) — TASK-4605 Slice 2: credType-
// parameterized so ONE handler serves both 'license' (front, dlDocumentPath)
// and 'licenseBack' (back, dlDocumentBackPath) — never a per-side copy
// (Pitfall #8).
// ============================================
const _HR_DL_CRED_FIELDS = {
  license:     { pathField: 'dlDocumentPath',     urlField: 'dlDocumentUrl',     slug: 'dl' },
  licenseBack: { pathField: 'dlDocumentBackPath', urlField: 'dlDocumentBackUrl', slug: 'dlback' },
};
async function uploadDlDocument(personId, credType) {
  // TASK-4605 (integrated-diff COD) — defence in depth. The card now hides
  // these buttons for a member who cannot write the person doc, but HIDING A
  // BUTTON IS NOT A GATE: this function is reachable from the console and from
  // a stale render. `_hrCanWriteRoster()` mirrors the authoritative server gate
  // (requireRosterWriteAccess / firestore.rules people:update), and the other
  // uploads in this file already guard the same way.
  if (!_hrCanWriteRoster()) {
    window.showToast?.('You do not have permission to upload driver documents.', 'error');
    return;
  }
  const tenantId = HRState.tenantId;
  if (!tenantId || !personId) return;
  const fields = _HR_DL_CRED_FIELDS[credType] || _HR_DL_CRED_FIELDS.license;
  const isBack = fields === _HR_DL_CRED_FIELDS.licenseBack;

  let input = document.getElementById('hr-dl-file-input');
  if (!input) {
    input = document.createElement('input');
    input.type = 'file';
    input.id = 'hr-dl-file-input';
    // TASK-4605 — a licence back is often a phone photo; accept any image plus
    // PDF (was .pdf,.jpg,.jpeg,.png, which silently rejected HEIC).
    input.accept = 'image/*,application/pdf';
    input.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
    document.body.appendChild(input);
  }
  input.value = '';

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;

    // TASK-4609 — warn, do not block: the file is still stored and usable,
    // it just will not DISPLAY for whoever reviews it. Said at selection time,
    // while re-saving as JPEG is still cheap.
    const heicNote = window.PersonSections?.unviewableImageNote?.(file);


    if (file.size > 10 * 1024 * 1024) {
      window.showToast?.('File too large (max 10MB)', 'error');
      return;
    }

    try {
      window.showToast?.('Uploading...', 'info');
      let storagePath;
      if (_hrPartnerClientTenantId()) {
        // TASK-9449 — managing partner: server upload (create-only) + pointer + audit.
        storagePath = (await _hrPartnerUploadPersonDoc(personId, isBack ? 'licenseBack' : 'license', file)).storagePath;
      } else {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      storagePath = `hr/${tenantId}/${personId}/${fields.slug}_${Date.now()}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: inferContentType(file) });
      // TASK-996 — PATH-ONLY (no bearer URL in the member-readable people doc);
      // view via the HR-gated getComplianceDocBytes byte-stream callable.
      await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
        [fields.urlField]: null,
        [fields.pathField]: storagePath
      });
      }

      const person = HRState.people.find(p => p.id === personId);
      if (person) {
        person[fields.urlField] = null;
        person[fields.pathField] = storagePath;
      }

      window.showToast?.(isBack ? "Driver's license (back) uploaded" : "Driver's license uploaded", 'success');
      // See compliance.js — shown after success so it is not buried.
      if (heicNote) window.showToast?.(heicNote, 'warning');
      await openEmployeeDetail(personId);
      switchDetailTab('compliance');
    } catch (err) {
      console.error('[HR] DL upload error:', err);
      // TASK-9449 — a partner's server refusal (size/type/permission) is actionable.
      window.showToast?.((_hrPartnerClientTenantId() && err?.message) ? String(err.message).replace(/^internal:\s*/, '') : 'Upload failed', 'error');
    }
  };

  setTimeout(() => input.click(), 50);
}

async function removeDlDocument(personId, credType) {
  // TASK-4605 (integrated-diff COD) — defence in depth. The card now hides
  // these buttons for a member who cannot write the person doc, but HIDING A
  // BUTTON IS NOT A GATE: this function is reachable from the console and from
  // a stale render. `_hrCanWriteRoster()` mirrors the authoritative server gate
  // (requireRosterWriteAccess / firestore.rules people:update), and the other
  // uploads in this file already guard the same way.
  if (!_hrCanWriteRoster()) {
    window.showToast?.('You do not have permission to remove driver documents.', 'error');
    return;
  }
  const tenantId = HRState.tenantId;
  if (!tenantId || !personId) return;

  const person = HRState.people.find(p => p.id === personId);
  if (!person) return;

  const fields = _HR_DL_CRED_FIELDS[credType] || _HR_DL_CRED_FIELDS.license;
  const isBack = fields === _HR_DL_CRED_FIELDS.licenseBack;

  if (!confirm(isBack ? "Remove driver's license (back) file?" : "Remove driver's license file?")) return;

  try {
    if (person[fields.pathField]) {
      await deleteObject(ref(storage, person[fields.pathField])).catch(e => console.warn('[HR] Old DL delete failed:', e.message));
    }

    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), {
      [fields.urlField]: '',
      [fields.pathField]: ''
    });

    person[fields.urlField] = '';
    person[fields.pathField] = '';

    window.showToast?.(isBack ? "Driver's license (back) removed" : "Driver's license removed", 'success');
    await openEmployeeDetail(personId);
    switchDetailTab('compliance');
  } catch (err) {
    console.error('[HR] DL remove error:', err);
    window.showToast?.('Failed to remove', 'error');
  }
}

// ============================================
// ONBOARDING DOCUMENTS — generic upload/remove for the dedicated slots
// in the Compliance tab (I-9, W-4, direct deposit, etc.).
// ============================================

async function uploadOnboardingDoc(personId, itemId) {
  const tenantId = HRState.tenantId;
  if (!tenantId || !personId || !itemId) return;
  // Resolve from per-tenant config (TASK-221 Slice 1) — falls back to
  // ONBOARDING_DOC_SLOTS when no settings.hiringPacket is set.
  const slot = getHiringPacketItems().find(s => s.itemId === itemId)
    || ONBOARDING_DOC_SLOTS.find(s => s.itemId === itemId);
  if (!slot) {
    console.warn('[HR] uploadOnboardingDoc: unknown itemId', itemId);
    return;
  }

  const inputId = `hr-onboarding-doc-${itemId}-input`;
  let input = document.getElementById(inputId);
  if (!input) {
    input = document.createElement('input');
    input.type = 'file';
    input.id = inputId;
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
    document.body.appendChild(input);
  }
  input.value = '';

  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      window.showToast?.('File too large (max 10MB)', 'error');
      return;
    }
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'jpg', 'jpeg', 'png'].includes(ext)) {
      window.showToast?.('Only PDF, JPG, and PNG files allowed', 'error');
      return;
    }

    try {
      window.showToast?.('Uploading...', 'info');
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `hr/${tenantId}/${personId}/${itemId}_${Date.now()}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: inferContentType(file) });
      // TASK-1029 — PATH-ONLY: no getDownloadURL bearer `url` is minted or
      // persisted (slot fields + documents row). The slot "View" link and the
      // Documents tab both open via the HR-gated getComplianceDocBytes
      // byte-stream callable. fieldUrl is explicitly NULLED so a re-upload
      // kills any stale legacy bearer left on the person doc.

      // TASK-1381 🔴 (Blast) — when the slot is ALREADY filled, this upload is
      // an ADDITIONAL file (e.g. a direct-deposit split across two bank
      // accounts = two forms): SKIP the slot-triplet write entirely so the
      // pointer to file #1 is never clobbered. The extra file rides the
      // documents subcollection (tagged checklistItemId below) and renders in
      // the file list under the slot.
      const person = HRState.people.find(p => p.id === personId);
      const slotFilled = (() => {
        try {
          const cur = person ? getHiringPacketDocFor(person, slot) : null;
          return !!(cur && (cur.url || cur.storagePath));
        } catch { return false; }
      })();

      try {
        window.logAudit?.('hr', 'PERSON_DOCS_SAVE', {
          entityType: 'person',
          entityId: personId,
          source: 'hr-onboarding-slot-upload',
          slotItemId: itemId,
          redFlags: {
            docCountDrop: false,
            fileRefsRemoved: [],
            expiryChanged: false,
            slotLabel: slot.label || null,
            fileName: file.name,
            additionalFile: slotFilled
          }
        });
      } catch {}

      if (!slotFilled) {
        // Built-in items have hardcoded top-level fields (slot.fieldUrl etc.).
        // Custom items (no fieldUrl) write to the customPacketDocs.<itemId> map.
        const updates = {};
        const localUpdates = {};
        if (slot.fieldUrl) {
          updates[slot.fieldUrl] = null;
          updates[slot.fieldStoragePath] = storagePath;
          updates[slot.fieldUploadedAt] = Timestamp.now();
          Object.assign(localUpdates, updates);
        } else {
          const now = Timestamp.now();
          updates[`customPacketDocs.${itemId}`] = { url: null, storagePath, uploadedAt: now };
          // Local cache: build the same nested-map state.
          const existingMap = (person && person.customPacketDocs) || {};
          localUpdates.customPacketDocs = { ...existingMap, [itemId]: { url: null, storagePath, uploadedAt: now } };
        }
        await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), updates);
        if (person) Object.assign(person, localUpdates);
      }

      // Also write a record into the documents subcollection so the file shows
      // up in the generic Documents tab too. Single source of truth = Storage;
      // dedicated slot and Documents tab both render the same file.
      const userName = window.AppState?.userName || 'Unknown';
      await addDoc(
        collection(db, 'tenants', tenantId, 'people', personId, 'documents'),
        {
          fileName: file.name,
          storagePath, // TASK-1029 — path-only; view via getComplianceDocBytes
          mimeType: file.type,
          isPdf: file.type === 'application/pdf',
          sizeBytes: file.size,
          category: 'general',
          checklistItemId: itemId,
          uploadedAt: Timestamp.now(),
          uploadedBy: userName,
        }
      );
      HRState.employeeDocsLoaded = null;

      // Auto-check the matching New Hire Checklist item.
      try {
        const personChecklists = (HRState.hrChecklists || []).filter(c => c.employeeId === personId);
        const activeChecklist = personChecklists.find(c => (c.completionPercent || 0) < 100) || personChecklists[0];
        if (activeChecklist && !activeChecklist.items?.[itemId]?.completed) {
          await toggleChecklistItem(activeChecklist.id, itemId, true);
        }
      } catch (e) {
        console.warn('[HR] Auto-check from onboarding upload failed (non-fatal):', e);
      }

      window.showToast?.(slotFilled ? `File added to ${slot.label}` : `${slot.label} uploaded`, 'success');
      // Re-render only the Compliance tab in place — keeps the user where they are.
      // (buildComplianceTabHTML schedules the slot extra-file list refill.)
      const container = document.getElementById('hr-detail-tab-compliance');
      if (container && person) container.innerHTML = buildComplianceTabHTML(person);
    } catch (err) {
      console.error('[HR] Onboarding doc upload error:', err);
      window.showToast?.('Upload failed', 'error');
    }
  };

  setTimeout(() => input.click(), 50);
}

async function removeOnboardingDoc(personId, itemId) {
  const tenantId = HRState.tenantId;
  if (!tenantId || !personId || !itemId) return;
  // Resolve from per-tenant config (TASK-221 Slice 1) — falls back to
  // ONBOARDING_DOC_SLOTS when no settings.hiringPacket is set.
  const slot = getHiringPacketItems().find(s => s.itemId === itemId)
    || ONBOARDING_DOC_SLOTS.find(s => s.itemId === itemId);
  if (!slot) return;

  const person = HRState.people.find(p => p.id === personId);
  if (!person) return;
  if (!confirm(`Remove ${slot.label}?`)) return;
  // fix-1999-followups — the documents row that mirrors the slot file (same
  // storagePath, written by the slot upload so the file also shows on the
  // Documents tab) is removed through the deleteHrPersonDocument callable.
  // v1.15.1999 closed client deletes of people/{pid}/documents; the old direct
  // deleteDoc here was refused, only console.warn'd, and left a dead row.
  // Order, so a refusal at any step leaves the slot and its row consistent:
  //   1. find the mirror row(s) — a failed lookup stops before any write;
  //   2. clear the slot on the person doc — refused (e.g. a login without the
  //      roster module or dispatcher role, or a view-&-upload-only login) →
  //      nothing changed, the reason is shown;
  //   3. remove each mirror row via the callable — a refusal (module gate,
  //      upload-only, §391.51 retention lock) puts the slot back and shows the
  //      server's reason. Never swallowed.
  // The Storage object is KEPT: the callable retains files, and hr/ person
  // objects are create-only in storage.rules (the old deleteObject was refused).
  // Extra files (different storagePath) are untouched — they stay listed under
  // the slot after the primary is removed (TASK-1381).
  const failMsg = `Couldn't remove ${slot.label} — try again.`;
  try {
    // Built-in: clear the dedicated fields. Custom: null the
    // customPacketDocs.<itemId> map entry. `restore` is the exact inverse.
    const updates = {};
    const restore = {};
    const localUpdates = {};
    const localRestore = {};
    if (slot.fieldUrl) {
      for (const f of [slot.fieldUrl, slot.fieldStoragePath, slot.fieldUploadedAt]) {
        restore[f] = person[f] === undefined ? null : person[f];
      }
      updates[slot.fieldUrl] = '';
      updates[slot.fieldStoragePath] = '';
      updates[slot.fieldUploadedAt] = null;
      Object.assign(localUpdates, updates);
      Object.assign(localRestore, restore);
    } else {
      // Write null to the nested map entry. Render path treats null as "no
      // doc" (getHiringPacketDocFor returns url: null). Doesn't shrink the
      // map (vs. deleteField), but Slice 1 ships without the firebase-config.js
      // cache-buster bump that 31 imports would require. Cleanup pass later.
      const prevEntry = (person.customPacketDocs || {})[itemId];
      updates[`customPacketDocs.${itemId}`] = null;
      restore[`customPacketDocs.${itemId}`] = prevEntry === undefined ? null : prevEntry;
      const existingMap = { ...(person.customPacketDocs || {}) };
      existingMap[itemId] = null;
      localUpdates.customPacketDocs = existingMap;
      localRestore.customPacketDocs = { ...(person.customPacketDocs || {}) };
    }
    // Clear and restore are both conditional, in a transaction (COD Code Reviewer):
    // the clear only lands while the slot still names the file the user chose to
    // remove, and the restore only while the slot is still empty — a concurrent
    // upload is never overwritten either way.
    const pRef = doc(db, 'tenants', tenantId, 'people', personId);
    const slotNow = (data) => {
      const d = getHiringPacketDocFor(data || {}, slot) || {};
      return { storagePath: d.storagePath || '', url: d.url || '' };
    };
    const chosen = slotNow(person);
    // Everything above is synchronous: chosen / updates / restore come from ONE
    // snapshot of the slot taken at the tap, before the first await (COD r3).
    const storagePath = chosen.storagePath;
    let mirrorIds = [];
    if (storagePath) {
      try {
        const mirror = await getDocs(query(
          collection(db, 'tenants', tenantId, 'people', personId, 'documents'),
          where('storagePath', '==', storagePath)
        ));
        mirrorIds = mirror.docs.map(m => m.id);
      } catch (e) {
        console.error('[HR] slot mirror-row lookup failed:', e);
        window.showToast?.(failMsg, 'error');
        return;
      }
    }
    let slotChanged = false;
    await runTransaction(db, async (tx) => {
      const cur = slotNow((await tx.get(pRef)).data());
      if (cur.storagePath !== chosen.storagePath || cur.url !== chosen.url) { slotChanged = true; return; }
      tx.update(pRef, updates);
    });
    if (slotChanged) {
      window.showToast?.(`${slot.label} changed while you were removing it — refresh and try again.`, 'error');
      return;
    }
    Object.assign(person, localUpdates);

    for (const rowId of mirrorIds) {
      try {
        await _hrDeletePersonDocRow(tenantId, personId, rowId);
      } catch (e) {
        if (e && e.code === 'functions/not-found') continue; // already gone — the goal
        console.error('[HR] slot mirror-row delete refused:', e);
        HRState.employeeDocsLoaded = null;
        let restored = true;
        try {
          let refilled = false;
          await runTransaction(db, async (tx) => {
            const cur = slotNow((await tx.get(pRef)).data());
            if (cur.storagePath || cur.url) { refilled = true; return; } // a new upload owns the slot now
            tx.update(pRef, restore);
          });
          if (!refilled) Object.assign(person, localRestore);
        } catch (re) {
          restored = false;
          console.error('[HR] slot restore after refusal failed:', re);
          // The slot clear stands without its row removal — leave a trail (COD Domain).
          try {
            window.logAudit?.('hr', 'PERSON_DOCS_DELETE', {
              entityType: 'person', entityId: personId, source: 'hr-onboarding-slot-remove-partial', slotItemId: itemId,
              redFlags: { docCountDrop: true, fileRefsRemoved: storagePath ? [storagePath] : [], expiryChanged: false, slotLabel: slot.label || null, slotRestoreFailed: true },
            });
          } catch {}
        }
        const reason = _hrDocDeleteErrMsg(e, failMsg);
        window.showToast?.(restored ? reason : `${reason} (The slot was cleared but its Documents-tab row stays — refresh and check.)`, 'error');
        const c = document.getElementById('hr-detail-tab-compliance');
        if (c) c.innerHTML = buildComplianceTabHTML(person);
        return;
      }
    }
    if (mirrorIds.length) HRState.employeeDocsLoaded = null;

    try {
      window.logAudit?.('hr', 'PERSON_DOCS_DELETE', {
        entityType: 'person',
        entityId: personId,
        source: 'hr-onboarding-slot-remove',
        slotItemId: itemId,
        redFlags: {
          docCountDrop: true,
          fileRefsRemoved: storagePath ? [storagePath] : [],
          expiryChanged: false,
          slotLabel: slot.label || null
        }
      });
    } catch {}

    // Note: we do NOT auto-uncheck the checklist item on remove. If someone
    // reuploads later the checkbox stays consistent; if they're deleting in
    // error, they don't lose their checklist progress.

    window.showToast?.(`${slot.label} removed`, 'success');
    // Refresh just the Compliance tab — don't bounce out to the default tab.
    const container = document.getElementById('hr-detail-tab-compliance');
    if (container) container.innerHTML = buildComplianceTabHTML(person);
  } catch (err) {
    console.error('[HR] Onboarding doc remove error:', err);
    // The person-doc write is the step a firestore rule can refuse — say why.
    window.showToast?.(err && err.code === 'permission-denied'
      ? `Your login can't change this person's record, so ${slot.label} was not removed. Ask an admin.`
      : failMsg, 'error');
  }
}

// ============================================
// TASK-1381 — MULTI-FILE PACKET SLOTS
// A slot's triplet on the people doc stays the PRIMARY file; any additional
// files (e.g. an employee splitting direct deposit across two bank accounts =
// two forms) live as (a) documents-subcollection rows tagged checklistItemId
// (carrier-side "add file" upload) and (b) packetDocs subcollection docs
// (driver self-serve link uploads — CF-written, HR-readable per rules). This
// fills the hr-slot-extra-files-* containers rendered under each slot row.
// All files open path-only via the HR-gated getComplianceDocBytes byte-stream
// (openDqDoc). packetDocs rows are client-UNDELETABLE by rules (write:false),
// so they get View only.
// ============================================

async function _populateOnboardingSlotFiles(personId) {
  const containers = document.querySelectorAll('[id^="hr-slot-extra-files-"]');
  if (!containers.length) return;
  const tenantId = HRState.tenantId;
  if (!tenantId || !personId) return;

  // Documents subcollection (lazy-loaded behind the employeeDocsLoaded gate —
  // the Compliance tab must trigger the load itself; loadEmployeeDocuments is
  // null-safe when the Documents-tab container isn't mounted).
  if (HRState.employeeDocsLoaded !== personId) {
    try { await loadEmployeeDocuments(personId); }
    catch (e) { console.warn('[HR] slot files: documents load failed (non-fatal):', e); }
  }
  const docRows = HRState.employeeDocsLoaded === personId
    ? (HRState.employeeDocuments || []).filter(d => d.checklistItemId)
    : [];

  // Self-serve packetDocs (driver link uploads). Cached per person.
  let packetRows = [];
  if (HRState.packetDocsFor === personId) {
    packetRows = HRState.packetDocs || [];
  } else {
    try {
      const snap = await getDocs(collection(db, 'tenants', tenantId, 'people', personId, 'packetDocs'));
      packetRows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      HRState.packetDocs = packetRows;
      HRState.packetDocsFor = personId;
    } catch (e) {
      // Non-fatal (e.g. a viewer without packetDocs read) — slots still render.
      console.warn('[HR] slot files: packetDocs load failed (non-fatal):', e);
    }
  }

  const person = (HRState.people || []).find(p => p.id === personId);
  const tsMs = (t) => {
    try { return t?.toDate?.() ? t.toDate().getTime() : (t ? new Date(t).getTime() || 0 : 0); } catch { return 0; }
  };

  containers.forEach(el => {
    if (el.getAttribute('data-person-id') !== personId) return;
    const itemId = el.getAttribute('data-slot-item') || '';
    if (!itemId) return;
    const slot = getHiringPacketItems().find(s => s.itemId === itemId)
      || ONBOARDING_DOC_SLOTS.find(s => s.itemId === itemId);
    // The slot row already shows the primary (the triplet) — exclude its
    // storagePath from the list so it isn't shown twice.
    let primaryPath = '';
    try { primaryPath = (person && slot) ? (getHiringPacketDocFor(person, slot).storagePath || '') : ''; } catch {}

    const rows = [];
    docRows
      .filter(d => d.checklistItemId === itemId && (d.storagePath || '') !== primaryPath)
      .forEach(d => rows.push({
        fileName: d.fileName || 'Document',
        storagePath: d.storagePath || '',
        uploadedAt: d.uploadedAt,
        docId: d.id,
      }));
    packetRows
      .filter(x => (x.itemId || x.id) === itemId)
      .forEach(x => rows.push({
        fileName: x.fileName || (x.storagePath ? String(x.storagePath).split('/').pop() : 'Uploaded file'),
        storagePath: x.storagePath || '',
        uploadedAt: x.uploadedAt,
        viaLink: true,
      }));

    if (!rows.length) { el.innerHTML = ''; el.style.display = 'none'; return; }
    rows.sort((a, b) => tsMs(a.uploadedAt) - tsMs(b.uploadedAt));

    el.innerHTML = rows.map(r => {
      const dateStr = r.uploadedAt ? (formatDateShort(r.uploadedAt?.toDate?.() || r.uploadedAt) || '') : '';
      const view = r.storagePath
        ? `<a href="#" data-doc-path="${_dqEscAttr(r.storagePath)}" data-doc-name="${_dqEscAttr(r.fileName)}" onclick="event.preventDefault(); window.HRModule.openDqDoc(this)" style="font-size:0.74rem;color:var(--brand-blue);display:inline-flex;align-items:center;gap:3px;"><span class="material-icons" style="font-size:13px;">visibility</span> View</a>`
        : '';
      const del = r.docId
        ? `<button class="btn-icon" onclick="window.HRModule.deleteSlotExtraFile('${escJsAttr(personId)}', '${escJsAttr(r.docId)}')" title="Delete ${_dqEscAttr(r.fileName)}"><span class="material-icons" style="font-size:13px;color:#DC2626;">delete</span></button>`
        : '';
      return `
        <div style="display:flex;align-items:center;gap:8px;padding:3px 0 3px 24px;">
          <span class="material-icons" style="font-size:13px;color:var(--text-muted);">description</span>
          <span style="flex:1;min-width:0;font-size:0.74rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(r.fileName)}${r.viaLink ? ` <span style="color:var(--text-muted);">· sent by employee</span>` : ''}</span>
          ${dateStr ? `<span style="font-size:0.7rem;color:var(--text-muted);">${escapeHtml(dateStr)}</span>` : ''}
          ${view}
          ${del}
        </div>`;
    }).join('');
    el.style.cssText = 'display:block;padding-bottom:6px;';
  });
}

// Delete an extra (documents-row) file listed under a slot, then refresh the
// slot lists. Reuses deleteEmployeeDocument (confirm + the deleteHrPersonDocument
// callable: row + credential pointer + version void + audit; file retained).
async function deleteSlotExtraFile(personId, docId) {
  await deleteEmployeeDocument(personId, docId);
  try { await _populateOnboardingSlotFiles(personId); } catch {}
}

// ============================================
// TASK-221 Slice 2 — HIRING PACKET TEMPLATES
// Each packet item can optionally carry a blank-PDF template. Carrier
// uploads once in Settings; new hires see "Download blank" next to
// their Upload button. Storage reuses the existing /tenants/{tid}/hr/
// company-documents/ path (no new rule needed). Settings array on
// tenant.settings.hiringPacket gets templateUrl/templateStoragePath/
// templateUploadedAt/templateFileName per item.
// ============================================

function _hpIsAdmin() {
  // TASK-637 — the hiring-packet TEMPLATE is an HR-module feature. Module access
  // = FULL access within the module (Chris 2026-06-17): an hr-module STAFF member
  // can manage it, not just owner/admin. This used to be a role carve-out
  // ("Admin only") that trapped hr-staff. The tenant-doc write is now routed
  // through the requireHrAccess-gated updateHiringPacketTemplate callable
  // (_hpPersist + uploadHiringPacketTemplate/removeHiringPacketTemplate), so
  // opening this UI gate to hr-module holders is safe — they can't touch the
  // tenant doc's billing fields. Delegates to the shared app.js predicate.
  // TASK-932 — the founder-tenant arm is gone: it showed this UI to every member
  // of that tenant while the requireHrAccess callable refused the save.
  return typeof window.hrCanManageModule === 'function'
    ? window.hrCanManageModule()
    : (() => {
        const ma = window.AppState?.memberAccess;
        if (!ma) return true;
        if (['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
        return ma.role === 'owner' || ma.role === 'admin';
      })();
}

async function uploadHiringPacketTemplate(itemId) {
  if (!_hpIsAdmin()) { window.showToast?.('Admin only', 'error'); return; }
  const tenantId = HRState.tenantId || window.AppState?.tenant;
  if (!tenantId || !itemId) return;

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.pdf,application/pdf';
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    // Enforce extension AND MIME (storage rule only checks size on this
    // path, per Code Reviewer's Slice 2 review).
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== 'pdf') { window.showToast?.('PDF only', 'error'); return; }
    if (file.type && file.type !== 'application/pdf') {
      window.showToast?.('PDF only', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      window.showToast?.('Template too large (max 10MB)', 'error');
      return;
    }
    try {
      window.showToast?.('Uploading template...', 'info');
      const ts = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      // Path: reuse existing company-documents bucket so no new storage
      // rule is needed. Template_<itemId>_<ts> prefix disambiguates from
      // other company docs and prevents collisions across items.
      const storagePath = `tenants/${tenantId}/hr/company-documents/template_${itemId}_${ts}_${safeName}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: 'application/pdf' });
      const url = await getDownloadURL(storageRef);

      // Update the item in tenant.settings.hiringPacket array.
      const tenantRef = doc(db, 'tenants', tenantId);
      const snap = await getDoc(tenantRef);
      const current = (snap.exists() && Array.isArray(snap.data()?.settings?.hiringPacket))
        ? snap.data().settings.hiringPacket
        : [];
      // If the array is empty (tenant hasn't seeded yet), seed from
      // built-ins first, then stamp the template on the matching item.
      const baseArr = current.length ? current : (window.AppState?.settings?.hiringPacket || []);
      const updated = (baseArr.length ? baseArr : []).slice();
      const idx = updated.findIndex(i => i?.itemId === itemId);
      const stamp = {
        templateUrl: url,
        templateStoragePath: storagePath,
        templateUploadedAt: new Date().toISOString(),
        templateFileName: file.name,
      };
      if (idx >= 0) {
        updated[idx] = { ...updated[idx], ...stamp };
      } else {
        // Item not yet in stored array — settings not yet seeded. Seed
        // built-ins from window.AppState (already populated by app.js
        // boot fallback) then stamp the template.
        // Defensive: if window.AppState's hiringPacket is also empty we
        // can't safely seed — bail with a useful error.
        const fallback = window.AppState?.settings?.hiringPacket;
        if (Array.isArray(fallback) && fallback.length) {
          const seeded = fallback.slice();
          const fIdx = seeded.findIndex(i => i?.itemId === itemId);
          if (fIdx >= 0) seeded[fIdx] = { ...seeded[fIdx], ...stamp };
          updated.length = 0;
          updated.push(...seeded);
        } else {
          window.showToast?.('Open the Hiring Packet modal once before uploading templates so the list is initialized', 'error');
          return;
        }
      }
      // TASK-637 — route the tenant-doc write through the requireHrAccess-gated
      // callable (via app.js _hpPersist) instead of a direct updateDoc, so an
      // hr-module STAFF member (who can't write tenants/{tid} directly) can
      // upload templates without hitting a raw permission error. _hpPersist
      // mirrors AppState.settings.hiringPacket on success.
      const okUp = await window._hpPersist?.(updated);
      if (okUp === false) return; // _hpPersist already toasted the failure
      window.showToast?.('Template uploaded', 'success');
      // Refresh the Hiring Packet modal if open.
      window._hpRenderList?.();
    } catch (err) {
      console.error('[HR] Template upload error:', err);
      window.showToast?.(`Upload failed: ${err.message || 'unknown'}`, 'error');
    }
  };
  input.click();
}

async function removeHiringPacketTemplate(itemId) {
  if (!_hpIsAdmin()) { window.showToast?.('Admin only', 'error'); return; }
  const tenantId = HRState.tenantId || window.AppState?.tenant;
  if (!tenantId || !itemId) return;

  const current = window.AppState?.settings?.hiringPacket;
  if (!Array.isArray(current)) { return; }
  const item = current.find(i => i?.itemId === itemId);
  if (!item || !item.templateUrl) { return; }
  if (!confirm(`Remove template "${item.templateFileName || item.label || itemId}"?`)) return;

  // Reverse delete order (per Edge Case Hunter's Slice 2 review):
  // clear URL fields in Firestore FIRST, then attempt storage delete.
  // If storage delete fails, we leave an orphan blob — small storage
  // cost, easy to clean up later — but we don't end up with a broken
  // URL pointing at a gone file.
  try {
    const updated = current.map(i => i?.itemId === itemId
      ? { ...i, templateUrl: '', templateStoragePath: '', templateUploadedAt: null, templateFileName: '' }
      : i);
    // TASK-637 — route through the requireHrAccess-gated callable (via _hpPersist)
    // so an hr-module STAFF member can clear a template without a raw tenant-doc
    // permission error. _hpPersist mirrors AppState.settings.hiringPacket.
    const okRm = await window._hpPersist?.(updated);
    if (okRm === false) return; // _hpPersist already toasted the failure

    // Best-effort storage delete (don't block on failure).
    if (item.templateStoragePath) {
      try {
        await deleteObject(ref(storage, item.templateStoragePath));
      } catch (e) {
        console.warn('[HR] Template storage delete failed (orphan left):', e.message);
      }
    }

    window.showToast?.('Template removed', 'success');
    window._hpRenderList?.();
  } catch (err) {
    console.error('[HR] Template remove error:', err);
    window.showToast?.(`Remove failed: ${err.message || 'unknown'}`, 'error');
  }
}

// ============================================
// PRINT ISNET ROSTER
// ============================================

async function printISNetRoster() {
  // Tax PII (SSN) lives in a restricted subcollection — load for every person
  // before rendering the print template.
  await loadAllSecureTax();

  const people = HRState.people.filter(p => p.active !== false).sort((a, b) => {
    const aLast = splitNameISNet(a.name).last.toLowerCase();
    const bLast = splitNameISNet(b.name).last.toLowerCase();
    return aLast.localeCompare(bLast);
  });

  const companyName = window.AppState?.companyName || 'Company';
  const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Sheet 1: Employee Profile (22 columns)
  const sheet1Rows = people.map(emp => {
    const { first, last } = splitNameISNet(emp.name);
    const hireDate = emp.hireDate ? formatDateShort(typeof emp.hireDate?.toDate === 'function' ? emp.hireDate.toDate().toISOString().split('T')[0] : emp.hireDate) : '';
    return `<tr>
      <td>${escapeHtml(last)}</td>
      <td>${escapeHtml(first)}</td>
      <td>${escapeHtml(emp.isnetNickname || '')}</td>
      <td>${escapeHtml(emp.secureTax?.ssn || '')}</td>
      <td>${formatDateShort(emp.dob)}</td>
      <td>${escapeHtml(emp.employeeId || '')}</td>
      <td>${escapeHtml(getISNetJobTitle(emp))}</td>
      <td>${escapeHtml(emp.isnetEmployeeType || '')}</td>
      <td>${escapeHtml(emp.goToField || '')}</td>
      <td>${hireDate}</td>
      <td>${escapeHtml(emp.dlState || '')}</td>
      <td>${escapeHtml(emp.dlNumber || '')}</td>
      <td>${escapeHtml(emp.cdlClass || '')}</td>
      <td>${escapeHtml(emp.cdlEndorsements || '')}</td>
      <td>${formatDateShort(emp.cdlIssueDate)}</td>
      <td>${formatDateShort(emp.cdlExpiryDate)}</td>
      <td>${escapeHtml(emp.phone || '')}</td>
      <td>${escapeHtml(emp.email || '')}</td>
      <td>${escapeHtml(emp.safetyTrainingVerified || '')}</td>
      <td>${escapeHtml(emp.specializedCertification || '')}</td>
      <td>${escapeHtml(emp.supervisorRole || '')}</td>
      <td>${escapeHtml(emp.reasonableSuspicionTraining || '')}</td>
    </tr>`;
  }).join('');

  // Sheet 2: Training & Certifications (9 columns) — flattened
  const allTrainings = [];
  people.forEach(emp => {
    (emp.isnetTrainings || []).forEach(t => {
      const { first, last } = splitNameISNet(emp.name);
      allTrainings.push({ first, last, ...t });
    });
  });

  const sheet2Rows = allTrainings.map(t => `<tr>
    <td>${escapeHtml(t.last)}</td>
    <td>${escapeHtml(t.first)}</td>
    <td>${escapeHtml(t.trainingName || '')}</td>
    <td>${escapeHtml(t.certificationName || '')}</td>
    <td>${escapeHtml(t.organization || '')}</td>
    <td>${formatDateShort(t.issueDate)}</td>
    <td>${formatDateShort(t.expiryDate)}</td>
    <td>${escapeHtml(t.certCourseNumber || '')}</td>
    <td>${t.hasScannedCopy ? 'YES' : 'NO'}</td>
  </tr>`).join('');

  const printHtml = `<!DOCTYPE html>
<html><head><title>ISNet Roster - ${escapeHtml(companyName)}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 20px; color: #1e293b; }
  h1 { font-size: 1.3rem; margin: 0 0 4px; }
  h2 { font-size: 1.1rem; margin: 24px 0 8px; color: #334155; }
  .subtitle { color: #64748b; font-size: 0.85rem; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 0.65rem; }
  th { background: #f1f5f9; color: #334155; font-weight: 600; text-align: left; padding: 5px 4px; border-bottom: 2px solid #cbd5e1; white-space: nowrap; }
  td { padding: 4px; border-bottom: 1px solid #e2e8f0; vertical-align: top; white-space: nowrap; }
  tr:nth-child(even) td { background: #f8fafc; }
  .footer { margin-top: 16px; font-size: 0.7rem; color: #94a3b8; text-align: right; }
  .print-btn { position: fixed; top: 12px; right: 12px; padding: 8px 20px; background: #3b82f6; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 0.9rem; z-index: 100; }
  .print-btn:hover { background: #2563eb; }
  .page-break { page-break-before: always; }
  @media print {
    .print-btn { display: none; }
    body { padding: 10px; }
    table { font-size: 0.6rem; }
  }
  @page { size: landscape; margin: 0.5in; }
</style></head>
<body>
  <button class="print-btn" onclick="window.print()">Print / Save PDF</button>

  <h1>${escapeHtml(companyName)}</h1>
  <div class="subtitle">ISNet Employee Profile &bull; ${dateStr} &bull; ${people.length} employee${people.length !== 1 ? 's' : ''}</div>

  <table>
    <thead><tr>
      <th>LAST</th><th>FIRST</th><th>ISNETWORLD #</th><th>SSN</th><th>DOB</th><th>EMP ID</th><th>JOB TITLE</th>
      <th>EMP TYPE</th><th>GO TO FIELD</th><th>HIRE DATE</th><th>DL STATE</th><th>DL #</th>
      <th>CDL CLASS</th><th>ENDORSE</th><th>CDL ISSUE</th><th>CDL EXPIRY</th><th>PHONE</th><th>EMAIL</th>
      <th>SAFETY TRNG</th><th>SPEC CERT</th><th>SUPERVISOR</th><th>SUSP TRNG</th>
    </tr></thead>
    <tbody>${sheet1Rows}</tbody>
  </table>

  <div class="page-break"></div>

  <h1>${escapeHtml(companyName)}</h1>
  <div class="subtitle">ISNet Training & Certifications &bull; ${dateStr} &bull; ${allTrainings.length} record${allTrainings.length !== 1 ? 's' : ''}</div>

  <table>
    <thead><tr>
      <th>LAST</th><th>FIRST</th><th>TRAINING NAME</th><th>CERTIFICATION</th><th>ORGANIZATION</th>
      <th>ISSUE DATE</th><th>EXPIRY DATE</th><th>CERT/COURSE #</th><th>SCAN</th>
    </tr></thead>
    <tbody>${sheet2Rows.length > 0 ? sheet2Rows : '<tr><td colspan="9" style="text-align:center;padding:20px;color:#94a3b8;">No training records entered yet</td></tr>'}</tbody>
  </table>

  <div class="footer">Generated from DispatchDeck &bull; ${dateStr}</div>
</body></html>`;

  const w = window.open('', '_blank');
  if (w) {
    w.document.write(printHtml);
    w.document.close();
  }
}

// ============================================
// TASK-513 — SAFETY ROSTER MATRIX (required trainings × employees)
// ============================================

// TASK-723 — when a cert save originates from the interactive matrix panel
// (openMatrixCertUpload), this holds { personId, itemId } so saveIsnetTraining
// re-renders the panel in place instead of bouncing to the employee detail view.
// Set right before the modal opens, consumed + cleared on save / modal close.
let _matrixSaveContext = null;

// Resolve the effective state of ONE required training for ONE person.
// Returns { state: 'missing'|'valid'|'expiring'|'expired'|'noexpiry', date, days }.
//
// Edge cases handled (per spec):
//  - TASK-897 — the cert's explicit expiryDate is the SOURCE OF TRUTH for expiry.
//  - recurrenceMonths is only a SILENT FALLBACK for legacy certs that have an
//    issueDate but no expiryDate; it no longer short-circuits a real expiryDate.
//  - No expiry derivable (no expiryDate, and no issueDate+recurrence) → no-expiry,
//    render WITHOUT a date, never expires ("On file").
// The CURRENT cert among one requirement's entries: most recent by issueDate
// (stable sort, so a tie / blank dates keep the first). One rule for the matrix
// cell and the matrix pencil (openMatrixCertUpload, TASK-4573b).
function _currentTrainingEntry(entries) {
  return entries.slice().sort((a, b) => String(b.issueDate || '').localeCompare(String(a.issueDate || '')))[0];
}

function computeRequiredTrainingState(person, configItem) {
  // TASK-1353 — position-tier scoping: a training scoped (appliesTo) to other
  // tiers is 'na' for this person — evaluated-and-not-applicable, resolved
  // BEFORE the per-person exemption. Excluded from rollups like 'exempt'.
  if (!window.DqMatrix.trainingAppliesTo(person, configItem)) {
    return { state: 'na', date: null, days: null };
  }
  // TASK-919 — per-employee "Not Applicable" exemption short-circuits ALL expiry
  // logic (office/admin staff exempt from field-only certs). Absent map = not exempt.
  const exemptions = person.safetyTrainingExemptions || {};
  if (exemptions[configItem.itemId]) {
    return { state: 'exempt', reason: exemptions[configItem.itemId].reason, date: null, days: null };
  }
  const recurrence = Number(configItem?.recurrenceMonths) || 0;
  const entries = (person.isnetTrainings || []).filter(t => t && t.requiredTrainingId === configItem.itemId);
  if (entries.length === 0) return { state: 'missing', date: null, days: null };

  const entry = _currentTrainingEntry(entries);

  // Cert's explicit expiryDate wins; recurrence only fills in when there's no
  // expiryDate on file but there is an issueDate + a configured recurrence.
  let expiry = entry.expiryDate || null;
  if (!expiry && entry.issueDate && recurrence > 0) {
    const d = new Date(entry.issueDate);
    if (!isNaN(d.getTime())) {
      d.setMonth(d.getMonth() + recurrence);
      expiry = d.toISOString().split('T')[0];
    }
  }
  if (!expiry) return { state: 'noexpiry', date: null, days: null };

  const days = daysUntilExpiry(expiry);
  let state = 'valid';
  if (days !== null && days < 0) state = 'expired';
  else if (days !== null && days <= 30) state = 'expiring';
  return { state, date: expiry, days };
}

// TASK-723 — Safety Roster Matrix is now an IN-APP interactive full-screen panel
// (rendered in SPA context, NOT a detached popup) so "Missing"/Expired cells can be
// clicked to upload a cert via the existing showIsnetTrainingModal → saveIsnetTraining
// → uploadSafetyCert flow. The panel's "Print / Save PDF" button spawns the EXISTING
// read-only detached popup (_printSafetyRosterMatrixPdf) for the actual PDF — so the
// detached-popup-can't-host-auth constraint never blocks the upload path.
//
// SECURITY (folded from COD):
//  - Clickable affordance + openMatrixCertUpload BOTH gate on _canCarrierEditTrainings()
//    (partner-managed carriers get read-only cells, no upload — must-fix #3).
//  - Cells carry personId/itemId via data-attrs read by a delegated listener (NOT inline
//    onclick string interpolation — itemIds are user-defined → XSS, must-fix #4).
//  - All user-controlled strings are escapeHtml'd before innerHTML injection (must-fix #4).
//  - openMatrixCertUpload validates personId ∈ HRState.people (tenant guard, must-fix #5).

function _safetyMatrixCellHtml(emp, configItem, canEdit) {
  const st = computeRequiredTrainingState(emp, configItem);
  // A cell is "actionable" (upload-able) when it's missing/expired AND the carrier
  // is allowed to edit trainings. Valid/expiring/on-file cells stay static.
  const needsCert = (st.state === 'missing' || st.state === 'expired');
  const clickable = needsCert && canEdit;
  const pid = escapeHtml(String(emp.id || ''));
  const itemId = escapeHtml(String(configItem.itemId || ''));
  const cue = clickable
    ? `<div style="font-weight:400;font-size:0.62rem;color:#DC2626;margin-top:2px;display:flex;align-items:center;gap:2px;justify-content:flex-start;"><span class="material-icons" style="font-size:12px;">add_circle</span> Click to Upload</div>`
    : '';
  let inner, bg, color;
  switch (st.state) {
    case 'missing':  bg = '#FEF2F2'; color = '#DC2626'; inner = `<span style="font-weight:600;">Missing</span>${cue}`; break;
    case 'expired':  bg = '#FEF2F2'; color = '#DC2626'; inner = `<span style="font-weight:600;">Expired ${st.date ? formatDateShort(st.date) : ''}</span>${cue}`; break;
    case 'expiring': bg = '#FFFBEB'; color = '#B45309'; inner = `<span style="font-weight:600;">${st.days}d (${st.date ? formatDateShort(st.date) : ''})</span>`; break;
    case 'noexpiry': bg = '#F0FDF4'; color = '#059669'; inner = 'On file'; break;
    case 'valid':    bg = '#F0FDF4'; color = '#059669'; inner = `Valid${st.date ? ' (' + formatDateShort(st.date) + ')' : ''}`; break;
    case 'exempt':   bg = '#F1F5F9'; color = 'var(--text-secondary)';
      inner = `<span title="${escapeHtml(st.reason || 'Exempt')}" style="font-weight:500;">N/A</span>`; break;
    case 'na':       bg = '#F1F5F9'; color = 'var(--text-secondary)';
      // TASK-1353 — tier-scoped: visible grey N/A (never silently dropped), not clickable.
      inner = `<span title="Not required for this role — training scoped to other position types" style="font-weight:500;">N/A</span>`; break;
    default:         bg = ''; color = ''; inner = '—';
  }
  const clickAttrs = clickable
    ? ` data-matrix-upload="1" data-matrix-pid="${pid}" data-matrix-itemid="${itemId}" role="button" tabindex="0" title="Upload certificate"`
    : '';
  const cursor = clickable ? 'cursor:pointer;' : '';
  return `<td class="${clickable ? 'safety-matrix-cell-clickable' : ''}" style="background:${bg};color:${color};padding:5px;border:1px solid #e2e8f0;vertical-align:top;white-space:nowrap;${cursor}"${clickAttrs}>${inner}</td>`;
}

// Build the interactive matrix body (shared by initial render + in-place re-render).
function _renderSafetyMatrixPanelBody() {
  const body = document.getElementById('safety-matrix-panel-body');
  if (!body) return;
  const canEdit = _canCarrierEditTrainings();
  const config = _getSafetyTrainingsList();

  // Must-fix #6 (COD-flagged hydration race) — in the Compliance-module path,
  // initSafetyStandalone subscribes people asynchronously. If the panel opens
  // before the first snapshot lands, show a loading state and re-render once
  // people hydrate (poll a few times; the onSnapshot sets HRState.people).
  if ((!HRState.people || HRState.people.length === 0) && HRState.peopleLoaded !== true) {
    body.innerHTML = `<div style="display:flex;align-items:center;justify-content:center;gap:8px;padding:40px;color:var(--text-secondary);"><span class="btn-spinner"></span> Loading employees…</div>`;
    const start = Date.now();
    const poll = setInterval(() => {
      const stillOpen = document.getElementById('safety-matrix-panel-body');
      if (!stillOpen) { clearInterval(poll); return; } // panel closed
      if ((HRState.people && HRState.people.length > 0) || HRState.peopleLoaded === true) {
        clearInterval(poll);
        _renderSafetyMatrixPanelBody();
      } else if (Date.now() - start > 8000) {
        clearInterval(poll);
        _renderSafetyMatrixPanelBody(); // give up waiting → render empty state
      }
    }, 300);
    return;
  }

  const people = (HRState.people || []).filter(p => p.active !== false).sort((a, b) => {
    const aLast = splitNameISNet(a.name).last.toLowerCase();
    const bLast = splitNameISNet(b.name).last.toLowerCase();
    return aLast.localeCompare(bLast);
  });
  const dateStr = formatDateShort(new Date().toISOString().split('T')[0]);

  const headerCols = config.map(c => {
    const recur = Number(c.recurrenceMonths) || 0;
    const sub = recur > 0 ? (recur % 12 === 0 ? `${recur/12}yr` : `${recur}mo`) : 'one-time';
    return `<th style="background:#f1f5f9;color:#334155;font-weight:600;text-align:left;padding:6px 5px;border:1px solid #cbd5e1;vertical-align:top;white-space:nowrap;">${escapeHtml(c.label)}<div style="font-weight:400;font-size:0.85em;color:#94a3b8;">${sub}</div></th>`;
  }).join('');

  const bodyRows = people.map(emp => {
    const { first, last } = splitNameISNet(emp.name);
    const cells = config.map(c => _safetyMatrixCellHtml(emp, c, canEdit)).join('');
    return `<tr><td style="white-space:nowrap;font-weight:600;padding:5px;border:1px solid #e2e8f0;position:sticky;left:0;background:#fff;z-index:1;">${escapeHtml(last)}, ${escapeHtml(first)}</td>${cells}</tr>`;
  }).join('');

  body.innerHTML = `
    <div style="font-size:0.78rem;color:var(--text-secondary);margin-bottom:10px;">
      ${dateStr} &bull; ${people.length} employee${people.length !== 1 ? 's' : ''} &bull; ${config.length} required training${config.length !== 1 ? 's' : ''}
      ${canEdit ? `&bull; <span style="color:#DC2626;font-weight:600;">Click a "Missing" / Expired cell to upload that cert.</span>` : ''}
    </div>
    <div style="overflow-x:auto;-webkit-overflow-scrolling:touch;border:1px solid var(--border-light);border-radius:8px;">
      <table style="width:100%;border-collapse:collapse;font-size:0.72rem;color:#1e293b;">
        <thead><tr><th style="background:#f1f5f9;color:#334155;font-weight:600;text-align:left;padding:6px 5px;border:1px solid #cbd5e1;position:sticky;left:0;z-index:2;">EMPLOYEE</th>${headerCols}</tr></thead>
        <tbody>${bodyRows || `<tr><td colspan="${config.length + 1}" style="text-align:center;padding:20px;color:#94a3b8;">No active employees</td></tr>`}</tbody>
      </table>
    </div>
    <div style="margin-top:14px;font-size:0.72rem;color:#64748b;display:flex;gap:14px;flex-wrap:wrap;">
      <span style="display:inline-flex;align-items:center;gap:4px;"><span style="width:12px;height:12px;border-radius:3px;display:inline-block;background:#F0FDF4;border:1px solid #059669;"></span> Valid / On file</span>
      <span style="display:inline-flex;align-items:center;gap:4px;"><span style="width:12px;height:12px;border-radius:3px;display:inline-block;background:#FFFBEB;border:1px solid #B45309;"></span> Expiring (≤30d)</span>
      <span style="display:inline-flex;align-items:center;gap:4px;"><span style="width:12px;height:12px;border-radius:3px;display:inline-block;background:#FEF2F2;border:1px solid #DC2626;"></span> Missing / Expired</span>
      <span style="display:inline-flex;align-items:center;gap:4px;"><span style="width:12px;height:12px;border-radius:3px;display:inline-block;background:#F1F5F9;border:1px solid #cbd5e1;"></span> N/A (exempt / not required for role)</span>
    </div>`;
}

// Entry point (button: "Safety Roster Matrix") — opens the interactive panel.
async function printSafetyRosterMatrix() {
  const config = _getSafetyTrainingsList(); // active required trainings
  if (config.length === 0) {
    window.showToast?.('Add required trainings first (Manage Required Trainings).', 'error');
    return;
  }
  // Must-fix #6 — Compliance-module path may not have hydrated people yet.
  // initSafetyStandalone subscribes people when empty; the re-render after a
  // save reads the latest HRState.people, and the snapshot above keeps it fresh.
  // TASK-9512 — HRState.tenant is never assigned (only tenantId), so this printed
  // "Company" on every tenant. Use the shared print-brand helper (AppState.tenantData).
  const companyName = _hrSafetyMatrixCompanyName();

  let panel = document.getElementById('safety-matrix-panel');
  if (!panel) {
    panel = document.createElement('div');
    panel.id = 'safety-matrix-panel';
    // z-index 900 sits BELOW the .modal layer (z-index 1000) so the cert-upload
    // modal renders ON TOP of the panel, not behind it.
    panel.style.cssText = 'position:fixed;inset:0;z-index:900;background:#fff;display:flex;flex-direction:column;overflow:hidden;';
    document.body.appendChild(panel);
    // Delegated click listener (must-fix #4 — no inline onclick w/ user-defined ids).
    panel.addEventListener('click', _onSafetyMatrixPanelClick);
    panel.addEventListener('keydown', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.target?.closest?.('[data-matrix-upload]')) {
        e.preventDefault();
        _onSafetyMatrixPanelClick(e);
      }
    });
  }
  panel.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 16px;border-bottom:1px solid var(--border-light);background:var(--bg-page);flex-shrink:0;">
      <div style="display:flex;align-items:center;gap:8px;min-width:0;">
        <span class="material-icons" style="color:var(--brand-blue);">grid_on</span>
        <h2 style="margin:0;font-size:1.05rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${companyName ? `${escapeHtml(companyName)} — ` : ''}Safety Roster Matrix</h2>
      </div>
      <div style="display:flex;align-items:center;gap:8px;flex-shrink:0;">
        <button class="btn btn-secondary btn-sm" onclick="window.HRModule._printSafetyRosterMatrixPdf()">
          <span class="material-icons" style="font-size:16px;">print</span> Print / Save PDF
        </button>
        <button class="btn btn-sm" style="background:var(--bg-elevated);" onclick="window.HRModule.closeSafetyMatrixPanel()" aria-label="Close">
          <span class="material-icons" style="font-size:18px;">close</span>
        </button>
      </div>
    </div>
    <div id="safety-matrix-panel-body" style="flex:1;overflow-y:auto;padding:16px;"></div>`;
  panel.style.display = 'flex';
  document.documentElement.classList.add('modal-open');
  document.body.classList.add('modal-open');
  _renderSafetyMatrixPanelBody();
}

function closeSafetyMatrixPanel() {
  const panel = document.getElementById('safety-matrix-panel');
  if (panel) panel.style.display = 'none';
  // Only release the body scroll-lock if no .modal is currently open.
  const openModal = document.querySelector('.modal:not(.hidden)');
  if (!openModal) {
    document.documentElement.classList.remove('modal-open');
    document.body.classList.remove('modal-open');
  }
}

function _onSafetyMatrixPanelClick(e) {
  const cell = e.target?.closest?.('[data-matrix-upload]');
  if (!cell) return;
  const personId = cell.getAttribute('data-matrix-pid');
  const itemId = cell.getAttribute('data-matrix-itemid');
  if (!personId || !itemId) return;
  openMatrixCertUpload(personId, itemId);
}

// Cell-click handler — opens the cert-upload modal prefilled to the right person +
// required training, then re-renders the matrix in place on save (no bounce to
// employee detail). Folds all 7 COD must-fixes.
function openMatrixCertUpload(personId, itemId) {
  // Edge (COD) — guard against a double-click opening two modals / stacking saves.
  const existingModal = document.getElementById('isnet-training-modal');
  if (existingModal && !existingModal.classList.contains('hidden')) return;
  // Must-fix #3 — partner-managed carriers cannot upload (server already rejects;
  // don't surface a dead affordance / no-op the click).
  if (!_canCarrierEditTrainings()) {
    window.showToast?.('Required trainings are managed by your compliance partner.', 'info');
    return;
  }
  // Must-fix #5 — tenant guard: the person must be in this tenant's roster.
  const emp = (HRState.people || []).find(p => p.id === personId);
  if (!emp) { window.showToast?.('Employee not found.', 'error'); return; }

  // Resolve the required-training label for prefill.
  const configItem = _getSafetyTrainingsList().find(i => i && i.itemId === itemId);
  const label = configItem?.label || '';

  // Must-fix #1 — find an EXISTING entry for this required training → open TRUE edit
  // (pass the entry + its real array index). Else open ADD with null, then prefill.
  const trainings = emp.isnetTrainings || [];
  // TASK-4573b — the CURRENT cert (the one the cell shows), not the first linked row.
  const linked = trainings.filter(t => t && t.requiredTrainingId === itemId);
  const existingIdx = linked.length ? trainings.indexOf(_currentTrainingEntry(linked)) : -1;

  // Context so saveIsnetTraining re-renders the matrix instead of bouncing away (#2).
  _matrixSaveContext = { personId, itemId };

  if (existingIdx >= 0) {
    // True edit — pass the entry + its index (correctly flips isEdit=true).
    showIsnetTrainingModal(personId, { ...trainings[existingIdx], _index: existingIdx });
  } else {
    // Add path — open with null (isEdit=false, editIndex=-1), then prefill AFTER
    // the modal renders. Must-fix #1: training-name MUST be set (save hard-rejects empty).
    showIsnetTrainingModal(personId, null);
    const sel = document.getElementById('isnet-required-training');
    const nameEl = document.getElementById('isnet-training-name');
    if (sel) sel.value = itemId;
    if (nameEl && !nameEl.value.trim()) nameEl.value = label;
  }
}

// Builds + writes the read-only landscape PDF popup (the ORIGINAL detached-popup
// export, unchanged). Called from the panel's "Print / Save PDF" button.
function _printSafetyRosterMatrixPdf() {
  const config = _getSafetyTrainingsList();
  const people = (HRState.people || []).filter(p => p.active !== false).sort((a, b) => {
    const aLast = splitNameISNet(a.name).last.toLowerCase();
    const bLast = splitNameISNet(b.name).last.toLowerCase();
    return aLast.localeCompare(bLast);
  });

  const companyName = _hrSafetyMatrixCompanyName(); // TASK-9512 (was HRState.tenant — never assigned)
  const dateStr = formatDateShort(new Date().toISOString().split('T')[0]);

  if (config.length === 0) {
    window.showToast?.('Add required trainings first (Manage Required Trainings).', 'error');
    return;
  }

  const cellFor = (st) => {
    switch (st.state) {
      case 'missing':  return '<td style="background:#FEF2F2;color:#DC2626;font-weight:600;">Missing</td>';
      case 'expired':  return `<td style="background:#FEF2F2;color:#DC2626;font-weight:600;">Expired ${st.date ? formatDateShort(st.date) : ''}</td>`;
      case 'expiring': return `<td style="background:#FFFBEB;color:#B45309;font-weight:600;">${st.days}d (${st.date ? formatDateShort(st.date) : ''})</td>`;
      case 'noexpiry': return '<td style="background:#F0FDF4;color:#059669;">On file</td>';
      case 'valid':    return `<td style="background:#F0FDF4;color:#059669;">Valid${st.date ? ' (' + formatDateShort(st.date) + ')' : ''}</td>`;
      case 'exempt':   return `<td style="background:#F1F5F9;color:#64748b;" title="${escapeHtml(st.reason || 'Exempt')}">N/A</td>`;
      case 'na':       return '<td style="background:#F1F5F9;color:#64748b;" title="Not required for this role">N/A</td>';
      default:         return '<td>—</td>';
    }
  };

  const headerCols = config.map(c => {
    const recur = Number(c.recurrenceMonths) || 0;
    const sub = recur > 0 ? (recur % 12 === 0 ? `${recur/12}yr` : `${recur}mo`) : 'one-time';
    return `<th>${escapeHtml(c.label)}<div style="font-weight:400;font-size:0.85em;color:#94a3b8;">${sub}</div></th>`;
  }).join('');

  const bodyRows = people.map(emp => {
    const { first, last } = splitNameISNet(emp.name);
    const cells = config.map(c => cellFor(computeRequiredTrainingState(emp, c))).join('');
    return `<tr><td style="white-space:nowrap;font-weight:600;">${escapeHtml(last)}, ${escapeHtml(first)}</td>${cells}</tr>`;
  }).join('');

  const printHtml = `<!DOCTYPE html>
<html><head><title>Safety Roster${companyName ? ` - ${escapeHtml(companyName)}` : ''}</title>
<style>
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 20px; color: #1e293b; }
  h1 { font-size: 1.3rem; margin: 0 0 4px; }
  .subtitle { color: #64748b; font-size: 0.85rem; margin-bottom: 12px; }
  table { width: 100%; border-collapse: collapse; font-size: 0.7rem; }
  th { background: #f1f5f9; color: #334155; font-weight: 600; text-align: left; padding: 6px 5px; border: 1px solid #cbd5e1; vertical-align: top; }
  td { padding: 5px; border: 1px solid #e2e8f0; vertical-align: top; white-space: nowrap; }
  .legend { margin-top: 14px; font-size: 0.7rem; color: #64748b; display:flex; gap:14px; flex-wrap:wrap; }
  .legend span { display:inline-flex; align-items:center; gap:4px; }
  .sw { width:12px; height:12px; border-radius:3px; display:inline-block; }
  .footer { margin-top: 16px; font-size: 0.7rem; color: #94a3b8; text-align: right; }
  .print-btn { position: fixed; top: 12px; right: 12px; padding: 8px 20px; background: #3b82f6; color: #fff; border: none; border-radius: 6px; cursor: pointer; font-size: 0.9rem; z-index: 100; }
  @media print { .print-btn { display: none; } body { padding: 10px; } @page { size: landscape; margin: 0.5in; } }
  @page { size: landscape; margin: 0.5in; }
</style></head>
<body>
  <button class="print-btn" onclick="window.print()">Print / Save PDF</button>
  <h1>${companyName ? `${escapeHtml(companyName)} — ` : ''}Safety Roster</h1>
  <div class="subtitle">Required Trainings Matrix &bull; ${dateStr} &bull; ${people.length} employee${people.length !== 1 ? 's' : ''} &bull; ${config.length} required training${config.length !== 1 ? 's' : ''}</div>
  <table>
    <thead><tr><th>EMPLOYEE</th>${headerCols}</tr></thead>
    <tbody>${bodyRows || `<tr><td colspan="${config.length + 1}" style="text-align:center;padding:20px;color:#94a3b8;">No active employees</td></tr>`}</tbody>
  </table>
  <div class="legend">
    <span><span class="sw" style="background:#F0FDF4;border:1px solid #059669;"></span> Valid / On file</span>
    <span><span class="sw" style="background:#FFFBEB;border:1px solid #B45309;"></span> Expiring (≤30d)</span>
    <span><span class="sw" style="background:#FEF2F2;border:1px solid #DC2626;"></span> Missing / Expired</span>
    <span><span class="sw" style="background:#F1F5F9;border:1px solid #cbd5e1;"></span> N/A (exempt / not required for role)</span>
  </div>
  <div class="footer">Generated from DispatchDeck &bull; ${dateStr}</div>
</body></html>`;

  const w = window.open('', '_blank');
  if (w) { w.document.write(printHtml); w.document.close(); }
}

// ============================================
// COMPLIANCE DOCUMENTS (per-employee)
// ============================================

// TASK-9234 — resolve a compliance doc's download URL ON DEMAND from its
// storagePath, instead of reading a bearer URL persisted on the broadcast
// person doc. Addressed by (employeeId, index) rather than by path so no
// storage path is ever interpolated into an onclick JS-string attribute.
//
// 🔴 WHY A CLIENT getDownloadURL AND NOT getComplianceDocBytes: the byte-stream
// callable EXPLICITLY REJECTS this prefix. _assertComplianceDocPathAllowed
// fences tenants/{tid}/people/ to the CRED subfolders
// (mvr/clearinghouse/annualReview/daPolicy/medCard/medExam/license) plus the
// `documents` docRows tree — `docs` is in neither, so it throws
// permission-denied ("Only credential documents can be viewed here").
// functions/src/smartFiling.js:394 documents that same rejection. Widening that
// fence is a functions/src/index.js change and is NOT this lane's file.
// The client read is gated instead by storage.rules:648
//   match /tenants/{tenantId}/people/{personId}/docs/{fileName}
//   allow read: if canReadCompliancePII(tenantId)   // owner/admin or hr/compliance module
// which is the block TASK-9222 added. A driver-PIN session has no members/{uid}
// doc, so isHrModuleMember() fails → denied. That gate only becomes real when
// 9222's storage.rules DEPLOYS; see the TASK-9234 report for the ordering.
async function _openComplianceDocFile(employeeId, index, anchorEl) {
  const restoreLink = _hrBusyDocLink(anchorEl);
  // Only ever open https:// — blocks javascript:/data: in a legacy persisted url.
  const _openUrl = (u) => {
    try {
      if (!/^https:\/\//i.test(String(u || ''))) return false;
      window.open(u, '_blank', 'noopener');
      return true;
    } catch (_) { return false; }
  };
  try {
    const emp = HRState.people.find(e => e.id === employeeId);
    const row = ((emp && emp.complianceDocs) || [])[index];
    if (!row) { window.showToast?.('No document on file', 'error'); return; }
    // 🔴 TASK-9234 (COD Edge Case Hunter) — NO legacy-url fallback, at all, ever.
    // The obvious shape here is "if there's no storagePath, open the stored url",
    // and it is wrong twice over. (a) It is dead code: a census of all 456 people
    // docs across all 74 tenants found 6 rows carrying a url and ZERO carrying a
    // url without a storagePath — nothing would ever reach it. (b) It is an
    // injection sink: firestore.rules does not constrain `complianceDocs`, so
    // anyone with people-write (dispatcher, roster editor) could plant
    // {url:'https://evil/…', storagePath:null} and have HR's own click open it.
    // Reading the url NOWHERE is what makes the field inert rather than merely
    // unwritten. Fail closed — a row with no path has no viewable file.
    if (!row.storagePath) { window.showToast?.('No document on file', 'error'); return; }
    // TASK-9855 — bytes through the Storage SDK (rules-checked), not a
    // getDownloadURL token URL handed to a new tab.
    const opening = _hrOpenStorageStrict(row.storagePath);
    window.showToast?.('Opening document…', 'info');
    await opening;
  } catch (e) {
    console.error('[HR] _openComplianceDocFile failed:', e);
    // 🔴 NO fallback to row.url on an ERROR — viewComplianceDoc's rule (TASK-996
    // COD Adversary) applies verbatim: a storage/unauthorized means the
    // canReadCompliancePII gate DENIED this viewer, and opening the persisted
    // bearer URL would defeat exactly the gate that just fired.
    const denied = e && (e.sfReason === 'denied' || e.code === 'storage/unauthorized' || /unauthorized|permission/i.test(e.message || ''));
    window.showToast?.(denied ? 'You do not have access to this document' : (e?.message || 'Could not open the document'), 'error');
  } finally {
    restoreLink();
  }
}

// TASK-9234 — SELF-HEAL. Stopping the writer only protects docs uploaded from
// now on; the rows already on the person doc keep broadcasting their bearer URL
// to every driver session forever. Both hr.js writers read the WHOLE
// complianceDocs array and write the WHOLE array back, so they can carry the
// old rows across clean at no extra cost — the leak drains as employees are
// touched, without a prod migration.
//
// ⚠️ This nulls the FIELD; it does NOT revoke the TOKEN. A URL someone already
// copied keeps working until the object's download token is rotated, which is
// deliberately NOT done here (it would break any saved link — Chris's call).
// Only rows that carry a storagePath are scrubbed: a row whose url is its only
// pointer would be orphaned, and the reader no longer opens a persisted url
// anyway. (Census 2026-09-15: 6 rows carry a url, all 6 have a storagePath, all
// on trippin-trucking — so this scrub covers every live row.)
function _scrubComplianceDocUrls(rows) {
  return (rows || []).map((d) => (d && d.url && d.storagePath) ? { ...d, url: null } : d);
}

// TASK-9234 — ONE renderer for the compliance-doc rows, shared by the Documents
// section of the edit-employee modal and the post-add inline refresh. These two
// were byte-identical copies; the leak had to be fixed in both, so they are now
// one function. Contrast: light row background → var(--text-primary) / the
// existing var(--text-secondary), never var(--text-muted).
function _renderComplianceDocRows(employeeId, docs) {
  const today = new Date().toISOString().split('T')[0];
  const empIdJs = escJsAttr(employeeId);
  return (docs || []).map((d, i) => {
    const expired = !!(d.expiresAt && d.expiresAt < today);
    // TASK-9234 — viewable means storagePath, full stop. The resolver no longer
    // opens a persisted url, so offering "View file" on a url-only row would
    // render a link that can only toast an error. Census: zero such rows exist.
    const hasFile = !!d.storagePath;
    return `
      <div class="hr-doc-row" style="display:flex;align-items:center;gap:8px;padding:8px 12px;background:var(--bg-secondary);border-radius:var(--radius-sm);border:1px solid var(--border-light);">
        <span class="material-icons" style="font-size:16px;color:${expired ? '#dc2626' : 'var(--text-secondary)'};">description</span>
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;font-size:0.85rem;color:var(--text-primary);">${escapeHtml(d.name)}</div>
          ${d.expiresAt ? `<div style="font-size:0.75rem;color:${expired ? '#dc2626' : 'var(--text-secondary)'};">Expires: ${escapeHtml(d.expiresAt)}${expired ? ' — EXPIRED' : ''}</div>` : ''}
          ${hasFile ? `<a href="#" data-no-print onclick="event.preventDefault();window.HRModule.openComplianceDocFile('${empIdJs}', ${i}, this);return false;" style="font-size:0.75rem;color:var(--brand-blue);cursor:pointer;">View file</a>` : ''}
        </div>
        <button type="button" onclick="window.HRModule.removeComplianceDoc('${empIdJs}', ${i})" style="background:none;border:none;cursor:pointer;color:#94A3B8;padding:2px;" title="Remove">
          <span class="material-icons" style="font-size:16px;">close</span>
        </button>
      </div>
    `;
  }).join('');
}

async function addComplianceDoc(employeeId) {
  const name = document.getElementById('hr-doc-name')?.value?.trim();
  if (!name) { showToast('Document name is required', 'error'); return; }
  const expiresAt = document.getElementById('hr-doc-expiry')?.value || null;
  const fileInput = document.getElementById('hr-doc-file');
  const file = fileInput?.files?.[0];

  let storagePath = null;
  let fileName = null;

  if (file) {
    if (file.size > 10 * 1024 * 1024) { showToast('File must be under 10MB', 'error'); return; }
    try {
      storagePath = `tenants/${HRState.tenantId}/people/${employeeId}/docs/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, { contentType: inferContentType(file) });
      // TASK-9234 🔴 PATH-ONLY — do NOT mint/persist a getDownloadURL here.
      // A getDownloadURL carries its own `?token=` ACCESS TOKEN in the query
      // string: it is a BEARER CREDENTIAL, not a path, and it works with no
      // further auth. Writing it onto the person doc published it to every
      // driver session, because jobboard.js subscribeToJobBoardData onSnapshots
      // the WHOLE `tenants/{tid}/people` collection (jobboard.js ~:971) — the
      // same broadcast-PII failure class as the driver-PIN leak. Medical cards,
      // MVRs and DQ paperwork were readable by any driver who opened devtools.
      // The row keeps `storagePath`; the URL is resolved ON DEMAND at click
      // time by _openComplianceDocFile below, through the storage.rules
      // canReadCompliancePII gate on tenants/{tid}/people/{pid}/docs/.
      fileName = file.name;
    } catch (e) {
      console.error('[HR] Doc upload failed:', e);
      showToast('Upload failed', 'error');
      return;
    }
  }

  const personRef = doc(db, 'tenants', HRState.tenantId, 'people', employeeId);
  const personSnap = await getDoc(personRef);
  // TASK-9234 — scrub any bearer URL still sitting on the OLD rows as we go.
  const existing = _scrubComplianceDocUrls(
    personSnap.exists() ? (personSnap.data().complianceDocs || []) : []);
  // TASK-9234 — `url` stays NULL forever. The key is kept (not dropped) so the
  // shape is stable for legacy rows and so a backfill that nulls the remaining
  // ones is a pure field update, not a schema change.
  const newDoc = { name, expiresAt, url: null, storagePath, fileName, addedAt: new Date().toISOString() };
  existing.push(newDoc);

  await updateDoc(personRef, { complianceDocs: existing });

  // Update local state
  const emp = HRState.people.find(e => e.id === employeeId);
  if (emp) emp.complianceDocs = existing;

  // Re-render the doc list inline
  const listEl = document.getElementById('hr-emp-documents-list');
  if (listEl) {
    // TASK-9234 — shared renderer; resolves the file through the gated read.
    listEl.innerHTML = _renderComplianceDocRows(employeeId, existing);
  }

  // Clear form
  document.getElementById('hr-doc-name').value = '';
  document.getElementById('hr-doc-expiry').value = '';
  if (fileInput) fileInput.value = '';
  showToast('Document added', 'success');
}

async function removeComplianceDoc(employeeId, index) {
  const personRef = doc(db, 'tenants', HRState.tenantId, 'people', employeeId);
  const personSnap = await getDoc(personRef);
  // TASK-9234 — same scrub on the remove path; both writers rewrite the whole
  // array, so both drain the legacy bearer URLs.
  const existing = _scrubComplianceDocUrls(
    personSnap.exists() ? (personSnap.data().complianceDocs || []) : []);
  const removed = existing[index];

  // Delete from storage if uploaded
  if (removed?.storagePath) {
    try { await deleteObject(ref(storage, removed.storagePath)); } catch (e) { /* may not exist */ }
  }

  existing.splice(index, 1);
  await updateDoc(personRef, { complianceDocs: existing });

  const emp = HRState.people.find(e => e.id === employeeId);
  if (emp) emp.complianceDocs = existing;

  // Re-open the edit form to refresh
  editEmployeeHR(employeeId);
  showToast('Document removed', 'success');
}

// TASK-505 — mirror of server _resolveBgHandledBy. Returns true when the
// effective BG owner is the partner (managed carrier, not explicitly handed
// back to the carrier). Drives whether the carrier sees "Request from your DOT
// manager" (partner) vs a direct-run + confirm (carrier self-managed). The
// server gate (tenantAdminRunBackgroundCheck / checkrInitiateBackgroundCheck)
// is authoritative; this just keeps the UI honest.
function hrBgHandledByPartner() {
  const td = window.AppState?.tenantData || {};
  const hasManager = !!(td.complianceManagerUid && String(td.complianceManagerUid).trim());
  if (!hasManager) return false;
  const stored = td.compliance?.bgHandledBy;
  if (stored === 'carrier') return false;
  return true; // 'partner' or absent → partner-managed
}

// TASK-1649 — mirror of server _resolveBgPaidBy: WHO PAYS, which can now differ
// from who runs. A carrier that chose to pay for its own checks still sends
// "Request from your DOT manager" when the partner runs them, and IS billed —
// so request copy must not promise "no charge" off hrBgHandledByPartner().
// No partner → carrier; a stored choice wins; unset → follows who runs.
function hrBgPaidByPartner() {
  const td = window.AppState?.tenantData || {};
  const hasManager = !!(td.complianceManagerUid && String(td.complianceManagerUid).trim());
  if (!hasManager) return false;
  const stored = td.compliance?.bgPaidBy;
  if (stored === 'partner') return true;
  if (stored === 'carrier') return false;
  return hrBgHandledByPartner();
}

// TASK-1367/TASK-1368 — client mirror of the SERVER order gate on
// `checkrInitiateBackgroundCheck` / `tenantAdminRunBackgroundCheck`
// (`assertBgOrderAccess`, functions/src/index.js). TASK-1368 (Chris: "anyone
// who has HR access" → "yeah you could") widened it from owner/admin-only to
// admin ∪ `hr` module ∪ `compliance` module — the same set that can already
// READ the full report — with the TASK-608 "view & upload only" restriction
// excluded (a viewUploadOnly member must not put a paid report on the company
// card). The partner leg (complianceManager + managedTenants) exists server-
// side only; a partner never signs into this app.
//
// SINGLE SEAM ON PURPOSE: every carrier-side ordering affordance reads THIS
// predicate — don't scatter it across call sites.
function hrCanOrderBackgroundCheck() {
  // FAIL CLOSED (COD Code Reviewer ⚠️ / AuthZ Sentinel): this gates a money +
  // FCRA-trigger action, so a missing helper must not read as "allowed". hr.js
  // is only ever loaded by app.js, which publishes hrEffectiveAdmin — if it's
  // absent, the shell is broken and nobody should be ordering reports.
  if (typeof window.hrEffectiveAdmin !== 'function') {
    console.warn('[HR] hrEffectiveAdmin unavailable — blocking BG-check ordering (fail closed)');
    return false;
  }
  if (window.hrEffectiveAdmin()) return true;
  // Module leg — mirrors assertBgOrderAccess path 1: active member holding the
  // hr OR compliance module, minus viewUploadOnly. Status gate mirrors the
  // sibling client gates (_hrIsOwnerOrAdmin / hrCanManageModule).
  const ma = window.AppState?.memberAccess;
  if (!ma) return false; // no member doc + not effective admin → broken state, fail closed
  if (['revoked', 'inactive', 'pending'].includes(ma.status)) return false;
  if (ma.viewUploadOnly === true) return false;
  return !!(Array.isArray(ma.modules) && (ma.modules.includes('hr') || ma.modules.includes('compliance')));
}

function hrPartnerLabel() {
  const td = window.AppState?.tenantData || {};
  return (typeof td.compliancePartner === 'string' && td.compliancePartner.trim())
    ? td.compliancePartner.trim()
    : 'your DOT manager';
}

// TASK-1605 (Chris 2026-09-29) — an account with no screening package STOPS and
// says "pick a package", with a link. Never a silently missing button (what Run
// MVR did), never a dead end for the applicant (what apply.html step 7 did), and
// never a priced package quietly chosen on the carrier's behalf (the read-time
// fallback this row first proposed — rejected).
//
// ONE renderer for every carrier surface that keys off compliancePackageDefault:
// the Hiring header, the candidate MVR row, the person MVR card, and (through
// window.HRModule.screeningPackageStopHtml — Pitfall #1, no cross-import) the
// Compliance → People MVR section. Returns '' the moment a default exists. The
// BG modal gets NO stop: a hosted invitation picks its package per order and
// works without a default (checkrInitiateBackgroundCheck only treats the default
// as always-allowed).
//
// Who can fix it, in order:
//   • partner RUNS checks (hrBgHandledByPartner) → the default is the partner's
//     to pick, in their portal (TASK-1217, Chris: "if the Compliance portal is
//     in charge then don't give carrier option") — name them, no link.
//   • owner / `billing` module (hrCanManageComplianceBilling — the same gate the
//     server applies to manageCarrierComplianceCard set_package_default) → the
//     link opens Settings → Compliance Billing, where the picker lives.
//   • anyone else → told who to ask. The server gate is authoritative either way.
function hrScreeningPackageStopHtml(opts = {}) {
  const td = window.AppState?.tenantData || {};
  if (td.compliancePackageDefault) return '';
  const partner = hrBgHandledByPartner();
  const canSet = !partner
    && typeof window.hrCanManageComplianceBilling === 'function' && window.hrCanManageComplianceBilling()
    && typeof window.openCarrierComplianceBillingModal === 'function';
  const partnerLbl = escapeHtml(hrPartnerLabel());
  const pickBtn = `<button type="button" class="btn btn-sm btn-primary dd-pkg-pick" data-t1605-pick onclick="window.openCarrierComplianceBillingModal()">
      <span class="material-icons">fact_check</span> Pick a package
    </button>`;
  const fix = partner
    ? `Ask <strong>${partnerLbl}</strong> to pick a screening package — they manage background checks for your account.`
    : canSet
      ? pickBtn
      : 'Ask the account owner (or someone with the Billing module) to pick one under <strong>Settings → Compliance Billing</strong>.';
  if (opts.compact) {
    // Inline, where a Run / Request button would otherwise have been.
    return `<span data-t1605-stop class="dd-pkg-stop-c">
        <span class="material-icons">report_problem</span>
        <span>No screening package picked.</span> ${fix}
      </span>`;
  }
  const what = opts.context === 'hiring'
    ? 'Applicants can still apply online, but no background check runs with their application until a package is picked — each new applicant arrives needing a check ordered by hand, and Run MVR is unavailable.'
    : 'An MVR cannot be ordered through Checkr for this account until a package is picked. Picking one orders nothing — each check is still billed only when you run it.';
  // integ/2129 perf: the static look is .dd-pkg-stop / .dd-pkg-stop-c in styles.css (same values as the inline styles were).
  return `<div data-t1605-stop class="dd-pkg-stop">
      <span class="material-icons">report_problem</span>
      <div>
        <b>Pick a screening package</b>
        <div>${what}</div>
        <div class="dd-pkg-fix">${fix}</div>
      </div>
    </div>`;
}

// TASK-811 — Compliance-module delegation hydration. The MVR / Background-Check
// modals (openMvrPullModal / openBackgroundCheckModal) resolve the subject from
// HRState.people, which is ONLY populated when initHR() runs (i.e. the HR view
// was opened). A compliance-only carrier (HR paywalled) never opens HR, so
// HRState.people is empty and those openers silently no-op — the exact
// TASK-781 trap. This lets the Compliance module hand HR the one person doc it
// already has + the tenantId so the existing, FCRA-correct HR modals work
// without a full initHR. Idempotent: updates the cached copy if already present.
// Returns true when HRState has a usable subject afterward.
function ensureSubjectInHrState(person) {
  if (!person || !person.id) return false;
  // Seed tenantId from whatever source of truth is available (AppState is the
  // canonical tenant; compliance passes its own ComplianceState.tenantId via
  // AppState already). Never clobber a real, different tenantId.
  const appTenant = window.AppState?.tenant || null;
  // FAIL CLOSED on tenant mismatch (COD #4): the subject belongs to the current
  // AppState tenant. If HRState is already bound to a DIFFERENT tenant, refuse —
  // otherwise we'd insert a cross-tenant person into HRState and run the BG/MVR
  // request against the wrong tenant.
  if (HRState.tenantId && appTenant && HRState.tenantId !== appTenant) return false;
  if (!HRState.tenantId && appTenant) HRState.tenantId = appTenant;
  if (!Array.isArray(HRState.people)) HRState.people = [];
  const idx = HRState.people.findIndex(p => p.id === person.id);
  if (idx >= 0) {
    // Merge the freshest compliance copy over the stale HR copy so the modal
    // reads current med-card/CDL/DL values without losing HR-only fields.
    HRState.people[idx] = { ...HRState.people[idx], ...person };
  } else {
    HRState.people.push(person);
  }
  return !!HRState.tenantId;
}

// TASK-447 — Render the read-only "alerts routed to your compliance partner"
// note. Shown ONLY when the tenant has a complianceManagerUid AND the effective
// bgHandledBy is 'partner' (default for managed carriers). Mirrors the
// server-side _resolveBgHandledBy effective-default logic so UI + routing agree.
function renderBgRoutingNote() {
  const el = document.getElementById('hr-bg-routing-note');
  if (!el) return;
  const td = window.AppState?.tenantData || {};
  const hasManager = !!(td.complianceManagerUid && String(td.complianceManagerUid).trim());
  const stored = td.compliance?.bgHandledBy;
  // Effective: partner unless explicitly 'carrier'. Requires a manager.
  const partnerHandles = hasManager && stored !== 'carrier';
  if (!partnerHandles) {
    el.style.display = 'none';
    el.innerHTML = '';
    return;
  }
  const partnerName = (typeof td.compliancePartner === 'string' && td.compliancePartner.trim())
    ? td.compliancePartner.trim()
    : 'your compliance partner';
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  el.innerHTML = `
    <div style="display:flex;align-items:flex-start;gap:10px;padding:12px 14px;background:rgba(16,185,129,0.06);border:1px solid rgba(16,185,129,0.22);border-radius:var(--radius-lg);">
      <span class="material-icons" style="color:#10B981;font-size:20px;flex-shrink:0;margin-top:1px;">forward_to_inbox</span>
      <div style="font-size:0.85rem;color:var(--text-secondary);line-height:1.45;">
        Background check &amp; MVR completion alerts are routed to <strong style="color:var(--text-primary);">${esc(partnerName)}</strong>, who manages compliance for your account. You'll always see results here in HR, but the completion email/text goes to their team.
      </div>
    </div>`;
  el.style.display = 'block';
}

function renderComplianceDashboard() {
  // TASK-447 — keep the partner-routing note in sync with the dashboard.
  renderBgRoutingNote();
  const container = document.getElementById('hr-compliance-dashboard');
  if (!container) return;

  const active = (HRState.people || []).filter(p => p.active !== false && !p.terminated);
  const win = getComplianceWindow();

  // New applicants
  const newApps = (HRState.candidates || []).filter(c => c.status === 'new' || c.status === 'applied').length;

  // Count expired OR expiring within 30 days — via the SAME shared predicate
  // (getComplianceTileStatus) the directory's compliance filter uses (TASK-582),
  // so the tile counts and the tile-filtered list can never disagree.
  let expiringCdl = 0, expiredCdl = 0;
  let expiringMed = 0, expiredMed = 0;
  let clearinghouseDue = 0, clearinghouseOverdue = 0;
  let annualReviewDue = 0, annualReviewOverdue = 0;

  let unclassifiedCount = 0; // TASK-1391

  for (const emp of active) {
    const cdl = getComplianceTileStatus(emp, 'cdl', win);
    if (cdl === 'expired') expiredCdl++; else if (cdl === 'expiring') expiringCdl++;
    const med = getComplianceTileStatus(emp, 'medCard', win);
    if (med === 'expired') expiredMed++; else if (med === 'expiring') expiringMed++;
    const ch = getComplianceTileStatus(emp, 'clearinghouse', win);
    if (ch === 'expired') clearinghouseOverdue++; else if (ch === 'expiring') clearinghouseDue++;
    const ar = getComplianceTileStatus(emp, 'annualReview', win);
    if (ar === 'expired') annualReviewOverdue++; else if (ar === 'expiring') annualReviewDue++;
    if (getComplianceTileStatus(emp, 'unclassified', win)) unclassifiedCount++;
  }
  // TASK-9850 — legacy Drives, no CMV people never asked the two CMV questions.
  // Shown only while someone needs answers AND this login can answer (the
  // answerCmvChecks gate: owner, or hr/compliance module, not view & upload only).
  const cmvNeedsCount = _hrCanAnswerCmvChecks() ? active.filter(p => window.DqMatrix.needsCmvAnswers(p)).length : 0;
  // TASK-9145 — drivers who owe a §382.601(d) receipt and have none (bulk send).
  const daMissingCount = window.DaReceiptBulk ? window.DaReceiptBulk.missingReceiptPeople(active, { isActive: _hrDaBulkActive }).length : 0;

  const tileColor = (expired, expiring) => expired > 0 ? '#DC2626' : expiring > 0 ? '#F59E0B' : '#059669';

  const tile = (icon, label, count, color, onclick) => `
    <button class="hr-compliance-tile" onclick="${onclick || ''}" style="display:flex;flex-direction:column;align-items:center;gap:6px;padding:16px 12px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:var(--radius-lg);cursor:pointer;border-top:3px solid ${color};transition:transform 0.15s,box-shadow 0.15s;" onmouseenter="this.style.transform='translateY(-2px)';this.style.boxShadow='var(--shadow-md)'" onmouseleave="this.style.transform='';this.style.boxShadow=''">
      <span class="material-icons" style="font-size:28px;color:${color};">${icon}</span>
      <span style="font-size:1.5rem;font-weight:700;color:var(--text-primary);">${count}</span>
      <span style="font-size:0.75rem;color:var(--text-secondary);text-align:center;line-height:1.3;">${label}</span>
    </button>`;

  container.innerHTML =
    tile('person_add', 'New Applicants', newApps, newApps > 0 ? '#3B82F6' : '#94A3B8', "window.HRModule.openHiringSubView()") +
    tile('badge', 'CDL Expiring<br>/ Expired', expiredCdl + expiringCdl, tileColor(expiredCdl, expiringCdl), "window.HRModule.openComplianceDirectory('cdl')") +
    tile('medical_services', 'Med Card<br>Expiring / Expired', expiredMed + expiringMed, tileColor(expiredMed, expiringMed), "window.HRModule.openComplianceDirectory('medCard')") +
    tile('verified_user', 'Clearinghouse<br>Due / Overdue', clearinghouseOverdue + clearinghouseDue, tileColor(clearinghouseOverdue, clearinghouseDue), "window.HRModule.openComplianceDirectory('clearinghouse')") +
    tile('rate_review', 'Annual Review<br>Due / Overdue', annualReviewOverdue + annualReviewDue, tileColor(annualReviewOverdue, annualReviewDue), "window.HRModule.openComplianceDirectory('annualReview')") +
    // TASK-1391 — amber (never red): a missing classification is a data-hygiene
    // gap, not a credential violation. Clicking filters the directory to them.
    tile('assignment_ind', 'Needs DOT<br>Classification', unclassifiedCount, unclassifiedCount > 0 ? '#F59E0B' : '#059669', "window.HRModule.openComplianceDirectory('unclassified')") +
    // TASK-9850 — amber, like the classification tile: a missing answer, not a violation.
    (cmvNeedsCount > 0 ? tile('local_shipping', 'Needs the two<br>CMV answers', cmvNeedsCount, '#F59E0B', 'window.HRModule.openCmvNeedsAnswers()') : '') +
    // TASK-9145 — click sends the receipt link to everyone missing one.
    (daMissingCount > 0 ? tile('assignment_turned_in', 'D&amp;A Receipt<br>Not Signed', daMissingCount, '#F59E0B', 'window.HRModule.openDaReceiptBulkSend()') : '');
}

// TASK-9145 — tenant-wide §382.601(d) receipt-link send. window.DaReceiptBulk
// renders; this host passes the SAME callable the per-person Send Link uses
// (member path; tenantId pinned when the list opens). HRState.people is a live
// snapshot, so the tile count repaints as drivers sign.
// A separation DATE alone also means gone (same as compliance.js getPersonEmploymentStatus).
const _hrDaBulkActive = (p) => p.active !== false && !p.terminated && !p.terminationDate;
function openDaReceiptBulkSend() {
  if (!window.DaReceiptBulk) return;
  const tid = HRState.tenantId;
  const td = window.AppState?.tenantData || {};
  window.DaReceiptBulk.openBulkSendModal({
    people: HRState.people || [],
    isActive: _hrDaBulkActive,
    carrierName: td.companyName || td.name || '',
    sms: { offered: true, ...window.DaReceiptBulk.smsCreditsLeft(td) },
    send: async (personId, o) => {
      const fn = httpsCallable(functions, 'mintIncumbentDrugAlcoholReceiptToken');
      const res = await fn({ tenantId: tid, personId, sendSms: !!o.sendSms, dedupeWindowMs: o.dedupeWindowMs, ...(o.allowMissingDer ? { allowMissingDer: true } : {}) });
      return res && res.data;
    },
    confirmDerOverride: (n) => (window.showConfirm
      ? window.showConfirm('No Designated Employer Representative set', 'Sending anyway applies to all ' + n + ' drivers in this send. The D&A policy notice would go out with a placeholder instead of a real contact (49 CFR §382.601(b)(1)), and a receipt signed against it cannot be fixed later.\n\nSet the DER under Drug & Alcohol settings first, or send anyway.', { confirmText: 'Send anyway', cancelText: 'Cancel', danger: true })
      : Promise.resolve(window.confirm('No DER is set - the notice would carry a placeholder contact. Send anyway?'))),
    onDone: () => { try { renderComplianceDashboard(); } catch (_) { /* snapshot repaints anyway */ } },
  });
}

// TASK-9850 — who may answer the two CMV questions from the carrier side. Same
// shape as the answerCmvChecks callable's gate (and the ND exemption's): the
// tenant owner by ownerId, or an hr/compliance module holder who is not view &
// upload only. Modules, never roles. The server re-checks.
function _hrCanAnswerCmvChecks() {
  return _hrCanSetIntrastateExemption();
}

// TASK-9850 — open the shared needs-answers list (DqMatrix renders; this host
// authorizes and passes the callable). HRState.people is a live snapshot, so the
// dashboard repaints itself after each answer.
function openCmvNeedsAnswers() {
  if (!_hrCanAnswerCmvChecks()) { window.showToast?.('Only the account owner, or someone with HR or Compliance access, can answer these.', 'error'); return; }
  const tid = HRState.tenantId; // COD r2 — pinned to the tenant the list opened for
  window.DqMatrix.openCmvAnswersList({
    people: (HRState.people || []),
    answer: async (personId, cmvChecks, acceptTier) => {
      const fn = httpsCallable(functions, 'answerCmvChecks');
      const res = await fn({ tenantId: tid, personId, cmvChecks, acceptTier: acceptTier || null });
      return res && res.data;
    },
    onChanged: () => { try { renderComplianceDashboard(); } catch (_) { /* snapshot repaints anyway */ } },
  });
}

// ============================================
// TASK-582 — compliance tile → filtered directory
// ============================================
// Single source of truth for the tile predicate ("expired OR due within 30 days").
// Used by BOTH renderComplianceDashboard's counts and renderEmployeeDirectory's
// filter, so the number on a tile and the list it opens can never disagree.
const COMPLIANCE_TILE_DEFS = {
  cdl: { field: 'cdlExpiryDate', chipLabel: 'CDL expiring/expired' },
  medCard: { field: 'medCardExpiryDate', chipLabel: 'Med Card expiring/expired' },
  clearinghouse: { field: 'clearinghouseNextDue', chipLabel: 'Clearinghouse due/overdue' },
  annualReview: { field: 'annualReviewNextDue', chipLabel: 'Annual Review due/overdue' },
  // TASK-1391 — absence-of-field predicate (no `field` date compare): people with
  // no explicit positionType. Pairs with the amber "Needs DOT Role" directory
  // badge so an admin can clear the classification backlog in one filtered pass.
  unclassified: {
    chipLabel: 'Needs DOT classification',
    predicate: (emp) => window.DqMatrix.explicitPositionType(emp) ? null : 'expiring',
  },
  // TASK-9985 — the HR nav badge's own filter. Same predicate the badge counts
  // with (hrHasRedTag), so the number on the badge and the list it opens agree.
  redTags: {
    chipLabel: 'Expired or missing credential or training',
    predicate: (emp) => hrHasRedTag(emp) ? 'expired' : null,
  },
};

function getComplianceWindow() {
  // TASK-988 — use LOCAL calendar dates, not UTC. `toISOString()` returns the UTC
  // date, so after ~7pm CT (midnight UTC) todayStr/in30Str rolled to TOMORROW and
  // the compliance tiles flagged CDL/med-card/clearinghouse/annual-review expiry a
  // day early. Same bug class as getTodayDateString (TASK-342). The tile predicate
  // does lexicographic YYYY-MM-DD compares, so local-part strings are correct.
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const today = new Date();
  const in30 = new Date();
  in30.setDate(in30.getDate() + 30);
  return { todayStr: fmt(today), in30Str: fmt(in30) };
}

// 'expired' | 'expiring' | null for one employee against one tile key.
// TASK-934 — gated by the shared DQ-applicability matrix: an item that doesn't
// apply to the person's classification never counts (a Non-Driver's stale CDL
// date can't turn the tile red). Because BOTH the dashboard counts and the
// tile-filtered directory share this predicate (TASK-582), the number on a
// tile and the list it opens stay in agreement.
const COMPLIANCE_TILE_MATRIX_KEYS = {
  cdl: 'cdlValid', medCard: 'medCard', clearinghouse: 'clearinghouse', annualReview: 'annualReview',
};
function getComplianceTileStatus(emp, key, win) {
  const def = COMPLIANCE_TILE_DEFS[key];
  if (!def) return null;
  // TASK-1391 — custom-predicate tiles (e.g. `unclassified`) don't do the
  // `emp[def.field]` date compare below; the predicate returns the status.
  if (def.predicate) return def.predicate(emp);
  const matrixKey = COMPLIANCE_TILE_MATRIX_KEYS[key];
  if (matrixKey && !window.DqMatrix.getApplicableDqItems(emp).items[matrixKey]) return null;
  const val = emp[def.field];
  if (!val) return null;
  if (val < win.todayStr) return 'expired';
  if (val <= win.in30Str) return 'expiring';
  return null;
}

// Tile click handler: set the filter, expand the directory, scroll to it.
// Count-0 tiles open the directory UNFILTERED — an empty filtered list with no
// explanation is worse than no filter.
function openComplianceDirectory(filterKey) {
  if (_hrGateComplianceOnly('directory')) return; // TASK-1117 — filtered Employee Directory (post-hire mgmt)
  if (!COMPLIANCE_TILE_DEFS[filterKey]) return;
  const win = getComplianceWindow();
  const hasMatches = (HRState.people || []).some(p =>
    p.active !== false && !p.terminated && getComplianceTileStatus(p, filterKey, win));
  HRState.directoryComplianceFilter = hasMatches ? filterKey : null;

  // Fresh filter context: a lingering search term / position selection would AND
  // with the tile filter and make the list disagree with the tile count.
  const searchEl = document.getElementById('hr-employee-search');
  if (searchEl) searchEl.value = '';
  const posEl = document.getElementById('hr-position-filter');
  if (posEl) posEl.value = '';

  const content = document.getElementById('hr-employee-directory');
  if (content && content.style.display === 'none') {
    toggleSection('employee-directory'); // expands + renders with the new filter
  } else {
    applyDirectoryFilters(); // already open — re-render with the new filter
  }
  document.getElementById('hr-directory-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// Renders/hides the "Showing N — Med Card expiring/expired ✕ Clear" chip.
// Called from renderEmployeeDirectory with the post-filter list length.
function renderComplianceFilterChip(count) {
  const el = document.getElementById('hr-compliance-filter-chip');
  if (!el) return;
  const def = COMPLIANCE_TILE_DEFS[HRState.directoryComplianceFilter];
  if (!def) {
    el.style.display = 'none';
    el.innerHTML = '';
    return;
  }
  el.style.cssText = 'display:flex;align-items:center;gap:8px;flex-wrap:wrap;max-width:100%;margin:0 0 var(--spacing-md);padding:6px 12px;background:#FFFBEB;border:1px solid #FDE68A;border-radius:var(--radius-md);font-size:0.8rem;color:#92400E;';
  el.innerHTML = `
    <span class="material-icons" style="font-size:16px;color:#D97706;">filter_alt</span>
    <span style="font-weight:600;min-width:0;overflow-wrap:break-word;">Showing ${count} — ${def.chipLabel}</span>
    <button onclick="window.HRModule.clearDirectoryComplianceFilter()" style="display:inline-flex;align-items:center;gap:4px;border:1px solid #FDE68A;background:#fff;border-radius:999px;padding:2px 10px;font-size:0.75rem;font-weight:600;color:#92400E;cursor:pointer;white-space:nowrap;">
      ✕ Clear
    </button>`;
}

function clearDirectoryComplianceFilter() {
  HRState.directoryComplianceFilter = null;
  applyDirectoryFilters();
}

// ============================================
// TASK-9985 — HR badge says what it counts + opens exactly those people
// ============================================
// A person carries a RED tag on their directory card when any entry from
// getExpiredDocs is an 'alert' (amber 'classify' entries are not counted —
// TASK-1391). ONE predicate for the badge count AND the filtered list.
function hrHasRedTag(emp) {
  return getExpiredDocs(emp).some(d => d.severity === 'alert');
}

// The people the HR badge counts: active (not terminated, not inactive) with a
// red tag. Same status rule as the directory's default 'active' filter.
function hrRedTagPeople(people) {
  return (people || []).filter(p => p.active !== false && !p.terminated && getComplianceTileStatus(p, 'redTags'));
}

function hrRedTagBadgeLabel(n) {
  return n === 1
    ? '1 person has an expired or missing credential or training'
    : `${n} people have an expired or missing credential or training`;
}

// Open the Employee Directory filtered to exactly the badge's people. Status is
// forced to Active (the badge only counts active people) and search/position
// are cleared by openComplianceDirectory, so the list length == badge count.
function openRedTagDirectory() {
  HRState.directoryStatus = 'active';
  const statusEl = document.getElementById('hr-status-filter');
  if (statusEl) statusEl.value = 'active';
  openComplianceDirectory('redTags');
}

// Badge tap (sidebar, phone More sheet, directory header). Says what it counts,
// then lands on the filtered list instead of the HR front page.
function _hrRedTagBadgeTap(e) {
  e.preventDefault();
  e.stopPropagation();
  const n = hrRedTagPeople(HRState.people).length;
  // COD r1 — a session that cannot open the Employee Directory (compliance-only
  // tenant, or hr-directory not granted) gets the label only, never a promise.
  document.getElementById('mobile-more-menu')?.classList.add('hidden');
  if (!_hrCanOpenRedTagList()) {
    if (n > 0) window.showToast?.(window.AppState?.tenantData?.complianceOnly
      ? `${hrRedTagBadgeLabel(n)}.`
      : `${hrRedTagBadgeLabel(n)}. Ask an admin for Employee Directory access to see who.`, 'info');
    return;
  }
  if (n > 0) window.showToast?.(`${hrRedTagBadgeLabel(n)}. Showing them now.`, 'info');
  const onHrLanding = /^#\/hr\b/.test(window.location.hash || '')
    && HRState.currentView === 'landing' && HRState.peopleLoaded
    && document.getElementById('hr-employee-directory');
  if (onHrLanding) { openRedTagDirectory(); return; }
  if (window.AppState) window.AppState.pendingHrDirectoryFilter = 'redTags';
  if (/^#\/hr\b/.test(window.location.hash || '')) backToHRLanding();
  else window.location.hash = '#/hr';
}

// Can this session open the filtered Employee Directory? (compliance-only
// tenants and members without hr-directory cannot — the tap then only says what
// the badge counts, and the label does not promise a list.)
function _hrCanOpenRedTagList() {
  return !window.AppState?.tenantData?.complianceOnly && isSubModuleUnlocked('hr-directory');
}

function _hrPaintRedTagBadge(el, n, shortText) {
  if (!el) return;
  if (n > 0) {
    const label = hrRedTagBadgeLabel(n);
    const canOpen = _hrCanOpenRedTagList();
    el.textContent = shortText ? shortText(n) : String(n);
    el.title = canOpen ? `${label} — tap to see who` : label;
    el.setAttribute('aria-label', canOpen ? `${label}. Tap to see who.` : `${label}.`);
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.setAttribute('data-badge-tap', '1');
    el.classList.remove('hidden');
    if (!el.dataset.badgeBound) {
      el.dataset.badgeBound = '1';
      el.addEventListener('click', _hrRedTagBadgeTap);
      el.addEventListener('keydown', (ev) => { if (ev.key === 'Enter' || ev.key === ' ') _hrRedTagBadgeTap(ev); });
    }
  } else {
    el.classList.add('hidden');
    el.removeAttribute('title');
    el.removeAttribute('aria-label');
    el.removeAttribute('role');
    el.removeAttribute('tabindex');
  }
}

// Returns [{ label, severity }] — severity 'alert' renders the red .poor badge,
// 'classify' renders the amber needs-classification affordance (TASK-1391).
function getExpiredDocs(emp) {
  const today = new Date().toISOString().split('T')[0];
  const expired = [];
  const push = (label) => expired.push({ label, severity: 'alert' });
  // TASK-1391 — resolvePositionType deliberately defaults an UNCLASSIFIED person
  // to the full CDL checklist (TASK-934 — correct for the DQ file; do not change
  // it). But on the directory card that default painted red "CDL Missing" badges
  // on shop managers/swampers who were simply never classified — indistinguishable
  // from a real gap on an actual CDL driver. Until a tier is chosen, show ONE
  // amber "Needs DOT Role" affordance instead of the three DOT credential reds.
  // DQ file / compliance module / drug pool keep the conservative fallback.
  if (!window.DqMatrix.explicitPositionType(emp)) {
    expired.push({ label: 'Needs DOT Role', severity: 'classify' });
  } else {
    // TASK-934 — DOT credential expiries only count for the tiers they apply to
    // (shared matrix). Trainings + complianceDocs below stay tier-independent.
    const dqItems = window.DqMatrix.getApplicableDqItems(emp).items;
    // TASK-1336: an APPLICABLE credential with NO date on file is a gap, not a pass
    // (mirrors TASK-1330). Each entry is a COMPLETE label (the renderer no longer
    // appends " Expired") so missing vs expired both read correctly on the badge.
    if (dqItems.cdlValid) { if (!emp.cdlExpiryDate) push('CDL Missing'); else if (emp.cdlExpiryDate < today) push('CDL Expired'); }
    if (dqItems.medCard) { if (!emp.medCardExpiryDate) push('Med Card Missing'); else if (emp.medCardExpiryDate < today) push('Med Card Expired'); }
    if (dqItems.dlExpiry) { if (!emp.dlExpiryDate) push('DL Missing'); else if (emp.dlExpiryDate < today) push('DL Expired'); }
  }
  if (emp.complianceDocs) {
    for (const d of emp.complianceDocs) {
      if (d.expiresAt && d.expiresAt < today) push(`${d.name} Expired`);
    }
  }
  if (emp.isnetTrainings) {
    for (const t of emp.isnetTrainings) {
      if (t.expiryDate && t.expiryDate < today) push(`${t.trainingName || 'Training'} Expired`);
    }
  }
  return expired;
}

function updateComplianceBadges() {
  // TASK-9985 — the count comes from hrRedTagPeople, the same function the
  // filtered directory uses (redTags tile def), so badge == list it opens.
  // TASK-1391 integrated-gate: amber 'classify' entries are NOT counted.
  const alertCount = hrRedTagPeople(HRState.people).length;
  _hrPaintRedTagBadge(document.getElementById('hr-compliance-badge'), alertCount);
  _hrPaintRedTagBadge(document.getElementById('hr-compliance-badge-mobile'), alertCount);
  _hrPaintRedTagBadge(document.getElementById('hr-directory-compliance-badge'), alertCount,
    (n) => `${n} alert${n !== 1 ? 's' : ''}`);
}

// ============================================
// ACTIVITY TIMELINE
// ============================================

// TASK-9745 follow-up — the timeline's "Clocked in — <job>" label. Clock entries
// carry jobTitle + customerName + jobId and never jobName, so reading jobName
// alone always printed a bare "Clocked in". Title first, then a legacy jobName,
// then the customer ("Shop" / "Office" time has no job title). Plain text; the
// timeline renderer escapes it.
function _timelineClockJobLabel(c) {
  if (!c) return '';
  for (const v of [c.jobTitle, c.jobName, c.customerName]) {
    const t = typeof v === 'string' ? v.trim() : '';
    if (t) return t;
  }
  return '';
}

// TASK-4807 — `empName` used to be a second parameter, passed only so the
// maintenance lane below could join on a person's NAME. Every lane keys off the
// person id now, so taking a name at all is a trap for the next reader.
async function loadEmployeeActivity(empId) {
  const container = document.getElementById('hr-activity-content');
  if (!container) return;

  container.innerHTML = '<div class="empty-state-small">Loading activity...</div>';

  try {
    const tenant = HRState.tenantId;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const cutoff = Timestamp.fromDate(thirtyDaysAgo);

    const events = [];

    // TASK-931 Slice B — status changes (deactivated / no longer employed) were
    // invisible on a person's own timeline because this function never read
    // `tenants/{tid}/auditLog`, the collection the tech-support runbook names as
    // THE answer to "who changed X". Live proof on the founder tenant: William
    // Moore's ASSET_DEACTIVATE row (brian@, 2026-06-30, 140 jobDay assignments
    // cascaded off) has existed the whole time and rendered nowhere.
    //
    // Two queries because person-scoped audit rows use two different key
    // fields and always have: ASSET_DEACTIVATE writes `entityId`, while
    // UPDATE_PERSON / CREATE_PERSON write `personId`. A single query on either
    // one misses half the history.
    //
    // NO orderBy: `where(x) + orderBy('timestamp')` needs a composite index that
    // does not exist in firestore.indexes.json, and the .catch() below would
    // swallow the failed-precondition into an empty list — a feature that ships
    // looking fine and shows nothing, forever. Equality-only runs on the
    // automatic single-field index; the rows are sorted with everything else
    // further down. Bounded by limit() since the caller can't page.
    const _auditCol = collection(db, 'tenants', tenant, 'auditLog');
    // TASK-4807 — every lane below now reports its own failure. The old blind
    // `.catch(() => ({ docs: [] }))` is what let three of these eight lanes die
    // in prod for their whole life without a single console line (see below).
    const _laneCatch = (which) => (err) => {
      console.warn(`[HR] employee activity: ${which} query failed —`, err?.message || err);
      return { docs: [] };
    };

    // TASK-4807 — the maintenance lane was reported as a name-vs-id join bug
    // (`where('assignedTo','==', empName)` against writers that store a
    // personId). It IS one — live scan, 8 tenants with maintenance data, 284
    // docs: 263 carry no assignedTo at all, 21 carry a personId, ZERO carry a
    // name. So `empName` could never match anything and there is nothing to
    // backfill; personId is the only shape any writer has ever produced
    // (maintenance.js saveTask reads a <select> of person ids; the driver path
    // auto-assigns getDriverPersonId()).
    //
    // But fixing only the value would have changed NOTHING, and this is the
    // part that matters: `where(x) + orderBy('createdAt','desc')` on
    // `maintenance`, `timeOff` and `signDocuments` needs a composite index that
    // has never existed — not in firestore.indexes.json and not deployed
    // (verified against the live index list, 51 indexes, none of these three).
    // All three threw FAILED_PRECONDITION on every call and the blind catch
    // above turned it into an empty list. Probed on the founder tenant:
    //   maintenance  assignedTo==id + orderBy -> THREW;  without orderBy -> 3 docs
    //   timeOff      personId   + orderBy -> THREW;  without orderBy -> 90-doc collection, rows present
    //   signDocuments personId  + orderBy -> THREW;  without orderBy -> 1 doc
    // clockEntries and pretrips DO have their composite indexes deployed, which
    // is why the tab always looked like it was working — it rendered a
    // plausible timeline with three whole categories silently missing.
    //
    // Same remedy TASK-931 Slice B already chose 30 lines up: drop the orderBy,
    // run equality-only on the automatic single-field index, and let the
    // client-side sort below order everything. No index deploy required.
    // limit(100) is a blast-radius bound, NOT a recency window — without an
    // orderBy Firestore returns an arbitrary 100 by document id. Safe here
    // because these are per-person slices of small per-tenant collections
    // (largest tenant: 197 maintenance / 90 timeOff / 16 signDocuments in
    // total, ≤3 per person), and the timeline slices to 50 after sorting.
    //
    // COD (Code Reviewer, TASK-4807) — the cap is the one thing here that could
    // re-create the defect it fixes: without an orderBy, a person who crosses it
    // gets an ARBITRARY 100 rows, so a recent event could drop out of a timeline
    // that still looks complete. It is not enough to say "counts are small
    // today". A capped lane is therefore DETECTED (`snap.size === LANE_CAP`) and
    // said out loud under the timeline — the panel never claims completeness it
    // cannot deliver. The real cure is a composite index + orderBy + paging;
    // that needs `firebase deploy --only firestore:indexes`, which is a
    // separate, deliberate change.
    const LANE_CAP = 100;
    const _q = (col, field) => query(
      collection(db, 'tenants', tenant, col), where(field, '==', empId), limit(LANE_CAP));

    // Query multiple collections in parallel
    const [clockSnap, pretripSnap, timeOffSnap, maintenanceSnap, signDocsSnap, empDocsSnap, auditEntitySnap, auditPersonSnap] = await Promise.all([
      getDocs(query(collection(db, 'tenants', tenant, 'clockEntries'), where('personId', '==', empId), orderBy('clockIn', 'desc'))).catch(_laneCatch('clockEntries')),
      getDocs(query(collection(db, 'tenants', tenant, 'pretrips'), where('driverId', '==', empId), orderBy('createdAt', 'desc'))).catch(_laneCatch('pretrips')),
      getDocs(_q('timeOff', 'personId')).catch(_laneCatch('timeOff')),
      getDocs(_q('maintenance', 'assignedTo')).catch(_laneCatch('maintenance')),
      getDocs(_q('signDocuments', 'personId')).catch(_laneCatch('signDocuments')),
      getDocs(collection(db, 'tenants', tenant, 'people', empId, 'documents')).catch(_laneCatch('people/documents')),
      getDocs(query(_auditCol, where('entityId', '==', empId), limit(100))).catch(_laneCatch('auditLog/entityId')),
      getDocs(query(_auditCol, where('personId', '==', empId), limit(100))).catch(_laneCatch('auditLog/personId')),
    ]);

    // Clock entries
    clockSnap.docs.forEach(d => {
      const c = d.data();
      const ts = c.clockIn?.toDate?.() || null;
      if (!ts) return;
      const hrs = c.totalHours ? `${Number(c.totalHours).toFixed(1)} hrs` : 'in progress';
      events.push({
        date: ts,
        icon: 'schedule',
        color: '#3B82F6',
        label: `Clocked in${_timelineClockJobLabel(c) ? ` — ${_timelineClockJobLabel(c)}` : ''}`,
        detail: hrs,
      });
    });

    // DVIRs
    pretripSnap.docs.forEach(d => {
      const p = d.data();
      const ts = p.createdAt?.toDate?.() || null;
      if (!ts) return;
      const hasDefects = p.defects?.length > 0;
      events.push({
        date: ts,
        icon: hasDefects ? 'warning' : 'check_circle',
        color: hasDefects ? '#DC2626' : '#059669',
        label: `${p.type === 'pre' ? 'Pre-trip' : 'Post-trip'} DVIR — ${p.truckUnit || 'Unknown'}`,
        detail: hasDefects ? `${p.defects.length} defect${p.defects.length !== 1 ? 's' : ''}` : 'Pass',
      });
    });

    // Time off
    timeOffSnap.docs.forEach(d => {
      const t = d.data();
      const ts = t.createdAt?.toDate?.() || null;
      if (!ts) return;
      const statusColors = { approved: '#059669', denied: '#DC2626', pending: '#D97706' };
      events.push({
        date: ts,
        icon: 'event_busy',
        color: statusColors[t.status] || '#64748B',
        label: `Time off request — ${t.type || 'PTO'}`,
        detail: t.status?.charAt(0).toUpperCase() + t.status?.slice(1) || 'Pending',
      });
    });

    // Maintenance tasks
    // TASK-4807 — this lane rendered nothing for anyone until now (dead query,
    // see above), so its formatting had never met real data. Two things it does
    // not survive untouched: `description` is a free-text field clamped at 2000
    // chars by maintenance.js saveTask, which would run a single timeline row
    // off the page; and the generic capitalize prints TASK-4773's driver-logged
    // work as "Pending_review".
    const MAINT_STATUS = {
      open: 'Open',
      in_progress: 'In progress',
      pending_review: 'Awaiting office review',
      approved: 'Approved',
      rejected: 'Rejected',
      completed: 'Completed',
      dismissed: 'Dismissed',
    };
    maintenanceSnap.docs.forEach(d => {
      const m = d.data();
      const ts = m.createdAt?.toDate?.() || null;
      if (!ts) return;
      const what = String(m.description || m.title || 'Task').trim() || 'Task';
      const bits = [];
      if (m.status) bits.push(MAINT_STATUS[m.status] || (m.status.charAt(0).toUpperCase() + m.status.slice(1)));
      // The driver's own "log work I did" (TASK-4773) and an office-created task
      // assigned to them read identically otherwise — say which it was.
      if (m.source === 'driver') bits.push('logged by driver');
      if (m.truckUnit || m.trailerUnit || m.assetName) bits.push(String(m.truckUnit || m.trailerUnit || m.assetName));
      events.push({
        date: ts,
        icon: 'build',
        color: '#7c3aed',
        label: `Maintenance — ${what.length > 80 ? what.substring(0, 80) + '…' : what}`,
        detail: bits.join(' · '),
      });
    });

    // Also pull from already-loaded HR data (reviews, disciplinary)
    (HRState.hrReviews || []).filter(r => r.employeeId === empId).forEach(r => {
      const ts = r.createdAt?.toDate?.() || null;
      if (!ts) return;
      events.push({
        date: ts,
        icon: 'rate_review',
        color: '#0891B2',
        label: `Performance review — ${r.rating || 'N/A'}`,
        detail: r.notes ? r.notes.substring(0, 60) + (r.notes.length > 60 ? '...' : '') : '',
      });
    });

    (HRState.hrForms || []).filter(f => f.employeeId === empId).forEach(f => {
      const ts = f.createdAt?.toDate?.() || null;
      if (!ts) return;
      events.push({
        date: ts,
        icon: 'gavel',
        color: '#DC2626',
        label: `Disciplinary — ${f.type || 'Form'}`,
        detail: f.notes ? f.notes.substring(0, 60) + (f.notes.length > 60 ? '...' : '') : '',
      });
    });

    // Signature documents
    signDocsSnap.docs.forEach(d => {
      const s = d.data();
      const ts = s.createdAt?.toDate?.() || null;
      if (!ts) return;
      const statusColors = { completed: '#059669', pending: '#D97706', sent: '#3B82F6' };
      events.push({
        date: ts,
        icon: 'draw',
        color: statusColors[s.status] || '#64748B',
        label: `Signature — ${s.title || s.fileName || 'Document'}`,
        detail: s.status?.charAt(0).toUpperCase() + s.status?.slice(1) || '',
      });
    });

    // Uploaded documents
    empDocsSnap.docs.forEach(d => {
      const doc = d.data();
      const ts = doc.uploadedAt?.toDate?.() || doc.createdAt?.toDate?.() || null;
      if (!ts) return;
      events.push({
        date: ts,
        icon: 'upload_file',
        color: '#7c3aed',
        label: `Document uploaded — ${doc.name || doc.fileName || 'File'}`,
        detail: doc.type || '',
      });
    });

    // TASK-931 Slice B — employment status changes from the audit log.
    // Only status-relevant actions are surfaced; the same collection also holds
    // telemetry (viewPersonCredentialDocs et al.) that would bury the timeline.
    const STATUS_AUDIT = {
      ASSET_DEACTIVATE:              { icon: 'person_off',      color: '#B45309', label: 'Deactivated' },
      PERSON_DEACTIVATED_LOGIN_KEPT: { icon: 'lock_open',       color: '#B45309', label: 'Deactivated — staff login kept active' },
      PERSON_TERMINATED:             { icon: 'work_off',        color: '#DC2626', label: 'Marked No Longer Employed' },
      UPDATE_PERSON:                 { icon: 'manage_accounts', color: '#64748B', label: 'Active status changed' },
      // TASK-9752 — server-authored "who looked at this person's fuel-card PIN".
      FUEL_CARD_PIN_VIEWED:            { icon: 'visibility',      color: '#7C3AED', label: 'Fuel-card PIN viewed' },
      FUEL_CARD_PIN_VIEW_RATE_LIMITED: { icon: 'block',           color: '#DC2626', label: 'Fuel-card PIN views blocked (too many)' },
    };
    const seenAuditIds = new Set();
    [...(auditEntitySnap.docs || []), ...(auditPersonSnap.docs || [])].forEach(d => {
      if (seenAuditIds.has(d.id)) return;   // a row can carry BOTH keys — render once
      seenAuditIds.add(d.id);
      const a = d.data() || {};
      const meta = STATUS_AUDIT[a.action];
      if (!meta) return;
      // entityId is shared with trucks/trailers/assets, and legacy tenants have
      // numeric ids ('1') that collide across entity types. Only trust an
      // entityId match when the row says it is about a person; a personId match
      // is unambiguous on its own.
      if (a.personId !== empId && a.entityType !== 'person') return;
      // UPDATE_PERSON fires on EVERY person save. Only the ones that actually
      // moved the active flag belong on a status timeline. (Direction isn't
      // recorded — the audit row carries `activeChanged` as a boolean only — so
      // the label deliberately doesn't claim one.)
      if (a.action === 'UPDATE_PERSON' && a.activeChanged !== true) return;
      const ts = a.timestamp?.toDate?.() || null;
      if (!ts) return;   // audit rows have been written without every field before (TASK-1479)
      const bits = [];
      if (a.user) bits.push(`by ${a.user}`);
      if (a.action === 'PERSON_TERMINATED') {
        if (a.lastDayWorked) bits.push(`last day ${a.lastDayWorked}`);
        if (a.reason) bits.push(String(a.reason).substring(0, 80));
      }
      if (a.action === 'FUEL_CARD_PIN_VIEWED' || a.action === 'FUEL_CARD_PIN_VIEW_RATE_LIMITED') {
        if (a.screenLabel) bits.push(`on ${String(a.screenLabel).substring(0, 60)}`);
        if (a.action === 'FUEL_CARD_PIN_VIEWED' && a.hadPin === false) bits.push('no PIN on file');
      }
      if (a.action === 'ASSET_DEACTIVATE') {
        const cascaded = Number(a.redFlags?.cascadedJobDays || 0);
        if (cascaded > 0) bits.push(`pulled off ${cascaded} scheduled job${cascaded === 1 ? '' : 's'}`);
        const revoked = Number(a.redFlags?.cascadedLoginRevokes || 0);
        if (revoked > 0) bits.push(`${revoked} staff login${revoked === 1 ? '' : 's'} revoked`);
      }
      events.push({
        date: ts,
        icon: meta.icon,
        color: meta.color,
        label: meta.label,
        detail: bits.join(' · '),
      });
    });

    // Sort newest first
    events.sort((a, b) => b.date - a.date);

    // TASK-4807 (COD fold-in) — any lane that came back exactly at the cap may
    // have dropped rows, and because those lanes run without an orderBy the
    // dropped ones are arbitrary, not merely the oldest. Say so rather than
    // render a plausible-looking timeline that quietly omits history.
    const _cappedLanes = [
      [timeOffSnap, 'time off'],
      [maintenanceSnap, 'maintenance'],
      [signDocsSnap, 'signature documents'],
      [auditEntitySnap, 'status changes'],
      [auditPersonSnap, 'status changes'],
    ].filter(([s]) => (s?.docs?.length || 0) >= LANE_CAP).map(([, name]) => name);
    const cappedNames = [...new Set(_cappedLanes)];
    if (cappedNames.length) {
      console.warn(`[HR] employee activity: lane(s) hit the ${LANE_CAP}-doc cap for ${empId} —`, cappedNames.join(', '));
    }
    const capNote = cappedNames.length
      ? `<div class="empty-state-small" style="margin-bottom:var(--spacing-sm);color:var(--text-secondary);">
           This person has more than ${LANE_CAP} ${escapeHtml(cappedNames.join(' / '))} records — some older entries are not shown here.
         </div>`
      : '';

    // Limit to 50 most recent
    const display = events.slice(0, 50);

    if (display.length === 0) {
      container.innerHTML = capNote || '<div class="empty-state-small">No recent activity found.</div>';
      return;
    }

    // Group by date
    const grouped = {};
    display.forEach(e => {
      const key = e.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(e);
    });

    let html = capNote;
    for (const [dateLabel, items] of Object.entries(grouped)) {
      html += `<div style="margin-bottom:var(--spacing-md);">
        <div style="font-weight:700;font-size:0.8rem;color:var(--text-secondary);margin-bottom:6px;padding-bottom:4px;border-bottom:1px solid var(--border-light);">${dateLabel}</div>`;
      items.forEach(e => {
        const time = e.date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        html += `
          <div style="display:flex;align-items:flex-start;gap:10px;padding:6px 0;">
            <span class="material-icons" style="font-size:18px;color:${e.color};margin-top:1px;">${e.icon}</span>
            <div style="flex:1;min-width:0;">
              <div style="font-size:0.85rem;color:var(--text-primary);font-weight:500;">${escapeHtml(e.label)}</div>
              <div style="font-size:0.75rem;color:var(--text-secondary);display:flex;gap:8px;">
                <span>${time}</span>
                ${e.detail ? `<span>· ${escapeHtml(e.detail)}</span>` : ''}
              </div>
            </div>
          </div>`;
      });
      html += '</div>';
    }

    container.innerHTML = html;
  } catch (err) {
    console.error('[HR] Error loading employee activity:', err);
    container.innerHTML = '<div class="empty-state-small">Error loading activity.</div>';
  }
}

// ============================================
// EXPOSE TO WINDOW FOR ONCLICK HANDLERS
// ============================================
// Warn before flipping FLSA Status from OT-eligible → no-OT. Reverts the
// dropdown if the user cancels. Wired via inline onchange on both
// `#edit-hr-flsa-status` (Edit Employee modal) and `#tax-flsa-status` (Tax tab).
async function warnFlsaChange(selectEl) {
  if (!selectEl) return;
  const prev = selectEl.dataset.prevValue || 'non_exempt';
  const next = selectEl.value;
  if (prev === next) return;
  // Only warn when switching FROM OT-eligible TO a "no OT" mode. Other
  // direction (turning OT back on) is always safe — never warn.
  const goingNoOt = prev === 'non_exempt' && (next === 'ot_exempt' || next === 'mca_exempt' || next === 'exempt');
  if (!goingNoOt) {
    selectEl.dataset.prevValue = next;
    return;
  }
  const labelByValue = {
    non_exempt: 'Hourly + OT Eligible',
    ot_exempt: 'Hourly — No OT',
    mca_exempt: 'Hourly — No OT (Motor Carrier Act exempt)',
    exempt: 'Salaried — No Hour Tracking',
  };
  // TASK-9394 — the MCA test is duties + vehicle, not duties + salary level.
  const message = next === 'mca_exempt'
    ? `Switching from "${labelByValue[prev]}" to "${labelByValue[next]}" stops OT from being calculated on every future payroll report (hours are still tracked). ` +
      `The Motor Carrier Act exemption depends on the employee's duties and the vehicles they work on (interstate commerce, over 10,000 lb GVWR, small-vehicle exception), not job title, ` +
      `and misclassifying a worker creates back-pay liability.`
    : `Switching from "${labelByValue[prev]}" to "${labelByValue[next]}" stops OT from being calculated on every future payroll report. ` +
      `This is rarely correct for hourly workers — the FLSA exempt classification is a legal test based on duties and salary level, not job title, ` +
      `and misclassifying an hourly worker creates back-pay liability.`;
  const ok = window.showConfirm
    ? await window.showConfirm('Disable overtime pay?', message, {
      confirmText: 'Yes, disable OT',
      cancelText: 'Keep OT eligible',
      danger: true,
    })
    : window.confirm(message);
  if (ok) {
    selectEl.dataset.prevValue = next;
  } else {
    selectEl.value = prev;
  }
}

// =============================================================================
// TASK-321 — OFFER LETTER GENERATOR (v1)
// Slice 1: per-tenant offer letter template stored at
//   `tenant.settings.hr.offerLetterTemplate` (single multi-line string with
//   {{merge_field}} placeholders).
// Slice 2: "Generate Offer Letter" button on the candidate detail modal +
//   client-side PDF render (jsPDF via CDN, same pattern as checkPrinter.js) +
//   auto-attach to `tenants/{tid}/people/{personId}/documents`.
// Scope locked: template + merge + PDF + attach. NO e-sign, NO countersign,
// NO comp library, NO email send, NO multi-template, NO payroll integration.
// =============================================================================

// Frozen whitelist of merge fields. Save-time validator warns on anything
// not in this list before persisting; render-time merge leaves unknown
// {{...}} placeholders intact (defensive - user was warned).
const OFFER_LETTER_MERGE_FIELDS = [
  'candidate_name',
  'candidate_email',
  'position_title',
  'pay_rate',
  'start_date',
  'employment_type',
  'reports_to',
  'company_name',
  'company_address',
  'today',
  'signing_authority_name',
  'signing_authority_title',
];

// Plain-text default template. Every supported merge field is referenced at
// least once so new tenants learn the system by seeing it work. Tone matches
// existing DD copy (Chris-voice: friendly, plain hyphens, no em-dashes).
const OFFER_LETTER_DEFAULT_TEMPLATE = `{{today}}

{{candidate_name}}
{{candidate_email}}

Dear {{candidate_name}},

On behalf of {{company_name}}, I am pleased to offer you the position of {{position_title}}. Your start date will be {{start_date}}, and you will report to {{reports_to}}.

Your compensation will be {{pay_rate}}. This is a {{employment_type}} position. Standard employment terms apply: at-will employment, subject to company policies, and contingent on successful completion of any required background checks, drug screening, and onboarding paperwork.

Please review this letter and let us know if you have any questions. If everything looks good, sign below and return a copy at your earliest convenience.

We are excited to have you on the team.

Sincerely,


_________________________
{{signing_authority_name}}
{{signing_authority_title}}
{{company_name}}
{{company_address}}


Accepted by:


_________________________
{{candidate_name}}                                 Date
`;

// Resolve the offer letter template for the current tenant. Falls back to
// the default template if no template has been configured.
function _getOfferLetterTemplate() {
  const stored = window.AppState?.settings?.hr?.offerLetterTemplate;
  return (typeof stored === 'string' && stored.trim().length > 0)
    ? stored
    : OFFER_LETTER_DEFAULT_TEMPLATE;
}

// True only when the tenant explicitly has a stored template. Used by the
// generate-modal's empty-state CTA so we can nudge admins to set one up
// the first time (they still get a sensible default automatically if they
// ignore the nudge).
function _hasOfferLetterTemplate() {
  const stored = window.AppState?.settings?.hr?.offerLetterTemplate;
  return typeof stored === 'string' && stored.trim().length > 0;
}

// Build the offer-letter-template settings modal markup. Dynamically
// inserted on first open. Mirrors getViewCandidateModalHTML pattern.
function _getOfferLetterTemplateModalHTML() {
  const current = _getOfferLetterTemplate();
  const fieldList = OFFER_LETTER_MERGE_FIELDS
    .map(f => `<code style="background:var(--bg-page);color:var(--text-primary);padding:2px 6px;border-radius:4px;font-size:0.78rem;">{{${f}}}</code>`)
    .join(' ');
  return `
    <div class="modal" id="modal-offer-letter-template" style="z-index:1100;">
      <div class="modal-content" style="max-width:760px;">
        <div class="modal-header">
          <h3 style="margin:0;display:flex;align-items:center;gap:8px;">
            <span class="material-icons" style="color:var(--brand-blue);">description</span>
            Offer Letter Template
          </h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('offer-letter-template')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="padding:var(--spacing-lg);">
          <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 var(--spacing-md);line-height:1.5;">
            Edit your fleet's offer letter template. Use merge fields like <code style="background:var(--bg-page);padding:2px 6px;border-radius:4px;font-size:0.78rem;">{{candidate_name}}</code> and they'll be filled in automatically when you generate a letter for a candidate.
          </p>
          <div style="background:var(--bg-page);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm) var(--spacing-md);margin-bottom:var(--spacing-md);">
            <div style="font-weight:600;font-size:0.8rem;color:var(--text-primary);margin-bottom:6px;">Supported merge fields</div>
            <div style="display:flex;flex-wrap:wrap;gap:6px;line-height:1.8;">${fieldList}</div>
            <div style="font-size:0.72rem;color:var(--text-secondary);margin-top:6px;">Pay rate, start date, and "reports to" are filled in per-candidate when you generate the letter. Everything else pulls from the candidate's record or your company profile.</div>
          </div>
          <div class="form-group">
            <label style="font-weight:600;font-size:0.85rem;display:block;margin-bottom:6px;">Template</label>
            <textarea id="offer-letter-template-textarea" rows="18" class="form-control" style="font-family:'Courier New', Consolas, monospace;font-size:0.85rem;line-height:1.45;resize:vertical;min-height:340px;">${escapeHtml(current)}</textarea>
            <p id="offer-letter-template-warning" style="font-size:0.78rem;color:var(--status-danger);margin-top:6px;display:none;"></p>
          </div>
        </div>
        <div class="modal-footer" style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:var(--spacing-sm);">
          <button type="button" class="btn-cancel-text" onclick="window.HRModule.resetOfferLetterTemplate()" style="background:transparent;border:none;color:var(--text-secondary);cursor:pointer;font-size:0.82rem;">
            Reset to default
          </button>
          <div style="display:flex;gap:var(--spacing-sm);">
            <button class="btn btn-secondary" onclick="window.HRModule.closeModal('offer-letter-template')">Cancel</button>
            <button class="btn btn-primary" onclick="window.HRModule.saveOfferLetterTemplate()">
              <span class="material-icons" style="font-size:16px;">save</span> Save Template
            </button>
          </div>
        </div>
      </div>
    </div>`;
}

function openOfferLetterTemplateModal() {
  if (!_hpIsAdmin()) {
    window.showToast?.('Admin only - ask an owner or admin to manage the offer letter template', 'error');
    return;
  }
  const existing = document.getElementById('modal-offer-letter-template');
  if (existing) existing.remove();
  document.body.insertAdjacentHTML('beforeend', _getOfferLetterTemplateModalHTML());
  const m = document.getElementById('modal-offer-letter-template');
  if (m) m.classList.remove('hidden');
}

// Reset the textarea to the default template. Does not persist - admin still
// has to hit Save. Convenience for tenants who edited something they want
// to roll back from.
function resetOfferLetterTemplate() {
  const ta = document.getElementById('offer-letter-template-textarea');
  if (!ta) return;
  if (typeof window.confirm === 'function' && !window.confirm('Reset the textarea to the default template? This does not save until you click Save Template.')) return;
  ta.value = OFFER_LETTER_DEFAULT_TEMPLATE;
  const warn = document.getElementById('offer-letter-template-warning');
  if (warn) { warn.style.display = 'none'; warn.textContent = ''; }
}

async function saveOfferLetterTemplate() {
  if (!_hpIsAdmin()) { window.showToast?.('Admin only', 'error'); return; }
  const tenantId = HRState.tenantId || window.AppState?.tenant;
  if (!tenantId) { window.showToast?.('No tenant context', 'error'); return; }
  const ta = document.getElementById('offer-letter-template-textarea');
  if (!ta) return;
  const value = ta.value || '';
  const warn = document.getElementById('offer-letter-template-warning');
  if (warn) { warn.style.display = 'none'; warn.textContent = ''; }

  // Validate placeholders against the frozen whitelist. Lowercase-compare so
  // {{Candidate_Name}} matches candidate_name. Unknown placeholders get a
  // soft warning + confirm - admin can save anyway (maybe they're using
  // {{notes}} as a comment marker), but they're told.
  const allFields = new Set();
  const re = /\{\{([a-z0-9_]+)\}\}/gi;
  let match;
  while ((match = re.exec(value)) !== null) {
    allFields.add(match[1].toLowerCase());
  }
  const unknown = [...allFields].filter(f => !OFFER_LETTER_MERGE_FIELDS.includes(f));
  if (unknown.length > 0) {
    const msg = `Unknown merge fields found: ${unknown.map(f => '{{' + f + '}}').join(', ')}.\n\nThese won't be filled in when you generate a letter - they'll show up as literal text. Save anyway?`;
    const ok = typeof window.confirm === 'function' ? window.confirm(msg) : true;
    if (!ok) {
      if (warn) {
        warn.style.display = 'block';
        warn.textContent = `Unknown placeholders: ${unknown.map(f => '{{' + f + '}}').join(', ')}`;
      }
      return;
    }
  }

  try {
    // TASK-637 — settings.hr.offerLetterTemplate lives on the TENANT DOC
    // (isTenantAdmin-only by rules). Route through the requireHrAccess-gated
    // updateHiringPacketTemplate callable so an hr-module STAFF member can save
    // the offer-letter template without a raw permission error. The callable
    // writes ONLY this whitelisted field path (billing fields stay protected).
    const call = httpsCallable(functions, 'updateHiringPacketTemplate');
    await call({ tenantId, offerLetterTemplate: value });
    // Mirror to local AppState so re-opens reflect the change immediately.
    if (!window.AppState.settings) window.AppState.settings = {};
    if (!window.AppState.settings.hr) window.AppState.settings.hr = {};
    window.AppState.settings.hr.offerLetterTemplate = value;
    window.showToast?.('Offer letter template saved', 'success');
    document.getElementById('modal-offer-letter-template')?.remove();
  } catch (err) {
    console.error('[OfferLetter] Template save failed:', err);
    window.showToast?.(`Save failed: ${err?.message || 'unknown error'}`, 'error');
  }
}

// =============================================================================
// SLICE 2 — Generate Offer Letter modal + PDF render + auto-attach
// =============================================================================

// Derive a human-readable employment-type label from a candidate record.
// Falls back to "Employee (W-2)" because that's the dominant trucking
// hire shape; tenants who hire 1099 contractors will set taxClassification
// explicitly on the candidate before generating.
// TASK-9178 COD r3 — the ONE way a classification <select> is drawn (Tax tab
// and Employment tab). Options/selection come from WorkerClass.selectModel; the
// onchange is WorkerClass.SELECT_ONCHANGE. `selectField` is the host tab's own
// local field renderer (each tab styles its fields differently).
function _hrClassSelect(selectField, label, id, person, unsetLabel) {
  const wc = window.WorkerClass;
  if (!wc) return `<div style="font-size:0.8rem;color:#DC2626;">${label}: unavailable — reload the page.</div>`;
  const m = wc.selectModel(person, unsetLabel);
  return selectField(label, id, m.selected, m.options)
    .replace(`<select id="${id}"`, `<select id="${id}" onchange="${wc.SELECT_ONCHANGE}"`);
}

// TASK-9178 — resolved by the ONE rule, like every other W-2 / 1099 reader.
function _offerLetterEmploymentTypeLabel(candidate) {
  const wc = window.WorkerClass;
  if (!wc) return 'Employee (W-2)';
  const c = wc.resolve(candidate).classification;
  return wc.label(c === null ? 'employee' : c);
}

// Format a YYYY-MM-DD date string as a long human date ("January 5, 2026").
// Tolerates Date objects, ISO strings, and Firestore Timestamps (via toDate).
function _offerLetterFormatLongDate(value) {
  if (!value) return '';
  let d;
  if (typeof value === 'string') {
    // YYYY-MM-DD strings would otherwise be parsed as UTC midnight and shift
    // a day in negative timezones. Parse as local-date explicitly.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (m) d = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    else d = new Date(value);
  } else if (value?.toDate) {
    d = value.toDate();
  } else if (value instanceof Date) {
    d = value;
  } else {
    return '';
  }
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

// Resolve the signing-authority defaults from tenant data. Settings override
// per-fleet (e.g. "Lacey Tripp / CFO"); fallback to current user / "Owner".
function _offerLetterSigningAuthority() {
  const td = window.AppState?.tenantData || {};
  const s = window.AppState?.settings || {};
  const u = window.AppState?.user || {};
  const name = (s?.hr?.signingAuthorityName)
    || td?.ownerName
    || u?.displayName
    || u?.name
    || u?.email
    || '';
  const title = (s?.hr?.signingAuthorityTitle) || '';
  return { name, title };
}

// Build the merge payload object passed into the template replacement step.
// Returns lowercase keys matching the whitelist. Missing inputs render as
// the placeholder itself (defensive - admin sees `{{pay_rate}}` if they
// forgot to type one) rather than empty string.
function _buildOfferLetterMergePayload(candidate, inputs) {
  const td = window.AppState?.tenantData || {};
  const sa = _offerLetterSigningAuthority();
  const today = _offerLetterFormatLongDate(new Date());
  return {
    candidate_name: candidate?.name || '',
    candidate_email: candidate?.email || '',
    position_title: inputs?.positionTitle || candidate?.position || '',
    pay_rate: inputs?.payRate || '',
    start_date: _offerLetterFormatLongDate(inputs?.startDate) || inputs?.startDate || '',
    employment_type: _offerLetterEmploymentTypeLabel(candidate),
    reports_to: inputs?.reportsTo || '',
    company_name: td?.name || 'Our Company',
    company_address: td?.companyAddress || td?.address || '',
    today,
    signing_authority_name: sa.name,
    // No hardcoded "Owner" — fall back to the position the new hire reports to.
    signing_authority_title: sa.title || inputs?.reportsTo || '',
  };
}

// Apply the merge payload to the template. Unknown placeholders are left
// intact so admins can spot them; whitelisted-but-empty placeholders fall
// through to the literal placeholder text too (signals "fill this in").
function _mergeOfferLetterTemplate(template, payload) {
  return (template || '').replace(/\{\{([a-z0-9_]+)\}\}/gi, (m, key) => {
    const lower = key.toLowerCase();
    const val = payload[lower];
    if (val === undefined || val === null || val === '') return m;
    return String(val);
  });
}

// TASK-1496-S11 — the jsPDF loader + _renderOfferLetterPdf moved, verbatim, to
// the lazy hrOfferLetter.js (with the offer-terms store) to keep hr.js's static
// chunk inside the appColdLoadGzip budget.

// Build the Generate-Offer-Letter modal markup. Pre-fills from the
// candidate record + the saved terms (TASK-1496-S11: peopleComp, via the lazy
// hrOfferLetter.js openPrior — passed in as `prior`).
function _getOfferLetterGenerateModalHTML(candidate, prior = {}) {
  const c = candidate;
  const empType = _offerLetterEmploymentTypeLabel(c);
  const td = window.AppState?.tenantData || {};
  const companyName = td?.name || '-';
  const companyAddress = td?.companyAddress || td?.address || '';
  const sa = _offerLetterSigningAuthority();
  const today = _offerLetterFormatLongDate(new Date());

  // If the tenant hasn't set up a template yet, show a single CTA pointing
  // them to the template modal instead of the form. UX Lead's actionable-
  // guard recommendation - they hit Generate, get sent to setup, come back.
  if (!_hasOfferLetterTemplate()) {
    return `
      <div class="modal" id="modal-offer-letter-generate" style="z-index:1080;">
        <div class="modal-content" style="max-width:520px;">
          <div class="modal-header">
            <h3 style="margin:0;display:flex;align-items:center;gap:8px;">
              <span class="material-icons" style="color:var(--brand-blue);">description</span>
              Set up your Offer Letter Template
            </h3>
            <button class="btn-icon" onclick="window.HRModule.closeModal('offer-letter-generate')">
              <span class="material-icons">close</span>
            </button>
          </div>
          <div class="modal-body" style="padding:var(--spacing-lg);">
            <p style="font-size:0.9rem;color:var(--text-primary);line-height:1.5;margin:0 0 var(--spacing-md);">
              You haven't customized your offer letter template yet. We'll use a sensible default for now, but you can edit it to match your fleet's voice and policies.
            </p>
            <p style="font-size:0.85rem;color:var(--text-secondary);line-height:1.5;margin:0;">
              Once your template is set up, the Generate button merges in the candidate's name, position, pay rate, and other fields automatically.
            </p>
          </div>
          <div class="modal-footer" style="display:flex;justify-content:space-between;gap:var(--spacing-sm);flex-wrap:wrap;">
            <button class="btn btn-secondary" onclick="window.HRModule.closeModal('offer-letter-generate');window.HRModule.openOfferLetterGenerateModal('${escJsAttr(c.id)}', true)">
              Use default for now
            </button>
            <button class="btn btn-primary" onclick="window.HRModule.closeModal('offer-letter-generate');window.HRModule.openOfferLetterTemplateModal()">
              <span class="material-icons" style="font-size:16px;">edit</span> Open Template Settings
            </button>
          </div>
        </div>
      </div>`;
  }

  return `
    <div class="modal" id="modal-offer-letter-generate" style="z-index:1080;">
      <div class="modal-content" style="max-width:640px;">
        <div class="modal-header">
          <h3 style="margin:0;display:flex;align-items:center;gap:8px;">
            <span class="material-icons" style="color:var(--brand-blue);">description</span>
            Generate Offer Letter - ${escapeHtml(c.name || '')}
          </h3>
          <button class="btn-icon" onclick="window.HRModule.closeModal('offer-letter-generate')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="modal-body" style="padding:var(--spacing-lg);">
          <p style="font-size:0.85rem;color:var(--text-secondary);margin:0 0 var(--spacing-md);line-height:1.5;">
            Fill in the four fields below - everything else is auto-merged from the candidate record and your company profile. The PDF is saved to this candidate's Documents tab.
          </p>

          <div style="background:var(--bg-page);border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-sm) var(--spacing-md);margin-bottom:var(--spacing-md);">
            <div style="font-weight:600;font-size:0.78rem;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;margin-bottom:6px;">Auto-filled from candidate / company</div>
            <div style="display:grid;grid-template-columns:auto 1fr;gap:4px 12px;font-size:0.82rem;">
              <div style="color:var(--text-secondary);">Candidate:</div><div style="color:var(--text-primary);">${escapeHtml(c.name || '-')}</div>
              <div style="color:var(--text-secondary);">Email:</div><div style="color:var(--text-primary);">${escapeHtml(c.email || '-')}</div>
              <div style="color:var(--text-secondary);">Employment type:</div><div style="color:var(--text-primary);">${escapeHtml(empType)}</div>
              <div style="color:var(--text-secondary);">Company:</div><div style="color:var(--text-primary);">${escapeHtml(companyName)}</div>
              ${companyAddress ? `<div style="color:var(--text-secondary);">Address:</div><div style="color:var(--text-primary);">${escapeHtml(companyAddress)}</div>` : ''}
              <div style="color:var(--text-secondary);">Today:</div><div style="color:var(--text-primary);">${escapeHtml(today)}</div>
              <div style="color:var(--text-secondary);">Signed by:</div><div style="color:var(--text-primary);">${escapeHtml(sa.name || '-')}${sa.title ? ', ' + escapeHtml(sa.title) : ''}</div>
            </div>
          </div>

          <div class="form-group">
            <label for="offer-letter-position" style="font-weight:600;font-size:0.85rem;display:block;margin-bottom:4px;">Position <span style="color:var(--status-danger);">*</span></label>
            <input type="text" id="offer-letter-position" class="form-control" maxlength="120" placeholder="e.g. Vac Truck Operator, CDL Driver" value="${escapeHtml(prior.position || c.position || '')}">
          </div>

          <div class="form-group">
            <label for="offer-letter-pay-rate" style="font-weight:600;font-size:0.85rem;display:block;margin-bottom:4px;">Pay Rate <span style="color:var(--status-danger);">*</span></label>
            <input type="text" id="offer-letter-pay-rate" class="form-control" maxlength="200" placeholder="e.g. \$0.62 per mile, \$25/hr, \$1,400/week salary" value="${escapeHtml(prior.payRate || '')}">
            <p style="font-size:0.72rem;color:var(--text-secondary);margin-top:4px;">Free-text. Be as specific as your offer is - hourly, salary, per-mile, per-load.</p>
          </div>

          <div class="form-group">
            <label for="offer-letter-start-date" style="font-weight:600;font-size:0.85rem;display:block;margin-bottom:4px;">Start Date <span style="color:var(--status-danger);">*</span></label>
            <input type="date" id="offer-letter-start-date" class="form-control" value="${escapeHtml(prior.startDate || '')}">
          </div>

          <div class="form-group">
            <label for="offer-letter-reports-to" style="font-weight:600;font-size:0.85rem;display:block;margin-bottom:4px;">Reports To <span style="color:var(--status-danger);">*</span></label>
            <input type="text" id="offer-letter-reports-to" class="form-control" maxlength="120" placeholder="e.g. Dispatch Manager" value="${escapeHtml(prior.reportsTo || '')}">
          </div>
        </div>
        <div class="modal-footer" style="display:flex;justify-content:space-between;gap:var(--spacing-sm);flex-wrap:wrap;">
          <button class="btn-cancel-text" onclick="window.HRModule.openOfferLetterTemplateModal()" style="background:transparent;border:none;color:var(--text-secondary);cursor:pointer;font-size:0.8rem;text-decoration:underline;">
            Edit template
          </button>
          <div style="display:flex;gap:var(--spacing-sm);">
            <button class="btn btn-secondary" onclick="window.HRModule.closeModal('offer-letter-generate')">Cancel</button>
            <button id="offer-letter-generate-btn" class="btn btn-primary" onclick="window.HRModule.generateOfferLetterPdf('${escJsAttr(c.id)}')">
              <span class="material-icons" style="font-size:16px;">picture_as_pdf</span> Generate PDF
            </button>
          </div>
        </div>
      </div>
    </div>`;
}

// Open the Generate-Offer-Letter modal. The 2nd argument `forceForm` lets
// the empty-state CTA bypass the template-check and use the default
// template anyway ("Use default for now").
// TASK-1496-S11 — the offer terms store (peopleComp), loaded on first use.
const _olTerms = () => __ddChunkWrap(import('./hrOfferLetter.js?v=3'));
async function openOfferLetterGenerateModal(candidateId, forceForm = false) {
  const candidate = HRState.candidates.find(c => c.id === candidateId);
  if (!candidate) { window.showToast?.('Candidate not found', 'error'); return; }
  if (_hrArchivedStageBlock(candidate)) return; // TASK-9419
  // Force-form path: stuff the default into AppState so _hasOfferLetterTemplate
  // returns true for this render. Persists only if the user actually saves.
  if (forceForm && !_hasOfferLetterTemplate()) {
    if (!window.AppState.settings) window.AppState.settings = {};
    if (!window.AppState.settings.hr) window.AppState.settings.hr = {};
    window.AppState.settings.hr.offerLetterTemplate = OFFER_LETTER_DEFAULT_TEMPLATE;
  }
  // TASK-1496-S11 — saved terms come from peopleComp (lazy hrOfferLetter.js
  // openPrior); DENIED != ABSENT: it toasts when they cannot be read. If the
  // chunk itself fails, the modal does not open (no silently blank form).
  const olm = await _olTerms().catch(() => null);
  if (!olm) { window.showToast?.('Could not open that screen. Check your connection and try again.', 'error'); return; }
  // TASK-10127 — the record, not the id: after the hire the terms live on the person.
  const prior = await olm.openPrior(HRState.tenantId, candidate, candidate.offerLetterData);
  const existing = document.getElementById('modal-offer-letter-generate');
  if (existing) existing.remove();
  document.body.insertAdjacentHTML('beforeend', _getOfferLetterGenerateModalHTML(candidate, prior));
  document.getElementById('modal-offer-letter-generate')?.classList.remove('hidden');
}

// Generate the PDF, persist inputs, auto-attach, trigger download.
async function generateOfferLetterPdf(candidateId) {
  const candidate = HRState.candidates.find(c => c.id === candidateId);
  if (!candidate) { window.showToast?.('Candidate not found', 'error'); return; }
  const tenantId = HRState.tenantId || window.AppState?.tenant;
  if (!tenantId) { window.showToast?.('No tenant context', 'error'); return; }

  const position = (document.getElementById('offer-letter-position')?.value || '').trim();
  const payRate = (document.getElementById('offer-letter-pay-rate')?.value || '').trim();
  const startDate = (document.getElementById('offer-letter-start-date')?.value || '').trim();
  const reportsTo = (document.getElementById('offer-letter-reports-to')?.value || '').trim();
  if (!position) { window.showToast?.('Enter the position', 'error'); document.getElementById('offer-letter-position')?.focus(); return; }
  if (!payRate) { window.showToast?.('Enter the pay rate', 'error'); document.getElementById('offer-letter-pay-rate')?.focus(); return; }
  if (!startDate) { window.showToast?.('Pick a start date', 'error'); document.getElementById('offer-letter-start-date')?.focus(); return; }
  if (!reportsTo) { window.showToast?.('Enter who this hire reports to', 'error'); document.getElementById('offer-letter-reports-to')?.focus(); return; }

  const btn = document.getElementById('offer-letter-generate-btn');
  if (btn) {
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Generating...';
    // Scroll the spinner into view so the user sees progress (esp. on mobile,
    // where the footer may be below the fold of a tall fullscreen modal).
    try { btn.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch {}
  }

  // Installed PWA / iOS-Safari standalone detector (mirrors app.js ~L24750).
  // On standalone, clicking a blob: <a download> navigates the single webview
  // away from the SPA and ejects the user out of HR — so we skip the blob
  // auto-download and offer a user-tapped link instead (no navigation hijack).
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

  try {
    // 1. Build the merge payload + apply to template.
    const template = _getOfferLetterTemplate();
    const payload = _buildOfferLetterMergePayload(candidate, { payRate, startDate, reportsTo, positionTitle: position });
    const mergedText = _mergeOfferLetterTemplate(template, payload);

    // 2. Render PDF.
    const olm = await _olTerms();
    const blob = await olm._renderOfferLetterPdf(mergedText, candidate.name || 'candidate');
    const sizeBytes = blob.size;

    // 3. Persist user inputs so re-generation doesn't re-prompt. TASK-1496-S11:
    //    to peopleComp ONLY (pay terms; `people` is broadcast to driver PINs).
    //    Non-fatal - the PDF is still useful - but never silent.
    // Mirror locally so the modal pre-fill works on next open.
    // TASK-10127: keyed on the hired person once candidates/{C}.personId links
    // one, else the applicant (the hire carries applicant-keyed terms + PDF row
    // to the person). TASK-10178: save() and attachRow() each re-read that link
    // inside their own transaction, so a hire landing mid-generation never
    // strands the terms or the row on the applicant.
    const live = HRState.candidates.find(c => c.id === candidateId) || candidate;
    live.offerLetterData = candidate.offerLetterData = { position, payRate, startDate, reportsTo };
    // save() toasts its own failure and never throws.
    const saved = await olm.save(tenantId, live, candidate.offerLetterData);
    const ownerId = saved.ownerId || olm.offerSubject(live).ownerId;

    // 4. Upload PDF to storage. Mirrors uploadEmployeeDocument path pattern.
    const ts = Date.now();
    const safeName = (candidate.name || 'candidate').replace(/[^a-zA-Z0-9._-]/g, '_');
    const fileName = `Offer Letter - ${candidate.name || 'Candidate'}.pdf`;
    const storagePath = `hr/${tenantId}/${ownerId}/offer-letter-${ts}_${safeName}.pdf`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, blob, { contentType: 'application/pdf' });
    // TASK-1029 — PATH-ONLY: no getDownloadURL bearer `url` persisted on the
    // documents row; the Documents tab opens it via getComplianceDocBytes.
    // The standalone tap-link below uses the in-memory blob directly.

    // 5. Auto-attach to documents subcollection. TASK-10127: category 'general'
    //    (a non-DQ bucket; row read gate unchanged), NOT 'employment_app' — a DQ-file
    //    §391.21 slot (DQ_FILE_CATEGORY_KEYS), so an offer letter (with PAY)
    //    would show to compliance/partner DQ views as the employment application.
    //    TASK-10178: the row's owner is the link as attachRow's transaction reads
    //    it (the PDF's folder may be the applicant's — the same shape a carried row has).
    const userName = window.AppState?.userName || window.AppState?.user?.email || 'Unknown';
    await olm.attachRow(tenantId, live,
      {
        fileName,
        storagePath,
        mimeType: 'application/pdf',
        isPdf: true,
        sizeBytes,
        category: 'general',
        uploadedAt: Timestamp.now(),
        uploadedBy: userName,
        // TASK-321 marker - lets future "Offer Letter History" view filter.
        source: 'offer_letter_generated',
      }
    );

    // 6. Audit log.
    try {
      window.logAudit?.('hr', 'OFFER_LETTER_GENERATED', {
        entityType: 'person',
        entityId: candidateId,
        source: 'hr-candidate-offer-letter',
        redFlags: { payRate, startDate, reportsTo, sizeBytes },
      });
    } catch {}

    // 7. Deliver the PDF to the user.
    if (isStandalone) {
      // MOBILE / installed PWA: do NOT auto-click a blob: <a download> — that
      // navigates the single webview away from the SPA and ejects the user out
      // of HR (TASK-634). Swap the generate button for a user-tapped link (a
      // real tap = user gesture, no popup block / no navigation hijack —
      // mirrors createNewHirePacket() ~L4793). Leave the modal OPEN so the user
      // has something to tap. TASK-1029 — the link is the IN-MEMORY blob (the
      // PDF we just rendered), not a persisted storage bearer URL.
      const swapBtn = document.getElementById('offer-letter-generate-btn');
      if (swapBtn) {
        const tapUrl = URL.createObjectURL(blob);
        swapBtn.outerHTML = `
          <a href="${escapeHtml(tapUrl)}" target="_blank" rel="noopener" class="btn btn-primary" style="text-decoration:none;display:inline-flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:16px;">download</span> View / Download offer letter
          </a>`;
        try { document.querySelector('#modal-offer-letter-generate a.btn-primary')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }); } catch {}
      }
      window.showToast?.('Offer letter saved to Documents', 'success');
    } else {
      // DESKTOP: auto-download for convenience, then close the modal (unchanged).
      try {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { try { a.remove(); URL.revokeObjectURL(blobUrl); } catch {} }, 1000);
      } catch (dlErr) {
        console.warn('[OfferLetter] Download trigger failed:', dlErr);
      }

      window.showToast?.('Offer letter saved to Documents', 'success');
      document.getElementById('modal-offer-letter-generate')?.remove();
    }
  } catch (err) {
    console.error('[OfferLetter] Generate failed:', err);
    window.showToast?.(`Generate failed: ${err?.message || 'unknown error'}`, 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">picture_as_pdf</span> Generate PDF'; }
  }
}

// TASK-221 Slice 3 — Send self-serve hiring packet link
async function sendHiringPacketLink(personId) {
  if (!personId) return;
  const tenantId = HRState.tenantId;
  if (!tenantId) { window.showToast?.('No tenant', 'error'); return; }
  const emp = (HRState.people || []).find(p => p.id === personId);
  const personEmail = (emp?.email || emp?.personalEmail || '').trim();
  if (!personEmail) {
    window.showToast?.('No email on file for this employee — add one first', 'error');
    return;
  }
  const ok = window.confirm
    ? window.confirm(`Email a self-serve hiring packet link to ${personEmail}?\n\nThey'll get a secure link valid for 14 days.`)
    : true;
  if (!ok) return;
  try {
    const callable = httpsCallable(functions, 'sendHiringPacketLink');
    const res = await callable({ tenantId, personId });
    if (res?.data?.emailed) {
      window.showToast?.(`Hiring packet link sent to ${personEmail}`, 'success');
    } else {
      window.showToast?.(`Link generated but email failed: ${res?.data?.error || 'unknown'}`, 'error');
    }
  } catch (err) {
    console.error('[sendHiringPacketLink] Error:', err);
    window.showToast?.(`Failed: ${err.message || err}`, 'error');
  }
}

// TASK-1304 — Request a §395.8(j)(2) Driver Statement of On-Duty Hours. Sends the
// driver a tokenized phone link (server mints the request with a SERVER-BOUND
// personId); each call = a new request = a new dated statement (re-requestable for
// intermittent drivers). The filed PDF surfaces in this driver's DQ documents.
async function requestSevenDayStatement(personId) {
  if (!personId) return;
  const tenantId = HRState.tenantId;
  if (!tenantId) { window.showToast?.('No tenant', 'error'); return; }
  const emp = (HRState.people || []).find(p => p.id === personId);
  const hasEmail = !!(emp?.email || emp?.personalEmail);
  const hasPhone = !!(emp?.phone || emp?.cellPhone);
  if (!hasEmail && !hasPhone) {
    window.showToast?.('No email or phone on file for this driver — add one first', 'error');
    return;
  }
  const via = hasEmail && hasPhone ? 'email and text' : (hasEmail ? 'email' : 'text');
  const ok = window.confirm
    ? window.confirm(`Send ${emp?.name || 'this driver'} a link (by ${via}) to complete their 7-day on-duty hours statement?\n\nThey'll sign it on their phone; a dated PDF files into their DQ file. Link valid 30 days.`)
    : true;
  if (!ok) return;
  try {
    const callable = httpsCallable(functions, 'requestSevenDayStatement');
    const res = await callable({ tenantId, personId });
    const d = res?.data || {};
    if (d.emailed || d.texted) {
      window.showToast?.(`7-day hours statement request sent${d.emailed ? ' by email' : ''}${d.emailed && d.texted ? ' and' : ''}${d.texted ? ' by text' : ''}.`, 'success');
    } else {
      window.showToast?.('Request created but delivery failed — check the driver\'s contact info.', 'error');
    }
  } catch (err) {
    console.error('[requestSevenDayStatement] Error:', err);
    window.showToast?.(`Failed: ${err.message || err}`, 'error');
  }
}

// =============================================================================
// TASK-263 — DRIVER MEMOS (Phase 1: MVP)
// Sender UI: "Send Memo" modal + Memos sub-view with status table.
// Server: createMemoAndSend / peekMemoToken / streamMemoPdf / confirmMemoReceipt
// (functions/src/index.js bottom of file).
// =============================================================================

const MemoState = {
  memos: [],                  // memo docs
  memosUnsub: null,
  receiptsByMemo: {},         // memoId -> { receiptId: receiptDoc }
  receiptUnsubs: {},          // memoId -> unsubscribe fn
  selectedRecipientIds: new Set(),
  pickerSearch: '',
  pickerOpen: false,
  pendingPdfFile: null,
  sendMethod: 'standard',     // TASK-1044 — 'standard' | 'text-direct'
  drillMemoId: null,
  // TASK-9841 — saved messages (server-written memoTemplates), schedule mode
  templates: [],
  templatesUnsub: null,
  scheduleMode: false,        // false = send now, true = schedule (tenant tz)
  selectedTemplateId: '',
  // TASK-9980 — double-send guard. sendInFlight survives closing + reopening
  // the composer (the Send button stays disabled until the call settles);
  // sendDone turns Send into "Done" so a finished send can't be re-pressed;
  // resendOf = the memo "Send again" was opened from (server keys it apart).
  sendInFlight: false,
  sendDone: false,
  sendTicker: null,
  resendOf: null,
};

// TASK-9841 — the tenant's zone (tenant.timezone → settings.timezone → Chicago),
// the SAME resolution the server uses to interpret a scheduled time.
function _memoTenantTz() {
  const t = window.AppState?.tenantData || {};
  const raw = (typeof t.timezone === 'string' && t.timezone) || (t.settings && typeof t.settings.timezone === 'string' && t.settings.timezone) || 'America/Chicago';
  try { new Intl.DateTimeFormat('en-US', { timeZone: raw }); return raw; } catch (_) { return 'America/Chicago'; }
}
// 'YYYY-MM-DDTHH:mm' (tenant wall clock) → "Oct 15, 2026, 8:00 AM"
function _memoFmtLocalStr(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(String(s || ''));
  if (!m) return '';
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]));
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
// The memo's planned time for display: the stored wall-clock string (exact,
// no zone math) plus the zone it was scheduled in.
function _memoFmtScheduled(m) {
  if (!m) return '';
  const when = m.scheduledFor ? _memoFmtLocalStr(m.scheduledFor) : _memoFmtTs(m.scheduledAt || m.sentAt);
  const tz = m.scheduledTz || _memoTenantTz();
  return when ? `${when} (${tz})` : '';
}
// Local-now as a datetime-local value (for the input's min attribute).
function _memoNowLocalInputValue() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const MEMO_STATUS_LABELS = {
  scheduled: 'Scheduled', sending: 'Sending…', sent: 'Sent', cancelled: 'Cancelled', failed: 'Failed',
};
function _memoStatusPill(m) {
  const st = m?.status || 'sent';
  if (st === 'scheduled') {
    return `<span style="display:inline-block;padding:3px 10px;border-radius:999px;background:#DBEAFE;color:#1E40AF;font-size:0.78rem;font-weight:600;" title="${_memoEscape(_memoFmtScheduled(m))}">Scheduled</span>`;
  }
  if (st === 'sending') return '<span style="display:inline-block;padding:3px 10px;border-radius:999px;background:#FEF3C7;color:#92400E;font-size:0.78rem;font-weight:600;">Sending…</span>';
  if (st === 'cancelled') return `<span style="display:inline-block;padding:3px 10px;border-radius:999px;background:#F3F4F6;color:#374151;font-size:0.78rem;font-weight:600;" title="${_memoEscape(m.cancelReason || '')}">Cancelled</span>`;
  if (st === 'failed') return `<span style="display:inline-block;padding:3px 10px;border-radius:999px;background:#FEE2E2;color:#991B1B;font-size:0.78rem;font-weight:600;" title="${_memoEscape(m.failReason || '')}">Failed</span>`;
  return '';
}

// TASK-9840 — who may SEND a memo. Client mirror of the server
// _memoSenderDecision (createMemoAndSend): demo, the OWNER via
// tenants/{tid}.ownerId, or an active, non-readOnly member whose modules list
// holds 'hr'. members.role is never read. View-only HR logins CAN send (sending
// is a create). The UI may hide more than the server, never offer more.
const MEMO_SEND_DENIED_MSG = 'Only the account owner or someone with HR access can send memos.';
function canSendMemos() {
  const st = window.AppState || {};
  if (st.isDemo || st.tenant === 'demo') return true;
  if (st.driverMode || st.isPublicAccess || st.driverModePin) return false;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('hr');
}

// TASK-10210 — Assign Training (a memo with kind 'training'): the LAZY
// hrTraining.js (perf: hr.js is a static chunk). It reuses the memo pieces below.
function openTrainingAssign() {
  if (_hrGateComplianceOnly('memos')) return;
  __ddChunkWrap(import('./hrTraining.js?v=1')).then((m) => m.open({
    db, fs: { collection, query, where, getDocs, limit }, call: (n) => httpsCallable(functions, n, { timeout: 150000 }),
    tid: HRState.tenantId, people: () => (HRState.peopleLoaded ? HRState.people || [] : null), eligible: _memoEligiblePeople,
    reach: _memoPersonReach, awaiting: _memoAwaitingReply, plan: _memoReminderPlan, names: _memoNameList, esc: _memoEscape, canSend: canSendMemos(),
  })).catch((e) => { console.warn('[HR] training', e); window.showToast?.('Could not open Assign Training. Reload and try again.', 'error'); });
}

function openMemosSubView() {
  if (_hrGateComplianceOnly('memos')) return; // TASK-1117 — memos are post-hire employee mgmt (paywalled)
  HRState.currentView = 'memos';
  if (!HRState.tenantId) return;
  const container = document.getElementById('hr-content') || document.querySelector('.hr-view');
  if (!container) return;
  container.innerHTML = getMemosSubViewHTML();
  subscribeMemos();
}

function backToHRLandingFromMemos() {
  unsubscribeMemos();
  backToHRLanding();
}

function getMemosSubViewHTML() {
  return `
    <div class="hr-sub-view-header" style="display:flex;align-items:center;gap:12px;padding:16px 20px;border-bottom:1px solid var(--border-light);background:var(--bg-elevated);">
      <button class="btn-icon" onclick="window.HRModule.backToHRLandingFromMemos()" title="Back">
        <span class="material-icons">arrow_back</span>
      </button>
      <h2 style="font-size:1.15rem;font-weight:700;margin:0;flex:1;">Driver Memos</h2>
      ${canSendMemos() ? `<button class="btn btn-primary" onclick="window.HRModule.openNewMemoModal()">
        <span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;">campaign</span>
        New Memo
      </button>` : ''}
    </div>
    <div style="padding:20px;">
      ${canSendMemos() ? '' : `<div id="memo-send-denied" style="background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:8px;padding:10px 14px;margin-bottom:12px;color:var(--text-primary);font-size:0.88rem;">${MEMO_SEND_DENIED_MSG}</div>`}
      <div style="background:#EFF6FF;border:1px solid #BFDBFE;border-radius:8px;padding:12px 14px;margin-bottom:16px;color:#1E40AF;font-size:0.88rem;">
        <strong>Driver Memos</strong> push a PDF or text bulletin to hand-picked recipients via email, with a "Read it" button so you can see who actually opened it — or choose <strong>Text it directly</strong> to land the message straight in their phones as a plain text (no link, no read receipt). Save a message you send often, schedule one for later, and open any memo to send it again or remind the people who haven't confirmed.
      </div>
      <div id="memos-list-host"><p style="color:var(--text-secondary);padding:18px 0;">Loading memos...</p></div>
    </div>

    <!-- New Memo Modal -->
    <div id="memo-modal" class="modal hidden">
      <div class="modal-content modal-fullscreen" style="max-width:760px;display:flex;flex-direction:column;max-height:90vh;position:relative;">
        <div class="modal-header" style="display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid var(--border-light);">
          <h3 style="margin:0;font-size:1.1rem;">New Memo</h3>
          <button class="btn-icon" onclick="window.HRModule.closeNewMemoModal()"><span class="material-icons">close</span></button>
        </div>
        <div class="modal-body" style="flex:1;overflow-y:auto;min-height:0;padding:20px;">
          <!-- TASK-9841 — saved messages -->
          <div style="margin-bottom:14px;display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap;">
            <div style="flex:1;min-width:200px;">
              <label style="display:block;font-weight:600;margin-bottom:6px;font-size:0.88rem;">Saved message <span style="color:var(--text-secondary);font-weight:400;">(optional)</span></label>
              <select id="memo-template-select" data-trailguide="memo-saved-messages" onchange="window.HRModule.applyMemoTemplate(this.value)" style="width:100%;padding:9px 12px;border:1px solid var(--border-medium);border-radius:8px;font:inherit;background:#fff;">
                <option value="">Start from scratch</option>
              </select>
            </div>
            <button type="button" class="btn btn-secondary btn-sm" data-trailguide="memo-save-template" onclick="window.HRModule.beginSaveMemoTemplate()">
              <span class="material-icons" style="font-size:15px;vertical-align:middle;">bookmark_add</span> Save as saved message
            </button>
            <button type="button" id="memo-template-delete" class="btn btn-secondary btn-sm" style="display:none;color:#B91C1C;" onclick="window.HRModule.deleteMemoTemplateAction()">Delete saved message</button>
          </div>
          <div id="memo-template-save-row" style="display:none;margin:-6px 0 14px;padding:10px 12px;background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:8px;">
            <label for="memo-template-name" style="display:block;font-weight:600;margin-bottom:6px;font-size:0.85rem;">Name this message</label>
            <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
              <input type="text" id="memo-template-name" maxlength="60" placeholder="e.g. Weekly safety reminder" style="flex:1;min-width:180px;padding:8px 12px;border:1px solid var(--border-medium);border-radius:8px;font:inherit;">
              <button type="button" class="btn btn-primary btn-sm" id="memo-template-save-btn" onclick="window.HRModule.confirmSaveMemoTemplate()">Save</button>
              <button type="button" class="btn btn-secondary btn-sm" onclick="window.HRModule.cancelSaveMemoTemplate()">Cancel</button>
            </div>
            <div id="memo-template-save-hint" style="margin-top:6px;font-size:0.8rem;color:var(--text-secondary);">Saves the title, body and send method — not the recipients or a PDF.</div>
          </div>
          <div style="margin-bottom:14px;">
            <label style="display:block;font-weight:600;margin-bottom:6px;font-size:0.88rem;">Title <span style="color:#DC2626;">*</span> <span id="memo-title-note" style="display:none;color:var(--text-secondary);font-weight:400;">(for your records — not included in the text)</span></label>
            <input type="text" id="memo-title" maxlength="120" placeholder="e.g. Updated Cell Phone Policy — Effective May 20" style="width:100%;padding:10px 12px;border:1px solid var(--border-medium);border-radius:8px;font:inherit;">
          </div>
          <div style="margin-bottom:14px;">
            <label style="display:block;font-weight:600;margin-bottom:6px;font-size:0.88rem;">Send as</label>
            <div style="display:flex;gap:14px;flex-wrap:wrap;">
              <label style="display:flex;align-items:center;gap:6px;font-size:0.9rem;cursor:pointer;"><input type="radio" name="memo-send-method" value="standard" checked onchange="window.HRModule.onMemoSendMethodChange('standard')"> Email w/ Read-it button</label>
              <label style="display:flex;align-items:center;gap:6px;font-size:0.9rem;cursor:pointer;"><input type="radio" name="memo-send-method" value="text-direct" onchange="window.HRModule.onMemoSendMethodChange('text-direct')"> Text it directly</label>
            </div>
            <!-- TASK-10027 muted-tenant UI hints (app.js paintTenantSendNotices) -->
            <div data-tenant-send-notice="memo" style="display:none;margin-top:8px;"></div>
            <div id="memo-method-hint" style="margin-top:6px;font-size:0.8rem;color:var(--text-secondary);display:none;">The message body lands as a plain text on each recipient's phone — no link, no read receipt. Only people with a phone number and SMS opt-in can receive it.</div>
            <!-- TASK-9891 — the owner's memo link length (Settings → Security), shown to the sender. -->
            <div id="memo-link-length-hint" style="margin-top:6px;font-size:0.8rem;color:var(--text-secondary);">The &ldquo;Open Memo&rdquo; link works for 24 hours. The account owner sets this in Settings → Security.</div>
          </div>
          <div style="margin-bottom:14px;">
            <label style="display:block;font-weight:600;margin-bottom:6px;font-size:0.88rem;">Body <span id="memo-body-optional" style="color:var(--text-secondary);font-weight:400;">(optional if PDF attached)</span><span id="memo-body-required" style="color:#DC2626;display:none;">*</span></label>
            <textarea id="memo-body" maxlength="4000" rows="5" placeholder="Optional plain-text body shown above the PDF on the driver-facing page." oninput="window.HRModule.onMemoBodyInput()" style="width:100%;padding:10px 12px;border:1px solid var(--border-medium);border-radius:8px;font:inherit;font-size:0.95rem;resize:vertical;min-height:100px;"></textarea>
            <div id="memo-sms-counter" style="display:none;margin-top:6px;font-size:0.82rem;color:var(--text-secondary);"></div>
          </div>
          <div style="margin-bottom:14px;" id="memo-pdf-block">
            <label style="display:block;font-weight:600;margin-bottom:6px;font-size:0.88rem;">PDF Attachment <span style="color:var(--text-secondary);font-weight:400;">(optional, max 10MB)</span></label>
            <input type="file" id="memo-pdf-input" accept="application/pdf,image/*" onchange="window.HRModule.onMemoPdfChange(event)" style="font:inherit;">
            <div id="memo-pdf-status" style="margin-top:6px;font-size:0.85rem;color:var(--text-secondary);"></div>
          </div>
          <div style="margin-bottom:8px;">
            <label style="display:block;font-weight:600;margin-bottom:6px;font-size:0.88rem;">Recipients <span style="color:#DC2626;">*</span></label>
            <div style="display:flex;gap:8px;align-items:center;margin-bottom:8px;flex-wrap:wrap;">
              <input type="search" id="memo-picker-search" placeholder="Search people..." oninput="window.HRModule.onMemoPickerSearch(event)" style="flex:1;min-width:180px;padding:8px 12px;border:1px solid var(--border-medium);border-radius:8px;font:inherit;">
              <button class="btn btn-secondary btn-sm" onclick="window.HRModule.selectAllForMemo()">Select all</button>
              <button class="btn btn-secondary btn-sm" onclick="window.HRModule.selectAllDriversForMemo()">All active drivers</button>
              <button class="btn btn-secondary btn-sm" onclick="window.HRModule.clearMemoRecipients()">Clear</button>
            </div>
            <div id="memo-picker-list" style="border:1px solid var(--border-light);border-radius:8px;max-height:240px;overflow-y:auto;overflow-anchor:none;background:#fff;"></div>
            <!-- TASK-10142 — who is picked, BELOW the list: ticking someone must never move the rows above it. -->
            <div id="memo-selected-chips" style="display:none;margin-top:8px;"></div>
            <div id="memo-recipient-count" style="margin-top:6px;font-size:0.82rem;color:var(--text-secondary);">0 selected</div>
            <!-- TASK-10112 — who gets it / who doesn't and why (window.SendFeedback) -->
            <div id="memo-send-preview" style="margin-top:6px;"></div>
          </div>
        </div>
        <div class="modal-footer" style="display:flex;gap:8px;justify-content:flex-end;align-items:center;flex-wrap:wrap;padding:14px 20px;border-top:1px solid var(--border-light);flex-shrink:0;">
          <!-- TASK-9841 — send now / schedule (tenant time zone) -->
          <div id="memo-when-block" style="flex:1 1 100%;display:flex;gap:14px;align-items:center;flex-wrap:wrap;font-size:0.88rem;">
            <label style="display:flex;align-items:center;gap:6px;cursor:pointer;"><input type="radio" name="memo-when" value="now" checked onchange="window.HRModule.onMemoWhenChange('now')"> Send now</label>
            <label style="display:flex;align-items:center;gap:6px;cursor:pointer;" data-trailguide="memo-schedule"><input type="radio" name="memo-when" value="later" onchange="window.HRModule.onMemoWhenChange('later')"> Schedule for later</label>
            <input type="datetime-local" id="memo-schedule-at" class="control-auto" style="display:none;padding:7px 10px;border:1px solid var(--border-medium);border-radius:8px;font:inherit;font-size:16px;">
            <span id="memo-schedule-tz" style="display:none;color:var(--text-secondary);font-size:0.8rem;"></span>
          </div>
          <div id="memo-send-status" role="status" aria-live="polite" style="display:none;flex:1 1 100%;font-size:0.9rem;color:var(--text-primary);background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:8px;padding:10px 14px;"></div>
          <div id="memo-send-error" role="alert" style="display:none;flex:1 1 100%;color:#B91C1C;font-size:0.85rem;"></div>
          <button class="btn btn-secondary" id="memo-cancel-btn" onclick="window.HRModule.closeNewMemoModal()">Cancel</button>
          <button class="btn btn-primary" id="memo-send-btn" onclick="window.HRModule.submitNewMemo()">
            <span class="material-icons" style="font-size:16px;vertical-align:middle;">send</span>
            Send Memo
          </button>
        </div>
        <!-- TASK-10142 — review every recipient by name before anything goes out (a text can't be unsent). -->
        <div id="memo-review" role="dialog" aria-modal="true" aria-labelledby="memo-review-title" style="display:none;position:absolute;inset:0;z-index:20;background:var(--bg-primary,#fff);border-radius:inherit;flex-direction:column;"></div>
      </div>
    </div>

    <!-- Drill-down Modal -->
    <div id="memo-drill-modal" class="modal hidden">
      <div class="modal-content modal-fullscreen" style="max-width:760px;display:flex;flex-direction:column;max-height:90vh;">
        <div class="modal-header" style="display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid var(--border-light);">
          <h3 id="memo-drill-title" style="margin:0;font-size:1.05rem;flex:1;">Memo details</h3>
          <button class="btn-icon" onclick="window.HRModule.closeMemoDrillModal()"><span class="material-icons">close</span></button>
        </div>
        <div class="modal-body" id="memo-drill-body" style="flex:1;overflow-y:auto;min-height:0;padding:18px 20px;">Loading...</div>
      </div>
    </div>
  `;
}

function subscribeMemos() {
  unsubscribeMemos();
  const tenantId = HRState.tenantId;
  if (!tenantId) return;
  try {
    const q = query(collection(db, 'tenants', tenantId, 'memos'), orderBy('sentAt', 'desc'), limit(100));
    MemoState.memosUnsub = onSnapshot(q, (snap) => {
      MemoState.memos = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      renderMemosList();
    }, (err) => {
      console.warn('[memos] subscribe failed:', err.message);
      MemoState.memos = [];
      renderMemosList();
    });
  } catch (err) {
    console.warn('[memos] subscribe error:', err.message);
  }
  subscribeMemoTemplates();
}

// TASK-9841 — saved messages. Server-written (saveMemoTemplate), read here.
function subscribeMemoTemplates() {
  try { if (MemoState.templatesUnsub) MemoState.templatesUnsub(); } catch (_) {}
  MemoState.templatesUnsub = null;
  const tenantId = HRState.tenantId;
  if (!tenantId) return;
  try {
    const q = query(collection(db, 'tenants', tenantId, 'memoTemplates'), orderBy('name'), limit(100));
    MemoState.templatesUnsub = onSnapshot(q, (snap) => {
      MemoState.templates = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      renderMemoTemplateOptions();
    }, (err) => {
      console.warn('[memos] templates subscribe failed:', err.message);
      MemoState.templates = [];
      renderMemoTemplateOptions();
    });
  } catch (err) {
    console.warn('[memos] templates subscribe error:', err.message);
  }
}

function renderMemoTemplateOptions() {
  const sel = document.getElementById('memo-template-select');
  if (!sel) return;
  const current = MemoState.selectedTemplateId || '';
  sel.innerHTML = '<option value="">Start from scratch</option>' + MemoState.templates.map((t) =>
    `<option value="${_memoEscape(t.id)}">${_memoEscape(t.name || t.title || '(unnamed)')}</option>`).join('');
  sel.value = MemoState.templates.some(t => t.id === current) ? current : '';
  if (sel.value !== current) MemoState.selectedTemplateId = sel.value;
  const del = document.getElementById('memo-template-delete');
  if (del) del.style.display = sel.value ? '' : 'none';
}

function applyMemoTemplate(templateId) {
  MemoState.selectedTemplateId = templateId || '';
  const del = document.getElementById('memo-template-delete');
  if (del) del.style.display = templateId ? '' : 'none';
  if (!templateId) return;
  const t = MemoState.templates.find(x => x.id === templateId);
  if (!t) return;
  const titleEl = document.getElementById('memo-title');
  const bodyEl = document.getElementById('memo-body');
  if (titleEl) titleEl.value = t.title || '';
  if (bodyEl) bodyEl.value = t.body || '';
  const method = t.sendMethod === 'text-direct' ? 'text-direct' : 'standard';
  const radio = document.querySelector(`input[name="memo-send-method"][value="${method}"]`);
  if (radio) radio.checked = true;
  onMemoSendMethodChange(method);
}

function beginSaveMemoTemplate() {
  const row = document.getElementById('memo-template-save-row');
  const nameEl = document.getElementById('memo-template-name');
  const title = document.getElementById('memo-title')?.value.trim() || '';
  if (!title) { window.showToast?.('Write a title first — that is what gets saved', 'error'); return; }
  if (!row || !nameEl) return;
  const existing = MemoState.selectedTemplateId ? MemoState.templates.find(t => t.id === MemoState.selectedTemplateId) : null;
  nameEl.value = existing ? (existing.name || '') : title.slice(0, 60);
  const hint = document.getElementById('memo-template-save-hint');
  if (hint) {
    hint.textContent = existing
      ? `Updates "${existing.name || existing.title}" with what is in the form now. Saves the title, body and send method — not the recipients or a PDF.`
      : 'Saves the title, body and send method — not the recipients or a PDF.';
  }
  row.style.display = '';
  nameEl.focus();
}

function cancelSaveMemoTemplate() {
  const row = document.getElementById('memo-template-save-row');
  if (row) row.style.display = 'none';
}

async function confirmSaveMemoTemplate() {
  if (!canSendMemos()) { window.showToast?.(MEMO_SEND_DENIED_MSG, 'error'); return; }
  const btn = document.getElementById('memo-template-save-btn');
  if (btn?.disabled) return;
  const name = document.getElementById('memo-template-name')?.value.trim() || '';
  const title = document.getElementById('memo-title')?.value.trim() || '';
  const body = document.getElementById('memo-body')?.value.trim() || '';
  if (!name) { window.showToast?.('Give the saved message a name', 'error'); return; }
  if (!title) { window.showToast?.('Title is required', 'error'); return; }
  if (MemoState.sendMethod === 'text-direct' && !body) { window.showToast?.('A text-direct message needs a body', 'error'); return; }
  if (btn) btn.disabled = true;
  try {
    const callable = httpsCallable(functions, 'saveMemoTemplate');
    const res = await callable({
      tenantId: HRState.tenantId,
      templateId: MemoState.selectedTemplateId || null,
      name, title, body,
      sendMethod: MemoState.sendMethod || 'standard',
    });
    const id = res?.data?.templateId || '';
    if (id) MemoState.selectedTemplateId = id;
    window.showToast?.(`Saved "${name}"`, 'success');
    cancelSaveMemoTemplate();
    renderMemoTemplateOptions();
  } catch (err) {
    console.error('[confirmSaveMemoTemplate]', err);
    window.showToast?.(`Not saved — ${err?.message || 'unknown error'}`, 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

async function deleteMemoTemplateAction() {
  const id = MemoState.selectedTemplateId;
  const t = id ? MemoState.templates.find(x => x.id === id) : null;
  if (!t) return;
  const go = typeof window.showConfirm === 'function'
    ? await window.showConfirm('Delete saved message?', `"${t.name || t.title}" will be removed from the list. Memos already sent from it are not affected.`, { confirmText: 'Delete', danger: true })
    : window.confirm(`Delete saved message "${t.name || t.title}"?`);
  if (!go) return;
  try {
    const callable = httpsCallable(functions, 'deleteMemoTemplate');
    await callable({ tenantId: HRState.tenantId, templateId: id });
    MemoState.selectedTemplateId = '';
    renderMemoTemplateOptions();
    window.showToast?.('Saved message deleted', 'success');
  } catch (err) {
    console.error('[deleteMemoTemplateAction]', err);
    window.showToast?.(`Not deleted — ${err?.message || 'unknown error'}`, 'error');
  }
}

// TASK-9841 — "Send now" vs "Schedule for later" (tenant time zone).
function onMemoWhenChange(mode) {
  MemoState.scheduleMode = mode === 'later';
  const input = document.getElementById('memo-schedule-at');
  const tzEl = document.getElementById('memo-schedule-tz');
  const btn = document.getElementById('memo-send-btn');
  if (input) {
    input.style.display = MemoState.scheduleMode ? '' : 'none';
    input.min = _memoNowLocalInputValue();
    if (MemoState.scheduleMode && !input.value) {
      // Default: next whole hour (local clock) — the sender picks the real time.
      const d = new Date(Date.now() + 60 * 60 * 1000);
      d.setMinutes(0, 0, 0);
      const pad = (n) => String(n).padStart(2, '0');
      input.value = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
  }
  if (tzEl) {
    tzEl.style.display = MemoState.scheduleMode ? '' : 'none';
    tzEl.textContent = `Company time (${_memoTenantTz()}) · between 8:00 AM and 9:00 PM`;
  }
  // TASK-9980 (COD UX) — after a finished send, "Cancel" beside "Done" reads
  // like it could undo the send; hide it.
  const cancelBtn = document.getElementById('memo-cancel-btn');
  if (cancelBtn) cancelBtn.style.display = MemoState.sendDone ? 'none' : '';
  if (btn && !btn.disabled) {
    btn.innerHTML = MemoState.sendDone
      ? '<span class="material-icons" style="font-size:16px;vertical-align:middle;">check</span> Done'
      : MemoState.scheduleMode
        ? '<span class="material-icons" style="font-size:16px;vertical-align:middle;">schedule_send</span> Schedule Memo'
        : '<span class="material-icons" style="font-size:16px;vertical-align:middle;">send</span> Send Memo';
  }
  renderMemoSendPreview(); // quiet-hours line only applies to "Send now"
}

// TASK-10112 — the shared send preview / progress / result component, lazy
// (first memo composer open). Same file + ?v= as dispatch.js and notes.js;
// read through window.SendFeedback only (Pitfall #1).
let _memoSendFeedbackLoad = null;
function _memoLoadSendFeedback() {
  if (window.SendFeedback) return Promise.resolve(window.SendFeedback);
  if (!_memoSendFeedbackLoad) {
    _memoSendFeedbackLoad = __ddChunkWrap(import('../utils/sendFeedback.js?v=10112001')).then(() => window.SendFeedback)
      .catch((e) => { _memoSendFeedbackLoad = null; throw e; });
  }
  return _memoSendFeedbackLoad;
}

// TASK-10112 — BEFORE send: who gets this memo and who does not (from the
// SAME reach rule the picker and the server use, _memoPersonReach), and what
// the texts cost. The text-direct size + credit line stays in
// #memo-sms-counter and the START line in #memo-recipient-count; this adds the
// names and "Not receiving this: ...".
function renderMemoSendPreview() {
  const host = document.getElementById('memo-send-preview');
  if (!host) return;
  const SF = window.SendFeedback;
  if (!SF) { _memoLoadSendFeedback().then(() => renderMemoSendPreview()).catch(() => {}); return; }
  const ids = Array.from(MemoState.selectedRecipientIds);
  if (!ids.length) { host.innerHTML = ''; return; }
  const textDirect = MemoState.sendMethod === 'text-direct';
  const byId = new Map((HRState.people || []).map((p) => [p.id, p]));
  const recipients = [];
  const noPhone = []; const noOptIn = []; const noContact = []; const gone = [];
  let smsCount = 0;
  for (const id of ids) {
    const p = byId.get(id);
    if (!p || p.active === false || p.terminated) { gone.push(p?.name || 'Someone'); continue; }
    const reach = _memoPersonReach(p);
    if (textDirect) {
      if (reach.canSms) { recipients.push({ name: p.name || '(no name)' }); smsCount++; }
      else if (!reach.hasPhone) noPhone.push(p.name || '(no name)');
      else noOptIn.push(p.name || '(no name)');
    } else if (reach.reachable) {
      recipients.push({ name: p.name || '(no name)' });
      if (reach.canSms) smsCount++;
    } else noContact.push(p.name || '(no name)');
  }
  host.innerHTML = SF.previewHtml({
    recipients,
    linkWarn: false,   // the START line is already in #memo-recipient-count
    excluded: [
      { label: 'no phone', count: noPhone.length, names: noPhone },
      { label: 'no SMS opt-in', count: noOptIn.length, names: noOptIn },
      { label: 'no phone or email', count: noContact.length, names: noContact },
      { label: 'no longer on the roster', count: gone.length, names: gone },
    ],
    empty: textDirect ? 'Nobody you picked can get a text.' : 'Nobody you picked can be reached.',
  }) + (!textDirect && smsCount > 0
    ? `<div style="margin-top:6px;font-size:0.8rem;color:var(--text-secondary,#475569);"><span class="material-icons" style="font-size:14px;vertical-align:-2px;" aria-hidden="true">sms</span> ~${smsCount} credit${smsCount === 1 ? '' : 's'}: ${smsCount} ${smsCount === 1 ? 'person gets' : 'people get'} the link by text (1 credit each). Email is free.</div>`
    : '')
    // Quiet hours (Chris 2026-10-26) — only when someone gets a TEXT and it
    // goes now (a scheduled memo already refuses 9 PM-8 AM on the server).
    + (MemoState.scheduleMode ? '' : SF.quietHoursHtml(SF.quietHoursWarning({ nowMs: Date.now(), tz: _memoTenantTz(), count: smsCount })));
}

// Quiet hours (Chris 2026-10-26) — how many of the picked people get THIS memo
// by text (text-direct: all reachable; standard: those whose link goes by SMS).
function _memoSmsCount(ids) {
  const byId = new Map((HRState.people || []).map((p) => [p.id, p]));
  let n = 0;
  for (const id of ids) {
    const p = byId.get(id);
    if (p && p.active !== false && !p.terminated && _memoPersonReach(p).canSms) n++;
  }
  return n;
}

// TASK-9980 — the status line under the composer (progress / done / duplicate).
// tone: 'progress' | 'ok' | 'warn'. Text only (textContent) — never HTML.
function _setMemoSendStatus(msg, tone = 'progress') {
  const el = document.getElementById('memo-send-status');
  if (!el) return;
  el.textContent = msg || '';
  el.style.display = msg ? 'block' : 'none';
  el.style.borderLeft = '1px solid var(--border-light)'; // TASK-10112 — undo a finished run's accent
  el.style.borderColor = tone === 'ok' ? '#16A34A' : tone === 'warn' ? '#D97706' : 'var(--border-light)';
}

// TASK-9980 — pure: what the sender is told once createMemoAndSend answers.
// `done` = the composer switches to its Done state (Send can't be re-pressed).
function _memoSendOutcome(data, { textDirect = false, fmtTime = (ms) => new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) } = {}) {
  const d = data || {};
  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  if (d.ok && d.duplicate) {
    const at = d.firstSentAtMs ? fmtTime(d.firstSentAtMs) : 'a few minutes ago';
    if (d.stillSending) {
      return { done: true, tone: 'warn', toastType: 'info', message: `This memo is already going out (started at ${at}). It will not be sent twice — check the memo list in a minute.` };
    }
    const counts = (typeof d.sentCount === 'number' && typeof d.totalRecipients === 'number') ? ` — reached ${d.sentCount} of ${d.totalRecipients}` : '';
    return { done: true, tone: 'warn', toastType: 'info', message: `This memo was already sent at ${at}${counts}. It was not sent again. To send it on purpose, open it in the list and use Send again.` };
  }
  if (d.ok && d.scheduled) return { done: true, tone: 'ok', toastType: 'success', message: null };
  if (d.ok) {
    const total = Number(d.totalRecipients) || 0;
    const sent = Number(d.sentCount) || 0;
    const missed = Math.max(0, total - sent);
    const skipped = Number(d.skippedCount) || 0;
    let message = `${textDirect ? 'Texted' : 'Sent to'} ${sent} of ${plural(total, 'person', 'people')}.`;
    if (missed) message += ` ${plural(missed, 'person', 'people')} could not be reached — open the memo to see who.`;
    if (skipped) message += ` ${skipped} no longer on the roster.`;
    return { done: true, tone: missed ? 'warn' : 'ok', toastType: 'success', message };
  }
  return { done: false, tone: 'warn', toastType: 'error', message: 'Failed to send memo' };
}

// TASK-10112 — createMemoAndSend's answer -> the shared result shape (mapping
// in the lazy chunk, SendFeedback.fromMemo; only called when a run exists).
function _memoRunResult(data, textDirect) {
  return window.SendFeedback.fromMemo(data, textDirect);
}

// TASK-9980 — pure: an error where we do NOT know whether the memo went out
// (the client gave up waiting, or the connection dropped mid-call). The server
// may still be sending, so the composer must not offer Send again.
function _memoIsUncertainCallError(err) {
  const code = String((err && err.code) || '').replace(/^functions\//, '');
  return code === 'deadline-exceeded' || code === 'unavailable';
}

function unsubscribeMemos() {
  try { if (MemoState.memosUnsub) MemoState.memosUnsub(); } catch (_) {}
  MemoState.memosUnsub = null;
  try { if (MemoState.templatesUnsub) MemoState.templatesUnsub(); } catch (_) {}
  MemoState.templatesUnsub = null;
  Object.values(MemoState.receiptUnsubs).forEach((fn) => { try { fn(); } catch (_) {} });
  MemoState.receiptUnsubs = {};
}

function _memoEscape(s) {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
}

function _memoFmtTs(ts) {
  if (!ts) return '';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (_) { return ''; }
}

function renderMemosList() {
  const host = document.getElementById('memos-list-host');
  if (!host) return;
  if (!MemoState.memos.length) {
    host.innerHTML = `
      <div style="text-align:center;padding:48px 24px;color:var(--text-secondary);">
        <span class="material-icons" style="font-size:48px;color:#CBD5E1;">campaign</span>
        <p style="margin-top:12px;">No memos sent yet. Click <strong>New Memo</strong> to broadcast a PDF or bulletin with read receipts.</p>
      </div>`;
    return;
  }
  const rows = MemoState.memos.map((m) => {
    const recipientCount = m.recipientCount || 0;
    const confirmedCount = m.confirmedCount || 0;
    const pct = recipientCount > 0 ? Math.round((confirmedCount / recipientCount) * 100) : 0;
    let pillBg = '#FEF3C7', pillColor = '#92400E';
    if (recipientCount > 0 && confirmedCount === recipientCount) { pillBg = '#D1FAE5'; pillColor = '#065F46'; }
    else if (confirmedCount === 0) { pillBg = '#FEE2E2'; pillColor = '#991B1B'; }
    // TASK-1044: text-direct memos have NO ack mechanism — a confirmed count
    // would sit at a misleading 0/N forever. Show delivery instead.
    // TASK-9841 — a memo that has not gone out yet (scheduled / sending /
    // cancelled / failed) shows its state instead of a 0/N confirmed pill.
    const notSent = m.status && m.status !== 'sent';
    const confirmCell = notSent
      ? _memoStatusPill(m)
      : m.requiresAck === false
      ? `<span style="display:inline-block;padding:3px 10px;border-radius:999px;background:#F1F5F9;color:#475569;font-size:0.78rem;font-weight:600;" title="Sent as a direct text — no read tracking">
          Delivered · no read tracking
        </span>`
      : `<span style="display:inline-block;padding:3px 10px;border-radius:999px;background:${pillBg};color:${pillColor};font-size:0.78rem;font-weight:600;">
          ${confirmedCount} / ${recipientCount} (${pct}%)
        </span>`;
    const whenCell = m.status === 'scheduled'
      ? `<div>${_memoEscape(_memoFmtScheduled(m))}</div>
         ${canSendMemos() ? `<button type="button" class="btn btn-secondary btn-sm" style="margin-top:6px;" onclick="window.HRModule.cancelScheduledMemoAction('${escJsAttr(m.id)}', event)">Cancel</button>` : ''}`
      : m.status === 'cancelled' || m.status === 'failed'
      ? `<div>Was ${_memoEscape(_memoFmtScheduled(m) || _memoFmtTs(m.sentAt))}</div>`
      : _memoEscape(_memoFmtTs(m.sentAt));
    return `
      <tr style="cursor:pointer;border-top:1px solid var(--border-light);" onclick="window.HRModule.openMemoDrillModal('${escJsAttr(m.id)}')">
        <td style="padding:12px 14px;">
          <div style="font-weight:600;color:var(--text-primary);">${_memoEscape(m.title || '(untitled)')}</div>
          ${m.sendMethod === 'text-direct' ? '<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;"><span class="material-icons" style="font-size:12px;vertical-align:middle;">sms</span> Sent as text</div>' : ''}
          ${m.kind === 'training' ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;"><span class="material-icons" style="font-size:12px;vertical-align:middle;">school</span> Training${m.dueDate ? ` · due ${_memoEscape(m.dueDate)}` : ''}</div>` : ''}
          ${m.pdfStoragePath ? '<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;"><span class="material-icons" style="font-size:12px;vertical-align:middle;">attach_file</span> PDF attached</div>' : ''}
          ${m.reminderCount > 0 ? `<div style="font-size:0.75rem;color:var(--text-secondary);margin-top:2px;"><span class="material-icons" style="font-size:12px;vertical-align:middle;">notifications_active</span> Reminded ${m.reminderCount}×</div>` : ''}
        </td>
        <td style="padding:12px 14px;font-size:0.85rem;color:var(--text-secondary);white-space:nowrap;">${whenCell}</td>
        <td style="padding:12px 14px;text-align:center;font-weight:600;">${recipientCount}</td>
        <td style="padding:12px 14px;text-align:center;">
          ${confirmCell}
        </td>
      </tr>`;
  }).join('');
  host.innerHTML = `
    <div style="background:#fff;border:1px solid var(--border-light);border-radius:8px;overflow:hidden;overflow-x:auto;">
      <table style="width:100%;border-collapse:collapse;min-width:560px;">
        <thead style="background:#F8FAFC;">
          <tr>
            <th style="text-align:left;padding:10px 14px;font-size:0.78rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;">Title</th>
            <th style="text-align:left;padding:10px 14px;font-size:0.78rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;">When</th>
            <th style="text-align:center;padding:10px 14px;font-size:0.78rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;">Recipients</th>
            <th style="text-align:center;padding:10px 14px;font-size:0.78rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;">Confirmed</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

// TASK-9841 — pure: what "Send again" carries into the composer from a past
// memo. Title/body/method come across; recipients only those still on the
// roster and reachable for that method; a PDF is never carried (it must be
// attached again — the storage object may be gone).
function _memoPrefillFromMemo(memo, people) {
  if (!memo) return null;
  const sendMethod = memo.sendMethod === 'text-direct' ? 'text-direct' : 'standard';
  const ids = Array.isArray(memo.recipientCriteria?.ids) ? memo.recipientCriteria.ids : [];
  const byId = new Map((people || []).map(p => [p.id, p]));
  const recipientIds = ids.filter((id) => {
    const p = byId.get(id);
    if (!p || p.active === false || p.terminated) return false;
    const reach = _memoPersonReach(p);
    return sendMethod === 'text-direct' ? reach.canSms : reach.reachable;
  });
  return { title: memo.title || '', body: memo.body || '', sendMethod, recipientIds, hadPdf: !!memo.pdfStoragePath, droppedCount: ids.length - recipientIds.length };
}

function openNewMemoModal(prefill = null) {
  if (_hrGateComplianceOnly('memos')) return; // TASK-1117 — memo composer (send path is a callable, rules can't stop it)
  if (!canSendMemos()) { window.showToast?.(MEMO_SEND_DENIED_MSG, 'error'); return; } // TASK-9840
  _setMemoSendError('');
  MemoState.selectedRecipientIds = new Set();
  MemoState.pickerSearch = '';
  MemoState.pendingPdfFile = null;
  MemoState.sendMethod = 'standard';
  MemoState.scheduleMode = false;
  MemoState.selectedTemplateId = '';
  MemoState.resendOf = (prefill && prefill.resendOf) || null; // TASK-9980
  if (!MemoState.sendInFlight) { MemoState.sendDone = false; _setMemoSendStatus(''); }
  _memoLoadSendFeedback().catch(() => {}); // TASK-10112 — ready before Send is pressed
  const modal = document.getElementById('memo-modal');
  if (!modal) return;
  window.paintTenantSendNotices?.(modal); // TASK-10027 muted-tenant UI hints
  modal.classList.remove('hidden');
  ['memo-title', 'memo-body', 'memo-picker-search', 'memo-schedule-at', 'memo-template-name'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.value = '';
  });
  const pdfInput = document.getElementById('memo-pdf-input');
  if (pdfInput) pdfInput.value = '';
  const pdfStatus = document.getElementById('memo-pdf-status');
  if (pdfStatus) pdfStatus.textContent = '';
  const stdRadio = document.querySelector('input[name="memo-send-method"][value="standard"]');
  if (stdRadio) stdRadio.checked = true;
  const nowRadio = document.querySelector('input[name="memo-when"][value="now"]');
  if (nowRadio) nowRadio.checked = true;
  cancelSaveMemoTemplate();
  renderMemoTemplateOptions();
  onMemoWhenChange('now');
  // TASK-9841 — "Send again": a past memo pre-fills the composer.
  if (prefill) {
    const titleEl = document.getElementById('memo-title');
    const bodyEl = document.getElementById('memo-body');
    if (titleEl) titleEl.value = prefill.title || '';
    if (bodyEl) bodyEl.value = prefill.body || '';
    MemoState.sendMethod = prefill.sendMethod === 'text-direct' ? 'text-direct' : 'standard';
    const radio = document.querySelector(`input[name="memo-send-method"][value="${MemoState.sendMethod}"]`);
    if (radio) radio.checked = true;
    (prefill.recipientIds || []).forEach((id) => MemoState.selectedRecipientIds.add(id));
    if (prefill.hadPdf) {
      if (pdfStatus) { pdfStatus.textContent = 'The original PDF is not carried over — attach it again if you want it included.'; pdfStatus.style.color = '#92400E'; }
    }
    if (prefill.droppedCount > 0) {
      window.showToast?.(`${prefill.droppedCount} original recipient${prefill.droppedCount === 1 ? ' is' : 's are'} no longer reachable and were left out`, 'info');
    }
  }
  _applyMemoSendMethodUI();
  renderMemoPicker();
  updateMemoRecipientCount();
  _paintMemoLinkLengthHint(); // TASK-9891
}

// TASK-9891 — "The Open Memo link works for 3 days." Reads the owner's setting
// (tenants/{tid}/settings/linkSecurity; written only by setMemoLinkLength). The
// SERVER reads the same doc at send and stamps each link, so this line is a
// display only; a failed read leaves the 24h default text.
async function _paintMemoLinkLengthHint() {
  const el = document.getElementById('memo-link-length-hint');
  const tid = HRState.tenantId || window.AppState?.tenant;
  if (!el || !tid) return;
  let hours = 24;
  try {
    const snap = await getDoc(doc(db, 'tenants', tid, 'settings', 'linkSecurity'));
    const v = snap.exists() ? Number((snap.data() || {}).memoLinkHours) : NaN;
    if (Number.isFinite(v)) hours = Math.min(168, Math.max(1, Math.round(v)));
  } catch (e) {
    console.warn('[HR] memo link length read failed:', e?.message || e);
  }
  const label = hours >= 48 && hours % 24 === 0 ? `${hours / 24} days` : `${hours} hour${hours === 1 ? '' : 's'}`;
  el.textContent = `The “Open Memo” link works for ${label}. The account owner sets this in Settings → Security.`;
}

// TASK-1044 — "Text it directly" mode: the memo BODY is delivered as a plain
// SMS (no memo.html link, no read receipt). Picker reachability flips to
// SMS-only (phone + smsOptIn), PDF attach hides (can't inline a PDF in SMS),
// body becomes required, and a live segment/credit counter appears.
function onMemoSendMethodChange(method) {
  MemoState.sendMethod = method === 'text-direct' ? 'text-direct' : 'standard';
  if (MemoState.sendMethod === 'text-direct') {
    // PDF can't ride an SMS — drop any pending attachment
    MemoState.pendingPdfFile = null;
    const pdfInput = document.getElementById('memo-pdf-input');
    if (pdfInput) pdfInput.value = '';
    const pdfStatus = document.getElementById('memo-pdf-status');
    if (pdfStatus) pdfStatus.textContent = '';
    // Prune selected recipients who aren't reachable by SMS
    for (const pid of [...MemoState.selectedRecipientIds]) {
      const p = (HRState.people || []).find(x => x.id === pid);
      if (!p || !_memoPersonReach(p).canSms) MemoState.selectedRecipientIds.delete(pid);
    }
  }
  _applyMemoSendMethodUI();
  renderMemoPicker();
  updateMemoRecipientCount();
}

function _applyMemoSendMethodUI() {
  const textDirect = MemoState.sendMethod === 'text-direct';
  const pdfBlock = document.getElementById('memo-pdf-block');
  if (pdfBlock) pdfBlock.style.display = textDirect ? 'none' : '';
  const hint = document.getElementById('memo-method-hint');
  if (hint) hint.style.display = textDirect ? '' : 'none';
  const linkHint = document.getElementById('memo-link-length-hint'); // TASK-9891 — text-direct has no link
  if (linkHint) linkHint.style.display = textDirect ? 'none' : '';
  const optional = document.getElementById('memo-body-optional');
  if (optional) optional.style.display = textDirect ? 'none' : '';
  const required = document.getElementById('memo-body-required');
  if (required) required.style.display = textDirect ? '' : 'none';
  const counter = document.getElementById('memo-sms-counter');
  if (counter) counter.style.display = textDirect ? '' : 'none';
  const titleNote = document.getElementById('memo-title-note'); // TASK-10142 — a text carries only the body
  if (titleNote) titleNote.style.display = textDirect ? '' : 'none';
  updateMemoSmsCounter();
  renderMemoSendPreview(); // TASK-10112 — who can be reached depends on the method
}

// Client mirror of the server's countSmsSegments (functions/src/index.js:11953)
// — display-only estimate; the SERVER owns the real segment gate/debit.
function _memoCountSegments(text) {
  if (!text) return 0;
  const isUnicode = /[^\x20-\x7E\n\r\t\xA0£$¥èéùìòÇØøÅåΔΦΓΛΩΠΨΣΘΞÆæßÉ!\"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà@{}\\[\]~|^€]/.test(text);
  const len = text.length;
  if (isUnicode) return len <= 70 ? 1 : Math.ceil(len / 67);
  return len <= 160 ? 1 : Math.ceil(len / 153);
}

function _memoCompanyName() {
  return window.AppState?.tenantData?.companyName || window.AppState?.tenantData?.name || 'DispatchDeck';
}

// TASK-10142 — pure: text segments ONE person gets for a text-direct body
// (company prefix + STOP footer included). Mirrors the server's long-message
// handling: >600 chars splits into ~592-char parts each with a "(1/N) "
// header, sent as separate texts. Display-only; the server owns the debit.
function _memoTextDirectSegments(body, company) {
  if (!body) return 0;
  const full = `${company}: ${body}\n\nMsg/data rates may apply. Reply STOP to stop.`;
  if (full.length <= 600) return _memoCountSegments(full);
  let segs = 0;
  for (let i = 0; i < full.length; i += 592) {
    segs += _memoCountSegments('(1/9) ' + full.slice(i, i + 592));
  }
  return segs;
}

// Past this many texts per person the size line turns into a warning.
const MEMO_LONG_TEXT_SEGMENTS = 3;

// The size line ABOVE the recipient list depends on the BODY only. The credit
// total (body x recipients) lives under the list, in #memo-recipient-count —
// TASK-10142: when it lived here, the first tick wrapped this line onto a second
// row and pushed every recipient row down under the cursor (Chris 10/27: the
// next tap landed on the wrong person and a text went to them).
function updateMemoSmsCounter() {
  const el = document.getElementById('memo-sms-counter');
  if (!el || MemoState.sendMethod !== 'text-direct') return;
  const body = document.getElementById('memo-body')?.value.trim() || '';
  const segs = _memoTextDirectSegments(body, _memoCompanyName());
  const long = segs > MEMO_LONG_TEXT_SEGMENTS;
  el.textContent = !body
    ? 'Type a message to see the size'
    : long
      ? `Long text: ${body.length} characters = about ${segs} texts per person, and each one uses a credit. Shorten it, or send it as Email w/ Read-it button.`
      : `${body.length} chars · ~${segs} text segment${segs === 1 ? '' : 's'} per person`;
  el.style.color = long ? '#92400E' : 'var(--text-secondary)';
  el.style.background = long ? '#FFFBEB' : '';
  el.style.border = long ? '1px solid #FCD34D' : '';
  el.style.borderRadius = long ? '8px' : '';
  el.style.padding = long ? '8px 12px' : '';
  el.style.fontWeight = long ? '600' : '';
}

function closeNewMemoModal() {
  _memoSettleReview(false); // TASK-10142 — closing mid-review is "don't send"
  const modal = document.getElementById('memo-modal');
  if (modal) modal.classList.add('hidden');
}

function onMemoPickerSearch(ev) {
  MemoState.pickerSearch = (ev.target.value || '').toLowerCase().trim();
  renderMemoPicker();
}

function _memoEligiblePeople() {
  // Active people; CDL drivers + drivers first, then by name
  // TASK-10142 (left-open 5): a person with only a terminationDate is gone too —
  // same rule as the review panel and the server (_memoPersonIsActive).
  const people = (HRState.people || []).filter(p => p.active !== false && !p.terminated && !p.terminationDate);
  const roleOrder = (p) => {
    const r = (p.crewType || p.role || p.roles?.[0] || '').toString().toLowerCase();
    if (r.includes('cdl')) return 0;
    if (r.includes('driver')) return 1;
    return 2;
  };
  return [...people].sort((a, b) => {
    const ra = roleOrder(a), rb = roleOrder(b);
    if (ra !== rb) return ra - rb;
    return (a.name || '').localeCompare(b.name || '');
  });
}

function _memoFilteredPeople(search) {
  const s = (search === undefined ? MemoState.pickerSearch : search) || '';
  return _memoEligiblePeople().filter((p) => {
    if (!s) return true;
    const hay = `${p.name || ''} ${p.email || ''} ${p.crewType || p.role || ''}`.toLowerCase();
    return hay.includes(s);
  });
}

// Channel reachability for a person — mirrors the backend memo resolver
// (functions/src/index.js ~50406, TASK-663). A person is reachable if they
// have an email (and prefer email/both) OR a phone + smsOptIn (and prefer
// sms/both). Honors each person's existing preferredChannel + smsOptIn.
function _memoPersonReach(p) {
  const preferred = p.notifications?.preferredChannel || p.preferredChannel || 'both';
  // TASK-1044 (COD mitigation #5): mirror the SERVER resolver exactly —
  // explicit smsOptIn === false (carrier STOP, TASK-699/779) is authoritative
  // and overrides a stale nested notifications.sms:true. The old bare OR could
  // show a STOP'd person as SMS-reachable while the server (correctly) skips them.
  const smsOptIn = p.smsOptIn === false ? false : !!(p.notifications?.sms || p.smsOptIn);
  const hasEmail = !!(p.email && String(p.email).trim().includes('@')); // server _memoResolveChannels requires an @
  const phoneDigits = String(p.phone || '').replace(/\D/g, '');
  const hasPhone = phoneDigits.length >= 10;
  const canEmail = (preferred === 'email' || preferred === 'both') && hasEmail;
  const canSms = (preferred === 'sms' || preferred === 'both') && hasPhone && smsOptIn;
  return { hasEmail, hasPhone, smsOptIn, canEmail, canSms, reachable: canEmail || canSms };
}

// TASK-9893 — the memo link reaches this person by TEXT and they have never
// texted the company's number (server-owned smsRepliedAt, stamped by
// twilioIncomingSms). Mirrors services/knownSender.js hasReplied.
const MEMO_AWAITING_REPLY_TIP = "Hasn't texted your number yet. On an iPhone the link won't open until they reply START; this text asks them to.";
function _memoReplied(p) {
  if (!p || !p.smsRepliedAt) return false;
  const last10 = (v) => String(v == null ? '' : v).replace(/\D/g, '').slice(-10);
  const from = last10(p.smsRepliedFrom);
  return !from || ['phone', 'cellPhone', 'mobilePhone'].some((k) => last10(p[k]) === from);
}
function _memoAwaitingReply(p, reach) {
  const r = reach || _memoPersonReach(p);
  return !!(r.canSms && !_memoReplied(p));
}

// Mode-aware reachability: text-direct memos can only reach SMS-capable people.
function _memoReachable(p) {
  const reach = _memoPersonReach(p);
  return MemoState.sendMethod === 'text-direct' ? reach.canSms : reach.reachable;
}

function renderMemoPicker() {
  const host = document.getElementById('memo-picker-list');
  if (!host) return;
  const people = _memoFilteredPeople();
  if (!people.length) {
    host.innerHTML = '<div style="padding:14px;color:var(--text-secondary);font-size:0.88rem;">No matching people.</div>';
    return;
  }
  const textDirect = MemoState.sendMethod === 'text-direct';
  host.innerHTML = people.map((p) => {
    const checked = MemoState.selectedRecipientIds.has(p.id);
    const role = _hrRoleLabel(p.crewType || p.role || p.roles?.[0] || '');
    const reach = _memoPersonReach(p);
    // Contact line: prefer showing the channel(s) this person will actually
    // receive on. Text-direct = SMS-only (TASK-1044); otherwise email > phone(SMS).
    let contact;
    let selectable = true;
    if (textDirect) {
      if (reach.canSms) {
        contact = `${_memoEscape(String(p.phone))} <span style="color:var(--text-muted,#94A3B8);">(SMS)</span>`;
      } else {
        contact = '<span style="color:#DC2626;">not textable (needs phone + SMS opt-in)</span>';
        selectable = false;
      }
    } else if (reach.canEmail) {
      contact = _memoEscape(p.email);
    } else if (reach.canSms) {
      contact = `${_memoEscape(String(p.phone))} <span style="color:var(--text-muted,#94A3B8);">(SMS)</span>`;
    } else {
      contact = '<span style="color:#DC2626;">no contact method</span>';
    }
    // TASK-9893 — the link goes by text and this person has never texted the
    // company's number: on an iPhone it shows as plain text. Their text asks
    // them to reply START. Subtle grey marker, not a warning.
    const awaitIcon = (!textDirect && _memoAwaitingReply(p, reach))
      ? ` <span class="material-icons" style="font-size:14px;vertical-align:-2px;color:var(--text-secondary);" title="${MEMO_AWAITING_REPLY_TIP}" aria-label="${MEMO_AWAITING_REPLY_TIP}" role="img">link_off</span>`
      : '';
    return `
      <label style="display:flex;align-items:center;gap:10px;padding:9px 14px;border-bottom:1px solid var(--border-light);cursor:${selectable ? 'pointer' : 'not-allowed'};${selectable ? '' : 'opacity:0.6;'}" ${selectable ? `onmouseenter="this.style.background='#F8FAFC'" onmouseleave="this.style.background=''"` : ''}>
        <input type="checkbox" data-pid="${_memoEscape(p.id)}" ${checked ? 'checked' : ''} ${selectable ? '' : 'disabled'} onchange="window.HRModule.toggleMemoRecipient('${escJsAttr(p.id)}')">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:600;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${_memoEscape(p.name || '(no name)')}${awaitIcon}</div>
          <div style="font-size:0.78rem;color:var(--text-secondary);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${_memoEscape(role)}${role ? ' · ' : ''}${contact}</div>
        </div>
      </label>`;
  }).join('');
}

// TASK-10142 — ticking never re-renders or re-orders the list. The rows stay
// exactly where they are; only each checkbox's state is synced in place (a
// chip's ✕ or Select all / Clear land here too).
function toggleMemoRecipient(personId) {
  if (MemoState.selectedRecipientIds.has(personId)) {
    MemoState.selectedRecipientIds.delete(personId);
  } else {
    MemoState.selectedRecipientIds.add(personId);
  }
  _memoSyncPickerChecks();
  updateMemoRecipientCount();
}

function _memoSyncPickerChecks() {
  const host = document.getElementById('memo-picker-list');
  if (!host) return;
  host.querySelectorAll('input[type="checkbox"][data-pid]').forEach((cb) => {
    const want = MemoState.selectedRecipientIds.has(cb.getAttribute('data-pid'));
    if (cb.checked !== want) cb.checked = want;
  });
}

// TASK-10142 — "Selected (N)" under the list, in roster order, each with a ✕.
// Picked people who are hidden by the search still show here.
function renderMemoSelectedChips() {
  const host = document.getElementById('memo-selected-chips');
  if (!host) return;
  const ids = MemoState.selectedRecipientIds;
  if (!ids.size) { host.style.display = 'none'; host.innerHTML = ''; return; }
  const picked = _memoEligiblePeople().filter((p) => ids.has(p.id));
  const known = new Set(picked.map((p) => p.id));
  const missing = [...ids].filter((id) => !known.has(id)).length;
  host.style.display = '';
  host.innerHTML = `<div style="font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:4px;">Selected (${ids.size})</div>
    <div style="display:flex;flex-wrap:wrap;gap:6px;">${picked.map((p) => `<span style="display:inline-flex;align-items:center;gap:2px;padding:3px 4px 3px 10px;border:1px solid #BFDBFE;background:#EFF6FF;color:#1E3A8A;border-radius:999px;font-size:0.82rem;">${_memoEscape(p.name || '(no name)')}<button type="button" class="btn-icon" style="width:28px;height:28px;min-width:28px;padding:0;color:#1E3A8A;" title="Remove ${_memoEscape(p.name || '')}" aria-label="Remove ${_memoEscape(p.name || '')}" onclick="window.HRModule.toggleMemoRecipient('${escJsAttr(p.id)}')"><span class="material-icons" style="font-size:16px;">close</span></button></span>`).join('')}${missing ? `<span style="font-size:0.8rem;color:var(--text-secondary);align-self:center;">+${missing} no longer on the roster (won't be sent)</span>` : ''}</div>`;
}

function selectAllDriversForMemo() {
  const people = _memoEligiblePeople();
  for (const p of people) {
    const role = (p.crewType || p.role || p.roles?.[0] || '').toLowerCase();
    if (role.includes('cdl') || role.includes('driver')) {
      // Reachable = email OR phone+smsOptIn (TASK-663); SMS-only in
      // text-direct mode (TASK-1044).
      if (_memoReachable(p)) {
        MemoState.selectedRecipientIds.add(p.id);
      }
    }
  }
  _memoSyncPickerChecks(); // TASK-10142 — in place, rows never re-mount
  updateMemoRecipientCount();
}

function selectAllForMemo() {
  // Select every person currently SHOWN in the picker (respects the search filter)
  // who has a reachable channel — email OR phone+smsOptIn (TASK-663). People with
  // no contact method are skipped at send (sentChannel:'none') so selecting them
  // would inflate the count misleadingly.
  const people = _memoFilteredPeople();
  for (const p of people) {
    if (_memoReachable(p)) {
      MemoState.selectedRecipientIds.add(p.id);
    }
  }
  _memoSyncPickerChecks(); // TASK-10142
  updateMemoRecipientCount();
}

function clearMemoRecipients() {
  MemoState.selectedRecipientIds.clear();
  _memoSyncPickerChecks(); // TASK-10142
  updateMemoRecipientCount();
}

function updateMemoRecipientCount() {
  const el = document.getElementById('memo-recipient-count');
  if (!el) return;
  const n = MemoState.selectedRecipientIds.size;
  el.textContent = `${n} selected`;
  // TASK-9893 — say how many of the selected will get a link they can't tap
  // on an iPhone yet (link mode only; a text-direct memo carries no link).
  if (n > 0 && MemoState.sendMethod !== 'text-direct') {
    const byId = new Map((HRState.people || []).map((p) => [p.id, p]));
    let awaiting = 0;
    for (const id of MemoState.selectedRecipientIds) {
      const p = byId.get(id);
      if (p && _memoAwaitingReply(p)) awaiting++;
    }
    if (awaiting > 0) {
      el.textContent += ` · ${awaiting} haven't texted your number yet: their text asks them to reply START so the link opens on an iPhone`;
    }
  }
  // TASK-10142 — the credit total sits here, UNDER the list (see updateMemoSmsCounter).
  if (n > 0 && MemoState.sendMethod === 'text-direct') {
    const body = document.getElementById('memo-body')?.value.trim() || '';
    const credits = _memoTextDirectSegments(body, _memoCompanyName()) * n;
    if (credits > 0) el.textContent += ` · ~${credits} credit${credits === 1 ? '' : 's'}`;
  }
  renderMemoSelectedChips();
  el.style.color = n > 0 ? 'var(--text-primary)' : 'var(--text-secondary)';
  updateMemoSmsCounter(); // TASK-1044 — recipient count feeds the credit estimate
  renderMemoSendPreview(); // TASK-10112
}

function onMemoPdfChange(ev) {
  const file = ev.target.files?.[0];
  const status = document.getElementById('memo-pdf-status');
  if (!file) {
    MemoState.pendingPdfFile = null;
    if (status) status.textContent = '';
    return;
  }
  if (file.size > 10 * 1024 * 1024) {
    MemoState.pendingPdfFile = null;
    ev.target.value = '';
    if (status) {
      status.textContent = 'File too large (max 10MB)';
      status.style.color = '#DC2626';
    }
    return;
  }
  MemoState.pendingPdfFile = file;
  if (status) {
    const sizeKb = Math.round(file.size / 1024);
    status.textContent = `Ready: ${file.name} (${sizeKb} KB)`;
    status.style.color = 'var(--text-secondary)';
  }
}

// TASK-10142 — the body feeds both the size line and the credit total.
function onMemoBodyInput() {
  updateMemoRecipientCount(); // also repaints #memo-sms-counter
}

// TASK-10142 — pure: what the pre-send review shows. Every picked person by
// name, in roster order, with how it reaches them; anyone it will NOT reach
// (and why); and the credit estimate. Same reach rule as the picker and the
// server (_memoPersonReach). Text-direct: segments x textable people.
// Email-with-link: 1 credit per person whose link goes by text.
function _memoReviewSummary({ ids, people, order: orderIds, sendMethod, body, company }) {
  const textDirect = sendMethod === 'text-direct';
  const want = new Set(ids || []);
  const all = Array.isArray(people) ? people : [];
  const byId = new Map(all.map((p) => [p.id, p]));
  const order = new Map((Array.isArray(orderIds) ? orderIds : all.map((p) => p.id)).map((id, i) => [id, i]));
  const sorted = [...want].sort((a, b) => (order.has(a) ? order.get(a) : 1e9) - (order.has(b) ? order.get(b) : 1e9));
  const rows = [];
  const notReached = [];
  let smsPeople = 0;
  for (const id of sorted) {
    const p = byId.get(id);
    if (!p || p.active === false || p.terminated || p.terminationDate) { notReached.push({ id, name: (p && p.name) || 'Someone', why: 'no longer on the roster', gone: true }); continue; }
    const reach = _memoPersonReach(p);
    const name = p.name || '(no name)';
    if (textDirect) {
      if (reach.canSms) { rows.push({ id, name, via: `text to ${String(p.phone || '').trim()}` }); smsPeople++; }
      else notReached.push({ id, name, why: reach.hasPhone ? 'no SMS opt-in' : 'no phone' });
    } else if (reach.reachable) {
      const via = [reach.canEmail ? `email ${String(p.email || '').trim()}` : '', reach.canSms ? `text ${String(p.phone || '').trim()}` : ''].filter(Boolean).join(' + ');
      rows.push({ id, name, via });
      if (reach.canSms) smsPeople++;
    } else {
      notReached.push({ id, name, why: 'no phone or email' });
    }
  }
  const segsPerPerson = textDirect ? _memoTextDirectSegments(body || '', company || 'Your company') : 0;
  const credits = textDirect ? segsPerPerson * smsPeople : smsPeople;
  return { textDirect, rows, notReached, smsPeople, segsPerPerson, credits, company: company || 'Your company' };
}

// TASK-10142 — the review panel over the composer. Resolves true only on the
// explicit Send button; Go back, Esc, or closing the composer resolve false.
let _memoReviewResolve = null;
function _memoSettleReview(answer) {
  const panel = document.getElementById('memo-review');
  if (panel) { panel.style.display = 'none'; panel.innerHTML = ''; }
  document.querySelectorAll('#memo-modal .modal-header, #memo-modal .modal-body, #memo-modal .modal-footer').forEach((el) => el.removeAttribute('inert'));
  if (panel && panel._ddKey) { document.removeEventListener('keydown', panel._ddKey, true); panel._ddKey = null; }
  const r = _memoReviewResolve;
  _memoReviewResolve = null;
  if (r) r(!!answer);
}

function _memoReviewRecipients({ summary, title, body, scheduledFor }) {
  const panel = document.getElementById('memo-review');
  if (!panel) return Promise.resolve(false); // no panel = no review = no send
  _memoSettleReview(false); // a stale review never lingers
  const s = summary;
  const n = s.rows.length;
  const people = `${n} ${n === 1 ? 'person' : 'people'}`;
  const heading = scheduledFor
    ? `Schedule this memo for ${people}?`
    : s.textDirect ? `Text ${people} now?` : `Send this memo to ${people} now?`;
  const warn = scheduledFor
    ? `It goes out ${_memoFmtLocalStr(scheduledFor)} (${_memoTenantTz()}). You can cancel it from the memo list until then.`
    : s.textDirect ? 'A text cannot be unsent. Check every name below.' : 'Texts and emails cannot be unsent. Check every name below.';
  const creditLine = s.textDirect
    ? `About ${s.credits} credit${s.credits === 1 ? '' : 's'} (${s.segsPerPerson} text${s.segsPerPerson === 1 ? '' : 's'} each × ${s.smsPeople})`
    : s.smsPeople > 0
      ? `About ${s.credits} credit${s.credits === 1 ? '' : 's'} (${s.smsPeople} ${s.smsPeople === 1 ? 'person gets' : 'people get'} the link by text). Email is free.`
      : 'No texts, so no credits. Email is free.';
  // Text-direct: the exact text each phone gets (server: company prefix + body + STOP footer).
  const preview = s.textDirect ? `${s.company}: ${body}\n\nMsg/data rates may apply. Reply STOP to stop.` : title;
  panel.innerHTML = `
    <div style="padding:16px 20px;border-bottom:1px solid var(--border-light);">
      <h3 id="memo-review-title" style="margin:0;font-size:1.1rem;color:var(--text-primary);">${_memoEscape(heading)}</h3>
      <div style="margin-top:6px;font-size:0.88rem;color:${scheduledFor ? 'var(--text-secondary)' : '#B91C1C'};font-weight:${scheduledFor ? '400' : '600'};">${_memoEscape(warn)}</div>
    </div>
    <div style="flex:1;min-height:0;overflow-y:auto;padding:14px 20px;">
      <div style="font-weight:600;font-size:0.85rem;margin-bottom:6px;color:var(--text-primary);">Going to (${n})</div>
      <ol id="memo-review-list" style="margin:0 0 12px;padding-left:22px;font-size:0.92rem;color:var(--text-primary);">
        ${s.rows.map((r) => `<li data-pid="${_memoEscape(r.id)}" style="padding:3px 0;"><strong>${_memoEscape(r.name)}</strong> <span style="color:var(--text-secondary);font-size:0.82rem;">· ${_memoEscape(r.via)}</span></li>`).join('')}
      </ol>
      ${s.notReached.length ? `<div style="font-size:0.85rem;color:#92400E;background:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;padding:8px 12px;margin-bottom:12px;"><strong>Not getting it</strong> — tell them another way: ${s.notReached.map((x) => `${_memoEscape(x.name)} (${_memoEscape(x.why)})`).join(', ')}</div>` : ''}
      <div style="font-size:0.88rem;color:var(--text-primary);margin-bottom:12px;">${_memoEscape(creditLine)}</div>
      ${preview ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:4px;">${s.textDirect ? (s.segsPerPerson > 1 ? `The text (arrives as ${s.segsPerPerson} parts)` : 'The text') : 'Title'}</div>
      <div style="white-space:pre-wrap;word-break:break-word;font-size:0.88rem;color:var(--text-primary);background:var(--bg-elevated);border:1px solid var(--border-light);border-radius:8px;padding:10px 12px;max-height:140px;overflow-y:auto;">${_memoEscape(preview)}</div>` : ''}
    </div>
    <div style="display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap;padding:14px 20px;border-top:1px solid var(--border-light);">
      <button type="button" class="btn btn-secondary" id="memo-review-back">Go back</button>
      <button type="button" class="btn btn-primary" id="memo-review-send" ${n ? '' : 'disabled'}><span class="material-icons" style="font-size:16px;vertical-align:middle;">${scheduledFor ? 'schedule_send' : 'send'}</span> ${scheduledFor ? `Schedule for ${_memoEscape(people)}` : s.textDirect ? `Text ${_memoEscape(people)}` : `Send to ${_memoEscape(people)}`}</button>
    </div>`;
  panel.style.display = 'flex';
  document.querySelectorAll('#memo-modal .modal-header, #memo-modal .modal-body, #memo-modal .modal-footer').forEach((el) => el.setAttribute('inert', ''));
  return new Promise((resolve) => {
    _memoReviewResolve = resolve;
    panel.querySelector('#memo-review-back').onclick = () => _memoSettleReview(false);
    panel.querySelector('#memo-review-send').onclick = () => _memoSettleReview(true);
    panel._ddKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); _memoSettleReview(false); } };
    document.addEventListener('keydown', panel._ddKey, true);
    try { panel.querySelector('#memo-review-back').focus(); } catch (_) {}
  });
}

function _setMemoSendError(msg) {
  const el = document.getElementById('memo-send-error');
  if (!el) return;
  el.textContent = msg || '';
  el.style.display = msg ? 'block' : 'none';
}

async function submitNewMemo() {
  // TASK-1117 — createMemoAndSend runs under the Admin SDK (bypasses rules),
  // so this client gate is the belt on the send path for complianceOnly.
  if (_hrGateComplianceOnly('memos')) return;
  // TASK-9840 — the server refuses non-owner / non-HR senders; say so here
  // before uploading a PDF that would never be sent.
  if (!canSendMemos()) { _setMemoSendError(MEMO_SEND_DENIED_MSG); return; }
  const btn = document.getElementById('memo-send-btn');
  if (btn?.disabled || MemoState.sendInFlight) return; // debounce — a double-click can't double-fire (TASK-1044 mitigation #6; TASK-9980 in-flight flag)
  // TASK-9980 — after a finished send the button reads "Done" and only closes.
  if (MemoState.sendDone) { closeNewMemoModal(); return; }
  _setMemoSendError('');
  _setMemoSendStatus('');
  const title = document.getElementById('memo-title')?.value.trim() || '';
  const body = document.getElementById('memo-body')?.value.trim() || '';
  const textDirect = MemoState.sendMethod === 'text-direct';
  // TASK-9980 (COD Code Reviewer) — snapshot at press: closing + reopening the
  // composer mid-send resets MemoState, which must not change THIS request.
  const sendMethodAtPress = MemoState.sendMethod || 'standard';
  const resendOfAtPress = MemoState.resendOf || null;
  if (!title) { window.showToast?.('Title is required', 'error'); return; }
  if (textDirect && !body) {
    window.showToast?.('A message body is required to text it directly', 'error');
    return;
  }
  if (!body && !MemoState.pendingPdfFile) {
    window.showToast?.('Add a body or attach a PDF', 'error');
    return;
  }
  let recipientIds = Array.from(MemoState.selectedRecipientIds);
  if (!recipientIds.length) { window.showToast?.('Pick at least one recipient', 'error'); return; }
  // TASK-9841 — schedule: the wall-clock string goes to the server as-is and is
  // read in the COMPANY time zone there (never the browser's offset).
  let scheduledFor = null;
  if (MemoState.scheduleMode) {
    scheduledFor = document.getElementById('memo-schedule-at')?.value || '';
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(scheduledFor)) {
      _setMemoSendError('Pick a date and time to schedule the memo.');
      return;
    }
    // COD UX 2026-10-14: say it here, not after a round trip — the server
    // refuses the same window (21:00–07:59 company time) and defers at send.
    const hour = Number(scheduledFor.slice(11, 13));
    if (hour >= 21 || hour < 8) {
      _setMemoSendError(`Pick a time between 8:00 AM and 9:00 PM company time (${_memoTenantTz()}). Memos are not sent overnight.`);
      return;
    }
  }
  // Quiet hours (Chris 2026-10-26: "soft warning for anything after 7pm in
  // memo or notes") — 7 PM-6 AM company time, a memo that goes by TEXT needs
  // one extra tap ("Send anyway"). Never blocks, never schedules; an email-only
  // memo and a scheduled one never ask.
  const _qsf = !scheduledFor ? window.SendFeedback : null;
  const _qm = _qsf ? _qsf.quietHoursWarning({ nowMs: Date.now(), tz: _memoTenantTz(), count: _memoSmsCount(recipientIds) }) : null;
  if (_qm && !_qsf.quietConfirm(`memo|${sendMethodAtPress}|${title}|${body}|${recipientIds.join(',')}`, Date.now())) {
    _setMemoSendStatus(`${_qm} Tap Send anyway to go ahead.`, 'warn');
    if (btn) btn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">send</span> Send anyway';
    return;
  }
  // TASK-9980 — disabled the moment it is pressed, and a live "Sending to N
  // people… 0:12" line so a long send never looks frozen (Wolverine 10/18: a
  // 66-person text ran with no sign of life and was sent twice).
  MemoState.sendInFlight = true;
  // TASK-10142 — every recipient by name + the credit estimate, and one more
  // tap, before anything is sent (Chris 10/27: a text went to the wrong people
  // and there is no undo). sendInFlight is already set, so a second Send press
  // while this is open is a no-op.
  const reviewed = _memoReviewSummary({ ids: recipientIds, people: HRState.people || [], order: _memoEligiblePeople().map((p) => p.id), sendMethod: sendMethodAtPress, body, company: _memoCompanyName() });
  const reviewOk = await _memoReviewRecipients({ summary: reviewed, title, body, scheduledFor });
  if (!reviewOk || !reviewed.rows.length) { MemoState.sendInFlight = false; return; }
  // COD r1/r2: send to EXACTLY the people the sender just read. Anyone no
  // longer on the roster (e.g. deactivated while picked) is never posted; an
  // active person with no usable channel stays in so the memo records them as
  // not reached (the server never texts or emails them).
  recipientIds = reviewed.rows.map((r) => r.id).concat(reviewed.notReached.filter((x) => !x.gone && x.id).map((x) => x.id));
  if (btn) { btn.disabled = true; btn.innerHTML = `<span class="material-icons" style="font-size:16px;vertical-align:middle;">hourglass_empty</span> ${scheduledFor ? 'Scheduling...' : 'Sending...'}`; }
  const nPeople = `${recipientIds.length} ${recipientIds.length === 1 ? 'person' : 'people'}`;
  const startedMs = Date.now();
  const tick = () => {
    if (scheduledFor) { _setMemoSendStatus(`Scheduling for ${nPeople}…`); return; }
    const secs = Math.floor((Date.now() - startedMs) / 1000);
    _setMemoSendStatus(`Sending to ${nPeople}… ${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')} — this can take a minute. Keep this window open; it will not be sent twice.`);
  };
  // TASK-10112 — with the shared component loaded (preloaded on open), the
  // status line is its staged run: "Uploading PDF → Checking credits → Sending
  // → Done" with the elapsed timer, then the result with who failed and why.
  // The plain ticker above stays as the fallback if the chunk did not load.
  const _sf = !scheduledFor ? window.SendFeedback : null;
  const memoRun = _sf ? _sf.createRun({
    host: document.getElementById('memo-send-status'),
    total: recipientIds.length,
    steps: (MemoState.pendingPdfFile && !textDirect ? ['Uploading PDF'] : []).concat(['Checking credits', 'Sending', 'Done']),
    hint: 'This can take a minute. Keep this window open; it will not be sent twice.',
  }) : null;
  if (!memoRun) {
    tick();
    if (MemoState.sendTicker) clearInterval(MemoState.sendTicker);
    MemoState.sendTicker = setInterval(tick, 1000);
  }
  let finished = false;   // true = Done state (Send never re-arms in this composer)
  const tenantId = HRState.tenantId;
  try {
    // Mint a client-side memoId first so the PDF storage path is stable;
    // server uses its own id but for storage upload we need it pre-known.
    // Approach: store under a temp path keyed by ms timestamp + random;
    // server validates the path is in `tenants/{tid}/memos/` (loose match).
    let pdfStoragePath = null;
    let pdfFileName = null;
    if (MemoState.pendingPdfFile && !textDirect) {
      const ts = Date.now();
      const rand = Math.random().toString(36).slice(2, 10);
      const safe = MemoState.pendingPdfFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      pdfStoragePath = `tenants/${tenantId}/memos/${ts}_${rand}_${safe}`;
      pdfFileName = MemoState.pendingPdfFile.name;
      const sref = ref(storage, pdfStoragePath);
      await uploadBytes(sref, MemoState.pendingPdfFile, { contentType: inferContentType(MemoState.pendingPdfFile) });
    }
    // TASK-10112 — the server checks recipients + credits, then sends to all at
    // once (no per-person progress to read), so the run moves on a clock.
    if (memoRun) { memoRun.step('Checking credits'); setTimeout(() => memoRun.step('Sending'), 1500); }
    // TASK-9980 — wait as long as the server may run (timeoutSeconds 120).
    // The SDK default is 70s: a big send outlived it, the client called it a
    // failure, re-armed Send, and the server was still texting.
    const callable = httpsCallable(functions, 'createMemoAndSend', { timeout: 150000 });
    const res = await callable({
      tenantId,
      title,
      body,
      pdfStoragePath,
      pdfFileName,
      recipientPersonIds: recipientIds,
      sendMethod: sendMethodAtPress,
      ...(scheduledFor ? { scheduledFor } : {}),
      ...(resendOfAtPress && !scheduledFor ? { resendOf: resendOfAtPress } : {}),
    });
    const data = res?.data || {};
    const outcome = _memoSendOutcome(data, { textDirect });
    if (data.ok && data.scheduled) {
      window.showToast?.(`Memo scheduled for ${_memoFmtLocalStr(scheduledFor)} (${data.scheduledTz || _memoTenantTz()}) — ${data.totalRecipients} recipient${data.totalRecipients === 1 ? '' : 's'}`, 'success');
      _setMemoSendStatus('');
      MemoState.sendDone = false;
      closeNewMemoModal();
    } else if (outcome.done) {
      // Sent, or refused as a duplicate: stay open on a Done state with the
      // counts, so the sender SEES the result and Send can't fire again.
      finished = true;
      if (memoRun && data.ok && !data.duplicate) {
        // TASK-10112 — AFTER: "Texted 14 · 1 failed", who + why, credits used.
        memoRun.finish(_memoRunResult(data, textDirect));
      } else if (memoRun) {
        memoRun.fail(outcome.message, { tone: 'warn' });
      } else {
        _setMemoSendStatus(outcome.message, outcome.tone);
      }
      window.showToast?.(outcome.message, outcome.toastType);
    } else {
      if (memoRun) memoRun.dispose();
      _setMemoSendStatus('');
      window.showToast?.(outcome.message, 'error');
    }
  } catch (err) {
    console.error('[submitNewMemo]', err);
    if (!scheduledFor && _memoIsUncertainCallError(err)) {
      // TASK-9980 — we lost the answer, not necessarily the send. Never
      // re-arm Send here; point at the list instead.
      finished = true;
      const msg = 'Lost contact while sending — the memo may still be going out. Check the memo list before doing anything else. Pressing Send again will not text anyone twice.';
      if (memoRun) memoRun.fail(msg, { tone: 'warn' }); else _setMemoSendStatus(msg, 'warn');
      window.showToast?.(msg, 'info');
    } else {
      if (memoRun) memoRun.dispose();
      _setMemoSendStatus('');
      window.showToast?.(`Failed: ${err?.message || 'unknown error'}`, 'error');
      // TASK-9840 — a refusal must read in the modal, not only as a toast.
      if (scheduledFor) _setMemoSendError(`Not scheduled — ${err?.message || 'unknown error'}`);
      else _setMemoSendError(`Not sent — ${err?.message || 'unknown error'}`);
    }
  } finally {
    if (MemoState.sendTicker) { clearInterval(MemoState.sendTicker); MemoState.sendTicker = null; }
    if (memoRun) memoRun.dispose();   // TASK-10112 — no-op once finished/failed
    MemoState.sendInFlight = false;
    if (finished) MemoState.sendDone = true;
    if (btn) { btn.disabled = false; onMemoWhenChange(MemoState.scheduleMode ? 'later' : 'now'); }
  }
}

// TASK-9841 — Cancel a scheduled memo (list row or drill modal).
async function cancelScheduledMemoAction(memoId, ev) {
  try { ev?.stopPropagation?.(); } catch (_) {}
  if (!canSendMemos()) { window.showToast?.(MEMO_SEND_DENIED_MSG, 'error'); return; }
  const memo = MemoState.memos.find(m => m.id === memoId);
  const go = typeof window.showConfirm === 'function'
    ? await window.showConfirm('Cancel this scheduled memo?', `"${memo?.title || 'This memo'}" will not be sent. You can schedule it again later.`, { confirmText: 'Cancel memo', cancelText: 'Keep it', danger: true })
    : window.confirm('Cancel this scheduled memo?');
  if (!go) return;
  try {
    const callable = httpsCallable(functions, 'cancelScheduledMemo');
    await callable({ tenantId: HRState.tenantId, memoId });
    window.showToast?.('Scheduled memo cancelled', 'success');
  } catch (err) {
    console.error('[cancelScheduledMemoAction]', err);
    window.showToast?.(`Not cancelled — ${err?.message || 'unknown error'}`, 'error');
  }
}

// TASK-9841 — "Send again": close the drill-down and open the composer
// pre-filled from that memo (title, body, method, still-reachable recipients).
function resendMemo(memoId) {
  const memo = MemoState.memos.find(m => m.id === memoId);
  if (!memo) return;
  const prefill = _memoPrefillFromMemo(memo, HRState.people || []);
  if (prefill) prefill.resendOf = memoId; // TASK-9980 — a deliberate re-send, keyed apart server-side
  closeMemoDrillModal();
  openNewMemoModal(prefill);
}

// TASK-10142 (left-open item 2) — who a reminder would reach, BY NAME, before
// anything is sent. Same "is this person still on the roster" rule as the
// server (_memoPersonIsActive): missing from the roster, active:false,
// terminated or a terminationDate = not reminded. On the roster but with no
// usable email / text (the same check as the composer, _memoPersonReach) =
// named as "can't be reached". Only the toRemind ids are sent to the server,
// which reminds no one else.
function _memoReminderPlan(receipts, people) {
  const byId = new Map((people || []).filter(Boolean).map((p) => [p.id, p]));
  const toRemind = [];
  const unreachable = [];
  const offRoster = [];
  for (const r of (receipts || [])) {
    if (!r || r.confirmedAt || !r.personId) continue;
    const p = byId.get(r.personId);
    const name = String((p && p.name) || r.personName || 'Unnamed person');
    const onRoster = !!p && p.active !== false && p.terminated !== true && !p.terminationDate;
    if (!onRoster) offRoster.push({ id: r.personId, name });
    else if (!_memoPersonReach(p).reachable) unreachable.push({ id: r.personId, name });
    else toRemind.push({ id: r.personId, name });
  }
  const byName = (a, b) => a.name.localeCompare(b.name);
  return { toRemind: toRemind.sort(byName), unreachable: unreachable.sort(byName), offRoster: offRoster.sort(byName) };
}

// Plain text (showConfirm / showToast render with textContent). Every name,
// never "and N more" — this is the review before an irreversible send.
function _memoNameList(list) {
  return list.map((x) => x.name).join(', ');
}

// TASK-9841 — "Remind who hasn't confirmed": one tap re-sends to everyone with
// no read receipt, each with a fresh 24h link.
async function remindMemoUnconfirmedAction(memoId) {
  if (!canSendMemos()) { window.showToast?.(MEMO_SEND_DENIED_MSG, 'error'); return; }
  const memo = MemoState.memos.find(m => m.id === memoId);
  const receipts = MemoState.receiptsByMemo[memoId] || [];
  const unconfirmed = receipts.filter(r => !r.confirmedAt).length;
  if (!unconfirmed) { window.showToast?.('Everyone has confirmed', 'info'); return; }
  const btn = document.getElementById('memo-remind-btn');
  if (btn?.disabled) return;
  // TASK-10142 item 2 — name every person before a reminder goes out (a
  // count-only confirm is how the wrong people get texted). The roster must
  // have loaded: an empty or failed list would mark everyone "off the roster".
  if (!HRState.peopleLoaded || !(HRState.people || []).length) {
    window.showToast?.('The roster is still loading — try the reminder again in a moment', 'info');
    return;
  }
  const plan = _memoReminderPlan(receipts, HRState.people);
  const notLines = [];
  if (plan.unreachable.length) notLines.push(`No email or text on file, so not reminded: ${_memoNameList(plan.unreachable)}.`);
  if (plan.offRoster.length) notLines.push(`No longer on the roster, so not reminded: ${_memoNameList(plan.offRoster)}.`);
  if (!plan.toRemind.length) {
    window.showToast?.(`Nobody to remind. ${notLines.join(' ')}`, 'info');
    return;
  }
  const n = plan.toRemind.length;
  const notText = notLines.length ? ` ${notLines.join(' ')}` : '';
  // COD r2 (Independent / Edge): a long list must not push the buttons past
  // the dialog's 90vh clip on desktop — the shared confirm box scrolls only on
  // phones. Scroll it for this one dialog, then put it back.
  const confirmBox = document.querySelector?.('#confirm-modal .confirm-modal-content');
  const prevOverflow = confirmBox ? confirmBox.style.overflowY : '';
  if (confirmBox) confirmBox.style.overflowY = 'auto';
  let go;
  try {
    go = typeof window.showConfirm === 'function'
      ? await window.showConfirm('Send a reminder?', `Not confirmed "${memo?.title || 'this memo'}" yet. Reminding ${n} ${n === 1 ? 'person' : 'people'}: ${_memoNameList(plan.toRemind)}.${notText} Each gets a fresh link by their usual channel (email and/or text).`, { confirmText: `Remind ${n}` })
      : window.confirm(`Remind ${n}: ${_memoNameList(plan.toRemind)}?${notText}`);
  } finally {
    if (confirmBox) confirmBox.style.overflowY = prevOverflow;
  }
  if (!go) return;
  // COD r2/r3 (Edge, Independent): the roster may have changed while the
  // dialog was open. If anyone named has since left the roster or lost every
  // way to reach them, stop and show the list again. Someone who CONFIRMED in
  // the meantime is fine — the server skips confirmed people.
  const again = _memoReminderPlan(MemoState.receiptsByMemo[memoId] || [], HRState.people);
  const nowBlocked = new Set([...again.offRoster, ...again.unreachable].map((x) => x.id));
  if (plan.toRemind.some((x) => nowBlocked.has(x.id))) {
    window.showToast?.('The list changed while you were looking (someone left the roster or has no email or text now) — press Remind again to see who is left', 'info');
    return;
  }
  if (btn) btn.disabled = true;
  try {
    const callable = httpsCallable(functions, 'remindMemoUnconfirmed');
    // Only the people named above — the server reminds no one else.
    const res = await callable({ tenantId: HRState.tenantId, memoId, personIds: plan.toRemind.map((x) => x.id) });
    const d = res?.data || {};
    window.showToast?.(`Reminder reached ${d.remindedCount || 0} of ${d.unconfirmedCount || 0}${d.skippedCount ? ` (${d.skippedCount} with no way to reach them)` : ''}`, 'success');
  } catch (err) {
    console.error('[remindMemoUnconfirmedAction]', err);
    window.showToast?.(`Reminder not sent — ${err?.message || 'unknown error'}`, 'error');
  } finally {
    if (btn) btn.disabled = false;
  }
}

function openMemoDrillModal(memoId) {
  MemoState.drillMemoId = memoId;
  const modal = document.getElementById('memo-drill-modal');
  if (!modal) return;
  modal.classList.remove('hidden');
  const body = document.getElementById('memo-drill-body');
  if (body) body.innerHTML = '<p style="color:var(--text-secondary);">Loading receipts...</p>';
  // Subscribe to receipts for this memo
  if (MemoState.receiptUnsubs[memoId]) {
    try { MemoState.receiptUnsubs[memoId](); } catch (_) {}
  }
  const tenantId = HRState.tenantId;
  const q = query(
    collection(db, 'tenants', tenantId, 'memoReceipts'),
    where('memoId', '==', memoId),
  );
  MemoState.receiptUnsubs[memoId] = onSnapshot(q, (snap) => {
    const receipts = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    MemoState.receiptsByMemo[memoId] = receipts;
    renderMemoDrillBody(memoId);
  }, (err) => {
    console.warn('[memos] drill subscribe failed:', err.message);
    const bodyEl = document.getElementById('memo-drill-body');
    if (bodyEl) bodyEl.innerHTML = `<p style="color:#991B1B;">Failed to load receipts: ${_memoEscape(err.message)}</p>`;
  });
}

function closeMemoDrillModal() {
  const modal = document.getElementById('memo-drill-modal');
  if (modal) modal.classList.add('hidden');
  if (MemoState.drillMemoId && MemoState.receiptUnsubs[MemoState.drillMemoId]) {
    try { MemoState.receiptUnsubs[MemoState.drillMemoId](); } catch (_) {}
    delete MemoState.receiptUnsubs[MemoState.drillMemoId];
  }
  MemoState.drillMemoId = null;
}

function renderMemoDrillBody(memoId) {
  const memo = MemoState.memos.find(m => m.id === memoId);
  const receipts = MemoState.receiptsByMemo[memoId] || [];
  const titleEl = document.getElementById('memo-drill-title');
  if (titleEl) titleEl.textContent = memo?.title || 'Memo details';
  const body = document.getElementById('memo-drill-body');
  if (!body) return;

  const statusPill = (r) => {
    if (r.confirmedAt) {
      return '<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;background:#D1FAE5;color:#065F46;font-size:0.74rem;font-weight:600;"><span class="material-icons" style="font-size:13px;">check_circle</span>Confirmed</span>';
    }
    if (r.openedAt) {
      return '<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;background:#DBEAFE;color:#1E40AF;font-size:0.74rem;font-weight:600;"><span class="material-icons" style="font-size:13px;">visibility</span>Opened</span>';
    }
    if (r.sentError) {
      // sentError is only set when NOTHING landed (every requested channel failed).
      const failLabel = (r.sentError === 'no_email' || r.sentError === 'no_contact_method')
        ? 'No contact' : 'Failed';
      return `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;background:#FEE2E2;color:#991B1B;font-size:0.74rem;font-weight:600;" title="${_memoEscape(r.sentError)}"><span class="material-icons" style="font-size:13px;">error</span>${failLabel}</span>`;
    }
    if (r.sentChannel === 'none') {
      return '<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;background:#F1F5F9;color:#475569;font-size:0.74rem;font-weight:600;"><span class="material-icons" style="font-size:13px;">block</span>Skipped</span>';
    }
    // Sent on one or more channels. Label the channel; flag partial failures
    // (e.g. 'both' requested, SMS failed but email landed) via tooltip + icon.
    const ch = r.sentChannel === 'both' ? 'Email+SMS'
      : r.sentChannel === 'sms' ? 'SMS'
      : r.sentChannel === 'email' ? 'Email' : '';
    const partialErr = r.sentEmailError || r.sentSmsError || '';
    const label = ch ? `Sent · ${ch}` : 'Sent';
    if (partialErr) {
      return `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;background:#FEF3C7;color:#92400E;font-size:0.74rem;font-weight:600;" title="Partial: ${_memoEscape(partialErr)}"><span class="material-icons" style="font-size:13px;">send</span>${label}</span>`;
    }
    return `<span style="display:inline-flex;align-items:center;gap:4px;padding:2px 8px;border-radius:999px;background:#F1F5F9;color:#475569;font-size:0.74rem;font-weight:600;"><span class="material-icons" style="font-size:13px;">send</span>${label}</span>`;
  };

  const confirmedCount = receipts.filter(r => r.confirmedAt).length;
  const total = receipts.length;
  // TASK-1044: text-direct memos have no ack path — show delivery, not 0/N.
  const noAck = memo?.requiresAck === false;
  const deliveredCount = receipts.filter(r => r.sentChannel && r.sentChannel !== 'none' && !r.sentError).length;
  const summaryLine = noAck
    ? `<div style="margin-top:10px;font-weight:600;color:var(--text-primary);">Delivered to ${deliveredCount} of ${total} <span style="font-weight:400;color:var(--text-secondary);">(sent as a direct text — no read tracking)</span></div>`
    : `<div style="margin-top:10px;font-weight:600;color:var(--text-primary);">Confirmed: ${confirmedCount} of ${total}</div>`;
  // TASK-9841 — state line + actions. A scheduled memo has no receipts yet;
  // a sent ack-tracked memo offers "Remind who hasn't confirmed"; every memo
  // that is not still pending offers "Send again".
  const st = memo?.status || 'sent';
  const unconfirmedCount = receipts.filter(r => !r.confirmedAt).length;
  const can = canSendMemos();
  const claimedMs = memo?.sendClaimedAt?.toMillis?.() ?? null;
  const stuckSending = st === 'sending' && claimedMs !== null && Date.now() - claimedMs > 15 * 60 * 1000;
  let stateLine;
  if (st === 'scheduled') stateLine = `Scheduled for ${_memoEscape(_memoFmtScheduled(memo))}${memo?.sentByName ? ' by ' + _memoEscape(memo.sentByName) : ''}${memo?.deferredReason === 'quiet_hours' ? ' · moved past overnight quiet hours' : ''}`;
  else if (st === 'sending') {
    // COD Domain 2026-10-14: a send that died after the claim is never retried
    // (no double-blast), so it must be VISIBLE as stuck, with the way out.
    stateLine = stuckSending
      ? `<span style="color:#B91C1C;font-weight:600;">Stuck — the send started ${_memoEscape(_memoFmtTs(memo.sendClaimedAt))} and never finished.</span> Nobody will be texted twice from this memo; check the receipts below and use Send again if it has to go out.`
      : 'Sending now…';
  }
  else if (st === 'cancelled') stateLine = `Cancelled — was set for ${_memoEscape(_memoFmtScheduled(memo))}${memo?.cancelReason === 'cancelled_by_sender' ? '' : ` (${_memoEscape(String(memo?.cancelReason || '').replace(/_/g, ' '))})`}`;
  else if (st === 'failed') stateLine = `Not sent — ${_memoEscape(memo?.failReason || 'send failed')} (was set for ${_memoEscape(_memoFmtScheduled(memo))})`;
  else stateLine = `Sent ${_memoEscape(_memoFmtTs(memo?.sentAt))} ${memo?.sentByName ? 'by ' + _memoEscape(memo.sentByName) : ''}`;
  const reminderLine = memo?.lastReminderAt
    ? `<div style="font-size:0.82rem;color:var(--text-secondary);margin-top:4px;"><span class="material-icons" style="font-size:14px;vertical-align:middle;">notifications_active</span> Last reminder ${_memoEscape(_memoFmtTs(memo.lastReminderAt))} — reached ${memo.lastReminderReached ?? '?'} of ${memo.lastReminderUnconfirmed ?? '?'} unconfirmed${memo.reminderCount > 1 ? ` · ${memo.reminderCount} reminders total` : ''}</div>`
    : '';
  const actions = [];
  if (can && st === 'scheduled') {
    actions.push(`<button type="button" class="btn btn-secondary btn-sm" style="color:#B91C1C;" onclick="window.HRModule.cancelScheduledMemoAction('${escJsAttr(memoId)}', event)"><span class="material-icons" style="font-size:15px;vertical-align:middle;">event_busy</span> Cancel scheduled memo</button>`);
  }
  if (can && st === 'sent' && memo?.requiresAck !== false && unconfirmedCount > 0) {
    // TASK-10142 item 2 (COD r1 UX): the label counts who the reminder would
    // actually reach, the same number the confirm shows.
    const remindCount = HRState.peopleLoaded && (HRState.people || []).length ? _memoReminderPlan(receipts, HRState.people).toRemind.length : unconfirmedCount;
    actions.push(`<button type="button" id="memo-remind-btn" class="btn btn-primary btn-sm" data-trailguide="memo-remind" onclick="window.HRModule.remindMemoUnconfirmedAction('${escJsAttr(memoId)}')"><span class="material-icons" style="font-size:15px;vertical-align:middle;">notifications_active</span> Remind who hasn't confirmed (${remindCount})</button>`);
  }
  if (can && st !== 'scheduled' && (st !== 'sending' || stuckSending) && memo?.kind !== 'training') { // TASK-10210: a training is re-assigned from Assign Training (Send again would drop its link)
    actions.push(`<button type="button" class="btn btn-secondary btn-sm" data-trailguide="memo-send-again" onclick="window.HRModule.resendMemo('${escJsAttr(memoId)}')"><span class="material-icons" style="font-size:15px;vertical-align:middle;">replay</span> Send again</button>`);
  }
  const headerHtml = `
    <div style="margin-bottom:14px;">
      <div style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:4px;">${stateLine}</div>
      ${memo?.body ? `<div style="background:#F8FAFC;border:1px solid var(--border-light);border-radius:6px;padding:10px 12px;color:var(--text-primary);font-size:0.92rem;white-space:pre-wrap;margin-bottom:10px;">${_memoEscape(memo.body)}</div>` : ''}
      ${memo?.pdfStoragePath ? '<div style="font-size:0.82rem;color:var(--text-secondary);"><span class="material-icons" style="font-size:14px;vertical-align:middle;">attach_file</span> PDF attached</div>' : ''}
      ${st === 'sent' || !memo?.status ? summaryLine : `<div style="margin-top:10px;font-weight:600;color:var(--text-primary);">${memo?.recipientCount || 0} recipient${(memo?.recipientCount || 0) === 1 ? '' : 's'}</div>`}
      ${reminderLine}
      ${actions.length ? `<div style="display:flex;gap:8px;flex-wrap:wrap;margin-top:12px;">${actions.join('')}</div>` : ''}
    </div>
  `;

  const rows = receipts.length === 0
    ? `<tr><td colspan="4" style="padding:18px;text-align:center;color:var(--text-secondary);">${st === 'scheduled' ? 'Receipts appear once the memo goes out.' : 'No receipts yet.'}</td></tr>`
    : receipts.map((r) => `
        <tr style="border-top:1px solid var(--border-light);">
          <td style="padding:10px 12px;font-weight:500;">${_memoEscape(r.personName || r.personId || '?')}${r.reminderCount > 0 ? `<div style="font-size:0.74rem;color:var(--text-secondary);font-weight:400;" title="${_memoEscape(r.lastReminderError || '')}">Reminded ${r.reminderCount}× · last ${_memoEscape(_memoFmtTs(r.lastReminderAt) || '')}${r.lastReminderChannel === 'none' ? ' · not reachable' : ''}</div>` : ''}</td>
          <td style="padding:10px 12px;font-size:0.85rem;color:var(--text-secondary);white-space:nowrap;">${_memoEscape(_memoFmtTs(r.openedAt) || '—')}</td>
          <td style="padding:10px 12px;font-size:0.85rem;color:var(--text-secondary);white-space:nowrap;">${_memoEscape(_memoFmtTs(r.confirmedAt) || '—')}</td>
          <td style="padding:10px 12px;">${statusPill(r)}</td>
        </tr>
      `).join('');

  body.innerHTML = headerHtml + `
    <div style="border:1px solid var(--border-light);border-radius:8px;overflow:hidden;overflow-x:auto;">
      <table style="width:100%;border-collapse:collapse;min-width:520px;">
        <thead style="background:#F8FAFC;">
          <tr>
            <th style="text-align:left;padding:10px 12px;font-size:0.74rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;">Person</th>
            <th style="text-align:left;padding:10px 12px;font-size:0.74rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;">Opened</th>
            <th style="text-align:left;padding:10px 12px;font-size:0.74rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;">Confirmed</th>
            <th style="text-align:left;padding:10px 12px;font-size:0.74rem;font-weight:600;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.04em;">Status</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

// ============================================================================
// TASK-972 Slice 4 — DOT Auditor Access Links (admin mint / list / revoke UI)
//
// Time-boxed READ-ONLY links a tenant owner/admin hands a DOT auditor
// (New Entrant audit, compliance review) instead of emailing ZIPs. Server
// side (Slice 1, LIVE): mintAuditorLink / listAuditorLinks / revokeAuditorLink
// callables — all requireTenantMember + requireTenantAdmin gated; token stored
// SHA-256-only at rest, raw secret returned exactly ONCE in the mint response.
//
// Client rules (COD Secrets lane):
// • The raw link is rendered ONCE into the result box and never persisted —
//   no module state, no localStorage, nothing survives the modal closing.
//   _dotAuditLinks holds listAuditorLinks metadata only (tokenId = sha256,
//   deliberately not replayable as the ?token= secret).
// • The link URL is displayed VERBATIM from the mint response — never
//   constructed client-side (server owns the auditor.html URL format).
// • UI is owner/admin-gated to mirror the server gate (never render the
//   surface for non-admins); the server remains the real boundary.
//
// Shared by compliance.js (compliance-only carriers have no HR module):
// window.HRModule.openDotAuditAccess({ tenantId, people }) — window.* at call
// time per pitfall #1, never a second ?v= import.
// ============================================================================

const _DOT_AUDIT_MODAL_ID = 'dot-audit-access-modal';
// Context for the open modal. NEVER holds a raw token — tenant + roster only.
let _dotAuditCtx = { tenantId: null, people: [] };
let _dotAuditLinks = [];          // last listAuditorLinks rows (hash ids + metadata only)
let _dotAuditShowInactive = false;
// TASK-1209 (d) — search/pill driver selector state (replaces the bare
// <select multiple>, unusable past ~20 drivers). Rebuilt on every modal open.
let _dotAuditDrivers = [];        // [{id, name, separated}] — separated included for §391.51 retention audits
let _dotAuditSelected = new Map(); // id -> {name, separated}
let _dotAuditSearch = '';
let _dotAuditMintInFlight = false; // debounce — a double-click must not mint two tokens

function _dotAuditDriverish(p) {
  // Same predicate as the D&A audit-packet driver picker (compliance.js).
  const role = (p.crewType || p.role || (p.roles?.[0]) || '').toLowerCase();
  return role.includes('driver') || p.cdlNumber || p.hasCdlNumber === true || p.licenseNumber; // TASK-9359 marker
}

// TASK-1209 — the server already accepts separated drivers in scopedPersonIds
// (§391.51 keeps their DQ file 3 years post-separation, and retention audits
// target exactly those). The old picker listed active only — widen with a tag.
function _dotAuditIsSeparated(p) {
  return p.active === false || p.terminated === true || !!p.terminationDate;
}

function _dotAuditFmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function openDotAuditAccessModal(opts = {}) {
  // UI mirror of the server's requireTenantAdmin gate (server still enforces).
  if (!_hrIsOwnerOrAdmin()) {
    window.showToast?.('Only the owner or an admin can manage auditor access links', 'error');
    return;
  }
  const tenantId = opts.tenantId || HRState.tenantId || window.AppState?.tenant;
  if (!tenantId) { window.showToast?.('Tenant not loaded yet', 'error'); return; }
  if (tenantId === 'demo' || window.AppState?.isDemo) {
    window.showToast?.('Auditor access links are not available on the demo account', 'error');
    return;
  }
  _dotAuditCtx = {
    tenantId,
    people: (Array.isArray(opts.people) && opts.people.length ? opts.people : (HRState.people || [])),
  };
  _dotAuditShowInactive = false;

  document.getElementById(_DOT_AUDIT_MODAL_ID)?.remove();

  // TASK-1209 (d) — include separated drivers (tagged), active first, A→Z within group.
  _dotAuditDrivers = _dotAuditCtx.people
    .filter(p => p.id && _dotAuditDriverish(p))
    .map(p => ({ id: p.id, name: p.name || '(unnamed)', separated: _dotAuditIsSeparated(p) }))
    .sort((a, b) => (a.separated === b.separated) ? a.name.localeCompare(b.name) : (a.separated ? 1 : -1));
  _dotAuditSelected = new Map();
  _dotAuditSearch = '';
  _dotAuditMintInFlight = false;

  const modal = document.createElement('div');
  modal.id = _DOT_AUDIT_MODAL_ID;
  modal.className = 'modal';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:9999;display:flex;align-items:center;justify-content:center;padding:16px;';
  modal.innerHTML = `
    <div style="background:#fff;border-radius:var(--radius-lg);max-width:640px;width:100%;max-height:90vh;max-height:90dvh;overflow-y:auto;">
      <div style="padding:14px 16px;border-bottom:1px solid var(--border-light);display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">policy</span>
        <h3 style="margin:0;font-size:1.05rem;font-weight:700;flex:1;">DOT Audit Access</h3>
        <button class="btn-icon" onclick="document.getElementById('${_DOT_AUDIT_MODAL_ID}').remove()"><span class="material-icons">close</span></button>
      </div>
      <div style="padding:16px;">
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-bottom:12px;">
          Create a time-boxed, <strong>read-only</strong> link a DOT auditor can open in any browser — no login, no ZIPs to email.
          Links expire automatically and can be revoked at any time. Auditors see driver qualification records only; nothing is editable.
        </div>

        <div class="form-group">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Label (optional — who is this link for?)</label>
          <input type="text" id="daa-label" class="form-control" maxlength="120" placeholder="e.g. FMCSA New Entrant audit — Inv. Smith">
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:10px;">
          <div class="form-group">
            <label style="font-size:0.78rem;color:var(--text-secondary);">Link expires after</label>
            <select id="daa-expiry" class="form-control">
              <option value="1">1 day</option>
              <option value="3">3 days</option>
              <option value="7" selected>7 days</option>
              <option value="14">14 days</option>
              <option value="21">21 days</option>
              <option value="30">30 days (max)</option>
            </select>
          </div>
        </div>

        <div class="form-group">
          <label style="font-size:0.78rem;color:var(--text-secondary);">Drivers the auditor can see</label>
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
            <input type="checkbox" id="daa-all-drivers" checked onchange="window.HRModule.setDotAuditAllDrivers(this.checked)">
            <label for="daa-all-drivers" style="font-size:0.8rem;cursor:pointer;">All drivers</label>
          </div>
          <div id="daa-picker" style="opacity:0.6;pointer-events:none;">
            <div id="daa-pills" style="display:flex;flex-wrap:wrap;gap:6px;"></div>
            <input type="text" id="daa-driver-search" class="form-control" placeholder="Search drivers…" autocomplete="off" oninput="window.HRModule.searchDotAuditDrivers(this.value)" style="margin-top:6px;">
            <div id="daa-driver-list" style="max-height:180px;overflow-y:auto;border:1px solid var(--border-light);border-radius:8px;margin-top:6px;"></div>
            <div style="font-size:0.7rem;color:var(--text-muted);margin-top:4px;">Separated drivers are included — DOT retention audits (&sect;391.51) can cover former drivers.</div>
          </div>
        </div>

        <div style="display:flex;justify-content:flex-end;">
          <button class="btn btn-primary" id="daa-mint-btn" onclick="window.HRModule.mintDotAuditLink()">
            <span class="material-icons" style="font-size:14px;">add_link</span> Create Auditor Link
          </button>
        </div>

        <div id="daa-mint-result" style="font-size:0.8rem;"></div>

        <div style="margin-top:16px;padding-top:12px;border-top:1px solid var(--border-light);">
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
            <span style="font-size:0.85rem;font-weight:700;color:var(--text-primary);">Existing links</span>
            <label style="margin-left:auto;display:flex;align-items:center;gap:5px;font-size:0.72rem;color:var(--text-secondary);cursor:pointer;">
              <input type="checkbox" id="daa-show-inactive" onchange="window.HRModule.toggleDotAuditInactive(this.checked)">
              Show revoked &amp; expired
            </label>
          </div>
          <div id="daa-links"><div style="font-size:0.75rem;color:var(--text-muted);padding:6px 0;">Loading…</div></div>
        </div>
      </div>
      <div style="padding:12px 16px;border-top:1px solid var(--border-light);display:flex;justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="document.getElementById('${_DOT_AUDIT_MODAL_ID}').remove()">Close</button>
      </div>
    </div>`;
  document.body.appendChild(modal);

  _dotAuditRenderPicker();
  _dotAuditRefreshLinks();
}

// ── TASK-1209 (d) — search/pill driver selector ─────────────────────────────
function _dotAuditSetAllDrivers(checked) {
  const picker = document.getElementById('daa-picker');
  if (!picker) return;
  picker.style.opacity = checked ? '0.6' : '1';
  picker.style.pointerEvents = checked ? 'none' : 'auto';
  if (!checked) document.getElementById('daa-driver-search')?.focus();
}

function _dotAuditSearchDrivers(value) {
  _dotAuditSearch = String(value || '').trim().toLowerCase();
  _dotAuditRenderPicker();
}

function _dotAuditToggleDriver(id) {
  if (_dotAuditSelected.has(id)) {
    _dotAuditSelected.delete(id);
  } else {
    const d = _dotAuditDrivers.find(x => x.id === id);
    if (!d) return;
    _dotAuditSelected.set(id, { name: d.name, separated: d.separated });
  }
  _dotAuditRenderPicker();
}

function _dotAuditRenderPicker() {
  const pillsEl = document.getElementById('daa-pills');
  const listEl = document.getElementById('daa-driver-list');
  if (!pillsEl || !listEl) return;

  pillsEl.innerHTML = Array.from(_dotAuditSelected.entries()).map(([id, d]) => `
    <span style="display:inline-flex;align-items:center;gap:4px;background:#DBEAFE;color:#1D4ED8;font-size:0.74rem;font-weight:600;padding:3px 8px;border-radius:999px;max-width:100%;">
      <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(d.name)}${d.separated ? ' <span style="font-weight:400;opacity:0.75;">(separated)</span>' : ''}</span>
      <button type="button" aria-label="Remove ${escapeHtml(d.name)}" onclick="window.HRModule.toggleDotAuditDriver('${escJsAttr(id)}')" style="border:none;background:none;cursor:pointer;color:#1D4ED8;padding:0;display:inline-flex;"><span class="material-icons" style="font-size:14px;">close</span></button>
    </span>`).join('');

  const q = _dotAuditSearch;
  const matches = q
    ? _dotAuditDrivers.filter(d => d.name.toLowerCase().includes(q))
    : _dotAuditDrivers;
  if (matches.length === 0) {
    listEl.innerHTML = `<div style="font-size:0.75rem;color:var(--text-muted);padding:10px 12px;">${
      _dotAuditDrivers.length === 0 ? 'No drivers on the roster yet.' : 'No drivers match your search.'
    }</div>`;
    return;
  }
  listEl.innerHTML = matches.map(d => {
    const on = _dotAuditSelected.has(d.id);
    return `
      <div onclick="window.HRModule.toggleDotAuditDriver('${escJsAttr(d.id)}')" style="display:flex;align-items:center;gap:8px;padding:7px 10px;cursor:pointer;border-bottom:1px solid var(--border-light);${on ? 'background:#EFF6FF;' : ''}">
        <span class="material-icons" style="font-size:18px;color:${on ? 'var(--brand-blue)' : 'var(--text-muted)'};">${on ? 'check_box' : 'check_box_outline_blank'}</span>
        <span style="font-size:0.82rem;color:var(--text-primary);flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escapeHtml(d.name)}</span>
        ${d.separated ? '<span style="font-size:0.64rem;font-weight:700;color:#92400E;background:#FEF3C7;padding:2px 7px;border-radius:8px;white-space:nowrap;">SEPARATED</span>' : ''}
      </div>`;
  }).join('');
}

async function _dotAuditRefreshLinks() {
  const listEl = document.getElementById('daa-links');
  if (!listEl) return;
  try {
    const fn = httpsCallable(functions, 'listAuditorLinks');
    const res = await fn({ tenantId: _dotAuditCtx.tenantId });
    _dotAuditLinks = (res.data && Array.isArray(res.data.links)) ? res.data.links : [];
    _dotAuditRenderLinks();
  } catch (err) {
    console.error('[DOT Audit Access] list error:', err);
    const el = document.getElementById('daa-links');
    if (el) el.innerHTML = `<div style="color:#DC2626;font-size:0.78rem;padding:6px 0;">Could not load links: ${escapeHtml(err?.message || 'unknown error')}</div>`;
  }
}

function _dotAuditScopeLabel(link) {
  const ids = link.scopedPersonIds;
  if (!Array.isArray(ids) || ids.length === 0) return 'All drivers';
  if (ids.length <= 3) {
    const names = ids.map(id => {
      const p = (_dotAuditCtx.people || []).find(x => x.id === id);
      return p?.name || 'Unknown';
    });
    return escapeHtml(names.join(', '));
  }
  return `${ids.length} drivers`;
}

function _dotAuditRenderLinks() {
  const listEl = document.getElementById('daa-links');
  if (!listEl) return;
  const rows = _dotAuditShowInactive ? _dotAuditLinks : _dotAuditLinks.filter(l => l.active);
  if (rows.length === 0) {
    listEl.innerHTML = `<div style="font-size:0.75rem;color:var(--text-muted);padding:6px 0;">${
      _dotAuditLinks.length === 0 ? 'No auditor links yet.' : 'No active links. Check "Show revoked & expired" to see past ones.'
    }</div>`;
    return;
  }
  listEl.innerHTML = rows.map(l => {
    const status = l.revoked
      ? `<span style="font-size:0.66rem;font-weight:700;color:#6B7280;background:#E5E7EB;padding:2px 8px;border-radius:8px;">REVOKED</span>`
      : l.expired
        ? `<span style="font-size:0.66rem;font-weight:700;color:#92400E;background:#FDE68A;padding:2px 8px;border-radius:8px;">EXPIRED</span>`
        : `<span style="font-size:0.66rem;font-weight:700;color:#065F46;background:#D1FAE5;padding:2px 8px;border-radius:8px;">ACTIVE</span>`;
    const used = (l.useCount || 0) > 0
      ? `Opened ${l.useCount}× · last ${_dotAuditFmtDate(l.lastUsedAt)}`
      : 'Never opened';
    // TASK-1209 (b) — carrier-visible provenance: a partner-minted link is
    // labeled so the carrier can see WHO created it (and revoke it — the same
    // Revoke button works on partner-minted rows; the server allows either side).
    const mintedBadge = l.mintedByRole === 'partner'
      ? `<span style="font-size:0.66rem;font-weight:700;color:#5B21B6;background:#EDE9FE;padding:2px 8px;border-radius:8px;">Generated by ${escapeHtml(l.mintedByName || 'your compliance partner')}</span>`
      : '';
    const revokeBtn = l.active
      ? `<button class="btn btn-sm btn-secondary" style="color:#DC2626;white-space:nowrap;" onclick="window.HRModule.revokeDotAuditLink('${escJsAttr(l.tokenId)}')">
           <span class="material-icons" style="font-size:14px;">link_off</span> Revoke
         </button>`
      : '';
    return `
      <div style="display:flex;align-items:center;gap:10px;padding:8px 10px;background:var(--bg-secondary);border-radius:8px;margin-bottom:6px;${l.active ? '' : 'opacity:0.55;'}">
        <div style="flex:1;min-width:0;">
          <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
            <span style="font-size:0.8rem;font-weight:600;color:var(--text-primary);overflow:hidden;text-overflow:ellipsis;">${escapeHtml(l.label || 'Auditor link')}</span>
            ${status}
            ${mintedBadge}
          </div>
          <div style="font-size:0.7rem;color:var(--text-secondary);margin-top:2px;">
            ${_dotAuditScopeLabel(l)} · Created ${_dotAuditFmtDate(l.createdAt)} · ${l.revoked ? `Revoked ${_dotAuditFmtDate(l.revokedAt)}` : `Expires ${_dotAuditFmtDate(l.expiresAt)}`}
          </div>
          <div style="font-size:0.7rem;color:var(--text-muted);margin-top:1px;">${used}</div>
        </div>
        ${revokeBtn}
      </div>`;
  }).join('');
}

async function mintDotAuditLinkAction() {
  // TASK-1209 (d) — debounce: a double-click must not mint two live tokens
  // (the disabled-button guard alone leaves a pre-disable window on slow devices).
  if (_dotAuditMintInFlight) return;
  const allDrivers = document.getElementById('daa-all-drivers')?.checked;
  let scopedPersonIds; // undefined = omit param = all drivers
  if (!allDrivers) {
    scopedPersonIds = Array.from(_dotAuditSelected.keys());
    if (scopedPersonIds.length === 0) { window.showToast?.('Pick at least one driver, or check "All drivers"', 'error'); return; }
  }
  const expiresInDays = parseInt(document.getElementById('daa-expiry')?.value, 10) || 7;
  const label = document.getElementById('daa-label')?.value?.trim() || null;

  const btn = document.getElementById('daa-mint-btn');
  const resultEl = document.getElementById('daa-mint-result');
  _dotAuditMintInFlight = true;
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Creating…'; }

  try {
    const fn = httpsCallable(functions, 'mintAuditorLink');
    const payload = { tenantId: _dotAuditCtx.tenantId, expiresInDays, label };
    if (scopedPersonIds) payload.scopedPersonIds = scopedPersonIds;
    const res = await fn(payload);
    const out = res.data || {};
    // ── ONE-TIME raw link display. `out.url` is shown verbatim from the server
    // response and lives ONLY in this input element — the server stores a hash,
    // so the link can never be shown again. Do not stash it anywhere else. ──
    if (resultEl) {
      resultEl.innerHTML = `
        <div style="background:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;padding:12px;margin-top:12px;">
          <div style="display:flex;align-items:center;gap:6px;font-weight:700;color:#92400E;margin-bottom:4px;">
            <span class="material-icons" style="font-size:16px;">warning_amber</span> Copy this link now — it won't be shown again
          </div>
          <div style="font-size:0.74rem;color:#92400E;margin-bottom:8px;">
            For security we only store a fingerprint (hash) of this link, so it cannot be recovered later.
            If you lose it, revoke it below and create a new one. Expires ${_dotAuditFmtDate(out.expiresAt)}.
          </div>
          <div style="display:flex;gap:6px;align-items:center;">
            <input type="text" id="daa-raw-url" class="form-control" readonly value="${escapeHtml(out.url || '')}" onclick="this.select()" style="flex:1;font-size:0.75rem;font-family:monospace;">
            <button class="btn btn-primary btn-sm" style="white-space:nowrap;" onclick="navigator.clipboard.writeText(document.getElementById('daa-raw-url').value).then(()=>{this.innerHTML='<span class=\\'material-icons\\' style=\\'font-size:14px;\\'>check</span> Copied';setTimeout(()=>{this.innerHTML='<span class=\\'material-icons\\' style=\\'font-size:14px;\\'>content_copy</span> Copy'},2000);}).catch(()=>{window.showToast?.('Copy failed — select the link and copy it manually','error');document.getElementById('daa-raw-url')?.select();})">
              <span class="material-icons" style="font-size:14px;">content_copy</span> Copy
            </button>
          </div>
        </div>`;
    }
    window.showToast?.('Auditor link created', 'success');
    _dotAuditRefreshLinks();
  } catch (err) {
    console.error('[DOT Audit Access] mint error:', err);
    if (resultEl) resultEl.innerHTML = `<div style="color:#DC2626;padding:8px 0;">Error: ${escapeHtml(err?.message || 'unknown error')}</div>`;
  } finally {
    _dotAuditMintInFlight = false;
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:14px;">add_link</span> Create Auditor Link'; }
  }
}

async function revokeDotAuditLinkAction(tokenId) {
  const link = _dotAuditLinks.find(l => l.tokenId === tokenId);
  const who = link?.label ? `"${link.label}"` : 'this auditor link';
  // App-pattern confirm only — no native confirm() fallback (COD Code Reviewer);
  // fail closed if the shared dialog isn't available for some reason.
  if (!window.showConfirm) { window.showToast?.('Confirm dialog unavailable — reload and try again', 'error'); return; }
  const ok = await window.showConfirm(
    'Revoke auditor link?',
    `Revoking ${who} takes effect immediately — the auditor loses access on their very next request. This cannot be undone (you can always create a new link).`);
  if (!ok) return;
  try {
    const fn = httpsCallable(functions, 'revokeAuditorLink');
    await fn({ tenantId: _dotAuditCtx.tenantId, tokenId });
    window.showToast?.('Auditor link revoked', 'success');
    _dotAuditRefreshLinks();
  } catch (err) {
    console.error('[DOT Audit Access] revoke error:', err);
    window.showToast?.('Could not revoke: ' + (err?.message || 'unknown error'), 'error');
  }
}

// =============================================================================
// TASK-1482 — Driver record portability between DispatchDeck carriers (v1).
//
// Carrier-side UI over four Admin-SDK callables (initiateDriverRecordPort /
// confirmDriverRecordPort / cancelDriverRecordPort / sendPeiRequestInApp).
// The DRIVER authorizes by SMS OTP; carrier B types the code the driver reads
// them. The pending state renders IDENTICALLY whether or not a cross-tenant
// match exists (existence-oracle fence — see the spec §4); the server is the
// only party that knows. person.recordPort / person.portedRecords are
// DISPLAY-ONLY mirrors the server writes — the real state lives in the
// client-unreadable _driverRecordTransfers collection.
// =============================================================================

const _PORT_FIELD_LABELS = {
  dlState: 'DL State', dlNumber: 'DL Number', dlIssueDate: 'DL Issue Date', dlExpiryDate: 'DL Expiry',
  cdlLicensed: 'CDL Licensed', cdlClass: 'CDL Class', cdlEndorsements: 'Endorsements', cdlRestrictions: 'Restrictions',
  cdlIssueDate: 'CDL Issue Date', cdlExpiryDate: 'CDL Expiry',
  medCardExpiryDate: 'Med Card Expiry', medCardNumber: 'Med Card Provider',
};
const _PORT_DOC_LABELS = { license: "Driver's License document", medCard: 'Medical Card document' };

function _recordPortSectionHTML(emp) {
  const rp = emp.recordPort || {};
  const ported = emp.portedRecords || null;
  const pid = escapeHtml(emp.id || '');

  // Pending — "Awaiting driver SMS confirmation" + code entry (spec §3).
  if (rp.status === 'pending') {
    const expMs = rp.expiresAt ? new Date(rp.expiresAt).getTime() : 0;
    const expired = expMs && expMs < Date.now();
    return `
    <div class="compliance-section-card" id="hr-record-port-card" style="border-left:4px solid ${expired ? '#94A3B8' : '#F59E0B'};">
      <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;margin-bottom:6px;">
        <span class="material-icons" style="font-size:18px;color:${expired ? '#64748B' : '#F59E0B'};">sms</span>
        Bring records from a previous employer
      </div>
      ${expired ? `
      <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">The last request expired with no driver confirmation. You can send a new one.</div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        <button class="btn btn-sm btn-primary" onclick="window.HRModule.startRecordPort('${pid}')" style="font-size:0.78rem;">
          <span class="material-icons" style="font-size:14px;">refresh</span> Send a new code
        </button>
      </div>` : `
      <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:8px;">
        <b>Awaiting driver SMS confirmation.</b> If this driver has records at a previous DispatchDeck employer, they just got a text with a 6-digit approval code (expires 15 minutes after the request). Ask the driver for the code and enter it here — no code means no prior records, or the driver declined.
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;align-items:center;">
        <input type="text" id="port-code-input-${pid}" inputmode="numeric" maxlength="6" placeholder="6-digit code" autocomplete="one-time-code"
               style="width:120px;padding:6px 10px;font-size:0.95rem;letter-spacing:3px;border:1px solid var(--border-light);border-radius:6px;background:var(--bg-primary);color:var(--text-primary);">
        <button class="btn btn-sm btn-primary" onclick="window.HRModule.confirmRecordPort('${pid}')" style="font-size:0.78rem;">
          <span class="material-icons" style="font-size:14px;">check</span> Confirm code
        </button>
        <button class="btn btn-sm btn-secondary" onclick="window.HRModule.cancelRecordPort('${pid}')" style="font-size:0.78rem;">Cancel request</button>
      </div>`}
    </div>`;
  }

  // Approved — the "Ported — review & confirm" banner (spec §3 step 3/4).
  if (ported || rp.status === 'approved') {
    const p = ported || {};
    const fields = Array.isArray(p.fields) ? p.fields : [];
    const docs = Array.isArray(p.docs) ? p.docs : [];
    const items = fields.map(f => _PORT_FIELD_LABELS[f] || f).concat(docs.map(d => _PORT_DOC_LABELS[d] || d));
    const reviewed = !!p.reviewedAt;
    const from = escapeHtml(p.fromCarrierName || rp.fromCarrierName || 'a previous DispatchDeck employer');
    const when = p.at ? formatDateShort(String(p.at).slice(0, 10)) : '';
    return `
    <div class="compliance-section-card" id="hr-record-port-card" style="border-left:4px solid ${reviewed ? '#059669' : '#F59E0B'};">
      <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;margin-bottom:6px;flex-wrap:wrap;">
        <span class="material-icons" style="font-size:18px;color:${reviewed ? '#059669' : '#F59E0B'};">move_down</span>
        Records ported from ${from}
        ${reviewed
          ? '<span style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;background:#D1FAE5;color:#065F46;border-radius:6px;font-size:0.7rem;font-weight:600;"><span class="material-icons" style="font-size:12px;">verified</span> Reviewed</span>'
          : '<span style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;background:#FEF3C7;color:#92400E;border-radius:6px;font-size:0.7rem;font-weight:600;"><span class="material-icons" style="font-size:12px;">pending</span> Ported — review &amp; confirm</span>'}
      </div>
      <div style="font-size:0.78rem;color:var(--text-secondary);margin-bottom:8px;">
        Driver-approved by SMS${when ? ` on ${escapeHtml(when)}` : ''}. ${items.length
          ? `Pre-filled: ${escapeHtml(items.join(', '))}. Review each value before treating this DQ file as complete — porting is a convenience, not a verification.`
          : 'No fields needed pre-filling (this file already had its own values).'}
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        ${(!reviewed && _hrCanWriteRoster()) ? `
        <button class="btn btn-sm btn-primary" onclick="window.HRModule.markPortReviewed('${pid}')" style="font-size:0.78rem;">
          <span class="material-icons" style="font-size:14px;">task_alt</span> Mark reviewed
        </button>` : ''}
        <button class="btn btn-sm btn-secondary" onclick="window.HRModule.openInAppPeiModal('${pid}')" style="font-size:0.78rem;" title="Send the §391.23 Safety Performance History request to the previous employer inside DispatchDeck — it lands in their HR action queue instead of a fax">
          <span class="material-icons" style="font-size:14px;">forward_to_inbox</span> Send §391.23 request via DispatchDeck
        </button>
      </div>
    </div>`;
  }

  // Idle — offer the port only when the person has a phone (the driver
  // approves by SMS; without a phone there is nothing to offer).
  if (!emp.phone) return '';
  return `
  <div class="compliance-section-card" id="hr-record-port-card">
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;">
      <div style="flex:1;min-width:220px;">
        <div style="font-weight:700;font-size:0.9rem;color:var(--text-primary);display:flex;align-items:center;gap:6px;">
          <span class="material-icons" style="font-size:18px;color:var(--brand-blue);">move_down</span>
          Bring records from a previous employer
        </div>
        <div style="font-size:0.78rem;color:var(--text-secondary);margin-top:2px;">
          If this driver worked at another company on DispatchDeck, they can bring their license &amp; med card records over. The driver approves by SMS — nothing is shared until they do.
        </div>
      </div>
      <button class="btn btn-sm btn-secondary" onclick="window.HRModule.startRecordPort('${pid}')" style="font-size:0.78rem;flex-shrink:0;">
        <span class="material-icons" style="font-size:14px;">sms</span> Text the driver
      </button>
    </div>
  </div>`;
}

// Small chip appended next to a section header when that section carries
// un-reviewed ported values ("every field badged" — spec §3 step 3).
function _portChipFor(emp, sectionFields, sectionDocs) {
  const p = emp.portedRecords;
  if (!p || p.reviewedAt) return '';
  const fields = Array.isArray(p.fields) ? p.fields : [];
  const docs = Array.isArray(p.docs) ? p.docs : [];
  const hit = fields.some(f => sectionFields.includes(f)) || docs.some(d => (sectionDocs || []).includes(d));
  if (!hit) return '';
  return '<span title="These values were ported from a previous DispatchDeck employer with the driver\'s approval — review before relying on them" style="display:inline-flex;align-items:center;gap:3px;padding:2px 8px;background:#FEF3C7;color:#92400E;border-radius:6px;font-size:0.68rem;font-weight:600;"><span class="material-icons" style="font-size:12px;">move_down</span> Ported — review &amp; confirm</span>';
}

function _portRerenderComplianceTab(personId) {
  const emp = HRState.people.find(p => p.id === personId);
  const container = document.getElementById('hr-detail-tab-compliance');
  if (emp && container) {
    container.innerHTML = buildComplianceTabHTML(emp);
    loadEmployeeCitations(personId);
  }
}

let _portBusy = false;
async function startRecordPort(personId) {
  if (_portBusy) return;
  const tenantId = window.AppState?.tenant || HRState.tenantId;
  const emp = HRState.people.find(p => p.id === personId);
  if (!tenantId || !emp) return;
  if (!window.showConfirm) { window.showToast?.('Confirm dialog unavailable — reload and try again', 'error'); return; }
  const ok = await window.showConfirm(
    'Text the driver an approval code?',
    `${emp.name || 'This person'} will get an SMS at ${emp.phone || 'their phone on file'} asking to approve bringing their license & med card records over from a previous DispatchDeck employer. Nothing is shared unless they approve.`,
    { confirmText: 'Send the text', cancelText: 'Not now' });
  if (!ok) return;
  _portBusy = true;
  try {
    const fn = httpsCallable(functions, 'initiateDriverRecordPort');
    const res = await fn({ tenantId, personId });
    // Optimistic mirror — the server wrote the same shape to the person doc.
    emp.recordPort = { status: 'pending', transferId: res?.data?.transferId || '', requestedAt: new Date().toISOString(), expiresAt: res?.data?.expiresAt || new Date(Date.now() + 15 * 60000).toISOString() };
    window.showToast?.('Text sent — ask the driver for the 6-digit code', 'success');
    _portRerenderComplianceTab(personId);
  } catch (err) {
    console.error('[HR] initiateDriverRecordPort failed', err);
    window.showToast?.(err?.message || 'Could not start the record port', 'error');
  } finally { _portBusy = false; }
}

async function confirmRecordPort(personId) {
  if (_portBusy) return;
  const tenantId = window.AppState?.tenant || HRState.tenantId;
  const emp = HRState.people.find(p => p.id === personId);
  if (!tenantId || !emp) return;
  const code = (document.getElementById(`port-code-input-${personId}`)?.value || '').trim();
  if (!/^\d{6}$/.test(code)) { window.showToast?.('Enter the 6-digit code from the driver\'s text', 'error'); return; }
  _portBusy = true;
  try {
    const fn = httpsCallable(functions, 'confirmDriverRecordPort');
    const res = await fn({ tenantId, personId, code });
    const d = res?.data || {};
    emp.recordPort = { status: 'approved', approvedAt: new Date().toISOString(), fromCarrierName: d.fromCarrierName || '' };
    emp.portedRecords = { at: new Date().toISOString(), fromCarrierName: d.fromCarrierName || '', fields: d.fields || [], docs: d.docs || [], reviewedAt: null };
    window.showToast?.((d.fields?.length || d.docs?.length)
      ? `Records ported from ${d.fromCarrierName || 'the previous employer'} — review & confirm the pre-filled values`
      : 'Transfer approved — this file already had its own values', 'success');
    // The server also copied fields onto the person doc; the live people
    // snapshot will refresh them. Re-render now for the banner.
    _portRerenderComplianceTab(personId);
  } catch (err) {
    console.error('[HR] confirmDriverRecordPort failed', err);
    window.showToast?.(err?.message || 'Could not confirm the code', 'error');
  } finally { _portBusy = false; }
}

async function cancelRecordPort(personId) {
  if (_portBusy) return;
  const tenantId = window.AppState?.tenant || HRState.tenantId;
  const emp = HRState.people.find(p => p.id === personId);
  if (!tenantId || !emp) return;
  _portBusy = true;
  try {
    const fn = httpsCallable(functions, 'cancelDriverRecordPort');
    await fn({ tenantId, personId });
    emp.recordPort = { status: 'none' };
    window.showToast?.('Request cancelled', 'success');
    _portRerenderComplianceTab(personId);
  } catch (err) {
    console.error('[HR] cancelDriverRecordPort failed', err);
    window.showToast?.(err?.message || 'Could not cancel the request', 'error');
  } finally { _portBusy = false; }
}

// "Mark reviewed" — client write on the DISPLAY mirror only. Gated on the same
// predicate as the people-update rule (_hrCanWriteRoster — dispatcher+/roster),
// so the button never advertises a write the rules will deny (dead-door rule).
async function markPortReviewed(personId) {
  const tenantId = window.AppState?.tenant || HRState.tenantId;
  const emp = HRState.people.find(p => p.id === personId);
  if (!tenantId || !emp || !emp.portedRecords) return;
  try {
    const reviewed = { ...emp.portedRecords, reviewedAt: new Date().toISOString(), reviewedBy: window.AppState?.user?.email || null };
    await updateDoc(doc(db, 'tenants', tenantId, 'people', personId), { portedRecords: reviewed });
    emp.portedRecords = reviewed;
    window.showToast?.('Ported records marked reviewed', 'success');
    _portRerenderComplianceTab(personId);
  } catch (err) {
    console.error('[HR] markPortReviewed failed', err);
    window.showToast?.('Could not save — roster write access is required to confirm ported records', 'error');
  }
}

// In-app §391.23 request — employer picker over the candidate's application
// employers[] (same entries the PEI email lane uses), then sendPeiRequestInApp.
async function openInAppPeiModal(personId) {
  const tenantId = window.AppState?.tenant || HRState.tenantId;
  const emp = HRState.people.find(p => p.id === personId);
  if (!tenantId || !emp) return;
  if (!emp.candidateId) {
    window.showToast?.('No digital application on file — the §391.23 request form is generated from the signed application', 'error');
    return;
  }
  let cand = null;
  try {
    const snap = await getDoc(doc(db, 'tenants', tenantId, 'candidates', emp.candidateId));
    if (snap.exists()) cand = snap.data();
  } catch (_) { /* fall through */ }
  const employers = Array.isArray(cand?.employers) ? cand.employers : [];
  if (!employers.length) {
    window.showToast?.('The application lists no previous employers', 'error');
    return;
  }
  document.getElementById('port-pei-modal')?.remove();
  const from = escapeHtml(emp.portedRecords?.fromCarrierName || emp.recordPort?.fromCarrierName || 'the previous employer');
  const rows = employers.map((e, i) => {
    const nm = e && (e.name || e.companyName || e.company || e.employerName);
    if (!nm) return '';
    const dates = [e.dateFrom, e.dateTo].filter(Boolean).join(' → ');
    return `<label style="display:flex;align-items:center;gap:8px;padding:8px 10px;border:1px solid var(--border-light);border-radius:6px;margin-bottom:6px;cursor:pointer;background:var(--bg-primary);">
      <input type="radio" name="port-pei-emp" value="${i}" ${i === 0 ? 'checked' : ''}>
      <span style="flex:1;min-width:0;">
        <span style="display:block;font-size:0.85rem;font-weight:600;color:var(--text-primary);">${escapeHtml(nm)}</span>
        ${dates ? `<span style="display:block;font-size:0.72rem;color:var(--text-secondary);">${escapeHtml(dates)}</span>` : ''}
      </span>
    </label>`;
  }).join('');
  const modal = document.createElement('div');
  modal.id = 'port-pei-modal';
  modal.className = 'modal';
  modal.style.cssText = 'display:flex;align-items:center;justify-content:center;z-index:1080;';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;width:94%;">
      <div class="modal-header" style="padding:14px 16px;"><h3 style="margin:0;font-size:1rem;">Send §391.23 request via DispatchDeck</h3></div>
      <div class="modal-body" style="padding:16px;">
        <p style="font-size:0.82rem;color:var(--text-secondary);margin:0 0 10px;">
          The signed Safety Performance History request form (the same PDF the email lane sends) is delivered to <b>${from}</b>'s HR action queue inside DispatchDeck. Pick which application employer entry this request is for — the response is recorded against it.
        </p>
        <div style="max-height:260px;overflow-y:auto;">${rows}</div>
      </div>
      <div class="form-actions" style="display:flex;justify-content:flex-end;gap:8px;padding:12px 16px;">
        <button class="btn btn-secondary" id="port-pei-cancel">Cancel</button>
        <button class="btn btn-primary" id="port-pei-send"><span class="material-icons" style="font-size:16px;">forward_to_inbox</span> Send request</button>
      </div>
    </div>`;
  document.body.appendChild(modal);
  modal.querySelector('#port-pei-cancel').onclick = () => modal.remove();
  modal.querySelector('#port-pei-send').onclick = async () => {
    if (_portBusy) return;
    const sel = modal.querySelector('input[name="port-pei-emp"]:checked');
    const employerIndex = sel ? Number(sel.value) : -1;
    if (employerIndex < 0) { window.showToast?.('Pick the employer entry this request is for', 'error'); return; }
    const btn = modal.querySelector('#port-pei-send');
    btn.disabled = true;
    btn.innerHTML = '<span class="btn-spinner"></span> Sending…';
    _portBusy = true;
    try {
      const fn = httpsCallable(functions, 'sendPeiRequestInApp');
      const res = await fn({ tenantId, personId, employerIndex });
      modal.remove();
      window.showToast?.(res?.data?.duplicate
        ? 'A request is already open with the previous employer — they have been notified'
        : 'Request delivered to the previous employer\'s DispatchDeck queue — investigation marked contacted', 'success');
    } catch (err) {
      console.error('[HR] sendPeiRequestInApp failed', err);
      window.showToast?.(err?.message || 'Could not send the in-app request', 'error');
      btn.disabled = false;
      btn.innerHTML = '<span class="material-icons" style="font-size:16px;">forward_to_inbox</span> Send request';
    } finally { _portBusy = false; }
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Inbound §391.23 queue (carrier A side) — open sphInquiries rendered as an
// action card on the HR landing. Reads are already granted to admin / HR-module
// / managing partner by the existing sphInquiries rule (no rules change). The
// doc carries only a responseTokenHash (TASK-1482/1541) — the respond link is
// minted server-side, so surfacing it here grants nothing new.
// ─────────────────────────────────────────────────────────────────────────────
async function _loadInboundSphInquiries(tenantId) {
  const host = document.getElementById('hr-sph-inbound');
  if (!host || !tenantId) return;
  let open = [];
  try {
    const snap = await getDocs(collection(db, 'tenants', tenantId, 'sphInquiries'));
    const now = Date.now();
    open = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(q => ['pending_verification', 'verified_pending_approval'].includes(q.status))
      .filter(q => !(q.expiresAt?.toMillis && q.expiresAt.toMillis() < now))
      .sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
  } catch (_) {
    // Not readable for this member (or offline) — leave the card hidden.
    host.style.display = 'none';
    return;
  }
  if (!open.length) { host.style.display = 'none'; host.innerHTML = ''; return; }
  // COD round-1 Code Reviewer + round-2 Adversary (2026-08-12) — responding IS
  // the §391.23(g) disclosure decision. Hash-shape inquiries (BOTH lanes since
  // TASK-1541: in-app AND external + backfilled) carry only a token HASH, so
  // the respond link is minted by the tenantAdmin-gated
  // getSphInquiryRespondLink callable — an hr-module member's doc read grants
  // nothing. The raw-responseToken branch below is TRANSITION support for docs
  // written before the TASK-1541 backfill ran; for those the link renders
  // owner/admin-only so this UI at least never widens who is HANDED the token.
  // Safe to remove once backfill-sph-inquiry-token-hash.cjs --apply has run.
  const canRespond = _hrIsOwnerOrAdmin();
  const rowsHtml = open.slice(0, 10).map(q => {
    const created = q.createdAt?.toDate?.() ? q.createdAt.toDate().toLocaleDateString() : '';
    const awaiting = q.status === 'verified_pending_approval';
    let respondCta = '';
    if (awaiting && canRespond && q.responseTokenHash) {
      respondCta = `
      <button class="btn btn-sm btn-primary" onclick="window.HRModule.openSphRespondLink('${escJsAttr(q.id)}')" style="font-size:0.75rem;">
        <span class="material-icons" style="font-size:14px;">rate_review</span> Review &amp; respond
      </button>`;
    } else if (awaiting && canRespond && q.responseToken) {
      const respondUrl = `sph-inquiry-respond.html?t=${encodeURIComponent(tenantId)}&i=${encodeURIComponent(q.id)}&token=${encodeURIComponent(q.responseToken)}`;
      respondCta = `
      <a href="${respondUrl}" target="_blank" rel="noopener" class="btn btn-sm btn-primary" style="font-size:0.75rem;text-decoration:none;">
        <span class="material-icons" style="font-size:14px;">rate_review</span> Review &amp; respond
      </a>`;
    } else if (awaiting && !canRespond) {
      respondCta = '<span style="font-size:0.72rem;color:var(--text-secondary);">Response link emailed to your admin</span>';
    }
    return `
    <div style="display:flex;align-items:center;gap:10px;padding:10px 0;border-top:1px solid var(--border-light);flex-wrap:wrap;">
      <span class="material-icons" style="font-size:20px;color:${awaiting ? '#F59E0B' : '#94A3B8'};flex-shrink:0;">${awaiting ? 'pending_actions' : 'hourglass_empty'}</span>
      <div style="flex:1;min-width:200px;">
        <div style="font-size:0.85rem;font-weight:600;color:var(--text-primary);">${escapeHtml(q.requestingCompany || 'A prospective employer')} → ${escapeHtml(q.driverName || 'former driver')}</div>
        <div style="font-size:0.72rem;color:var(--text-secondary);">§391.23 Safety Performance History request${created ? ` · received ${escapeHtml(created)}` : ''}${q.ddInApp ? ' · via DispatchDeck' : ''}${awaiting ? '' : ' · requester email not yet verified'}</div>
      </div>
      ${respondCta}
    </div>`;
  }).join('');
  host.innerHTML = `
    <div class="hr-section" style="border-left:4px solid #F59E0B;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:2px;">
        <span class="material-icons" style="color:#F59E0B;">assignment_late</span>
        <span style="font-size:1rem;font-weight:700;color:var(--text-primary);">Safety Performance History requests</span>
        <span style="font-size:0.72rem;font-weight:700;background:#FEF3C7;color:#92400E;padding:2px 9px;border-radius:10px;">${open.length}</span>
      </div>
      <div style="font-size:0.76rem;color:var(--text-secondary);">Former drivers' prospective employers are requesting §391.23 records. FMCSA requires a response within 30 days of a valid request.</div>
      ${rowsHtml}
    </div>`;
  host.style.display = 'block';
}

// Mint + open the respond link for an in-app inquiry (raw token is server-only;
// the callable is tenantAdmin-gated — see the queue renderer note above).
async function openSphRespondLink(inquiryId) {
  const tenantId = window.AppState?.tenant || HRState.tenantId;
  if (!tenantId || !inquiryId) return;
  try {
    const fn = httpsCallable(functions, 'getSphInquiryRespondLink');
    const res = await fn({ tenantId, inquiryId });
    if (res?.data?.url) {
      window.open(res.data.url, '_blank', 'noopener');
    } else {
      window.showToast?.('No respond link available for this inquiry', 'error');
    }
  } catch (err) {
    console.error('[HR] getSphInquiryRespondLink failed', err);
    window.showToast?.(err?.message || 'Only an owner or admin can open the respond link', 'error');
  }
}

window.HRModule = {
  closeHrJsaDetailModal,
  // TASK-1605 — "pick a screening package" stop, shared with compliance.js
  // (Pitfall #1 — reached through window, never a cross-import).
  screeningPackageStopHtml: hrScreeningPackageStopHtml,
  // Exposed for the driver-side Team directory (roster.js) to resolve a
  // person's HR Title from hrPosition. Built-in REVIEW_POSITIONS resolve in any
  // session (static const); custom positions need HRState (admin sessions only),
  // so callers fall back to crewType when this returns the raw id.
  getPositionTitle,
  warnFlsaChange,
  sendHiringPacketLink,
  requestSevenDayStatement,
  // TASK-219 Slice 2 — carrier-side run-bg-check trigger
  runHrTwoStageBgCheck,
  // TASK-10033 — office picks the package a two-stage Run bills
  openHrScreeningPackagePicker,
  // TASK-1386 — cancel an open carrier→partner BG request
  cancelHrBgRequest,
  // TASK-1384 — person-typed counterpart (person MVR card "requested" state)
  cancelPersonBgRequest,
  // TASK-1482 — driver record portability (driver-approved, SMS OTP)
  startRecordPort,
  confirmRecordPort,
  cancelRecordPort,
  markPortReviewed,
  openInAppPeiModal,
  openSphRespondLink,
  openDisciplinaryForm,
  selectWarningType,
  _onDiscEmployeeChange,
  prefillTerminationReason,
  submitDisciplinaryForm,
  viewDisciplinaryForm,
  editDisciplinaryForm,
  printDisciplinaryForm,
  deleteDisciplinaryForm,
  deleteViewedDisciplinaryForm,
  clearSignatureCanvas,
  clearSupervisorSig,
  openNewChecklistModal,
  createNewChecklist,
  viewChecklist,
  toggleChecklistItem,
  assignChecklistItem,
  createNewHirePacket,
  createPeiForm,
  generateSphResponse,
  _submitSphResponse,
  openReviewModal,
  onEmployeeSelect,
  onPositionChange,
  calculateScores,
  calculateCompensation,
  submitReview,
  finalizeReview, // TASK-4760 — the Submit & Lock button's onclick
  viewReview,
  editReview,
  printReview,
  printCurrentReview,
  closeModal,
  toggleSection,
  toggleCompanyDocsSection,
  filterByCategory,
  setDocCategory,
  setDocCategoryDirect,
  renameHrResource,
  promptCategorizeAny,
  _onDocDragStart,
  _onDocDragEnd,
  _onChipDragOver,
  _onChipDragLeave,
  _onChipDrop,
  setDirectoryStatus,
  filterEmployees,
  // TASK-582 — compliance tile → filtered directory
  openComplianceDirectory,
  openCmvNeedsAnswers, // TASK-9850
  openDaReceiptBulkSend, // TASK-9145
  clearDirectoryComplianceFilter,
  openEmployeeDetail,
  switchDetailTab,
  editEmployeeHR,
  openEmployeeClassification, // TASK-1391 — amber "Needs DOT Role" badge tap
  // TASK-980 / TASK-9424 — add a flag / clear one named flag (reason required)
  addDriverFlag,
  clearDriverFlag,
  promptAddHrPosition,
  saveEmployeeHR,
  deleteReview,
  voidReview,
  voidReviewFromModal,
  savePersonReviewReminder,
  renderReviewScheduleEditor, addReviewScheduleRow, removeReviewScheduleRow, saveReviewSchedule, // TASK-9456
  saveEmployeeNotes,
  printEmployeeNotes,
  // TASK-346 Slice 3 — structured hrNotes feed
  loadEmployeeNotesFeed,
  addEmployeeNote,
  deleteEmployeeNote,
  // TASK-464 — collapsible notes feed header toggle
  toggleEmployeeNotesSection,
  // TASK-350 — calendar-feel observedAt + click-to-edit own notes
  editEmployeeNote,
  cancelEditEmployeeNote,
  saveEmployeeNoteEdit,
  // TASK-346 reframe (2026-05-21) — Add Employee Note modal
  // (replaces killed My Crew dedicated sub-view + sidebar entry).
  // openAddEmployeeNoteModal is the public contract called by Worker R's
  // driver-portal Actions sheet Team-note tile (TASK-347 Slice 3).
  openAddEmployeeNoteModal,
  submitAddEmployeeNote,
  applyAddEmployeeNoteGate,
  // TASK-351 polish (2026-05-22) — own-notes-only feed in Add Note modal
  _onAddNoteEmployeeChange,
  // TASK-684 — attach image/PDF to an HR note (both composers)
  _onHrNoteAttachPick,
  _clearHrNoteAttach,
  resetAllDvirData,
  resetMissedDvirCount,
  resetMissedJsaCount,
  openMissedEventsDrill, // TASK-1003 — dated missed-DVIR/JSA drill-in
  deleteDvirFlag,
  deleteJsaFlag,
  loadTimeOffBalance,
  updateTimeOffLevel,
  dismissTimeOffLevelSuggestion,
  // TASK-9535 — requests by employee + everyone's balances (Lacey)
  setTimeOffPersonFilter,
  setTimeOffGroupByPerson,
  // TASK-9601 — Time Off list summary strip + filter bar
  setTimeOffSearch,
  setTimeOffGroup,
  setTimeOffWindow,
  setTimeOffSort,
  toggleTimeOffStatus,
  toggleTimeOffType,
  toggleTimeOffTile,
  clearTimeOffFilters,
  toggleTimeOffMoreFilters,
  openTimeOffForEmployee,
  // TASK-9602 — employee Time Off tab: year selector + history CSV
  setTimeOffHistoryYear,
  downloadTimeOffHistoryCsv,
  toggleTimeOffBalances,
  renderTimeOffBalancesTable,
  assignTimeOffLevelFromTable,
  printTimeOffBalances,
  computeEmployeeTimeOffBalances,
  buildTimeOffBalanceTable,
  openTimeOffPolicyEditor,
  editPolicyLevel,
  savePolicyLevel,
  addPolicyGrantRow,
  removePolicyGrantRow,
  previewPolicyGrants,
  saveBereavementPolicy,
  deletePolicyLevel,
  addPolicyLevel,
  onAccrualMethodChange,
  // TASK-341 Company Holidays
  addCompanyHoliday,
  editCompanyHoliday,
  saveCompanyHoliday,
  deleteCompanyHoliday,
  approveTimeOff,
  denyTimeOff,
  applyDenyReasonChip,
  closeDenyTimeOffModal,
  confirmDenyTimeOff,
  deleteTimeOff,
  openDayPicker,
  closeDayPicker,
  saveDayPicker,
  refreshDayPickerBalance,
  toggleDayPickerPerDay,
  onDayPickerBaseTypeChange,
  openTimeOffRequest,
  toggleTimeOffHistory,
  submitTimeOffRequest,
  updateTimeOffHours,
  autofillBereavementHours,
  // Safety Sign-In
  copySafetyLink,
  // TASK-342 Slice D — safety meeting enable toggle
  toggleSafetyMeetingEnabled,
  // TASK-437 Slice 1 — PPE driver sign-off enable toggle
  toggleSafetyEquipmentDriverEnabled,
  generateSafetyPdf,
  loadSafetySignInsForDate,
  openMeetingDetailsModal,
  closeMeetingDetailsModal,
  saveMeetingDetails,
  // TASK-9742 — safety meeting templates + attached company documents
  applySafetyMeetingTemplate,
  toggleSaveAsTemplate,
  saveMeetingAsTemplate,
  openMeetingDocPicker,
  filterMeetingDocPicker,
  closeMeetingDocPicker,
  applyMeetingDocPicker,
  removeMeetingDoc,
  uploadMeetingDoc,
  openSafetyTemplateManager,
  closeSafetyTemplateManager,
  startRenameSafetyTemplate,
  renameSafetyTemplate,
  deleteSafetyTemplate,
  filterSignInsByRange,
  clearSignInDateRange,
  // JSA Reports
  renderJSAReportsList,
  viewJSADetail,
  printSingleJSA,
  clearJSAFilters,
  printJSAReports,
  toggleJSAReports, // TASK-9581
  loadMoreJSAReports, // TASK-9581
  // Safety Sub-View
  openSafetySubView,
  // TASK-1140 Slice 1 — Incident & Accident Reports (capture)
  renderIncidentsList,
  openIncidentModal,
  handleIncidentPhotoCapture,
  removeIncidentPhoto,
  saveIncident,
  viewIncidentDetail,
  // TASK-1140 Slice 2 — §390.15 accident register (generated view over incidents)
  openAccidentRegister,
  // TASK-9570 — injury rates (TRIR / DART)
  setSafetyRateOption,
  retrySafetyRates,
  setIncidentOsha,
  printSafetyRates,
  printAccidentRegister,
  exportAccidentRegisterCsv,
  // TASK-772 — Safety Records (OSHA 300/300A)
  renderSafetyRecordsList,
  openSafetyRecordUploadModal,
  _submitSafetyRecordUpload,
  switchSafetyMode,
  switchSafetyManagerTab,
  // TASK-9452 — PPE Reports tab
  setPpeReportPreset, setPpeReportDates, setPpeReportFilter, setPpeReportView,
  clearPpeReportFilters, drillPpeReport, printPpeReport, exportPpeReportCsv,
  onSafetySearchInput, // TASK-840 — PPE serial-number search (user mode)
  onEquipmentTypeSelect,
  promptAddEquipmentType,
  setAddEquipTypeHasSize,
  submitAddEquipmentType,
  openManageEquipmentTypes,
  deleteEquipmentType,
  submitSafetyEquipment,
  refreshSafetyJobContext, // TASK-9721
  onSafetyJobChange,
  refreshIssueEquipJobContext,
  onIssueEquipJobChange,
  // TASK-9719 — void (never delete) a PPE issue; Return with qty + condition
  voidSafetyEquipmentRecord,
  submitVoidSafetyEquipment,
  submitEquipmentReturn,
  // TASK-9720 — stock counts (Track stock, Receive / Adjust)
  openTrackStockPicker,
  openTrackStockSheet,
  submitTrackStock,
  stopTrackingStock,
  openStockMoveSheet,
  submitStockMove,
  // TASK-9722 — Count now, History (also opened from Roster > Assets), strip, return signature
  _stockMovePreview: _hrStockMovePreview,
  openSupplyCountSheet,
  _supplyCountPreview,
  submitSupplyCount,
  openSupplyHistory,
  loadMoreSupplyHistory,
  setSupplyHistoryFilter,
  openSupplyIssueRecord,
  priceSupplyReceiptRow, // TASK-9724 S2 — add a price to an unpriced receipt (owner / Billing, switch on)
  exportSupplyHistoryCsv,
  printSupplyHistory,
  renderSupplyStripInto,
  _clearReturnSignature,
  _supplyCsvText: _hrSupplyCsvText,
  // TASK-437 Slice 4 — driver PPE sign-off viewer (admin side)
  viewPpeSignoff,
  // TASK-881 — full PPE record detail viewer (click a Recent-Log / search row)
  viewPpeDetail,
  // TASK-1578 Slice 1 — person-card Issued Equipment (loan/return ledger over
  // the SAME safetyEquipment collection — no new collection, no rules delta)
  openIssueEquipmentModal,
  selectIssueEquipPreset,
  pickIssueEquipSerial, // TASK-9728
  _markIssueEquipCategoryTouched,
  submitIssueEquipment,
  markEquipmentReturned,
  // TASK-1578 Slice 2 — printables (tenant-branded; no hardcoded carrier).
  // The two INSERTS fill in the tenant's own signed paperwork; the generic
  // agreement is the demoted fallback for a tenant that has none.
  printEquipmentIssuedInsert,
  printSeparationCheckoutInsert,
  printEquipmentIssueReceipt,
  printCompanyPropertyAgreement,
  // TASK-1578 Slice 4 — separation property checkout (live list on the
  // termination notice). Only the answer setter is exported; the renderer is
  // driven from the form's own lifecycle hooks.
  _setTermEquipAnswer: _hrSetTermEquipAnswer,
  // Sub-Views
  openDisciplinarySubView,
  openChecklistsSubView,
  openReviewsSubView,
  copySurveyLink,
  showAddLinkForm,
  saveLink,
  removeLink,
  // Hiring / Applicant Tracking
  openHiringSubView,
  backToHRLanding,
  openAddCandidateModal,
  openEditCandidateModal,
  saveCandidateForm,
  deleteCandidate,
  deleteCandidateForever, // TASK-1216 — the ONLY permanent-destruction path (archived view)
  restoreCandidate,
  toggleShowArchivedCandidates,
  setCandidateView, // TASK-9293 — Active / Hired / Rejected & Withdrawn / Archived
  candidateListView, // TASK-9293 — exported for the unit tripwire + any host that needs the bucket
  setCandidateSort, // TASK-1101 — Hiring/Applicants sort control
  toggleFoldedDrafts, // TASK-9583 — show / hide folded duplicate no-SSN drafts
  // TASK-1213 — candidate Notes & Calls feed
  addCandidateNote,
  deleteCandidateNote,
  // TASK-1212 — action-bar dispositions (open the existing quick-status modal preset)
  openQuickStatusModalPreset,
  onQuickStatusStageChange,
  // TASK-1219 — interview scheduling
  openScheduleInterviewModal,
  saveScheduleInterview,
  cancelCandidateInterview,
  viewCandidate,
  openCandidateBgRun, // TASK-10029
  // TASK-4550 — send an application back / correct a typo without a round trip
  openSendBackModal,
  submitSendBack,
  sendApplicationLink, // TASK-9530
  cancelApplicationLink, // TASK-9530
  openCorrectEmployerModal,
  submitEmployerCorrection,
  recordPriorEmployerVerification,
  updateReportStatus,
  recordExistingCandidateMvr,
  applyCheckrResult,
  resetReportToCheckr,
  overrideReportAnyway,
  manualSetReportStatus,
  copyApplyLink,
  _copyApplyLinkConfirm,
  openCitationModal,
  saveCitationFromHR,
  deleteCitationRecord,
  openQuickStatusModal,
  saveQuickStatus,
  // TASK-1155 — Adverse-Action CTA transforms (inline onchange handlers).
  syncQuickStatusDecisionCta,
  syncCandidateFormDecisionCta,
  openApproveToRosterModal,
  confirmApproveToRoster,
  // TASK-1312 — inline onchange on #approve-position-type.
  syncApproveCdlMismatchWarning,
  // Clearinghouse (Phase 4)
  openClearinghouseModal,
  saveClearinghouseQuery,
  // TASK-1045 — shared single-source mapping/next-due helpers; the Compliance
  // driver detail (compliance.js) calls these via window.HRModule at save time
  // so the clearinghouse enum + annual next-due never fork again (TASK-1022).
  deriveClearinghouseStatus,
  sortClearinghouseHistoryNewestFirst, // TASK-9449
  complianceNextDueOneYear,
  // TASK-9449 — partner-session upload + Clearinghouse paths, shared with compliance.js.
  partnerClientTenantId: _hrPartnerClientTenantId,
  partnerUploadPersonDoc: _hrPartnerUploadPersonDoc,
  partnerSaveClearinghouseQuery: _hrPartnerSaveClearinghouseQuery,
  partnerChErrorText: _hrPartnerChErrorText,
  // TASK-4748 — access → mode for the "Log Clearinghouse Query" form, shared with compliance.js.
  clearinghouseLogMode,
  // TASK-1320 — Break-in-Service work history (gate + opener), shared with compliance.js.
  hrCanRecordBisGap,
  openBisGapAccount,
  CLEARINGHOUSE_READONLY_MSG,
  // Annual Review (Phase 5)
  openAnnualReviewModal,
  saveAnnualReview,
  // MVR + DQ File (Phase 6 — compliance lifecycle)
  ensureSubjectInHrState, // TASK-811 — let Compliance hydrate HRState before delegating MVR/BG openers
  openMvrPullModal,
  checkrPackageCoversMvr,
  // TASK-1384 — bg-doc invitation evidence classifier ('signature'/'processing'/
  // null), shared with compliance.js via window.HRModule (Pitfall #1).
  mvrBgDocPendingKind,
  saveMvrPull,
  openMvrGoodFaith, // TASK-9822
  onMvrFilePicked,
  autofillMvrFromPdf,
  // Background Check (Checkr)
  openBackgroundCheckModal,
  // TASK-1367 — exported so any other surface that offers a "run/request a
  // background check" affordance (compliance.js, future entry points, TASK-1368's
  // button gating) reads the SAME predicate instead of re-deriving it.
  hrCanOrderBackgroundCheck,
  runBackgroundCheck,
  // TASK-1628 — hire-date correction (the PSP pre-hire-only escape hatch).
  openHireDateCorrection,
  submitHireDateCorrection,
  openBgReportViewer,
  openBgReportViewerCompliance, // TASK-832 — minimized compliance-mode viewer (no AA/raw-PDF/dashboard)
  closeBgReportViewer,
  // TASK-969 — MVR as a first-class, separate, printable piece
  openMvrReportViewer,
  openMvrReportViewerCompliance, // minimized compliance-mode MVR viewer (no AA routing/raw PDF)
  openMvrReportFromBgViewer,
  closeMvrReportViewer,
  printMvrReport,
  downloadBgReportPdf,
  hrCanOpenMvrReport, // TASK-9295
  hrMvrReportOnClick, // TASK-9295 — compliance.js MVR row host
  hrMvrReportButton, // TASK-9295 — { onClick } | { disabledReason } | null
  hrCdlisCardHtml, // TASK-9438 — CDLIS row on the driver card (carrier host; '' when this viewer may not see it)
  hrGetsFullCheckrReport, // TASK-9295
  printBgSnapshot,
  printCandidateApplication,
  // TASK-1488 — audited full-SSN reveal on the §391.21 application modal
  toggleCandidateSsnReveal,
  openEnterCandidateSsnModal, // TASK-9455
  // TASK-1103 — post-hire "View/Print Application" (Roster / Compliance /
  // employee-detail delegate here via person.candidateId)
  openCandidateApplication,
  openCandidateApplicationForPerson,
  // TASK-1099 — §391.23 Safety Performance History Investigation pop-out
  openPeiInvestigation,
  setPeiContactStatus,
  onPeiContactMethodChange,
  markPeiContacted,
  markPeiNotApplicable,
  restorePeiEmployer,
  savePeiResponse,
  printPeiRequestForm,
  // TASK-1374 — §391.23(c)(2) good-faith attempt log (inline onclick handlers
  // in the PEI pop-out employer cards).
  addPeiAttempt,
  printPeiGoodFaithLog,
  emailPeiRequestForm,
  printPeiSheet,
  openDqPacketViewer,
  closeDqPacketViewer,
  printDqPacket,
  // BG Decision UI Phase 1+2 (2026-05-12)
  clearBgDecision,
  holdBgDecision,
  adverseActionBgDecision,
  declineNotBasedOnReport,
  // FCRA AA Workflow TASK-243 (2026-05-14)
  cancelAdverseAction,
  sendFinalAdverseAction,
  dotRemoteFinalAdverseAction, // TASK-9790 q10 — FCRA §604(b)(3)(B) DOT remote-applicant path
  runDqFileAudit,
  openMedExamModal,
  openIntrastateExemptionModal, // TASK-1403
  saveIntrastateExemption, // TASK-1403
  saveMedExam,
  sendMedUploadLink,
  // TASK-1047 (818-S3) — single-source the med-exam restriction enum so
  // compliance.js can consume it via window.HRModule (never a third copy).
  MED_RESTRICTION_OPTIONS,
  // TASK-1048 (818-S4) — single-source the MVR purpose enum so compliance.js's
  // MVR modal consumes the canonical config via window.HRModule (never a copy).
  MVR_PURPOSE_DISPLAY,
  handleResumeFileSelect,
  switchResumeTab,
  removeResume,
  filterCandidatesByStatus,
  searchCandidates,
  clearCandidateFilters,
  retryCandidatesNow,
  toggleRejectionReason,
  // Employee Document Uploads
  uploadEmployeeDocument,
  deleteEmployeeDocument,
  uploadOnboardingDoc,
  removeOnboardingDoc,
  deleteSlotExtraFile, // TASK-1381 — extra files under a packet slot
  viewPacketDoc, // TASK-1029 — packet-slot View via byte-stream callable
  uploadHiringPacketTemplate,
  removeHiringPacketTemplate,
  retagEmployeeDocument,
  renameEmployeeDocument,
  // TASK-970 — Full DQ File ZIP (owner/admin) + read-only grouped doc browser for compliance.js
  downloadFullDqFile,
  renderDqDocBrowserHtml,
  toggleDqDocSection, // TASK-1100 — collapsible DQ/compliance doc sections
  jumpToDqDocSection, // TASK-1100
  openDqDoc,
  // TASK-321 — Offer Letter Generator (v1)
  openOfferLetterTemplateModal,
  saveOfferLetterTemplate,
  resetOfferLetterTemplate,
  openOfferLetterGenerateModal,
  generateOfferLetterPdf,
  // Payroll Roster Export
  toggleRosterExportMenu,
  exportPayrollRoster,
  printPayrollRoster,
  // TASK-843 — Export roster as QuickBooks Online Payroll import CSV
  exportQboPayrollCsv,
  // ISNet
  addIsnetTraining,
  editIsnetTraining,
  saveIsnetTraining,
  deleteIsnetTraining,
  toggleTrainingExemption,
  printISNetRoster,
  // TASK-513 Safety Roster
  _onRequiredTrainingPick,
  printSafetyRosterMatrix,
  // TASK-723 — interactive matrix panel + click-to-upload
  _printSafetyRosterMatrixPdf,
  closeSafetyMatrixPanel,
  openMatrixCertUpload,
  // TASK-9911 M2 — the one Skills & certifications screen (door on HR → Safety),
  // its count refresh, and the cert-state calc the roster person section reads.
  openSkillsAndCerts,
  refreshSafetyCertDoor,
  computeRequiredTrainingState,
  // Drug Tests (HR-owned)
  showDrugTestModal,
  handleDrugTestFile: handleDrugTestFileUpload,
  removeDrugTestFile,
  saveDrugTest: saveDrugTestRecord,
  deleteDrugTestRecord,
  viewDrugTestDocument,
  // §382.601(d) incumbent policy receipt (TASK-DA-v1 slice 2)
  sendIncumbentDaReceiptLink,
  viewSignedDaReceipt, // TASK-1154 — open the signed receipt PDF (byte-stream)
  // Medical Card (HR-owned)
  uploadMedCardDocument,
  removeMedCardDocument,
  removeMedCertPdf, // TASK-647-FF — clear a cert-source (medCertPdfUrl) med card
  updateMedCardExpiry,
  // TASK-647 — shared med-card reader; compliance.js calls this so the partner
  // portal + DQ file see driver SMS self-uploads (medCertPdfUrl), not just
  // medCardDocumentUrl. One canonical reader, one link rendered everywhere.
  getEffectiveMedCardDoc,
  // Driver's License (HR-owned)
  uploadDlDocument,
  removeDlDocument,
  // HR Resources (Company Docs & Links)
  showAddResourceModal,
  showAddDocumentMenu,
  saveHrResource,
  deleteHrResource,
  togglePublicShare,
  toggleAiSearch,
  sharePublicDocsPage,
  resetPublicDocsLink, // TASK-9888
  // TASK-483 — in-app driver document access
  toggleDriverDocs,
  openDriverDocs,
  // TASK-1017 — driver self-service "My Documents" (own DQ file)
  openDriverMyDocuments,
  _driverMyDocOpen,
  _driverSafetySheetOpen, // TASK-4883
  // TASK-261 Slice 1B / TASK-482 — uploadCompanyDocument is an alias for the
  // category-picker modal entry point. _submitUploadCompanyDocumentModal +
  // _uploadCompanyDocumentFile back the modal flow; the (+) "create folder"
  // handlers below back the new picker pattern (TASK-482).
  uploadCompanyDocument: showUploadCompanyDocumentModal,
  showUploadCompanyDocumentModal,
  _submitUploadCompanyDocumentModal,
  _showUploadDocNewCategory,
  _addUploadDocNewCategory,
  _cancelUploadDocNewCategory,
  _uploadCompanyDocumentFile,
  openDocAssistant,
  sendDocQuestion,
  saveGeneratedDoc,
  copyGeneratedDoc,
  // Compliance Documents
  addComplianceDoc,
  removeComplianceDoc,
  // TASK-9234 — resolves a compliance doc's URL on demand from storagePath
  // through the storage.rules canReadCompliancePII gate; nothing bearer-shaped
  // is persisted on the broadcast person doc any more.
  openComplianceDocFile: _openComplianceDocFile,
  getExpiredDocs,
  // Compliance Tab Edit
  editComplianceTab,
  cancelComplianceEdit,
  toggleFieldReveal,
  saveComplianceFields,
  // Employment Tab Edit
  editEmploymentTab,
  cancelEmploymentEdit,
  saveEmploymentFields,
  // TASK-4865 — fuel-card PIN reveal-on-demand (display header + edit box).
  revealFuelCardPin,
  revealFuelCardPinInput,
  // TASK-9291 — additional-card assignment
  addAdditionalCard,
  removeAdditionalCard,
  addCardFromPersonPage, // TASK-9593
  // Recurring Adjustments (TASK-941; widened past deduction-only by TASK-4848).
  // `addRecurringDeduction` keeps its name on purpose — the Add form here is
  // still deduction-only; only the LIST shows every recurring type.
  addRecurringDeduction,
  removeRecurringAdjustment,
  // Tax Tab Edit
  editTaxTab,
  cancelTaxEdit,
  saveTaxFields,
  fillTaxFromHomeAddress,
  // Document Signing
  showSendForSignature,
  handleSignDocFile,
  clearSignDocFile,
  sendForSignature,
  openPlacementModal,
  closePlacementModal,
  addSignatureField,
  addAuxField,
  removeSignatureField,
  updateSignerField,
  placeFieldOnCurrentPage,
  sigPlacementPage,
  sendWithFields,
  withdrawSignDocument, // TASK-1634 — renamed from deleteSignDocument: it now soft-cancels via a callable
  // TASK-1638 — Document Library (tenant-scoped reusable e-sign documents)
  showSignDocLibrary,
  closeSignDocLibrary,
  openSignPackets: () => _spCall('open'), // TASK-10175 S4
  signLibUpload,
  signLibRename,
  signLibDelete,
  onSignLibrarySelect,
  fillSignerFromRoster,
  viewSignedDoc, // TASK-1118 — byte-stream View for signed e-sign PDFs
  // Refresh employee detail (for use by compliance tab)
  refreshEmployeeDetail: () => { if (HRState.selectedEmployee) openEmployeeDetail(HRState.selectedEmployee.id); },
  // OSHA 1910.1020(g) — expose live people for the annual-notification modal
  // in app.js. Reads HRState.people (authoritative onSnapshot source), since
  // AppState.people is never populated.
  getOshaPeopleForNotification,
  updateOshaNotificationBadge,
  // Exposed for compliance.js — do NOT import hr.js directly from other modules
  // (different ?v= query strings create duplicate module instances with separate state)
  initSafetyStandalone,
  // TASK-263 Driver Memos (Phase 1)
  openMemosSubView,
  backToHRLandingFromMemos,
  openNewMemoModal,
  closeNewMemoModal,
  onMemoPickerSearch,
  toggleMemoRecipient,
  selectAllForMemo,
  selectAllDriversForMemo,
  clearMemoRecipients,
  onMemoPdfChange,
  onMemoSendMethodChange,
  updateMemoSmsCounter,
  onMemoBodyInput, // TASK-10142
  submitNewMemo,
  openMemoDrillModal,
  closeMemoDrillModal,
  // TASK-9841 — saved messages, schedule send, cancel, send again, remind
  applyMemoTemplate,
  beginSaveMemoTemplate,
  confirmSaveMemoTemplate,
  cancelSaveMemoTemplate,
  deleteMemoTemplateAction,
  onMemoWhenChange,
  cancelScheduledMemoAction,
  resendMemo,
  remindMemoUnconfirmedAction,
  openTrainingAssign, // TASK-10210
  // TASK-972 S4 — DOT auditor access links (mint/list/revoke, owner/admin only).
  // compliance.js delegates here at call time (window.*, never a ?v= import).
  openDotAuditAccess: (opts) => openDotAuditAccessModal(opts),
  mintDotAuditLink: () => mintDotAuditLinkAction(),
  revokeDotAuditLink: (tokenId) => revokeDotAuditLinkAction(tokenId),
  toggleDotAuditInactive: (show) => { _dotAuditShowInactive = !!show; _dotAuditRenderLinks(); },
  // TASK-1209 (d) — search/pill driver selector handlers
  setDotAuditAllDrivers: (checked) => _dotAuditSetAllDrivers(!!checked),
  searchDotAuditDrivers: (value) => _dotAuditSearchDrivers(value),
  toggleDotAuditDriver: (id) => _dotAuditToggleDriver(id),
};

export default { initHR };

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
